import { useState, useMemo, useRef, useCallback, useEffect, Fragment, forwardRef } from "react";
import { createPortal } from "react-dom";
import "leaflet/dist/leaflet.css";
import { PrivacyPage, TermsPage, MerchantPage } from "./lib/LegalPages.jsx";
import { PLAY_STORE_URL } from "./lib/appLinks.js";
import { fetchOnlineBookingQuota, fetchStaffSeats } from "./lib/api.js";
import {
  Home, Calendar, Users, Settings, LogOut, Star, Clock, DollarSign, BookOpen,
  PawPrint, Plus, ChevronRight, ChevronLeft, Check, X, Camera,
  MessageCircle, Wallet, BarChart2, Crown, Zap, Bell, Search,
  Edit, Trash2, ArrowUpRight, ArrowDownLeft, Lock, AlertTriangle,
  TrendingUp, Package, CheckCheck, Filter, Heart,
  Award, ThumbsUp, Box, Repeat, Info, Save, HelpCircle, BadgeCheck,
  Megaphone, Tag, Scissors, StickyNote, PartyPopper,
  Link, Globe, Store, Eye, EyeOff, ChevronDown, ChevronUp, UserPlus,
  Upload, Image, Send, Phone, MapPin, Briefcase, UserCheck, UserX,
  CalendarDays, CalendarClock, Moon, Sun, Sunrise, Sunset, Activity, Menu,
  Copy, ClipboardEdit, FileText, Printer, Circle, GripVertical, Mail,
  Sparkles, Loader, Mic, ShoppingBag } from "lucide-react";

// ── PET ICONS (cute avatars for header) ────────────────────────────────────
const PetIcon = ({ type, size = 28 }) => {
  const icons = {
    dog: "🐕", cat: "🐈", paw: "🐾", bone: "🦴", heart: "💕",
    star: "⭐", ribbon: "🎀", crown: "👑", bath: "🛁", sparkle: "✨"
  };
  return <span style={{ fontSize: size, lineHeight: 1 }}>{icons[type] || "🐾"}</span>;
};

const THEMES = {
  peach: { '--c-primary': '#FF758F', '--c-primary-15': '#FF758F26', '--c-primary-20': '#FF758F33', '--c-primary-30': '#FF758F4D', '--c-primary-40': '#FF758F66', '--c-grad1': '#FF9A9E', '--c-grad1-30': '#FF9A9E4D', '--c-grad2': '#FFB8D0', '--c-light': '#FFF0F3', '--c-lighter': '#FFF5F7', '--c-border': '#FFE4E8', '--c-dark': '#C21A44' },
  lavender: { '--c-primary': '#A855F7', '--c-primary-15': '#A855F726', '--c-primary-20': '#A855F733', '--c-primary-30': '#A855F74D', '--c-primary-40': '#A855F766', '--c-grad1': '#C084FC', '--c-grad1-30': '#C084FC4D', '--c-grad2': '#E9D5FF', '--c-light': '#F3E8FF', '--c-lighter': '#FAF5FF', '--c-border': '#E9D5FF', '--c-dark': '#7E22CE' },
  mint: { '--c-primary': '#10B981', '--c-primary-15': '#10B98126', '--c-primary-20': '#10B98133', '--c-primary-30': '#10B9814D', '--c-primary-40': '#10B98166', '--c-grad1': '#34D399', '--c-grad1-30': '#34D3994D', '--c-grad2': '#A7F3D0', '--c-light': '#D1FAE5', '--c-lighter': '#ECFDF5', '--c-border': '#A7F3D0', '--c-dark': '#047857' },
  sky: { '--c-primary': '#3B82F6', '--c-primary-15': '#3B82F626', '--c-primary-20': '#3B82F633', '--c-primary-30': '#3B82F64D', '--c-primary-40': '#3B82F666', '--c-grad1': '#60A5FA', '--c-grad1-30': '#60A5FA4D', '--c-grad2': '#BFDBFE', '--c-light': '#DBEAFE', '--c-lighter': '#EFF6FF', '--c-border': '#BFDBFE', '--c-dark': '#1D4ED8' },
  sunset: { '--c-primary': '#F97316', '--c-primary-15': '#F9731626', '--c-primary-20': '#F9731633', '--c-primary-30': '#F973164D', '--c-primary-40': '#F9731666', '--c-grad1': '#FB923C', '--c-grad1-30': '#FB923C4D', '--c-grad2': '#FED7AA', '--c-light': '#FFF7ED', '--c-lighter': '#FFFBF5', '--c-border': '#FFEDD5', '--c-dark': '#C2410C' },
  rose: { '--c-primary': '#E11D48', '--c-primary-15': '#E11D4826', '--c-primary-20': '#E11D4833', '--c-primary-30': '#E11D484D', '--c-primary-40': '#E11D4866', '--c-grad1': '#FB7185', '--c-grad1-30': '#FB71854D', '--c-grad2': '#FECDD3', '--c-light': '#FFF1F2', '--c-lighter': '#FFF5F6', '--c-border': '#FFE4E6', '--c-dark': '#9F1239' },
  ocean: { '--c-primary': '#0891B2', '--c-primary-15': '#0891B226', '--c-primary-20': '#0891B233', '--c-primary-30': '#0891B24D', '--c-primary-40': '#0891B266', '--c-grad1': '#22D3EE', '--c-grad1-30': '#22D3EE4D', '--c-grad2': '#A5F3FC', '--c-light': '#ECFEFF', '--c-lighter': '#F5FEFF', '--c-border': '#CFFAFE', '--c-dark': '#155E75' },
  berry: { '--c-primary': '#7C3AED', '--c-primary-15': '#7C3AED26', '--c-primary-20': '#7C3AED33', '--c-primary-30': '#7C3AED4D', '--c-primary-40': '#7C3AED66', '--c-grad1': '#8B5CF6', '--c-grad1-30': '#8B5CF64D', '--c-grad2': '#C4B5FD', '--c-light': '#EDE9FE', '--c-lighter': '#F5F3FF', '--c-border': '#DDD6FE', '--c-dark': '#5B21B6' },
};
// A customer's own colour for their portal (我的 → 主題顏色). Per device.
// Unset = follow the shop's theme; set = the shop's theme no longer changes it.
const CUSTOMER_THEME_KEY = "petos:customer-theme";
function getCustomerTheme() {
  try { const v = localStorage.getItem(CUSTOMER_THEME_KEY); return v && THEMES[v] ? v : null; } catch { return null; }
}
function setCustomerTheme(key) {
  try { if (key) localStorage.setItem(CUSTOMER_THEME_KEY, key); else localStorage.removeItem(CUSTOMER_THEME_KEY); } catch { /* private mode */ }
  try { window.dispatchEvent(new Event(CUSTOMER_THEME_KEY)); } catch { /* ignore */ }
}
function useCustomerTheme() {
  const [k, setK] = useState(getCustomerTheme);
  useEffect(() => {
    const on = () => setK(getCustomerTheme());
    window.addEventListener(CUSTOMER_THEME_KEY, on);
    return () => window.removeEventListener(CUSTOMER_THEME_KEY, on);
  }, []);
  return k;
}
const THEME_NAMES = { peach:"蜜桃粉", lavender:"薰衣草紫", mint:"薄荷綠", sky:"晴空藍", sunset:"夕陽橘", rose:"玫瑰紅", ocean:"海洋青", berry:"莓果紫" };

const PLANS = {
  free:       { name:"免費版",   price:0,   maxStaff:1, onlineBookingsPerMonth:10, color:"#888",            features:{ pos:false, onlineBooking:true, scheduling:false, whatsapp:false, wallet:false, payroll:false, analytics:false, loyalty:false, stampCard:false, autoNotify:false, removeBranding:false, brandedApp:false, aiConcierge:false, shopAssistant:false } },
  basic:      { name:"基本版",   price:128,  maxStaff:3, posMaxProducts:100, color:"#3b82f6",         features:{ pos:"lite", onlineBooking:true,  scheduling:true,  whatsapp:false, wallet:false, payroll:false, analytics:true,  loyalty:false, stampCard:false, autoNotify:true,  removeBranding:false, brandedApp:false, aiConcierge:false, shopAssistant:false } },
  pro:        { name:"專業版",   price:268, maxStaff:5, posMaxProducts:500, color:"var(--c-primary)",features:{ pos:"standard", onlineBooking:true,  scheduling:true,  whatsapp:true,  wallet:true,  payroll:true,  analytics:true,  loyalty:false, stampCard:true,  autoNotify:true,  removeBranding:false, brandedApp:false, aiConcierge:false, shopAssistant:true } },
  enterprise: { name:"旗艦版",   price:528, maxStaff:8, posMaxProducts:0, color:"#7C3AED",         features:{ pos:"full", onlineBooking:true,  scheduling:true,  whatsapp:true,  wallet:true,  payroll:true,  analytics:true,  loyalty:true,  stampCard:true,  autoNotify:true,  removeBranding:true,  brandedApp:true, aiConcierge:true, shopAssistant:true } },
};

// Feature rows shown when comparing plans (plan page + upgrade confirmation).
const ALL_PLANS = () => true;
const PLAN_COMPARE_ROWS = [
  // Included on every plan, Free too — so the Free column shows what it CAN do
  // instead of a row of dashes. (排班系統 below is the staff roster, a paid
  // feature; this is the appointment calendar itself.)
  { k: "calendar", zh: "預約排程（行事曆：日 / 週 / 月，即場 / 手動落單）", en: "Appointment scheduling (day / week / month calendar, walk-ins)", on: ALL_PLANS },
  { k: "crm", zh: "顧客 + 毛孩資料管理", en: "Customer & pet records", on: ALL_PLANS },
  { k: "priceList", zh: "價目表 + QR 海報", en: "Price list + QR poster", on: ALL_PLANS },
  { k: "directory", zh: "店舖專頁 + 上目錄", en: "Shop page + directory listing", on: ALL_PLANS },
  { k: "onlineBooking", zh: "網上預約", en: "Online booking", val: (p, en) => p?.onlineBookingsPerMonth ? (en ? p.onlineBookingsPerMonth + " / month" : "每月 " + p.onlineBookingsPerMonth + " 個") : null },
  { k: "scheduling", zh: "排班系統", en: "Staff scheduling" },
  { k: "autoNotify", zh: "電郵通知（確認 / 提醒）", en: "Email confirmations & reminders" },
  { k: "analytics", zh: "營業報表", en: "Business reports" },
  { k: "wallet", zh: "會員錢包", en: "Member wallet" },
  { k: "stampCard", zh: "電子積分卡", en: "Digital stamp card" },
  { k: "whatsapp", zh: "WhatsApp 通知（需自行設定帳號）", en: "WhatsApp alerts (your own account)" },
  { k: "payroll", zh: "員工薪酬", en: "Payroll" },
  { k: "loyalty", zh: "會員積分", en: "Loyalty points" },
  { k: "pos", zh: "收銀 POS（收款 / 收據 / 商品）", en: "POS (checkout, receipts, products)", on: (p) => planPosRank(p) >= 1, val: (p, en) => planPosMaxLabel(p, en) },
  { k: "posStandard", zh: "收銀：庫存 / 款式 / 退款換貨 / 交更 / 優惠 / 錢包", en: "POS: stock, variants, refunds, shifts, promos, wallet", on: (p) => planPosRank(p) >= 2 },
  { k: "posFull", zh: "收銀：採購 / AI 新增商品 / 批量匯入 / 毛利報表", en: "POS: purchasing, AI product setup, import, margin reports", on: (p) => planPosRank(p) >= 3 },
  { k: "aiConcierge", zh: "WhatsApp AI 客服（需自行接駁 WATI / Chatwoot）", en: "WhatsApp AI concierge (needs WATI / Chatwoot)" },
  { k: "removeBranding", zh: "移除品牌標示", en: "Remove PettoBook branding" },
];

// POS by plan (mirrors pos_tier in SQL, pos_phase11): features.pos is
// false | "lite" | "standard" | "full"; posMaxProducts 0 = unlimited.
const POS_RANK = { lite: 1, standard: 2, full: 3 };
const planPosRank = (p) => { const f = p?.features?.pos; return f === true ? 3 : POS_RANK[f] || 0; };
const planPosMax = (p) => {
  const n = p?.posMaxProducts;
  if (n != null && n !== "") return Number(n) > 0 ? Number(n) : null;
  return ({ 1: 100, 2: 500 })[planPosRank(p)] || null;
};
const planPosMaxLabel = (p, en) => !planPosRank(p) ? null
  : planPosMax(p) ? (en ? "Up to " + planPosMax(p) + " products" : planPosMax(p) + " 件商品") : (en ? "Unlimited products" : "商品無上限");
const planRowOn = (r, p) => r.on ? r.on(p) : !!p?.features?.[r.k];
const planRowLabel = (r) => zhEn(r.zh, r.en || r.zh);

// Faint paw-print doodle for the WhatsApp-style chat wallpaper (on-brand nod to
// WhatsApp's doodle background). Very low opacity so bubbles stay legible.
const WA_CHAT_DOODLE = "data:image/svg+xml," + encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><g fill='#000' fill-opacity='0.035'><ellipse cx='24' cy='30' rx='9' ry='7'/><circle cx='14' cy='18' r='3.6'/><circle cx='24' cy='13' r='3.6'/><circle cx='34' cy='18' r='3.6'/><circle cx='41' cy='27' r='3'/><ellipse cx='72' cy='74' rx='9' ry='7'/><circle cx='62' cy='62' r='3.6'/><circle cx='72' cy='57' r='3.6'/><circle cx='82' cy='62' r='3.6'/><circle cx='89' cy='71' r='3'/></g></svg>"
);

// Online booking is a PAID feature — free shops appear in the directory /
// have a public profile, but cannot take online bookings (contact only).
// Single source of truth used across the public booking page, the shop
// profile CTA, and the owner directory settings.
// Free shops get a monthly allowance (online_booking_quota, enforced in the
// database); once it is used up the booking page falls back to contact-only.
const shopHasOnlineBooking = (tenant) => !!PLANS[tenant?.plan || "free"]?.features?.onlineBooking;

// 電子積分卡 (stamp card) is a 專業版/旗艦版 feature. Used to gate the owner
// editor AND the customer-facing stamp tile so a downgraded shop's card hides.
const shopHasStampCard = (tenant) => !!PLANS[tenant?.plan || "free"]?.features?.stampCard;

// Stripe billing is only live once the platform admin has switched it on in
// 平台設定 (platformConfig.stripe.enabled) — that toggle is what says the keys
// and webhook are actually configured. Until then every paid upgrade CTA is
// hidden: pressing one only reached Stripe Checkout, failed, and left the shop
// staring at "升級失敗" with no way to know it was us, not them. Free stays
// fully usable, so a shop can still sign up and try the product.
// Stripe is web-only. Inside the native iOS/Android shells a card checkout
// (or a link out to one) breaks App Store 3.1.1 / Play payments policy, so
// every Stripe upgrade entry point reads this and stays hidden there.
const isNativeApp = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
// Paid plan picked at sign-up, still to be paid for (opens the 方案 popup on
// first login when it wasn't paid straight away).
const SIGNUP_PLAN_KEY = "petos:signup-plan";
// Owner-side copy that has no i18n key: Chinese for zh-HK, English for
// everyone else (ja owners fall back to English like the rest of the owner UI).
const zhEn = (zh, en) => (i18nGetLocale() || "zh-HK").startsWith("zh") ? zh : en;
// Dates on screen: Hong Kong style in Chinese, UK style otherwise.
const dateLoc = () => (i18nGetLocale() || "zh-HK").startsWith("zh") ? "zh-HK" : "en-GB";
// "2026-10" -> 2026年10月 / October 2026
const fmtYM = (ym) => { const [y, m] = String(ym).split("-"); return zhEn(y + "年" + Number(m) + "月", new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" })); };
const PLAN_NAME_EN = { free: "Free", basic: "Basic", pro: "Pro", enterprise: "Enterprise" };
// Plan name in the owner's language (admin can rename the Chinese names).
const planName = (key, plans) => zhEn(plans?.[key]?.name || PLAN_NAME_EN[key] || key, PLAN_NAME_EN[key] || plans?.[key]?.name || key);
const perMonth = () => zhEn("/月", "/mo");
const perYear = () => zhEn("/年", "/yr");
const stripeBillingLive = (platformConfig) => platformConfig?.stripe?.enabled === true && !isNativeApp();

// ── Android app download nudge ──────────────────────────────────────────
// Branded customer PWA (per-shop manifest + logo home-screen icon) — 旗艦版.
const shopHasBrandedApp = (tenant) => !!PLANS[tenant?.plan || "free"]?.features?.brandedApp;

// "White-label" check: Pro plan can choose to hide PettoBook footer/branding
// from public-facing surfaces (booking page, customer emails, share images).
// Owner opts in via tenant.hideBranding; free/basic always show branding.
const tenantHidesBranding = (tenant) => {
  const planKey = tenant?.plan || "free";
  const plan = PLANS[planKey];
  return !!(plan?.features?.removeBranding && tenant?.hideBranding);
};

// Read the active plan dictionary — superadmin overrides on
// `platformConfig.plans` win; otherwise fall back to the hardcoded
// PLANS above. Overrides merge per-key so superadmin can change just
// the price (or the feature toggles) without re-declaring everything.
const getPlans = (platformConfig) => {
  const overrides = platformConfig?.plans || {};
  const merged = {};
  for (const k of Object.keys(PLANS)) {
    merged[k] = { ...PLANS[k], ...(overrides[k] || {}), features: { ...PLANS[k].features, ...(overrides[k]?.features || {}) } };
  }
  return merged;
};

// Convert a plan's stored base price into the tenant's display
// currency using platformConfig.fxRates. `basePlanCurrency` (default
// HKD) anchors the rate table. Rates are { CODE: factor } where
// `display = base * factor`. Returns { amount, symbol } so callers
// don't have to look the symbol up themselves.
const getPlanPrice = (planKey, tenantCurrency, platformConfig) => {
  const plans = getPlans(platformConfig);
  const plan = plans[planKey] || plans.free;
  const basePrice = Number(plan?.price) || 0;
  const baseCurrency = platformConfig?.basePlanCurrency || "HKD";
  const targetCurrency = tenantCurrency || baseCurrency;
  const symbol = CURRENCY_SYMBOLS[targetCurrency] || "$";
  if (basePrice === 0 || targetCurrency === baseCurrency) {
    return { amount: basePrice, symbol, baseCurrency, targetCurrency };
  }
  const rate = Number(platformConfig?.fxRates?.[targetCurrency]) || 0;
  if (!rate) {
    // No rate configured — fall back to the base currency symbol so
    // the price is at least correct in its own currency.
    return { amount: basePrice, symbol: CURRENCY_SYMBOLS[baseCurrency] || "$", baseCurrency, targetCurrency: baseCurrency };
  }
  return { amount: Math.round(basePrice * rate), symbol, baseCurrency, targetCurrency };
};

// FIX: use actual today's date throughout, not a hardcoded demo date
// Local-date YYYY-MM-DD. NEVER use d.toISOString().slice(0,10) for a "today"
// string — toISOString returns UTC, so at midnight HKT (UTC+8) it produces
// the previous day's string for the first 8 hours. Same bug exists across
// the codebase; new code should use localDateStr to avoid re-introducing it.
const localDateStr = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return "";
  return x.getFullYear() + "-" + String(x.getMonth() + 1).padStart(2, "0") + "-" + String(x.getDate()).padStart(2, "0");
};
const dateNow = () => localDateStr(new Date());
const DEMO_TODAY = dateNow();

// Sum a part-timer's daily wage-log entries within [from, to] (inclusive).
// wageLog is [{date, hours, amount, note, at}]. `amount` is the source of
// truth for pay (it may be a manual day-wage / lump-sum override, not just
// hours × rate). Returns totals + the matching entries (newest first) so
// the payslip and the staff card stay in sync with what the owner entered.
const sumWageLog = (staff, from, to) => {
  const rows = (staff?.wageLog || []).filter(r => r && r.date && r.date >= from && r.date <= to);
  const hours = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const pay = rows.reduce((s, r) => s + Math.round(Number(r.amount) || 0), 0);
  const days = new Set(rows.map(r => r.date)).size;
  const entries = rows.slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return { hours, pay, days, entries };
};

// Find a payout whose recorded period CONTAINS [from, to] — i.e. the viewed
// range was already paid (a monthly payout covers any day within it). Returns
// the matching payout entry, or undefined. Used for the 已出薪 badge.
// Voided payouts and explicit adjustments never count as "paid" — same rule
// as the DB's overlap constraint (payroll.js is the single source).
const payoutCovering = (staff, from, to) => payoutCoveringRow(staff?.payoutLog, from, to);

// Find ALL existing payouts whose recorded period OVERLAPS [from, to]. Two
// ranges overlap iff a.from <= b.to && b.from <= a.to. This catches the
// 重覆出薪 (double-pay) cases payoutCovering misses: partial overlaps and
// reverse-containment (e.g. paid 05-01~05-15 then trying to pay 05-01~05-31).
const payoutsOverlapping = (staff, from, to) => payoutsOverlappingRows(staff?.payoutLog, from, to);
// Defaults for a staff member with no staff_pay_settings row yet.
const PAY_DEFAULTS = { ...PAY_ENGINE_DEFAULTS, settleMode: "daily", mpfEnabled: false };

// Was the staff member employed at any point during [from,to]? Used to gate a
// full-timer's monthly base salary so months before 入職 (or after 離職) don't
// show phantom pay in the chart / totals.
const isEmployed = (staff, from, to) =>
  (!staff?.joinDate || staff.joinDate <= to) && (!staff?.leaveDate || staff.leaveDate >= from);

// ── Staff scheduling constraints (used by booking + walk-in slot logic) ──
// On holiday/time-off for the given YYYY-MM-DD?
const staffOffOn = (staff, dateStr) =>
  (staff?.timeOff || []).some(p => p && p.from && p.to && p.from <= dateStr && dateStr <= p.to);
// Returns "sick" | "personal" | null — staff is on leave that day per a
// kind='event' appointment row with leave_type set. Used by booking
// flows to warn the owner before scheduling a customer into a slot the
// staffer has explicitly marked as away.
const staffLeaveOn = (apts, staffId, dateStr) => {
  if (!staffId || !dateStr) return null;
  const hit = (apts || []).find(a =>
    a && a.kind === "event" && a.leaveType && a.staffId === staffId && a.date === dateStr
  );
  return hit ? hit.leaveType : null;
};
// Break windows [{start,end}] configured for a given day-of-week.
const staffDayBreaks = (staff, dow) => {
  const sh = staff?.staffHours?.[dow] || staff?.staffHours?.[String(dow)];
  return Array.isArray(sh?.breaks) ? sh.breaks.filter(b => b && b.start && b.end) : [];
};
// Can this staff provide a service? Empty list = can do everything.
const staffCanDo = (staff, svcId) => {
  const ids = staff?.canServiceIds || [];
  return ids.length === 0 || (svcId && ids.includes(svcId));
};
// Does [startMin,endMin) overlap any break window?
const overlapsBreak = (startMin, endMin, breaks) => breaks.some(b => {
  const [sh, sm] = String(b.start).split(":").map(Number);
  const [eh, em] = String(b.end).split(":").map(Number);
  return startMin < (eh*60+em) && endMin > (sh*60+sm);
});

// One staff member's pay for [from,to] — a thin wrapper over the shared
// engine (src/lib/payroll.js) so the summary, 12-month chart, reminders and
// payslip can never disagree again. `extras` carries shop-wide inputs the
// engine needs: package rows (per-visit value + package sales) and the
// staff_deductions rows (bonuses / deductions). Returns the full engine result
// plus the legacy keys older call sites read (total = net pay).
const staffPeriodPay = (staff, apts, from, to, extras = {}) => {
  const packages = extras.packages || [];
  const p = computePay({
    staff,
    settings: staff,                       // merged pay settings live on the staff object
    apts,
    wages: staff?.wageLog || [],
    adjustments: (extras.adjustments || []).filter(d => d && d.staffId === staff?.id),
    packagesSold: packages.map(c => ({ ...c, purchasedDate: c.purchasedAt ? localDateStr(new Date(c.purchasedAt)) : null })),
    from, to,
    revenueOf: aptRevenue,
    packageVisitValueOf: (a) => aptPackageRealizedValue(a, packages),
  });
  return {
    ...p,
    orders: p.orders.count,
    rev: p.orders.revenue,
    comm: p.serviceCommission + p.redemptionCommission + p.packageSales.commission,
    hPay: p.hourly.amount,
    basePay: p.base.amount,
    total: p.net,
  };
};


const uid = () => Math.random().toString(36).slice(2, 9);
// For entities that get persisted as top-level rows (services, pets, addons,
// appointments, etc.), use a proper UUID — the DB columns are uuid-typed and
// reject base36 strings, which previously caused silent insert failures.
const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
      (Number(c) ^ (Math.random() * 16) >> Number(c) / 4).toString(16)));
// ── Payment-method scope ────────────────────────────────────────────────
// A tenant.depositMethods row can be offered in two very different places:
//   • CUSTOMER side — the booking / deposit page, where the customer pays
//     remotely. Needs a QR + instructions (FPS, PayMe, bank transfer…).
//   • Internal CHECKOUT — the owner's 收款 sheet at the counter, where the
//     shop takes the money in person (現金, 信用卡, 八達通…). A credit-card
//     terminal makes no sense as a customer online option, and a bank
//     transfer QR makes no sense at the counter — hence the split.
// Rows saved before this split have neither flag; treat those as BOTH so no
// existing shop silently loses a payment option.
const methodForCustomer = (m) => !!m && m.enabled !== false && m.forCustomer !== false;
const methodForCheckout = (m) => !!m && m.enabled !== false && m.forCheckout !== false;
const isCashMethod = (m) => m?.presetKey === "cash" || String(m?.name || "").includes("現金");
// Checkout list, in the owner's arranged order but with 現金 pulled to the
// front — it's the most-used counter method, so it should always be the
// default first tap regardless of where it sits in the settings list.
const checkoutMethodsOf = (tenant) => {
  const rows = (tenant?.depositMethods || []).filter(methodForCheckout);
  return [...rows.filter(isCashMethod), ...rows.filter(m => !isCashMethod(m))];
};
// Whole years since the birthday, or null when there is no usable birthday.
// It used to diff getFullYear() on `new Date(b)` — and new Date(null) is
// 1 Jan 1970, so every pet with NO birthday on file came out 56 years old:
// badged 高齡 on the customer's home screen and, worse, forced to sign the
// senior-pet waiver before an online booking (71% of one shop's pets have no
// birthday). null makes every `petAge(x) >= senior` check false, which is
// right: an unknown age is not evidence of a senior pet. Also counts whole
// years properly instead of by calendar year (Dec-born pets aged a year early).
const petAge = b => {
  if (!b) return null;
  const d = new Date(b);
  if (Number.isNaN(d.getTime())) return null;
  const n = new Date();
  let y = n.getFullYear() - d.getFullYear();
  if (n.getMonth() < d.getMonth() || (n.getMonth() === d.getMonth() && n.getDate() < d.getDate())) y -= 1;
  return Math.max(0, y);
};
// Shop-configurable senior-pet age threshold. Falls back to the platform default
// (SENIOR_AGE_YEARS in defaultTemplates.js, currently 10) when a tenant hasn't
// customised it. Owners change this in Settings → 條款 → 高齡年齡.
const seniorAgeFor = (t) => {
  const n = Number(t?.seniorAgeYears);
  return Number.isFinite(n) && n > 0 ? n : 10;
};

// HK Mandatory Provident Fund (MPF) — DISABLED pending compliance work.
// See audit (2026-06-14): the previous implementation had multiple
// statutory gaps (60-day rule, wrong birthday field, no HK-region gate,
// calendar-month chunking). Re-enable only after those land + the
// `profiles.date_of_birth` + `tenants.country` columns ship.
// DB columns (profiles.mpf_enabled) are kept so re-enabling is a code-
// only change with no migration rollback.
const computeMpf = () => null;

// 薪金 tab — HIDDEN in production (2026-09-28) until the payroll audit lands:
// staff could rewrite their own wage_log / payout_log / per-service rates
// via the API, the summary and payslip used two disagreeing pay engines,
// payouts weren't immutable, and MPF is off. Local dev (vite) keeps it on so
// the fix can be built. Flip to true only after the audit items ship.
const PAYROLL_ENABLED = !!import.meta.env.DEV;
// Pet age in whole months — used to block bookings for pets younger than 3 mo
// (vaccines incomplete, immune system fragile, industry-standard cut-off).
const petAgeMonths = b => {
  if (!b) return null;
  const d = new Date(b);
  if (Number.isNaN(d.getTime())) return null;
  const n = new Date();
  let m = (n.getFullYear() - d.getFullYear()) * 12 + (n.getMonth() - d.getMonth());
  if (n.getDate() < d.getDate()) m -= 1;
  return Math.max(0, m);
};
const MIN_PET_AGE_MONTHS = 3;
// Max plausible pet age (years). Validation guard to catch data-entry typos —
// e.g. a birth year of 1990 instead of 2020. Set generously: even record-
// breaking cats reach only ~38, but an age >30 typed into a form is almost
// always a wrong year, not a real pet. Tune in this one place.
const MAX_PET_AGE_YEARS = 30;
// Validate a pet birthday string (YYYY-MM-DD). Returns a translated error
// message, or null when OK / empty (birthday is optional). Called at every
// pet-save entry point; the date inputs also get min/max for UX.
const petBirthdayError = b => {
  if (!b) return null;
  if (b > dateNow()) return i18nT("val.petBirthdayFuture");
  const d = new Date(b);
  if (Number.isNaN(d.getTime())) return null;
  const years = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years > MAX_PET_AGE_YEARS) return i18nT("val.petBirthdayTooOld");
  return null;
};
// Earliest plausible birthday (YYYY-MM-DD) for a date input's `min` attribute.
const petBirthdayMin = () => { const d = new Date(); d.setFullYear(d.getFullYear() - MAX_PET_AGE_YEARS); return localDateStr(d); };
// Soft weight sanity check. Dogs seldom exceed 50kg, cats 10kg — a value above
// that is usually a typo (5.0 → 50). Returns a warning string (still allowed,
// since a few breeds genuinely exceed), or null. Used under pet-weight inputs.
const petWeightWarn = (weight, type) => {
  const w = parseFloat(weight);
  if (!w || w <= 0) return null;
  const cap = type === "cat" ? 10 : type === "small" ? 5 : 50;
  if (w > cap) return zhEn("⚠️ 體重 " + w + "kg 較為罕見，請確認冇打錯（例：5.0 誤打成 50）。", "⚠️ " + w + "kg is unusual — please check for typos (e.g. 5.0 entered as 50).");
  return null;
};
const timeNow = () => new Date().toTimeString().slice(0, 5);

// (Clock-in / clock-out feature removed — owners now enter hours manually
// on the payslip sheet.)

// ── PRICING ENGINE ─────────────────────────────────────────────────────────
// Shop-wide percentage adjustment (新年加價 +10%, 新開張 -20% etc.). Returns
// the first enabled campaign whose date range covers today, or null. Signed
// percent: positive = markup, negative = discount. Helper exposed so both
// the pricing engine and the customer-facing service cards can render a
// matching badge / strike-through.
// Today's date as YYYY-MM-DD in the tenant's timezone. Defaults to
// Asia/Hong_Kong since that's the primary market — but reading tenant.timezone
// lets shops in other regions (e.g. +0800 Singapore, +0900 Tokyo) have their
// campaigns activate based on local midnight rather than the customer's
// browser local time. Without this, a HK shop's "2026-05-23 starts" campaign
// activates 7 hours late for a customer browsing from London.
const tenantTodayISO = (tenant) => {
  const tz = tenant?.timezone || "Asia/Hong_Kong";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
    return parts; // en-CA already returns YYYY-MM-DD
  } catch {
    const d = new Date();
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  }
};

const activePriceAdjustment = (tenant) => {
  const list = tenant?.priceAdjustments || [];
  if (!Array.isArray(list) || list.length === 0) return null;
  const today = tenantTodayISO(tenant);
  return list.find(c => c && c.enabled !== false && c.percent
    && (!c.startDate || c.startDate <= today)
    && (!c.endDate || c.endDate >= today)) || null;
};

// Apply the active campaign's percent to a base amount. Rounds to the
// nearest dollar so the headline price stays clean.
const applyAdjustment = (amount, tenant) => {
  const adj = activePriceAdjustment(tenant);
  if (!adj) return amount;
  const factor = 1 + (Number(adj.percent) || 0) / 100;
  // floorCents: never charge customer more than the displayed campaign
  // factor would suggest. Discounts round in the customer's favor.
  return floorCents(amount * factor);
};

// pet.coatLength: "short" | "long"   (new field, defaults "short")
// Small-animal species catalog. Stored on pet.species when pet.type === "small".
// Used both by the new-pet form (drop-down picker) and the avatar emoji
// fallback. Keys are stable so the pricing matrix can match on them later.
const SMALL_ANIMAL_SPECIES = [
  { key: "rabbit",     label: zhEn("兔仔", "Rabbit"),   emoji: "🐇" },
  { key: "hamster",    label: zhEn("倉鼠", "Hamster"),   emoji: "🐹" },
  { key: "guinea_pig", label: zhEn("天竺鼠", "Guinea pig"), emoji: "🐹" },
];
const petEmoji = (pet) => {
  if (pet?.type === "cat") return "🐱";
  if (pet?.type === "small") {
    const m = SMALL_ANIMAL_SPECIES.find(s => s.key === pet?.species);
    return m?.emoji || "🐾";
  }
  return "🐶";
};

// Shared shop-level size tiers. Defined once per shop (tenant.pricingConfig.tiers);
// every service prices against these instead of re-defining its own size table.
const DEFAULT_PRICE_TIERS = [
  { id:"t-s",   label:zhEn("小型", "Small"), minW:0.1,  maxW:5 },
  { id:"t-m",   label:zhEn("中型", "Medium"), minW:5.1,  maxW:10 },
  { id:"t-l",   label:zhEn("大型", "Large"), minW:10.1, maxW:15 },
  { id:"t-xl",  label:zhEn("超大", "XL"), minW:15.1, maxW:20 },
  { id:"t-xxl", label:zhEn("巨型", "XXL"), minW:20.1, maxW:999 },
];
const getShopTiers = (tenant) => {
  const t = tenant?.pricingConfig?.tiers;
  return Array.isArray(t) && t.length ? t : DEFAULT_PRICE_TIERS;
};
// Wallet top-up bonus (儲值送): tenant.pricingConfig.walletBonus = [{spend,bonus}].
// Returns the bonus for a top-up amount — the bonus of the HIGHEST spend
// threshold the amount reaches (spend $5000 uses the $5000 tier, not $3000).
const getWalletBonusTiers = (tenant) => (Array.isArray(tenant?.pricingConfig?.walletBonus) ? tenant.pricingConfig.walletBonus : []);
const walletBonusFor = (amt, tenant) => {
  const a = Number(amt) || 0;
  const reached = getWalletBonusTiers(tenant)
    .filter(t => (Number(t?.spend) || 0) > 0 && a >= (Number(t?.spend) || 0))
    .sort((x, y) => (Number(y.spend) || 0) - (Number(x.spend) || 0));
  return reached.length ? Math.max(0, Number(reached[0].bonus) || 0) : 0;
};
// Normalize a service's per-tier matrix prices into a common shape
// [{id,label,minW,maxW,shortHair,longHair}] regardless of storage format:
// the shared `byTier` map (keyed by getShopTiers ids — cat / newer services) OR
// the legacy per-service `sizes` array (dog services). Used by the 套票 pricing
// form so auto-prefill / ⚡ read prices from EITHER format.
const svcTierPrices = (svc, tenant) => {
  const mp = svc?.matrixPricing;
  if (!mp) return [];
  const byTier = mp.byTier && typeof mp.byTier === "object" ? mp.byTier : null;
  if (byTier && Object.keys(byTier).length) {
    return getShopTiers(tenant).map(t => {
      const e = byTier[t.id] || {};
      const s = Number(e.short) || 0;
      return { id: t.id, label: t.label, minW: t.minW, maxW: t.maxW, shortHair: s, longHair: Number(e.long) || s };
    });
  }
  if (Array.isArray(mp.sizes) && mp.sizes.length) {
    return mp.sizes.map(r => {
      const s = Number(r.shortHair) || 0;
      return { id: r.id, label: r.label, minW: r.minW, maxW: r.maxW, shortHair: s, longHair: Number(r.longHair) || s };
    });
  }
  return [];
};
// Match a pet to one of the shop's special breeds (tenant.specialBreeds) — by
// catalog breed id or by name substring. Mirrors getPricingDetail's matchBreedS.
const matchSpecialBreed = (pet, tenant) => {
  if (!pet) return null;
  return (tenant?.specialBreeds || []).find(sb => {
    const idList = Array.isArray(sb.catalogBreedIds) ? sb.catalogBreedIds : (sb.catalogBreedId ? [sb.catalogBreedId] : []);
    if (idList.length && pet.breedId && idList.includes(pet.breedId)) return true;
    const nameList = Array.isArray(sb.names) ? sb.names : (sb.name ? [sb.name] : []);
    return !!pet.breed && nameList.some(n => n && pet.breed.includes(n));
  }) || null;
};
// The shop size tier a pet's weight falls into (nearest if none contains it).
const petSizeTier = (pet, tenant) => {
  const pw = parseFloat(pet?.weight) || 0;
  const tiers = [...getShopTiers(tenant)].sort((a, b) => (Number(a.minW) || 0) - (Number(b.minW) || 0));
  let t = tiers.find(x => pw >= (Number(x.minW) || 0) && pw <= (Number(x.maxW) || 999));
  if (!t && tiers.length) t = tiers.slice().sort((a, b) => Math.abs(pw - ((Number(a.minW) + Number(a.maxW)) / 2)) - Math.abs(pw - ((Number(b.minW) + Number(b.maxW)) / 2)))[0];
  return t || null;
};
// A service's SPECIAL-BREED per-tier prices for one breed, from
// matrixPricing.byBreed[breedId][tierId] → [{id,label,minW,maxW,shortHair,longHair}].
// Only byTier-format services carry byBreed pricing.
const svcBreedTierPrices = (svc, breedId, tenant) => {
  const byBreed = svc?.matrixPricing?.byBreed;
  if (!byBreed || !byBreed[breedId]) return [];
  return getShopTiers(tenant).map(t => {
    const e = (byBreed[breedId] || {})[t.id] || {};
    const s = Number(e.short) || 0;
    return { id: t.id, label: t.label, minW: t.minW, maxW: t.maxW, shortHair: s, longHair: Number(e.long) || s };
  });
};
// Special breeds that a given service actually prices (has a non-zero byBreed
// entry for). Returns the tenant.specialBreeds objects.
const svcPricedBreeds = (svc, tenant) => {
  const byBreed = svc?.matrixPricing?.byBreed;
  if (!byBreed) return [];
  return (tenant?.specialBreeds || []).filter(sb => {
    const entry = byBreed[sb.id];
    return entry && Object.values(entry).some(e => Number(e?.short) > 0 || Number(e?.long) > 0);
  });
};
// Pet type a special breed applies to ("dog" | "cat"), derived from its catalog
// breeds (or typed names matched against the catalog). Returns null when it
// can't be determined — callers then treat the breed as applying to everything.
const specialBreedType = (b) => {
  if (!b) return null;
  const ids = Array.isArray(b.catalogBreedIds) ? b.catalogBreedIds : (b.catalogBreedId ? [b.catalogBreedId] : []);
  for (const id of ids) { const c = getBreedById(id); if (c?.type) return c.type; }
  const names = Array.isArray(b.names) ? b.names : (b.name ? [b.name] : []);
  for (const nm of names) { const c = BREEDS.find(x => x.zh === nm || x.en === nm || (x.aliases||[]).includes(nm)); if (c?.type) return c.type; }
  return null;
};
// Pet types a service serves (defaults to dog).
const serviceTypes = (svc) => (Array.isArray(svc?.petTypes) && svc.petTypes.length) ? svc.petTypes : [svc?.petType || "dog"];
// A special breed applies to a service only when their pet types match — a
// 貓 breed shouldn't price a 狗 service and vice-versa. Unknown breed type
// applies to all (don't hide legitimate rows).
const breedAppliesToService = (b, svc) => { const bt = specialBreedType(b); if (!bt) return true; return serviceTypes(svc).includes(bt); };
// A service is bookable 上門 when the owner flips its 可上門 toggle
// (mobileAvailable). The 上門 price lives in the 上門 column (byTier[].mobile /
// mobilePrice); a blank 上門 cell falls back to the normal price.
const serviceMobileBookable = (svc) => !!svc?.mobileAvailable;

const getMatrixPrice = (pet, svc, tenant, opts = {}) => {
  if (!svc || !pet) { const b0 = Number(svc?.basePrice) || 0; return { base: b0, label:zhEn("主服務", "Main service"), extra:0, extraLabel:"", total: b0 }; }
  const mp = svc.matrixPricing;
  const pw = parseFloat(pet.weight) || 0;
  const coat = pet.coatLength || "short";
  const mobile = !!opts.mobile; // 上門 booking — prefer the 上門 price column
  const defaultBase = Number(svc.basePrice) || 0;
  // Huge-extra (超重加價) is a 大型犬 surcharge, not meant for cats or
  // small animals. Owners also don't want it stacked when a customer
  // redeems a package — the package price already covers the size tier
  // they signed up for, so adding extra at the till feels like a bait
  // and switch. Either condition collapses the surcharge to 0.
  const skipHugeByTypeOrPackage = pet.type === "cat" || pet.type === "small" || !!opts.usesPackage;

  // Small animals use dedicated services (each service is tagged with the
  // pet types it applies to). The matrix is dog/cat only, so when a small
  // animal somehow lands here, fall back to the service's flat basePrice.
  if (pet.type === "small") {
    const total = applyAdjustment(defaultBase, tenant);
    return { base: defaultBase, label: zhEn("主服務 (小動物)", "Main service (small animal)"), extra: 0, extraLabel: "", total, rawTotal: defaultBase, adjustment: activePriceAdjustment(tenant) };
  }

  if (svc.pricingMode === "static" || !mp) {
    const sBase = (mobile && Number(svc.mobilePrice) > 0) ? Number(svc.mobilePrice) : defaultBase;
    return { base:sBase, label: mobile ? zhEn("主服務 (上門)", "Main service (mobile)") : zhEn("主服務 (固定價格)", "Main service (fixed price)"), extra:0, extraLabel:"", total:sBase };
  }

  // ── Shared shop-tier model (preferred) ──────────────────────────────
  // When the shop has defined pricing_config.tiers AND this service stored
  // per-tier prices (byTier), price from the shared taxonomy. Otherwise fall
  // through to the legacy per-service `mp.sizes` path below (non-destructive).
  {
    const shopTiers = getShopTiers(tenant);
    const byTier = mp.byTier && typeof mp.byTier === "object" ? mp.byTier : null;
    const byBreed = mp.byBreed && typeof mp.byBreed === "object" ? mp.byBreed : null;
    // If this pet matches a LEGACY per-service special breed, defer to the
    // legacy path so existing breed pricing is never lost (special-breed
    // sharing is a later increment).
    const hasLegacyBreedMatch = Array.isArray(mp.specialBreeds) && mp.specialBreeds.length > 0 && mp.specialBreeds.some(sb => {
      const idList = Array.isArray(sb.catalogBreedIds) ? sb.catalogBreedIds : (sb.catalogBreedId ? [sb.catalogBreedId] : []);
      if (idList.length && pet.breedId && idList.includes(pet.breedId)) return true;
      const nameList = Array.isArray(sb.names) ? sb.names : (sb.name ? [sb.name] : []);
      return !!pet.breed && nameList.some(n => n && pet.breed.includes(n));
    });
    if (!hasLegacyBreedMatch && shopTiers && shopTiers.length && byTier && Object.keys(byTier).length) {
      const splitS = tenant?.pricingConfig?.coatSplit === true || mp.coatSplit === true;
      const coatTagS = splitS ? (coat === "long" ? zhEn("長毛", "Long coat") : zhEn("短毛", "Short coat")) : null;
      const withCoatS = (s) => coatTagS ? `${s} · ${coatTagS}` : s;
      // Pick a price from a {short,long,mobile} entry. 上門 prefers `mobile`,
      // falling back to the normal short/long price when 上門 isn't priced.
      const pickEntry = (e) => {
        if (!e) return 0;
        const normal = (splitS && coat === "long") ? (Number(e.long)||Number(e.short)||0) : (Number(e.short)||Number(e.long)||0);
        if (mobile) { const m = Number(e.mobile)||0; return m > 0 ? m : normal; }
        return normal;
      };
      const tenantBreeds = tenant?.specialBreeds || [];
      const matchBreedS = tenantBreeds.find(sb => {
        const idList = Array.isArray(sb.catalogBreedIds) ? sb.catalogBreedIds : (sb.catalogBreedId ? [sb.catalogBreedId] : []);
        if (idList.length && pet.breedId && idList.includes(pet.breedId)) return true;
        const nameList = Array.isArray(sb.names) ? sb.names : (sb.name ? [sb.name] : []);
        if (!pet.breed) return false;
        return nameList.some(n => n && pet.breed.includes(n));
      });
      // Resolve the size tier for this weight (shared by breed + normal pricing).
      const sortedTiers = [...shopTiers].sort((a,b)=>(Number(a.minW)||0)-(Number(b.minW)||0));
      let matchedTier = sortedTiers.find(t => pw >= (Number(t.minW)||0) && pw <= (Number(t.maxW)||999));
      if (!matchedTier && sortedTiers.length) matchedTier = sortedTiers.slice().sort((a,b)=>Math.abs(pw-((Number(a.minW)+Number(a.maxW))/2))-Math.abs(pw-((Number(b.minW)+Number(b.maxW))/2)))[0];
      let base = defaultBase, label = mobile ? zhEn("主服務 (上門)", "Main service (mobile)") : zhEn("主服務", "Main service"), durationMin = null, priced = false, breedExcludeHuge = false, unavailable = false, quote = false;
      // Special breed: priced PER size tier — byBreed[breedId][tierId]. Falls
      // back to the normal tier price when this breed/tier has no entry.
      if (matchBreedS) {
        breedExcludeHuge = !!matchBreedS.excludeHugeExtra; // applies whenever the breed matches
        const be = (byBreed && byBreed[matchBreedS.id] && matchedTier) ? byBreed[matchBreedS.id][matchedTier.id] : null;
        if (be) {
          if (be.na) unavailable = true; // this breed isn't offered at this size
          if (be.quote) quote = true; // 面議 — price varies, settle in person
          const p = pickEntry(be);
          if (p > 0) { base = p; priced = true; }
          durationMin = Number(be.durationMin) || null;
          const bn = matchBreedS.name || (matchBreedS.names||[])[0] || zhEn("特殊品種", "Special breeds");
          label = zhEn(`主服務 (${withCoatS(bn + (matchedTier ? " · " + matchedTier.label : ""))})${mobile ? " · 上門" : ""}`, `Main service (${withCoatS(bn + (matchedTier ? " · " + matchedTier.label : ""))})${mobile ? " · 上門" : ""}`);
        }
      }
      if (!priced && matchedTier && byTier[matchedTier.id]) {
        const e = byTier[matchedTier.id];
        if (e.na && !matchBreedS) unavailable = true; // size not offered (only when no breed override)
        if (e.quote && !matchBreedS) quote = true; // 面議 — price settled in person
        const p = pickEntry(e);
        if (p > 0) base = p;
        durationMin = durationMin || Number(e.durationMin) || null;
        if (!matchBreedS) label = zhEn(`主服務 (${withCoatS(matchedTier.label)})${mobile ? " · 上門" : ""}`, `Main service (${withCoatS(matchedTier.label)})${mobile ? " · 上門" : ""}`);
      }
      let extraS = 0, extraLabelS = "";
      const heS = tenant?.pricingConfig?.hugeExtra || mp.hugeExtra;
      const surchargeS = !!heS && svc.kind !== "spa" && svc.kind !== "addon" && (heS.enabled !== false) && Number(heS.stepPrice) > 0;
      if (surchargeS && !skipHugeByTypeOrPackage && !breedExcludeHuge) {
        const sorted = [...shopTiers].sort((a,b)=>(Number(a.minW)||0)-(Number(b.minW)||0));
        const baseW = sorted.length ? Number(sorted[sorted.length-1].maxW) : (Number(heS.baseW)||0);
        const capW = Number(heS.maxW) || Number(tenant?.maxPetWeightKg) || Infinity;
        if (pw > baseW && pw <= capW) {
          const perStep = Number(heS.perStep)||5, stepPrice = Number(heS.stepPrice)||0;
          const steps = Math.ceil((pw - baseW)/perStep);
          extraS = steps*stepPrice;
          extraLabelS = zhEn(`超重加價 (>${baseW}kg, 每${perStep}kg +$${stepPrice})`, `Oversize surcharge (>${baseW}kg, +$${stepPrice} per ${perStep}kg)`);
        }
      }
      const rawTotalS = base + extraS;
      return { base, label, extra: extraS, extraLabel: extraLabelS, total: applyAdjustment(rawTotalS, tenant), rawTotal: rawTotalS, adjustment: activePriceAdjustment(tenant), durationMin, unavailable, quote };
    }
  }

  // Check special breed first. Match priority:
  //   1. NEW catalog form — pet.breedId matches an entry's catalogBreedId
  //      (set by the matrix editor when picking from the curated list)
  //   2. NEW free-form — { name, extraNote, sizes:[...] } substring match
  //   3. LEGACY — { breedId, prices:{ short, long } } referencing
  //      tenant.specialBreeds by id
  // The catalog match is exact and language-agnostic; substring is only the
  // fallback for legacy free-text pets / hand-typed special-breed entries.
  const tenantBreeds = tenant?.specialBreeds || [];
  const matchBreed = (mp.specialBreeds || []).find(sb => {
    // New shape — multiple catalog ids + names share a single price row.
    const idList = Array.isArray(sb.catalogBreedIds) ? sb.catalogBreedIds : (sb.catalogBreedId ? [sb.catalogBreedId] : []);
    if (idList.length && pet.breedId && idList.includes(pet.breedId)) return true;
    const nameList = Array.isArray(sb.names) ? sb.names : [sb.name || tenantBreeds.find(t => t.id === sb.breedId)?.name].filter(Boolean);
    if (!pet.breed) return false;
    return nameList.some(n => n && pet.breed.includes(n));
  });
  let base = defaultBase, label = zhEn("主服務", "Main service");
  // When the shop hasn't enabled separate 長毛/短毛 pricing, we read the
  // shortHair column as the single price (longHair is ignored). Owner
  // opts in by toggling 分長短毛 in the matrix editor.
  //   coatSplit undefined + any row with longHair != shortHair → infer ON
  //     so legacy data keeps working without the owner re-saving.
  //   coatSplit === true / false → respect the explicit value.
  const split = mp.coatSplit === true
    || (mp.coatSplit === undefined && (mp.sizes || []).some(r => Number(r.longHair || 0) > 0 && Number(r.longHair) !== Number(r.shortHair || 0)));
  const pick = (row) => {
    if (split && coat === "long") return row.longHair || row.shortHair || defaultBase;
    return row.shortHair || defaultBase;
  };
  const coatTag = split ? (coat === "long" ? zhEn("長毛", "Long coat") : zhEn("短毛", "Short coat")) : null;
  const withCoat = (s) => coatTag ? `${s} · ${coatTag}` : s;
  if (matchBreed) {
    const breedName = matchBreed.name || tenantBreeds.find(t => t.id === matchBreed.breedId)?.name || "";
    if (Array.isArray(matchBreed.sizes) && matchBreed.sizes.length) {
      const row = matchBreed.sizes.find(s => pw >= (s.minW ?? 0) && pw <= (s.maxW ?? 999));
      if (row) {
        base = pick(row);
        label = zhEn(`主服務 (${withCoat(`${breedName} · ${row.label || `${row.minW}-${row.maxW}kg`}`)})`, `Main service (${withCoat(`${breedName} · ${row.label || `${row.minW}-${row.maxW}kg`}`)})`);
      } else {
        label = zhEn(`主服務 (${withCoat(`${breedName} 專屬`)})`, `Main service (${withCoat(`${breedName} 專屬`)})`);
      }
    } else {
      if (split) {
        base = coat === "long" ? (matchBreed.prices?.long || defaultBase) : (matchBreed.prices?.short || defaultBase);
      } else {
        base = matchBreed.prices?.short || matchBreed.prices?.long || defaultBase;
      }
      label = zhEn(`主服務 (${withCoat(`${breedName} 專屬`)})`, `Main service (${withCoat(`${breedName} 專屬`)})`);
    }
  } else {
    // Helper: pick the size row whose [minW,maxW] contains pw. If the
    // owner's matrix has a gap (e.g. rows 0-5 and 5.1-10, customer's pet
    // weighs 5.05) the literal `pw >= minW && pw <= maxW` returns nothing
    // and the price silently fell back to `defaultBase` (often $200).
    // Now we snap to the nearest band — better than charging a flat fee
    // the owner never intended.
    const rows = (mp.sizes || []);
    let sizeRow = rows.find(s => pw >= s.minW && pw <= s.maxW);
    if (!sizeRow && rows.length > 0) {
      sizeRow = rows
        .slice()
        .sort((a, b) => {
          const midA = (Number(a.minW) + Number(a.maxW)) / 2;
          const midB = (Number(b.minW) + Number(b.maxW)) / 2;
          return Math.abs(pw - midA) - Math.abs(pw - midB);
        })[0];
    }
    if (sizeRow) {
      base = pick(sizeRow);
      label = zhEn(`主服務 (${withCoat(sizeRow.label)})`, `Main service (${withCoat(sizeRow.label)})`);
    }
  }

  // Huge extra surcharge (>35kg, per 5kg). Skipped when the matched
  // special breed sets `excludeHugeExtra: true` — owner toggles this for
  // breeds whose per-row price already accounts for size (e.g. 巨型品種
  // priced 15-40kg flat) so we don't double-charge.
  let extra = 0, extraLabel = "";
  const he = mp.hugeExtra;
  // Owner-toggleable surcharge. Skip entirely for SPA + add-on services
  // (user said: not appropriate there — too-large dogs are excluded from
  // SPA via the matrix tier cap instead). For other services we honour
  // the explicit `enabled` flag; older rows without the flag are treated
  // as enabled when they have a positive stepPrice (legacy back-compat).
  const surchargeApplies = !!he
    && svc.kind !== "spa"
    && svc.kind !== "addon"
    && (he.enabled !== false)
    && Number(he.stepPrice) > 0;
  // Auto-derive baseW from the LAST size tier's maxW so the surcharge
  // starts immediately after the entire matrix range — any pet heavier
  // than the largest tier-priced size. Previously this used the
  // second-to-last tier, which double-counted the last tier's range.
  const _sortedTiers = surchargeApplies
    ? [...(mp.sizes || [])].sort((a, b) => (Number(a.minW) || 0) - (Number(b.minW) || 0))
    : null;
  const _autoBaseW = _sortedTiers && _sortedTiers.length >= 1
    ? Number(_sortedTiers[_sortedTiers.length - 1].maxW)
    : Number(he?.baseW) || 0;
  const _capW = Number(he?.maxW) || Number(tenant?.maxPetWeightKg) || Infinity;
  const skipHuge = !!(matchBreed && matchBreed.excludeHugeExtra) || skipHugeByTypeOrPackage;
  if (surchargeApplies && pw > _autoBaseW && pw <= _capW && !skipHuge) {
    const perStep = Number(he.perStep) || 5;
    const stepPrice = Number(he.stepPrice) || 0;
    const steps = Math.ceil((pw - _autoBaseW) / perStep);
    extra = steps * stepPrice;
    extraLabel = zhEn(`超重加價 (>${_autoBaseW}kg, 每${perStep}kg +$${stepPrice})`, `Oversize surcharge (>${_autoBaseW}kg, +$${stepPrice} per ${perStep}kg)`);
  }
  // Pick up per-size durationMin (set by owner in the matrix editor). Falls
  // back to the service-level estimatedMinutes if the row didn't set one.
  let durationMin = null;
  if (matchBreed && Array.isArray(matchBreed.sizes)) {
    const r = matchBreed.sizes.find(s => pw >= (s.minW ?? 0) && pw <= (s.maxW ?? 999));
    if (r && r.durationMin) durationMin = Number(r.durationMin) || null;
  }
  if (!durationMin) {
    const r = (mp.sizes || []).find(s => pw >= s.minW && pw <= s.maxW);
    if (r && r.durationMin) durationMin = Number(r.durationMin) || null;
  }
  // Apply shop-wide adjustment campaign LAST so it stacks on top of base +
  // breed/size + extra. Returns the original rawTotal too so the UI can show
  // a strike-through for marketing campaigns.
  const rawTotal = base + extra;
  const total = applyAdjustment(rawTotal, tenant);
  const adj = activePriceAdjustment(tenant);
  return { base, label, extra, extraLabel, total, rawTotal, adjustment: adj, durationMin };
};

// Single source of truth for "what did this appointment actually earn".
// Used by calendar chip, EOD report, SuperAdmin dashboard, top-shops list,
// per-tenant revenue card, owner-side totals — they all used different
// formulas before and the numbers disagreed. Formula:
//   base + addons + Σ surcharges − discount
const aptSubtotal = (a) => {
  if (!a) return 0;
  return (Number(a.basePrice) || 0)
    + (Number(a.addonPrice) || 0)
    + (Array.isArray(a.surcharges) ? a.surcharges.reduce((s, x) => s + (Number(x?.amount) || 0), 0) : 0);
};
const aptDiscount = (a) => {
  if (!a || !a.discount) return 0;
  const sub = aptSubtotal(a);
  return a.discount.type === "percent"
    ? Math.round(sub * (Number(a.discount.amount) || 0) / 100)
    : (Number(a.discount.amount) || 0);
};
// Cash-basis revenue. When a booking redeems a package the basePrice
// portion has already been recognized at the time of the package SALE
// (see 套票概況 panel) — counting it again here double-books the same
// dollar. So if customerPackageId is set, only the non-package cash
// (addons + surcharges − discount, floored at 0) flows into daily
// revenue / EOD / month totals.
//
// `aptCommissionBase` is the work-basis counterpart for staff
// commission: staff did the labour, deserves a % of full work value,
// including the package portion's per-visit equivalent.
const aptRevenue = (a) => {
  if (!a) return 0;
  if (a.kind === "event") return 0;
  // Voucher discount is canonical — written by customer_settle_booking
  // RPC against the full booking total. Owner till must subtract it
  // alongside the manual discount so the customer's promised "免費 SPA"
  // (or fixed-amount voucher) actually reduces what they pay.
  const voucher = Number(a.voucherDiscount) || 0;
  const full = aptSubtotal(a) - aptDiscount(a) - voucher;
  if (a.customerPackageId) {
    // basePrice covered by package — strip it out, keep the rest.
    const cashSubtotal = (Number(a.addonPrice) || 0)
      + (Array.isArray(a.surcharges) ? a.surcharges.reduce((s, x) => s + (Number(x?.amount) || 0), 0) : 0);
    // Discount + voucher sit at the booking level. Apply them proportionally
    // to the cash portion so a $50 discount on a $660 booking with $480
    // package + $180 addons takes $13.6 ($50 × 180/660) off the cash.
    const sub = aptSubtotal(a);
    const d = sub > 0 ? Math.round(aptDiscount(a) * cashSubtotal / sub) : 0;
    const v = sub > 0 ? Math.round(voucher * cashSubtotal / sub) : 0;
    return Math.max(0, cashSubtotal - d - v);
  }
  return Math.max(0, full);
};
// Per-visit cash-recognition value of a package redemption. Used as the
// base for `packageRedemptionCommissionRate`. amountPaid is the cash the
// customer paid for the whole package; spread it over totalVisits to
// get the dollars recognized at THIS visit. Returns 0 for non-package
// apts.
// Net cash actually collected against an apt, derived from payment_log
// (payment − refund). Used by EOD + receipt so the numbers reflect
// what was ACTUALLY received, not what we'd theoretically owe.
const aptCollectedFromLog = (a) => {
  if (!a || !Array.isArray(a.paymentLog) || a.paymentLog.length === 0) return null;
  // NET of refunds — a `refund` entry must SUBTRACT, not add. Without the sign
  // an undone 收款 counted its payment AND its refund as positive, overstating
  // "money received" by 2× the refunded amount (EOD/receipt showed received >
  // sales). Every other payment_log sum in the app already nets refunds.
  return a.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0);
};
// ⚡ 一按收款 — the tenant's configured quick-collect method, or null when the
// toggle is off / the method row was deleted (callers fall back to the sheet).
const quickCollectMethodOf = (tenant) => {
  const qc = tenant?.quickCollect;
  if (!qc?.enabled) return null;
  if (qc.methodId === "__cash__") return { id: "__cash__", name: "💵 現金", presetKey: "cash" };
  return checkoutMethodsOf(tenant).find(m => m.id === qc.methodId) || null;
};
// Outstanding balance for 收款 — MUST mirror CollectPaymentSheet /
// record_payment_atomic exactly: (subtotal − discount − voucher) − deposit −
// net-collected. The naive basePrice+addons−deposit formula overcharges
// discounted bookings (the RPC then rejects the over-total entry).
const aptRemainingBalance = (a) => {
  // aptRevenue() IS the cash total: for a 套票 booking it strips the
  // package-covered basePrice and prorates discount+voucher onto the cash
  // portion; with no package it equals subtotal − discount − voucher.
  //
  // This used to recompute the package-UNAWARE total inline, which meant
  // ⚡一按收款 billed a package customer for the visit their package had
  // already paid for — in one tap, without opening the sheet that does the
  // right maths. Mirrors public.apt_cash_total() in the DB.
  return Math.max(0, aptRevenue(a) - (Number(a?.deposit) || 0) - (aptCollectedFromLog(a) || 0));
};
const aptPackageRealizedValue = (a, customerPackages) => {
  if (!a?.customerPackageId) return 0;
  const cp = (customerPackages || []).find(c => c.id === a.customerPackageId);
  if (!cp) return 0;
  const tv = Number(cp.totalVisits) || 0;
  if (tv <= 0) return 0;
  return (Number(cp.amountPaid) || 0) / tv;
};
// How much money has actually been COLLECTED toward the deposit owed.
// Returns 0 for cancelled/refunded apts so EOD doesn't count them.
//   - depositPaidWallet + depositPaidPoints: settled via the
//     customer_settle_booking RPC (records the breakdown).
//   - depositConfirmedAt + a.deposit: customer paid externally (PayMe / FPS)
//     and the owner tapped "✓ 確認收訂金".
// Excludes apt.deposit when it's actually a cancellation late-fee
// (cancelled status with refundedAt set re-stamps deposit = lateFee).
const aptDepositReceived = (a) => {
  if (!a) return 0;
  if (a.status === "cancelled" || a.status === "noshow") return 0;
  const w = Number(a.depositPaidWallet) || 0;
  const p = Number(a.depositPaidPoints) || 0;
  const external = a.depositConfirmedAt ? Math.max(0, (Number(a.deposit) || 0) - w - p) : 0;
  return w + p + external;
};

const getPricingDetail = (pet, svc, tenant, opts = {}) => {
  if (!svc || !pet) {
    // A $0 base is valid (e.g. 快速護理 — the add-ons carry the price), so keep
    // 0 rather than coercing it to a phantom 200 default.
    const base = Number(svc?.basePrice) || 0;
    return { base, baseLabel:zhEn("主服務", "Main service"), extra:0, ruleLabel:"", total: applyAdjustment(base, tenant), rawTotal: base, adjustment: activePriceAdjustment(tenant) };
  }
  // Per-dog SPECIAL / legacy price override (set in the pet's CRM record):
  // `pet.specialPrices[svc.id]` wins over matrix / smart / flat pricing so a
  // grandfathered customer keeps their old price. Shop-wide promo adjustments
  // are NOT stacked on top (it's already the agreed price). Duration still
  // comes from the normal calc so slot length stays correct.
  const _sp = pet?.specialPrices?.[svc?.id];
  if (_sp != null && _sp !== "" && Number.isFinite(Number(_sp))) {
    const sp = Number(_sp);
    let durationMin;
    try { durationMin = (svc.pricingMode === "matrix" || svc.matrixPricing) ? getMatrixPrice(pet, svc, tenant, opts).durationMin : (svc.estimatedMinutes || undefined); } catch { durationMin = svc.estimatedMinutes || undefined; }
    return { base: sp, baseLabel: zhEn("主服務（特別價）", "Main service (special price)"), extra: 0, ruleLabel: zhEn("舊客特別價", "Returning-customer special price"), total: sp, rawTotal: sp, adjustment: null, durationMin, special: true };
  }
  if (svc.pricingMode === "matrix" || svc.matrixPricing) {
    const r = getMatrixPrice(pet, svc, tenant, opts);
    return { base:r.base, baseLabel:r.label, extra:r.extra, ruleLabel:r.extraLabel, total:r.total, rawTotal:r.rawTotal, adjustment:r.adjustment, durationMin:r.durationMin, unavailable:r.unavailable, quote:r.quote };
  }
  // 上門 on a non-matrix (static) service: use the flat 上門 price if set,
  // else fall back to the normal base.
  if (opts.mobile && Number(svc.mobilePrice) > 0) {
    const b = Number(svc.mobilePrice);
    return { base:b, baseLabel:zhEn("主服務 (上門)", "Main service (mobile)"), extra:0, ruleLabel:"", total: applyAdjustment(b, tenant), rawTotal:b, adjustment: activePriceAdjustment(tenant) };
  }
  // Legacy smart pricing fallback. Same rule: cats / small animals /
  // package redemptions skip the 超重加價 exceed_step step.
  const skipExtra = pet.type === "cat" || pet.type === "small" || !!opts.usesPackage;
  const defaultBase = Number(svc.basePrice) || 0;
  let base = defaultBase, baseLabel = zhEn("主服務", "Main service"), extra = 0, ruleLabel = "";
  if (svc.smartPricing?.enabled) {
    const rules = svc.smartPricing.rules || [];
    const pw = parseFloat(pet.weight) || 0;
    const breedRules = rules.filter(r => r.type === "breed" && r.breed && pet.breed.includes(r.breed));
    let matchedRule = breedRules.find(r => pw >= parseFloat(r.min||0) && pw <= parseFloat(r.max||999));
    if (!matchedRule) { const wr = rules.filter(r => r.type === "weight_range"); matchedRule = wr.find(r => pw >= parseFloat(r.min||0) && pw <= parseFloat(r.max||999)); }
    if (matchedRule) { base = parseFloat(matchedRule.price)||defaultBase; baseLabel = matchedRule.type==="breed" ? zhEn(`主服務 (${matchedRule.breed} 專屬)`, `Main service (${matchedRule.breed} only)`) : zhEn(`主服務 (${matchedRule.min||0}-${matchedRule.max||"∞"}kg)`, `Main service (${matchedRule.min||0}-${matchedRule.max||"∞"}kg)`); }
    if (!skipExtra) {
      for (const r of rules.filter(r => r.type==="exceed_step")) {
        const bw = parseFloat(r.baseWeight||20), sw = parseFloat(r.stepWeight||5), sp = parseFloat(r.stepPrice||100);
        if (pw > bw && sw > 0) { const steps = Math.ceil((pw-bw)/sw); extra += steps*sp; ruleLabel = zhEn(`超重加價 (>${bw}kg, 每${sw}kg +$${sp})`, `Oversize surcharge (>${bw}kg, +$${sp} per ${sw}kg)`); }
      }
    }
  }
  const rawTotal = base + extra;
  return { base, baseLabel, extra, ruleLabel, total: applyAdjustment(rawTotal, tenant), rawTotal, adjustment: activePriceAdjustment(tenant) };
};

// Addon pricing — flat or weight-tier variants.
// Returns { price, variantLabel } where variantLabel describes the chosen tier
// (empty string for flat addons).
const computeAddonPrice = (addon, pet) => {
  if (!addon) return { price: 0, variantLabel: "" };
  if (addon.pricingMode === "variants" && Array.isArray(addon.variants) && addon.variants.length) {
    const pw = parseFloat(pet?.weight) || 0;
    const row = addon.variants.find(v => pw >= (Number(v.minW) || 0) && pw <= (Number(v.maxW) || 999));
    if (row) return { price: Number(row.price) || 0, variantLabel: row.label || (row.minW + "-" + row.maxW + "kg") };
    // No matching variant — fall through to flat price as safe default
  }
  return { price: Number(addon.price) || 0, variantLabel: "" };
};

// Add-on price ACCOUNTING for inclusion in the chosen main service: an add-on
// listed in svc.includedAddonIds is free (基本護理 baked into the service), so
// it returns price 0 with a 「已包」 label. Otherwise normal computeAddonPrice.
const computeAddonNet = (addon, pet, svc) => {
  if (addon && svc && Array.isArray(svc.includedAddonIds) && svc.includedAddonIds.includes(addon.id)) {
    return { price: 0, variantLabel: zhEn("已包", "Included"), included: true };
  }
  return computeAddonPrice(addon, pet);
};

// ── DEPOSIT CALCULATOR ─────────────────────────────────────────────────────
// ── CURRENCY + LANGUAGE ───────────────────────────────────────────────────
const CURRENCY_SYMBOLS = {
  HKD: "HK$", USD: "US$", TWD: "NT$", CNY: "¥", JPY: "¥",
  SGD: "S$", MYR: "RM", THB: "฿", PHP: "₱", IDR: "Rp", VND: "₫",
  KRW: "₩", AUD: "A$", NZD: "NZ$", GBP: "£", EUR: "€", CAD: "C$",
};
const SUPPORTED_CURRENCIES = [
  { code: "HKD", name: zhEn("港幣 HK$", "Hong Kong dollar HK$") }, { code: "USD", name: zhEn("美元 US$", "US dollar US$") },
  { code: "TWD", name: zhEn("新台幣 NT$", "New Taiwan dollar NT$") }, { code: "CNY", name: zhEn("人民幣 ¥", "Chinese yuan ¥") },
  { code: "JPY", name: zhEn("日圓 ¥", "Japanese yen ¥") }, { code: "SGD", name: zhEn("新加坡元 S$", "Singapore dollar S$") },
  { code: "MYR", name: zhEn("馬幣 RM", "Malaysian ringgit RM") }, { code: "THB", name: zhEn("泰銖 ฿", "Thai baht ฿") },
  { code: "PHP", name: zhEn("菲律賓披索 ₱", "Philippine peso ₱") }, { code: "IDR", name: zhEn("印尼盾 Rp", "Indonesian rupiah Rp") },
  { code: "VND", name: zhEn("越南盾 ₫", "Vietnamese dong ₫") }, { code: "KRW", name: zhEn("韓元 ₩", "Korean won ₩") },
  { code: "AUD", name: zhEn("澳元 A$", "Australian dollar A$") }, { code: "NZD", name: zhEn("紐元 NZ$", "New Zealand dollar NZ$") },
  { code: "GBP", name: zhEn("英鎊 £", "Pound sterling £") }, { code: "EUR", name: zhEn("歐元 €", "Euro €") },
  { code: "CAD", name: zhEn("加元 C$", "Canadian dollar C$") },
];
const cur = (tenant) => CURRENCY_SYMBOLS[tenant?.currency || "HKD"] || "$";

// Compute the deposit owed for a booking. Deposit is now EXPLICIT-OPT-IN:
// the policy must have `enabled === true` to charge anything. `isReturningCustomer`
// is honored when the shop has the "only require deposit from new customers"
// policy set; callers should pass true when the customer has any prior
// appointment.
const calcDeposit = (total, tenant, isReturningCustomer = false) => {
  const dp = tenant?.depositPolicy;
  if (!dp || dp.enabled !== true) return 0;
  if (dp.newCustomersOnly && isReturningCustomer) return 0;
  let deposit = dp.mode === "fixed" ? (dp.fixedAmount || 0) : floorCents(total * (dp.percent || 30) / 100);
  if (dp.minDeposit && deposit < dp.minDeposit) deposit = Math.min(dp.minDeposit, total);
  return Math.min(deposit, total);
};

// ── DOMAIN HELPER ──────────────────────────────────────────────────────────
const getBookingUrl = (slug, state) => {
  const pc = state?.platformConfig || {};
  let prefix = pc.bookingUrlPrefix || (pc.domain || "pettobook.com") + "/shop/";
  // Legacy migration: old configs stored "…/#/book/" or "…/book/". Normalize
  // them to the new clean "/shop/" route so the displayed URL matches what
  // the SPA actually serves.
  prefix = prefix.replace(/\/#\/book\/?$/, "/shop/").replace(/\/book\/?$/, "/shop/");
  if (!prefix.endsWith("/")) prefix += "/";
  return prefix + slug;
};

// Quick bilingual helper for inline strings: T("中文","English"). Reads the
// current locale at render time (components subscribed via i18nUseLocale
// re-render on switch). Used for newer UI not yet keyed into i18n.js.
// Inline bilingual/trilingual helper: T("中文","English"[,"日本語"]).
// Japanese falls back to English when a `ja` string isn't supplied, so the
// hundreds of existing 2-arg calls still read coherently for ja users.
const T = (zh, en, ja) => { const l = i18nGetLocale(); return l === "ja" ? (ja != null ? ja : en) : l === "en" ? en : zh; };

const SL = { pending:zhEn("待開始", "Not started"), checkedin:zhEn("已到店", "Arrived"), grooming:zhEn("美容中", "Grooming"), done:zhEn("已完成", "Done"), cancelled:zhEn("已取消", "Cancelled"), noshow:zhEn("未到店", "No-show") };
const NI = { booking_confirmed:"✅", schedule_reminder:"📅", new_booking:"🆕", grooming_done:"🎉", whatsapp:"💬", cancel:"❌", noshow:"⚠️", system:"ℹ️", announcement:"📢", checkin:"📍", payment:"💰", deposit_pending:"💸", apt_reminder:"⏰", rfq_quote:"💰", rfq_accepted:"✅" };
// Weekday short labels. Helper so consumers get fresh strings on locale
// change without each call site importing i18n. Keep the array shape
// (length 7, indexed Sun..Sat) so existing call sites work unchanged.
const dayLabels = () => [0,1,2,3,4,5,6].map(i => i18nT("day." + i));
const DAYS = new Proxy([0,1,2,3,4,5,6], {
  get(_t, prop) {
    const i = Number(prop);
    if (Number.isInteger(i) && i >= 0 && i < 7) return i18nT("day." + i);
    if (prop === "length") return 7;
    if (prop === "map") return (fn) => dayLabels().map(fn);
    if (prop === Symbol.iterator) return function* () { for (const v of dayLabels()) yield v; };
    return undefined;
  },
});
const POINTS_RATE = 1;

// Country dialling codes shown in the phone-number picker. Hong Kong default;
// owners can pick the right one per customer. Stored separately from the
// digits so we don't have to parse formatted strings on display.
const COUNTRY_CODES = [
  { code: "+852", label: "🇭🇰 HK +852" },
  { code: "+886", label: "🇹🇼 TW +886" },
  { code: "+86",  label: "🇨🇳 CN +86" },
  { code: "+65",  label: "🇸🇬 SG +65" },
  { code: "+60",  label: "🇲🇾 MY +60" },
  { code: "+1",   label: "🇺🇸 US +1" },
  { code: "+44",  label: "🇬🇧 UK +44" },
  { code: "+61",  label: "🇦🇺 AU +61" },
];

// ── TIME SLOT ENGINE ──────────────────────────────────────────────────────
// True when an appointment should be treated as "no longer occupying its
// slot" — explicit cancellation, no-show, OR an unconfirmed
// pending_payment booking whose 30-min receipt-upload window has passed.
// The latter releases held timeslots without needing a server-side cron.
const isAptDead = (a) => {
  if (!a) return true;
  if (a.status === "cancelled" || a.status === "noshow") return true;
  if (a.status === "pending_payment" && a.expiresAt && new Date(a.expiresAt).getTime() < Date.now()) return true;
  return false;
};

const generateTimeSlots = (date, staffId, svcId, tenant, appointments, staffProfile, anyStaffPool = null, extraBusy = []) => {
  if (!tenant || !date) return [];
  const dayOfWeek = new Date(date).getDay();
  const bh = tenant.businessHours?.[dayOfWeek];
  if (!bh || !bh.enabled) return [];
  const svc = (tenant.services || []).find(s => s.id === svcId);
  const duration = svc?.estimatedMinutes || 60;
  const buffer = svc?.bufferMinutes || 0;
  const totalBlock = duration + buffer;
  const [openH, openM] = (bh.open || "09:00").split(":").map(Number);
  const [closeH, closeM] = (bh.close || "18:00").split(":").map(Number);
  let openMin = openH * 60 + openM;
  let closeMin = closeH * 60 + closeM;
  // Per-staff hours override — narrows the shop window for part-timers.
  // If the day is enabled in `staffHours[dow]`, intersect (later open /
  // earlier close). If the staff has hours configured AND this day is NOT
  // enabled, they're off today → bail.
  // Staff on holiday that day → no slots.
  if (staffId && staffProfile && staffOffOn(staffProfile, date)) return [];
  // Outside employment window (before joinDate / after leaveDate) → no slots.
  if (staffId && staffProfile) {
    if (staffProfile.joinDate && date < staffProfile.joinDate) return [];
    if (staffProfile.leaveDate && date > staffProfile.leaveDate) return [];
  }
  const dayBreaks = staffId && staffProfile ? staffDayBreaks(staffProfile, dayOfWeek) : [];
  const sh = staffProfile?.staffHours?.[dayOfWeek] || staffProfile?.staffHours?.[String(dayOfWeek)];
  const hasStaffSchedule = staffProfile && staffProfile.staffHours && Object.values(staffProfile.staffHours).some(d => d && d.enabled);
  // Part-time staff without ANY weekly schedule → treat as unavailable.
  // Otherwise customers see the full shop window for someone who never
  // works. Full-time staff legitimately rely on shop hours when no
  // override is set, so they're exempt.
  if (staffId && staffProfile?.jobType === "parttime" && !hasStaffSchedule) return [];
  if (staffId && hasStaffSchedule) {
    if (!sh || !sh.enabled) return [];
    if (sh.open) {
      const [h, m] = sh.open.split(":").map(Number);
      openMin = Math.max(openMin, h * 60 + m);
    }
    if (sh.close) {
      const [h, m] = sh.close.split(":").map(Number);
      closeMin = Math.min(closeMin, h * 60 + m);
    }
    if (closeMin <= openMin) return [];
  }

  // Past-time + advance-notice filter. A slot is bookable only once it is at
  // least `earliestMs` from now, where the required lead is the LARGER of a
  // small travel/prep buffer (bookingLeadMinutes, default 15) and the shop's
  // minimum advance-booking requirement (cancellationPolicy.minAdvanceHours —
  // 0 = allow same-day / walk-in, 24 = must book a day ahead, etc.). Comparing
  // ABSOLUTE time handles multi-day advance uniformly, not just "today".
  const leadMinutes = Number(tenant.bookingLeadMinutes) || 15;
  const minAdvanceHours = Math.max(0, Number(tenant.cancellationPolicy?.minAdvanceHours) || 0);
  const earliestMs = Date.now() + Math.max(leadMinutes, minAdvanceHours * 60) * 60 * 1000;
  const dayStartMs = new Date(date + "T00:00:00").getTime();

  // Merge real appointments visible to this session with anonymised busy
  // windows fetched via public_busy_slots. Customer sessions can only see
  // their OWN appointments via RLS, so without extraBusy every other
  // customer's booking is invisible and the slot picker marks everything
  // free. extraBusy items have the shape { staffId, date, time, durationMin }.
  const _extraBusy = Array.isArray(extraBusy) ? extraBusy.filter(b => b && b.date === date) : [];
  const _extraAsApts = _extraBusy.map(b => ({
    id: "busy_" + (b.staffId || "x") + "_" + b.time,
    tenantId: tenant.id,
    staffId: b.staffId,
    date: b.date,
    time: b.time,
    // getAptDuration reads `customDuration` (not the DB column name) and
    // falls back to service.estimatedMinutes when null. We carry the
    // already-derived duration from the RPC so getAptDuration returns
    // the right value even when the calling session has no view of the
    // underlying service row.
    customDuration: Number(b.durationMin) || 60,
  }));
  const _allApts = appointments.concat(_extraAsApts);
  // Dedup the merged list — if a customer's OWN apt is in both `appointments`
  // and `extraBusy`, keep only one to avoid double-conflict math.
  const _seen = new Set();
  const mergedApts = _allApts.filter(a => {
    const k = (a.staffId || "") + "|" + (a.date || "") + "|" + (a.time || "");
    if (_seen.has(k)) return false;
    _seen.add(k);
    return true;
  });
  const overlapsApt = (a, startMin, endMin) => {
    if (!a.time) return false;
    const [ah, am] = String(a.time).split(":").map(Number);
    if (!Number.isFinite(ah) || !Number.isFinite(am)) return false;
    const aptStart = ah * 60 + am;
    return startMin < aptStart + getAptDuration(a, tenant) && endMin > aptStart;
  };
  const remoteKeys = new Set(_extraBusy.map(a => (a.staffId || "") + "|" + a.date + "|" + a.time));
  const localOnlyCount = appointments.filter(a =>
    a.tenantId === tenant.id && a.date === date && !isAptDead(a)
      && !remoteKeys.has((a.staffId || "") + "|" + a.date + "|" + a.time)
  ).length;
  const dailyLimit = Number(tenant.dailyLimit) || 8;
  const dayAtLimit = _extraBusy.length + localOnlyCount >= dailyLimit;
  const staffApts = staffId
    ? mergedApts.filter(a => (a.staffId === staffId || !a.staffId) && a.date === date && !isAptDead(a))
    : [];

  // Slot grid: always 30-min step. Earlier we tried stepping by the
  // service's duration so the customer never saw a "blocked because it
  // would overlap" slot, but it dropped every off-grid start time —
  // e.g. with a 75-min service starting at 10:00 the next candidate
  // was 11:15, so 11:00 wasn't even offered. The user explicitly asked
  // to fall back to the half-hour grid. Slots that would overlap an
  // existing apt are shown as crossed-out (still rendered, not picked).
  const allSlots = [];
  for (let m = openMin; m + duration <= closeMin; m += 30) {
    allSlots.push({
      time: String(Math.floor(m/60)).padStart(2,"0") + ":" + String(m%60).padStart(2,"0"),
      startMin: m,
      endMin: m + totalBlock,
      past: (dayStartMs + m * 60000) < earliestMs,
    });
  }
  // "Any staff" availability: a slot is bookable if at least one eligible
  // staffer (in anyStaffPool) is actually free — respecting each one's
  // time-off, weekly hours, breaks, and existing appointments. Without a
  // pool we fall back to "always available" (back-compat / solo-owner shop).
  const shopOpenMin = openMin, shopCloseMin = closeMin;
  const poolFree = (st, startMin, endMin) => {
    if (staffOffOn(st, date)) return false;
    if (st.joinDate && date < st.joinDate) return false;
    if (st.leaveDate && date > st.leaveDate) return false;
    const stHasSched = st.staffHours && Object.values(st.staffHours).some(d => d && d.enabled);
    if (st.jobType === "parttime" && !stHasSched) return false;
    const shp = st.staffHours?.[dayOfWeek] || st.staffHours?.[String(dayOfWeek)];
    const hasSched = st.staffHours && Object.values(st.staffHours).some(d => d && d.enabled);
    let oMin = shopOpenMin, cMin = shopCloseMin;
    if (hasSched) {
      if (!shp || !shp.enabled) return false;
      if (shp.open) { const [h, m] = shp.open.split(":").map(Number); oMin = Math.max(oMin, h*60+m); }
      if (shp.close) { const [h, m] = shp.close.split(":").map(Number); cMin = Math.min(cMin, h*60+m); }
    }
    if (startMin < oMin || startMin + duration > cMin) return false;
    if (overlapsBreak(startMin, endMin, staffDayBreaks(st, dayOfWeek))) return false;
    return !mergedApts.some(a => {
      if ((a.staffId && a.staffId !== st.id) || a.date !== date || isAptDead(a)) return false;
      return overlapsApt(a, startMin, endMin);
    });
  };
  return allSlots.map(slot => {
    if (dayAtLimit) return { ...slot, available: false };
    if (slot.past) return { ...slot, available: false };
    if (!staffId) {
      if (Array.isArray(anyStaffPool) && anyStaffPool.length)
        return { ...slot, available: anyStaffPool.some(st => poolFree(st, slot.startMin, slot.endMin)) };
      return { ...slot, available: !mergedApts.some(a => a.date === date && !isAptDead(a) && overlapsApt(a, slot.startMin, slot.endMin)) };
    }
    if (overlapsBreak(slot.startMin, slot.endMin, dayBreaks)) return { ...slot, available: false };
    const conflict = staffApts.some(a => overlapsApt(a, slot.startMin, slot.endMin));
    return { ...slot, available: !conflict };
  });
};
const isBusinessDay = (date, tenant) => {
  if (!tenant?.businessHours) return true;
  return tenant.businessHours[new Date(date).getDay()]?.enabled !== false;
};

// ── Date picker: horizontal day strip (default) + popout month grid ────────
// Compact, mobile-friendly. Click 📅 to expand to a full month view.
function DateStripPicker({ value, onChange, tenant, appointments, daysAhead = 14 }) {
  const [expanded, setExpanded] = useState(false);
  const [month, setMonth] = useState(() => (value || dateNow()).slice(0,7));
  const today = dateNow();

  const isFull = (ds) => {
    const cnt = (appointments || []).filter(a => a.tenantId === tenant?.id && a.date === ds && a.status !== "cancelled" && a.status !== "noshow").length;
    return cnt >= (tenant?.dailyLimit || 8);
  };
  const isAvail = (ds) => ds >= today && isBusinessDay(ds, tenant) && !(tenant?.holidays||[]).includes(ds) && !isFull(ds);

  // Build the rolling strip (next N days)
  const stripDays = [];
  for (let i = 0; i < daysAhead; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    const ds = localDateStr(d);
    stripDays.push({ ds, day: d.getDate(), dow: d.getDay(), month: d.getMonth()+1 });
  }

  // Month grid (when expanded)
  const [mY, mM] = month.split("-").map(Number);
  const firstDow = new Date(mY, mM-1, 1).getDay();
  const daysInMonth = new Date(mY, mM, 0).getDate();

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:800}}>{zhEn("選擇日期","Choose date")}</p>
        <button onClick={() => setExpanded(e => !e)} style={{background:"var(--c-lighter)",border:"none",borderRadius:99,padding:"6px 12px",fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
          <CalendarDays size={14} strokeWidth={2.8} />{expanded ? zhEn("收起","Collapse") : zhEn("月曆","Calendar")}
        </button>
      </div>

      {!expanded && (
        <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:6,marginBottom:4,scrollbarWidth:"none"}}>
          {stripDays.map(({ds, day, dow, month: mo}) => {
            const avail = isAvail(ds);
            const sel = ds === value;
            const isToday = ds === today;
            const ph = getHolidayForDate(tenant?.country || "HK", ds);
            return (
              <button key={ds} disabled={!avail} onClick={() => onChange(ds)} title={ph ? ph.name : ""} style={{flex:"0 0 auto",minWidth:60,padding:"10px 4px",borderRadius:16,border:sel?"2px solid var(--c-primary)":isToday?"2px solid var(--c-grad1)":ph?"2px solid #C4B5FD":"2px solid var(--c-light)",background:sel?"var(--c-primary)":avail?(ph?"#F5F3FF":"#FFFDFD"):"var(--c-light)",cursor:avail?"pointer":"not-allowed",display:"flex",flexDirection:"column",alignItems:"center",gap:2,boxShadow:sel?"0 4px 12px var(--c-primary-30)":"none",transition:"all 0.15s"}}>
                <span style={{fontSize:10,fontWeight:700,color:sel?"rgba(255,255,255,0.85)":(dow===0||dow===6?"#EF4444":"#AFAFC4")}}>{DAYS[dow]}</span>
                <span style={{fontSize:18,fontWeight:900,color:sel?"#fff":avail?"#4A4A68":"#AFAFC4"}}>{day}</span>
                <span style={{fontSize:9,fontWeight:800,color:sel?"rgba(255,255,255,0.85)":(ph?"#6B21A8":"#AFAFC4"),maxWidth:54,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ph ? (ph.name.length > 4 ? ph.name.slice(0,3)+"…" : ph.name) : mo+zhEn("月", "Month")}</span>
              </button>
            );
          })}
        </div>
      )}

      {expanded && (
        <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <button onClick={()=>{const d=new Date(mY,mM-2,1);setMonth(d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"));}} style={{background:"#fff",border:"none",borderRadius:99,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={16} color="var(--c-primary)" strokeWidth={3}/></button>
            <p style={{fontWeight:900,fontSize:15,margin:0,color:"#4A4A68"}}>{month.replace("-","年")+zhEn("月", "Month")}</p>
            <button onClick={()=>{const d=new Date(mY,mM,1);setMonth(d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"));}} style={{background:"#fff",border:"none",borderRadius:99,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={16} color="var(--c-primary)" strokeWidth={3}/></button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:6}}>
            {DAYS.map((d,i) => <div key={d} style={{textAlign:"center",fontSize:10,color:i===0||i===6?"#EF4444":"#AFAFC4",fontWeight:800}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
            {Array(firstDow).fill(null).map((_,i) => <div key={"e"+i}/>)}
            {Array(daysInMonth).fill(null).map((_,i) => {
              const day = i+1;
              const ds = month+"-"+String(day).padStart(2,"0");
              const avail = isAvail(ds);
              const sel = ds === value;
              const isToday = ds === today;
              const ph = getHolidayForDate(tenant?.country || "HK", ds);
              const bg = sel ? "var(--c-primary)" : ph && avail ? "#F5F3FF" : avail ? "#fff" : "var(--c-light)";
              return (
                <button key={ds} disabled={!avail} onClick={() => { onChange(ds); setExpanded(false); }} title={ph ? ph.name : ""} style={{aspectRatio:"1",borderRadius:10,border:ph&&!sel?"2px solid #C4B5FD":isToday&&!sel?"2px solid var(--c-grad1)":"none",background:bg,color:sel?"#fff":ph&&avail?"#6B21A8":avail?"#4A4A68":"#AFAFC4",fontSize:12,fontWeight:sel||isToday||ph?900:700,cursor:avail?"pointer":"not-allowed",textDecoration:avail?"none":"line-through",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2px",overflow:"hidden"}}>
                  <span style={{lineHeight:1}}>{day}</span>
                  {ph && <span style={{fontSize:8,fontWeight:800,lineHeight:1,marginTop:2,maxWidth:"100%",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:sel?"rgba(255,255,255,0.9)":"#6B21A8"}}>{ph.name.length > 3 ? ph.name.slice(0,2)+"…" : ph.name}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
// The no-card 14-day Pro trial new shops start on (DB: start_free_trial /
// expire_free_trials). Null once it ended or the shop subscribed.
const freeTrialOf = (t) => {
  if (!t?.trialEndsAt || t.trialEndedAt || t.plan !== "pro") return null;
  if (["active", "trialing"].includes(t.subscription?.status)) return null;
  const ms = Date.parse(t.trialEndsAt) - Date.now();
  if (!(ms > 0)) return null;
  return { daysLeft: Math.ceil(ms / 86400000), ends: localDateStr(new Date(t.trialEndsAt)) };
};
const trialTag = (tr) => tr ? zhEn(" · 試用剩 " + tr.daysLeft + " 日", " · trial " + tr.daysLeft + "d left") : "";

// Plan staff seats: { max, used, active:[ids], locked:[ids] } from the
// staff_seats() RPC (owner first, then staff by account age; disabled staff
// take no seat). Locked staff stay visible but can't take new bookings —
// the DB guard refuses them too. `key` re-fetches when plan / staff change.
function useStaffSeats(tenantId, key) {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    if (!tenantId) { setSeats(null); return; }
    let alive = true;
    fetchStaffSeats(tenantId).then(r => { if (alive) setSeats(r); }).catch(() => {});
    return () => { alive = false; };
  }, [tenantId, key]);
  return seats;
}
const seatLocked = (seats, id) => !!id && Array.isArray(seats?.locked) && seats.locked.includes(id);
const seatLockedMsg = () => zhEn("呢位員工超出方案員工名額，暫時唔可以接新預約。升級方案，或喺「員工」停用其他員工即可解鎖。", "This staff member is over your plan's staff limit and can't take new bookings. Upgrade, or disable another staff member under Staff, to unlock.");

// A staff member whose seat is locked (shop over its plan limit) can't use
// the app until the owner upgrades or frees a seat. Owners always get in.
function StaffSeatGate({ user, onLogout, children }) {
  const seats = useStaffSeats(user?.role === "staff" ? user?.tenantId : null, user?.id);
  if (user?.role !== "staff" || !seatLocked(seats, user.id)) return children;
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:"var(--c-lighter, #FFF5F7)"}}>
      <div style={{maxWidth:380,width:"100%",background:"#fff",borderRadius:24,padding:"28px 22px",textAlign:"center",boxShadow:"0 10px 30px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:44,marginBottom:8}}>🔒</div>
        <p style={{fontSize:18,fontWeight:900,color:"#1D1D2E",margin:"0 0 8px"}}>{zhEn("帳戶暫時停用", "Account paused")}</p>
        <p style={{fontSize:13.5,fontWeight:700,color:"#6A6A85",margin:"0 0 18px",lineHeight:1.6}}>{zhEn("你嘅店舖已超出方案嘅員工名額，所以你個帳戶暫時用唔到。請聯絡店主升級方案，或者調整員工名單。", "Your shop is over its plan's staff limit, so this account is paused. Ask the shop owner to upgrade the plan or adjust the staff list.")}</p>
        <Btn onClick={onLogout} style={{width:"100%"}}>{zhEn("登出", "Log out")}</Btn>
      </div>
    </div>
  );
}

// The shop's open window (minutes since midnight) on a date, or null when
// the day has no hours set / is closed (the closed-day banner covers that).
const shopWindowOn = (tenant, date) => {
  const bh = tenant?.businessHours?.[new Date(date).getDay()];
  if (!bh || bh.enabled === false || !bh.open || !bh.close) return null;
  const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const open = toMin(bh.open), close = toMin(bh.close);
  if (!(close > open)) return null;
  return { open, close, openStr: bh.open, closeStr: bh.close };
};

const canCancelFree = (apt, tenant) => {
  const policy = tenant?.cancelPolicy;
  if (!policy) return true;
  const hoursUntil = (new Date(apt.date + "T" + apt.time) - new Date()) / 3600000;
  return hoursUntil >= (policy.deadlineHours ?? 24);
};
const daysSinceLastGroom = (petId, appointments) => {
  const last = appointments.filter(a => a.petId === petId && a.status === "done").sort((a, b) => b.date.localeCompare(a.date))[0]?.date;
  return last ? Math.floor((new Date() - new Date(last)) / 86400000) : null;
};

// Returns the appointment's effective duration (with optional buffer).
// Order of precedence: apt.customDuration → service.estimatedMinutes → 60min.
const getAptDuration = (apt, tenant, includeBuffer = true) => {
  if (apt?.kind === "event") return apt.customDuration || 30;
  const svc = (tenant?.services || []).find(s => s && (s.name === apt?.service || s.id === apt?.svcId));
  // customDuration carries the per-pet duration captured at booking time
  // (either set via the matrix-pricing per-size durationMin, or by a manual
  // owner override in the edit-apt sheet). Falls back to svc.estimatedMinutes.
  const base = apt?.customDuration || svc?.estimatedMinutes || 60;
  return includeBuffer ? base + (svc?.bufferMinutes || 0) : base;
};

// ── PET ALERTS ─────────────────────────────────────────────────────────────
const petAlerts = (pet, appointments, tenant) => {
  if (!pet) return [];
  const out = [];
  if (pet.vaccineDate) {
    const validDays = 365;
    const ageDays = Math.floor((new Date() - new Date(pet.vaccineDate)) / 86400000);
    const remaining = validDays - ageDays;
    if (remaining < 0) out.push({ severity:"high", text:zhEn("疫苗已過期 "+(-remaining)+" 天", "Vaccine expired "+(-remaining)+" days ago"), icon:"💉" });
    else if (remaining <= 30) out.push({ severity:"med", text:zhEn("疫苗 "+remaining+" 天後到期", "Vaccine expires in "+remaining+" days"), icon:"💉" });
  } else {
    out.push({ severity:"low", text:zhEn("無疫苗記錄","No vaccine record"), icon:"💉" });
  }
  if (pet.allergies && !["", "無", "无", "None", "none", "NONE", "n/a", "N/A", "nil", "Nil"].includes(String(pet.allergies).trim())) out.push({ severity:"high", text:zhEn("過敏: ","Allergies: ") + pet.allergies, icon:"🩺" });
  if (pet.behavior?.bites)              out.push({ severity:"high", text:zhEn("會咬人","Bites"),       icon:"🦷" });
  if (pet.behavior?.muzzleNeeded)       out.push({ severity:"high", text:zhEn("需口套","Needs muzzle"),       icon:"🚫" });
  if (pet.behavior?.anxious)            out.push({ severity:"med",  text:zhEn("易焦慮","Anxious"),       icon:"😰" });
  if (pet.behavior?.dryerFear)          out.push({ severity:"med",  text:zhEn("怕吹風機","Afraid of dryer"),     icon:"🌪️" });
  if (pet.behavior?.aggressiveWithDogs) out.push({ severity:"med",  text:zhEn("不可與其他狗","Not with other dogs"), icon:"🐕" });
  if (pet.medicalFlags?.orthopedic)     out.push({ severity:"med",  text:zhEn("關節注意","Joint care"),     icon:"🦴" });
  if (pet.medicalFlags?.heart)          out.push({ severity:"high", text:zhEn("心臟病史","Heart condition"),     icon:"❤️" });
  if (pet.medicalFlags?.seizures)       out.push({ severity:"high", text:zhEn("癲癇病史","Seizure history"),     icon:"⚡" });
  if (pet.medicalFlags?.skinCondition)  out.push({ severity:"med",  text:zhEn("皮膚狀況","Skin condition"),     icon:"🧴" });
  const last = daysSinceLastGroom(pet.id, appointments);
  if (last !== null && last > 60) out.push({ severity:"med", text:zhEn("上次美容 " + last + " 天前", "Last groom " + last + " days ago"), icon:"⏰" });
  const sb = (tenant?.specialBreeds || []).find(s => pet.breed && pet.breed.includes(s.name));
  if (sb) out.push({ severity:"info", text:zhEn("特殊品種: ","Special breed: ") + sb.name, icon:"✨" });
  return out;
};
const sevStyle = (sev) => ({
  high:{ bg:"#FEF2F2", border:"#FECACA", color:"#DC2626" },
  med: { bg:"#FFFBEB", border:"#FEF3C7", color:"#D97706" },
  low: { bg:"#F8FAFC", border:"#E5E7EB", color:"#6A6A85" },
  info:{ bg:"#EFF6FF", border:"#BFDBFE", color:"#2563EB" },
}[sev] || { bg:"#F8FAFC", border:"#E5E7EB", color:"#6A6A85" });

const PetAlertChips = ({ pet, appointments, tenant, size = "sm" }) => {
  const alerts = petAlerts(pet, appointments, tenant);
  if (alerts.length === 0) return null;
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>
      {alerts.map((a, i) => {
        const s = sevStyle(a.severity);
        return (
          <span key={i} style={{display:"inline-flex",alignItems:"center",gap:3,padding:size==="sm"?"2px 7px":"4px 10px",borderRadius:99,background:s.bg,border:"1px solid "+s.border,color:s.color,fontSize:size==="sm"?10:12,fontWeight:800}}>
            <span>{a.icon}</span>{a.text}
          </span>
        );
      })}
    </div>
  );
};

// ── GROOMING TIMER ─────────────────────────────────────────────────────────
const GroomingTimer = ({ apt, tenant }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (apt.status !== "grooming") return;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [apt.status]);
  if (apt.status !== "grooming") return null;
  const estimated = getAptDuration(apt, tenant, false);
  let startedAt;
  if (apt.startedAt && /^\d{2}:\d{2}$/.test(apt.startedAt)) {
    startedAt = new Date(apt.date + "T" + apt.startedAt + ":00");
  } else if (apt.startedAt) {
    startedAt = new Date(apt.startedAt);
  } else {
    startedAt = new Date(apt.date + "T" + apt.time + ":00");
  }
  const elapsed = Math.max(0, Math.floor((now - startedAt.getTime()) / 60000));
  const pct = Math.min(100, Math.round((elapsed / estimated) * 100));
  const overdue = elapsed > estimated;
  return (
    <div style={{background:overdue?"#FEF2F2":"#EFF6FF",borderRadius:14,padding:"8px 12px",border:"2px solid "+(overdue?"#FECACA":"#BFDBFE"),marginTop:6}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <span style={{fontSize:12,fontWeight:800,color:overdue?"#DC2626":"#2563EB"}}>{zhEn("✂️ 美容中 "+elapsed+" / "+estimated+" 分鐘", "✂️ Grooming "+elapsed+" / "+estimated+" min")}</span>
        <span style={{fontSize:11,fontWeight:700,color:overdue?"#DC2626":"#6A6A85"}}>{overdue ? zhEn("超時 +","Overtime +")+(elapsed-estimated)+"min" : pct+"%"}</span>
      </div>
      <div style={{height:5,borderRadius:99,background:"rgba(0,0,0,0.06)",overflow:"hidden"}}>
        <div style={{height:"100%",width:Math.min(100,pct)+"%",background:overdue?"#DC2626":"#3B82F6",transition:"width 0.5s"}}/>
      </div>
    </div>
  );
};

// ── HEALTH REPORT QUICK CHIPS ──────────────────────────────────────────────
// Report chips — i18n-aware via Proxy so consumers using
// `Object.entries(REPORT_CHIPS)` get fresh translated arrays on each
// locale switch without changing call sites. Values are stored in zh-HK
// (canonical) so the existing apt.report.skin === "正常" predicates still
// work; the localized strings are display-only via REPORT_CHIP_LABEL().
// Traffic-light triage — exactly 3 options per category: 好 / 中 / 差.
// Lays out as a perfect 3-column grid so the groomer's eye finds the
// same position for the same severity across every section. Fine-grained
// variants (e.g. 油性 vs 乾燥, 蜱蟲 vs 跳蚤) collapse into the closest
// bucket; specifics go in 美容師叮嚀 free-text.
const REPORT_CHIPS = {
  skin:        ["正常","乾燥","發炎"],
  ear:         ["正常","發炎","有耳蟎"],
  teeth:       ["正常","輕度牙石","建議洗牙"],
  mood:        ["良好","緊張害怕","攻擊性"],
  nailTrim:    ["已修剪","過長","斷裂"],
  analGlands:  ["已擠","很少","發炎堵塞"],
  parasites:   ["無發現","跳蚤","蜱蟲"],
  eyeDischarge:["正常","少量分泌","過多分泌"],
  matting:     ["無","輕微","嚴重"],
};
// Default selection per category — pre-fills a "好" state so the groomer
// only has to tap the ones that deviate. Saves ~30s per report.
const REPORT_DEFAULTS = {
  skin:"正常", ear:"正常", teeth:"正常", mood:"良好", nailTrim:"已修剪",
  analGlands:"已擠", parasites:"無發現", eyeDischarge:"正常", matting:"無",
};
// Zh-HK → i18n key map. Lookup misses fall back to the raw string so a
// custom chip value (or an older report's free-text) still renders.
const CHIP_KEY = {
  "正常":"chip.normal","良好":"chip.good","無":"chip.none","不需要":"chip.notNeeded",
  "無發現":"chip.notFound","已修剪":"chip.trimmed","已擠":"chip.expressed",
  "乾燥":"chip.skin.dry","有皮屑":"chip.skin.flakes","紅斑":"chip.skin.rash","油性":"chip.skin.oily","發炎":"chip.skin.inflamed",
  "有分泌物":"chip.ear.discharge","異味":"chip.ear.odor","紅腫":"chip.ear.red","耳螨疑似":"chip.ear.mites","有耳蟎":"chip.ear.mites",
  "輕度牙石":"chip.teeth.mild","重度牙石":"chip.teeth.heavy","建議洗牙":"chip.teeth.needsClean","脫落牙齒":"chip.teeth.missing",
  "緊張":"chip.mood.nervous","興奮":"chip.mood.excited","害怕":"chip.mood.scared","攻擊性":"chip.mood.aggressive",
  "過長未剪":"chip.nails.long","斷裂":"chip.nails.broken",
  "堵塞":"chip.ag.blocked",
  "跳蚤":"chip.para.fleas","蜱蟲":"chip.para.ticks","耳螨":"chip.para.mites",
  "少量":"chip.eye.little","眼屎多":"chip.eye.much","發紅":"chip.eye.red","異常":"chip.eye.abnormal",
  "輕微":"chip.mat.mild","中度":"chip.mat.medium","嚴重需剃毛":"chip.mat.severe",
};
const REPORT_CHIP_LABEL = (v) => {
  const k = CHIP_KEY[v];
  if (!k) return v;
  return i18nT(k, v);
};
// Field labels — proxy so consumers don't need refactoring. Same pattern
// as DAYS: lazy-translates on each property access.
const REPORT_LABELS = new Proxy({}, {
  get(_t, prop) { return i18nT("report." + String(prop), String(prop)); },
});

// The built-in default field list, used when a shop hasn't configured
// tenants.report_template. Kept in the same shape resolveReportFields()
// returns, so the ReportSheet renders one code path either way.
const REPORT_DEFAULT_FIELDS = [
  { key:"skin",         label:zhEn("皮膚 🩺", "Skin 🩺"),     type:"chip", options: REPORT_CHIPS.skin,         enabled:true, order:0 },
  { key:"ear",          label:zhEn("耳朵 👂", "Ears 👂"),     type:"chip", options: REPORT_CHIPS.ear,          enabled:true, order:1 },
  { key:"teeth",        label:zhEn("牙齒 🦷", "Teeth 🦷"),     type:"chip", options: REPORT_CHIPS.teeth,        enabled:true, order:2 },
  { key:"mood",         label:zhEn("情緒 😊", "Mood 😊"),     type:"chip", options: REPORT_CHIPS.mood,         enabled:true, order:3 },
  { key:"nailTrim",     label:zhEn("剪甲 ✂️", "Nail trim ✂️"),     type:"chip", options: REPORT_CHIPS.nailTrim,     enabled:true, order:4 },
  { key:"analGlands",   label:zhEn("肛門腺", "Anal glands"),       type:"chip", options: REPORT_CHIPS.analGlands,   enabled:true, order:5 },
  { key:"parasites",    label:zhEn("寄生蟲 🔍", "Parasites 🔍"),   type:"chip", options: REPORT_CHIPS.parasites,    enabled:true, order:6 },
  { key:"eyeDischarge", label:zhEn("眼睛分泌 👁️", "Eye discharge 👁️"), type:"chip", options: REPORT_CHIPS.eyeDischarge, enabled:true, order:7 },
  { key:"matting",      label:zhEn("打結程度 🪮", "Matting 🪮"), type:"chip", options: REPORT_CHIPS.matting,      enabled:true, order:8 },
];

// Return the ordered, enabled report-field list for a tenant. When the
// shop hasn't configured a custom template we fall back to the built-in
// defaults so nothing regresses. Custom fields are respected as-is; any
// legacy fields without a `type` are treated as chip fields.
function resolveReportFields(tenant) {
  const raw = tenant && Array.isArray(tenant.reportTemplate) ? tenant.reportTemplate : null;
  const list = (raw && raw.length ? raw : REPORT_DEFAULT_FIELDS)
    .filter(f => f && f.key && f.enabled !== false)
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
    .map(f => ({
      key: String(f.key),
      label: f.label || REPORT_LABELS[f.key] || f.key,
      type: f.type === "text" ? "text" : "chip",
      options: Array.isArray(f.options) && f.options.length
        ? f.options
        : (REPORT_CHIPS[f.key] || ["正常","中","差"]),
      placeholder: f.placeholder || "",
    }));
  return list;
}

// Toast
let _toastFn = null;
const toast = (msg, type) => _toastFn && _toastFn(msg, type || "success");
// Expose toast to non-React code (e.g. useAppState sync-error surface). Set
// in the ToastContainer mount effect; useAppState calls window.__petosToast.
if (typeof window !== "undefined") {
  window.__petosToast = (msg, type) => toast(msg, type);
}

// Settings pages that are too long for one scroll, split into tabs. Each
// card on the page is gated by inSec(page, tabId); kw feeds the settings
// search so a query lands on the right tab, not just the right page.
const SETTINGS_SECTIONS = {
  "directory": [
    { id: "link", label: () => T("預約連結","Booking link","予約リンク"), kw: "link qr 連結 分享 share" },
    { id: "dir",  label: () => T("公開目錄","Directory","ディレクトリ"), kw: "directory 目錄 boost 公開" },
  ],
  "shop-brand": [
    { id: "page",    label: () => T("品牌頁面","Shop page","店舗ページ"), kw: "logo banner 封面 標誌 介紹" },
    { id: "gallery", label: () => T("作品集","Gallery","ギャラリー"), kw: "gallery 作品 相片 photo" },
    { id: "theme",   label: () => T("主題色 / 品牌","Theme & branding","テーマ"), kw: "theme 主題 顏色 color branding 移除" },
  ],
  "shop-hours": [
    { id: "hours",    label: () => T("營業時間","Opening hours","営業時間"), kw: "hours 營業 時間" },
    { id: "holidays", label: () => T("公休日","Closed days","定休日"), kw: "holiday 公休 假期 closed" },
  ],
  "terms": [
    { id: "booking", label: () => T("預約條款","Booking terms","予約規約"), kw: "terms 條款" },
    { id: "senior",  label: () => T("高齡毛孩","Senior pets","シニア"), kw: "senior 高齡 老犬 免責 disclaimer 年齡" },
    { id: "cat",     label: () => T("貓貓須知","Cats","猫"), kw: "cat 貓" },
  ],
  "payment": [
    { id: "methods", label: () => T("收款方式","Payment methods","支払方法"), kw: "payme fps 現金 cash stripe 銀行 method" },
    { id: "deposit", label: () => T("訂金","Deposit","デポジット"), kw: "deposit 訂金" },
    { id: "tax",     label: () => T("稅務","Tax","税金"), kw: "tax 稅" },
  ],
  "messaging": [
    { id: "auto",      label: () => T("自動通知","Auto messages","自動通知"), kw: "auto 自動 提醒 reminder 通知 開關" },
    { id: "triggers",  label: () => T("各通知設定","Per-message settings","通知ごとの設定"), kw: "trigger 各通知 獨立 channel 渠道" },
    { id: "templates", label: () => T("訊息範本","Templates","テンプレート"), kw: "template 範本 文字" },
    { id: "channels",  label: () => T("WhatsApp 號碼 / 進階","WhatsApp number / Advanced","WhatsApp番号 / 詳細"), kw: "whatsapp 號碼 api ai 進階 advanced sms email" },
  ],
  "points": [
    { id: "stamp",  label: () => T("印花卡","Stamp card","スタンプカード"), kw: "stamp 印花" },
    { id: "rules",  label: () => T("發放規則","Earning rules","付与ルール"), kw: "rules 規則 合併 merge walk-in" },
    { id: "redeem", label: () => T("積分兌換","Redeem","交換"), kw: "redeem 兌換 voucher 獎勵券 reward" },
  ],
  "system": [
    { id: "plan",     label: () => T("訂閱方案","Plan","プラン"), kw: "plan 方案 訂閱 subscription stripe", show: (u) => u?.role === "owner" },
    { id: "backup",   label: () => T("備份","Backup","バックアップ"), kw: "backup 備份 匯出 export 重設 reset" },
    { id: "calendar", label: () => T("日曆同步","Calendar sync","カレンダー"), kw: "google calendar ical 日曆 月曆" },
    { id: "diag",     label: () => T("診斷紀錄","Diagnostics","診断"), kw: "log 紀錄 sync auth 認證" },
  ],
};
function settingsSectionsFor(sub, user) {
  const list = (SETTINGS_SECTIONS[sub] || []).filter(x => !x.show || x.show(user));
  return list.length > 1 ? list : null;
}
function SettingsSectionTabs({ sections, active, onPick }) {
  if (!sections) return null;
  return (
    <div role="tablist" style={{display:"flex",gap:6,overflowX:"auto",padding:"2px 0 4px",scrollbarWidth:"none",flexShrink:0}}>
      {sections.map(x => {
        const on = x.id === active;
        return (
          <button key={x.id} type="button" role="tab" aria-selected={on} onClick={() => onPick(x.id)}
            style={{flexShrink:0,whiteSpace:"nowrap",padding:"8px 14px",borderRadius:99,fontSize:13,fontWeight:800,cursor:"pointer",
              border:on ? "2px solid var(--c-primary)" : "2px solid var(--c-light)",
              background:on ? "var(--c-primary)" : "#FFFDFD",color:on ? "#fff" : "#4A4A68"}}>
            {x.label()}
          </button>
        );
      })}
    </div>
  );
}

// Force-password-change modal. Renders as a non-dismissable overlay when
// the profile has must_change_password=true (set by the owner-provisioned
// flow). 「稍後再改」 hides it for this visit only — the flag stays set, so it
// returns on the next login. That lets the owner sign in with the password
// they sent to see the customer's view without changing (and so breaking)
// it. Calls supabase.auth.updateUser then patches the flag.
const ForcePasswordChange = ({ profile, onDone, onLater }) => {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    if (pw1.length < 6) { setErr(i18nT("fpc.pwTooShort")); return; }
    if (pw1 !== pw2) { setErr(i18nT("fpc.pwMismatch")); return; }
    setBusy(true);
    try {
      const { supabase } = await import("./lib/supabaseClient.js");
      const { error: pErr } = await supabase.auth.updateUser({ password: pw1 });
      if (pErr) throw pErr;
      const { patchProfile } = await import("./lib/api.js");
      await patchProfile(profile.id, { mustChangePassword: false });
      toast(i18nT("fpc.updatedToast"));
      onDone && onDone();
    } catch (e) {
      setErr(e?.message || i18nT("fpc.updateFailed"));
    } finally { setBusy(false); }
  };
  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(74,74,104,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:28,padding:28,maxWidth:420,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{fontSize:48,textAlign:"center",marginBottom:8}}>🔒</div>
        <h2 style={{fontWeight:900,fontSize:22,color:"#4A4A68",margin:"0 0 6px",textAlign:"center"}}>{i18nT("fpc.title")}</h2>
        <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:"0 0 20px",textAlign:"center",lineHeight:1.6}}>{i18nT("fpc.sub")}</p>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>{i18nT("fpc.newPw")}</p>
            <input type="password" value={pw1} onChange={e => setPw1(e.target.value)} placeholder="••••••••" style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:15,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
          </div>
          <div>
            <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>{i18nT("fpc.confirmPw")}</p>
            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="••••••••" onKeyDown={e => { if (e.key === "Enter") submit(); }} style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:15,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
          </div>
          {err && <p style={{fontSize:12,color:"#DC2626",fontWeight:800,margin:0}}>{err}</p>}
          <button onClick={submit} disabled={busy} style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,var(--c-grad1),var(--c-primary))",color:"#fff",border:"none",borderRadius:99,fontSize:15,fontWeight:900,cursor:busy?"not-allowed":"pointer",opacity:busy?0.6:1,marginTop:4}}>
            {busy ? i18nT("fpc.saving") : i18nT("fpc.confirmBtn")}
          </button>
          {onLater && (
            <button type="button" onClick={onLater} disabled={busy} style={{width:"100%",padding:"8px",background:"none",border:"none",color:"#8A8A9E",fontSize:13,fontWeight:800,cursor:"pointer"}}>
              {i18nT("fpc.later")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// #3 Undo snackbar — global. Anywhere in the app can call:
//   undoSnackbar({ message: "已刪除 X", onUndo: () => restore() })
// which shows a 5-second snackbar with an "撤銷" button at the bottom.
// If the user doesn't act, the snackbar fades out and the delete sticks.
let _undoFn = null;
function undoSnackbar(opts) { if (_undoFn) _undoFn(opts); }
if (typeof window !== "undefined") window.__petosUndo = undoSnackbar;
const UndoSnackbarContainer = () => {
  const [item, setItem] = useState(null);
  useEffect(() => {
    _undoFn = (opts) => {
      setItem({ ...opts, id: Math.random().toString(36).slice(2), at: Date.now() });
    };
    return () => { _undoFn = null; };
  }, []);
  useEffect(() => {
    if (!item) return;
    const t = setTimeout(() => setItem(null), 5500);
    return () => clearTimeout(t);
  }, [item]);
  if (!item) return null;
  return (
    <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",zIndex:9990,background:"#1F2937",color:"#fff",borderRadius:99,padding:"10px 8px 10px 18px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 16px 40px rgba(0,0,0,0.3)",maxWidth:"calc(100vw - 32px)"}}>
      <span style={{fontSize:13,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.message}</span>
      <button onClick={() => { try { item.onUndo && item.onUndo(); } catch {} setItem(null); }} style={{background:"#FF7AA2",color:"#fff",border:"none",borderRadius:99,padding:"6px 14px",fontSize:13,fontWeight:900,cursor:"pointer",flexShrink:0}}>{i18nT("undo.btn")}</button>
    </div>
  );
};

// #9 Printable receipt — opens a new window with a minimal 80mm-thermal
// styled receipt and auto-fires window.print(). Customer keeps a paper
// copy / shares as PDF via "Save as PDF" in the print dialog. No new
// libraries; uses inline CSS + escape for safety.
function escapeHtml(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, ch => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;" })[ch]); }
function printReceipt(apt, { tenant, customer, pet }) {
  const w = window.open("", "_blank", "width=420,height=720");
  if (!w) { try { window.__petosToast && window.__petosToast(i18nT("tx.popupBlocked"), "error"); } catch {} return; }
  const cs = cur(tenant);
  // M2: use the shared apt helpers so the receipt agrees with EOD +
  // PaymentSheet. Package bookings show the base service crossed out
  // and the cash portion as the actual total; the payment_log lines
  // render below so the customer sees how/when/by whom they paid.
  const surTotal = (apt.surcharges || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const fullSubtotal = (Number(apt.basePrice) || 0) + (Number(apt.addonPrice) || 0) + surTotal;
  const discountVal = apt.discount
    ? (apt.discount.type === "percent"
        ? Math.round(fullSubtotal * (Number(apt.discount.amount) || 0) / 100)
        : (Number(apt.discount.amount) || 0))
    : 0;
  const fullTotal = Math.max(0, fullSubtotal - discountVal);
  const usesPackage = !!apt.customerPackageId;
  const cashTotal = usesPackage
    ? Math.max(0, (Number(apt.addonPrice) || 0) + surTotal - (fullSubtotal > 0 ? Math.round(discountVal * ((Number(apt.addonPrice) || 0) + surTotal) / fullSubtotal) : 0))
    : fullTotal;
  const deposit = Number(apt.deposit) || 0;
  const tipAmt = Number(apt.tipAmount) || 0;
  // Net collected from log so receipt shows actual money in.
  const logEntries = Array.isArray(apt.paymentLog) ? apt.paymentLog : [];
  const collected = logEntries.reduce((s, e) => s + (Number(e?.amount) || 0), 0);
  const balance = Math.max(0, cashTotal - deposit - collected);
  const lines = [
    { label: apt.service || zhEn("美容服務", "Grooming service"), amount: apt.basePrice || 0, strike: usesPackage },
    ...(apt.addonPrice > 0 ? [{ label: "加購 / SPA", amount: apt.addonPrice }] : []),
    ...(apt.surcharges || []).map(s => ({ label: s.name || zhEn("附加費", "Surcharge"), amount: s.amount || 0 })),
  ];
  const now = new Date().toLocaleString(dateLoc(), { hour12: false });
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>收據 - ${escapeHtml(apt.id)}</title>
  <style>
    body { font-family: ui-monospace, "SF Mono", monospace; max-width: 320px; margin: 0 auto; padding: 16px; color: #111; }
    h1 { text-align: center; font-size: 18px; margin: 0 0 4px; }
    .meta { text-align: center; font-size: 11px; color: #555; margin-bottom: 14px; }
    hr { border: none; border-top: 1px dashed #999; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; font-size: 13px; padding: 2px 0; }
    .row strong { font-weight: 700; }
    .total { font-size: 16px; font-weight: 900; padding: 4px 0; }
    .foot { font-size: 11px; text-align: center; color: #555; margin-top: 14px; line-height: 1.6; }
    @media print { @page { size: 80mm auto; margin: 4mm; } }
  </style></head><body>
    <h1>${escapeHtml(tenant?.name || "Pet Salon")}</h1>
    <div class="meta">
      ${tenant?.address ? escapeHtml(tenant.address) + "<br>" : ""}
      ${tenant?.phone ? "Tel: " + escapeHtml(tenant.phone) + "<br>" : ""}
      ${escapeHtml(now)}
    </div>
    <hr>
    <div class="row"><span>客戶</span><span>${escapeHtml(customer?.name || "—")}</span></div>
    <div class="row"><span>毛孩</span><span>${escapeHtml(pet?.name || "—")}${pet?.breed ? " · " + escapeHtml(pet.breed) : ""}</span></div>
    <div class="row"><span>日期</span><span>${escapeHtml(apt.date || "")} ${escapeHtml(apt.time || "")}</span></div>
    <hr>
    ${lines.map(l => `<div class="row"><span>${escapeHtml(l.label)}${l.strike ? " 📦套票" : ""}</span><span style="${l.strike ? "text-decoration:line-through;color:#999" : ""}">${cs}${l.amount}</span></div>`).join("")}
    <hr>
    ${discountVal > 0 ? `<div class="row"><span>原價小計</span><span>${cs}${fullSubtotal}</span></div><div class="row"><span>折扣</span><span>-${cs}${discountVal}</span></div>` : ""}
    <div class="row total"><span>${usesPackage ? "實收" : "小計"}</span><span>${cs}${cashTotal}</span></div>
    ${deposit > 0 ? `<div class="row"><span>已付訂金</span><span>-${cs}${deposit}</span></div>` : ""}
    ${collected > 0 ? `<div class="row"><span>已收款</span><span>-${cs}${collected}</span></div>` : ""}
    ${tipAmt > 0 ? `<div class="row"><span>小費</span><span>${cs}${tipAmt}</span></div>` : ""}
    <div class="row total"><span>${balance > 0 ? "尚欠" : "已結清"}</span><span>${cs}${balance + tipAmt}</span></div>
    ${logEntries.length > 0 ? `<hr><div style="font-size:11px;color:#555;margin-bottom:4px;">付款紀錄</div>${logEntries.map(e => `<div class="row" style="font-size:11px;color:#555"><span>${escapeHtml((e.at || "").slice(0,16).replace("T"," "))} · ${escapeHtml(e.methodName || e.method || "—")}</span><span style="color:${(Number(e.amount) || 0) < 0 ? "#DC2626" : "#10B981"}">${(Number(e.amount) || 0) < 0 ? "-" : ""}${cs}${Math.abs(Number(e.amount) || 0)}</span></div>`).join("")}` : ""}
    <div class="foot">
      多謝惠顧 🐾<br>
      ${tenant?.bookingSlug ? "下次預約：" + escapeHtml((tenant.bookingUrlPrefix || "pettobook.com/shop/") + tenant.bookingSlug) : ""}
    </div>
    <script>window.onload = () => setTimeout(() => window.print(), 100);</script>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
}

// Senior-pet waiver printable — opens a new window with the disclaimer
// text + captured signature image, formatted for browser Print → Save
// as PDF. Fills the same role as printReceipt: no third-party PDF
// library needed, works on iPad / desktop / mobile via the platform
// print dialog. Owner uses this to keep a signed record on file.
function printSeniorWaiver(apt, { tenant, customer, pet }) {
  const w = window.open("", "_blank", "width=720,height=960");
  if (!w) { try { window.__petosToast && window.__petosToast(i18nT("tx.popupBlocked"), "error"); } catch {} return; }
  const disclaimer = (tenant?.disclaimerSenior || "").trim() || "本人明白高齡寵物 (10 歲以上) 在美容過程中存在較高風險，包括但不限於：\n- 心臟、呼吸系統壓力\n- 皮膚敏感、傷口愈合較慢\n- 情緒緊張反應\n\n本人已充份了解上述風險，同意在此進行美容服務並自願承擔相關責任。";
  const signedAt = new Date().toLocaleString(dateLoc(), { hour12: false });
  const sig = apt?.signature || "";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>高齡寵物免責聲明 - ${escapeHtml(pet?.name || "")}</title>
  <style>
    body { font-family: system-ui, -apple-system, "PingFang HK", "Microsoft JhengHei", sans-serif; max-width: 640px; margin: 0 auto; padding: 40px 32px; color: #111; line-height: 1.6; }
    h1 { text-align: center; font-size: 20px; margin: 0 0 8px; letter-spacing: 2px; }
    .shop { text-align: center; font-size: 14px; color: #555; margin-bottom: 32px; }
    .meta { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 12px 16px; margin-bottom: 24px; font-size: 13px; }
    .meta div { margin: 3px 0; }
    .meta b { display: inline-block; min-width: 72px; color: #6B7280; font-weight: 600; }
    .disclaimer { white-space: pre-wrap; font-size: 13px; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 8px; padding: 16px; margin: 16px 0 32px; }
    .sig-box { border: 1px solid #D1D5DB; border-radius: 8px; padding: 12px; margin-top: 16px; }
    .sig-box p { margin: 0 0 8px; font-size: 12px; color: #6B7280; font-weight: 700; }
    .sig-box img { display: block; max-width: 100%; height: auto; max-height: 200px; }
    .sig-empty { display: flex; align-items: center; justify-content: center; height: 120px; color: #EF4444; font-size: 12px; font-weight: 700; border: 2px dashed #FCA5A5; border-radius: 8px; }
    .signed { margin-top: 12px; font-size: 12px; color: #6B7280; text-align: right; }
    .foot { margin-top: 40px; font-size: 11px; color: #9CA3AF; text-align: center; }
    @media print { @page { size: A4; margin: 20mm; } .no-print { display: none; } }
    .btn { display: inline-block; background: #FF758F; color: #fff; border: none; border-radius: 99px; padding: 10px 20px; font-weight: 900; font-size: 13px; cursor: pointer; margin-right: 8px; }
  </style></head><body>
    <div class="no-print" style="text-align:center; margin-bottom:20px;">
      <button class="btn" onclick="window.print()">🖨️ 列印 / 儲存 PDF</button>
      <button class="btn" style="background:#6B7280" onclick="window.close()">關閉</button>
    </div>
    <h1>🐾 高齡寵物美容免責聲明</h1>
    <p class="shop">${escapeHtml(tenant?.name || "")}</p>
    <div class="meta">
      <div><b>客戶</b> ${escapeHtml(customer?.name || "—")}</div>
      <div><b>電話</b> ${escapeHtml(customer?.phone || "—")}</div>
      <div><b>毛孩</b> ${escapeHtml(pet?.name || "—")} · ${escapeHtml(pet?.breed || "")} · ${pet?.weight ? pet.weight + "kg" : ""}</div>
      <div><b>服務</b> ${escapeHtml(apt?.service || "美容")}</div>
      <div><b>日期</b> ${escapeHtml(apt?.date || "")} ${escapeHtml(apt?.time || "")}</div>
    </div>
    <div class="disclaimer">${escapeHtml(disclaimer)}</div>
    <div class="sig-box">
      <p>客戶簽署 · Signature</p>
      ${sig ? `<img src="${escapeHtml(sig)}" alt="signature" />` : `<div class="sig-empty">⚠️ 未有簽名紀錄</div>`}
      <div class="signed">簽署日期：${escapeHtml(signedAt)}</div>
    </div>
    <p class="foot">Powered by PettoBook · 本文件由系統自動生成</p>
  </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
}

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _toastFn = (msg, type) => {
      const id = uid();
      setToasts(t => [...t, { id, msg, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
    };
  }, []);
  const cfg = { error:["#FFF0F3","#FFB3C1","#FF4D6D","❌"], warning:["#FFFBEB","#FCD34D","#D97706","⚠️"], success:["#F0FDF4","#86EFAC","#10B981","✅"] };
  return (
    <div style={{position:"fixed",top:24,left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",flexDirection:"column",gap:12,pointerEvents:"none",width:"calc(100% - 40px)",maxWidth:360}}>
      {toasts.map(t => { const [bg,border,color,icon] = cfg[t.type] || cfg.success; return (
        <div key={t.id} style={{background:bg,border:"2px solid "+border,color,borderRadius:24,padding:"12px 20px",fontSize:14,fontWeight:800,display:"flex",alignItems:"center",gap:10,boxShadow:"0 12px 30px rgba(0,0,0,0.08)"}}>
          <span style={{fontSize:18}}>{icon}</span>{t.msg}
        </div>
      ); })}
    </div>
  );
};

// Capture the beforeinstallprompt event so we can later trigger the install
// flow from a click (Chrome/Edge/Android). Stored on window so any component
// can read it without prop-drilling.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    window.__petosInstallPrompt = e;
    window.dispatchEvent(new CustomEvent("petos:install-available"));
  });
  window.addEventListener("appinstalled", () => {
    window.__petosInstallPrompt = null;
    try { localStorage.setItem("petos:installed", "1"); } catch { /* ignore */ }
  });
}

// Reusable ? help bubble. Owners click → small popover-style modal opens
// with the title + body. Body accepts plain text (with \n) OR a JSX node.
// Place next to any section heading: <HelpTip topic="packages" />.
const HELP_CONTENT = {
  packages: {
    title: zhEn("套票如何運作 📦", "How packages work 📦"),
    body: zhEn("1. 喺「服務 → 套票」設定套票（付費次數 + 贈送次數 + 體型 × 毛長價）。\n2. 喺客戶頁面「📦 送贈／販售套票」賣畀客戶（揀付款方式、自動記錄銷售員）。\n3. 客戶預約時會見到「使用套票」選項，揀咗系統會記低。\n4. 你完成預約（done）時自動扣 1 次。冇揀套票會自動用最快到期嗰張（FIFO）。\n5. 退款 / 延期 / 還原次數喺客戶頁「📋 管理客戶套票」。", "1. Set up a package under Services → Packages (paid visits + free visits + size × coat prices).\n2. Sell it to customers from the customer page via \"📦 Gift / sell package\" (pick the payment method; the salesperson is recorded automatically).\n3. When booking, customers see a \"Use package\" option and the system records their choice.\n4. When you complete the booking (done), 1 visit is deducted automatically. If no package is picked, the one expiring soonest is used (FIFO).\n5. Refund / extend / restore visits from \"📋 Manage packages\" on the customer page.")
  },
  packageEditor: {
    title: zhEn("套票設定提示 ✂️", "Package setup tips ✂️"),
    body: zhEn("· 付費次數 + 贈送次數 = 客戶實得總次數（例：付 10 送 2 = 12 次）。\n· 計價方式：固定價或按體型 × 毛長矩陣。\n· 按 ⚡ 自動計算會用「服務價 × 付費次數」填滿矩陣。\n· 套票只覆蓋主要服務 — SPA 同加購正常收費。\n· 低餘量提醒：客戶剩 N 次以下會見到紅色警告。", "· Paid visits + free visits = total visits the customer gets (e.g. pay 10 get 2 = 12).\n· Pricing: a fixed price or a size × coat matrix.\n· ⚡ Auto-calculate fills the matrix with \"service price × paid visits\".\n· Packages cover the main service only — SPA and add-ons are charged normally.\n· Low-balance alert: customers see a red warning when N visits or fewer remain.")
  },
  points: {
    title: zhEn("積分兌換 🎁", "Points redemption 🎁"),
    body: zhEn("1. 客戶消費 ${earnPer} 元自動賺 1 積分（系統自動 · 喺「設定 → 積分」改）。\n2. 你喺「設定 → 獎勵 → 積分兌換」設定可換項目（SPA / 加購 / 積分價）。\n3. 客戶喺個人資料「🎁 用積分換取」按兌換 → 扣積分 → 得一張單次兌換券。\n4. 客戶預約時揀「使用兌換券」，員工備註會記低，你結帳時comp相應服務。\n5. 兌換券用一次就自動標記已使用。", "1. Customers earn 1 point automatically for every set amount they spend (change it under Settings → Points).\n2. Set the redeemable items under Settings → Rewards → Points redemption (SPA / add-on / points price).\n3. Customers tap \"🎁 Redeem with points\" in their profile → points are deducted → they get a single-use coupon.\n4. When booking, customers choose \"Use coupon\"; a staff note is recorded and you comp the matching service at checkout.\n5. A coupon is marked used automatically after one use.")
  },
  scheduleSwipe: {
    title: zhEn("日曆操作提示 📅", "Calendar tips 📅"),
    body: zhEn("· 喺日子列（上面 7 個圓圈）左右掃可切換日期。\n· 拍住預約卡 + 拖去其他美容師欄 = 改期 / 換員工（手機要按住 400ms）。\n· 雙指縮放 = 放大時段格高度。\n· 直接點空白時段 = 即場新增預約。", "· Swipe the date strip (the 7 circles at the top) left / right to change day.\n· Press and drag a booking card to another groomer's column = reschedule / change staff (on mobile, press and hold 400ms).\n· Pinch = make the time rows taller.\n· Tap an empty slot = add a walk-in booking.")
  },
  vouchers: {
    title: zhEn("兌換券如何結帳 🎟️", "Redeeming coupons at checkout 🎟️"),
    body: zhEn("客戶預約時揀咗兌換券，預約詳情會見到「🎟️ 兌換券：xxx」嘅員工備註。結帳時：\n1. 照樣完成美容流程。\n2. 結尾時把對應嘅 SPA / 加購費用免去（手動 comp）。\n3. 兌換券會自動標記已使用，扣咗就唔可以再用。", "When a customer picks a coupon while booking, the booking details show a staff note \"🎟️ Coupon: xxx\". At checkout:\n1. Complete the grooming as usual.\n2. At the end, waive the matching SPA / add-on charge (manual comp).\n3. The coupon is marked used automatically and can't be used again.")
  },
  staffOrder: {
    title: zhEn("員工排序 👥", "Staff order 👥"),
    body: zhEn("用每位員工卡上的 ▲ / ▼ 按鈕調整顯示次序。喺日曆、預約頁、報表等所有地方都會跟呢個次序排。新加入嘅員工會自動排喺最後。", "Use the ▲ / ▼ buttons on each staff card to change the display order. The calendar, booking page, reports and everywhere else follow this order. New staff are added at the end automatically.")
  },
  pushNotif: {
    title: zhEn("推送通知 🔔", "Push notifications 🔔"),
    body: zhEn("· 開啟通知後，新預約 / 改期 / 取消 / 完成等事件會即時送到（即使瀏覽器關閉）。\n· iPhone 用戶要先「加到主畫面」做成 PWA 先收得到。\n· 唔小心拒絕咗？喺瀏覽器設定（網址列鎖頭）將「通知」改返「允許」即可。", "· Once enabled, new bookings / reschedules / cancellations / completions reach you instantly (even when the browser is closed).\n· iPhone users must first \"Add to Home Screen\" to make it a PWA before they can receive them.\n· Blocked by accident? In your browser settings (the lock icon in the address bar) change \"Notifications\" back to \"Allow\".")
  },
  shopBranding: {
    title: zhEn("店鋪資料 🏪", "Shop details 🏪"),
    body: zhEn("· 店鋪名稱／地址／電話：會喺客戶預約頁、收據、通知顯示。\n· 所在地區：用嚟自動匯入公眾假期。\n· 貨幣 / 語言：影響界面顯示。\n· 改完要按「儲存」先生效（避免每打一個字就同步）。", "· Shop name / address / phone: shown on the customer booking page, receipts and notifications.\n· Region: used to import public holidays automatically.\n· Currency / language: affect how the interface is displayed.\n· Tap \"Save\" for changes to take effect (so it doesn't sync on every keystroke).")
  },
  bookingSlug: {
    title: zhEn("客戶預約連結 🔗", "Customer booking link 🔗"),
    body: zhEn("· 呢條連結畀客戶喺手機 / WhatsApp / IG 開，唔需要 APP。\n· 改連結後舊嘅就失效，記得通知客戶。\n· 預覽按鈕會喺新分頁打開客戶版本，自己 check 下啱唔啱。\n· 可以複製貼上去 WhatsApp Business 自動回覆。", "· Customers can open this link on their phone / WhatsApp / IG with no app needed.\n· After you change the link the old one stops working, so tell your customers.\n· The preview button opens the customer version in a new tab so you can check it.\n· You can copy it into a WhatsApp Business auto-reply.")
  },
  businessHours: {
    title: zhEn("營業時間 ⏰", "Opening hours ⏰"),
    body: zhEn("· 設定每日開門 / 關門時間，系統按此顯示可預約時段。\n· 關閉某日 = 客戶嗰日見唔到可預約時間。\n· 改後即時生效，但已有預約唔受影響。", "· Set opening / closing times for each day; bookable slots are shown based on them.\n· Closing a day = customers see no available times that day.\n· Changes take effect immediately, but existing bookings are unaffected.")
  },
  holidays: {
    title: zhEn("公休日 🏖️", "Days off 🏖️"),
    body: zhEn("· 公眾假期會喺月曆用紫色標籤顯示作參考，但唔會自動關門。\n· 想休假要喺呢度點擊或手動加日期。\n· 嗰日客戶完全 book 唔到。\n· 紅色 🛑 = 已標記休息；按一下解除。", "· Public holidays show as purple labels in the calendar for reference but don't close the shop automatically.\n· To take a holiday, tap it here or add the date manually.\n· Customers can't book at all on that day.\n· Red 🛑 = marked closed; tap to undo.")
  },
  payment: {
    title: zhEn("收款方式 💵", "Payment methods 💵"),
    body: zhEn("· 設定接受嘅付款方式（現金 / 信用卡 / FPS / Stripe）。\n· 啟用 Stripe 可以做到客戶網上預付訂金。\n· 稅務設定（HK 服務費 / SG GST / TW VAT）會自動加埋落收據。", "· Set the payment methods you accept (cash / card / FPS / Stripe).\n· Enabling Stripe lets customers pay deposits online.\n· Tax settings (HK service charge / SG GST / TW VAT) are added to receipts automatically.")
  },
  messaging: {
    title: zhEn("WhatsApp / SMS / Email 自動訊息 💬", "WhatsApp / SMS / Email automatic messages 💬"),
    body: zhEn("· 預設用 PettoBook 平台寄出（你唔使配置）。\n· 想用自己嘅 Twilio / Resend 帳戶？喺「進階」開啟 BYOK。\n· 範本每個事件可獨立自訂（預約確認 / 改期 / 取消 / 完成）。\n· 留空 = 用預設範本。", "· Sent through the PettoBook platform by default (no setup needed).\n· Want to use your own Twilio / Resend account? Turn on BYOK under \"Advanced\".\n· Each event's template can be customised separately (confirmation / reschedule / cancellation / completion).\n· Blank = use the default template.")
  },
  templates: {
    title: zhEn("訊息範本 📝", "Message templates 📝"),
    body: zhEn("· 用 {shopName}、{customerName}、{petName}、{date}、{time}、{service} 等動態欄位。\n· 客戶收到時會自動填入相應內容。\n· 留空表示用預設範本。\n· 已自訂嘅範本上方會顯示橙色標記。", "· Use placeholders like {shopName}, {customerName}, {petName}, {date}, {time}, {service}.\n· They are filled in automatically when customers receive the message.\n· Blank means the default template is used.\n· Customised templates show an orange marker above them.")
  },
  terms: {
    title: zhEn("條款與聲明 📋", "Terms & declarations 📋"),
    body: zhEn("· 預約條款：客戶提交預約時必須同意。\n· 高齡毛孩免責聲明：≥10 歲時客戶需簽署。\n· 貓貓條款：揀「貓」時客戶需確認。\n· 寫好要 click 儲存先生效。", "· Booking terms: customers must agree when submitting a booking.\n· Senior pet disclaimer: customers must sign for pets ≥10 years old.\n· Cat terms: customers must confirm when they pick \"Cat\".\n· Tap Save for changes to take effect.")
  },
  shopPolicy: {
    title: zhEn("政策與功能 ⚙️", "Policies & features ⚙️"),
    body: zhEn("· 訂金政策：揀「總額幾 %」或「固定金額」。0% = 唔收訂金。\n· 取消政策：N 小時內取消扣百分之幾，過時扣全費。\n· 每日預約上限：避免員工超負荷。", "· Deposit policy: choose \"% of total\" or \"fixed amount\". 0% = no deposit.\n· Cancellation policy: what percentage is charged for cancellations within N hours, and the full fee after that.\n· Daily booking limit: avoids overloading staff.")
  },
  analytics: {
    title: zhEn("分析 📊", "Analytics 📊"),
    body: zhEn("· EOD 日結：今日營收 / 訂單 / 完成率，按到揀其他日期。\n· 員工抽成：自動按底薪 + 時數 + 抽成計算。\n· 客戶分析：高頻 / 流失 / 平均消費。\n· 套票概況：本月銷售 + 即將到期張數。", "· End-of-day report: today's revenue / bookings / completion rate; pick another date to view it.\n· Staff commission: calculated automatically from base pay + hours + commission.\n· Customer analytics: frequent / churned / average spend.\n· Package overview: this month's sales + packages about to expire.")
  },
  reviews: {
    title: zhEn("評價 ⭐", "Reviews ⭐"),
    body: zhEn("· 預約完成後客戶可以打分（1–5 星）+ 留評語。\n· 評分計入員工平均分。\n· 公開頁面（/shop/...）會顯示星評。", "· After a booking is completed, customers can rate (1–5 stars) and leave a comment.\n· Ratings count toward each groomer's average.\n· The public page (/shop/...) shows the star rating.")
  },
  activity: {
    title: zhEn("活動記錄 📜", "Activity log 📜"),
    body: zhEn("· 系統會自動記錄每一個操作：預約創建 / 改期 / 取消 / 員工新增 / 設定變更等。\n· 可按日期 / 員工 / 操作類型過濾。\n· 用嚟追溯邊個改咗咩、幾時改。", "· Every action is recorded automatically: bookings created / rescheduled / cancelled, staff added, settings changed, etc.\n· Filter by date / staff / action type.\n· Use it to trace who changed what, and when.")
  },
  walkin: {
    title: zhEn("即場預約 🚶", "Walk-in booking 🚶"),
    body: zhEn("· 揀客戶（或新增 walk-in）→ 揀毛孩 → 揀服務 + SPA / 加購 → 揀員工 → 揀時段 → 即場登記。\n· 折扣：可即時減 % 或固定金額（會記入活動）。\n· 已建立嘅 walk-in 客戶可以日後升級為正式帳戶。", "· Pick a customer (or add a walk-in) → pick a pet → pick a service + SPA / add-ons → pick staff → pick a time → register on the spot.\n· Discounts: take off a % or a fixed amount instantly (recorded in the activity log).\n· A walk-in customer you created can later be upgraded to a full account.")
  },
  statusFlow: {
    title: zhEn("預約狀態流程 🔄", "Booking status flow 🔄"),
    body: zhEn("預約狀態：\n1. 待處理 (pending) — 已預約未到\n2. 已登到 (checked-in) — 客戶到店\n3. 美容中 (grooming) — 開始洗剪\n4. 完成 (done) — 美容完，可收款\n\n按一下狀態圖示即可切換，客戶會自動收到通知。", "Booking statuses:\n1. Pending — booked, not arrived\n2. Checked in — customer is at the shop\n3. Grooming — washing and cutting started\n4. Done — grooming finished, ready to collect payment\n\nTap the status icon to switch; the customer is notified automatically.")
  },
  crm: {
    title: zhEn("客戶管理 👥", "Customer management 👥"),
    body: zhEn("· 點客戶卡進入詳情：所有預約、消費、毛孩、套票、積分。\n· 編輯資料 / 加值錢包 / 加標籤 / 送贈套票 / 管理套票，全部喺度做。\n· 標籤可以用嚟篩選 VIP / 流失客戶。", "· Tap a customer card for details: all bookings, spending, pets, packages and points.\n· Edit details / top up wallet / add tags / gift packages / manage packages — all done here.\n· Use tags to filter VIP / lapsed customers.")
  },
  shifts: {
    title: zhEn("員工排班 📅", "Staff roster 📅"),
    body: zhEn("· 設定每位員工嘅每日上班時段。\n· 預約系統會根據排班顯示有冇人值班。\n· 公休日 / 員工請假可在此標記。", "· Set each staff member's daily working hours.\n· The booking system shows who is on duty based on the roster.\n· Mark days off / staff leave here.")
  },
  bookingFlow: {
    title: zhEn("預約流程 🐾", "Booking flow 🐾"),
    body: zhEn("1. 揀毛孩（或登記新毛孩）\n2. 揀主要服務（必選）\n3. 加 SPA 服務（可選，多項）\n4. 加額外加購（可選）\n5. 揀美容師同時段\n6. 確認 → 可揀使用套票 / 兌換券 → 完成", "1. Pick a pet (or register a new one)\n2. Pick the main service (required)\n3. Add SPA services (optional, multiple)\n4. Add extra add-ons (optional)\n5. Pick a groomer and time\n6. Confirm → optionally use a package / coupon → done")
  },
  pricing: {
    title: zhEn("服務計價 💰", "Service pricing 💰"),
    body: zhEn("· 固定價：所有毛孩都同價。\n· 體型 × 毛長矩陣：依毛孩體重 + 短毛/長毛自動計算。\n· 特殊品種：可加單獨價（例：泰迪、貴婦）。\n· 巨型超重加價：>35kg 每 5kg 加 $X。", "· Fixed price: the same for every pet.\n· Size × coat matrix: calculated automatically from the pet's weight + short / long coat.\n· Special breeds: add separate prices (e.g. Teddy, Poodle).\n· Oversize surcharge: for pets over 35kg, add $X per 5kg.")
  },
  addons: {
    title: zhEn("加購服務 ✨", "Add-on services ✨"),
    body: zhEn("· 預約時可加多項（刷牙、貴婦腳、解結等）。\n· 平價或按體型分級皆可。\n· 客戶喺預約頁見到加購選項。", "· Several add-ons can be added when booking (teeth brushing, paw trimming, de-matting, etc.).\n· Flat price or tiered by size.\n· Customers see the add-on options on the booking page.")
  },
  wallets: {
    title: zhEn("儲值卡管理 💳", "Wallet management 💳"),
    body: zhEn("· 每位客戶都有一個儲值錢包（per-shop）。\n· 客戶充值 → 預約時扣訂金 / 全費。\n· 取消預約：訂金自動退入錢包。\n· 喺呢頁可批量檢視所有客戶嘅錢包餘額、每筆交易紀錄、手動加值 / 退款 / 調整。\n· 匯出 CSV 方便對帳。", "· Every customer has a prepaid wallet (per shop).\n· Customers top up → deposits / full fees are deducted when booking.\n· Cancelled bookings: the deposit is refunded to the wallet automatically.\n· On this page you can review every customer's wallet balance and each transaction, and manually top up / refund / adjust.\n· Export CSV for reconciliation.")
  },
  pointsAdmin: {
    title: zhEn("積分管理 🏆", "Points management 🏆"),
    body: zhEn("· 每位客戶都有一個積分餘額（per-shop）。\n· 客戶消費 ${earnPer} 元自動賺 1 積分（喺「設定 → 積分」改）。\n· 客戶喺個人資料可用積分兌換 SPA / 加購（你喺「設定 → 獎勵 → 積分兌換」設定可換項目）。\n· 喺呢頁批量檢視所有客戶積分、已兌換紀錄、手動調整。\n· 匯出 CSV 方便核數。", "· Every customer has a points balance (per shop).\n· Customers earn 1 point for each set amount they spend (change it under Settings → Points).\n· Customers can redeem points for SPA / add-ons in their profile (set the redeemable items under Settings → Rewards → Points redemption).\n· On this page you can review all customers' points, redemption history and make manual adjustments.\n· Export CSV for auditing.")
  },
};
const HelpTip = ({ topic, content, vars, size = 16 }) => {
  const [open, setOpen] = useState(false);
  const raw = content || HELP_CONTENT[topic];
  if (!raw) return null;
  // Interpolate ${key} placeholders in the body when vars is provided.
  // Keeps the copy dynamic (e.g. shop's earn rate) without turning every
  // help entry into a function or threading tenant through everywhere.
  const c = vars && typeof raw.body === "string"
    ? { ...raw, body: raw.body.replace(/\$\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : "")) }
    : raw;
  return <>
    <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label={zhEn("說明", "Help")} title={zhEn("點擊查看說明", "Tap for help")} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:size+8,height:size+8,padding:0,borderRadius:99,border:"none",background:"var(--c-lighter)",color:"var(--c-primary)",cursor:"pointer",verticalAlign:"middle"}}>
      <HelpCircle size={size} strokeWidth={2.5} />
    </button>
    {open && <div onClick={() => setOpen(false)} style={{position:"fixed",inset:0,zIndex:9990,background:"rgba(74,74,104,0.45)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e => e.stopPropagation()} style={{maxWidth:420,width:"100%",background:"#FFFDFD",borderRadius:24,padding:"22px 22px 18px",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:10}}>
          <h3 style={{margin:0,fontSize:17,fontWeight:900,color:"#4A4A68"}}>{c.title}</h3>
          <button onClick={() => setOpen(false)} aria-label={zhEn("關閉", "Close")} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:30,height:30,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <X size={14} color="var(--c-primary)" strokeWidth={3}/>
          </button>
        </div>
        {typeof c.body === "string"
          ? <p style={{margin:0,fontSize:13,fontWeight:600,color:"#4A4A68",lineHeight:1.7,whiteSpace:"pre-line"}}>{c.body}</p>
          : c.body}
      </div>
    </div>}
  </>;
};

// Inline banner that asks for OS-level notification permission. Browsers
// (especially Safari) require requestPermission() to be called from a real
// user gesture — silent post-login calls get blocked, so we render this and
// let the user click "啟用". Hides itself once permission is granted, or for
// 7 days if dismissed.
const PushPermissionBanner = ({ user }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  // iOS Safari only allows web push when the page is installed as a PWA
  // (Add to Home Screen). For regular Safari tabs there is no API to even
  // request the prompt — so the banner explains the workaround instead of
  // failing silently.
  const isIosSafari = typeof navigator !== "undefined"
    && /iPhone|iPad|iPod/.test(navigator.userAgent)
    && /Safari/.test(navigator.userAgent)
    && !/CriOS|FxiOS/.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined"
    && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);
  useEffect(() => {
    if (typeof window === "undefined" || !user?.id) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      // On iOS Safari (non-PWA) the APIs are missing — still show the banner
      // with a "add to home screen" message so the user knows what to do.
      if (isIosSafari && !isStandalone) {
        const snoozedUntil = Number(localStorage.getItem("petos.push.snooze") || 0);
        if (!snoozedUntil || Date.now() >= snoozedUntil) setShow(true);
      }
      return;
    }
    // Granted = already subscribed (no banner). Denied = blocked at browser
    // level — show an explainer banner so the user can re-enable via settings.
    // Default = first time, the click handler will trigger the OS prompt.
    const perm = Notification.permission;
    if (perm === "granted") return;
    const snoozedUntil = Number(localStorage.getItem("petos.push.snooze") || 0);
    if (snoozedUntil && Date.now() < snoozedUntil) return;
    setShow(true);
  }, [user?.id, isIosSafari, isStandalone]);
  if (!show) return null;
  const enable = async () => {
    setBusy(true);
    try {
      const { fetchPlatformConfig, ensurePushSubscription } = await import("./lib/api.js");
      const cfg = await fetchPlatformConfig();
      const vapid = cfg?.config?.vapidPublicKey;
      if (!vapid) { toast(zhEn("尚未設定推送金鑰，請聯絡管理員", "Push key not set up yet, please contact the admin"), "error"); setShow(false); return; }
      const res = await ensurePushSubscription({ userId: user.id, vapidPublicKey: vapid });
      if (res?.ok) { toast(zhEn("通知已啟用 🔔", "Notifications enabled 🔔")); setShow(false); }
      else if (res?.skipped === "permission_denied") { toast(zhEn("已封鎖通知。請到瀏覽器設定重新開啟。", "Notifications are blocked. Re-enable them in your browser settings."), "warning"); setShow(false); }
      else { toast(zhEn("無法啟用通知：", "Couldn't enable notifications: ") + (res?.skipped || "unknown"), "error"); }
    } catch (e) {
      console.warn("[PettoBook] push enable failed:", e);
      toast(i18nT("tx.enableFailed"), "error");
    } finally { setBusy(false); }
  };
  const snooze = () => {
    localStorage.setItem("petos.push.snooze", String(Date.now() + 7 * 24 * 3600 * 1000));
    setShow(false);
  };
  // Decide the message + button mode based on browser state.
  const perm = typeof window !== "undefined" && "Notification" in window ? Notification.permission : null;
  const iosNeedsPwa = isIosSafari && !isStandalone;
  const denied = perm === "denied";
  let msg, btnLabel, btnAction;
  if (iosNeedsPwa) {
    msg = zhEn("iPhone 請先：分享 ⬆️ → 加到主畫面，之後喺主畫面開啟先可以收推送通知", "On iPhone: tap Share ⬆️ → Add to Home Screen, then open it from the Home Screen to receive push notifications");
    btnLabel = zhEn("知道喇", "Got it");
    btnAction = snooze;
  } else if (denied) {
    msg = zhEn("通知已被封鎖。請喺瀏覽器設定（網站／網址列鎖頭）將通知改為「允許」", "Notifications are blocked. In your browser settings (the lock in the address bar) set Notifications to \"Allow\"");
    btnLabel = zhEn("知道喇", "Got it");
    btnAction = snooze;
  } else {
    msg = zhEn("開啟通知 — 新預約、改期、提醒都會即時送到（即使瀏覽器關閉）", "Turn on notifications — new bookings, reschedules and reminders arrive instantly (even when the browser is closed)");
    btnLabel = busy ? "…" : zhEn("啟用", "On");
    btnAction = enable;
  }
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,zIndex:9998,background:"linear-gradient(135deg, var(--c-primary), var(--c-grad2))",color:"#fff",padding:"10px 16px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 4px 18px rgba(0,0,0,0.15)"}}>
      <span style={{fontSize:22}}>🔔</span>
      <div style={{flex:1,fontSize:13,fontWeight:700,lineHeight:1.35}}>{msg}</div>
      <button onClick={btnAction} disabled={busy} style={{background:"#fff",color:"var(--c-primary)",border:"none",borderRadius:99,padding:"8px 14px",fontWeight:900,fontSize:13,cursor:busy?"wait":"pointer"}}>{btnLabel}</button>
      <button onClick={snooze} aria-label="dismiss" style={{background:"rgba(255,255,255,0.2)",color:"#fff",border:"none",borderRadius:99,width:30,height:30,fontWeight:900,fontSize:14,cursor:"pointer"}}>×</button>
    </div>
  );
};

const Card = forwardRef(function Card({ children, style = {}, onClick }, ref) {
  return (
    <div ref={ref} onClick={onClick} className="cute-card" style={{background:"rgba(255,255,255,0.85)",backdropFilter:"blur(20px)",borderRadius:32,border:"1px solid rgba(255,255,255,0.8)",boxShadow:"0 12px 40px var(--c-primary-15)",overflow:"hidden",cursor:onClick?"pointer":"default",...style}}>{children}</div>
  );
});
const Badge = ({ children, color, bg }) => (
  <span style={{fontSize:12,fontWeight:800,padding:"4px 12px",borderRadius:99,background:bg||"var(--c-primary-20)",color:color||"var(--c-primary)",display:"inline-block"}}>{children}</span>
);
// Helper: pull just the before/after photos out of an apt.report.photos
// array. report.photos can also contain "avatar" entries (the pet
// portrait managed in the CRM) — those stay private.
// Live-derive a stamp-card milestone's display label from the linked
// service / addon name so old vouchers minted with stale wording
// ("FREE SPA") show the current service name without needing a
// per-row backfill. Falls back to the stored `rewardLabel` only when
// the voucher has no refId to resolve (e.g. cash / percent vouchers).
const milestoneLiveLabel = (ms, tenant) => {
  const vc = ms?.voucher || {};
  const shortName = (full) => {
    const raw = String(full || "").trim();
    const head = raw.split(/[\-：(（]/)[0].trim();
    const pick = head || raw.replace(/[\-：(（)）]/g, "").trim();
    return pick.length > 10 ? pick.slice(0, 10) + "…" : pick;
  };
  if (vc.type === "free_spa" && vc.refId) {
    const sp = (tenant?.services || []).find(s => s.id === vc.refId);
    if (sp) return zhEn("免費 ", "Free ") + shortName(sp.name);
  }
  if (vc.type === "free_addon" && vc.refId) {
    const ad = (tenant?.addons || []).find(a => a.id === vc.refId);
    if (ad) return zhEn("免費 ", "Free ") + shortName(ad.name);
  }
  if (vc.type === "discount" && Number(vc.amount) > 0) return "$" + Number(vc.amount) + zhEn(" 折扣券", " off coupon");
  if (vc.type === "percent" && Number(vc.amount) > 0) return Number(vc.amount) + zhEn("% 折扣券", "% off coupon");
  // Last resort: the stored rewardLabel (legacy mints), then a generic
  // fallback so the tile never renders blank when refId can't resolve.
  return ms?.rewardLabel || (vc.type === "free_spa" ? zhEn("免費 SPA", "Free SPA") : vc.type === "free_addon" ? zhEn("免費 加購", "Free add-on") : zhEn("🎁 獎勵", "🎁 Reward"));
};

// Split "免費 X" into ["免費", "X"] so the stamp tile can render it on
// two lines for readability. Returns [first, rest] or [whole, ""].
const splitMilestoneLabel = (lbl) => {
  const s = String(lbl || "").trim();
  const idx = s.indexOf(" ");
  if (idx > 0 && idx < s.length - 1) return [s.slice(0, idx), s.slice(idx + 1)];
  return [s, ""];
};

// Live-derive the display label for an ISSUED voucher (in a customer's
// wallet) the same way milestoneLiveLabel does for upcoming milestones,
// so the customer never sees a stale "FREE 刷牙" / "FREE 解結" snapshot
// when the owner has since renamed or re-pointed the linked addon.
const liveVoucherLabel = (v, tenant) => {
  const spec = v?.voucher || v || {};
  const t = spec.type || v?.type;
  const shortName = (full) => {
    const raw = String(full || "").trim();
    const head = raw.split(/[\-：(（]/)[0].trim();
    const pick = head || raw.replace(/[\-：(（)）]/g, "").trim();
    return pick.length > 12 ? pick.slice(0, 12) + "…" : pick;
  };
  if (t === "free_spa" && spec.refId) {
    const sp = (tenant?.services || []).find(s => s.id === spec.refId);
    if (sp) return zhEn("免費 ", "Free ") + shortName(sp.name);
  }
  if (t === "free_addon" && spec.refId) {
    const ad = (tenant?.addons || []).find(a => a.id === spec.refId);
    if (ad) return zhEn("免費 ", "Free ") + shortName(ad.name);
  }
  if (t === "discount" && Number(spec.amount) > 0) return "$" + Number(spec.amount) + zhEn(" 折扣券", " off coupon");
  if (t === "percent" && Number(spec.amount) > 0) return Number(spec.amount) + zhEn("% 折扣券", "% off coupon");
  return v?.label || zhEn("獎勵", "Reward");
};

// Shared helper for saving a customer phone. Centralises the graceful
// handling for the profiles_customer_phone_unique constraint so every
// callsite gets identical behaviour and can't drift again:
//
// - Calls patchProfile → sets phone + countryCode
// - Optionally patches customer_memberships in one shot
// - On 23505 duplicate, fires requestCustomerClaim (owner-approval merge)
//   and returns { ok: true, claimFiled: true } instead of throwing
// - Returns { ok: false, error } for any other failure so the caller
//   picks its own UI response
//
// Callers should render toasts based on the returned shape.
async function saveCustomerPhone({ userId, phone, countryCode = "+852", tenantId = null, patchMembership = false }) {
  try {
    await patchProfile(userId, { phone, countryCode });
    if (patchMembership && tenantId) {
      try { await patchCustomerMembership({ userId, tenantId, phone }); }
      catch (memErr) { console.warn("[PettoBook] membership phone sync failed:", memErr); }
    }
    return { ok: true, claimFiled: false };
  } catch (e) {
    const msg = e?.message || String(e);
    if (/unique|duplicate|23505/i.test(msg) && /phone/i.test(msg)) {
      if (tenantId) {
        try { await requestCustomerClaim({ tenantId, phone }); }
        catch { /* best-effort */ }
      }
      return { ok: true, claimFiled: true };
    }
    return { ok: false, error: e };
  }
}

const aptBeforeAfterPhotos = (apt) => {
  const arr = apt?.report?.photos;
  if (!Array.isArray(arr)) return [];
  return arr.filter(p => p && (p.category === "before" || p.category === "after"));
};

const MAX_APT_BA_PHOTOS = 4;

// Serialises photo/token writes per appointment. Each upsert sends the whole
// row, so concurrent writes built from the same render snapshot (e.g. a
// multi-select upload) would overwrite each other. Queued tasks receive the
// latest written apt instead of the stale closure copy.
const aptPhotoQueue = new Map();
function queueAptWrite(apt, task) {
  let q = aptPhotoQueue.get(apt.id);
  if (!q) { q = { chain: Promise.resolve(), apt, pending: 0 }; aptPhotoQueue.set(apt.id, q); }
  q.pending++;
  const run = q.chain.then(() => task(q.apt, (next) => { q.apt = next; }));
  q.chain = run.catch(() => {}).finally(() => { if (--q.pending === 0) aptPhotoQueue.delete(apt.id); });
  return run;
}

// Shared before/after photo gallery — used on both the owner-side
// EditApt sheet (so the shop sees the same layout the customer sees)
// AND the public anon-access #/album/{token} page. Photos array shape:
// [{ id, url, category: "before" | "after", date?, name? }] — same
// shape PhotoUpload writes into apt.report.photos, so this gallery
// reads straight from the existing record (no duplicate storage).
const BeforeAfterGallery = ({ photos = [], onPhotoTap }) => {
  if (!Array.isArray(photos) || photos.length === 0) return null;
  // Accept either `category` (canonical) or legacy `kind` for back-compat.
  const kindOf = (p) => p.category || p.kind || "after";
  const before = photos.filter(p => kindOf(p) === "before");
  const after  = photos.filter(p => kindOf(p) !== "before");
  const pairCount = Math.min(before.length, after.length);
  const pairs = Array.from({ length: pairCount }, (_, i) => ({ before: before[i], after: after[i] }));
  const extraBefore = before.slice(pairCount);
  const extraAfter  = after.slice(pairCount);
  const tile = (ph) => (
    <button key={ph.id || ph.url} type="button" onClick={() => onPhotoTap && onPhotoTap(ph)} style={{position:"relative",padding:0,border:"none",background:"transparent",borderRadius:14,overflow:"hidden",aspectRatio:"1/1",cursor:"pointer",boxShadow:"0 4px 14px rgba(31,41,55,0.08)"}}>
      <img src={ph.url} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
      <span style={{position:"absolute",top:6,left:6,background:((ph.category||ph.kind) === "before") ? "rgba(239,68,68,0.92)" : "rgba(16,185,129,0.92)",color:"#fff",fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,letterSpacing:0.5}}>{(ph.category||ph.kind) === "before" ? "BEFORE" : "AFTER"}</span>
    </button>
  );
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {pairs.map((p, i) => (
        <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {tile(p.before)}
          {tile(p.after)}
        </div>
      ))}
      {(extraBefore.length > 0 || extraAfter.length > 0) && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))",gap:8}}>
          {extraBefore.map(tile)}
          {extraAfter.map(tile)}
        </div>
      )}
    </div>
  );
};

// Public anon-access page rendering an album by URL token. Mounts when
// the hash route matches `#/album/{token}`. Owner gives this URL to
// the customer via WhatsApp / SMS; customer opens, sees the gallery,
// taps to enlarge, hits Share to post the after-photo to IG / FB.
const PublicAlbumPage = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!token) { setErrMsg(zhEn("缺少分享連結 token", "Share link token is missing")); setLoading(false); return; }
    setLoading(true);
    fetchPublicAlbum(token)
      .then(r => { if (!cancelled) { if (!r) { setErrMsg(zhEn("此分享連結已失效或尚未上載照片", "This share link has expired or no photos have been uploaded yet")); } else { setData(r); } setLoading(false); } })
      .catch(e => { if (!cancelled) { setErrMsg(e?.message || zhEn("載入失敗", "Failed to load")); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token]);
  const themeVars = data?.shopTheme && THEMES[data.shopTheme] ? THEMES[data.shopTheme] : THEMES.peach;
  const primary = themeVars["--c-primary"] || "#FF758F";
  const grad = themeVars["--c-grad1"] || "#FFB6C1";
  return (
    <div style={{...themeVars, minHeight:"100vh", background:"linear-gradient(180deg, var(--c-lighter) 0%, #FFF7F0 60%, var(--c-light) 100%)", paddingBottom:80}}>
      {loading && <div style={{padding:80, textAlign:"center", color:"#8A8A9E", fontWeight:700}}>{zhEn("載入中…", "Loading…")}</div>}
      {!loading && errMsg && (
        <div style={{maxWidth:520, margin:"60px auto", padding:24, background:"#fff", borderRadius:18, boxShadow:"0 12px 32px rgba(0,0,0,0.08)", textAlign:"center"}}>
          <div style={{fontSize:42, marginBottom:8}}>🐾</div>
          <p style={{fontSize:14, color:"#4A4A68", fontWeight:800, margin:0}}>{errMsg}</p>
        </div>
      )}
      {!loading && data && (
        <div style={{maxWidth:680, margin:"0 auto"}}>
          {/* Hero */}
          <div style={{background:`linear-gradient(135deg, ${grad}, ${primary})`, padding:"40px 22px 50px", color:"#fff", textAlign:"center", borderRadius:"0 0 32px 32px", boxShadow:"0 12px 32px rgba(0,0,0,0.16)", position:"relative", overflow:"hidden"}}>
            <span aria-hidden style={{position:"absolute", left:"-6px", top:"60%", fontSize:90, opacity:0.18, transform:"rotate(-20deg)", color:"#fff"}}>🐾</span>
            <span aria-hidden style={{position:"absolute", right:"-8px", top:"-8px", fontSize:80, opacity:0.18, transform:"rotate(18deg)", color:"#fff"}}>🐾</span>
            {data.shopLogoUrl ? (
              <img src={data.shopLogoUrl} alt="" style={{width:72, height:72, borderRadius:22, objectFit:"cover", border:"3px solid #fff", boxShadow:"0 8px 22px rgba(0,0,0,0.2)", marginBottom:10}} />
            ) : (
              <div style={{width:72, height:72, borderRadius:22, background:"#fff", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:10, fontSize:36, boxShadow:"0 8px 22px rgba(0,0,0,0.2)"}}>🐾</div>
            )}
            <p style={{fontSize:22, fontWeight:900, margin:0, letterSpacing:0.3, textShadow:"0 2px 8px rgba(0,0,0,0.25)"}}>{data.shopName}</p>
            {data.shopTagline && <p style={{fontSize:12, fontWeight:700, opacity:0.92, margin:"4px 0 0"}}>{data.shopTagline}</p>}
            <div style={{marginTop:18, background:"rgba(255,255,255,0.18)", backdropFilter:"blur(10px)", borderRadius:99, padding:"6px 16px", display:"inline-block"}}>
              <p style={{fontSize:13, fontWeight:800, margin:0, color:"#fff"}}>🐾 {data.petName || zhEn("毛孩", "Pet")} · {data.aptDate} {data.aptTime}</p>
            </div>
          </div>
          {/* Album */}
          <div style={{padding:"20px 16px 0"}}>
            <h2 style={{fontSize:18, fontWeight:900, color:"#4A4A68", margin:"0 0 14px"}}>{zhEn("美容紀念冊 ✂️ Before & After", "Grooming album ✂️ Before & After")}</h2>
            <BeforeAfterGallery photos={data.photos} onPhotoTap={(ph) => setLightbox(ph)} />
            <div style={{marginTop:24, padding:18, background:"#fff", borderRadius:18, boxShadow:"0 6px 18px rgba(0,0,0,0.06)", textAlign:"center"}}>
              <p style={{fontSize:13, color:"#4A4A68", fontWeight:700, margin:"0 0 12px"}}>{zhEn("喜歡的話，幫", "If you like it, help ")} {data.petName || zhEn("佢", "them")} {zhEn("分享出去 🐾✨", "share it 🐾✨")}</p>
              <div style={{display:"flex", flexWrap:"wrap", gap:10, justifyContent:"center"}}>
                <button onClick={async () => {
                  try {
                    const url = window.location.href;
                    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
                      await navigator.share({ title: data.shopName, text: data.petName + zhEn(" 的美容紀念冊 🐾", "'s grooming album 🐾"), url });
                    } else {
                      navigator.clipboard?.writeText(url);
                    }
                  } catch { /* user cancelled */ }
                }} style={{padding:"12px 22px", background:`linear-gradient(135deg, ${grad}, ${primary})`, color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:14, cursor:"pointer", boxShadow:"0 8px 22px rgba(0,0,0,0.18)"}}>
                  {zhEn("📤 分享連結", "📤 Share link")}
                </button>
                {/* Save the watermarked before/after image to post on IG/FB. */}
                <button onClick={async () => {
                  const before = (data.photos || []).find(p => (p.category || p.kind) === "before");
                  const after = (data.photos || []).find(p => (p.category || p.kind) !== "before");
                  if (!before || !after) { try { window.alert(zhEn("需要美容前後照片先可以儲存對比相", "You need before and after photos to save the comparison image")); } catch { /* ignore */ } return; }
                  try {
                    const blob = await buildBeforeAfterComposite({
                      before: before.url, after: after.url, shopName: data.shopName || "", petName: data.petName || "",
                      bookingUrl: "", phone: "", platformDomain: "pettobook.com", hideBranding: false,
                    });
                    await shareImageBlob({ blob, text: (data.petName ? data.petName + " " : "") + zhEn("美容前後對比 ✨ ", "Grooming before/after ✨ ") + (data.shopName || ""), filename: (data.petName || "pet") + "-before-after.jpg" });
                  } catch { try { window.alert(zhEn("儲存失敗，請再試", "Save failed, please try again")); } catch { /* ignore */ } }
                }} style={{padding:"12px 22px", background:"#fff", color:primary, border:`2px solid ${primary}`, borderRadius:99, fontWeight:900, fontSize:14, cursor:"pointer"}}>
                  {zhEn("⬇️ 儲存相片", "⬇️ Save photo")}
                </button>
              </div>
            </div>
          </div>
          {lightbox && (
            <div onClick={() => setLightbox(null)} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:20, cursor:"zoom-out"}}>
              <img src={lightbox.url} alt="" style={{maxWidth:"100%", maxHeight:"90vh", objectFit:"contain", borderRadius:8}} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Btn = ({ children, onClick, variant, style = {}, disabled, type, size }) => {
  const [hov, setHov] = useState(false);
  const v = variant || "primary"; const sm = size === "sm";
  const map = {
    primary:{background:hov?"var(--c-primary)":"linear-gradient(135deg, var(--c-grad1) 0%, var(--c-primary) 100%)",color:"#fff",boxShadow:disabled?"none":"0 8px 24px var(--c-primary-40)"},
    secondary:{background:hov?"var(--c-border)":"var(--c-light)",color:"var(--c-primary)"},
    danger:{background:hov?"#E11D48":"#FF4D6D",color:"#fff",boxShadow:"0 8px 24px rgba(255,77,109,0.3)"},
    ghost:{background:hov?"var(--c-light)":"transparent",color:"#8A8A9E"},
    success:{background:hov?"#059669":"#10B981",color:"#fff",boxShadow:"0 8px 24px rgba(16,185,129,0.3)"},
    amber:{background:hov?"#D97706":"#F59E0B",color:"#fff",boxShadow:"0 8px 24px rgba(245,158,11,0.3)"},
  };
  const base = {borderRadius:99,fontWeight:800,border:"none",cursor:disabled?"not-allowed":"pointer",transition:"all 0.3s cubic-bezier(0.4,0,0.2,1)",opacity:disabled?0.5:1,fontSize:sm?13:15,padding:sm?"8px 16px":"14px 24px",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,transform:hov&&!disabled?"scale(1.02)":"scale(1)",...style};
  return <button type={type||"button"} onClick={onClick} disabled={!!disabled} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{...base,...(map[v]||map.primary)}}>{children}</button>;
};
// Inline "working…" ring for buttons mid-submit. Uses the global `spin`
// keyframe. Purely visual — the actual double-tap guard is always a ref.
const Spinner = ({ size = 14, color = "currentColor" }) => (
  <span style={{
    width: size, height: size, borderRadius: "50%", display: "inline-block", flexShrink: 0,
    border: "2px solid " + color, borderTopColor: "transparent",
    animation: "spin 0.7s linear infinite", verticalAlign: "-2px",
  }} />
);
const Input = ({ label, hint, ...p }) => (
  <div>
    {label && <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{label}</p>}
    <input style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:20,padding:"14px 18px",fontSize:15,fontWeight:600,outline:"none",boxSizing:"border-box",background:"#FFFDFD",color:"#4A4A68",transition:"border 0.3s"}} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-light)"} {...p} />
    {hint && <p style={{fontSize:12,color:"#AFAFC4",marginTop:6,fontWeight:600}}>{hint}</p>}
  </div>
);
// Locally-buffered number input. The matrix editor's many `<input>` cells
// fired setState on every keystroke, which re-rendered the whole editor
// (~30 inputs) and caused choppy typing / cursor jumps. NumCell holds the
// in-progress string locally and only pushes the parsed number up on blur
// or Enter — so typing feels instant and the matrix doesn't re-render
// mid-keystroke. Keeps the same visual style as the raw <input>.
const NumCell = ({ value, onCommit, placeholder = "", style = {}, parse = parseFloat, ...rest }) => {
  const [v, setV] = useState(String(value ?? ""));
  // External value can change for unrelated reasons (other field saved);
  // resync the buffer when it does, but ONLY if the user isn't actively
  // editing this field (focus state tracked via a ref).
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setV(value === 0 || value === "0" ? "0" : String(value ?? ""));
  }, [value]);
  const commit = () => {
    focusedRef.current = false;
    const parsed = parse(v);
    const next = isNaN(parsed) ? 0 : parsed;
    if (next !== Number(value)) onCommit(next);
  };
  return (
    <input
      type="number"
      value={v}
      placeholder={placeholder}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      style={style}
      {...rest}
    />
  );
};

const Sel = ({ label, children, ...p }) => (
  <div>
    {label && <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{label}</p>}
    <select style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:20,padding:"14px 18px",fontSize:15,fontWeight:600,outline:"none",boxSizing:"border-box",background:"#FFFDFD",color:"#4A4A68",transition:"border 0.3s"}} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-light)"} {...p}>{children}</select>
  </div>
);

// BreedCombobox — typeahead picker over the curated BREEDS list.
//
// Props:
//   label       — heading text (optional)
//   type        — "dog" | "cat" — filters to that species
//   lang        — "zh-HK" | "zh-CN" | "en" — picks the localized display label
//   value       — current breedId (preferred). If only a legacy free-text
//                 breed string is available pass `valueLabel` instead.
//   valueLabel  — fallback label for legacy pets without breedId
//   onChange    — fn({ breedId, breed, coatHint }) called when user picks an
//                 entry. `breed` is the localized display label.
//   placeholder — text shown when nothing chosen (defaults per language)
//
// Behavior:
//   - Typing filters across zh/en/aliases
//   - Sorted by popularity desc
//   - Top of the dropdown surfaces "混種"/"家貓" / "其他"  so unusual cases
//     are always one tap away
//   - On select, fires onChange with the canonical breedId AND localized
//     label so the pet record stays human-readable in lists
const BreedCombobox = ({ label, type = "dog", lang = "zh-HK", value, valueLabel, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Resolve current display label: prefer breedId lookup, fall back to the
  // free-text label that's already on the pet record.
  const current = value ? getBreedById(value) : null;
  const currentLabel = current ? breedLabel(current, lang) : (valueLabel || "");

  // Close the dropdown when the user clicks outside it.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const results = searchBreeds({ type, query });
  const ph = placeholder || (String(lang).startsWith("en") ? "Type breed…" : zhEn("輸入或選擇品種…", "Type or choose a breed…"));

  const pick = (b) => {
    onChange?.({ breedId: b.id, breed: breedLabel(b, lang), coatHint: b.coatHint });
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{position:"relative"}}>
      {label && <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{label}</p>}
      <input
        ref={inputRef}
        value={open ? query : currentLabel}
        placeholder={ph}
        onFocus={(e) => { setOpen(true); setQuery(""); e.target.style.borderColor = "var(--c-grad1)"; }}
        onBlur={(e) => { e.target.style.borderColor = "var(--c-light)"; }}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:20,padding:"14px 18px",fontSize:15,fontWeight:600,outline:"none",boxSizing:"border-box",background:"#FFFDFD",color:"#4A4A68",transition:"border 0.3s"}}
      />
      {open && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:4,background:"#fff",border:"2px solid var(--c-light)",borderRadius:16,boxShadow:"0 8px 24px rgba(31,41,55,0.12)",maxHeight:280,overflowY:"auto",zIndex:50}}>
          {results.length === 0 ? (
            <p style={{margin:0,padding:"14px 16px",fontSize:13,color:"#8A8A9E",fontWeight:700}}>{zhEn("未找到符合品種", "No matching breed found")}</p>
          ) : (
            results.slice(0, 50).map(b => {
              const isSel = b.id === value;
              return (
                <button key={b.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(b); }}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",padding:"10px 14px",border:"none",background:isSel?"var(--c-lighter)":"transparent",cursor:"pointer",textAlign:"left",borderBottom:"1px solid var(--c-light)"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:2,minWidth:0,flex:1}}>
                    <span style={{fontSize:14,fontWeight:800,color:"#4A4A68",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{breedLabel(b, lang)}</span>
                    <span style={{fontSize:11,fontWeight:700,color:"#AFAFC4",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{String(lang).startsWith("en") ? b.zh : b.en}{b.coatHint ? " · " + (b.coatHint === "long" ? zhEn("長毛", "Long coat") : b.coatHint === "medium" ? zhEn("中毛", "Medium coat") : zhEn("短毛", "Short coat")) : ""}</span>
                  </div>
                  {isSel && <span style={{fontSize:11,fontWeight:900,color:"var(--c-primary)"}}>✓</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
const StatusBadge = ({ status, cancelReason, approvalPending }) => {
  // Approval mode: a pending booking awaiting the shop's accept/reject shows
  // an amber 待確認 badge instead of 待開始 (both sides see the same truth).
  if (approvalPending && status === "pending") {
    return <span style={{fontSize:12,fontWeight:800,padding:"4px 12px",borderRadius:99,background:"#FEF3C7",color:"#B45309"}}>{"⏳ " + i18nT("status.awaitApproval", "待確認")}</span>;
  }
  // #15: a "cancelled" appointment that was actually a reschedule (cancel
  // reason starts with "客戶改期至 ...") deserves a distinct visual so
  // staff don't confuse it with a true cancellation. Same status code,
  // different badge.
  const isReschedule = status === "cancelled" && typeof cancelReason === "string" && cancelReason.startsWith("客戶改期至");
  // Labels run through i18nT so EN customers see "Pending / Checked in /
  // Grooming / Done / Cancelled / No-show". Color palette stays the same.
  const cfg = {
    pending:   { bg:"var(--c-light)", color:"var(--c-primary)", text:i18nT("status.pending") },
    checkedin: { bg:"#DBEAFE", color:"#2563EB", text:i18nT("status.checkedin") },
    grooming:  { bg:"#E0F2FE", color:"#3B82F6", text:i18nT("status.grooming") },
    done:      { bg:"#ECFDF5", color:"#10B981", text:i18nT("status.done") },
    cancelled: { bg:"#FEF2F2", color:"#EF4444", text:i18nT("status.cancelled") },
    noshow:    { bg:"#FFFBEB", color:"#D97706", text:i18nT("status.noshow") },
  };
  if (isReschedule) {
    return <span title={cancelReason} style={{fontSize:12,fontWeight:800,padding:"4px 12px",borderRadius:99,background:"#EFF6FF",color:"#1D4ED8"}}>{zhEn("↻ 已改期", "↻ Rescheduled")}</span>;
  }
  const c = cfg[status] || cfg.pending;
  return <span style={{fontSize:12,fontWeight:800,padding:"4px 12px",borderRadius:99,background:c.bg,color:c.color}}>{c.text}</span>;
};

// Payment status badge — derives from appointment + tenant deposit policy.
// Returns null when there's nothing meaningful to show (no deposit required,
// no QR flow set up). On the shop's calendar / list cards, this badge sits
// beside the StatusBadge so staff can see at a glance which bookings are
// waiting on deposit verification.
const StatCard = ({ icon:Icon, label, value, sub, color, bg }) => {
  const col = color || "var(--c-primary)";
  const isVar = col.startsWith("var");
  const background = bg || (isVar ? col.replace(")", "-15)") : col + "15");
  return (
    <Card style={{padding:20}}>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        <div style={{width:48,height:48,borderRadius:16,background,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <Icon size={24} color={col} strokeWidth={2.5} />
        </div>
        <div>
          <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{label}</p>
          <p style={{fontSize:22,fontWeight:900,color:"#4A4A68",margin:0,lineHeight:1.2}}>{value}</p>
          {sub && <p style={{fontSize:12,color:"#AFAFC4",margin:0,fontWeight:600}}>{sub}</p>}
        </div>
      </div>
    </Card>
  );
};
const Toggle = ({ on, onChange }) => (
  <button onClick={() => onChange(!on)} style={{width:52,height:32,borderRadius:99,background:on?"var(--c-primary)":"#EAEAF2",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:on?"flex-end":"flex-start",padding:4,transition:"all 0.3s",flexShrink:0}}>
    <div style={{width:24,height:24,borderRadius:99,background:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,0.15)",transition:"all 0.3s"}} />
  </button>
);
// ── Shop-owner AI assistant (floating bubble, read-only answer + find) ──────
// 專業/旗艦 feature. A dismissible chat bubble in the bottom-right corner; the
// owner asks how-to questions or looks things up. Server (shop-assistant edge
// fn) re-enforces plan gating. Read-only — for changes it points to the screen.
function ShopAssistantWidget({ user, tenant }) {
  const _voiceLoc = i18nUseLocale();
  const [open, setOpen] = useState(false);
  // Owner can fully hide the bubble; a slim edge handle brings it back. Remember
  // the choice (localStorage; on native it resets to visible after a cold
  // launch, which is a fine default).
  // Collapsed by DEFAULT (slim edge tab) so it never blocks the view; the owner
  // taps the tab to expand it straight into the chat panel. Choice is remembered.
  const [hidden, setHidden] = useState(() => { try { const v = localStorage.getItem("petos:waAsst.hidden"); return v === null ? true : v === "1"; } catch { return true; } });
  const hide = () => { setOpen(false); setHidden(true); try { localStorage.setItem("petos:waAsst.hidden", "1"); } catch { /* ignore */ } };
  const unhide = () => { setHidden(false); setOpen(true); try { localStorage.setItem("petos:waAsst.hidden", "0"); } catch { /* ignore */ } };
  const [msgs, setMsgs] = useState([]); // {role:'user'|'model', text}
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  // Voice-input hooks MUST live above the `if (hidden) return` early-return
  // below — React forbids hooks after a conditional return.
  const [listening, setListening] = useState(false);
  const heardRef = useRef("");
  const listenersRef = useRef([]);
  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs.length, busy, open]);

  // Hidden → show only a slim tab peeking from the right edge to restore it.
  if (hidden) {
    return (
      <button onClick={unhide} title={T("顯示 AI 助手","Show AI assistant","AIアシスタントを表示")}
        style={{position:"fixed",right:0,bottom:"calc(160px + env(safe-area-inset-bottom,0px))",zIndex:60,width:26,height:46,borderRadius:"14px 0 0 14px",border:"none",cursor:"pointer",
          background:"linear-gradient(135deg,var(--c-grad1,#a78bfa),var(--c-primary,#7c3aed))",color:"#fff",boxShadow:"0 6px 18px rgba(124,58,237,0.4)",display:"flex",alignItems:"center",justifyContent:"center",opacity:0.9}}>
        <Sparkles size={15} />
      </button>
    );
  }

  const suggestions = [
    T("今日有幾多預約？","How many bookings today?","今日の予約は何件？"),
    T("本月生意額？","This month's revenue?","今月の売上は？"),
    T("點樣改營業時間？","How do I change opening hours?","営業時間の変更方法は？"),
  ];

  const ask = async (q) => {
    const body = (q ?? text).trim();
    if (!body || busy) return;
    const next = [...msgs, { role: "user", text: body }];
    setMsgs(next);
    setText("");
    setBusy(true);
    try {
      const res = await askShopAssistant({ tenantId: user.tenantId, messages: next });
      setMsgs(m => [...m, { role: "model", text: res?.reply || T("暫時答唔到，請再試。","No answer, please retry.","回答できませんでした。") }]);
    } catch (e) {
      setMsgs(m => [...m, { role: "model", text: e?.message || T("發生錯誤，請再試。","Something went wrong.","エラーが発生しました。"), error: true }]);
    } finally { setBusy(false); }
  };

  // ── Voice input (native only) ─────────────────────────────────────────────
  // WKWebView has no Web Speech API, so we use the native speech-recognition
  // plugin: tap 🎤 → speak → live transcript fills the box → tap again to stop
  // and it auto-sends. iOS also auto-stops on silence (listeningState).
  // NOTE: the listening/heardRef/listenersRef hooks are declared ABOVE the
  // hidden early-return; only non-hook helpers live here.
  const voiceOn = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
  const speechLang = _voiceLoc === "ja" ? "ja-JP" : _voiceLoc === "en" ? "en-US" : "zh-HK";

  const stopVoice = async (send) => {
    try { const { SpeechRecognition } = await import("@capacitor-community/speech-recognition"); await SpeechRecognition.stop().catch(() => {}); } catch { /* ignore */ }
    listenersRef.current.forEach(h => { try { h?.remove?.(); } catch { /* ignore */ } });
    listenersRef.current = [];
    setListening(false);
    const heard = (heardRef.current || "").trim();
    if (send && heard) ask(heard);
  };

  const startVoice = async () => {
    if (!voiceOn) return;
    try {
      const { SpeechRecognition } = await import("@capacitor-community/speech-recognition");
      let perm = await SpeechRecognition.checkPermissions().catch(() => null);
      if (!perm || perm.speechRecognition !== "granted") perm = await SpeechRecognition.requestPermissions().catch(() => null);
      if (!perm || perm.speechRecognition !== "granted") { setMsgs(m => [...m, { role: "model", text: T("請喺設定開啟麥克風同語音權限。","Enable microphone & speech permission in Settings.","設定でマイクと音声認識を許可してください。"), error: true }]); return; }
      heardRef.current = "";
      const p1 = await SpeechRecognition.addListener("partialResults", (data) => {
        const m = data?.matches?.[0];
        if (m != null) { heardRef.current = m; setText(m); }
      });
      const p2 = await SpeechRecognition.addListener("listeningState", (d) => {
        if (d?.status === "stopped" && listenersRef.current.length) stopVoice(true);
      });
      listenersRef.current = [p1, p2];
      setListening(true);
      SpeechRecognition.start({ language: speechLang, partialResults: true, popup: false }).catch(() => stopVoice(false));
    } catch { setListening(false); }
  };

  const toggleVoice = () => { if (listening) stopVoice(true); else startVoice(); };

  return (
    <>
      {/* Bubble (with a small × to fully hide it when the panel is closed) */}
      <div style={{position:"fixed",right:18,bottom:"calc(150px + env(safe-area-inset-bottom,0px))",zIndex:60,width:56,height:56}}>
        {!open && (
          <button onClick={hide} title={T("隱藏","Hide","非表示")}
            style={{position:"absolute",top:-4,right:-4,zIndex:1,width:22,height:22,borderRadius:99,border:"2px solid #fff",cursor:"pointer",background:"#6B7280",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
            <X size={12} strokeWidth={3} />
          </button>
        )}
        <button onClick={() => setOpen(o => !o)} title={T("AI 助手","AI assistant","AIアシスタント")}
          style={{width:56,height:56,borderRadius:99,border:"none",cursor:"pointer",
            background:"linear-gradient(135deg,var(--c-grad1,#a78bfa),var(--c-primary,#7c3aed))",color:"#fff",boxShadow:"0 12px 32px rgba(124,58,237,0.45)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          {open ? <X size={24} /> : <Sparkles size={24} />}
        </button>
      </div>

      {/* Panel */}
      {open && (
        <div style={{position:"fixed",right:16,bottom:"calc(216px + env(safe-area-inset-bottom,0px))",zIndex:60,width:"min(360px, calc(100vw - 32px))",height:"min(460px, calc(100vh - 320px))",
          background:"#FFFDFD",borderRadius:20,boxShadow:"0 20px 60px rgba(0,0,0,0.25)",border:"1px solid var(--c-light)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{padding:"12px 14px",background:"linear-gradient(135deg,var(--c-grad1,#a78bfa),var(--c-primary,#7c3aed))",color:"#fff",display:"flex",alignItems:"center",gap:8}}>
            <Sparkles size={18} />
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:900,fontSize:14}}>{T("店主 AI 助手","Shop AI assistant","店主AIアシスタント")}</div>
              <div style={{fontSize:10,opacity:0.9,fontWeight:700}}>{T("查詢 · 預約 · 改期（可講嘢）","Ask · book · reschedule (voice)","照会・予約・変更（音声可）")}</div>
            </div>
            <button onClick={hide} title={T("收起","Collapse","閉じる")} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",padding:2,display:"flex"}}><X size={18} /></button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"12px",display:"flex",flexDirection:"column",gap:8}}>
            {msgs.length === 0 && (
              <div style={{color:"#8A8A9E",fontSize:13,fontWeight:600,lineHeight:1.6}}>
                <div style={{marginBottom:8}}>{T("你好！問我關於店舖嘅嘢，例如：","Hi! Ask me about your shop, e.g.:","こんにちは！お店について聞いてください：")}</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {suggestions.map((s,i) => (
                    <button key={i} onClick={() => ask(s)} style={{textAlign:"left",padding:"8px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",background:"var(--c-lighter,#faf7ff)",color:"var(--c-primary)",fontWeight:700,fontSize:12.5,cursor:"pointer"}}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m,i) => (
              <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                <div style={{maxWidth:"85%",padding:"9px 12px",borderRadius:14,fontSize:13.5,fontWeight:600,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word",
                  background:m.role==="user"?"var(--c-primary)":(m.error?"#FEF2F2":"#F3F0FF"),color:m.role==="user"?"#fff":(m.error?"#B91C1C":"#3A3A5C"),
                  borderBottomRightRadius:m.role==="user"?4:14,borderBottomLeftRadius:m.role==="user"?14:4}}>{m.text}</div>
              </div>
            ))}
            {busy && <div style={{display:"flex",alignItems:"center",gap:6,color:"#8A8A9E",fontSize:12,fontWeight:700}}><Loader size={14} className="spin" /> {T("諗緊…","Thinking…","考え中…")}</div>}
            <div ref={bottomRef} />
          </div>
          <div style={{display:"flex",gap:8,padding:"10px",borderTop:"1px solid var(--c-light)"}}>
            <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder={listening ? T("聆聽緊…請講嘢","Listening… speak now","聞き取り中…") : T("輸入或講嘢…","Type or speak…","入力または音声…")}
              style={{flex:1,padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:13.5,fontWeight:600,outline:"none"}} />
            {voiceOn && (
              <button onClick={toggleVoice} title={T("語音輸入","Voice input","音声入力")}
                style={{width:40,minWidth:40,borderRadius:12,border:"1.5px solid "+(listening?"#FCA5A5":"var(--c-light)"),cursor:"pointer",
                  background:listening?"#FEE2E2":"#fff",color:listening?"#DC2626":"var(--c-primary)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {listening ? <Loader size={16} className="spin" /> : <Mic size={16} />}
              </button>
            )}
            <Btn size="sm" disabled={busy || !text.trim()} onClick={() => ask()}><Send size={16} /></Btn>
          </div>
        </div>
      )}
    </>
  );
}

// ── WhatsApp inbox (owner) ─────────────────────────────────────────────────
// Conversations captured by the whatsapp-webhook edge function. Polls while
// open (the wa_* tables are in the realtime publication, but the inbox is a
// low-traffic owner view — a light poll keeps it simple and WKWebView-safe).
// In-app / website chat threads use wa_id "app:<profile id>" (see the
// customer_open_conversation RPC) — no WhatsApp number behind them.
const isAppConv = (c) => String(c?.waId || "").startsWith("app:");

function WaInboxView({ user, state, onOpenCustomer, onOpenAiSettings }) {
  // Resolve a linked customer (state.users[profileId]) → avatar + pets, so the
  // owner sees WHO they're chatting with (photo, name, dog name + breed).
  const custCard = (conv) => {
    const cu = conv?.profileId ? state?.users?.[conv.profileId] : null;
    const pets = (cu?.pets || []).map(pid => state?.pets?.[pid]).filter(Boolean);
    const avatarUrl = cu?.avatar || pets.find(p => p?.photos?.[0]?.url)?.photos?.[0]?.url || null;
    return { cu, pets, avatarUrl };
  };
  const petsLabel = (pets) => pets.map(p => p.name + (p.breed ? " · " + p.breed : "")).join("、");
  // iOS Safari/WebView cannot decode Opus (WhatsApp's voice format) — its player
  // just shows 0:00. Detect support so we can hide the dead player there and
  // show the transcript + download link instead.
  const canPlayOpus = useMemo(() => {
    try { return document.createElement("audio").canPlayType("audio/ogg; codecs=opus") !== ""; } catch { return false; }
  }, []);
  const [convos, setConvos] = useState(null);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  // Shop-wide AI auto-reply on/off (tenant_messaging_secrets.wa_ai_enabled),
  // toggled from the inbox header. null = still loading.
  const [aiOn, setAiOn] = useState(null);
  // WATI echoes our OWN outbound text sends back via its webhook (recorded as a
  // staff msg). So for WATI we must NOT also insert the staff msg from here or
  // it doubles. providerRef tells send() whether to skip the local insert.
  const providerRef = useRef(null);
  useEffect(() => { fetchMessagingSecrets(user.tenantId).then(s => { setAiOn(!!s?.waAiEnabled); providerRef.current = s?.waProvider || null; }).catch(() => setAiOn(null)); }, [user.tenantId]);
  useEffect(() => {
    const onAiChanged = (ev) => { if (ev?.detail?.tenantId === user.tenantId) setAiOn(!!ev.detail.waAiEnabled); };
    window.addEventListener("petos:wa-ai-changed", onAiChanged);
    return () => window.removeEventListener("petos:wa-ai-changed", onAiChanged);
  }, [user.tenantId]);
  // Reopen-with-template (past the 24h WhatsApp window). Approved templates are
  // fetched lazily on first need; picker shows when >1 exists.
  const [tmpls, setTmpls] = useState(null); // null=unloaded, []=none, [...]
  const [tmplPick, setTmplPick] = useState(false);
  const [paramForm, setParamForm] = useState(null); // { tmpl, values:{name:val} }
  const WINDOW_MS = 24 * 60 * 60 * 1000;
  const windowClosed = !isAppConv(sel) && (sel && (!sel.lastInboundAt || (Date.now() - new Date(sel.lastInboundAt).getTime() > WINDOW_MS)));  // the 24h rule is WhatsApp's; app/web chats have none
  // A template with {{n}} slots opens a fill-in form first; a fixed one sends now.
  const chooseTemplate = (tmpl) => {
    if (tmpl?.params?.length) {
      const values = {};
      tmpl.params.forEach(p => { values[p.name] = p.default || ""; });
      setTmplPick(false);
      setParamForm({ tmpl, values });
    } else {
      sendTemplate(tmpl);
    }
  };
  const sendTemplate = async (tmpl, values) => {
    if (!sel || !tmpl?.name || sending) return;
    setTmplPick(false); setParamForm(null);
    const templateParams = (tmpl.params || []).map(p => ({ name: p.name, value: (values?.[p.name] ?? p.default ?? "").trim() }));
    const tmpId = "tmp-" + Date.now();
    setMsgs(m => [...m, { id: tmpId, direction: "out", sender: "staff", body: "📋 " + T("已發送範本","Template sent","テンプレート送信") + zhEn("：", ": ") + tmpl.name, createdAt: new Date().toISOString(), pending: true }]);
    setSending(true);
    try {
      const res = await sendWhatsApp({ tenantId: user.tenantId, to: sel.waId, template: tmpl.name, templateParams });
      if (!res?.ok) throw new Error(res?.error || res?.raw?.info || T("發送失敗","Send failed","送信に失敗しました"));
      setMsgs(m => m.map(x => x.id === tmpId ? { ...x, pending: false } : x));
      toast(T("已發送重開範本，客戶回覆後即可正常對話","Reopen template sent — you can chat once they reply","テンプレート送信。返信後に会話できます"));
    } catch (e) {
      setMsgs(m => m.filter(x => x.id !== tmpId));
      toast(e?.message || T("發送失敗","Send failed","送信に失敗しました"), "error");
    } finally { setSending(false); }
  };
  const openReopen = async () => {
    let list = tmpls;
    if (list === null) {
      try { const r = await listWaTemplates(user.tenantId); list = r?.templates || []; setTmpls(list); }
      catch { toast(T("無法載入範本","Couldn't load templates","テンプレート読込失敗"), "error"); return; }
    }
    if (!list.length) { toast(T("未有已批核範本，請先喺 WATI 建立並批核","No approved template yet — create + approve one in WATI","承認済みテンプレートがありません"), "warning"); return; }
    if (list.length === 1) { chooseTemplate(list[0]); return; }
    setTmplPick(true);
  };
  const toggleAi = async () => {
    const next = !aiOn;
    setAiOn(next);
    try {
      await upsertMessagingSecrets(user.tenantId, { waAiEnabled: next });
      // Read it back. This switch decides whether customers get answered by a
      // machine or by nobody, so "the request didn't throw" is not good enough
      // — confirm the value is actually what the shop now believes it is.
      const check = await fetchMessagingSecrets(user.tenantId);
      if (!!check?.waAiEnabled !== next) throw new Error(T("設定未能儲存，請再試","Setting did not save — please retry","保存できませんでした"));
      // 設定 → 通訊 holds its own copy of this row and saves the WHOLE form.
      // Without this it keeps the stale value and silently writes AI back off
      // the next time the owner saves any communication setting.
      try { window.dispatchEvent(new CustomEvent("petos:wa-ai-changed", { detail: { tenantId: user.tenantId, waAiEnabled: next } })); } catch { /* ignore */ }
      toast(next ? T("AI 自動回覆已開 🤖","AI auto-reply ON 🤖","AI自動返信 ON") : T("AI 自動回覆已關 — 由你人手回覆","AI auto-reply OFF","AI自動返信 OFF"));
    }
    catch (e) { setAiOn(!next); toast(e?.message || T("設定失敗","Failed","失敗"), "error"); }
  };

  const loadConvos = useCallback(() => {
    fetchWaConversations(user.tenantId).then(setConvos).catch(() => setConvos(c => c || []));
  }, [user.tenantId]);
  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 15000); return () => clearInterval(t); }, [loadConvos]);

  // Merge server messages with any still-unacked optimistic bubbles (tmp-*)
  // that the server hasn't reflected YET — otherwise the 8s poll wholesale-
  // replaces the list and a just-sent message vanishes for a few seconds until
  // the next poll (the flicker). Once the server includes it (matched by
  // direction+body), the tmp is dropped so there's no duplicate.
  const mergeInto = (server) => setMsgs(cur => {
    const seen = new Set((server || []).map(m => m.direction + "|" + (m.body || "")));
    const keptTmp = (cur || []).filter(m => String(m.id).startsWith("tmp-") && !seen.has(m.direction + "|" + (m.body || "")));
    return [...(server || []), ...keptTmp];
  });
  useEffect(() => {
    if (!sel) return;
    let alive = true;
    const load = () => fetchWaMessages(sel.id).then(m => { if (alive) mergeInto(m); }).catch(() => {});
    load();
    markWaConversationRead(sel.id).catch(() => {});
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [sel]);
  // NOTE: no visibilitychange/focus resume listener here — the app's own resume
  // logic (useAppState) handles reopen; adding inbox-level resume refresh
  // regressed iOS (froze on reopen). The 15s/8s polls refresh on foreground.
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs.length]);

  // When a human takes over an AI thread, send a ONE-TIME "X is now assisting
  // you" line as its OWN message (like a bank chat's grey system notice) — the
  // actual replies stay clean, with no name prefixed on each message. WhatsApp
  // has no real grey/system styling, so a standalone short line is the closest.
  const _tenant = state.tenants?.find(t => t.id === user.tenantId);
  const signOn = _tenant?.messagingConfig?.waSignStaff !== false;
  const staffName = (user?.name || "").trim();
  // Label shown in the handoff notice: the shop's custom label (e.g. 客服 /
  // 同事 / a specific name) wins; blank → the logged-in staff's real name.
  const handoffLabel = ((_tenant?.messagingConfig?.waHandoffName || "").trim()) || staffName;
  // In-app / website chats ("app:<profile id>") have no WhatsApp number —
  // send-whatsapp can't deliver them, so the customer never saw the shop's
  // reply. Record those straight into the thread (the customer reads it in
  // the app / web chat) and push the customer instead.
  const sendToApp = async (conv, body, extra = {}) => {
    await insertWaStaffMessage({ conversationId: conv.id, tenantId: user.tenantId, body, ...extra });
    if (conv.profileId) {
      const shopName = (state?.tenants || []).find(t => t.id === user.tenantId)?.name || zhEn("店舖", "Shop");
      pushNotification({ id: uuid(), tenantId: user.tenantId, userId: conv.profileId, type: "chat",
        title: "💬 " + shopName, body: String(body || "📷").slice(0, 140), read: false }).catch(() => {});
    }
    return { ok: true };
  };
  const emitHandoffNotice = async () => {
    if (!signOn || !handoffLabel) return;
    const notice = `👩‍💼 ${handoffLabel}${T(" 現在為你服務 😊"," is now assisting you 😊"," が対応します 😊")}`;
    const nid = "ntc-" + Date.now();
    setMsgs(m => [...m, { id: nid, direction: "out", sender: "staff", body: notice, createdAt: new Date().toISOString(), pending: true }]);
    try {
      if (isAppConv(sel)) { await sendToApp(sel, notice); setMsgs(m => m.map(x => x.id === nid ? { ...x, pending: false } : x)); return; }
      const nres = await sendWhatsApp({ tenantId: user.tenantId, to: sel.waId, text: notice });
      if (nres?.ok && providerRef.current !== "wati") {
        await insertWaStaffMessage({ conversationId: sel.id, tenantId: user.tenantId, body: notice, waMessageId: nres.externalId });
      }
      setMsgs(m => m.map(x => x.id === nid ? { ...x, pending: false } : x));
    } catch { setMsgs(m => m.filter(x => x.id !== nid)); /* best-effort */ }
  };
  const send = async () => {
    const body = text.trim();
    if (!body || !sel || sending) return;
    const isHandoff = sel.mode === "ai";
    setText("");
    setSending(true);
    try {
      // Handoff notice first (its own bubble), then the plain reply.
      if (isHandoff) await emitHandoffNotice();
      // Optimistic UI: show the reply bubble instantly; reconcile on success.
      const tmpId = "tmp-" + Date.now();
      setMsgs(m => [...m, { id: tmpId, direction: "out", sender: "staff", body, createdAt: new Date().toISOString(), pending: true }]);
      try {
        if (isAppConv(sel)) {
          await sendToApp(sel, body);
          setMsgs(m => m.map(x => x.id === tmpId ? { ...x, pending: false } : x));
          if (sel.mode === "ai") { await setWaConversationMode(sel.id, "human").catch(() => {}); setSel(s => s && { ...s, mode: "human" }); }
          return;
        }
        const res = await sendWhatsApp({ tenantId: user.tenantId, to: sel.waId, text: body });
        if (!res?.ok) throw new Error(res?.raw?.error?.message || res?.error || T("發送失敗","Send failed","送信に失敗しました"));
        // WATI records this send via its outbound echo webhook — inserting here
        // too would double it. Other providers don't echo, so we insert locally.
        if (providerRef.current !== "wati") {
          await insertWaStaffMessage({ conversationId: sel.id, tenantId: user.tenantId, body, waMessageId: res.externalId });
        }
        setMsgs(m => m.map(x => x.id === tmpId ? { ...x, pending: false } : x));
      } catch (e) {
        setMsgs(m => m.filter(x => x.id !== tmpId));
        setText(body);
        toast(e?.message || T("發送失敗","Send failed","送信に失敗しました"), "error");
        return;
      }
      // A manual reply means a human owns this thread now — pause the AI so
      // it doesn't talk over the staff member.
      if (sel.mode === "ai") {
        await setWaConversationMode(sel.id, "human").catch(() => {});
        setSel(s => s && { ...s, mode: "human" });
      }
    } finally { setSending(false); }
  };

  // Send a photo: upload to storage → send via WhatsApp → record. Optimistic
  // preview via a local object URL, swapped for the hosted URL on success.
  const sendPhoto = async (file) => {
    if (!file || !sel || sending) return;
    if (!file.type?.startsWith("image/")) { toast(T("只支援圖片檔案","Images only","画像のみ対応"), "error"); return; }
    const isHandoff = sel.mode === "ai";
    const tmpId = "tmp-" + Date.now();
    const localUrl = URL.createObjectURL(file);
    setSending(true);
    try {
      if (isHandoff) await emitHandoffNotice();
      setMsgs(m => [...m, { id: tmpId, direction: "out", sender: "staff", body: "", mediaType: file.type, mediaUrl: localUrl, createdAt: new Date().toISOString(), pending: true }]);
      const url = await uploadTenantMedia(user.tenantId, file, "wa");
      if (isAppConv(sel)) {
        await sendToApp(sel, "", { mediaUrl: url, mediaType: file.type });
        setMsgs(m => m.map(x => x.id === tmpId ? { ...x, mediaUrl: url, pending: false } : x));
        if (sel.mode === "ai") { await setWaConversationMode(sel.id, "human").catch(() => {}); setSel(s => s && { ...s, mode: "human" }); }
        return;
      }
      const res = await sendWhatsApp({ tenantId: user.tenantId, to: sel.waId, mediaUrl: url, mediaType: file.type });
      if (!res?.ok) throw new Error(res?.raw?.info || res?.raw?.error?.message || res?.error || T("發送失敗","Send failed","送信に失敗しました"));
      await insertWaStaffMessage({ conversationId: sel.id, tenantId: user.tenantId, body: "", waMessageId: res.externalId, mediaUrl: url, mediaType: file.type });
      setMsgs(m => m.map(x => x.id === tmpId ? { ...x, mediaUrl: url, pending: false } : x));
      if (sel.mode === "ai") { await setWaConversationMode(sel.id, "human").catch(() => {}); setSel(s => s && { ...s, mode: "human" }); }
    } catch (e) {
      setMsgs(m => m.filter(x => x.id !== tmpId));
      toast(e?.message || T("相片發送失敗","Photo send failed","写真の送信に失敗"), "error");
    } finally { setSending(false); }
  };

  const toggleMode = async () => {
    if (!sel) return;
    const next = sel.mode === "ai" ? "human" : "ai";
    try {
      await setWaConversationMode(sel.id, next);
      setSel(s => s && { ...s, mode: next });
      setConvos(cs => (cs || []).map(c => c.id === sel.id ? { ...c, mode: next } : c));
      toast(next === "ai" ? T("已交回 AI 助手接手 🤖","AI assistant re-enabled 🤖","AIアシスタントに戻しました 🤖") : T("AI 已暫停，人手跟進中 🙋","AI paused — staff handling 🙋","AIを停止し、スタッフ対応中 🙋"));
    } catch (e) { toast(e?.message || "Error", "error"); }
  };

  const timeLbl = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const hm = String(d.getHours()).padStart(2,"0") + ":" + String(d.getMinutes()).padStart(2,"0");
    return sameDay ? hm : (d.getMonth()+1) + "/" + d.getDate() + " " + hm;
  };
  // WhatsApp-style date separator label (今日 / 昨日 / M月D日).
  const dayLabel = (iso) => {
    if (!iso) return "";
    const d = new Date(iso); const now = new Date();
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((t0 - dd) / 86400000);
    if (diff === 0) return T("今日","Today","今日");
    if (diff === 1) return T("昨日","Yesterday","昨日");
    return (d.getMonth()+1) + T("月","/","月") + d.getDate() + T("日","","日");
  };

  // ── Thread view ──
  if (sel) {
    return (
      <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 180px)",minHeight:360}}>
        {(() => {
          const { cu, pets, avatarUrl } = custCard(sel);
          const linked = !!(sel.profileId && cu);
          const clickable = linked && typeof onOpenCustomer === "function";
          return (
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 8px",background:"#008069",color:"#fff"}}>
          <button onClick={() => { setSel(null); setMsgs([]); loadConvos(); }} style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"flex",color:"#fff"}}><ChevronLeft size={24} /></button>
          <div onClick={() => { if (clickable) onOpenCustomer(sel.profileId); }}
            style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:10,cursor:clickable?"pointer":"default"}}>
            <div style={{width:38,height:38,flexShrink:0,borderRadius:99,overflow:"hidden",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"#fff",fontSize:16}}>
              {avatarUrl ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} /> : (sel.customerName?.[0] || "💬")}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:800,fontSize:15.5,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"#fff"}}>
                {sel.customerName || (isAppConv(sel) ? "App" : "+" + sel.waId)}
              </div>
              {pets.length > 0 ? (
                <div style={{fontSize:11.5,color:"rgba(255,255,255,0.85)",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🐾 {petsLabel(pets)}{clickable ? " ›" : ""}</div>
              ) : (
                <div style={{fontSize:11,color:"rgba(255,255,255,0.8)",fontWeight:600}}>+{sel.waId}{linked ? " · " + T("已配對","Linked","連携") : ""}</div>
              )}
            </div>
          </div>
          <button onClick={toggleMode} style={{border:"none",cursor:"pointer",borderRadius:99,padding:"5px 11px",fontSize:11.5,fontWeight:800,background:sel.mode==="ai"?"rgba(255,255,255,0.9)":"#FEF3C7",color:sel.mode==="ai"?"#065F46":"#92400E",flexShrink:0}}>
            {sel.mode === "ai" ? T("🤖 AI","🤖 AI","🤖 AI") : T("🙋 人手","🙋 Staff","🙋 手動")}
          </button>
        </div>
          );
        })()}
        <div style={{flex:1,overflowY:"auto",padding:"14px 8px",display:"flex",flexDirection:"column",gap:5,background:`#ECE5DD url("${WA_CHAT_DOODLE}")`}}>
          {msgs.map((m, i) => {
            const _dk = m.createdAt ? new Date(m.createdAt).toDateString() : "";
            const _pk = (i > 0 && msgs[i-1].createdAt) ? new Date(msgs[i-1].createdAt).toDateString() : "";
            const _sep = _dk && _dk !== _pk;
            return (
            <Fragment key={m.id}>
            {_sep && <div style={{alignSelf:"center",background:"#FFFFFFd0",color:"#54656F",fontSize:11.5,fontWeight:700,padding:"3px 12px",borderRadius:8,margin:"6px 0",boxShadow:"0 1px 0.5px rgba(0,0,0,0.1)"}}>{dayLabel(m.createdAt)}</div>}
            <div style={{display:"flex",justifyContent:m.direction==="in"?"flex-start":"flex-end"}}>
              <div style={{maxWidth:"80%",padding:"6px 9px 5px",borderRadius:9,fontSize:14.5,fontWeight:500,lineHeight:1.4,whiteSpace:"pre-wrap",wordBreak:"break-word",color:"#111B21",boxShadow:"0 1px 0.5px rgba(0,0,0,0.13)",
                background:m.direction==="in"?"#FFFFFF":(m.sender==="ai"?"#EAE0F7":"#D9FDD3"),
                borderBottomLeftRadius:m.direction==="in"?2:9,borderBottomRightRadius:m.direction==="in"?9:2}}>
                {(() => {
                  const mt = (m.mediaType || "").toLowerCase();
                  const isAudio = /audio|voice|ptt|opus|ogg|mp3|m4a|amr/.test(mt);
                  const isImage = /image|photo|jpg|jpeg|png|webp|gif/.test(mt);
                  const isVideo = /video|mp4|mov/.test(mt);
                  if (m.mediaUrl && isAudio) {
                    const isOpus = /ogg|opus/.test(mt);
                    const playable = !isOpus || canPlayOpus; // iOS can't decode opus
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {playable && <audio controls src={m.mediaUrl} style={{maxWidth:"100%",height:36}} />}
                        <span style={{fontSize:13.5}}>🎤 {m.body || T("語音訊息","Voice message","音声メッセージ")}</span>
                        <a href={m.mediaUrl} target="_blank" rel="noreferrer" style={{fontSize:11,color:"var(--c-primary)",fontWeight:800}}>{playable ? T("下載語音","Download","ダウンロード") : T("下載語音（喺電腦/Android 可播放）","Download (plays on desktop/Android)","ダウンロード")}</a>
                      </div>
                    );
                  }
                  if (m.mediaUrl && isImage) return <a href={m.mediaUrl} target="_blank" rel="noreferrer"><img src={m.mediaUrl} alt="" style={{maxWidth:"100%",borderRadius:10,display:"block"}} /></a>;
                  if (m.mediaUrl && isVideo) return <video controls src={m.mediaUrl} style={{maxWidth:"100%",borderRadius:10}} />;
                  if (m.mediaUrl) return <a href={m.mediaUrl} target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800}}>📎 {m.body || T("開啟附件","Open attachment","添付を開く")}</a>;
                  // No URL — at least surface a clear placeholder / transcript.
                  if (isAudio) return <span>🎤 {m.body || T("語音訊息","Voice message","音声メッセージ")}</span>;
                  return m.body;
                })()}
                <div style={{fontSize:10.5,color:"#667781",fontWeight:600,marginTop:2,textAlign:"right"}}>
                  {m.direction==="out" ? (m.sender==="ai" ? "🤖 AI · " : "") : ""}{timeLbl(m.createdAt)}{m.direction==="out" && m.status!=="failed" ? " ✓✓" : ""}{m.status==="failed" ? " ⚠️" : ""}
                </div>
              </div>
            </div>
            </Fragment>
            );
          })}
          <div ref={bottomRef} />
        </div>
        {/* 24h window closed → free text is blocked by WhatsApp; offer a
            one-tap approved-template send to reopen the conversation. */}
        {windowClosed && (
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"#FEF3C7",borderTop:"1px solid #FDE68A"}}>
            <span style={{fontSize:12,fontWeight:800,color:"#92400E",flex:1,lineHeight:1.4}}>
              ⏰ {T("客戶已超過 24 小時未回覆，需用範本重開對話","Past the 24-hour window — reopen with an approved template","24時間経過 — テンプレートで再開")}
            </span>
            <button onClick={openReopen} disabled={sending}
              style={{flexShrink:0,padding:"8px 14px",borderRadius:99,border:"none",cursor:sending?"default":"pointer",background:"#D97706",color:"#fff",fontSize:12.5,fontWeight:900}}>
              {T("用範本重開","Reopen","再開")}
            </button>
          </div>
        )}
        {tmplPick && (
          <div onClick={() => setTmplPick(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:60,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
            <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:"24px 24px 0 0",width:"100%",maxWidth:480,maxHeight:"70vh",overflowY:"auto",padding:"18px 16px calc(18px + env(safe-area-inset-bottom,0px))"}}>
              <p style={{fontWeight:900,fontSize:16,color:"#4A4A68",margin:"0 0 4px"}}>{T("揀一個範本重開對話","Pick a template to reopen","テンプレートを選択")}</p>
              <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 14px"}}>{T("客戶回覆後即可正常自由對話","Once they reply, free chat reopens for 24h","返信後に自由な会話が再開")}</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {(tmpls || []).map(t => (
                  <button key={t.name} onClick={() => chooseTemplate(t)} disabled={sending}
                    style={{textAlign:"left",padding:"12px 14px",borderRadius:14,border:"2px solid var(--c-light)",background:"#FFFDFD",cursor:sending?"default":"pointer"}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>{t.name} <span style={{fontSize:10,fontWeight:800,color:"#8A8A9E"}}>{t.language} · {t.category}{t.params?.length ? " · " + T("需填寫","fill-in","入力") : ""}</span></div>
                    {t.body && <div style={{fontSize:12,color:"#8A8A9E",fontWeight:600,marginTop:3,lineHeight:1.4}}>{t.body}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Variable fill-in — the template's fixed text is locked; only {{n}}
            slots are editable (WhatsApp rule). Body preview shows the wording. */}
        {paramForm && (
          <div onClick={() => setParamForm(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:60,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
            <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:"24px 24px 0 0",width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",padding:"18px 16px calc(18px + env(safe-area-inset-bottom,0px))"}}>
              <p style={{fontWeight:900,fontSize:16,color:"#4A4A68",margin:"0 0 4px"}}>{paramForm.tmpl.name}</p>
              <p style={{fontSize:12.5,color:"#6A6A85",fontWeight:600,margin:"0 0 4px",lineHeight:1.5,whiteSpace:"pre-wrap",background:"#F3F4F6",borderRadius:10,padding:"10px 12px"}}>{paramForm.tmpl.body}</p>
              <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"6px 0 14px"}}>{T("只可填寫 {{ }} 空格，其餘文字已由 WhatsApp 鎖定","Only the {{ }} blanks are editable — the rest is locked by WhatsApp","{{ }}の空欄のみ編集可能")}</p>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {paramForm.tmpl.params.map(p => (
                  <div key={p.name}>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span>{`{{${p.name}}}`}</span>
                      {/name|名/i.test(p.name) && sel?.customerName && (
                        <button onClick={() => setParamForm(f => ({ ...f, values: { ...f.values, [p.name]: sel.customerName } }))}
                          style={{background:"none",border:"none",color:"var(--c-primary)",fontSize:11,fontWeight:800,cursor:"pointer",padding:0}}>{T("填入客戶名","Use customer name","顧客名を入力")}</button>
                      )}
                    </div>
                    <Input value={paramForm.values[p.name] || ""} onChange={e => { const v = e.target.value; setParamForm(f => ({ ...f, values: { ...f.values, [p.name]: v } })); }} placeholder={p.default || p.name} />
                  </div>
                ))}
              </div>
              <Btn onClick={() => sendTemplate(paramForm.tmpl, paramForm.values)} disabled={sending} style={{width:"100%",marginTop:16}}>
                {sending ? T("發送中…","Sending…","送信中…") : T("發送重開範本","Send template","テンプレート送信")}
              </Btn>
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,padding:"8px 8px calc(8px + env(safe-area-inset-bottom,0px))",background:"#F0F2F5",alignItems:"center"}}>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) sendPhoto(f); }} />
          <button onClick={() => fileRef.current?.click()} disabled={sending} title={T("傳送相片","Send photo","写真を送る")}
            style={{flexShrink:0,width:40,height:40,borderRadius:99,border:"none",background:"transparent",cursor:sending?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#54656F"}}>
            <Image size={22} />
          </button>
          <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={T("輸入回覆…","Type a reply…","返信を入力…")}
            style={{flex:1,padding:"11px 16px",borderRadius:99,border:"none",fontSize:15,fontWeight:500,outline:"none",background:"#fff",boxShadow:"0 1px 1px rgba(0,0,0,0.06)"}} />
          <button onClick={send} disabled={sending || !text.trim()} title={T("傳送","Send","送信")}
            style={{flexShrink:0,width:44,height:44,borderRadius:99,border:"none",cursor:(sending||!text.trim())?"default":"pointer",background:(sending||!text.trim())?"#B9C0C6":"#00A884",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 1px 2px rgba(0,0,0,0.2)"}}>
            <Send size={19} />
          </button>
        </div>
      </div>
    );
  }

  // ── Conversation list ──
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"4px 0"}}>
        <h2 style={{fontSize:20,fontWeight:900,margin:0}}>{T("WhatsApp 訊息","WhatsApp Inbox","WhatsAppメッセージ")}</h2>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {/* The shop switch can read ON while the platform-wide AI switch
              (admin → 平台設定) is OFF — the webhook then never replies. Say so,
              instead of a green "AI 開" that does nothing. */}
          {aiOn && state?.platformConfig?.aiConciergeEnabled !== true && (
            <span title={T("平台已暫停 AI 自動回覆（管理員 → 平台設定）","AI auto-reply is paused platform-wide (admin → platform settings)","プラットフォームでAIが停止中")}
              style={{fontSize:11,fontWeight:900,color:"#92400E",background:"#FEF3C7",border:"1.5px solid #FDE68A",borderRadius:99,padding:"4px 10px"}}>
              ⚠️ {T("平台已暫停 AI","AI paused platform-wide","AI停止中")}
            </span>
          )}
          {/* Shop-wide AI auto-reply on/off */}
          <button onClick={toggleAi} disabled={aiOn === null} title={T("AI 自動回覆總開關","AI auto-reply master switch","AI自動返信")}
            style={{display:"flex",alignItems:"center",gap:6,border:"2px solid "+(aiOn?"#A7F3D0":"var(--c-light)"),background:aiOn?"#ECFDF5":"#F3F4F6",borderRadius:99,padding:"6px 12px",cursor:aiOn===null?"default":"pointer",color:aiOn?"#065F46":"#6B7280",fontWeight:800,fontSize:12}}>
            {aiOn === null ? "…" : (aiOn ? T("🤖 AI 開","🤖 AI ON","🤖 ON") : T("AI 關","AI OFF","AI OFF"))}
          </button>
          {typeof onOpenAiSettings === "function" && (
            <button onClick={onOpenAiSettings} title={T("AI 助手設定","AI assistant settings","AI設定")}
              style={{display:"flex",alignItems:"center",gap:6,border:"2px solid var(--c-light)",background:"#FFFDFD",borderRadius:99,padding:"6px 12px",cursor:"pointer",color:"var(--c-primary)",fontWeight:800,fontSize:12}}>
              <Settings size={15} /> {T("設定","Settings","設定")}
            </button>
          )}
        </div>
      </div>
      {convos === null ? (
        <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{T("載入中…","Loading…","読み込み中…")}</p>
      ) : convos.length === 0 ? (
        <Card>
          <div style={{textAlign:"center",padding:"28px 12px"}}>
            <div style={{fontSize:40,marginBottom:10}}>💬</div>
            <div style={{fontWeight:900,fontSize:15,marginBottom:6}}>{T("未有 WhatsApp 對話","No WhatsApp conversations yet","WhatsAppの会話はまだありません")}</div>
            <p style={{fontSize:13,color:"#8A8A9E",fontWeight:600,lineHeight:1.6,margin:0}}>
              {T("客戶 WhatsApp 你店鋪號碼後，對話會自動出現喺度。要接收訊息，請先喺 設定 → 通訊 → 進階設定 填好店鋪嘅 WhatsApp Business 號碼，並聯絡平台完成 webhook 設定。",
                 "Messages customers send to your shop's WhatsApp number will appear here. To receive them, set up your shop's WhatsApp Business number in Settings → Communication → Advanced settings, and contact the platform to finish webhook setup.",
                 "お客様が店舗のWhatsApp番号に送ったメッセージがここに表示されます。設定 → 通信 → 詳細設定 でWhatsApp Business番号を設定し、プラットフォームにWebhook設定を依頼してください。")}
            </p>
          </div>
        </Card>
      ) : convos.map(c => {
        const { pets, avatarUrl } = custCard(c);
        return (
        <Card key={c.id} onClick={() => setSel(c)}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:44,height:44,borderRadius:99,overflow:"hidden",background:avatarUrl?"#eee":"#D1FAE5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
              {avatarUrl ? <img src={avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} /> : "💬"}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:900,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.customerName || (isAppConv(c) ? "App" : "+" + c.waId)}</span>
                {isAppConv(c) && <span title={T("經 App / 網頁對話，唔經 WhatsApp","Chat from the app / website, not WhatsApp","アプリ／ウェブからのチャット")} style={{fontSize:10,fontWeight:900,padding:"2px 7px",borderRadius:99,background:"#E0E7FF",color:"#3730A3",flexShrink:0}}>App</span>}
                <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,background:c.mode==="ai"?"#D1FAE5":"#FEF3C7",color:c.mode==="ai"?"#065F46":"#92400E",flexShrink:0}}>{c.mode==="ai"?"🤖 AI":T("🙋 人手","🙋 Staff","🙋 スタッフ")}</span>
              </div>
              {pets.length > 0 && <div style={{fontSize:11,color:"var(--c-primary)",fontWeight:800,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>🐾 {petsLabel(pets)}</div>}
              <div style={{fontSize:12,color:"#8A8A9E",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2}}>{c.lastMessagePreview || ""}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
              <span style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>{timeLbl(c.lastMessageAt)}</span>
              {c.unreadCount > 0 && <span style={{minWidth:20,height:20,borderRadius:99,background:"var(--c-primary)",color:"#fff",fontSize:11,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 6px"}}>{c.unreadCount}</span>}
            </div>
          </div>
        </Card>
      ); })}
    </div>
  );
}

// Shows this calendar month's AI-reply usage against the platform cap.
function AiUsageBadge({ tenantId, cap }) {
  const [used, setUsed] = useState(null);
  useEffect(() => { fetchAiConciergeUsage(tenantId).then(setUsed).catch(() => setUsed(null)); }, [tenantId]);
  if (used === null) return null;
  const over = cap > 0 && used >= cap;
  const near = cap > 0 && used >= cap * 0.8;
  return (
    <div style={{fontSize:12,fontWeight:800,padding:"7px 11px",borderRadius:10,
      background: over ? "#FEE2E2" : near ? "#FEF3C7" : "#EDE9FE",
      color: over ? "#B91C1C" : near ? "#92400E" : "#5B21B6"}}>
      {T("本月 AI 回覆","AI replies this month","今月のAI返信")}: {used}{cap > 0 ? " / " + cap : ""}
      {over ? " · " + T("已達上限，暫停自動回覆","cap reached — auto-reply paused","上限に達しました") : ""}
    </div>
  );
}

// Match an approved WhatsApp template to each grooming event (confirm /
// reminder / done). The stored mapping { name, params } lets autoSendMessage
// send the template with {{variables}} auto-filled from the booking.
function WaTemplateMatcher({ user, tenant, setState }) {
  const [tmpls, setTmpls] = useState(null); // null=loading, []=none
  const [err, setErr] = useState(false);
  useEffect(() => { listWaTemplates(user.tenantId).then(r => setTmpls(r?.templates || [])).catch(() => setErr(true)); }, [user.tenantId]);
  const EVENTS = [
    ["booking_created", T("📅 預約確認","📅 Booking confirmed","📅 予約確認")],
    ["day_before",      T("⏰ 前一日提醒","⏰ Day-before reminder","⏰ 前日リマインダー")],
    ["grooming_done",   T("🎉 美容完成","🎉 Grooming done","🎉 トリミング完了")],
  ];
  const waT = tenant?.messagingConfig?.waTemplates || {};
  const setTmpl = (trig, name) => {
    const t = (tmpls || []).find(x => x.name === name);
    const next = { ...waT };
    if (name) next[trig] = { name, params: t?.params || [] };
    else delete next[trig];
    const nextMc = { ...(tenant?.messagingConfig || {}), waTemplates: next };
    setState(s => ({ ...s, tenants: s.tenants.map(t2 => t2.id === user.tenantId ? { ...t2, messagingConfig: nextMc } : t2) }));
    patchTenant(user.tenantId, { messagingConfig: nextMc }).catch(() => {});
  };
  return (
    <div style={{background:"#F5F3FF",borderRadius:20,padding:14,border:"2px solid #DDD6FE",marginTop:14}}>
      <p style={{fontSize:13,fontWeight:900,color:"#5B21B6",margin:"0 0 4px",display:"flex",alignItems:"center",gap:6}}>💬 {T("WhatsApp 範本自動通知","WhatsApp template auto-notify","WhatsAppテンプレート自動通知")}</p>
      <p style={{fontSize:11,color:"#7C6FD6",margin:"0 0 10px",fontWeight:700,lineHeight:1.5}}>{T("為每個事件揀一個已批核範本；系統會自動用 WhatsApp 發送，並填入客戶資料。","Pick an approved template per event — sent automatically via WhatsApp with the customer's details filled in.","イベントごとに承認済みテンプレートを選択 — お客様情報を自動入力してWhatsAppで送信。")}</p>
      {tmpls === null && !err && <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:0}}>{T("載入範本中…","Loading templates…","読み込み中…")}</p>}
      {err && <p style={{fontSize:12,color:"#B45309",fontWeight:700,margin:0}}>{T("無法載入範本，請確認上方 WhatsApp 已設定","Couldn't load templates — check WhatsApp is configured above","テンプレート読込失敗")}</p>}
      {tmpls && tmpls.length === 0 && <p style={{fontSize:12,color:"#92400E",fontWeight:700,margin:0,padding:"8px 10px",background:"#FEF3C7",borderRadius:8}}>{T("未有已批核範本，請先喺 WATI 建立並批核（例如 booking_confirm / booking_reminder / grooming_done）","No approved template yet — create + approve them in WATI first","承認済みテンプレートがありません")}</p>}
      {tmpls && tmpls.length > 0 && EVENTS.map(([trig, label]) => (
        <div key={trig} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid #E9E5FB",gap:10}}>
          <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{label}</span>
          <select value={waT[trig]?.name || ""} onChange={e => setTmpl(trig, e.target.value)}
            style={{border:"2px solid #DDD6FE",borderRadius:10,padding:"6px 10px",fontSize:12,fontWeight:800,background:"#fff",color:"#4A4A68",maxWidth:200}}>
            <option value="">{T("（不發 WhatsApp）","（no WhatsApp）","（送信しない）")}</option>
            {tmpls.map(t => <option key={t.name} value={t.name}>{t.name}{t.params?.length ? ` (${t.params.length})` : ""}</option>)}
          </select>
        </div>
      ))}
      {tmpls && tmpls.length > 0 && (
        <p style={{fontSize:10.5,color:"#8A8A9E",fontWeight:700,margin:"10px 0 0",lineHeight:1.5}}>
          {T("自動填入嘅變數名：","Auto-filled variables: ","自動入力変数：")}<code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>name</code> <code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>pet</code> <code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>service</code> <code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>date</code> <code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>time</code> <code style={{background:"#EDE9FE",padding:"1px 4px",borderRadius:4}}>shop</code>
        </p>
      )}
    </div>
  );
}

const Stars = ({ value, onChange, size }) => {
  const sz = size || 18;
  return (
    <div style={{display:"flex",gap:4}}>
      {[1,2,3,4,5].map(i => (
        <button key={i} onClick={() => onChange && onChange(i)} style={{background:"none",border:"none",cursor:onChange?"pointer":"default",padding:2}}>
          <Star size={sz} strokeWidth={onChange?2.5:2} style={{color:i<=value?"#F59E0B":"#EAEAF2",fill:i<=value?"#F59E0B":"#EAEAF2",transition:"all 0.2s"}} />
        </button>
      ))}
    </div>
  );
};
const EmptyState = ({ icon:Icon, title, sub, action }) => (
  <div style={{textAlign:"center",padding:"48px 20px"}}>
    <div style={{width:80,height:80,borderRadius:32,background:"var(--c-light)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
      <Icon size={36} color="var(--c-grad1)" strokeWidth={2.5} />
    </div>
    <p style={{fontWeight:800,fontSize:18,color:"#4A4A68",margin:"0 0 6px"}}>{title}</p>
    {sub && <p style={{fontSize:14,color:"#8A8A9E",margin:"0 0 20px",fontWeight:600}}>{sub}</p>}
    {action}
  </div>
);
const Divider = () => <div style={{height:2,background:"var(--c-light)",margin:"12px 0",borderRadius:99}} />;

const BottomSheet = ({ open, onClose, title, children, z = 200 }) => {
  if (!open) return null;
  // Close is X-button only. Swipe-down (kept hijacking textarea scroll)
  // and backdrop-tap (kept dismissing mid-edit) are both disabled.
  return (
    <div style={{position:"fixed",inset:0,zIndex:z,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
      <div style={{position:"absolute",inset:0,background:"rgba(74,74,104,0.3)",backdropFilter:"blur(8px)"}} />
      <div style={{position:"relative",background:"#FCFCFF",borderRadius:"40px 40px 0 0",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 -12px 48px rgba(0,0,0,0.12)"}}>
        <div style={{padding:"16px 20px 0",textAlign:"center"}}>
          <div style={{width:48,height:6,borderRadius:99,background:"#EAEAF2",margin:"0 auto 16px"}} />
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 24px 16px",flexShrink:0,borderBottom:"2px solid var(--c-light)"}}>
          <h3 style={{fontWeight:800,fontSize:18,margin:0,color:"#4A4A68"}}>{title}</h3>
          <button onClick={onClose} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
            <X size={16} color="var(--c-primary)" strokeWidth={3} />
          </button>
        </div>
        <div style={{overflowY:"auto",flex:1,padding:"24px 24px 96px"}}>{children}</div>
      </div>
    </div>
  );
};

// Dedicated "daily wage entry" sheet for the owner. Pick a date, see EVERY
// part-timer in one list, type the hours (amount auto-fills from hourly
// rate, editable for day-wage / lump-sum), save once. Each (staff, date) is
// upserted into that staffer's wage_log — clearing a row removes its entry
// for the day. This replaces the old flow of opening each staffer's full
// edit form just to log one day.
function DailyWageSheet({ open, onClose, staffList, onPatched, focusStaffId }) {
  const [date, setDate] = useState(dateNow());
  const [drafts, setDrafts] = useState({}); // { [staffId]: { hours, amount, note } }
  const [saving, setSaving] = useState(false);

  // Prefill drafts from each staffer's existing entry for the selected date
  // when the sheet opens or the date changes. Intentionally NOT keyed on
  // staffList (it's a fresh array every parent render) so live edits aren't
  // clobbered while typing.
  useEffect(() => {
    if (!open) return;
    const next = {};
    for (const s of staffList) {
      const ex = (s.wageLog || []).find(r => r && r.date === date);
      next[s.id] = ex
        ? { hours: ex.hours ?? "", amount: ex.amount ?? "", note: ex.note || "" }
        : { hours: "", amount: "", note: "" };
    }
    setDrafts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const shiftDay = (n) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + n);
    setDate(localDateStr(d));
  };
  const upd = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));

  const save = async () => {
    // Validate before submit — no negative / non-numeric hours or amounts.
    if (!date) { toast("請選擇日期", "error"); return; }
    for (const s of staffList) {
      const d = drafts[s.id] || {};
      if (d.hours !== "" && d.hours != null) {
        const h = Number(d.hours);
        if (!Number.isFinite(h) || h < 0) { toast(s.name + "：工時必須係 0 或以上嘅數字", "error"); return; }
        if (h > 24) { toast(s.name + "：工時不可超過 24 小時", "error"); return; }
      }
      if (d.amount !== "" && d.amount != null) {
        const a = Number(d.amount);
        if (!Number.isFinite(a) || a < 0) { toast(s.name + "：工資必須係 0 或以上嘅數字", "error"); return; }
      }
    }
    setSaving(true);
    const ym = date.slice(0, 7);
    let touched = 0;
    try {
      let skippedBeforeJoin = 0;
      for (const s of staffList) {
        const d = drafts[s.id] || {};
        const hrs = parseFloat(d.hours);
        const amtRaw = d.amount === "" || d.amount == null ? null : Number(d.amount);
        let amount;
        if (amtRaw != null && !isNaN(amtRaw)) amount = Math.round(amtRaw);
        else if (!isNaN(hrs) && hrs > 0) amount = Math.round(hrs * (Number(s.hourlyRate) || 0));
        else amount = 0;
        const hasWork = (!isNaN(hrs) && hrs > 0) || amount > 0;
        const existing = (s.wageLog || []).find(r => r && r.date === date);
        if (!hasWork && !existing) continue; // nothing to write or clear
        // Guard: cannot record wage before joinDate or after leaveDate.
        if (s.joinDate && date < s.joinDate) { skippedBeforeJoin++; continue; }
        if (s.leaveDate && date > s.leaveDate) { skippedBeforeJoin++; continue; }
        const others = (s.wageLog || []).filter(r => r && r.date !== date);
        const nextLog = hasWork
          ? [{ date, hours: isNaN(hrs) ? 0 : hrs, amount, note: d.note || "", at: existing?.at || new Date().toISOString() }, ...others]
          : others;
        // One row per staff per day in staff_wage_entries (owner-only table).
        // verified / paidMethod aren't sent, so a re-entry keeps them.
        if (hasWork) await upsertWageEntry({ tenantId: s.tenantId, staffId: s.id, date, hours: isNaN(hrs) ? 0 : hrs, amount, note: d.note || "" });
        else await deleteWageEntry({ staffId: s.id, date });
        onPatched(s.id, nextLog);
        touched++;
      }
      if (skippedBeforeJoin > 0) toast("跳過 " + skippedBeforeJoin + " 位（日期早於入職 / 晚於離職）", "warning");
      toast(touched ? "已記錄 " + touched + " 位員工 " + date + " 工資 ✓" : (skippedBeforeJoin > 0 ? "已跳過所有員工" : "未有改動"));
      if (touched) onClose();
    } catch (e) {
      toast(e?.message || "儲存失敗", "error");
    } finally {
      setSaving(false);
    }
    return ym;
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="紀錄今日薪金 💵">
      {open && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Day navigator */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={() => shiftDay(-1)} title="前一日" style={{background:"var(--c-light)",border:"none",borderRadius:12,width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
              <ChevronLeft size={18} color="var(--c-primary)" strokeWidth={3} />
            </button>
            <input type="date" value={date} max={dateNow()} onChange={e => e.target.value && setDate(e.target.value)} style={{flex:1,border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:15,fontWeight:800,outline:"none",color:"#4A4A68",background:"#fff",textAlign:"center"}} />
            <button onClick={() => shiftDay(1)} disabled={date >= dateNow()} title="後一日" style={{background:"var(--c-light)",border:"none",borderRadius:12,width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",cursor:date>=dateNow()?"not-allowed":"pointer",opacity:date>=dateNow()?0.4:1,flexShrink:0}}>
              <ChevronRight size={18} color="var(--c-primary)" strokeWidth={3} />
            </button>
          </div>

          {staffList.length === 0 && (
            <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"24px 0"}}>未有兼職員工。喺員工資料將職位設為「兼職」並填時薪即可。</p>
          )}

          {[...staffList].sort((a, b) => (b.id === focusStaffId ? 1 : 0) - (a.id === focusStaffId ? 1 : 0)).map(s => {
            const d = drafts[s.id] || { hours:"", amount:"", note:"" };
            const rate = Number(s.hourlyRate) || 0;
            const hrs = parseFloat(d.hours);
            const autoCalc = !isNaN(hrs) && hrs > 0 && rate > 0 ? Math.round(hrs * rate) : 0;
            const ym = date.slice(0, 7);
            const mTotal = sumWageLog(s, ym + "-01", ym + "-31");
            const focused = s.id === focusStaffId;
            return (
              <div key={s.id} style={{background:focused?"#FEF3C7":"#FFFBEB",borderRadius:18,padding:14,border:focused?"2px solid #F59E0B":"2px solid #FDE68A",display:"flex",flexDirection:"column",gap:10,boxShadow:focused?"0 4px 16px rgba(245,158,11,0.25)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <div style={{minWidth:0}}>
                    <p style={{fontSize:15,fontWeight:900,color:"#92400E",margin:0}}>{s.name}</p>
                    <p style={{fontSize:11,color:"#92400E",opacity:0.8,fontWeight:700,margin:"2px 0 0"}}>時薪 ${rate} · 本月累計 ${mTotal.pay.toLocaleString()}（{mTotal.days}日）</p>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div>
                    <p style={{fontSize:10,color:"#92400E",margin:"0 0 3px",fontWeight:700,opacity:0.8}}>工時（h）</p>
                    <input type="number" inputMode="decimal" value={d.hours ?? ""} onChange={e => upd(s.id, { hours: e.target.value })} placeholder="例：5" style={{width:"100%",border:"1.5px solid #FDE68A",borderRadius:10,padding:"10px",fontSize:15,fontWeight:800,outline:"none",color:"#92400E",background:"#fff",textAlign:"right",boxSizing:"border-box"}} />
                  </div>
                  <div>
                    <p style={{fontSize:10,color:"#92400E",margin:"0 0 3px",fontWeight:700,opacity:0.8}}>工資 $ {autoCalc > 0 && (!d.amount || Number(d.amount) === 0) ? <span style={{color:"#D97706"}}>· 建議 ${autoCalc}</span> : null}</p>
                    <input type="number" inputMode="numeric" value={d.amount ?? ""} onChange={e => upd(s.id, { amount: e.target.value })} placeholder={autoCalc > 0 ? String(autoCalc) : "例：500"} style={{width:"100%",border:"1.5px solid #FDE68A",borderRadius:10,padding:"10px",fontSize:15,fontWeight:900,outline:"none",color:"#92400E",background:"#fff",textAlign:"right",boxSizing:"border-box"}} />
                  </div>
                </div>
                <input value={d.note || ""} onChange={e => upd(s.id, { note: e.target.value })} placeholder="備註（選填，例：替班 / 加時 / 半日）" style={{border:"1.5px solid #FDE68A",borderRadius:10,padding:"9px 10px",fontSize:12,fontWeight:700,outline:"none",color:"#92400E",background:"#fff"}} />
              </div>
            );
          })}

          {staffList.length > 0 && (
            <>
              <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:0,lineHeight:1.5}}>💡 填工時會自動建議工資（工時 × 時薪），可手動覆蓋作日薪 / 包薪。留空表示當日冇返工；清空已有紀錄會刪除當日該筆。</p>
              <Btn onClick={save} disabled={saving} style={{width:"100%"}}>
                <Save size={15} strokeWidth={2.5} /> {saving ? "儲存中…" : "儲存 " + date + " 工資"}
              </Btn>
            </>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

// Defensive guard: notifications without a real UUID userId would fail at
// the DB layer with `invalid input syntax for type uuid`. Drop them
// silently rather than letting a stale demo id ("o1", "s1") spam the user
// with "儲存失敗" toasts during a booking flow.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const pushNotif = (setState, notif) => {
  if (!notif?.userId || !UUID_RE.test(String(notif.userId))) {
    // eslint-disable-next-line no-console
    if (typeof console !== "undefined") console.warn("[PettoBook] pushNotif skipped — invalid userId:", notif?.userId);
    return;
  }
  // Stamp createdAt (ISO) so the notification list can sort newest-first
  // consistently across client-added and DB-loaded rows. `time` stays as the
  // local HH:mm for backward compat (rendered in the bell sheet).
  const now = new Date();
  const hhmm = String(now.getHours()).padStart(2, "0") + ":" + String(now.getMinutes()).padStart(2, "0");
  setState(s => ({ ...s, notifications: [...s.notifications, { id:uuid(), time: hhmm, createdAt: now.toISOString(), ...notif }] }));
};

// ── AUTO-SEND ENGINE ───────────────────────────────────────────────────────
// Routes a trigger to Email / SMS based on tenant config; deduped so the
// same (apt, trigger) can't fire twice within 5 minutes (double-tap, race).
const autoSendMessage = async (trigger, data, state, tenant) => {
  if (!_autoSendShouldFire(trigger, data?.aptId)) return;
  // Check platform-level auto-notification is enabled for this trigger
  const autoNotifs = state.autoNotifications || [];
  const rule = autoNotifs.find(n => n.trigger === trigger);
  if (rule && !rule.enabled) return; // platform disabled this trigger

  // Check tenant plan supports autoNotify
  const plan = PLANS[tenant?.plan || "free"];
  if (rule?.paidOnly && !plan?.features?.autoNotify) return;

  // Check tenant-level messaging preferences
  const mc = tenant?.messagingConfig || {};
  const triggerMap = {
    booking_created: mc.autoSendBookingConfirm,
    grooming_done: mc.autoSendGroomingDone,
    booking_cancelled: mc.autoSendCancel,
    day_before: mc.autoSendReminder,
  };
  if (triggerMap[trigger] === false) return; // tenant disabled this specific trigger

  // Build message — prefer the owner-edited template (with {merge} tags),
  // fall back to the legacy hardcoded function template (used by grooming_done
  // which has dynamic conditional sections).
  const triggerKey = trigger === "booking_created" ? "booking_confirmed"
                   : trigger === "booking_cancelled" ? "cancel"
                   : trigger === "booking_rescheduled" ? "reschedule"
                   : trigger;
  let msg = resolveTemplate(triggerKey, tenant, data);
  if (!msg) {
    const fn = MSG_TEMPLATES[triggerKey];
    if (!fn) return;
    msg = fn(data);
  }

  // ── Channel routing ───────────────────────────────────────────────────
  // Owner picks email / sms / both per-trigger in 訊息通知設定.
  // Free plan = email only (SMS is a paid feature; the SMS-enabled paid
  // tier upgrades to basic+). Default is email everywhere.
  let channel = resolveChannel(trigger, tenant);
  // Migrate legacy "whatsapp" channel rows transparently — anyone on the
  // old config falls back to SMS (paid) or email (free).
  if (channel === "whatsapp") channel = "sms";
  // SMS is gated platform-wide by the superadmin (admin → 平台設定 → 開放 SMS).
  // Until that's on, everything routes to email regardless of saved config.
  const smsGloballyOn = state?.platformConfig?.smsEnabled === true;
  const smsAllowed = smsGloballyOn && !!plan?.features?.autoNotify;
  if (!smsAllowed) channel = "email";
  const wantEmail = channel === "email" || channel === "both";
  const wantSms   = (channel === "sms" || channel === "both") && smsAllowed;

  // ── EMAIL ─────────────────────────────────────────────────────────────
  // Send-email edge function falls back to platform Resend if the shop
  // hasn't configured BYO Resend; tenant secret wins when set.
  if (wantEmail && data.email) {
    const subject = resolveSubject(triggerKey, tenant, {
      ...data,
      shopName: data.shopName || tenant?.name || "PettoBook",
    });
    // For email, convert linebreaks → <br> so the body keeps its shape in
    // mail clients. The send-email function accepts either html or text;
    // we send both so plain-text clients still work.
    const html = '<div style="font-family:system-ui,-apple-system,sans-serif;line-height:1.6;font-size:15px;color:#333;">' +
      String(msg).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>") +
      '</div>';
    sendEmail({
      tenantId: tenant?.id,
      to: data.email,
      subject,
      html,
      text: msg,
      appointmentId: data.aptId,
      userId: data.customerId,
    }).catch(() => { /* best-effort; in-app notif still fires */ });
  }

  // Email-to-owner on new booking — gives the shop a paper trail / inbox
  // pointer even if they're not at the dashboard. Customer's email branch
  // above goes to the customer; this one goes to the shop's notification
  // mailbox (owner's profile email).
  if (wantEmail && trigger === "booking_created") {
    const ownerProfile = state.users?.[tenant?.ownerId];
    const ownerEmail = (ownerProfile?.email || tenant?.email || "").trim();
    if (ownerEmail && ownerEmail !== data.email) {
      const shopSubject = "[" + (tenant?.name || "PettoBook") + "] 📅 新預約 · " +
        (data.customerName || "客戶") + " · " + (data.date || "") + " " + (data.time || "");
      const shopBody = [
        "你收到一個新預約：",
        "",
        "客戶：" + (data.customerName || "—"),
        "電話：" + (data.phone || "—"),
        "毛孩：" + (data.petName || "—"),
        "服務：" + (data.service || "—"),
        "日期：" + (data.date || "—") + " " + (data.time || ""),
        "總額：$" + (data.total || 0) + "（訂金 $" + (data.deposit || 0) + "）",
      ].join("\n");
      const shopHtml = '<div style="font-family:system-ui,-apple-system,sans-serif;line-height:1.6;font-size:15px;color:#333;">' +
        shopBody.replace(/\n/g, "<br>") + '</div>';
      sendEmail({
        tenantId: tenant?.id,
        to: ownerEmail,
        subject: shopSubject,
        html: shopHtml,
        text: shopBody,
        appointmentId: data.aptId,
        userId: tenant?.ownerId,
      }).catch(() => { /* best-effort */ });
    }
  }

  // Web Push fan-out is handled by the notifications-insert DB trigger
  // (trg_push_on_notification). Each pushNotif() call that ultimately syncs
  // a row into the notifications table will fire an OS-level push to the
  // target user automatically — no client-side sendPush call needed here.

  // ── WHATSAPP (approved template) ─────────────────────────────────────────
  // If the shop matched an approved template to this event (設定 → 訊息通知
  // 設定 → WhatsApp 範本), send it via the WhatsApp Business API with the
  // {{variables}} auto-filled from the booking. Independent of the email/SMS
  // channel choice, so it runs BEFORE the wantSms early-return below.
  const waCfg = (tenant?.messagingConfig?.waTemplates || {})[trigger];
  if (waCfg?.name && data.phone && plan?.features?.whatsapp) {
    const waVal = (n) => {
      const m = {
        name: data.customerName, customername: data.customerName, customer: data.customerName,
        pet: data.petName, petname: data.petName,
        service: data.service, date: data.date, time: data.time,
        shop: data.shopName || tenant?.name, shopname: data.shopName || tenant?.name,
        deposit: data.deposit, address: tenant?.address,
      };
      return String(m[String(n || "").toLowerCase()] ?? "");
    };
    const templateParams = (waCfg.params || []).map(p => ({ name: p.name, value: waVal(p.name) }));
    sendWhatsApp({ tenantId: tenant?.id, to: data.phone, template: waCfg.name, templateParams, appointmentId: data.aptId, userId: data.customerId })
      .catch(e => console.warn("[PettoBook] auto WhatsApp template failed:", e));
  }

  if (!wantSms) return;
  if (!data.phone) return;

  // Normalize phone to E.164 for Twilio. Country comes from the customer's
  // saved profile (countryCode) first, then falls back to tenant.country
  // mapping. Stripping non-digits then prefixing the cc ensures Twilio
  // never rejects with "21211 invalid 'To'".
  const CC_BY_COUNTRY = { HK: "852", TW: "886", SG: "65", MY: "60", US: "1", CA: "1", AU: "61" };
  const tenantCC = CC_BY_COUNTRY[tenant?.country || "HK"] || "852";
  const toE164 = (raw, profileCc) => {
    if (!raw) return "";
    const trimmed = String(raw).trim();
    if (trimmed.startsWith("+")) return "+" + trimmed.slice(1).replace(/\D/g, "");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    const cc = (profileCc || "").replace(/\D/g, "") || tenantCC;
    // If the phone already starts with the country code (e.g. "85291234567"),
    // don't double it.
    if (digits.startsWith(cc)) return "+" + digits;
    return "+" + cc + digits;
  };
  const customerPhone = toE164(data.phone, data.countryCode);

  // SMS fan-out via send-sms Edge Function (Twilio). Best-effort: if the
  // shop hasn't configured SMS credentials the function 412s and we skip.
  const sendOneSms = async (to, text) => {
    if (!to) return;
    try {
      await sendSms({
        tenantId: tenant?.id,
        to,
        text,
        appointmentId: data.aptId,
        userId: data.customerId,
      });
    } catch { /* best-effort */ }
  };

  await sendOneSms(customerPhone, msg);

  if (trigger === "booking_created") {
    const ownerProfile = state.users?.[tenant?.ownerId];
    const ownerPhone = toE164(ownerProfile?.phone || tenant?.phone || "", ownerProfile?.countryCode);
    if (ownerPhone && ownerPhone !== customerPhone) {
      const shopMsg = "📅 新預約 — " + (data.customerName || "客戶") +
        " 為 " + (data.petName || "毛孩") + " 預約「" + (data.service || "服務") + "」" +
        "\n日期：" + (data.date || "") + " " + (data.time || "") +
        "\n總額：$" + (data.total || 0) + "（訂金 $" + (data.deposit || 0) + "）";
      await sendOneSms(ownerPhone, shopMsg);
    }
  }
};

// In-memory dedupe — keep last 50 (apt, trigger) keys with a 5-min TTL so a
// double-tap on 確認 doesn't re-send. State lives at module scope: it's
// rebuilt on full reload, which is the right resolution window.
const _autoSendDedupe = new Map();
const AUTOSEND_DEDUPE_TTL_MS = 5 * 60 * 1000;
const _autoSendShouldFire = (trigger, aptId) => {
  if (!aptId) return true; // no key to dedupe
  const key = trigger + ":" + aptId;
  const now = Date.now();
  // Garbage-collect anything older than TTL so the map never grows unbounded.
  for (const [k, t] of _autoSendDedupe) {
    if (now - t > AUTOSEND_DEDUPE_TTL_MS) _autoSendDedupe.delete(k);
  }
  if (_autoSendDedupe.has(key)) return false;
  _autoSendDedupe.set(key, now);
  return true;
};

// ── PHOTO UPLOAD COMPONENT ─────────────────────────────────────────────────
// Builds a before/after composite image with the shop's watermark.
// Returns a Promise<Blob> (JPEG) suitable for `navigator.share({ files: [file] })`
// or for downloading as a single file the customer can post to FB/IG manually.
//
// Layout: two square panels side-by-side (1080×540 total) with the shop name
// across the bottom. We render the watermark at ~7% of the total height so it
// reads cleanly when reshared at thumbnail size on social feeds.
// Multi-pair grid composite — mirrors the on-screen BeforeAfterGallery
// preview (stacked rows of before | after). Falls back to the single-pair
// side-by-side composite via buildBeforeAfterComposite when the caller
// only has one pair, so old call sites keep working.
async function buildBeforeAfterGrid({ photos = [], shopName = "", petName = "", bookingUrl = "", phone = "", platformDomain = "", hideBranding = false }) {
  const loadImg = (src) => new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  const kindOf = (p) => p.category || p.kind || "after";
  const before = photos.filter(p => kindOf(p) === "before");
  const after  = photos.filter(p => kindOf(p) !== "before");
  const pairCount = Math.min(before.length, after.length);
  if (pairCount === 0) throw new Error(zhEn("需要至少一對 美容前+美容後 照片", "At least one before + after photo pair is needed"));

  const W = 1080;
  const PAD = 24;                              // outer margin
  const GAP = 16;                              // gap between tiles/rows
  const TILE_W = Math.floor((W - PAD * 2 - GAP) / 2);
  const TILE_H = TILE_W;                       // square tiles like the preview
  const FOOTER_H = 260;
  const HEADER_H = 84;
  const rowsH = pairCount * TILE_H + (pairCount - 1) * GAP;
  const H = HEADER_H + PAD + rowsH + PAD + FOOTER_H;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFDFD";
  ctx.fillRect(0, 0, W, H);

  // Header — pet name + "美容紀念冊 Before & After".
  ctx.fillStyle = "#4A4A68";
  ctx.font = "900 34px -apple-system, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  const title = "✂️  " + (petName ? petName + " · " : "") + "美容紀念冊";
  ctx.fillText(title, PAD, HEADER_H / 2);
  ctx.textAlign = "right";
  ctx.fillStyle = "#8A8A9E";
  ctx.font = "700 22px -apple-system, system-ui, sans-serif";
  ctx.fillText("Before & After", W - PAD, HEADER_H / 2);
  ctx.textAlign = "left";

  // object-fit: cover + rounded-rect clip so tiles look like the preview.
  const roundRectPath = (x, y, w, h, r) => {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); }
    else {
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }
  };
  const drawTile = (img, x, y, w, h, label, labelBg) => {
    ctx.save();
    roundRectPath(x, y, w, h, 22);
    ctx.clip();
    const ratio = Math.max(w / img.width, h / img.height);
    const iw = img.width * ratio, ih = img.height * ratio;
    ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
    // Label pill (mirrors preview: BEFORE red / AFTER green in top-left).
    ctx.fillStyle = labelBg;
    const pillW = 108, pillH = 32;
    roundRectPath(x + 12, y + 12, pillW, pillH, 16);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.font = "900 16px -apple-system, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + 12 + pillW / 2, y + 12 + pillH / 2);
    ctx.textAlign = "left";
    ctx.restore();
  };

  for (let i = 0; i < pairCount; i++) {
    const [b, a] = await Promise.all([loadImg(before[i].url), loadImg(after[i].url)]);
    const y = HEADER_H + PAD + i * (TILE_H + GAP);
    drawTile(b, PAD,                        y, TILE_W, TILE_H, "BEFORE", "rgba(239,68,68,0.92)");
    drawTile(a, PAD + TILE_W + GAP,         y, TILE_W, TILE_H, "AFTER",  "rgba(16,185,129,0.92)");
  }

  // Branded footer band — pink brand, shop name + booking URL + phone.
  const footerY = H - FOOTER_H;
  const cleanUrl = (bookingUrl || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  ctx.fillStyle = "#FF758F";
  ctx.fillRect(0, footerY, W, FOOTER_H);
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "bold 48px -apple-system, system-ui, sans-serif";
  ctx.fillText("✂️  " + (shopName || "PettoBook"), W / 2, footerY + 68);
  let yCursor = footerY + 116;
  if (cleanUrl) {
    ctx.font = "700 26px -apple-system, system-ui, sans-serif";
    ctx.fillText("📅 " + cleanUrl, W / 2, yCursor);
    yCursor += 36;
  }
  if (phone) {
    ctx.font = "700 26px -apple-system, system-ui, sans-serif";
    ctx.fillText("📞 " + phone, W / 2, yCursor);
    yCursor += 36;
  }
  if (!hideBranding) {
    ctx.font = "500 18px -apple-system, system-ui, sans-serif";
    ctx.globalAlpha = 0.75;
    ctx.fillText("Powered by " + (platformDomain || "pettobook.com"), W / 2, footerY + FOOTER_H - 18);
    ctx.globalAlpha = 1;
  }
  ctx.textAlign = "left";

  return await new Promise((resolve) => canvas.toBlob(b2 => resolve(b2), "image/jpeg", 0.92));
}

async function buildBeforeAfterComposite({ before, after, shopName = "", petName = "", bookingUrl = "", phone = "", platformDomain = "", hideBranding = false }) {
  const loadImg = (src) => new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  const [b, a] = await Promise.all([loadImg(before), loadImg(after)]);

  // Square 1080×1080 canvas — IG feed / FB post native ratio. Two side-by-
  // side photo tiles fill the upper portion (more pet, less text); a
  // branded footer holds the shop name + contact info + powered-by line.
  // Footer height grew to 260 to fit booking URL + phone + powered-by
  // attribution without crowding the shop name.
  const SIZE = 1080;
  const FOOTER_H = 260;
  const TILE_W = SIZE / 2;          // 540
  const TILE_H = SIZE - FOOTER_H;   // 820
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFDFD";
  ctx.fillRect(0, 0, SIZE, SIZE);

  // object-fit: cover into each tile.
  const drawCover = (img, dx, dy, dw, dh) => {
    const ratio = Math.max(dw / img.width, dh / img.height);
    const w = img.width * ratio, h = img.height * ratio;
    ctx.save();
    ctx.beginPath();
    ctx.rect(dx, dy, dw, dh);
    ctx.clip();
    ctx.drawImage(img, dx + (dw - w) / 2, dy + (dh - h) / 2, w, h);
    ctx.restore();
  };
  drawCover(b, 0, 0, TILE_W, TILE_H);
  drawCover(a, TILE_W, 0, TILE_W, TILE_H);

  // Hair-thin centre divider between BEFORE/AFTER so the seam stays crisp.
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillRect(TILE_W - 2, 0, 4, TILE_H);

  // Top-left BEFORE / top-right AFTER labels.
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, 0, TILE_W, 60);
  ctx.fillRect(TILE_W, 0, TILE_W, 60);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 32px -apple-system, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("BEFORE", 24, 30);
  ctx.fillText("AFTER", TILE_W + 24, 30);

  // Footer band — pink brand, shop name + booking URL + phone + powered-by.
  // Strip the protocol from the booking URL so it reads more cleanly on the
  // image ("petos.app/shop/petto" instead of "https://...").
  const cleanUrl = (bookingUrl || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  ctx.fillStyle = "#FF758F";
  ctx.fillRect(0, TILE_H, SIZE, FOOTER_H);
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";

  // Line 1 — shop name (anchor of the brand)
  ctx.font = "bold 48px -apple-system, system-ui, sans-serif";
  ctx.fillText("✂️  " + (shopName || "PettoBook"), SIZE / 2, TILE_H + 56);

  // Line 2 — pet name with sparkle, when known
  let yCursor = TILE_H + 100;
  if (petName) {
    ctx.font = "600 26px -apple-system, system-ui, sans-serif";
    ctx.fillText(petName + " ✨", SIZE / 2, yCursor);
    yCursor += 38;
  }

  // Line 3 — booking URL (so anyone seeing the IG post can tap to book)
  if (cleanUrl) {
    ctx.font = "700 24px -apple-system, system-ui, sans-serif";
    ctx.fillText("📅 " + cleanUrl, SIZE / 2, yCursor);
    yCursor += 32;
  }

  // Line 4 — phone (parallel to URL, for the customer who prefers calling)
  if (phone) {
    ctx.font = "700 24px -apple-system, system-ui, sans-serif";
    ctx.fillText("📞 " + phone, SIZE / 2, yCursor);
    yCursor += 32;
  }

  // Line 5 — Powered-by attribution. Sits flush against the bottom of the
  // footer in a lighter weight so it's visible but not competing with the
  // shop's own brand. Pro plan can hide this entirely.
  if (!hideBranding) {
    ctx.font = "500 18px -apple-system, system-ui, sans-serif";
    ctx.globalAlpha = 0.75;
    ctx.fillText("Powered by " + (platformDomain || "pettobook.com"), SIZE / 2, TILE_H + FOOTER_H - 18);
    ctx.globalAlpha = 1;
  }
  ctx.textAlign = "left";

  return await new Promise((resolve) => canvas.toBlob(b2 => resolve(b2), "image/jpeg", 0.92));
}

// QR image — small wrapper around the `qrcode` lib so we can render an
// inline preview without bloating the initial bundle. Dynamic-imports
// the lib on mount and stores the result as a data URL.
const QrImage = ({ url, size = 200 }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!url) { setSrc(null); return; }
    import("qrcode").then(({ default: QR }) => {
      QR.toDataURL(url, { width: size * 2, margin: 1, errorCorrectionLevel: "H", color: { dark: "#1F2937", light: "#FFFFFF" } })
        .then(dataUrl => { if (!cancelled) setSrc(dataUrl); })
        .catch(() => { if (!cancelled) setSrc(null); });
    }).catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [url, size]);
  return (
    <div style={{margin:"14px 0 8px",padding:14,background:"#fff",borderRadius:18,boxShadow:"0 8px 22px rgba(0,0,0,0.22)",display:"inline-block"}}>
      {src
        ? <img src={src} alt="QR" style={{display:"block",width:size,height:size}} />
        : <div style={{width:size,height:size,display:"flex",alignItems:"center",justifyContent:"center",color:"#9CA3AF",fontSize:12,fontWeight:700}}>{zhEn("產生中…", "Generating…")}</div>}
    </div>
  );
};

// Generate a high-resolution Instagram-style QR poster and trigger a
// download. Drawn entirely on canvas so the owner gets a print-ready
// PNG with their shop branding baked in. Logo is optional (falls back
// to a paw glyph) and tagline is optional. URL is shown small at the
// bottom for fallback typing.
async function downloadQrPoster({ tenant, link, primaryColor, gradColor }) {
  const W = 1080, H = 1440; // 3:4 Instagram-friendly
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background gradient — matches shop theme
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, gradColor || "#FFB6C1");
  grad.addColorStop(1, primaryColor || "#FF758F");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Decorative paws (lightly scattered, low opacity)
  ctx.globalAlpha = 0.14;
  ctx.font = "150px system-ui";
  ctx.textBaseline = "top";
  ctx.fillText("🐾", 60, 60);
  ctx.fillText("🐾", W - 220, 50);
  ctx.fillText("🦴", 40, H - 280);
  ctx.fillText("✨", W - 180, H - 240);
  ctx.globalAlpha = 1;

  // Shop logo (or fallback)
  const logoSize = 130;
  const logoX = (W - logoSize) / 2;
  const logoY = 110;
  if (tenant?.logoUrl) {
    try {
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = tenant.logoUrl;
        // Don't let a slow/stalled logo block the whole poster (esp. iOS WebView).
        setTimeout(() => rej(new Error("logo timeout")), 4000);
      });
      // Rounded mask
      ctx.save();
      const r = 36;
      ctx.beginPath();
      ctx.moveTo(logoX + r, logoY);
      ctx.arcTo(logoX + logoSize, logoY, logoX + logoSize, logoY + logoSize, r);
      ctx.arcTo(logoX + logoSize, logoY + logoSize, logoX, logoY + logoSize, r);
      ctx.arcTo(logoX, logoY + logoSize, logoX, logoY, r);
      ctx.arcTo(logoX, logoY, logoX + logoSize, logoY, r);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
      ctx.restore();
      // White border
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 6;
      ctx.strokeRect(logoX, logoY, logoSize, logoSize);
    } catch {
      // ignore — fall through to no-logo
    }
  } else {
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(logoX, logoY, logoSize, logoSize, 36) : ctx.rect(logoX, logoY, logoSize, logoSize);
    ctx.fill();
    ctx.fillStyle = primaryColor || "#FF758F";
    ctx.font = "78px system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("🐾", logoX + logoSize / 2, logoY + logoSize / 2 + 4);
  }

  // Shop name
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.shadowColor = "rgba(0,0,0,0.32)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 4;
  ctx.font = "900 64px system-ui";
  ctx.fillText(tenant?.name || zhEn("我的寵物店", "My pet shop"), W / 2, logoY + logoSize + 32);
  // Tagline
  ctx.shadowBlur = 12;
  ctx.font = "700 32px system-ui";
  ctx.fillText(tenant?.tagline || zhEn("歡迎使用線上預約", "Book online with us"), W / 2, logoY + logoSize + 110);
  ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;

  // QR card (white rounded panel)
  const qrCardSize = 720;
  const qrCardX = (W - qrCardSize) / 2;
  const qrCardY = 480;
  ctx.fillStyle = "#fff";
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(qrCardX, qrCardY, qrCardSize, qrCardSize, 40);
    ctx.fill();
  } else {
    ctx.fillRect(qrCardX, qrCardY, qrCardSize, qrCardSize);
  }

  // Render QR onto the card. Draw QR straight to a temp <canvas> via toCanvas
  // and blit it — drawing a canvas onto a canvas is synchronous and reliable,
  // unlike round-tripping through a data-URL <img> (which failed to appear in
  // the exported poster on some browsers).
  const qrSize = qrCardSize - 80;
  try {
    const { default: QR } = await import("qrcode");
    const qrCanvas = document.createElement("canvas");
    await QR.toCanvas(qrCanvas, link, { width: qrSize, margin: 0, errorCorrectionLevel: "H", color: { dark: "#1F2937", light: "#FFFFFF" } });
    ctx.drawImage(qrCanvas, qrCardX + 40, qrCardY + 40, qrSize, qrSize);
  } catch (e) {
    console.error("[qr-poster] QR render failed", e);
    // Fallback: try the data-URL → Image path before giving up.
    try {
      const { default: QR } = await import("qrcode");
      const qrDataUrl = await QR.toDataURL(link, { width: 1000, margin: 0, errorCorrectionLevel: "H", color: { dark: "#1F2937", light: "#FFFFFF" } });
      const qrImg = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = qrDataUrl; });
      ctx.drawImage(qrImg, qrCardX + 40, qrCardY + 40, qrSize, qrSize);
    } catch (e2) {
      ctx.fillStyle = "#9CA3AF";
      ctx.font = "32px system-ui";
      ctx.textAlign = "center";
      ctx.fillText(zhEn("QR 產生失敗", "QR generation failed"), W / 2, qrCardY + qrCardSize / 2);
    }
  }

  // CTA + URL at bottom
  ctx.fillStyle = "#fff";
  ctx.shadowColor = "rgba(0,0,0,0.25)";
  ctx.shadowBlur = 10;
  ctx.font = "900 44px system-ui";
  ctx.textAlign = "center";
  ctx.fillText(zhEn("掃描即可線上預約 ✂️🐾", "Scan to book online ✂️🐾"), W / 2, qrCardY + qrCardSize + 40);
  ctx.shadowBlur = 0;
  ctx.font = "700 28px ui-monospace, SF Mono, monospace";
  ctx.fillText(link.replace(/^https?:\/\//, ""), W / 2, qrCardY + qrCardSize + 110);

  // Trigger download. iOS/WKWebView (the Capacitor iPad app) can return null
  // from toBlob and is flaky with blob: anchors — so fall back to a data-URL
  // download, which works reliably across browsers.
  const fileName = (tenant?.bookingSlug || "shop") + "-qr-poster.png";
  // In the apps a download link does nothing — the Android WebView ignores
  // <a download> for data:/blob: URLs — so save straight to the photo
  // gallery through the native PhotoSaver plugin (iOS and Android).
  if (window?.Capacitor?.isNativePlatform?.()) {
    try {
      const { registerPlugin } = await import("@capacitor/core");
      const PhotoSaver = registerPlugin("PhotoSaver");
      const res = await PhotoSaver.save({ base64: canvas.toDataURL("image/png").split(",")[1], filename: fileName });
      if (res?.saved) { toast(zhEn("海報已儲存到相簿 ✓", "Poster saved to your photos ✓")); return; }
    } catch (e) {
      const msg = String(e?.message || e);
      toast(/not implemented|not available/i.test(msg) ? zhEn("請更新 PettoBook App 先可以儲存海報", "Please update the PettoBook app to save the poster") : msg.includes("permission") ? zhEn("未有權限儲存相片，請喺設定允許", "No permission to save photos — please allow it in Settings") : zhEn("儲存失敗：", "Save failed: ") + msg, "error");
      return;
    }
  }
  const downloadDataUrl = () => {
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      console.error("[qr-poster] export failed", e);
    }
  };
  try {
    canvas.toBlob((blob) => {
      if (!blob) { downloadDataUrl(); return; }
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1500);
    }, "image/png", 0.92);
  } catch {
    downloadDataUrl();
  }
}

// Share a generated Blob via the OS share sheet (works on iOS Safari, Chrome
// Android — opens IG / FB / Messages / etc). Falls back to download when
// navigator.share isn't available or rejects the file payload.
// Per-tenant transient cache of the WC categories the owner just loaded
// in settings. Survives cross-render tenant refetches (the tenants array
// gets rebuilt from DB and would otherwise drop our transient wcCatsCache
// field). Session-scoped; cleared on page reload.
const __wcCatsCache = new Map();

async function shareImageBlob({ blob, text = "", filename = "petos-before-after.jpg" }) {
  const file = new File([blob], filename, { type: blob.type });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return { shared: true };
    }
  } catch (e) {
    if (e?.name === "AbortError") return { shared: false, reason: "abort" };
  }
  // WKWebView can't route a browser download into the iOS Photos album,
  // so on native we bridge to a small Swift plugin that calls
  // PHPhotoLibrary. Capacitor 7 needs registerPlugin() on the JS side
  // for the proxy to exist under Capacitor.Plugins — without it, the
  // fallback download link fires. Mirrors the WebAuth pattern.
  const isNative = !!(window?.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());
  if (isNative) {
    try {
      const { registerPlugin } = await import("@capacitor/core");
      const PhotoSaver = registerPlugin("PhotoSaver");
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(String(r.result || "").split(",")[1] || "");
        r.onerror = () => reject(r.error || new Error("read failed"));
        r.readAsDataURL(blob);
      });
      const res = await PhotoSaver.save({ base64 });
      if (res?.saved) {
        try { toast?.(zhEn("已儲存到相簿", "Saved to photos")); } catch {}
        return { shared: true, reason: "photo-library" };
      }
    } catch (e) {
      try { toast?.(zhEn("儲存到相簿失敗：", "Failed to save to photos: ") + (e?.message || e), "error"); } catch {}
      // fall through to <a download> as a last resort
    }
  }
  // Fallback: trigger download (only reached on browsers with neither
  // Web Share for files nor the native PhotoSaver plugin).
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { shared: false, reason: "downloaded" };
}

const PhotoUpload = ({ photos = [], onAdd, onRemove, maxPhotos = 8, label = zhEn("上傳照片", "Upload photo"), category = null, tenantId = null, scope = null }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Phase 3.3 — track recently-failed File objects so we can show a
  // "重試" pill below the file picker. Resets when the next upload is
  // attempted OR when the parent removes the photo array. Keyed by an
  // internal id since File objects can't be compared cheaply.
  const [failedFiles, setFailedFiles] = useState([]);
  const retryFailed = (entry) => {
    setFailedFiles(prev => prev.filter(f => f.id !== entry.id));
    // Trigger the same processing path as a fresh selection.
    const fakeEvent = { target: { files: [entry.file], value: "" } };
    handleFile(fakeEvent);
  };
  // Resize/encode the file to an 800px JPEG. When the caller passes a tenantId
  // + scope, we upload to Supabase Storage and store the public URL — this
  // keeps `appointments.report.photos` / `pets.photos` small. Without those
  // props (legacy callers) we fall back to base64 in JSONB.
  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const room = Math.max(0, maxPhotos - photos.length);
    const files = picked.slice(0, room);
    if (picked.length > room) toast(zhEn("最多上傳 ", "Up to ") + maxPhotos + zhEn(" 張照片", " photos"), "warning");
    if (!files.length) { e.target.value = ""; return; }
    setBusy(true);
    let queued = files.length;
    const finish = () => { if (--queued <= 0) setBusy(false); };
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new window.Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const maxSize = 1200;
          let w = img.width, h = img.height;
          if (w > maxSize || h > maxSize) { const ratio = Math.min(maxSize / w, maxSize / h); w = Math.round(w * ratio); h = Math.round(h * ratio); }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const photoMeta = { id: "ph" + uid(), date: dateNow(), name: file.name, ...(category ? { category } : {}) };
          // Storage path — preferred
          if (tenantId && scope) {
            try {
              let blob = await new Promise(r => canvas.toBlob(b => r(b), "image/jpeg", 0.85));
              // Phase 3.1 — pre-upload size guard. If the compressed blob
              // is still >2MB, recompress at a lower quality. iOS WKWebView
              // OOM on large blobs is a real silent-abort vector; this
              // keeps the upload within a safe band without a UX prompt.
              if (blob && blob.size > 2_000_000) {
                console.warn("[PettoBook] large blob after first-pass compress:", blob.size, "— retrying at q=0.7");
                blob = await new Promise(r => canvas.toBlob(b => r(b), "image/jpeg", 0.7));
              }
              const { url } = await uploadGroomingMedia({ tenantId, blob, scope });
              onAdd({ ...photoMeta, url });
            } catch (err) {
              // Phase 1.5 — DO NOT silently base64-fallback into JSONB.
              // Phase 3.3 — record the failed File so we can show a
              // tap-to-retry pill below the picker. No phantom thumbnail.
              console.error("[PettoBook] storage upload failed:", err);
              toast(zhEn("照片上傳失敗：", "Photo upload failed: ") + (err?.message || err) + zhEn(" — 可以按下面「重試」", " — tap \"Retry\" below"), "error");
              setFailedFiles(prev => [...prev, { id: "f" + uid(), file, name: file.name }]);
            } finally { finish(); }
          } else {
            // Legacy / non-tenant context — keep base64.
            onAdd({ ...photoMeta, url: canvas.toDataURL("image/jpeg", 0.85) });
            finish();
          }
        };
        img.onerror = () => { toast(zhEn("照片讀取失敗", "Couldn't read the photo"), "error"); finish(); };
        img.src = ev.target.result;
      };
      reader.onerror = () => { toast(zhEn("檔案讀取失敗", "Couldn't read the file"), "error"); finish(); };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  return (
    <div>
      <p style={{ fontSize: 13, color: "#8A8A9E", marginBottom: 8, fontWeight: 700 }}>{label} ({photos.length}/{maxPhotos})</p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {photos.map((p, i) => (
          <div key={p.id || i} style={{ width: 80, height: 80, borderRadius: 16, overflow: "hidden", position: "relative", border: "2px solid var(--c-light)", flexShrink: 0 }}>
            <img src={p.url} alt={p.name || "pet"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            {onRemove && <button onClick={() => onRemove(p.id)} style={{ position: "absolute", top: 2, right: 2, width: 22, height: 22, borderRadius: 99, background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={12} strokeWidth={3} /></button>}
          </div>
        ))}
        {photos.length < maxPhotos && (
          <button onClick={() => fileRef.current?.click()} disabled={busy} aria-label={zhEn("上傳照片", "Upload photo")} style={{ width: 80, height: 80, borderRadius: 16, border: "2px dashed var(--c-border)", background: busy ? "var(--c-light)" : "var(--c-lighter)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: busy ? "wait" : "pointer", color: "var(--c-primary)", opacity: busy ? 0.6 : 1 }}>
            <Camera size={20} strokeWidth={2.5} />
            <span style={{ fontSize: 11, fontWeight: 700 }}>{busy ? zhEn("上傳中…", "Uploading…") : zhEn("上傳", "Upload")}</span>
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFile} />
      {failedFiles.length > 0 && (
        <div style={{marginTop:10,padding:"8px 10px",background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12}}>
          <p style={{fontSize:11,fontWeight:800,color:"#991B1B",margin:"0 0 6px"}}>{zhEn("上傳失敗 · 點下方重試", "Upload failed · tap below to retry")}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {failedFiles.map(f => (
              <button key={f.id} onClick={() => retryFailed(f)} style={{padding:"5px 10px",borderRadius:99,background:"#fff",border:"1.5px solid #FCA5A5",color:"#991B1B",fontSize:11,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                ↻ {f.name.length > 14 ? f.name.slice(0,12) + "…" : f.name}
              </button>
            ))}
            <button onClick={() => setFailedFiles([])} style={{padding:"5px 10px",borderRadius:99,background:"transparent",border:"1.5px dashed #FCA5A5",color:"#991B1B",fontSize:11,fontWeight:800,cursor:"pointer"}}>{zhEn("清除", "Clear")}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── SMS / WHATSAPP NOTIFICATION ENGINE ─────────────────────────────────────
// Owner-editable templates use `{key}` merge tags. Owners customize these in
// 設定 → 通訊 → 自訂訊息範本. Falls back to these defaults when the tenant
// hasn't customized a specific trigger.
const DEFAULT_TEMPLATE_STRINGS = {
  booking_confirmed:
    "親愛的 {customerName}，\n\n您已成功預約：\n📅 日期：{date}\n⏰ 時間：{time}\n✂️ 服務：{service}\n🐕 毛孩：{petName}\n💰 訂金：${deposit}\n\n如需改期或取消，請提前24小時聯絡我們。\n感謝您的信任 ❤️\n\n— 🐾 {shopName}",
  reschedule:
    "親愛的 {customerName}，\n\n{petName} 的預約已改期：\n📅 新日期：{date}\n⏰ 新時間：{time}\n✂️ 服務：{service}\n\n如有疑問請隨時聯絡我們 💕\n\n— 🐾 {shopName}",
  reminder:
    "溫馨提醒：{petName} 明天 {time} 有一個 {service} 的預約 📅\n\n地址：{address}\n\n期待見到您和 {petName}！🐾\n\n— 🐾 {shopName}",
  cancel:
    "您的預約已取消：\n📅 {date} {time}\n✂️ {service}\n🐕 {petName}\n\n原因：{reason}\n\n如需重新預約，歡迎隨時聯絡我們 💕\n\n— 🐾 {shopName}",
  checkin:
    "{petName} 已到店，準備開始洗浴 🛁\n\n預計 {time} 完成。\n\n— 🐾 {shopName}",
  grooming_started:
    "{petName} 嘅美容開始了 ✂️\n美容師會盡心照顧，完成後即時通知您 ❤️\n\n— 🐾 {shopName}",
  // The grooming_done template ships every report field as a merge tag so the
  // owner can rearrange / re-word everything from settings. Older versions of
  // this template used 🍑 🔍 👁️ 🪮 which rendered as black-diamond fallback
  // glyphs on some email clients (Gmail Sans on Linux, older Outlook), so we
  // swap to broadly-supported emoji here. The "next recommend" line was
  // removed per owner feedback.
  // {reportBody} auto-expands to every enabled field in the shop's
  // report template (see buildReportBody in ReportSheet.complete). Owners
  // can still switch back to explicit merge tags like {skin} {ear} for
  // fine control — those keep working as long as the field exists.
  grooming_done:
    "親愛的 {customerName}，\n\n{petName} 的美容已完成！\n\n[ 健康報告 ]\n{reportBody}\n\n[ 美容師叮嚀 ]\n{notes}\n\n本次合計：${totalPaid}{albumBlock}\n\n感謝您的光臨，期待下次見面 ❤️\n\n— 🐾 {shopName}",
};

// Default email subject lines, owner-editable. Same merge-tag system as
// DEFAULT_TEMPLATE_STRINGS — `{petName}`, `{date}`, `{shopName}` etc.
const DEFAULT_SUBJECT_STRINGS = {
  booking_confirmed: "[{shopName}] 預約確認 ✅ · {petName} {date} {time}",
  reschedule:        "[{shopName}] 預約改期 📅 · {petName} {date} {time}",
  reminder:          "[{shopName}] 預約提醒 ⏰ · 明天 {time} {petName}",
  checkin:           "[{shopName}] {petName} 已到店 🛁",
  grooming_started:  "[{shopName}] {petName} 美容開始 ✂️",
  grooming_done:     "[{shopName}] 美容完成 🎉 · {petName}",
  cancel:            "[{shopName}] 預約已取消 ❌ · {petName} {date}",
};

const resolveSubject = (trigger, tenant, data) => {
  const overrides = tenant?.messagingConfig?.subjects || {};
  const tpl = overrides[trigger] || DEFAULT_SUBJECT_STRINGS[trigger] || "";
  return applyTemplate(tpl, data) || "通知";
};

// Channel routing — owner picks email / whatsapp / both per-trigger, or a
// shop-wide default. Email is the platform default (everyone has it; WA
// requires BYO credentials and is a paid feature).
const resolveChannel = (trigger, tenant) => {
  const mc = tenant?.messagingConfig || {};
  const perTrigger = mc.channels?.[trigger];
  if (perTrigger) return perTrigger;
  return mc.defaultChannel || "email";
};

// Replace `{key}` placeholders in a template string with values from `data`.
// Missing keys are left blank rather than rendering "{undefined}".
const applyTemplate = (str, data) => {
  if (!str) return "";
  const filled = String(str).replace(/\{(\w+)\}/g, (_, k) => {
    const v = data?.[k];
    return v == null ? "" : String(v);
  });
  // Drop any line whose only remaining content is a label + colon + no
  // value (e.g. "- 牙齒：" after {teeth} resolved empty). This is what
  // happens when the shop disables a report field but the WA template
  // still references it. Matches both fullwidth (：) and half-width (:)
  // colons. Collapses ≥3 consecutive newlines into 2 so removed lines
  // don't leave holes.
  return filled
    .split("\n")
    .filter(line => !/^\s*[-•·]?\s*[^\s：:]{1,20}[：:]\s*$/.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// Resolves which template body to use: tenant override → default string.
// Returns null if neither configured (caller decides to skip / use legacy).
const resolveTemplate = (trigger, tenant, data) => {
  const overrides = tenant?.messagingConfig?.templates || {};
  const tpl = overrides[trigger] || DEFAULT_TEMPLATE_STRINGS[trigger];
  if (!tpl) return null;
  return applyTemplate(tpl, data);
};

const MSG_TEMPLATES = {
  booking_confirmed: (data) => `🐾 ${data.shopName}\n\n親愛的 ${data.customerName}，\n\n您已成功預約：\n📅 日期：${data.date}\n⏰ 時間：${data.time}\n✂️ 服務：${data.service}\n🐕 毛孩：${data.petName}\n💰 訂金：$${data.deposit}\n\n如需改期或取消，請提前24小時聯絡我們。\n感謝您的信任 ❤️`,
  grooming_done: (data) => {
    // Emoji-free template — newer emoji (🩺 🦷 🪮 etc.) render as ? on
    // older Android / WhatsApp / SMS-via-iMessage stacks. Plain Chinese
    // labels reach every device cleanly. A single 🐾 at the top is kept
    // (universally supported) so the message still feels branded.
    const lines = [
      `🐾 ${data.shopName}`,
      ``,
      `親愛的 ${data.customerName}，`,
      ``,
      `${data.petName} 的美容已完成！`,
      ``,
      `[ 健康報告 ]`,
    ];
    // Prefer the shop-template-aware reportBody built by the caller.
    // Falls back to the legacy hardcoded 9 fields for callers that
    // haven't been upgraded to pass reportBody yet.
    if (data.reportBody && String(data.reportBody).trim()) {
      lines.push(String(data.reportBody).trim());
    } else {
      lines.push(`- 皮膚：${data.skin || "—"}`);
      lines.push(`- 耳朵：${data.ear || "—"}`);
      lines.push(`- 牙齒：${data.teeth || "—"}`);
      lines.push(`- 情緒：${data.mood || "—"}`);
      if (data.nailTrim)     lines.push(`- 剪甲：${data.nailTrim}`);
      else if (data.nails)   lines.push(`- 剪甲：${data.nails}`);
      if (data.analGlands)   lines.push(`- 肛門腺：${data.analGlands}`);
      if (data.parasites)    lines.push(`- 寄生蟲：${data.parasites}`);
      if (data.eyeDischarge) lines.push(`- 眼睛分泌：${data.eyeDischarge}`);
      if (data.matting)      lines.push(`- 打結程度：${data.matting}`);
    }
    if (data.notes)        lines.push(``, `[ 美容師叮嚀 ]`, data.notes);
    if (Array.isArray(data.surcharges) && data.surcharges.length > 0) {
      lines.push(``, `[ 附加費用 ]`);
      for (const s of data.surcharges) lines.push(`- ${s.name} +$${s.amount}`);
    }
    if (data.totalPaid != null) {
      lines.push(``, `本次合計：$${data.totalPaid}` + (data.depositPaid ? ` (含已付訂金 $${data.depositPaid})` : ""));
    }
    // Album share link — only when the shop actually uploaded photos.
    // Sending a URL to an empty album reads as broken.
    if (data.albumUrl) {
      lines.push(``, `📸 美容紀念冊（前後對比）：`, data.albumUrl);
    }
    lines.push(``, `感謝您的光臨，期待下次見面 ❤️`);
    return lines.join("\n");
  },
  reminder: (data) => `🐾 ${data.shopName}\n\n溫馨提醒：${data.petName} 明天 ${data.time} 有一個 ${data.service} 的預約 📅\n\n地址：${data.address}\n\n期待見到您和 ${data.petName}！🐾`,
  cancel: (data) => `🐾 ${data.shopName}\n\n您的預約已取消：\n📅 ${data.date} ${data.time}\n✂️ ${data.service}\n🐕 ${data.petName}\n\n原因：${data.reason}\n\n如需重新預約，歡迎隨時聯絡我們 💕`,
};

// Owner-only marketing tab. Two independent tools:
//   1) 最新消息 — CRUD the shop_news feed customers see in the native app.
//   2) 廣播通知 — fan-out a single push+inbox message to all customers.
// Both use the same tenant-scoped RPCs (owner_grant_voucher not used here;
// broadcast fires notification rows; shop_news writes via RLS).
const MarketingTab = ({ tenant, user, state, setState }) => {
  const [sub, setSub] = useState("news"); // "news" | "broadcast"
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Broadcast composer
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcBusy, setBcBusy] = useState(false);
  const [bcTarget, setBcTarget] = useState("all"); // "all" | "recent" (past 90d) | "stale" (>90d no-visit)

  const load = async () => {
    setLoading(true);
    try { setNews(await fetchShopNews(user.tenantId)); }
    catch (e) { toast(zhEn("載入失敗：", "Failed to load: ") + (e?.message||""), "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [user.tenantId]);

  const startNew = () => setEditing({
    id: null, tenantId: user.tenantId, title: "", body: "",
    imageUrl: "", ctaLabel: "", ctaRoute: "", pinned: false, active: true,
  });
  const save = async () => {
    if (!editing?.title?.trim()) { toast(zhEn("請輸入標題", "Please enter a title"), "error"); return; }
    setSaving(true);
    try { await upsertShopNews(editing); await load(); toast(zhEn("已儲存 ✓", "Saved ✓")); setEditing(null); }
    catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message||""), "error"); }
    setSaving(false);
  };
  const del = async (id) => {
    if (!window.confirm(zhEn("確定刪除呢條消息？此操作不可撤銷。", "Delete this post? This can't be undone."))) return;
    try { await deleteShopNews(id); await load(); toast(zhEn("已刪除", "Deleted")); }
    catch (e) { toast(zhEn("刪除失敗：", "Delete failed: ") + (e?.message||""), "error"); }
  };

  // Broadcast: pick target user_ids by segment
  const audienceUsers = (() => {
    const memberships = (state.memberships || []).filter(m => m.tenantId === user.tenantId && !m.blockedAt);
    if (bcTarget === "all") return memberships.map(m => m.userId);
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const seenRecently = new Set(
      state.appointments
        .filter(a => a.tenantId === user.tenantId && a.status === "done" && a.date >= cutoffStr)
        .map(a => a.customerId)
    );
    if (bcTarget === "recent") return memberships.filter(m => seenRecently.has(m.userId)).map(m => m.userId);
    return memberships.filter(m => !seenRecently.has(m.userId)).map(m => m.userId);
  })();

  const sendBroadcast = async () => {
    if (!bcTitle.trim() || !bcBody.trim()) { toast(zhEn("請輸入標題同內容", "Please enter a title and content"), "error"); return; }
    if (audienceUsers.length === 0) { toast(zhEn("目標客戶群為空", "The target customer group is empty"), "error"); return; }
    if (!window.confirm(zhEn(`確定發送畀 ${audienceUsers.length} 位客戶？`, `Send to ${audienceUsers.length} customers?`))) return;
    setBcBusy(true);
    try {
      // Two channels: inbox row (permanent, always fires) + push (best-
      // effort, needs the customer to have a registered device_token).
      // We fire both in parallel; a push failure never blocks the inbox.
      const [n] = await Promise.all([
        ownerBroadcastNotification({
          userIds: audienceUsers, tenantId: user.tenantId,
          title: bcTitle.trim(), body: bcBody.trim(), type: "announcement",
        }),
        sendPush({
          userIds: audienceUsers, title: bcTitle.trim(),
          body: bcBody.trim(), tag: "broadcast",
        }).catch(err => { console.warn("[broadcast] push failed:", err); }),
      ]);
      toast(zhEn(`已發送 ${n} 則通知 + 手機推送 ✓`, `Sent ${n} notifications + push ✓`)); setBcTitle(""); setBcBody("");
    } catch (e) { toast(zhEn("發送失敗：", "Send failed: ") + (e?.message||""), "error"); }
    setBcBusy(false);
  };

  return (
    <div style={{padding:"0 4px",display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",gap:6,background:"var(--c-light)",borderRadius:99,padding:4}}>
        {[["news",zhEn("📣 最新消息", "📣 Latest news")],["broadcast",zhEn("🔔 廣播通知", "🔔 Broadcast")]].map(([v,l]) => (
          <button key={v} onClick={() => setSub(v)}
            style={{flex:1,padding:"10px 12px",borderRadius:99,fontSize:13,fontWeight:900,border:"none",background: sub===v ? "var(--c-primary)" : "transparent", color: sub===v ? "#fff" : "#8A8A9E", cursor:"pointer"}}>{l}</button>
        ))}
      </div>

      {sub === "news" && <>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:0,lineHeight:1.5}}>{zhEn("客戶會喺 App 首頁見到，可以貼相片、加 CTA、置頂。", "Customers see it on the app home page. You can add a photo, a CTA button and pin it.")}</p>
          <Btn onClick={startNew} style={{padding:"8px 14px",fontSize:13}}>{zhEn("+ 新增", "+ Add")}</Btn>
        </div>
        {loading && <p style={{fontSize:13,color:"#8A8A9E",padding:20,textAlign:"center"}}>{zhEn("載入中…", "Loading…")}</p>}
        {!loading && news.length === 0 && <EmptyState icon={Megaphone} title={zhEn("尚未有消息", "No posts yet")} sub={zhEn("按「+ 新增」發佈第一條", "Tap \"+ Add\" to publish the first one")} />}
        {!loading && news.map(n => (
          <div key={n.id} style={{background:"#fff",borderRadius:16,padding:14,border:"2px solid var(--c-light)",display:"flex",gap:12,alignItems:"flex-start"}}>
            {n.image_url && <img src={n.image_url} alt="" style={{width:64,height:64,borderRadius:12,objectFit:"cover",flexShrink:0}} />}
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:"0 0 3px"}}>
                {n.pinned && <span style={{background:"var(--c-primary)",color:"#fff",fontSize:10,fontWeight:900,padding:"2px 6px",borderRadius:99,marginRight:6}}>{zhEn("置頂", "Pinned")}</span>}
                {!n.active && <span style={{background:"#E5E7EB",color:"#4A4A68",fontSize:10,fontWeight:900,padding:"2px 6px",borderRadius:99,marginRight:6}}>{zhEn("下架", "Off sale")}</span>}
                {n.title}
              </p>
              {n.body && <p style={{fontSize:12,color:"#8A8A9E",fontWeight:600,margin:0,lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{n.body}</p>}
              <p style={{fontSize:10,color:"#AFAFC4",fontWeight:700,margin:"6px 0 0"}}>{String(n.published_at||"").slice(0,10)}</p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
              <Btn size="sm" variant="secondary" onClick={() => setEditing({
                id: n.id, tenantId: n.tenant_id, title: n.title, body: n.body || "",
                imageUrl: n.image_url || "", ctaLabel: n.cta_label || "", ctaRoute: n.cta_route || "",
                pinned: !!n.pinned, active: n.active !== false, expiresAt: n.expires_at || null,
              })}>{zhEn("編輯", "Edit ")}</Btn>
              <Btn size="sm" onClick={() => del(n.id)} style={{background:"#FEE2E2",color:"#991B1B"}}>{zhEn("刪除", "Delete")}</Btn>
            </div>
          </div>
        ))}
      </>}

      {sub === "broadcast" && <>
        <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:0,lineHeight:1.5}}>{zhEn("發送即時通知畀客戶。會出現喺 App 通知中心 + 手機通知欄。", "Send an instant notification to customers. It appears in the app's notification centre + the phone's notification bar.")}</p>
        <div>
          <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("發送對象", "Send to")}</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
            {[["all",zhEn("全部客戶", "All customers")],["recent",zhEn("近 90 日活躍", "Active in the last 90 days")],["stale",zhEn("逾 90 日未到訪", "No visit in 90+ days")]].map(([v,l]) => (
              <button key={v} onClick={() => setBcTarget(v)}
                style={{padding:"10px 8px",borderRadius:12,fontSize:12,fontWeight:900,cursor:"pointer",border:"2px solid "+(bcTarget===v?"var(--c-primary)":"var(--c-light)"),background:bcTarget===v?"var(--c-primary)":"#fff",color:bcTarget===v?"#fff":"#4A4A68"}}>{l}</button>
            ))}
          </div>
          <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"6px 0 0"}}>{zhEn("目標：", "Target: ")}<b style={{color:"var(--c-primary)"}}>{audienceUsers.length}</b> {zhEn("位客戶", " customers")}</p>
        </div>
        <div>
          <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("標題", "Title")}</p>
          <input value={bcTitle} onChange={e => setBcTitle(e.target.value)} placeholder={zhEn("例：💐 母親節優惠", "e.g. 💐 Mother's Day offer")}
            style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
        </div>
        <div>
          <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("內容", "Content")}</p>
          <textarea value={bcBody} onChange={e => setBcBody(e.target.value)} placeholder={zhEn("即日至月底前預約，全單享 85 折。", "Book before the end of the month and get 15% off your whole order.")}
            style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:600,color:"#4A4A68",outline:"none",boxSizing:"border-box",minHeight:100,resize:"vertical"}} />
        </div>
        <Btn onClick={sendBroadcast} disabled={bcBusy || !bcTitle.trim() || !bcBody.trim()} style={{width:"100%",padding:14,opacity:(bcBusy||!bcTitle.trim()||!bcBody.trim())?0.5:1}}>
          {bcBusy ? zhEn("發送中…", "Sending…") : zhEn(`🔔 發送 (${audienceUsers.length} 人)`, `🔔 Send (${audienceUsers.length} people)`)}
        </Btn>
      </>}

      <BottomSheet open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? zhEn("編輯消息", "Edit post") : zhEn("新增最新消息", "New news post")} z={280}>
        {editing && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("標題 *", "Title *")}</p>
              <input value={editing.title} onChange={e => setEditing(v => ({...v, title: e.target.value}))} placeholder={zhEn("例：🌸 春季開學優惠：全單 85 折", "e.g. 🌸 Spring offer: 15% off everything")}
                style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:900,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
            </div>
            <div>
              <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("內容", "Content")}</p>
              <textarea value={editing.body || ""} onChange={e => setEditing(v => ({...v, body: e.target.value}))} placeholder={zhEn("詳細描述優惠內容、有效日期等", "Describe the offer, validity dates, etc.")}
                style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:600,color:"#4A4A68",outline:"none",boxSizing:"border-box",minHeight:100,resize:"vertical"}} />
            </div>
            <div>
              <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("圖片", "Image")}</p>
              <input type="file" accept="image/*" onChange={async e => {
                const f = e.target.files?.[0]; if (!f) return;
                try {
                  const { url } = await uploadTenantMedia({ tenantId: user.tenantId, blob: f, scope: "news" });
                  setEditing(v => ({...v, imageUrl: url}));
                } catch (err) { toast(zhEn("上傳失敗：", "Upload failed: ") + (err?.message||""), "error"); }
              }} style={{fontSize:13,fontWeight:700,color:"#4A4A68"}} />
              {editing.imageUrl && <img src={editing.imageUrl} alt="" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:12,marginTop:8}} />}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div>
                <p style={{fontSize:11,fontWeight:900,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("CTA 按鈕文字", "CTA button text")}</p>
                <input value={editing.ctaLabel || ""} onChange={e => setEditing(v => ({...v, ctaLabel: e.target.value}))} placeholder={zhEn("立即預約", "Book now")}
                  style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
              </div>
              <div>
                <p style={{fontSize:11,fontWeight:900,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("CTA 動作", "CTA action")}</p>
                <select value={editing.ctaRoute || ""} onChange={e => setEditing(v => ({...v, ctaRoute: e.target.value}))}
                  style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,color:"#4A4A68",outline:"none",boxSizing:"border-box"}}>
                  <option value="">{zhEn("（無）", "(none)")}</option>
                  <option value="book">{zhEn("開啟預約", "Open booking")}</option>
                  <option value="wallet">{zhEn("開啟錢包", "Open wallet")}</option>
                </select>
              </div>
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:8,background:"var(--c-lighter)",borderRadius:10}}>
              <input type="checkbox" checked={!!editing.pinned} onChange={e => setEditing(v => ({...v, pinned: e.target.checked}))} />
              <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("📌 置頂顯示", "📌 Pin to top")}</span>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:8,background:"var(--c-lighter)",borderRadius:10}}>
              <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing(v => ({...v, active: e.target.checked}))} />
              <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("啟用（顯示畀客戶）", "Active (shown to customers)")}</span>
            </label>
            <Btn onClick={save} disabled={saving || !editing.title?.trim()} style={{width:"100%",padding:14,opacity:(saving||!editing.title?.trim())?0.5:1}}>
              {saving ? zhEn("儲存中…", "Saving…") : (editing.id ? zhEn("更新", "Update") : zhEn("發佈", "Publish"))}
            </Btn>
          </div>
        )}
      </BottomSheet>
    </div>
  );
};

const NotifSender = ({ type, data, tenant, onSend, onClose }) => {
  const [method, setMethod] = useState("whatsapp"); // "whatsapp" | "sms" | "line"
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [customMsg, setCustomMsg] = useState("");
  // Honour the shop's owner-edited template (settings → 自訂訊息範本).
  // Previously this sheet used MSG_TEMPLATES[type] unconditionally — the
  // hardcoded JS-function default — so the customised template was
  // visible in settings but the actual sent message ignored it. Fall
  // back to MSG_TEMPLATES only when the owner has not set a custom
  // template (resolveTemplate returns null only if BOTH the override
  // and DEFAULT_TEMPLATE_STRINGS are missing).
  const template = MSG_TEMPLATES[type];
  const resolved = (tenant && type) ? resolveTemplate(type, tenant, data) : null;
  const msg = customMsg || resolved || (template ? template(data) : "");

  const doSend = () => {
    setSending(true);
    setTimeout(() => {
      setSent(true); setSending(false);
      const phone = (data.phone || "").replace(/[^0-9]/g, "");
      const ccDigits = ((data.countryCode || "+852").replace(/\D/g, "") || "852");
      if (method === "whatsapp") {
        const waUrl = "https://wa.me/" + ccDigits + phone + "?text=" + encodeURIComponent(msg);
        window.open(waUrl, "_blank");
      } else if (method === "sms") {
        const intl = phone.startsWith(ccDigits) ? phone : (ccDigits + phone);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
        const sep = isIOS ? "&" : "?";
        const smsUrl = `sms:+${intl}${sep}body=${encodeURIComponent(msg)}`;
        window.location.href = smsUrl;
      } else if (method === "line") {
        // LINE share intent — opens LINE app (or web) with the message
        // pre-filled and lets the staff member pick which LINE contact
        // to send it to. No LINE ID required; no API setup. Works for
        // any device with LINE installed.
        const lineUrl = "https://line.me/R/share?text=" + encodeURIComponent(msg);
        window.open(lineUrl, "_blank");
      }
      onSend && onSend(method, msg);
      const label = method === "whatsapp" ? "WhatsApp" : method === "sms" ? zhEn("訊息 app", "Messages app") : "LINE";
      toast(label + zhEn(" 已開啟 💬", " opened 💬"));
    }, 400);
  };

  if (sent) return (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
      <div style={{ width: 72, height: 72, background: "#F0FDF4", borderRadius: 99, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><Check size={36} color="#10B981" strokeWidth={3} /></div>
      <p style={{ fontWeight: 800, fontSize: 18, color: "#4A4A68", margin: "0 0 4px" }}>{method === "whatsapp" ? zhEn("WhatsApp 已開啟", "WhatsApp opened") : method === "line" ? zhEn("LINE 已開啟", "LINE opened") : zhEn("訊息 app 已開啟", "Messages app opened")}{zhEn("！", "!")}</p>
      <p style={{ fontSize: 14, color: "#8A8A9E", fontWeight: 700, margin: 0 }}>{data.customerName + " (" + data.phone + ")"}</p>
      <Btn onClick={onClose} style={{ width: "100%", marginTop: 20 }}>{zhEn("完成", "Done")}</Btn>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 6, background: "var(--c-light)", borderRadius: 99, padding: 4 }}>
        {[["whatsapp", "WhatsApp 💬", "#22C55E"], ["sms", "SMS 📱", "#3B82F6"], ["line", "LINE 🟢", "#06C755"]].map(([v, l, c]) => (
          <button key={v} onClick={() => setMethod(v)} style={{ flex: 1, padding: "10px 0", borderRadius: 99, fontSize: 13, fontWeight: 800, border: "none", background: method === v ? c : "transparent", color: method === v ? "#fff" : "#8A8A9E", cursor: "pointer", transition: "all 0.2s" }}>{l}</button>
        ))}
      </div>
      <div style={{ background: method === "line" ? "#F0FDF4" : method === "whatsapp" ? "#F0FDF4" : "#EFF6FF", borderRadius: 20, padding: 16, border: "2px solid " + (method === "line" ? "#86EFAC" : method === "whatsapp" ? "#BBF7D0" : "#BFDBFE") }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: method === "line" ? "#15803D" : method === "whatsapp" ? "#166534" : "#1E40AF", margin: 0 }}>{zhEn("預覽訊息", "Message preview")}</p>
          <button onClick={() => setCustomMsg(msg)} style={{ background: "none", border: "none", fontSize: 12, color: "#8A8A9E", cursor: "pointer", fontWeight: 700 }}>{zhEn("✏️ 自訂", "✏️ Customise")}</button>
        </div>
        {customMsg ? (
          <textarea style={{ width: "100%", border: "2px solid var(--c-light)", borderRadius: 16, padding: "12px 14px", fontSize: 14, fontWeight: 600, height: 160, resize: "none", boxSizing: "border-box", outline: "none", color: "#4A4A68", lineHeight: 1.5 }} value={customMsg} onChange={e => setCustomMsg(e.target.value)} />
        ) : (
          <pre style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0, lineHeight: 1.5, fontWeight: 600 }}>{msg}</pre>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--c-lighter)", borderRadius: 16, padding: "12px 16px" }}>
        <Phone size={18} color="var(--c-primary)" strokeWidth={2.5} />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 14, fontWeight: 800, margin: "0 0 2px", color: "#4A4A68" }}>{data.customerName}</p>
          <p style={{ fontSize: 13, color: "#8A8A9E", margin: 0, fontWeight: 700 }}>{data.phone}</p>
        </div>
      </div>
      <Btn onClick={doSend} disabled={sending} style={{ width: "100%", padding: "16px", background: method === "line" ? "#06C755" : method === "whatsapp" ? "#22C55E" : "#3B82F6", boxShadow: "0 8px 24px " + (method === "line" ? "rgba(6,199,85,0.3)" : method === "whatsapp" ? "rgba(34,197,94,0.3)" : "rgba(59,130,246,0.3)") }}>
        <Send size={18} strokeWidth={2.5} /> {sending ? zhEn("處理中...", "Processing...") : (method === "whatsapp" ? zhEn("開啟 WhatsApp 發送", "Open WhatsApp to send") : method === "line" ? zhEn("開啟 LINE 分享", "Open LINE to share") : zhEn("開啟訊息 app 發送", "Open Messages app to send"))}
      </Btn>
    </div>
  );
};

// ── SUPPORT TICKET PANEL ───────────────────────────────────────────────────
// `mode` is "shop" (owner side — sees only their tenant's tickets, sends as
// senderRole=shop) or "platform" (superadmin — sees ALL tickets, sends as
// senderRole=platform).
// ── 網站查詢 inbox (platform admin) ────────────────────────────────────────
// The other end of SalesChatWidget. Visitors are anonymous, so there is no
// profile to open — what matters is the thread, the page they asked from, and
// whatever contact detail they volunteered.
const SalesChatPanel = () => {
  const [chats, setChats] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadChats = async () => {
    setLoading(true);
    try { setChats(await fetchSalesChats({ status: filter })); }
    catch (e) { toast("載入失敗：" + (e?.message || e), "error"); }
    setLoading(false);
  };
  useEffect(() => { loadChats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const openChat = async (c) => {
    setOpenId(c.id);
    try {
      setMsgs(await fetchSalesMessages(c.id));
      // Opening counts as handled even if there is nothing to say yet.
      if (c.unread) { await markSalesChatRead(c.id); loadChats(); }
    } catch (e) { toast("載入對話失敗：" + (e?.message || e), "error"); }
  };

  // Keep the open thread live so a reply typed here and a visitor reply there
  // don't talk past each other.
  useEffect(() => {
    if (!openId) return;
    const id = setInterval(async () => {
      try { setMsgs(await fetchSalesMessages(openId)); } catch { /* retry next tick */ }
    }, 10000);
    return () => clearInterval(id);
  }, [openId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body || !openId || sending) return;
    setSending(true);
    try {
      await replySalesChat(openId, body);
      setText("");
      setMsgs(await fetchSalesMessages(openId));
      loadChats();
    } catch (e) { toast("回覆失敗：" + (e?.message || e), "error"); }
    setSending(false);
  };

  const current = chats.find(c => c.id === openId);
  const FILTERS = [["open","進行中"],["closed","已結束"],["all","全部"]];

  if (openId && current) {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Card style={{padding:16}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <Btn size="sm" variant="ghost" onClick={() => { setOpenId(null); setMsgs([]); }}>← 返回</Btn>
              <p style={{fontSize:16,fontWeight:900,color:"#4A4A68",margin:"8px 0 2px"}}>
                {current.name || current.email || "訪客"}
                {current.topic && <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>{" · " + (current.topic === "sales" ? "銷售" : "技術")}</span>}
              </p>
              <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,wordBreak:"break-all"}}>
                {current.email || "未留電郵"}{current.phone ? " · " + current.phone : ""}
                {current.pageUrl ? " · 來自 " + current.pageUrl.replace(/^https?:\/\//, "").slice(0, 40) : ""}
              </p>
            </div>
            <Btn size="sm" variant="secondary" onClick={async () => {
              await setSalesChatStatus(current.id, current.status === "open" ? "closed" : "open");
              loadChats();
            }}>{current.status === "open" ? "結束" : "重開"}</Btn>
          </div>
        </Card>

        <Card style={{padding:0,overflow:"hidden"}}>
          <div style={{maxHeight:420,overflowY:"auto",padding:14,display:"flex",flexDirection:"column",gap:8,background:"#FFFDFD"}}>
            {msgs.map(m => {
              const mine = m.sender_role === "platform";
              return (
                <div key={m.id} style={{alignSelf: mine ? "flex-end" : "flex-start",maxWidth:"80%"}}>
                  <div style={{background: mine ? "var(--c-primary)" : "#F3F3F8",color: mine ? "#fff" : "#4A4A68",
                    borderRadius:14,padding:"9px 12px",fontSize:13,fontWeight:600,lineHeight:1.55,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                    {m.body}
                  </div>
                  <p style={{fontSize:10,color:"#AFAFC4",margin:"3px 4px 0",fontWeight:700,textAlign: mine ? "right" : "left"}}>
                    {String(m.created_at || "").slice(5,16).replace("T"," ")}
                  </p>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <div style={{display:"flex",gap:8,padding:12,borderTop:"1px solid #F3F3F8"}}>
            <input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="輸入回覆…"
              style={{flex:1,border:"2px solid var(--c-light)",borderRadius:99,padding:"10px 14px",fontSize:13,
                fontWeight:700,color:"#4A4A68",outline:"none",boxSizing:"border-box",minWidth:0}} />
            <Btn size="sm" onClick={send} disabled={sending || !text.trim()}>{sending ? "…" : "發送"}</Btn>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <Card style={{padding:20}}>
        <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>
          <MessageCircle size={20} color="var(--c-primary)" strokeWidth={2.5} /> 網站查詢
        </h3>
        <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>
          pettobook.com 訪客嘅銷售同技術查詢。回覆會即時出現喺佢哋個聊天視窗。
        </p>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {FILTERS.map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)} style={{padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:900,cursor:"pointer",
              border:"2px solid " + (filter===v ? "var(--c-primary)" : "var(--c-light)"),
              background: filter===v ? "var(--c-primary)" : "#fff", color: filter===v ? "#fff" : "#4A4A68"}}>{l}</button>
          ))}
        </div>
      </Card>

      {loading && <Card style={{padding:24,textAlign:"center",fontSize:13,fontWeight:700,color:"#8A8A9E"}}>載入中…</Card>}
      {!loading && chats.length === 0 && (
        <Card style={{padding:40,textAlign:"center"}}>
          <div style={{fontSize:40}}>💬</div>
          <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"8px 0 0"}}>暫時未有查詢</p>
        </Card>
      )}

      {!loading && chats.map(c => (
        <Card key={c.id} style={{padding:0,overflow:"hidden",cursor:"pointer"}} onClick={() => openChat(c)}>
          <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:15,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>
                {c.name || c.email || "訪客"}
                {c.topic && <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>{" · " + (c.topic === "sales" ? "銷售" : "技術")}</span>}
              </p>
              <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>
                {String(c.lastMsgAt || "").slice(0,16).replace("T"," ")}
                {c.lastMsgBy === "visitor" ? " · 等緊回覆" : " · 已回覆"}
              </p>
            </div>
            {c.unread > 0 && (
              <span style={{background:"#EF4444",color:"#fff",fontSize:11,fontWeight:900,padding:"3px 9px",borderRadius:99}}>{c.unread}</span>
            )}
            {c.status === "closed" && (
              <span style={{background:"#F3F4F6",color:"#8A8A9E",fontSize:11,fontWeight:900,padding:"3px 9px",borderRadius:99}}>已結束</span>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

// ── 店舖認領 (platform admin) ──────────────────────────────────────────────
// Someone found their salon in the public directory and asked to claim it.
// These are inbound leads: a real shop volunteering to onboard. They used to
// land in a table nobody could read (RLS on, no policies) and an email to a
// hardcoded cs@ address, so four sat untouched for six weeks.
//
// Approving does NOT auto-create a tenant — verifying that the claimant really
// runs the shop is a human step, and provisioning the wrong person the keys to
// a salon's bookings is not something to automate. This panel gets the claim
// in front of the admin with one tap to reply, and records the decision.
const ShopClaimsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [docUrl, setDocUrl] = useState({});   // claimId → signed URL
  const [docBusy, setDocBusy] = useState(null);

  const openDoc = async (r) => {
    if (docUrl[r.id]) { window.open(docUrl[r.id], "_blank", "noopener"); return; }
    setDocBusy(r.id);
    try {
      const u = await claimDocUrl(r.brDocPath);
      if (!u) throw new Error("冇檔案");
      setDocUrl(m => ({ ...m, [r.id]: u }));
      window.open(u, "_blank", "noopener");
    } catch (e) { toast("開啟文件失敗：" + (e?.message || e), "error"); }
    setDocBusy(null);
  };

  const load = async () => {
    setLoading(true);
    try { setRows(await fetchShopClaims({ status: filter })); }
    catch (e) { toast("載入失敗：" + (e?.message || e), "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const decide = async (row, status) => {
    if (status === "approved" && !window.confirm(
      `批准「${row.claimantName}」認領 ${row.shopName || "呢間店"}？\n\n` +
      `請先確認商業登記證上嘅名稱同地址，同目錄資料相符：\n` +
      `· ${row.listing?.name || row.shopName || "—"}\n` +
      `· ${row.listing?.address || "—"}\n\n` +
      `批准會即刻：開店舖帳戶、電郵登入資料畀 ${row.claimantEmail}、` +
      `並刪除已上載嘅商業登記證。`
    )) return;
    setBusyId(row.id);
    try {
      const r = await setShopClaimStatus(row.id, status, status === "rejected" ? note : undefined);
      // Say plainly whether the shop was actually emailed. A decision that
      // saved but failed to send is NOT the same as the shop being told, and
      // the admin needs to know to follow up by hand.
      if (status === "pending") toast("已重開（未通知對方）");
      else if (status === "approved") {
        // Three separate facts, and the admin needs all of them: was the shop
        // created, was the owner told, and if either failed, what to do next.
        // "Approved" alone used to imply all three.
        if (r?.provisionError) {
          toast("已批准，但未能開店舖：" + r.provisionError + " — 請手動處理", "error");
        } else if (r?.provisioned && r?.emailed) {
          toast("已批准 ✓ 店舖已開通，登入資料已電郵畀對方");
        } else if (r?.provisioned) {
          toast("店舖已開通，但電郵未發出 — 請手動將登入資料交畀對方", "warning");
        } else if (r?.emailed) {
          toast("已批准，已電郵通知（未自動開店舖）", "warning");
        } else {
          toast("已批准，但電郵未發出 — 請手動聯絡", "error");
        }
      }
      else if (r?.emailed) toast("已拒絕，已電郵通知對方 ✓");
      else toast("已拒絕，但電郵未發出 — 請手動聯絡", "error");
      setNoteFor(null); setNote("");
      await load();
    } catch (e) { toast("更新失敗：" + (e?.message || e), "error"); }
    setBusyId(null);
  };

  const FILTERS = [["pending","待處理"],["approved","已批准"],["rejected","已拒絕"],["all","全部"]];
  const badge = (st) => st === "pending"
    ? { bg:"#FEF3C7", fg:"#92400E", label:"待處理" }
    : st === "approved" ? { bg:"#DCFCE7", fg:"#166534", label:"已批准" }
    : { bg:"#FEE2E2", fg:"#991B1B", label:"已拒絕" };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <Card style={{padding:20}}>
        <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>
          <Store size={20} color="var(--c-primary)" strokeWidth={2.5} /> 店舖認領請求
        </h3>
        <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>
          有人喺公開目錄搵到自己間舖，要求認領。核實身份後再批准／拒絕 — 系統會自動電郵通知對方，然後人手開店舖帳戶。
        </p>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {FILTERS.map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)} style={{padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:900,cursor:"pointer",
              border:"2px solid " + (filter===v ? "var(--c-primary)" : "var(--c-light)"),
              background: filter===v ? "var(--c-primary)" : "#fff", color: filter===v ? "#fff" : "#4A4A68"}}>{l}</button>
          ))}
        </div>
      </Card>

      {loading && <Card style={{padding:24,textAlign:"center",fontSize:13,fontWeight:700,color:"#8A8A9E"}}>載入中…</Card>}

      {!loading && rows.length === 0 && (
        <Card style={{padding:40,textAlign:"center"}}>
          <div style={{fontSize:40}}>🏪</div>
          <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"8px 0 0"}}>
            {filter === "pending" ? "冇待處理嘅認領請求" : "冇紀錄"}
          </p>
        </Card>
      )}

      {!loading && rows.map(r => {
        const b = badge(r.status);
        const waPhone = (r.claimantPhone || "").replace(/\D/g, "");
        // HK numbers arrive as 8 digits; wa.me needs the country code.
        const waLink = waPhone ? "https://wa.me/" + (waPhone.length === 8 ? "852" + waPhone : waPhone) : null;
        const already = r.listing?.claimed_tenant_id;
        return (
          <Card key={r.id} style={{padding:18}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:16,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{r.shopName || r.listing?.name || "(未命名店舖)"}</p>
                <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>
                  {String(r.createdAt || "").slice(0,10)}
                  {r.listing?.region ? " · " + r.listing.region : ""}
                  {r.listing?.slug ? " · /listing/" + r.listing.slug : ""}
                </p>
              </div>
              <span style={{flexShrink:0,background:b.bg,color:b.fg,fontSize:11,fontWeight:900,padding:"4px 10px",borderRadius:99}}>{b.label}</span>
            </div>

            {/* Already-claimed listings are the dangerous case: approving a
                second claimant would hand over a shop that has an owner. */}
            {already && (
              <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12,padding:"8px 12px",marginBottom:10}}>
                <p style={{fontSize:12,fontWeight:900,color:"#991B1B",margin:0}}>⚠️ 呢個目錄項目已經被認領（已有店舖帳戶）— 批准前請小心核實。</p>
              </div>
            )}

            {/* ── The actual verification ────────────────────────────────
                A 商業登記證 carries the business NAME and its registered
                ADDRESS. Both are shown against the listing's own name and
                address so approving is a two-field comparison, not a guess.
                Phone is corroboration only: the listing's number is printed
                on the page the claimant is claiming from, so a typed match
                shows familiarity, not ownership. */}
            {(() => {
              const digits = (x) => String(x || "").replace(/\D/g, "");
              const lp = digits(r.listing?.phone), cp = digits(r.claimantPhone);
              const phoneMatch = lp && cp && (lp === cp || lp.endsWith(cp) || cp.endsWith(lp));
              return (
                <div style={{border:"2px solid var(--c-border)",borderRadius:14,padding:12,marginBottom:10,background:"#FFFDFD"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                    <p style={{fontSize:12,fontWeight:900,color:"var(--c-primary)",margin:0}}>🔍 核對商業登記證</p>
                    {r.brDocDeletedAt
                      ? <span style={{fontSize:10.5,fontWeight:800,color:"#8A8A9E"}}>文件已於批核後刪除</span>
                      : r.brDocPath
                        ? <Btn size="sm" variant="secondary" onClick={() => openDoc(r)} disabled={docBusy === r.id}>
                            {docBusy === r.id ? "開緊…" : (r.brDocMime === "application/pdf" ? "📄 開啟 BR" : "🖼️ 開啟 BR")}
                          </Btn>
                        : <span style={{fontSize:10.5,fontWeight:800,color:"#B45309",background:"#FEF3C7",borderRadius:99,padding:"2px 8px"}}>⚠️ 未有文件（舊申請）</span>}
                  </div>
                  <p style={{fontSize:10.5,fontWeight:700,color:"#8A8A9E",margin:"0 0 8px",lineHeight:1.45}}>
                    對照證書上嘅商業名稱同地址，同下面目錄資料是否相符：
                  </p>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <div>
                      <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:0}}>目錄名稱</p>
                      <p style={{fontSize:13,fontWeight:900,color:"var(--c-dark)",margin:0}}>{r.listing?.name || r.shopName || "—"}</p>
                    </div>
                    <div>
                      <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:0}}>目錄地址</p>
                      <p style={{fontSize:12.5,fontWeight:800,color:"var(--c-dark)",margin:0,lineHeight:1.45}}>{r.listing?.address || "—"}</p>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"baseline",flexWrap:"wrap"}}>
                      <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:0}}>電話</p>
                      <p style={{fontSize:12.5,fontWeight:800,color:"var(--c-dark)",margin:0}}>
                        {r.listing?.phone || "—"}
                        {r.claimantPhone ? <span style={{color:"#8A8A9E",fontWeight:700}}>{" / 申請人 " + r.claimantPhone}</span> : null}
                      </p>
                      {lp && cp && (phoneMatch
                        ? <span style={{fontSize:10,fontWeight:900,color:"#065F46",background:"#D1FAE5",borderRadius:99,padding:"2px 8px"}}>✅ 對得上</span>
                        : <span style={{fontSize:10,fontWeight:900,color:"#92400E",background:"#FEF3C7",borderRadius:99,padding:"2px 8px"}}>⚠️ 唔同</span>)}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{background:"var(--c-lighter)",borderRadius:12,padding:"10px 12px",display:"flex",flexDirection:"column",gap:4,marginBottom:10}}>
              <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0}}>
                {r.claimantName}{r.claimantRole ? <span style={{fontWeight:700,color:"#8A8A9E"}}>{" · " + r.claimantRole}</span> : null}
              </p>
              <p style={{fontSize:12,fontWeight:700,color:"#6A6A85",margin:0}}>✉️ {r.claimantEmail}</p>
              {r.claimantPhone && <p style={{fontSize:12,fontWeight:700,color:"#6A6A85",margin:0}}>📞 {r.claimantPhone}</p>}
              {r.listing?.address && <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:"2px 0 0"}}>📍 {r.listing.address}</p>}
              {r.message && <p style={{fontSize:12,color:"#4A4A68",margin:"6px 0 0",fontWeight:600,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{r.message}</p>}
              {r.adminNote && <p style={{fontSize:11,color:"#991B1B",margin:"6px 0 0",fontWeight:700}}>備註：{r.adminNote}</p>}
            </div>

            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <Btn size="sm" variant="secondary" onClick={() => { window.location.href = "mailto:" + r.claimantEmail + "?subject=" + encodeURIComponent("PettoBook — " + (r.shopName || "店舖認領")); }}>
                <Mail size={14} strokeWidth={2.5} /> 電郵
              </Btn>
              {waLink && <Btn size="sm" variant="secondary" onClick={() => window.open(waLink, "_blank", "noopener")}>
                <Phone size={14} strokeWidth={2.5} /> WhatsApp
              </Btn>}
              <div style={{flex:1}} />
              {r.status !== "approved" && (
                <Btn size="sm" onClick={() => decide(r, "approved")} disabled={busyId === r.id} style={{background:"#10B981",color:"#fff"}}>
                  <BadgeCheck size={14} strokeWidth={2.5} /> 批准
                </Btn>
              )}
              {r.status !== "rejected" && (
                <Btn size="sm" variant="secondary" onClick={() => { setNoteFor(r.id); setNote(""); }} disabled={busyId === r.id}>拒絕</Btn>
              )}
              {r.status !== "pending" && (
                <Btn size="sm" variant="ghost" onClick={() => decide(r, "pending")} disabled={busyId === r.id}>重開</Btn>
              )}
            </div>

            {noteFor === r.id && (
              <div style={{marginTop:10,background:"#FFFBEB",border:"1.5px solid #FDE68A",borderRadius:12,padding:12}}>
                <p style={{fontSize:12,fontWeight:900,color:"#92400E",margin:"0 0 6px"}}>拒絕原因（會保留畀之後跟進）</p>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="例：無法核實身份 / 重複申請"
                  style={{width:"100%",border:"2px solid #FDE68A",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
                <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
                  <Btn size="sm" variant="ghost" onClick={() => { setNoteFor(null); setNote(""); }}>取消</Btn>
                  <Btn size="sm" onClick={() => decide(r, "rejected")} disabled={busyId === r.id} style={{background:"#EF4444",color:"#fff"}}>確認拒絕</Btn>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ── Shop follow-up console (superadmin) ───────────────────────────────────
// Built after finding that 6 of 8 real shops signed up, stalled at one
// specific step and left WITHOUT ever opening a support ticket. Nothing in
// the product reached out when a shop went quiet, and the owner-side help
// channel is buried in 設定, so a stuck shop had no obvious way to ask.
//
// Deliberately manual: at this scale you want to read every reply, not
// automate a drip. The templates are starting points and are editable
// before sending — the point is to make contacting a stalled shop a
// 10-second job, not to mass-mail.
const FOLLOWUP_STAGES = {
  empty: {
    label: "空店舖", color: "#DC2626", bg: "#FEE2E2",
    why: "一個服務都冇 — 開店時嘅預設資料可能建立失敗",
  },
  no_prices: {
    label: "未填價錢", color: "#D97706", bg: "#FEF3C7",
    why: "有服務但一個價都未填 — 通常卡喺唔知點填價目矩陣",
  },
  no_customers: {
    label: "未加客戶", color: "#7C3AED", bg: "#EDE9FE",
    why: "價目已填好，但一個客都未入 — 多數係唔想逐個手動輸入",
  },
  no_booking: {
    label: "未有預約", color: "#2563EB", bg: "#DBEAFE",
    why: "有客戶但未開過單",
  },
  stalled: {
    label: "已靜止", color: "#6B7280", bg: "#F3F4F6",
    why: "曾經用過，但超過 30 日冇動靜",
  },
  active: {
    label: "活躍", color: "#059669", bg: "#D1FAE5",
    why: "最近有預約",
  },
};

const funnelStage = (r, nowMs = new Date().getTime()) => {
  const quiet = r.lastActivity ? (nowMs - new Date(r.lastActivity).getTime()) / 86400000 : 999;
  if (r.servicesCount === 0) return "empty";
  if (r.pricedCount === 0) return "no_prices";
  if (r.customersCount === 0) return "no_customers";
  if (r.aptsCount === 0) return "no_booking";
  return quiet > 30 ? "stalled" : "active";
};

// One template per stall point. Written to sound like a person who looked at
// the account, because that is what gets a reply — and every one ends with a
// question, since the goal is to LEARN why they stopped, not to sell.
const followupTemplate = (stage, r) => {
  const shop = r.shopName || "你嘅店舖";
  const hi = `${r.ownerName || "你好"}，我係 PettoBook 嘅 Wes。`;
  const sign = "\n\n直接覆呢封電郵就得，我會親自睇。\n\n— Wes · PettoBook";
  const map = {
    empty: {
      subject: `${shop} — 開店時好似出咗問題，我幫你睇睇？`,
      body: `${hi}\n\n我見到你 ${new Date(r.createdAt).toLocaleDateString(dateLoc())} 開咗「${shop}」，但個帳戶入面一個服務都冇 — 正常開店時系統會自動幫你建立一批服務同價目表，所以好可能係我哋嗰邊出咗錯。\n\n唔好意思，呢個唔係你做錯咗嘢。\n\n我可以幫你直接設定返，你只需要話我知你主要做狗定貓、大概幾多個服務就得。想我幫你搞掂嗎？${sign}`,
    },
    no_prices: {
      subject: `${shop} — 填價目表卡住咗？`,
      body: `${hi}\n\n見到你已經開咗「${shop}」同設定咗 ${r.servicesCount} 個服務，但價錢部分仲未填。\n\n填價目矩陣係成個系統最煩嘅一步，我知。我哋啱啱加咗「設定模板」— 揀一個最似你店嘅收費方式（3 級／5 級／7 級體型、要唔要分短長毛），體型分級同特殊品種會即刻幫你設定好，你淨係需要填數字。\n\n想我幫你設定埋？你只要 send 一張現有嘅價目表相片畀我，我可以幫你入晒。\n\n定係其實有第二樣嘢令你停低咗？${sign}`,
    },
    no_customers: {
      subject: `${shop} — 幫你將客人資料一次過搬入嚟`,
      body: `${hi}\n\n見到你已經將「${shop}」嘅服務同價目都設定好晒（${r.pricedCount} 個服務有價），但仲未加客戶。\n\n如果你同大部分店一樣有幾百個熟客喺筆記簿或者 WhatsApp 度，逐個打入去係冇可能嘅 — 呢步唔應該由你做。\n\n你可以 send 一個 Excel／CSV 畀我，我幫你一次過匯入；如果你係手寫本，影相畀我我都可以幫你入。\n\n想試嗎？定係有第二個原因令你停低？${sign}`,
    },
    no_booking: {
      subject: `${shop} — 要唔要我陪你開第一單？`,
      body: `${hi}\n\n見到你已經設定好「${shop}」同加咗 ${r.customersCount} 位客戶，但仲未開過預約單。\n\n想我用 15 分鐘 WhatsApp 通話陪你開第一單嗎？行一次流程之後通常就順晒。\n\n定係有啲位用起上嚟唔順手？直接話我知，我想改好佢。${sign}`,
    },
    stalled: {
      subject: `${shop} — 想知你點解冇再用`,
      body: `${hi}\n\n見到「${shop}」有排冇用過 PettoBook 喇。\n\n我唔係想推銷 — 我真係想知係邊度唔好用。係太煩？唔夠快？定係缺咗你需要嘅功能？\n\n一兩句就得，對我哋幫助好大。如果你已經轉咗用第二個系統，話我知邊個、好用喺邊，我都好想知。${sign}`,
    },
    active: {
      subject: `${shop} — 一切順利嗎？`,
      body: `${hi}\n\n見到「${shop}」用緊 PettoBook，多謝你！\n\n有冇咩用起上嚟唔順手，或者想加嘅功能？我想聽你講。${sign}`,
    },
  };
  return map[stage] || map.active;
};

const ShopFollowupPanel = ({ currentUserId }) => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [sheet, setSheet] = useState(null);   // { row, stage, subject, body }
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try { setRows(await fetchShopFunnel()); setErr(""); }
    catch (e) { setErr(e?.message || String(e)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSheet = async (r) => {
    const stage = funnelStage(r);
    const t = followupTemplate(stage, r);
    setSheet({ row: r, stage, subject: t.subject, body: t.body });
    try { setHistory(await fetchShopFollowups(r.tenantId)); } catch { setHistory([]); }
  };

  const send = async () => {
    if (!sheet) return;
    const { row, stage, subject, body } = sheet;
    if (!row.ownerEmail) { toast("呢間店冇登記電郵", "error"); return; }
    setBusy(true);
    let emailOk = false, inappOk = false, errMsg = "";
    // Email first — it's the one that actually reaches a shop that has
    // stopped logging in. The in-app bell only lands if they come back.
    try {
      await sendEmail({
        tenantId: row.tenantId,
        to: row.ownerEmail,
        subject,
        text: body,
        html: "<div style=\"font-family:-apple-system,Segoe UI,Roboto,sans-serif;font-size:15px;line-height:1.7;color:#333;white-space:pre-wrap\">"
          + body.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])) + "</div>",
        userId: row.ownerId,
      });
      emailOk = true;
    } catch (e) { errMsg += "email: " + (e?.message || e) + "; "; }
    try {
      if (row.ownerId) {
        await pushNotification({
          tenantId: row.tenantId, userId: row.ownerId,
          type: "platform", title: subject, body,
        });
        inappOk = true;
      }
    } catch (e) { errMsg += "inapp: " + (e?.message || e); }
    try {
      await logShopFollowup({
        tenantId: row.tenantId, stage, channel: "email+inapp",
        subject, body, sentTo: row.ownerEmail, sentBy: currentUserId,
        emailOk, inappOk, error: errMsg || undefined,
      });
    } catch (e) { console.warn("[followup] log failed", e); }
    setBusy(false);
    if (emailOk || inappOk) {
      toast(emailOk && inappOk ? "已發送（電郵 + 通知）✓" : emailOk ? "電郵已發送 ✓（通知失敗）" : "通知已發送（電郵失敗）", emailOk ? "success" : "warning");
      setSheet(null); load();
    } else {
      toast("發送失敗：" + errMsg, "error");
    }
  };

  // Sampled once per render — calling Date.now() inside a helper makes the
  // render impure and the row ages can disagree within one paint.
  const nowMs = new Date().getTime();
  const days = (d) => d ? Math.floor((nowMs - new Date(d).getTime()) / 86400000) : null;
  const list = (rows || []).filter(r => filter === "all" || funnelStage(r, nowMs) === filter);
  const counts = (rows || []).reduce((m, r) => { const s = funnelStage(r, nowMs); m[s] = (m[s] || 0) + 1; return m; }, {});

  return <div style={{display:"flex",flexDirection:"column",gap:14}}>
    <div>
      <h2 style={{fontWeight:900,fontSize:20,margin:"0 0 4px",color:"#4A4A68"}}>店舖跟進 📣</h2>
      <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>每間店卡喺邊一步，一撳發電郵 + 站內通知</p>
    </div>
    {err && <Card style={{padding:14,background:"#FEE2E2",border:"1.5px solid #FCA5A5"}}>
      <p style={{margin:0,fontSize:13,fontWeight:800,color:"#991B1B"}}>載入失敗：{err}</p></Card>}
    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      {["all", ...Object.keys(FOLLOWUP_STAGES)].map(k => {
        const on = filter === k;
        const st = FOLLOWUP_STAGES[k];
        const n = k === "all" ? (rows || []).length : (counts[k] || 0);
        if (k !== "all" && !n) return null;
        return <button key={k} onClick={() => setFilter(k)}
          style={{cursor:"pointer",fontSize:12,fontWeight:800,padding:"6px 12px",borderRadius:99,
            border:"2px solid "+(on ? (st?.color || "var(--c-primary)") : "var(--c-border)"),
            background: on ? (st?.bg || "var(--c-lighter)") : "#fff",
            color: on ? (st?.color || "var(--c-primary)") : "#6A6A85"}}>
          {(k === "all" ? "全部" : st.label) + " " + n}</button>;
      })}
    </div>
    {rows === null && <p style={{fontSize:13,fontWeight:700,color:"#8A8A9E"}}>載入中…</p>}
    {list.map(r => {
      const stage = funnelStage(r, nowMs);
      const st = FOLLOWUP_STAGES[stage];
      const quiet = days(r.lastActivity);
      return <Card key={r.tenantId} style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",flexWrap:"wrap"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <b style={{fontSize:15,fontWeight:900,color:"var(--c-dark)"}}>{r.shopName}</b>
              <span style={{fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,background:st.bg,color:st.color}}>{st.label}</span>
              <span style={{fontSize:10,fontWeight:800,color:"#8A8A9E"}}>{r.plan}</span>
            </div>
            <p style={{fontSize:11.5,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{r.ownerEmail || "（冇電郵）"}</p>
            <p style={{fontSize:11.5,color:st.color,margin:"4px 0 0",fontWeight:800}}>{st.why}</p>
          </div>
          <Btn size="sm" onClick={() => openSheet(r)} style={{flexShrink:0}}>✉️ 跟進</Btn>
        </div>
        <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:11,fontWeight:800,color:"#6A6A85"}}>
          <span>服務 {r.servicesCount}</span>
          <span>已填價 {r.pricedCount}</span>
          <span>客戶 {r.customersCount}</span>
          <span>預約 {r.aptsCount}</span>
          <span>{quiet == null ? "從未活動" : quiet + " 日前活動"}</span>
          {r.followupsCount > 0 && <span style={{color:"var(--c-primary)"}}>已跟進 {r.followupsCount} 次</span>}
        </div>
      </Card>;
    })}
    <BottomSheet open={!!sheet} onClose={() => !busy && setSheet(null)} title={"跟進：" + (sheet?.row?.shopName || "")}>
      {sheet && <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"var(--c-lighter)",borderRadius:14,padding:12}}>
          <p style={{fontSize:12,fontWeight:800,color:"var(--c-dark)",margin:0}}>
            寄去：{sheet.row.ownerEmail || "（冇電郵）"}
          </p>
          <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:"4px 0 0"}}>
            {FOLLOWUP_STAGES[sheet.stage].why}
          </p>
        </div>
        {history.length > 0 && <div style={{background:"#FEF3C7",border:"1.5px solid #FDE68A",borderRadius:14,padding:12}}>
          <p style={{fontSize:12,fontWeight:900,color:"#92400E",margin:"0 0 4px"}}>⚠️ 已經跟進過 {history.length} 次</p>
          {history.slice(0, 3).map(h => <p key={h.id} style={{fontSize:11,fontWeight:700,color:"#92400E",margin:"2px 0 0"}}>
            {new Date(h.created_at).toLocaleDateString(dateLoc())} · {h.stage} · {h.email_ok ? "已寄出" : "寄失敗"}
          </p>)}
        </div>}
        <Input label="主旨" value={sheet.subject} onChange={e => setSheet(s => ({ ...s, subject: e.target.value }))} />
        <div>
          <label style={{fontSize:12,fontWeight:800,color:"#6A6A85",display:"block",marginBottom:4}}>內容</label>
          <textarea value={sheet.body} onChange={e => setSheet(s => ({ ...s, body: e.target.value }))}
            rows={16} style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:12,fontSize:13,
              fontWeight:600,lineHeight:1.6,color:"var(--c-dark)",outline:"none",boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}} />
        </div>
        <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:0,lineHeight:1.5}}>
          會同時寄電郵同埋喺店主嘅站內通知鈴噹留一條訊息。發送後會記錄低，下次開返會提你。
        </p>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="secondary" onClick={() => setSheet(null)} disabled={busy} style={{flex:1}}>取消</Btn>
          <Btn onClick={send} disabled={busy || !sheet.row.ownerEmail} style={{flex:2}}>{busy ? "發送中…" : "確認發送"}</Btn>
        </div>
      </div>}
    </BottomSheet>
  </div>;
};

const SupportPanel = ({ tenantId = null, mode = "shop" }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTicket, setOpenTicket] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject: "", category: "general", priority: "normal", body: "" });
  const [statusFilter, setStatusFilter] = useState("active");

  const refresh = async () => {
    setLoading(true);
    try {
      const all = await fetchSupportTickets({ tenantId, all: mode === "platform" });
      setTickets(all || []);
    } catch (e) { toast(e?.message || "載入工單失敗", "error"); }
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tenantId, mode]);

  const openThread = async (t) => {
    setOpenTicket(t);
    setMsgs([]);
    try { setMsgs(await fetchTicketMessages(t.id)); }
    catch (e) { toast(e?.message || "載入訊息失敗", "error"); }
  };
  const send = async () => {
    const body = draft.trim();
    if (!body || !openTicket) return;
    try {
      const m = await postTicketMessage({ ticketId: openTicket.id, body, senderRole: mode });
      setMsgs(prev => [...prev, m]);
      setDraft("");
      refresh();
    } catch (e) { toast(e?.message || "發送失敗", "error"); }
  };
  const submitNew = async () => {
    const subject = newForm.subject.trim();
    const body = newForm.body.trim();
    if (!subject || !body) { toast("請填寫主題及內容", "error"); return; }
    if (!tenantId) { toast("缺少 tenant", "error"); return; }
    try {
      const t = await createSupportTicket({ tenantId, subject, body, category: newForm.category, priority: newForm.priority });
      toast("已開啟工單 ✓");
      setShowNew(false);
      setNewForm({ subject: "", category: "general", priority: "normal", body: "" });
      await refresh();
      openThread(t);
    } catch (e) { toast(e?.message || "建立失敗", "error"); }
  };
  const changeStatus = async (status) => {
    if (!openTicket) return;
    try {
      const updated = await updateTicketStatus(openTicket.id, status);
      setOpenTicket(updated);
      refresh();
    } catch (e) { toast(e?.message || "狀態更新失敗", "error"); }
  };

  const filtered = tickets.filter(t => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return t.status === "open" || t.status === "pending";
    return t.status === statusFilter;
  });
  const STATUS_LABEL = { open:"開啟", pending:"等待回覆", resolved:"已解決", closed:"已關閉" };
  const STATUS_COLOR = { open:"#F59E0B", pending:"#3B82F6", resolved:"#10B981", closed:"#9CA3AF" };
  const PRIORITY_LABEL = { low:"低", normal:"一般", high:"高", urgent:"緊急" };
  const PRIORITY_COLOR = { low:"#9CA3AF", normal:"#6B7280", high:"#F59E0B", urgent:"#EF4444" };

  return (
    <Card style={{padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:10,flexWrap:"wrap"}}>
        <div>
          <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#4A4A68"}}>💬 {mode === "platform" ? "支援工單（全平台）" : "客戶支援工單"}</h3>
          <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{mode === "platform" ? "PettoBook 收到嘅店主問題" : "聯絡 PettoBook 平台支援團隊"}</p>
        </div>
        {mode === "shop" && <Btn size="sm" onClick={() => setShowNew(true)}>＋ 開新工單</Btn>}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {[["active","🟡 進行中"],["resolved","✅ 已解決"],["closed","📁 已關閉"],["all","全部"]].map(([v,l]) => (
          <button key={v} onClick={() => setStatusFilter(v)} style={{padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:800,border:"2px solid "+(statusFilter===v?"var(--c-primary)":"var(--c-light)"),background:statusFilter===v?"var(--c-primary)":"#fff",color:statusFilter===v?"#fff":"#4A4A68",cursor:"pointer"}}>{l}</button>
        ))}
      </div>
      {loading ? <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>載入中…</p>
       : filtered.length === 0 ? <div style={{padding:30,textAlign:"center",color:"#8A8A9E",fontSize:13,fontWeight:700}}>暫無工單</div>
       : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(t => (
            <div key={t.id} onClick={() => openThread(t)} style={{padding:14,borderRadius:14,border:"2px solid var(--c-light)",cursor:"pointer",background:"#FFFDFD"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:4}}>
                <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0,flex:1}}>{t.subject}</p>
                <span style={{fontSize:10,fontWeight:800,padding:"3px 8px",borderRadius:99,background:STATUS_COLOR[t.status]+"22",color:STATUS_COLOR[t.status]}}>{STATUS_LABEL[t.status]}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#8A8A9E",fontWeight:700}}>
                <span>{PRIORITY_LABEL[t.priority] || t.priority} · {t.category || "其他"}</span>
                <span>{(t.lastMsgAt || t.updatedAt || "").slice(0,16).replace("T"," ")}</span>
              </div>
            </div>
          ))}
        </div>
       )}

      <BottomSheet open={!!openTicket} onClose={() => setOpenTicket(null)} title={openTicket?.subject || ""}>
        {openTicket && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",gap:6,alignItems:"center",fontSize:11,fontWeight:800,color:"#6A6A85"}}>
              <span style={{padding:"3px 8px",borderRadius:99,background:STATUS_COLOR[openTicket.status]+"22",color:STATUS_COLOR[openTicket.status]}}>{STATUS_LABEL[openTicket.status]}</span>
              <span style={{padding:"3px 8px",borderRadius:99,background:PRIORITY_COLOR[openTicket.priority]+"22",color:PRIORITY_COLOR[openTicket.priority]}}>{PRIORITY_LABEL[openTicket.priority]}</span>
              {openTicket.category && <span>· {openTicket.category}</span>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:360,overflowY:"auto",padding:6,background:"var(--c-lighter)",borderRadius:14}}>
              {msgs.length === 0 && <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,textAlign:"center",padding:14}}>暫無訊息</p>}
              {msgs.map(m => {
                const mine = m.senderRole === mode;
                return (
                  <div key={m.id} style={{alignSelf:mine?"flex-end":"flex-start",maxWidth:"82%",padding:"10px 14px",borderRadius:16,background:mine?"var(--c-primary)":"#fff",color:mine?"#fff":"#4A4A68",fontSize:13,fontWeight:600,lineHeight:1.5,whiteSpace:"pre-wrap",border:mine?"none":"2px solid var(--c-light)"}}>
                    <div style={{fontSize:10,opacity:0.7,marginBottom:4,fontWeight:800}}>{m.senderRole === "shop" ? "🏪 店主" : "🛟 PettoBook"} · {(m.createdAt || "").slice(0,16).replace("T"," ")}</div>
                    {m.body}
                  </div>
                );
              })}
            </div>
            <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="輸入訊息…" style={{width:"100%",minHeight:80,border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px",fontSize:13,fontFamily:"inherit",fontWeight:600,outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box",resize:"vertical"}} />
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <Btn onClick={send} disabled={!draft.trim()} style={{flex:1,minWidth:140}}>發送</Btn>
              {openTicket.status !== "resolved" && openTicket.status !== "closed" && (
                <Btn variant="secondary" size="sm" onClick={() => changeStatus("resolved")}>✅ 標記已解決</Btn>
              )}
              {openTicket.status !== "closed" && (
                <Btn variant="ghost" size="sm" onClick={() => changeStatus("closed")}>📁 關閉</Btn>
              )}
              {(openTicket.status === "resolved" || openTicket.status === "closed") && (
                <Btn variant="ghost" size="sm" onClick={() => changeStatus("open")}>🔄 重新開啟</Btn>
              )}
            </div>
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={showNew} onClose={() => setShowNew(false)} title="開新支援工單">
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Input label="主題" value={newForm.subject} onChange={e => setNewForm(f => ({...f, subject: e.target.value}))} placeholder="例：預約頁未能載入" />
          <Sel label="類別" value={newForm.category} onChange={e => setNewForm(f => ({...f, category: e.target.value}))}>
            <option value="general">一般查詢</option>
            <option value="billing">帳單／訂閱</option>
            <option value="bug">系統故障</option>
            <option value="feature">功能建議</option>
            <option value="account">帳號問題</option>
          </Sel>
          <Sel label="優先度" value={newForm.priority} onChange={e => setNewForm(f => ({...f, priority: e.target.value}))}>
            <option value="low">低</option>
            <option value="normal">一般</option>
            <option value="high">高</option>
            <option value="urgent">緊急</option>
          </Sel>
          <div>
            <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>內容</p>
            <textarea value={newForm.body} onChange={e => setNewForm(f => ({...f, body: e.target.value}))} placeholder="詳細描述問題或請求…" style={{width:"100%",minHeight:140,border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px",fontSize:13,fontFamily:"inherit",fontWeight:600,outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box",resize:"vertical"}} />
          </div>
          <Btn onClick={submitNew}>提交</Btn>
        </div>
      </BottomSheet>
    </Card>
  );
};

// ── STAFF EDITOR BOTTOM SHEET ──────────────────────────────────────────────
const StaffEditor = ({ staff, state, setState, tenantId, onClose, section = "staff", onPaySaved }) => {
  const wageOnly = section === "wage";
  const [staffTab, setStaffTab] = useState("basic");
  const isNew = !staff?.id;
  const [form, setForm] = useState({
    name: staff?.name || "", phone: staff?.phone || "",
    email: "", password: "",
    jobType: staff?.jobType || "fulltime",
    settleMode: staff?.settleMode || "daily",
    mpfEnabled: staff?.mpfEnabled === undefined ? null : staff.mpfEnabled,
    baseSalary: String(staff?.baseSalary || 0),
    hourlyRate: String(staff?.hourlyRate || 0), commissionMode: staff?.commissionMode || "percent",
    commissionRate: String(staff?.commissionRate || 10), commissionEnabled: staff?.commissionEnabled !== false,
    packageCommissionRate: String(staff?.packageCommissionRate || 0),
    packageRedemptionCommissionRate: String(staff?.packageRedemptionCommissionRate || 0),
    commissionTiers: Array.isArray(staff?.commissionTiers) ? staff.commissionTiers : [],
    serviceCommissionRates: (staff?.serviceCommissionRates && typeof staff.serviceCommissionRates === "object") ? { ...staff.serviceCommissionRates } : {},
    rateChangeLog: Array.isArray(staff?.rateChangeLog) ? staff.rateChangeLog : [],
    joinDate: staff?.joinDate || dateNow(),
    leaveDate: staff?.leaveDate || "", enabled: staff?.enabled !== false,
    schedulePreference: staff?.schedulePreference || "no_preference",
    staffHours: staff?.staffHours && typeof staff.staffHours === "object" ? { ...staff.staffHours } : {},
    wageLog: Array.isArray(staff?.wageLog) ? staff.wageLog.map(r => ({ ...r })) : [],
    canServiceIds: Array.isArray(staff?.canServiceIds) ? [...staff.canServiceIds] : [],
    timeOff: Array.isArray(staff?.timeOff) ? staff.timeOff.map(r => ({ ...r })) : [],
    permissions: staff?.permissions || { viewRevenue:false, manageCRM:false, manageSettings:false, discountCapPercent:20 },
  });
  const [saving, setSaving] = useState(false);
  const [svcRatesOpen, setSvcRatesOpen] = useState(false);
  const setPerm = (k, v) => setForm(p => ({ ...p, permissions: { ...(p.permissions||{}), [k]: v } }));
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const tenantServices = (state.tenants.find(t => t.id === tenantId)?.services || []).filter(sv => sv && sv.enabled !== false);
  const toggleService = (sid) => setForm(p => {
    const cur = Array.isArray(p.canServiceIds) ? p.canServiceIds : [];
    return { ...p, canServiceIds: cur.includes(sid) ? cur.filter(x => x !== sid) : [...cur, sid] };
  });
  const save = async () => {
    if (!form.name) { toast(i18nT("val.enterName"), "error"); return; }
    if (staff?.id) {
      // Edit existing staff — call patchProfile DIRECTLY so any RLS / unique
      // constraint violation surfaces immediately. The previous version used
      // setState + relied on syncStateDiff to write, which swallowed errors
      // and left owners with "looks saved" but stale DB data.
      setSaving(true);
      // Detect commission-field changes and append a rate_change_log entry
      // for audit. Snapshots before + after so the owner can answer "when
      // did we change Joey from 10% to 12%" months later.
      const before = {
        baseSalary: staff.baseSalary || 0,
        hourlyRate: staff.hourlyRate || 0,
        commissionMode: staff.commissionMode || "percent",
        commissionRate: Number(staff.commissionRate) || 0,
        commissionEnabled: staff.commissionEnabled !== false,
        packageCommissionRate: Number(staff.packageCommissionRate) || 0,
        packageRedemptionCommissionRate: Number(staff.packageRedemptionCommissionRate) || 0,
        commissionTiers: Array.isArray(staff.commissionTiers) ? staff.commissionTiers : [],
        serviceCommissionRates: staff.serviceCommissionRates || {},
      };
      const after = {
        // parseFloat (not parseInt) — owners enter $9.5/hr or $15000.50/mo
        // and the DB column is numeric. parseInt was silently dropping the
        // fractional part.
        baseSalary: parseFloat(form.baseSalary) || 0,
        hourlyRate: parseFloat(form.hourlyRate) || 0,
        commissionMode: form.commissionMode,
        commissionRate: parseFloat(form.commissionRate) || 0,
        commissionEnabled: form.commissionEnabled !== false,
        packageCommissionRate: parseFloat(form.packageCommissionRate) || 0,
        packageRedemptionCommissionRate: parseFloat(form.packageRedemptionCommissionRate) || 0,
        commissionTiers: (form.commissionTiers || []).map(t => ({ fromRev: Number(t.fromRev) || 0, rate: Number(t.rate) || 0 })),
        serviceCommissionRates: form.serviceCommissionRates || {},
      };
      const changed = JSON.stringify(before) !== JSON.stringify(after);
      const nextLog = changed
        ? [{ at: new Date().toISOString(), by: state?.users && Object.values(state.users).find(u => u.tenantId === tenantId && u.role === "owner")?.id, before, after }, ...(form.rateChangeLog || [])].slice(0, 100)
        : (form.rateChangeLog || []);
      const updates = {
        name: form.name,
        phone: form.phone || "",
        jobType: form.jobType,
        settleMode: form.jobType === "parttime" ? (form.settleMode || "daily") : "monthly",
        mpfEnabled: form.mpfEnabled,
        ...after,
        rateChangeLog: nextLog,
        schedulePreference: form.schedulePreference,
        staffHours: form.staffHours || {},
        canServiceIds: Array.isArray(form.canServiceIds) ? form.canServiceIds : [],
        timeOff: (form.timeOff || []).filter(r => r && r.from && r.to).map(r => ({ from: r.from, to: r.to, note: r.note || "" })),
        wageLog: (form.wageLog || [])
          .filter(r => r && r.date)
          .map(r => ({
            date: r.date,
            hours: Number(r.hours) || 0,
            amount: Math.round(Number(r.amount) || 0),
            note: r.note || "",
            at: r.at || new Date().toISOString(),
          })),
        permissions: form.permissions || {},
        joinDate: form.joinDate || null,
        leaveDate: form.leaveDate || null,
        enabled: form.enabled !== false,
      };
      try {
        // Pay lives in owner-only tables (colleagues could read it on the
        // profile). Split the save: profile fields → profiles; settings +
        // rate history → staff_pay_settings; wage days → staff_wage_entries.
        const PAY_KEYS = ["baseSalary","hourlyRate","commissionMode","commissionRate","commissionEnabled","packageCommissionRate",
          "packageRedemptionCommissionRate","commissionTiers","serviceCommissionRates","settleMode","mpfEnabled","rateChangeLog","wageLog"];
        const profileUpdates = Object.fromEntries(Object.entries(updates).filter(([k]) => !PAY_KEYS.includes(k)));
        await patchProfile(staff.id, profileUpdates);
        await upsertStaffPaySettings({ tenantId, staffId: staff.id, settings: {
          ...after, settleMode: updates.settleMode, mpfEnabled: !!updates.mpfEnabled, rateChangeLog: nextLog,
        } });
        // Wage days: upsert new/changed, delete removed.
        const oldByDate = Object.fromEntries((staff.wageLog || []).filter(r => r && r.date).map(r => [r.date, r]));
        const newByDate = Object.fromEntries(updates.wageLog.map(r => [r.date, r]));
        for (const [d, r] of Object.entries(newByDate)) {
          const o = oldByDate[d];
          if (!o || Number(o.hours) !== r.hours || Math.round(Number(o.amount) || 0) !== r.amount || (o.note || "") !== r.note) {
            await upsertWageEntry({ tenantId, staffId: staff.id, date: d, hours: r.hours, amount: r.amount, note: r.note });
          }
        }
        for (const d of Object.keys(oldByDate)) if (!newByDate[d]) await deleteWageEntry({ staffId: staff.id, date: d });
        setState(s => ({ ...s, users: { ...s.users, [staff.id]: { ...s.users[staff.id], ...profileUpdates } } }));
        onPaySaved && (await onPaySaved());
        toast(i18nT("tx.staffSaved"));
        onClose();
      } catch (e) {
        const msg = e?.message || String(e);
        if (/unique|duplicate|23505/i.test(msg) && /phone/i.test(msg)) {
          toast("此電話號碼已被其他員工／客戶使用", "error");
        } else {
          toast(msg || "儲存失敗", "error");
        }
      } finally {
        setSaving(false);
      }
    } else {
      if (!form.email) { toast(i18nT("val.enterEmail"), "error"); return; }
      if (!form.password || form.password.length < 6) { toast("密碼需要至少 6 個字元", "error"); return; }
      setSaving(true);
      try {
        const result = await createStaff({
          tenantId,
          email: form.email.trim(),
          password: form.password,
          name: form.name,
          phone: form.phone,
          jobType: form.jobType,
          baseSalary: parseInt(form.baseSalary) || 0,
          hourlyRate: parseInt(form.hourlyRate) || 0,
          commissionMode: form.commissionMode,
          commissionRate: parseFloat(form.commissionRate) || 0,
          commissionEnabled: form.commissionEnabled !== false,
          schedulePreference: form.schedulePreference,
          permissions: form.permissions,
          joinDate: form.joinDate || null,
        });
        // Optimistically add the new staff so the list reflects them immediately
        // without waiting for realtime/refresh. Use raw setState so syncStateDiff
        // sees the new user as "added" but skips it (profiles.added is ignored —
        // the edge function already created the row).
        const newId = result?.userId;
        if (newId) {
          // Pay settings go to the owner-only table, not the profile.
          try {
            await upsertStaffPaySettings({ tenantId, staffId: newId, settings: {
              baseSalary: parseFloat(form.baseSalary) || 0,
              hourlyRate: parseFloat(form.hourlyRate) || 0,
              commissionMode: form.commissionMode === "fixed" ? "fixed" : "percent",
              commissionRate: parseFloat(form.commissionRate) || 0,
              commissionEnabled: form.commissionEnabled !== false,
              packageCommissionRate: parseFloat(form.packageCommissionRate) || 0,
              packageRedemptionCommissionRate: parseFloat(form.packageRedemptionCommissionRate) || 0,
              commissionTiers: (form.commissionTiers || []).map(t => ({ fromRev: Number(t.fromRev) || 0, rate: Number(t.rate) || 0 })),
              serviceCommissionRates: form.serviceCommissionRates || {},
              settleMode: form.jobType === "parttime" ? (form.settleMode || "daily") : "monthly",
            } });
            onPaySaved && (await onPaySaved());
          } catch (payErr) {
            toast("員工已建立，但薪金設定未能儲存：" + (payErr?.message || payErr) + "。請到「薪金設定」再儲存一次。", "error");
          }
          setState(s => ({
            ...s,
            users: {
              ...s.users,
              [newId]: {
                id: newId, role: "staff", tenantId,
                name: form.name, phone: form.phone || "", email: form.email.trim(),
                enabled: true, jobType: form.jobType,
                baseSalary: parseInt(form.baseSalary) || 0,
                hourlyRate: parseInt(form.hourlyRate) || 0,
                commissionMode: form.commissionMode,
                commissionRate: parseFloat(form.commissionRate) || 0,
                commissionEnabled: form.commissionEnabled !== false,
                permissions: form.permissions || {},
                joinDate: form.joinDate || null,
                schedulePreference: form.schedulePreference || "no_preference",
                workDays: {}, rating: null, reviewCount: 0,
                clockRecords: [], wallet: 0, points: 0, walletTx: [], tags: [],
                pets: [],
              },
            },
            tenants: s.tenants.map(t => t.id === tenantId
              ? { ...t, staffIds: [...(t.staffIds || []), newId] }
              : t),
          }));
        }
        toast("新員工已加入 🎉 已透過 Email 發送登入資訊");
        onClose();
      } catch (e) {
        toast(e?.message || "新增員工失敗", "error");
      } finally {
        setSaving(false);
      }
    }
  };

  const scheduleOpts = [
    { v: "no_preference", l: "無偏好（系統自動分配）" },
    { v: "morning", l: "偏好上午 (9:00-13:00)" },
    { v: "afternoon", l: "偏好下午 (13:00-18:00)" },
    { v: "flexible", l: "彈性排班" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {wageOnly && (
        <div style={{ padding: "12px 14px", background: "var(--c-lighter)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 16, fontWeight: 900, color: "#4A4A68", margin: 0 }}>{form.name || staff?.name}</p>
            <p style={{ fontSize: 12, color: "#8A8A9E", margin: "2px 0 0", fontWeight: 700 }}>薪金與抽成設定 · {form.jobType === "fulltime" ? "全職" : "兼職"}</p>
          </div>
          {/* Quick exit to the full 員工編輯 sheet — keeps the wage
              dialog focused on rates while still letting the owner
              jump to rename / phone / job type / time-off without
              digging through tabs. */}
          {typeof window !== "undefined" && typeof window.__petosOpenFullStaffEdit === "function" && (
            <button
              type="button"
              onClick={() => { try { window.__petosOpenFullStaffEdit(staff); } catch { /* ignore */ } onClose?.(); }}
              style={{ background: "#fff", border: "1px solid var(--c-light)", borderRadius: 99, padding: "6px 12px", fontSize: 11, fontWeight: 900, color: "var(--c-primary)", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
            >
              ✏️ 編輯資料
            </button>
          )}
        </div>
      )}
      {/* Tabbed sections for staff management — easier to navigate */}
      {!wageOnly && (
        <div style={{display:"flex",gap:4,background:"var(--c-light)",borderRadius:14,padding:4,overflowX:"auto",position:"sticky",top:0,zIndex:2}}>
          {[["basic",i18nT("st.tabBasic")],["services",i18nT("st.tabService")],["time",i18nT("st.tabTime")],["leave",i18nT("st.tabLeave")],["perm",i18nT("st.tabPermissions")]].map(([v,l]) => (
            <button key={v} type="button" onClick={() => setStaffTab(v)} style={{flex:1,minWidth:54,padding:"8px 6px",borderRadius:10,fontSize:13,fontWeight:800,border:"none",background:staffTab===v?"#fff":"transparent",color:staffTab===v?"var(--c-primary)":"#9A9AAE",cursor:"pointer",boxShadow:staffTab===v?"0 1px 4px rgba(0,0,0,0.08)":"none",whiteSpace:"nowrap"}}>{l}</button>
          ))}
        </div>
      )}
      {!wageOnly && staffTab === "basic" && (<>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="姓名 *" value={form.name} onChange={e => f("name", e.target.value)} placeholder="員工姓名" />
        <Input label="聯絡電話" value={form.phone} onChange={e => f("phone", parseCopiedPhone(e.target.value).digits)} placeholder="9123 4567" type="tel" inputMode="tel" />
      </div>

      {isNew ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="登入 Email *" type="email" value={form.email} onChange={e => f("email", e.target.value)} placeholder="staff@example.com" />
          <Input label="臨時密碼 *" type="password" value={form.password} onChange={e => f("password", e.target.value)} placeholder="至少 6 個字元" />
        </div>
      ) : (
        <div style={{ background: "var(--c-lighter)", borderRadius: 16, padding: 14, border: "2px solid var(--c-border)" }}>
          <p style={{ fontSize: 12, fontWeight: 800, color: "#8A8A9E", margin: "0 0 6px" }}>登入帳號</p>
          <p style={{ fontSize: 14, fontWeight: 800, color: "#4A4A68", margin: "0 0 10px", wordBreak: "break-all" }}>
            {staff?.email || <span style={{ color: "#AFAFC4", fontWeight: 700 }}>未設定（透過 Google 登入或手機 OTP）</span>}
          </p>
          {staff?.email && !/@petos\.local$/i.test(staff.email) && (
            <Btn variant="secondary" size="sm" style={{ width: "100%" }} onClick={async () => {
              try { await sendPasswordReset(staff.email); toast("已發送密碼重設信至 " + staff.email); }
              catch (e) { toast(e?.message || "發送失敗", "error"); }
            }}><Send size={14} strokeWidth={2.5} /> 寄送密碼重設信</Btn>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, background: "var(--c-light)", borderRadius: 99, padding: 4 }}>
        {[["fulltime", "全職 🏢"], ["parttime", "兼職 ⏰"]].map(([v, l]) => (
          <button key={v} onClick={() => f("jobType", v)} style={{ flex: 1, padding: "10px 0", borderRadius: 99, fontSize: 14, fontWeight: 800, border: "none", background: form.jobType === v ? "var(--c-primary)" : "transparent", color: form.jobType === v ? "#fff" : "var(--c-grad1)", cursor: "pointer", transition: "all 0.2s" }}>{l}</button>
        ))}
      </div>
      </>)}

      {wageOnly && (form.jobType === "fulltime" ? (
        <Input label="月薪 ($)" type="number" value={form.baseSalary} onChange={e => f("baseSalary", e.target.value)} placeholder="例: 15000" />
      ) : (
        <>
          <Input label="時薪 ($)" type="number" value={form.hourlyRate} onChange={e => f("hourlyRate", e.target.value)} placeholder="例: 80" />
          <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
            <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>出薪週期</p>
            <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>影響薪金紀錄頁嘅顯示同提示。同一個員工只可揀一種。</p>
            <div style={{display:"flex",gap:8,background:"#fff",borderRadius:99,padding:4}}>
              {[["daily","日結 📅","每完成一日即支薪"],["monthly","月結 🗓️","月底一次過結算"]].map(([v,l,h]) => (
                <button key={v} onClick={() => f("settleMode", v)} style={{flex:1,padding:"10px 8px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:form.settleMode===v?"var(--c-primary)":"transparent",color:form.settleMode===v?"#fff":"var(--c-grad1)",cursor:"pointer",transition:"all 0.2s"}} title={h}>{l}</button>
              ))}
            </div>
            <p style={{fontSize:11,color:"#8A8A9E",margin:"10px 0 0",fontWeight:700,lineHeight:1.5}}>
              {form.settleMode === "monthly" ? "月結：每日只填工時，出薪一齊喺月底處理；已支咗嘅日結會自動扣減月結金額。" : "日結：每完成一日即記錄工時 + 出薪方式 + 金額。"}
            </p>
          </div>
        </>
      ))}

      {/* MPF picker disabled pending compliance work — see computeMpf comment. */}

      {wageOnly && <div style={{ background: "var(--c-lighter)", borderRadius: 20, padding: 16, border: "2px solid var(--c-border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: form.commissionEnabled !== false ? 12 : 0 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: "#4A4A68", margin: 0 }}>抽成設定 💰</p>
          <Toggle on={form.commissionEnabled !== false} onChange={v => f("commissionEnabled", v)} />
        </div>
        {form.commissionEnabled !== false && <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* How the rules combine (payroll.js): the old hints said tiers
              "override the percent above", but a per-service rate wins over
              both — owners couldn't tell which number applied. */}
          <p style={{fontSize:11.5,color:"#4B5563",fontWeight:700,margin:0,lineHeight:1.6,background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"8px 10px"}}>
            計算次序：① 有設「個別服務抽成」嘅服務，用該服務 % → ② 其餘訂單合計後，有「階梯式」就用階梯 → ③ 否則用下面嘅百分比 / 每單金額。
          </p>
          {staff?.id && (() => {
            // Live preview on LAST month's real bookings with the values in
            // this form — so an owner sees the effect before saving.
            const now = new Date();
            const settingsFromForm = {
              baseSalary: parseFloat(form.baseSalary) || 0, hourlyRate: parseFloat(form.hourlyRate) || 0,
              commissionEnabled: true, commissionMode: form.commissionMode === "fixed" ? "fixed" : "percent",
              commissionRate: parseFloat(form.commissionRate) || 0,
              commissionTiers: (form.commissionTiers || []).map(t => ({ fromRev: Number(t.fromRev) || 0, rate: Number(t.rate) || 0 })),
              serviceCommissionRates: form.serviceCommissionRates || {},
              packageRedemptionCommissionRate: parseFloat(form.packageRedemptionCommissionRate) || 0,
            };
            const run = (f, t) => computePay({ staff: { id: staff.id, jobType: form.jobType, joinDate: form.joinDate || null, leaveDate: form.leaveDate || null },
              settings: settingsFromForm, apts: state?.appointments || [], wages: staff.wageLog || [], from: localDateStr(f), to: localDateStr(t), revenueOf: aptRevenue });
            let pf = new Date(now.getFullYear(), now.getMonth() - 1, 1), pt = new Date(now.getFullYear(), now.getMonth(), 0);
            let periodLabel = `上個月（${pf.getMonth() + 1}月`;
            let pv0 = run(pf, pt);
            // Last month empty (new staff / new shop) → preview this month so far.
            if (pv0.orders.count === 0 && pv0.orders.packageVisits === 0) {
              pf = new Date(now.getFullYear(), now.getMonth(), 1); pt = now;
              periodLabel = `本月至今（${pf.getMonth() + 1}月`;
              pv0 = run(pf, pt);
            }
            const pv = pv0;
            const m = (n) => "$" + Math.round(n).toLocaleString();
            const parts = [form.jobType === "fulltime" ? "底薪 " + m(pv.base.amount) : "工資 " + m(pv.hourly.amount), "抽成 " + m(pv.serviceCommission + pv.redemptionCommission)];
            return (
              <div style={{background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:10,padding:"8px 10px"}}>
                <p style={{fontSize:11,fontWeight:800,color:"#065F46",margin:0}}>📊 用呢啲設定，按{periodLabel}，{pv.orders.count} 張完成訂單）試算</p>
                <p style={{fontSize:14,fontWeight:900,color:"#064E3B",margin:"2px 0 0"}}>{parts.join(" + ")} = {m(pv.base.amount + pv.hourly.amount + pv.serviceCommission + pv.redemptionCommission)}</p>
                <p style={{fontSize:10,fontWeight:700,color:"#047857",margin:"2px 0 0"}}>未計小費、套票銷售、獎金同扣減</p>
              </div>
            );
          })()}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Sel label="服務抽成方式" value={form.commissionMode} onChange={e => f("commissionMode", e.target.value)}>
              <option value="percent">百分比 %</option>
              <option value="fixed">固定金額 $</option>
            </Sel>
            <Input label={form.commissionMode === "percent" ? "服務抽成 (%)" : "每單抽成 ($)"} type="number" value={form.commissionRate} onChange={e => f("commissionRate", e.target.value)} />
          </div>
          <Input label={i18nT("st.packageCommission")} type="number" value={form.packageCommissionRate} onChange={e => f("packageCommissionRate", e.target.value)} hint={i18nT("st.packageCommissionHint")} />
          <Input label={i18nT("st.packageRedemptionCommission")} type="number" value={form.packageRedemptionCommissionRate} onChange={e => f("packageRedemptionCommissionRate", e.target.value)} hint={i18nT("st.packageRedemptionCommissionHint")} />
          {/* Per-service rate overrides — set a different % for specific
              services (e.g. SPA 5%, 沖涼剪毛 15%). Falls back to the flat /
              tiered rate above for services not listed here. */}
          {(() => {
            const tenant = (state?.tenants || []).find(t => t.id === tenantId);
            const allSvcs = tenant?.services || [];
            if (allSvcs.length === 0) return null;
            const overrides = form.serviceCommissionRates || {};
            const setCount = Object.keys(overrides).length;
            return (
              <div style={{background:"#FFFDFD",border:"1px solid #E5E7EB",borderRadius:14,padding:12}}>
                <button type="button" onClick={() => setSvcRatesOpen(o => !o)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left"}}>
                  <span style={{fontSize:12,fontWeight:800,color:"#374151"}}>例外：個別服務抽成{setCount ? `（已設 ${setCount} 項）` : "（未設定）"}</span>
                  <span style={{fontSize:12,fontWeight:900,color:"#6B7280"}}>{svcRatesOpen ? "收起 ▲" : "展開 ▼"}</span>
                </button>
                {svcRatesOpen && <>
                <p style={{fontSize:10.5,color:"#6B7280",fontWeight:700,margin:"6px 0 8px"}}>有填 % 嘅服務會優先用呢個 %，唔計階梯同下面嘅百分比。</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {allSvcs.map(sv => (
                    <div key={sv.id} style={{display:"grid",gridTemplateColumns:"1fr 80px",gap:8,alignItems:"center"}}>
                      <span style={{fontSize:12,fontWeight:700,color:"#4A4A68"}}>{(sv.kind==="spa"?"💆 ":"") + sv.name}</span>
                      <input
                        type="number"
                        value={overrides[sv.id] ?? ""}
                        placeholder="預設"
                        onChange={e => {
                          const v = e.target.value;
                          const next = { ...overrides };
                          if (v === "" || isNaN(parseFloat(v))) delete next[sv.id];
                          else next[sv.id] = parseFloat(v);
                          f("serviceCommissionRates", next);
                        }}
                        style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"6px 8px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}}
                      />
                    </div>
                  ))}
                </div>
                <p style={{fontSize:10,color:"#6B7280",fontWeight:700,margin:"8px 0 0"}}>※ 留空 = 用階梯 / 百分比計。數字直接係該服務嘅抽成 %。</p>
                </>}
              </div>
            );
          })()}
          {/* Tiered commission (optional) — if any band defined, OVERRIDES
              the flat percent above. e.g. 0–10000 = 8%, 10000+ = 12%. */}
          <div style={{background:"#FFFDFD",border:"2px dashed var(--c-light)",borderRadius:14,padding:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0}}>階梯式抽成（有填就代替百分比；個別服務例外照計）</p>
              <Btn size="sm" variant="secondary" onClick={() => f("commissionTiers", [...(form.commissionTiers || []), { fromRev: 0, rate: 0 }])}>+ 加一級</Btn>
            </div>
            {(form.commissionTiers || []).length === 0 && <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:0}}>未設定階梯。例：0 起 8%、10000 起 12%。</p>}
            {/* #26 Commission preview — owner enters a hypothetical monthly
                revenue and we apply the tiers exactly the way the payroll
                engine does. Helps the owner sanity-check the numbers
                before saving + explain payouts to staff. */}
            {(form.commissionTiers || []).length > 0 && (() => {
              const tiers = [...form.commissionTiers].sort((a,b) => (a.fromRev||0) - (b.fromRev||0));
              const samples = [3000, 8000, 15000, 30000];
              const calc = (rev) => {
                let total = 0;
                for (let i = 0; i < tiers.length; i++) {
                  const from = Number(tiers[i].fromRev) || 0;
                  const to = (i+1 < tiers.length) ? (Number(tiers[i+1].fromRev) || Infinity) : Infinity;
                  if (rev <= from) break;
                  const slice = Math.min(rev, to) - from;
                  total += slice * (Number(tiers[i].rate) || 0) / 100;
                }
                return Math.round(total);
              };
              return (
                <div style={{marginTop:10,background:"#EFF6FF",borderRadius:10,padding:"8px 10px",border:"1.5px solid #BFDBFE"}}>
                  <p style={{fontSize:11,fontWeight:800,color:"#1E40AF",margin:"0 0 6px"}}>💡 預覽抽成（依以上階梯計算）</p>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                    {samples.map(r => (
                      <div key={r} style={{textAlign:"center",background:"#fff",borderRadius:8,padding:"6px 4px"}}>
                        <p style={{fontSize:10,fontWeight:700,color:"#1E40AF",margin:0,opacity:0.7}}>{"營收 $"+r}</p>
                        <p style={{fontSize:13,fontWeight:900,color:"#1E40AF",margin:"2px 0 0"}}>{"$" + calc(r)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {(form.commissionTiers || []).map((t, i) => (
              <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:6,marginBottom:6,alignItems:"center"}}>
                <Input label={i===0?"營收門檻 ($)":""} type="number" value={t.fromRev} onChange={e => {
                  const next = [...form.commissionTiers]; next[i] = { ...next[i], fromRev: parseFloat(e.target.value) || 0 }; f("commissionTiers", next);
                }} />
                <Input label={i===0?"該段抽成 (%)":""} type="number" value={t.rate} onChange={e => {
                  const next = [...form.commissionTiers]; next[i] = { ...next[i], rate: parseFloat(e.target.value) || 0 }; f("commissionTiers", next);
                }} />
                <button onClick={() => f("commissionTiers", form.commissionTiers.filter((_, j) => j !== i))} title="刪除" style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"6px 8px",cursor:"pointer",alignSelf:i===0?"end":"center",marginBottom:i===0?2:0}}><Trash2 size={14} color="#EF4444" strokeWidth={2.5} /></button>
              </div>
            ))}
            {/* Validation hints — silent-bug guards. Surface bad tier
                configs the owner might miss (rate=0 band shadows the
                flat rate, rate>100% means giving away money, fromRev
                non-monotonic means later bands never apply). */}
            {(() => {
              const tiers = form.commissionTiers || [];
              if (tiers.length === 0) return null;
              const warnings = [];
              const zeroRateBands = tiers.filter(t => Number(t.rate) === 0).length;
              if (zeroRateBands > 0) warnings.push(zeroRateBands === tiers.length ? "全部階梯抽成 = 0%，等於關閉抽成" : `${zeroRateBands} 個階梯抽成為 0%，員工嗰段營收冇佣金`);
              const overRate = tiers.find(t => Number(t.rate) > 100);
              if (overRate) warnings.push("有階梯抽成 > 100%（" + overRate.rate + "%）— 你會蝕底，請檢查");
              const fromRevs = tiers.map(t => Number(t.fromRev) || 0);
              const sorted = [...fromRevs].sort((a, b) => a - b);
              const hasDuplicate = new Set(fromRevs).size !== fromRevs.length;
              if (hasDuplicate) warnings.push("營收門檻有重複 — 請每段用不同金額起點");
              else if (JSON.stringify(fromRevs) !== JSON.stringify(sorted)) warnings.push("營收門檻順序錯亂 — 應該由低到高排列");
              if (warnings.length === 0) return null;
              return (
                <div style={{marginTop:10,padding:"8px 10px",background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:10}}>
                  {warnings.map((w, wi) => (
                    <p key={wi} style={{fontSize:11,fontWeight:800,color:"#B91C1C",margin:wi===0?0:"4px 0 0"}}>⚠️ {w}</p>
                  ))}
                </div>
              );
            })()}
            {/* Flat-rate sanity guard (when no tiers, the flat % above is
                authoritative). Catches >100% input that would dwarf
                revenue. */}
            {form.commissionEnabled !== false && (form.commissionTiers || []).length === 0 && Number(form.commissionRate) > 100 && form.commissionMode === "percent" && (
              <div style={{marginTop:10,padding:"8px 10px",background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:10}}>
                <p style={{fontSize:11,fontWeight:800,color:"#B91C1C",margin:0}}>⚠️ 抽成 {form.commissionRate}% — 高過 100%，請檢查</p>
              </div>
            )}
          </div>
        </div>}
      </div>}

      {!wageOnly && staffTab === "basic" && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="入職日期" type="date" value={form.joinDate} onChange={e => f("joinDate", e.target.value)} />
        <Input label="離職日期（如已離職）" type="date" value={form.leaveDate} onChange={e => f("leaveDate", e.target.value)} />
      </div>}

      {/* Rate change audit log — view only. Last 50 changes shown. */}
      {wageOnly && (form.rateChangeLog || []).length > 0 && (
        <details style={{background:"#FFFDFD",border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px"}}>
          <summary style={{fontSize:12,fontWeight:800,color:"#4A4A68",cursor:"pointer"}}>📜 抽成設定變更記錄（{form.rateChangeLog.length} 項）</summary>
          <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:8,maxHeight:240,overflowY:"auto"}}>
            {form.rateChangeLog.slice(0, 50).map((entry, i) => {
              const at = (entry.at || "").slice(0, 10);
              const beforeDesc = entry.before ? ("舊：" + entry.before.commissionRate + "% / 套票 " + (entry.before.packageCommissionRate || 0) + "%") : "";
              const afterDesc = entry.after ? ("新：" + entry.after.commissionRate + "% / 套票 " + (entry.after.packageCommissionRate || 0) + "%") : "";
              return (
                <div key={i} style={{padding:"8px 10px",background:"var(--c-lighter)",borderRadius:8,fontSize:11,fontWeight:700,color:"#4A4A68"}}>
                  <p style={{margin:0,color:"var(--c-primary)"}}>{at}</p>
                  <p style={{margin:"3px 0 0"}}>{beforeDesc}</p>
                  <p style={{margin:"2px 0 0"}}>{afterDesc}</p>
                </div>
              );
            })}
          </div>
        </details>
      )}

      {!wageOnly && staffTab === "time" && <Sel label={zhEn("排班偏好", "Schedule preference")} value={form.schedulePreference} onChange={e => f("schedulePreference", e.target.value)}>
        {scheduleOpts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </Sel>}

      {/* ── Services this staff can provide ───────────────────────────── */}
      {!wageOnly && staffTab === "services" && <div style={{background:"#F5F3FF",borderRadius:20,padding:16,border:"2px solid #DDD6FE"}}>
        <p style={{fontSize:13,fontWeight:800,color:"#5B21B6",margin:"0 0 4px"}}>{zhEn("✂️ 可提供服務", "✂️ Services offered")}</p>
        <p style={{fontSize:11,color:"#5B21B6",margin:"0 0 10px",fontWeight:700,opacity:0.8,lineHeight:1.5}}>{zhEn("勾選此員工可以負責的服務。預約時只會分配識做嘅員工。", "Tick the services this staff member can handle. Bookings are only assigned to staff who can do them.")}<strong>{zhEn("全部唔揀 = 可做所有服務", "Select none = can do all services")}</strong>{zhEn("。", ". ")}</p>
        {tenantServices.length === 0
          ? <p style={{fontSize:12,color:"#8B5CF6",fontWeight:700,margin:0,opacity:0.8}}>{zhEn("尚未有服務，請先到「服務」頁新增。", "No services yet — add some on the Services page first.")}</p>
          : <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {tenantServices.map(sv => {
                const on = (form.canServiceIds || []).includes(sv.id);
                return <button key={sv.id} type="button" onClick={() => toggleService(sv.id)} style={{padding:"7px 12px",borderRadius:99,fontSize:12,fontWeight:800,border:"none",background:on?"#7C3AED":"#fff",color:on?"#fff":"#5B21B6",cursor:"pointer",boxShadow:on?"none":"inset 0 0 0 1.5px #DDD6FE"}}>{on?"✓ ":""}{sv.name}</button>;
              })}
            </div>}
      </div>}

      {/* Per-staff working hours + breaks — overrides shop business hours
          when a day is enabled. Customer booking + walk-in slots avoid the
          off-shift times AND the break windows. */}
      {!wageOnly && staffTab === "time" && (() => {
        const DAYS_ZH = [zhEn("日", "Sun"),zhEn("一", "Mon"),zhEn("二", "Tue"),zhEn("三", "Wed"),zhEn("四", "Thu"),zhEn("五", "Fri"),zhEn("六", "Sat")];
        const setDay = (dow, patch) => setForm(p => ({ ...p, staffHours: { ...(p.staffHours || {}), [dow]: { ...((p.staffHours || {})[dow] || {}), ...patch } } }));
        const addBreak = (dow) => setForm(p => {
          const cell = (p.staffHours || {})[dow] || {};
          const breaks = [...(cell.breaks || []), { start: "13:00", end: "14:00" }];
          return { ...p, staffHours: { ...(p.staffHours || {}), [dow]: { ...cell, breaks } } };
        });
        const setBreak = (dow, i, patch) => setForm(p => {
          const cell = (p.staffHours || {})[dow] || {};
          const breaks = (cell.breaks || []).map((b, j) => j === i ? { ...b, ...patch } : b);
          return { ...p, staffHours: { ...(p.staffHours || {}), [dow]: { ...cell, breaks } } };
        });
        const rmBreak = (dow, i) => setForm(p => {
          const cell = (p.staffHours || {})[dow] || {};
          const breaks = (cell.breaks || []).filter((_, j) => j !== i);
          return { ...p, staffHours: { ...(p.staffHours || {}), [dow]: { ...cell, breaks } } };
        });
        const applyToAll = (dow) => setForm(p => {
          const src = (p.staffHours || {})[dow] || {};
          const tpl = { enabled: true, open: src.open || "10:00", close: src.close || "19:00", breaks: (src.breaks || []).map(b => ({ ...b })) };
          const next = {};
          for (let d = 0; d <= 6; d++) next[d] = { ...tpl, breaks: tpl.breaks.map(b => ({ ...b })) };
          return { ...p, staffHours: next };
        });
        return (
          <div style={{background:"#F0F9FF",borderRadius:20,padding:16,border:"2px solid #BAE6FD"}}>
            <p style={{fontSize:13,fontWeight:800,color:"#0369A1",margin:"0 0 4px"}}>{zhEn("⏰ 每週工作時段 + 休息（覆蓋店鋪營業時間）", "⏰ Weekly working hours + breaks (overrides shop hours)")}</p>
            <p style={{fontSize:11,color:"#0369A1",margin:"0 0 10px",fontWeight:700,opacity:0.85,lineHeight:1.5}}>{zhEn("啟用某一天 = 用此開始/結束時間作可預約範圍;未啟用 = 依店鋪營業時間。休息時段內唔會接受預約。", "Enable a day = use these start / end times as the bookable range; not enabled = follow shop opening hours. No bookings are accepted during breaks.")}</p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[0,1,2,3,4,5,6].map(dow => {
                const cell = (form.staffHours || {})[dow] || {};
                const enabled = !!cell.enabled;
                const breaks = cell.breaks || [];
                return (
                  <div key={dow} style={{padding:"8px 10px",background:"#fff",borderRadius:12,border:"1.5px solid " + (enabled ? "#7DD3FC" : "#E0F2FE")}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:24,fontSize:13,fontWeight:900,color: enabled ? "#0369A1" : "#94A3B8",textAlign:"center"}}>{DAYS_ZH[dow]}</div>
                      <Toggle on={enabled} onChange={v => setDay(dow, { enabled: v, open: cell.open || "10:00", close: cell.close || "19:00" })} />
                      <input type="time" disabled={!enabled} value={cell.open || "10:00"} onChange={e => setDay(dow, { open: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid " + (enabled ? "#BAE6FD" : "#E5E7EB"),borderRadius:10,padding:"6px 6px",fontSize:13,fontWeight:800,outline:"none",color: enabled ? "#0369A1" : "#9CA3AF",background:"#fff",opacity: enabled ? 1 : 0.5}} />
                      <span style={{color: enabled ? "#0369A1" : "#94A3B8",fontWeight:700}}>–</span>
                      <input type="time" disabled={!enabled} value={cell.close || "19:00"} onChange={e => setDay(dow, { close: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid " + (enabled ? "#BAE6FD" : "#E5E7EB"),borderRadius:10,padding:"6px 6px",fontSize:13,fontWeight:800,outline:"none",color: enabled ? "#0369A1" : "#9CA3AF",background:"#fff",opacity: enabled ? 1 : 0.5}} />
                    </div>
                    {enabled && (
                      <div style={{marginTop:8,paddingLeft:32,display:"flex",flexDirection:"column",gap:6}}>
                        {breaks.map((b, i) => (
                          <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:11,fontWeight:800,color:"#F59E0B"}}>{zhEn("休息", "Day off")}</span>
                            <input type="time" value={b.start || "13:00"} onChange={e => setBreak(dow, i, { start: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid #FDE68A",borderRadius:10,padding:"5px 6px",fontSize:12,fontWeight:800,outline:"none",color:"#92400E",background:"#FFFBEB"}} />
                            <span style={{color:"#F59E0B",fontWeight:700}}>–</span>
                            <input type="time" value={b.end || "14:00"} onChange={e => setBreak(dow, i, { end: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid #FDE68A",borderRadius:10,padding:"5px 6px",fontSize:12,fontWeight:800,outline:"none",color:"#92400E",background:"#FFFBEB"}} />
                            <button type="button" onClick={() => rmBreak(dow, i)} style={{background:"#FEF2F2",border:"none",borderRadius:8,width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}><Trash2 size={12} color="#EF4444" strokeWidth={2.5}/></button>
                          </div>
                        ))}
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <button type="button" onClick={() => addBreak(dow)} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:99,background:"#FEF3C7",border:"none",color:"#B45309",fontWeight:800,fontSize:11,cursor:"pointer"}}><Plus size={12} strokeWidth={3}/> {zhEn("加休息", "Add break")}</button>
                          <button type="button" onClick={() => applyToAll(dow)} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:99,background:"#E0F2FE",border:"none",color:"#0369A1",fontWeight:800,fontSize:11,cursor:"pointer"}}><Copy size={12} strokeWidth={2.5}/> {zhEn("套用至每一天", "Apply to every day")}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Time off / holidays — multiple periods ─────────────────────── */}
      {!wageOnly && staffTab === "leave" && (() => {
        const rows = form.timeOff || [];
        const addRow = () => setForm(p => ({ ...p, timeOff: [{ from: dateNow(), to: dateNow(), note: "" }, ...(p.timeOff || [])] }));
        const updRow = (i, patch) => setForm(p => ({ ...p, timeOff: (p.timeOff || []).map((r, j) => j === i ? { ...r, ...patch } : r) }));
        const rmRow = (i) => setForm(p => ({ ...p, timeOff: (p.timeOff || []).filter((_, j) => j !== i) }));
        return (
          <div style={{background:"#FFF7ED",borderRadius:20,padding:16,border:"2px solid #FED7AA"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,gap:8}}>
              <div style={{minWidth:0}}>
                <p style={{fontSize:13,fontWeight:800,color:"#9A3412",margin:0}}>{zhEn("🏖️ 休假 / 放假", "🏖️ Time off / holidays")}</p>
                <p style={{fontSize:11,color:"#9A3412",margin:"2px 0 0",fontWeight:700,opacity:0.8}}>{zhEn("可加多段日期;期間內唔會接受預約。", "You can add several date ranges; no bookings are accepted during them.")}</p>
              </div>
              <button type="button" onClick={addRow} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"6px 12px",borderRadius:99,background:"#EA580C",border:"none",color:"#fff",fontWeight:900,fontSize:12,cursor:"pointer",flexShrink:0}}><Plus size={13} strokeWidth={3}/> {zhEn("加休假", "Add time off")}</button>
            </div>
            {rows.length === 0 && <p style={{fontSize:12,color:"#9A3412",fontWeight:700,opacity:0.75,textAlign:"center",padding:"8px 0",margin:0}}>{zhEn("暫無休假", "No time off set")}</p>}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {rows.map((r, i) => (
                <div key={i} style={{background:"#fff",borderRadius:12,padding:10,border:"1.5px solid #FED7AA",display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <input type="date" value={r.from || ""} onChange={e => updRow(i, { from: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid #FED7AA",borderRadius:10,padding:"7px 8px",fontSize:12,fontWeight:800,outline:"none",color:"#9A3412",background:"#FFF7ED"}} />
                    <span style={{color:"#EA580C",fontWeight:700}}>→</span>
                    <input type="date" value={r.to || ""} onChange={e => updRow(i, { to: e.target.value })} style={{flex:1,minWidth:0,border:"1.5px solid #FED7AA",borderRadius:10,padding:"7px 8px",fontSize:12,fontWeight:800,outline:"none",color:"#9A3412",background:"#FFF7ED"}} />
                    <button type="button" onClick={() => rmRow(i)} style={{background:"#FEF2F2",border:"none",borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}><Trash2 size={13} color="#EF4444" strokeWidth={2.5}/></button>
                  </div>
                  <input value={r.note || ""} onChange={e => updRow(i, { note: e.target.value })} placeholder={zhEn("原因（選填，例：年假 / 病假）", "Reason (optional, e.g. annual leave / sick leave)")} style={{border:"1.5px solid #FED7AA",borderRadius:10,padding:"7px 8px",fontSize:12,fontWeight:700,outline:"none",color:"#9A3412",background:"#FFF7ED"}} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* History view moved to a dedicated sheet (員工卡片 → 薪金紀錄
          button) so 薪金設定 stays focused on rates / commission. */}

      {/* Permissions */}
      {!wageOnly && staffTab === "perm" && (<>
      <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
        <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 10px"}}>{zhEn("權限 🔐", "Permissions 🔐")}</p>
        {[
          ["viewRevenue",   zhEn("可查看營收數據", "Can view revenue figures")],
          ["manageCRM",     zhEn("可管理客戶與毛孩資料", "Can manage customers & pets")],
        ].map(([k,l]) => (
          <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0"}}>
            <span style={{fontSize:13,color:"#4A4A68",fontWeight:700}}>{l}</span>
            <Toggle on={!!form.permissions?.[k]} onChange={v => setPerm(k, v)} />
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0 0",marginTop:6,borderTop:"1px dashed var(--c-border)"}}>
          <span style={{fontSize:13,color:"#4A4A68",fontWeight:700}}>{zhEn("最高可給予折扣 (%)", "Maximum discount allowed (%)")}</span>
          <input type="number" min="0" max="100" value={form.permissions?.discountCapPercent ?? 20} onChange={e => setPerm("discountCapPercent", Math.max(0, Math.min(100, parseInt(e.target.value)||0)))} style={{width:70,border:"2px solid var(--c-light)",borderRadius:12,padding:"6px 10px",fontSize:14,fontWeight:800,textAlign:"center",outline:"none",color:"#4A4A68"}} />
        </div>
        <p style={{fontSize:11,color:"#8A8A9E",margin:"4px 0 0",fontWeight:700}}>{zhEn("店主無上限。超過此上限的折扣會被阻擋並提示需店主授權。", "No limit for the owner. Discounts above this limit are blocked and need owner authorisation.")}</p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderRadius: 20, border: "2px solid " + (form.enabled ? "#BBF7D0" : "#FECACA"), background: form.enabled ? "#F0FDF4" : "#FEF2F2" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {form.enabled ? <UserCheck size={20} color="#10B981" strokeWidth={2.5} /> : <UserX size={20} color="#EF4444" strokeWidth={2.5} />}
          <span style={{ fontSize: 14, fontWeight: 800, color: form.enabled ? "#166534" : "#991B1B" }}>{form.enabled ? zhEn("帳號啟用中", "Account active") : zhEn("帳號已停用", "Account disabled")}</span>
        </div>
        <Toggle on={form.enabled} onChange={v => f("enabled", v)} />
      </div>
      </>)}

      <Btn onClick={save} disabled={saving} style={{ width: "100%", padding: "16px" }}><Save size={18} strokeWidth={2.5} /> {saving ? zhEn("處理中…", "Processing…") : (wageOnly ? zhEn("儲存薪金設定", "Save pay settings") : (staff?.id ? zhEn("儲存修改", "Save changes") : zhEn("新增員工", "Add staff")))}</Btn>
    </div>
  );
};

// ── BUSINESS HOURS EDITOR ──────────────────────────────────────────────────
const BusinessHoursEditor = ({ hours, onChange }) => {
  const dayNames = ["日 (Sun)", "一 (Mon)", "二 (Tue)", "三 (Wed)", "四 (Thu)", "五 (Fri)", "六 (Sat)"].map((d, i) => zhEn(d, ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][i]));
  // Extended range 08:00-22:00 (was 08:00-20:00) so late-evening shops can
  // configure proper hours. Half-hour granularity.
  const timeOpts = (() => {
    const out = [];
    for (let h = 8; h <= 22; h++) {
      out.push(String(h).padStart(2,"0") + ":00");
      if (h < 22) out.push(String(h).padStart(2,"0") + ":30");
    }
    return out;
  })();
  const update = (day, field, val) => onChange({ ...hours, [day]: { ...hours[day], [field]: val } });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[0, 1, 2, 3, 4, 5, 6].map(day => {
        const d = hours[day] || { enabled: false, open: "09:00", close: "18:00" };
        return (
          <div key={day} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 16, background: d.enabled ? "var(--c-lighter)" : "#F8F8FC", border: "2px solid " + (d.enabled ? "var(--c-border)" : "#EAEAF2") }}>
            <div style={{ width: 72, flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: d.enabled ? "#4A4A68" : "#AFAFC4" }}>{dayNames[day]}</span>
            </div>
            <Toggle on={d.enabled} onChange={v => update(day, "enabled", v)} />
            {d.enabled ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                <select value={d.open} onChange={e => update(day, "open", e.target.value)} style={{ flex: 1, border: "2px solid var(--c-light)", borderRadius: 12, padding: "8px 10px", fontSize: 13, fontWeight: 700, outline: "none", background: "#FFFDFD", color: "#4A4A68" }}>
                  {timeOpts.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <span style={{ color: "#AFAFC4", fontWeight: 800 }}>—</span>
                <select value={d.close} onChange={e => update(day, "close", e.target.value)} style={{ flex: 1, border: "2px solid var(--c-light)", borderRadius: 12, padding: "8px 10px", fontSize: 13, fontWeight: 700, outline: "none", background: "#FFFDFD", color: "#4A4A68" }}>
                  {timeOpts.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: "#AFAFC4", fontWeight: 700, flex: 1 }}>{zhEn("休息日", "Day off")}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const FeatureGate = ({ tenantId, feature, state, setState, children }) => {
  const [show, setShow] = useState(false); const [paid, setPaid] = useState(false);
  const tenant = state.tenants.find(t => t.id === tenantId);
  const plan = PLANS[tenant?.plan || "free"];
  if (plan.features[feature]) return children;
  const doUpgrade = async () => {
    try {
      const { url } = await createSubscriptionCheckout({ tenantId, plan: "pro", returnUrl: window.location.href, expectedAmount: Number(PLANS.pro?.price) });
      if (url) { window.location.href = url; return; }
      throw new Error(zhEn("無法建立付款連結", "Couldn't create a payment link"));
    } catch (e) {
      toast(e?.message || zhEn("升級失敗，請稍後再試", "Upgrade failed, please try again later"), "error");
    }
  };
  return <>
    <div onClick={() => setShow(true)} style={{cursor:"pointer",opacity:0.55}}>{children}</div>
    <BottomSheet open={show} onClose={() => setShow(false)} title={zhEn("升級解鎖此功能", "Upgrade to unlock this feature")}>
      {paid
        ? <div style={{textAlign:"center",padding:"32px 0"}}><div style={{width:80,height:80,background:"#F0FDF4",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}><Check size={40} color="#10B981" strokeWidth={3} /></div><p style={{fontWeight:800,fontSize:20,color:"#4A4A68"}}>{zhEn("升級成功！", "Upgrade successful!")}</p></div>
        : <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{background:"#FFFBEB",borderRadius:24,padding:16,display:"flex",gap:12,alignItems:"center"}}><Lock size={24} color="#D97706" style={{flexShrink:0}} /><div><p style={{fontWeight:800,color:"#92400E",margin:"0 0 4px",fontSize:16}}>{zhEn("需升級至專業版", "Requires the Pro plan")}</p><p style={{fontSize:13,color:"#B45309",margin:0,fontWeight:600}}>{zhEn("解鎖所有進階功能", "Unlock all advanced features")}</p></div></div>
            {stripeBillingLive(state?.platformConfig)
              ? <Btn onClick={doUpgrade} style={{width:"100%"}}>{zhEn("立即升級專業版", "Upgrade to Pro now")}</Btn>
              : <div style={{background:"#F3F4F6",borderRadius:16,padding:"14px 16px",textAlign:"center"}}>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("線上升級暫未開放", "Online upgrades aren't available yet")}</p>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{isNativeApp() ? zhEn("此功能需要較高方案。", "This feature needs a higher plan.") : zhEn("想升級請直接聯絡我哋，我哋會幫你開通。", "Contact us and we'll upgrade you.")}</p>
                </div>}
          </div>
      }
    </BottomSheet>
  </>;
};

// FIX: signature pad uses canvas.width/height dynamically, not hardcoded 320
const SigPad = ({ onSave, ageThreshold = 10 }) => {
  const ref = useRef(null); const dr = useRef(false);
  const gp = (e, c) => { const r = c.getBoundingClientRect(), s = e.touches ? e.touches[0] : e; return { x: (s.clientX - r.left) * (c.width / r.width), y: (s.clientY - r.top) * (c.height / r.height) }; };
  const start = e => { dr.current = true; const c = ref.current, ctx = c.getContext("2d"), p = gp(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = e => { if (!dr.current) return; const c = ref.current, ctx = c.getContext("2d"), p = gp(e, c); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "var(--c-primary)"; ctx.lineWidth = 4; ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.stroke(); };
  const end = () => { dr.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{background:"#FEF2F2",borderRadius:24,padding:16,display:"flex",gap:12,alignItems:"center"}}><AlertTriangle size={24} color="#EF4444" style={{flexShrink:0}} /><p style={{fontSize:13,color:"#DC2626",margin:0,fontWeight:700,lineHeight:1.4}}>{zhEn("您的寵物超過 " + ageThreshold + " 歲，屬高齡動物，美容過程存在一定風險，請簽署同意書。", "Your pet is over " + ageThreshold + " years old. Grooming a senior animal carries some risk, so please sign the consent form.")}</p></div>
      <canvas ref={ref} width={640} height={360} style={{border:"2px dashed var(--c-grad2)",borderRadius:24,width:"100%",touchAction:"none",background:"#FFFDFD"}} onMouseDown={start} onMouseMove={draw} onMouseUp={end} onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
      <p style={{fontSize:13,textAlign:"center",color:"#AFAFC4",margin:0,fontWeight:700}}>{zhEn("請在上方簽名", "Sign above")}</p>
      <div style={{display:"flex",gap:12}}>
        <Btn variant="secondary" onClick={clear} style={{flex:1}}>{zhEn("清除", "Clear")}</Btn>
        <Btn onClick={() => onSave(ref.current.toDataURL())} style={{flex:1}}>{zhEn("確認簽名", "Confirm signature")}</Btn>
      </div>
    </div>
  );
};

// Owner-facing editor for the per-shop health-report template. Loads the
// current tenant.reportTemplate (or seeds from REPORT_DEFAULT_FIELDS if
// none set), lets the shop toggle / rename / retype fields, edit chip
// options, reorder, and add custom fields. Persists to tenants.report_template
// on save; the ReportSheet reads it live via resolveReportFields().
const ReportTemplateEditor = ({ open, tenant, onClose, onSave }) => {
  const [fields, setFields] = useState([]);
  useEffect(() => {
    if (!open) return;
    const src = (tenant && Array.isArray(tenant.reportTemplate) && tenant.reportTemplate.length)
      ? tenant.reportTemplate
      : REPORT_DEFAULT_FIELDS;
    // Deep-clone + normalise so edits don't mutate defaults.
    setFields(src.map((f, i) => ({
      key: String(f.key || ("f" + i)),
      label: f.label || REPORT_LABELS[f.key] || f.key || "",
      type: f.type === "text" ? "text" : "chip",
      options: Array.isArray(f.options) && f.options.length
        ? [...f.options]
        : [...(REPORT_CHIPS[f.key] || ["正常","中","差"])],
      enabled: f.enabled !== false,
      order: Number(f.order) || i,
    })));
  }, [open, tenant]);

  const updateAt = (i, patch) => setFields(list => list.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const move = (i, dir) => setFields(list => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next.map((f, idx) => ({ ...f, order: idx }));
  });
  const remove = (i) => setFields(list => list.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, order: idx })));
  const addField = () => {
    const nextIdx = fields.length + 1;
    const key = "custom" + nextIdx;
    setFields(list => [...list, {
      key, label: "自訂欄位 " + nextIdx,
      type: "chip", options: ["正常","注意","異常"],
      enabled: true, order: list.length,
    }]);
  };
  const addOption = (i) => updateAt(i, { options: [...fields[i].options, "新選項"] });
  const setOption = (i, oi, v) => updateAt(i, { options: fields[i].options.map((o, idx) => idx === oi ? v : o) });
  const removeOption = (i, oi) => updateAt(i, { options: fields[i].options.filter((_, idx) => idx !== oi) });

  const canSave = fields.every(f => f.key && f.label && (f.type === "text" || (f.options && f.options.length)));

  return (
    <BottomSheet open={open} onClose={onClose} title={zhEn("自訂健康報告欄位", "Customise health report fields")} z={340}>
      <div style={{display:"flex",flexDirection:"column",gap:14,paddingBottom:8}}>
        <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>
          {zhEn("可以隨時新增、移除、重新排列。", "Add, remove and reorder any time. ")}<b style={{color:"var(--c-primary)"}}>{zhEn("文字", "Text")}</b>{zhEn("類型會顯示大輸入格，方便美容師詳細描述；", " fields show a large input box so groomers can describe in detail; ")}<b style={{color:"var(--c-primary)"}}>{zhEn("選項", "Options")}</b>{zhEn("類型會顯示 3 個一按嘅按鈕。", " fields show 3 one-tap buttons.")}
        </p>
        {fields.map((f, i) => (
          <div key={i} style={{border:"2px solid var(--c-light)",borderRadius:20,padding:14,background: f.enabled ? "#fff" : "#FFF7F0"}}>
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
              <input
                value={f.label}
                onChange={e => updateAt(i, { label: e.target.value })}
                placeholder={zhEn("欄位名稱", "Field name")}
                style={{flex:1,border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none"}}
              />
              <button onClick={() => move(i, -1)} disabled={i===0}
                style={{padding:"8px 10px",borderRadius:10,border:"2px solid var(--c-light)",background:"#fff",fontWeight:900,cursor: i===0 ? "not-allowed" : "pointer",opacity: i===0 ? 0.4 : 1}}>↑</button>
              <button onClick={() => move(i, +1)} disabled={i===fields.length-1}
                style={{padding:"8px 10px",borderRadius:10,border:"2px solid var(--c-light)",background:"#fff",fontWeight:900,cursor: i===fields.length-1 ? "not-allowed" : "pointer",opacity: i===fields.length-1 ? 0.4 : 1}}>↓</button>
              <Toggle on={f.enabled} onChange={v => updateAt(i, { enabled: v })} />
            </div>

            <div style={{display:"flex",gap:6,marginBottom:10}}>
              {["chip","text"].map(t => (
                <button key={t} onClick={() => updateAt(i, { type: t })}
                  style={{
                    flex:1,padding:"8px 10px",borderRadius:10,fontSize:12,fontWeight:900,cursor:"pointer",
                    border:"2px solid "+(f.type===t ? "var(--c-primary)" : "var(--c-light)"),
                    background: f.type===t ? "var(--c-primary)" : "#fff",
                    color: f.type===t ? "#fff" : "#4A4A68",
                  }}>
                  {t === "chip" ? zhEn("🏷️ 三選一按鈕", "🏷️ 3-choice buttons") : zhEn("📝 自由文字", "📝 Free text")}
                </button>
              ))}
              <button onClick={() => remove(i)}
                style={{padding:"8px 10px",borderRadius:10,fontSize:12,fontWeight:900,cursor:"pointer",border:"2px solid #FECACA",background:"#FEF2F2",color:"#991B1B"}}>
                {zhEn("刪除", "Delete")}
              </button>
            </div>

            {f.type === "chip" && (
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {f.options.map((o, oi) => (
                  <div key={oi} style={{display:"flex",alignItems:"center",gap:4,border:"2px solid var(--c-light)",borderRadius:10,padding:"3px 6px",background:"var(--c-lighter)"}}>
                    <input
                      value={o}
                      onChange={e => setOption(i, oi, e.target.value)}
                      style={{border:"none",background:"transparent",outline:"none",fontSize:12,fontWeight:800,color:"#4A4A68",width:70}}
                    />
                    <button onClick={() => removeOption(i, oi)} style={{border:"none",background:"transparent",color:"#EF4444",fontWeight:900,cursor:"pointer",fontSize:14}}>×</button>
                  </div>
                ))}
                <button onClick={() => addOption(i)}
                  style={{border:"2px dashed var(--c-primary)",background:"transparent",borderRadius:10,padding:"3px 10px",color:"var(--c-primary)",fontWeight:900,fontSize:12,cursor:"pointer"}}>
                  {zhEn("+ 選項", "+ Option")}
                </button>
              </div>
            )}
            {f.type === "text" && (
              <p style={{margin:0,fontSize:11,color:"#8A8A9E",fontWeight:700}}>{zhEn("美容師會見到一個文字框，可以自由輸入詳情。", "Groomers see a text box where they can type details freely.")}</p>
            )}
          </div>
        ))}

        <button onClick={addField}
          style={{padding:"12px 14px",borderRadius:14,border:"2px dashed var(--c-primary)",background:"var(--c-lighter)",color:"var(--c-primary)",fontWeight:900,fontSize:14,cursor:"pointer"}}>
          {zhEn("+ 新增自訂欄位", "+ Add custom field")}
        </button>

        <div style={{display:"flex",gap:8,marginTop:6}}>
          <button onClick={() => setFields(REPORT_DEFAULT_FIELDS.map((f, i) => ({...f, options:[...f.options], order:i})))}
            style={{padding:"12px 14px",borderRadius:14,border:"2px solid var(--c-light)",background:"#fff",color:"#4A4A68",fontWeight:900,fontSize:13,cursor:"pointer"}}>
            {zhEn("回復預設", "Restore defaults")}
          </button>
          <Btn onClick={() => onSave(fields)} disabled={!canSave} style={{flex:1,padding:"14px",opacity: canSave ? 1 : 0.5}}>
            {zhEn("儲存", "Save")}
          </Btn>
        </div>
      </div>
    </BottomSheet>
  );
};

const ReportSheet = ({ open, aptId, state, setState, onClose }) => {
  // Resolve the tenant's active template EARLY so defaults respect the
  // current field types. Text fields default to "" (not the chip default
  // "正常") and only currently-declared fields get seeded — dead keys
  // from removed template entries no longer leak back into the DB.
  const apt = state.appointments.find(a => a.id === aptId);
  const pet = apt ? state.pets[apt.petId] : null;
  const cust = apt ? state.users[apt.customerId] : null;
  const tenant = apt ? state.tenants.find(t => t.id === apt.tenantId) : null;
  const activeFields = resolveReportFields(tenant);
  const templateDefaults = () => {
    const d = { notes: "", photos: [], surcharges: [], tipAmount: 0 };
    for (const f of activeFields) {
      d[f.key] = f.type === "text"
        ? ""
        : (REPORT_DEFAULTS[f.key] || (f.options && f.options[0]) || "");
    }
    return d;
  };
  const [rep, setRep] = useState(templateDefaults());
  // Pre-fill from an EXISTING report when re-opening a completed
  // appointment so the groomer edits the saved values instead of
  // overwriting them with defaults. Photos, surcharges, and tip all
  // round-trip; missing fields fall back to the chip-default so older
  // reports without a chip pick still render selectable.
  useEffect(() => {
    if (!open || !aptId) return;
    const a = state.appointments.find(x => x.id === aptId);
    if (!a) { setRep(templateDefaults()); return; }
    const base = templateDefaults();
    if (a.report) {
      setRep({
        ...base,
        ...a.report,
        photos: Array.isArray(a.report.photos) ? a.report.photos : [],
        surcharges: Array.isArray(a.surcharges) ? a.surcharges : (a.report.surcharges || []),
        tipAmount: Number(a.tipAmount) || Number(a.report.tipAmount) || 0,
      });
    } else {
      setRep({ ...base, surcharges: Array.isArray(a.surcharges) ? a.surcharges : [], tipAmount: Number(a.tipAmount) || 0 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aptId, tenant?.reportTemplate]);
  // Surcharge entry mode: fixed dollar amount or % of the appointment subtotal
  // (basePrice + addonPrice). Computed amount is rounded to the nearest dollar
  // and stored on the surcharge row as `amount`, with `mode`/`percent` kept
  // alongside for audit + later re-render in the editApt sheet.
  const [surMode, setSurMode] = useState("amount");
  const [surPct, setSurPct] = useState("");
  const [showNotif, setShowNotif] = useState(false);
  const [addSurcharge, setAddSurcharge] = useState(false);
  const [surName, setSurName] = useState(""); const [surAmt, setSurAmt] = useState("");
  const lastGroom = pet ? daysSinceLastGroom(pet.id, state.appointments) : null;
  // Use shared chip-style report templates (REPORT_CHIPS / REPORT_LABELS)
  const optLabels = { skin:zhEn("皮膚 🩺", "Skin 🩺"), ear:zhEn("耳朵 👂", "Ears 👂"), teeth:zhEn("牙齒 🦷", "Teeth 🦷"), mood:zhEn("情緒 😊", "Mood 😊"), nailTrim:zhEn("剪甲 ✂️", "Nail trim ✂️"), analGlands:zhEn("肛門腺", "Anal glands"), parasites:zhEn("寄生蟲 🔍", "Parasites 🔍"), eyeDischarge:zhEn("眼睛分泌 👁️", "Eye discharge 👁️"), matting:zhEn("打結程度 🪮", "Matting 🪮") };
  const addSur = () => {
    if (!surName) return;
    let amount = 0;
    let mode = surMode;
    let percent = null;
    if (mode === "percent") {
      const p = parseFloat(surPct);
      if (!p) return;
      const subtotal = Number(apt?.basePrice || 0) + Number(apt?.addonPrice || 0);
      amount = Math.round(subtotal * p / 100);
      percent = p;
    } else {
      const a = parseInt(surAmt);
      if (!a) return;
      amount = a;
    }
    if (amount <= 0) return;
    setRep(r => ({ ...r, surcharges: [...(r.surcharges||[]), { name: surName, amount, mode, percent }] }));
    setSurName(""); setSurAmt(""); setSurPct(""); setAddSurcharge(false);
  };
  const totalSurcharge = (rep.surcharges||[]).reduce((s,x) => s + x.amount, 0);
  const afterPhotoCount = (rep.photos || []).filter(p => (p.category || "after") === "after").length;
  // Edit mode = the appointment is already done. We allow re-saving (add
  // late photos, fix typos) without re-firing the referrer reward or the
  // grooming_done notification, and we don't bump completedAt.
  const isEditMode = !!apt?.report && apt?.status === "done";
  const complete = async () => {
    try { await _complete(); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ReportSheet.complete] failed:", err);
      toast(zhEn("儲存失敗：", "Save failed: ") + (err?.message || "unknown"), "error");
    }
  };
  const _complete = async () => {
    // Bail-out guard — if apt got unloaded mid-flow (e.g. sync deleted
    // the row), everything downstream would throw undefined.name.
    if (!apt) { toast(zhEn("預約資料未載入，請重試", "Booking data didn't load, please retry"), "error"); return; }
    // Photos and per-category chip selections are now optional — the
    // chip defaults pre-fill "好" states so most reports submit cleanly
    // with one tap. Groomers can still add 美容前 / 後 photos but aren't
    // forced to.
    // Detect: is this customer's FIRST done appointment? If yes and referredBy set,
    // award referrer 100 points and tag both. SKIPPED in edit mode — the
    // reward already fired the first time this report was saved.
    const customerDoneBefore = state.appointments.some(a => a.customerId === apt.customerId && a.status === "done" && a.id !== aptId);
    const referrerId = (!isEditMode && !customerDoneBefore && cust?.referredBy && !cust?.referredAwarded) ? cust.referredBy : null;
    const refReward = 100;

    // Scrub keys the current template no longer declares so removed
    // fields don't sludge up in the DB. Reserved metadata (notes,
    // recommendations, photos, surcharges, tipAmount) always allowed.
    const templateKeys = new Set(activeFields.map(f => f.key));
    const reserved = new Set(["notes", "recommendations", "photos", "surcharges", "tipAmount"]);
    const cleanReport = {};
    for (const [k, v] of Object.entries(rep)) {
      if (templateKeys.has(k) || reserved.has(k)) cleanReport[k] = v;
    }

    setState(s => {
      const apts = s.appointments.map(a => a.id === aptId ? {
        ...a,
        report: cleanReport,
        status: "done",
        // Preserve original completedAt in edit mode; only stamp on first complete.
        completedAt: isEditMode ? a.completedAt : new Date().toISOString(),
        surcharges: rep.surcharges || [],
        tipAmount: Number(rep.tipAmount) || 0,
        remainingBalance: (a.basePrice + a.addonPrice + totalSurcharge - a.deposit),
      } : a);
      const users = { ...s.users };
      if (referrerId && users[referrerId]) {
        const r = users[referrerId];
        users[referrerId] = { ...r, points:(r.points||0) + refReward, walletTx:[...(r.walletTx||[]), { id:"tx"+uid(), type:"points", amount:refReward, note:"推薦獎勵: " + (cust?.name||"新客") + " 完成首次美容", date:dateNow() }] };
        users[apt.customerId] = { ...users[apt.customerId], referredAwarded: true };
      }
      return { ...s, appointments: apts, users };
    });
    if (!isEditMode) {
      // Customer notification + referrer reward + auto-send fire once.
      // Filling the grooming report is a distinct event from marking the
      // booking "完成"; the notification's wording reflects that — invites
      // the customer to view the health summary rather than implying the
      // pet's now ready for pickup (that's the done-status notification).
      pushNotif(setState, { userId:apt.customerId, type:"grooming_done", title:"健康報告已準備好 📋", body:(pet?.name||"")+" 嘅健康報告已完成，隨時可查看詳細紀錄 ❤️", read:false, aptId });
      if (referrerId) {
        pushNotif(setState, { userId: referrerId, type:"system", title:"推薦獎勵 🎁", body:"您介紹的 " + (cust?.name||"客戶") + " 完成首次美容，獲贈 " + refReward + " 積分", read:false });
      }
    }
    toast(isEditMode ? zhEn("報告已更新 ✅","Report updated ✅") : zhEn((pet?.name || "") + " 美容報告已完成" + (referrerId ? "（推薦獎勵 +" + refReward + " pt）" : ""), (pet?.name || "") + " grooming report completed" + (referrerId ? " (referral reward +" + refReward + " pt)" : "")));
    const totalPaid = apt.basePrice + apt.addonPrice + totalSurcharge;
    // If the owner uploaded before/after photos in this report, mint a
    // public share token (if not already minted) and include the album
    // URL in the customer-facing grooming_done message. No photos →
    // no link added — sending a URL to an empty album reads as broken.
    let albumUrl = "";
    const baPhotos = Array.isArray(rep.photos)
      ? rep.photos.filter(p => p && (p.category === "before" || p.category === "after"))
      : [];
    if (baPhotos.length > 0) {
      let token = apt.publicPhotoToken;
      if (!token) {
        token = "al" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
        try {
          // Spread the full apt — partial upsert would fail NOT NULL on
          // tenant_id / date / time before the ON CONFLICT redirects.
          await upsertAppointment({ ...apt, publicPhotoToken: token });
          setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === aptId ? { ...a, publicPhotoToken: token } : a) }));
        } catch (e) {
          console.warn("[PettoBook] album token mint failed at report-complete:", e);
          token = null;
        }
      }
      if (token) {
        const origin = (typeof window !== "undefined" && window.location.origin) || "";
        albumUrl = origin + "/album/" + token;
      }
    }
    // `albumBlock` is the pre-formatted insertion for the {albumBlock}
    // merge tag in the owner-editable string template. Empty when no
    // photos so the message reads cleanly; with photos it expands to a
    // section header + URL on its own lines.
    const albumBlock = albumUrl ? "\n\n📸 美容紀念冊（前後對比）：\n" + albumUrl : "";
    // Show the WhatsApp / SMS picker after EVERY save (including edits)
    // so the groomer can choose to re-send the report on a follow-up
    // edit (e.g. corrected a typo, added a photo).
    // Build the report body from the SHOP'S CURRENT TEMPLATE — any
    // custom / renamed / removed fields flow through here so the WA
    // message stays in sync with what the customer sees in-app.
    const reportBody = (() => {
      const fields = resolveReportFields(tenant);
      const rows = [];
      for (const f of fields) {
        const v = rep[f.key];
        if (v == null) continue;
        const s = String(v).trim();
        if (!s) continue;
        rows.push("- " + (f.label || f.key) + zhEn("：", ": ") + s);
      }
      return rows.join("\n");
    })();

    autoSendMessage("grooming_done", {
      shopName: tenant?.name||"",
      customerName: cust?.name||"",
      petName: pet?.name||"",
      phone: cust?.phone||"",
      email: cust?.email||"",
      aptId, customerId: apt.customerId,
      // Spread every rep field so custom-key merge tags ({furSmell} etc.)
      // resolve in owner-edited templates. The named fields below stay
      // for backward-compat with legacy templates still using {skin}.
      ...rep,
      skin: rep.skin, ear: rep.ear, teeth: rep.teeth, mood: rep.mood,
      nailTrim: rep.nailTrim ?? rep.nails, nails: rep.nailTrim ?? rep.nails, analGlands: rep.analGlands, parasites: rep.parasites,
      eyeDischarge: rep.eyeDischarge, matting: rep.matting,
      notes: rep.notes,
      reportBody,
      surcharges: rep.surcharges || [],
      totalPaid, depositPaid: apt.deposit,
      nextRecommendDays: 60,
      albumUrl,
      albumBlock,
    }, state, tenant);
    setShowNotif(true);
  };
  return <>
    <BottomSheet open={open && !showNotif} onClose={onClose} title={zhEn("美容報告 — ", "Grooming report — ") + (pet?.name || "")} z={300}>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* Pet info + behavior warnings */}
        {pet && <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14,border:"2px solid var(--c-border)"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
            <div style={{fontSize:28}}>{petEmoji(pet)}</div>
            <div><p style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#4A4A68"}}>{pet.name + " · " + pet.breed + " · " + pet.weight + "kg"}</p>
            <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn((pet.coatLength==="long"?"長毛":"短毛") + (lastGroom !== null ? " · 上次美容 " + lastGroom + " 天前" : " · 首次到訪"), (pet.coatLength==="long"?"Long coat":"Short coat") + (lastGroom !== null ? " · last groom " + lastGroom + " days ago" : " · first visit"))}</p></div>
          </div>
          {/* Behavior warnings */}
          {pet.behavior && (pet.behavior.bites || pet.behavior.muzzleNeeded || pet.behavior.anxious || pet.behavior.dryerFear || pet.behavior.aggressiveWithDogs) && (
            <div style={{background:"#FEF2F2",borderRadius:14,padding:"10px 14px",border:"2px solid #FECACA",display:"flex",flexDirection:"column",gap:4}}>
              <p style={{fontSize:12,fontWeight:900,color:"#991B1B",margin:0}}>{zhEn("⚠️ 行為注意事項", "⚠️ Behaviour notes")}</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {pet.behavior.bites && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#EF4444",color:"#fff"}}>{zhEn("會咬人", "Bites")}</span>}
                {pet.behavior.muzzleNeeded && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#EF4444",color:"#fff"}}>{zhEn("需口罩", "Needs muzzle")}</span>}
                {pet.behavior.anxious && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#F59E0B",color:"#fff"}}>{zhEn("容易緊張", "Easily nervous")}</span>}
                {pet.behavior.dryerFear && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#F59E0B",color:"#fff"}}>{zhEn("怕吹風機", "Afraid of dryer")}</span>}
                {pet.behavior.aggressiveWithDogs && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#D97706",color:"#fff"}}>{zhEn("犬隻攻擊性", "Dog-aggressive")}</span>}
              </div>
              {pet.behavior.notes && <p style={{fontSize:12,color:"#991B1B",margin:0,fontWeight:700}}>{pet.behavior.notes}</p>}
            </div>
          )}
          {pet.groomingStyle?.preferred && <p style={{fontSize:12,color:"var(--c-primary)",margin:"6px 0 0",fontWeight:800}}>{zhEn("✂️ 偏好造型:", "✂️ Preferred style:")} {pet.groomingStyle.preferred}</p>}
          {pet.medicalFlags && (pet.medicalFlags.heart || pet.medicalFlags.seizures || pet.medicalFlags.skinCondition || pet.medicalFlags.orthopedic) && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
              {pet.medicalFlags.heart && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#DC2626",color:"#fff"}}>{zhEn("❤️ 心臟", "❤️ Heart")}</span>}
              {pet.medicalFlags.seizures && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#DC2626",color:"#fff"}}>{zhEn("⚡ 癲癇", "⚡ Seizures")}</span>}
              {pet.medicalFlags.skinCondition && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#D97706",color:"#fff"}}>{zhEn("🩹 皮膚", "🩹 Skin")}</span>}
              {pet.medicalFlags.orthopedic && <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#D97706",color:"#fff"}}>{zhEn("🦴 骨科", "🦴 Orthopaedic")}</span>}
            </div>
          )}
        </div>}
        {/* Report fields — chip-style only. The right-side free-text
            input was removed; the chip set already covers what shops
            actually report and the extra input crowded the row. */}
        {/* Grooming-report fields — sourced from tenant.reportTemplate if
            the shop configured one, else falls back to REPORT_CHIPS +
            optLabels defaults. Two field types supported: "chip" (3-col
            severity grid) and "text" (free-text textarea for anything
            the shop doesn't want reduced to preset options — e.g. 皮膚
            details worth typing out). */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {resolveReportFields(tenant).map(field => {
            const k = field.key;
            if (field.type === "text") {
              return (
                <div key={k}>
                  <p style={{fontSize:14,color:"#4A4A68",fontWeight:900,margin:"0 0 8px"}}>{field.label}</p>
                  <textarea
                    style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:16,padding:"12px 14px",fontSize:14,fontWeight:600,minHeight:72,resize:"vertical",boxSizing:"border-box",outline:"none",color:"#4A4A68"}}
                    value={rep[k] || ""}
                    onChange={e => setRep(r => ({ ...r, [k]: e.target.value }))}
                    placeholder={field.placeholder || i18nT("report.textPh", "輸入詳細情況…")}
                  />
                </div>
              );
            }
            // chip (default)
            let vals = field.options || [];
            // Preserve legacy values: if the current rep has a chip value
            // that isn't in the shop's current options list (e.g. shop
            // renamed the options after this report was saved), tack it
            // on so the groomer sees the historical selection instead of
            // an empty state.
            if (rep[k] && !vals.includes(rep[k])) vals = [...vals, rep[k]];
            const GOOD = new Set(["正常","良好","無","已修剪","已擠","無發現","不需要"]);
            const HIGH = new Set(["發炎","紅腫發炎","建議洗牙","攻擊性","斷裂","發炎堵塞","跳蚤","蜱蟲","過多分泌","嚴重","嚴重需剃毛"]);
            return (
              <div key={k}>
                <p style={{fontSize:14,color:"#4A4A68",fontWeight:900,margin:"0 0 8px"}}>{field.label}</p>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:8}}>
                  {vals.map(v => {
                    const on = rep[k] === v;
                    const sev = GOOD.has(v) ? "good" : HIGH.has(v) ? "high" : "mid";
                    const palette = {
                      good: { bg: "#ECFDF5", fg: "#166534", border: "#A7F3D0", onBg: "#10B981" },
                      mid:  { bg: "#FFFBEB", fg: "#92400E", border: "#FDE68A", onBg: "#F59E0B" },
                      high: { bg: "#FEF2F2", fg: "#991B1B", border: "#FECACA", onBg: "#EF4444" },
                    }[sev];
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setRep(r => ({ ...r, [k]:v }))}
                        style={{
                          minHeight:44, padding:"10px 8px", borderRadius:12,
                          border:"2px solid "+(on ? palette.onBg : palette.border),
                          background: on ? palette.onBg : palette.bg,
                          color: on ? "#fff" : palette.fg,
                          fontWeight:800, fontSize:14, cursor:"pointer",
                          lineHeight:1.2, textAlign:"center",
                          transition:"all 0.12s",
                        }}
                      >
                        {REPORT_CHIP_LABEL(v)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div><p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{i18nT("report.groomerNote", "美容師叮嚀")}</p><textarea style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:20,padding:"14px 18px",fontSize:15,fontWeight:600,height:80,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68"}} value={rep.notes} onChange={e => setRep(r => ({ ...r, notes:e.target.value }))} placeholder={i18nT("report.groomerNotePh", "輸入溫馨備注...")} /></div>
        {/* Tip input removed from grooming report — recorded at checkout
            instead, via the 編輯預約 sheet, where it belongs alongside the
            payment-status flow. */}
        {/* Photo uploads removed from this sheet — they now live solely
            in the dedicated PhotoShareSheet (📸 on the appointment card)
            so groomers only see ONE place to add before/after photos.
            The grooming report focuses purely on the health check. */}
        <Btn onClick={complete} style={{width:"100%",padding:"16px"}}>{isEditMode ? zhEn("更新報告", "Update report") : zhEn("完成美容 & 發送通知", "Finish grooming & notify")}</Btn>
      </div>
    </BottomSheet>
    <BottomSheet open={showNotif} onClose={() => { setShowNotif(false); onClose(); }} title={zhEn("發送通知給客人", "Send notification to customer")} z={320}>
      {apt && cust && <NotifSender
        type="grooming_done"
        tenant={tenant}
        data={{
          shopName: tenant?.name || "",
          customerName: cust?.name || "",
          petName: pet?.name || "",
          phone: cust?.phone || "",
          ...rep,
          skin: rep.skin, ear: rep.ear, teeth: rep.teeth, mood: rep.mood,
          nailTrim: rep.nailTrim ?? rep.nails, nails: rep.nailTrim ?? rep.nails, analGlands: rep.analGlands, parasites: rep.parasites,
          eyeDischarge: rep.eyeDischarge, matting: rep.matting,
          notes: rep.notes,
          reportBody: (() => {
            const fields = resolveReportFields(tenant);
            return fields.map(f => {
              const v = rep[f.key];
              if (v == null || String(v).trim() === "") return "";
              return "- " + (f.label || f.key) + zhEn("：", ": ") + String(v).trim();
            }).filter(Boolean).join("\n");
          })(),
          surcharges: rep.surcharges || [],
          totalPaid: apt.basePrice + apt.addonPrice + (rep.surcharges||[]).reduce((s,x)=>s+x.amount,0),
          depositPaid: apt.deposit,
          nextRecommendDays: 60,
        }}
        onSend={() => {}}
        onClose={() => { setShowNotif(false); onClose(); }}
      />}
    </BottomSheet>
  </>;
};

const NotifBell = ({ userId, state, setState, onOpenApt, onViewDay }) => {
  const [open, setOpen] = useState(false);
  // Newest first by createdAt (ISO). Memoized so realtime ticks that don't
  // actually change notifications don't re-sort the whole list each render.
  const mine = useMemo(() => state.notifications
    .filter(n => n.userId === userId || n.userId === "all")
    .slice()
    .sort((a, b) => {
      const av = a.createdAt || "";
      const bv = b.createdAt || "";
      if (av && bv) return bv.localeCompare(av);
      if (av) return -1;
      if (bv) return 1;
      return b.id.localeCompare(a.id);
    }), [state.notifications, userId]);
  const unread = mine.filter(n => !n.read).length;
  const markRead = id => setState(s => ({ ...s, notifications: s.notifications.map(n => n.id === id ? { ...n, read:true } : n) }));
  const markAll = () => setState(s => ({ ...s, notifications: s.notifications.map(n => (n.userId === userId || n.userId === "all") ? { ...n, read:true } : n) }));
  const del = id => setState(s => ({ ...s, notifications: s.notifications.filter(n => n.id !== id) }));
  // Confirm deposit inline — same write the old 報表 list did, then drop
  // the notif so the owner's drawer doesn't accumulate stale items.
  const confirmDeposit = (notif) => {
    const aptId = notif.aptId;
    if (!aptId) return;
    const stamp = new Date().toISOString();
    setState(s => ({
      ...s,
      appointments: s.appointments.map(a => a.id === aptId ? { ...a, depositConfirmedAt: stamp } : a),
      notifications: s.notifications.filter(n => n.id !== notif.id),
    }));
    toast(zhEn("已標記訂金已收 ✓", "Deposit marked as received ✓"));
  };
  return <>
    <button onClick={() => setOpen(true)} style={{position:"relative",background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
      <Bell size={22} color="#fff" strokeWidth={2.5} />
      {unread > 0 && <span style={{position:"absolute",top:-2,right:-2,width:22,height:22,background:"#FF4D6D",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,color:"#fff",border:"3px solid rgba(255,255,255,0.5)"}}>{unread > 9 ? "9+" : unread}</span>}
    </button>
    <BottomSheet open={open} onClose={() => setOpen(false)} title={zhEn("通知中心", "Notifications")}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontSize:14,color:"#8A8A9E",fontWeight:700}}>{unread > 0 ? zhEn(unread + " 則未讀", unread + " unread") : zhEn("全部已讀","All read")}</span>
        {unread > 0 && <button onClick={markAll} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,color:"var(--c-primary)",fontWeight:800,display:"flex",alignItems:"center",gap:6}}><CheckCheck size={16} strokeWidth={3} />{zhEn("全部已讀", "Mark all read")}</button>}
      </div>
      {mine.length === 0
        ? <EmptyState icon={Bell} title={zhEn("目前沒有通知", "No notifications")} sub={zhEn("新的消息會在這裡顯示", "New messages appear here")} />
        : <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {mine.map(n => (
              <div key={n.id} onClick={() => markRead(n.id)} style={{padding:16,borderRadius:24,border:"2px solid "+(n.read?"var(--c-light)":"var(--c-border)"),background:n.read?"#FFFDFD":"var(--c-lighter)",cursor:"pointer",display:"flex",gap:14,alignItems:"flex-start"}}>
                <span style={{fontSize:24,flexShrink:0,background:"#fff",width:40,height:40,borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 12px rgba(0,0,0,0.05)"}}>{NI[n.type] || "🔔"}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
                    <p style={{fontSize:15,fontWeight:800,margin:0,color:n.read?"#8A8A9E":"#4A4A68",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.title}</p>
                    <button onClick={e => { e.stopPropagation(); del(n.id); }} style={{background:"none",border:"none",cursor:"pointer",padding:4,flexShrink:0,display:"flex"}}><X size={16} color="#AFAFC4" strokeWidth={3} /></button>
                  </div>
                  <p style={{fontSize:14,color:"#6A6A85",margin:"4px 0 0",fontWeight:600}}>{n.body}</p>
                  <p style={{fontSize:12,color:"#AFAFC4",margin:"6px 0 0",fontWeight:700}}>{n.time}</p>
                  {/* Inline confirm for deposit-pending notifs — moves the
                       old 報表 → 訂金待確認 list's primary action right
                       next to the message. */}
                  {n.type === "deposit_pending" && n.aptId && (() => {
                    const apt = (state.appointments || []).find(a => a.id === n.aptId);
                    const stillPending = apt && !apt.depositConfirmedAt && !apt.refundedAt && apt.status !== "cancelled" && apt.status !== "noshow";
                    if (!stillPending) return null;
                    return (
                      <button
                        onClick={e => { e.stopPropagation(); confirmDeposit(n); }}
                        style={{marginTop:10,padding:"7px 14px",borderRadius:99,background:"#10B981",border:"none",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,boxShadow:"0 4px 12px rgba(16,185,129,0.3)"}}
                      >{zhEn("✓ 確認收訂金 $", "✓ Confirm deposit received $")}{apt.deposit}</button>
                    );
                  })()}
                  {/* "查看預約" — jumps to the linked appointment detail so
                      the owner can accept / edit / see photos in one tap
                      instead of hunting through the calendar. Shows for
                      any notif that carries an aptId (booking_confirmed /
                      new_booking / grooming_done / apt_reminder / etc.). */}
                  {n.aptId && onOpenApt && (() => {
                    const apt = (state.appointments || []).find(a => a.id === n.aptId);
                    if (!apt) return null;
                    return (
                      <button
                        onClick={e => { e.stopPropagation(); markRead(n.id); onOpenApt(apt); setOpen(false); }}
                        style={{marginTop:8,marginLeft:n.type==="deposit_pending"?8:0,padding:"7px 14px",borderRadius:99,background:"#EFF6FF",border:"1.5px solid #93C5FD",color:"#1D4ED8",fontSize:12,fontWeight:900,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}
                      >{zhEn("📋 查看預約 →", "📋 View booking →")}</button>
                    );
                  })()}
                  {/* "查看當日" — jumps to that appointment's DAY in the
                      schedule so the owner can see the whole day's bookings
                      around it, not just the single apt. Owner-only (customers
                      have no schedule view → onViewDay is undefined there). */}
                  {n.aptId && onViewDay && (() => {
                    const apt = (state.appointments || []).find(a => a.id === n.aptId);
                    if (!apt || !apt.date) return null;
                    return (
                      <button
                        onClick={e => { e.stopPropagation(); markRead(n.id); onViewDay(apt); setOpen(false); }}
                        style={{marginTop:8,marginLeft:8,padding:"7px 14px",borderRadius:99,background:"#F5F3FF",border:"1.5px solid #C4B5FD",color:"#6D28D9",fontSize:12,fontWeight:900,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}
                      >{zhEn("📅 查看當日 →", "📅 View that day →")}</button>
                    );
                  })()}
                </div>
                {!n.read && <div style={{width:10,height:10,background:"var(--c-primary)",borderRadius:99,marginTop:6,flexShrink:0}} />}
              </div>
            ))}
          </div>
      }
    </BottomSheet>
  </>;
};


const BookingSuccess = ({ apt, pet, svc, total, deposit, ptEarned, tenant, onDone, onMarkPaid }) => {
  const depositOutstanding = deposit > 0 && !apt?.depositConfirmedAt;
  const methods = (tenant?.depositMethods || []).filter(methodForCustomer);
  const [activeMethodId, setActiveMethodId] = useState(() => methods[0]?.id || null);
  const activeMethod = methods.find(m => m.id === activeMethodId) || methods[0];
  const [claimedLocal, setClaimedLocal] = useState(!!apt?.depositClaimedAt);
  const [claimBusy, setClaimBusy] = useState(false);
  const shopPhone = (tenant?.phone || "").replace(/\D/g, "");
  const shopCC = (tenant?.countryCode || "+852").replace(/[^\d+]/g, "").replace(/^\+/, "");
  const waNumber = shopPhone ? shopCC + shopPhone : "";
  const receiptMsg = "您好，我已轉帳訂金 $" + deposit + "\n收款方式：" + (activeMethod?.name || "") + "\n預約：" + (svc || "美容服務") + "\n日期：" + (apt?.date || "") + " " + (apt?.time || "") + "\n毛孩：" + (pet?.name || "") + "\n請查收附上收據截圖，謝謝！";
  const waLink = waNumber ? "https://wa.me/" + waNumber + "?text=" + encodeURIComponent(receiptMsg) : null;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"32px 0",gap:20,textAlign:"center"}}>
      <div style={{width:100,height:100,background:"linear-gradient(135deg,var(--c-light),var(--c-grad2))",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 16px 40px var(--c-primary-30)",animation:"popIn 0.5s ease-out"}}>
        <span style={{fontSize:52}}>🎉</span>
      </div>
      <div>
        <h2 style={{fontWeight:900,fontSize:26,margin:"0 0 8px",color:"#4A4A68"}}>{i18nT("cust.book.success")}</h2>
        <p style={{fontSize:15,color:"#8A8A9E",margin:0,fontWeight:700}}>{i18nT("cust.book.successSub").replace("{petName}", pet?.name || (i18nT("lang.name") === "English" ? "your pet" : zhEn("您的毛孩", "Your pet")))} 🐾</p>
      </div>
      <Card style={{width:"100%",padding:20,background:"var(--c-lighter)",border:"2px solid var(--c-border)"}}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {[[zhEn("服務", "Service"),svc],[zhEn("日期", "Date"),apt.date],[zhEn("時間", "Time"),apt.time],[zhEn("訂金", "Deposit"),(deposit > 0 ? "$"+deposit : "—")]].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:15}}>
              <span style={{color:"#8A8A9E",fontWeight:700}}>{l}</span>
              <span style={{fontWeight:800,color:"#4A4A68"}}>{v}</span>
            </div>
          ))}
          <Divider />
          <div style={{display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:900}}>
            <span>{zhEn("服務總金額", "Service total")}</span><span style={{color:"var(--c-primary)"}}>{"$"+total}</span>
          </div>
        </div>
      </Card>
      {/* Deposit QR + receipt-confirmation flow. Tabs across methods so
          the panel stays compact even when the shop has 4-5 channels. */}
      {depositOutstanding && methods.length > 0 && (
        <Card style={{width:"100%",padding:20,background:"#FFFBEB",border:"2px solid #FEF3C7"}}>
          <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#92400E",textAlign:"left"}}>💰 {i18nT("cust.book.actualDeposit")} ${deposit}</h3>
          <p style={{fontSize:12,color:"#92400E",margin:"0 0 14px",fontWeight:700,opacity:0.85,textAlign:"left",lineHeight:1.5}}>
            {zhEn("揀收款方式付款後，按下方「WhatsApp 發送收據」連同截圖通知店家確認。", "After paying with a method, tap \"Send receipt on WhatsApp\" below and attach the screenshot so the shop can confirm.")}
          </p>
          {/* Method tabs — horizontal scroll if many */}
          <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:6,marginBottom:12,scrollbarWidth:"none"}}>
            {methods.map(m => {
              const on = m.id === (activeMethod?.id);
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveMethodId(m.id)}
                  style={{flexShrink:0,padding:"8px 14px",borderRadius:99,border:"1.5px solid " + (on ? "#D97706" : "#FDE68A"),background: on ? "#D97706" : "#fff",color: on ? "#fff" : "#92400E",fontSize:13,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
          {activeMethod && (
            <div style={{background:"#fff",borderRadius:12,padding:14,border:"1.5px solid #FDE68A",display:"flex",flexDirection:"column",alignItems:"center",gap:10,textAlign:"center"}}>
              {activeMethod.qrUrl ? (
                <a href={activeMethod.qrUrl} target="_blank" rel="noreferrer" style={{display:"block"}}>
                  <img src={activeMethod.qrUrl} alt={activeMethod.name} style={{width:200,height:200,objectFit:"contain",borderRadius:10,border:"1px solid #FEF3C7",background:"#fff"}} />
                </a>
              ) : (
                <div style={{width:200,height:200,borderRadius:10,border:"1.5px dashed #FDE68A",display:"flex",alignItems:"center",justifyContent:"center",color:"#D97706",fontSize:13,fontWeight:700}}>{zhEn("店家未上載 QR", "The shop hasn't uploaded a QR")}</div>
              )}
              <p style={{fontSize:15,fontWeight:900,color:"#92400E",margin:0}}>{activeMethod.name}</p>
              {activeMethod.instructions && (
                <p style={{fontSize:12,color:"#78350F",margin:0,fontWeight:600,whiteSpace:"pre-wrap",lineHeight:1.6}}>{activeMethod.instructions}</p>
              )}
            </div>
          )}
          {/* "I've paid" — customer-side ack that creates a notification
              for the shop owner so they don't have to scan WhatsApp +
              their PettoBook appointment list separately. Shop confirms with
              the green ✓ 確認收訂金 button on the appointment detail. */}
          {onMarkPaid && !claimedLocal && (
            <button
              onClick={async () => {
                if (claimBusy) return;
                setClaimBusy(true);
                try {
                  await onMarkPaid({ aptId: apt.id, methodName: activeMethod?.name || "", amount: deposit });
                  setClaimedLocal(true);
                  toast(zhEn("已通知店家確認訂金 ✓", "Shop notified to confirm the deposit ✓"), "success");
                } catch (e) {
                  toast(e?.message || zhEn("通知失敗，請再試", "Notification failed, please retry"), "error");
                } finally {
                  setClaimBusy(false);
                }
              }}
              style={{marginTop:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"14px",borderRadius:14,background:"#10B981",border:"none",color:"#fff",fontWeight:900,fontSize:15,cursor: claimBusy ? "wait" : "pointer",boxShadow:"0 6px 16px rgba(16,185,129,0.35)",width:"100%"}}
            >
              {claimBusy ? (i18nT("lang.name") === "English" ? "Notifying shop…" : zhEn("通知店家中…", "Notifying shop…")) : i18nT("cust.book.markComplete")}
            </button>
          )}
          {claimedLocal && (
            <div style={{marginTop:14,padding:"12px 14px",borderRadius:14,background:"#ECFDF5",border:"1.5px solid #A7F3D0",color:"#065F46",fontSize:13,fontWeight:800,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {i18nT("cust.book.alreadyNotified")}
            </div>
          )}
          {waLink ? (
            <a href={waLink} target="_blank" rel="noreferrer" style={{marginTop:10,display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"14px",borderRadius:14,background:"#25D366",color:"#fff",textDecoration:"none",fontWeight:900,fontSize:15,boxShadow:"0 6px 16px rgba(37,211,102,0.35)"}}>
              <MessageCircle size={18} strokeWidth={2.8} /> {i18nT("cust.book.sendReceipt")}
            </a>
          ) : (
            <p style={{fontSize:11,color:"#92400E",margin:"12px 0 0",fontWeight:700,textAlign:"center",opacity:0.75}}>{i18nT("cust.book.waNotConfigured")}</p>
          )}
        </Card>
      )}
      {ptEarned > 0 && (
        <div style={{background:"#FFFBEB",border:"2px solid #FEF3C7",borderRadius:24,padding:"14px 20px",width:"100%",display:"flex",alignItems:"center",gap:12}}>
          <Award size={24} color="#D97706" />
          <p style={{fontSize:14,color:"#92400E",margin:0,fontWeight:800}}>{zhEn("本次完成後將獲得", "You'll earn ")} <strong>{ptEarned}</strong> {zhEn("積分！", " points on completion!")}</p>
        </div>
      )}
      <Btn onClick={onDone} style={{width:"100%",padding:"16px"}}>{zhEn("返回首頁", "Back to home")}</Btn>
    </div>
  );
};

// ── SHOP REGISTER ─────────────────────────────────────────────────────────
const ShopRegister = ({ onBack, onRegistered, state, setState }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ shopName:"", address:"", phone:"", ownerName:"", email:"", password:"", confirmPw:"", slug:"", theme:"peach", plan:"free" });
  const billingLive = stripeBillingLive(state.platformConfig);
  // Android app sells through Google Play; the shop is opened on 免費版 and
  // the plan popup opens on first login (see SIGNUP_PLAN_KEY).
  const playBilling = window.Capacitor?.getPlatform?.() === "android" && !!import.meta.env.VITE_RC_GOOGLE_KEY;
  const canBuy = billingLive || playBilling;
  const [paying, setPaying] = useState(false);
  const [slugErr, setSlugErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [regErr, setRegErr] = useState("");
  const [done, setDone] = useState(null); // { tenantId, slug, bookingUrl }
  // Platform Terms-of-Service / Privacy acceptance — required to open a shop
  // (protects the operator: liability, payments, data handling, termination).
  const [agreedTos, setAgreedTos] = useState(false);
  // True once the user types in the slug field — locks auto-derivation so we
  // don't fight their edits.
  const [slugTouched, setSlugTouched] = useState(false);
  const slugify = s => (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")          // strip diacritics
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 32);
  const f = (k, v) => setForm(p => {
    const next = { ...p, [k]: v };
    // Auto-fill slug from shop name until the user manually edits the slug.
    if (k === "shopName" && !slugTouched) {
      const auto = slugify(v);
      next.slug = auto;
      // Clear any leftover "已被使用" error from a previous auto-fill
      if (auto) setSlugErr("");
    }
    return next;
  });

  const validateSlug = v => {
    const s = slugify(v);
    if (!s) { setSlugErr(zhEn("連結不能為空", "The link can't be empty")); return false; }
    if (s.length < 3) { setSlugErr(zhEn("至少需要 3 個字元", "At least 3 characters")); return false; }
    if (state.tenants.some(t => t.bookingSlug === s)) { setSlugErr(zhEn("此連結已被使用，請換一個", "This link is taken, please choose another")); return false; }
    setSlugErr(""); return true;
  };

  // Version stamp for the accepted platform agreement. Bump when the legal
  // text at #/terms or #/privacy materially changes so re-acceptance is
  // recorded distinctly (and old acceptances stay attributable to their text).
  const PLATFORM_TOS_VERSION = "2026-07-06";

  const register = async () => {
    setRegErr("");
    if (!agreedTos) { setRegErr(zhEn("請先閱讀並同意《服務條款》及《私隱政策》", "Please read and agree to the Terms of Service and Privacy Policy first")); return; }
    if (form.password !== form.confirmPw) { setRegErr(zhEn("密碼不一致", "Passwords don't match")); return; }
    if (!form.email) { setRegErr(zhEn("請填寫 Email", "Please enter your email")); return; }
    if (!validateSlug(form.slug)) return;
    setLoading(true);
    try {
      const slug = slugify(form.slug);
      // Persist the pending shop payload BEFORE signUp so the tenant can be
      // created after email confirmation (when the user logs in fresh and
      // has no in-memory form state). Cleared once the tenant is created.
      // The picker already blocks paid plans while billing is off, but the
      // plan is what grants features — re-assert it here so a stale form
      // value (or a plan disabled between page-load and submit) can never
      // open a shop on a tier nobody can be charged for.
      // Every shop opens on 免費版 (the DB enforces it too); a picked paid
      // plan is paid for right after — Stripe checkout on the web, or the
      // plan popup (Google Play) on first login.
      const safePlan = "free";
      const wantPlan = canBuy && form.plan !== "free" ? form.plan : null;
      try { if (wantPlan) localStorage.setItem(SIGNUP_PLAN_KEY, wantPlan); else localStorage.removeItem(SIGNUP_PLAN_KEY); } catch {}
      const pendingShop = {
        name: form.shopName, plan: safePlan, theme: form.theme,
        bookingSlug: slug, address: form.address, phone: form.phone,
        ownerName: form.ownerName, email: form.email.trim(),
        // Carry the accepted-agreement version so consent is recorded even on
        // the email-confirm path (tenant is created later in useAppState).
        tosVersion: PLATFORM_TOS_VERSION, tosAcceptedAt: new Date().toISOString(),
      };
      try { localStorage.setItem("petos:pending-shop", JSON.stringify(pendingShop)); } catch {}
      // 1. Sign up the owner (creates auth.users + profile via trigger, no tenant_id yet)
      const { user, session } = await authSignUp({
        email: form.email.trim(),
        password: form.password,
        name: form.ownerName,
        phone: form.phone,
        role: "owner",
        tenant_id: null,
      });
      const ownerId = user?.id;
      if (!ownerId) throw new Error(zhEn("註冊失敗", "Sign-up failed"));
      if (!session) {
        // Email confirmation required — the pending payload above will be
        // picked up by useAppState's onAuthChange after the user logs in.
        setDone({ pendingEmailConfirm:true, email:form.email });
        return;
      }
      // 2. Create tenant with owner_id
      const newTenant = await insertTenant({
        name: form.shopName,
        plan: safePlan,
        theme: form.theme,
        ownerId,
        bookingSlug: slug,
        address: form.address,
        phone: form.phone,
        dailyLimit: 8,
      });
      try { localStorage.removeItem("petos:pending-shop"); } catch {}
      // 3. Attach owner profile to the new tenant
      // Link first, permissions second — see useAppState's completePendingShop:
      // combining them meant a rejected permissions change also dropped the
      // tenant_id link and left the owner shopless.
      await patchProfile(ownerId, { tenantId: newTenant.id });
      try {
        await patchProfile(ownerId, { permissions:{ viewRevenue:true, manageCRM:true, manageSettings:true } });
      } catch (permErr) { console.warn("[PettoBook] owner permissions not set at signup", permErr); }
      // 3b. Record the owner's platform T&C acceptance (legal proof: who, which
      //     version, when + IP/UA captured server-side). Best-effort.
      try { await recordConsent({ tenantId: newTenant.id, purpose: "platform_tos", version: PLATFORM_TOS_VERSION, context: { role: "owner", via: "shop_signup", shop_slug: slug } }); } catch { /* non-fatal */ }
      // 4. Seed default services + addons + placeholder staff so the owner
      //    has something to assign appointments to from day one. Best-effort.
      await seedTenantDefaults({ tenantId: newTenant.id, tenantSlug: slug, shopName: form.shopName });
      // NOTE: We deliberately do NOT call setState with the new tenant here.
      //   useAppState's `profile` is still cached from authSignUp BEFORE we
      //   patched tenant_id. If we showed the dashboard now, every owner
      //   setState handler would key off `user.tenantId === null` and silently
      //   no-op. Instead we force a clean re-bootstrap via window.location.reload()
      //   in the "進入後台" button below — that re-runs useAppState's init with
      //   the freshly-linked profile, loading the seeded services/addons/staff
      //   from DB in one shot.
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      setDone({ tenantId:newTenant.id, slug, bookingUrl: origin + "/shop/" + slug, plan: safePlan, wantPlan, trial: freeTrialOf(newTenant) });
    } catch (e) {
      setRegErr(e?.message || zhEn("註冊失敗，請稍後再試", "Sign-up failed, please try again later"));
    } finally {
      setLoading(false);
    }
  };

  // Success / pending screen
  if (done) {
    return (
      <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{width:"100%",maxWidth:420,background:"rgba(255,255,255,0.95)",borderRadius:36,padding:32,boxShadow:"0 20px 60px var(--c-primary-30)",textAlign:"center"}}>
          {done.pendingEmailConfirm ? <>
            <div style={{fontSize:54,marginBottom:8}}>📧</div>
            <h2 style={{fontWeight:900,fontSize:22,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("請查收 Email 確認連結", "Check your email for the confirmation link")}</h2>
            <p style={{fontSize:14,color:"#8A8A9E",fontWeight:700,margin:"0 0 18px"}}>{zhEn("已寄出至 ","Sent to ")}<strong>{done.email}</strong>{zhEn("，點擊連結後再回來登入完成店鋪設定。", ". Tap the link, then come back and log in to finish setting up your shop.")}</p>
            <Btn onClick={onBack} style={{width:"100%"}}>{zhEn("返回登入", "Back to login")}</Btn>
          </> : <>
            <div style={{fontSize:54,marginBottom:8}}>🎉</div>
            <h2 style={{fontWeight:900,fontSize:24,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("店鋪已成功開設！", "Your shop is open!")}</h2>
            {done.trial && (
              <div style={{background:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",border:"2px solid #C4B5FD",borderRadius:20,padding:14,margin:"8px 0 16px",textAlign:"left"}}>
                <p style={{fontSize:14,fontWeight:900,color:"#5B21B6",margin:"0 0 4px"}}>{zhEn("🎁 已開始 " + done.trial.daysLeft + " 日專業版免費試用", "🎁 Your " + done.trial.daysLeft + "-day free Pro trial has started")}</p>
                <p style={{fontSize:12,fontWeight:700,color:"#6D28D9",margin:0,lineHeight:1.6}}>{zhEn("所有專業版功能已開通（收銀、無限網上預約、最多 5 位員工等），唔使信用卡。試用至 " + done.trial.ends + "，之後自動轉免費版，唔會收費。", "Every Pro feature is on (POS, unlimited online booking, up to 5 staff and more), no card needed. The trial runs until " + done.trial.ends + ", then you move to Free automatically with no charge.")}</p>
              </div>
            )}
            {done.wantPlan && billingLive ? (
              <div style={{background:"#FFF7ED",border:"2px solid #FED7AA",borderRadius:20,padding:16,margin:"0 0 16px",textAlign:"left"}}>
                <p style={{fontSize:14,fontWeight:900,color:"#9A3412",margin:"0 0 4px"}}>{zhEn("最後一步：付款開通", "Last step: pay for ")}{planName(done.wantPlan, getPlans(state.platformConfig))}</p>
                <p style={{fontSize:12,fontWeight:700,color:"#9A3412",margin:"0 0 12px",lineHeight:1.6}}>{done.trial ? zhEn("你可以先用試用，或者而家付款即時開通。", "You can use the trial first, or pay now to start right away.") : zhEn("店鋪已經用免費版開好。付款後即時升級。", "Your shop is open on Free. It upgrades as soon as you pay.")}</p>
                <Btn disabled={paying} style={{width:"100%"}} onClick={async () => {
                  setPaying(true);
                  try {
                    const r = await createSubscriptionCheckout({ tenantId: done.tenantId, plan: done.wantPlan, returnUrl: window.location.origin + "/", customerEmail: form.email.trim(), expectedAmount: Number(getPlans(state.platformConfig)[done.wantPlan]?.price) });
                    try { localStorage.removeItem(SIGNUP_PLAN_KEY); } catch {}
                    if (r?.url) { window.location.href = r.url; return; }
                    toast(zhEn("無法建立付款連結", "Couldn't create the payment link"), "error");
                  } catch (e) { toast(e?.message || zhEn("付款連結建立失敗", "Couldn't create the payment link"), "error"); }
                  setPaying(false);
                }}>{paying ? zhEn("開緊付款頁…", "Opening payment…") : zhEn("前往付款 →", "Go to payment →")}</Btn>
              </div>
            ) : (
              <p style={{fontSize:14,color:"#8A8A9E",fontWeight:700,margin:"0 0 18px"}}>{zhEn("方案：", "Plan: ")}<strong style={{color:"var(--c-primary)"}}>{planName(done.plan, getPlans(state.platformConfig))}</strong>{done.wantPlan ? zhEn("，入到後台就可以升級" + planName(done.wantPlan, getPlans(state.platformConfig)), " — upgrade to " + planName(done.wantPlan, getPlans(state.platformConfig)) + " once you're in") : zhEn("，你可以隨時在「設定」變更", " — change it anytime in Settings")}</p>
            )}
            <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,margin:"0 0 16px",border:"2px solid var(--c-border)",textAlign:"left"}}>
              <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 4px",fontWeight:800}}>{zhEn("📲 你的預約連結（分享給客戶）", "📲 Your booking link (share with customers)")}</p>
              <p style={{fontSize:13,fontWeight:800,color:"var(--c-primary)",margin:0,wordBreak:"break-all"}}>{done.bookingUrl}</p>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <Btn variant="secondary" style={{flex:1}} onClick={() => { navigator.clipboard?.writeText(done.bookingUrl); toast(i18nT("toast.copyOk")); }}>{zhEn("複製連結", "Copy link")}</Btn>
              <Btn style={{flex:1}} onClick={() => {
                // Force a clean re-bootstrap so useAppState picks up the
                // freshly-linked profile (tenant_id was patched into the DB
                // after the signed-in session was first cached).
                window.location.href = window.location.origin + "/";
              }}>{done.wantPlan && billingLive ? (done.trial ? zhEn("先試用 →", "Start with the trial →") : zhEn("暫時用免費版", "Stay on Free for now")) : zhEn("進入後台 →", "Go to dashboard →")}</Btn>
            </div>
          </>}
        </div>
      </div>
    );
  }

  const steps = [zhEn("店鋪資料", "Shop details"),zhEn("預約連結", "Booking link"),zhEn("老闆帳號", "Owner account"),zhEn("選方案", "Choose plan")];
  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:400}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:72,height:72,background:"#fff",borderRadius:28,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",boxShadow:"0 12px 32px var(--c-primary-40)"}}><Store size={36} color="var(--c-primary)" strokeWidth={2.5} /></div>
          <h1 style={{fontSize:28,fontWeight:900,color:"#fff",margin:"0 0 4px"}}>{zhEn("開設你的美容院", "Open your grooming shop")}</h1>
          <p style={{color:"rgba(255,255,255,0.85)",fontSize:14,margin:0,fontWeight:700}}>{zhEn("幾分鐘即可上線 ✨", "Live in minutes ✨")}</p>
        </div>
        {/* Step tabs */}
        <div style={{display:"flex",gap:8,marginBottom:20,justifyContent:"center"}}>
          {steps.map((s,i) => <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:28,height:28,borderRadius:99,background:i<=step?"#fff":"rgba(255,255,255,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:i<=step?"var(--c-primary)":"rgba(255,255,255,0.6)"}}>{i<step?<Check size={14} strokeWidth={3}/>:i+1}</div>
            {i<steps.length-1&&<div style={{width:20,height:3,borderRadius:99,background:i<step?"#fff":"rgba(255,255,255,0.3)"}}/>}
          </div>)}
        </div>
        <div style={{background:"rgba(255,255,255,0.92)",backdropFilter:"blur(24px)",borderRadius:36,padding:28,boxShadow:"0 20px 60px var(--c-primary-30)"}}>
          {step === 0 && <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{zhEn("店鋪資料", "Shop details")}</h3>
            <Input label={zhEn("美容院名稱 *", "Shop name *")} placeholder={zhEn("例：毛孩天堂美容院", "e.g. Happy Paws Grooming")} value={form.shopName} onChange={e=>f("shopName",e.target.value)} />
            <Input label={zhEn("地址", "Address")} placeholder={zhEn("例：九龍旺角花園街88號", "e.g. 88 Fa Yuen Street, Mong Kok")} value={form.address} onChange={e=>f("address",e.target.value)} />
            <Input label={zhEn("店鋪電話", "Shop phone")} placeholder={zhEn("例：2345 6789", "e.g. 2345 6789")} value={form.phone} onChange={e=>f("phone",parseCopiedPhone(e.target.value).digits)} type="tel" inputMode="tel" />
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>{zhEn("主題色彩", "Theme colour")}</p>
            <div style={{display:"flex",gap:12}}>
              {Object.entries(THEMES).map(([k,tv])=><div key={k} onClick={()=>f("theme",k)} style={{flex:1,height:36,borderRadius:16,background:`linear-gradient(135deg,${tv["--c-grad1"]},${tv["--c-primary"]})`,border:form.theme===k?"3px solid #4A4A68":"3px solid transparent",cursor:"pointer",transition:"all 0.2s"}} />)}
            </div>
            <Btn disabled={!form.shopName} onClick={()=>setStep(1)} style={{width:"100%",marginTop:8}}>{zhEn("下一步 →", "Next →")}</Btn>
          </div>}
          {step === 1 && <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{zhEn("預約連結", "Booking link")}</h3>
            <p style={{fontSize:13,color:"#6A6A85",margin:0,fontWeight:700}}>{zhEn("客人可透過這個連結直接預約，你可以自訂專屬網址", "Customers can book directly through this link, and you can customise the address")}</p>
            <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("你的預約連結", "Your booking link")}</p>
              <div style={{display:"flex",alignItems:"center",background:"var(--c-light)",borderRadius:20,padding:"12px 16px",gap:8,border:"2px solid "+(slugErr?"#FF4D6D":"var(--c-border)")}}>
                <span style={{fontSize:13,color:"#8A8A9E",fontWeight:700,whiteSpace:"nowrap"}}>{(state.platformConfig?.domain || "pettobook.com") + "/shop/"}</span>
                <input style={{flex:1,background:"none",border:"none",outline:"none",fontSize:15,fontWeight:800,color:"var(--c-primary)"}} placeholder="your-shop" value={form.slug} onChange={e=>{ setSlugTouched(true); f("slug",slugify(e.target.value)); validateSlug(e.target.value); }} />
              </div>
              {slugErr && <p style={{fontSize:12,color:"#FF4D6D",margin:"6px 0 0",fontWeight:700}}>{slugErr}</p>}
              {form.slug && !slugErr && <p style={{fontSize:12,color:"#10B981",margin:"6px 0 0",fontWeight:700}}>{zhEn("✓ 連結可用！", "✓ Link available!")}</p>}
            </div>
            <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:"0 0 6px"}}>{zhEn("預覽連結", "Preview link")}</p>
              <p style={{fontSize:14,color:"var(--c-primary)",fontWeight:700,margin:0,wordBreak:"break-all"}}>{(state.platformConfig?.domain || "pettobook.com") + "/shop/" + (form.slug||"your-shop")}</p>
            </div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="secondary" onClick={()=>setStep(0)} style={{flex:1}}>{zhEn("← 上一步", "← Back")}</Btn>
              <Btn disabled={!form.slug||!!slugErr} onClick={()=>setStep(2)} style={{flex:1}}>{zhEn("下一步 →", "Next →")}</Btn>
            </div>
          </div>}
          {step === 2 && <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{zhEn("老闆帳號", "Owner account")}</h3>
            <Input label={zhEn("老闆姓名 *", "Owner name *")} placeholder={zhEn("例：王老闆", "e.g. Mr Wong")} value={form.ownerName} onChange={e=>f("ownerName",e.target.value)} />
            <Input label="Email *" type="email" placeholder={zhEn("例：me@example.com", "e.g. me@example.com")} value={form.email} onChange={e=>f("email",e.target.value)} hint={zhEn("用作登入帳號", "Used as your login")} />
            <Input label={zhEn("密碼 *", "Password *")} type="password" placeholder={zhEn("至少 6 字元", "At least 6 characters")} value={form.password} onChange={e=>f("password",e.target.value)} />
            <Input label={zhEn("確認密碼 *", "Confirm password *")} type="password" placeholder={zhEn("再次輸入密碼", "Re-enter password")} value={form.confirmPw} onChange={e=>f("confirmPw",e.target.value)} />
            <div style={{display:"flex",gap:10}}>
              <Btn variant="secondary" onClick={()=>setStep(1)} style={{flex:1}}>{zhEn("← 上一步", "← Back")}</Btn>
              <Btn disabled={!form.ownerName||!form.email||!form.password||form.password.length<6||form.password!==form.confirmPw} onClick={()=>setStep(3)} style={{flex:1}}>{zhEn("下一步 →", "Next →")}</Btn>
            </div>
          </div>}
          {step === 3 && <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{zhEn("選擇方案", "Choose a plan")}</h3>
            <p style={{fontSize:13,color:"#6A6A85",margin:0,fontWeight:700}}>{zhEn("之後可隨時在「設定」中升級或降級", "You can change plan anytime in Settings")}</p>
            <div style={{background:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",border:"2px solid #C4B5FD",borderRadius:14,padding:"10px 12px"}}>
              <p style={{margin:0,fontSize:13,fontWeight:900,color:"#5B21B6"}}>{zhEn("🎁 新店舖送 14 日專業版免費試用", "🎁 New shops get a 14-day free Pro trial")}</p>
              <p style={{margin:"2px 0 0",fontSize:11.5,fontWeight:700,color:"#6D28D9",lineHeight:1.5}}>{zhEn("唔使信用卡。試用完自動轉免費版，唔會收費。", "No card needed. Afterwards you move to Free automatically — no charge.")}</p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {Object.entries(getPlans(state.platformConfig)).map(([k, p]) => {
                const on = form.plan === k;
                const price = getPlanPrice(k, form.currency || "HKD", state.platformConfig);
                // Until online billing is switched on there is no way to
                // charge for a paid plan, so offering one at signup would
                // hand out 專業版 for free. Free stays open so a shop can
                // still sign up and try the product today.
                const locked = !canBuy && price.amount > 0;
                return (
                  <div key={k} onClick={()=>{ if (!locked) f("plan", k); }} style={{padding:16,borderRadius:20,border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-lighter)":(locked?"#FAFAFA":"#FFFDFD"),cursor:locked?"not-allowed":"pointer",opacity:locked?0.55:1,display:"flex",alignItems:"center",justifyContent:"space-between",transition:"all 0.2s"}}>
                    <div style={{flex:1}}>
                      <p style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:6}}>
                        {planName(k, getPlans(state.platformConfig))} {k==="pro" && "👑"}
                        {locked && <span style={{fontSize:10,fontWeight:900,color:"#6B7280",background:"#F3F4F6",borderRadius:99,padding:"2px 8px"}}>{zhEn("即將開放", "Coming soon")}</span>}
                      </p>
                      <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("最多 " + p.maxStaff + " 員工", "Up to " + p.maxStaff + " staff") + (planPosRank(p) ? " · " + zhEn("收銀 ✓", "POS ✓") : "") + (p.features.whatsapp ? " · WhatsApp ✓" : "") + (p.features.analytics ? " · " + zhEn("分析 ✓", "Reports ✓") : "") + (p.features.payroll ? " · " + zhEn("薪資 ✓", "Payroll ✓") : "")}</p>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <p style={{fontSize:18,fontWeight:900,margin:0,color:on?"var(--c-primary)":"#4A4A68"}}>{price.amount === 0 ? zhEn("免費", "Free") : price.symbol + price.amount}</p>
                      {price.amount > 0 && <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700}}>{perMonth()}</p>}
                    </div>
                  </div>
                );
              })}
              {canBuy && form.plan !== "free" && (
                <p style={{fontSize:12,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700,lineHeight:1.6}}>
                  {zhEn("店鋪會先用免費版開好，" + (billingLive ? "下一步用信用卡付款" : "入到後台用 Google Play 付款") + "後即時升級。", "Your shop opens on Free first, then upgrades as soon as you pay " + (billingLive ? "by card in the next step." : "with Google Play in the app."))}
                </p>
              )}
              {!canBuy && (
                <p style={{fontSize:12,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700,lineHeight:1.6}}>
                  {isNativeApp() ? zhEn("付費方案暫未開放，而家可以免費開店試用。", "Paid plans aren't available here yet — start on Free today.") : zhEn("付費方案嘅線上付款仲未開放。而家可以免費開店試用，想升級隨時聯絡我哋幫你開通。", "Online payment for paid plans isn't open yet. Start on Free today and contact us to upgrade.")}
                </p>
              )}
            </div>
            {/* Platform agreement gate — required before opening a shop. */}
            <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background:agreedTos?"#F0FDF4":"var(--c-lighter)",border:"2px solid "+(agreedTos?"#86EFAC":"var(--c-border)"),borderRadius:14,cursor:"pointer"}}>
              <input type="checkbox" checked={agreedTos} onChange={e=>setAgreedTos(e.target.checked)} style={{marginTop:2,transform:"scale(1.25)",accentColor:"#10B981"}} />
              <span style={{fontSize:12,fontWeight:700,color:"#4A4A68",lineHeight:1.6}}>
                {zhEn("我已閱讀並同意 PettoBook 的", "I have read and agree to PettoBook's ")}
                <a href="#/merchant" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800,textDecoration:"underline"}}>{zhEn("《商戶服務協議》", "Merchant Agreement")}</a>{zhEn("、", ", ")}
                <a href="#/terms" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800,textDecoration:"underline"}}>{zhEn("《服務條款》", "Terms of Service")}</a>
                {zhEn("及", " and ")}
                <a href="#/privacy" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800,textDecoration:"underline"}}>{zhEn("《私隱政策》", "Privacy Policy")}</a>
                {zhEn("，並確認有權代表本店締約。", ", and confirm I'm authorised to contract on behalf of this shop.")}
              </span>
            </label>
            {regErr && <p style={{color:"#FF4D6D",fontSize:13,margin:0,fontWeight:700,textAlign:"center"}}>{regErr}</p>}
            <div style={{display:"flex",gap:10}}>
              <Btn variant="secondary" onClick={()=>setStep(2)} style={{flex:1}} disabled={loading}>{zhEn("← 上一步", "← Back")}</Btn>
              <Btn disabled={loading || !agreedTos} onClick={register} style={{flex:2,background:"#10B981",boxShadow:"0 8px 24px rgba(16,185,129,0.3)",opacity:agreedTos?1:0.55}}>{loading?zhEn("開設中...", "Opening..."):zhEn("🎉 立即開設", "🎉 Open now")}</Btn>
            </div>
          </div>}
          <button onClick={onBack} style={{width:"100%",marginTop:16,background:"none",border:"none",fontSize:13,color:"#8A8A9E",cursor:"pointer",fontWeight:700}}>{zhEn("← 返回登入", "← Back to login")}</button>
        </div>
      </div>
    </div>
  );
};

// ── PUBLIC SHOP HOMEPAGE ────────────────────────────────────────────────────
// Tabs: overview / gallery / info / policies (`book` is handled by the
// PublicBooking component below). The component receives `activeTab` from
// the URL (parsed in App.jsx) and calls `onTab` to navigate between them
// so each tab gets its own shareable URL (good for SEO + link sharing).

// HK administrative districts — used as the region filter in the
// public shop directory. Grouped by major area so the region picker
// can render a 4-section sheet (HK Island / Kowloon / NT / Islands).
const HK_REGIONS = [
  { id:"hk-central",    zh:"中西區",   en:"Central & Western", group:"hk" },
  { id:"hk-wanchai",    zh:"灣仔",     en:"Wan Chai",          group:"hk" },
  { id:"hk-east",       zh:"東區",     en:"Eastern",            group:"hk" },
  { id:"hk-south",      zh:"南區",     en:"Southern",           group:"hk" },
  { id:"kln-yautsim",   zh:"油尖旺",   en:"Yau Tsim Mong",      group:"kln" },
  { id:"kln-shamshui",  zh:"深水埗",   en:"Sham Shui Po",       group:"kln" },
  { id:"kln-kowloon",   zh:"九龍城",   en:"Kowloon City",       group:"kln" },
  { id:"kln-wongtai",   zh:"黃大仙",   en:"Wong Tai Sin",       group:"kln" },
  { id:"kln-kwuntong",  zh:"觀塘",     en:"Kwun Tong",          group:"kln" },
  { id:"nt-kwaitsing",  zh:"葵青",     en:"Kwai Tsing",         group:"nt"  },
  { id:"nt-tsuenwan",   zh:"荃灣",     en:"Tsuen Wan",          group:"nt"  },
  { id:"nt-tuenmun",    zh:"屯門",     en:"Tuen Mun",           group:"nt"  },
  { id:"nt-yuenlong",   zh:"元朗",     en:"Yuen Long",          group:"nt"  },
  { id:"nt-north",      zh:"北區",     en:"North",              group:"nt"  },
  { id:"nt-taipo",      zh:"大埔",     en:"Tai Po",             group:"nt"  },
  { id:"nt-shatin",     zh:"沙田",     en:"Sha Tin",            group:"nt"  },
  { id:"nt-saikung",    zh:"西貢",     en:"Sai Kung",           group:"nt"  },
  { id:"islands",       zh:"離島",     en:"Islands",            group:"islands" },
];
const HK_REGION_GROUPS = [
  { id:"hk",      zh:"港島",   en:"HK Island" },
  { id:"kln",     zh:"九龍",   en:"Kowloon" },
  { id:"nt",      zh:"新界",   en:"New Territories" },
  { id:"islands", zh:"離島",   en:"Islands" },
];
const lookupRegion = (id) => HK_REGIONS.find(r => r.id === id) || null;

// Preset directory tags an owner can toggle (plus free-text custom ones). Shown
// on the directory card so customers can tell acceptance/specialties at a glance.
const PRESET_DIRECTORY_TAGS = [
  "接受中小型犬", "接受大型犬", "接受貓貓", "日式美容", "免麻醉洗牙",
  "上門服務", "可寄養", "不接惡犬", "不接惡貓", "不接有蚤",
];

// Pet-type icon prefix + stable sort for a service list (cat → dog → both →
// none, then by name). Used on the public shop page AND the appointment-
// calendar service picker so ordering/labels stay consistent.
const petServiceKind = (s) => {
  const types = (Array.isArray(s?.petTypes) && s.petTypes.length ? s.petTypes : (s?.petType ? [s.petType] : [])).map(t => String(t).toLowerCase());
  const dog = types.includes("dog"), cat = types.includes("cat");
  return dog && cat ? "both" : dog ? "dog" : cat ? "cat" : "";
};
const PET_SERVICE_ICON = { dog: "🐶", cat: "🐱", both: "🐶🐱" };
const petServiceLabel = (s) => {
  const kind = petServiceKind(s);
  let nm = s?.name || "";
  if (kind) nm = nm.replace(/^\s*(dog\s*[·/&]\s*cat|cat\s*[·/&]\s*dog|dog|cat)\s*[-:·]?\s*/i, "");
  return kind ? `${PET_SERVICE_ICON[kind]} ${nm}`.trim() : (s?.name || "");
};
const decorateServiceList = (list) => (list || []).map(s => ({ ...s, petKind: petServiceKind(s), displayName: petServiceLabel(s) }))
  .sort((a, b) => {
    const o = { cat: 0, dog: 1, both: 2, "": 3 };
    const d = (o[a.petKind] ?? 3) - (o[b.petKind] ?? 3);
    return d !== 0 ? d : (a.displayName || "").localeCompare(b.displayName || "", undefined, { numeric: true, sensitivity: "base" });
  });

// Haversine great-circle distance in km between two lat/lng points.
const distanceKm = (lat1, lng1, lat2, lng2) => {
  if ([lat1, lng1, lat2, lng2].some(v => v == null || Number.isNaN(v))) return null;
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};
const fmtDistance = (km) => km == null ? null : (km < 1 ? `${Math.round(km * 1000)} m` : `${km < 10 ? km.toFixed(1) : Math.round(km)} km`);

// Hero photo candidates — selectable live via the switcher. The chosen one is
// blended as a soft-light texture over the theme gradient. "none" = gradient
// only. Photo failure is graceful (gradient remains).
const HERO_PHOTOS = [
  { id: "dog",   label: "🐶", url: "https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=1400&q=70" },
  { id: "cat",   label: "🐱", url: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=1400&q=70" },
  { id: "groom", label: "✂️", url: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1400&q=70" },
  { id: "puppy", label: "🐾", url: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?auto=format&fit=crop&w=1400&q=70" },
  { id: "paws",  label: "💗", url: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1400&q=70" },
  { id: "none",  label: "∅",  url: null },
];
const HERO_PHOTO_KEY = "petos:dir-hero";
const getHeroPhoto = (id) => HERO_PHOTOS.find(p => p.id === id) || HERO_PHOTOS[0];

// Clean Apple-style monogram avatar palette — a per-shop gradient keyed off the
// name, so every shop gets a consistent, professional logo even without one.
const SHOP_AVATAR_COLORS = [
  ["#FF8FB1", "#FF6B81"], ["#FFC36B", "#FF8F45"], ["#7FD8FF", "#0A84FF"],
  ["#7BE0A3", "#34C759"], ["#C9A3FF", "#AF82FF"], ["#7AD7FF", "#5AC8FA"], ["#FFA6C9", "#FF7AB6"],
];
const shopAvatarColor = (name) => {
  let h = 0; for (const ch of String(name || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return SHOP_AVATAR_COLORS[h % SHOP_AVATAR_COLORS.length];
};
// Rich, theme-driven hero backdrop: the palette gradient leads, layered with
// soft radial glows + a pet photo blended as subtle texture + a bottom scrim.
const HeroPhotoBackdrop = ({ img }) => (
  <>
    {/* Theme gradient base */}
    <div aria-hidden style={{position:"absolute", inset:0, background:"linear-gradient(145deg, var(--c-grad2) 0%, var(--c-grad1) 42%, var(--c-primary) 100%)"}} />
    {/* Soft luminous glows for depth */}
    <div aria-hidden style={{position:"absolute", width:280, height:280, left:-90, top:-120, borderRadius:"50%", background:"radial-gradient(circle, rgba(255,255,255,0.30), transparent 70%)"}} />
    <div aria-hidden style={{position:"absolute", width:240, height:240, right:-80, bottom:-110, borderRadius:"50%", background:"radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)"}} />
    <div aria-hidden style={{position:"absolute", inset:0, background:"radial-gradient(120% 80% at 80% 0%, rgba(255,255,255,0.18), transparent 55%)"}} />
    {/* Pet photo as subtle blended texture */}
    {img && <img src={img} alt="" aria-hidden style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:0.38, mixBlendMode:"soft-light"}} onError={(e)=>{e.currentTarget.style.display="none";}} />}
    {/* Bottom scrim for text legibility */}
    <div aria-hidden style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.22) 100%)"}} />
  </>
);

// Directory / public shop palettes — switchable for previewing. Each sets the
// same CSS vars the directory + listing pages consume, plus a page background.
const DIRECTORY_THEMES = {
  mocha: { label: "Mocha", zh: "摩卡", dot: "#6F4E37",
    vars: { '--c-primary':'#6F4E37','--c-primary-15':'#6F4E3726','--c-primary-20':'#6F4E3733','--c-primary-30':'#6F4E374D','--c-primary-40':'#6F4E3766','--c-grad1':'#B07E58','--c-grad1-30':'#B07E584D','--c-grad2':'#D9B38C','--c-light':'#F3EBE2','--c-lighter':'#F7F2EC','--c-border':'#E7D9C9','--c-dark':'#4A3526' },
    bg: 'linear-gradient(180deg, #F7F2EC 0%, #FAF7F2 55%, #F3EBE2 100%)' },
  teal: { label: "Teal", zh: "藍綠", dot: "#0F766E",
    vars: { '--c-primary':'#0F766E','--c-primary-15':'#0F766E26','--c-primary-20':'#0F766E33','--c-primary-30':'#0F766E4D','--c-primary-40':'#0F766E66','--c-grad1':'#14B8A6','--c-grad1-30':'#14B8A64D','--c-grad2':'#5EEAD4','--c-light':'#ECFDF7','--c-lighter':'#F4FBF9','--c-border':'#D6EDE7','--c-dark':'#115E59' },
    bg: 'linear-gradient(180deg, #F4FBF9 0%, #FAF9F6 55%, #ECFDF7 100%)' },
  sage: { label: "Sage", zh: "鼠尾草", dot: "#4B7363",
    vars: { '--c-primary':'#4B7363','--c-primary-15':'#4B736326','--c-primary-20':'#4B736333','--c-primary-30':'#4B73634D','--c-primary-40':'#4B736366','--c-grad1':'#6E9080','--c-grad1-30':'#6E90804D','--c-grad2':'#A9C3B5','--c-light':'#EEF3EF','--c-lighter':'#F4F7F3','--c-border':'#DCE7E0','--c-dark':'#2F4A3E' },
    bg: 'linear-gradient(180deg, #F4F7F3 0%, #FAFAF6 55%, #EEF3EF 100%)' },
  navy: { label: "Navy", zh: "海軍藍", dot: "#1E293B",
    vars: { '--c-primary':'#1E293B','--c-primary-15':'#1E293B26','--c-primary-20':'#1E293B33','--c-primary-30':'#1E293B4D','--c-primary-40':'#1E293B66','--c-grad1':'#3E4C63','--c-grad1-30':'#3E4C634D','--c-grad2':'#64748B','--c-light':'#EEF1F5','--c-lighter':'#F6F8FA','--c-border':'#DDE3EB','--c-dark':'#0F172A' },
    bg: 'linear-gradient(180deg, #F6F8FA 0%, #FBFAF7 55%, #EEF1F5 100%)' },
  indigo: { label: "Indigo", zh: "靛藍", dot: "#4F46E5",
    vars: { '--c-primary':'#4F46E5','--c-primary-15':'#4F46E526','--c-primary-20':'#4F46E533','--c-primary-30':'#4F46E54D','--c-primary-40':'#4F46E566','--c-grad1':'#6366F1','--c-grad1-30':'#6366F14D','--c-grad2':'#A5B4FC','--c-light':'#EEF0FE','--c-lighter':'#F5F6FE','--c-border':'#DDE0FB','--c-dark':'#3730A3' },
    bg: 'linear-gradient(180deg, #F5F6FE 0%, #FAFAFE 55%, #EEF0FE 100%)' },
  charcoal: { label: "Charcoal", zh: "炭灰", dot: "#374151",
    vars: { '--c-primary':'#374151','--c-primary-15':'#37415126','--c-primary-20':'#37415133','--c-primary-30':'#3741514D','--c-primary-40':'#37415166','--c-grad1':'#4B5563','--c-grad1-30':'#4B55634D','--c-grad2':'#9CA3AF','--c-light':'#F3F4F6','--c-lighter':'#F9FAFB','--c-border':'#E5E7EB','--c-dark':'#1F2937' },
    bg: 'linear-gradient(180deg, #F9FAFB 0%, #FCFCFD 55%, #F3F4F6 100%)' },
  blush: { label: "Blush", zh: "胭脂", dot: "#E05A7A",
    vars: { '--c-primary':'#E05A7A','--c-primary-15':'#E05A7A26','--c-primary-20':'#E05A7A33','--c-primary-30':'#E05A7A4D','--c-primary-40':'#E05A7A66','--c-grad1':'#FF9A9E','--c-grad1-30':'#FF9A9E4D','--c-grad2':'#FFB8D0','--c-light':'#FFF0F3','--c-lighter':'#FFF5F7','--c-border':'#FFE4E8','--c-dark':'#C21A44' },
    bg: 'linear-gradient(180deg, #FFF5F7 0%, #FFF8F4 55%, #FFF0F3 100%)' },
  rosegold: { label: "Rose Gold", zh: "玫瑰金", dot: "#B76E79",
    vars: { '--c-primary':'#B76E79','--c-primary-15':'#B76E7926','--c-primary-20':'#B76E7933','--c-primary-30':'#B76E794D','--c-primary-40':'#B76E7966','--c-grad1':'#D7A0A8','--c-grad1-30':'#D7A0A84D','--c-grad2':'#EFD0CE','--c-light':'#FCF2F1','--c-lighter':'#FDF7F6','--c-border':'#F2DCDA','--c-dark':'#8C4A55' },
    bg: 'linear-gradient(180deg, #FDF7F6 0%, #FBF6F2 55%, #FCF2F1 100%)' },
  forest: { label: "Forest", zh: "森林綠", dot: "#2F6E4F",
    vars: { '--c-primary':'#2F6E4F','--c-primary-15':'#2F6E4F26','--c-primary-20':'#2F6E4F33','--c-primary-30':'#2F6E4F4D','--c-primary-40':'#2F6E4F66','--c-grad1':'#4F9070','--c-grad1-30':'#4F90704D','--c-grad2':'#9CC9AE','--c-light':'#ECF6F0','--c-lighter':'#F3F9F5','--c-border':'#D5E8DD','--c-dark':'#1C4632' },
    bg: 'linear-gradient(180deg, #F3F9F5 0%, #F9FAF6 55%, #ECF6F0 100%)' },
  plum: { label: "Plum", zh: "梅紫", dot: "#6D4C7D",
    vars: { '--c-primary':'#6D4C7D','--c-primary-15':'#6D4C7D26','--c-primary-20':'#6D4C7D33','--c-primary-30':'#6D4C7D4D','--c-primary-40':'#6D4C7D66','--c-grad1':'#9270A3','--c-grad1-30':'#9270A34D','--c-grad2':'#C9B0D4','--c-light':'#F4EFF7','--c-lighter':'#F8F5FA','--c-border':'#E6D9EC','--c-dark':'#45304F' },
    bg: 'linear-gradient(180deg, #F8F5FA 0%, #FAF7F8 55%, #F4EFF7 100%)' },
  sunset: { label: "Sunset", zh: "夕陽橘", dot: "#E0683C",
    vars: { '--c-primary':'#E0683C','--c-primary-15':'#E0683C26','--c-primary-20':'#E0683C33','--c-primary-30':'#E0683C4D','--c-primary-40':'#E0683C66','--c-grad1':'#F2945E','--c-grad1-30':'#F2945E4D','--c-grad2':'#FAC79A','--c-light':'#FFF2EA','--c-lighter':'#FFF7F1','--c-border':'#FBE0CE','--c-dark':'#A8431F' },
    bg: 'linear-gradient(180deg, #FFF7F1 0%, #FFF8F0 55%, #FFF2EA 100%)' },
  ocean: { label: "Ocean", zh: "海洋藍", dot: "#1E6F8E",
    vars: { '--c-primary':'#1E6F8E','--c-primary-15':'#1E6F8E26','--c-primary-20':'#1E6F8E33','--c-primary-30':'#1E6F8E4D','--c-primary-40':'#1E6F8E66','--c-grad1':'#3E95B5','--c-grad1-30':'#3E95B54D','--c-grad2':'#8FCBDD','--c-light':'#EAF6FA','--c-lighter':'#F2FAFC','--c-border':'#CFE8F0','--c-dark':'#134B61' },
    bg: 'linear-gradient(180deg, #F2FAFC 0%, #F7FBFB 55%, #EAF6FA 100%)' },
  lavender: { label: "Lavender", zh: "薰衣草", dot: "#7C6BC4",
    vars: { '--c-primary':'#7C6BC4','--c-primary-15':'#7C6BC426','--c-primary-20':'#7C6BC433','--c-primary-30':'#7C6BC44D','--c-primary-40':'#7C6BC466','--c-grad1':'#9D8FD6','--c-grad1-30':'#9D8FD64D','--c-grad2':'#C9C0EC','--c-light':'#F2EFFB','--c-lighter':'#F7F5FD','--c-border':'#E2DBF5','--c-dark':'#4F3F94' },
    bg: 'linear-gradient(180deg, #F7F5FD 0%, #F9F7FB 55%, #F2EFFB 100%)' },
  amber: { label: "Amber", zh: "琥珀金", dot: "#C8961F",
    vars: { '--c-primary':'#B07D1E','--c-primary-15':'#B07D1E26','--c-primary-20':'#B07D1E33','--c-primary-30':'#B07D1E4D','--c-primary-40':'#B07D1E66','--c-grad1':'#D6A744','--c-grad1-30':'#D6A7444D','--c-grad2':'#F0D58C','--c-light':'#FBF4E2','--c-lighter':'#FDF9EF','--c-border':'#F2E4C4','--c-dark':'#7A5512' },
    bg: 'linear-gradient(180deg, #FDF9EF 0%, #FCF8EE 55%, #FBF4E2 100%)' },
  crimson: { label: "Crimson", zh: "緋紅", dot: "#B23A48",
    vars: { '--c-primary':'#B23A48','--c-primary-15':'#B23A4826','--c-primary-20':'#B23A4833','--c-primary-30':'#B23A484D','--c-primary-40':'#B23A4866','--c-grad1':'#D26370','--c-grad1-30':'#D263704D','--c-grad2':'#ECA6AE','--c-light':'#FBEEF0','--c-lighter':'#FDF5F6','--c-border':'#F4D6DA','--c-dark':'#7E2531' },
    bg: 'linear-gradient(180deg, #FDF5F6 0%, #FBF6F3 55%, #FBEEF0 100%)' },
};
const DIR_THEME_ORDER = ["mocha", "teal", "sage", "forest", "ocean", "navy", "indigo", "lavender", "plum", "charcoal", "amber", "sunset", "rosegold", "crimson", "blush"];

// Auto-detect the visitor's market from timezone (then language). Falls back to
// a live market (HK) if the detected one isn't available yet.
const detectDirectoryCountry = () => {
  let det = null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const tzMap = { "Asia/Hong_Kong": "HK", "Asia/Taipei": "TW", "Asia/Tokyo": "JP", "Asia/Singapore": "SG", "Asia/Kuala_Lumpur": "MY", "Asia/Kuching": "MY" };
    if (tzMap[tz]) det = tzMap[tz];
    else if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Montreal|Regina|St_Johns)/.test(tz)) det = "CA";
    else if (/^America\//.test(tz)) det = "US";
    if (!det) {
      const lang = ((typeof navigator !== "undefined" && navigator.language) || "").toLowerCase();
      if (lang.includes("zh-tw") || lang.includes("hant-tw")) det = "TW";
      else if (lang.includes("zh-hk") || lang.includes("zh-mo")) det = "HK";
      else if (lang.startsWith("ja")) det = "JP";
      else if (lang.endsWith("-sg")) det = "SG";
      else if (lang.endsWith("-my")) det = "MY";
      else if (lang.endsWith("-ca")) det = "CA";
      else if (lang.startsWith("en-us") || lang === "en") det = "US";
    }
  } catch { /* ignore */ }
  const meta = DIRECTORY_COUNTRIES.find(c => c.id === det);
  return meta && meta.live ? det : "HK";
};
const getDirTheme = (id) => DIRECTORY_THEMES[id] || DIRECTORY_THEMES.mocha;
const DIR_THEME_KEY = "petos:dir-theme";

// Markets the directory covers. Rollout order: HK → TW → US. `live` gates
// whether the country has any seeded shops yet (TW/US show a "coming soon"
// state until their data lands).
const DIRECTORY_COUNTRIES = [
  { id:"HK", flag:"🇭🇰", zh:"香港",  en:"Hong Kong", live:true  },
  { id:"TW", flag:"🇹🇼", zh:"台灣",  en:"Taiwan",    live:true  },
  { id:"US", flag:"🇺🇸", zh:"美國",  en:"USA",       live:true  },
  { id:"SG", flag:"🇸🇬", zh:"新加坡", en:"Singapore", live:false },
  { id:"MY", flag:"🇲🇾", zh:"馬來西亞", en:"Malaysia", live:false },
  { id:"JP", flag:"🇯🇵", zh:"日本",   en:"Japan",     live:false },
  { id:"CA", flag:"🇨🇦", zh:"加拿大", en:"Canada",   live:true  },
];

// Owner-pitch features shown in the "list your shop" section (horizontal
// scroller). Lucide icons for a clean, professional look (no emoji).
// Home landing look + motion. Motion is transform/opacity only, and every
// animation is off under prefers-reduced-motion. Sections fade up via
// .lp-reveal, which is only hidden once JS has added html.lp-motion — so the
// page is fully visible if the effect never runs. The hero headline animates
// transform only (never from opacity 0) so it doesn't delay LCP.
const LANDING_CSS = `
.lp-hero{background:radial-gradient(1100px 480px at 88% -10%,#FFDDE9 0%,transparent 62%),linear-gradient(160deg,#FFF7FA,#FBF4FF);padding:40px 18px 52px;overflow:hidden}
.lp-hero-in{max-width:1080px;margin:0 auto;display:grid;grid-template-columns:1.05fr 1fr;gap:48px;align-items:center}
@media(max-width:860px){.lp-hero-in{grid-template-columns:1fr;gap:30px}.lp-hero{padding:24px 16px 36px}}
.lp-chip{display:inline-flex;align-items:center;gap:6px;background:#fff;border:1px solid var(--c-border);color:var(--c-primary);border-radius:99px;padding:6px 13px;font-size:12.5px;font-weight:900}
.lp-h1{font-size:clamp(32px,5vw,54px);line-height:1.13;font-weight:900;color:#2E2A45;letter-spacing:-1px;margin:14px 0}
.lp-lead{font-size:clamp(15px,1.6vw,18px);line-height:1.65;color:#6E6A86;font-weight:700;margin:0 0 24px;max-width:520px}
.lp-ctas{display:flex;gap:10px;flex-wrap:wrap}
.lp-btn{display:inline-flex;align-items:center;gap:8px;border-radius:99px;padding:14px 24px;font-size:15.5px;font-weight:900;cursor:pointer;text-decoration:none;border:none;font-family:inherit;transition:transform .18s ease,box-shadow .25s ease}
.lp-btn.pri{background:var(--c-primary);color:#fff;box-shadow:0 12px 28px var(--c-primary-40)}
.lp-btn.sec{background:#fff;color:var(--c-primary);border:2px solid var(--c-border)}
.lp-btn.ghost{background:#fff;color:var(--c-primary);box-shadow:0 8px 20px rgba(0,0,0,.18)}
.lp-btn.line{background:transparent;color:#fff;border:1.5px solid rgba(255,255,255,.7)}
.lp-btn:hover{transform:translateY(-2px)}
.lp-btn:active{transform:scale(.97)}
.lp-btn .arr{display:inline-block;transition:transform .2s ease}
.lp-btn:hover .arr{transform:translateX(3px)}
.lp-owner{display:inline-block;margin-top:20px;font-size:14px;font-weight:800;color:#6E6A86;text-decoration:none;border-bottom:1.5px dashed #E5C6D3;transition:color .2s}
.lp-owner:hover{color:var(--c-primary)}
.lp-art{position:relative;max-width:540px;justify-self:center;width:100%}
.lp-art img{display:block;width:100%;height:auto;border-radius:30px;box-shadow:0 30px 70px rgba(122,62,96,.22);animation:lpFloat 7s ease-in-out infinite}
.lp-toast{position:absolute;background:#fff;border-radius:14px;padding:10px 14px;font-size:13px;font-weight:900;color:#2E2A45;box-shadow:0 14px 34px rgba(46,42,69,.18);white-space:nowrap;opacity:0;animation:lpToast 9s ease-in-out infinite;pointer-events:none}
.lp-toast.t1{left:-16px;top:12%;animation-delay:.6s}
.lp-toast.t2{right:-12px;top:48%;animation-delay:3.6s}
.lp-toast.t3{left:8%;bottom:-12px;animation-delay:6.6s}
@media(max-width:860px){.lp-toast.t1{left:10px}.lp-toast.t2{right:10px}}
.lp-rise{animation:lpRise .8s cubic-bezier(.2,.7,.2,1) both}
.lp-rise.d1{animation-delay:.08s}.lp-rise.d2{animation-delay:.16s}.lp-rise.d3{animation-delay:.24s}
@keyframes lpRise{from{transform:translateY(16px)}to{transform:none}}
@keyframes lpFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
@keyframes lpToast{0%{opacity:0;transform:translateY(10px) scale(.96)}6%,30%{opacity:1;transform:none}36%,100%{opacity:0;transform:translateY(-6px)}}
html.lp-motion .lp-reveal{opacity:0;transform:translateY(20px);transition:opacity .7s ease,transform .7s cubic-bezier(.2,.7,.2,1)}
html.lp-motion .lp-reveal.in{opacity:1;transform:none}
.lp-card{transition:transform .25s ease,box-shadow .25s ease}
.lp-card:hover{transform:translateY(-4px);box-shadow:0 16px 34px rgba(46,42,69,.10)!important}
.lp-owner-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:11px;padding:2px 20px 4px}
.lp-foot{max-width:1000px;margin:34px auto 0;padding:26px 16px 10px;border-top:1px solid var(--c-border);display:flex;flex-wrap:wrap;gap:10px 22px;justify-content:center;font-size:13.5px;font-weight:800}
.lp-foot a,.lp-foot button{color:#6E6A86;text-decoration:none;background:none;border:none;font:inherit;cursor:pointer;padding:0}
.lp-foot a:hover,.lp-foot button:hover{color:var(--c-primary)}
@media (prefers-reduced-motion:reduce){
  .lp-art img,.lp-rise{animation:none}
  .lp-toast{animation:none}.lp-toast.t1{opacity:1}
  html.lp-motion .lp-reveal{opacity:1;transform:none;transition:none}
  .lp-btn,.lp-card,.lp-btn .arr{transition:none}
}
`;

const OWNER_SLIDES = [
  { Icon: Calendar,      zh: ["網上預約 24/7", "客人隨時自助預約，唔再 miss call"], en: ["Online booking 24/7", "Customers book anytime"] },
  { Icon: CalendarClock, zh: ["智能排程", "員工、時段、容量一目了然"], en: ["Smart scheduling", "Staff, slots & capacity at a glance"] },
  { Icon: BadgeCheck,    zh: ["認證徽章", "提升信任，喺目錄突圍而出"], en: ["Verified badge", "Build trust in the directory"] },
  { Icon: Store,         zh: ["店舖專頁", "相片、服務、價目一站式展示"], en: ["Your shop page", "Photos, services & prices"] },
  { Icon: MapPin,        zh: ["區內曝光", "附近客人即時搵到你"], en: ["Local exposure", "Nearby customers find you"] },
  { Icon: Users,         zh: ["客戶管理", "客戶、寵物、紀錄、自動提醒"], en: ["Client management", "Customers, pets & reminders"] },
  { Icon: Award,         zh: ["積分卡", "自動儲分換禮遇，留住熟客"], en: ["Loyalty points", "Rewards that keep regulars"] },
  { Icon: Package,       zh: ["套票", "預售套票，鎖定收入"], en: ["Prepaid packages", "Lock in revenue upfront"] },
  { Icon: Camera,        zh: ["美容前後照", "影低靚相，分享俾客人"], en: ["Before & after photos", "Capture & share albums"] },
  { Icon: FileText,      zh: ["美容報告", "每次紀錄健康同美容狀況"], en: ["Grooming reports", "Log health & notes each visit"] },
  { Icon: Briefcase,     zh: ["員工排班", "安排更表，清楚易管理"], en: ["Staff rosters", "Plan shifts easily"] },
  { Icon: DollarSign,    zh: ["薪金紀錄", "自動計人工同佣金"], en: ["Payroll", "Auto wages & commission"] },
];

// Verified badge — shown on shops that are real PetOS members (auto: member =
// verified). Small green pill, reused on directory cards + the public shop page.
const VerifiedBadge = ({ size = "sm", label }) => {
  const sm = size === "sm";
  return (
    <span style={{display:"inline-flex", alignItems:"center", gap:4, background:"#D1FAE5", color:"#047857", borderRadius:99, padding: sm ? "2px 8px" : "4px 12px", fontSize: sm ? 11 : 13, fontWeight:900, whiteSpace:"nowrap"}}>
      <BadgeCheck size={sm ? 13 : 16} strokeWidth={2.6} />
      {label}
    </span>
  );
};

// Nearby map — Leaflet + free OpenStreetMap tiles (no API key, no Google
// billing). Plots the visitor + nearby shops as pins; tapping a pin opens that
// shop. Leaflet JS is dynamically imported so it stays out of the main bundle.
const NearbyMap = ({ userPos, shops, onPick, primaryHex = "#B76E79" }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mod = await import("leaflet");
      const L = mod.default || mod;
      if (cancelled || !elRef.current) return;
      if (mapRef.current) { try { mapRef.current.remove(); } catch {} mapRef.current = null; }
      const map = L.map(elRef.current, { scrollWheelZoom: false, attributionControl: true });
      mapRef.current = map;
      map.setView([userPos.lat, userPos.lng], 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
      const pts = [[userPos.lat, userPos.lng]];
      L.circleMarker([userPos.lat, userPos.lng], { radius: 8, color: "#fff", weight: 2, fillColor: "#2563EB", fillOpacity: 1 }).addTo(map).bindTooltip(i18nGetLocale() !== "zh-HK" ? "You" : "你喺呢度");
      (shops || []).forEach(s => {
        if (s.latitude == null || s.longitude == null) return;
        pts.push([s.latitude, s.longitude]);
        const mk = L.circleMarker([s.latitude, s.longitude], { radius: 7, color: "#fff", weight: 2, fillColor: primaryHex, fillOpacity: 0.95 }).addTo(map);
        mk.bindTooltip(s.name || "", { direction: "top" });
        mk.on("click", () => onPick?.(s));
      });
      try { if (pts.length > 1) map.fitBounds(pts, { padding: [28, 28], maxZoom: 16 }); } catch {}
      // Leaflet sometimes mis-measures size inside a just-shown container.
      setTimeout(() => { try { map.invalidateSize(); } catch {} }, 200);
    })();
    return () => { cancelled = true; if (mapRef.current) { try { mapRef.current.remove(); } catch {} mapRef.current = null; } };
  }, [userPos, shops, onPick, primaryHex]);
  return <div ref={elRef} style={{ height: 280, width: "100%", borderRadius: 16, overflow: "hidden", border: "1px solid var(--c-border)", zIndex: 0 }} />;
};

// Public shop directory — anonymous-accessible discovery page. Lists BOTH
// member shops (real PetOS tenants with `directory_listed = true`, get a ✓
// verified badge + online booking) and non-member external listings
// (AI-researched / scraped, basic info + a "claim this shop" CTA). Reads the
// unified public_directory_v2 RPC, scoped by country (HK / TW / US).
const DirectoryIndex = ({ onPickShop, onPickListing, onShopLogin, onShopRegister, onPostRequest, landing = false, onBrowse, social = {}, rfqEnabled = false }) => {
  // Platform social/contact links — only rendered when the superadmin has
  // filled them (admin → 平台設定). Instagram/Facebook accept a handle or a
  // full URL; normalise to a full URL.
  const igUrl = social.instagram ? (/^https?:\/\//i.test(social.instagram) ? social.instagram : ("https://instagram.com/" + String(social.instagram).replace(/^@/, ""))) : null;
  const fbUrl = social.facebook ? (/^https?:\/\//i.test(social.facebook) ? social.facebook : ("https://facebook.com/" + String(social.facebook).replace(/^@/, ""))) : null;
  const mailUrl = social.email ? ("mailto:" + social.email) : null;
  i18nUseLocale();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [region, setRegion] = useState(null); // null = 全部
  const [regionSheetOpen, setRegionSheetOpen] = useState(false);
  const [regionExpanded, setRegionExpanded] = useState(false); // region chips: collapsed by default, expand to see all (no scroll)
  const [country, setCountry] = useState(detectDirectoryCountry);
  const isZh = i18nGetLocale() === "zh-HK";
  const countryMeta = DIRECTORY_COUNTRIES.find(c => c.id === country) || DIRECTORY_COUNTRIES[0];
  const countryLive = !!countryMeta.live;

  // Home-page navigation + Contact-us form (landing only).
  const scrollToId = (id) => { const el = typeof document !== "undefined" && document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); else if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" }); };
  const [billCycle, setBillCycle] = useState("monthly"); // pricing section: monthly | annual — monthly shows the round list prices
  // Landing motion: fade sections up once as they scroll into view.
  useEffect(() => {
    if (!landing || typeof window === "undefined") return undefined;
    const root = document.documentElement;
    const els = [...document.querySelectorAll(".lp-reveal")];
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce || !("IntersectionObserver" in window)) return undefined;
    root.classList.add("lp-motion");
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
    }), { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
    els.forEach((e) => io.observe(e));
    return () => { io.disconnect(); root.classList.remove("lp-motion"); };
  }, [landing]);
  const [demo, setDemo] = useState({ email: "", company: "" });
  const [demoState, setDemoState] = useState("idle"); // idle | sending | sent | error
  const [demoErr, setDemoErr] = useState("");
  const submitDemoReq = async () => {
    setDemoErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demo.email.trim())) {
      setDemoErr(isZh ? "請輸入有效電郵。" : "Please enter a valid email.");
      setDemoState("error"); return;
    }
    setDemoState("sending");
    try {
      await requestDemo({ email: demo.email.trim(), company: demo.company });
      setDemoState("sent"); setDemo({ email: "", company: "" });
    } catch (e) {
      setDemoErr(e?.message || (isZh ? "傳送失敗，請稍後再試。" : "Could not send — please try again."));
      setDemoState("error");
    }
  };
  const [contact, setContact] = useState({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
  const [contactState, setContactState] = useState("idle"); // idle | sending | sent | error
  const [contactErr, setContactErr] = useState("");
  const submitContact = async () => {
    setContactErr("");
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());
    if (!contact.name.trim() || !emailOk || !contact.message.trim()) {
      setContactErr(isZh ? "請填寫姓名、有效電郵及訊息內容。" : "Please enter your name, a valid email, and a message.");
      setContactState("error");
      return;
    }
    setContactState("sending");
    try {
      await submitContactMessage({ name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim(), subject: contact.subject.trim(), message: contact.message.trim(), company: contact.company });
      setContactState("sent");
      setContact({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
    } catch (e) {
      setContactErr(e?.message || (isZh ? "傳送失敗，請稍後再試。" : "Could not send — please try again."));
      setContactState("error");
    }
  };
  const navItems = [
    { key: "home", label: isZh ? "主頁" : "Home", onClick: () => scrollToId("__top") },
    { key: "find", label: isZh ? "搵美容店" : "Find shops", onClick: () => (onBrowse ? onBrowse() : scrollToId("__top")) },
    { key: "features", label: isZh ? "功能特色" : "Features", onClick: () => { if (typeof window !== "undefined") window.location.href = "/features"; } },
    { key: "pricing", label: isZh ? "收費" : "Pricing", onClick: () => scrollToId("pricing") },
    { key: "blog", label: isZh ? "美容知識" : "Blog", onClick: () => { if (typeof window !== "undefined") window.location.href = "/blog"; } },
    { key: "demo", label: isZh ? "免費試用" : "Try demo", onClick: () => scrollToId("demo") },
    { key: "contact", label: isZh ? "聯絡我們" : "Contact", onClick: () => scrollToId("contact") },
  ];

  // Debounce the search query so we don't refetch on every keystroke.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 280);
    return () => clearTimeout(t);
  }, [q]);

  // Region taxonomy is currently HK-only; reset region when leaving HK.
  useEffect(() => { setRegion(null); }, [country]);

  // Load ALL shops for the country once (one Supabase call per country switch).
  // Region + search are then filtered client-side — instant, includes region
  // names, and avoids a DB round-trip on every keystroke.
  useEffect(() => {
    // Landing mode (home page) shows NO shop list — so it loads no shop photos
    // and costs nothing. The full listing lives on /directory.
    if (landing) { setShops([]); setLoading(false); setErr(""); return; }
    if (!countryLive) { setShops([]); setLoading(false); setErr(""); return; }
    let cancelled = false;
    setLoading(true);
    fetchPublicDirectory({ country, limit: 2000 })
      .then(rows => { if (!cancelled) { setShops(rows); setErr(""); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e?.message || "載入失敗"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [country, countryLive]);

  // Region display label for a shop (HK uses the localized taxonomy; other
  // countries store a free-text city, shown as-is).
  const regionLabelOf = (s) => s?.region ? (country === "HK" ? (lookupRegion(s.region)?.[isZh ? "zh" : "en"] || s.region) : s.region) : "";
  // Region filter options — HK from the fixed taxonomy, others derived from the
  // loaded shops' regions.
  const regionOptions = useMemo(() => {
    if (country === "HK") return HK_REGIONS.map(r => ({ id: r.id, label: isZh ? r.zh : r.en }));
    const seen = new Map();
    for (const s of shops) { if (s.region && !seen.has(s.region)) seen.set(s.region, s.region); }
    return [...seen.values()].sort().map(r => ({ id: r, label: r }));
  }, [country, shops, isZh]);

  // ── Near me (GPS) ────────────────────────────────────────────────────
  // One-shot geolocation (not a watch). We don't store the position server
  // side. Coordinates on shops are district/city-centroid level, so distance
  // is approximate — good enough to sort "nearest areas first".
  const [userPos, setUserPos] = useState(null);
  const [nearMe, setNearMe] = useState(false);
  const [mobileOnly, setMobileOnly] = useState(false); // 上門服務 filter
  const [geoBusy, setGeoBusy] = useState(false);
  const toggleNearMe = () => {
    if (nearMe) { setNearMe(false); return; }
    if (userPos) { setNearMe(true); return; }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      toast(isZh ? "此裝置不支援定位" : "Location not supported on this device", "error");
      return;
    }
    if (typeof window !== "undefined" && !window.isSecureContext) {
      toast(isZh ? "定位需要安全連線 (HTTPS)" : "Location needs a secure (HTTPS) connection", "error");
      return;
    }
    setGeoBusy(true);
    const onOk = (p) => { setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setNearMe(true); setGeoBusy(false); };
    const onErr = (err) => {
      setGeoBusy(false);
      // Surface the real reason so "cannot locate" is actionable.
      let msg;
      if (err && err.code === 1) msg = isZh ? "定位被拒絕，請喺瀏覽器／系統設定允許位置權限" : "Location permission denied — allow it in your browser/system settings";
      else if (err && err.code === 2) msg = isZh ? "暫時取得唔到位置（訊號不足），請再試" : "Position unavailable (weak signal) — try again";
      else if (err && err.code === 3) msg = isZh ? "定位逾時，請再試一次" : "Location timed out — please try again";
      else msg = isZh ? "無法取得你的位置" : "Couldn't get your location";
      toast(msg, "error");
    };
    // First try a fast, low-accuracy fix (wifi/IP). If it times out, retry once
    // with high accuracy + a longer window before giving up.
    navigator.geolocation.getCurrentPosition(
      onOk,
      (err) => {
        if (err && err.code === 3) {
          navigator.geolocation.getCurrentPosition(onOk, onErr, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
        } else { onErr(err); }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 120000 }
    );
  };

  // Auto-start "near me" when arriving from the home-page "尋找附近寵物店" card
  // (/directory?near=1). Runs once on mount for the full directory view.
  const nearAutoRan = useRef(false);
  useEffect(() => {
    if (landing || nearAutoRan.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("near") === "1") { nearAutoRan.current = true; toggleNearMe(); }
    // Region landing pages: /directory/{region} pre-selects that district so
    // the SPA view matches the SEO page the crawler/visitor arrived on.
    const rm = (window.location.pathname || "").match(/^\/directory\/([a-z-]+)\/?$/);
    if (rm && lookupRegion(rm[1])) setRegion(rm[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter (region + search incl. region name) → decorate with distance →
  // optionally sort nearest-first. All client-side.
  const displayShops = useMemo(() => {
    const ql = (debouncedQ || "").toLowerCase().trim();
    let list = shops;
    if (region) list = list.filter(s => s.region === region);
    if (mobileOnly) list = list.filter(s => s.serviceMode === "mobile" || s.serviceMode === "both");
    if (ql) list = list.filter(s =>
      (s.name || "").toLowerCase().includes(ql) ||
      (s.tagline || "").toLowerCase().includes(ql) ||
      (s.address || "").toLowerCase().includes(ql) ||
      regionLabelOf(s).toLowerCase().includes(ql)
    );
    const withDist = list.map(s => ({
      ...s,
      distanceKm: (nearMe && userPos && s.latitude != null && s.longitude != null)
        ? distanceKm(userPos.lat, userPos.lng, s.latitude, s.longitude) : null,
    }));
    if (nearMe && userPos) {
      // Near-me = show shops near you, nearest first. NOTE: most shop coords are
      // district/city centroids (not exact addresses), so a sub-km radius would
      // collapse to nothing. Use a 3 km radius and, if too few fall inside,
      // widen to the nearest 8 so the list is always useful.
      const RADIUS_KM = 3;
      const sorted = withDist.filter(s => s.distanceKm != null).sort((a, b) => a.distanceKm - b.distanceKm);
      const within = sorted.filter(s => s.distanceKm <= RADIUS_KM);
      return within.length >= 3 ? within : sorted.slice(0, 8);
    }
    return withDist;
  }, [shops, region, debouncedQ, nearMe, userPos, country, isZh, mobileOnly]);

  // Paginate — rendering all ~500+ cards at once causes heavy repaint/scroll
  // flicker. Show a page at a time with "load more".
  const [visibleCount, setVisibleCount] = useState(48);
  useEffect(() => { setVisibleCount(48); }, [country, region, debouncedQ, nearMe, mobileOnly]);
  const shownShops = displayShops.slice(0, visibleCount);

  // Theme switcher (preview) — persisted so the listing page matches.
  const [themeId, setThemeId] = useState(() => {
    try { return localStorage.getItem(DIR_THEME_KEY) || "rosegold"; } catch { return "rosegold"; }
  });
  const dirTheme = getDirTheme(themeId);
  const themeVars = dirTheme.vars;
  const pickTheme = (id) => { setThemeId(id); try { localStorage.setItem(DIR_THEME_KEY, id); } catch {} };
  const [heroId, setHeroId] = useState(() => {
    try { return localStorage.getItem(HERO_PHOTO_KEY) || "dog"; } catch { return "dog"; }
  });
  const heroPhoto = getHeroPhoto(heroId).url;
  const pickHero = (id) => { setHeroId(id); try { localStorage.setItem(HERO_PHOTO_KEY, id); } catch {} };
  const regionLabel = region ? (isZh ? lookupRegion(region)?.zh : lookupRegion(region)?.en) : (isZh ? "全部地區" : "All regions");

  return (
    <div style={{...themeVars, minHeight:"100vh", background:dirTheme.bg, paddingBottom:60}}>
      {landing && <style>{LANDING_CSS}</style>}
      {/* Top bar — brand · location · language · social links */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, padding:"10px 14px"}}>
        <button onClick={() => { window.location.href = "/"; }} title={isZh ? "返回主頁" : "Home"} style={{display:"inline-flex", alignItems:"center", gap:6, fontSize:16, fontWeight:900, color:"var(--c-primary)", background:"none", border:"none", padding:0, cursor:"pointer"}}>
          <PawPrint size={18} strokeWidth={2.6} /> PettoBook
        </button>
        <div style={{display:"flex", alignItems:"center", justifyContent:"flex-end", gap:6, flexWrap:"wrap"}}>
          <label style={{position:"relative", display:"inline-flex", alignItems:"center", gap:4, background:"#fff", border:"1px solid var(--c-border)", borderRadius:99, padding:"5px 10px", fontSize:12, fontWeight:800, color:"#4A4A68", cursor:"pointer"}}>
            <MapPin size={13} color="var(--c-primary)" /> {countryMeta.flag} {isZh ? countryMeta.zh : countryMeta.en}
            <ChevronDown size={12} color="#AFAFC4" />
            <select value={country} onChange={e => setCountry(e.target.value)} aria-label="Location" style={{position:"absolute", inset:0, width:"100%", height:"100%", opacity:0, cursor:"pointer"}}>
              {DIRECTORY_COUNTRIES.map(c => <option key={c.id} value={c.id} disabled={!c.live}>{c.flag} {isZh ? c.zh : c.en}{!c.live ? (isZh ? "（即將）" : " (soon)") : ""}</option>)}
            </select>
          </label>
          <button onClick={() => i18nCycleLocale()} title="Language / 語言 / 言語" style={{background:"#fff", border:"1px solid var(--c-border)", borderRadius:99, padding:"5px 11px", fontSize:12, fontWeight:900, color:"var(--c-primary)", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4}}>
            <Globe size={13} /> {i18nLocaleShort()}
          </button>
          {mailUrl && <a href={mailUrl} title="Email" style={{textDecoration:"none", width:30, height:30, borderRadius:"50%", background:"#fff", border:"1px solid var(--c-border)", display:"inline-flex", alignItems:"center", justifyContent:"center"}}><Mail size={15} color="var(--c-primary)" /></a>}
          {igUrl && <a href={igUrl} target="_blank" rel="noreferrer" title="Instagram" style={{textDecoration:"none", width:30, height:30, borderRadius:"50%", background:"#fff", border:"1px solid var(--c-border)", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:14}}>📷</a>}
          {fbUrl && <a href={fbUrl} target="_blank" rel="noreferrer" title="Facebook" style={{textDecoration:"none", width:30, height:30, borderRadius:"50%", background:"#fff", border:"1px solid var(--c-border)", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:900, color:"#1877F2"}}>f</a>}
          {onShopLogin && (
            <button onClick={onShopLogin} title={isZh ? "店主登入" : "Shop login"} style={{display:"inline-flex", alignItems:"center", gap:6, background:"#fff", color:"var(--c-primary)", border:"2px solid var(--c-primary)", borderRadius:99, padding:"9px 18px", fontSize:14, fontWeight:900, cursor:"pointer"}}>
              <Store size={16} strokeWidth={2.6} /> {isZh ? "登入" : "Login"}
            </button>
          )}
          {onShopRegister && (
            <button onClick={onShopRegister} title={isZh ? "登記店舖" : "Register shop"} style={{display:"inline-flex", alignItems:"center", gap:5, background:"var(--c-primary)", color:"#fff", border:"none", borderRadius:99, padding:"10px 18px", fontSize:14, fontWeight:900, cursor:"pointer", boxShadow:"0 6px 16px var(--c-primary-40)"}}>
              {isZh ? "登記" : "Register"}
            </button>
          )}
        </div>
      </div>

      {/* Home navigation bar (landing only) — anchors to on-page sections. */}
      {landing && (
        <nav style={{position:"sticky", top:0, zIndex:50, background:"rgba(255,255,255,0.96)", backdropFilter:"blur(8px)", borderBottom:"1px solid var(--c-border)", padding:"8px 10px", display:"flex", gap:4, alignItems:"center", justifyContent:"center", flexWrap:"wrap", overflowX:"auto"}}>
          {navItems.map(it => (
            <button key={it.key} onClick={it.onClick} style={{background:"none", border:"none", color:"#4A4A68", fontSize:13.5, fontWeight:800, cursor:"pointer", padding:"7px 12px", borderRadius:99, whiteSpace:"nowrap"}}
              onMouseEnter={e=>{e.currentTarget.style.background="var(--c-lighter)";e.currentTarget.style.color="var(--c-primary)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#4A4A68";}}>
              {it.label}
            </button>
          ))}
        </nav>
      )}

      {/* Landing hero — headline + illustration with floating "booking" toasts. */}
      {landing && (
        <section className="lp-hero">
          <div className="lp-hero-in">
            <div>
              <span className="lp-chip lp-rise"><PawPrint size={14} strokeWidth={2.6} /> {isZh ? "香港寵物美容目錄" : "Hong Kong pet grooming"}</span>
              <h1 className="lp-h1 lp-rise d1">{isZh ? <>搵啱寵物美容，<br />即時網上預約。</> : <>Find the right groomer.<br />Book in a minute.</>}</h1>
              <p className="lp-lead lp-rise d2">{isZh ? "比較附近美容店嘅服務、價錢同相片。狗狗貓貓美容、SPA、上門服務，揀好時間就預約。" : "Compare services, prices and photos of groomers near you — dogs, cats, spa and mobile grooming — then book a time that suits you."}</p>
              <div className="lp-ctas lp-rise d3">
                {onBrowse && <button className="lp-btn pri" onClick={onBrowse}><Search size={17} strokeWidth={2.8} /> {isZh ? "瀏覽店舖目錄" : "Browse groomers"} <span className="arr">→</span></button>}
                <a className="lp-btn sec" href="/directory?near=1"><MapPin size={17} strokeWidth={2.6} /> {isZh ? "搵附近" : "Near me"}</a>
              </div>
              <a className="lp-owner" href="/features">{isZh ? "美容店老闆？免費開店，睇吓點用 →" : "Own a grooming shop? Start free — see how it works →"}</a>
              <a className="lp-owner" href={PLAY_STORE_URL} target="_blank" rel="noopener" style={{marginLeft:14}}>{isZh ? "Android 店主 App · Google Play →" : "Shop app for Android · Google Play →"}</a>
            </div>
            <div className="lp-art">
              <picture>
                <source type="image/webp" srcSet="/hero-art-640.webp 640w, /hero-art-1200.webp 1200w" sizes="(max-width: 860px) 92vw, 540px" />
                <img src="/hero-art-1200.jpg" width="1200" height="1175" alt={isZh ? "美容師幫薩摩耶梳毛" : "A groomer brushing a Samoyed"} fetchpriority="high" />
              </picture>
              <div className="lp-toast t1" aria-hidden>✅ {isZh ? "預約成功 · 明天 14:30" : "Booked · tomorrow 14:30"}</div>
              <div className="lp-toast t2" aria-hidden>✂️ {isZh ? "Mochi 美容完成" : "Mochi is ready"}</div>
              <div className="lp-toast t3" aria-hidden>⭐ {isZh ? "比較價錢同相片先揀" : "Compare prices & photos"}</div>
            </div>
          </div>
        </section>
      )}

      {/* Directory hero — illustrated banner with overlay + title */}
      {!landing && <div style={{position:"relative", overflow:"hidden", borderRadius:"0 0 28px 28px", boxShadow:"0 12px 32px rgba(0,0,0,0.14)", background:"linear-gradient(145deg, var(--c-grad2), var(--c-grad1) 45%, var(--c-primary))"}}>
        <div style={{position:"relative", maxWidth:960, margin:"0 auto"}}>
          <img src="/hero-grooming-1600.webp" width="1600" height="893" alt={isZh ? "寵物美容平台" : "Pet grooming platform"} style={{display:"block", width:"100%", height:"auto"}} />
          {/* Overlay scrim — darkens bottom for legible text */}
          <div aria-hidden style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.20) 62%, rgba(0,0,0,0.62) 100%)"}} />
          {/* Overlay content */}
          <div style={{position:"absolute", left:0, right:0, bottom:0, padding:"0 18px 18px", color:"#fff"}}>
            <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.25)", border:"1px solid rgba(255,255,255,0.35)", borderRadius:99, padding:"4px 12px", fontSize:11.5, fontWeight:800, marginBottom:9, letterSpacing:0.4}}>
              <PawPrint size={13} strokeWidth={2.6} /> {isZh ? "寵物美容目錄" : "PET GROOMING DIRECTORY"}
            </div>
            <h1 style={{fontSize:26, fontWeight:900, margin:"0 0 10px", letterSpacing:"-0.4px", textShadow:"0 3px 14px rgba(0,0,0,0.5)", lineHeight:1.15}}>{isZh ? "搵啱寵物美容 · 即時預約" : "Find your groomer · book instantly"}</h1>
            <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center"}}>
              {landing && onBrowse && (
                <button onClick={onBrowse} style={{display:"inline-flex", alignItems:"center", gap:6, background:"#fff", color:"var(--c-primary)", border:"none", borderRadius:99, padding:"10px 18px", fontWeight:900, fontSize:14, cursor:"pointer", boxShadow:"0 6px 16px rgba(0,0,0,0.25)"}}>
                  <Search size={15} strokeWidth={2.8} /> {isZh ? "瀏覽店舖目錄" : "Browse the directory"} →
                </button>
              )}
              {!landing && countryLive && !loading && shops.length > 0 && (
                <span style={{display:"inline-flex", alignItems:"center", gap:5, background:"rgba(255,255,255,0.95)", color:"var(--c-primary)", borderRadius:99, padding:"8px 15px", fontWeight:900, fontSize:13, boxShadow:"0 6px 16px rgba(0,0,0,0.25)"}}>
                  <Store size={15} strokeWidth={2.6} /> {shops.length} {isZh ? "間店舖" : "shops"}
                </span>
              )}
              {rfqEnabled && onPostRequest && (
                <button onClick={onPostRequest} style={{background:"rgba(255,255,255,0.22)", color:"#fff", border:"1.5px solid rgba(255,255,255,0.65)", borderRadius:99, padding:"8px 16px", fontWeight:900, fontSize:13, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6}}>
                  📢 {isZh ? "出價搵店" : "Post a request"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>}

      {/* Find nearby pet shops — GPS-based entry card. Retired on the landing:
          the hero's 搵附近 button does the same thing one scroll higher. */}
      {false && landing && (
      <div className="lp-reveal" style={{maxWidth:1000, margin:"0 auto", padding:"18px 14px 4px"}}>
        <button className="lp-card" onClick={() => { if (typeof window !== "undefined") window.location.href = "/directory?near=1"; }}
          style={{width:"100%", display:"flex", alignItems:"center", gap:14, textAlign:"left", cursor:"pointer", background:"linear-gradient(135deg, var(--c-grad2), var(--c-grad1) 55%, var(--c-primary))", color:"#fff", border:"none", borderRadius:22, padding:"18px 20px", boxShadow:"0 12px 30px var(--c-primary-40)"}}>
          <div style={{flexShrink:0, width:52, height:52, borderRadius:15, background:"rgba(255,255,255,0.22)", border:"1px solid rgba(255,255,255,0.35)", display:"flex", alignItems:"center", justifyContent:"center"}}>
            <MapPin size={26} strokeWidth={2.6} color="#fff" />
          </div>
          <div style={{flex:1, minWidth:0}}>
            <p style={{fontSize:17, fontWeight:900, margin:"0 0 3px"}}>{isZh ? "尋找附近寵物店" : "Find nearby pet shops"}</p>
            <p style={{fontSize:12.5, fontWeight:700, opacity:0.92, margin:0, lineHeight:1.4}}>{isZh ? "用你嘅位置，即刻搵到最近嘅寵物美容店 📍" : "Use your location to find the closest groomers instantly 📍"}</p>
          </div>
          <span style={{flexShrink:0, background:"#fff", color:"var(--c-primary)", borderRadius:99, padding:"9px 16px", fontSize:13.5, fontWeight:900, boxShadow:"0 6px 16px rgba(0,0,0,0.18)", whiteSpace:"nowrap"}}>{isZh ? "搵附近 →" : "Near me →"}</span>
        </button>
      </div>
      )}

      {/* How it works (landing/home only) */}
      {landing && (
      <div id="features" className="lp-reveal" style={{maxWidth:1000, margin:"0 auto", padding:"22px 14px 6px", scrollMarginTop:60}}>
        <p style={{textAlign:"center", fontSize:17, fontWeight:900, color:"#3A3A56", margin:"0 0 14px"}}>{isZh ? "點樣用？" : "How it works"}</p>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:12}}>
          {[
            { Icon:Search, zh:["搜尋","搵附近寵物美容，比較地區同評分"], en:["Search","Find nearby groomers, compare area & rating"] },
            { Icon:Store, zh:["比較","睇店舖資料、相片同服務"], en:["Compare","Browse details, photos & services"] },
            { Icon:Calendar, zh:["預約 / 聯絡","會員店可即時網上預約"], en:["Book / contact","Members take instant online bookings"] },
            { Icon:MessageCircle, zh:["一鍵聯絡","致電、WhatsApp、睇地圖直達"], en:["One-tap contact","Call, WhatsApp & get directions"] },
          ].map((s, i) => (
            <div key={i} className="lp-card" style={{background:"#fff", borderRadius:18, padding:"20px 14px", textAlign:"center", boxShadow:"0 3px 14px rgba(0,0,0,0.05)", border:"1px solid var(--c-border)"}}>
              <div style={{width:48, height:48, borderRadius:14, background:"var(--c-lighter)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:10}}><s.Icon size={23} strokeWidth={2.3} color="var(--c-primary)" /></div>
              <p style={{fontSize:14.5, fontWeight:900, color:"#3A3A56", margin:"0 0 4px"}}>{isZh ? s.zh[0] : s.en[0]}</p>
              <p style={{fontSize:12.5, fontWeight:600, color:"#8A8A9E", margin:0, lineHeight:1.45}}>{isZh ? s.zh[1] : s.en[1]}</p>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Owner CTA — horizontal feature scroller (landing/home only) */}
      {landing && (
      <div className="lp-reveal" style={{maxWidth:1000, margin:"0 auto", padding:"6px 14px 4px"}}>
        <div style={{background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", borderRadius:24, padding:"22px 0 20px", color:"#fff", boxShadow:"0 14px 34px var(--c-primary-40)", overflow:"hidden"}}>
          <div style={{padding:"0 20px"}}>
            <p style={{fontSize:18, fontWeight:900, margin:"0 0 3px"}}>{isZh ? "你係寵物美容店主？" : "Own a pet grooming shop?"}</p>
            <p style={{fontSize:12.5, fontWeight:700, opacity:0.92, margin:"0 0 14px"}}>{isZh ? "免費登記，預約系統一站搞掂 👇" : "List free — the all-in-one booking system 👇"} <a href="/features" style={{color:"#fff", textDecoration:"underline", marginLeft:4}}>{isZh ? "睇全部功能 →" : "See all features →"}</a></p>
          </div>

          {/* Feature cards — a wrapping grid, so none is cut off at the edge */}
          <div className="lp-owner-grid">
            {OWNER_SLIDES.map((s, i) => {
              const Ic = s.Icon;
              return (
                <div key={i} className="lp-card" style={{background:"rgba(255,255,255,0.16)", border:"1px solid rgba(255,255,255,0.22)", borderRadius:16, padding:"14px 13px"}}>
                  <div style={{width:38, height:38, borderRadius:11, background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10, boxShadow:"0 4px 12px rgba(0,0,0,0.12)"}}>
                    <Ic size={20} strokeWidth={2.4} color="var(--c-primary)" />
                  </div>
                  <p style={{fontSize:13.5, fontWeight:900, margin:"0 0 3px", lineHeight:1.25}}>{isZh ? s.zh[0] : s.en[0]}</p>
                  <p style={{fontSize:11.5, fontWeight:600, opacity:0.9, margin:0, lineHeight:1.4}}>{isZh ? s.zh[1] : s.en[1]}</p>
                </div>
              );
            })}
          </div>

          {onShopLogin && (
            <div style={{display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap", padding:"18px 20px 0"}}>
              <button className="lp-btn ghost" onClick={onShopLogin}>
                {isZh ? "免費登記店舖" : "List your shop free"} <span className="arr">→</span>
              </button>
              <a className="lp-btn line" href="/features">{isZh ? "睇功能介紹" : "See how it works"} <span className="arr">→</span></a>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Pricing — public plan tiers for shop owners (landing only). */}
      {landing && (() => {
        const plans = getPlans(social);
        const cur = social.basePlanCurrency || "HKD";
        const order = ["free", "basic", "pro", "enterprise"];
        const featRows = [
          { k: "onlineBooking", zh: "網上預約", en: "Online booking" },
          { k: "scheduling", zh: "排班系統", en: "Staff scheduling" },
          { k: "autoNotify", zh: "電郵通知（確認 / 提醒）", en: "Email notifications" },
          { k: "analytics", zh: "營業報表", en: "Analytics" },
          { k: "wallet", zh: "會員錢包", en: "Member wallet" },
          { k: "stampCard", zh: "電子積分卡", en: "Digital stamp card" },
          { k: "loyalty", zh: "會員積分", en: "Loyalty points" },
          { k: "whatsapp", zh: "WhatsApp 通知（需自行設定帳號）", en: "WhatsApp alerts (your own account)" },
          { k: "payroll", zh: "員工薪酬", en: "Payroll" },
          { k: "pos", zh: "收銀 POS", en: "Point of sale (POS)", on: (p) => planPosRank(p) >= 1, val: (p, en) => planPosMaxLabel(p, en) },
          { k: "posStandard", zh: "收銀：庫存 / 退款 / 交更 / 優惠", en: "POS: stock, refunds, shifts, promos", on: (p) => planPosRank(p) >= 2 },
          { k: "posFull", zh: "收銀：採購 / AI 新增商品 / 毛利報表", en: "POS: purchasing, AI products, margins", on: (p) => planPosRank(p) >= 3 },
          { k: "removeBranding", zh: "移除品牌標示", en: "White-label" },
        ];
        const annualMonths = Math.max(0, Math.min(12, social.annualDiscountMonths ?? 2)); // free months on annual
        const annual = billCycle === "annual" && annualMonths > 0;
        const money = (n) => (cur === "HKD" ? "$" : cur + " ") + n;
        return (
          <div id="pricing" className="lp-reveal" style={{maxWidth:1000, margin:"0 auto", padding:"26px 14px 6px", scrollMarginTop:60}}>
            <p style={{textAlign:"center", fontSize:18, fontWeight:900, color:"#3A3A56", margin:"0 0 4px"}}>{isZh ? "店舖收費方案" : "Plans for shops"}</p>
            <p style={{textAlign:"center", fontSize:12.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 14px"}}>{isZh ? "新店舖送 14 日專業版免費試用，唔使信用卡" : "New shops get a 14-day free Pro trial — no card needed"}</p>
            {annualMonths > 0 && (
              <div style={{display:"flex", justifyContent:"center", marginBottom:18}}>
                <div style={{display:"inline-flex", background:"var(--c-lighter)", borderRadius:99, padding:4, gap:4}}>
                  <button onClick={() => setBillCycle("monthly")} style={{background: billCycle === "monthly" ? "#fff" : "transparent", color: billCycle === "monthly" ? "var(--c-primary)" : "#8A8A9E", border:"none", borderRadius:99, padding:"7px 18px", fontSize:13, fontWeight:900, cursor:"pointer", boxShadow: billCycle === "monthly" ? "0 2px 8px rgba(0,0,0,0.1)" : "none"}}>{isZh ? "月繳" : "Monthly"}</button>
                  <button onClick={() => setBillCycle("annual")} style={{background: billCycle === "annual" ? "#fff" : "transparent", color: billCycle === "annual" ? "var(--c-primary)" : "#8A8A9E", border:"none", borderRadius:99, padding:"7px 18px", fontSize:13, fontWeight:900, cursor:"pointer", boxShadow: billCycle === "annual" ? "0 2px 8px rgba(0,0,0,0.1)" : "none", display:"inline-flex", alignItems:"center", gap:6}}>{isZh ? "年繳" : "Annual"}<span style={{background:"#10B981", color:"#fff", fontSize:10, fontWeight:900, borderRadius:99, padding:"1px 7px"}}>{isZh ? `慳 ${annualMonths} 個月` : `${annualMonths} mo free`}</span></button>
                </div>
              </div>
            )}
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))", gap:12, alignItems:"start"}}>
              {order.map(key => {
                const p = plans[key]; if (!p) return null;
                const isPro = key === "pro";
                const yearTotal = p.price * (12 - annualMonths);
                return (
                  <div key={key} style={{background:"#fff", borderRadius:20, padding:"20px 18px", border: isPro ? "2px solid var(--c-primary)" : "1px solid var(--c-border)", boxShadow: isPro ? "0 12px 30px var(--c-primary-40)" : "0 3px 14px rgba(0,0,0,0.05)", position:"relative"}}>
                    {isPro && <span style={{position:"absolute", top:-11, left:"50%", transform:"translateX(-50%)", background:"var(--c-primary)", color:"#fff", fontSize:11, fontWeight:900, borderRadius:99, padding:"3px 12px", whiteSpace:"nowrap"}}>{isZh ? "推薦" : "Recommended"}</span>}
                    {key === "free" && <span style={{position:"absolute", top:-11, left:"50%", transform:"translateX(-50%)", background:"#10B981", color:"#fff", fontSize:11, fontWeight:900, borderRadius:99, padding:"3px 12px", whiteSpace:"nowrap"}}>{isZh ? "新店試用" : "Try it free"}</span>}
                    <p style={{fontSize:15, fontWeight:900, color:"#3A3A56", margin:"0 0 6px"}}>{planName(key, plans)}</p>
                    {p.price > 0 ? (
                      annual ? (
                        <p style={{margin:"0 0 2px"}}>
                          <span style={{fontSize:30, fontWeight:900, color: isPro ? "var(--c-primary)" : "#3A3A56"}}>{money(Math.round(yearTotal / 12))}</span>
                          <span style={{fontSize:13, fontWeight:700, color:"#8A8A9E"}}>{isZh ? " / 月" : " / mo"}</span>
                          <span style={{fontSize:11, fontWeight:800, color:"#10B981", display:"block", marginTop:2}}>{isZh ? `年繳 ${money(yearTotal)}（原價 ${money(p.price * 12)}）` : `${money(yearTotal)}/yr (was ${money(p.price * 12)})`}</span>
                        </p>
                      ) : (
                        <p style={{margin:"0 0 4px"}}><span style={{fontSize:30, fontWeight:900, color: isPro ? "var(--c-primary)" : "#3A3A56"}}>{money(p.price)}</span><span style={{fontSize:13, fontWeight:700, color:"#8A8A9E"}}>{isZh ? " / 月" : " / mo"}</span></p>
                      )
                    ) : (
                      <p style={{margin:"0 0 4px"}}><span style={{fontSize:30, fontWeight:900, color:"#3A3A56"}}>{isZh ? "免費" : "Free"}</span></p>
                    )}
                    <p style={{fontSize:11.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 4px"}}>{isZh ? `最多 ${p.maxStaff} 位員工` : `Up to ${p.maxStaff} staff`}</p>
                    {p.seatOverage > 0 && <p style={{fontSize:11, fontWeight:700, color:"#AFAFC4", margin:"0 0 12px"}}>{isZh ? `超過 ${p.maxStaff} 人，每位 +${money(p.seatOverage)}/月` : `+${money(p.seatOverage)}/mo per extra staff`}</p>}
                    {!(p.seatOverage > 0) && <div style={{height:8}} />}
                    <div style={{display:"flex", flexDirection:"column", gap:6}}>
                      {featRows.map(f => {
                        const on = planRowOn(f, p);
                        const extra = on && f.val ? f.val(p, !isZh) : null;
                        return <div key={f.k} style={{display:"flex", alignItems:"center", gap:7, fontSize:12.5, fontWeight:700, color: on ? "#3A3A56" : "#C4C4D0"}}>
                          <span style={{color: on ? "#10B981" : "#D4D4DE", fontWeight:900}}>{on ? "✓" : "—"}</span>{isZh ? f.zh : f.en}{extra ? <span style={{color:"#8A8A9E"}}>（{extra}）</span> : null}
                        </div>;
                      })}
                    </div>
                    {onShopRegister && <button onClick={onShopRegister} style={{marginTop:16, width:"100%", background: isPro ? "var(--c-primary)" : "#fff", color: isPro ? "#fff" : "var(--c-primary)", border: isPro ? "none" : "2px solid var(--c-primary)", borderRadius:99, padding:"11px", fontSize:13.5, fontWeight:900, cursor:"pointer"}}>{isZh ? "立即登記" : "Get started"}</button>}
                  </div>
                );
              })}
            </div>
            {/* WhatsApp AI is not a plan feature — it runs on the shop's own
                third-party Chatwoot / WATI account, billed by them. */}
            <p style={{textAlign:"center", fontSize:12, fontWeight:700, color:"#8A8A9E", margin:"14px 0 0", lineHeight:1.6}}>{isZh ? "* WhatsApp AI 自動回覆唔包喺任何方案：需要另外付費開通第三方 Chatwoot 或 WATI 帳戶。" : "* WhatsApp AI auto-reply is not included in any plan: it needs a separately paid Chatwoot or WATI account."}</p>
          </div>
        );
      })()}

      {/* Request a demo — email capture; system emails the demo logins. */}
      {landing && (
        <div id="demo" className="lp-reveal" style={{maxWidth:1000, margin:"0 auto", padding:"22px 14px 6px", scrollMarginTop:60}}>
          <div style={{position:"relative", overflow:"hidden", borderRadius:24, padding:"26px 22px", background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", color:"#fff", boxShadow:"0 16px 38px var(--c-primary-40)"}}>
            <div aria-hidden style={{position:"absolute", right:-10, bottom:-30, fontSize:130, opacity:0.14, transform:"rotate(-12deg)"}}>🐾</div>
            <div style={{position:"relative", maxWidth:560}}>
              <span style={{display:"inline-flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.22)", border:"1px solid rgba(255,255,255,0.35)", borderRadius:99, padding:"4px 12px", fontSize:11.5, fontWeight:800, marginBottom:10}}>✨ {isZh ? "免費試用" : "Free trial"}</span>
              <h2 style={{fontSize:22, fontWeight:900, margin:"0 0 6px", letterSpacing:"-0.3px"}}>{isZh ? "即試示範帳戶" : "Try the live demo"}</h2>
              <p style={{fontSize:13.5, fontWeight:700, opacity:0.95, margin:"0 0 16px", lineHeight:1.55}}>{isZh ? "留低電郵，我哋即刻寄示範帳戶畀你 — 親手試吓店主後台同客戶預約流程。" : "Drop your email — we'll instantly send you demo logins to explore the shop dashboard and the customer booking flow."}</p>
              {demoState === "sent" ? (
                <div style={{background:"rgba(255,255,255,0.95)", borderRadius:16, padding:"16px 18px", color:"#3A3A56"}}>
                  <p style={{fontSize:15, fontWeight:900, margin:"0 0 4px"}}>✅ {isZh ? "已寄出！請查收電郵" : "Sent! Check your inbox"}</p>
                  <p style={{fontSize:12.5, fontWeight:700, color:"#8A8A9E", margin:0}}>{isZh ? "示範帳戶同登入連結已寄到你嘅電郵（如收唔到請檢查垃圾郵件）。" : "Demo logins are on the way to your email (check spam if you don't see it)."}</p>
                </div>
              ) : (
                <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-start"}}>
                  <div style={{flex:"1 1 240px", minWidth:0}}>
                    <input value={demo.email} onChange={e => setDemo(d => ({ ...d, email: e.target.value }))} type="email" inputMode="email" placeholder={isZh ? "你的電郵地址" : "you@email.com"}
                      onKeyDown={e => { if (e.key === "Enter") submitDemoReq(); }}
                      style={{width:"100%", border:"none", borderRadius:12, padding:"13px 15px", fontSize:14.5, fontWeight:700, outline:"none", color:"#4A4A68", boxSizing:"border-box"}} />
                    {/* honeypot */}
                    <input value={demo.company} onChange={e => setDemo(d => ({ ...d, company: e.target.value }))} tabIndex={-1} autoComplete="off" aria-hidden style={{position:"absolute", left:"-9999px", width:1, height:1, opacity:0}} />
                  </div>
                  <button onClick={submitDemoReq} disabled={demoState === "sending"} style={{flexShrink:0, background:"#fff", color:"var(--c-primary)", border:"none", borderRadius:12, padding:"13px 22px", fontSize:14.5, fontWeight:900, cursor: demoState === "sending" ? "wait" : "pointer", opacity: demoState === "sending" ? 0.7 : 1, boxShadow:"0 6px 16px rgba(0,0,0,0.2)"}}>
                    {demoState === "sending" ? (isZh ? "傳送中…" : "Sending…") : (isZh ? "免費取得示範帳戶 →" : "Get demo access →")}
                  </button>
                </div>
              )}
              {demoErr && <p style={{fontSize:12.5, fontWeight:800, margin:"8px 0 0", background:"rgba(255,255,255,0.95)", color:"#DC2626", borderRadius:10, padding:"6px 10px", display:"inline-block"}}>{demoErr}</p>}
              {demoState !== "sent" && <p style={{fontSize:11, fontWeight:700, opacity:0.85, margin:"10px 0 0"}}>{isZh ? "🔒 只用作寄示範帳戶，唔會濫發。" : "🔒 Only used to send your demo access."}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Contact us — public form emailing the platform inbox (landing only). */}
      {landing && (
        <div id="contact" className="lp-reveal" style={{maxWidth:640, margin:"0 auto", padding:"26px 14px 10px", scrollMarginTop:60}}>
          <p style={{textAlign:"center", fontSize:18, fontWeight:900, color:"#3A3A56", margin:"0 0 4px"}}>{isZh ? "聯絡我們" : "Contact us"}</p>
          <p style={{textAlign:"center", fontSize:12.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 16px"}}>{isZh ? "有問題或合作查詢？填妥下表，我哋會盡快回覆。" : "Questions or partnership? Send us a note and we'll reply soon."}</p>
          <div style={{background:"#fff", borderRadius:20, padding:"18px", border:"1px solid var(--c-border)", boxShadow:"0 3px 14px rgba(0,0,0,0.05)"}}>
            {contactState === "sent" ? (
              <div style={{textAlign:"center", padding:"20px 10px"}}>
                <div style={{fontSize:40, marginBottom:8}}>✅</div>
                <p style={{fontSize:15, fontWeight:900, color:"#3A3A56", margin:"0 0 4px"}}>{isZh ? "已收到，多謝你！" : "Message sent — thank you!"}</p>
                <p style={{fontSize:12.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 14px"}}>{isZh ? "我哋會用電郵回覆你。" : "We'll get back to you by email."}</p>
                <button onClick={() => setContactState("idle")} style={{background:"var(--c-lighter)", color:"var(--c-primary)", border:"none", borderRadius:99, padding:"9px 18px", fontSize:13, fontWeight:800, cursor:"pointer"}}>{isZh ? "再發送一則" : "Send another"}</button>
              </div>
            ) : (
              <div style={{display:"flex", flexDirection:"column", gap:10}}>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:10}}>
                  <input value={contact.name} onChange={e => setContact(c => ({ ...c, name: e.target.value }))} placeholder={isZh ? "姓名 *" : "Name *"} style={{minWidth:0, width:"100%", border:"1.5px solid var(--c-border)", borderRadius:12, padding:"11px 13px", fontSize:14, fontWeight:600, outline:"none", boxSizing:"border-box"}} />
                  <input value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} placeholder={isZh ? "電話（可選）" : "Phone (optional)"} style={{minWidth:0, width:"100%", border:"1.5px solid var(--c-border)", borderRadius:12, padding:"11px 13px", fontSize:14, fontWeight:600, outline:"none", boxSizing:"border-box"}} />
                </div>
                <input value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} type="email" placeholder={isZh ? "電郵 *" : "Email *"} style={{border:"1.5px solid var(--c-border)", borderRadius:12, padding:"11px 13px", fontSize:14, fontWeight:600, outline:"none", boxSizing:"border-box"}} />
                <input value={contact.subject} onChange={e => setContact(c => ({ ...c, subject: e.target.value }))} placeholder={isZh ? "主題（可選）" : "Subject (optional)"} style={{border:"1.5px solid var(--c-border)", borderRadius:12, padding:"11px 13px", fontSize:14, fontWeight:600, outline:"none", boxSizing:"border-box"}} />
                <textarea value={contact.message} onChange={e => setContact(c => ({ ...c, message: e.target.value }))} placeholder={isZh ? "訊息內容 *" : "Your message *"} style={{border:"1.5px solid var(--c-border)", borderRadius:12, padding:"11px 13px", fontSize:14, fontWeight:600, outline:"none", minHeight:110, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box"}} />
                {/* Honeypot — hidden from humans, bots fill it. */}
                <input value={contact.company} onChange={e => setContact(c => ({ ...c, company: e.target.value }))} tabIndex={-1} autoComplete="off" aria-hidden style={{position:"absolute", left:"-9999px", width:1, height:1, opacity:0}} />
                {contactErr && <p style={{fontSize:12.5, color:"#DC2626", fontWeight:700, margin:0}}>{contactErr}</p>}
                <button onClick={submitContact} disabled={contactState === "sending"} style={{background:"var(--c-primary)", color:"#fff", border:"none", borderRadius:99, padding:"13px", fontSize:14.5, fontWeight:900, cursor: contactState === "sending" ? "wait" : "pointer", opacity: contactState === "sending" ? 0.7 : 1, boxShadow:"0 6px 16px var(--c-primary-40)"}}>
                  {contactState === "sending" ? (isZh ? "傳送中…" : "Sending…") : (isZh ? "傳送訊息" : "Send message")}
                </button>
                {mailUrl && <p style={{textAlign:"center", fontSize:11.5, fontWeight:700, color:"#8A8A9E", margin:0}}>{isZh ? "或直接電郵：" : "Or email us: "}<a href={mailUrl} style={{color:"var(--c-primary)", textDecoration:"none"}}>{social.email}</a></p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Region filter chips — wrap (no horizontal scroll); collapsed shows a
          few + 更多, expand to see ALL at once; selecting auto-collapses. */}
      {!landing && countryLive && regionOptions.length > 0 && (() => {
        const COLLAPSED = 8;
        let shown = regionExpanded ? regionOptions : regionOptions.slice(0, COLLAPSED);
        if (!regionExpanded && region && !shown.some(r => r.id === region)) {
          const sel = regionOptions.find(r => r.id === region);
          if (sel) shown = [sel, ...shown.slice(0, COLLAPSED - 1)];
        }
        const pick = (id) => { setRegion(id); setRegionExpanded(false); };
        return (
          <div style={{display:"flex", flexWrap:"wrap", gap:7, padding:"8px 14px 2px"}}>
            <button onClick={() => pick(null)} style={{background: region == null ? "var(--c-primary)" : "#fff", color: region == null ? "#fff" : "#4A4A68", border:"1px solid var(--c-border)", borderRadius:99, padding:"6px 13px", fontSize:12.5, fontWeight:800, cursor:"pointer"}}>📍 {isZh ? "全部" : "All"}</button>
            {shown.map(r => {
              const sel = region === r.id;
              return <button key={r.id} onClick={() => pick(r.id)} style={{background: sel ? "var(--c-primary)" : "#fff", color: sel ? "#fff" : "#4A4A68", border:"1px solid var(--c-border)", borderRadius:99, padding:"6px 13px", fontSize:12.5, fontWeight:800, cursor:"pointer"}}>{r.label}</button>;
            })}
            {regionOptions.length > COLLAPSED && (
              <button onClick={() => setRegionExpanded(v => !v)} style={{background:"var(--c-lighter)", color:"var(--c-primary)", border:"1px dashed var(--c-primary)", borderRadius:99, padding:"6px 13px", fontSize:12.5, fontWeight:800, cursor:"pointer"}}>
                {regionExpanded ? (isZh ? "收起 ▲" : "Less ▲") : (isZh ? `更多 (${regionOptions.length - COLLAPSED}) ▾` : `More (${regionOptions.length - COLLAPSED}) ▾`)}
              </button>
            )}
          </div>
        );
      })()}

      {/* Sticky filter bar */}
      {!landing && (
      <div style={{position:"sticky", top:0, zIndex:40, background:"#FFFDFD", borderBottom:"1px solid var(--c-border)", padding:"10px 14px", display:"flex", gap:8, alignItems:"center", overflowX:"auto"}}>
        <div style={{flex:1, minWidth:160, display:"flex", alignItems:"center", gap:8, background:"var(--c-lighter)", borderRadius:99, padding:"8px 12px"}}>
          <Search size={16} color="var(--c-primary)" strokeWidth={2.5} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={isZh ? "搜尋店舖名 / 地區…" : "Search shop / area…"} style={{flex:1, border:"none", background:"transparent", outline:"none", fontSize:14, fontWeight:600, color:"#4A4A68", minWidth:0}} />
          {q && <button onClick={() => setQ("")} style={{background:"none", border:"none", color:"#AFAFC4", cursor:"pointer", fontSize:18, lineHeight:1}}>×</button>}
        </div>
        <button onClick={toggleNearMe} disabled={geoBusy} style={{flexShrink:0, background: nearMe ? "var(--c-primary)" : "var(--c-lighter)", color: nearMe ? "#fff" : "var(--c-primary)", border:"none", borderRadius:99, padding:"8px 14px", fontSize:13, fontWeight:800, cursor: geoBusy ? "wait" : "pointer", display:"flex", alignItems:"center", gap:6, opacity: geoBusy ? 0.6 : 1}}>
          <MapPin size={15} strokeWidth={2.6} /> {geoBusy ? (isZh ? "定位中…" : "Locating…") : (isZh ? "搵附近美容店" : "Near me")}
        </button>
        <button onClick={() => setMobileOnly(v => !v)} title={isZh ? "只睇上門服務" : "Home-visit only"} style={{flexShrink:0, background: mobileOnly ? "var(--c-primary)" : "var(--c-lighter)", color: mobileOnly ? "#fff" : "var(--c-primary)", border:"none", borderRadius:99, padding:"8px 14px", fontSize:13, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:6}}>
          🚗 {isZh ? "上門服務" : "Home visit"}
        </button>
      </div>
      )}

      {/* Result list */}
      {!landing && (
      <div style={{maxWidth:1000, margin:"0 auto", padding:"16px 14px"}}>
        {!countryLive && (
          <div style={{textAlign:"center", padding:"50px 20px", background:"#fff", borderRadius:20, boxShadow:"0 8px 24px rgba(0,0,0,0.04)"}}>
            <div style={{fontSize:42, marginBottom:8}}>{countryMeta.flag}</div>
            <p style={{fontSize:14, color:"#4A4A68", fontWeight:800, margin:"0 0 4px"}}>{isZh ? `${countryMeta.zh}即將推出` : `${countryMeta.en} coming soon`}</p>
            <p style={{fontSize:12, color:"#8A8A9E", margin:0, fontWeight:700}}>{isZh ? "我哋正準備呢個地區嘅店舖名單" : "We're preparing the shop list for this market"}</p>
          </div>
        )}
        {countryLive && loading && <p style={{textAlign:"center", color:"#AFAFC4", fontWeight:700, padding:"40px 0"}}>{isZh ? "載入中…" : "Loading…"}</p>}
        {countryLive && !loading && err && <p style={{textAlign:"center", color:"#EF4444", fontWeight:700, padding:"40px 0"}}>{err}</p>}
        {countryLive && !loading && !err && displayShops.length === 0 && (
          <div style={{textAlign:"center", padding:"50px 20px", background:"#fff", borderRadius:20, boxShadow:"0 8px 24px rgba(0,0,0,0.04)"}}>
            <div style={{fontSize:42, marginBottom:8}}>🐾</div>
            <p style={{fontSize:14, color:"#4A4A68", fontWeight:800, margin:"0 0 4px"}}>{isZh ? "未有符合條件嘅店舖" : "No matching shops"}</p>
            <p style={{fontSize:12, color:"#8A8A9E", margin:0, fontWeight:700}}>{isZh ? "試吓改其他地區 / 清空搜尋" : "Try a different region or clear the search"}</p>
          </div>
        )}
        {countryLive && !loading && !err && displayShops.length > 0 && nearMe && userPos && (
          <div style={{marginBottom:12}}>
            <NearbyMap userPos={userPos} shops={displayShops} primaryHex={themeVars["--c-primary"] || "#B76E79"} onPick={(s) => s.isMember ? onPickShop?.(s.slug) : onPickListing?.(s.slug)} />
            <p style={{fontSize:12, fontWeight:800, color:"#8A8A9E", margin:"10px 2px 0", textAlign:"center"}}>{isZh ? "顯示你附近嘅美容店（按距離排序）" : "Showing groomers near you (sorted by distance)"}</p>
          </div>
        )}
        {countryLive && !loading && !err && displayShops.length > 0 && (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:10}}>
            {shownShops.map(s => {
              const boosted = s.boostUntil && new Date(s.boostUntil) > new Date();
              const regZh = s.region ? (lookupRegion(s.region)?.[isZh ? "zh" : "en"] || s.region) : null;
              const onTap = () => s.isMember ? onPickShop?.(s.slug) : onPickListing?.(s.slug);
              const domain = s.website ? s.website.replace(/^https?:\/\//, "").split("/")[0] : null;
              const logoSrc = s.logoUrl || s.photoUrl || (domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null);
              const initial = (s.name || "🐾").trim().charAt(0);
              // Members show their short description (tagline); listings show address.
              const subtitle = s.isMember ? (s.tagline || s.address || s.aboutPreview) : (s.address || s.tagline || s.aboutPreview);
              const dirDest = (s.latitude != null && s.longitude != null)
                ? `${s.latitude},${s.longitude}`
                : [s.name, s.address].filter(Boolean).join(" ");
              const directionsUrl = dirDest ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dirDest)}` : null;
              const tags = (s.tags || []).slice(0, 4);
              const prices = (s.servicesHead || []).map(x => Number(x.basePrice)).filter(p => p > 0);
              const minPrice = prices.length ? Math.min(...prices) : 0;
              return (
                <div key={s.id} onClick={onTap} style={{cursor:"pointer", background:"#fff", borderRadius:18, overflow:"hidden", boxShadow: boosted ? "0 12px 30px rgba(255,77,109,0.22)" : "0 4px 16px rgba(0,0,0,0.06)", border: boosted ? "2px solid transparent" : "1px solid var(--c-border)", backgroundImage: boosted ? "linear-gradient(#fff,#fff), linear-gradient(135deg, var(--c-grad1), var(--c-grad2))" : undefined, backgroundOrigin: boosted ? "border-box" : undefined, backgroundClip: boosted ? "padding-box, border-box" : undefined, position:"relative", transition:"transform .15s"}}>
                  {boosted && (
                    <div style={{position:"absolute", top:10, right:10, zIndex:2, background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", color:"#fff", fontSize:10, fontWeight:900, padding:"3px 10px", borderRadius:99, boxShadow:"0 4px 10px rgba(255,77,109,0.4)"}}>🌟 BOOST</div>
                  )}
                  <div style={{padding:"13px 14px", display:"flex", gap:13, alignItems:"center"}}>
                    {/* Logo / favicon avatar with initial fallback */}
                    <div style={{width:54, height:54, borderRadius:14, flexShrink:0, position:"relative", overflow:"hidden", background:"linear-gradient(135deg, var(--c-lighter), var(--c-light))", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid var(--c-border)"}}>
                      <span style={{fontSize:22, fontWeight:900, color:"var(--c-primary)"}}>{initial}</span>
                      {logoSrc && <img src={logoSrc} alt="" style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover"}} onError={(e)=>{e.currentTarget.style.display="none";}} />}
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{display:"flex", alignItems:"center", gap:5, margin:"0 0 3px"}}>
                        <p style={{fontSize:15, fontWeight:900, color:"#3A3A56", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0}}>{s.name}</p>
                        {s.isMember && <BadgeCheck size={15} strokeWidth={2.8} color="#047857" style={{flexShrink:0}} />}
                      </div>
                      {subtitle && <p style={{fontSize:12, color:"#8A8A9E", margin:"0 0 3px", fontWeight:600, lineHeight:1.35, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{subtitle}</p>}
                      {s.phone && <p style={{fontSize:11.5, color:"#6A6A85", margin:"0 0 6px", fontWeight:700, display:"flex", alignItems:"center", gap:4}}><Phone size={11} /> {s.phone}</p>}
                      <div style={{display:"flex", gap:5, flexWrap:"wrap", alignItems:"center"}}>
                        {s.distanceKm != null && <span style={{fontSize:11, fontWeight:900, color:"#0369A1", background:"#E0F2FE", borderRadius:99, padding:"2px 8px"}}>📍 {fmtDistance(s.distanceKm)}</span>}
                        {regZh && <span style={{fontSize:11, fontWeight:800, color:"#7C3AED", background:"#EDE9FE", borderRadius:99, padding:"2px 8px"}}>{regZh}</span>}
                        {s.rating > 0 && <span style={{fontSize:11, fontWeight:800, color:"#B45309", background:"#FEF3C7", borderRadius:99, padding:"2px 8px"}}>⭐ {s.rating}{s.ratingCount ? ` (${s.ratingCount})` : ""}</span>}
                        {(s.serviceMode === "mobile" || s.serviceMode === "both") && <span style={{fontSize:11, fontWeight:800, color:"#7C3AED", background:"#EDE9FE", borderRadius:99, padding:"2px 8px"}}>🚗 {isZh ? (s.serviceMode === "both" ? "可上門" : "上門服務") : "Home visit"}</span>}
                        {tags.map((tag, ti) => <span key={ti} style={{fontSize:11, fontWeight:800, color:"var(--c-primary)", background:"var(--c-lighter)", borderRadius:99, padding:"2px 8px"}}>{tag}</span>)}
                        {s.isMember && minPrice > 0 && <span style={{fontSize:11, fontWeight:800, color:"#6A6A85", background:"#F3F4F6", borderRadius:99, padding:"2px 8px"}}>💲{isZh ? `由 $${minPrice} 起` : `from $${minPrice}`}</span>}
                        {s.isMember && s.bookingEnabled !== false && <span style={{fontSize:11, fontWeight:800, color:"#047857", background:"#D1FAE5", borderRadius:99, padding:"2px 8px"}}>📅 {isZh ? "可線上預約" : "Book online"}</span>}
                        {directionsUrl && <a href={directionsUrl} target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()} style={{fontSize:11, fontWeight:800, color:"#0369A1", background:"#E0F2FE", borderRadius:99, padding:"2px 8px", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:3}}>🧭 {isZh ? "尋找路線" : "Directions"}</a>}
                      </div>
                    </div>
                    <ChevronRight size={18} color="#C4C4D4" style={{flexShrink:0}} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {countryLive && !loading && !err && displayShops.length > visibleCount && (
          <div style={{textAlign:"center", marginTop:16}}>
            <button onClick={() => setVisibleCount(c => c + 48)} style={{background:"#fff", color:"var(--c-primary)", border:"1.5px solid var(--c-primary)", borderRadius:99, padding:"11px 26px", fontWeight:900, fontSize:14, cursor:"pointer"}}>
              {isZh ? `載入更多（仲有 ${displayShops.length - visibleCount} 間）` : `Load more (${displayShops.length - visibleCount} left)`}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Region picker bottom sheet */}
      {!landing && regionSheetOpen && (
        <div onClick={() => setRegionSheetOpen(false)} style={{position:"fixed", inset:0, zIndex:200, background:"rgba(31,41,55,0.55)", display:"flex", alignItems:"flex-end", justifyContent:"center"}}>
          <div onClick={e => e.stopPropagation()} style={{width:"100%", maxWidth:560, maxHeight:"82vh", overflowY:"auto", background:"#FFFDFD", borderRadius:"24px 24px 0 0", padding:"18px 18px 24px"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
              <p style={{fontSize:16, fontWeight:900, color:"#4A4A68", margin:0}}>{isZh ? "揀地區" : "Pick a region"}</p>
              <button onClick={() => setRegionSheetOpen(false)} style={{background:"none", border:"none", fontSize:20, color:"#8A8A9E", cursor:"pointer", padding:4}}>×</button>
            </div>
            <button onClick={() => { setRegion(null); setRegionSheetOpen(false); }} style={{width:"100%", padding:"12px 14px", background: region == null ? "var(--c-primary)" : "var(--c-lighter)", color: region == null ? "#fff" : "#4A4A68", border:"none", borderRadius:14, fontWeight:800, fontSize:14, cursor:"pointer", marginBottom:10}}>
              📍 {isZh ? "全部地區" : "All regions"}
            </button>
            {HK_REGION_GROUPS.map(g => (
              <div key={g.id} style={{marginBottom:8}}>
                <p style={{fontSize:11, fontWeight:900, color:"#8A8A9E", margin:"10px 4px 6px", letterSpacing:0.5}}>{(isZh ? g.zh : g.en).toUpperCase()}</p>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6}}>
                  {HK_REGIONS.filter(r => r.group === g.id).map(r => {
                    const sel = region === r.id;
                    return (
                      <button key={r.id} onClick={() => { setRegion(r.id); setRegionSheetOpen(false); }} style={{padding:"10px 12px", background: sel ? "var(--c-primary)" : "#fff", color: sel ? "#fff" : "#4A4A68", border:"2px solid " + (sel ? "var(--c-primary)" : "var(--c-border)"), borderRadius:12, fontWeight:800, fontSize:13, cursor:"pointer", textAlign:"left"}}>
                        {isZh ? r.zh : r.en}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Landing footer — the pages a visitor looks for, plus owner login. */}
      {landing && (
        <footer className="lp-foot">
          <a href="/directory">{isZh ? "寵物美容店目錄" : "Groomer directory"}</a>
          <a href="/features">{isZh ? "美容店預約系統" : "For grooming shops"}</a>
          <a href="/help">{isZh ? "使用教學" : "Help centre"}</a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener">{isZh ? "Android 店主 App" : "Android shop app"}</a>
          <a href="/blog">{isZh ? "美容知識" : "Grooming tips"}</a>
          <a href="#/privacy">{isZh ? "私隱政策" : "Privacy"}</a>
          <a href="#/terms">{isZh ? "服務條款" : "Terms"}</a>
          {onShopLogin && <button onClick={onShopLogin}>🏪 {isZh ? "店主登入" : "Shop login"}</button>}
          <span style={{flexBasis:"100%", textAlign:"center", color:"#AFAFC4", fontWeight:700}}>© PettoBook</span>
        </footer>
      )}
      {/* Unobtrusive shop-owner login link at the bottom (directory page) */}
      {!landing && onShopLogin && (
        <div style={{textAlign:"center", padding:"18px 20px 10px"}}>
          <button onClick={onShopLogin} style={{background:"transparent", border:"none", color:"#AFAFC4", fontSize:13, fontWeight:700, cursor:"pointer", textDecoration:"underline"}}>
            🏪 {isZh ? "店主登入" : "Shop owner? Log in"} →
          </button>
        </div>
      )}
      {/* Sales / technical enquiries. Mounted here rather than in
          MarketingHome, which is dead code — nothing renders it, so a widget
          placed there is silently tree-shaken out of the bundle. */}
      <SalesChatWidget />
    </div>
  );
};

// Read-only basic profile for a non-member (external) shop — a salon found via
// research that hasn't joined PetOS. Shows only public basic info (no booking)
// plus a prominent "Claim this shop" CTA that opens the claim form.
const NonMemberShop = ({ listing, loading, onBack }) => {
  i18nUseLocale();
  const isZh = i18nGetLocale() === "zh-HK";
  const [claimOpen, setClaimOpen] = useState(false);
  let dirThemeId = "mocha", heroPhotoId = "dog";
  try { dirThemeId = localStorage.getItem(DIR_THEME_KEY) || "mocha"; heroPhotoId = localStorage.getItem(HERO_PHOTO_KEY) || "dog"; } catch {}
  const dirTheme = getDirTheme(dirThemeId);
  const themeVars = dirTheme.vars;
  const listingHeroPhoto = getHeroPhoto(heroPhotoId).url;

  if (loading) {
    return (
      <div style={{...themeVars, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--c-lighter)"}}>
        <p style={{color:"#AFAFC4", fontWeight:800}}>{isZh ? "載入中…" : "Loading…"}</p>
      </div>
    );
  }
  if (!listing) {
    return (
      <div style={{...themeVars, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--c-lighter)", padding:24}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:40, marginBottom:8}}>😢</div>
          <p style={{fontWeight:800, color:"#4A4A68", margin:"0 0 12px"}}>{isZh ? "找不到此店舖" : "Shop not found"}</p>
          <Btn variant="secondary" onClick={onBack}>{isZh ? "返回目錄" : "Back to directory"}</Btn>
        </div>
      </div>
    );
  }

  const regLabel = listing.region ? (lookupRegion(listing.region)?.[isZh ? "zh" : "en"] || listing.region) : null;
  const hasGeo = listing.latitude != null && listing.longitude != null;
  const mapQuery = encodeURIComponent(listing.address || listing.name || "");
  const mapUrl = hasGeo
    ? `https://www.google.com/maps/search/?api=1&query=${listing.latitude},${listing.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;
  const mapEmbed = hasGeo
    ? `https://www.google.com/maps?q=${listing.latitude},${listing.longitude}&z=16&output=embed`
    : (listing.address ? `https://www.google.com/maps?q=${mapQuery}&z=15&output=embed` : null);

  // International phone for tel: / wa.me, inferred from the listing's market.
  const digits = (listing.phone || "").replace(/\D/g, "");
  let intl = digits;
  if (digits) {
    if (listing.country === "HK" && digits.length === 8) intl = "852" + digits;
    else if (listing.country === "TW") intl = "886" + digits.replace(/^0/, "");
    else if (listing.country === "US" || listing.country === "CA") intl = digits.length === 10 ? "1" + digits : digits;
    else if (listing.country === "SG" && digits.length === 8) intl = "65" + digits;
    else if (listing.country === "MY") intl = "60" + digits.replace(/^0/, "");
    else if (listing.country === "JP") intl = "81" + digits.replace(/^0/, "");
  }
  const telHref = digits ? `tel:+${intl}` : null;
  // WhatsApp only for numbers that look like mobiles (per market). US/CA can't
  // be told apart from landlines, so WhatsApp is hidden there.
  const isMobileNumber = (() => {
    if (!digits) return false;
    const f = digits[0];
    const noZero = digits.replace(/^0/, "");
    switch (listing.country) {
      case "HK": return digits.length === 8 && !["2", "3"].includes(f);
      case "TW": return noZero.startsWith("9");
      case "SG": return digits.length === 8 && ["8", "9"].includes(f);
      case "MY": return noZero.startsWith("1");
      case "JP": return ["7", "8", "9"].includes(noZero[0]);
      default: return false; // US / CA / unknown
    }
  })();
  const waHref = isMobileNumber ? `https://wa.me/${intl}` : null;

  // Logo pulled from the shop's website / social link favicon (Google s2),
  // else an initial-letter avatar.
  const domain = listing.website ? listing.website.replace(/^https?:\/\//, "").split("/")[0] : null;
  const isSocial = domain && /facebook|instagram|fb\.com|ig\./i.test(domain);
  const avatarC = shopAvatarColor(listing.name);

  const Section = ({ title, icon, children, pad = "14px 16px" }) => (
    <div style={{background:"#fff", borderRadius:20, boxShadow:"0 6px 20px rgba(0,0,0,0.06)", marginBottom:14, overflow:"hidden"}}>
      {title && (
        <div style={{display:"flex", alignItems:"center", gap:8, padding:"13px 16px 9px", borderBottom:"1px solid var(--c-border)"}}>
          <span style={{color:"var(--c-primary)", display:"inline-flex"}}>{icon}</span>
          <p style={{fontSize:13.5, fontWeight:900, color:"#4A4A68", margin:0, letterSpacing:0.2}}>{title}</p>
        </div>
      )}
      <div style={{padding:pad}}>{children}</div>
    </div>
  );
  const Row = ({ icon, label, children, href, last }) => {
    const inner = (
      <div style={{display:"flex", gap:12, alignItems:"flex-start", padding:"11px 0", borderBottom: last ? "none" : "1px solid var(--c-lighter)"}}>
        <div style={{flexShrink:0, color:"var(--c-primary)", marginTop:1}}>{icon}</div>
        <div style={{flex:1, minWidth:0}}>
          {label && <p style={{fontSize:11, fontWeight:800, color:"#AFAFC4", margin:"0 0 1px", textTransform:"uppercase", letterSpacing:0.4}}>{label}</p>}
          <div style={{fontSize:14, fontWeight:700, color:"#4A4A68", wordBreak:"break-word", lineHeight:1.45}}>{children}</div>
        </div>
        {href && <ChevronRight size={16} color="#C4C4D4" style={{flexShrink:0, marginTop:2}} />}
      </div>
    );
    return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" style={{textDecoration:"none", display:"block"}}>{inner}</a> : inner;
  };
  const Action = ({ href, bg, icon, label }) => (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
       style={{flex:1, minWidth:72, textDecoration:"none", background:bg, color:"#fff", borderRadius:16, padding:"12px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:5, fontWeight:800, fontSize:12, boxShadow:"0 6px 16px rgba(0,0,0,0.12)"}}>
      {icon}{label}
    </a>
  );

  const hoursList = Array.isArray(listing.hours?.weekdayDescriptions) ? listing.hours.weekdayDescriptions : [];
  const photos = Array.isArray(listing.photos) ? listing.photos : [];
  const reviews = Array.isArray(listing.reviews) ? listing.reviews : [];

  return (
    <div style={{...themeVars, minHeight:"100vh", background:dirTheme.bg, paddingBottom:44}}>
      {/* Hero block */}
      <div style={{position:"relative", color:"#fff", padding:"14px 16px 28px", overflow:"hidden", borderRadius:"0 0 26px 26px", boxShadow:"0 12px 30px rgba(0,0,0,0.18)"}}>
        <HeroPhotoBackdrop img={listingHeroPhoto} />
        <button onClick={onBack} style={{position:"relative", zIndex:1, background:"rgba(255,255,255,0.92)", color:"#2A2A35", border:"none", borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4, marginBottom:16}}>
          <ChevronLeft size={16} /> {isZh ? "目錄" : "Directory"}
        </button>
        <div style={{position:"relative", zIndex:1, display:"flex", gap:14, alignItems:"center"}}>
          <div style={{width:72, height:72, borderRadius:18, flexShrink:0, boxShadow:"0 8px 22px rgba(0,0,0,0.28)", background:`linear-gradient(135deg, ${avatarC[0]}, ${avatarC[1]})`, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", color:"#fff"}}>
            <span style={{fontSize:32, fontWeight:900}}>{(listing.name||"🐾").trim().charAt(0)}</span>
          </div>
          <div style={{flex:1, minWidth:0}}>
            <h1 style={{fontSize:22, fontWeight:900, margin:"0 0 6px", textShadow:"0 2px 8px rgba(0,0,0,0.2)", lineHeight:1.2}}>{listing.name}</h1>
            <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
              {regLabel && <span style={{fontSize:12, fontWeight:800, background:"rgba(255,255,255,0.22)", borderRadius:99, padding:"3px 10px"}}>📍 {regLabel}</span>}
              {listing.rating > 0 && <span style={{fontSize:12, fontWeight:800, background:"rgba(255,255,255,0.22)", borderRadius:99, padding:"3px 10px"}}>⭐ {listing.rating}{listing.ratingCount ? ` (${listing.ratingCount})` : ""}</span>}
              {listing.priceLevel > 0 && <span style={{fontSize:12, fontWeight:800, background:"rgba(255,255,255,0.22)", borderRadius:99, padding:"3px 10px"}}>{"$".repeat(listing.priceLevel)}</span>}
              {(listing.businessStatus === "CLOSED_TEMPORARILY" || listing.businessStatus === "CLOSED_PERMANENTLY") && <span style={{fontSize:12, fontWeight:800, background:"rgba(239,68,68,0.85)", borderRadius:99, padding:"3px 10px"}}>{isZh ? "已暫停營業" : "Closed"}</span>}
            </div>
          </div>
        </div>
      </div>

      <div style={{maxWidth:620, margin:"0 auto", padding:"16px 14px"}}>
        {/* Quick actions */}
        {(telHref || waHref || mapUrl || listing.website) && (
          <div style={{display:"flex", gap:9, marginBottom:14}}>
            {telHref && <Action href={telHref} bg="linear-gradient(135deg,#34D399,#059669)" icon={<Phone size={20} />} label={isZh ? "致電" : "Call"} />}
            {waHref && <Action href={waHref} bg="linear-gradient(135deg,#25D366,#128C7E)" icon={<MessageCircle size={20} />} label="WhatsApp" />}
            <Action href={mapUrl} bg="linear-gradient(135deg,#60A5FA,#2563EB)" icon={<MapPin size={20} />} label={isZh ? "路線" : "Directions"} />
            {listing.website && <Action href={listing.website} bg="linear-gradient(135deg,#A78BFA,#7C3AED)" icon={<Globe size={20} />} label={isZh ? "網站" : "Website"} />}
          </div>
        )}

        {/* Photo gallery */}
        {photos.length > 0 && (
          <div style={{display:"flex", gap:8, overflowX:"auto", marginBottom:14, paddingBottom:4, scrollSnapType:"x mandatory"}}>
            {photos.map((src, i) => (
              <img key={i} src={src} alt="" loading="lazy" style={{height:150, minWidth:200, borderRadius:16, objectFit:"cover", flexShrink:0, scrollSnapAlign:"start", boxShadow:"0 4px 14px rgba(0,0,0,0.08)"}} onError={(e)=>{e.currentTarget.style.display="none";}} />
            ))}
          </div>
        )}

        {/* About */}
        {listing.editorialSummary && (
          <Section title={isZh ? "簡介" : "About"} icon={<Info size={16} />}>
            <p style={{fontSize:14, fontWeight:600, color:"#4A4A68", margin:0, lineHeight:1.55}}>{listing.editorialSummary}</p>
          </Section>
        )}

        {/* Not-on-PetOS notice */}
        <div style={{background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:16, padding:"12px 14px", display:"flex", gap:10, alignItems:"flex-start", marginBottom:14}}>
          <Info size={18} color="#EA580C" style={{flexShrink:0, marginTop:1}} />
          <p style={{fontSize:13, fontWeight:700, color:"#9A3412", margin:0, lineHeight:1.5}}>
            {isZh ? "呢間店舖暫未加入 PettoBook，未能網上預約。如你係店主，可認領此店啟用網上預約。" : "Not on PettoBook yet, so online booking isn't available. If you're the owner, claim it to enable booking."}
          </p>
        </div>

        {/* Map block */}
        {mapEmbed && (
          <Section title={isZh ? "位置" : "Location"} icon={<MapPin size={16} />} pad="0">
            <iframe title="map" src={mapEmbed} width="100%" height="200" style={{border:0, display:"block"}} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            {listing.address && (
              <a href={mapUrl} target="_blank" rel="noreferrer" style={{display:"flex", alignItems:"center", gap:8, padding:"12px 16px", textDecoration:"none", borderTop:"1px solid var(--c-border)"}}>
                <MapPin size={16} color="var(--c-primary)" style={{flexShrink:0}} />
                <span style={{flex:1, fontSize:13.5, fontWeight:700, color:"#4A4A68", lineHeight:1.4}}>{listing.address}</span>
                <ChevronRight size={16} color="#C4C4D4" />
              </a>
            )}
          </Section>
        )}

        {/* Contact & info block */}
        <Section title={isZh ? "聯絡及資料" : "Contact & info"} icon={<Info size={16} />}>
          {listing.phone && <Row icon={<Phone size={18} />} label={isZh ? "電話" : "Phone"} href={telHref}>{listing.phone}</Row>}
          {listing.website && <Row icon={<Globe size={18} />} label={isSocial ? (isZh ? "社交媒體" : "Social") : (isZh ? "網站" : "Website")} href={listing.website}>{listing.website.replace(/^https?:\/\//, "")}</Row>}
          {!mapEmbed && listing.address && <Row icon={<MapPin size={18} />} label={isZh ? "地址" : "Address"} href={mapUrl}>{listing.address}</Row>}
          {!listing.phone && !listing.website && (mapEmbed || !listing.address) && (
            <p style={{fontSize:13, color:"#8A8A9E", fontWeight:700, margin:0}}>{isZh ? "暫無聯絡資料" : "No contact details yet"}</p>
          )}
        </Section>

        {/* Hours block */}
        {hoursList.length > 0 && (
          <Section title={isZh ? "營業時間" : "Opening hours"} icon={<Clock size={16} />}>
            {hoursList.map((d, i) => (
              <p key={i} style={{fontSize:13, fontWeight:700, color:"#4A4A68", margin:"5px 0", lineHeight:1.4}}>{d}</p>
            ))}
          </Section>
        )}

        {/* Services block */}
        {listing.servicesText && (
          <Section title={isZh ? "服務" : "Services"} icon={<Scissors size={16} />}>
            <p style={{fontSize:14, fontWeight:700, color:"#4A4A68", margin:0, lineHeight:1.5}}>{listing.servicesText}</p>
          </Section>
        )}

        {/* Rating — aggregate only (no republished review text). Reviews live
            on Google to avoid defamation/ToS exposure. */}
        {listing.rating > 0 && (
          <Section title={isZh ? "評分" : "Rating"} icon={<Star size={16} />}>
            <div style={{display:"flex", alignItems:"center", gap:12}}>
              <div style={{fontSize:34, fontWeight:900, color:"#2A2A35", lineHeight:1}}>{listing.rating}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:15, letterSpacing:1}}>{"★".repeat(Math.round(listing.rating))}<span style={{color:"#E5E7EB"}}>{"★".repeat(5 - Math.round(listing.rating))}</span></div>
                <p style={{fontSize:12, fontWeight:700, color:"#8A8A9E", margin:"2px 0 0"}}>{listing.ratingCount ? (isZh ? `${listing.ratingCount} 則 Google 評價` : `${listing.ratingCount} Google reviews`) : (isZh ? "Google 評分" : "Google rating")}</p>
              </div>
            </div>
            <a href={mapUrl} target="_blank" rel="noreferrer" style={{display:"inline-flex", alignItems:"center", gap:6, marginTop:12, fontSize:13.5, fontWeight:800, color:"var(--c-primary)", textDecoration:"none"}}>
              {isZh ? "在 Google 查看評價" : "See reviews on Google"} <ChevronRight size={15} />
            </a>
          </Section>
        )}

        {/* Claim CTA block */}
        <div style={{background:"linear-gradient(135deg,#fff,#FFF7F0)", border:"1.5px solid var(--c-border)", borderRadius:20, padding:"20px 16px", textAlign:"center", boxShadow:"0 8px 24px rgba(0,0,0,0.05)"}}>
          <div style={{fontSize:32, marginBottom:6}}>🏪</div>
          <p style={{fontSize:16, fontWeight:900, color:"#4A4A68", margin:"0 0 4px"}}>{isZh ? "你係呢間店嘅店主？" : "Are you the owner?"}</p>
          <p style={{fontSize:12.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 14px", lineHeight:1.5}}>{isZh ? "認領此店，免費加入 PettoBook，啟用網上預約同店舖專頁。" : "Claim this shop to join PettoBook and unlock online booking & a shop page."}</p>
          <Btn onClick={() => setClaimOpen(true)} style={{width:"100%"}}><BadgeCheck size={18} /> {isZh ? "認領此店" : "Claim this shop"}</Btn>
        </div>
      </div>

      <ClaimShopSheet open={claimOpen} onClose={() => setClaimOpen(false)} listing={listing} />
    </div>
  );
};

// "Claim this shop" form (bottom sheet) for a non-member listing. Submits to
// the public submit-shop-claim edge function which logs a shop_claims row and
// emails cs@pettobook.com. No login required.
const ClaimShopSheet = ({ open, onClose, listing }) => {
  const isZh = i18nGetLocale() === "zh-HK";
  const [form, setForm] = useState({ name:"", role:"owner", email:"", phone:"", shopName:"", message:"", consent:false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  // 商業登記證 — required. It carries the business name AND registered address,
  // and every directory listing has both, so approving is a two-field
  // comparison rather than a judgement call.
  const [brDoc, setBrDoc] = useState(null);   // { name, mime, dataBase64, size }

  // Prefill the shop name from the listing whenever the sheet opens.
  useEffect(() => {
    if (open) { setForm(f => ({ ...f, shopName: listing?.name || "" })); setDone(false); setErr(""); setBrDoc(null); }
  }, [open, listing]);

  if (!open) return null;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const BR_MIME = ["image/jpeg","image/png","image/webp","image/heic","application/pdf"];
  const BR_MAX = 5 * 1024 * 1024;
  const pickBrDoc = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";            // allow re-picking the same file after an error
    if (!f) return;
    if (!BR_MIME.includes((f.type || "").toLowerCase())) {
      setErr(isZh ? "只接受 JPG / PNG / WEBP / HEIC / PDF" : "Only JPG, PNG, WEBP, HEIC or PDF"); return;
    }
    if (f.size > BR_MAX) {
      setErr(isZh ? "檔案太大，上限 5MB" : "File too large — 5MB maximum"); return;
    }
    const r = new FileReader();
    r.onerror = () => setErr(isZh ? "讀取檔案失敗" : "Could not read that file");
    r.onload = () => {
      const res = String(r.result || "");
      setBrDoc({ name: f.name, mime: (f.type || "").toLowerCase(), size: f.size,
                 dataBase64: res.slice(res.indexOf(",") + 1) });
      setErr("");
    };
    r.readAsDataURL(f);
  };

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) { setErr(isZh ? "請填寫你的姓名" : "Please enter your name"); return; }
    if (!emailOk) { setErr(isZh ? "請填寫有效電郵" : "Please enter a valid email"); return; }
    if (!brDoc) { setErr(isZh ? "請上載商業登記證" : "Please upload your business registration"); return; }
    if (!form.consent) { setErr(isZh ? "請確認你係店主或獲授權" : "Please confirm you're the owner / authorised"); return; }
    setBusy(true);
    try {
      await submitShopClaim({
        listingId: listing?.id || null,
        listingSlug: listing?.slug || null,
        shopName: form.shopName.trim() || listing?.name || "",
        claimantName: form.name.trim(),
        claimantRole: form.role,
        claimantEmail: form.email.trim(),
        claimantPhone: form.phone.trim(),
        message: form.message.trim(),
        brDoc: { name: brDoc.name, mime: brDoc.mime, dataBase64: brDoc.dataBase64 },
      });
      setDone(true);
    } catch (e) {
      setErr(e?.message || (isZh ? "提交失敗，請稍後再試" : "Submission failed, please try again"));
    }
    setBusy(false);
  };

  const roleOpts = [
    { v:"owner",   zh:"店主",   en:"Owner" },
    { v:"manager", zh:"經理",   en:"Manager" },
    { v:"staff",   zh:"員工",   en:"Staff" },
    { v:"other",   zh:"其他",   en:"Other" },
  ];

  return (
    <div onClick={onClose} style={{position:"fixed", inset:0, zIndex:300, background:"rgba(31,41,55,0.55)", display:"flex", alignItems:"flex-end", justifyContent:"center"}}>
      <div onClick={e => e.stopPropagation()} style={{width:"100%", maxWidth:560, maxHeight:"90vh", overflowY:"auto", background:"#FFFDFD", borderRadius:"24px 24px 0 0", padding:"18px 18px 26px"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
          <p style={{fontSize:17, fontWeight:900, color:"#4A4A68", margin:0}}>{isZh ? "認領此店" : "Claim this shop"}</p>
          <button onClick={onClose} style={{background:"none", border:"none", fontSize:22, color:"#8A8A9E", cursor:"pointer", padding:4}}>×</button>
        </div>

        {done ? (
          <div style={{textAlign:"center", padding:"24px 10px 10px"}}>
            <div style={{fontSize:44, marginBottom:8}}>🎉</div>
            <p style={{fontSize:16, fontWeight:900, color:"#4A4A68", margin:"0 0 6px"}}>{isZh ? "已收到你的認領申請！" : "Claim submitted!"}</p>
            <p style={{fontSize:13, fontWeight:700, color:"#8A8A9E", margin:"0 0 18px", lineHeight:1.5}}>{isZh ? "我哋會盡快透過電郵聯絡你核實。" : "We'll be in touch by email to verify shortly."}</p>
            <Btn onClick={onClose} style={{width:"100%"}}>{isZh ? "完成" : "Done"}</Btn>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:12, paddingTop:8}}>
            <p style={{fontSize:13, fontWeight:700, color:"#8A8A9E", margin:0, lineHeight:1.5}}>{isZh ? "填寫以下資料，我哋會核實後幫你開通。" : "Fill in the details below and we'll verify & onboard you."}</p>
            <Input label={isZh ? "店舖名稱" : "Shop name"} value={form.shopName} onChange={set("shopName")} />
            <Input label={isZh ? "你的姓名 *" : "Your name *"} value={form.name} onChange={set("name")} />
            <Sel label={isZh ? "你的身份" : "Your role"} value={form.role} onChange={set("role")}>
              {roleOpts.map(o => <option key={o.v} value={o.v}>{isZh ? o.zh : o.en}</option>)}
            </Sel>
            <Input label={isZh ? "電郵 *" : "Email *"} type="email" value={form.email} onChange={set("email")} />
            <Input label={isZh ? "聯絡電話" : "Phone"} value={form.phone} onChange={set("phone")} />
            <div>
              <p style={{fontSize:13, color:"#8A8A9E", marginBottom:6, fontWeight:700}}>{isZh ? "備註 (選填)" : "Message (optional)"}</p>
              <textarea value={form.message} onChange={set("message")} rows={3} style={{width:"100%", border:"2px solid var(--c-light)", borderRadius:20, padding:"12px 16px", fontSize:15, fontWeight:600, outline:"none", boxSizing:"border-box", background:"#FFFDFD", color:"#4A4A68", resize:"vertical", fontFamily:"inherit"}} />
            </div>
            {/* 商業登記證 — the actual verification. Name + address on the
                certificate are compared against the directory listing. */}
            <div style={{border:"2px dashed " + (brDoc ? "var(--c-primary)" : "var(--c-light)"), borderRadius:20, padding:14, background: brDoc ? "var(--c-lighter)" : "#FFFDFD"}}>
              <p style={{fontSize:13, fontWeight:900, color:"#4A4A68", margin:"0 0 4px"}}>
                {isZh ? "商業登記證 *" : "Business registration *"}
              </p>
              <p style={{fontSize:11.5, fontWeight:700, color:"#8A8A9E", margin:"0 0 10px", lineHeight:1.5}}>
                {isZh
                  ? "我哋會核對證書上嘅商業名稱同地址，同目錄資料是否相符。文件只用作核實，批核後即刪除。"
                  : "We check the business name and address on the certificate against the directory listing. Used only for verification and deleted once decided."}
              </p>
              {brDoc ? (
                <div style={{display:"flex", alignItems:"center", gap:10}}>
                  <span style={{fontSize:20}}>{brDoc.mime === "application/pdf" ? "📄" : "🖼️"}</span>
                  <span style={{flex:1, minWidth:0, fontSize:12.5, fontWeight:800, color:"#4A4A68", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                    {brDoc.name} <span style={{fontWeight:700, color:"#8A8A9E"}}>· {Math.round(brDoc.size / 1024)} KB</span>
                  </span>
                  <button type="button" onClick={() => setBrDoc(null)}
                    style={{background:"none", border:"none", color:"#EF4444", fontSize:12, fontWeight:800, cursor:"pointer", padding:4}}>
                    {isZh ? "移除" : "Remove"}
                  </button>
                </div>
              ) : (
                <label style={{display:"block", cursor:"pointer"}}>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic"
                    onChange={pickBrDoc} style={{display:"none"}} />
                  <span style={{display:"block", textAlign:"center", background:"var(--c-lighter)", color:"var(--c-primary)",
                    border:"1.5px solid var(--c-primary)", borderRadius:99, padding:"10px 16px", fontSize:13.5, fontWeight:800}}>
                    {isZh ? "📎 選擇檔案（JPG / PNG / PDF，5MB 以內）" : "📎 Choose file (JPG / PNG / PDF, max 5MB)"}
                  </span>
                </label>
              )}
            </div>
            <label style={{display:"flex", gap:10, alignItems:"flex-start", cursor:"pointer"}}>
              <input type="checkbox" checked={form.consent} onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} style={{marginTop:3, width:18, height:18, flexShrink:0, accentColor:"var(--c-primary)"}} />
              <span style={{fontSize:12.5, fontWeight:700, color:"#6A6A85", lineHeight:1.5}}>{isZh ? "我確認我係呢間店嘅店主或獲授權代表。" : "I confirm I'm the owner or an authorised representative of this shop."}</span>
            </label>
            {err && <p style={{color:"#EF4444", fontSize:13, fontWeight:700, margin:0}}>{err}</p>}
            <Btn onClick={submit} disabled={busy} style={{width:"100%"}}>{busy ? (isZh ? "提交中…" : "Submitting…") : (isZh ? "提交認領申請" : "Submit claim")}</Btn>
          </div>
        )}
      </div>
    </div>
  );
};

// 店主詢價收件箱 — owner sees grooming requests in their region, submits
// a quote (price + slot + optional message). Each request expands into
// an inline quote-reply form to keep the friction minimal.
const OwnerInboxSheet = ({ open, onClose, tenantId, services = [] }) => {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("all"); // all | local | mobile | quoted
  // Per-request quote draft state. Keyed by requestId so we don't lose
  // input when the user collapses + re-expands.
  const [drafts, setDrafts] = useState({});

  const refresh = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const list = await fetchShopInbox(tenantId);
      setReqs(list);
    } catch (e) { toast(e?.message || "載入失敗", "error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) refresh(); }, [open, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDraft = (rid, patch) => setDrafts(d => ({ ...d, [rid]: { ...(d[rid] || {}), ...patch } }));

  const submit = async (req) => {
    const d = drafts[req.id] || {};
    const price = Number(d.price);
    if (!price || price <= 0) { toast(zhEn("輸入報價金額", "Enter a quote amount"), "warning"); return; }
    if (!d.slotDate) { toast(zhEn("揀日期", "Choose a date"), "warning"); return; }
    if (!d.slotTime) { toast(zhEn("輸入時間 (例 14:30)", "Enter a time (e.g. 14:30)"), "warning"); return; }
    setBusy(true);
    try {
      await submitQuote({
        requestId: req.id,
        tenantId,
        price,
        slotDate: d.slotDate,
        slotTime: d.slotTime,
        serviceId: d.serviceId || null,
        message: (d.message || "").trim(),
      });
      toast(zhEn("報價已發出 ✓", "Quote sent ✓"), "success");
      updateDraft(req.id, { sent: true });
      await refresh();
    } catch (e) {
      const msg = e?.message || "";
      if (/duplicate/i.test(msg)) toast(zhEn("你之前已經報過呢個詢價", "You've already quoted this request"), "warning");
      else toast(msg || zhEn("發出失敗", "Failed to send"), "error");
    } finally { setBusy(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={zhEn("📨 詢價收件箱", "📨 Quote requests inbox")} z={400}>
      <div style={{display:"flex",flexDirection:"column",gap:10,padding:"4px 2px 12px"}}>
        <div style={{background:"linear-gradient(135deg,#FAF5FF,#EDE9FE)",border:"2px solid #DDD6FE",borderRadius:14,padding:"10px 14px"}}>
          <p style={{fontSize:12,fontWeight:800,color:"#5B21B6",margin:0,lineHeight:1.5}}>{zhEn("客戶詢價會出現喺呢度（本區優先，上門詢價跨區都會見到）。報出價錢 + 時段，客戶接受即時建立預約。", "Customer requests appear here (local ones first; mobile-service requests are shown across areas). Send a price + time slot and the booking is created as soon as the customer accepts.")}</p>
        </div>
        {/* Filter tabs */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[["all",zhEn("全部", "All")],["local",zhEn("本區", "Local")],["mobile",zhEn("🚗 上門", "🚗 Mobile")],["quoted",zhEn("已報價", "Quoted")]].map(([k,l]) => {
            const cnt = k === "all" ? reqs.length : k === "local" ? reqs.filter(r => r.regionMatch).length : k === "mobile" ? reqs.filter(r => r.mobile).length : reqs.filter(r => r.alreadyQuoted).length;
            return (
              <button key={k} onClick={() => setTab(k)} style={{padding:"7px 13px",borderRadius:99,border:"2px solid "+(tab===k?"var(--c-primary)":"var(--c-border)"),background:tab===k?"var(--c-primary)":"#fff",color:tab===k?"#fff":"#6A6A85",fontWeight:800,fontSize:12.5,cursor:"pointer"}}>{l}{cnt>0?` (${cnt})`:""}</button>
            );
          })}
        </div>
        {loading && <p style={{textAlign:"center",color:"#AFAFC4",fontWeight:700,padding:"30px 0"}}>{zhEn("載入中…", "Loading…")}</p>}
        {!loading && reqs.length === 0 && (
          <div style={{textAlign:"center",padding:"30px 20px",color:"#AFAFC4",fontWeight:700}}>
            <div style={{fontSize:42,marginBottom:6}}>📭</div>
            <p style={{margin:0,fontSize:14,fontWeight:800,color:"#4A4A68"}}>{zhEn("暫無詢價", "No requests yet")}</p>
          </div>
        )}
        {reqs.filter(r => tab === "all" ? true : tab === "local" ? r.regionMatch : tab === "mobile" ? r.mobile : r.alreadyQuoted).map(r => {
          const isExpanded = expanded === r.id;
          const draft = drafts[r.id] || {};
          const svcLbl = ({wash:zhEn("沖涼", "Bath"),cut:zhEn("沖涼剪毛", "Bath & trim"),spa:"SPA",quick:zhEn("快速護理", "Quick care"),other:zhEn("其他", "Other")})[r.serviceKind] || r.serviceKind;
          return (
            <div key={r.id} style={{border:"2px solid var(--c-border)",borderRadius:14,background:"#fff",overflow:"hidden"}}>
              <div onClick={() => setExpanded(isExpanded ? null : r.id)} style={{padding:"12px 14px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{r.customerName} · {r.petName || (r.petType==="dog"?zhEn("狗狗", "Dog"):r.petType==="cat"?zhEn("貓貓", "Cat"):zhEn("毛孩", "Pet"))}</p>
                  {r.alreadyQuoted && <span style={{fontSize:10,fontWeight:900,padding:"3px 8px",borderRadius:99,background:"#D1FAE5",color:"#065F46"}}>{zhEn("已報價", "Quoted")}</span>}
                </div>
                <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:4}}>
                  {r.mobile && <span style={{fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,background:"#EDE9FE",color:"#7C3AED"}}>{zhEn("🚗 上門", "🚗 Mobile")}</span>}
                  {r.regionMatch && <span style={{fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,background:"#DBEAFE",color:"#1E40AF"}}>{zhEn("📍 本區", "📍 Local")}</span>}
                  {r.region && <span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"var(--c-lighter)",color:"#6A6A85"}}>{lookupRegion(r.region)?.zh || r.region}</span>}
                </div>
                <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{svcLbl} · {r.dateStart}{r.dateEnd !== r.dateStart ? " → " + r.dateEnd : ""} · {({any:zhEn("任何時段", "Any time"),morning:zhEn("上午", "Morning"),afternoon:zhEn("下午", "Afternoon"),evening:zhEn("夜晚", "Evening")})[r.timeWindow] || ""} · {r.petWeight ? r.petWeight + "kg" : "—"} · {r.petCoat === "long" ? zhEn("長毛", "Long coat") : r.petCoat === "short" ? zhEn("短毛", "Short coat") : ""}</p>
                {(r.budgetMin || r.budgetMax) && <p style={{fontSize:11,color:"#9A3412",margin:"4px 0 0",fontWeight:800}}>{zhEn("💰 預算", "💰 Budget")} {r.budgetMin ? "$" + r.budgetMin : ""}{r.budgetMin && r.budgetMax ? " — " : ""}{r.budgetMax ? "$" + r.budgetMax : ""}</p>}
              </div>
              {isExpanded && (
                <div style={{padding:"0 14px 14px",borderTop:"1px solid var(--c-border)"}}>
                  {r.description && <p style={{fontSize:12,color:"#4A4A68",margin:"10px 0 8px",padding:"8px 10px",background:"var(--c-lighter)",borderRadius:10,fontWeight:600}}>{zhEn("「", "\"")}{r.description}{zhEn("」", "\"")}</p>}
                  {r.photos && r.photos.length > 0 && (
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:10}}>
                      {r.photos.slice(0, 6).map((p, i) => (
                        <div key={p.id || i} style={{aspectRatio:"1",borderRadius:10,overflow:"hidden",background:"var(--c-lighter)"}}>
                          <img src={p.url} alt="" loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                        </div>
                      ))}
                    </div>
                  )}
                  {r.alreadyQuoted ? (
                    <p style={{fontSize:12,color:"#065F46",fontWeight:800,textAlign:"center",padding:"10px 0",margin:0}}>{zhEn("你已經對呢個詢價報過價。客戶接受咗會通知你。", "You've already quoted this request. You'll be notified if the customer accepts.")}</p>
                  ) : (
                    <>
                      <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"6px 0 8px"}}>{zhEn("你嘅報價", "Your quote")}</p>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                        <Input label={zhEn("價錢 $", "Price $")} type="number" value={draft.price || ""} onChange={e => updateDraft(r.id, { price: e.target.value })} />
                        <Sel label={zhEn("服務 (可選)", "Service (optional)")} value={draft.serviceId || ""} onChange={e => updateDraft(r.id, { serviceId: e.target.value || null })}>
                          <option value="">{zhEn("— 不指定 —", "— Not specified —")}</option>
                          {decorateServiceList(services.filter(s => s.kind !== "addon")).map(s => <option key={s.id} value={s.id}>{s.displayName}</option>)}
                        </Sel>
                        <Input label={zhEn("日期", "Date")} type="date" value={draft.slotDate || r.dateStart} onChange={e => updateDraft(r.id, { slotDate: e.target.value })} />
                        <Input label={zhEn("時間 (HH:MM)", "Time (HH:MM)")} placeholder="14:30" value={draft.slotTime || ""} onChange={e => updateDraft(r.id, { slotTime: e.target.value })} />
                      </div>
                      <textarea value={draft.message || ""} onChange={e => updateDraft(r.id, { message: e.target.value })} placeholder={zhEn("額外訊息 (可選) — 例：歡迎查詢詳情", "Extra message (optional) — e.g. feel free to ask for details")} style={{width:"100%",minHeight:50,padding:"8px 10px",borderRadius:10,border:"2px solid var(--c-border)",fontSize:12,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box",marginBottom:8}} />
                      <Btn onClick={() => submit(r)} disabled={busy} style={{width:"100%"}}>{busy ? zhEn("送出中…", "Sending…") : zhEn("📩 送出報價", "📩 Send quote")}</Btn>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
};

// 我的詢價 — customer's inbox of posted requests + incoming quotes.
// Mounted from a card on the customer home; each request expands into
// its quote list. "接受報價" calls accept_grooming_quote which atomically
// converts the quote into an appointment.
const MyRequestsSheet = ({ open, onClose, onAccepted }) => {
  const [reqs, setReqs] = useState([]);
  const [quotesByReq, setQuotesByReq] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busyQuoteId, setBusyQuoteId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await fetchMyGroomingRequests();
      setReqs(list);
      // Eagerly fetch quotes for OPEN requests so the customer sees counts.
      const next = {};
      await Promise.all(list.filter(r => r.status === "open" || r.status === "booked").map(async r => {
        try { next[r.id] = await fetchQuotesForRequest(r.id); } catch { next[r.id] = []; }
      }));
      setQuotesByReq(next);
    } catch (e) { toast(e?.message || zhEn("載入失敗", "Failed to load"), "error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) refresh(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async (q) => {
    if (!window.confirm(zhEn("接受呢個報價會即時建立預約。確認？", "Accepting this quote creates a booking right away. Confirm?"))) return;
    setBusyQuoteId(q.id);
    try {
      const aptId = await acceptGroomingQuote(q.id);
      toast(zhEn("✓ 已建立預約", "✓ Booking created"), "success");
      onAccepted?.(aptId);
      await refresh();
    } catch (e) { toast(e?.message || zhEn("接受失敗", "Failed to accept"), "error"); }
    finally { setBusyQuoteId(null); }
  };

  const cancelReq = async (rid) => {
    if (!window.confirm(zhEn("取消呢個詢價？店主就唔再收到佢。", "Cancel this request? Shops will no longer receive it."))) return;
    try { await cancelGroomingRequest(rid); toast(zhEn("已取消", "Cancelled")); await refresh(); }
    catch (e) { toast(e?.message || zhEn("取消失敗", "Failed to cancel"), "error"); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={zhEn("📨 我的詢價", "📨 My quote requests")} z={400}>
      <div style={{display:"flex",flexDirection:"column",gap:10,padding:"4px 2px 12px"}}>
        {loading && <p style={{textAlign:"center",color:"#AFAFC4",fontWeight:700,padding:"30px 0"}}>{zhEn("載入中…", "Loading…")}</p>}
        {!loading && reqs.length === 0 && (
          <div style={{textAlign:"center",padding:"30px 20px",color:"#AFAFC4",fontWeight:700}}>
            <div style={{fontSize:42,marginBottom:6}}>📭</div>
            <p style={{margin:"0 0 4px",fontSize:14,fontWeight:800,color:"#4A4A68"}}>{zhEn("未有任何詢價", "No requests yet")}</p>
            <p style={{fontSize:12,margin:0}}>{zhEn("喺『目錄』頁按「📢 出價搵店」發出第一個詢價", "On the Directory page, tap \"📢 Request quotes\" to send your first request")}</p>
          </div>
        )}
        {reqs.map(r => {
          const quotes = quotesByReq[r.id] || [];
          const isOpen = r.status === "open";
          const isBooked = r.status === "booked";
          const isExpanded = expanded === r.id;
          const statusBadge = isOpen ? {label:zhEn("等候中", "Waiting"), bg:"#FEF3C7", fg:"#92400E"}
            : isBooked ? {label:zhEn("✓ 已成交", "✓ Booked"), bg:"#D1FAE5", fg:"#065F46"}
            : r.status === "expired" ? {label:zhEn("已過期", "Expired"), bg:"#F3F4F6", fg:"#6B7280"}
            : {label:zhEn("已取消", "Cancelled"), bg:"#FEE2E2", fg:"#991B1B"};
          return (
            <div key={r.id} style={{border:"2px solid var(--c-border)",borderRadius:14,background:"#fff",overflow:"hidden"}}>
              <div onClick={() => setExpanded(isExpanded ? null : r.id)} style={{padding:"12px 14px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{r.petName || (r.petType==="dog"?zhEn("狗狗", "Dog"):r.petType==="cat"?zhEn("貓貓", "Cat"):zhEn("毛孩", "Pet"))} · {({wash:zhEn("沖涼", "Bath"),cut:zhEn("沖涼剪毛", "Bath & trim"),spa:"SPA",quick:zhEn("快速護理", "Quick care"),other:zhEn("其他", "Other")})[r.serviceKind] || r.serviceKind}</p>
                  <span style={{fontSize:10,fontWeight:900,padding:"3px 8px",borderRadius:99,background:statusBadge.bg,color:statusBadge.fg,flexShrink:0}}>{statusBadge.label}</span>
                </div>
                <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{r.mobile ? zhEn("🚗 上門 · ", "🚗 Mobile · ") : ""}{r.dateStart}{r.dateEnd !== r.dateStart ? " → " + r.dateEnd : ""} · {({any:zhEn("任何時段", "Any time"),morning:zhEn("上午", "Morning"),afternoon:zhEn("下午", "Afternoon"),evening:zhEn("夜晚", "Evening")})[r.timeWindow] || ""} · 💼 {quotes.length} {zhEn("個報價", "quotes")}</p>
              </div>
              {isExpanded && (
                <div style={{padding:"0 14px 12px",borderTop:"1px solid var(--c-border)"}}>
                  {r.description && <p style={{fontSize:12,color:"#4A4A68",margin:"10px 0 8px",padding:"8px 10px",background:"var(--c-lighter)",borderRadius:10,fontWeight:600}}>{zhEn("「", "\"")}{r.description}{zhEn("」", "\"")}</p>}
                  {quotes.length === 0 && (
                    <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,padding:"14px 0",textAlign:"center",margin:0}}>{isOpen ? zhEn("等候店主回覆中…", "Waiting for shops to reply…") : zhEn("未有報價", "No quotes yet")}</p>
                  )}
                  {quotes.map(q => {
                    const isAccepted = q.status === "accepted";
                    const isRejected = q.status === "rejected";
                    return (
                      <div key={q.id} style={{padding:"10px 12px",border:"1.5px solid "+(isAccepted?"#10B981":"var(--c-border)"),borderRadius:10,marginBottom:8,background:isAccepted?"#F0FDF4":isRejected?"#F9FAFB":"#fff",opacity:isRejected?0.55:1}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6}}>
                          <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0}}>💰 ${q.price} · 📅 {q.slotDate} {q.slotTime}</p>
                          {isAccepted && <span style={{fontSize:10,fontWeight:900,color:"#fff",background:"#10B981",borderRadius:99,padding:"2px 8px"}}>{zhEn("✓ 已接受", "✓ Accepted")}</span>}
                          {isRejected && <span style={{fontSize:10,fontWeight:900,color:"#fff",background:"#9CA3AF",borderRadius:99,padding:"2px 8px"}}>{zhEn("未選", "Not chosen")}</span>}
                        </div>
                        {q.message && <p style={{fontSize:11,color:"#4A4A68",margin:"0 0 8px",fontWeight:600,fontStyle:"italic"}}>{zhEn("「", "\"")}{q.message}{zhEn("」", "\"")}</p>}
                        {isOpen && q.status === "open" && (
                          <Btn size="sm" onClick={() => accept(q)} disabled={busyQuoteId === q.id} style={{width:"100%",background:"#10B981"}}>{busyQuoteId === q.id ? zhEn("處理中…", "Processing…") : zhEn("✓ 接受報價 → 建立預約", "✓ Accept quote → create booking")}</Btn>
                        )}
                      </div>
                    );
                  })}
                  {isOpen && (
                    <Btn variant="ghost" size="sm" onClick={() => cancelReq(r.id)} style={{width:"100%",color:"#EF4444"}}>{zhEn("取消呢個詢價", "Cancel this request")}</Btn>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
};

// 詢價 / Request For Quote — customer posts a grooming demand and shops
// in their region submit price + slot quotes. Form lives in a
// BottomSheet so it can be triggered from anywhere (currently the
// /directory hero). Caller passes the logged-in profile + onClose +
// onPosted. Anon visitors are redirected before this is shown.
const PostRequestSheet = ({ open, onClose, onPosted, profile, pets = [] }) => {
  const [serviceKind, setServiceKind] = useState("wash");
  const [petPick, setPetPick] = useState(pets[0]?.id || "manual");
  const [manualPet, setManualPet] = useState({ name:"", type:"dog", weight:"", coat:"short" });
  const [dateStart, setDateStart] = useState(dateNow());
  const [dateEnd, setDateEnd] = useState(dateNow());
  const [timeWindow, setTimeWindow] = useState("any");
  const [region, setRegion] = useState("");
  const [mobile, setMobile] = useState(false); // 上門服務 (groomer comes to me)
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  // Quick date pills — most customers want "this week / weekend".
  const quickDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const setDateRange = (start, end) => { setDateStart(start); setDateEnd(end); };

  const submit = async () => {
    if (!profile?.id) { toast(zhEn("請先登入", "Please log in first"), "error"); return; }
    if (!serviceKind) { toast(zhEn("揀服務類型", "Choose a service type"), "warning"); return; }
    if (dateEnd < dateStart) { toast(zhEn("結束日期不可早於開始日期", "End date can't be before start date"), "warning"); return; }
    let pet = null;
    if (petPick === "manual") {
      if (!manualPet.type) { toast(zhEn("揀毛孩類型", "Choose a pet type"), "warning"); return; }
      pet = { name: manualPet.name || "", type: manualPet.type, weight: Number(manualPet.weight) || null, coat: manualPet.coat };
    } else {
      const p = pets.find(x => x.id === petPick);
      if (!p) { toast(zhEn("揀毛孩", "Choose a pet"), "warning"); return; }
      pet = { id: p.id, name: p.name || "", type: p.type, weight: p.weight, coat: p.coatLength };
    }
    setBusy(true);
    try {
      const created = await postGroomingRequest({
        customerId: profile.id,
        petId: pet.id || null,
        petType: pet.type,
        petWeight: pet.weight,
        petCoat: pet.coat,
        petName: pet.name,
        serviceKind,
        description: description.trim(),
        dateStart, dateEnd, timeWindow,
        region: region || null,
        mobile,
        budgetMin: budgetMin ? Number(budgetMin) : null,
        budgetMax: budgetMax ? Number(budgetMax) : null,
        photos,
      });
      toast(zhEn("詢價已發出 — 24 小時內等店主報價 🐾", "Request sent — shops will quote within 24 hours 🐾"), "success");
      onPosted?.(created);
      onClose?.();
    } catch (e) {
      toast(e?.message || zhEn("發出失敗", "Failed to send"), "error");
    } finally { setBusy(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={zhEn("📢 出價搵店 — 等店主報價", "📢 Request quotes — let shops bid")} z={400}>
      <div style={{display:"flex", flexDirection:"column", gap:14, padding:"4px 2px 12px"}}>
        <div style={{background:"linear-gradient(135deg,#FAF5FF,#EDE9FE)",border:"2px solid #DDD6FE",borderRadius:14,padding:"10px 14px"}}>
          <p style={{fontSize:12,fontWeight:800,color:"#5B21B6",margin:0,lineHeight:1.5}}>{zhEn("填寫一次，附近店家 24 小時內報價。揀最啱嘅一間，自動轉成預約。", "Fill it in once and nearby shops quote within 24 hours. Pick the best one and it turns into a booking automatically.")}</p>
        </div>

        {/* Pet picker */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("毛孩 *", "Pet *")}</p>
          {pets.length > 0 && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {pets.map(p => (
                <button key={p.id} onClick={() => setPetPick(p.id)} style={{padding:"8px 12px",borderRadius:99,border:"2px solid "+(petPick===p.id?"var(--c-primary)":"var(--c-border)"),background:petPick===p.id?"var(--c-primary)":"#fff",color:petPick===p.id?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{p.name || p.id.slice(0,4)}</button>
              ))}
              <button onClick={() => setPetPick("manual")} style={{padding:"8px 12px",borderRadius:99,border:"2px solid "+(petPick==="manual"?"var(--c-primary)":"var(--c-border)"),background:petPick==="manual"?"var(--c-primary)":"#fff",color:petPick==="manual"?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{zhEn("+ 新毛孩", "+ New pet")}</button>
            </div>
          )}
          {petPick === "manual" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,padding:12,background:"var(--c-lighter)",borderRadius:12}}>
              <Input label={zhEn("名稱 (可選)", "Name (optional)")} value={manualPet.name} onChange={e => setManualPet(m => ({...m, name:e.target.value}))} />
              <Sel label={zhEn("類型", "Type")} value={manualPet.type} onChange={e => setManualPet(m => ({...m, type:e.target.value}))}>
                <option value="dog">{zhEn("狗狗", "Dog")}</option>
                <option value="cat">{zhEn("貓貓", "Cat")}</option>
                <option value="small">{zhEn("細小動物", "Small animal")}</option>
              </Sel>
              <Input label={zhEn("體重 (kg)", "Weight (kg)")} type="number" value={manualPet.weight} onChange={e => setManualPet(m => ({...m, weight:e.target.value}))} />
              {petWeightWarn(manualPet.weight, manualPet.type) && <p style={{gridColumn:"1 / -1", fontSize:12, color:"#B45309", margin:0, fontWeight:700}}>{petWeightWarn(manualPet.weight, manualPet.type)}</p>}
            </div>
          )}
        </div>

        {/* Service kind */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("服務 *", "Service *")}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[["wash",zhEn("沖涼", "Bath")],["cut",zhEn("沖涼剪毛", "Bath & trim")],["spa","SPA"],["quick",zhEn("快速護理", "Quick care")],["other",zhEn("其他", "Other")]].map(([k,l]) => (
              <button key={k} onClick={() => setServiceKind(k)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(serviceKind===k?"var(--c-primary)":"var(--c-border)"),background:serviceKind===k?"var(--c-primary)":"#fff",color:serviceKind===k?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
        </div>

        {/* Date range */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("期望日期 *", "Preferred date *")}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
            <button onClick={() => setDateRange(quickDate(0), quickDate(0))} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-border)",background:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{zhEn("今日", "Today")}</button>
            <button onClick={() => setDateRange(quickDate(1), quickDate(1))} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-border)",background:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{zhEn("聽日", "Tomorrow")}</button>
            <button onClick={() => setDateRange(quickDate(0), quickDate(7))} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-border)",background:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{zhEn("未來 7 日", "Next 7 days")}</button>
            <button onClick={() => {
              const today = new Date(); const day = today.getDay();
              const sat = new Date(today); sat.setDate(today.getDate() + (6 - day + 7) % 7);
              const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
              setDateRange(sat.toISOString().slice(0,10), sun.toISOString().slice(0,10));
            }} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-border)",background:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{zhEn("本週末", "This weekend")}</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <Input label={zhEn("由", "From")} type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} />
            <Input label={zhEn("至", "To")} type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
          </div>
        </div>

        {/* Time window */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("時段 (可選)", "Time of day (optional)")}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[["any",zhEn("唔指定", "Any")],["morning",zhEn("上午", "Morning")],["afternoon",zhEn("下午", "Afternoon")],["evening",zhEn("夜晚", "Evening")]].map(([k,l]) => (
              <button key={k} onClick={() => setTimeWindow(k)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(timeWindow===k?"var(--c-primary)":"var(--c-border)"),background:timeWindow===k?"var(--c-primary)":"#fff",color:timeWindow===k?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
        </div>

        {/* 上門服務 toggle */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("服務方式", "Service type")}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <button onClick={() => setMobile(false)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(!mobile?"var(--c-primary)":"var(--c-border)"),background:!mobile?"var(--c-primary)":"#fff",color:!mobile?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{zhEn("🏠 去店舖", "🏠 Go to the shop")}</button>
            <button onClick={() => setMobile(true)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(mobile?"var(--c-primary)":"var(--c-border)"),background:mobile?"var(--c-primary)":"#fff",color:mobile?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{zhEn("🚗 要上門", "🚗 Mobile service")}</button>
          </div>
          {mobile && <p style={{fontSize:11,color:"#7C3AED",margin:"6px 0 0",fontWeight:700,lineHeight:1.4}}>{zhEn("上門詢價會通知埋跨區嘅上門美容師。揀返你嘅地區，方便佢哋計車程。", "Mobile requests also notify mobile groomers in other areas. Pick your area so they can estimate travel time.")}</p>}
        </div>

        {/* Region */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{mobile ? zhEn("你嘅地區 (方便上門師傅計車程)", "Your area (so mobile groomers can estimate travel)") : zhEn("地區 (可選 — 只通知該區店舖)", "Area (optional — only shops in that area are notified)")}</p>
          <Sel value={region} onChange={e => setRegion(e.target.value)}>
            <option value="">{zhEn("所有地區嘅店舖都會見到", "Shops in every area will see it")}</option>
            {HK_REGION_GROUPS.map(g => (
              <optgroup key={g.id} label={g.zh}>
                {HK_REGIONS.filter(r => r.group === g.id).map(r => <option key={r.id} value={r.id}>{r.zh}</option>)}
              </optgroup>
            ))}
          </Sel>
        </div>

        {/* Budget */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Input label={zhEn("預算下限 $ (可選)", "Minimum budget $ (optional)")} type="number" value={budgetMin} onChange={e => setBudgetMin(e.target.value)} />
          <Input label={zhEn("預算上限 $ (可選)", "Maximum budget $ (optional)")} type="number" value={budgetMax} onChange={e => setBudgetMax(e.target.value)} />
        </div>

        {/* Notes + photos */}
        <div>
          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("特別要求 (可選)", "Special requests (optional)")}</p>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={zhEn("例：毛打結嚴重 / 第一次美容 / 怕剪甲", "e.g. badly matted / first groom / afraid of nail trims")} style={{width:"100%",minHeight:70,padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-border)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
        </div>

        <PhotoUpload label={zhEn("📷 上載照片 (可選，最多 6 張)", "📷 Upload photos (optional, up to 6)")} photos={photos} onAdd={p => setPhotos(prev => [...prev, p])} onRemove={(id) => setPhotos(prev => prev.filter(x => (x.id || x.url) !== id))} maxPhotos={6} />

        <Btn onClick={submit} disabled={busy} style={{width:"100%",padding:"14px",marginTop:6}}>{busy ? zhEn("發出中…", "Sending…") : zhEn("📨 發出詢價 (24 小時內等店主回覆)", "📨 Send request (shops reply within 24 hours)")}</Btn>
      </div>
    </BottomSheet>
  );
};

// Owner-side tap-to-edit overlay. When PublicShopHome is rendered in
// editMode, wrap each editable section with this to surface a dashed
// outline + ✏️ chip and a single tap-handler that calls onEditSection
// with the relevant settings-subtab id (or special "tab:..." target).
const EditTarget = ({ active, label, target, onEdit, children, style }) => {
  if (!active) return <div style={style}>{children}</div>;
  return (
    <div
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(target); }}
      style={{
        ...style,
        position: "relative",
        cursor: "pointer",
        outline: "2px dashed rgba(255,77,109,0.55)",
        outlineOffset: 2,
        borderRadius: style?.borderRadius || 12,
      }}
      title={zhEn("編輯：", "Edit: ") + label}
    >
      <div style={{
        position: "absolute",
        top: 6,
        right: 6,
        zIndex: 5,
        background: "#FF4D6D",
        color: "#fff",
        fontSize: 11,
        fontWeight: 900,
        padding: "3px 8px",
        borderRadius: 99,
        boxShadow: "0 4px 12px rgba(255,77,109,0.4)",
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        gap: 4,
        letterSpacing: 0.3,
      }}>
        ✏️ {label}
      </div>
      {children}
    </div>
  );
};

const PublicShopHome = ({ tenant, staffUsers = [], activeTab = "overview", onTab, onBook, editMode = false, onEditSection = null }) => {
  const E = ({ label, target, children, style }) => (
    <EditTarget active={editMode} label={label} target={target} onEdit={onEditSection} style={style}>{children}</EditTarget>
  );
  const themeVars = THEMES[tenant?.theme] || THEMES.peach;
  const gallery = tenant?.shopGallery || [];
  // Pet-type icon prefix + stable sort (shared helper) so cat/dog group nicely.
  const decorateServices = decorateServiceList;
  // Online booking can be turned off by the shop (directory "listing only"
  // mode) — then we hide the booking CTAs and offer contact instead.
  // It is ALSO a paid feature: free-plan shops never show booking CTAs.
  const bookingOn = shopHasOnlineBooking(tenant) && tenant?.directoryBooking !== false;
  const contactHref = tenant?.phone ? ("tel:" + tenant.phone) : null;
  // WhatsApp contact (used when online booking is disabled). Builds an
  // international number from the shop phone (HK 8-digit → +852).
  const _waDigits = (tenant?.phone || "").replace(/\D/g, "");
  const _waIntl = _waDigits ? ((tenant?.country === "HK" || !tenant?.country) && _waDigits.length === 8 ? "852" + _waDigits : (tenant?.country === "TW" ? "886" + _waDigits.replace(/^0/, "") : _waDigits)) : "";
  const waHref = _waDigits ? ("https://wa.me/" + _waIntl) : null;
  const mainServices = decorateServices((tenant?.services || []).filter(s => s.kind !== "spa" && s.enabled !== false && s.customerVisible !== false));
  const spaServices = decorateServices((tenant?.services || []).filter(s => s.kind === "spa" && s.enabled !== false && s.customerVisible !== false));
  const tabs = [
    { id: "overview", label: zhEn("概覽", "Overview") },
    { id: "gallery",  label: zhEn("作品集 (", "Gallery (") + gallery.length + ")" },
    { id: "info",     label: zhEn("店鋪資料", "Shop details") },
    { id: "policies", label: zhEn("條款", "Policies") },
  ];
  // Update document title for tab labels + bookmarks. Crawlers that DO
  // execute JS get the right title even without edge-side SSR (full social
  // preview support comes later via a Vercel middleware).
  useEffect(() => {
    if (typeof document === "undefined" || !tenant?.name) return;
    document.title = tenant.name + zhEn(" · PettoBook 寵物美容", " · PettoBook pet grooming");
    const setMeta = (name, content) => {
      let el = document.querySelector('meta[name="' + name + '"]');
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setOg = (prop, content) => {
      let el = document.querySelector('meta[property="' + prop + '"]');
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = tenant.tagline || (tenant.about || "").slice(0, 160) || zhEn("預約專業寵物美容服務", "Book professional pet grooming");
    setMeta("description", desc);
    setOg("og:title", tenant.name + zhEn(" · 寵物美容預約", " · Pet grooming booking"));
    setOg("og:description", desc);
    setOg("og:type", "website");
    if (tenant.bannerUrl || tenant.logoUrl) setOg("og:image", tenant.bannerUrl || tenant.logoUrl);
    setOg("og:url", window.location.href);
    setMeta("twitter:card", "summary_large_image");
  }, [tenant?.id, tenant?.name, tenant?.tagline, tenant?.about, tenant?.bannerUrl, tenant?.logoUrl]);

  // iOS "install this shop's app" hint — 旗艦版 shops only. iOS Safari has no
  // automatic install prompt, so we coach the Share → Add to Home Screen flow.
  // Shown once per shop (dismissal remembered) and never inside a standalone PWA.
  // PWA install prompt DISABLED. Installed PWAs go zombie after long idle
  // (Safari suspends the WebView; the app looks logged-in but writes silently
  // fail because the token expired mid-flight). Until we ship a native
  // customer app or fix silent-write-loss, don't push customers into a form
  // factor with worse reliability than the browser tab.
  const [iosHintOpen] = useState(false);
  const dismissIosHint = () => {};

  return (
    <div style={{...themeVars, minHeight:"100vh", background:"linear-gradient(180deg, var(--c-lighter) 0%, #FFF7F0 50%, var(--c-light) 100%)", position:"relative", overflow:"hidden"}}>
      {iosHintOpen && (
        <div style={{position:"relative", zIndex:5, margin:"10px 12px 0", background:"var(--c-primary)", color:"#fff", borderRadius:16, padding:"12px 14px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 8px 20px var(--c-primary-40)"}}>
          {(tenant?.appIcon || tenant?.logoUrl) ? <img src={tenant.appIcon || tenant.logoUrl} alt="" style={{width:42, height:42, borderRadius:11, objectFit:"cover", flexShrink:0, background:"#fff"}} /> : <span style={{fontSize:26}}>📱</span>}
          <div style={{flex:1, minWidth:0}}>
            <p style={{fontSize:13.5, fontWeight:900, margin:"0 0 2px"}}>{zhEn("安裝「", "Install \"")}{tenant?.name || zhEn("本店", "This shop")}{zhEn("」App", "\"App")}</p>
            <p style={{fontSize:11.5, fontWeight:700, margin:0, opacity:0.95, lineHeight:1.4}}>{zhEn("按下方", "Tap ")} <strong>{zhEn("分享", "Share")}</strong> {zhEn("⬆️ →「", "⬆️ →\"")}<strong>{zhEn("加入主畫面", "Add to Home Screen")}</strong>{zhEn("」，即可一撳預約。", "\" to book in one tap.")}</p>
          </div>
          <button onClick={dismissIosHint} aria-label={zhEn("關閉", "Close")} style={{flexShrink:0, background:"rgba(255,255,255,0.25)", border:"none", color:"#fff", borderRadius:99, width:28, height:28, fontSize:16, fontWeight:900, cursor:"pointer"}}>✕</button>
        </div>
      )}
      {/* Decorative paw confetti — pure CSS, fixed pos, no images, doesn't block clicks. */}
      <div aria-hidden style={{position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden", zIndex:0}}>
        {[
          {t:"🐾", left:"6%",  top:"8%",  size:42, op:0.10, rot:-18},
          {t:"🐾", left:"88%", top:"4%",  size:54, op:0.12, rot:24},
          {t:"🦴", left:"82%", top:"30%", size:38, op:0.10, rot:-12},
          {t:"🐶", left:"4%",  top:"40%", size:46, op:0.08, rot:8},
          {t:"🐱", left:"92%", top:"58%", size:44, op:0.09, rot:-8},
          {t:"🐾", left:"10%", top:"72%", size:50, op:0.11, rot:30},
          {t:"✨", left:"50%", top:"90%", size:34, op:0.18, rot:0},
        ].map((p,i)=>(
          <span key={i} style={{position:"absolute", left:p.left, top:p.top, fontSize:p.size, opacity:p.op, transform:"rotate("+p.rot+"deg)"}}>{p.t}</span>
        ))}
      </div>
      {/* Back-to-directory chip — only when visitor came from /directory. */}
      {!editMode && (() => {
        const fromDir = (() => {
          try {
            if (typeof window === "undefined") return false;
            if (window.history?.state?.from === "directory") return true;
            if (document?.referrer && /\/directory\/?$/.test(new URL(document.referrer).pathname)) return true;
          } catch { /* ignore */ }
          return false;
        })();
        if (!fromDir) return null;
        return (
          <button
            onClick={() => {
              try { sessionStorage.removeItem("petos:from-directory"); } catch {}
              window.location.href = "/directory";
            }}
            style={{position:"absolute", top:14, left:14, zIndex:10, background:"rgba(255,255,255,0.22)", backdropFilter:"blur(10px)", color:"#fff", border:"1px solid rgba(255,255,255,0.35)", borderRadius:99, padding:"6px 14px", fontSize:13, fontWeight:800, cursor:"pointer", textShadow:"0 1px 4px rgba(0,0,0,0.25)"}}
          >
            {zhEn("← 返回目錄", "← Back to directory")}
          </button>
        );
      })()}
      {/* Hero — banner image with overlaid logo + name + tagline */}
      <E label={zhEn("封面圖", "Cover image")} target="shop-brand">
      <div style={{position:"relative", minHeight:260, background:tenant.bannerUrl ? "#000" : "linear-gradient(135deg, var(--c-grad1), var(--c-grad2))", overflow:"hidden", zIndex:1}}>
        {tenant.bannerUrl && <img src={tenant.bannerUrl} alt="" style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:0.6}} />}
        {/* Soft scrim so text reads on any banner */}
        <div style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 100%)"}}/>
        {/* Floating paws on the hero itself */}
        <span aria-hidden style={{position:"absolute", left:"-8px", top:"60%", fontSize:90, opacity:0.18, transform:"rotate(-20deg)", color:"#fff"}}>🐾</span>
        <span aria-hidden style={{position:"absolute", right:"-6px", top:"-6px", fontSize:80, opacity:0.18, transform:"rotate(18deg)", color:"#fff"}}>🐾</span>
        <div style={{position:"relative", padding:"16px 20px 26px", maxWidth:680, margin:"0 auto", color:"#fff", textShadow:"0 2px 14px rgba(0,0,0,0.45)"}}>
          {!editMode && (
            <button onClick={() => { window.location.href = "/directory"; }} style={{background:"rgba(255,255,255,0.92)", color:"var(--c-primary)", border:"none", borderRadius:99, padding:"7px 14px", fontSize:13, fontWeight:800, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4, marginBottom:16, textShadow:"none"}}>
              <ChevronLeft size={16} /> {zhEn("目錄", "Directory")}
            </button>
          )}
          {tenant.logoUrl && <div style={{display:"block"}}><img src={tenant.logoUrl} alt={tenant.name} style={{width:78, height:78, borderRadius:22, objectFit:"cover", marginBottom:14, border:"3px solid #fff", boxShadow:"0 8px 24px rgba(0,0,0,0.25)"}} /></div>}
          <h1 style={{fontSize:32, fontWeight:900, margin:"0 0 6px", letterSpacing:"-0.5px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
            {tenant.name}
            <span title={zhEn("PettoBook 認證店舖", "PettoBook verified shop")} style={{display:"inline-flex", alignItems:"center", gap:4, background:"rgba(255,255,255,0.92)", color:"#047857", borderRadius:99, padding:"3px 10px", fontSize:13, fontWeight:900, textShadow:"none"}}><BadgeCheck size={15} strokeWidth={2.6} /> {zhEn("認證", "Verified")}</span>
          </h1>
          {tenant.tagline && <p style={{fontSize:15, fontWeight:700, margin:"0 0 16px", opacity:0.95}}>{tenant.tagline}</p>}
          {(editMode || bookingOn) ? (
            <button onClick={editMode ? (e) => { e.stopPropagation(); onEditSection("shop-basic"); } : onBook} style={{padding:"13px 26px", borderRadius:99, background:"#fff", color:"var(--c-primary)", border:"none", fontWeight:900, fontSize:15, cursor:"pointer", boxShadow:"0 10px 28px rgba(0,0,0,0.22)", display:"inline-flex", alignItems:"center", gap:8}}>
              <span>🐾</span> {zhEn("立即預約 →", "Book now →")}
            </button>
          ) : contactHref ? (
            <a href={contactHref} style={{padding:"13px 26px", borderRadius:99, background:"#fff", color:"var(--c-primary)", border:"none", fontWeight:900, fontSize:15, cursor:"pointer", boxShadow:"0 10px 28px rgba(0,0,0,0.22)", display:"inline-flex", alignItems:"center", gap:8, textDecoration:"none"}}>
              <span>📞</span> {zhEn("致電查詢", "Call us")}
            </a>
          ) : null}
        </div>
      </div>
      </E>

      {/* Tab bar */}
      <div style={{maxWidth:680, margin:"0 auto", padding:"0 12px", background:"#FFFDFD", borderBottom:"1px solid var(--c-border)", display:"flex", gap:4, overflowX:"auto", position:"relative", zIndex:1}}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => onTab(t.id)} style={{padding:"14px 16px", background:"transparent", border:"none", borderBottom:"3px solid " + (activeTab === t.id ? "var(--c-primary)" : "transparent"), color:activeTab === t.id ? "var(--c-primary)" : "#8A8A9E", fontWeight:800, fontSize:14, cursor:"pointer", whiteSpace:"nowrap", marginBottom:-1}}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{maxWidth:680, margin:"0 auto", padding:"16px 16px 100px", position:"relative", zIndex:1}}>
        {/* ── OVERVIEW ─────────────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <E label={zhEn("關於本店", "About the shop")} target="shop-basic">
            {(tenant.about || editMode) && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 10px", color:"#4A4A68"}}>{zhEn("關於本店", "About the shop")}</h3>
                <p style={{fontSize:14, color:"#4A4A68", margin:0, fontWeight:600, lineHeight:1.7, whiteSpace:"pre-line"}}>{tenant.about || <span style={{color:"#AFAFC4",fontStyle:"italic"}}>{zhEn("（按此新增店舖介紹）", "(tap to add a shop introduction)")}</span>}</p>
              </Card>
            )}
            </E>
            <E label={zhEn("服務 / 價錢", "Services / prices")} target="tab:services">
            {mainServices.length > 0 && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 12px", color:"#4A4A68"}}>{zhEn("主要服務", "Main services")}</h3>
                <div style={{display:"flex", flexDirection:"column", gap:8}}>
                  {mainServices.map(s => (
                    <div key={s.id} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px dashed var(--c-border)"}}>
                      <span style={{fontSize:14, fontWeight:800, color:"#4A4A68"}}>{s.displayName || s.name}</span>
                      {s.basePrice > 0 && <span style={{fontSize:13, color:"var(--c-primary)", fontWeight:800}}>{zhEn("由 $", "From $")}{s.basePrice} {zhEn("起", "+")}</span>}
                    </div>
                  ))}
                </div>
                {(editMode || bookingOn) ? (
                  <button onClick={editMode ? (e) => { e.stopPropagation(); onEditSection("tab:services"); } : onBook} style={{marginTop:14, padding:"12px", width:"100%", background:"var(--c-primary)", color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:14, cursor:"pointer"}}>
                    {zhEn("立即預約 →", "Book now →")}
                  </button>
                ) : contactHref ? (
                  <a href={contactHref} style={{marginTop:14, padding:"12px", width:"100%", background:"var(--c-primary)", color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:14, cursor:"pointer", display:"block", textAlign:"center", textDecoration:"none", boxSizing:"border-box"}}>
                    {zhEn("📞 致電查詢", "📞 Call us")}
                  </a>
                ) : null}
              </Card>
            )}
            </E>
            <E label={zhEn("作品集 / 相片", "Gallery / photos")} target="shop-brand#gallery">
            {(gallery.length > 0 || editMode) && (
              <Card style={{padding:20}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
                  <h3 style={{fontWeight:900, fontSize:16, margin:0, color:"#4A4A68"}}>{zhEn("作品集", "Portfolio")}</h3>
                  {!editMode && <button onClick={() => onTab("gallery")} style={{background:"none", border:"none", color:"var(--c-primary)", fontWeight:800, fontSize:13, cursor:"pointer"}}>{zhEn("查看全部 →", "View all →")}</button>}
                </div>
                {gallery.length === 0 ? (
                  <p style={{fontSize:13, color:"#AFAFC4", margin:0, fontStyle:"italic"}}>{zhEn("（按此上載作品照片）", "(tap to upload gallery photos)")}</p>
                ) : (
                <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6}}>
                  {gallery.slice(0, 6).map((g, i) => (
                    <div key={g.id || i} style={{aspectRatio:"1", borderRadius:10, overflow:"hidden"}}>
                      <img src={g.url} alt={g.caption || "gallery " + (i+1)} style={{width:"100%", height:"100%", objectFit:"cover"}} />
                    </div>
                  ))}
                </div>
                )}
              </Card>
            )}
            </E>
          </div>
        )}

        {/* ── GALLERY ──────────────────────────────────────────────────── */}
        {activeTab === "gallery" && (
          <Card style={{padding:16}}>
            <h2 style={{fontWeight:900, fontSize:18, margin:"0 0 14px", color:"#4A4A68"}}>{zhEn("作品集 ·", "Gallery ·")} {gallery.length} {zhEn("張", "unpaid")}</h2>
            {gallery.length === 0 ? (
              <p style={{fontSize:13, color:"#AFAFC4", padding:"40px 20px", textAlign:"center", fontWeight:700, margin:0}}>{zhEn("店主仲未上傳作品照片。", "The owner hasn't uploaded gallery photos yet.")}</p>
            ) : (
              <div style={{display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8}}>
                {gallery.map((g, i) => (
                  <div key={g.id || i} style={{aspectRatio:"1", borderRadius:12, overflow:"hidden", background:"var(--c-lighter)"}}>
                    <img src={g.url} alt={g.caption || "gallery " + (i+1)} loading="lazy" style={{width:"100%", height:"100%", objectFit:"cover"}} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* ── INFO ─────────────────────────────────────────────────────── */}
        {activeTab === "info" && (
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <E label={zhEn("聯絡方式", "Contact")} target="shop-basic">
            <Card style={{padding:20}}>
              <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 12px", color:"#4A4A68"}}>{zhEn("聯絡方式", "Contact")}</h3>
              {(tenant.serviceMode === "mobile" || tenant.serviceMode === "both") && (
                <p style={{fontSize:14, color:"#7C3AED", margin:"0 0 8px", fontWeight:800}}>🚗 {T("上門服務","Home visit")} · {T("服務範圍","Service area")}{zhEn("：", ": ")}{(i18nGetLocale()!== "zh-HK" ? lookupRegion(tenant.region)?.en : lookupRegion(tenant.region)?.zh) || T("全港上門","Hong Kong-wide")}</p>
              )}
              {tenant.serviceMode !== "mobile" && tenant.address && <p style={{fontSize:14, color:"#4A4A68", margin:"0 0 8px", fontWeight:700}}>📍 {tenant.address}</p>}
              {tenant.phone && <p style={{fontSize:14, color:"#4A4A68", margin:"0 0 8px", fontWeight:700}}>📞 {editMode ? tenant.phone : <a href={"tel:" + tenant.phone} style={{color:"var(--c-primary)", textDecoration:"none"}}>{tenant.phone}</a>}</p>}
              {tenant.website && <p style={{fontSize:14, color:"#4A4A68", margin:"0 0 8px", fontWeight:700}}>🌐 {editMode ? tenant.website : <a href={tenant.website} target="_blank" rel="noopener" style={{color:"var(--c-primary)", wordBreak:"break-all"}}>{tenant.website}</a>}</p>}
              {tenant.instagram && <p style={{fontSize:14, color:"#4A4A68", margin:"0 0 8px", fontWeight:700}}>📷 Instagram: {editMode ? tenant.instagram : <a href={"https://instagram.com/" + tenant.instagram.replace(/^@/, "")} target="_blank" rel="noopener" style={{color:"var(--c-primary)"}}>{tenant.instagram}</a>}</p>}
              {tenant.facebook && <p style={{fontSize:14, color:"#4A4A68", margin:"0 0 8px", fontWeight:700}}>📘 Facebook: {editMode ? tenant.facebook : <a href={"https://facebook.com/" + tenant.facebook} target="_blank" rel="noopener" style={{color:"var(--c-primary)"}}>{tenant.facebook}</a>}</p>}
              {!tenant.address && !tenant.phone && editMode && <p style={{fontSize:13, color:"#AFAFC4", margin:0, fontStyle:"italic"}}>{zhEn("（按此填寫地址 / 電話 / 社交媒體）", "(tap to add address / phone / social media)")}</p>}
            </Card>
            </E>
            <E label={zhEn("營業時間", "Opening hours")} target="shop-hours">
            {(tenant.businessHours && Object.keys(tenant.businessHours).length > 0) || editMode ? (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 12px", color:"#4A4A68"}}>{zhEn("營業時間", "Opening hours")}</h3>
                {DAYS.map((d, i) => {
                  const bh = tenant.businessHours[i];
                  return (
                    <div key={i} style={{display:"flex", justifyContent:"space-between", padding:"6px 0", fontSize:13, fontWeight:700, color:"#4A4A68"}}>
                      <span>{zhEn("星期", "Weekday")}{d}</span>
                      <span>{bh?.enabled === false || bh?.closed || (!bh?.open && !bh?.close) ? <span style={{color:"#AFAFC4"}}>{zhEn("休息", "Day off")}</span> : (bh.open + " – " + bh.close)}</span>
                    </div>
                  );
                })}
              </Card>
            ) : null}
            </E>
            {staffUsers.length > 0 && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 12px", color:"#4A4A68"}}>{zhEn("美容師團隊", "Groomers")}</h3>
                <div style={{display:"flex", flexWrap:"wrap", gap:14}}>
                  {staffUsers.map(s => (
                    <div key={s.id} style={{display:"flex", flexDirection:"column", alignItems:"center", width:80, textAlign:"center"}}>
                      <div style={{width:64, height:64, borderRadius:99, overflow:"hidden", background:"var(--c-lighter)", marginBottom:6, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, color:"var(--c-primary)", fontWeight:900}}>
                        {s.avatar ? <img src={s.avatar} alt={s.name} style={{width:"100%", height:"100%", objectFit:"cover"}} /> : (s.name?.[0] || "?")}
                      </div>
                      <span style={{fontSize:12, fontWeight:800, color:"#4A4A68", lineHeight:1.3}}>{s.name}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            <E label={zhEn("服務項目", "Services")} target="tab:services">
            {(mainServices.length > 0 || spaServices.length > 0) && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 12px", color:"#4A4A68"}}>{zhEn("服務項目", "Services")}</h3>
                {mainServices.map(s => (
                  <div key={s.id} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px dashed var(--c-border)"}}>
                    <div>
                      <p style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:0}}>{s.displayName || s.name}</p>
                      {s.description && <p style={{fontSize:12, color:"#8A8A9E", margin:"3px 0 0", fontWeight:600}}>{s.description}</p>}
                    </div>
                    {s.basePrice > 0 && <span style={{fontSize:13, color:"var(--c-primary)", fontWeight:800, whiteSpace:"nowrap"}}>{zhEn("由 $", "From $")}{s.basePrice} {zhEn("起", "+")}</span>}
                  </div>
                ))}
                {spaServices.length > 0 && (
                  <>
                    <p style={{fontSize:12, fontWeight:800, color:"#8A8A9E", margin:"14px 0 6px"}}>{zhEn("水療 (可選)", "Hydrotherapy (optional)")}</p>
                    {spaServices.map(s => (
                      <div key={s.id} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px dashed var(--c-border)"}}>
                        <span style={{fontSize:14, fontWeight:700, color:"#4A4A68"}}>💆 {s.displayName || s.name}</span>
                        {s.basePrice > 0 && <span style={{fontSize:13, color:"var(--c-primary)", fontWeight:800}}>+${s.basePrice}</span>}
                      </div>
                    ))}
                  </>
                )}
              </Card>
            )}
            </E>
          </div>
        )}

        {/* ── POLICIES ─────────────────────────────────────────────────── */}
        {activeTab === "policies" && (
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <E label={zhEn("取消政策 / 條款", "Cancellation policy / terms")} target="cancel-policy">
            {tenant.cancellationPolicy && Object.keys(tenant.cancellationPolicy).length > 0 && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 8px", color:"#4A4A68"}}>{zhEn("取消／改期政策", "Cancellation / reschedule policy")}</h3>
                <p style={{fontSize:13, color:"#4A4A68", margin:"0 0 6px", fontWeight:700, lineHeight:1.6}}>{zhEn("須喺預約時間前", "Cancel or reschedule at least ")} <strong>{tenant.cancellationPolicy.deadlineHours ?? 24} {zhEn("小時", "hours")}</strong> {zhEn("取消或改期。", " before your appointment.")}</p>
                {tenant.cancellationPolicy.lateCancelFeePercent != null && <p style={{fontSize:13, color:"#4A4A68", margin:"0 0 4px", fontWeight:700}}>{zhEn("逾時取消收費：訂金 ×", "Late-cancellation fee: deposit ×")} {tenant.cancellationPolicy.lateCancelFeePercent}%</p>}
                {tenant.cancellationPolicy.noShowFeePercent != null && <p style={{fontSize:13, color:"#4A4A68", margin:0, fontWeight:700}}>{zhEn("失約收費：訂金 ×", "No-show fee: deposit ×")} {tenant.cancellationPolicy.noShowFeePercent}%</p>}
              </Card>
            )}
            {tenant.termsBooking && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 8px", color:"#4A4A68"}}>{zhEn("📋 預約條款", "📋 Booking terms")}</h3>
                <pre style={{fontSize:12, color:"#4A4A68", fontWeight:600, whiteSpace:"pre-wrap", lineHeight:1.6, margin:0, fontFamily:"inherit"}}>{tenant.termsBooking}</pre>
              </Card>
            )}
            {tenant.disclaimerSenior && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 8px", color:"#4A4A68"}}>{zhEn("🐕‍🦺 高齡毛孩免責聲明", "🐕‍🦺 Senior pet disclaimer")}</h3>
                <pre style={{fontSize:12, color:"#4A4A68", fontWeight:600, whiteSpace:"pre-wrap", lineHeight:1.6, margin:0, fontFamily:"inherit"}}>{tenant.disclaimerSenior}</pre>
              </Card>
            )}
            {tenant.termsCat && (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900, fontSize:16, margin:"0 0 8px", color:"#4A4A68"}}>{zhEn("🐱 貓貓專屬條款", "🐱 Cat-specific terms")}</h3>
                <pre style={{fontSize:12, color:"#4A4A68", fontWeight:600, whiteSpace:"pre-wrap", lineHeight:1.6, margin:0, fontFamily:"inherit"}}>{tenant.termsCat}</pre>
              </Card>
            )}
            </E>
          </div>
        )}
      </div>

      {/* Sticky bottom CTA — keeps booking one tap away on every tab.
          Hidden in edit-preview mode so the owner can scroll freely. */}
      {!editMode && (bookingOn || contactHref || waHref) && <div style={{position:"fixed", bottom:0, left:0, right:0, padding:"12px 16px", background:"#FFFDFD", borderTop:"1px solid var(--c-border)", zIndex:50}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
          {bookingOn ? (
            <button onClick={onBook} style={{width:"100%", padding:"14px", background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:15, cursor:"pointer", boxShadow:"0 8px 20px var(--c-primary-30)"}}>
              {zhEn("預約服務 →", "Book a service →")}
            </button>
          ) : (
            <div style={{display:"flex", gap:10}}>
              {waHref && (
                <a href={waHref} target="_blank" rel="noreferrer" style={{flex:1, padding:"14px", background:"linear-gradient(135deg,#25D366,#128C7E)", color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:15, boxShadow:"0 8px 20px rgba(18,140,126,0.3)", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, textDecoration:"none", boxSizing:"border-box"}}>
                  <MessageCircle size={18} /> WhatsApp
                </a>
              )}
              {contactHref && (
                <a href={contactHref} style={{flex:1, padding:"14px", background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:15, boxShadow:"0 8px 20px var(--c-primary-30)", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, textDecoration:"none", boxSizing:"border-box"}}>
                  <Phone size={17} /> {zhEn("致電", "Call")}
                </a>
              )}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
};

// ── PUBLIC BOOKING PAGE ────────────────────────────────────────────────────
const PublicBooking = ({ tenant, state, setState, profile, onDone, editMode = false, onEditSection = null, testMode = false }) => {
  // Subscribe to locale changes so the language pill toggle re-renders
  // every translated string in this subtree.
  i18nUseLocale();
  // Landing-page column count — 1 on phone, 2 on tablet+. Listens to
  // window resize so the CSS-multi-column wrapper rebalances when the
  // viewport flips between portrait and landscape on an iPad.
  const [bookingCols, setBookingCols] = useState(() => (typeof window !== "undefined" && window.innerWidth >= 760) ? 2 : 1);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setBookingCols(window.innerWidth >= 760 ? 2 : 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Gallery lightbox — open from any thumb in the 6-grid or the
  // "查看全部 (N)" button. Tracks the current index so prev/next arrows
  // can flip through every photo in the shop's full shopGallery.
  const [galLightboxIdx, setGalLightboxIdx] = useState(null);
  const galPhotos = tenant?.shopGallery || [];
  const closeGalLightbox = () => setGalLightboxIdx(null);
  const nextGalPhoto = () => setGalLightboxIdx(i => (i == null ? 0 : (i + 1) % galPhotos.length));
  const prevGalPhoto = () => setGalLightboxIdx(i => (i == null ? 0 : (i - 1 + galPhotos.length) % galPhotos.length));
  // Tap-to-edit overlay shorthand — wraps a section in the EditTarget
  // helper only when the owner is previewing. Inert otherwise.
  const E = ({ label, target, children, style }) => (
    <EditTarget active={editMode} label={label} target={target} onEdit={onEditSection} style={style}>{children}</EditTarget>
  );
  // When the user is already authenticated, the auth step is reshaped to
  // "continue as {name}". This avoids the broken case where a logged-in user
  // re-triggers Google OAuth and gets bounced to the role-based router.
  const isAuthed = !!profile;
  const isOwnerOfThisShop = isAuthed && profile.role === "owner" && profile.tenantId === tenant.id;
  // Test mode (owner "試做客戶預約"): jump straight into the booking steps,
  // drive everything off local `booking` state, and sandbox the final submit —
  // never patches the owner's profile or writes a real appointment.
  // Free shops: has this month's allowance run out? (paid shops are unlimited)
  const [bookingQuota, setBookingQuota] = useState(null);
  useEffect(() => {
    if (!tenant?.id || testMode) return;
    let alive = true;
    fetchOnlineBookingQuota(tenant.id).then(q => { if (alive) setBookingQuota(q); }).catch(() => {});
    return () => { alive = false; };
  }, [tenant?.id, testMode]);
  const [step, setStep] = useState(() => {
    if (testMode) return "petinfo";
    try {
      // Resume a mid-booking after an OAuth round-trip (component remounts).
      const b = JSON.parse(localStorage.getItem("petos:pending-booking") || "{}");
      if (b && b.serviceId && b.date && b.time) return "confirm";
    } catch { /* ignore */ }
    // PRIVATE, unlisted "check price & availability" link — enters the browse
    // flow (price + live slots, no login). Deliberately NOT shown anywhere on
    // the public page; the owner shares the ?quote=1 link with whoever they want.
    try {
      if (typeof window !== "undefined" && /[?&]quote=1(?:&|$)/.test(window.location.search || "")) return "petinfo";
    } catch { /* ignore */ }
    return "landing";
  });
  const [booking, setBooking] = useState(() => {
    try { return JSON.parse(localStorage.getItem("petos:pending-booking") || "{}"); }
    catch { return {}; }
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submitBusyRef = useRef(false); // synchronous double-submit guard (see submitBooking)
  const [expandedAuth, setExpandedAuth] = useState(null);
  const [authErr, setAuthErr] = useState("");
  // When Supabase needs the customer to confirm their email before login.
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState("");
  // Anonymised busy windows — see generateTimeSlots' `extraBusy` param.
  // Anon sessions have NO visibility into other customers' appointments
  // via RLS, so without this fetch every time slot looked free.
  const [pubBusySlots, setPubBusySlots] = useState([]);
  useEffect(() => {
    if (!tenant?.id) { setPubBusySlots([]); return; }
    let cancelled = false;
    const today = new Date();
    const todayStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
    const sixty = new Date(); sixty.setDate(sixty.getDate() + 60);
    const sixtyStr = sixty.getFullYear() + "-" + String(sixty.getMonth() + 1).padStart(2, "0") + "-" + String(sixty.getDate()).padStart(2, "0");
    fetchPublicBusySlots(tenant.id, todayStr, sixtyStr)
      .then(rows => { if (!cancelled) setPubBusySlots(rows); })
      .catch(err => { console.warn("[PettoBook] public busy slots fetch failed:", err); });
    return () => { cancelled = true; };
  }, [tenant?.id]);
  // Auto-scroll to the top of each step so the next section is in view — the
  // previous step's scroll position otherwise carries over, leaving the new
  // section scrolled past.
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { try { window.scrollTo(0, 0); } catch { /* ignore */ } }
  }, [step]);
  // On the schedule step, once a date is chosen scroll the time slots into view
  // (they render below the date strip, often past the fold).
  const slotsRef = useRef(null);
  useEffect(() => {
    if (step === "schedule" && booking.date && slotsRef.current) {
      const t = setTimeout(() => { try { slotsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ } }, 80);
      return () => clearTimeout(t);
    }
  }, [booking.date, step]);
  const themeVars = THEMES[tenant.theme] || THEMES.peach;

  // Auto-advance past landing/auth when the user is already authenticated.
  // Triggers right after Google OAuth round-trip (profile loads into a fresh
  // PublicBooking mount) so they don't have to click through the same auth
  // step that they already completed. Owners testing on their own shop are
  // excluded so they still see the warning + visitor option.
  useEffect(() => {
    // NEVER auto-convert privileged accounts (owner/staff/admin/superadmin of
    // ANY shop, or the platform superadmin). This effect used to demote a
    // superadmin/other-shop-owner into this tenant's customer just for
    // OPENING the page — clobbering role + tenant_id (recurred 5x on the
    // platform admin account). Privileged visitors stay on the landing and
    // can browse as 訪客; a real customer identity needs a separate account.
    const isPrivilegedVisitor = ["owner", "staff", "admin", "superadmin"].includes(profile?.role) || profile?.isSuper;
    if (!testMode && isAuthed && !isOwnerOfThisShop && !isPrivilegedVisitor && (step === "landing" || step === "auth")) {
      // We no longer run the custom wizard for authenticated users — instead,
      // we hand them off to CustomerApp where they pick / add their pet and
      // book using the polished dashboard UI. We also patch their profile so
      // they're properly registered as a customer of this tenant.
      (async () => {
        try {
          if (profile.tenantId !== tenant.id || profile.role !== "customer") {
            await patchProfile(profile.id, {
              tenantId: tenant.id,
              role: "customer",
              name: profile.name || (profile.email || "").split("@")[0],
              phone: profile.phone || "",
            });
            // Local state still has the pre-patch profile — update it so when
            // CustomerApp mounts, `liveUser.tenantId` is correct (otherwise
            // pet inserts fail RLS because user_tenant_id() returns NULL).
            setState(s => ({
              ...s,
              users: {
                ...s.users,
                [profile.id]: { ...(s.users?.[profile.id] || profile), tenantId: tenant.id, role: "customer" },
              },
            }));
            // PDPO/PDPA — record consent on first-time tenant binding.
            try {
              await recordConsent({ tenantId: tenant.id, purpose: "privacy_policy", version: "2026-05-19", context: { shop_slug: tenant.bookingSlug, via: "google_oauth" } });
            } catch { /* non-fatal */ }
          }
        } catch (e) { /* eslint-disable-next-line no-console */ console.warn("Profile patch failed:", e); }
        try { localStorage.removeItem("petos:pending-booking"); } catch {}
        if (typeof window !== "undefined" && window.__petosRouteAfterAuth) {
          window.__petosRouteAfterAuth({ slug: tenant.bookingSlug, tab: "pets" });
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, isOwnerOfThisShop]);

  // Data shape: tenant.services has { kind: 'service' | 'spa' }, tenant.addons is separate.
  // Staff: tenant.staffIds[] -> state.users[id]
  const allServices = tenant?.services || [];
  // Respect owner-defined display order — the 服務管理 page lets the
  // owner drag-reorder; the customer-facing list must mirror it. Was
  // missing this sort, so the drag UI did nothing customer-visible.
  const byOrder = (a, b) => (Number(a.displayOrder)||0) - (Number(b.displayOrder)||0);
  const mainServices = allServices.filter(s => s.kind !== "spa" && s.enabled !== false && s.customerVisible !== false).sort(byOrder);
  const spaServices = allServices.filter(s => s.kind === "spa" && s.enabled !== false && s.customerVisible !== false).sort(byOrder);
  const addonsList = (tenant?.addons || []).filter(a => a.enabled !== false);
  // 上門 (home-visit) booking. mobile-only shops force 上門; 兩者 shops let the
  // customer choose (booking.isMobile); in-shop shops never offer it. When 上門,
  // only services the owner opted in (mobileAvailable) are bookable, and we
  // collect the customer's address in the confirm step.
  const shopMobile = tenant?.serviceMode === "mobile" || tenant?.serviceMode === "both";
  const shopMobileOnly = tenant?.serviceMode === "mobile";
  const wantMobile = shopMobileOnly ? true : (shopMobile ? !!booking.isMobile : false);
  const visibleMainServicesBase = wantMobile ? mainServices.filter(serviceMobileBookable) : mainServices;
  const visibleSpaServicesBase = wantMobile ? spaServices.filter(serviceMobileBookable) : spaServices;
  // Show every active groomer — staff AND owners (one-person shops have
  // the owner as the only groomer). Exclude disabled accounts and anyone
  // whose leaveDate is already in the past (resigned).
  const todayDateStr = localDateStr(new Date());
  const staffList = (tenant?.staffIds || [])
    .map(sid => state.users?.[sid])
    .filter(u => u && u.enabled !== false
      && u.customerVisible !== false
      && (u.role === "owner" || u.role === "staff")
      && (!u.leaveDate || u.leaveDate >= todayDateStr)
      && (!u.joinDate || u.joinDate <= todayDateStr));

  // Pet info collected in step "petinfo" — used to compute exact pricing on
  // every later step. When weight is 0 (e.g. still on landing), the pricing
  // helpers fall back to base price.
  const petForPricing = {
    weight: parseFloat(booking.petWeight) || 0,
    breed: booking.petBreed || "",
    breedId: booking.petBreedId || null,
    coatLength: booking.coatLength || "short",
  };
  // Filter services to the chosen pet:
  //  (a) SPECIES — hide services that don't apply to this pet type (a dog owner
  //      must not see cat-only services). Uses the same serviceTypes(svc) source
  //      (svc.petTypes || [svc.petType||"dog"]) as the owner's booking picker.
  //  (b) SIZE — hide services whose matched size-tier is 不適用 for the pet's
  //      weight (e.g. a SPA only for 小/中型). Only once we know the weight.
  const petSpecies = booking.petType || "dog";
  const availForPet = (s) => {
    const types = serviceTypes(s);
    if (types.length && !types.includes(petSpecies)) return false;
    return !(petForPricing.weight > 0) || !getPricingDetail(petForPricing, s, tenant, { mobile: wantMobile }).unavailable;
  };
  const visibleMainServices = visibleMainServicesBase.filter(availForPet);
  const visibleSpaServices = visibleSpaServicesBase.filter(availForPet);

  // Persistent "logged in as X" pill so customers always know whether they're
  // signed in (especially after a Google OAuth round-trip that auto-advances
  // them past the auth step — without this they wouldn't see any login
  // confirmation). Owners testing their own shop are excluded since the auth
  // step itself shows the warning + visitor option.
  const authBanner = isAuthed && !isOwnerOfThisShop ? (
    <div style={{background:"var(--c-lighter)", borderRadius:99, padding:"6px 14px", marginBottom:12, display:"inline-flex", alignItems:"center", gap:6, fontSize:12, fontWeight:800, color:"var(--c-primary)", maxWidth:"100%"}}>
      <span>🔐</span>
      <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{zhEn("已登入：", "Logged in: ")}{profile.name || profile.email}</span>
    </div>
  ) : null;

  const saveBooking = (updates) => {
    const newBooking = { ...booking, ...updates };
    setBooking(newBooking);
    try { localStorage.setItem("petos:pending-booking", JSON.stringify(newBooking)); } catch {}
  };

  // Final submit — authenticated path only. Guest booking has been removed
  // (was a spam/abuse vector); callers MUST be logged in before reaching
  // this point, gated by the auth step.
  const submitBooking = async () => {
    // Ref, not `loading`: two taps in the same tick both read the pre-update
    // state, so the disabled prop alone can't stop a fast double-tap.
    if (submitBusyRef.current) return;
    submitBusyRef.current = true;
    setErr("");
    setLoading(true);
    try {
      // Test mode — owner walking the flow. Don't touch the DB or profile;
      // just show the success screen so they can review the whole UX.
      if (testMode) { setLoading(false); setStep("done"); return; }
      const isAuthedNonOwner = isAuthed && !isOwnerOfThisShop;
      if (!isAuthedNonOwner) {
        // Guest (came via the private price link) tapped to book → the register
        // wall appears HERE. Their selection is in `booking` (persisted to
        // petos:pending-booking); default to register + resume after auth.
        setLoading(false);
        try { localStorage.setItem("petos:pending-booking", JSON.stringify(booking)); } catch { /* ignore */ }
        setExpandedAuth("email-register");
        setStep("auth");
        return;
      }
      {
        // Bind the user to this tenant for booking purposes. Two cases:
        //  (a) First-time customer (no tenantId yet): patch profile so the
        //      legacy single-shop path works, AND create a membership row
        //      so dataLoader's multi-shop reload picks them up.
        //  (b) Already a customer of ANOTHER shop: ONLY insert a membership
        //      row — DO NOT overwrite profile.tenantId. Overwriting would
        //      silently kick them out of their primary shop. (H2 fix.)
        // Privileged accounts (superadmin/owner/staff of any shop) must never
        // be re-homed/demoted by booking — membership row only. (Same guard
        // as the auto-convert effect; DB trigger also blocks it server-side.)
        const isPrivilegedBooker = ["owner", "staff", "admin", "superadmin"].includes(profile?.role) || profile?.isSuper;
        const isFirstTimeCustomer = !profile.tenantId && !isPrivilegedBooker;
        // Persist the contact phone entered on the confirm screen (required +
        // validated there). Without this the typed number was dropped and the
        // shop had no way to reach the customer. PublicBooking is +852-only, so
        // strip a leading +852 and store bare digits (matches profile.phone).
        const enteredName = (booking.guestName || "").trim() || profile.name || (profile.email || "").split("@")[0];
        const enteredPhone = normalizePhoneDigits((booking.guestPhone || "").replace(/^\+852/, "")) || profile.phone || "";
        if (isFirstTimeCustomer) {
          try {
            await patchProfile(profile.id, {
              tenantId: tenant.id,
              role: "customer",
              name: enteredName,
              phone: enteredPhone,
            });
          } catch (e) { /* eslint-disable-next-line no-console */ console.warn("Profile patch failed:", e); }
        } else if (enteredPhone && !profile.phone) {
          // Returning/other-shop customer with no phone on file — save it.
          try { await patchProfile(profile.id, { phone: enteredPhone }); }
          catch (e) { /* eslint-disable-next-line no-console */ console.warn("Phone patch failed:", e); }
        }
        // Always ensure a membership row exists for this (user, tenant) pair.
        // Idempotent: upsert with ignoreDuplicates makes re-booking cheap.
        try {
          await upsertCustomerMembership({
            tenantId: tenant.id,
            displayName: enteredName,
            phone: enteredPhone,
          });
        } catch (e) { /* eslint-disable-next-line no-console */ console.warn("Membership upsert failed:", e); }
        // Direct API path
        let petId = booking.petId;
        // If they didn't pick an existing pet (or the picked one is invalid),
        // create a new one under their profile.
        const myPets = (state.users?.[profile.id]?.pets || []).map(pid => state.pets?.[pid]).filter(Boolean);
        const validPickedPet = myPets.find(p => p.id === petId);
        if (!validPickedPet) {
          const newPet = await upsertPet({
            id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uid(),
            tenantId: tenant.id,
            ownerId: profile.id,
            name: booking.petName,
            type: booking.petType || "dog",
            breed: booking.petBreed || "",
            breedId: booking.petBreedId || null,
            weight: parseFloat(booking.petWeight) || 0,
            coatLength: booking.coatLength || "short",
            allergies: zhEn("無", "None"),
          });
          petId = newPet?.id;
        }
        if (!petId) throw new Error(zhEn("毛孩資料儲存失敗", "Failed to save pet details"));

        const svc = (tenant?.services || []).find(s => s.id === booking.serviceId);
        // M3: guard against the owner deleting / disabling the service after
        // the customer loaded the page. Without this we'd write an orphan
        // appointment with svcId=undefined and service="".
        if (!svc) { throw new Error(zhEn("服務已下架，請重新選擇服務", "This service is no longer available, please choose again")); }
        const petForPricing = { weight: parseFloat(booking.petWeight) || 0, breed: booking.petBreed || "", breedId: booking.petBreedId || null, coatLength: booking.coatLength || "short", type: booking.petType || "dog" };
        const svcPd = getPricingDetail(petForPricing, svc, tenant, { mobile: wantMobile });
        const spaItems = (booking.spaIds || []).map(id => (tenant?.services || []).find(s => s.id === id)).filter(Boolean);
        const addonItems = (booking.addons || []).map(id => (tenant?.addons || []).find(a => a.id === id)).filter(Boolean);
        const spaTotal = spaItems.reduce((s, sp) => s + getPricingDetail(petForPricing, sp, tenant, { mobile: wantMobile }).total, 0);
        const addonTotal = addonItems.reduce((s, a) => s + computeAddonPrice(a, petForPricing).price, 0);
        const total = (svcPd.total || 0) + spaTotal + addonTotal;
        const isReturning = (state.appointments || []).some(a => a.customerId === profile.id && a.tenantId === tenant.id);
        const deposit = calcDeposit(total, tenant, isReturning);

        const appt = await upsertAppointment({
          id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uid(),
          tenantId: tenant.id,
          customerId: profile.id,
          petId,
          staffId: booking.staffId || null,
          svcId: booking.serviceId || null,
          service: svc?.name || "",
          addons: addonItems.map(a => a.name),
          spaIds: booking.spaIds || [],
          date: booking.date,
          time: booking.time,
          status: "pending",
          approvalStatus: tenant?.cancellationPolicy?.requireApproval ? "pending" : null,
          basePrice: svcPd.total || 0,
          addonPrice: spaTotal + addonTotal,
          deposit,
          isMobile: wantMobile,
          serviceAddress: wantMobile ? (booking.serviceAddress || "").trim() : null,
          serviceNotes: wantMobile ? (booking.serviceNotes || "").trim() : null,
        });
        // Belt-and-suspenders: upsert with `.select("*").maybeSingle()` can
        // return null if the row was silently dropped (RLS, network blip,
        // misconfigured Supabase). Refuse to show the "success" screen until
        // we have a real id back from the DB.
        if (!appt?.id) {
          throw new Error(zhEn("預約未儲存成功（資料庫無回傳），請重新嘗試或聯絡店家", "The booking wasn't saved (no response from the database). Please try again or contact the shop."));
        }
        saveBooking({ appointmentId: appt.id, submitted: true });
        // Persist the 上門 address on the customer's membership so it auto-fills
        // next time (RLS allows the customer to update their own row).
        if (wantMobile && (booking.serviceAddress || "").trim()) {
          patchCustomerMembership({ userId: profile.id, tenantId: tenant.id, address: (booking.serviceAddress || "").trim() }).catch(() => {});
        }
        // Best-effort promo redemption with one retry. If it ultimately
        // fails we DON'T rollback the appointment (the booking is the
        // valuable artifact) but we DO surface a non-fatal note so the
        // customer knows to mention the promo at the shop. Without this,
        // the discount silently disappears and the customer is over-billed.
        if (booking.promoId && booking.promoDiscount > 0) {
          let redeemed = false;
          for (let i = 0; i < 2 && !redeemed; i++) {
            try {
              await redeemPromoCode({ tenantId: tenant.id, promoCodeId: booking.promoId, customerId: profile.id, appointmentId: appt?.id, amount: booking.promoDiscount });
              redeemed = true;
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn("[PettoBook] promo redeem attempt", i + 1, "failed:", e);
              if (i === 0) await new Promise(r => setTimeout(r, 400));
            }
          }
          if (!redeemed) {
            // Queue for later retry (a service-worker / next-load sweep could
            // drain pending-promo-* keys); meanwhile attach the warning to
            // the saved booking so the confirmation screen can display it.
            try { localStorage.setItem("petos:pending-promo-" + appt.id, JSON.stringify({ tenantId: tenant.id, promoCodeId: booking.promoId, customerId: profile.id, appointmentId: appt.id, amount: booking.promoDiscount, at: Date.now() })); } catch {}
            saveBooking({ appointmentId: appt.id, submitted: true, promoNote: zhEn("優惠碼未能即時兌換，請到店時告知店家以套用折扣。", "The promo code couldn't be redeemed right away; tell the shop when you visit to apply the discount.") });
          }
        }
      }
      try { localStorage.removeItem("petos:pending-booking"); } catch {}
      setStep("done");
    } catch (e) {
      setErr(e?.message || zhEn("預約失敗", "Booking failed"));
    } finally {
      submitBusyRef.current = false;
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthErr("");
    try {
      // Fallback only — with clean /shop/{slug} paths the path itself is
      // preserved through Supabase OAuth (the access_token hash is stripped
      // by detectSessionInUrl). The hash-form copy below is a backup for
      // edge cases where the path somehow gets lost.
      try { localStorage.setItem("petos:booking-redirect", `#/shop/${tenant.bookingSlug}`); } catch {}
      const { signInWithGoogle } = await import("./lib/auth");
      // redirectTo MUST be a hash-free URL so Supabase doesn't append the
      // OAuth fragment INTO our existing hash and produce a percent-encoded
      // mess. The clean pathname (/shop/{slug}) survives the round-trip
      // naturally and the App initializer re-parses it.
      const cleanRedirect = window.location.origin + window.location.pathname;
      await signInWithGoogle({ redirectTo: cleanRedirect });
    } catch (e) {
      setAuthErr(e?.message || zhEn("Google 登入失敗", "Google sign-in failed"));
    }
  };

  const handleAppleAuth = async () => {
    setAuthErr("");
    try {
      try { localStorage.setItem("petos:booking-redirect", `#/shop/${tenant.bookingSlug}`); } catch {}
      const { signInWithApple } = await import("./lib/auth");
      const cleanRedirect = window.location.origin + window.location.pathname;
      await signInWithApple({ redirectTo: cleanRedirect });
    } catch (e) {
      setAuthErr(e?.message || zhEn("Apple 登入失敗", "Apple sign-in failed"));
    }
  };

  const handleEmailAuth = async (email, password, isRegister) => {
    setAuthErr("");
    setLoading(true);
    try {
      const auth = await import("./lib/auth");
      if (isRegister) {
        // Pass tenant_id so the new profile is associated with this shop
        // from the moment it's created. Without this, the customer ends
        // up with no shop context and can't book appointments. Also stash
        // the slug in localStorage so the redirect after email
        // confirmation lands them on the correct shop's landing page.
        try { localStorage.setItem("petos:signup-tenant-slug", tenant.bookingSlug); } catch {}
        const res = await auth.signUp({ email, password, name: booking.guestName || email.split("@")[0], phone: booking.guestPhone || "", role: "customer", tenant_id: tenant.id });
        // Supabase returns { user, session: null } when email confirmation
        // is required. Without a session, ANY route-after-auth would dump
        // the user back on the marketing page (they're not logged in yet).
        // Surface the confirmation-pending screen instead so the customer
        // knows to check their inbox.
        if (!res?.session) {
          setPendingConfirmEmail(email);
          return;
        }
      } else {
        // Phone login: no "@" means the customer typed the number the shop
        // sent them. Their account's login address is derived from the phone
        // + this shop's slug (kept in sync by send-customer-login). Strip a
        // typed HK/Macau country code — shops store the 8-digit local number.
        const raw = String(email || "").trim();
        if (!raw.includes("@")) {
          let digits = raw.replace(/\D+/g, "");
          if (digits.length === 11 && /^85[23]/.test(digits)) digits = digits.slice(3);
          if (digits.length < 8) throw new Error(zhEn("請輸入 Email 或 8 位電話號碼", "Enter an email or an 8-digit phone number"));
          try {
            await auth.signIn({ email: customerSyntheticEmail(digits, tenant.bookingSlug), password });
          } catch (e) {
            if (/invalid login|credentials/i.test(String(e?.message || ""))) throw new Error(zhEn("電話或密碼錯誤。如未收過密碼，請聯絡店家發送登入資料。", "Wrong phone or password. If you haven't received a password, ask the shop to send your login details."));
            throw e;
          }
        } else {
          try {
            await auth.signIn({ email: raw, password });
          } catch (e) {
            if (/invalid login|credentials/i.test(String(e?.message || ""))) throw new Error(zhEn("Email 或密碼錯誤。", "Wrong email or password."));
            throw e;
          }
        }
      }
      // PDPO/PDPA — record explicit consent at signup. The session is now
      // active so recordConsent (which reads auth.uid()) will find a user.
      try {
        await recordConsent({ tenantId: tenant.id, purpose: isRegister ? "privacy_policy" : "session_login", version: "2026-05-19", context: { shop_slug: tenant.bookingSlug } });
      } catch { /* non-fatal */ }
      // Ensure the customer has a membership at this shop so they can
      // book / earn points / use the wallet. No-op for repeat sign-ins.
      try {
        const { data: { user: authUser } } = await supabaseDirect.auth.getUser();
        if (authUser?.id) {
          await upsertCustomerMembership({ userId: authUser.id, tenantId: tenant.id, displayName: (booking.guestName || authUser.email?.split("@")[0] || ""), phone: booking.guestPhone || authUser.phone || "" });
        }
      } catch (memErr) { console.warn("[PettoBook] membership ensure failed:", memErr); }
      saveBooking({ authMode: "user" });
      // If mid-booking (came from the private price link), resume on the confirm
      // step so they finish in one tap; otherwise route to their dashboard.
      const _midBooking = !!(booking.serviceId && booking.date && booking.time);
      if (_midBooking) {
        setStep("confirm");
      } else {
        try { localStorage.removeItem("petos:pending-booking"); } catch {}
        if (typeof window !== "undefined" && window.__petosRouteAfterAuth) {
          window.__petosRouteAfterAuth({ slug: tenant.bookingSlug, tab: "pets" });
        }
      }
    } catch (e) {
      setAuthErr(e?.message || zhEn("登入失敗", "Sign-in failed"));
    } finally {
      setLoading(false);
    }
  };

  // ── Email-confirmation pending screen. Shown after a fresh signUp
  //    when Supabase's response has no session (= email confirmation
  //    required). Replaces the previous behavior of bouncing back to
  //    the marketing landing without explanation.
  if (pendingConfirmEmail) {
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24}}>
        <div style={{maxWidth:420, width:"100%", background:"#fff", borderRadius:24, padding:32, boxShadow:"0 12px 32px rgba(0,0,0,0.06)", textAlign:"center"}}>
          <div style={{fontSize:56, marginBottom:12}}>📧</div>
          <h2 style={{fontWeight:900, fontSize:22, color:"#1C1C1E", margin:"0 0 8px"}}>{zhEn("請確認您的電郵", "Please confirm your email")}</h2>
          <p style={{fontSize:14, color:"#6A6A85", fontWeight:700, margin:"0 0 4px"}}>{zhEn("我們已寄送確認電郵至", "We've sent a confirmation email to")}</p>
          <p style={{fontSize:15, color:"var(--c-primary)", fontWeight:900, margin:"0 0 20px",wordBreak:"break-all"}}>{pendingConfirmEmail}</p>
          <p style={{fontSize:13, color:"#8A8A9E", fontWeight:700, margin:"0 0 24px", lineHeight:1.6}}>{zhEn("請打開電郵點擊確認連結。確認後返回此頁面，按下方按鈕登入。", "Open the email and tap the confirmation link. Then come back to this page and tap the button below to log in.")}</p>
          <Btn onClick={() => { setPendingConfirmEmail(""); setStep("landing"); setExpandedAuth("login"); }} style={{width:"100%", marginBottom:10}}>{zhEn("已確認，前往登入", "Confirmed — go to login")}</Btn>
          <button onClick={() => setPendingConfirmEmail("")} style={{background:"none", border:"none", color:"#8A8A9E", fontSize:13, fontWeight:700, cursor:"pointer", padding:"8px 0"}}>{zhEn("← 返回", "← Back")}</button>
        </div>
      </div>
    );
  }

  // Platform maintenance — block customer booking during an upgrade.
  if (state?.platformConfig?.maintenanceMode === true) {
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center"}}>
        <div style={{fontSize:52, marginBottom:14}}>🛠️</div>
        <h1 style={{fontSize:22, fontWeight:900, color:"#3A3A56", margin:"0 0 10px"}}>{T("系統升級維護中","Under maintenance")}</h1>
        <p style={{fontSize:14, fontWeight:700, color:"#6A6A85", margin:0, maxWidth:380, lineHeight:1.6}}>{state.platformConfig?.maintenanceMessage || T("我哋正在進行系統升級，暫停網上預約，請稍後再試。不便之處，敬請原諒。","We're upgrading the system. Online booking is paused — please try again later. Sorry for the inconvenience.")}</p>
      </div>
    );
  }

  // Online booking is a PAID feature. A free-plan shop's booking link still
  // resolves, but instead of the booking flow we show a contact-only screen
  // so customers can still reach the shop (and the owner sees the upgrade nudge).
  const quotaFull = shopHasOnlineBooking(tenant) && bookingQuota?.open === false && !testMode;
  if (!shopHasOnlineBooking(tenant) || quotaFull) {
    const tel = tenant?.phone ? ("tel:" + tenant.phone) : null;
    const wa = tenant?.phone ? ("https://wa.me/" + String(tenant.phone).replace(/[^0-9]/g, "")) : null;
    const btn = { padding:"13px 22px", borderRadius:99, fontWeight:900, fontSize:15, cursor:"pointer", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:8, border:"none" };
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center"}}>
        {tenant?.logoUrl && <img src={tenant.logoUrl} alt="" style={{width:84, height:84, borderRadius:20, objectFit:"cover", marginBottom:16, boxShadow:"0 8px 24px rgba(0,0,0,0.12)"}} />}
        <h1 style={{fontSize:22, fontWeight:900, color:"#3A3A56", margin:"0 0 8px"}}>{tenant?.name || T("美容店","Grooming shop")}</h1>
        <p style={{fontSize:15, fontWeight:800, color:"#6A6A85", margin:"0 0 4px", maxWidth:360}}>{quotaFull ? T("此店今個月網上預約名額已滿","This month's online bookings are full") : T("此店暫未開啟網上預約","Online booking not available")}</p>
        <p style={{fontSize:13, fontWeight:600, color:"#8A8A9E", margin:"0 0 22px", maxWidth:360, lineHeight:1.5}}>{T("請直接致電或 WhatsApp 聯絡店家預約。","Please call or WhatsApp the shop to book.")}</p>
        <div style={{display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center"}}>
          {tel && <a href={tel} style={{...btn, background:"var(--c-primary)", color:"#fff"}}>📞 {T("致電","Call")}</a>}
          {wa && <a href={wa} target="_blank" rel="noreferrer" style={{...btn, background:"#25D366", color:"#fff"}}>💬 WhatsApp</a>}
        </div>
        {!tel && !wa && <p style={{fontSize:13, color:"#AFAFC4", fontWeight:700}}>{T("請於店舖頁面查看聯絡方式","See the shop page for contact details")}</p>}
      </div>
    );
  }

  // ── LANDING: Shop name + login / register / visitor entry points.
  // We deliberately keep this simple now — no services / team preview here.
  // The customer enters the app via login or register, then sees their
  // dashboard (CustomerApp) which has all the rich content.
  if (step === "landing") {
    const curLoc = i18nGetLocale();
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", display:"flex", flexDirection:"column"}}>
        {/* Back-to-directory chip — only when the visitor arrived from
            the public /directory listing. Detection: either history.state
            .from === "directory" (set by DirectoryIndex.onPickShop) or
            document.referrer ending in "/directory". Hidden in edit
            preview mode so the owner doesn't see a stray chip. */}
        {!editMode && (() => {
          const fromDir = (() => {
            try {
              if (typeof window === "undefined") return false;
              // sessionStorage survives reloads but not new tabs — exactly
              // what we want for the back-to-directory chip.
              if (sessionStorage.getItem("petos:from-directory") === "1") return true;
              if (window.history?.state?.from === "directory") return true;
              if (document?.referrer && /\/directory\/?$/.test(new URL(document.referrer).pathname)) return true;
            } catch { /* ignore */ }
            return false;
          })();
          if (!fromDir) return null;
          return (
            <button
              onClick={() => {
                // Prefer browser back so we land on the directory with
                // their filters preserved; fall back to a hard nav if
                // there's no history entry.
                if (typeof window !== "undefined" && window.history.length > 1) window.history.back();
                else window.location.href = "/directory";
              }}
              style={{position:"absolute", top:14, left:14, zIndex:5, background:"rgba(255,255,255,0.22)", backdropFilter:"blur(10px)", color:"#fff", border:"1px solid rgba(255,255,255,0.35)", borderRadius:99, padding:"6px 14px", fontSize:13, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:6, textShadow:"0 1px 4px rgba(0,0,0,0.25)"}}
            >
              {zhEn("← 返回目錄", "← Back to directory")}
            </button>
          );
        })()}

        {/* Hero — banner image (full strength) with dark scrim for text
            legibility. Previous version overlaid the photo at 35% opacity
            over the theme's pink/peach gradient, which washed every shop's
            photography into the same pink blob. Now: full banner, soft
            dark scrim only for text contrast. Without a banner we fall
            back to the theme gradient. */}
        <E label={zhEn("封面圖 / Logo / 店名", "Cover / logo / shop name")} target="shop-brand">
        <div style={{position:"relative", padding:"56px 20px 80px", background: tenant.bannerUrl ? "#1a1a2e" : "linear-gradient(135deg, var(--c-grad1), var(--c-primary))", textAlign:"center", color:"#fff", minHeight:tenant.bannerUrl ? 320 : undefined, overflow:"hidden"}}>
          {/* #8 i18n quick toggle — top-right pill. Persists to localStorage. */}
          <button onClick={() => i18nCycleLocale()} title="Change language" style={{position:"absolute",top:14,right:14,zIndex:3,background:"rgba(255,255,255,0.22)",backdropFilter:"blur(8px)",border:"none",color:"#fff",borderRadius:99,padding:"6px 12px",fontSize:11,fontWeight:900,cursor:"pointer"}}>
            🌐 {i18nLocaleShort(curLoc)}
          </button>
          {tenant.bannerUrl && (
            <>
              <img src={tenant.bannerUrl} alt="" style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:1, zIndex:0}} />
              {/* Scrim — darker at the bottom where text + button sit, lighter at top.
                  Keeps any uploaded photo readable without flattening it. */}
              <div style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)", zIndex:0}} />
            </>
          )}
          <div style={{position:"relative", zIndex:1}}>
            <div style={{width:96, height:96, background:"#fff", borderRadius:28, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:"0 16px 40px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,255,255,0.4)", overflow:"hidden"}}>
              {tenant.logoUrl
                ? <img src={tenant.logoUrl} alt="logo" style={{width:"100%", height:"100%", objectFit:"cover"}} />
                : <PawPrint size={48} color="var(--c-primary)" strokeWidth={2.5} />}
            </div>
            <h1 style={{fontSize:28, fontWeight:900, margin:"0 0 6px", letterSpacing:"0.3px", textShadow: tenant.bannerUrl ? "0 2px 12px rgba(0,0,0,0.4)" : "none"}}>{tenant.name}</h1>
            {tenant.tagline && <p style={{fontSize:14, opacity:0.95, fontWeight:700, margin:0, textShadow: tenant.bannerUrl ? "0 1px 8px rgba(0,0,0,0.4)" : "none"}}>{tenant.tagline}</p>}
          </div>
        </div>
        </E>

        {/* Two-column masonry on tablet+ (≥760px), single column on
            phone. CSS multi-column auto-balances heights so both
            columns end on the same line — no manual splitting. The
            inline <style> below forces every direct child to stay
            whole inside its column (the only reliable way to do this
            via class targeting, since inline styles can't reach
            children). */}
        <style>{`.pb-mcol > * { break-inside: avoid; -webkit-column-break-inside: avoid; page-break-inside: avoid; margin-bottom: 14px; display: block; }`}</style>
        <div className="pb-mcol" style={{padding:"0 20px 32px", maxWidth:980, margin:"0 auto", width:"100%", boxSizing:"border-box", columnCount:bookingCols, columnGap:14}}>

          {/* ── AUTH CARD ──────────────────────────────────────── */}
          <div style={{breakInside:"avoid", WebkitColumnBreakInside:"avoid", marginTop:-40, marginBottom:14, background:"#fff", borderRadius:28, padding:24, boxShadow:"0 16px 40px rgba(0,0,0,0.08)", position:"relative", zIndex:2}}>
            <p style={{fontSize:15, fontWeight:900, color:"#4A4A68", margin:"0 0 4px", textAlign:"center"}}>{i18nT("pub.welcome")}</p>
            <p style={{fontSize:13, color:"#8A8A9E", fontWeight:700, margin:"0 0 20px", textAlign:"center"}}>{i18nT("pub.signupPrompt")}</p>

            {isAuthed && !isOwnerOfThisShop ? (
              // Already signed in (Google return or returning visit) — single CTA.
              <Btn onClick={() => {
                try { localStorage.removeItem("petos:pending-booking"); } catch {}
                if (typeof window !== "undefined" && window.__petosRouteAfterAuth) {
                  window.__petosRouteAfterAuth({ slug: tenant.bookingSlug, tab: "pets" });
                }
              }} style={{width:"100%", padding:"16px", fontSize:15}}>
                {i18nT("pub.continueWith") + (profile.name || profile.email)} →
              </Btn>
            ) : (
              <>
                {/* Email / phone + password right on the landing card — no
                    extra tap through a separate 登入 screen. 註冊 is the
                    toggle under the button. The owner's edit-mode preview
                    keeps the plain buttons (they jump to settings). */}
                {editMode ? (
                  <>
                    <Btn onClick={() => onEditSection("shop-brand")} style={{width:"100%", padding:"16px", fontSize:15, marginBottom:10}}>
                      {i18nT("auth.login")}
                    </Btn>
                    <Btn variant="secondary" onClick={() => onEditSection("shop-brand")} style={{width:"100%", padding:"16px", fontSize:15}}>
                      {i18nT("auth.register")}
                    </Btn>
                  </>
                ) : (
                  <EmailAuthForm onSubmit={handleEmailAuth} loading={loading} error={authErr} defaultRegister={expandedAuth === "email-register"} big />
                )}

                <div style={{display:"flex", alignItems:"center", gap:10, margin:"18px 0 14px"}}>
                  <div style={{flex:1, height:1, background:"var(--c-light)"}} />
                  <span style={{fontSize:11, color:"#AFAFC4", fontWeight:700}}>{i18nT("common.or", curLoc !== "zh-HK" ? "or" : "或")}</span>
                  <div style={{flex:1, height:1, background:"var(--c-light)"}} />
                </div>

                <button onClick={editMode ? () => onEditSection("shop-brand") : handleGoogleAuth} style={{width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"13px 14px", borderRadius:99, background:"#fff", border:"2px solid #E5E7EB", color:"#3C4043", fontSize:14, fontWeight:800, cursor:"pointer"}}>
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.4 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.378 0-9.93-3.057-11.273-7.946l-6.522 5.025C9.5 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.565.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                  {i18nT("auth.googleSignin")}
                </button>
                <button onClick={editMode ? () => onEditSection("shop-brand") : handleAppleAuth} style={{width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"13px 14px", borderRadius:99, background:"#000", border:"2px solid #000", color:"#fff", fontSize:14, fontWeight:800, cursor:"pointer", marginTop:8}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="#fff"><path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.45-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                  {i18nT("pub.appleSignin")}
                </button>
                <p style={{fontSize:10, color:"#AFAFC4", textAlign:"center", margin:"10px 0 0", fontWeight:700, lineHeight:1.5}}>
                  {curLoc !== "zh-HK"
                    ? (<>By continuing you agree to our <a href="#/privacy" style={{color:"var(--c-primary)",textDecoration:"underline"}}>Privacy Policy</a> and <a href="#/terms" style={{color:"var(--c-primary)",textDecoration:"underline"}}>Terms of Service</a></>)
                    : (<>{zhEn("繼續即表示您同意我們的", "By continuing you agree to our ")}<a href="#/privacy" style={{color:"var(--c-primary)",textDecoration:"underline"}}>{zhEn("私隱政策", "Privacy Policy")}</a>{zhEn("及", " and ")}<a href="#/terms" style={{color:"var(--c-primary)",textDecoration:"underline"}}>{zhEn("服務條款", "Terms of Service")}</a></>)}
                </p>
                <p style={{fontSize:11, textAlign:"center", margin:"8px 0 0", fontWeight:800}}>
                  <a href="#/help" style={{color:"var(--c-primary)",textDecoration:"none"}}>📖 {curLoc !== "zh-HK" ? "How to use / FAQ" : zhEn("使用說明 / 常見問題", "How to use / FAQ")}</a>
                </p>
              </>
            )}
          </div>

          {/* All info cards below now flow through the same multi-column
              wrapper as the auth card so heights balance on tablet+.
              Each card sits inside a breakInside:"avoid" sibling so a
              single card never splits across the column gap. */}

          {/* #20 Stamp card progress — shown only to authed customers
              who already have a membership at THIS shop so they can see
              what they're working toward before booking again. */}
          {isAuthed && tenant?.stampCard?.enabled && shopHasStampCard(tenant) && (() => {
            const sc = tenant.stampCard;
            const mem = (state?.memberships || []).find(m => m.userId === profile?.id && m.tenantId === tenant.id);
            if (!mem) return null;
            const total = sc.totalSlots || 10;
            const stamps = Math.min(Number(mem.stamps || 0), total);
            const milestones = [...(sc.milestones || [])].sort((a, b) => (a.atSlot || 0) - (b.atSlot || 0));
            const next = milestones.find(m => (m.atSlot || 0) > stamps);
            return (
              <Card style={{padding:"14px 18px", margin:0, background:"linear-gradient(135deg,#FB923C,#F97316)", border:"none", color:"#fff"}}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6}}>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <span style={{fontSize:18}}>📇</span>
                    <p style={{fontWeight:900, fontSize:14, margin:0}}>{zhEn("我的印花卡", "My stamp card")}</p>
                  </div>
                  <span style={{fontSize:13, fontWeight:900, background:"rgba(255,255,255,0.22)", padding:"2px 10px", borderRadius:99}}>{stamps}/{total}</span>
                </div>
                <div style={{height:5, background:"rgba(255,255,255,0.25)", borderRadius:99, overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${(stamps/total)*100}%`, background:"#fff", borderRadius:99, transition:"width 0.4s ease"}}/>
                </div>
                <p style={{fontSize:11, margin:"6px 0 0", fontWeight:700, opacity:0.95}}>
                  {next ? zhEn(`再 ${(next.atSlot||0) - stamps} 個印花即可換「${next.reward || i18nT("pub.rewards")}」`, `${(next.atSlot||0) - stamps} more stamps to redeem "${next.reward || i18nT("pub.rewards")}"`) : i18nT("pub.maxReward")}
                </p>
              </Card>
            );
          })()}

          {/* #17 First-time booking guide — 4-step roadmap so new visitors
              know what to expect before they tap "登入/註冊". Hidden once
              the user is authed (they've done it before). */}
          {!isAuthed && (
            <Card style={{padding:"16px 20px", margin:0, background:"linear-gradient(135deg, #FEF3C7, #FDE68A)", border:"none"}}>
              <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:10}}>
                <div style={{width:32, height:32, borderRadius:10, background:"rgba(255,255,255,0.8)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>📋</div>
                <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#78350F"}}>{zhEn("首次預約流程", "First-booking steps")}</h3>
              </div>
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                {[
                  ["1", i18nT("pub.loginOrSignup"), i18nT("pub.emailGoogle")],
                  ["2", i18nT("pub.stepPet"), i18nT("pub.multiPet")],
                  ["3", i18nT("pub.stepTime"), i18nT("pub.shopSchedule")],
                  // Hide "付訂金" wording when the shop has disabled deposits.
                  ["4", tenant?.depositPolicy?.enabled === true ? i18nT("pub.confirmDeposit") : i18nT("pub.confirmBtn"), i18nT("pub.bookSuccess")],
                ].map(([n,t,sub]) => (
                  <div key={n} style={{display:"flex", alignItems:"center", gap:10}}>
                    <div style={{width:22, height:22, borderRadius:99, background:"#fff", color:"#78350F", fontSize:12, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>{n}</div>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{fontSize:13, fontWeight:900, color:"#78350F", margin:0}}>{t}</p>
                      <p style={{fontSize:11, fontWeight:700, color:"#92400E", margin:0, opacity:0.85}}>{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ── ABOUT / 店家簡介 ──────────────────────────────────
              Quote-style with brand-colour accent bar on the left and
              a larger first-letter mark. Reads more inviting than a
              flat paragraph. */}
          <E label={zhEn("關於本店", "About the shop")} target="shop-basic">
          {(tenant.about || editMode) && (
            <Card style={{padding:0, margin:0, overflow:"hidden"}}>
              <div style={{display:"flex"}}>
                <div style={{width:5, background:"linear-gradient(180deg, var(--c-grad1), var(--c-grad2))", flexShrink:0}} />
                <div style={{padding:"18px 20px", flex:1}}>
                  <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:8}}>
                    <div style={{width:32, height:32, borderRadius:10, background:"var(--c-lighter)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>🐾</div>
                    <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{i18nT("pub.about")}</h3>
                  </div>
                  <p style={{fontSize:14, color:"#4A4A68", margin:0, fontWeight:500, lineHeight:1.75, whiteSpace:"pre-line", fontStyle:"italic"}}>{tenant.about ? zhEn("「", "\"") + tenant.about + zhEn("」", "\"") : <span style={{color:"#AFAFC4"}}>{zhEn("（按此新增店舖介紹）", "(tap to add a shop introduction)")}</span>}</p>
                </div>
              </div>
            </Card>
          )}
          </E>

          {/* ── STAFF / 美容師團隊 ──────────────────────────────
              Larger avatars (72px) with double-ring style; rating
              moved into a coloured pill. Empty state when only one
              member still feels intentional. */}
          <E label={zhEn("美容師團隊", "Groomers")} target="tab:staff">
          {staffList.length > 0 && (
            <Card style={{padding:"18px 20px", margin:0}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
                <div style={{display:"flex", alignItems:"center", gap:10}}>
                  <div style={{width:32, height:32, borderRadius:10, background:"#FEF3C7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>✂️</div>
                  <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{i18nT("pub.team")}</h3>
                </div>
                <span style={{fontSize:11, fontWeight:800, color:"#AFAFC4"}}>{staffList.length} {zhEn("位", " groomers")}</span>
              </div>
              <div style={{display:"grid", gridTemplateColumns:`repeat(auto-fill, minmax(${staffList.length === 1 ? 140 : 92}px, 1fr))`, gap:14}}>
                {staffList.map(s => (
                  <div key={s.id} style={{display:"flex", flexDirection:"column", alignItems:"center", gap:8, padding:"12px 6px", background:"linear-gradient(180deg, var(--c-lighter) 0%, transparent 100%)", borderRadius:16}}>
                    <div style={{position:"relative"}}>
                      <div style={{width:72, height:72, borderRadius:99, padding:3, background:"linear-gradient(135deg, var(--c-grad1), var(--c-grad2))"}}>
                        <div style={{width:"100%", height:"100%", borderRadius:99, background:s.avatar ? "#fff" : "var(--c-lighter)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", fontSize:30, border:"3px solid #fff"}}>
                          {s.avatar
                            ? <img src={s.avatar} alt={s.name} style={{width:"100%", height:"100%", objectFit:"cover", borderRadius:99}} />
                            : "👤"}
                        </div>
                      </div>
                      {s.rating > 0 && (
                        <span style={{position:"absolute", bottom:-4, right:-4, background:"#F59E0B", color:"#fff", fontSize:10, fontWeight:900, borderRadius:99, padding:"2px 7px", border:"2px solid #fff", boxShadow:"0 2px 6px rgba(245,158,11,0.4)"}}>⭐ {Number(s.rating).toFixed(1)}</span>
                      )}
                    </div>
                    <p style={{fontSize:13, fontWeight:800, color:"#4A4A68", margin:0, textAlign:"center", lineHeight:1.2}}>{s.name}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          </E>

          {/* ── GALLERY / 作品集 ──────────────────────────────────
              Grid of shopGallery thumbnails on the landing page itself,
              so customers see the work before booking. Always rendered
              in editMode so the owner has a tap-target; in customer
              view we hide entirely when no photos exist. */}
          {((tenant.shopGallery || []).length > 0 || editMode) && (
            <E label={zhEn("作品集 / 相片", "Gallery / photos")} target="shop-brand#gallery">
              <Card style={{padding:"18px 20px", margin:0}}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12}}>
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <div style={{width:32, height:32, borderRadius:10, background:"#EDE9FE", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>🖼️</div>
                    <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{zhEn("作品集", "Portfolio")}</h3>
                  </div>
                  {(tenant.shopGallery || []).length > 0 && <span style={{fontSize:11, fontWeight:800, color:"#AFAFC4"}}>{(tenant.shopGallery || []).length} {zhEn("張", "unpaid")}</span>}
                </div>
                {(tenant.shopGallery || []).length === 0 ? (
                  <p style={{fontSize:13, color:"#AFAFC4", margin:0, fontStyle:"italic"}}>{zhEn("（按此上載作品照片）", "(tap to upload gallery photos)")}</p>
                ) : (() => {
                  const all = tenant.shopGallery || [];
                  const shown = all.slice(0, 6);
                  const extra = all.length - shown.length;
                  return (
                    <>
                      <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6}}>
                        {shown.map((g, i) => {
                          const isLast = i === shown.length - 1 && extra > 0;
                          return (
                            <div
                              key={g.id || i}
                              onClick={editMode ? null : (e) => { e.stopPropagation(); setGalLightboxIdx(i); }}
                              style={{aspectRatio:"1", borderRadius:10, overflow:"hidden", background:"var(--c-lighter)", cursor: editMode ? "default" : "pointer", position:"relative"}}
                            >
                              <img src={g.url} alt={g.caption || ("gallery " + (i+1))} loading="lazy" style={{width:"100%", height:"100%", objectFit:"cover"}} />
                              {isLast && (
                                <div style={{position:"absolute", inset:0, background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:18, fontWeight:900}}>
                                  +{extra}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {all.length > 0 && (
                        <button
                          onClick={editMode ? (e) => { e.stopPropagation(); onEditSection("shop-brand#gallery"); } : () => setGalLightboxIdx(0)}
                          style={{marginTop:10, width:"100%", padding:"10px 14px", background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", color:"#fff", border:"none", borderRadius:99, fontSize:13, fontWeight:900, cursor:"pointer", boxShadow:"0 6px 16px var(--c-primary-20)"}}
                        >
                          {zhEn("🖼️ 查看全部 (", "🖼️ View all (")}{all.length}) →
                        </button>
                      )}
                    </>
                  );
                })()}
              </Card>
            </E>
          )}

          {/* ── BOOKING POLICY / 預約政策 ─────────────────────────
              Icon-led rows replacing the dense paragraph copy. Each
              policy gets its own colour to read at a glance. In edit-
              preview mode the card always renders so the owner has a
              tap-target even when no policy is set yet. */}
          <E label={zhEn("取消政策 / 訂金", "Cancellation policy / deposit")} target="cancel-policy">
          {(() => {
            const dp = tenant.depositPolicy || {};
            const cp = tenant.cancellationPolicy || {};
            const hasDeposit = dp.enabled === true && (dp.percent > 0 || dp.fixedAmount > 0);
            const hasCancellation = cp.deadlineHours != null || cp.lateCancelFeePercent != null || cp.noShowFeePercent != null;
            if (!hasDeposit && !hasCancellation && !editMode) return null;
            return (
              <Card style={{padding:"18px 20px", margin:0}}>
                <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
                  <div style={{width:32, height:32, borderRadius:10, background:"#DBEAFE", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>📋</div>
                  <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{zhEn("預約政策", "Booking policy")}</h3>
                </div>
                <div style={{display:"flex", flexDirection:"column", gap:10}}>
                  {hasDeposit && (
                    <div style={{display:"flex", gap:12, alignItems:"flex-start", padding:"12px 14px", background:"linear-gradient(135deg, #FFFBEB, #FEF3C7)", borderRadius:14, border:"1px solid #FDE68A"}}>
                      <div style={{width:34, height:34, borderRadius:10, background:"#F59E0B", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#fff", fontWeight:900, fontSize:18}}>$</div>
                      <div style={{flex:1, minWidth:0}}>
                        <p style={{margin:"0 0 2px", fontSize:13, fontWeight:900, color:"#92400E"}}>{zhEn("訂金", "Deposit")}</p>
                        <p style={{margin:0, fontSize:12, fontWeight:700, color:"#92400E", lineHeight:1.5, opacity:0.9}}>
                          {dp.mode === "fixed" ? zhEn(`每次預約 $${dp.fixedAmount || 0}`, `$${dp.fixedAmount || 0} per booking`) : zhEn(`總額 ${dp.percent || 0}%`, `${dp.percent || 0}% of total`)}
                          {dp.minDeposit > 0 ? zhEn(`　·　最少 $${dp.minDeposit}`, ` · minimum $${dp.minDeposit}`) : ""}
                          {dp.newCustomersOnly ? i18nT("pub.newCustOnly") : ""}
                        </p>
                      </div>
                    </div>
                  )}
                  {hasCancellation && (
                    <div style={{display:"flex", gap:12, alignItems:"flex-start", padding:"12px 14px", background:"linear-gradient(135deg, #FEF2F2, #FEE2E2)", borderRadius:14, border:"1px solid #FECACA"}}>
                      <div style={{width:34, height:34, borderRadius:10, background:"#EF4444", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#fff", fontSize:18}}>⏰</div>
                      <div style={{flex:1, minWidth:0}}>
                        <p style={{margin:"0 0 2px", fontSize:13, fontWeight:900, color:"#991B1B"}}>{zhEn("取消／改期", "Cancel / reschedule")}</p>
                        <p style={{margin:0, fontSize:12, fontWeight:700, color:"#991B1B", lineHeight:1.5, opacity:0.9}}>
                          {zhEn("須預約前", "At least ")} <strong>{cp.deadlineHours ?? 24} {zhEn("小時", "hours")}</strong>
                          {cp.lateCancelFeePercent != null && zhEn(`　·　逾時扣訂金 ${cp.lateCancelFeePercent}%`, ` · late cancellation keeps ${cp.lateCancelFeePercent}% of the deposit`)}
                          {cp.noShowFeePercent != null && zhEn(`　·　失約扣 ${cp.noShowFeePercent}%`, ` · no-show keeps ${cp.noShowFeePercent}%`)}
                        </p>
                      </div>
                    </div>
                  )}
                  {!hasDeposit && !hasCancellation && editMode && (
                    <p style={{fontSize:13, color:"#AFAFC4", margin:0, fontStyle:"italic"}}>{zhEn("（按此設定訂金 / 取消政策）", "(tap to set deposit / cancellation policy)")}</p>
                  )}
                </div>
              </Card>
            );
          })()}
          </E>

          {/* ── BUSINESS HOURS / 營業時間 ──────────────────────── */}
          <E label={zhEn("營業時間", "Opening hours")} target="shop-hours">
          {(tenant.businessHours || editMode) && (() => {
            const bh = tenant.businessHours;
            const days = [i18nT("day.0"), i18nT("day.1"), i18nT("day.2"), i18nT("day.3"), i18nT("day.4"), i18nT("day.5"), i18nT("day.6")];
            const today = new Date().getDay();
            const todayOpen = bh[today]?.enabled;
            return (
              <Card style={{padding:"18px 20px", margin:0}}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12}}>
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <div style={{width:32, height:32, borderRadius:10, background:"#D1FAE5", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>🕒</div>
                    <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{i18nT("pub.hours")}</h3>
                  </div>
                  <span style={{fontSize:11, fontWeight:900, color: todayOpen ? "#10B981" : "#EF4444", background: todayOpen ? "#D1FAE5" : "#FEE2E2", padding:"4px 10px", borderRadius:99}}>
                    {todayOpen ? "● " + i18nT("owner.shopOpen") : "● " + i18nT("pub.closedToday")}
                  </span>
                </div>
                <div style={{display:"flex", flexDirection:"column", gap:2}}>
                  {[1,2,3,4,5,6,0].map(d => {
                    const day = bh[d] || {};
                    const isToday = d === today;
                    return (
                      <div key={d} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", borderRadius:10, background: isToday ? "linear-gradient(90deg, var(--c-grad1), var(--c-grad2))" : "transparent", color: isToday ? "#fff" : undefined}}>
                        <span style={{fontSize:13, fontWeight: isToday ? 900 : 700, color: isToday ? "#fff" : "#4A4A68"}}>
                          {days[d]}{isToday && i18nT("pub.todayOpen")}
                        </span>
                        <span style={{fontSize:13, fontWeight: isToday ? 900 : 700, color: isToday ? "#fff" : (day.enabled ? "#4A4A68" : "#AFAFC4")}}>
                          {day.enabled ? `${day.open || "09:00"} – ${day.close || "18:00"}` : i18nT("pub.holiday")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })()}
          </E>

          {/* ── SHOP INFO / 店家資訊 ────────────────────────────
              Each contact action gets its own coloured icon disc so the
              row reads as a primary CTA, not just a list item. */}
          <E label={zhEn("聯絡方式 / 地址", "Contact / address")} target="shop-basic">
          {(tenant.phone || tenant.address || tenant.website || tenant.instagram || tenant.facebook || editMode) && (
            <Card style={{padding:"18px 20px", margin:0}}>
              <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
                <div style={{width:32, height:32, borderRadius:10, background:"#FCE7F3", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16}}>📍</div>
                <h3 style={{fontWeight:900, fontSize:15, margin:0, color:"#4A4A68"}}>{i18nT("pub.contact")}</h3>
              </div>
              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {tenant.phone && (
                  <a href={"tel:" + tenant.phone} style={{display:"flex", alignItems:"center", gap:12, padding:"10px 12px", background:"#fff", border:"1.5px solid var(--c-border)", borderRadius:14, textDecoration:"none", color:"#4A4A68", transition:"all 0.2s"}}>
                    <div style={{width:36, height:36, borderRadius:10, background:"linear-gradient(135deg, #10B981, #059669)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                      <Phone size={16} color="#fff" strokeWidth={2.8} />
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{margin:0, fontSize:11, fontWeight:700, color:"#8A8A9E"}}>{zhEn("電話", "Phone")}</p>
                      <p style={{margin:0, fontSize:14, fontWeight:900, color:"#4A4A68"}}>{tenant.phone}</p>
                    </div>
                    <span style={{fontSize:12, color:"#10B981", fontWeight:900}}>{zhEn("致電 →", "Call →")}</span>
                  </a>
                )}
                {tenant.address && (
                  <a href={"https://maps.google.com/?q=" + encodeURIComponent(tenant.address)} target="_blank" rel="noopener noreferrer" style={{display:"flex", alignItems:"flex-start", gap:12, padding:"10px 12px", background:"#fff", border:"1.5px solid var(--c-border)", borderRadius:14, textDecoration:"none", color:"#4A4A68"}}>
                    <div style={{width:36, height:36, borderRadius:10, background:"linear-gradient(135deg, #F43F5E, #BE123C)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                      <MapPin size={16} color="#fff" strokeWidth={2.8} />
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{margin:0, fontSize:11, fontWeight:700, color:"#8A8A9E"}}>{zhEn("地址", "Address")}</p>
                      <p style={{margin:0, fontSize:13, fontWeight:700, color:"#4A4A68", lineHeight:1.4}}>{tenant.address}</p>
                    </div>
                    <span style={{fontSize:12, color:"#F43F5E", fontWeight:900, whiteSpace:"nowrap"}}>{zhEn("地圖 →", "Map →")}</span>
                  </a>
                )}
                {tenant.website && (
                  <a href={tenant.website.startsWith("http") ? tenant.website : "https://" + tenant.website} target="_blank" rel="noopener noreferrer" style={{display:"flex", alignItems:"center", gap:12, padding:"10px 12px", background:"#fff", border:"1.5px solid var(--c-border)", borderRadius:14, textDecoration:"none", color:"#4A4A68"}}>
                    <div style={{width:36, height:36, borderRadius:10, background:"linear-gradient(135deg, #3B82F6, #1D4ED8)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                      <Globe size={16} color="#fff" strokeWidth={2.8} />
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{margin:0, fontSize:11, fontWeight:700, color:"#8A8A9E"}}>{zhEn("網站", "Website")}</p>
                      <p style={{margin:0, fontSize:13, fontWeight:800, color:"#4A4A68", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{tenant.website.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span style={{fontSize:12, color:"#3B82F6", fontWeight:900}}>{zhEn("開啟 →", "Open →")}</span>
                  </a>
                )}
                {(tenant.instagram || tenant.facebook) && (
                  <div style={{display:"flex", gap:8, marginTop:2}}>
                    {tenant.instagram && (
                      <a href={tenant.instagram.startsWith("http") ? tenant.instagram : "https://instagram.com/" + tenant.instagram.replace(/^@/, "")} target="_blank" rel="noopener noreferrer" style={{flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"12px", background:"linear-gradient(135deg, #FFD541, #F9418E, #B5439A)", borderRadius:14, textDecoration:"none", color:"#fff", fontSize:13, fontWeight:900, boxShadow:"0 4px 12px rgba(249,65,142,0.25)"}}>
                        <span style={{fontSize:16}}>📷</span> Instagram
                      </a>
                    )}
                    {tenant.facebook && (
                      <a href={tenant.facebook.startsWith("http") ? tenant.facebook : "https://facebook.com/" + tenant.facebook} target="_blank" rel="noopener noreferrer" style={{flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"12px", background:"linear-gradient(135deg, #1877F2, #1457b8)", borderRadius:14, textDecoration:"none", color:"#fff", fontSize:13, fontWeight:900, boxShadow:"0 4px 12px rgba(24,119,242,0.25)"}}>
                        <span style={{fontSize:16, fontWeight:900}}>ƒ</span> Facebook
                      </a>
                    )}
                  </div>
                )}
                {!tenant.phone && !tenant.address && !tenant.website && !tenant.instagram && !tenant.facebook && editMode && (
                  <p style={{fontSize:13, color:"#AFAFC4", margin:0, fontStyle:"italic"}}>{zhEn("（按此填寫地址 / 電話 / 社交媒體）", "(tap to add address / phone / social media)")}</p>
                )}
              </div>
            </Card>
          )}
          </E>

        </div>
        {/* end multi-column wrapper */}

        {/* Footer — sits below the grid, full-width but text centered. */}
        <div style={{padding:"6px 20px 32px", maxWidth:980, margin:"0 auto", width:"100%", boxSizing:"border-box"}}>
          {(() => {
            if (tenantHidesBranding(tenant)) return <div style={{height:6}} />;
            const pb = state.platformConfig?.poweredByText ?? "Powered by PettoBook";
            return pb ? <p style={{fontSize:11, color:"#AFAFC4", fontWeight:700, textAlign:"center", margin:"6px 0 6px"}}>{pb}</p> : <div style={{height:6}} />;
          })()}
          <p style={{fontSize:11, color:"#AFAFC4", fontWeight:700, textAlign:"center", margin:0}}>
            <a href="#/privacy" style={{color:"#AFAFC4", textDecoration:"none"}}>{zhEn("私隱政策", "Privacy Policy")}</a>
            {" · "}
            <a href="#/terms" style={{color:"#AFAFC4", textDecoration:"none"}}>{zhEn("服務條款", "Terms of Service")}</a>
          </p>
        </div>
        {/* Gallery lightbox — fullscreen photo viewer with prev/next +
            close. Closed when galLightboxIdx is null. Tap backdrop or
            ✕ to dismiss; tap left/right halves of the screen to flip. */}
        {galLightboxIdx != null && galPhotos[galLightboxIdx] && (
          <div onClick={closeGalLightbox} style={{position:"fixed", inset:0, zIndex:9500, background:"rgba(0,0,0,0.94)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:16, cursor:"zoom-out"}}>
            <button onClick={(e) => { e.stopPropagation(); closeGalLightbox(); }} style={{position:"absolute", top:18, right:18, background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff", borderRadius:99, padding:"6px 14px", fontSize:14, fontWeight:900, cursor:"pointer", backdropFilter:"blur(8px)"}}>✕</button>
            <span style={{position:"absolute", top:22, left:18, color:"#fff", fontSize:13, fontWeight:800, background:"rgba(255,255,255,0.15)", padding:"4px 12px", borderRadius:99, backdropFilter:"blur(8px)"}}>{galLightboxIdx + 1} / {galPhotos.length}</span>
            <img src={galPhotos[galLightboxIdx].url} alt="" onClick={(e) => e.stopPropagation()} style={{maxWidth:"100%", maxHeight:"82vh", objectFit:"contain", borderRadius:8, boxShadow:"0 24px 60px rgba(0,0,0,0.5)"}} />
            {galPhotos.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prevGalPhoto(); }} style={{position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff", borderRadius:99, width:44, height:44, fontSize:20, fontWeight:900, cursor:"pointer", backdropFilter:"blur(8px)"}}>‹</button>
                <button onClick={(e) => { e.stopPropagation(); nextGalPhoto(); }} style={{position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff", borderRadius:99, width:44, height:44, fontSize:20, fontWeight:900, cursor:"pointer", backdropFilter:"blur(8px)"}}>›</button>
              </>
            )}
            {galPhotos[galLightboxIdx].caption && (
              <p style={{position:"absolute", bottom:24, left:"50%", transform:"translateX(-50%)", color:"#fff", fontSize:13, fontWeight:700, background:"rgba(0,0,0,0.5)", padding:"6px 14px", borderRadius:99, maxWidth:"80%", textAlign:"center"}}>{galPhotos[galLightboxIdx].caption}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── AUTH: Identify the customer first
  if (step === "auth") {
    // When the customer clicked 登入 / 註冊 on the landing page we know
    // their intent — go straight to the email form, with Google as a
    // secondary option below. The old accordion-of-options page was an
    // unnecessary stop. When `expandedAuth` is unset (e.g. they hit a
    // booking URL that requires auth without picking a method first), we
    // still default to the login form since that's the common case.
    const wantsRegister = expandedAuth === "email-register";
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:480, margin:"0 auto"}}>
        <button onClick={() => setStep("landing")} style={{background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#8A8A9E", fontSize:14, fontWeight:700, padding:0, marginBottom:16}}>
          <ChevronLeft size={18}/> {i18nT("common.back")}
        </button>
        <h2 style={{fontSize:24, fontWeight:900, color:"#4A4A68", margin:"0 0 6px"}}>{wantsRegister ? zhEn("註冊帳戶", "Create account") : zhEn("登入", "Log in")}</h2>
        <p style={{fontSize:13, color:"#8A8A9E", fontWeight:700, margin:"0 0 20px"}}>{wantsRegister ? zhEn("建立新帳戶以開始預約", "Create an account to start booking") : zhEn("登入繼續預約", "Log in to continue booking")}</p>

        {/* Already logged in — fast-path "continue as X" */}
        {isAuthed && !isOwnerOfThisShop && (
          <div style={{background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))", borderRadius:16, padding:16, marginBottom:16, color:"#fff"}}>
            <p style={{fontSize:12, fontWeight:800, opacity:0.85, margin:"0 0 4px"}}>{zhEn("已登入", "Logged in")}</p>
            <p style={{fontSize:15, fontWeight:900, margin:"0 0 10px"}}>{profile.name || profile.email}</p>
            <button onClick={() => {
              try { localStorage.removeItem("petos:pending-booking"); } catch {}
              if (typeof window !== "undefined" && window.__petosRouteAfterAuth) {
                window.__petosRouteAfterAuth({ slug: tenant.bookingSlug, tab: "pets" });
              }
            }} style={{width:"100%", padding:"10px", background:"#fff", color:"var(--c-primary)", border:"none", borderRadius:10, fontWeight:900, fontSize:14, cursor:"pointer"}}>
              {zhEn("繼續預約 →", "Continue booking →")}
            </button>
          </div>
        )}

        {isOwnerOfThisShop && (
          <div style={{background:"#FEF3C7", borderRadius:12, padding:16, marginBottom:12, border:"2px solid #F59E0B"}}>
            <p style={{fontSize:13, fontWeight:900, color:"#92400E", margin:"0 0 4px"}}>{zhEn("⚠ 您是本店店主", "⚠ You are the owner of this shop")}</p>
            <p style={{fontSize:12, color:"#92400E", fontWeight:700, margin:0, lineHeight:1.5}}>{zhEn("無法在自己的店家以客戶身份預約。請以其他帳戶登入測試預約流程。", "You can't book as a customer at your own shop. Log in with another account to test the booking flow.")}</p>
          </div>
        )}

        {/* Email form — directly visible (no accordion). Primary auth method. */}
        {!isAuthed && (
          <div style={{background:"#fff", borderRadius:16, padding:18, marginBottom:14, boxShadow:"0 4px 12px rgba(0,0,0,0.04)"}}>
            <EmailAuthForm onSubmit={handleEmailAuth} loading={loading} error={authErr} defaultRegister={wantsRegister} />
          </div>
        )}

        {/* OR divider + Google as secondary option */}
        {!isAuthed && (
          <>
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 14px"}}>
              <div style={{flex:1,height:1,background:"var(--c-border)"}} />
              <span style={{fontSize:12,color:"#AFAFC4",fontWeight:700}}>{zhEn("或", "or")}</span>
              <div style={{flex:1,height:1,background:"var(--c-border)"}} />
            </div>
            <button onClick={handleGoogleAuth} style={{width:"100%", padding:"14px", background:"#fff", color:"#4A4A68", border:"2px solid var(--c-border)", borderRadius:14, fontWeight:800, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10}}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.4 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.378 0-9.93-3.057-11.273-7.946l-6.522 5.025C9.5 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.565.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
              {i18nT("auth.googleSignin")}
            </button>
          </>
        )}

        </div>
      </div>
    );
  }

  // ── SERVICE: Select main service + SPA + addons
  if (step === "service") {
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
        {authBanner}
        <button onClick={() => setStep("petinfo")} style={{background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#8A8A9E", fontSize:14, fontWeight:700, padding:0, marginBottom:16}}>
          <ChevronLeft size={18}/> {i18nT("common.back")}
        </button>
        <h2 style={{fontSize:22, fontWeight:900, color:"#4A4A68", margin:"0 0 16px"}}>{i18nT("booking.selectService")}</h2>

        {/* 在店 / 上門 mode picker — only for 兩者 shops. Switching to 上門 clears
            a chosen service if it isn't 上門-available. */}
        {tenant?.serviceMode === "both" && (
          <div style={{marginBottom:18}}>
            <h4 style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:"0 0 10px"}}>{T("服務方式","Service type")} <span style={{color:"var(--c-primary)"}}>*</span></h4>
            <div style={{display:"flex", gap:8}}>
              {[[false,"🏠 "+T("到店","In-shop")],[true,"🚗 "+T("上門","Home visit")]].map(([val,label]) => {
                const on = !!booking.isMobile === val;
                return (
                  <button key={String(val)} onClick={() => { const keep = booking.serviceId && (!val || mainServices.find(s => s.id === booking.serviceId)?.mobileAvailable); saveBooking({ isMobile: val, serviceId: keep ? booking.serviceId : "" }); setErr(""); }} style={{flex:1, padding:"12px", borderRadius:14, border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"), background:on?"var(--c-primary)":"#fff", color:on?"#fff":"#4A4A68", fontWeight:800, fontSize:14, cursor:"pointer"}}>{label}</button>
                );
              })}
            </div>
          </div>
        )}
        {shopMobileOnly && <div style={{marginBottom:16, background:"#EDE9FE", border:"2px solid #DDD6FE", borderRadius:14, padding:"10px 14px", fontSize:13, fontWeight:800, color:"#5B21B6"}}>🚗 {T("此店為上門服務，美容師會到你嘅地址","Home-visit shop — the groomer comes to your address")}</div>}

        {/* Main service (required) */}
        <div style={{marginBottom:20}}>
          <h4 style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:"0 0 12px"}}>{T("主要服務","Main service")} <span style={{color:"var(--c-primary)"}}>*</span></h4>
          {visibleMainServices.length === 0 ? (
            <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:20, textAlign:"center", color:"#8A8A9E", fontSize:13, fontWeight:700}}>
              {wantMobile ? T("本店暫未有可上門嘅服務，請聯絡店家或揀「到店」。","No home-visit services available — please contact the shop or choose In-shop.") : T("本店暫無公開服務，請聯絡店家。","No public services yet — please contact the shop.")}
            </div>
          ) : (
            <div style={{display:"grid", gridTemplateColumns:"1fr", gap:10}}>
              {visibleMainServices.map(s => (
                <PublicServiceCard
                  key={s.id}
                  svc={s}
                  tenant={tenant}
                  pet={petForPricing}
                  mobile={wantMobile}
                  selected={booking.serviceId === s.id}
                  multi={false}
                  onToggle={() => { setErr(""); saveBooking({serviceId: s.id, basePrice: s.basePrice || 0}); }}
                />
              ))}
            </div>
          )}
        </div>

        {/* SPA services (optional) */}
        {visibleSpaServices.length > 0 && (
          <div style={{marginBottom:20}}>
            <h4 style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:"0 0 12px"}}>{zhEn("水療服務（可選）", "Hydrotherapy (optional)")}</h4>
            <div style={{display:"grid", gridTemplateColumns:"1fr", gap:10}}>
              {visibleSpaServices.map(s => (
                <PublicServiceCard
                  key={s.id}
                  svc={s}
                  tenant={tenant}
                  pet={petForPricing}
                  mobile={wantMobile}
                  selected={(booking.spaIds || []).includes(s.id)}
                  multi
                  onToggle={() => {
                    const newSpaIds = (booking.spaIds || []).includes(s.id)
                      ? (booking.spaIds || []).filter(id => id !== s.id)
                      : [...(booking.spaIds || []), s.id];
                    saveBooking({spaIds: newSpaIds});
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Addons (optional) */}
        {addonsList.length > 0 && (
          <div style={{marginBottom:20}}>
            <h4 style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:"0 0 12px"}}>{zhEn("額外服務（可選）", "Extra services (optional)")}</h4>
            <div style={{display:"grid", gridTemplateColumns:"1fr", gap:10}}>
              {addonsList.map(a => (
                <PublicAddonCard
                  key={a.id}
                  addon={a}
                  pet={petForPricing}
                  selected={(booking.addons || []).includes(a.id)}
                  onToggle={() => {
                    const newAddons = (booking.addons || []).includes(a.id)
                      ? (booking.addons || []).filter(id => id !== a.id)
                      : [...(booking.addons || []), a.id];
                    saveBooking({addons: newAddons});
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <Btn onClick={() => {
          if (!booking.serviceId) { setErr(i18nT("pub.pickSvc")); return; }
          setErr("");
          // Default to "any groomer" if no staff list; otherwise the combined
          // schedule step lets the user pick from staff pills + date + time.
          if (staffList.length === 0) saveBooking({ staffId: null });
          setStep("schedule");
        }} style={{width:"100%", padding:"16px"}}>
          {i18nT("btn.next")} →
        </Btn>
        {err && <p style={{color:"#FF4D6D", fontSize:13, margin:"10px 0 0", fontWeight:700, textAlign:"center"}}>{err}</p>}
        </div>
      </div>
    );
  }

  // ── SCHEDULE: Combined groomer + date + time picker
  if (step === "schedule" || step === "groomer" || step === "datetime") {
    // Real available time slots: business hours × service duration − conflicts.
    const slots = booking.date
      ? generateTimeSlots(booking.date, booking.staffId || null, booking.serviceId, tenant, state.appointments || [], booking.staffId ? state.users?.[booking.staffId] : null, staffList.filter(u => staffCanDo(u, booking.serviceId)), pubBusySlots)
      : [];

    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
        {authBanner}
        <button onClick={() => setStep("service")} style={{background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#8A8A9E", fontSize:14, fontWeight:700, padding:0, marginBottom:16}}>
          <ChevronLeft size={18}/> {i18nT("common.back")}
        </button>
        <h2 style={{fontSize:22, fontWeight:900, color:"#4A4A68", margin:"0 0 16px"}}>{i18nT("book.pickStaff") + " · " + i18nT("book.pickTime")}</h2>

        {/* Groomer section — collapsed pills when staff exist */}
        {staffList.length > 0 && (
          <div style={{marginBottom:16}}>
            <h4 style={{fontSize:13, fontWeight:800, color:"#4A4A68", margin:"0 0 10px"}}>{zhEn("美容師", "Groomer")}</h4>
            <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:6, scrollbarWidth:"none"}}>
              {/* Any groomer */}
              <button
                onClick={() => saveBooking({staffId: null})}
                style={{
                  flex:"0 0 auto", display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                  padding:"8px 4px", minWidth:80, borderRadius:12, border: !booking.staffId ? "2px solid var(--c-primary)" : "2px solid transparent",
                  background: !booking.staffId ? "var(--c-lighter)" : "rgba(255,255,255,0.7)", cursor:"pointer",
                }}
              >
                <div style={{width:48, height:48, background:"var(--c-lighter)", borderRadius:99, display:"flex", alignItems:"center", justifyContent:"center"}}>
                  <Users size={20} color="var(--c-primary)" />
                </div>
                <p style={{fontSize:11, fontWeight:800, color:"#4A4A68", margin:0}}>{zhEn("任何", "Any")}</p>
              </button>
              {/* Named staff */}
              {staffList.map(u => (
                <button
                  key={u.id}
                  onClick={() => saveBooking({staffId: u.id})}
                  style={{
                    flex:"0 0 auto", display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                    padding:"8px 4px", minWidth:80, borderRadius:12, border: booking.staffId === u.id ? "2px solid var(--c-primary)" : "2px solid transparent",
                    background: booking.staffId === u.id ? "var(--c-lighter)" : "rgba(255,255,255,0.7)", cursor:"pointer",
                  }}
                >
                  <div style={{width:48, height:48, background:"var(--c-lighter)", borderRadius:99, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden"}}>
                    {u.avatar ? <img src={u.avatar} alt={u.name} style={{width:"100%", height:"100%", objectFit:"cover"}} /> : <PawPrint size={22} color="var(--c-primary)" />}
                  </div>
                  <p style={{fontSize:11, fontWeight:800, color:"#4A4A68", margin:0, maxWidth:72, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{u.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Compact date strip (with optional month popout) */}
        <div style={{background:"rgba(255,255,255,0.7)", borderRadius:16, padding:14, marginBottom:16}}>
          <DateStripPicker
            value={booking.date || ""}
            onChange={(d) => { setErr(""); saveBooking({ date: d, time: "" }); }}
            tenant={tenant}
            appointments={state.appointments || []}
            daysAhead={21}
          />
        </div>

        {/* Time slots — only after a date is picked */}
        {booking.date && (
          <div ref={slotsRef}>
            <h4 style={{fontSize:13, fontWeight:800, color:"#4A4A68", margin:"0 0 10px"}}>{zhEn("時間", "Time")} <span style={{color:"var(--c-primary)"}}>*</span></h4>
            {slots.length === 0 ? (
              <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:20, textAlign:"center", color:"#8A8A9E", fontSize:13, fontWeight:700, marginBottom:20}}>
                {zhEn("本日休息 · 請選擇其他日期", "Closed today · please choose another date")}
              </div>
            ) : (
              <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(72px, 1fr))", gap:6, marginBottom:20}}>
                {slots.map(s => (
                  <button
                    key={s.time}
                    onClick={() => { if (s.available) { setErr(""); saveBooking({time: s.time}); } }}
                    disabled={!s.available}
                    style={{
                      padding:"10px 4px", borderRadius:10, border:"none",
                      background: booking.time === s.time ? "var(--c-primary)" : s.available ? "rgba(255,255,255,0.7)" : "var(--c-light)",
                      color: booking.time === s.time ? "#fff" : s.available ? "#4A4A68" : "#C5C5D2",
                      fontWeight:800, fontSize:13,
                      cursor: s.available ? "pointer" : "not-allowed",
                      textDecoration: s.available ? "none" : "line-through",
                    }}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <Btn onClick={() => { if (!booking.date) { setErr(i18nT("pub.pickDate")); return; } if (!booking.time) { setErr(i18nT("pub.pickTime")); return; } setErr(""); setStep("confirm"); }} style={{width:"100%", padding:"16px"}}>
          {i18nT("btn.next")} →
        </Btn>
        {err && <p style={{color:"#FF4D6D", fontSize:13, margin:"10px 0 0", fontWeight:700, textAlign:"center"}}>{err}</p>}
        </div>
      </div>
    );
  }

  // ── PETINFO: Pet details (after auth, before service so prices can be exact)
  if (step === "petinfo") {
    const petTypes = [["dog",i18nT("pub.dog")],["cat",i18nT("pub.cat")],["rabbit",i18nT("pub.rabbit")],["hamster",i18nT("pub.hamster")],["other",i18nT("pub.otherPet")]];
    // Returning-customer pet picker — pull pets from the loaded profile.
    const myPets = isAuthed
      ? (state.users?.[profile.id]?.pets || []).map(pid => state.pets?.[pid]).filter(Boolean)
      : [];
    const hasExistingPets = myPets.length > 0;
    // "addNew" mode: user wants to add a fresh pet rather than pick one.
    const addingNew = !!booking._addingNewPet;
    const showForm = !hasExistingPets || addingNew || !booking.petId; // no saved picks → show form

    const pickPet = (pet) => {
      saveBooking({
        petId: pet.id,
        petName: pet.name,
        petType: pet.type || "dog",
        petBreed: pet.breed || "",
        petBreedId: pet.breedId || inferBreedId(pet.breed, pet.type) || null,
        petWeight: pet.weight ? String(pet.weight) : "",
        coatLength: pet.coatLength || "short",
        _addingNewPet: false,
      });
    };

    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
        {authBanner}
        <button onClick={() => setStep("landing")} style={{background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#8A8A9E", fontSize:14, fontWeight:700, padding:0, marginBottom:16}}>
          <ChevronLeft size={18}/> {i18nT("common.back")}
        </button>
        <h2 style={{fontSize:22, fontWeight:900, color:"#4A4A68", margin:"0 0 8px"}}>{i18nT("booking.selectPet")}</h2>
        <p style={{fontSize:13, color:"#8A8A9E", fontWeight:700, margin:"0 0 16px"}}>{hasExistingPets ? i18nT("pub.choosePet") : i18nT("pub.fillForPrice")}</p>

        {/* Existing-pet picker */}
        {hasExistingPets && (
          <div style={{marginBottom:16}}>
            <div style={{display:"grid", gridTemplateColumns:"1fr", gap:8}}>
              {myPets.map(p => (
                <label key={p.id} style={{display:"flex", alignItems:"center", gap:12, padding:12, background:"rgba(255,255,255,0.7)", borderRadius:12, cursor:"pointer", border: (booking.petId === p.id && !addingNew) ? "2px solid var(--c-primary)" : "2px solid transparent"}}>
                  <input type="radio" name="pet" checked={booking.petId === p.id && !addingNew} onChange={() => pickPet(p)} style={{width:20, height:20, accentColor:"var(--c-primary)"}} />
                  <div style={{width:44, height:44, background:"var(--c-lighter)", borderRadius:99, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:22}}>
                    {p.type === "cat" ? "🐈" : p.type === "rabbit" ? "🐇" : "🐕"}
                  </div>
                  <div style={{flex:1, minWidth:0}}>
                    <p style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:0, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                      {p.name}
                      {/* #30 Signature-required badge: warn the customer
                          upfront if their pet is 10+ so they're not blindsided
                          at checkout when the signature pad appears. */}
                      {petAge(p.birthday) >= seniorAgeFor(tenant) && (
                        <span title={i18nT("pub.elderRequired")} style={{fontSize:9, fontWeight:900, padding:"2px 6px", borderRadius:99, background:"#FEF2F2", color:"#991B1B", border:"1px solid #FECACA"}}>
                          ✍️ {i18nT("cust.elderSign")}
                        </span>
                      )}
                      {/* #19 Last-visit hint: returning customers see when
                          their pet last groomed here so they can judge
                          timing (typical 4-8 week cycle). */}
                      {(() => {
                        const past = (state?.appointments || []).filter(a => a.petId === p.id && a.tenantId === tenant.id && a.status === "done").sort((a,b) => (b.date||"").localeCompare(a.date||""));
                        if (past.length === 0) return null;
                        const last = past[0];
                        const days = Math.floor((Date.now() - new Date(last.date).getTime()) / 86400000);
                        if (days < 0 || days > 365) return null;
                        return <span style={{fontSize:9, fontWeight:800, padding:"2px 6px", borderRadius:99, background:"#EFF6FF", color:"#1E40AF"}}>{i18nT("pub.lastVisit") + days + i18nT("pub.daysAgo")}</span>;
                      })()}
                    </p>
                    <p style={{fontSize:11, color:"#8A8A9E", margin:"3px 0 0", fontWeight:700}}>{p.breed || "—"} · {p.weight || "?"}kg · {p.coatLength === "long" ? i18nT("pub.coatLong") : p.coatLength === "medium" ? i18nT("pub.coatMed") : i18nT("pub.coatShort")}</p>
                  </div>
                </label>
              ))}
              <button onClick={() => saveBooking({_addingNewPet: true, petId: null, petName: "", petBreed: "", petWeight: "", coatLength: "short"})} style={{display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:12, background:"transparent", border:"2px dashed var(--c-border)", borderRadius:12, cursor:"pointer", fontWeight:800, fontSize:13, color:"var(--c-primary)"}}>
                <Plus size={16} strokeWidth={3}/> {i18nT("book.addPet")}
              </button>
            </div>
          </div>
        )}

        {/* New-pet form (shown when no existing pets, or user clicked "add new") */}
        {showForm && (
          <div style={{background:"rgba(255,255,255,0.7)", borderRadius:16, padding:16, display:"flex", flexDirection:"column", gap:12}}>
            {hasExistingPets && (
              <p style={{fontSize:12, fontWeight:800, color:"var(--c-primary)", margin:0}}>{zhEn("新毛孩資料", "New pet details")}</p>
            )}
            <label style={{display:"block"}}>
              <p style={{fontSize:13, fontWeight:700, color:"#4A4A68", margin:"0 0 6px"}}>{i18nT("pet.name")} <span style={{color:"var(--c-primary)"}}>*</span></p>
              <Input placeholder={i18nT("pub.namePh")} value={booking.petName || ""} onChange={e => saveBooking({petName: e.target.value})} />
            </label>

            <label style={{display:"block"}}>
              <p style={{fontSize:13, fontWeight:700, color:"#4A4A68", margin:"0 0 6px"}}>{i18nT("pet.type")} <span style={{color:"var(--c-primary)"}}>*</span></p>
              <select value={booking.petType || "dog"} onChange={e => saveBooking({petType: e.target.value})} style={{width:"100%", padding:"12px 14px", borderRadius:12, border:"2px solid var(--c-border)", fontSize:13, fontWeight:700, background:"#fff"}}>
                {petTypes.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>

            <div>
              <p style={{fontSize:13, fontWeight:700, color:"#4A4A68", margin:"0 0 6px"}}>{i18nT("pet.breed")} <span style={{color:"var(--c-primary)"}}>*</span></p>
              <BreedCombobox
                type={booking.petType || "dog"}
                lang={tenant?.lang || "zh-HK"}
                value={booking.petBreedId}
                valueLabel={booking.petBreed}
                onChange={({ breedId, breed, coatHint }) =>
                  saveBooking({ petBreedId: breedId, petBreed: breed, coatLength: booking.coatLength || coatHint || "short" })
                }
              />
            </div>

            <label style={{display:"block"}}>
              <p style={{fontSize:13, fontWeight:700, color:"#4A4A68", margin:"0 0 6px"}}>{i18nT("pet.weightKg")} <span style={{color:"var(--c-primary)"}}>*</span></p>
              <Input type="number" step="0.1" placeholder={i18nT("pub.weightPh")} value={booking.petWeight || ""} onChange={e => saveBooking({petWeight: e.target.value})} />
              {petWeightWarn(booking.petWeight, booking.petType) && <p style={{fontSize:12, color:"#B45309", margin:"6px 0 0", fontWeight:700}}>{petWeightWarn(booking.petWeight, booking.petType)}</p>}
              {/* #18 Weight price hint — let the customer know weight
                  drives matrix pricing so they take care entering it.
                  Suppressed if pricing is static (all-pets-same-price). */}
              <p style={{fontSize:11, color:"#8A8A9E", margin:"4px 0 0", fontWeight:700, lineHeight:1.5}}>
                {zhEn("💡 體重會影響美容價格（依體型分級計價），請填寫準確數字", "💡 Weight affects the grooming price (priced by size tier), so please enter an accurate number")}
              </p>
            </label>

            {/* Coat length is set automatically from the breed (coatHint) — the
                customer can't pick "short" to get a cheaper price. The shop
                confirms/adjusts the coat at drop-off. */}
          </div>
        )}

        <Btn onClick={() => {
          if (!booking.petName?.trim()) { setErr(zhEn("請選擇或填寫毛孩", "Please choose or enter a pet")); return; }
          if (!booking.petBreed?.trim()) { setErr(zhEn("請輸入品種", "Please enter a breed")); return; }
          if (!booking.petWeight || parseFloat(booking.petWeight) <= 0) { setErr(zhEn("請輸入重量", "Please enter a weight")); return; }
          setErr("");
          setStep("service");
        }} style={{width:"100%", padding:"16px", marginTop:16}}>
          {i18nT("btn.next")} →
        </Btn>
        {err && <p style={{color:"#FF4D6D", fontSize:13, margin:"10px 0 0", fontWeight:700, textAlign:"center"}}>{err}</p>}
        </div>
      </div>
    );
  }

  // ── CONFIRM: Review summary and submit the booking
  if (step === "confirm") {
    const petForPricing = {
      weight: parseFloat(booking.petWeight) || 0,
      breed: booking.petBreed || "",
      coatLength: booking.coatLength || "short",
      type: booking.petType || "dog",
    };
    const service = (tenant?.services || []).find(s => s.id === booking.serviceId);
    const spaItems = (booking.spaIds || []).map(id => (tenant?.services || []).find(s => s.id === id)).filter(Boolean);
    const addonItems = (booking.addons || []).map(id => (tenant?.addons || []).find(a => a.id === id)).filter(Boolean);
    const staff = booking.staffId ? state.users?.[booking.staffId] : null;
    const svcPd = service ? getPricingDetail(petForPricing, service, tenant, { mobile: wantMobile }) : { total: 0 };
    const spaTotal = spaItems.reduce((s, sp) => s + getPricingDetail(petForPricing, sp, tenant, { mobile: wantMobile }).total, 0);
    const addonTotal = addonItems.reduce((s, a) => s + computeAddonPrice(a, petForPricing).price, 0);
    // 面議 — if the main service (or any selected SPA) prices "by consultation",
    // show 面議 instead of a number and take no deposit (price settled in person).
    const quoteLbl = i18nGetLocale() !== "zh-HK" ? "Quote" : "面議";
    const hasQuote = !!svcPd.quote || spaItems.some(sp => getPricingDetail(petForPricing, sp, tenant, { mobile: wantMobile }).quote);
    const subtotal = (svcPd.total || 0) + spaTotal + addonTotal;
    // Promo discount (applied to subtotal before tax)
    const promoDiscount = Number(booking.promoDiscount) || 0;
    const subtotalAfterDiscount = Math.max(0, subtotal - promoDiscount);
    // Tax computed per tenant's config (inclusive or exclusive). When the shop
    // has tax disabled, taxAmount is 0 and totalIncTax === subtotalAfterDiscount.
    const tax = calcTax(subtotalAfterDiscount, tenant);
    const grandTotal = tax.totalIncTax;
    // Deposit applies to the post-tax total. No deposit on a 面議 booking.
    const isReturningCustomer = isAuthed && (state.appointments || []).some(a => a.customerId === profile?.id && a.tenantId === tenant.id);
    const depositAmount = hasQuote ? 0 : calcDeposit(grandTotal, tenant, isReturningCustomer);
    const taxConfig = tenant?.taxConfig || { enabled: false };
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
        {authBanner}
        <button onClick={() => setStep("schedule")} style={{background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:"#8A8A9E", fontSize:14, fontWeight:700, padding:0, marginBottom:16}}>
          <ChevronLeft size={18}/> {i18nT("common.back")}
        </button>
        <h2 style={{fontSize:22, fontWeight:900, color:"#4A4A68", margin:"0 0 16px"}}>{i18nT("book.confirm")}</h2>

        <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:12}}>
          <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("📅 日期時間", "📅 Date & time")}</p>
          <p style={{fontSize:16, fontWeight:900, color:"#4A4A68", margin:0}}>{booking.date} {booking.time}</p>
        </div>

        {service && (
          <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:12}}>
            <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 8px"}}>{zhEn("✂️ 服務", "✂️ Service")}</p>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline"}}>
              <p style={{fontSize:15, fontWeight:900, color:"#4A4A68", margin:0}}>{service.name}</p>
              <p style={{fontSize:15, fontWeight:900, color:"var(--c-primary)", margin:0}}>{svcPd.quote ? quoteLbl : "$"+svcPd.total}</p>
            </div>
            {spaItems.map(sp => {
              const spd = getPricingDetail(petForPricing, sp, tenant, { mobile: wantMobile });
              return <div key={sp.id} style={{display:"flex", justifyContent:"space-between", marginTop:6, fontSize:13, color:"#6A6A85", fontWeight:700}}><span>+ {sp.name}</span><span>{spd.quote ? quoteLbl : "$"+spd.total}</span></div>;
            })}
            {addonItems.map(a => {
              const p = computeAddonPrice(a, petForPricing).price;
              return <div key={a.id} style={{display:"flex", justifyContent:"space-between", marginTop:6, fontSize:13, color:"#6A6A85", fontWeight:700}}><span>+ {a.name}</span><span>${p}</span></div>;
            })}
            {/* Promo code redemption — collapses to a single line until applied */}
            <PromoCodeRow
              tenant={tenant}
              subtotal={subtotal}
              isReturning={isReturningCustomer}
              customerId={profile?.id}
              appliedCode={booking.promoCode || ""}
              appliedDiscount={promoDiscount}
              onApplied={({ code, discount, promoId }) => saveBooking({ promoCode: code, promoDiscount: discount, promoId })}
              onCleared={() => saveBooking({ promoCode: "", promoDiscount: 0, promoId: null })}
            />

            <div style={{height:8}} />
            <div style={{display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:"1px solid rgba(74,74,104,0.1)", fontSize:13, color:"#6A6A85", fontWeight:700}}>
              <span>{zhEn("小計", "Subtotal")}</span><span>{hasQuote ? (subtotal > 0 ? "$"+subtotal+" + "+quoteLbl : quoteLbl) : "$"+subtotal}</span>
            </div>
            {promoDiscount > 0 && <div style={{display:"flex", justifyContent:"space-between", marginTop:4, fontSize:13, color:"#10B981", fontWeight:800}}>
              <span>{zhEn("折扣","Discount")} ({booking.promoCode})</span><span>− ${promoDiscount}</span>
            </div>}
            {taxConfig.enabled && tax.taxAmount > 0 && <div style={{display:"flex", justifyContent:"space-between", marginTop:4, fontSize:13, color:"#6A6A85", fontWeight:700}}>
              <span>{taxConfig.label || zhEn("稅", "Tax")} ({taxConfig.rate}%{taxConfig.mode === "inclusive" ? zhEn(" 內含", " included") : ""})</span><span>${tax.taxAmount}</span>
            </div>}
            <div style={{display:"flex", justifyContent:"space-between", marginTop:8, fontSize:15, fontWeight:900, color:"#4A4A68"}}>
              <span>{zhEn("總計", "Total")}</span><span style={{color:"var(--c-primary)"}}>{hasQuote ? (grandTotal > 0 ? "$"+grandTotal+" + "+quoteLbl : quoteLbl) : "$"+grandTotal}</span>
            </div>
            {hasQuote && <p style={{fontSize:11, color:"#92400E", background:"#FEF3C7", padding:"6px 10px", borderRadius:8, fontWeight:700, margin:"8px 0 0"}}>{i18nGetLocale()!== "zh-HK" ? "Some items are priced on consultation. The final price is confirmed by the shop." : zhEn("部分項目為面議，最終價格由店家確認。", "Some items are priced on consultation. The shop confirms the final price.")}</p>}
            {depositAmount > 0 && (
              <div style={{display:"flex", justifyContent:"space-between", marginTop:8, fontSize:12, color:"#92400E", background:"#FEF3C7", padding:"6px 10px", borderRadius:8, fontWeight:700}}>
                <span>{zhEn("📌 訂金（店家現場收取）", "📌 Deposit (collected at the shop)")}</span>
                <span>${depositAmount}</span>
              </div>
            )}
          </div>
        )}

        {staff && (
          <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:12}}>
            <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("👤 美容師", "👤 Groomer")}</p>
            <p style={{fontSize:15, fontWeight:900, color:"#4A4A68", margin:0}}>{staff.name}</p>
          </div>
        )}

        <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:12}}>
          <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("🐾 毛孩", "🐾 Pet")}</p>
          <p style={{fontSize:15, fontWeight:900, color:"#4A4A68", margin:0}}>{booking.petName}</p>
          <p style={{fontSize:12, color:"#6A6A85", margin:"4px 0 0", fontWeight:700}}>{booking.petBreed} · {booking.petWeight}kg · {booking.coatLength === "long" ? zhEn("長毛", "Long coat") : booking.coatLength === "medium" ? zhEn("中毛", "Medium coat") : zhEn("短毛", "Short coat")}</p>
        </div>

        <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:24}}>
          <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 10px"}}>{zhEn("📞 聯絡資料", "📞 Contact details")}</p>
          <Input placeholder={zhEn("名字", "Name")} value={booking.guestName || ""} onChange={e => saveBooking({guestName: e.target.value})} />
          <div style={{height:8}} />
          <Input placeholder={zhEn("電話（例 9123 4567）", "Phone (e.g. 9123 4567)")} type="tel" inputMode="tel" value={booking.guestPhone || ""} onChange={e => saveBooking({guestPhone: parseCopiedPhone(e.target.value).digits})} />
          <p style={{fontSize:10, color:"#AFAFC4", margin:"6px 0 0", fontWeight:700}}>{zhEn("店家將透過此電話聯絡你確認預約 · 預設 +852（香港）", "The shop will contact you on this number to confirm · default +852 (Hong Kong)")}</p>
        </div>

        {/* 上門 — collect the address the groomer travels to + door/parking notes. */}
        {wantMobile && (
          <div style={{background:"#F5F3FF", border:"2px solid #DDD6FE", borderRadius:12, padding:16, marginBottom:24}}>
            <p style={{fontSize:11, fontWeight:800, color:"#5B21B6", margin:"0 0 10px"}}>🚗 {T("上門地址","Home-visit address")} <span style={{color:"#FF4D6D"}}>*</span></p>
            <textarea placeholder={T("完整地址（大廈 / 街道 / 樓層 / 單位）","Full address (building / street / floor / unit)")} value={booking.serviceAddress || ""} onChange={e => saveBooking({ serviceAddress: e.target.value })} style={{width:"100%", minHeight:64, padding:"10px 12px", borderRadius:10, border:"2px solid var(--c-border)", fontSize:14, fontWeight:600, fontFamily:"inherit", resize:"vertical", outline:"none", boxSizing:"border-box"}} />
            <div style={{height:8}} />
            <Input placeholder={T("門鈴 / 泊車 / 特別指示（可選）","Doorbell / parking / special notes (optional)")} value={booking.serviceNotes || ""} onChange={e => saveBooking({ serviceNotes: e.target.value })} />
            <p style={{fontSize:10, color:"#8A8A9E", margin:"6px 0 0", fontWeight:700}}>{T("美容師會根據此地址上門","The groomer will visit this address")}</p>
          </div>
        )}

        <Btn onClick={() => {
          const name = (booking.guestName || "").trim();
          if (!name) { setErr(T("請輸入名字","Please enter your name")); return; }
          const check = validatePhone("+852", booking.guestPhone || "");
          if (!check.valid) { setErr(check.error); return; }
          if (wantMobile && !(booking.serviceAddress || "").trim()) { setErr(T("上門服務請填寫地址","Please enter the home-visit address")); return; }
          saveBooking({ guestPhone: check.e164 });
          setErr("");
          submitBooking();
        }} disabled={loading} style={{width:"100%", padding:"16px"}}>
          {loading ? <><Spinner /> {zhEn("提交中…", "Submitting…")}</> : (isAuthed ? i18nT("pub.review") : T("登入／註冊並確認預約 →","Sign in / register to confirm →","ログイン／登録して予約"))}
        </Btn>
        {!isAuthed && <p style={{fontSize:11, color:"#8A8A9E", fontWeight:700, textAlign:"center", margin:"8px 0 0"}}>{T("差最後一步：註冊即完成預約","One step left — register to finish","あと一歩")}</p>}
        {err && <p style={{color:"#FF4D6D", fontSize:13, margin:"10px 0 0", fontWeight:700, textAlign:"center"}}>{err}</p>}
        </div>
      </div>
    );
  }

  // ── DONE: Success page after submission
  if (step === "done") {
    return (
      <div style={{...themeVars, minHeight:"100vh", background:"var(--c-light)", padding:20}}>
        <div style={{maxWidth:680, margin:"0 auto"}}>
          <div style={{textAlign:"center", marginTop:40, marginBottom:24}}>
            <div style={{width:80, height:80, background:"var(--c-primary)", borderRadius:99, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:"0 8px 24px var(--c-primary-40)"}}>
              <Check size={42} color="#fff" strokeWidth={3} />
            </div>
            <h2 style={{fontSize:26, fontWeight:900, color:"#4A4A68", margin:"0 0 6px"}}>{testMode ? T("測試完成 ✅","Test complete ✅") : i18nT("pub.confirmed")}</h2>
            <p style={{fontSize:14, color:"#8A8A9E", fontWeight:700, margin:0}}>{testMode ? T("這是測試模式，未建立真實預約。客戶實際會見到呢個流程。","Test mode — no real booking was created. This is exactly what your customers see.") : zhEn("店家會盡快確認您的預約，並透過短訊或電話聯絡您", "The shop will confirm your booking soon and contact you by SMS or phone")}</p>
          </div>

          <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, padding:16, marginBottom:12}}>
            <p style={{fontSize:11, fontWeight:700, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("📅 預約時間", "📅 Booking time")}</p>
            <p style={{fontSize:16, fontWeight:900, color:"#4A4A68", margin:0}}>{booking.date} {booking.time}</p>
          </div>

          <button onClick={() => {
            try { localStorage.removeItem("petos:pending-booking"); } catch {}
            setBooking({});
            setStep("landing");
          }} style={{width:"100%", padding:"14px", marginTop:16, background:"transparent", border:"2px solid var(--c-primary)", color:"var(--c-primary)", borderRadius:12, fontWeight:800, cursor:"pointer", fontSize:14}}>
            {zhEn("返回店家頁面", "Back to shop page")}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// ── EMAIL AUTH FORM ────────────────────────────────────────────────────────
// ── PROMO CODE ROW ─────────────────────────────────────────────────────────
// Compact inline input on the confirm step. Calls validatePromoCode (which
// hits the DB) and reports back to the parent via onApplied/onCleared.
const PromoCodeRow = ({ tenant, subtotal, isReturning, customerId, appliedCode, appliedDiscount, onApplied, onCleared }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true); setErr("");
    try {
      const res = await validatePromoCode({ tenantId: tenant.id, code, subtotal, isReturning, customerId });
      if (!res.valid) {
        const reasons = {
          not_found: zhEn("優惠碼不存在", "Promo code not found"),
          expired: zhEn("優惠碼已過期", "Promo code has expired"),
          not_yet_active: zhEn("優惠碼尚未生效", "Promo code isn't active yet"),
          exhausted: zhEn("優惠碼已用完", "Promo code has been used up"),
          new_customers_only: zhEn("此優惠碼僅限新客戶", "This promo code is for new customers only"),
          per_customer_limit: zhEn("您已使用過此優惠碼", "You've already used this promo code"),
          min_subtotal: zhEn("未達最低消費 $", "Minimum spend not reached: $") + (res.minSubtotal || 0),
          empty: zhEn("請輸入優惠碼", "Please enter a promo code"),
        };
        setErr(reasons[res.reason] || zhEn("優惠碼無效", "Invalid promo code"));
        return;
      }
      onApplied({ code: res.promo.code, discount: res.discount, promoId: res.promo.id });
      setCode(""); setOpen(false);
    } catch (e) {
      setErr(e?.message || zhEn("驗證失敗", "Verification failed"));
    } finally { setBusy(false); }
  };

  // Already applied state — show pill + remove button
  if (appliedCode) {
    return (
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10, padding:"8px 12px", background:"#ECFDF5", borderRadius:10, border:"1px solid #A7F3D0"}}>
        <span style={{fontSize:13, fontWeight:800, color:"#047857"}}>🎁 {appliedCode} (-${appliedDiscount})</span>
        <button onClick={onCleared} style={{background:"transparent", border:"none", color:"#047857", fontSize:12, fontWeight:800, cursor:"pointer", padding:"2px 6px"}}>{zhEn("移除", "Remove")}</button>
      </div>
    );
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{display:"flex", alignItems:"center", gap:6, marginTop:10, background:"transparent", border:"none", color:"var(--c-primary)", fontSize:12, fontWeight:800, cursor:"pointer", padding:"4px 0"}}>
        {zhEn("🎁 有優惠碼？", "🎁 Have a promo code?")}
      </button>
    );
  }
  return (
    <div style={{marginTop:10}}>
      <div style={{display:"flex", gap:6}}>
        <Input placeholder={zhEn("輸入優惠碼", "Enter promo code")} value={code} onChange={e => { setCode(e.target.value.toUpperCase()); setErr(""); }} onKeyDown={e => e.key === "Enter" && apply()} style={{flex:1}} />
        <Btn onClick={apply} disabled={busy || !code.trim()} size="sm">{busy ? zhEn("驗證…", "Checking…") : zhEn("套用", "Apply")}</Btn>
      </div>
      {err && <p style={{color:"#FF4D6D", fontSize:11, margin:"6px 0 0", fontWeight:700}}>{err}</p>}
    </div>
  );
};

const EmailAuthForm = ({ onSubmit, loading, error, defaultRegister = false, big = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRegister, setIsRegister] = useState(!!defaultRegister);
  const submit = () => {
    if (!email || !password) return;
    onSubmit(email, password, isRegister);
  };
  return (
    <>
      {/* Login also takes a phone number: shop-created customers have no email
          of their own and sign in with the phone + password the shop sent them
          (handleEmailAuth maps it to the shop's synthetic login). */}
      {/* Spacing on a wrapper: a style prop on <Input> replaces its whole look. */}
      <div style={{marginBottom:8}}><Input type={isRegister ? "email" : "text"} autoComplete="username" placeholder={isRegister ? "Email" : zhEn("Email 或電話號碼", "Email or phone number")} value={email} onChange={e => setEmail(e.target.value)} /></div>
      <div style={{marginBottom:8}}><Input type="password" autoComplete={isRegister ? "new-password" : "current-password"} placeholder={zhEn("密碼", "Password")} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submit(); }} /></div>
      {error && <p style={{color:"#FF4D6D", fontSize:12, margin:"8px 0", fontWeight:700}}>{error}</p>}
      <Btn onClick={submit} disabled={loading || !email || !password} style={{width:"100%", padding:big ? "16px" : "10px", fontSize:big ? 15 : 13, marginBottom:8}}>
        {loading ? zhEn("處理中…", "Processing…") : isRegister ? zhEn("建立帳戶", "Create account") : zhEn("登入", "Log in")}
      </Btn>
      <button onClick={() => setIsRegister(!isRegister)} style={{width:"100%", padding:"8px", background:"transparent", border:"none", color:"#8A8A9E", fontSize:12, fontWeight:700, cursor:"pointer"}}>
        {isRegister ? zhEn("已有帳戶？登入", "Have an account? Log in") : zhEn("沒有帳戶？註冊", "No account? Sign up")}
      </button>
    </>
  );
};

// ── PUBLIC SERVICE CARD ────────────────────────────────────────────────────
// Renders one service row on the public booking page. When `pet` info is
// available (weight, breed, coat), the headline price is the exact computed
// price for that pet. Without pet info, falls back to the matrix range.
const PublicServiceCard = ({ svc, tenant, pet, selected, multi, onToggle, mobile = false }) => {
  const [showMatrix, setShowMatrix] = useState(false);
  const mp = svc.matrixPricing;
  const isMatrix = (svc.pricingMode === "matrix" || !!mp) && mp;
  const sizes = (mp?.sizes || []).filter(r => r && (r.shortHair || r.longHair));
  const specialBreeds = (mp?.specialBreeds || []).filter(b => b && (b.name || b.breedId));

  // When we know the pet, compute the exact price for it. Otherwise show a range.
  const hasPet = pet && pet.weight > 0;
  let priceHead = svc.basePrice > 0 ? `$${svc.basePrice}` : "";
  let priceSub = "";
  let rawPriceHead = ""; // strike-through original when a campaign is active
  let adjustmentBadge = null;
  const quoteLbl = i18nGetLocale() !== "zh-HK" ? "Quote" : "面議";
  if (hasPet) {
    const pd = getPricingDetail(pet, svc, tenant, { mobile });
    if (pd.quote) {
      // 面議 — price varies (breed/coat); no single number is accurate.
      priceHead = quoteLbl;
    } else {
      priceHead = `$${pd.total}`;
      if (pd.adjustment && pd.rawTotal && pd.rawTotal !== pd.total) {
        rawPriceHead = `$${pd.rawTotal}`;
        adjustmentBadge = pd.adjustment;
      }
    }
    if (pd.baseLabel && isMatrix) priceSub = pd.baseLabel.replace(/^(主服務|Main service)\s*\(?|\)?$/g, "");
  } else if (isMatrix) {
    const all = [];
    sizes.forEach(r => { if (r.shortHair) all.push(Number(r.shortHair)); if (r.longHair) all.push(Number(r.longHair)); });
    specialBreeds.forEach(b => {
      (b.sizes || []).forEach(r => { if (r.shortHair) all.push(Number(r.shortHair)); if (r.longHair) all.push(Number(r.longHair)); });
      if (b.prices?.short) all.push(Number(b.prices.short));
      if (b.prices?.long) all.push(Number(b.prices.long));
    });
    // Shared-tier model: prices live in byTier (skip 不適用 rows; pick 上門 price when mobile).
    let hasQuote = false;
    if (mp?.byTier && typeof mp.byTier === "object") {
      Object.values(mp.byTier).forEach(e => {
        if (!e || e.na) return;
        if (e.quote) { hasQuote = true; return; }
        const v = mobile && Number(e.mobile) > 0 ? Number(e.mobile) : (Number(e.short) || Number(e.long) || 0);
        if (v > 0) all.push(v);
      });
    }
    if (mp?.byBreed && typeof mp.byBreed === "object") {
      Object.values(mp.byBreed).forEach(sub => sub && Object.values(sub).forEach(e => { if (e && !e.na && e.quote) hasQuote = true; }));
    }
    if (svc.basePrice) all.push(Number(svc.basePrice));
    const valid = all.filter(n => !isNaN(n) && n > 0);
    if (valid.length) {
      const min = Math.min(...valid), max = Math.max(...valid);
      priceHead = min === max ? `$${min}` : `$${min} – $${max}`;
      if (hasQuote) priceHead += " / " + quoteLbl;
    } else if (hasQuote) {
      priceHead = quoteLbl;
    }
  }

  return (
    <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, border: selected ? "2px solid var(--c-primary)" : "2px solid transparent", overflow:"hidden"}}>
      <label style={{display:"flex", alignItems:"flex-start", gap:12, padding:14, cursor:"pointer"}}>
        <input type={multi ? "checkbox" : "radio"} name={multi ? undefined : "svc"} checked={selected} onChange={onToggle} style={{width:20, height:20, accentColor:"var(--c-primary)", marginTop:2}} />
        <div style={{flex:1, minWidth:0}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8}}>
            <p style={{fontSize:14, fontWeight:800, color:"#4A4A68", margin:0}}>{svc.name}</p>
            <div style={{textAlign:"right"}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                {rawPriceHead && <span style={{fontSize:11,fontWeight:700,color:"#9CA3AF",textDecoration:"line-through",whiteSpace:"nowrap"}}>{rawPriceHead}</span>}
                {priceHead && <span style={{fontSize:14, fontWeight:900, color:"var(--c-primary)", whiteSpace:"nowrap"}}>{priceHead}</span>}
                {adjustmentBadge && (
                  <span style={{fontSize:9,fontWeight:900,color:"#fff",background: adjustmentBadge.percent >= 0 ? "#F59E0B" : "#10B981",borderRadius:99,padding:"1px 6px",whiteSpace:"nowrap"}}>
                    {adjustmentBadge.label || (adjustmentBadge.percent >= 0 ? zhEn("節日加價", "Holiday surcharge") : zhEn("限時優惠", "Limited-time offer"))} {adjustmentBadge.percent >= 0 ? "+" : ""}{adjustmentBadge.percent}%
                  </span>
                )}
              </div>
              {priceSub && <p style={{fontSize:10, color:"#8A8A9E", margin:"2px 0 0", fontWeight:700, whiteSpace:"nowrap"}}>{priceSub}</p>}
            </div>
          </div>
          {svc.description && <p style={{fontSize:12, color:"#8A8A9E", margin:"4px 0 0", fontWeight:600, lineHeight:1.5}}>{svc.description}</p>}
          {(svc.estimatedMinutes || svc.bufferMinutes) > 0 && (
            <p style={{fontSize:11, color:"#AFAFC4", margin:"4px 0 0", fontWeight:700}}>{zhEn("⏱ 約", "⏱ About")} {svc.estimatedMinutes || 60} {zhEn("分鐘", "min")}</p>
          )}
          {isMatrix && (sizes.length > 0 || specialBreeds.length > 0) && (
            <button type="button" onClick={(e) => { e.preventDefault(); setShowMatrix(v => !v); }} style={{background:"transparent", border:"none", color:"var(--c-primary)", fontSize:11, fontWeight:800, cursor:"pointer", padding:"6px 0 0", display:"flex", alignItems:"center", gap:4}}>
              {showMatrix ? zhEn("收起價目表", "Hide price list") : zhEn("查看詳細價目", "View detailed prices")} <ChevronDown size={12} style={{transform: showMatrix ? "rotate(180deg)" : "", transition:"transform 0.2s"}}/>
            </button>
          )}
        </div>
      </label>

      {isMatrix && showMatrix && (sizes.length > 0 || specialBreeds.length > 0) && (
        <div style={{padding:"0 14px 14px", borderTop:"1px solid rgba(74,74,104,0.08)"}}>
          {sizes.length > 0 && (
            <div style={{marginTop:10}}>
              <p style={{fontSize:11, fontWeight:800, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("一般體型", "Standard sizes")}</p>
              <div style={{display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:4, background:"#fff", borderRadius:8, padding:8, fontSize:11}}>
                <div style={{fontWeight:800, color:"#8A8A9E"}}>{zhEn("體型", "Size")}</div>
                <div style={{fontWeight:800, color:"#8A8A9E", textAlign:"right"}}>{zhEn("短毛", "Short coat")}</div>
                <div style={{fontWeight:800, color:"#8A8A9E", textAlign:"right"}}>{zhEn("長毛", "Long coat")}</div>
                {sizes.map((r, i) => (
                  <Fragment key={i}>
                    <div style={{color:"#4A4A68", fontWeight:700}}>{r.label || `${r.minW}-${r.maxW}kg`}</div>
                    <div style={{textAlign:"right", color:"#4A4A68", fontWeight:800}}>{r.shortHair ? `$${r.shortHair}` : "—"}</div>
                    <div style={{textAlign:"right", color:"#4A4A68", fontWeight:800}}>{r.longHair ? `$${r.longHair}` : "—"}</div>
                  </Fragment>
                ))}
              </div>
              {(() => {
                // Customer-facing surcharge hint. Only show when enabled +
                // not a SPA / add-on (those don't apply hugeExtra at compute
                // time). baseW is auto-derived from the LAST tier's maxW
                // so the hint matches the compute logic exactly.
                const he = mp.hugeExtra;
                if (!he || svc.kind === "spa" || svc.kind === "addon") return null;
                if (he.enabled === false) return null;
                if (!(Number(he.stepPrice) > 0)) return null;
                const _sorted = [...(mp.sizes || [])].sort((a, b) => (Number(a.minW) || 0) - (Number(b.minW) || 0));
                const _last = _sorted[_sorted.length - 1];
                const _autoBaseW = _last ? Number(_last.maxW) : Number(he.baseW) || 0;
                if (!_autoBaseW) return null;
                return (
                  <p style={{fontSize:10, color:"#8A8A9E", margin:"6px 0 0", fontWeight:700}}>{zhEn("※ 超過", "※ Over ")} {_autoBaseW}{zhEn("kg，每", "kg, every ")} {he.perStep || 5}{zhEn("kg 加 $", "kg adds $")}{he.stepPrice}</p>
                );
              })()}
            </div>
          )}

          {specialBreeds.length > 0 && (
            <div style={{marginTop:10}}>
              <p style={{fontSize:11, fontWeight:800, color:"#8A8A9E", margin:"0 0 6px"}}>{zhEn("特殊品種", "Special breeds")}</p>
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                {specialBreeds.map((b, i) => {
                  // Multi-breed display: prefer the names[] array, fall back
                  // to the single legacy name.
                  const breedNames = (Array.isArray(b.names) && b.names.length ? b.names : [b.name])
                    .filter(Boolean);
                  const breedLabel = breedNames.length
                    ? breedNames.join(" / ")
                    : ((tenant?.specialBreeds || []).find(t => t.id === b.breedId)?.name || "—");
                  return (
                    <div key={i} style={{background:"#fff", borderRadius:8, padding:8, fontSize:11}}>
                      <p style={{fontWeight:800, color:"#4A4A68", margin:"0 0 4px"}}>{breedLabel}</p>
                      {Array.isArray(b.sizes) && b.sizes.length > 0 ? (
                        <div style={{display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:4}}>
                          {b.sizes.map((r, j) => (
                            <Fragment key={j}>
                              <div style={{color:"#6A6A85"}}>{r.label || `${r.minW}-${r.maxW}kg`}</div>
                              <div style={{textAlign:"right", fontWeight:700}}>{r.shortHair ? `$${r.shortHair}` : "—"}</div>
                              <div style={{textAlign:"right", fontWeight:700}}>{r.longHair ? `$${r.longHair}` : "—"}</div>
                            </Fragment>
                          ))}
                        </div>
                      ) : (
                        <div style={{display:"flex", justifyContent:"space-between", color:"#4A4A68", fontWeight:700}}>
                          <span>{zhEn("短毛 $", "Short $")}{b.prices?.short || "—"}</span>
                          <span>{zhEn("長毛 $", "Long $")}{b.prices?.long || "—"}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── PUBLIC ADDON CARD ──────────────────────────────────────────────────────
const PublicAddonCard = ({ addon, pet, selected, onToggle }) => {
  const [showVariants, setShowVariants] = useState(false);
  const variants = addon.pricingMode === "variants" && Array.isArray(addon.variants) ? addon.variants.filter(v => Number(v.price) > 0) : [];
  const hasPet = pet && pet.weight > 0;

  let priceHead = addon.price > 0 ? `$${addon.price}` : "";
  let priceSub = "";
  if (hasPet && variants.length > 0) {
    const ap = computeAddonPrice(addon, pet);
    priceHead = `$${ap.price}`;
    if (ap.variantLabel) priceSub = ap.variantLabel;
  } else if (variants.length > 0) {
    const prices = variants.map(v => Number(v.price) || 0).filter(n => n > 0);
    if (prices.length) {
      const min = Math.min(...prices), max = Math.max(...prices);
      priceHead = min === max ? `$${min}` : `$${min} – $${max}`;
    }
  }

  return (
    <div style={{background:"rgba(255,255,255,0.7)", borderRadius:12, border: selected ? "2px solid var(--c-primary)" : "2px solid transparent", overflow:"hidden"}}>
      <label style={{display:"flex", alignItems:"flex-start", gap:12, padding:12, cursor:"pointer"}}>
        <input type="checkbox" checked={selected} onChange={onToggle} style={{width:20, height:20, accentColor:"var(--c-primary)", marginTop:2}} />
        <div style={{flex:1, minWidth:0}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8}}>
            <p style={{fontSize:13, fontWeight:800, color:"#4A4A68", margin:0}}>{addon.name}</p>
            <div style={{textAlign:"right"}}>
              {priceHead && <span style={{fontSize:13, fontWeight:900, color:"var(--c-primary)", whiteSpace:"nowrap"}}>{priceHead}</span>}
              {priceSub && <p style={{fontSize:10, color:"#8A8A9E", margin:"2px 0 0", fontWeight:700, whiteSpace:"nowrap"}}>{priceSub}</p>}
            </div>
          </div>
          {variants.length > 0 && (
            <button type="button" onClick={(e) => { e.preventDefault(); setShowVariants(v => !v); }} style={{background:"transparent", border:"none", color:"var(--c-primary)", fontSize:11, fontWeight:800, cursor:"pointer", padding:"6px 0 0", display:"flex", alignItems:"center", gap:4}}>
              {showVariants ? zhEn("收起", "Hide") : zhEn("查看價目", "View prices")} <ChevronDown size={12} style={{transform: showVariants ? "rotate(180deg)" : "", transition:"transform 0.2s"}}/>
            </button>
          )}
        </div>
      </label>

      {variants.length > 0 && showVariants && (
        <div style={{padding:"0 12px 12px", borderTop:"1px solid rgba(74,74,104,0.08)"}}>
          <div style={{marginTop:8, display:"grid", gridTemplateColumns:"2fr 1fr", gap:4, background:"#fff", borderRadius:8, padding:8, fontSize:11}}>
            {variants.map((v, i) => (
              <Fragment key={i}>
                <div style={{color:"#4A4A68", fontWeight:700}}>{v.label || `${v.minW || 0}-${v.maxW || 999}kg`}</div>
                <div style={{textAlign:"right", color:"#4A4A68", fontWeight:800}}>${v.price}</div>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ── LOGIN (Supabase Auth: email + password) ────────────────────────────────
// ── Forgot-password screen — sends a Supabase magic recovery email ─────────
const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    setErr(""); if (!email) { setErr(zhEn("請輸入 Email", "Please enter your email")); return; }
    setLoading(true);
    try { await sendPasswordReset(email.trim()); setSent(true); }
    catch (e) { setErr(e?.message || zhEn("發送失敗，請稍後再試", "Failed to send, please try again later")); }
    finally { setLoading(false); }
  };
  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:80,height:80,background:"#fff",borderRadius:32,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",boxShadow:"0 16px 40px var(--c-primary-40)"}}><PawPrint size={40} color="var(--c-primary)" strokeWidth={2.5} /></div>
          <h1 style={{fontSize:28,fontWeight:900,color:"#fff",margin:"0 0 6px"}}>{zhEn("忘記密碼", "Forgot password")}</h1>
          <p style={{color:"rgba(255,255,255,0.9)",fontSize:14,margin:0,fontWeight:700}}>{zhEn("輸入您的 Email，我們會寄重設連結給您", "Enter your email and we'll send you a reset link")}</p>
        </div>
        <div style={{background:"rgba(255,255,255,0.9)",backdropFilter:"blur(24px)",borderRadius:40,padding:28,boxShadow:"0 20px 60px var(--c-primary-30)"}}>
          {sent ? (
            <>
              <div style={{textAlign:"center",fontSize:42,marginBottom:10}}>📧</div>
              <p style={{textAlign:"center",fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("已寄出重設密碼信件", "Password reset email sent")}</p>
              <p style={{textAlign:"center",fontSize:13,color:"#8A8A9E",fontWeight:700,margin:"0 0 18px"}}>{zhEn("請查看", "Check the inbox of ")} {email} {zhEn("的收件匣（包含垃圾郵件夾），按下信內連結即可設定新密碼", " (including spam) and tap the link in the email to set a new password")}</p>
              <Btn onClick={onBack} style={{width:"100%"}}>{zhEn("返回登入", "Back to login")}</Btn>
            </>
          ) : (
            <>
              <Input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")submit();}} />
              {err && <p style={{color:"#FF4D6D",fontSize:13,marginTop:10,textAlign:"center",fontWeight:700}}>{err}</p>}
              <Btn onClick={submit} disabled={loading||!email} style={{width:"100%",padding:"14px",marginTop:14}}>{loading?zhEn("寄送中…", "Sending…"):zhEn("寄送重設連結", "Send reset link")}</Btn>
              <button onClick={onBack} style={{width:"100%",marginTop:10,background:"transparent",border:"none",color:"#8A8A9E",fontSize:13,fontWeight:800,cursor:"pointer"}}>{zhEn("← 返回登入", "← Back to login")}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Reset-password screen — shown after the user clicks the email link ─────
const ResetPassword = ({ onDone }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setErr("");
    if (!pw || pw.length < 6) { setErr(zhEn("密碼至少 6 字元", "Password must be at least 6 characters")); return; }
    if (pw !== pw2) { setErr(zhEn("兩次密碼不一致", "Passwords don't match")); return; }
    setLoading(true);
    try {
      await updatePassword(pw);
      try { await supabaseDirect.auth.signOut(); } catch {}
      toast(zhEn("密碼已更新，請重新登入", "Password updated, please log in again"));
      window.location.hash = "";
      onDone?.();
    } catch (e) { setErr(e?.message || zhEn("重設失敗", "Reset failed")); }
    finally { setLoading(false); }
  };
  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:80,height:80,background:"#fff",borderRadius:32,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",boxShadow:"0 16px 40px var(--c-primary-40)"}}><PawPrint size={40} color="var(--c-primary)" strokeWidth={2.5} /></div>
          <h1 style={{fontSize:28,fontWeight:900,color:"#fff",margin:"0 0 6px"}}>{zhEn("設定新密碼", "Set a new password")}</h1>
        </div>
        <div style={{background:"rgba(255,255,255,0.9)",backdropFilter:"blur(24px)",borderRadius:40,padding:28,boxShadow:"0 20px 60px var(--c-primary-30)"}}>
          <Input type="password" placeholder={zhEn("新密碼（至少 6 字元）", "New password (at least 6 characters)")} value={pw} onChange={e=>setPw(e.target.value)} />
          <div style={{height:10}}/>
          <Input type="password" placeholder={zhEn("再次輸入新密碼", "Re-enter new password")} value={pw2} onChange={e=>setPw2(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")submit();}} />
          {err && <p style={{color:"#FF4D6D",fontSize:13,marginTop:10,textAlign:"center",fontWeight:700}}>{err}</p>}
          <Btn onClick={submit} disabled={loading||!pw||!pw2} style={{width:"100%",padding:"14px",marginTop:14}}>{loading?zhEn("更新中…", "Updating…"):zhEn("確認更新", "Confirm")}</Btn>
        </div>
      </div>
    </div>
  );
};

// ── MARKETING HOME ─────────────────────────────────────────────────────────
// Public landing page shown at `/` when no one is logged in. Sells the
// product to shop owners: hero with screenshot mockup, feature cards,
// 3-step "how it works", pricing comparison, and CTAs that funnel to
// login / shop-registration.
// PhoneMockup — pure-CSS iPhone frame that hosts six pre-built screens
// keyed by `variant`. We mirror the live app's owner shell exactly: the
// brand gradient (`var(--c-grad1) → var(--c-primary)`), the hero pet
// card pattern, the rounded card stack, the same emoji set, the same
// font weights — so a visitor sees what they'll actually use after
// signing in, not an idealised mockup. Localised via i18nT so toggling
// 繁中 / EN in the nav also flips the mockup labels.
const PhoneMockup = ({ variant = "schedule" }) => {
  const loc = i18nGetLocale();
  const frame = {
    width:"100%", maxWidth:340, margin:"0 auto", aspectRatio:"9 / 19",
    background:"#0F172A", borderRadius:42, padding:10,
    boxShadow:"0 36px 72px rgba(255,107,139,0.28), 0 14px 28px rgba(31,41,55,0.22), inset 0 0 0 1px rgba(255,255,255,0.05)",
    position:"relative", transform:"rotate(-1deg)",
  };
  const notch = {
    position:"absolute", top:18, left:"50%", transform:"translateX(-50%)",
    width:104, height:24, background:"#000", borderRadius:99, zIndex:3,
    boxShadow:"inset 0 1px 2px rgba(255,255,255,0.06)",
  };
  const screen = {
    width:"100%", height:"100%", borderRadius:32, background:"#FCFCFF",
    overflow:"hidden", display:"flex", flexDirection:"column",
    fontSize:11, color:"#4A4A68", position:"relative",
  };
  const statusBar = (
    <div style={{height:42,display:"flex",alignItems:"flex-end",justifyContent:"space-between",padding:"0 22px 6px",fontSize:11,fontWeight:900,color:"#1F2937"}}>
      <span>{loc !== "zh-HK" ? "9:41" : "9:41"}</span>
      <span style={{display:"inline-flex",gap:4,alignItems:"center",fontSize:9}}>
        <span>●●●●●</span><span>5G</span><span style={{display:"inline-block",width:18,height:8,borderRadius:2,border:"1.2px solid #1F2937",position:"relative"}}><span style={{position:"absolute",inset:1,width:11,background:"#1F2937",borderRadius:1}} /></span>
      </span>
    </div>
  );
  // Hero band mirrors the OwnerApp header (gradient pill + pet icons +
  // 24px rounded bottom corners) so the visitor recognises it the
  // moment they sign in.
  const ownerHeader = (title, sub) => (
    <div style={{background:"linear-gradient(135deg, var(--c-grad1, #FFB6C1) 0%, var(--c-primary, #FF6B8B) 100%)",padding:"14px 18px 22px",color:"#fff",borderRadius:"0 0 28px 28px",position:"relative",overflow:"hidden",boxShadow:"0 8px 22px rgba(255,107,139,0.25)"}}>
      <div style={{position:"absolute",top:8,right:14,opacity:0.16,fontSize:18}}>🐾</div>
      <div style={{position:"absolute",bottom:6,left:14,opacity:0.12,fontSize:14}}>🦴</div>
      <p style={{margin:0,fontSize:13,fontWeight:900,letterSpacing:0.2}}>{title}</p>
      {sub && <p style={{margin:"3px 0 0",fontSize:10,fontWeight:700,opacity:0.9}}>{sub}</p>}
    </div>
  );
  const sheetHeader = (title, sub) => (
    <div style={{padding:"14px 16px 10px",borderBottom:"1px solid #F5E5EC",background:"#fff"}}>
      <p style={{margin:0,fontSize:13,fontWeight:900,color:"#1F2937"}}>{title}</p>
      {sub && <p style={{margin:"3px 0 0",fontSize:10,fontWeight:700,color:"#8A8A9E"}}>{sub}</p>}
    </div>
  );
  const renderBody = () => {
    if (variant === "schedule") {
      return (
        <>
          {ownerHeader(i18nT("mk.mock.headerToday"), i18nT("mk.mock.headerTodaySub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,flex:1}}>
            {[
              {t:"09:30",pet:"Mochi",svc:loc!== "zh-HK"?"Bath & Cut":zhEn("沖涼剪毛", "Bath & trim"),st:"Jason",bg:"#FFE9F2",dot:"#FF6B8B"},
              {t:"10:00",pet:"Momo",svc:loc!== "zh-HK"?"Bath + SPA":"沖涼 + SPA",st:"Mary",bg:"#F5F3FF",dot:"#7C3AED"},
              {t:"11:30",pet:loc!== "zh-HK"?"Sesame":"芝麻",svc:loc!== "zh-HK"?"Full cut":"全身剪毛",st:"Jason",bg:"#EFF6FF",dot:"#3B82F6"},
              {t:"14:00",pet:loc!== "zh-HK"?"Snowy":"小白",svc:loc!== "zh-HK"?"Bath":"洗澡",st:"Mary",bg:"#FFFBEB",dot:"#F59E0B"},
              {t:"15:30",pet:loc!== "zh-HK"?"Milky":"奶茶",svc:loc!== "zh-HK"?"Cut + SPA":"剪毛 SPA",st:"Jason",bg:"#F0FDF4",dot:"#10B981"},
            ].map((b,i)=>(
              <div key={i} style={{background:b.bg,borderRadius:14,padding:"9px 11px",display:"flex",alignItems:"center",gap:9,border:"1px solid rgba(0,0,0,0.03)"}}>
                <span style={{width:6,height:36,borderRadius:99,background:b.dot,flexShrink:0}} />
                <span style={{fontSize:10,fontWeight:900,color:"#1F2937",minWidth:34,fontVariantNumeric:"tabular-nums"}}>{b.t}</span>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:0,fontSize:11,fontWeight:900,color:"#1F2937"}}>{b.pet}</p>
                  <p style={{margin:0,fontSize:9,color:"#6A6A85",fontWeight:700}}>{b.svc} · {b.st}</p>
                </div>
                <span style={{fontSize:9,fontWeight:900,color:b.dot,padding:"3px 6px",borderRadius:99,background:"rgba(255,255,255,0.7)"}}>⋮⋮</span>
              </div>
            ))}
          </div>
        </>
      );
    }
    if (variant === "booking") {
      return (
        <>
          {ownerHeader(i18nT("mk.mock.bookingHeader"), i18nT("mk.mock.bookingHeaderSub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:10,flex:1}}>
            <p style={{margin:0,fontSize:10,fontWeight:800,color:"#8A8A9E",letterSpacing:0.3}}>{i18nT("mk.mock.step1")}</p>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {(loc!== "zh-HK"?["Bath & Cut","Full Shave","SPA"]:["沖涼剪毛","全身剃毛","SPA"]).map((s,i)=>(
                <span key={s} style={{padding:"6px 11px",borderRadius:99,background:i===0?"linear-gradient(135deg,#FF6B8B,#FF4D6D)":"#fff",color:i===0?"#fff":"#4A4A68",fontSize:10,fontWeight:900,border:i===0?"1.5px solid #FF4D6D":"1.5px solid #FFE0EC",boxShadow:i===0?"0 4px 10px rgba(255,77,109,0.3)":"none"}}>{s}</span>
              ))}
            </div>
            <p style={{margin:"6px 0 0",fontSize:10,fontWeight:800,color:"#8A8A9E",letterSpacing:0.3}}>{i18nT("mk.mock.step2")}</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5}}>
              {["10:00","10:30","11:00","11:30","14:00","14:30","15:00","15:30"].map((t,i)=>(
                <div key={t} style={{padding:"7px 0",textAlign:"center",fontSize:10,fontWeight:900,borderRadius:10,background:i===4?"linear-gradient(135deg,#FF6B8B,#FF4D6D)":"#fff",color:i===4?"#fff":"#4A4A68",border:"1.5px solid "+(i===4?"#FF4D6D":"#FFE0EC"),boxShadow:i===4?"0 4px 10px rgba(255,77,109,0.25)":"none",fontVariantNumeric:"tabular-nums"}}>{t}</div>
              ))}
            </div>
            <button style={{marginTop:"auto",padding:"11px",borderRadius:14,background:"linear-gradient(135deg,#FF7AA2,#FF5C8A)",color:"#fff",border:"none",fontSize:12,fontWeight:900,boxShadow:"0 8px 18px rgba(255,92,138,0.4)"}}>{i18nT("mk.mock.confirmBtn")}</button>
          </div>
        </>
      );
    }
    if (variant === "pricing") {
      return (
        <>
          {sheetHeader(i18nT("mk.mock.pricingHeader"), i18nT("mk.mock.pricingSub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,flex:1,background:"#FCFCFF"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 11px",borderRadius:12,background:"linear-gradient(135deg,#FFF0F5,#FFE0EC)",border:"1.5px solid #FFD8E8"}}>
              <span style={{fontSize:10,fontWeight:900,color:"#FF4D6D"}}>{i18nT("mk.mock.pricingDog")}</span>
              <span style={{fontSize:9,fontWeight:900,color:"#FF4D6D",background:"#fff",padding:"2px 7px",borderRadius:99}}>{i18nT("mk.mock.autoDetect")}</span>
            </div>
            {[
              {l:i18nT("mk.mock.basePrice"),v:"HK$ 380",c:"#1F2937"},
              {l:i18nT("mk.mock.longCoatFee"),v:"+ HK$ 80",c:"#92400E"},
              {l:i18nT("mk.mock.mattingFee"),v:"+ HK$ 50",c:"#92400E"},
              {l:"💆 " + i18nT("mk.mock.spaA"),v:"+ HK$ 120",c:"#7C3AED"},
            ].map((r,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,fontWeight:700,color:r.c,padding:"5px 4px"}}>
                <span>{r.l}</span><span style={{fontVariantNumeric:"tabular-nums"}}>{r.v}</span>
              </div>
            ))}
            <div style={{borderTop:"2px solid #1F2937",paddingTop:9,marginTop:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,fontWeight:900,color:"#1F2937"}}>{i18nT("mk.mock.balanceDue")}</span>
              <span style={{fontSize:20,fontWeight:900,color:"#1F2937",fontVariantNumeric:"tabular-nums"}}>HK$ 630</span>
            </div>
            <button style={{marginTop:6,padding:"12px",borderRadius:14,background:"linear-gradient(135deg,#FF7AA2 0%,#FF5C8A 50%,#F472B6 100%)",color:"#fff",border:"none",fontSize:12,fontWeight:900,boxShadow:"0 10px 22px rgba(255,92,138,0.4)",letterSpacing:0.3}}>{i18nT("mk.mock.collect")} HK$ 630</button>
          </div>
        </>
      );
    }
    if (variant === "payroll") {
      return (
        <>
          {sheetHeader(i18nT("mk.mock.payrollHeader"), i18nT("mk.mock.payrollSub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:6,flex:1,background:"#FCFCFF"}}>
            {[
              {l:i18nT("mk.mock.basePay"),v:"HK$ 18,000",c:"#4A4A68"},
              {l:i18nT("mk.mock.svcComm"),v:"+ HK$ 4,260",c:"#10B981"},
              {l:i18nT("mk.mock.pkgComm"),v:"+ HK$ 1,120",c:"#10B981"},
              {l:i18nT("mk.mock.ot"),v:"+ HK$ 960",c:"#10B981"},
              {l:i18nT("mk.mock.deduct"),v:"− HK$ 600",c:"#EF4444"},
            ].map((r,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,fontWeight:700,color:r.c,padding:"6px 4px",borderBottom:"1px dashed #F5E5EC"}}>
                <span>{r.l}</span><span style={{fontWeight:900,fontVariantNumeric:"tabular-nums"}}>{r.v}</span>
              </div>
            ))}
            <div style={{marginTop:8,padding:"12px 14px",borderRadius:14,background:"linear-gradient(135deg,#10B981,#059669)",color:"#fff",display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:"0 10px 22px rgba(16,185,129,0.35)"}}>
              <span style={{fontSize:12,fontWeight:900}}>{i18nT("mk.mock.netPay")}</span>
              <span style={{fontSize:20,fontWeight:900,fontVariantNumeric:"tabular-nums"}}>HK$ 23,740</span>
            </div>
          </div>
        </>
      );
    }
    if (variant === "wallet") {
      return (
        <>
          {ownerHeader(i18nT("mk.mock.walletHeader"), i18nT("mk.mock.walletSub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:9,flex:1}}>
            <div style={{display:"flex",gap:6}}>
              {[
                {ic:"💰",v:"HK$ 480",c:"#0369A1",bg:"#EFF6FF",bd:"#BFDBFE"},
                {ic:"💎",v:"320 pt",c:"#7C3AED",bg:"#F5F3FF",bd:"#DDD6FE"},
                {ic:"📦",v:i18nT("mk.mock.pkgVisitsCards"),c:"#059669",bg:"#F0FDF4",bd:"#BBF7D0"},
              ].map((b,i)=>(
                <div key={i} style={{flex:1,background:b.bg,borderRadius:12,padding:"8px 6px",textAlign:"center",border:"1.5px solid "+b.bd}}>
                  <p style={{margin:0,fontSize:16}}>{b.ic}</p>
                  <p style={{margin:"3px 0 0",fontSize:9,fontWeight:900,color:b.c,whiteSpace:"nowrap"}}>{b.v}</p>
                </div>
              ))}
            </div>
            <p style={{margin:"6px 0 0",fontSize:10,fontWeight:800,color:"#8A8A9E",letterSpacing:0.3}}>{i18nT("mk.mock.pkgList")}</p>
            {[
              {n:i18nT("mk.mock.pkg10"),r:i18nT("mk.mock.left4"),d:i18nT("mk.mock.expires1")},
              {n:i18nT("mk.mock.pkgSpa6"),r:i18nT("mk.mock.left2"),d:i18nT("mk.mock.expires2")},
            ].map((p,i)=>(
              <div key={i} style={{background:"#fff",border:"1.5px solid #FFE0EC",borderRadius:12,padding:"9px 11px",display:"flex",alignItems:"center",gap:9,boxShadow:"0 2px 6px rgba(255,107,139,0.06)"}}>
                <span style={{fontSize:18}}>📦</span>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:0,fontSize:11,fontWeight:900,color:"#1F2937"}}>{p.n}</p>
                  <p style={{margin:0,fontSize:9,fontWeight:700,color:"#8A8A9E"}}>{p.d}</p>
                </div>
                <span style={{fontSize:10,fontWeight:900,color:"#10B981",background:"#F0FDF4",padding:"3px 8px",borderRadius:99}}>{p.r}</span>
              </div>
            ))}
            <div style={{marginTop:"auto",padding:"9px 11px",borderRadius:12,background:"linear-gradient(135deg,#FEF3C7,#FDE68A)",border:"1.5px solid #FCD34D",fontSize:10,fontWeight:800,color:"#92400E",display:"flex",alignItems:"center",gap:6}}>
              {i18nT("mk.mock.walletEarn")}
            </div>
          </div>
        </>
      );
    }
    if (variant === "security") {
      return (
        <>
          {sheetHeader(i18nT("mk.mock.secHeader"), i18nT("mk.mock.secSub"))}
          <div style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:9,flex:1,background:"#FCFCFF"}}>
            <div style={{background:"linear-gradient(135deg,#F0FDF4,#DCFCE7)",border:"1.5px solid #86EFAC",borderRadius:14,padding:"11px 13px",display:"flex",alignItems:"center",gap:9}}>
              <span style={{fontSize:22}}>🛡️</span>
              <div>
                <p style={{margin:0,fontSize:11,fontWeight:900,color:"#166534"}}>{i18nT("mk.mock.backupOn")}</p>
                <p style={{margin:"2px 0 0",fontSize:9,fontWeight:700,color:"#166534",opacity:0.85}}>{i18nT("mk.mock.backupSub")}</p>
              </div>
            </div>
            <p style={{margin:"4px 0 0",fontSize:10,fontWeight:800,color:"#8A8A9E",letterSpacing:0.3}}>{i18nT("mk.mock.staffPerms")}</p>
            {[
              {n:"Jason",r:i18nT("mk.mock.roleMgr"),c:"linear-gradient(135deg,#FFB6C1,#FF6B8B)"},
              {n:"Mary",r:i18nT("mk.mock.roleGroom"),c:"linear-gradient(135deg,#A5B4FC,#7C3AED)"},
              {n:"Anna",r:i18nT("mk.mock.roleFront"),c:"linear-gradient(135deg,#6EE7B7,#10B981)"},
            ].map((s,i)=>(
              <div key={i} style={{background:"#fff",border:"1.5px solid #FFE0EC",borderRadius:12,padding:"8px 10px",display:"flex",alignItems:"center",gap:9,boxShadow:"0 2px 6px rgba(255,107,139,0.06)"}}>
                <div style={{width:26,height:26,borderRadius:99,background:s.c,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,flexShrink:0}}>{s.n[0]}</div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:0,fontSize:11,fontWeight:900,color:"#1F2937"}}>{s.n}</p>
                  <p style={{margin:0,fontSize:9,fontWeight:700,color:"#8A8A9E"}}>{s.r}</p>
                </div>
              </div>
            ))}
            <div style={{display:"flex",gap:6,marginTop:"auto"}}>
              <button style={{flex:1,padding:"9px",borderRadius:10,background:"#fff",border:"1.5px solid #FFE0EC",fontSize:10,fontWeight:900,color:"#4A4A68"}}>{i18nT("mk.mock.exportData")}</button>
              <button style={{flex:1,padding:"9px",borderRadius:10,background:"#fff",border:"1.5px solid #FCA5A5",fontSize:10,fontWeight:900,color:"#EF4444"}}>{i18nT("mk.mock.deleteAcc")}</button>
            </div>
          </div>
        </>
      );
    }
    return null;
  };
  return (
    <div style={frame}>
      <div style={notch} />
      <div style={screen}>
        {statusBar}
        {renderBody()}
      </div>
    </div>
  );
};

// ── Shop-door login guard ─────────────────────────────────────────────────
// Someone pressed 店主登入 / 開設我的美容院 and then signed in with an account
// that isn't a shop account. Previously they were dropped silently into the
// customer app and reasonably concluded the shop login was broken.
//
// This screen only ever INFORMS and offers a route. An earlier version of this
// guard signed people out and stranded genuine customers on the login screen,
// so "繼續" is always present and always works.
const SHOP_DOOR_KEY = "petos:auth-source";
const SHOP_DOOR_FRESH_MS = 10 * 60 * 1000;

// True when the shop door was used recently. Stale markers are ignored so a
// flag left over from last week can never interrupt a normal customer login.
const cameThroughShopDoor = () => {
  try {
    const raw = localStorage.getItem(SHOP_DOOR_KEY) || "";
    if (!raw.startsWith("shop-login:")) return false;
    const ts = Number(raw.split(":")[1] || 0);
    return Number.isFinite(ts) && ts > 0 && (Date.now() - ts) < SHOP_DOOR_FRESH_MS;
  } catch { return false; }
};
const clearShopDoor = () => { try { localStorage.removeItem(SHOP_DOOR_KEY); } catch { /* ignore */ } };

const ShopDoorGuard = ({ user, onContinue, onRegisterShop, onLogout }) => {
  const isCustomerAcct = true;
  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:420,background:"rgba(255,255,255,0.96)",borderRadius:32,padding:30,boxShadow:"0 20px 60px var(--c-primary-30)"}}>
        <div style={{fontSize:46,textAlign:"center",marginBottom:6}}>{isCustomerAcct ? "🐾" : "🏪"}</div>
        <h2 style={{fontWeight:900,fontSize:21,color:"#4A4A68",margin:"0 0 8px",textAlign:"center"}}>
          {isCustomerAcct ? zhEn("呢個係客戶帳戶", "This is a customer account") : zhEn("店舖未開設完成", "Shop setup isn't finished")}
        </h2>
        <p style={{fontSize:14,color:"#8A8A9E",fontWeight:700,lineHeight:1.65,margin:"0 0 18px",textAlign:"center"}}>
          {isCustomerAcct
            ? <>{zhEn("你用緊", "You're signed in as ")} <b style={{color:"#4A4A68"}}>{user?.email || zhEn("呢個帳戶", "this account")}</b> {zhEn("登入，佢係客戶帳戶，未有店舖。想開店請撳下面，或者以客戶身分繼續。", ", which is a customer account with no shop. To open a shop, tap below, or continue as a customer.")}</>
            : <>{zhEn("你嘅帳戶已經係店主，但係開店流程未完成，所以仲未有店舖資料。撳下面完成開店就可以用後台。", "Your account is already a shop owner, but shop setup isn't finished so there's no shop data yet. Tap below to finish setup and use the dashboard.")}</>}
        </p>

        <div style={{display:"flex",flexDirection:"column",gap:9}}>
          <Btn onClick={() => { clearShopDoor(); onRegisterShop(); }} style={{width:"100%"}}>
            {isCustomerAcct ? zhEn("🏪 開設我的美容院", "🏪 Open my grooming shop") : zhEn("🏪 完成開店設定", "🏪 Finish shop setup")}
          </Btn>
          {isCustomerAcct && (
            <Btn variant="secondary" onClick={() => { clearShopDoor(); onContinue(); }} style={{width:"100%"}}>
              {zhEn("以客戶身分繼續 →", "Continue as a customer →")}
            </Btn>
          )}
          <Btn variant="ghost" onClick={() => { clearShopDoor(); onLogout(); }} style={{width:"100%"}}>
            {zhEn("用另一個帳戶登入", "Log in with another account")}
          </Btn>
        </div>

        {!isCustomerAcct && (
          <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,lineHeight:1.6,margin:"14px 0 0",textAlign:"center"}}>
            {zhEn("如果你已經開過店但見到呢個畫面，請聯絡我哋，我哋會幫你接返個帳戶。", "If you've already opened a shop but see this screen, please contact us and we'll reconnect your account.")}
          </p>
        )}
      </div>
    </div>
  );
};

// ── 網站查詢 chat bubble (public) ──────────────────────────────────────────
// A prospect on the marketing site asking a sales or technical question. No
// account needed — the thread is keyed by a token their browser mints, so the
// conversation survives a reload and they can come back to our reply.
//
// The panel is deliberately NOT opened automatically: a chat window that jumps
// open over a page someone is still reading is an interruption, not a service.
const SalesChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [topic, setTopic] = useState("sales");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [started, setStarted] = useState(false);
  const bottomRef = useRef(null);

  // Poll only while the panel is open — a background poll on every marketing
  // page view would be a lot of function calls for nothing.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await pollSalesMessages();
        if (!alive) return;
        setMsgs(r?.messages || []);
        if ((r?.messages || []).length) setStarted(true);
      } catch { /* offline — the next tick retries */ }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true); setErr("");
    // Optimistic echo: on a marketing page a message that vanishes for a
    // second reads as "it didn't send" and people retype it.
    const optimistic = { id: "tmp-" + Date.now(), sender_role: "visitor", body, created_at: new Date().toISOString() };
    setMsgs(m => [...m, optimistic]);
    setText("");
    try {
      const r = await sendSalesMessage({
        body, topic,
        email: email.trim() || undefined,
        pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
        locale: i18nT("lang.name"),
      });
      setMsgs(r?.messages || []);
      setStarted(true);
    } catch (e) {
      setMsgs(m => m.filter(x => x.id !== optimistic.id));
      setText(body);
      setErr(e?.message || zhEn("發送失敗，請再試一次", "Failed to send, please try again"));
    }
    setSending(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Chat with PettoBook"
        style={{position:"fixed",right:18,bottom:18,zIndex:960,width:58,height:58,borderRadius:99,border:"none",
          background:"linear-gradient(135deg,#FF6B8B,#FF4D6D)",color:"#fff",fontSize:24,cursor:"pointer",
          boxShadow:"0 10px 28px rgba(255,77,109,0.42)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {open ? "✕" : "💬"}
      </button>

      {open && (
        <div style={{position:"fixed",right:18,bottom:86,zIndex:960,width:"min(360px, calc(100vw - 36px))",
          maxHeight:"min(560px, calc(100vh - 120px))",background:"#fff",borderRadius:20,overflow:"hidden",
          boxShadow:"0 24px 60px rgba(74,74,104,0.28)",display:"flex",flexDirection:"column"}}>

          <div style={{background:"linear-gradient(135deg,#FF6B8B,#FF4D6D)",padding:"14px 16px",color:"#fff"}}>
            <p style={{margin:0,fontSize:15,fontWeight:900}}>{zhEn("PettoBook 查詢", "PettoBook enquiries")}</p>
            <p style={{margin:"2px 0 0",fontSize:11,fontWeight:700,opacity:0.92}}>
              {zhEn("銷售或技術問題都可以問 · Sales or technical — ask away", "Ask about sales or technical issues")}
            </p>
          </div>

          {!started && (
            <div style={{padding:"12px 14px",borderBottom:"1px solid #F3F3F8",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",gap:6}}>
                {[["sales",zhEn("💼 銷售查詢", "💼 Sales")],["technical",zhEn("🛠 技術支援", "🛠 Technical support")]].map(([v,l]) => (
                  <button key={v} onClick={() => setTopic(v)}
                    style={{flex:1,padding:"8px 6px",borderRadius:10,fontSize:12,fontWeight:900,cursor:"pointer",
                      border:"2px solid " + (topic===v ? "#FF6B8B" : "#FFE0EC"),
                      background: topic===v ? "#FF6B8B" : "#fff", color: topic===v ? "#fff" : "#FF6B8B"}}>{l}</button>
                ))}
              </div>
              {/* Optional, and said so: demanding an email before the first
                  question is what makes people close the widget. */}
              <input value={email} onChange={e => setEmail(e.target.value)} type="email"
                placeholder={zhEn("電郵（可選，方便我哋回覆你）", "Email (optional, so we can reply)")}
                style={{border:"2px solid #F3F3F8",borderRadius:10,padding:"9px 11px",fontSize:13,fontWeight:700,
                  color:"#4A4A68",outline:"none",boxSizing:"border-box",width:"100%"}} />
            </div>
          )}

          <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,background:"#FFFDFD"}}>
            {msgs.length === 0 && (
              <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,lineHeight:1.6,margin:0}}>
                {zhEn("你好 👋 想了解邊個方案啱你？定係遇到技術問題？留低訊息，我哋會盡快回覆。", "Hi 👋 Want to know which plan suits you, or have a technical problem? Leave a message and we'll reply soon.")}
              </p>
            )}
            {msgs.map(m => {
              const mine = m.sender_role === "visitor";
              return (
                <div key={m.id} style={{alignSelf: mine ? "flex-end" : "flex-start",maxWidth:"82%"}}>
                  <div style={{background: mine ? "#FF6B8B" : "#F3F3F8",color: mine ? "#fff" : "#4A4A68",
                    borderRadius:14,padding:"9px 12px",fontSize:13,fontWeight:600,lineHeight:1.55,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
                    {m.body}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {err && <p style={{margin:0,padding:"6px 14px",fontSize:11,fontWeight:800,color:"#B91C1C",background:"#FEF2F2"}}>{err}</p>}

          <div style={{display:"flex",gap:8,padding:"10px 12px",borderTop:"1px solid #F3F3F8",background:"#fff"}}>
            <input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={zhEn("輸入訊息…", "Type a message…")}
              style={{flex:1,border:"2px solid #F3F3F8",borderRadius:99,padding:"10px 14px",fontSize:13,
                fontWeight:700,color:"#4A4A68",outline:"none",boxSizing:"border-box",minWidth:0}} />
            <button onClick={send} disabled={sending || !text.trim()}
              style={{border:"none",borderRadius:99,padding:"0 16px",fontSize:13,fontWeight:900,cursor:"pointer",
                background: text.trim() ? "#FF6B8B" : "#FFD8E4", color:"#fff"}}>
              {sending ? "…" : zhEn("發送", "Send")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const MarketingHome = ({ onLogin, onRegister }) => {
  // Subscribe to locale so the whole landing flips between zh-HK
  // (standard written Chinese, not Cantonese any more) and English.
  i18nUseLocale();
  const painPoints = [1,2,3,4,5,6].map(i => ({
    tag: i18nT("mk.pain" + i + ".tag"),
    pain: i18nT("mk.pain" + i + ".pain"),
    painDetail: i18nT("mk.pain" + i + ".detail"),
    fix: i18nT("mk.pain" + i + ".fix"),
    fixBullets: [i18nT("mk.pain" + i + ".b1"), i18nT("mk.pain" + i + ".b2"), i18nT("mk.pain" + i + ".b3")],
    mockup: ["schedule","booking","pricing","payroll","wallet","security"][i - 1],
  }));
  // Visitor isn't authed here so we don't have platformConfig — show
  // the hardcoded PLANS defaults. Superadmin's price edits only apply
  // once the user signs in / starts onboarding.
  const planList = Object.entries(PLANS || {}).map(([id, p]) => ({ id, ...p }));
  return (
    <div style={{minHeight:"100vh",background:"#FCFCFF",color:"#4A4A68",fontFamily:"'Nunito', 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, sans-serif"}}>
      {/* ─── NAV BAR ─── */}
      <nav style={{position:"sticky",top:0,zIndex:50,background:"rgba(252,252,255,0.85)",backdropFilter:"blur(12px)",borderBottom:"1px solid #FFE0EC",padding:"14px 20px"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:36,height:36,background:"linear-gradient(135deg,#FFB6C1,#FF6B8B)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 6px 16px rgba(255,107,139,0.3)"}}>
              <PawPrint size={20} color="#fff" strokeWidth={3} />
            </div>
            <span style={{fontSize:20,fontWeight:900,color:"#4A4A68"}}>PettoBook</span>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {/* Language switcher — anonymous visitors get the same lang
                 control that signed-in users have in Profile / Settings.
                 Cycles 繁中 ↔ EN with one tap. */}
            <button onClick={() => i18nCycleLocale()} title="Language" style={{padding:"7px 12px",borderRadius:99,background:"rgba(255,182,193,0.18)",border:"1.5px solid #FFD8E8",color:"#FF4D6D",fontWeight:900,fontSize:12,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}>
              🌐 {i18nLocaleShort()}
            </button>
            <button onClick={onLogin} style={{padding:"8px 16px",borderRadius:99,background:"transparent",border:"none",color:"#4A4A68",fontWeight:800,fontSize:14,cursor:"pointer"}}>{i18nT("mk.nav.login")}</button>
            <button onClick={onRegister} style={{padding:"10px 18px",borderRadius:99,background:"linear-gradient(135deg,#FFB6C1,#FF6B8B)",border:"none",color:"#fff",fontWeight:900,fontSize:14,cursor:"pointer",boxShadow:"0 6px 16px rgba(255,107,139,0.35)"}}>
              {i18nT("mk.nav.startFree")}
            </button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section style={{position:"relative",overflow:"hidden",background:"radial-gradient(circle at 15% 30%, #FFE9F2 0%, #FFD8E8 40%, #FFC4DE 100%)",padding:"60px 20px 80px"}}>
        {/* Floating paw deco */}
        <div style={{position:"absolute",top:40,right:60,opacity:0.18,fontSize:60,transform:"rotate(15deg)"}}>🐾</div>
        <div style={{position:"absolute",bottom:40,left:80,opacity:0.15,fontSize:50,transform:"rotate(-20deg)"}}>🐾</div>
        <div style={{position:"absolute",top:140,right:200,opacity:0.10,fontSize:40,transform:"rotate(10deg)"}}>✂️</div>
        <div style={{position:"absolute",top:80,left:40,opacity:0.12,fontSize:46,transform:"rotate(-30deg)"}}>💆</div>

        <div style={{maxWidth:1100,margin:"0 auto",display:"grid",gridTemplateColumns:"1fr",gap:48,alignItems:"center",position:"relative"}}>
          <div style={{textAlign:"center"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.7)",padding:"8px 16px",borderRadius:99,marginBottom:24,boxShadow:"0 4px 16px rgba(255,107,139,0.15)"}}>
              <span style={{fontSize:18}}>🐶</span>
              <span style={{fontSize:13,fontWeight:800,color:"#FF6B8B"}}>{i18nT("mk.hero.badge")}</span>
            </div>
            <h1 style={{fontSize:48,fontWeight:900,margin:"0 0 20px",color:"#4A4A68",lineHeight:1.15,letterSpacing:"-0.5px"}}>
              {i18nT("mk.hero.titleLine1")}<br/>
              <span style={{background:"linear-gradient(135deg,#FF6B8B,#FF8FA3)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>{i18nT("mk.hero.titleLine2")}</span> 🌸
            </h1>
            <p style={{fontSize:18,color:"#6A6A85",fontWeight:600,margin:"0 0 32px",lineHeight:1.6,maxWidth:560,marginLeft:"auto",marginRight:"auto"}}>
              {i18nT("mk.hero.subtitle")}<br/>{i18nT("mk.hero.subtitleLine2")}
            </p>
            <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:32}}>
              <button onClick={onRegister} style={{padding:"16px 28px",borderRadius:99,background:"linear-gradient(135deg,#FF6B8B,#FF4D6D)",border:"none",color:"#fff",fontWeight:900,fontSize:16,cursor:"pointer",boxShadow:"0 12px 28px rgba(255,77,109,0.4)",display:"inline-flex",alignItems:"center",gap:8}}>
                {i18nT("mk.hero.ctaPrimary")} <ChevronRight size={18} strokeWidth={3} />
              </button>
              <button onClick={onLogin} style={{padding:"16px 28px",borderRadius:99,background:"rgba(255,255,255,0.85)",border:"2px solid #FFE0EC",color:"#4A4A68",fontWeight:900,fontSize:16,cursor:"pointer"}}>
                {i18nT("mk.hero.ctaSecondary")}
              </button>
            </div>
            <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{i18nT("mk.hero.bullets")}</p>
          </div>

          {/* App mockup */}
          <div style={{position:"relative",maxWidth:540,margin:"0 auto",width:"100%"}}>
            <div style={{background:"#fff",borderRadius:32,padding:14,boxShadow:"0 30px 80px rgba(255,107,139,0.25), 0 8px 24px rgba(74,74,104,0.08)",transform:"rotate(-1.5deg)"}}>
              <div style={{background:"linear-gradient(135deg,#FFB6C1,#FF6B8B)",borderRadius:"20px 20px 0 0",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{color:"#fff",fontWeight:900,fontSize:14}}>📅 {i18nT("mk.hero.mockToday")}</span>
                <span style={{color:"#fff",opacity:0.85,fontSize:11,fontWeight:700}}>{i18nGetLocale() !== "zh-HK" ? "Jun 8" : "6月 8"}</span>
              </div>
              <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:8,background:"#FFFAFC",borderRadius:"0 0 20px 20px"}}>
                {[
                  { time:"10:00", pet:zhEn("奶茶", "Milky"), svc:zhEn("沖涼全套", "Full groom"), staff:"Jason", price:"$280", color:"#FFE9F2" },
                  { time:"11:30", pet:zhEn("奶皇", "Custard"), svc:zhEn("剪毛 + SPA - A", "Trim + SPA - A"), staff:"Mary", price:"$520", color:"#F5F3FF" },
                  { time:"14:00", pet:zhEn("芝麻", "Sesame"), svc:zhEn("沖涼剪毛", "Bath & trim"), staff:"Jason", price:"$350", color:"#EFF6FF" },
                ].map((b, i) => (
                  <div key={i} style={{background:b.color,borderRadius:14,padding:"10px 12px",display:"flex",alignItems:"center",gap:10}}>
                    <div style={{fontSize:11,fontWeight:900,color:"#4A4A68",minWidth:36}}>{b.time}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#1F2937",margin:0}}>{b.pet}</p>
                      <p style={{fontSize:10,color:"#6A6A85",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.svc} · {b.staff}</p>
                    </div>
                    <span style={{fontSize:12,fontWeight:900,color:"#FF6B8B"}}>{b.price}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Floating chip */}
            <div style={{position:"absolute",top:-10,right:-10,background:"#fff",borderRadius:16,padding:"8px 14px",boxShadow:"0 8px 24px rgba(74,74,104,0.12)",display:"flex",alignItems:"center",gap:6,transform:"rotate(6deg)"}}>
              <span style={{fontSize:18}}>🔔</span>
              <span style={{fontSize:11,fontWeight:900,color:"#4A4A68"}}>{zhEn("新預約 + $280", "New booking + $280")}</span>
            </div>
            <div style={{position:"absolute",bottom:-12,left:-12,background:"linear-gradient(135deg,#10B981,#059669)",borderRadius:16,padding:"8px 14px",boxShadow:"0 8px 24px rgba(16,185,129,0.35)",display:"flex",alignItems:"center",gap:6,transform:"rotate(-4deg)"}}>
              <span style={{fontSize:14}}>✅</span>
              <span style={{fontSize:11,fontWeight:900,color:"#fff"}}>{zhEn("WhatsApp 已自動發送", "WhatsApp sent automatically")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PAIN POINTS — 6 alternating rows. Each row = pain card on
            one side + phone-frame mockup of the actual feature on the
            other. Mockups are pure CSS so they stay in sync with the
            real UI as it evolves. ─── */}
      <section style={{padding:"60px 20px 30px",background:"#FCFCFF"}}>
        <div style={{maxWidth:980,margin:"0 auto",textAlign:"center"}}>
          <p style={{fontSize:13,fontWeight:900,color:"#FF6B8B",margin:"0 0 12px",letterSpacing:1.5}}>{i18nT("mk.painsHeading.label")}</p>
          <h2 style={{fontSize:36,fontWeight:900,color:"#4A4A68",margin:"0 0 14px",lineHeight:1.2}}>{i18nT("mk.painsHeading.title")}</h2>
          <p style={{fontSize:16,color:"#8A8A9E",margin:0,fontWeight:700,maxWidth:620,marginLeft:"auto",marginRight:"auto"}}>{i18nT("mk.painsHeading.sub")}</p>
        </div>
      </section>

      {/* Inline media-query CSS — inline styles can't express @media, so we
           inject one <style> tag for the pain-row responsive collapse.
           Below 820px (iPad portrait / phone) we drop to single column and
           force ltr direction so the copy/mockup order stays natural. */}
      <style>{`
        .pb-pain-row{max-width:1080px;margin:0 auto;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,420px);gap:48px;align-items:center}
        .pb-pain-row.flip{direction:rtl}
        .pb-pain-row > *{direction:ltr;min-width:0}
        @media (max-width: 820px){
          .pb-pain-row{grid-template-columns:1fr;gap:28px;direction:ltr !important}
          .pb-pain-row .pb-mockup-wrap{max-width:340px;margin:0 auto;width:100%}
        }
      `}</style>
      {painPoints.map((p, i) => {
        const flip = i % 2 === 1;
        return (
          <section key={p.tag} style={{padding:"40px 20px",background: i % 2 === 0 ? "#FCFCFF" : "linear-gradient(180deg,#FFFAFC 0%,#FFF5F8 100%)"}}>
            <div className={"pb-pain-row" + (flip ? " flip" : "")}>
              {/* Copy side */}
              <div style={{direction:"ltr"}}>
                <div style={{display:"inline-flex",alignItems:"center",gap:6,background:"#FFF0F5",border:"1.5px solid #FFD8E8",padding:"5px 12px",borderRadius:99,marginBottom:14}}>
                  <span style={{fontSize:11,fontWeight:900,color:"#FF4D6D",letterSpacing:1}}>{p.tag.toUpperCase()}</span>
                </div>
                <div style={{background:"#FEF2F2",borderRadius:18,padding:"14px 18px",border:"1.5px solid #FCA5A5",marginBottom:18,display:"flex",alignItems:"flex-start",gap:10}}>
                  <span style={{fontSize:20,flexShrink:0}}>😩</span>
                  <div>
                    <p style={{fontSize:14,fontWeight:900,color:"#991B1B",margin:0}}>{p.pain}</p>
                    <p style={{fontSize:12,color:"#7F1D1D",margin:"4px 0 0",fontWeight:600,lineHeight:1.5}}>{p.painDetail}</p>
                  </div>
                </div>
                <h3 style={{fontSize:24,fontWeight:900,color:"#1F2937",margin:"0 0 12px",lineHeight:1.3,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:99,background:"linear-gradient(135deg,#10B981,#059669)",color:"#fff",fontSize:14,boxShadow:"0 4px 12px rgba(16,185,129,0.4)"}}>✓</span>
                  {p.fix}
                </h3>
                <ul style={{listStyle:"none",padding:0,margin:0,display:"flex",flexDirection:"column",gap:8}}>
                  {p.fixBullets.map((b, j) => (
                    <li key={j} style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:14,color:"#4A4A68",fontWeight:700,lineHeight:1.5}}>
                      <span style={{color:"#FF6B8B",fontWeight:900,flexShrink:0}}>•</span>{b}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Mockup side */}
              <div className="pb-mockup-wrap">
                <PhoneMockup variant={p.mockup} />
              </div>
            </div>
          </section>
        );
      })}

      {/* ─── COMPARISON TABLE ─── */}
      <section style={{padding:"60px 20px",background:"linear-gradient(180deg,#FFFAFC 0%,#FFEEF2 100%)"}}>
        <div style={{maxWidth:920,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:36}}>
            <p style={{fontSize:13,fontWeight:900,color:"#FF6B8B",margin:"0 0 12px",letterSpacing:1.5}}>{i18nT("mk.cmp.label")}</p>
            <h2 style={{fontSize:32,fontWeight:900,color:"#4A4A68",margin:"0 0 8px",lineHeight:1.2}}>{i18nT("mk.cmp.title")}</h2>
          </div>
          <div style={{background:"#fff",borderRadius:24,padding:24,boxShadow:"0 16px 40px rgba(255,107,139,0.10)",border:"2px solid #FFE0EC",overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:560}}>
              <thead>
                <tr>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:12,fontWeight:800,color:"#8A8A9E"}}></th>
                  <th style={{padding:"10px 12px",fontSize:13,fontWeight:800,color:"#8A8A9E"}}>{i18nT("mk.cmp.colPaper")}</th>
                  <th style={{padding:"10px 12px",fontSize:13,fontWeight:800,color:"#8A8A9E"}}>{i18nT("mk.cmp.colGeneric")}</th>
                  <th style={{padding:"10px 12px",fontSize:13,fontWeight:900,color:"#FF4D6D",background:"#FFF0F5",borderRadius:"12px 12px 0 0"}}>{i18nT("mk.cmp.colUs")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [i18nT("mk.cmp.r1"), "✗", "✗", "✓"],
                  [i18nT("mk.cmp.r2"), "✗", "△", "✓"],
                  [i18nT("mk.cmp.r3"), "△", "△", "✓"],
                  [i18nT("mk.cmp.r4"), "✗", "✗", "✓"],
                  [i18nT("mk.cmp.r5"), "✗", "△", "✓"],
                  [i18nT("mk.cmp.r6"), "✗", "✓", "✓"],
                  [i18nT("mk.cmp.r7"), "✗", "△", "✓"],
                  [i18nT("mk.cmp.r8"), "—", "△", "✓"],
                ].map((row, i) => (
                  <tr key={i} style={{borderTop:"1px solid #F5E5EC"}}>
                    <td style={{padding:"12px",fontSize:13,fontWeight:700,color:"#4A4A68"}}>{row[0]}</td>
                    {row.slice(1).map((cell, j) => {
                      const isUs = j === 2;
                      const color = cell === "✓" ? "#10B981" : cell === "✗" ? "#EF4444" : cell === "△" ? "#F59E0B" : "#9CA3AF";
                      return (
                        <td key={j} style={{padding:"12px",textAlign:"center",fontSize:15,fontWeight:900,color,background: isUs ? "#FFF0F5" : "transparent"}}>{cell}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{fontSize:11,color:"#8A8A9E",margin:"14px 0 0",textAlign:"center",fontWeight:700}}>{i18nT("mk.cmp.legend")}</p>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS — kept tight at the bottom so visitor doesn't
            scroll past the pain → fix story to get here. ─── */}
      <section style={{padding:"60px 20px 40px",background:"#FCFCFF"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:36}}>
            <p style={{fontSize:13,fontWeight:900,color:"#FF6B8B",margin:"0 0 12px",letterSpacing:1.5}}>{i18nT("mk.how.label")}</p>
            <h2 style={{fontSize:30,fontWeight:900,color:"#4A4A68",margin:"0 0 8px",lineHeight:1.2}}>{i18nT("mk.how.title")}</h2>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:20}}>
            {[
              { n: "01", title: i18nT("mk.how.s1.title"), body: i18nT("mk.how.s1.body") },
              { n: "02", title: i18nT("mk.how.s2.title"), body: i18nT("mk.how.s2.body") },
              { n: "03", title: i18nT("mk.how.s3.title"), body: i18nT("mk.how.s3.body") },
            ].map((s, i) => (
              <div key={i} style={{background:"#fff",borderRadius:24,padding:24,border:"2px solid #FFE0EC",position:"relative",boxShadow:"0 8px 24px rgba(255,107,139,0.06)"}}>
                <div style={{position:"absolute",top:-14,left:20,background:"linear-gradient(135deg,#FF6B8B,#FF4D6D)",color:"#fff",fontWeight:900,fontSize:15,padding:"4px 12px",borderRadius:12,boxShadow:"0 6px 16px rgba(255,77,109,0.4)"}}>{s.n}</div>
                <h3 style={{fontSize:18,fontWeight:900,color:"#1F2937",margin:"14px 0 8px"}}>{s.title}</h3>
                <p style={{fontSize:13,color:"#6A6A85",margin:0,fontWeight:600,lineHeight:1.6}}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section style={{padding:"80px 20px",background:"#FCFCFF"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:48}}>
            <p style={{fontSize:13,fontWeight:900,color:"#FF6B8B",margin:"0 0 12px",letterSpacing:1.5}}>{i18nT("mk.price.label")}</p>
            <h2 style={{fontSize:36,fontWeight:900,color:"#4A4A68",margin:"0 0 12px",lineHeight:1.2}}>{i18nT("mk.price.title")}</h2>
            <p style={{fontSize:16,color:"#8A8A9E",margin:0,fontWeight:700}}>{i18nT("mk.price.sub")}</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:20}}>
            {planList.map((p, i) => {
              const featured = i === 1; // middle plan highlighted
              return (
                <div key={p.id} style={{background:featured?"linear-gradient(180deg,#FFEEF2,#fff)":"#fff",borderRadius:28,padding:28,border:"2px solid "+(featured?"#FF6B8B":"#FFE0EC"),position:"relative",boxShadow:featured?"0 24px 60px rgba(255,107,139,0.18)":"0 8px 20px rgba(74,74,104,0.06)",transform:featured?"scale(1.04)":"scale(1)"}}>
                  {featured && <div style={{position:"absolute",top:-14,left:"50%",transform:"translateX(-50%)",background:"linear-gradient(135deg,#FF6B8B,#FF4D6D)",color:"#fff",fontSize:11,fontWeight:900,padding:"5px 14px",borderRadius:99,boxShadow:"0 6px 16px rgba(255,77,109,0.4)"}}>{i18nT("mk.price.popular")}</div>}
                  <h3 style={{fontSize:20,fontWeight:900,color:"#1F2937",margin:"4px 0 6px"}}>{p.name}</h3>
                  <div style={{display:"flex",alignItems:"baseline",gap:4,margin:"0 0 16px"}}>
                    <span style={{fontSize:36,fontWeight:900,color:"#FF6B8B"}}>{p.price === 0 ? i18nT("mk.price.free") : "HK$" + p.price}</span>
                    {p.price > 0 && <span style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{i18nT("mk.price.permonth")}</span>}
                  </div>
                  <p style={{fontSize:13,color:"#6A6A85",margin:"0 0 18px",fontWeight:700,minHeight:36,lineHeight:1.5}}>
                    {p.maxStaff ? i18nT("mk.price.maxStaffPrefix") + p.maxStaff + i18nT("mk.price.maxStaffSuffix") : i18nT("mk.price.unlimitedStaff")}
                  </p>
                  <ul style={{listStyle:"none",padding:0,margin:"0 0 20px",display:"flex",flexDirection:"column",gap:8}}>
                    {[
                      p.features?.scheduling && i18nT("mk.price.f.scheduling"),
                      p.features?.crm && i18nT("mk.price.f.crm"),
                      p.features?.whatsapp && i18nT("mk.price.f.whatsapp"),
                      p.features?.analytics && i18nT("mk.price.f.analytics"),
                      p.features?.payroll && i18nT("mk.price.f.payroll"),
                      p.features?.smartPricing && i18nT("mk.price.f.smartPricing"),
                      p.features?.recurring && i18nT("mk.price.f.recurring"),
                      planPosRank(p) >= 1 && i18nT(["", "mk.price.f.posLite", "mk.price.f.posStandard", "mk.price.f.posFull"][planPosRank(p)]),
                    ].filter(Boolean).map((f, j) => (
                      <li key={j} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#4A4A68",fontWeight:700}}>
                        <Check size={14} color="#10B981" strokeWidth={3} />{f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={onRegister} style={{width:"100%",padding:"12px",borderRadius:99,background:featured?"linear-gradient(135deg,#FF6B8B,#FF4D6D)":"#FFFAFC",border:featured?"none":"2px solid #FFE0EC",color:featured?"#fff":"#FF6B8B",fontWeight:900,fontSize:14,cursor:"pointer",boxShadow:featured?"0 8px 20px rgba(255,77,109,0.35)":"none"}}>
                    {p.price === 0 ? i18nT("mk.price.btnFree") : i18nT("mk.price.btnChoose")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── BIG CTA ─── */}
      <section style={{padding:"80px 20px",background:"linear-gradient(135deg,#FF6B8B 0%,#FF4D6D 50%,#FF8FA3 100%)",textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:20,left:30,opacity:0.18,fontSize:60}}>🐾</div>
        <div style={{position:"absolute",bottom:30,right:60,opacity:0.2,fontSize:50}}>✂️</div>
        <div style={{position:"absolute",top:50,right:100,opacity:0.15,fontSize:40}}>🐶</div>
        <div style={{maxWidth:680,margin:"0 auto",position:"relative"}}>
          <h2 style={{fontSize:36,fontWeight:900,color:"#fff",margin:"0 0 16px",lineHeight:1.2}}>
            {i18nT("mk.cta.title")}
          </h2>
          <p style={{fontSize:17,color:"rgba(255,255,255,0.92)",margin:"0 0 32px",fontWeight:700,lineHeight:1.5}}>
            {i18nT("mk.cta.sub")}
          </p>
          <button onClick={onRegister} style={{padding:"18px 36px",borderRadius:99,background:"#fff",border:"none",color:"#FF4D6D",fontWeight:900,fontSize:17,cursor:"pointer",boxShadow:"0 16px 40px rgba(0,0,0,0.18)",display:"inline-flex",alignItems:"center",gap:10}}>
            <PawPrint size={20} strokeWidth={3} /> {i18nT("mk.cta.btn")}
          </button>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{padding:"32px 20px",background:"#FFFAFC",borderTop:"1px solid #FFE0EC"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",gap:20,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:28,height:28,background:"linear-gradient(135deg,#FFB6C1,#FF6B8B)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <PawPrint size={16} color="#fff" strokeWidth={3} />
            </div>
            <span style={{fontSize:14,color:"#8A8A9E",fontWeight:800}}>{i18nT("mk.foot.copy")}</span>
          </div>
          <div style={{display:"flex",gap:18,fontSize:13,fontWeight:700}}>
            <a href="#/privacy" style={{color:"#8A8A9E",textDecoration:"none"}}>{i18nT("mk.foot.privacy")}</a>
            <a href="#/terms" style={{color:"#8A8A9E",textDecoration:"none"}}>{i18nT("mk.foot.terms")}</a>
            <a href="mailto:hello@petos.app" style={{color:"#8A8A9E",textDecoration:"none"}}>{i18nT("mk.foot.contact")}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Login = ({ onSignIn, onRegister, externalErr, onForgot }) => {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (externalErr) setErr(externalErr); }, [externalErr]);
  // Surface OAuth errors. Supabase appends `?error=…&error_description=…`
  // (PKCE) or `#error=…&error_description=…` (implicit) to the redirect URL
  // when the OAuth round-trip fails or the user cancels. We sniff both,
  // show a human-readable message, and clean the URL so reloads don't echo.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const parse = (str) => {
      const params = new URLSearchParams(str.replace(/^[?#]/, ""));
      return { error: params.get("error"), description: params.get("error_description") };
    };
    const q = parse(window.location.search);
    const h = parse(window.location.hash);
    const err = q.error || h.error;
    const desc = q.description || h.description;
    if (err) {
      const friendly = {
        access_denied: zhEn("您取消了 Google 登入。如要繼續，請再按一次「使用 Google 登入」。", "You cancelled the Google sign-in. To continue, tap \"Sign in with Google\" again."),
        server_error: zhEn("Google 登入服務暫時無法回應，請稍後再試。", "The Google sign-in service isn't responding right now. Please try again later."),
        unauthorized_client: zhEn("此網域未獲授權使用 Google 登入。請聯絡系統管理員。", "This domain isn't authorised for Google sign-in. Please contact the administrator."),
        invalid_request: zhEn("登入請求格式錯誤，請重新嘗試。", "The sign-in request was malformed. Please try again."),
      };
      setErr(friendly[err] || (desc ? decodeURIComponent(desc).replace(/\+/g, " ") : (zhEn("Google 登入失敗：", "Google sign-in failed: ") + err)));
      try {
        const clean = window.location.origin + window.location.pathname;
        window.history.replaceState({}, "", clean);
      } catch { /* ignore */ }
      // Also drop the auth intent so a stale flag doesn't promote an
      // unrelated future login into owner mode.
      try { localStorage.removeItem("petos:auth-intent"); } catch { /* ignore */ }
    }
  }, []);
  const doLogin = async (mail, password) => {
    setErr(""); setLoading(true);
    // Mark this as a shop-login attempt so onAuthChange can block role
    // === customer users from quietly slipping into CustomerApp through
    // the owner's front door.
    try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch { /* ignore */ }
    try {
      await onSignIn(mail, password);
    }
    catch (e) { setErr(e?.message || zhEn("登入失敗", "Sign-in failed")); }
    finally { setLoading(false); }
  };
  return (
    <div style={{minHeight:"100vh",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:80,height:80,background:"#fff",borderRadius:32,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",boxShadow:"0 16px 40px var(--c-primary-40)"}}><PawPrint size={40} color="var(--c-primary)" strokeWidth={2.5} /></div>
          <h1 style={{fontSize:36,fontWeight:900,color:"#fff",margin:"0 0 8px"}}>PettoBook</h1>
          <p style={{color:"rgba(255,255,255,0.9)",fontSize:15,margin:0,fontWeight:700}}>{zhEn("企業級寵物美容 SaaS 平台", "Pet grooming management platform")}</p>
        </div>
        <div style={{background:"rgba(255,255,255,0.9)",backdropFilter:"blur(24px)",borderRadius:40,padding:32,boxShadow:"0 20px 60px var(--c-primary-30)"}}>
          <div style={{display:"flex",flexDirection:"column",gap:16,marginBottom:20}}>
            <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
            <Input type="password" placeholder={zhEn("密碼", "Password")} value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => { if (e.key === "Enter") doLogin(email, pw); }} />
          </div>
          {err && <p style={{color:"#FF4D6D",fontSize:13,marginBottom:16,textAlign:"center",fontWeight:700}}>{err}</p>}
          <Btn onClick={() => doLogin(email, pw)} disabled={loading || !email || !pw} style={{width:"100%",padding:"16px"}}>{loading ? zhEn("登入中...", "Signing in...") : zhEn("登入", "Log in")}</Btn>
          <Btn variant="secondary" onClick={onRegister} style={{width:"100%",padding:"16px",marginTop:10}}>
            <Store size={18} strokeWidth={2.5} /> {zhEn("開設我的美容院", "Open my grooming shop")}
          </Btn>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12}}>
            <button onClick={onForgot} style={{background:"transparent",border:"none",color:"var(--c-primary)",fontSize:13,fontWeight:800,cursor:"pointer",padding:0}}>{zhEn("忘記密碼？", "Forgot password?")}</button>
            <span style={{fontSize:12,color:"#AFAFC4",fontWeight:700}}>{zhEn("使用 Email 登入", "Sign in with email")}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"18px 0 14px"}}>
            <div style={{flex:1,height:2,background:"var(--c-light)",borderRadius:99}} />
            <span style={{fontSize:12,color:"#AFAFC4",fontWeight:700}}>{zhEn("或", "or")}</span>
            <div style={{flex:1,height:2,background:"var(--c-light)",borderRadius:99}} />
          </div>
          <button onClick={async () => {
            // Two flags: owner-register tells the post-OAuth code to
            // promote a brand-new customer-default profile to owner so
            // ShopRegister renders. auth-source = shop-login lets the
            // reject-customer guard distinguish "customer used the wrong
            // door" from a legitimate /shop/{slug} customer login.
            try { localStorage.setItem("petos:auth-intent", "owner-register"); } catch {}
            try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch {}
            try {
              // Force a hash-free redirect URL. Without this, auth.js defaults
              // to window.location.href which can carry a stale `#/forgot` or
              // `#/reset` hash from prior navigation — Google appends its own
              // `#access_token=…` and we end up with a percent-encoded mess
              // (`#/foo%23access_token=…`) that requires the malformedMatch
              // recovery path in App init and sometimes still loses context.
              const cleanRedirect = window.location.origin + "/";
              await signInWithGoogle({ redirectTo: cleanRedirect });
            }
            catch (e) {
              try { localStorage.removeItem("petos:auth-intent"); } catch {}
              setErr(e?.message || zhEn("Google 登入失敗", "Google sign-in failed"));
            }
          }} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"13px 14px",borderRadius:20,background:"#fff",border:"2px solid #E5E7EB",color:"#3C4043",fontSize:14,fontWeight:800,cursor:"pointer"}}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.4 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.378 0-9.93-3.057-11.273-7.946l-6.522 5.025C9.5 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.094 5.565.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
            {zhEn("使用 Google 登入", "Sign in with Google")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── CUSTOMER APP ───────────────────────────────────────────────────────────
// Multi-shop sheet: lists every shop the customer belongs to (each with its
// OWN wallet / points / stamps), lets them switch active shop, and leave a
// shop. Reads only state.memberships + state.tenants (no fetch). Doubles as
// the login shop-picker and the "我的店舖" manage page.
const ShopSwitcherSheet = ({ memberships, tenants, activeTenantId, onSwitch, onLeave, onClose }) => {
  const [confirmLeave, setConfirmLeave] = useState(null); // membership pending leave
  const rows = (memberships || [])
    .map(m => ({ m, t: (tenants || []).find(tt => tt.id === m.tenantId) }))
    .filter(r => r.t);
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:6000,background:"rgba(20,18,40,0.55)",backdropFilter:"blur(4px)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:480,background:"#fff",borderRadius:"28px 28px 0 0",padding:"20px 20px calc(24px + env(safe-area-inset-bottom,0px))",maxHeight:"86vh",overflowY:"auto",boxShadow:"0 -12px 40px rgba(0,0,0,0.25)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{margin:0,fontSize:19,fontWeight:900,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Store size={19} strokeWidth={2.6} color="var(--c-primary)"/>{T("我的店舖","My Shops","マイショップ")}</h3>
          <button onClick={onClose} style={{background:"#F4F3FA",border:"none",borderRadius:99,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}><X size={18} color="#8A8A9E" strokeWidth={2.5}/></button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {rows.map(({ m, t }) => {
            const active = t.id === activeTenantId;
            return (
              <div key={m.tenantId} style={{border:"2px solid "+(active?"var(--c-primary)":"#EDEBF5"),borderRadius:18,padding:"14px 14px",background:active?"var(--c-light,#F6F5FF)":"#fff"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <button onClick={()=>{ if(!active){ onSwitch(t.bookingSlug); onClose(); } }} disabled={active} style={{flex:1,display:"flex",alignItems:"center",gap:12,background:"none",border:"none",padding:0,cursor:active?"default":"pointer",textAlign:"left",minWidth:0}}>
                    <div style={{width:44,height:44,borderRadius:12,flexShrink:0,background:t.logoUrl?"#fff":"var(--c-primary-10,#EEF)",border:"1px solid #EDEBF5",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                      {t.logoUrl ? <img src={t.logoUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <Store size={20} color="var(--c-primary)" strokeWidth={2.4}/>}
                    </div>
                    <div style={{minWidth:0,flex:1}}>
                      <p style={{margin:"0 0 3px",fontSize:15,fontWeight:900,color:"#4A4A68",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.name}</p>
                      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                        <span style={{fontSize:12,fontWeight:800,color:"#3B82F6"}}><Wallet size={11} strokeWidth={2.6} style={{verticalAlign:"-1px"}}/> ${m.wallet||0}</span>
                        <span style={{fontSize:12,fontWeight:800,color:"#D97706"}}><Award size={11} strokeWidth={2.6} style={{verticalAlign:"-1px"}}/> {m.points||0}pt</span>
                        {(m.stamps||0)>0 && <span style={{fontSize:12,fontWeight:800,color:"#10B981"}}>🎯 {m.stamps}</span>}
                      </div>
                    </div>
                    {active
                      ? <span style={{flexShrink:0,fontSize:11,fontWeight:900,color:"var(--c-primary)",background:"#fff",border:"1px solid var(--c-primary)",borderRadius:99,padding:"3px 9px",display:"inline-flex",alignItems:"center",gap:3}}><Check size={12} strokeWidth={3}/>{T("使用中","Active","使用中")}</span>
                      : <ChevronRight size={18} color="#B8B6C8" strokeWidth={2.5} style={{flexShrink:0}}/>}
                  </button>
                </div>
                {confirmLeave?.tenantId === m.tenantId ? (
                  <div style={{marginTop:12,padding:"12px 12px",borderRadius:12,background:"#FEF2F2",border:"1px solid #FCA5A5"}}>
                    <p style={{margin:"0 0 8px",fontSize:12.5,fontWeight:800,color:"#991B1B",lineHeight:1.5}}>
                      {((m.wallet||0)>0 || (m.points||0)>0)
                        ? T(`⚠️ 你喺呢間店仲有 $${m.wallet||0} 錢包 · ${m.points||0} 積分，離開後會失去。確定離開？`, `⚠️ You still have $${m.wallet||0} wallet · ${m.points||0} points here — leaving forfeits them. Leave anyway?`, `⚠️ 残高 $${m.wallet||0}・${m.points||0}pt が失われます。退出しますか？`)
                        : T("確定離開呢間店？你之後可以再次預約加入。","Leave this shop? You can rejoin by booking again.","このショップを退出しますか？")}
                    </p>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setConfirmLeave(null)} style={{flex:1,padding:"9px 0",borderRadius:10,border:"1px solid #E5E3F0",background:"#fff",fontSize:13,fontWeight:800,color:"#6B6980",cursor:"pointer"}}>{T("取消","Cancel","キャンセル")}</button>
                      <button onClick={()=>{ setConfirmLeave(null); onLeave(m.tenantId); }} style={{flex:1,padding:"9px 0",borderRadius:10,border:"none",background:"#DC2626",color:"#fff",fontSize:13,fontWeight:800,cursor:"pointer"}}>{T("離開","Leave","退出")}</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>setConfirmLeave(m)} style={{marginTop:10,display:"inline-flex",alignItems:"center",gap:5,background:"none",border:"none",padding:0,cursor:"pointer",fontSize:12,fontWeight:800,color:"#C2410C"}}><Trash2 size={12} strokeWidth={2.5}/>{T("離開店舖","Leave shop","退出")}</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CustomerThemePicker = ({ shopTheme }) => {
  const mine = useCustomerTheme();
  const swatch = (key, on, onClick, label) => {
    const tv = THEMES[key] || THEMES.peach;
    return (
      <button key={label} type="button" onClick={onClick} aria-label={label} aria-pressed={on} title={label}
        style={{width:36,height:36,borderRadius:"50%",cursor:"pointer",padding:0,flexShrink:0,
          background:`linear-gradient(135deg,${tv["--c-grad1"]},${tv["--c-primary"]})`,
          border:on ? "3px solid #4A4A68" : "3px solid #fff", boxShadow:"0 0 0 1px rgba(0,0,0,0.08)",
          display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14,fontWeight:900}}>{on ? "✓" : ""}</button>
    );
  };
  return (
    <Card style={{padding:"14px 18px",display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:18}}>🎨</span>
        <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0,flex:1}}>{T("主題顏色","Theme colour","テーマカラー")}</p>
        <button type="button" onClick={() => setCustomerTheme(null)}
          style={{padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:800,cursor:"pointer",
            border: !mine ? "2px solid var(--c-primary)" : "2px solid var(--c-border)", background: !mine ? "var(--c-primary)" : "#fff", color: !mine ? "#fff" : "#4A4A68"}}>
          {T("跟隨店舖","Shop's colour","店舗に合わせる")}
        </button>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {Object.keys(THEMES).map(k => swatch(k, mine === k, () => setCustomerTheme(k), k))}
      </div>
      <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:0}}>
        {mine ? T("已用你揀嘅顏色，店舖更改主題都唔會影響。","Using your colour — the shop's theme won't change it.","選んだ色を使用中。店舗のテーマ変更の影響を受けません。")
              : T("而家跟隨店舖嘅主題（" + (shopTheme || "peach") + "）。","Following the shop's theme.","店舗のテーマに合わせています。")}
      </p>
    </Card>
  );
};

const CustomerApp = ({ state, setState, user, onLogout, initialTab, onInitialTabConsumed, onSwitchShop, onLeaveShop }) => {
  const [tab, setTab] = useState(initialTab || "home");
  // Pet album lightbox — index into the open pet's album (null = closed).
  const [albumLb, setAlbumLb] = useState(null);
  const [step, setStep] = useState(0);
  // Shop news feed — customer sees latest offers/announcements the shop
  // published (same shop_news rows the native app reads).
  const [shopNewsItems, setShopNewsItems] = useState([]);
  const [newsListOpen, setNewsListOpen] = useState(false);
  const [newsDetail, setNewsDetail] = useState(null);
  // Browseable vouchers list.
  const [myVouchersOpen, setMyVouchersOpen] = useState(false);
  // 詢價 — auto-open the PostRequestSheet if the visitor came from
  // /directory's "📢 出價搵店" button (intent flag in localStorage).
  const [postRfqOpen, setPostRfqOpen] = useState(false);
  const [myRfqOpen, setMyRfqOpen] = useState(false);
  // Multi-shop switcher / picker / manage sheet.
  const [shopSheetOpen, setShopSheetOpen] = useState(false);
  // Choose-on-login: first-time multi-shop customer with no remembered shop
  // gets the picker once per session (a shop is already loaded underneath).
  useEffect(() => {
    if ((state.memberships || []).length < 2) return;
    try {
      if (sessionStorage.getItem("petos:shopPickerShown")) return;
      const hadRemembered = !!localStorage.getItem("petos:lastCustomerSlug");
      sessionStorage.setItem("petos:shopPickerShown", "1");
      if (!hadRemembered) setShopSheetOpen(true);
    } catch { /* storage unavailable — skip picker */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(state.memberships || []).length]);
  // initialTab is a post-login LANDING hint, and it can arrive late: the shop
  // page's auto-advance effect sends it, and handleEmailAuth sends it again
  // only after awaiting the consent + membership writes (measured +11s after
  // tapping 登入). A customer who had already tapped 預約 or 改期 was thrown back
  // to 毛孩 mid-flow. Honour the hint only until they navigate themselves.
  const landingTabRef = useRef(tab);
  const userNavigatedRef = useRef(false);
  useEffect(() => {
    if (tab !== landingTabRef.current) userNavigatedRef.current = true;
  }, [tab]);
  useEffect(() => {
    if (initialTab) {
      if (!userNavigatedRef.current) {
        landingTabRef.current = initialTab;
        setTab(initialTab);
        setStep(0);
      }
      onInitialTabConsumed?.();
    }
  }, [initialTab, onInitialTabConsumed]);
  // Load latest shop news whenever the active shop changes.
  useEffect(() => {
    const tid = user?.tenantId;
    if (!tid) { setShopNewsItems([]); return; }
    let alive = true;
    fetchShopNews(tid)
      .then(rows => { if (alive) setShopNewsItems(rows || []); })
      .catch(() => { if (alive) setShopNewsItems([]); });
    return () => { alive = false; };
  }, [user?.tenantId]);
  useEffect(() => {
    try {
      if (localStorage.getItem("petos:pending-rfq") === "1") {
        localStorage.removeItem("petos:pending-rfq");
        if (state.platformConfig?.rfqEnabled === true) setPostRfqOpen(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Walk-in account-merge prompt (Google customers only) ───────────────
  // Google OAuth doesn't give us a phone number, so a customer who was
  // pre-registered as a walk-in by the shop ends up with TWO profiles: the
  // walk-in one holding all their pets / history / packages, and the new
  // Google one which is empty. We close the gap by asking the customer for
  // their phone the first time they land in CustomerApp without one — if a
  // walk-in profile in this tenant matches, `linkPetsOnLogin` merges
  // server-side. Skipped via localStorage so we don't pester forever.
  const [topupSheetOpen, setTopupSheetOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  // Popup-block fallback — if window.open is null (Safari ITP, strict
  // popup blocker), surface a clickable link in the sheet so the user
  // can still send the WhatsApp message manually.
  const [topupFallback, setTopupFallback] = useState(null);
  const [mergePromptOpen, setMergePromptOpen] = useState(false);
  const [mergePhone, setMergePhone] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeCountry, setMergeCountry] = useState("+852");
  // DISABLED: the customer-driven "連結舊資料" phone-claim prompt is turned off.
  // Without an OTP step it (a) can't verify the phone is the caller's own —
  // a customer could try to map someone else's number and steal their pets /
  // history / wallet — and (b) its 401 was forcing a logout. Merging now
  // happens owner-side only (shop adds the customer → find-or-link), which is
  // trusted. Customers can still set their own phone in profile settings.
  // (mergePromptOpen kept declared so the unused modal code below is harmless.)

  const handleMergeSubmit = async () => {
    const cleaned = (mergePhone || "").replace(/\D/g, "");
    if (cleaned.length < 6) { toast(i18nT("val.enterPhone"), "error"); return; }
    setMergeBusy(true);
    try {
      // Order matters: TRY THE MERGE FIRST. If a walk-in profile already
      // holds this phone (the common case), patchProfile would throw a
      // unique-key violation BEFORE the merge ever runs, leaving the
      // customer stuck. linkPetsOnLogin migrates the walk-in's pets and
      // membership to this auth profile AND clears the duplicate phone
      // on the orphan walk-in row, so the subsequent patchProfile then
      // succeeds cleanly.
      const slug = (typeof window !== "undefined" && window.__petosActiveCustomerSlug) || (state.tenants.find(t => t.id === user.tenantId)?.bookingSlug);
      let merged = { mergedPets: 0, mergedAppointments: 0 };
      if (slug) {
        try { merged = await linkPetsOnLogin({ slug, phone: cleaned }) || merged; } catch (linkErr) { console.warn("[PettoBook] linkPetsOnLogin failed:", linkErr); }
      }
      // Now save phone on this auth profile. If the duplicate is STILL
      // present (linker found nothing to merge — phone belongs to another
      // tenant's customer entirely), surface a clearer message.
      try {
        await patchProfile(user.id, { phone: cleaned, countryCode: mergeCountry });
      } catch (patchErr) {
        const msg = String(patchErr?.message || patchErr || "");
        if (msg.includes("profiles_customer_phone_unique") || msg.includes("duplicate key")) {
          toast(zhEn("此電話號碼已被另一個帳戶使用。請使用其他號碼或聯絡店家協助合併。", "This phone number is already used by another account. Use a different number or contact the shop to help merge them."), "error");
          return;
        }
        throw patchErr;
      }
      setState(s => ({ ...s, users: { ...s.users, [user.id]: { ...(s.users[user.id] || user), phone: cleaned, countryCode: mergeCountry } } }));
      try { localStorage.setItem("petos:merge-skip:" + user.id, "1"); } catch { /* ignore */ }
      setMergePromptOpen(false);
      const mp = Number(merged?.mergedPets || merged?.pets || 0);
      if (mp > 0) toast(zhEn("✅ 已成功連結 ", "✅ Linked ") + mp + zhEn(" 隻毛孩嘅舊資料", " pets' existing records"));
      else toast(zhEn("已儲存電話。如果店家之前未用此號碼登記，將不會有資料合併。", "Phone saved. If the shop hasn't registered this number before, no records will be merged."));
    } catch (e) {
      toast(zhEn("連結失敗：", "Link failed: ") + (e?.message || e), "error");
    } finally {
      setMergeBusy(false);
    }
  };
  const handleMergeSkip = () => {
    try { localStorage.setItem("petos:merge-skip:" + user.id, "1"); } catch { /* ignore */ }
    setMergePromptOpen(false);
  };
  // Pre-fill `date` with today so the date strip + time slots are visible
  // immediately. Customer can still tap another day; this just removes the
  // dead "nothing happens until you pick a date" gap when they reach the
  // schedule step.
  const [bk, setBk] = useState({ petId:"", extraPetIds:[], svcId:"", addons:[], spaIds:[], staffId:"", date: dateNow(), time:"", groomingRequest:"", voucherId:"", customerPackageId:"", depositReceiptUrl:"" });
  // Scroll refs for the progressive booking flow. Each section gets
  // scrolled into view when the user makes the preceding selection,
  // so the next-needed field is always visible without manual scrolling.
  const svcCardRef = useRef(null);
  const spaCardRef = useRef(null);
  const addonCardRef = useRef(null);
  const scheduleCardRef = useRef(null);
  const summaryCardRef = useRef(null);
  const scrollIntoView = (ref) => {
    if (!ref?.current) return;
    // 80ms gives the new card time to mount + apply its border radius
    setTimeout(() => { try { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {} }, 80);
  };
  const [showSig, setShowSig] = useState(false); const [sig, setSig] = useState(null);
  const [showPay, setShowPay] = useState(false);
  // Phone-required gate — a customer (e.g. Google sign-up) may have no phone on
  // file. Booking without a contact number leaves the shop unable to reach them,
  // so we block checkout and collect it inline before proceeding.
  const [needPhoneOpen, setNeedPhoneOpen] = useState(false);
  const [resumeCheckout, setResumeCheckout] = useState(false);
  const [payMethodId, setPayMethodId] = useState(null);
  // Booking legal acceptance — required to confirm
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSenior, setAcceptedSenior] = useState(false);
  const [acceptedCat, setAcceptedCat] = useState(false);
  const [showTermsFull, setShowTermsFull] = useState(true);
  const [cancelApt, setCancelApt] = useState(null);
  const [rescheduleApt, setRescheduleApt] = useState(null); // holds the apt being rescheduled
  // FIX: cancel reason now has both presets and a free-text fallback
  const [cancelReason, setCancelReason] = useState(""); const [cancelCustom, setCancelCustom] = useState("");
  const [rateApt, setRateApt] = useState(null); const [rating, setRating] = useState(5); const [comment, setComment] = useState("");
  const [viewReportApt, setViewReportApt] = useState(null);
  // #5 Photo lightbox — tap any report photo to fullscreen-view.
  // Was a new-tab <a target=_blank> which broke flow + missed mobile UX.
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showRecurring, setShowRecurring] = useState(false);
  const [recForm, setRecForm] = useState({ petId:"", svcId:"", staffId:"", dayOfWeek:"1", time:"10:00", interval:"monthly" });
  const [petForm, setPetForm] = useState(null);
  // Inline pet form shown on the pets tab when the customer has zero pets.
  // Separate from petForm (which controls the BottomSheet for editing /
  // adding another pet) so the empty-state form doesn't fight the sheet.
  const [inlineNewPet, setInlineNewPet] = useState({ name:"", breed:"", weight:"", type:"dog", coatLength:"short", phone:"", countryCode:"+852" });
  // Editable phone draft on the customer profile tab — pre-seeded from the
  // current liveUser value so the picker shows their existing number. The
  // sync useEffect lives BELOW the `liveUser = …` line so the deps array
  // doesn't read a not-yet-declared const (temporal dead zone).
  const [profilePhoneDraft, setProfilePhoneDraft] = useState({ phone: "", countryCode: "+852" });
  const [profileNameDraft, setProfileNameDraft] = useState("");
  const [profileAddressDraft, setProfileAddressDraft] = useState("");
  const [bookMonth, setBookMonth] = useState(dateNow().slice(0, 7));
  const [searchQ, setSearchQ] = useState("");
  const [usePoints, setUsePoints] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showPetDetail, setShowPetDetail] = useState(null);
  // NEW: booking success state
  const [bookingSuccess, setBookingSuccess] = useState(null);
  // One-time success banner after the phone-OTP claim flow merged walk-in
  // pets into this account. Stashed by PublicBooking; read + cleared here.
  const [claimSummary, setClaimSummary] = useState(() => {
    try {
      const raw = sessionStorage.getItem("petos:claim-summary");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  });
  useEffect(() => {
    if (claimSummary) { try { sessionStorage.removeItem("petos:claim-summary"); } catch {} }
  }, [claimSummary]);

  const liveUser = state.users[user.id] || user;
  // Keep the profile-phone editor in sync with whatever liveUser currently
  // holds. Placed AFTER the liveUser declaration to avoid the TDZ.
  useEffect(() => {
    setProfilePhoneDraft({ phone: liveUser?.phone || "", countryCode: liveUser?.countryCode || "+852" });
    setProfileNameDraft(liveUser?.name || "");
    setProfileAddressDraft(liveUser?.address || "");
  }, [liveUser?.phone, liveUser?.countryCode, liveUser?.name, liveUser?.address]);
  // Active multi-visit packages for this customer at the current tenant.
  // Refreshes on user/tenant change and after a purchase notification arrives.
  const [myPackages, setMyPackages] = useState([]);
  const [shopPackages, setShopPackages] = useState([]);
  // Re-fetch whenever any of THIS customer's appointments transitions to
  // a done/cancelled/noshow state — those are the only events that can
  // change remaining_visits server-side. Previously myPackages only loaded
  // once at mount, so a customer who used a package visit at the shop
  // would still see the old count (e.g. 8/8 when the owner shows 7/8)
  // until they reloaded the app.
  const myPackageSig = useMemo(() => {
    return (state.appointments || [])
      .filter(a => a.customerId === liveUser?.id && a.tenantId === liveUser?.tenantId && a.customerPackageId)
      .map(a => a.id + ":" + a.status)
      .sort()
      .join("|");
  }, [state.appointments, liveUser?.id, liveUser?.tenantId]);
  useEffect(() => {
    if (!liveUser?.id || !liveUser.tenantId) return;
    fetchCustomerPackages(liveUser.id, liveUser.tenantId).then(setMyPackages).catch(() => setMyPackages([]));
    fetchServicePackages(liveUser.tenantId).then(setShopPackages).catch(() => setShopPackages([]));
  }, [liveUser?.id, liveUser?.tenantId, myPackageSig]);
  // Keep the shop's package offers fresh for customers too (service_packages is
  // not in realtime + is component state) — refetch on focus + every 30s visible.
  useEffect(() => {
    if (!liveUser?.tenantId) return;
    const refetch = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      fetchServicePackages(liveUser.tenantId).then(setShopPackages).catch(() => {});
    };
    const iv = setInterval(refetch, 30000);
    const onFocus = () => { if (typeof document === "undefined" || document.visibilityState === "visible") refetch(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("focus", onFocus); };
  }, [liveUser?.tenantId]);
  const tenant = state.tenants.find(t => t.id === liveUser.tenantId);
  // Online booking is a paid feature — free-plan shops can't be booked online
  // from inside the app either (contact the shop directly instead).
  const canBook = shopHasOnlineBooking(tenant);
  const cMaint = state?.platformConfig?.maintenanceMode === true; // platform upgrade — block booking
  const myPets = (liveUser.pets || []).map(pid => state.pets[pid]).filter(Boolean);
  const myApts = state.appointments.filter(a => a.customerId === liveUser.id);
  // CustomerApp's staff picker also honours customerVisible. The
  // list_tenant_staff_public RPC already filters server-side for fresh
  // customer sessions, but a switching-shop reload may leave a stale
  // entry in state.users with customerVisible=false — filter here too
  // as a belt-and-braces.
  const staffList = (tenant?.staffIds || []).map(s => state.users[s]).filter(s => s && s.enabled !== false && s.customerVisible !== false);
  // CustomerApp booking view — hide services flagged customer_visible=false.
  // Customer-side service list — narrowed to the picked pet's type so a
  // dog booking never sees cat / small-animal services (and vice versa).
  // If no pet picked yet (initial booking step), show everything.
  const _selectedPet = state.pets[bk.petId];
  const matchesPet = (s) => {
    if (!_selectedPet) return true;
    // Per-pet exclusion: owner marked this service N.A. for this pet.
    if ((_selectedPet.blockedServiceIds || []).includes(s.id)) return false;
    const petType = _selectedPet.type || "dog";
    // petTypes array is the canonical source; fall back to the legacy
    // single petType column so pre-migration services still resolve.
    const types = (Array.isArray(s.petTypes) && s.petTypes.length) ? s.petTypes : [s.petType || "dog"];
    return types.includes(petType);
  };
  const services = (tenant?.services || []).filter(s => s.kind !== "spa" && s.customerVisible !== false && matchesPet(s));
  const spaServices = (tenant?.services || []).filter(s => s.kind === "spa" && s.customerVisible !== false && matchesPet(s));
  // Addons narrowed to the picked pet's type so cat / small-animal bookings
  // don't see dog-only add-ons. Multi-type addons (petTypes = ["dog","cat"])
  // appear in both bookings.
  const addons = (tenant?.addons || []).filter(a => {
    if (!_selectedPet) return true;
    const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
    return pts.includes(_selectedPet.type || "dog");
  });

  // Progressive-flow auto-scroll. Each selection in the booking funnel
  // reveals the next card; we smooth-scroll it into view so the customer
  // If today's business hours are over (or today is a rest day), bump the
  // default date forward to the next bookable day so the customer doesn't
  // land on a page of strike-through slots. Only fires once on entry —
  // user picks override after that.
  const [dateAutoSet, setDateAutoSet] = useState(false);
  useEffect(() => {
    if (tab !== "book" || dateAutoSet || !tenant) return;
    const today = new Date();
    const todayStr = localDateStr(today);
    if (bk.date !== todayStr) { setDateAutoSet(true); return; }
    const svc = (tenant.services || []).find(s => s.id === bk.svcId);
    const duration = svc?.estimatedMinutes || 60;
    const leadMinutes = Number(tenant.bookingLeadMinutes) || 15;
    const nowMin = today.getHours() * 60 + today.getMinutes() + leadMinutes;
    const hasRoom = (d) => {
      const bh = tenant.businessHours?.[new Date(d).getDay()];
      if (!bh || !bh.enabled) return false;
      const [ch, cm] = (bh.close || "18:00").split(":").map(Number);
      const closeMin = ch * 60 + cm;
      const isToday = d === todayStr;
      const earliestStart = isToday ? nowMin : 0;
      return earliestStart + duration <= closeMin;
    };
    if (hasRoom(todayStr)) { setDateAutoSet(true); return; }
    for (let i = 1; i <= 60; i++) {
      const d = new Date(today); d.setDate(d.getDate() + i);
      const ds = localDateStr(d);
      if (hasRoom(ds)) { setBk(b => ({ ...b, date: ds, time: "" })); setDateAutoSet(true); return; }
    }
    setDateAutoSet(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tenant, bk.svcId]);
  useEffect(() => {
    if (tab !== "book") return;
    if (bk.petId) scrollIntoView(svcCardRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.petId, tab]);
  useEffect(() => {
    if (tab !== "book") return;
    if (bk.svcId) scrollIntoView(spaServices.length > 0 ? spaCardRef : (addons.length > 0 ? addonCardRef : scheduleCardRef));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.svcId, tab]);
  // Track whether bk.staffId was set by a USER tap vs. the auto-pick
  // useEffect below. Without this, picking a service triggered the
  // auto-pick → bk.staffId changed → the scheduleCardRef scroll fired
  // and yanked the user PAST the SPA + addon cards before they could
  // see them. Now we only scroll to schedule when the user explicitly
  // picks a groomer (or when no auto-pick happened).
  const userPickedStaffRef = useRef(false);
  useEffect(() => {
    if (tab !== "book") return;
    if (!bk.staffId) return;
    if (!userPickedStaffRef.current) return; // auto-pick: don't yank scroll past SPA/addons
    scrollIntoView(scheduleCardRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.staffId, tab]);
  // Auto-pick the first eligible groomer so customers don't need a tap for
  // the default choice. They can still switch by tapping another pill.
  useEffect(() => {
    if (tab !== "book") return;
    if (bk.staffId) return;
    if (!bk.svcId) return;
    const eligible = staffList.filter(s => staffCanDo(s, bk.svcId));
    if (eligible.length >= 1) {
      userPickedStaffRef.current = false; // mark this as auto, not user-driven
      setBk(b => ({ ...b, staffId: eligible[0].id }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList.length, tab, bk.svcId]);
  // Reset the "user picked staff" flag whenever the booking is reset to a
  // fresh state (new pet / new service) so a previous user-tap doesn't
  // bleed into the next funnel iteration.
  useEffect(() => { userPickedStaffRef.current = false; }, [bk.petId, bk.svcId]);
  useEffect(() => {
    if (tab !== "book") return;
    if (bk.time) scrollIntoView(summaryCardRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.time, tab]);

  const selPet = state.pets[bk.petId];
  const selSvc = services.find(s => s.id === bk.svcId);
  // 上門 (home-visit) in the in-app flow. mobile-only shop forces 上門; 兩者
  // shop lets the customer toggle (bk.isMobile); in-shop never offers it.
  const cShopMobile = tenant?.serviceMode === "mobile" || tenant?.serviceMode === "both";
  const cShopMobileOnly = tenant?.serviceMode === "mobile";
  const cWantMobile = cShopMobileOnly ? true : (cShopMobile ? !!bk.isMobile : false);
  const pricingDetail = useMemo(() => getPricingDetail(selPet, selSvc, tenant, { usesPackage: !!bk.customerPackageId, mobile: cWantMobile }), [selPet, selSvc, tenant, bk.customerPackageId, cWantMobile]);
  // #28 Multi-pet: each extra pet adds its own matrix-priced base. Weight/
  // breed differ per pet so we can't just multiply — compute per-pet.
  const extraPets = useMemo(() => (bk.extraPetIds || []).map(id => state.pets[id]).filter(Boolean), [bk.extraPetIds, state.pets]);
  const extraPetPricing = useMemo(() => extraPets.map(p => ({ pet: p, detail: getPricingDetail(p, selSvc, tenant, { usesPackage: !!bk.customerPackageId, mobile: cWantMobile }) })), [extraPets, selSvc, tenant, bk.customerPackageId, cWantMobile]);
  const extraPetsBaseSum = extraPetPricing.reduce((s, x) => s + (x.detail?.total || 0), 0);
  const basePrice = pricingDetail.total + extraPetsBaseSum;
  const spaIds = bk.spaIds || [];
  const spaPriceFull = spaIds.reduce((sum, sid) => {
    const sp = spaServices.find(s => s.id === sid);
    return sum + (sp ? getPricingDetail(selPet, sp, tenant, { mobile: cWantMobile }).total : 0);
  }, 0);
  const addonPriceFull = bk.addons.reduce((s, id) => { const a = addons.find(x => x.id === id); return s + (a ? computeAddonNet(a, selPet, selSvc).price : 0); }, 0);
  // 上門 service/SPA visibility: drop services not bookable 上門 (when 上門) and
  // those whose matched size-tier is 不適用 for the selected pet.
  const cAvailForPet = (s) => !(selPet && parseFloat(selPet.weight) > 0) || !getPricingDetail(selPet, s, tenant, { mobile: cWantMobile }).unavailable;
  // Shop-wide max weight: when the selected pet exceeds it, the shop can't serve
  // it at all → hide ALL services/SPA (not just disable the confirm button).
  const cShopMaxKg = Number(tenant?.maxPetWeightKg) || 0;
  const cOverShopMax = cShopMaxKg > 0 && Number(selPet?.weight || 0) > cShopMaxKg;
  const cVisibleServices = cOverShopMax ? [] : (cWantMobile ? services.filter(serviceMobileBookable) : services).filter(cAvailForPet);
  const cVisibleSpa = cOverShopMax ? [] : (cWantMobile ? spaServices.filter(serviceMobileBookable) : spaServices).filter(cAvailForPet);
  // Coverage flags: a picked package may cover SPA + addons so the customer
  // sees the items as "免費 (套票包含)" and the total skips the line.
  const pickedPkgDef = (() => {
    if (!bk.customerPackageId) return null;
    const cp = (myPackages || []).find(m => m.id === bk.customerPackageId);
    return cp ? shopPackages.find(p => p.id === cp.packageId) : null;
  })();
  const spaCovered = !!pickedPkgDef?.coversSpas;
  const addonsCovered = !!pickedPkgDef?.coversAddons;
  const spaPrice = spaCovered ? 0 : spaPriceFull;
  const addonPrice = addonsCovered ? 0 : addonPriceFull;
  const total = basePrice + spaPrice + addonPrice;
  // 面議 — main service / sibling pet / SPA priced by consultation. Show 面議
  // instead of a number and take no deposit (price settled in person).
  const cQuoteLbl = i18nGetLocale() !== "zh-HK" ? "Quote" : "面議";
  const cHasQuote = !!pricingDetail.quote || extraPetPricing.some(x => x.detail?.quote)
    || spaIds.some(sid => { const sp = spaServices.find(s => s.id === sid); return sp && getPricingDetail(selPet, sp, tenant, { mobile: cWantMobile }).quote; });
  // `isReturning` — has this customer ever booked at this tenant before
  // (any status besides the current reschedule). Drives the new-customer-
  // only deposit-policy exemption. PublicBooking already passed this; the
  // in-app flow was missing it and re-charged returning customers.
  const isReturning = (state.appointments || []).some(a =>
    a.customerId === liveUser.id && a.tenantId === liveUser.tenantId &&
    a.status !== "cancelled" && (!rescheduleApt || a.id !== rescheduleApt.id)
  );
  const deposit = cHasQuote ? 0 : calcDeposit(total, tenant, isReturning);
  const ptDiscount = usePoints ? Math.min(liveUser.points, deposit) : 0;
  // Voucher discount — read from the membership row's vouchers[]. The
  // booking-success / wallet UI promised to "免費" the redeemed SPA but
  // the deposit was previously calculated without subtracting the
  // voucher value, so customers were overcharged at the till.
  const myMemForBk = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
  const pickedVoucherForBk = bk.voucherId ? (myMemForBk?.vouchers || []).find(v => v.id === bk.voucherId && !v.used) : null;
  const voucherSpec = pickedVoucherForBk?.voucher || pickedVoucherForBk || null;

  // Auto-attach the SPA / addon a "free_spa" / "free_addon" voucher
  // covers. Previously the customer had to ALSO tick the matching
  // service themselves — easy to forget, and the voucher silently did
  // nothing on submit. Now picking the voucher attaches the item; un-
  // picking removes it (only if the item was auto-added, so a manually-
  // picked SPA stays). Uses bk._voucherAutoSpa / _voucherAutoAddon to
  // remember what came from the voucher.
  useEffect(() => {
    const vt = voucherSpec?.type || pickedVoucherForBk?.type;
    const refId = voucherSpec?.refId || pickedVoucherForBk?.refId;
    setBk(prev => {
      if (!prev) return prev;
      let next = prev;
      // Step 1: drop whatever the previous voucher had auto-attached.
      if (prev._voucherAutoSpa) {
        next = { ...next, spaIds: (next.spaIds || []).filter(id => id !== prev._voucherAutoSpa), _voucherAutoSpa: null };
      }
      if (prev._voucherAutoAddon) {
        next = { ...next, addons: (next.addons || []).filter(id => id !== prev._voucherAutoAddon), _voucherAutoAddon: null };
      }
      // Step 2: attach the new voucher's target if it's free-* and we
      // know which item it covers.
      if (vt === "free_spa" && refId) {
        const sp = (tenant?.services || []).find(s => s.id === refId && s.kind === "spa");
        if (sp && !(next.spaIds || []).includes(refId)) {
          next = { ...next, spaIds: [...(next.spaIds || []), refId], _voucherAutoSpa: refId };
        }
      } else if (vt === "free_addon" && refId) {
        const ad = (tenant?.addons || []).find(a => a.id === refId);
        if (ad && !(next.addons || []).includes(refId)) {
          next = { ...next, addons: [...(next.addons || []), refId], _voucherAutoAddon: refId };
        }
      }
      return next === prev ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.voucherId, tenant?.id]);
  // Voucher cap is the full bill (`total`), not the deposit — when the
  // shop has no deposit policy (deposit = $0) the customer would otherwise
  // see "兌換券折抵 $0" and the voucher silently does nothing. The deposit
  // gets reduced first (down to 0), the rest is honoured at the till when
  // staff collects the final balance.
  const voucherDiscount = (() => {
    if (!voucherSpec) return 0;
    const t = voucherSpec.type || pickedVoucherForBk?.type;
    if (t === "percent") {
      const pct = Number(voucherSpec.amount) || 0;
      return Math.max(0, Math.min(total, Math.round(total * pct / 100)));
    }
    if (t === "discount" || t === "fixed") {
      return Math.max(0, Math.min(total, Number(voucherSpec.amount) || 0));
    }
    if (t === "free_spa") {
      // Free-SPA voucher only fires when the linked SPA is actually in
      // the booking; otherwise the voucher applies nothing and the
      // customer keeps it for the next visit.
      const refId = voucherSpec.refId;
      if (!refId || !(bk.spaIds || []).includes(refId)) return 0;
      const sp = (tenant?.services || []).find(s => s.id === refId);
      if (!sp) return 0;
      const pd = selPet ? getPricingDetail(selPet, sp, tenant) : { total: sp.basePrice };
      return Math.max(0, Math.min(total, pd.total));
    }
    if (t === "free_addon") {
      const refId = voucherSpec.refId;
      if (!refId || !(bk.addons || []).includes(refId)) return 0;
      const ad = (tenant?.addons || []).find(a => a.id === refId);
      if (!ad) return 0;
      const ap = computeAddonPrice(ad, selPet);
      return Math.max(0, Math.min(total, ap.price));
    }
    return 0;
  })();
  const toPay = Math.max(0, deposit - ptDiscount - voucherDiscount);

  const filteredApts = useMemo(() => {
    let r = myApts;
    if (filterStatus !== "all") r = r.filter(a => a.status === filterStatus);
    if (searchQ) { const q = searchQ.toLowerCase(); r = r.filter(a => state.pets[a.petId]?.name.toLowerCase().includes(q) || a.service.toLowerCase().includes(q) || a.date.includes(q)); }
    return r.sort((a, b) => b.date.localeCompare(a.date));
  }, [myApts, searchQ, filterStatus, state.pets]);

  // ⚠️ Every fresh booking mints a NEW `uuid()`, so a second tap creates a
  // SECOND real appointment — not a harmless retry. The write can legitimately
  // take up to ~12s on a cold/aged mobile connection (see the raw-fetch
  // timeout in api.js), which is more than long enough for a customer to
  // decide nothing happened and tap again. The ref is the actual guard
  // (synchronous — a state update wouldn't have landed before the second
  // tap's handler runs); the state only drives the button's visible feedback.
  const bookingBusyRef = useRef(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const submitBooking = async signature => {
    if (bookingBusyRef.current) return;
    bookingBusyRef.current = true;
    setBookingBusy(true);
    try {
      return await submitBookingInner(signature);
    } finally {
      // Always release — the inner function has many early validation
      // returns, and a stuck flag would lock the customer out of booking.
      bookingBusyRef.current = false;
      setBookingBusy(false);
    }
  };
  const submitBookingInner = async signature => {
    const svc = services.find(s => s.id === bk.svcId);
    if (!svc) { toast(zhEn("此服務已下架，請重新選擇", "This service is no longer available, please choose again"), "error"); return; }
    if (!rescheduleApt) {
      let latestBusy;
      try {
        latestBusy = await fetchPublicBusySlots(tenant.id, bk.date, bk.date);
      } catch (e) {
        console.warn("[PettoBook] final availability check failed:", e);
        setBkBusyStatus("error");
        setBk(b => ({ ...b, time: "" }));
        scrollIntoView(scheduleCardRef);
        toast(zhEn("暫時未能確認最新時段，請重試", "Couldn't confirm the latest slots, please retry"), "error");
        return;
      }
      const latestSlots = generateTimeSlots(
        bk.date, bk.staffId, bk.svcId, tenant, state.appointments,
        state.users?.[bk.staffId], staffList.filter(u => staffCanDo(u, bk.svcId)), latestBusy
      );
      setBkBusySlots(latestBusy);
      setBkBusyStatus("ready");
      if (!latestSlots.some(slot => slot.time === bk.time && slot.available)) {
        setBk(b => ({ ...b, time: "" }));
        toast(zhEn("此時段剛被預約，請重新選擇時間", "This slot was just booked, please choose another time"), "warning");
        return;
      }
    }
    // 上門 — resolve the effective address (typed, else the saved one) and
    // require it. Saved back to the membership after a successful booking.
    const cMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
    // Only fall back to the saved address when the field was NEVER touched
    // (undefined). If the customer cleared it ("") we must NOT silently reuse
    // the old one — that hid the "address missing" error.
    const cAddr = cWantMobile ? (bk.serviceAddress !== undefined ? bk.serviceAddress.trim() : (cMem?.address || "").trim()) : "";
    const cNotes = cWantMobile ? (bk.serviceNotes || "").trim() : "";
    if (cWantMobile && !cAddr) { toast(T("上門服務請填寫地址","Please enter the home-visit address"), "warning"); return; }
    // Block-guard — if the shop has flagged this customer as blocked, do
    // not let them book. Authoritative gate is also enforced server-side
    // via trg_block_apt_insert_if_blocked.
    const myMemForBlock = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
    if (myMemForBlock?.blockedAt) {
      toast(zhEn("此店家暫時無法為您提供預約服務。如有疑問請聯絡店家。", "This shop can't take bookings right now. Please contact the shop if you have questions."), "error");
      return;
    }
    const myMemForVc = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
    let pickedVoucher = bk.voucherId ? (myMemForVc?.vouchers || []).find(v => v.id === bk.voucherId && !v.used) : null;
    // Defensive guard: if user somehow has both a cash voucher AND a package
    // selected, drop the voucher silently so it never reaches server-side.
    if (pickedVoucher && bk.customerPackageId) {
      const _spec = pickedVoucher.voucher || pickedVoucher;
      const _t = _spec.type || pickedVoucher.type;
      if (_t === "discount" || _t === "fixed" || _t === "percent") pickedVoucher = null;
    }
    const voucherNote = pickedVoucher ? ("🎟️ 兌換券：" + pickedVoucher.label + "（" + pickedVoucher.pointsCost + " 積分）") : "";
    const reschedNote = rescheduleApt ? ("改期自 " + rescheduleApt.date + " " + rescheduleApt.time) : "";
    const mergedNote = [reschedNote, voucherNote].filter(Boolean).join(" · ");
    // Single-pet appointment. Multi-pet "同行" mode was removed — customers
    // book each pet in its own appointment so partial-failure recovery is
    // trivial and price/deposit accounting is unambiguous.
    //
    // RESCHEDULE: keep the SAME appointment id and skip a fresh deposit
    // settlement. The previous flow cancelled the old apt (forfeiting the
    // deposit, no refund) and inserted a new one (charging the deposit
    // again) — double-charged the customer for what is logically a
    // date/time edit.
    const isReschedule = !!rescheduleApt;
    let newApt = isReschedule
      ? {
          ...rescheduleApt,
          petId: bk.petId,
          // staff_id, base_price and addon_price are LOCKED for customers by the
          // guard_appointment_self_update DB trigger (a customer may only touch
          // date/time/cancel — not price or groomer). The old code re-sent a
          // RECOMPUTED base/addon price and bk.staffId here, so any drift from
          // the stored values (price-config change, staff-adjusted price, a
          // different auto-assigned groomer) tripped
          // "permission denied: addon_price locked" and the reschedule couldn't
          // save. A reschedule is a date/time move, so preserve the original
          // groomer + pricing (matches the "reschedule is NOT a new payment
          // event" intent below). Changing service/price is a re-booking.
          staffId: rescheduleApt.staffId,
          svcId: svc?.id,
          service: svc?.name || "",
          spaIds: bk.spaIds || [],
          addons: bk.addons.map(id => addons.find(a => a.id === id)?.name || "").filter(Boolean),
          date: bk.date,
          time: bk.time,
          status: "pending",
          basePrice: rescheduleApt.basePrice,
          addonPrice: rescheduleApt.addonPrice,
          voucherDiscount,
          // Preserve the existing deposit + breakdown; reschedule is NOT a new payment event.
          // Carry the reschedule note + voucher metadata into staffNote for the owner.
          staffNote: mergedNote,
          groomingRequest: (bk.groomingRequest || "").trim(),
          customDuration: pricingDetail?.durationMin || null,
          isMobile: cWantMobile, serviceAddress: cWantMobile ? cAddr : null, serviceNotes: cWantMobile ? cNotes : null,
          // MUST preserve — guard_appointment_self_update rejects any customer
          // change to approval_status (sending null for an approved apt fails).
          approvalStatus: rescheduleApt.approvalStatus ?? null,
          cancelReason: null, // clear any prior cancel marker
        }
      : { id:uuid(), tenantId:liveUser.tenantId, customerId:liveUser.id, petId:bk.petId, staffId:bk.staffId || null, svcId:svc?.id || null, service:svc?.name||"", spaIds: bk.spaIds || [], addons: bk.addons.map(id=>addons.find(a=>a.id===id)?.name||"").filter(Boolean), date:bk.date, time:bk.time, status:"pending", approvalStatus: tenant?.cancellationPolicy?.requireApproval ? "pending" : null, basePrice: pricingDetail.total, addonPrice: addonPrice + spaPrice, deposit, voucherDiscount, report:null, signature:signature||null, rating:null, cancelReason:null, staffNote: mergedNote, groomingRequest: (bk.groomingRequest||"").trim(), customDuration: pricingDetail?.durationMin || null, voucherId: pickedVoucher?.id || null, customerPackageId: bk.customerPackageId || null, groupId: null, isMobile: cWantMobile, serviceAddress: cWantMobile ? cAddr : null, serviceNotes: cWantMobile ? cNotes : null,
          // Settlement intent — server-side auto-settle trigger uses these to
          // replay wallet/points/voucher deductions even if the explicit
          // settle RPC from the client never fires (race, network blip,
          // stale bundle). Once breakdown columns are populated the
          // trigger is idempotent.
          intendedUseWallet: isReschedule ? 0 : Math.min(toPay, liveUser.wallet),
          intendedUsePoints: ptDiscount,
          intendedVoucherId: pickedVoucher?.id || null,
        };
    const extraApts = [];
    const walletDeduct = isReschedule ? 0 : Math.min(toPay, liveUser.wallet);

    // ── SECURITY: settlement of wallet/points/vouchers moved to the
    //   customer_settle_booking SECURITY DEFINER RPC. The previous flow
    //   PATCHed customer_memberships directly from the client, which
    //   let a malicious customer self-credit wallet/points. To preserve
    //   the legitimate booking UX we INSERT the apt directly (awaited)
    //   so the RPC can FK to it, then run the settle RPC, then mirror
    //   the canonical response to local state.
    //
    //   Why direct insert vs. setState + debounced sync: the previous
    //   timer-based approach (800ms cushion) was a race — if the diff
    //   sync took longer the RPC would 23503. A direct awaited insert
    //   gives us a deterministic happens-before edge.
    try {
      const savedApt = await upsertAppointment(newApt);
      // The database assigns a groomer atomically for "any groomer" requests.
      // Keep the server's choice in the immediate success state as well.
      if (savedApt?.id) newApt = { ...newApt, ...savedApt };
      // Insert sibling appointments for multi-pet bookings. Done sequentially
      // so a partial failure (e.g. RLS rejects one) doesn't leave us in
      // unknown state — we report which pet failed and the customer can
      // retry. The primary insert already succeeded so the booking is
      // partly valid even if one sibling errors.
      for (const ex of extraApts) {
        try { await upsertAppointment(ex); }
        catch (sibErr) {
          // eslint-disable-next-line no-console
          console.error("[PettoBook] sibling apt insert failed:", ex.petId, sibErr);
          toast(zhEn("「", "\"") + (state.pets[ex.petId]?.name || zhEn("另一隻毛孩", "another pet")) + zhEn("」預約建立失敗，請到店補登記", "\" booking failed — please register it at the shop"), "error");
        }
      }
    } catch (insertErr) {
      const writeError = [insertErr?.message, insertErr?.details, insertErr?.hint].filter(Boolean).join(" ");
      if (/BOOKING_(DAY_FULL|SLOT_UNAVAILABLE|DAY_UNAVAILABLE|SHOP_UNAVAILABLE|SERVICE_UNAVAILABLE)/.test(writeError)) {
        setBk(b => ({ ...b, time: "" }));
        setBkBusyRetry(n => n + 1);
        scrollIntoView(scheduleCardRef);
        toast(/BOOKING_DAY_FULL|BOOKING_DAY_UNAVAILABLE/.test(writeError)
          ? zhEn("此日期已滿或不開放預約，請重新選擇日期", "This date is full or not open for booking, please choose another date")
          : zhEn("此時段剛被預約，請重新選擇時間", "This slot was just booked, please choose another time"), "warning");
        return;
      }
      // The INSERT path is sometimes successful while the follow-up
      // SELECT/representation fetch fails (RLS race, network blip, or
      // realtime channel reattach). Probe the DB before declaring failure
      // — if the row is actually present, treat the booking as saved and
      // continue to the success screen instead of leaving the customer
      // staring at an error toast while a real appointment sits in the
      // calendar.
      let actuallySaved = false;
      try {
        const { data: probe } = await supabaseDirect.from("appointments").select("id, date, time, staff_id").eq("id", newApt.id).maybeSingle();
        // For a RESCHEDULE the row always exists (same id), so existence alone
        // is a false positive — the date/time UPDATE could have been rejected
        // (self-update guard) or silently dropped (dead PWA session) while the
        // old row sits there. Require the new date+time to have actually landed;
        // for a fresh booking, existence is the right check.
        actuallySaved = isReschedule
          ? (!!probe?.id && probe.date === newApt.date && probe.time === newApt.time)
          : !!probe?.id;
        if (actuallySaved && !newApt.staffId && probe?.staff_id) newApt = { ...newApt, staffId: probe.staff_id };
      } catch { /* probe is best-effort → treat as not-saved (surface an error) */ }
      if (!actuallySaved) {
        // eslint-disable-next-line no-console
        console.error("[PettoBook] apt insert failed:", insertErr);
        toast(zhEn("預約建立失敗，請重試", "Booking failed, please retry"), "error");
        return;
      }
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] apt INSERT returned an error but the row is present — proceeding to success screen.", insertErr);
    }
    setState(s => {
      // Reschedule = replace the same id in place (no extra row, no
      // "cancelled" sibling). Fresh booking = append.
      const apts = isReschedule
        ? s.appointments.map(a => a.id === newApt.id ? { ...newApt } : a)
        : [...s.appointments, newApt, ...extraApts];
      // Persist the 上門 address on the membership so it auto-fills next time.
      const memberships = (cWantMobile && cAddr && cAddr !== (cMem?.address || ""))
        ? (s.memberships || []).map(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId ? { ...m, address: cAddr } : m)
        : s.memberships;
      return { ...s, appointments: apts, memberships };
    });
    if (cWantMobile && cAddr && cAddr !== (cMem?.address || "")) {
      patchCustomerMembership({ userId: liveUser.id, tenantId: liveUser.tenantId, address: cAddr }).catch(() => {});
    }
    // Skip settlement on reschedule — the original deposit + voucher
    // already booked the slot, this is a date/time edit only.
    if (!isReschedule && (walletDeduct > 0 || ptDiscount > 0 || pickedVoucher)) {
      const settleTask = (async () => {
        let settled = null;
        let lastErr = null;
        for (let attempt = 0; attempt < 3 && !settled; attempt++) {
          if (attempt > 0) await new Promise(r => setTimeout(r, 250 * attempt));
          try {
            settled = await settleBookingDeposit({
              aptId: newApt.id,
              useWallet: walletDeduct,
              usePoints: ptDiscount,
              voucherId: pickedVoucher?.id || null,
            });
          } catch (e) {
            lastErr = e;
            const message = String(e?.message || e || "");
            const status = Number(e?.status || e?.statusCode) || 0;
            const retryable = e?.name === "AbortError"
              || status === 408 || status === 429 || status >= 500
              || /^08/.test(String(e?.code || ""))
              || /failed to fetch|networkerror|network request failed|fetch failed|load failed|connection reset|timeout/i.test(message);
            if (!retryable) break;
          }
        }

        if (settled) {
          // Mirror the server's canonical balance locally without firing
          // another sync round. The server RPC is idempotent by appointment.
          setState(s => ({
            ...s,
            memberships: (s.memberships || []).map(m =>
              (m.userId === liveUser.id && m.tenantId === liveUser.tenantId)
                ? { ...m, wallet: settled.newWallet, points: settled.newPoints, vouchers: pickedVoucher ? (m.vouchers || []).map(v => v.id === pickedVoucher.id ? { ...v, used: true, usedAt: new Date().toISOString(), aptId: newApt.id } : v) : (m.vouchers || []) }
                : m
            ),
            users: { ...s.users, [liveUser.id]: { ...s.users[liveUser.id], wallet: settled.newWallet, points: settled.newPoints } },
          }));
          return;
        }

        // Do not locally consume a voucher when the database has not
        // confirmed settlement. Keep the appointment confirmed and tell the
        // customer the payment state needs a refresh/shop check.
        console.warn("[PettoBook] settle failed:", lastErr);
        const errMsg = String(lastErr?.message || lastErr || "").trim();
        toast(zhEn("預約已建立，但訂金狀態未能確認，請稍後刷新或聯絡店家。", "Booking created, but the deposit status couldn't be confirmed. Please refresh later or contact the shop.") + (errMsg ? zhEn("（", " (") + errMsg.slice(0, 80) + zhEn("）", ")") : ""), "info");
      })().catch(e => {
        console.error("[PettoBook] settlement recovery failed:", e);
        toast(zhEn("預約已建立，但訂金狀態未能確認，請稍後刷新或聯絡店家。", "Booking created, but the deposit status couldn't be confirmed. Please refresh later or contact the shop."), "info");
      });
      let settleWaitTimer;
      const settledInForeground = await Promise.race([
        settleTask.then(() => true),
        new Promise(resolve => { settleWaitTimer = setTimeout(() => resolve(false), 4000); }),
      ]);
      if (settleWaitTimer) clearTimeout(settleWaitTimer);
      if (!settledInForeground) toast(zhEn("預約已建立，正在確認訂金狀態…", "Booking created, confirming deposit status…"), "info");
    }
    // The owner / assigned-staff notifications are now fan-out by the
    // `trg_notify_owner_on_appointment` DB trigger — clients can't insert
    // notifications targeted at someone else (RLS), and pushing them from
    // here caused "invalid uuid 'o1'" + RLS violation toasts.
    if (rescheduleApt) {
      pushNotif(setState, { userId:liveUser.id, type:"booking_confirmed", title:"改期成功 ✅", body:(selPet?.name||"")+" 的預約已改期至 "+bk.date+" "+bk.time, read:false, aptId:newApt.id });
      toast(i18nT("cust.book.rescheduleSuccess"));
    } else {
      pushNotif(setState, { userId:liveUser.id, type:"booking_confirmed", title:"預約確認 ✅", body:(selPet?.name||"")+" 的"+(svc?.name||"")+"已確認，"+bk.date+" "+bk.time, read:false, aptId:newApt.id });
    }
    // AUTO-SEND: trigger booking confirmation message (in-app + email + WA)
    autoSendMessage(rescheduleApt ? "booking_rescheduled" : "booking_created", {
      shopName:tenant?.name||"", customerName:liveUser.name, petName:selPet?.name||"",
      phone:liveUser.phone||"", email:liveUser.email||"",
      date:bk.date, time:bk.time, service:svc?.name||"",
      deposit:deposit, address:tenant?.address||"",
      aptId:newApt.id, customerId:liveUser.id,
    }, state, tenant);
    // ptEarned — mirrors the server-side `award_loyalty_for_apt` formula:
    //   cashBasis = total − walletDeduct − ptDiscount − voucherDiscount
    //   points    = floor(cashBasis / tenant.loyaltyEarnPer)
    // Skipped entirely when the shop has loyalty disabled. The owner sets
    // earn-per in 設定 → 店鋪政策 → 啟用積分回贈.
    const earnPer = Math.max(1, Number(tenant?.loyaltyEarnPer) || 100);
    const ptEarned = tenant?.loyaltyEnabled
      ? Math.max(0, Math.floor(Math.max(0, total - walletDeduct - ptDiscount - voucherDiscount) / earnPer))
      : 0;
    setBookingSuccess({ apt:newApt, pet:selPet, svc:svc?.name||"", total, deposit:walletDeduct, ptEarned });
    setStep(0); setBk({ petId:"", extraPetIds:[], svcId:"", addons:[], spaIds:[], staffId:"", date: dateNow(), time:"", groomingRequest:"", voucherId:"", customerPackageId:"", depositReceiptUrl:"" }); setSig(null); setUsePoints(false); setRescheduleApt(null);
  };

  const handleCheckout = () => {
    // Require a contact phone before any booking can be placed.
    if (!(liveUser.phone || "").trim()) {
      setProfilePhoneDraft({ phone: "", countryCode: liveUser.countryCode || "+852" });
      setNeedPhoneOpen(true);
      return;
    }
    if (!sig && selPet && petAge(selPet.birthday) >= seniorAgeFor(tenant)) { setShowSig(true); return; }
    if (toPay <= liveUser.wallet) submitBooking(sig);
    else setShowPay(true);
  };

  // Resume checkout after the phone gate saved a number — waits for the fresh
  // liveUser.phone to land in state so the handleCheckout guard passes and
  // submitBooking/autoSendMessage see the real phone.
  useEffect(() => {
    if (resumeCheckout && (liveUser.phone || "").trim()) {
      setResumeCheckout(false);
      handleCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCheckout, liveUser.phone]);

  // FIX: effective cancel reason merges preset and custom text
  const effectiveCancelReason = cancelReason === "其他" ? (cancelCustom.trim() || zhEn("其他", "Other")) : cancelReason;

  const cancelBooking = async () => {
    const apt = myApts.find(a => a.id === cancelApt);
    if (!apt) return;
    const isFreeCancel = canCancelFree(apt, tenant);
    const policy = tenant?.cancelPolicy;
    const rawFee = !isFreeCancel && policy ? Math.round((apt.deposit || 0) * (policy.lateCancelFee || 0)) : 0;
    const lateFee = Math.max(0, Math.min(apt.deposit || 0, rawFee));
    // Server-side refund via SECURITY DEFINER RPC. Previous flow PATCHed
    // customer_memberships.wallet from the client which RLS rejects since
    // the 2026-05-23 hardening — the refund was silently dropped on
    // reload. RPC also restores wallet+points in the proportions the
    // deposit was originally paid, and unmarks any voucher.
    let rpcResult = null;
    try {
      rpcResult = await refundCancellation({ aptId: apt.id, lateFee, reason: effectiveCancelReason });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[PettoBook] refundCancellation failed:", e);
      toast(i18nT("tx.cancelFailed") + zhEn("：", ": ") + (e?.message || e), "error");
      return;
    }
    const walletRefund = Number(rpcResult?.walletRefund) || 0;
    const pointsRefund = Number(rpcResult?.pointsRefund) || 0;
    setState(s => {
      const memberships = (s.memberships || []).map(m =>
        (m.userId === liveUser.id && m.tenantId === liveUser.tenantId)
          ? {
              ...m,
              wallet: (Number(m.wallet) || 0) + walletRefund,
              points: (Number(m.points) || 0) + pointsRefund,
              walletTx: walletRefund > 0
                ? [...(m.walletTx || []), { id:"tx"+uid(), type:"topup", amount: walletRefund, note: lateFee > 0 ? ("取消退還訂金 (扣手續費 $" + lateFee + ")") : "取消退還訂金", date: dateNow(), aptId: apt.id }]
                : (m.walletTx || []),
            }
          : m
      );
      return { ...s,
        appointments: s.appointments.map(a => {
          if (a.id !== cancelApt) return a;
          // H1: append a refund entry to payment_log so the booking's
          // ledger reflects the reversal. Sums of (payment − refund) =
          // net cash collected, which EOD now reads.
          const refundEntry = {
            id: "refund" + uid(),
            at: new Date().toISOString(),
            amount: -(walletRefund + pointsRefund),
            method: "refund",
            methodName: zhEn("退款", "Refund"),
            by: liveUser.id,
            type: "refund",
            note: lateFee > 0 ? ("取消（扣手續費 $" + lateFee + "）") : "取消退款",
          };
          return {
            ...a,
            status: "cancelled",
            cancelReason: effectiveCancelReason,
            deposit: lateFee,
            depositConfirmedAt: lateFee > 0 ? a.depositConfirmedAt : null,
            refundedAt: new Date().toISOString(),
            refundAmount: walletRefund + pointsRefund,
            paymentLog: (walletRefund + pointsRefund) > 0 ? [...(a.paymentLog || []), refundEntry] : (a.paymentLog || []),
          };
        }),
        memberships,
      };
    });
    // Owner cancellation notification handled server-side (see
    // trg_notify_owner_on_appointment_status for update-on-status — TODO if
    // we want the owner to be pinged on customer cancellations too).
    // AUTO-SEND: trigger cancellation message to customer
    if (apt) {
      const pet = state.pets[apt.petId];
      autoSendMessage("booking_cancelled", {
        shopName:tenant?.name||"", customerName:liveUser.name, petName:pet?.name||"",
        phone:liveUser.phone||"", email:liveUser.email||"",
        date:apt.date, time:apt.time, service:apt.service,
        reason:effectiveCancelReason,
        aptId: apt.id, customerId: liveUser.id,
      }, state, tenant);
    }
    // `walletRefund` + `pointsRefund` come from refundCancellation's
    // proportional split; sum them for the toast. The old `refund`
    // identifier was orphaned by the RPC migration.
    const totalRefunded = walletRefund + pointsRefund;
    if (totalRefunded > 0) {
      const parts = [];
      if (walletRefund > 0) parts.push(zhEn("錢包 $", "Wallet $") + walletRefund);
      if (pointsRefund > 0) parts.push(zhEn("積分 ", "Points ") + pointsRefund);
      toast(zhEn("預約已取消，已退還 ", "Booking cancelled, refunded ") + parts.join(" + ") + (lateFee > 0 ? zhEn(" (手續費 $", " (fee $") + lateFee + ")" : ""), "warning");
    } else {
      toast(i18nT("cust.book.cancelSuccess"), "warning");
    }
    setCancelApt(null); setCancelReason(""); setCancelCustom("");
  };

  const submitRating = async () => {
    const apt = state.appointments.find(a => a.id === rateApt);
    if (!apt) return;
    // Belt-and-braces: ensure the customer has a membership row for the apt's
    // tenant. Reviews RLS requires either `user_tenant_id() = tenant_id` or
    // an `is_member_of` row. Walk-in customers (created by the shop) may
    // never have gone through the public booking flow that normally inserts
    // the membership, so we self-upsert here. Idempotent if it already exists.
    try {
      await upsertCustomerMembership({
        tenantId: apt.tenantId,
        displayName: liveUser.name || "",
        phone: liveUser.phone || "",
      });
    } catch (e) { /* eslint-disable-next-line no-console */ console.warn("Review membership ensure failed:", e); }
    setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === rateApt ? { ...a, rating } : a), reviews:[...s.reviews, { id:uuid(), tenantId: apt.tenantId, aptId:rateApt, customerId:liveUser.id, staffId:apt.staffId, rating, comment, date:dateNow() }] }));
    toast(zhEn("評價已提交，感謝你的回饋！", "Review submitted, thanks for your feedback!")); setRateApt(null); setRating(5); setComment("");
  };

  const addPet = () => {
    if (!petForm?.name || !petForm?.breed) { toast(i18nT("val.enterFirstNameBreed"), "error"); return; }
    const bErr = petBirthdayError(petForm.birthday); if (bErr) { toast(bErr, "error"); return; }
    if (petForm.id) {
      const updated = { ...state.pets[petForm.id], name:petForm.name, breed:petForm.breed, breedId: petForm.breedId || state.pets[petForm.id]?.breedId || null, weight:parseFloat(petForm.weight)||3, birthday:petForm.birthday||"2020-01-01", type:petForm.type||"dog", species: petForm.species || null, coatLength:petForm.coatLength||"short", allergies:petForm.allergies||zhEn("無", "None"), vaccineDate:petForm.vaccineDate||"", vetNote:petForm.vetNote||"", photos:petForm.photos||[], behavior:petForm.behavior||state.pets[petForm.id]?.behavior||{}, groomingStyle:petForm.groomingStyle||state.pets[petForm.id]?.groomingStyle||{} };
      setState(s => ({ ...s, pets:{ ...s.pets, [petForm.id]:updated } }));
      toast(updated.name + zhEn(" 的資料已更新！", " updated!")); setPetForm(null);
    } else {
      // Pets table RLS requires tenant_id = user_tenant_id() AND owner_id = auth.uid().
      // tenantId was missing from the new-pet payload, which caused the
      // insert to silently violate the policy and return 403.
      const np = { id:uuid(), tenantId:liveUser.tenantId, ownerId:liveUser.id, name:petForm.name, breed:petForm.breed, breedId: petForm.breedId || null, weight:parseFloat(petForm.weight)||3, birthday:petForm.birthday||"2020-01-01", type:petForm.type||"dog", species: petForm.species || null, coatLength:petForm.coatLength||"short", allergies:petForm.allergies||zhEn("無", "None"), vaccineDate:petForm.vaccineDate||"", vetNote:petForm.vetNote||"", photos:petForm.photos||[], logs:[], behavior:{}, groomingStyle:{} };
      setState(s => ({ ...s, pets:{ ...s.pets, [np.id]:np }, users:{ ...s.users, [liveUser.id]:{ ...s.users[liveUser.id], pets:[...(s.users[liveUser.id].pets || []), np.id] } } }));
      toast(np.name + zhEn(" 已成功新增！", " added!")); setPetForm(null);
    }
  };

  const saveRecurring = () => {
    setState(s => ({ ...s, users:{ ...s.users, [liveUser.id]:{ ...s.users[liveUser.id], recurringApts:[...(s.users[liveUser.id].recurringApts||[]), { id:"rec"+uid(), ...recForm }] } } }));
    toast(zhEn("定期預約設定成功", "Recurring booking set up")); setShowRecurring(false);
  };

  const [bookYear, bookMon] = bookMonth.split("-").map(Number);
  const bookFirstDay = new Date(bookYear, bookMon - 1, 1).getDay();
  const bookDaysInMonth = new Date(bookYear, bookMon, 0).getDate();
  // Other customers' bookings + shop events are hidden from this session's
  // state.appointments by RLS. Without these, generateTimeSlots saw an
  // almost-empty conflict set and marked every slot free. Fetch the
  // anonymised busy windows for the active tenant covering a 60-day
  // window so the slot picker is accurate.
  const [bkBusySlots, setBkBusySlots] = useState([]);
  const [bkBusyStatus, setBkBusyStatus] = useState("loading");
  const [bkBusyRetry, setBkBusyRetry] = useState(0);
  useEffect(() => {
    if (!tenant?.id) { setBkBusySlots([]); setBkBusyStatus("ready"); return; }
    let cancelled = false;
    const targetDate = bk.date || dateNow();
    setBkBusyStatus("loading");
    fetchPublicBusySlots(tenant.id, targetDate, targetDate)
      .then(rows => {
        if (cancelled) return;
        setBkBusySlots(rows);
        setBkBusyStatus("ready");
      })
      .catch(err => {
        console.warn("[PettoBook] fetchPublicBusySlots failed:", err);
        if (cancelled) return;
        setBkBusySlots([]);
        setBkBusyStatus("error");
      });
    return () => { cancelled = true; };
  }, [tenant?.id, bk.date, state.appointments?.length, bkBusyRetry]);
  const timeSlots = useMemo(() => bkBusyStatus === "ready"
    ? generateTimeSlots(bk.date, bk.staffId, bk.svcId, tenant, state.appointments, bk.staffId ? state.users?.[bk.staffId] : null, staffList.filter(u => staffCanDo(u, bk.svcId)), bkBusySlots)
    : [], [bk.date, bk.staffId, bk.svcId, tenant, state.appointments, state.users, staffList, bkBusySlots, bkBusyStatus]);

  // FIX: removed the two useEffect hooks that auto-advanced booking steps

  // Customer-side bottom nav. Labels run through i18nT so switching the
  // language pill on the landing page also flips this nav on next render.
  const _loc = i18nUseLocale();
  const walletOn = tenant?.walletEnabled !== false;
  const navTabs = [
    { id:"home", label:i18nT("tab.home"), icon:Home },
    { id:"pets", label:i18nT("tab.pets"), icon:PawPrint },
    { id:"book", label:i18nT("tab.book"), icon:Calendar },
    { id:"rewards", label:i18nT("nav.rewards", _loc !== "zh-HK" ? "Rewards" : "禮遇"), icon:Award },
    ...(walletOn ? [{ id:"wallet", label:i18nT("nav.wallet", _loc !== "zh-HK" ? "Wallet" : "錢包"), icon:Wallet }] : []),
    // 網店 — shops that sell through WooCommerce (same as the iOS customer app).
    ...(tenant?.wcEnabled ? [{ id:"store", label:_loc !== "zh-HK" ? "Shop" : zhEn("網店", "Online"), icon:ShoppingBag }] : []),
    { id:"profile", label:i18nT("tab.profile"), icon:Settings },
  ];

  // Show booking success fullscreen if just booked
  if (bookingSuccess) {
    return (
      <div style={{minHeight:"100vh",background:"#FCFCFF",padding:"40px 20px"}}>
        <BookingSuccess
          apt={bookingSuccess.apt}
          pet={bookingSuccess.pet}
          svc={bookingSuccess.svc}
          total={bookingSuccess.total}
          deposit={bookingSuccess.deposit}
          ptEarned={bookingSuccess.ptEarned}
          tenant={tenant}
          onDone={() => { setBookingSuccess(null); setTab("home"); }}
          onMarkPaid={async ({ aptId, methodName, amount }) => {
            const stamp = new Date().toISOString();
            // Mirror to DB first so the shop's pending-deposits list
            // reflects the claim even if the local push notif fails.
            const aptUpdate = { ...bookingSuccess.apt, depositClaimedAt: stamp };
            try { await upsertAppointment(aptUpdate); } catch (e) { /* fall through to setState */ console.warn("[PettoBook] apt claim update failed:", e); }
            setState(s => ({
              ...s,
              appointments: s.appointments.map(a => a.id === aptId ? { ...a, depositClaimedAt: stamp } : a),
            }));
            // Owner-targeted notification — the notify_owner_on_appointment
            // DB trigger picks this up via the appointments change feed,
            // but a fresh in-app notif row is the most reliable nudge.
            try {
              await pushNotification({
                tenantId: bookingSuccess.apt.tenantId,
                userId: tenant?.ownerId,
                type: "deposit_pending",
                title: "💰 訂金待確認",
                body: ((bookingSuccess.pet?.name || "客戶") + " 已轉帳 $" + amount + (methodName ? "（" + methodName + "）" : "") + "，請查 WhatsApp 並確認"),
                read: false,
                aptId: aptId,
              });
            } catch (e) { /* eslint-disable-next-line no-console */ console.warn("[PettoBook] push deposit-pending notif failed:", e); }
          }}
        />
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#FCFCFF",paddingBottom:120}}>
      <PushPermissionBanner user={user} />
      <div style={{background: tenant?.bannerUrl ? "#1a1a2e" : "linear-gradient(135deg, var(--c-grad1) 0%, var(--c-primary) 100%)",color:"#fff",padding:"40px 24px 60px",borderRadius:"0 0 48px 48px",boxShadow:"0 12px 36px var(--c-primary-30)",position:"relative",overflow:"hidden",minHeight: tenant?.bannerUrl ? 280 : undefined}}>
        {/* Shop banner — full strength with dark scrim. Customers who
            return often see their shop's photography subtly behind the
            greeting, instead of a generic pink template. Falls back to
            the theme gradient when no banner uploaded. */}
        {tenant?.bannerUrl && (
          <>
            <img src={tenant.bannerUrl} alt="" style={{position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:1, zIndex:0}} />
            <div style={{position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.50) 100%)", zIndex:0}} />
          </>
        )}
        {/* Floating pet icons — slightly bolder when over a banner so they
            don't disappear into the photo. */}
        <div style={{position:"absolute",top:12,right:20,opacity: tenant?.bannerUrl ? 0.28 : 0.15, display:"flex",gap:8, zIndex:1}}>
          <PetIcon type="paw" size={24} /><PetIcon type="bone" size={20} /><PetIcon type="heart" size={18} /><PetIcon type="sparkle" size={16} />
        </div>
        <div style={{position:"absolute",bottom:16,left:28,opacity: tenant?.bannerUrl ? 0.22 : 0.1, display:"flex",gap:12, zIndex:1}}>
          <PetIcon type="dog" size={28} /><PetIcon type="cat" size={24} /><PetIcon type="ribbon" size={20} />
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,position:"relative",zIndex:1}}>
          <div>
            {tenant?.name && <p style={{fontSize:12,color:"rgba(255,255,255,0.92)",margin:"0 0 2px",fontWeight:800,letterSpacing:0.3,textShadow: tenant?.bannerUrl ? "0 1px 6px rgba(0,0,0,0.4)" : "none"}}>{tenant.logoUrl ? "✂️ " : ""}{tenant.name}</p>}
            <p style={{fontSize:14,color:"rgba(255,255,255,0.88)",margin:"0 0 4px",fontWeight:700,textShadow: tenant?.bannerUrl ? "0 1px 6px rgba(0,0,0,0.4)" : "none"}}>{zhEn("歡迎回來 🐶", "Welcome back 🐶")}</p>
            <h2 style={{fontSize:26,fontWeight:900,margin:0,textShadow: tenant?.bannerUrl ? "0 2px 10px rgba(0,0,0,0.45)" : "none"}}>{"Hi, " + liveUser.name}</h2>
            {/* Multi-shop switcher — tappable pill opening the ShopSwitcherSheet.
                Only when the customer is a member of 2+ shops. */}
            {(state.memberships || []).length > 1 && tenant && (
              <button onClick={() => setShopSheetOpen(true)} style={{marginTop:10,display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.22)",backdropFilter:"blur(8px)",borderRadius:99,padding:"6px 12px 6px 14px",border:"none",cursor:"pointer",maxWidth:200}}>
                <Store size={14} color="#fff" strokeWidth={2.5} />
                <span style={{color:"#fff",fontWeight:800,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{tenant.name}</span>
                <ChevronDown size={14} color="#fff" strokeWidth={2.5} style={{flexShrink:0}} />
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:12}}>
            <NotifBell userId={liveUser.id} state={state} setState={setState} />
            <CustomerShopChat tenantId={tenant?.id} shopName={tenant?.name} T={T} />
            <button onClick={onLogout} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><LogOut size={22} color="#fff" strokeWidth={2.5} /></button>
          </div>
        </div>
      </div>

      {shopSheetOpen && (
        <ShopSwitcherSheet
          memberships={state.memberships}
          tenants={state.tenants}
          activeTenantId={tenant?.id}
          onSwitch={(slug) => { if (onSwitchShop) onSwitchShop(slug); }}
          onLeave={(tenantId) => { setShopSheetOpen(false); if (onLeaveShop) onLeaveShop(tenantId); }}
          onClose={() => setShopSheetOpen(false)}
        />
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,padding:"0 20px",marginTop:-40,position:"relative",zIndex:10}}>
        {[
          { icon:Wallet,label:i18nT("home.wallet"),val:"$"+liveUser.wallet,c:"#3B82F6",bg:"#EFF6FF" },
          { icon:Award,label:i18nT("home.points"),val:liveUser.points+"pt",c:"#D97706",bg:"#FFFBEB" },
          { icon:Scissors,label:i18nT("home.grooms"),val:myApts.filter(a=>a.status==="done").length + i18nT("home.times"),c:"#10B981",bg:"#F0FDF4" }
        ].map(({ icon:Ic, label, val, c, bg }) => (
          <div key={label} style={{background:"rgba(255,255,255,0.9)",backdropFilter:"blur(24px)",borderRadius:28,padding:"16px 10px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:10,border:"1px solid rgba(255,255,255,0.8)",boxShadow:"0 10px 30px rgba(0,0,0,0.06)"}}>
            <div style={{width:40,height:40,borderRadius:99,background:bg,color:c,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic size={20} strokeWidth={2.5} /></div>
            <div><p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 2px",fontWeight:800}}>{label}</p><p style={{fontSize:17,fontWeight:900,margin:0,color:"#4A4A68"}}>{val}</p></div>
          </div>
        ))}
      </div>

      <div style={{padding:"24px 20px 0"}}>
        {tab === "home" && <>
          {/* Latest shop news teaser — first pinned/newest item + link to
              the full list. Silently hidden when shop has no news yet. */}
          {shopNewsItems.length > 0 && (() => {
            const top = shopNewsItems.slice().sort((a,b) => (b.pinned?1:0) - (a.pinned?1:0) || String(b.published_at||"").localeCompare(String(a.published_at||"")))[0];
            return (
              <button onClick={() => setNewsDetail(top)} style={{width:"100%",display:"flex",alignItems:"stretch",gap:0,background:"#fff",border:"2px solid var(--c-border)",borderRadius:16,marginBottom:12,cursor:"pointer",overflow:"hidden",textAlign:"left",padding:0}}>
                <div style={{width:5,background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))"}} />
                <div style={{flex:1,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:10,fontWeight:900,color:"var(--c-primary)",margin:0,letterSpacing:0.3}}>{zhEn("📣 最新消息", "📣 Latest news")} {top.pinned && <span style={{background:"var(--c-primary)",color:"#fff",fontWeight:900,padding:"1px 6px",borderRadius:99,marginLeft:4}}>{zhEn("置頂", "Pinned")}</span>}</p>
                    <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"3px 0 2px",lineHeight:1.35,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{top.title}</p>
                    {shopNewsItems.length > 1 && <p style={{fontSize:10,color:"#8A8A9E",fontWeight:700,margin:0}}>+{shopNewsItems.length - 1} {zhEn("條消息 · 查看全部 →", " more · view all →")}</p>}
                  </div>
                  {top.image_url && <img src={top.image_url} alt="" style={{width:56,height:56,borderRadius:10,objectFit:"cover",flexShrink:0}} />}
                </div>
              </button>
            );
          })()}
          {/* 詢價 quick-tile — shortcut to post a new request + view
              incoming quotes on existing ones. Hidden until the platform
              enables the RFQ marketplace (platformConfig.rfqEnabled). */}
          {state.platformConfig?.rfqEnabled === true && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <button onClick={() => setPostRfqOpen(true)} style={{padding:"14px 12px",borderRadius:18,border:"2px solid #DDD6FE",background:"linear-gradient(135deg,#FAF5FF,#EDE9FE)",cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",gap:4}}>
              <span style={{fontSize:18}}>📢</span>
              <p style={{fontSize:13,fontWeight:900,color:"#5B21B6",margin:0}}>{zhEn("出價搵店", "Request quotes")}</p>
              <p style={{fontSize:10,color:"#7C3AED",margin:0,fontWeight:700,opacity:0.85}}>{zhEn("等附近店主報價", "Nearby shops send you quotes")}</p>
            </button>
            <button onClick={() => setMyRfqOpen(true)} style={{padding:"14px 12px",borderRadius:18,border:"2px solid #FED7AA",background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)",cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",gap:4}}>
              <span style={{fontSize:18}}>📨</span>
              <p style={{fontSize:13,fontWeight:900,color:"#9A3412",margin:0}}>{zhEn("我的詢價", "My quote requests")}</p>
              <p style={{fontSize:10,color:"#C2410C",margin:0,fontWeight:700,opacity:0.85}}>{zhEn("睇店主報價 + 接受", "View shop quotes + accept")}</p>
            </button>
          </div>}
          {/* Missing-phone banner. Google login only gives us email; without
              a phone the shop can't reach the customer for confirmations. */}
          {!liveUser?.phone && (
            <div onClick={() => setTab("profile")} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderRadius:16,marginBottom:12,background:"#FEF3C7",border:"2px solid #FCD34D",cursor:"pointer"}}>
              <span style={{fontSize:20}}>📞</span>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"0 0 2px"}}>{i18nT("cust.contactPhone")}</p>
                <p style={{fontSize:11,color:"#92400E",margin:0,fontWeight:700}}>{zhEn("美容師需要電話聯絡您 · 點擊前往「個人」分頁", "The groomer needs your phone number · tap to go to the Profile tab")}</p>
              </div>
              <ChevronRight size={16} color="#92400E" strokeWidth={2.5} />
            </div>
          )}
          {/* Slim rewards summary — replaces the big stamp grid that used to
              live on home. Taps through to the 📇 禮遇 tab for full detail
              (stamp grid / unlocked rewards / voucher list / points). */}
          {(() => {
            const sc = tenant?.stampCard;
            const myMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
            const stamps = sc?.enabled ? Math.min(Number(myMem?.stamps || 0), sc.totalSlots || 10) : 0;
            const totalSlots = sc?.totalSlots || 10;
            const voucherCount = (myMem?.vouchers || []).filter(v => !v.used).length;
            const pointsBal = Number(myMem?.points || 0);
            const loyaltyOn = !!tenant?.loyaltyEnabled;
            const stampOn = sc?.enabled && shopHasStampCard(tenant);
            if (!stampOn && voucherCount === 0 && !(loyaltyOn && pointsBal > 0)) return null;
            const bits = [];
            if (stampOn) bits.push(zhEn(`📇 ${stamps}/${totalSlots} 印章`, `📇 ${stamps}/${totalSlots} stamps`));
            if (voucherCount > 0) bits.push(zhEn(`🎁 ${voucherCount} 張券`, `🎁 ${voucherCount} coupons`));
            if (loyaltyOn && pointsBal > 0) bits.push(zhEn(`⭐ ${pointsBal} 積分`, `⭐ ${pointsBal} points`));
            return (
              <div onClick={() => setTab("rewards")} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"14px 18px",borderRadius:18,background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)",border:"2px solid #FED7AA",cursor:"pointer",marginBottom:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:11,fontWeight:800,color:"#9A3412",margin:"0 0 2px",opacity:0.7,letterSpacing:0.5}}>{zhEn("我的禮遇", "My perks")}</p>
                  <p style={{fontSize:14,fontWeight:900,color:"#9A3412",margin:0}}>{bits.join(" · ")}</p>
                </div>
                {voucherCount > 0 && (
                  <button onClick={e => { e.stopPropagation(); setMyVouchersOpen(true); }}
                    style={{background:"var(--c-primary)",color:"#fff",border:"none",borderRadius:99,padding:"6px 12px",fontSize:11,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>
                    {zhEn("🎫 查看券", "🎫 View coupons")}
                  </button>
                )}
                <ChevronRight size={18} color="#9A3412" strokeWidth={2.8} />
              </div>
            );
          })()}
          {/* Legacy big stamp card — kept disabled via `false &&` so it
              doesn't render. Detail view lives on the rewards tab now. */}
          {false && (() => {
            const sc = tenant?.stampCard;
            const myMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
            if (!sc?.enabled) return null;
            const totalSlots = sc.totalSlots || 10;
            const stamps = Math.min(Number(myMem?.stamps || 0), totalSlots);
            const milestones = [...(sc.milestones || [])].sort((a, b) => (a.atSlot || 0) - (b.atSlot || 0));
            const nextMs = milestones.find(m => (m.atSlot || 0) > stamps);
            const stampsToNext = nextMs ? Math.max(0, (nextMs.atSlot || 0) - stamps) : 0;
            // Rewards bar above grid: collapse all milestones into a single
            // horizontal pill list with full reward names so customer can
            // see what they're working toward without squinting at 9px text.
            return (
              <Card style={{padding:0,marginBottom:12,background:"#FFFDFA",border:"2px solid #FED7AA",overflow:"hidden"}}>
                {/* Header band — pet-friendly warm gradient with the brand
                    voice. Bigger "stamps / total" badge. */}
                <div style={{padding:"16px 20px",background:"linear-gradient(135deg,#FB923C,#F97316)",color:"#fff"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                    <h3 style={{fontWeight:900,fontSize:19,margin:0,display:"flex",alignItems:"center",gap:8,letterSpacing:0.5}}>
                      <span style={{fontSize:22}}>📇</span> {zhEn("我的印花卡", "My stamp card")}
                    </h3>
                    <div style={{background:"rgba(255,255,255,0.22)",padding:"4px 12px",borderRadius:99,backdropFilter:"blur(8px)"}}>
                      <span style={{fontSize:15,fontWeight:900}}>{stamps}</span>
                      <span style={{fontSize:11,fontWeight:700,opacity:0.85}}> / {totalSlots}</span>
                    </div>
                  </div>
                  {/* Progress bar replacing the "再 N 印" text line — visual,
                      scannable, leaves more room for big stamp circles. */}
                  <div style={{height:6,background:"rgba(255,255,255,0.25)",borderRadius:99,overflow:"hidden",marginTop:6}}>
                    <div style={{height:"100%",width:`${(stamps / totalSlots) * 100}%`,background:"#fff",borderRadius:99,transition:"width 0.4s ease"}} />
                  </div>
                  <p style={{fontSize:12,margin:"8px 0 0",fontWeight:700,opacity:0.95}}>
                    {nextMs
                      ? zhEn(`再 ${stampsToNext} 個印章獲贈「${milestoneLiveLabel(nextMs, tenant)}」🎁`, `${stampsToNext} more stamps to earn "${milestoneLiveLabel(nextMs, tenant)}" 🎁`)
                      : (milestones.length === 0 ? zhEn("完成預約即獲印章", "Complete a booking to earn a stamp") : zhEn("🎊 印滿啦，下次預約開新卡", "🎊 Card full! A new card starts with your next booking"))}
                  </p>
                </div>

                {/* Stamp grid — boxes (not circles) so milestone slots can
                    fit the reward name INSIDE. Regular slots = orange box
                    with a paw or stamp number. Milestone slots = green box
                    with the reward label baked in. Earned slots flip to a
                    filled gradient with a checkmark or 🎉. */}
                <div style={{padding:"16px 14px"}}>
                  {/* 已解鎖獎賞 — vouchers earned via stamp-card milestones
                      that haven't been used yet. Tapping 「立即使用」 jumps
                      to the booking flow with the voucher pre-selected so
                      the customer doesn't have to hunt for it.
                      Supports both "stampcard" (new RPC) and "stamp_card"
                      (legacy client) source markers. */}
                  {(() => {
                    const unlocked = (myMem?.vouchers || []).filter(v => !v.used && (v.source === "stampcard" || v.source === "stamp_card"));
                    if (unlocked.length === 0) return null;
                    return (
                      <div style={{background:"linear-gradient(135deg,#ECFDF5,#D1FAE5)",border:"2px solid #6EE7B7",borderRadius:16,padding:"12px 14px",marginBottom:14}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <p style={{fontSize:13,fontWeight:900,color:"#065F46",margin:0,display:"flex",alignItems:"center",gap:6}}>
                            {zhEn("🔓 已解鎖獎賞", "🔓 Rewards unlocked")} <span style={{fontSize:11,padding:"1px 8px",borderRadius:99,background:"#10B981",color:"#fff",fontWeight:900}}>{unlocked.length}</span>
                          </p>
                          <span style={{fontSize:10,color:"#065F46",fontWeight:700,opacity:0.8}}>{zhEn("下次預約時自動套用", "Applied automatically on your next booking")}</span>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {unlocked.map(v => (
                            <div key={v.id} style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1.5px solid #A7F3D0",display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:22}}>🎁</span>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontSize:13,fontWeight:900,color:"#065F46",margin:0}}>{liveVoucherLabel(v, tenant) || zhEn("印花卡獎勵", "Stamp card reward")}</p>
                                <p style={{fontSize:10,color:"#047857",margin:"2px 0 0",fontWeight:700,opacity:0.8}}>
                                  {v.redeemedAt ? zhEn("獲得日期 ", "Earned on ") + (v.redeemedAt || "").slice(0,10) : zhEn("印花卡贈送", "Stamp card reward")}
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  setBk(b => ({ ...b, voucherId: v.id }));
                                  setTab("book");
                                  toast(zhEn("已套用：", "Applied: ") + (v.label || zhEn("獎勵", "Reward")) + zhEn("，下一步預約即可使用 🎁", " — use it in the next booking step 🎁"), "success");
                                }}
                                style={{padding:"7px 14px",borderRadius:99,background:"#10B981",border:"none",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 3px 8px rgba(16,185,129,0.35)"}}
                              >{zhEn("立即使用 →", "Use now →")}</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(totalSlots, 5)}, minmax(0, 1fr))`,gap:8,width:"100%"}}>
                    {Array.from({length: totalSlots}, (_, i) => {
                      const slot = i + 1;
                      const filled = slot <= stamps;
                      const ms = milestones.find(m => m.atSlot === slot);
                      const reached = ms && slot <= stamps;
                      // Reward label is the hero copy inside milestone
                      // boxes — bump significantly so it's actually
                      // legible on a phone. Auto-scale by string length so
                      // "$50 折扣" gets big type while "免費 SPA 高級護理"
                      // still fits without overflowing.
                      // Shrink fonts so labels of any length fit without
                      // truncation; user feedback was the previous 11-17px
                      // range was too big on multi-line labels.
                      const labelLen = ms?.rewardLabel?.length || 0;
                      // clamp() so the tile font grows on iPad (≥768px) but
                      // stays compact on phones. Min: phone-fit. Max: tablet.
                      // Three buckets by label length so long labels still
                      // shrink to fit.
                      const fontPx = ms
                        ? (labelLen > 10 ? "clamp(11px, 1.6vw, 18px)" : labelLen > 7 ? "clamp(12px, 1.9vw, 20px)" : labelLen > 4 ? "clamp(14px, 2.2vw, 24px)" : "clamp(16px, 2.6vw, 28px)")
                        : "clamp(16px, 2.6vw, 28px)";
                      const fullRefName = (() => {
                        const vc = ms?.voucher || {};
                        if (vc.type === "free_spa") return (tenant?.services || []).find(s => s.id === vc.refId)?.name || "";
                        if (vc.type === "free_addon") return (tenant?.addons || []).find(a => a.id === vc.refId)?.name || "";
                        return "";
                      })();
                      return (
                        <div key={i} style={{
                          aspectRatio:"1",
                          borderRadius:14,
                          padding:"6px 4px",
                          background: filled
                            ? (ms ? "linear-gradient(135deg,#34D399,#10B981)" : "linear-gradient(135deg,#FB923C,#F97316)")
                            : (ms ? "#fff" : "#FFF7ED"),
                          border: ms
                            ? (filled ? "2px solid #10B981" : "2px dashed #10B981")
                            : (filled ? "2px solid #F97316" : "2px dashed #FED7AA"),
                          display:"flex",
                          flexDirection:"column",
                          alignItems:"center",
                          justifyContent:"space-between",
                          boxShadow: filled
                            ? (ms ? "0 6px 18px rgba(16,185,129,0.45)" : "0 4px 12px rgba(249,115,22,0.4)")
                            : "inset 0 2px 4px rgba(0,0,0,0.04)",
                          transition:"all 0.3s",
                          position:"relative",
                          overflow:"hidden",
                          cursor: fullRefName ? "pointer" : "default",
                        }}
                        title={fullRefName || undefined}
                        onClick={fullRefName ? () => toast(fullRefName, "info") : undefined}>
                          {/* Top row — slot number on left, status icon on right */}
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
                            <span style={{fontSize:9,fontWeight:900,color: filled ? "rgba(255,255,255,0.85)" : (ms ? "#10B981" : "#FB923C"),letterSpacing:0.5}}>#{slot}</span>
                            {filled && <span style={{fontSize:"clamp(13px, 2vw, 20px)",color:"#fff"}}>{reached ? "🎉" : "✓"}</span>}
                          </div>

                          {/* Centre — milestone gets the reward name; regular
                              slots get a paw print or large slot number */}
                          {ms ? (
                            <div style={{textAlign:"center",lineHeight:1.15,padding:"0 1px",flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                              <div style={{fontSize:"clamp(13px, 1.8vw, 20px)",marginBottom:2,lineHeight:1}}>🎁</div>
                              {(() => {
                                const [line1, line2] = splitMilestoneLabel(milestoneLiveLabel(ms, tenant));
                                const sty = {margin:0,fontSize:fontPx,fontWeight:900,color: filled ? "#fff" : "#065F46",wordBreak:"break-word",textShadow: filled ? "0 1px 2px rgba(0,0,0,0.2)" : "none",lineHeight:1.2};
                                return line2 ? (<><p style={{...sty,opacity:0.95}}>{line1}</p><p style={sty}>{line2}</p></>) : (<p style={sty}>{line1}</p>);
                              })()}
                            </div>
                          ) : (
                            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              <span style={{fontSize: filled ? 24 : 20,opacity: filled ? 1 : 0.4}}>{filled ? "🐾" : "🐾"}</span>
                            </div>
                          )}

                          {/* Bottom hint for unfilled milestone slots */}
                          {ms && !filled && (
                            <div style={{fontSize:7,fontWeight:800,color:"#10B981",opacity:0.7,letterSpacing:0.3}}>
                              REWARD
                            </div>
                          )}
                          {ms && filled && (
                            <div style={{fontSize:7,fontWeight:900,color:"rgba(255,255,255,0.95)",letterSpacing:0.3}}>
                              EARNED
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* "How to use" footnote — keeps the card self-explanatory
                      without competing with the visual grid above. */}
                  <p style={{fontSize:10,color:"#9A3412",margin:"12px 4px 0",fontWeight:700,opacity:0.7,textAlign:"center"}}>
                    {zhEn("🐾 = 印章格 · 🎁 = 獎勵格（集到自動發送）", "🐾 = stamp slot · 🎁 = reward slot (sent automatically when reached)")}
                  </p>
                </div>
              </Card>
            );
          })()}
          {/* Big prominent CTA — booking from home without going to bottom nav */}
          <button onClick={() => { setTab("book"); setStep(0); }} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,width:"100%",padding:"18px 20px",borderRadius:24,border:"none",background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))",color:"#fff",fontSize:17,fontWeight:900,cursor:"pointer",marginBottom:16,boxShadow:"0 12px 32px var(--c-primary-30)",letterSpacing:"0.3px"}}>
            <Calendar size={22} strokeWidth={2.8} />
            <span>{i18nT("btn.bookNow")}</span>
            <span style={{fontSize:14,fontWeight:700,opacity:0.85,marginLeft:4}}>→</span>
          </button>

          {/* Low-balance / expiry alerts — surface high-priority warnings
              ABOVE the regular packages card so customers act before they
              lapse. Deduped per (cp.id + bucket) via localStorage so we
              don't pester on every visit. */}
          {(() => {
            const alerts = [];
            for (const cp of (myPackages || [])) {
              const def = shopPackages.find(p => p.id === cp.packageId);
              const threshold = Number(def?.lowBalanceThreshold) || 2;
              const lowB = cp.remainingVisits > 0 && cp.remainingVisits <= threshold;
              const daysLeft = cp.expiresAt ? Math.ceil((new Date(cp.expiresAt).getTime() - Date.now()) / 86400000) : null;
              const expSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 14;
              if (lowB || expSoon) alerts.push({ cp, def, lowB, expSoon, daysLeft });
            }
            if (alerts.length === 0) return null;
            return (
              <Card style={{padding:14,marginBottom:12,background:"linear-gradient(135deg,#FEE2E2,#FECACA)",border:"2px solid #FCA5A5"}}>
                <p style={{fontSize:13,fontWeight:900,color:"#991B1B",margin:"0 0 8px"}}>{zhEn("⚠️ 套票即將用完或到期", "⚠️ Package almost used up or expiring")}</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {alerts.map(({cp, def, lowB, expSoon, daysLeft}) => (
                    <div key={cp.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#991B1B",fontWeight:700}}>
                      <span style={{fontSize:14}}>·</span>
                      <span style={{flex:1,minWidth:0}}>
                        <strong>{def?.name || zhEn("套票", "Package")}</strong>
                        {lowB && <span> {zhEn("· 剩", "· ")} {cp.remainingVisits} {zhEn("次", "times")}</span>}
                        {expSoon && <span> · {daysLeft} {zhEn("日後到期", "days until expiry")}</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{fontSize:11,color:"#991B1B",margin:"10px 0 0",fontWeight:700,opacity:0.8}}>{zhEn("請聯絡店家續購或安排使用", "Please contact the shop to renew or arrange use")}</p>
              </Card>
            );
          })()}

          {/* Unified packages card. Two render paths:
              (a) Customer HAS active packages → show owned + a 訂購更多 inline
                  for each available shop package (renewal CTA).
              (b) Customer has NO packages → marketing card with regular price
                  ($/visit × visits) struck through next to the deal price,
                  highlighting savings to drive first purchase.
              Buying flow is identical: 訂購 fires the in-app request RPC,
              WhatsApp opens a prefilled chat. */}
          {(() => {
            const availablePackages = shopPackages.filter(p => p.enabled);
            if (availablePackages.length === 0 && myPackages.length === 0) return null;

            // Compute "regular" (no-deal) price per visit for a package. We pick
            // the highest-priced eligible service (or the first if none is
            // priced), assuming the customer would otherwise pay that price per
            // visit. Falls back to dividing the package price by visits if no
            // service price is set up — produces 0 savings, but at least the
            // pricing line doesn't break.
            const services = tenant?.services || [];
            const regularPricePerVisit = (pkg) => {
              const eligible = (pkg.serviceIds || []).map(id => services.find(s => s.id === id)).filter(Boolean);
              const pool = eligible.length ? eligible : services.filter(s => s.kind !== "spa");
              const prices = pool.map(s => Number(s.basePrice) || 0).filter(n => n > 0);
              if (prices.length === 0) return 0;
              return Math.max(...prices);
            };

            // Resolve the actual package price the customer would pay. When
            // the package has `pricing.mode === "perSize"`, the flat
            // `package.price` is just a legacy fallback — the real cost
            // depends on the pet's size band + coat length. Match the
            // customer's first pet (or fall back to a sensible default size)
            // to a price tier. Returns null when no matching tier so the UI
            // can show "$X 起" instead of a wrong flat number.
            const myFirstPet = myPets[0];
            const sizeLabelForPet = (pet) => {
              const w = parseFloat(pet?.weight) || 0;
              // Default matrix sizes from seedDefaults.js. The shop's matrix
              // may use slightly different labels but the package pricing
              // dictionary is keyed by these exact strings, so we match by
              // label.
              if (w <= 5) return "小型犬";
              if (w <= 10) return "中型犬";
              if (w <= 15) return "大型犬";
              if (w <= 20) return "超大型犬";
              return "巨型犬";
            };
            const packagePriceFor = (pkg, pet) => {
              const mode = pkg?.pricing?.mode;
              if (mode !== "perSize") return { price: Number(pkg.price) || 0, label: null };
              const perSize = pkg.pricing.perSize || {};
              const perBreed = pkg.pricing.perBreed || {};
              if (pet) {
                const isLong = pet.coatLength === "long";
                const pick = (row) => Number(isLong ? row.longHair : row.shortHair) || Number(row.shortHair) || Number(row.longHair) || 0;
                // Resolve the pet's tier by the shop's real tiers; try the legacy
                // hardcoded label too so packages keyed under either scheme match.
                const tier = petSizeTier(pet, tenant);
                const labels = [tier?.label, sizeLabelForPet(pet)].filter(Boolean);
                // Special breed price OVERRIDES the size price when set.
                const sb = matchSpecialBreed(pet, tenant);
                if (sb && perBreed[sb.id]) {
                  for (const lb of labels) {
                    const br = perBreed[sb.id][lb];
                    if (br) { const bp = pick(br); if (bp > 0) return { price: bp, label: (sb.name || (sb.names || [])[0] || zhEn("特殊品種","Special breed")) + " · " + lb + (isLong ? zhEn(" 長毛", " long coat") : "") }; }
                  }
                }
                for (const lb of labels) {
                  const row = perSize[lb];
                  if (row) return { price: pick(row), label: lb + " · " + (isLong ? zhEn("長毛", "Long coat") : zhEn("短毛", "Short coat")) };
                }
              }
              // No matching pet → cheapest across size + breed tiers as "from".
              const allPrices = [...Object.values(perSize), ...Object.values(perBreed).flatMap(b => Object.values(b || {}))]
                .flatMap(r => [Number(r.shortHair) || 0, Number(r.longHair) || 0]).filter(n => n > 0);
              if (allPrices.length === 0) return { price: Number(pkg.price) || 0, label: null };
              return { price: Math.min(...allPrices), label: zhEn("起", "+") };
            };

            const buyViaWhatsApp = (p) => {
              const rawPhone = (tenant?.phone || "").replace(/\D/g, "");
              if (!rawPhone) {
                toast(zhEn("店家未設定電話，請改用「應用內訂購」", "The shop hasn't set a phone number; use \"Order in app\" instead"), "error");
                return;
              }
              const phone = rawPhone.length >= 10 ? rawPhone : "852" + rawPhone;
              // Use the per-size price computed for the customer's pet — the
              // raw `p.price` is just a legacy flat fallback and would mislead
              // the shop if their package is perSize-priced.
              const { price: realPrice, label: sizeLabel } = packagePriceFor(p, myFirstPet);
              const lines = [
                "你好，我想購買套票：",
                "📦 " + p.name + " ($" + realPrice + (sizeLabel ? " · " + sizeLabel : "") + ")",
                "次數：" + p.visits + " 次" + (p.validityDays > 0 ? " · " + p.validityDays + " 日有效" : ""),
                "客戶：" + (liveUser?.name || liveUser?.email || ""),
                myFirstPet ? ("毛孩：" + (myFirstPet.name || "") + (myFirstPet.weight ? " · " + myFirstPet.weight + "kg" : "")) : null,
                "請問如何付款？",
              ].filter(Boolean).join("\n");
              window.open("https://wa.me/" + phone + "?text=" + encodeURIComponent(lines), "_blank", "noopener,noreferrer");
            };
            const orderInApp = async (p) => {
              try {
                const n = await requestPackagePurchase({ tenantId: tenant.id, packageId: p.id });
                if (n > 0) toast(zhEn("✅ 已通知店家，等待回覆付款方式", "✅ Shop notified — waiting for payment instructions"), "success");
                else toast(zhEn("店家暫未有負責人接收通知，請改用 WhatsApp", "No one at the shop is set up to receive notifications; please use WhatsApp instead"), "info");
              } catch (e) {
                const msg = e?.message || String(e);
                if (/not_authenticated/i.test(msg)) toast(zhEn("請先登入再訂購", "Please log in before ordering"), "error");
                else if (/package_not_found/i.test(msg)) toast(i18nT("tx.packageDelisted"), "error");
                else toast(zhEn("訂購失敗：", "Order failed: ") + msg, "error");
              }
            };

            const OrderButtons = ({ p }) => (
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={() => orderInApp(p)} title={zhEn("在應用內訂購", "Order in app")}
                  style={{fontSize:11,fontWeight:900,color:"#fff",background:"var(--c-primary)",border:"none",borderRadius:99,padding:"6px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>{zhEn("📦 訂購", "📦 Order")}</button>
                <button onClick={() => buyViaWhatsApp(p)} title={zhEn("WhatsApp 聯絡店家", "Contact shop on WhatsApp")}
                  style={{fontSize:11,fontWeight:900,color:"#fff",background:"#25D366",border:"none",borderRadius:99,padding:"6px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>WhatsApp</button>
              </div>
            );

            const hasActive = myPackages.length > 0;
            // Surface renewal buttons only when at least one owned package is
            // running low (≤ low_balance_threshold visits) or expiring within
            // 30 days. Keeps the home card uncluttered for healthy packages
            // while still letting customers re-purchase one tap before they
            // lapse. The 套票優惠 marketing block only appears for customers
            // with NO active package.
            const renewalNeeded = myPackages.some(cp => {
              const def = shopPackages.find(p => p.id === cp.packageId);
              const threshold = Number(def?.lowBalanceThreshold) || 2;
              const lowB = cp.remainingVisits > 0 && cp.remainingVisits <= threshold;
              const daysLeft = cp.expiresAt ? Math.ceil((new Date(cp.expiresAt).getTime() - Date.now()) / 86400000) : null;
              const expSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;
              return lowB || expSoon;
            });

            return (
              <Card style={{padding:16,marginBottom:16,background: hasActive ? "linear-gradient(135deg, #FEF3C7, #FFFFFF)" : "#fff",border: hasActive ? "2px solid #FCD34D" : "2px solid var(--c-light)"}}>
                <p style={{fontSize:13,fontWeight:900,color:hasActive?"#92400E":"#4A4A68",margin:"0 0 10px",display:"flex",alignItems:"center",gap:6}}>📦 {hasActive ? zhEn("您的套票", "Your packages") : zhEn("套票優惠 — 買越多越抵", "Package deals — the more you buy, the more you save")} <HelpTip topic="packages" /></p>

                {/* Owned packages. Each row shows progress + (when this card
                    is running low) inline 訂購 / WhatsApp buttons so the
                    customer can re-purchase the exact same package without
                    scrolling through the catalogue. */}
                {hasActive && (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {myPackages.map(cp => {
                      const pkg = shopPackages.find(p => p.id === cp.packageId);
                      const threshold = Number(pkg?.lowBalanceThreshold) || 2;
                      const lowB = cp.remainingVisits > 0 && cp.remainingVisits <= threshold;
                      const daysLeft = cp.expiresAt ? Math.ceil((new Date(cp.expiresAt).getTime() - Date.now()) / 86400000) : null;
                      const expSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;
                      const showRenew = lowB || expSoon;
                      return (
                        <div key={cp.id} style={{background:"rgba(255,255,255,0.85)",borderRadius:12,padding:"10px 12px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{pkg?.name || zhEn("套票", "Package")}</p>
                              <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>
                                {zhEn("剩餘", "Left")} <strong style={{color:cp.remainingVisits === 0 ? "#EF4444" : "var(--c-primary)"}}>{cp.remainingVisits}</strong> {zhEn("次", "times")}
                                {cp.expiresAt && <span style={{color:expSoon?"#EF4444":"#8A8A9E"}}> · {cp.expiresAt.slice(0, 10)} {zhEn("到期", "Expires")}</span>}
                              </p>
                            </div>
                            <div style={{background:"var(--c-primary)",color:"#fff",borderRadius:99,minWidth:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:15}}>{cp.remainingVisits}</div>
                          </div>
                          {/* Inline renewal: only when this specific package is
                              low / expiring. Buttons re-purchase the same SKU
                              at the price for the customer's pet (per-size). */}
                          {showRenew && pkg && (() => {
                            const { price, label } = packagePriceFor(pkg, myFirstPet);
                            return (
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,paddingTop:10,borderTop:"1px dashed #FCD34D",gap:8}}>
                                <p style={{fontSize:11,fontWeight:800,color:"#92400E",margin:0,flex:1}}>{zhEn("快用完？續購 $", "Running low? Renew for $")}{price}{label ? " (" + label + ")" : ""}</p>
                                <OrderButtons p={pkg} />
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Marketing block — first-time purchase incentive with
                    struck-through regular total + green savings pill. Only
                    shown when the customer has zero active packages so we're
                    not duplicating the renewal CTAs above. */}
                {!hasActive && availablePackages.length > 0 && (
                  <>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 10px",fontWeight:700}}>{zhEn("單次美容 vs 套票價，慳到笑 😀", "Single grooming vs package price — save a lot 😀")}</p>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {availablePackages.map(p => {
                        const { price: pkgPrice, label: sizeLabel } = packagePriceFor(p, myFirstPet);
                        const regularPerVisit = regularPricePerVisit(p);
                        const regularTotal = regularPerVisit * (p.visits || 0);
                        const savings = Math.max(0, regularTotal - pkgPrice);
                        const savingsPct = regularTotal > 0 ? Math.round((savings / regularTotal) * 100) : 0;
                        return (
                          <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--c-lighter)",borderRadius:12,padding:"10px 12px",gap:10}}>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0}}>{p.name}</p>
                              <p style={{fontSize:10,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>
                                {p.visits} {zhEn("次 ·", "visits ·")} {p.validityDays > 0 ? p.validityDays + zhEn(" 日有效", " days valid") : zhEn("永久", "No expiry")}{sizeLabel ? " · " + sizeLabel : ""}
                              </p>
                              <p style={{fontSize:11,margin:"4px 0 0",fontWeight:800,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                {regularTotal > 0 && <span style={{color:"#9CA3AF",textDecoration:"line-through"}}>${regularTotal}</span>}
                                <span style={{color:"var(--c-primary)",fontWeight:900,fontSize:13}}>${pkgPrice}</span>
                                {savings > 0 && <span style={{color:"#10B981",background:"#D1FAE5",borderRadius:99,padding:"1px 6px",fontWeight:900,fontSize:10}}>{zhEn("慳 $", "Save $")}{savings}{savingsPct > 0 ? " · " + savingsPct + "% off" : ""}</span>}
                              </p>
                              {/* Multi-pet: each pet is priced separately (size /
                                  special breed), so show a per-pet price line.
                                  A package is bought per pet at that pet's price. */}
                              {myPets.length > 1 && p.pricing?.mode === "perSize" && (
                                <div style={{marginTop:6,paddingTop:6,borderTop:"1px dashed var(--c-border)",display:"flex",flexDirection:"column",gap:3}}>
                                  {myPets.map(pt => {
                                    const { price: pp, label: pl } = packagePriceFor(p, pt);
                                    return (
                                      <div key={pt.id} style={{display:"flex",justifyContent:"space-between",gap:8,fontSize:11,fontWeight:700,color:"#6A6A85"}}>
                                        <span style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🐾 {pt.name}{pl ? " · " + pl : ""}</span>
                                        <span style={{color:"var(--c-primary)",fontWeight:900,flexShrink:0}}>${pp}</span>
                                      </div>
                                    );
                                  })}
                                  <p style={{fontSize:10,color:"#AFAFC4",fontWeight:700,margin:"2px 0 0"}}>{zhEn("每隻毛孩一張套票（按其體型 / 品種價）", "One package per pet (priced by its size / breed)")}</p>
                                </div>
                              )}
                            </div>
                            <OrderButtons p={p} />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {/* Suppress unused-var lint for renewalNeeded — kept for
                    potential future "show only one badge" optimizations. */}
                {renewalNeeded && null}
              </Card>
            );
          })()}
          {/* Safety net (A): only show the "美容進行中" hero when the apt
              date is TODAY. If a shop forgot to close out an apt from a
              prior day, the customer would otherwise see their pet
              "still grooming" forever. Past-date stale apts will be
              auto-closed nightly by the auto_complete_past_stale_appointments
              cron, but this client-side guard hides the strangeness
              between status drift and cron run. */}
          {myApts.filter(a => a.status === "grooming" && a.date === dateNow()).map(a => (
            <Card key={a.id} style={{padding:20,marginBottom:16,border:"2px solid #DBEAFE",background:"linear-gradient(to right, #ffffff, #F0F9FF)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <div style={{width:10,height:10,background:"#3B82F6",borderRadius:99}} />
                <span style={{fontSize:14,fontWeight:800,color:"#1D4ED8"}}>{zhEn("美容進行中 🛁", "Grooming in progress 🛁")}</span>
              </div>
              <p style={{fontWeight:800,fontSize:18,margin:"0 0 6px",color:"#4A4A68"}}>{((state.pets[a.petId]?.name || a.petName) || zhEn("(已刪除毛孩)", "(deleted pet)")) + " — " + a.service}</p>
              <p style={{fontSize:14,color:"#6A6A85",margin:0,fontWeight:600}}>{zhEn("美容師: ", "Groomer: ") + (state.users[a.staffId]?.name||"") + " · " + a.time}</p>
            </Card>
          ))}
          {(state.announcements||[]).filter(a => a.active && (a.audience || "both") !== "shop").map(a => (
            <Card key={a.id} style={{padding:16,marginBottom:16,background:"var(--c-lighter)",border:"2px solid var(--c-border)"}}>
              <div style={{display:"flex",gap:12}}><div style={{background:"var(--c-primary)",width:36,height:36,borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Megaphone size={18} color="#fff" strokeWidth={2.5} /></div><div><p style={{fontWeight:800,color:"var(--c-dark)",margin:"0 0 4px",fontSize:15}}>{a.title}</p><p style={{fontSize:14,color:"var(--c-primary)",margin:0,fontWeight:600,lineHeight:1.4}}>{a.body}</p></div></div>
            </Card>
          ))}
          <Card style={{padding:20,marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("cust.hist.recentBookings")} <HelpTip topic="bookingFlow" /></h3>
              <button onClick={() => setShowRecurring(true)} style={{background:"var(--c-light)",border:"none",borderRadius:99,padding:"6px 14px",fontSize:13,color:"var(--c-primary)",cursor:"pointer",fontWeight:800,display:"flex",alignItems:"center",gap:6}}><Repeat size={14} strokeWidth={3} />{i18nT("cust.hist.recurring")}</button>
            </div>
            <div style={{position:"relative",marginBottom:16}}>
              <Search size={18} strokeWidth={2.5} style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#AFAFC4"}} />
              <input style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:20,padding:"12px 14px 12px 42px",fontSize:15,fontWeight:600,boxSizing:"border-box",outline:"none",background:"#FFFDFD",color:"#4A4A68",transition:"border 0.3s"}} placeholder={i18nT("ph.searchApt")} value={searchQ} onChange={e => setSearchQ(e.target.value)} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-border)"} />
            </div>
            <div style={{display:"flex",gap:8,marginBottom:16,overflowX:"auto",paddingBottom:4}}>
              {[["all",i18nT("hist.all")],["pending",i18nT("status.pending")],["grooming",i18nT("status.grooming")],["done",i18nT("status.done")],["cancelled",i18nT("status.cancelled")]].map(([v, l]) => (
                <button key={v} onClick={() => setFilterStatus(v)} style={{padding:"8px 16px",borderRadius:99,fontSize:14,fontWeight:800,border:"none",background:filterStatus===v?"var(--c-primary)":"var(--c-light)",color:filterStatus===v?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap",transition:"all 0.3s",boxShadow:filterStatus===v?"0 4px 12px var(--c-primary-30)":"none"}}>{l}</button>
              ))}
            </div>
            {filteredApts.length === 0
              ? <EmptyState icon={Calendar} title={i18nT("cust.hist.noMatch")} sub={i18nT("cust.hist.noMatchSub")} />
              : filteredApts.slice(0, 6).map((a, i) => {
                  // Only allow cancel / reschedule for FUTURE pending
                  // appointments — past-dated ones (e.g. no-shows or stale
                  // pending rows the shop forgot to mark done) shouldn't
                  // surface action buttons to the customer, otherwise they
                  // can try to "改期" a slot that's already gone.
                  const aDateTime = a.date + " " + (a.time || "00:00");
                  const nowDateTime = dateNow() + " " + timeNow();
                  const isFuture = aDateTime >= nowDateTime;
                  const canCancel = a.status === "pending" && isFuture; const canRate = tenant?.reviewsEnabled !== false && a.status === "done" && !a.rating;
                  return <div key={a.id} style={{padding:"16px 0",borderBottom:i===filteredApts.slice(0,6).length-1?"none":"2px solid var(--c-light)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <p style={{fontSize:16,fontWeight:800,margin:"0 0 4px",color:"#4A4A68"}}>{((state.pets[a.petId]?.name || a.petName) || i18nT("cust.hist.deletedPet")) + " — " + a.service}</p>
                        <p style={{fontSize:14,color:"#8A8A9E",margin:0,fontWeight:600}}>{a.date + " " + a.time + " · " + (state.users[a.staffId]?.name||"")}</p>
                        {(() => {
                          const spaNames = (a.spaIds || []).map(id => (tenant?.services || []).find(sv => sv.id === id)?.name).filter(Boolean);
                          // Strip any legacy SPA labels from addons (old apts
                          // stored "💆 SPA-A · $180" in here); spaIds is the
                          // source of truth now.
                          const cleanAddons = (a.addons || []).filter(n => !String(n).startsWith("💆"));
                          const parts = [...spaNames.map(n => "💆 " + n), ...cleanAddons];
                          return parts.length > 0 ? <p style={{fontSize:13,color:"var(--c-grad1)",margin:"4px 0 0",fontWeight:700}}>{i18nT("cust.hist.addons") + parts.join(i18nT("lang.name") === "English" ? ", " : zhEn("、", ", "))}</p> : null;
                        })()}
                      </div>
                      {/* Past-date stale apts: if the shop forgot to mark
                          done, cosmetically display as 完成 so the customer
                          doesn't see "still grooming" on a 3-day-old booking. */}
                      <StatusBadge
                        status={(a.date && a.date < dateNow() && (a.status === "pending" || a.status === "checkedin" || a.status === "grooming")) ? "done" : a.status}
                        cancelReason={a.cancelReason}
                        approvalPending={a.approvalStatus === "pending"}
                      />
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {canCancel && <Btn variant="danger" size="sm" onClick={() => setCancelApt(a.id)}>{i18nT("cust.hist.cancelBtn")}</Btn>}
                      {canCancel && <Btn variant="secondary" size="sm" onClick={() => {
                        // Reschedule = date/time only. Keep the original service +
                        // SPA + staff so the customer jumps straight to picking a
                        // new time instead of re-selecting the service.
                        setRescheduleApt(a);
                        setBk({ petId:a.petId, svcId:a.svcId, spaIds:a.spaIds||[], addons:[], staffId:a.staffId||"", date:"", time:"", groomingRequest:a.groomingRequest||"" });
                        setTab("book"); setStep(3);
                        setTimeout(() => { try { scrollIntoView(scheduleCardRef); } catch {} }, 120);
                      }}>{i18nT("cust.hist.rescheduleBtn")}</Btn>}
                      {canRate && <Btn variant="amber" size="sm" onClick={() => setRateApt(a.id)}>{i18nT("cust.hist.rateBtn")}</Btn>}
                      {a.rating && <div style={{background:"#FFFBEB",padding:"4px 10px",borderRadius:99}}><Stars value={a.rating} size={14} /></div>}
                      {a.report && <Btn variant="ghost" size="sm" onClick={() => setViewReportApt(a.id)} style={{background:"var(--c-light)",color:"var(--c-primary)"}}>{i18nT("cust.hist.viewReport")}</Btn>}
                      {a.status === "cancelled" && a.cancelReason && <span style={{fontSize:13,color:"#EF4444",fontWeight:700,padding:"6px 0"}}>{i18nT("cust.hist.cancelReason") + a.cancelReason}</span>}
                    </div>
                  </div>;
                })
            }
          </Card>
        </>}

        {tab === "pets" && <>
          {/* New customer with zero pets — show the form INLINE so they
              don't have to tap a button to start. The "add another pet"
              button at the bottom still works for adding more later. */}
          {myPets.length === 0 && (
            <Card style={{padding:20,marginBottom:16}}>
              <div style={{textAlign:"center",marginBottom:14}}>
                <PawPrint size={36} color="var(--c-primary)" strokeWidth={2.5} style={{margin:"0 auto 6px",display:"block"}} />
                <p style={{fontSize:17,fontWeight:900,color:"#4A4A68",margin:"0 0 4px"}}>{i18nT("cust.pets.addFirst")}</p>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{i18nT("cust.pets.addFirstSub")}</p>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <Input label={i18nT("pet.name") + " *"} placeholder={zhEn("例：Fluffy", "e.g. Fluffy")} value={inlineNewPet.name} onChange={e => setInlineNewPet(f => ({...f, name: e.target.value}))} />
                <Sel label={i18nT("cust.pets.type") + " *"} value={inlineNewPet.type} onChange={e => setInlineNewPet(f => ({...f, type: e.target.value}))}>
                  <option value="dog">{i18nT("cust.pets.typeDog")}</option><option value="cat">{i18nT("cust.pets.typeCat")}</option><option value="rabbit">{i18nT("cust.pets.typeRabbit")}</option><option value="hamster">{i18nT("cust.pets.typeHamster")}</option>
                </Sel>
                <BreedCombobox label={zhEn("品種 *", "Breed *")} type={inlineNewPet.type || "dog"} lang={tenant?.lang || "zh-HK"}
                  value={inlineNewPet.breedId} valueLabel={inlineNewPet.breed}
                  onChange={({ breedId, breed, coatHint }) => setInlineNewPet(f => ({ ...f, breedId, breed, coatLength: (coatHint === "long" || coatHint === "short") ? coatHint : (f.coatLength || "short") }))} />
                <Input label={i18nT("cust.pets.weight") + " *"} type="number" step="0.1" placeholder={i18nT("cust.pets.weightPh")} value={inlineNewPet.weight} onChange={e => setInlineNewPet(f => ({...f, weight: e.target.value}))} />
                {petWeightWarn(inlineNewPet.weight, inlineNewPet.type) && <p style={{fontSize:12, color:"#B45309", margin:"6px 0 0", fontWeight:700}}>{petWeightWarn(inlineNewPet.weight, inlineNewPet.type)}</p>}
                {/* Only show the coat-length picker when the chosen breed has
                    real coat variants (e.g. Dachshund: smooth/wire/long).
                    Most breeds have an unambiguous coat — picking 西施 always
                    means 長毛, so the dropdown is pointless friction. */}
                {/* Coat length is derived from the breed (coatHint) — no customer
                    override, so it can't be flipped to "short" for a cheaper price. */}
                {/* Google login gives us email but no phone. Capture it here
                    so the shop can contact the customer for confirmations. */}
                {!liveUser?.phone && (
                  <div>
                    <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("聯絡電話 *", "Contact phone *")} <span style={{color:"#AFAFC4",fontWeight:700,fontSize:11}}>{zhEn("美容師會用此電話聯絡您", "The groomer will use this number to contact you")}</span></p>
                    <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:8}}>
                      <Sel value={inlineNewPet.countryCode} onChange={e => setInlineNewPet(f => ({...f, countryCode: e.target.value}))}>
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </Sel>
                      <Input value={inlineNewPet.phone} inputMode="tel" onChange={e => {
                        const parsed = parseCopiedPhone(e.target.value);
                        setInlineNewPet(f => ({
                          ...f,
                          phone: parsed.digits,
                          ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {}),
                        }));
                      }} placeholder="9123 4567" type="tel" />
                    </div>
                  </div>
                )}
                <Btn onClick={async () => {
                  if (!inlineNewPet.name?.trim()) { toast(i18nT("val.enterPetName"), "error"); return; }
                  if (!inlineNewPet.breed?.trim()) { toast(i18nT("val.enterBreed"), "error"); return; }
                  if (!inlineNewPet.weight || parseFloat(inlineNewPet.weight) <= 0) { toast(i18nT("val.enterWeight"), "error"); return; }
                  const needsPhone = !liveUser?.phone;
                  let phoneSaved = false; // becomes true only if the DB write succeeds
                  if (needsPhone) {
                    const check = validatePhone(inlineNewPet.countryCode || "+852", inlineNewPet.phone || "");
                    if (!check.valid) { toast(check.error, "error"); return; }
                  }
                  // Save phone to profile FIRST so the unique-phone-per-shop
                  // constraint catches duplicates before we create the pet.
                  if (needsPhone) {
                    try {
                      await patchProfile(liveUser.id, {
                        phone: normalizePhoneDigits(inlineNewPet.phone),
                        countryCode: inlineNewPet.countryCode || "+852",
                      });
                      phoneSaved = true;
                    } catch (e) {
                      const msg = e?.message || String(e);
                      if (/unique|duplicate|23505/i.test(msg) && /phone/i.test(msg)) {
                        // Phone belongs to a shop walk-in (their old record) →
                        // file an owner-approval claim instead of blocking. The
                        // phone + old pets/history are linked once the shop
                        // approves. We still let them add this pet now.
                        try { await requestCustomerClaim({ tenantId: liveUser.tenantId, phone: normalizePhoneDigits(inlineNewPet.phone) }); } catch { /* best-effort */ }
                        toast(zhEn("已通知店主核實並合併你喺呢間店嘅舊紀錄 ✓", "The shop owner has been asked to verify and merge your existing records ✓"), "info");
                      } else {
                        toast(zhEn("儲存電話失敗：", "Failed to save phone: ") + msg, "error");
                        return;
                      }
                    }
                  }
                  // coat_length DB check allows only 'short' | 'long' — clamp so a
                  // breed-hinted value (e.g. "medium"/"curly") can't violate it.
                  const coatLen = inlineNewPet.coatLength === "long" ? "long" : "short";
                  const np = { id: uuid(), tenantId: liveUser.tenantId, ownerId: liveUser.id, name: inlineNewPet.name.trim(), breed: inlineNewPet.breed.trim(), breedId: inlineNewPet.breedId || null, weight: parseFloat(inlineNewPet.weight) || 3, birthday: null, type: inlineNewPet.type || "dog", coatLength: coatLen, allergies: zhEn("無", "None"), vaccineDate: "", vetNote: "", photos: [], logs: [], behavior: {}, groomingStyle: {} };
                  setState(s => ({
                    ...s,
                    pets: { ...s.pets, [np.id]: np },
                    users: {
                      ...s.users,
                      [liveUser.id]: {
                        ...s.users[liveUser.id],
                        // Only persist the phone locally if the DB actually saved it —
                        // otherwise the background sync retries it forever → 409.
                        ...(needsPhone && phoneSaved ? { phone: normalizePhoneDigits(inlineNewPet.phone), countryCode: inlineNewPet.countryCode || "+852" } : {}),
                        pets: [...(s.users[liveUser.id].pets || []), np.id],
                      },
                    },
                  }));
                  toast(np.name + zhEn(" 已新增 ✓", " added ✓"));
                  setInlineNewPet({ name:"", breed:"", weight:"", type:"dog", coatLength:"short", phone:"", countryCode:"+852" });
                }} style={{width:"100%",padding:"14px",marginTop:6}}>
                  <Plus size={18} strokeWidth={3} /> {zhEn("新增並開始預約", "Add and start booking")}
                </Btn>
              </div>
            </Card>
          )}
          {myPets.map(p => {
            const age = petAge(p.birthday);
            // Click the pet row → open the pet's detail. Booking has its own
            // 預約 button on the card (and the bottom 預約 tab).
            const startBookingFor = () => {
              setBk({ petId: p.id, svcId: "", addons: [], spaIds: [], staffId: "", date: dateNow(), time: "" });
              setRescheduleApt(null);
              setStep(1); // skip the pet picker step
              setTab("book");
            };
            const openDetail = () => setShowPetDetail(p.id);
            return <Card key={p.id} style={{padding:20,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div onClick={openDetail} role="button" tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") openDetail(); }} style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0,cursor:"pointer",borderRadius:18,padding:6,margin:-6,transition:"background 0.15s"}} onMouseEnter={e => e.currentTarget.style.background="var(--c-lighter)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                  <div style={{width:72,height:72,background:p.photos?.length ? "none" : (p.type==="cat"?"linear-gradient(135deg,var(--c-light),var(--c-grad2))":"linear-gradient(135deg,#E0F2FE,#BAE6FD)"),borderRadius:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,overflow:"hidden",flexShrink:0}}>
                    {p.photos?.length ? <img src={p.photos[0].url} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : (petEmoji(p))}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                      <p style={{fontWeight:900,fontSize:20,margin:0,color:"var(--c-primary)"}}>{p.name}</p>
                      {age != null && age >= seniorAgeFor(tenant) && <Badge color="#DC2626" bg="#FEF2F2">{zhEn("高齡", "Senior")}</Badge>}
                    </div>
                    <p style={{fontSize:14,color:"#8A8A9E",margin:0,fontWeight:700}}>{p.breed + " · " + p.weight + "kg" + (age != null ? " · " + age + zhEn("歲", " yrs") : "")}</p>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,width:78,flexShrink:0}}>
                  <button onClick={() => setShowPetDetail(p.id)} style={{width:"100%",minHeight:38,background:"#fff",border:"1.5px solid var(--c-border)",borderRadius:10,padding:"6px 4px",fontSize:12,color:"var(--c-primary)",cursor:"pointer",fontWeight:800}}>{zhEn("詳情", "Details")}</button>
                  {canBook && <button onClick={startBookingFor} style={{width:"100%",minHeight:38,background:"var(--c-light)",border:"none",borderRadius:10,padding:"6px 4px",fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor:"pointer"}}>{zhEn("預約 →", "Book →")}</button>}
                </div>
              </div>
              <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"1px solid var(--c-border)"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:8}}><span style={{color:"#8A8A9E",fontWeight:700}}>{i18nT("cust.pets.allergyInfo")}</span><span style={{fontWeight:800,color:p.allergies && p.allergies!=="無" && p.allergies!=="None"?"#EF4444":"#4A4A68"}}>{p.allergies || i18nT("cust.pets.noAllergy")}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#8A8A9E",fontWeight:700}}>{zhEn("最近疫苗", "Latest vaccine")}</span><span style={{fontWeight:800,color:"#4A4A68"}}>{p.vaccineDate || zhEn("未記錄", "Not recorded")}</span></div>
                {p.vetNote && <div style={{fontSize:13,color:"#D97706",background:"#FFFBEB",borderRadius:12,padding:"10px 14px",marginTop:12,fontWeight:700}}>{"⚠️ " + p.vetNote}</div>}
              </div>
            </Card>;
          })}
          {myPets.length > 0 && <Btn onClick={() => setPetForm({ name:"", breed:"", weight:"", birthday:"", type:"dog", coatLength:"short", allergies:"", vaccineDate:"", vetNote:"" })} style={{width:"100%",marginBottom:16,padding:"16px"}}><Plus size={20} strokeWidth={3} /> {i18nT("cust.pets.addBtn")}</Btn>}
        </>}

        {tab === "book" && cMaint && (
          <div style={{textAlign:"center",padding:"40px 24px",background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:24}}>
            <div style={{fontSize:44,marginBottom:10}}>🛠️</div>
            <p style={{fontSize:16,fontWeight:900,color:"#B45309",margin:"0 0 6px"}}>{T("系統升級維護中","Under maintenance")}</p>
            <p style={{fontSize:13,fontWeight:700,color:"#92400E",margin:0,lineHeight:1.6}}>{state.platformConfig?.maintenanceMessage || T("暫停網上預約，請稍後再試。不便之處敬請原諒。","Online booking is paused — please try again later.")}</p>
          </div>
        )}
        {tab === "book" && !cMaint && !canBook && (
          <div style={{textAlign:"center",padding:"40px 24px",background:"#fff",borderRadius:24,boxShadow:"0 8px 24px rgba(0,0,0,0.05)"}}>
            <div style={{fontSize:44,marginBottom:10}}>📞</div>
            <p style={{fontSize:16,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{T("此店暫未開啟網上預約","Online booking not available")}</p>
            <p style={{fontSize:13,fontWeight:700,color:"#8A8A9E",margin:"0 0 18px",lineHeight:1.5}}>{T("請直接聯絡店家預約。","Please contact the shop to book.")}</p>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
              {tenant?.phone && <a href={"tel:"+tenant.phone} style={{padding:"12px 22px",borderRadius:99,background:"var(--c-primary)",color:"#fff",fontWeight:900,fontSize:14,textDecoration:"none"}}>📞 {T("致電","Call")}</a>}
              {tenant?.phone && <a href={"https://wa.me/"+String(tenant.phone).replace(/[^0-9]/g,"")} target="_blank" rel="noreferrer" style={{padding:"12px 22px",borderRadius:99,background:"#25D366",color:"#fff",fontWeight:900,fontSize:14,textDecoration:"none"}}>💬 WhatsApp</a>}
            </div>
          </div>
        )}
        {tab === "book" && !cMaint && canBook && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* 到店 / 上門 mode + 上門 address. Address is prefilled from the
              customer's saved address (membership.address) so they only enter
              it once; saved back on submit. */}
          {cShopMobile && (
            <Card style={{padding:18}}>
              {tenant?.serviceMode === "both" ? (
                <>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:"0 0 8px"}}>{T("服務方式","Service type")}</p>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={() => setBk(b => ({ ...b, isMobile:false, svcId:"", spaIds:[] }))} style={{flex:1,padding:"12px",borderRadius:14,border:"2px solid "+(!cWantMobile?"var(--c-primary)":"var(--c-light)"),background:!cWantMobile?"var(--c-primary)":"#fff",color:!cWantMobile?"#fff":"#4A4A68",fontWeight:800,fontSize:14,cursor:"pointer"}}>🏠 {T("到店","In-shop")}</button>
                    <button onClick={() => setBk(b => ({ ...b, isMobile:true, svcId:"", spaIds:[] }))} style={{flex:1,padding:"12px",borderRadius:14,border:"2px solid "+(cWantMobile?"var(--c-primary)":"var(--c-light)"),background:cWantMobile?"var(--c-primary)":"#fff",color:cWantMobile?"#fff":"#4A4A68",fontWeight:800,fontSize:14,cursor:"pointer"}}>🚗 {T("上門","Home visit")}</button>
                  </div>
                </>
              ) : (
                <p style={{fontSize:13,fontWeight:800,color:"#5B21B6",margin:0}}>🚗 {T("此店為上門服務，美容師會到你嘅地址","Home-visit shop — the groomer comes to your address")}</p>
              )}
              {cWantMobile && (
                <div style={{marginTop:12}}>
                  <p style={{fontSize:12,fontWeight:800,color:"#5B21B6",margin:"0 0 6px"}}>🚗 {T("上門地址","Home-visit address")} <span style={{color:"#FF4D6D"}}>*</span></p>
                  <textarea value={bk.serviceAddress ?? ((state.memberships||[]).find(m=>m.userId===liveUser.id&&m.tenantId===liveUser.tenantId)?.address || "")} onChange={e=>setBk(b=>({...b,serviceAddress:e.target.value}))} placeholder={T("完整地址（大廈 / 街道 / 樓層 / 單位）","Full address (building / street / floor / unit)")} style={{width:"100%",minHeight:60,padding:"10px 12px",borderRadius:10,border:"2px solid var(--c-border)",fontSize:14,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                  <input value={bk.serviceNotes || ""} onChange={e=>setBk(b=>({...b,serviceNotes:e.target.value}))} placeholder={T("門鈴 / 泊車 / 特別指示（可選）","Doorbell / parking / special notes (optional)")} style={{width:"100%",marginTop:8,padding:"10px 12px",borderRadius:10,border:"2px solid var(--c-border)",fontSize:14,fontWeight:600,outline:"none",boxSizing:"border-box"}} />
                  <p style={{fontSize:10,color:"#8A8A9E",margin:"6px 0 0",fontWeight:700}}>{T("地址會儲存，下次預約自動填上","Saved for next time — auto-filled")}</p>
                </div>
              )}
            </Card>
          )}
          {/* Payment-methods banner removed per owner feedback — shops
              now show concrete deposit-method QRs on the booking-success
              page (each with name + instructions), so the abstract
              "支援付款方式" header was redundant noise. */}
          {claimSummary && claimSummary.linkedPets > 0 && (
            <div style={{background:"linear-gradient(135deg,#FEF3C7,#FDE68A)",border:"2px solid #FBBF24",borderRadius:24,padding:"16px 18px",display:"flex",alignItems:"center",gap:14}}>
              <div style={{fontSize:32}}>✨</div>
              <div style={{flex:1}}>
                <p style={{fontSize:14,fontWeight:900,color:"#92400E",margin:"0 0 2px"}}>{zhEn("已自動連結", "Auto-linked")} {claimSummary.linkedPets} {zhEn("隻毛孩", " pets")}</p>
                <p style={{fontSize:12,color:"#92400E",fontWeight:700,margin:0,opacity:0.85}}>
                  {claimSummary.petNames && claimSummary.petNames.length > 0
                    ? claimSummary.petNames.join("、")
                    : zhEn("店家已登記嘅紀錄", "Records the shop already has")}
                  {claimSummary.linkedAppointments > 0 ? zhEn(` · ${claimSummary.linkedAppointments} 個歷史預約`, ` · ${claimSummary.linkedAppointments} past bookings`) : ""}
                </p>
              </div>
              <button onClick={() => setClaimSummary(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:"#92400E",fontSize:18,fontWeight:900,padding:6}} aria-label={zhEn("關閉", "Close")}>×</button>
            </div>
          )}
          {/* Single scrolling flow — each card reveals the next when its
              required field is set. No "上一步/下一步" navigation. */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,marginBottom:16,fontSize:20,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{myPets.length === 0 ? zhEn("先登記你的毛孩 🐾", "Register your pet first 🐾") : zhEn("帶哪位寶貝來？", "Which pet are you bringing?")} <HelpTip topic="bookingFlow" /></h3>
            {myPets.length === 0 ? (
              /* Inline pet creation — no need to go to a separate tab */
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("填寫毛孩資料後即可選擇服務並預約", "Fill in your pet's details, then choose a service and book")}</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <Input label={i18nT("pet.name") + " *"} placeholder={zhEn("例：奶茶", "e.g. Milk Tea")} value={petForm?.name||""} onChange={e => setPetForm(f => ({ ...(f||{ name:"",breed:"",weight:"",type:"dog",coatLength:"short",birthday:"",allergies:zhEn("無", "None") }), name:e.target.value }))} />
                  <Input label={i18nT("pet.weightKg")} type="number" placeholder="4.5" value={petForm?.weight||""} onChange={e => setPetForm(f => ({ ...(f||{}), weight:e.target.value }))} />
                  <Input label={i18nT("pet.birthday")} type="date" max={dateNow()} min={petBirthdayMin()} value={petForm?.birthday||""} onChange={e => setPetForm(f => ({ ...(f||{}), birthday:e.target.value }))} />
                </div>
                {petWeightWarn(petForm?.weight, petForm?.type) && <p style={{fontSize:12, color:"#B45309", margin:"-4px 0 0", fontWeight:700}}>{petWeightWarn(petForm?.weight, petForm?.type)}</p>}
                <Sel label={i18nT("pet.type")} value={petForm?.type||"dog"} onChange={e => setPetForm(f => ({ ...(f||{}), type:e.target.value, species: e.target.value === "small" ? (f?.species || "rabbit") : null }))}>
                  <option value="dog">{i18nT("pet.dog")}</option>
                  <option value="cat">{i18nT("pet.cat")}</option>
                  <option value="small">{zhEn("🐇 小動物", "🐇 Small animal")}</option>
                </Sel>
                {petForm?.type === "small" && (
                  <Sel label={zhEn("品種", "Breed")} value={petForm?.species || "rabbit"} onChange={e => setPetForm(f => ({ ...(f||{}), species: e.target.value }))}>
                    {SMALL_ANIMAL_SPECIES.map(s => <option key={s.key} value={s.key}>{s.emoji} {s.label}</option>)}
                  </Sel>
                )}
                <BreedCombobox label={zhEn("品種 *", "Breed *")} type={petForm?.type || "dog"} lang={tenant?.lang || "zh-HK"}
                  value={petForm?.breedId} valueLabel={petForm?.breed}
                  onChange={({ breedId, breed, coatHint }) => setPetForm(f => ({ ...(f||{ name:"",weight:"",type:"dog",coatLength:"short",birthday:"",allergies:zhEn("無", "None") }), breedId, breed, coatLength: (coatHint === "long" || coatHint === "short") ? coatHint : (f?.coatLength || "short") }))} />
                {/* Coat length is derived from the breed (coatHint) — no customer
                    override, so it can't be flipped to "short" for a cheaper price. */}
                <Btn disabled={!petForm?.name||!petForm?.breed} onClick={() => {
                  const bErr = petBirthdayError(petForm.birthday); if (bErr) { toast(bErr, "error"); return; }
                  const np = { id:uuid(), ownerId:liveUser.id, tenantId:liveUser.tenantId, name:petForm.name, breed:petForm.breed, breedId: petForm.breedId || null, weight:parseFloat(petForm.weight)||3, birthday:petForm.birthday||"2020-01-01", type:petForm.type||"dog", species: petForm.species || null, coatLength:petForm.coatLength||"short", allergies:petForm.allergies||zhEn("無", "None"), vaccineDate:"", vetNote:"", photos:[], logs:[], behavior:{}, groomingStyle:{} };
                  setState(s => ({ ...s, pets:{ ...s.pets, [np.id]:np }, users:{ ...s.users, [liveUser.id]:{ ...s.users[liveUser.id], pets:[ ...((s.users[liveUser.id]?.pets)||[]), np.id ] } } }));
                  setBk(b => ({ ...b, petId:np.id }));
                  setPetForm(null);
                  toast(np.name + zhEn(" 已新增，請選擇服務", " added, please choose a service"));
                }} style={{width:"100%",marginTop:6}}>{zhEn("登記", "Register")}</Btn>
              </div>
            ) : (
              <>
                {/* #28 Multi-pet booking — first tap sets primary; subsequent
                    taps on OTHER pets add them as extra pets (sibling appts
                    sharing a groupId). Tap the primary card again to clear
                    (auto-promotes first extra to primary). All pets share
                    the same service/addons/time slot. */}
                {myPets.length > 1 && bk.petId && (
                  <div style={{background:"#EFF6FF",borderRadius:12,padding:"8px 12px",marginBottom:10,fontSize:12,fontWeight:700,color:"#1E40AF"}}>
                    {zhEn("💡 每次只可揀一隻毛孩。如要為多隻預約，請完成後再次預約。", "💡 You can pick one pet at a time. To book for several pets, book again after finishing.")}
                  </div>
                )}
                {/* Single-select pet picker. Multi-pet booking was removed:
                    sibling appointments left ghost rows on failure and the
                    UX of "primary + extras with shared time slot" was
                    surprising for shops. Customers now book one pet per
                    appointment, then return to the picker for the next. */}
                {myPets.map(p => {
                  const isSel = bk.petId === p.id;
                  const onPick = () => {
                    setBk(b => ({ ...b, petId: isSel ? "" : p.id, extraPetIds: [] }));
                  };
                  return (
                    <div key={p.id} onClick={onPick} style={{display:"flex",alignItems:"center",gap:16,padding:16,borderRadius:24,border:"2px solid "+(isSel?"var(--c-primary)":"var(--c-light)"),background:isSel?"var(--c-lighter)":"#FFFDFD",marginBottom:12,cursor:"pointer",transition:"all 0.2s",transform:isSel?"scale(1.02)":"scale(1)"}}>
                      <div style={{width:56,height:56,background:p.type==="cat"?"var(--c-light)":"#E0F2FE",borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>
                        {petEmoji(p)}
                      </div>
                      <div style={{flex:1}}>
                        <p style={{fontWeight:800,margin:"0 0 4px",fontSize:17,color:"#4A4A68"}}>{p.name}</p>
                        <p style={{fontSize:14,color:"#8A8A9E",margin:0,fontWeight:600}}>{p.breed + " · " + p.weight + "kg"}</p>
                      </div>
                      <div style={{width:28,height:28,background:isSel?"var(--c-primary)":"var(--c-light)",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center"}}><Check size={16} color="#fff" strokeWidth={3} /></div>
                    </div>
                  );
                })}
                <button onClick={() => setPetForm({ name:"", breed:"", weight:"", birthday:"", type:"dog", coatLength:"short", allergies:zhEn("無", "None"), vaccineDate:"", vetNote:"" })} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,width:"100%",padding:"12px",background:"none",border:"2px dashed var(--c-light)",borderRadius:20,fontSize:14,fontWeight:800,color:"#8A8A9E",cursor:"pointer",marginBottom:8}}><Plus size={16} strokeWidth={3} />{i18nT("book.addPet")}</button>
              </>
            )}
          </Card>

          {!!bk.petId && <Card ref={svcCardRef} style={{padding:24,scrollMarginTop:16}}>
            <h3 style={{fontWeight:900,marginBottom:8,fontSize:20,color:"#4A4A68"}}>{i18nT("book.pickService")}</h3>
            {selPet && !cOverShopMax && <div style={{background:"var(--c-light)",padding:"10px 16px",borderRadius:16,marginBottom:16,display:"flex",alignItems:"center",gap:8}}><Zap size={16} color="var(--c-primary)" fill="var(--c-primary)" /><p style={{fontSize:13,color:"var(--c-primary)",margin:0,fontWeight:700}}>{zhEn("已為 " + selPet.name + " (" + selPet.weight + "kg) 智能計算專屬價格", "Price calculated for " + selPet.name + " (" + selPet.weight + "kg)")}</p></div>}
            {cOverShopMax && <div style={{background:"#FEF2F2",border:"2px solid #FCA5A5",borderRadius:16,padding:16,display:"flex",alignItems:"center",gap:10}}><AlertTriangle size={18} color="#DC2626" style={{flexShrink:0}} /><p style={{fontSize:13,color:"#991B1B",margin:0,fontWeight:800}}>{zhEn((selPet?.name || "毛孩") + " 的體重（" + (selPet?.weight || "?") + "kg）超出本店服務上限（" + cShopMaxKg + "kg），暫不提供服務。請聯絡店家查詢。", (selPet?.name || "Your pet") + "'s weight (" + (selPet?.weight || "?") + "kg) exceeds this shop's limit (" + cShopMaxKg + "kg), so we can't offer the service. Please contact the shop.")}</p></div>}
            {cVisibleServices.map(svc => {
              const pDetail = selPet ? getPricingDetail(selPet, svc, tenant, { mobile: cWantMobile }) : { total:0 };
              return (
                <div key={svc.id} onClick={() => setBk(b => ({ ...b, svcId:svc.id }))} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:16,borderRadius:24,border:"2px solid "+(bk.svcId===svc.id?"var(--c-primary)":"var(--c-light)"),background:bk.svcId===svc.id?"var(--c-lighter)":"#FFFDFD",marginBottom:12,cursor:"pointer",transition:"all 0.2s"}}>
                  <div><p style={{fontWeight:800,margin:"0 0 6px",fontSize:17,color:"#4A4A68"}}>{svc.name}</p>{svc.smartPricing?.enabled && <p style={{fontSize:13,color:"var(--c-primary)",margin:0,display:"flex",alignItems:"center",gap:4,fontWeight:700}}><Zap size={14} fill="var(--c-primary)" /> {zhEn("專屬智能計價", "Smart pricing for your pet")}</p>}</div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}><span style={{fontWeight:900,color:"var(--c-primary)",fontSize:22}}>{"$" + pDetail.total}</span><div style={{width:28,height:28,background:bk.svcId===svc.id?"var(--c-primary)":"var(--c-light)",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center"}}><Check size={16} color="#fff" strokeWidth={3} /></div></div>
                </div>
              );
            })}
          </Card>}

          {/* SPA + addons hidden for small animals — per shop policy they
              only apply to dogs / cats. The check uses selPet?.type so
              the cards reappear instantly when the customer switches to a
              dog or cat in step 1. */}
          {!!bk.svcId && cVisibleSpa.length > 0 && selPet?.type !== "small" && <Card ref={spaCardRef} style={{padding:24,scrollMarginTop:16}}>
            <h3 style={{fontWeight:900,marginBottom:6,fontSize:20,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("SPA 服務 💆", "SPA services 💆")} <HelpTip topic="pricing" /></h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("選擇性升級，只可揀一項", "Optional upgrade — pick one")}</p>
            {cVisibleSpa.map(sp => {
              const sel = (bk.spaIds || []).includes(sp.id);
              const pd = selPet ? getPricingDetail(selPet, sp, tenant, { mobile: cWantMobile }) : { total: sp.basePrice };
              return (
                <div key={sp.id} onClick={() => { setBk(b => ({ ...b, spaIds: sel ? [] : [sp.id] })); if (!sel) scrollIntoView(addons.length > 0 ? addonCardRef : scheduleCardRef); }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:16,borderRadius:24,border:"2px solid "+(sel?"var(--c-primary)":"var(--c-light)"),background:sel?"var(--c-lighter)":"#FFFDFD",marginBottom:12,cursor:"pointer",transition:"all 0.2s",gap:12}}>
                  <div style={{minWidth:0,flex:1}}>
                    <p style={{fontWeight:800,margin:0,fontSize:16,color:"#4A4A68"}}>{sp.name}</p>
                    {sp.description && <p style={{fontSize:12,color:"#6A6A85",margin:"4px 0 0",fontWeight:600,lineHeight:1.5}}>{sp.description}</p>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
                    <span style={{fontWeight:900,color:"var(--c-primary)",fontSize:18}}>{"+$" + pd.total}</span>
                    <div style={{width:28,height:28,background:sel?"var(--c-primary)":"var(--c-light)",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center"}}><Check size={16} color="#fff" strokeWidth={3} /></div>
                  </div>
                </div>
              );
            })}
          </Card>}

          {!!bk.svcId && selPet?.type !== "small" && <Card ref={addonCardRef} style={{padding:24,scrollMarginTop:16}}>
            <h3 style={{fontWeight:900,marginBottom:16,fontSize:20,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("加購", "Add-ons")} <HelpTip topic="addons" /></h3>
            {addons.map(a => {
              const sel = bk.addons.includes(a.id);
              const ap = computeAddonNet(a, selPet, selSvc);
              return (
                <div key={a.id} onClick={() => setBk(b => ({ ...b, addons:sel ? b.addons.filter(x=>x!==a.id) : [...b.addons,a.id] }))} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:16,borderRadius:24,border:"2px solid "+(sel?"var(--c-primary)":"var(--c-light)"),background:sel?"var(--c-lighter)":"#FFFDFD",marginBottom:12,cursor:"pointer",transition:"all 0.2s"}}>
                  <div style={{minWidth:0}}>
                    <p style={{fontWeight:800,margin:0,fontSize:16,color:"#4A4A68"}}>{a.name}</p>
                    {ap.variantLabel && <p style={{fontSize:12,fontWeight:700,color:"var(--c-primary)",margin:"2px 0 0"}}>{ap.variantLabel}</p>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}><span style={{fontWeight:900,color:ap.included?"#10B981":"var(--c-primary)",fontSize:ap.included?14:18}}>{ap.included ? T("已包免費","Included","込") : "+$" + ap.price}</span><div style={{width:28,height:28,background:sel?"var(--c-primary)":"var(--c-light)",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center"}}><Check size={16} color="#fff" strokeWidth={3} /></div></div>
                </div>
              );
            })}
          </Card>}

          {!!bk.svcId && <Card ref={scheduleCardRef} style={{padding:24,scrollMarginTop:16}}>
            {rescheduleApt && <div style={{background:"#EFF6FF",border:"2px solid #BFDBFE",borderRadius:20,padding:16,marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
              <CalendarClock size={24} color="#3B82F6" strokeWidth={2.5} style={{flexShrink:0}} />
              <div>
                <p style={{fontWeight:800,color:"#1E40AF",margin:"0 0 4px",fontSize:15}}>{zhEn("改期中 📅", "Rescheduling 📅")}</p>
                <p style={{fontSize:13,color:"#3B82F6",margin:0,fontWeight:700}}>{zhEn("原預約：", "Original booking: ") + rescheduleApt.date + " " + rescheduleApt.time + " · " + rescheduleApt.service}</p>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"4px 0 0",fontWeight:600}}>{zhEn("選擇新的時間後，原預約將自動取消", "Once you pick a new time, the original booking is cancelled automatically")}</p>
              </div>
              <button onClick={() => { setRescheduleApt(null); setTab("home"); }} style={{background:"none",border:"none",cursor:"pointer",padding:4,flexShrink:0}}><X size={18} color="#8A8A9E" strokeWidth={2.5} /></button>
            </div>}
            <h3 style={{fontWeight:900,marginBottom:20,fontSize:20,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{rescheduleApt ? zhEn("選擇新的時間", "Choose a new time") : zhEn("指定美容師與時間", "Choose groomer & time")} <HelpTip topic="scheduleSwipe" /></h3>
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              <div>
                <p style={{fontSize:14,color:"#8A8A9E",marginBottom:10,fontWeight:800}}>{zhEn("美容師 ✂️", "Groomer ✂️")}</p>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  {staffList.filter(s => staffCanDo(s, bk.svcId)).length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,margin:0}}>{zhEn("暫無可提供此服務嘅美容師", "No groomer offers this service")}</p>}
                  {staffList.filter(s => staffCanDo(s, bk.svcId)).map(s => {
                    const revs = state.reviews.filter(r => r.staffId === s.id);
                    const avg = revs.length ? (revs.reduce((a, r) => a + r.rating, 0) / revs.length).toFixed(1) : null;
                    return <div key={s.id} onClick={() => { userPickedStaffRef.current = true; setBk(b => ({ ...b, staffId:s.id, time:"" })); }} style={{padding:"12px 18px",borderRadius:99,border:"2px solid "+(bk.staffId===s.id?"var(--c-primary)":"var(--c-light)"),background:bk.staffId===s.id?"var(--c-primary)":"#FFFDFD",cursor:"pointer",fontSize:15,fontWeight:800,color:bk.staffId===s.id?"#fff":"#4A4A68",display:"flex",alignItems:"center",gap:8,transition:"all 0.2s"}}>
                      {s.name}{avg && <span style={{fontSize:13,color:bk.staffId===s.id?"var(--c-border)":"#F59E0B"}}>{"★" + avg}</span>}
                    </div>;
                  })}
                </div>
              </div>
              <DateStripPicker value={bk.date} onChange={(d) => setBk(b => ({ ...b, date:d, time:"" }))} tenant={tenant} appointments={state.appointments} />
              <div>
                <p style={{fontSize:14,color:"#8A8A9E",margin:"0 0 10px",fontWeight:800}}>{zhEn("可選時段 ⏰", "Available times ⏰")} {bk.svcId && (() => { const svc = services.find(s => s.id === bk.svcId); return svc ? <span style={{fontSize:12,color:"var(--c-primary)",fontWeight:700}}>({svc.estimatedMinutes || 60} {zhEn("分鐘)", " min)")}</span> : null; })()}</p>
                {bk.date && (() => {
                  const minAdv = Math.max(0, Number(tenant?.cancellationPolicy?.minAdvanceHours) || 0);
                  // All slots blocked purely because they're within the shop's
                  // advance-notice window → explain that, not "已滿".
                  const allTooSoon = timeSlots.length > 0 && timeSlots.every(s => s.past);
                  const banner = (msg) => <div style={{background:"#FFFBEB",borderRadius:16,padding:14,display:"flex",alignItems:"center",gap:10,border:"2px solid #FEF3C7"}}><AlertTriangle size={18} color="#D97706" /><p style={{fontSize:13,color:"#92400E",margin:0,fontWeight:700}}>{msg}</p></div>;
                  if (bkBusyStatus === "loading") return banner(T("正在確認可預約時段…", "Checking availability…"));
                  if (bkBusyStatus === "error") return <div role="alert" style={{background:"#FEF2F2",borderRadius:16,padding:14,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,border:"2px solid #FECACA"}}><p style={{fontSize:13,color:"#991B1B",margin:0,fontWeight:700}}>{T("暫時未能確認時段，請重試。", "Could not verify availability. Please retry.")}</p><Btn size="sm" onClick={() => setBkBusyRetry(n => n + 1)}>{T("重試", "Retry")}</Btn></div>;
                  if (timeSlots.length === 0) return banner(isBusinessDay(bk.date, tenant) ? zhEn("此日期所有時段已滿", "All slots on this date are full") : zhEn("此日為休息日，不提供服務", "Closed on this day"));
                  if (allTooSoon && minAdv > 0) return banner(zhEn("本店需提前 " + (minAdv % 24 === 0 ? (minAdv / 24) + " 日" : minAdv + " 小時") + " 預約，請選擇較後的時間", "This shop needs at least " + (minAdv % 24 === 0 ? (minAdv / 24) + (minAdv / 24 === 1 ? " day" : " days") : minAdv + " hours") + " notice, please choose a later time"));
                  return null;
                })()}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                  {timeSlots.map(slot => {
                    const t = slot.time;
                    const isReady = bk.staffId && bk.date;
                    const disabled = !isReady || !slot.available;
                    return <button key={t} disabled={disabled} onClick={() => { if (disabled) return; setBk(b => ({ ...b, time:t })); }} style={{padding:"12px 0",borderRadius:16,border:"2px solid "+(bk.time===t?"var(--c-primary)":"var(--c-light)"),background:bk.time===t?"var(--c-primary)":!slot.available?"var(--c-lighter)":!isReady?"var(--c-light)":"#FFFDFD",fontSize:15,fontWeight:800,color:bk.time===t?"#fff":!slot.available?"#AFAFC4":!isReady?"#8A8A9E":"#4A4A68",opacity:disabled?0.5:1,textDecoration:!slot.available?"line-through":"none",cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s",boxShadow:bk.time===t?"0 6px 16px var(--c-primary-30)":"none"}}>
                      {t}
                    </button>;
                  })}
                </div>
                <p style={{fontSize:13,color:"#FF4D6D",margin:"12px 4px 0",fontWeight:700}}>
                  {!bk.staffId ? zhEn("👉 請先選擇上方美容師", "👉 Pick a groomer above first") : !bk.date ? zhEn("👉 請先選擇日期", "👉 Pick a date first") : !bk.time ? zhEn("👉 最後，挑個好時間吧！", "👉 Finally, pick a time!") : ""}
                </p>
              </div>
            </div>
          </Card>}

          {!!(bk.staffId && bk.date && bk.time) && <Card ref={summaryCardRef} style={{padding:24,scrollMarginTop:16}}>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{width:64,height:64,background:"#F0FDF4",borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}><Check size={32} color="#10B981" strokeWidth={3} /></div>
              <h3 style={{fontWeight:900,margin:0,fontSize:22,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("book.summary")} <HelpTip topic="vouchers" /></h3>
            </div>
            {(() => {
              const ageM = petAgeMonths(selPet?.birthday);
              if (ageM !== null && ageM < MIN_PET_AGE_MONTHS) {
                return <div style={{background:"#FEF2F2",border:"2px solid #FCA5A5",borderRadius:20,padding:16,marginBottom:20}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#991B1B",margin:"0 0 6px"}}>{zhEn("🍼 毛孩太年幼，未能預約美容", "🍼 Your pet is too young to book grooming")}</p>
                  <p style={{fontSize:13,color:"#991B1B",margin:0,fontWeight:700,lineHeight:1.6}}>{selPet?.name} {zhEn("目前", "is currently")} {ageM} {zhEn("個月大，未滿 3 個月。為安全起見（疫苗未完成 + 免疫力較弱），本店不接受 3 個月以下嘅毛孩進行美容服務。請等到完成所有基本疫苗後再預約 🙏", "months old, under 3 months. For safety (vaccines incomplete + weaker immunity), we don't groom pets under 3 months. Please book again once all basic vaccines are done 🙏")}</p>
                </div>;
              }
              return null;
            })()}
            {selPet && petAge(selPet.birthday) >= seniorAgeFor(tenant) && <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:20,padding:16,marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
              <AlertTriangle size={24} color="#EF4444" style={{flexShrink:0}} />
              <p style={{fontSize:14,color:"#991B1B",margin:0,fontWeight:700}}>{zhEn("高齡寵物需簽署免責聲明", "Senior pets need a signed disclaimer")}{sig && <span style={{color:"#10B981",fontWeight:800}}> {zhEn("· 已簽署 ✅", "· Signed ✅")}</span>}</p>
            </div>}

            {/* Package picker — if the customer holds matching active packages,
                let them flag which one to deduct from. The shop sees this in the
                edit-apt sheet and the package auto-decrements when status flips
                to "done". Filter packages whose `serviceIds` is empty (all
                services) OR includes the chosen svc. */}
            {(() => {
              // Show ALL of the customer's active packages, with a
              // 「不適用」 reason badge on the ones bound to other pets /
              // other services. Helps the customer understand WHY their
              // 10 送 2 isn't picking up — they bought it for their
              // larger dog, and this booking is for the smaller cat.
              const allCps = (myPackages || []).filter(cp =>
                cp.remainingVisits > 0
                && (!cp.expiresAt || new Date(cp.expiresAt).getTime() > Date.now())
              );
              if (allCps.length === 0) return null;
              const selPetObj = bk.petId ? state.pets[bk.petId] : null;
              const annotated = allCps.map(cp => {
                const pkg = shopPackages.find(p => p.id === cp.packageId);
                const sids = pkg?.serviceIds || [];
                let reason = null;
                if (cp.petId && bk.petId && cp.petId !== bk.petId) {
                  const boundPet = state.pets[cp.petId];
                  reason = zhEn("綁定「" + (boundPet?.name || "另一隻") + "」(" + (boundPet?.breed || "") + (boundPet?.weight ? " " + boundPet.weight + "kg" : "") + ")", "Tied to \"" + (boundPet?.name || "another pet") + "\" (" + (boundPet?.breed || "") + (boundPet?.weight ? " " + boundPet.weight + "kg" : "") + ")");
                } else if (sids.length > 0 && !sids.includes(bk.svcId)) {
                  const svc = (tenant?.services || []).find(s => sids.includes(s.id));
                  reason = zhEn("只適用「" + (svc?.name || "其他服務") + "」", "Only valid for \"" + (svc?.name || "another service") + "\"");
                }
                return { cp, pkg, reason };
              });
              const hasEligible = annotated.some(a => !a.reason);
              return (
                <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:20,padding:16,marginBottom:16}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"0 0 8px",display:"flex",alignItems:"center",gap:6}}>{i18nT("cust.book.usePackage")}{selPetObj ? " · " + selPetObj.name : ""}</p>
                  <p style={{fontSize:11,color:"#92400E",margin:"0 0 10px",fontWeight:700,opacity:0.85}}>{zhEn("選一張套票，預約成功即扣 1 次（取消會自動退還）", "Pick a package; 1 visit is deducted when the booking succeeds (returned automatically if cancelled)")}</p>
                  {!hasEligible && (
                    <div style={{background:"#FEF2F2",border:"1.5px solid #FCA5A5",borderRadius:10,padding:"8px 12px",marginBottom:10,fontSize:11,fontWeight:800,color:"#991B1B",lineHeight:1.5}}>
                      {zhEn("⚠ 此寵物 / 服務組合冇可用套票。每張套票綁定 1 隻寵物 + 1 個服務，請確認揀啱客戶 + 服務。", "⚠ No package available for this pet / service. Each package is tied to 1 pet + 1 service, so please check you picked the right customer + service.")}
                    </div>
                  )}
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:bk.customerPackageId === "" ? "#fff" : "transparent",border:"2px solid "+(bk.customerPackageId === "" ? "#F59E0B" : "#FDE68A"),borderRadius:14,cursor:"pointer"}}>
                      <input type="radio" name="cp" checked={bk.customerPackageId === ""} onChange={() => setBk(b => ({...b, customerPackageId: ""}))} style={{accentColor:"#F59E0B"}}/>
                      <span style={{fontSize:13,fontWeight:800,color:"#92400E"}}>{zhEn("唔使用", "Don't use")}</span>
                    </label>
                    {annotated.map(({ cp, pkg, reason }) => {
                      const sel = bk.customerPackageId === cp.id;
                      const ineligible = !!reason;
                      const includes = [];
                      if (pkg?.coversSpas) includes.push(zhEn("SPA 免費", "Free SPA"));
                      if (pkg?.coversAddons) includes.push(zhEn("加購 免費", "Free add-on"));
                      return (
                        <label
                          key={cp.id}
                          onClick={e => {
                            if (ineligible) { e.preventDefault(); toast("⚠ " + (pkg?.name || zhEn("套票", "Package")) + " · " + reason, "warning"); }
                          }}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background: sel ? "#fff" : ineligible ? "#F9FAFB" : "transparent",border:"2px solid " + (sel ? "#F59E0B" : ineligible ? "#E5E7EB" : "#FDE68A"),borderRadius:14,cursor:"pointer",opacity: ineligible ? 0.7 : 1}}
                        >
                          <input type="radio" name="cp" checked={sel} disabled={ineligible} onChange={() => {
                            if (ineligible) return;
                            // Picking a package clears any currently-selected
                            // cash-discount voucher so the two never stack.
                            const myMem2 = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
                            const curVc = bk.voucherId ? (myMem2?.vouchers || []).find(v => v.id === bk.voucherId) : null;
                            const spec = curVc?.voucher || curVc;
                            const ct = spec?.type || curVc?.type;
                            const isCash = ct === "discount" || ct === "fixed" || ct === "percent";
                            if (isCash) { toast(zhEn("已自動移除現金折扣券（與套票不可同時使用）", "Cash coupon removed automatically (can't be combined with a package)"), "info"); setBk(b => ({...b, customerPackageId: cp.id, voucherId: ""})); }
                            else setBk(b => ({...b, customerPackageId: cp.id}));
                          }} style={{accentColor:"#F59E0B"}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:13,fontWeight:900,color: ineligible ? "#9CA3AF" : "#92400E",margin:0,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",textDecoration: ineligible ? "line-through" : "none"}}>
                              {pkg?.name || zhEn("套票", "Package")}
                              {includes.map((t,i) => <span key={i} style={{fontSize:9,fontWeight:800,color:"#fff",background: ineligible ? "#9CA3AF" : "#F59E0B",borderRadius:6,padding:"1px 6px",textDecoration:"none"}}>{t}</span>)}
                              {ineligible && <span style={{fontSize:10,fontWeight:900,color:"#DC2626",background:"#FEF2F2",border:"1px solid #FCA5A5",borderRadius:99,padding:"1px 7px",textDecoration:"none"}}>{zhEn("不適用 ·", "Not applicable ·")} {reason}</span>}
                            </p>
                            <p style={{fontSize:11,color: ineligible ? "#9CA3AF" : "#92400E",margin:"2px 0 0",fontWeight:700,opacity:0.85}}>{zhEn("剩餘 ", "Left ") + cp.remainingVisits + zhEn(" 次", " times") + (cp.expiresAt ? " · " + cp.expiresAt.slice(0,10) + zhEn(" 到期", " expiry") : "")}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Voucher picker — customer-redeemed point vouchers attach to
                this booking so the shop can deduct them at the till. The
                voucher's value is the underlying SPA/addon being free. */}
            {(() => {
              const myMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
              const usable = (myMem?.vouchers || []).filter(v => !v.used);
              if (usable.length === 0) return null;
              const usesPackage = !!bk.customerPackageId;
              // Cash-type vouchers (discount/percent) conflict with package:
              // package already covers the main service in pre-paid visits, so
              // applying a cash coupon on top double-discounts. SPA / addon
              // free-vouchers stay enabled (they apply to extras not covered
              // by the package).
              const isCashVoucher = (v) => {
                const spec = v.voucher || v;
                const t = spec.type || v.type;
                return t === "discount" || t === "fixed" || t === "percent";
              };
              return (
                <div style={{background:"#FFF7ED",border:"2px solid #FED7AA",borderRadius:20,padding:16,marginBottom:16}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#9A3412",margin:"0 0 6px",display:"flex",alignItems:"center",gap:6}}>{i18nT("cust.book.useVoucher")}</p>
                  {usesPackage && (
                    <div style={{background:"#FEF2F2",border:"1.5px solid #FCA5A5",borderRadius:10,padding:"8px 12px",marginBottom:10,fontSize:11,fontWeight:800,color:"#991B1B",lineHeight:1.5}}>
                      {zhEn("⚠ 已使用套票，現金 / 百分比折扣券暫不可疊用。可繼續使用「免費 SPA / 加購」券。", "⚠ A package is in use, so cash / percentage coupons can't be stacked. You can still use \"Free SPA / add-on\" coupons.")}
                    </div>
                  )}
                  <div style={{background:"#fff",border:"1px dashed #FDBA74",borderRadius:12,padding:"8px 10px",marginBottom:10}}>
                    <p style={{fontSize:11,color:"#9A3412",margin:"0 0 4px",fontWeight:800}}>{zhEn("📝 使用方法", "📝 How to use")}</p>
                    <p style={{fontSize:11,color:"#9A3412",margin:"0 0 2px",fontWeight:700,opacity:0.85}}>• <b>{zhEn("現金折扣 / 百分比", "Cash / percentage discount")}</b>{zhEn("：選咗即時抵總金額（用套票時不可用）", ": applied to the total immediately (not available with a package)")}</p>
                    <p style={{fontSize:11,color:"#9A3412",margin:"0 0 2px",fontWeight:700,opacity:0.85}}>• <b>{zhEn("免費 SPA", "Free SPA")}</b>{zhEn("：要先喺上面揀返對應 SPA 項目，先會抵扣", ": first pick the matching SPA item above to apply it")}</p>
                    <p style={{fontSize:11,color:"#9A3412",margin:0,fontWeight:700,opacity:0.85}}>• <b>{zhEn("免費加購", "Free add-on")}</b>{zhEn("：要先喺上面揀返對應加購，先會抵扣", ": first pick the matching add-on above to apply it")}</p>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:bk.voucherId === "" ? "#fff" : "transparent",border:"2px solid "+(bk.voucherId === "" ? "#FB923C" : "#FED7AA"),borderRadius:14,cursor:"pointer"}}>
                      <input type="radio" name="vc" checked={bk.voucherId === ""} onChange={() => setBk(b => ({...b, voucherId: ""}))} style={{accentColor:"#FB923C"}}/>
                      <span style={{fontSize:13,fontWeight:800,color:"#9A3412"}}>{zhEn("唔使用", "Don't use")}</span>
                    </label>
                    {usable.map(v => {
                      const sel = bk.voucherId === v.id;
                      const blocked = usesPackage && isCashVoucher(v);
                      return <label key={v.id} onClick={e => { if (blocked) { e.preventDefault(); toast(i18nT("tx.packageCashOnly"), "warning"); } }} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:sel ? "#fff" : blocked ? "#F9FAFB" : "transparent",border:"2px solid "+(sel ? "#FB923C" : blocked ? "#E5E7EB" : "#FED7AA"),borderRadius:14,cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.55:1}}>
                        <input type="radio" name="vc" checked={sel} disabled={blocked} onChange={() => { if (!blocked) setBk(b => ({...b, voucherId: v.id})); }} style={{accentColor:"#FB923C"}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:13,fontWeight:900,color:"#9A3412",margin:0}}>{liveVoucherLabel(v, tenant)}</p>
                          {(() => {
                            // Vouchers come in two shapes:
                            //   - Points-redeemed: top-level { type, pointsCost, label }
                            //   - Stamp-card milestone: { voucher: { type, amount, label }, source: "stampcard" }
                            // The old label code unconditionally read v.type / v.pointsCost
                            // → stamp-card vouchers always rendered "加購 · 已用 undefined 積分兌換".
                            const spec = v.voucher || v;
                            const t = spec.type || v.type;
                            const kindLabel = t === "spa" || t === "free_spa" ? "SPA"
                              : t === "addon" || t === "free_addon" ? zhEn("加購", "Add-ons")
                              : t === "discount" || t === "fixed" ? zhEn("現金折扣", "Cash discount")
                              : t === "percent" ? zhEn("百分比折扣", "Percentage discount")
                              : zhEn("兌換券", "Coupon");
                            const cost = v.pointsCost
                              ? zhEn("已用 " + v.pointsCost + " 積分兌換", "Redeemed with " + v.pointsCost + " points")
                              : v.source === "stampcard"
                                ? zhEn("印花卡贈送", "Stamp card reward")
                                : "";
                            return <p style={{fontSize:10,color:"#9A3412",margin:"2px 0 0",fontWeight:700,opacity:0.7}}>{kindLabel + (cost ? " · " + cost : "")}</p>;
                          })()}
                          {sel && (() => {
                            // Only show status hint for the currently-selected voucher,
                            // so the customer sees instantly whether their pick is active.
                            const spec = v.voucher || v;
                            const t = spec.type || v.type;
                            if (t === "free_spa") {
                              const refId = spec.refId;
                              const refSp = (tenant?.services || []).find(s => s.id === refId);
                              const has = refId && (bk.spaIds || []).includes(refId);
                              if (has && refSp) return <p style={{fontSize:11,color:"#059669",margin:"4px 0 0",fontWeight:800}}>{zhEn("✅ 已抵：", "✅ Applied: ")}{refSp.name}</p>;
                              if (refSp) return <p style={{fontSize:11,color:"#B45309",margin:"4px 0 0",fontWeight:800}}>{zhEn("⚠️ 請喺 SPA 服務揀「", "⚠️ Pick \"")}{refSp.name}{zhEn("」先會抵扣", "\" to apply it")}</p>;
                              return null;
                            }
                            if (t === "free_addon") {
                              const refId = spec.refId;
                              const refAd = (tenant?.addons || []).find(a => a.id === refId);
                              const has = refId && (bk.addons || []).includes(refId);
                              if (has && refAd) return <p style={{fontSize:11,color:"#059669",margin:"4px 0 0",fontWeight:800}}>{zhEn("✅ 已抵：", "✅ Applied: ")}{refAd.name}</p>;
                              if (refAd) return <p style={{fontSize:11,color:"#B45309",margin:"4px 0 0",fontWeight:800}}>{zhEn("⚠️ 請喺加購揀「", "⚠️ Pick \"")}{refAd.name}{zhEn("」先會抵扣", "\" to apply it")}</p>;
                              return null;
                            }
                            if (voucherDiscount > 0) return <p style={{fontSize:11,color:"#059669",margin:"4px 0 0",fontWeight:800}}>{zhEn("✅ 已抵 -$", "✅ Applied -$")}{voucherDiscount}</p>;
                            return null;
                          })()}
                        </div>
                      </label>;
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Optional 美容要求 — customer can describe what they want done
                ("剪短 + 留頭尾"). Shown to staff in the booking detail. */}
            <div style={{background:"#EFF6FF",border:"2px solid #DBEAFE",borderRadius:20,padding:16,marginBottom:16}}>
              <p style={{fontSize:13,fontWeight:900,color:"#1D4ED8",margin:"0 0 6px",display:"flex",alignItems:"center",gap:6}}>
                <Scissors size={14} color="#1D4ED8" strokeWidth={2.5} /> {zhEn("美容要求（可選）", "Grooming request (optional)")}
              </p>
              <p style={{fontSize:11,color:"#3B82F6",margin:"0 0 8px",fontWeight:700}}>{zhEn("同美容師講解今次想點剪、有冇特別要求", "Tell the groomer how you'd like the cut and any special requests")}</p>
              <textarea
                value={bk.groomingRequest || ""}
                onChange={e => setBk(b => ({ ...b, groomingRequest: e.target.value }))}
                placeholder={zhEn("例：盡量剪短，腳部留少少毛、面部圓型修剪", "e.g. Trim as short as possible, leave a little fur on the legs, round face trim")}
                style={{width:"100%",minHeight:60,padding:"10px 12px",borderRadius:12,border:"2px solid #DBEAFE",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",background:"#fff",color:"#1E40AF",boxSizing:"border-box"}}
              />
            </div>

            <div style={{background:"#FFFDFD",border:"2px dashed var(--c-light)",borderRadius:24,padding:20,display:"flex",flexDirection:"column",gap:12,marginBottom:24}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:15}}><span style={{color:"#8A8A9E",fontWeight:700}}>{(selPet?.name || zhEn("毛孩", "Pet")) + " · " + (pricingDetail.baseLabel || zhEn("主服務", "Main service"))}</span><span style={{fontWeight:800,color:"#4A4A68"}}>{pricingDetail.quote ? cQuoteLbl : "$" + pricingDetail.total}</span></div>
              {pricingDetail.extra > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:14}}><span style={{color:"var(--c-primary)",display:"flex",alignItems:"center",gap:6,fontWeight:800}}><Zap size={14} fill="var(--c-primary)" />{pricingDetail.ruleLabel}</span><span style={{fontWeight:900,color:"var(--c-primary)"}}>+${pricingDetail.extra}</span></div>}
              {/* #28 Multi-pet sibling lines */}
              {extraPetPricing.map(({ pet, detail }) => (
                <div key={pet.id} style={{display:"flex",justifyContent:"space-between",fontSize:15}}>
                  <span style={{color:"#8A8A9E",fontWeight:700}}>{pet.name + " · " + (detail.baseLabel || zhEn("主服務","Main service")) + zhEn(" (同行)", " (companion)")}</span>
                  <span style={{fontWeight:800,color:"#4A4A68"}}>{detail.quote ? cQuoteLbl : "$" + detail.total}</span>
                </div>
              ))}
              {/* SPA line items — picked from spaServices in step 4. Each
                  contributes to total but was missing from the breakdown,
                  so the customer's eye couldn't reconcile 服務 + SPA + 加購
                  with 總金額. */}
              {(bk.spaIds || []).map(id => {
                const sp = spaServices.find(s => s.id === id);
                if (!sp) return null;
                const sd = selPet ? getPricingDetail(selPet, sp, tenant) : { total: sp.basePrice };
                return (
                  <div key={"spa-" + id} style={{display:"flex",justifyContent:"space-between",fontSize:15}}>
                    <span style={{color:"#8A8A9E",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>💆 {sp.name}</span>
                    <span style={{fontWeight:800,color:"#4A4A68"}}>{sd.quote ? cQuoteLbl : "+$" + sd.total}</span>
                  </div>
                );
              })}
              {bk.addons.map(id => { const a = addons.find(x => x.id === id); const ap = a ? computeAddonNet(a, selPet, selSvc) : { price:0, variantLabel:"" }; return <div key={id} style={{display:"flex",justifyContent:"space-between",fontSize:15}}><span style={{color:"#8A8A9E",fontWeight:700}}>{a?.name}{ap.variantLabel && !ap.included ? <span style={{fontSize:12,color:"var(--c-primary)",marginLeft:6}}>· {ap.variantLabel}</span> : null}</span><span style={{fontWeight:800,color:ap.included?"#10B981":"#4A4A68"}}>{ap.included ? T("已包","incl.","込") : "+$" + ap.price}</span></div>; })}
              <Divider />
              <div style={{display:"flex",justifyContent:"space-between",fontSize:18,fontWeight:900}}><span>{zhEn("總金額", "Total")}</span><span style={{color:"var(--c-primary)"}}>{cHasQuote ? (total > 0 ? "$" + total + " + " + cQuoteLbl : cQuoteLbl) : "$" + total}</span></div>
              {cHasQuote && <p style={{fontSize:11,color:"#92400E",background:"#FEF3C7",padding:"8px 12px",borderRadius:10,fontWeight:700,margin:0,lineHeight:1.5}}>{i18nGetLocale()!== "zh-HK" ? "Some items are priced on consultation. The shop confirms the final price." : zhEn("部分項目為面議，最終價格由店家確認。", "Some items are priced on consultation. The shop confirms the final price.")}</p>}
              {deposit > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:14}}><span style={{color:"#8A8A9E",fontWeight:700}}>{zhEn("訂金", "Deposit")}</span><span style={{fontWeight:800,color:"#D97706"}}>{"$" + deposit}</span></div>}
              {liveUser.points > 0 && <div onClick={() => setUsePoints(!usePoints)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:16,border:"2px solid "+(usePoints?"var(--c-primary)":"var(--c-light)"),background:usePoints?"var(--c-lighter)":"#FFFDFD",cursor:"pointer",marginTop:8,transition:"all 0.2s"}}>
                <span style={{fontSize:14,color:usePoints?"var(--c-primary)":"#8A8A9E",fontWeight:800,display:"flex",alignItems:"center",gap:6}}><Award size={16} />{zhEn("使用積分折抵 (" + liveUser.points + " pt = $" + liveUser.points + ")", "Use points as discount (" + liveUser.points + " pt = $" + liveUser.points + ")")}</span>
                <div style={{width:24,height:24,borderRadius:99,border:"2px solid "+(usePoints?"var(--c-primary)":"#AFAFC4"),background:usePoints?"var(--c-primary)":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>{usePoints && <Check size={14} color="#fff" strokeWidth={3} />}</div>
              </div>}
              {ptDiscount > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:15,color:"#10B981",fontWeight:800,marginTop:4}}><span>{i18nT("cust.book.pointsDiscount")} 🎁</span><span>{"-$" + ptDiscount}</span></div>}
              {voucherDiscount > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:15,color:"#10B981",fontWeight:800,marginTop:4}}><span>🎟️ {i18nT("cust.book.voucherDiscount")}</span><span>{"-$" + voucherDiscount}</span></div>}
              {voucherDiscount > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:900,marginTop:4,paddingTop:8,borderTop:"1px dashed var(--c-light)"}}><span>{zhEn("應付總額", "Amount due")}</span><span style={{color:"var(--c-primary)"}}>{"$" + Math.max(0, total - voucherDiscount)}</span></div>}
              {deposit > 0 && <div style={{background:"var(--c-lighter)",padding:"16px",borderRadius:16,marginTop:8}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:900,marginBottom:8}}><span>{i18nT("cust.book.actualDeposit")}</span><span style={{color:"#10B981",fontSize:20}}>{"$" + toPay}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#8A8A9E",fontWeight:700}}>{i18nT("cust.book.walletBalance")}</span><span style={{color:liveUser.wallet>=toPay?"#10B981":"#EF4444",fontWeight:800}}>{"$" + liveUser.wallet}</span></div>
              </div>}
              <p style={{fontSize:13,color:"var(--c-grad1)",margin:0,textAlign:"center",fontWeight:800}}>{(() => { const pts = Math.max(0, Math.floor(Math.max(0, total - (Number(liveUser.wallet) >= toPay ? toPay : 0) - ptDiscount - voucherDiscount) / 100)); return zhEn("完成美容後可獲得 " + pts + " 積分 🎉", "Earn " + pts + " points after grooming 🎉"); })()}</p>
              {/* Estimated-only disclaimer. Real cases (解結 面議, 嚴重打結
                  要剃毛, 去蚤 要附加費) often push the actual total higher;
                  showing this upfront sets expectations + protects the
                  shop from "為咩你之前話 $X 而家收 $Y?" disputes. */}
              <div style={{background:"#FFFBEB",border:"1.5px solid #FDE68A",borderRadius:14,padding:"10px 14px",marginTop:6}}>
                <p style={{fontSize:11,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.55}}>
                  {i18nGetLocale() !== "zh-HK"
                    ? "⚠️ This amount is a preliminary estimate. The actual charge may differ (e.g. wrong breed or weight, dematting/shave-down, flea or tick removal, behavioural issues such as biting/anxiety)."
                    : zhEn("⚠️ 此金額為初步估算。實際收費可能不同(如品種或體重有誤，需解結剷結，去蚤或蜱蟲，行為問題如咬人/焦慮)", "⚠️ This is a preliminary estimate. The actual charge may differ (e.g. if the breed or weight is wrong, or for de-matting, fleas or ticks, or behaviour issues like biting / anxiety)")}
                </p>
              </div>
            </div>

            {/* Senior-pet disclaimer */}
            {selPet && selPet.type !== "small" && petAge(selPet.birthday) >= seniorAgeFor(tenant) && (() => {
              const seniorText = (tenant?.disclaimerSenior || "").trim() || DEFAULT_DISCLAIMER_SENIOR;
              return <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:20,padding:16,marginBottom:14}}>
                <p style={{fontSize:14,fontWeight:900,color:"#92400E",margin:"0 0 8px"}}>{zhEn("🐾 長者毛孩特別聲明（", "🐾 Senior pet declaration (")}{petAge(selPet.birthday)} {zhEn("歲）", " yrs and over)")}</p>
                <pre style={{fontSize:12,color:"#92400E",fontWeight:600,whiteSpace:"pre-wrap",lineHeight:1.6,margin:"0 0 10px",fontFamily:"inherit",maxHeight:180,overflowY:"auto"}}>{seniorText}</pre>
                <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={acceptedSenior} onChange={e => setAcceptedSenior(e.target.checked)} style={{marginTop:2,transform:"scale(1.2)"}} />
                  <span style={{fontSize:13,fontWeight:800,color:"#92400E"}}>{zhEn("本人已閱讀並理解上述條款，同意承擔風險。", "I have read and understood the terms above and accept the risks.")}</span>
                </label>
              </div>;
            })()}

            {/* Cat booking notice */}
            {selPet?.type === "cat" && (() => {
              const catText = (tenant?.termsCat || "").trim() || DEFAULT_TERMS_CAT;
              return <div style={{background:"#F5F3FF",border:"2px solid #C4B5FD",borderRadius:20,padding:16,marginBottom:14}}>
                <p style={{fontSize:14,fontWeight:900,color:"#5B21B6",margin:"0 0 8px"}}>{zhEn("🐱 貓貓美容預約須知", "🐱 Cat grooming booking notice")}</p>
                <pre style={{fontSize:12,color:"#5B21B6",fontWeight:600,whiteSpace:"pre-wrap",lineHeight:1.6,margin:"0 0 10px",fontFamily:"inherit",maxHeight:200,overflowY:"auto"}}>{catText}</pre>
                <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={acceptedCat} onChange={e => setAcceptedCat(e.target.checked)} style={{marginTop:2,transform:"scale(1.2)"}} />
                  <span style={{fontSize:13,fontWeight:800,color:"#5B21B6"}}>{zhEn("本人已閱讀並理解貓貓美容須知，同意承擔相關風險。", "I have read and understood the cat grooming notice and accept the related risks.")}</span>
                </label>
              </div>;
            })()}

            {/* Booking terms — always required */}
            {(() => {
              const termsText = (tenant?.termsBooking || "").trim() || DEFAULT_TERMS_BOOKING;
              return <div style={{background:"#FFFDFD",border:"2px solid var(--c-light)",borderRadius:20,padding:16,marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{zhEn("📋 預約條款", "📋 Booking terms")}</p>
                  <button onClick={() => setShowTermsFull(v => !v)} style={{background:"none",border:"none",fontSize:12,color:"var(--c-primary)",fontWeight:800,cursor:"pointer"}}>{showTermsFull ? zhEn("收起 ▲", "Collapse ▲") : zhEn("展開 ▼", "Expand ▼")}</button>
                </div>
                {showTermsFull && <pre style={{fontSize:12,color:"#4A4A68",fontWeight:600,whiteSpace:"pre-wrap",lineHeight:1.6,margin:"0 0 10px",fontFamily:"inherit",maxHeight:260,overflowY:"auto",background:"var(--c-lighter)",padding:10,borderRadius:12}}>{termsText}</pre>}
                <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} style={{marginTop:2,transform:"scale(1.2)"}} />
                  <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("本人已閱讀並同意", "I have read and agree to the booking terms")} <button type="button" onClick={() => setShowTermsFull(true)} style={{background:"none",border:"none",padding:0,color:"var(--c-primary)",fontWeight:900,cursor:"pointer",textDecoration:"underline"}}>{zhEn("預約條款", "Booking terms")}</button>{zhEn("。", ". ")}</span>
                </label>
              </div>;
            })()}

            {/* Over-weight block + blocked-breed block. Both render a red
                notice and disable 確認預約 when the shop's policy excludes
                this pet. */}
            {(() => {
              const maxW = Number(tenant?.maxPetWeightKg) || 0;
              const petW = Number(selPet?.weight) || 0;
              if (maxW <= 0 || petW <= 0 || petW <= maxW) return null;
              return (
                <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:16,padding:14,marginBottom:14}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#991B1B",margin:"0 0 4px",display:"flex",alignItems:"center",gap:6}}>{zhEn("⚠️ 體重超出店家服務範圍", "⚠️ Weight exceeds the shop's service range")}</p>
                  <p style={{fontSize:12,color:"#991B1B",margin:0,fontWeight:700,lineHeight:1.5,opacity:0.9}}>{zhEn("本店最大接受體重為", "This shop's maximum accepted weight is")} {maxW}{zhEn("kg，", "kg, ")}{selPet?.name || zhEn("您嘅毛孩", "Your pet")} {zhEn("為", "is")} {petW}{zhEn("kg。請聯絡店家了解詳情。", "kg. Please contact the shop for details.")}</p>
                </div>
              );
            })()}
            {(() => {
              const blocked = Array.isArray(tenant?.blockedBreedIds) ? tenant.blockedBreedIds : [];
              if (!selPet?.breedId || !blocked.includes(selPet.breedId)) return null;
              const b = getBreedById(selPet.breedId);
              const name = b ? (((tenant?.lang || "zh-HK").startsWith("zh") ? b.zh : b.en) || b.en) : (selPet?.breed || zhEn("此品種", "This breed"));
              return (
                <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:16,padding:14,marginBottom:14}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#991B1B",margin:"0 0 4px",display:"flex",alignItems:"center",gap:6}}>{zhEn("🚫 此品種暫不接受預約", "🚫 This breed isn't currently accepted for booking")}</p>
                  <p style={{fontSize:12,color:"#991B1B",margin:0,fontWeight:700,lineHeight:1.5,opacity:0.9}}>{zhEn("本店暫時不接受", "This shop isn't currently accepting")} {name} {zhEn("嘅美容預約。請聯絡店家了解詳情。", " grooming bookings. Please contact the shop for details.")}</p>
                </div>
              );
            })()}

            {(() => {
              const isSenior = selPet?.birthday && selPet?.type !== "small" && petAge(selPet.birthday) >= seniorAgeFor(tenant);
              const isCat = selPet?.type === "cat";
              const ageM = petAgeMonths(selPet?.birthday);
              const tooYoung = ageM !== null && ageM < MIN_PET_AGE_MONTHS;
              const maxW = Number(tenant?.maxPetWeightKg) || 0;
              const overWeight = maxW > 0 && Number(selPet?.weight || 0) > maxW;
              const blocked = Array.isArray(tenant?.blockedBreedIds) ? tenant.blockedBreedIds : [];
              const blockedBreed = !!(selPet?.breedId && blocked.includes(selPet.breedId));
              const canConfirm = !tooYoung && !overWeight && !blockedBreed && acceptedTerms && (!isSenior || acceptedSenior) && (!isCat || acceptedCat);
              // Surface the BLOCKING reason via toast when the customer
              // taps a disabled button. Silent-disabled was confusing —
              // owners would tell us "submit does nothing" because they
              // couldn't see which guard was failing.
              const blockerMsg = tooYoung ? zhEn("毛孩月齡未夠（少於 ", "Pet is too young (under ") + MIN_PET_AGE_MONTHS + zhEn(" 個月）", " months)")
                : overWeight ? (zhEn("體重超出本店服務範圍（", "Weight exceeds this shop's range (") + Number(tenant?.maxPetWeightKg) + zhEn("kg）", "kg)"))
                : blockedBreed ? zhEn("此品種暫不接受預約", "This breed isn't currently accepted")
                : !acceptedTerms ? zhEn("請先勾選同意「預約條款」", "Please tick to agree to the booking terms")
                : (isSenior && !acceptedSenior) ? zhEn("請先勾選同意「長者毛孩特別聲明」", "Please tick to agree to the senior pet declaration")
                : (isCat && !acceptedCat) ? zhEn("請先勾選同意「貓貓美容預約須知」", "Please tick to agree to the cat grooming notice")
                : "";
              return <Btn disabled={bookingBusy} style={{width:"100%",padding:"16px",opacity: bookingBusy ? 0.6 : (canConfirm?1:0.6)}} onClick={() => { if (bookingBusy) return; if (!canConfirm) { toast(blockerMsg, "warning"); return; } handleCheckout(); }}>{bookingBusy ? <><Spinner /> {zhEn("提交中…", "Submitting…")}</> : zhEn("確認預約 🎉", "Confirm booking 🎉")}</Btn>;
            })()}
          </Card>}
        </div>}

        {/* ── REWARDS TAB ─────────────────────────────────────────────
            Centralised view of everything the customer has earned at THIS
            shop: stamp-card progress, unredeemed milestone vouchers, shop-
            issued vouchers, loyalty points. Replaces the duplicated
            stamp/voucher cards that used to live on home + profile tabs. */}
        {tab === "rewards" && <div style={{paddingTop:12,display:"flex",flexDirection:"column",gap:16}}>
          {(() => {
            const sc = tenant?.stampCard;
            const myMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
            const totalSlots = sc?.totalSlots || 10;
            const stamps = Math.min(Number(myMem?.stamps || 0), totalSlots);
            const milestones = sc?.enabled ? [...(sc.milestones || [])].sort((a, b) => (a.atSlot || 0) - (b.atSlot || 0)) : [];
            const nextMs = milestones.find(m => (m.atSlot || 0) > stamps);
            const stampsToNext = nextMs ? Math.max(0, (nextMs.atSlot || 0) - stamps) : 0;
            const allVouchers = (myMem?.vouchers || []).filter(v => !v.used);
            const unlockedFromCard = allVouchers.filter(v => v.source === "stampcard" || v.source === "stamp_card");
            const shopVouchers = allVouchers.filter(v => !(v.source === "stampcard" || v.source === "stamp_card"));
            const points = Number(myMem?.points || liveUser.points || 0);

            return (
              <>
                {/* Hero summary strip — chips for stamps / vouchers / points.
                    Points chip surfaces only when the shop has enabled
                    loyalty (設定 → 店鋪政策 → 啟用積分回贈). */}
                <div style={{display:"grid",gridTemplateColumns: tenant?.loyaltyEnabled ? "1fr 1fr 1fr" : "1fr 1fr",gap:10}}>
                  <div style={{background:"linear-gradient(135deg,#FB923C,#F97316)",color:"#fff",borderRadius:20,padding:"14px 12px",textAlign:"center"}}>
                    <p style={{fontSize:10,fontWeight:800,opacity:0.85,margin:"0 0 4px",letterSpacing:0.5}}>{zhEn("印章", "Stamps")}</p>
                    <p style={{fontSize:20,fontWeight:900,margin:0}}>{sc?.enabled ? stamps + " / " + totalSlots : "—"}</p>
                  </div>
                  <div style={{background:"linear-gradient(135deg,#10B981,#059669)",color:"#fff",borderRadius:20,padding:"14px 12px",textAlign:"center"}}>
                    <p style={{fontSize:10,fontWeight:800,opacity:0.85,margin:"0 0 4px",letterSpacing:0.5}}>{zhEn("獎勵券", "Reward coupons")}</p>
                    <p style={{fontSize:20,fontWeight:900,margin:0}}>{allVouchers.length}</p>
                  </div>
                  {tenant?.loyaltyEnabled && (
                    <div title={zhEn("1 分 = $", "1 pt = $") + (Number(tenant?.loyaltyRedeemValue) || 1) + zhEn(" 現金", " cash")} style={{background:"linear-gradient(135deg,#8B5CF6,#7C3AED)",color:"#fff",borderRadius:20,padding:"14px 12px",textAlign:"center"}}>
                      <p style={{fontSize:10,fontWeight:800,opacity:0.85,margin:"0 0 4px",letterSpacing:0.5}}>{zhEn("積分", "Points")}</p>
                      <p style={{fontSize:20,fontWeight:900,margin:0}}>{points}</p>
                      <p style={{fontSize:9,fontWeight:700,opacity:0.7,margin:"2px 0 0"}}>≈ ${Math.round((points * (Number(tenant?.loyaltyRedeemValue) || 1)))}</p>
                    </div>
                  )}
                </div>

                {/* Stamp card detail — only shown if shop has it enabled */}
                {sc?.enabled && (
                  <Card style={{padding:0,background:"#FFFDFA",border:"2px solid #FED7AA",overflow:"hidden"}}>
                    <div style={{padding:"16px 20px",background:"linear-gradient(135deg,#FB923C,#F97316)",color:"#fff"}}>
                      <h3 style={{fontWeight:900,fontSize:18,margin:0,display:"flex",alignItems:"center",gap:8,letterSpacing:0.5}}>
                        <span style={{fontSize:22}}>📇</span> {zhEn("我的印花卡", "My stamp card")}
                      </h3>
                      <div style={{height:6,background:"rgba(255,255,255,0.25)",borderRadius:99,overflow:"hidden",marginTop:10}}>
                        <div style={{height:"100%",width:`${(stamps / totalSlots) * 100}%`,background:"#fff",borderRadius:99,transition:"width 0.4s ease"}} />
                      </div>
                      <p style={{fontSize:12,margin:"8px 0 0",fontWeight:700,opacity:0.95}}>
                        {nextMs
                          ? zhEn(`再 ${stampsToNext} 個印章獲贈「${milestoneLiveLabel(nextMs, tenant)}」🎁`, `${stampsToNext} more stamps to earn "${milestoneLiveLabel(nextMs, tenant)}" 🎁`)
                          : (milestones.length === 0 ? zhEn("完成預約即獲印章", "Complete a booking to earn a stamp") : zhEn("🎊 印滿啦，下次預約開新卡", "🎊 Card full! A new card starts with your next booking"))}
                      </p>
                    </div>
                    <div style={{padding:"14px",display:"grid",gridTemplateColumns:`repeat(${Math.min(totalSlots, 5)}, 1fr)`,gap:8}}>
                      {Array.from({length: totalSlots}, (_, i) => {
                        const slot = i + 1;
                        const filled = slot <= stamps;
                        const ms = milestones.find(m => m.atSlot === slot);
                        return (
                          <div key={i} style={{aspectRatio:"1/1",borderRadius:12,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,gap:2,background:filled?(ms?"linear-gradient(135deg,#10B981,#059669)":"linear-gradient(135deg,#FB923C,#F97316)"):(ms?"#ECFDF5":"#FFF7ED"),color:filled?"#fff":(ms?"#065F46":"#9A3412"),border:filled?"none":(ms?"2px dashed #6EE7B7":"2px dashed #FED7AA")}}>
                            {filled ? (ms ? "🎉" : "🐾") : (ms ? "🎁" : slot)}
                            {ms && !filled && <span style={{fontSize:8,fontWeight:800,opacity:0.7,maxWidth:"100%",padding:"0 2px",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{milestoneLiveLabel(ms, tenant)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Unlocked stamp-card rewards — direct CTA to book */}
                {unlockedFromCard.length > 0 && (
                  <Card style={{padding:16,background:"linear-gradient(135deg,#ECFDF5,#D1FAE5)",border:"2px solid #6EE7B7"}}>
                    <p style={{fontSize:14,fontWeight:900,color:"#065F46",margin:"0 0 10px",display:"flex",alignItems:"center",gap:6}}>
                      {zhEn("🔓 已解鎖獎賞", "🔓 Rewards unlocked")} <span style={{fontSize:11,padding:"1px 8px",borderRadius:99,background:"#10B981",color:"#fff",fontWeight:900}}>{unlockedFromCard.length}</span>
                    </p>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {unlockedFromCard.map(v => (
                        <div key={v.id} style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1.5px solid #A7F3D0",display:"flex",alignItems:"center",gap:10}}>
                          <span style={{fontSize:22}}>🎁</span>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:13,fontWeight:900,color:"#065F46",margin:0}}>{liveVoucherLabel(v, tenant) || zhEn("印花卡獎勵", "Stamp card reward")}</p>
                            <p style={{fontSize:10,color:"#047857",margin:"2px 0 0",fontWeight:700,opacity:0.8}}>{v.redeemedAt ? zhEn("獲得日期 ", "Earned on ") + (v.redeemedAt || "").slice(0,10) : zhEn("印花卡贈送", "Stamp card reward")}</p>
                          </div>
                          <button onClick={() => { setBk(b => ({ ...b, voucherId: v.id })); setTab("book"); toast(zhEn("已套用：", "Applied: ") + (v.label || zhEn("獎勵", "Reward")), "success"); }} style={{padding:"7px 14px",borderRadius:99,background:"#10B981",border:"none",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 3px 8px rgba(16,185,129,0.35)"}}>{zhEn("立即使用 →", "Use now →")}</button>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Shop-issued vouchers */}
                {shopVouchers.length > 0 && (
                  <Card style={{padding:20}}>
                    <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("🎟️ 店鋪贈送嘅獎勵券", "🎟️ Reward coupons from the shop")}</h3>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {shopVouchers.map(v => (
                        <div key={v.id} style={{padding:14,borderRadius:14,background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)",border:"2px dashed #FED7AA"}}>
                          <p style={{fontSize:14,fontWeight:900,color:"#9A3412",margin:0}}>{liveVoucherLabel(v, tenant) || zhEn("獎勵券", "Reward coupons")}</p>
                          <p style={{fontSize:11,color:"#9A3412",margin:"3px 0 0",fontWeight:700,opacity:0.8}}>{zhEn("下次預約時揀「使用獎勵券」即可", "Choose \"Use reward coupon\" on your next booking")}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Empty state — nothing yet */}
                {!sc?.enabled && allVouchers.length === 0 && !(tenant?.loyaltyEnabled && points > 0) && (
                  <Card style={{padding:30,textAlign:"center"}}>
                    <Award size={48} color="#AFAFC4" strokeWidth={1.5} style={{marginBottom:10}} />
                    <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("暫無禮遇", "No perks yet")}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("完成預約後會自動儲分及解鎖獎勵", "You earn points and unlock rewards automatically after completed bookings")}</p>
                  </Card>
                )}
              </>
            );
          })()}
        </div>}

        {tab === "wallet" && walletOn && <div style={{paddingTop:12,display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:"linear-gradient(135deg, var(--c-grad1) 0%, var(--c-grad2) 100%)",borderRadius:32,padding:32,color:"#fff",boxShadow:"0 16px 40px var(--c-primary-30)",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",right:-20,top:-20,opacity:0.1,transform:"scale(3)",pointerEvents:"none"}}><Wallet size={100} /></div>
            <p style={{fontSize:15,color:"rgba(255,255,255,0.9)",margin:"0 0 6px",fontWeight:800}}>{i18nT("cust.wallet.balance")}</p>
            <p style={{fontSize:48,fontWeight:900,margin:"0 0 18px",letterSpacing:"-1px"}}>{"$" + liveUser.wallet}</p>
            {/* Top-up CTA — payment happens out-of-band via WhatsApp; shop
                confirms in chat then adds funds via the 加值 button in CRM.
                Same pattern as package purchase: no card-on-file required. */}
            <button onClick={() => setTopupSheetOpen(true)} style={{width:"100%",padding:"12px 16px",borderRadius:14,background:"#fff",color:"var(--c-primary)",border:"none",fontWeight:900,fontSize:15,cursor:"pointer",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}>
              <Plus size={18} strokeWidth={3} /> {zhEn("申請儲值", "Request top-up")}
            </button>
            <div style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(12px)",borderRadius:20,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",border:"1px solid rgba(255,255,255,0.5)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}><Award size={20} strokeWidth={2.5} /><span style={{fontSize:15,fontWeight:800}}>{zhEn("忠誠積分", "Loyalty points")}</span></div>
              <span style={{fontWeight:900,fontSize:20}}>{liveUser.points + " pt"}</span>
            </div>
          </div>
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 16px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("交易明細", "Transactions")} <HelpTip content={{title:zhEn("錢包交易明細 💰", "Wallet transactions 💰"),body:zhEn("記錄所有錢包進出：訂金扣款、取消退款、店家加值、消費等。可隨時查閱核對。", "Every wallet movement: deposit deductions, cancellation refunds, shop top-ups, spending and more. Check any time.")}} /></h3>
            {(() => {
              const txList = (liveUser.walletTx || []).slice().sort((a,b) => (b.date||"").localeCompare(a.date||""));
              if (txList.length === 0) return <EmptyState icon={Wallet} title={zhEn("暫無交易記錄", "No transactions yet")} sub={zhEn("充值錢包後記錄會顯示在這裡", "Records appear here after you top up your wallet")} />;
              const TX_CFG = {
                topup:   { bg:"#F0FDF4", color:"#10B981", icon:ArrowUpRight, sign:"+", label:zhEn("加值", "Top-up") },
                deduct:  { bg:"#FEF2F2", color:"#EF4444", icon:ArrowDownLeft, sign:"−", label:zhEn("扣款", "Deduction") },
                refund:  { bg:"#EFF6FF", color:"#2563EB", icon:ArrowUpRight, sign:"+", label:zhEn("退款", "Refund") },
                points:  { bg:"#FFFBEB", color:"#D97706", icon:Award, sign:"+", label:zhEn("積分", "Points") },
              };
              return txList.map((tx, i) => {
                const cfg = TX_CFG[tx.type] || TX_CFG.topup;
                const Ic = cfg.icon;
                return (
                  <div key={tx.id || i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 0",borderBottom:i===txList.length-1?"none":"2px solid var(--c-light)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:14}}>
                      <div style={{width:44,height:44,borderRadius:99,background:cfg.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <Ic size={20} color={cfg.color} strokeWidth={3} />
                      </div>
                      <div>
                        <p style={{fontSize:15,fontWeight:800,margin:"0 0 3px",color:"#4A4A68"}}>{tx.note || cfg.label}</p>
                        <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:600}}>{tx.date} · {cfg.label}</p>
                      </div>
                    </div>
                    <span style={{fontWeight:900,fontSize:17,color:cfg.color}}>{cfg.sign}{tx.type==="points"?(tx.amount+"pt"):("$"+tx.amount)}</span>
                  </div>
                );
              });
            })()}
          </Card>
          {/* Booking history with deposit reference */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 16px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("訂金與消費歷史", "Deposits & spending history")} <HelpTip content={{title:zhEn("訂金與消費歷史 📃", "Deposits & spending history 📃"),body:zhEn("每次預約嘅訂金 / 結帳金額紀錄，方便核數同對帳。", "Deposit / checkout amounts for every booking, for easy auditing and reconciliation.")}} /></h3>
            {(() => {
              const aptsWithDeposit = (myApts || []).filter(a => a.deposit > 0 || a.status === "done").sort((a,b) => (b.date||"").localeCompare(a.date||""));
              if (aptsWithDeposit.length === 0) return <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"12px 0",margin:0}}>{zhEn("暫無記錄", "No records yet")}</p>;
              return aptsWithDeposit.slice(0, 10).map((a, i) => {
                const total = a.basePrice + a.addonPrice + (a.surcharges||[]).reduce((s,x)=>s+x.amount,0);
                const petName = state.pets[a.petId]?.name || "";
                return (
                  <div key={a.id} style={{padding:"12px 0",borderBottom:i===Math.min(9,aptsWithDeposit.length-1)?"none":"1px dashed var(--c-light)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{petName + " · " + a.service}</span>
                      <StatusBadge
                        status={(a.date && a.date < dateNow() && (a.status === "pending" || a.status === "checkedin" || a.status === "grooming")) ? "done" : a.status}
                        cancelReason={a.cancelReason}
                        approvalPending={a.approvalStatus === "pending"}
                      />
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#6A6A85",fontWeight:700}}>
                      <span>{a.date + " " + a.time}</span>
                      <span>{zhEn("訂金 $", "Deposit $")}{a.deposit} {zhEn("/ 總 $", "/ total $")}{total} {a.paid?"✓":""}</span>
                    </div>
                  </div>
                );
              });
            })()}
          </Card>
        </div>}

        {tab === "store" && tenant?.wcEnabled && (
          <CustomerShopStore tenantId={tenant.id} tenantName={tenant.name} user={liveUser} membership={myMemForBk} T={T} />
        )}
        {tab === "profile" && <div style={{paddingTop:12,display:"flex",flexDirection:"column",gap:16}}>
          {/* Marketing opt-out — customer-controlled kill switch for
              shop broadcast pushes. Booking / grooming-done / other
              transactional notifications are unaffected. */}
          {(() => {
            const meProfile = state.users[user.id];
            const optedOut = !!meProfile?.marketingOptOut;
            const toggle = async () => {
              const next = !optedOut;
              setState(s => ({ ...s, users: { ...s.users, [user.id]: { ...s.users[user.id], marketingOptOut: next } } }));
              try { await patchProfile(user.id, { marketingOptOut: next }); toast(next ? zhEn("已關閉推廣通知", "Promotional notifications turned off") : zhEn("推廣通知已開啟", "Promotional notifications turned on")); }
              catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message||""), "error"); }
            };
            return (
              <Card style={{padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                  <span style={{fontSize:18}}>📣</span>
                  <div style={{minWidth:0}}>
                    <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{T("推廣通知","Marketing pushes","マーケティング通知")}</p>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.4}}>{T("店舖優惠、活動、季節性推廣。預約提醒同健康報告等交易通知不受影響。","Shop offers + campaigns. Booking + report notifications still send.","店舗のお知らせ")}</p>
                  </div>
                </div>
                <button onClick={toggle} style={{border:"none",background: optedOut ? "#E5E7EB" : "var(--c-primary)",color:"#fff",borderRadius:99,padding:"7px 14px",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {optedOut ? T("已關閉","Off","オフ") : T("已開啟","On","オン")}
                </button>
              </Card>
            );
          })()}
          {/* Language picker — small inline switcher so customers who
              missed the toggle on the landing page can still change it. */}
          <Card style={{padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18}}>🌐</span>
              <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0}}>{i18nT("settings.language", _loc !== "zh-HK" ? "Language" : "語言")}</p>
            </div>
            <div style={{display:"flex",gap:6}}>
              {i18nListLocales().map(l => (
                <button key={l.code} onClick={() => i18nSetLocale(l.code)} style={{padding:"6px 12px",borderRadius:99,border: l.code === _loc ? "2px solid var(--c-primary)" : "2px solid var(--c-border)",background: l.code === _loc ? "var(--c-primary)" : "#fff",color: l.code === _loc ? "#fff" : "#4A4A68",fontWeight:800,fontSize:12,cursor:"pointer"}}>{l.name}</button>
              ))}
            </div>
          </Card>
          {/* 主題顏色 — the customer's own colour; 跟隨店舖 = use the shop's theme. */}
          <CustomerThemePicker shopTheme={tenant?.theme} />
          {/* My Shops — manage / switch / leave the shops this account belongs to. */}
          {(state.memberships || []).length >= 1 && (
            <Card style={{padding:0,overflow:"hidden"}}>
              <button onClick={() => setShopSheetOpen(true)} style={{width:"100%",padding:"16px 18px",display:"flex",alignItems:"center",gap:12,background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                <div style={{width:40,height:40,borderRadius:12,background:"var(--c-light,#F6F5FF)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Store size={20} color="var(--c-primary)" strokeWidth={2.4}/></div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:"0 0 2px",fontSize:14,fontWeight:900,color:"#4A4A68"}}>{T("我的店舖","My Shops","マイショップ")}</p>
                  <p style={{margin:0,fontSize:12,color:"#8A8A9E",fontWeight:700}}>{(state.memberships || []).length} {T("間店舖 · 切換 / 離開","shops · switch / leave","店舗")}</p>
                </div>
                <ChevronRight size={18} color="#B8B6C8" strokeWidth={2.5} style={{flexShrink:0}}/>
              </button>
            </Card>
          )}
          <Card style={{padding:24}}>
            <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:24}}>
              <div style={{width:72,height:72,background:"linear-gradient(135deg,var(--c-light),var(--c-grad2))",borderRadius:28,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,fontWeight:900,color:"var(--c-primary)"}}>{liveUser.name[0]}</div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontWeight:900,fontSize:22,margin:"0 0 4px",color:"#4A4A68"}}>{liveUser.name}</p>
                {liveUser.email && !liveUser.email.endsWith("@petos.local") && <p style={{fontSize:12,color:"#AFAFC4",margin:"0 0 2px",fontWeight:700,wordBreak:"break-all"}}>{liveUser.email}</p>}
                <p style={{fontSize:14,color:"#8A8A9E",fontWeight:700,margin:0}}>{liveUser.phone ? ((liveUser.countryCode || "+852") + " " + liveUser.phone) : <span style={{color:"#FF4D6D"}}>{zhEn("⚠ 未設定電話", "⚠ No phone set")}</span>}</p>
              </div>
            </div>

            {/* Name editor — writes to BOTH profiles.name AND
                customer_memberships.display_name. The loader in
                dataLoader.attachTenantChildren prefers display_name when
                set, so writing only to profiles would silently revert on
                next reload. */}
            <div style={{background:"var(--c-lighter)",borderRadius:16,padding:14,marginBottom:12,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>👤 {i18nT("profile.name")}</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,alignItems:"center"}}>
                <Input value={profileNameDraft} onChange={e => setProfileNameDraft(e.target.value)} placeholder={i18nT("profile.name")} />
                <Btn size="sm" onClick={async () => {
                  const name = (profileNameDraft || "").trim();
                  if (!name) { toast(i18nT("val.enterPetName"), "error"); return; }
                  try {
                    await patchProfile(liveUser.id, { name });
                    if (liveUser.tenantId) {
                      try { await patchCustomerMembership({ userId: liveUser.id, tenantId: liveUser.tenantId, displayName: name }); }
                      catch (memErr) { /* best-effort — profile.name is the canonical fallback */ console.warn("[PettoBook] membership name sync failed:", memErr); }
                    }
                    setState(s => ({
                      ...s,
                      users: { ...s.users, [liveUser.id]: { ...s.users[liveUser.id], name } },
                      memberships: (s.memberships || []).map(m =>
                        (m.userId === liveUser.id && m.tenantId === liveUser.tenantId)
                          ? { ...m, displayName: name }
                          : m
                      ),
                    }));
                    toast(i18nT("common.savedShort") + " ✓");
                  } catch (e) { toast(e?.message || i18nT("cust.saveFail"), "error"); }
                }}>{i18nT("common.save")}</Btn>
              </div>
            </div>

            {/* Phone editor — same two-table write as name. The membership
                row's phone is the displayed value; profile.phone is the
                shop-search fallback when no membership exists. */}
            <div style={{background:"var(--c-lighter)",borderRadius:16,padding:14,marginBottom:12,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>📱 {i18nT("cust.contactPhone")}</p>
              <div style={{display:"grid",gridTemplateColumns:"110px 1fr auto",gap:8,alignItems:"center"}}>
                <Sel value={profilePhoneDraft.countryCode} onChange={e => setProfilePhoneDraft(f => ({...f, countryCode: e.target.value}))}>
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </Sel>
                <Input value={profilePhoneDraft.phone}
                  // inputMode + the digits-only onChange make mobile
                  // keyboards show the numpad AND prevent typed letters
                  // / spaces from ever entering state. Country-aware
                  // length validation runs on save (validatePhone).
                  inputMode="tel"
                  onChange={e => { const parsed = parseCopiedPhone(e.target.value); setProfilePhoneDraft(f => ({...f, phone: parsed.digits, ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {})})); }}
                  placeholder={profilePhoneDraft.countryCode === "+852" ? "9123 4567" : profilePhoneDraft.countryCode === "+1" ? "212 555 1234" : zhEn("電話號碼", "Phone number")}
                  type="tel" />
                <Btn size="sm" onClick={async () => {
                  const cc = profilePhoneDraft.countryCode || "+852";
                  const check = validatePhone(cc, profilePhoneDraft.phone);
                  if (!check.valid) { toast(check.error, "error"); return; }
                  const phone = normalizePhoneDigits(profilePhoneDraft.phone);
                  const res = await saveCustomerPhone({ userId: liveUser.id, phone, countryCode: cc, tenantId: liveUser.tenantId, patchMembership: true });
                  if (res.ok) {
                    setState(s => ({
                      ...s,
                      users: { ...s.users, [liveUser.id]: { ...s.users[liveUser.id], phone, countryCode: cc } },
                      memberships: (s.memberships || []).map(m =>
                        (m.userId === liveUser.id && m.tenantId === liveUser.tenantId)
                          ? { ...m, phone }
                          : m
                      ),
                    }));
                    toast(res.claimFiled
                      ? zhEn("已通知店主核實並合併你喺呢間店嘅舊紀錄，核實後會自動連結 ✓", "The shop owner has been asked to verify and merge your existing records; they'll link automatically once verified ✓")
                      : (i18nT("common.savedShort") + " ✓"),
                      res.claimFiled ? "info" : "success");
                  } else {
                    toast(res.error?.message || i18nT("cust.saveFail"), "error");
                  }
                }}>{i18nT("common.save")}</Btn>
              </div>
            </div>

            {/* Address editor */}
            <div style={{background:"var(--c-lighter)",borderRadius:16,padding:14,marginBottom:18,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>📍 {i18nT("cust.address")}</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,alignItems:"center"}}>
                <Input value={profileAddressDraft} onChange={e => setProfileAddressDraft(e.target.value)} placeholder={i18nT("cust.address")} />
                <Btn size="sm" onClick={async () => {
                  const address = (profileAddressDraft || "").trim();
                  try {
                    await patchProfile(liveUser.id, { address });
                    setState(s => ({ ...s, users: { ...s.users, [liveUser.id]: { ...s.users[liveUser.id], address } } }));
                    toast(i18nT("common.savedShort") + " ✓");
                  } catch (e) { toast(e?.message || i18nT("cust.saveFail"), "error"); }
                }}>{i18nT("common.save")}</Btn>
              </div>
            </div>
            {[[i18nT("cust.profile.accountId"), liveUser.id],[i18nT("cust.profile.shopBranch"), tenant?.name || i18nT("cust.profile.notSet")],[i18nT("cust.profile.tags"), (liveUser.tags||[]).join(i18nT("lang.name") === "English" ? ", " : zhEn("、", ", ")) || i18nT("cust.profile.notSet")]].map(([l, v]) => (
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"14px 0",borderBottom:"2px solid var(--c-light)"}}>
                <span style={{fontSize:14,color:"#8A8A9E",fontWeight:700}}>{l}</span>
                <span style={{fontSize:14,color:"#4A4A68",fontWeight:800,textAlign:"right",maxWidth:"60%"}}>{v}</span>
              </div>
            ))}
          </Card>

          {/* Stamp card moved to 📇 禮遇 tab — kept disabled here so
              settings stays focused on account / privacy / preferences. */}
          {false && (() => {
            const sc = tenant?.stampCard;
            const myMem = (state.memberships || []).find(m => m.userId === liveUser.id && m.tenantId === liveUser.tenantId);
            if (!sc?.enabled) return null;
            const totalSlots = sc.totalSlots || 10;
            const stamps = Math.min(Number(myMem?.stamps || 0), totalSlots);
            const milestones = [...(sc.milestones || [])].sort((a, b) => (a.atSlot || 0) - (b.atSlot || 0));
            const nextMs = milestones.find(m => (m.atSlot || 0) > stamps);
            const stampsToNext = nextMs ? Math.max(0, (nextMs.atSlot || 0) - stamps) : 0;
            return (
              <Card style={{padding:0,background:"#FFFDFA",border:"2px solid #FED7AA",overflow:"hidden"}}>
                <div style={{padding:"16px 20px",background:"linear-gradient(135deg,#FB923C,#F97316)",color:"#fff"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                    <h3 style={{fontWeight:900,fontSize:19,margin:0,display:"flex",alignItems:"center",gap:8,letterSpacing:0.5}}>
                      <span style={{fontSize:22}}>📇</span> {zhEn("我的印花卡", "My stamp card")}
                    </h3>
                    <div style={{background:"rgba(255,255,255,0.22)",padding:"4px 12px",borderRadius:99,backdropFilter:"blur(8px)"}}>
                      <span style={{fontSize:15,fontWeight:900}}>{stamps}</span>
                      <span style={{fontSize:11,fontWeight:700,opacity:0.85}}> / {totalSlots}</span>
                    </div>
                  </div>
                  <div style={{height:6,background:"rgba(255,255,255,0.25)",borderRadius:99,overflow:"hidden",marginTop:6}}>
                    <div style={{height:"100%",width:`${(stamps / totalSlots) * 100}%`,background:"#fff",borderRadius:99,transition:"width 0.4s ease"}} />
                  </div>
                  <p style={{fontSize:12,margin:"8px 0 0",fontWeight:700,opacity:0.95}}>
                    {nextMs
                      ? zhEn(`再 ${stampsToNext} 個印章獲贈「${milestoneLiveLabel(nextMs, tenant)}」🎁`, `${stampsToNext} more stamps to earn "${milestoneLiveLabel(nextMs, tenant)}" 🎁`)
                      : (milestones.length === 0 ? zhEn("完成預約即獲印章", "Complete a booking to earn a stamp") : zhEn("🎊 印滿啦，下次預約開新卡", "🎊 Card full! A new card starts with your next booking"))}
                  </p>
                </div>
                <div style={{padding:"16px 14px"}}>
                  {/* 已解鎖獎賞 — vouchers earned via stamp-card milestones
                      that haven't been used yet. Tapping 「立即使用」 jumps
                      to the booking flow with the voucher pre-selected so
                      the customer doesn't have to hunt for it.
                      Supports both "stampcard" (new RPC) and "stamp_card"
                      (legacy client) source markers. */}
                  {(() => {
                    const unlocked = (myMem?.vouchers || []).filter(v => !v.used && (v.source === "stampcard" || v.source === "stamp_card"));
                    if (unlocked.length === 0) return null;
                    return (
                      <div style={{background:"linear-gradient(135deg,#ECFDF5,#D1FAE5)",border:"2px solid #6EE7B7",borderRadius:16,padding:"12px 14px",marginBottom:14}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <p style={{fontSize:13,fontWeight:900,color:"#065F46",margin:0,display:"flex",alignItems:"center",gap:6}}>
                            {zhEn("🔓 已解鎖獎賞", "🔓 Rewards unlocked")} <span style={{fontSize:11,padding:"1px 8px",borderRadius:99,background:"#10B981",color:"#fff",fontWeight:900}}>{unlocked.length}</span>
                          </p>
                          <span style={{fontSize:10,color:"#065F46",fontWeight:700,opacity:0.8}}>{zhEn("下次預約時自動套用", "Applied automatically on your next booking")}</span>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {unlocked.map(v => (
                            <div key={v.id} style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1.5px solid #A7F3D0",display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:22}}>🎁</span>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontSize:13,fontWeight:900,color:"#065F46",margin:0}}>{liveVoucherLabel(v, tenant) || zhEn("印花卡獎勵", "Stamp card reward")}</p>
                                <p style={{fontSize:10,color:"#047857",margin:"2px 0 0",fontWeight:700,opacity:0.8}}>
                                  {v.redeemedAt ? zhEn("獲得日期 ", "Earned on ") + (v.redeemedAt || "").slice(0,10) : zhEn("印花卡贈送", "Stamp card reward")}
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  setBk(b => ({ ...b, voucherId: v.id }));
                                  setTab("book");
                                  toast(zhEn("已套用：", "Applied: ") + (v.label || zhEn("獎勵", "Reward")) + zhEn("，下一步預約即可使用 🎁", " — use it in the next booking step 🎁"), "success");
                                }}
                                style={{padding:"7px 14px",borderRadius:99,background:"#10B981",border:"none",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 3px 8px rgba(16,185,129,0.35)"}}
                              >{zhEn("立即使用 →", "Use now →")}</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(totalSlots, 5)}, minmax(0, 1fr))`,gap:8,width:"100%"}}>
                    {Array.from({length: totalSlots}, (_, i) => {
                      const slot = i + 1;
                      const filled = slot <= stamps;
                      const ms = milestones.find(m => m.atSlot === slot);
                      const reached = ms && slot <= stamps;
                      // Reward label is the hero copy inside milestone
                      // boxes — bump significantly so it's actually
                      // legible on a phone. Auto-scale by string length so
                      // "$50 折扣" gets big type while "免費 SPA 高級護理"
                      // still fits without overflowing.
                      // Shrink fonts so labels of any length fit without
                      // truncation; user feedback was the previous 11-17px
                      // range was too big on multi-line labels.
                      const labelLen = ms?.rewardLabel?.length || 0;
                      // clamp() so the tile font grows on iPad (≥768px) but
                      // stays compact on phones. Min: phone-fit. Max: tablet.
                      // Three buckets by label length so long labels still
                      // shrink to fit.
                      const fontPx = ms
                        ? (labelLen > 10 ? "clamp(11px, 1.6vw, 18px)" : labelLen > 7 ? "clamp(12px, 1.9vw, 20px)" : labelLen > 4 ? "clamp(14px, 2.2vw, 24px)" : "clamp(16px, 2.6vw, 28px)")
                        : "clamp(16px, 2.6vw, 28px)";
                      const fullRefName = (() => {
                        const vc = ms?.voucher || {};
                        if (vc.type === "free_spa") return (tenant?.services || []).find(s => s.id === vc.refId)?.name || "";
                        if (vc.type === "free_addon") return (tenant?.addons || []).find(a => a.id === vc.refId)?.name || "";
                        return "";
                      })();
                      return (
                        <div key={i} style={{
                          aspectRatio:"1",borderRadius:14,padding:"6px 4px",
                          background: filled
                            ? (ms ? "linear-gradient(135deg,#34D399,#10B981)" : "linear-gradient(135deg,#FB923C,#F97316)")
                            : (ms ? "#fff" : "#FFF7ED"),
                          border: ms
                            ? (filled ? "2px solid #10B981" : "2px dashed #10B981")
                            : (filled ? "2px solid #F97316" : "2px dashed #FED7AA"),
                          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"space-between",
                          boxShadow: filled
                            ? (ms ? "0 6px 18px rgba(16,185,129,0.45)" : "0 4px 12px rgba(249,115,22,0.4)")
                            : "inset 0 2px 4px rgba(0,0,0,0.04)",
                          transition:"all 0.3s",position:"relative",overflow:"hidden",
                        }}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
                            <span style={{fontSize:9,fontWeight:900,color: filled ? "rgba(255,255,255,0.85)" : (ms ? "#10B981" : "#FB923C"),letterSpacing:0.5}}>#{slot}</span>
                            {filled && <span style={{fontSize:"clamp(13px, 2vw, 20px)",color:"#fff"}}>{reached ? "🎉" : "✓"}</span>}
                          </div>
                          {ms ? (
                            <div style={{textAlign:"center",lineHeight:1.2,padding:"0 2px"}}>
                              <div style={{fontSize:"clamp(14px, 2vw, 22px)",marginBottom:2}}>🎁</div>
                              {(() => {
                                const [line1, line2] = splitMilestoneLabel(milestoneLiveLabel(ms, tenant));
                                const sty = {margin:0,fontSize:fontPx,fontWeight:900,color: filled ? "#fff" : "#065F46",wordBreak:"break-word",textShadow: filled ? "0 1px 2px rgba(0,0,0,0.2)" : "none",lineHeight:1.2};
                                return line2 ? (<><p style={{...sty,opacity:0.95}}>{line1}</p><p style={sty}>{line2}</p></>) : (<p style={sty}>{line1}</p>);
                              })()}
                            </div>
                          ) : (
                            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              <span style={{fontSize: filled ? 24 : 20,opacity: filled ? 1 : 0.4}}>🐾</span>
                            </div>
                          )}
                          {ms && (
                            <div style={{fontSize:7,fontWeight:900,color: filled ? "rgba(255,255,255,0.95)" : "#10B981",opacity: filled ? 1 : 0.7,letterSpacing:0.3}}>
                              {filled ? "EARNED" : "REWARD"}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p style={{fontSize:10,color:"#9A3412",margin:"12px 4px 0",fontWeight:700,opacity:0.7,textAlign:"center"}}>
                    {zhEn("🐾 = 印章格 · 🎁 = 獎勵格（集到自動發送）", "🐾 = stamp slot · 🎁 = reward slot (sent automatically when reached)")}
                  </p>
                </div>
              </Card>
            );
          })()}

          {/* 我的獎勵券 moved to 📇 禮遇 tab — no longer duplicated here. */}

          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 16px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("cust.label.recurringSettings")} <HelpTip content={{title:zhEn("定期預約 🔁", "Recurring bookings 🔁"),body:zhEn("設定固定週期嘅預約（例：每兩星期一次嘅沖涼）。系統會自動提前一星期通知，方便定期客戶。", "Set up bookings on a fixed schedule (e.g. a bath every two weeks). You'll be notified a week ahead automatically.")}} /></h3>
            {(liveUser.recurringApts||[]).length === 0
              ? <EmptyState icon={Repeat} title={i18nT("cust.label.noRecurring")} sub={i18nT("lang.name") === "English" ? "Tap \"Recurring\" on the home tab to set one up." : zhEn("在首頁點擊「定期預約」按鈕來設定","Tap \"Recurring\" on the home tab to set one up.")} />
              : (liveUser.recurringApts||[]).map(r => {
                  const svc = tenant?.services?.find(s => s.id === r.svcId);
                  const staff = state.users[r.staffId];
                  return <div key={r.id} style={{padding:"14px 0",borderBottom:"2px solid var(--c-light)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <p style={{fontWeight:800,fontSize:15,margin:"0 0 4px",color:"#4A4A68"}}>{svc?.name || zhEn("未知服務", "Unknown service")}</p>
                      <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:0}}>{zhEn((r.interval==="monthly"?"每月":"每週") + " 星期" + DAYS[parseInt(r.dayOfWeek)] + " " + r.time + " · " + (staff?.name||"—"), (r.interval==="monthly"?"Monthly":"Weekly") + " " + ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][parseInt(r.dayOfWeek)] + " " + r.time + " · " + (staff?.name||"—"))}</p>
                    </div>
                    <button onClick={() => setState(s => ({ ...s, users:{ ...s.users, [liveUser.id]:{ ...s.users[liveUser.id], recurringApts: s.users[liveUser.id].recurringApts.filter(x => x.id !== r.id) } } }))} style={{background:"#FEF2F2",border:"none",borderRadius:99,padding:8,cursor:"pointer",display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
                  </div>;
                })
            }
          </Card>

          {/* Data subject rights — HK PDPO DPP6 (access) + TW PDPA Article 10/11 */}
          <Card style={{padding:20}}>
            <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("cust.profile.dataRights")} <HelpTip content={{title:zhEn("個人資料權利 🔐", "Your data rights 🔐"),body:zhEn("根據香港《個人資料（私隱）條例》及台灣《個人資料保護法》：\n· 下載：取得你所有個人資料嘅 JSON 副本。\n· 刪除：永久移除你嘅帳戶。預約紀錄會保留（個資已抹除）以符合稅務要求。", "Under Hong Kong's Personal Data (Privacy) Ordinance and Taiwan's Personal Data Protection Act:\n· Download: get a JSON copy of all your personal data.\n· Delete: permanently remove your account. Booking records are kept (with personal data erased) to meet tax requirements.")}} /></h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>
              {zhEn("根據香港《個人資料（私隱）條例》及台灣《個人資料保護法》，您有權查閱、複製或刪除您的個人資料。", "Under Hong Kong's Personal Data (Privacy) Ordinance and Taiwan's Personal Data Protection Act, you have the right to access, copy or delete your personal data.")}
            </p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Btn variant="secondary" size="sm" onClick={async () => {
                try { await exportMyData(); toast(i18nT("cust.svcDownloaded")); }
                catch (e) { toast(e?.message || i18nT("cust.downloadFail"), "error"); }
              }}>📥 {i18nT("cust.downloadData", "下載個人資料")}</Btn>
              <Btn variant="danger" size="sm" onClick={async () => {
                const c = window.prompt(zhEn("確認永久刪除帳戶？\n\n所有個人資料、毛孩、相片會立即移除。\n預約紀錄會保留（個資已抹除）以符合稅務要求。\n\n輸入 DELETE 以確認：", "Permanently delete your account?\n\nAll personal data, pets and photos are removed immediately.\nBooking records are kept (personal data erased) to meet tax requirements.\n\nType DELETE to confirm:"));
                if (c !== "DELETE") { toast(i18nT("common.cancel")); return; }
                try {
                  await requestDataDeletion({ reason: "user self-deletion" });
                  toast(i18nT("common.savedShort"));
                  setTimeout(() => onLogout?.(), 1500);
                } catch (e) { toast(e?.message || i18nT("cust.deleteFail"), "error"); }
              }}>🗑️ {i18nT("cust.deleteAccount")}</Btn>
            </div>
            <p style={{fontSize:10,color:"#AFAFC4",margin:"10px 0 0",fontWeight:700,textAlign:"center"}}>
              <a href="#/privacy" style={{color:"#AFAFC4",textDecoration:"underline"}}>{i18nT("cust.profile.viewPolicy")}</a>
            </p>
          </Card>

          <Btn variant="danger" onClick={onLogout} style={{width:"100%",marginTop:8}}>{i18nT("cust.logout")}</Btn>
        </div>}

        {/* Powered-by footer — visible above the bottom nav. Configurable in
            superadmin → platform settings → 平台標識. Empty string = hidden. */}
        {(() => {
          if (tenantHidesBranding(tenant)) return <div style={{height:96}} />;
          const pb = state.platformConfig?.poweredByText ?? "Powered by PettoBook";
          if (!pb) return <div style={{height:96}} />;
          return <p style={{fontSize:11, color:"#AFAFC4", fontWeight:700, textAlign:"center", margin:"24px 0 96px"}}>{pb}</p>;
        })()}
      </div>

      <div style={{position:"fixed",bottom:20,left:20,right:20,background:"rgba(255,255,255,0.85)",backdropFilter:"blur(24px)",borderRadius:36,display:"flex",boxShadow:"0 16px 40px var(--c-primary-15)",border:"1px solid rgba(255,255,255,0.6)",padding:"8px 12px",zIndex:100}}>
        {navTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",gap:4,background:tab===t.id?"var(--c-lighter)":"transparent",borderRadius:24,border:"none",cursor:"pointer",color:tab===t.id?"var(--c-primary)":"#AFAFC4",transition:"all 0.3s",transform:tab===t.id?"scale(1.05)":"scale(1)"}}>
            <t.icon size={22} strokeWidth={tab===t.id?2.5:2} /><span style={{fontSize:11,fontWeight:tab===t.id?900:700}}>{t.label}</span>
          </button>
        ))}
      </div>

      {/* FIX: cancel sheet now has preset reasons + free-text "其他" option */}
      <BottomSheet open={!!cancelApt} onClose={() => { setCancelApt(null); setCancelReason(""); setCancelCustom(""); }} title={zhEn("取消預約", "Cancel booking")}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <p style={{fontSize:14,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("請說明取消原因：", "Please tell us why you're cancelling:")}</p>
          {["臨時有事","寵物不舒服","改期","其他"].map(r => (
            <div key={r} onClick={() => setCancelReason(r)} style={{padding:16,borderRadius:20,border:"2px solid "+(cancelReason===r?"#FF4D6D":"var(--c-light)"),background:cancelReason===r?"var(--c-lighter)":"#FFFDFD",cursor:"pointer",fontSize:15,fontWeight:800,color:cancelReason===r?"var(--c-dark)":"#4A4A68",transition:"all 0.2s"}}>{zhEn(r, ({"臨時有事":"Something came up","寵物不舒服":"Pet isn't well","改期":"Reschedule","其他":"Other"})[r] || r)}</div>
          ))}
          {cancelReason === "其他" && (
            <textarea
              style={{border:"2px solid var(--c-grad1)",borderRadius:20,padding:"14px 18px",fontSize:15,fontWeight:600,height:88,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68",width:"100%",transition:"border 0.3s"}}
              placeholder={zhEn("請描述取消原因...", "Please describe the reason...")}
              value={cancelCustom}
              onChange={e => setCancelCustom(e.target.value)}
              autoFocus
            />
          )}
          <Btn variant="danger" disabled={!cancelReason || (cancelReason==="其他" && !cancelCustom.trim())} onClick={cancelBooking} style={{width:"100%",marginTop:12}}>{zhEn("確認取消", "Confirm cancellation")}</Btn>
        </div>
      </BottomSheet>

      <BottomSheet open={!!rateApt} onClose={() => setRateApt(null)} title={zhEn("評價美容師", "Rate the groomer")}>
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          <div style={{display:"flex",justifyContent:"center",background:"#FFFBEB",padding:"24px",borderRadius:32,border:"2px solid #FEF3C7"}}><Stars value={rating} onChange={setRating} size={48} /></div>
          <p style={{textAlign:"center",fontSize:18,color:"#D97706",margin:0,fontWeight:900}}>{["", zhEn("差強人意 😢","Poor 😢"), zhEn("普通 😐","Okay 😐"), zhEn("不錯 🙂","Good 🙂"), zhEn("很棒 😄","Great 😄"), zhEn("超級完美！ 😍","Perfect! 😍")][rating]}</p>
          <div><p style={{fontSize:14,color:"#8A8A9E",marginBottom:8,fontWeight:800}}>{zhEn("留言鼓勵", "Leave a comment")}</p><textarea style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:24,padding:"16px",fontSize:15,fontWeight:600,height:120,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68"}} placeholder={zhEn("分享你的美好體驗...", "Share your experience...")} value={comment} onChange={e => setComment(e.target.value)} /></div>
          <Btn onClick={submitRating} style={{width:"100%"}}>{zhEn("提交評價 💌", "Submit review 💌")}</Btn>
        </div>
      </BottomSheet>

      <BottomSheet open={showSig} onClose={() => setShowSig(false)} title={i18nT("lang.name") === "English" ? "Senior pet waiver" : zhEn("高齡寵物免責聲明", "Senior pet disclaimer")}><SigPad ageThreshold={seniorAgeFor(tenant)} onSave={s => { setSig(s); setShowSig(false); toast(i18nT("tx.signed")); }} /></BottomSheet>

      {/* Phone-required gate — collect a contact number before booking. Saves
          to BOTH profiles.phone and the customer_memberships row (same two-table
          write as the account phone editor), then resumes checkout. */}
      <BottomSheet open={needPhoneOpen} onClose={() => setNeedPhoneOpen(false)} title={T("請先填寫聯絡電話 📱","Add a contact phone 📱","連絡先電話を入力 📱")}>
        <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:"0 0 12px"}}>{T("店家需要你的電話以確認預約。","The shop needs your phone number to confirm the booking.","予約確認のため電話番号が必要です。")}</p>
        <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:8,alignItems:"center",marginBottom:14}}>
          <Sel value={profilePhoneDraft.countryCode} onChange={e => setProfilePhoneDraft(f => ({...f, countryCode: e.target.value}))}>
            {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </Sel>
          <Input value={profilePhoneDraft.phone}
            inputMode="tel" type="tel"
            onChange={e => { const parsed = parseCopiedPhone(e.target.value); setProfilePhoneDraft(f => ({...f, phone: parsed.digits, ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {})})); }}
            placeholder={profilePhoneDraft.countryCode === "+852" ? "9123 4567" : profilePhoneDraft.countryCode === "+1" ? "212 555 1234" : T("電話號碼","Phone number","電話番号")} />
        </div>
        <Btn onClick={async () => {
          const cc = profilePhoneDraft.countryCode || "+852";
          const check = validatePhone(cc, profilePhoneDraft.phone);
          if (!check.valid) { toast(check.error, "error"); return; }
          const phone = normalizePhoneDigits(profilePhoneDraft.phone);
          const res = await saveCustomerPhone({ userId: liveUser.id, phone, countryCode: cc, tenantId: liveUser.tenantId, patchMembership: true });
          if (res.ok) {
            setState(s => ({
              ...s,
              users: { ...s.users, [liveUser.id]: { ...s.users[liveUser.id], phone, countryCode: cc } },
              memberships: (s.memberships || []).map(m =>
                (m.userId === liveUser.id && m.tenantId === liveUser.tenantId)
                  ? { ...m, phone }
                  : m
              ),
            }));
            setNeedPhoneOpen(false);
            toast(res.claimFiled
              ? zhEn("已通知店主核實並合併你喺呢間店嘅舊紀錄，核實後會自動連結 ✓", "The shop owner has been asked to verify and merge your existing records; they'll link automatically once verified ✓")
              : (i18nT("common.savedShort") + " ✓"),
              res.claimFiled ? "info" : "success");
            // Resume checkout once the fresh phone lands in state.
            setResumeCheckout(true);
          } else {
            toast(res.error?.message || i18nT("cust.saveFail"), "error");
          }
        }} style={{width:"100%"}}>{T("儲存並繼續預約","Save & continue","保存して続行")}</Btn>
      </BottomSheet>

      {/* Walk-in merge prompt — shown once per customer when they sign in
          (typically via Google) without a saved phone. Lets the
          link-pets-on-login Edge Function reattach pets/history/packages
          from a walk-in profile the shop may have pre-created. */}
      {/* Top-up request sheet — customer picks an amount and sends a
          prefilled WhatsApp message to the shop. Shop confirms payment
          (cash / FPS / bank transfer / etc.) in chat then adds the funds
          to the wallet via the existing 加值 button in CRM. Keeps the
          payment outside the app while still giving customers a one-tap
          intent. */}
      <BottomSheet open={topupSheetOpen} onClose={() => { setTopupSheetOpen(false); setTopupAmount(""); setTopupFallback(null); }} title={zhEn("申請儲值 💰", "Request top-up 💰")}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <p style={{fontSize:13,color:"#6A6A85",margin:0,fontWeight:700,lineHeight:1.6}}>
            {zhEn("選擇金額後會開啟 WhatsApp 通知店家。店家確認收款後會將金額加到您的錢包。", "After choosing an amount, WhatsApp opens to notify the shop. Once the shop confirms receipt of payment, the amount is added to your wallet.")}
          </p>
          <div>
            <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{zhEn("常用金額", "Common amounts")}</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:8}}>
              {[100, 200, 500, 1000].map(amt => (
                <button key={amt} onClick={() => setTopupAmount(String(amt))}
                  style={{padding:"10px 0",borderRadius:12,border:`2px solid ${String(amt)===topupAmount?"var(--c-primary)":"var(--c-border)"}`,background:String(amt)===topupAmount?"var(--c-lighter)":"#fff",color:String(amt)===topupAmount?"var(--c-primary)":"#4A4A68",fontWeight:900,fontSize:14,cursor:"pointer"}}>
                  ${amt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{zhEn("或自訂金額", "Or enter an amount")}</p>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:18,top:"50%",transform:"translateY(-50%)",fontSize:15,fontWeight:800,color:"var(--c-primary)"}}>$</span>
              <Input type="number" placeholder={zhEn("如：350", "e.g. 350")} value={topupAmount} onChange={e => setTopupAmount(e.target.value.replace(/\D/g, ""))} style={{paddingLeft:36}} />
            </div>
          </div>
          <button
            disabled={!topupAmount || Number(topupAmount) <= 0}
            onClick={() => {
              const amtCheck = validateAmount(topupAmount, { min: 1, max: 999999, label: zhEn("儲值金額", "Top-up amount") });
              if (!amtCheck.valid) { toast(amtCheck.error, "error"); return; }
              const amt = Math.floor(amtCheck.value);
              const rawPhone = (tenant?.phone || "").replace(/\D/g, "");
              if (!rawPhone) {
                toast(zhEn("店家未設定電話，請直接致電查詢", "The shop hasn't set a phone number; please call them directly"), "error");
                return;
              }
              const phone = rawPhone.length >= 10 ? rawPhone : "852" + rawPhone;
              const lines = [
                "你好，我想為錢包儲值：",
                "💰 金額：$" + amt,
                "客戶：" + (liveUser?.name || liveUser?.email || ""),
                liveUser?.phone ? ("電話：" + liveUser.phone) : null,
                "請問如何付款？",
              ].filter(Boolean).join("\n");
              const url = "https://wa.me/" + phone + "?text=" + encodeURIComponent(lines);
              const w = window.open(url, "_blank", "noopener,noreferrer");
              if (!w) {
                // Popup blocked by Safari ITP / strict-tracking — give the
                // user a tappable link rather than lying that WhatsApp
                // opened. Sheet stays open so they don't have to retype
                // the amount.
                setTopupFallback({ url, lines });
                toast(zhEn("瀏覽器封鎖咗 WhatsApp 開啟，請點擊下方連結手動發送", "Your browser blocked WhatsApp from opening; tap the link below to send manually"), "warning");
                return;
              }
              setTopupSheetOpen(false);
              setTopupAmount("");
              toast(zhEn("WhatsApp 已開啟，等待店家確認收款", "WhatsApp opened — waiting for the shop to confirm payment"), "success");
            }}
            style={{
              width:"100%",padding:"14px",borderRadius:14,border:"none",
              background:!topupAmount?"var(--c-light)":"#25D366",
              color:!topupAmount?"#AFAFC4":"#fff",
              fontSize:15,fontWeight:900,cursor:!topupAmount?"not-allowed":"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }}>
            <MessageCircle size={18} strokeWidth={2.5} /> {zhEn("經 WhatsApp 通知店家", "Notify shop via WhatsApp")}
          </button>
          {topupFallback && (
            <a
              href={topupFallback.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setTopupFallback(null); setTopupSheetOpen(false); setTopupAmount(""); }}
              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px",borderRadius:12,background:"#FEF3C7",border:"1.5px dashed #F59E0B",color:"#92400E",fontSize:13,fontWeight:900,textDecoration:"none"}}
            >
              {zhEn("⚠️ 點此手動打開 WhatsApp", "⚠️ Tap here to open WhatsApp manually")}
            </a>
          )}
          <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center"}}>
            {zhEn("付款方式（現金 / FPS / 銀行轉帳）由店家在 WhatsApp 跟進", "The shop will follow up on payment (cash / FPS / bank transfer) in WhatsApp")}
          </p>
        </div>
      </BottomSheet>

      <BottomSheet open={mergePromptOpen} onClose={handleMergeSkip} title={zhEn("連結舊資料 📞", "Link your existing records 📞")}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <p style={{fontSize:13,fontWeight:700,color:"#6A6A85",margin:0,lineHeight:1.6}}>
            {zhEn("如果店家之前已用您嘅電話為您登記過，輸入號碼後系統會自動將寵物、預約紀錄、套票同積分連結到此帳戶。", "If the shop has registered you with your phone number before, entering it will automatically link your pets, bookings, packages and points to this account.")}
          </p>
          <div>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 6px",fontWeight:700}}>{zhEn("聯絡電話", "Contact phone")}</p>
            <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:8}}>
              <Sel value={mergeCountry} onChange={e => setMergeCountry(e.target.value)}>
                {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </Sel>
              <Input type="tel" inputMode="tel" placeholder="9123 4567" value={mergePhone} onChange={e => { const parsed = parseCopiedPhone(e.target.value); setMergePhone(parsed.digits); if (parsed.countryCode) setMergeCountry(parsed.countryCode); }} />
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="secondary" disabled={mergeBusy} onClick={handleMergeSkip} style={{flex:1}}>{zhEn("稍後再說", "Maybe later")}</Btn>
            <Btn disabled={mergeBusy || !mergePhone} onClick={handleMergeSubmit} style={{flex:2}}>{mergeBusy ? zhEn("連結中…", "Linking…") : zhEn("確認連結", "Confirm link")}</Btn>
          </div>
          <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center"}}>
            {zhEn("電話只會用於聯絡通知 ‧ 不會分享給其他店家", "Your phone is only used for notifications and is not shared with other shops")}
          </p>
        </div>
      </BottomSheet>

      {/* #5 Photo lightbox — fullscreen, tap-to-dismiss. */}
      {lightboxPhoto && (
        <div onClick={() => setLightboxPhoto(null)} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,cursor:"zoom-out"}}>
          <img src={lightboxPhoto} alt="full" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:8}} />
          <button onClick={(e) => { e.stopPropagation(); setLightboxPhoto(null); }} style={{position:"absolute",top:16,right:16,background:"rgba(255,255,255,0.18)",backdropFilter:"blur(8px)",border:"none",color:"#fff",borderRadius:99,width:44,height:44,fontSize:20,fontWeight:900,cursor:"pointer"}}>×</button>
        </div>
      )}
      <BottomSheet open={!!viewReportApt} onClose={() => setViewReportApt(null)} title={zhEn("美容報告 🐾", "Grooming report 🐾")}>
        {viewReportApt && (() => {
          const a = state.appointments.find(x => x.id === viewReportApt);
          const r = a?.report; const pet = state.pets[a?.petId];
          if (!r) return null;
          return <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:24,border:"2px solid var(--c-border)"}}>
              <div style={{fontSize:48,marginBottom:8}}>{petEmoji(pet)}</div>
              <p style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{pet?.name}</p>
              <p style={{fontSize:14,color:"#8A8A9E",fontWeight:700,margin:0}}>{a.service + " · " + a.date}</p>
            </div>
            {[
              [i18nT("report.skin", "皮膚狀況"), r.skin, "🩺"],
              [i18nT("report.ear", "耳朵檢查"), r.ear, "👂"],
              [i18nT("report.teeth", "牙齒狀況"), r.teeth, "🦷"],
              [i18nT("report.mood", "情緒狀態"), r.mood, "😊"],
            ].map(([label, val, icon]) => (
              <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",background:"#FFFDFD",borderRadius:20,border:"2px solid var(--c-light)"}}>
                <span style={{fontSize:15,fontWeight:700,color:"#6A6A85"}}>{icon + " " + label}</span>
                <span style={{fontSize:15,fontWeight:900,color:val==="正常"||val==="良好"?"#10B981":"#F59E0B"}}>{REPORT_CHIP_LABEL(val) || "—"}</span>
              </div>
            ))}
            {r.notes && <div style={{background:"#FFFBEB",borderRadius:20,padding:"16px",border:"2px solid #FEF3C7"}}>
              <p style={{fontSize:13,fontWeight:800,color:"#92400E",margin:"0 0 6px"}}>{zhEn("📋 美容師備注", "📋 Groomer's notes")}</p>
              <p style={{fontSize:15,color:"#78350F",fontWeight:700,margin:0,lineHeight:1.5}}>{r.notes}</p>
            </div>}

            {/* Surcharges (if any) — itemized adjustments */}
            {Array.isArray(a.surcharges) && a.surcharges.length > 0 && <div style={{background:"#FEF2F2",borderRadius:20,padding:16,border:"2px solid #FECACA"}}>
              <p style={{fontSize:13,fontWeight:800,color:"#991B1B",margin:"0 0 8px"}}>{zhEn("💸 額外收費", "💸 Extra charges")}</p>
              {a.surcharges.map((s, i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#991B1B",marginTop:i?6:0}}>
                  <span>{s.name || s.reason || zhEn("額外項目", "Extra item")}</span>
                  <span>${s.amount || 0}</span>
                </div>
              ))}
            </div>}

            {/* Grooming photos — grouped by category (before / after / spa).
                Untagged legacy photos default to "after" so existing data still
                renders. The before/after section gets a "share" CTA that builds
                a watermarked composite the customer can post to FB / IG. */}
            {Array.isArray(r.photos) && r.photos.length > 0 && (() => {
              const before = r.photos.filter(p => p.category === "before");
              const after  = r.photos.filter(p => p.category === "after" || !p.category);
              const spa    = r.photos.filter(p => p.category === "spa");
              const hasBeforeAfter = before.length > 0 && after.length > 0;
              const shopName = tenant?.name || "";

              const shareBeforeAfter = async () => {
                if (before.length === 0 || after.length === 0) return;
                try {
                  const prefix = state.platformConfig?.bookingUrlPrefix
                    || ((state.platformConfig?.domain || "pettobook.com") + "/shop/");
                  const slug = tenant?.bookingSlug || "";
                  const bookingUrl = slug ? (prefix + slug) : "";
                  const phoneStr = tenant?.phone || "";
                  const platformDomain = state.platformConfig?.domain || "pettobook.com";
                  const blob = await buildBeforeAfterGrid({
                    photos: r.photos, shopName, petName: pet?.name || "",
                    bookingUrl, phone: phoneStr, platformDomain,
                    hideBranding: tenantHidesBranding(tenant),
                  });
                  // Share text mirrors the image footer so the caption on FB
                  // / IG carries the same booking link + phone — tappable
                  // when the platform unfurls the URL.
                  const lines = [
                    (pet?.name ? pet.name + " " : "") + "在 " + shopName + " 美容前後對比 ✨",
                    bookingUrl ? "📅 立即預約：https://" + bookingUrl.replace(/^https?:\/\//, "") : null,
                    phoneStr ? "📞 " + phoneStr : null,
                    "#petgrooming #pet美容",
                  ].filter(Boolean);
                  const res = await shareImageBlob({ blob, text: lines.join("\n"), filename: (pet?.name || "pet") + "-before-after.jpg" });
                  if (res.reason === "downloaded") toast(zhEn("已下載照片，可上傳到 IG/FB ✨", "Photo downloaded — you can upload it to IG/FB ✨"));
                } catch (err) { toast(i18nT("tx.shareFailed") + zhEn("：", ": ") + (err?.message || err), "error"); }
              };

              const Gallery = ({ items }) => (
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(100px, 1fr))",gap:8}}>
                  {items.map((p, i) => (
                    <button key={p.id || i} onClick={() => setLightboxPhoto(p.url)} style={{display:"block",aspectRatio:"1",borderRadius:12,overflow:"hidden",border:"2px solid var(--c-light)",cursor:"zoom-in",padding:0,background:"none"}}>
                      <img src={p.url} alt={"photo " + (i+1)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                    </button>
                  ))}
                </div>
              );

              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Before/After hero — side-by-side reveal + share */}
                {hasBeforeAfter && <div style={{background:"linear-gradient(135deg, var(--c-light), var(--c-grad2))",borderRadius:20,padding:14}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:"0 0 10px",textAlign:"center"}}>{zhEn("✨ 美容前 / 美容後", "✨ Before / After")}</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{position:"relative"}}>
                      <span style={{position:"absolute",top:6,left:6,background:"rgba(0,0,0,0.55)",color:"#fff",fontSize:11,fontWeight:800,padding:"3px 8px",borderRadius:99,zIndex:1}}>BEFORE</span>
                      <button onClick={() => setLightboxPhoto(before[0].url)} style={{display:"block",aspectRatio:"1",borderRadius:12,overflow:"hidden",border:"none",padding:0,cursor:"zoom-in",background:"none",width:"100%"}}>
                        <img src={before[0].url} alt="before" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                      </button>
                    </div>
                    <div style={{position:"relative"}}>
                      <span style={{position:"absolute",top:6,left:6,background:"var(--c-primary)",color:"#fff",fontSize:11,fontWeight:800,padding:"3px 8px",borderRadius:99,zIndex:1}}>AFTER</span>
                      <button onClick={() => setLightboxPhoto(after[0].url)} style={{display:"block",aspectRatio:"1",borderRadius:12,overflow:"hidden",border:"none",padding:0,cursor:"zoom-in",background:"none",width:"100%"}}>
                        <img src={after[0].url} alt="after" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                      </button>
                    </div>
                  </div>
                  <Btn onClick={shareBeforeAfter} style={{width:"100%",padding:"12px"}}>{zhEn("📤 分享美容前後到 FB / IG", "📤 Share before/after to FB / IG")}</Btn>
                  <p style={{fontSize:10,color:"#6A6A85",margin:"6px 0 0",fontWeight:700,textAlign:"center"}}>{zhEn("分享圖會自動加上店家名稱浮水印", "The shared image gets the shop name watermark automatically")}</p>
                </div>}

                {/* Individual categorized galleries */}
                {!hasBeforeAfter && before.length > 0 && <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("📷 美容前 (", "📷 Before (")}{before.length})</p>
                  <Gallery items={before} />
                </div>}
                {!hasBeforeAfter && after.length > 0 && <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("✨ 美容後 (", "✨ After (")}{after.length})</p>
                  <Gallery items={after} />
                </div>}
                {/* When we have a before/after pair, still show the extras */}
                {hasBeforeAfter && (before.length > 1 || after.length > 1) && <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("更多照片", "More photos")}</p>
                  <Gallery items={[...before.slice(1), ...after.slice(1)]} />
                </div>}
                {spa.length > 0 && <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("💧 水療 (", "💧 Hydrotherapy (")}{spa.length})</p>
                  <Gallery items={spa} />
                </div>}

                {pet && <button onClick={() => {
                  const newPhotos = [...(pet.photos || []), ...r.photos.map(p => ({...p, fromAppt: a.id}))];
                  setState(s => ({...s, pets:{...s.pets, [pet.id]:{...s.pets[pet.id], photos: newPhotos}}}));
                  toast(zhEn("照片已加入毛孩相冊 ✨", "Photos added to the pet album ✨"));
                }} style={{background:"var(--c-lighter)",border:"none",color:"var(--c-primary)",fontSize:12,fontWeight:800,cursor:"pointer",padding:"10px",borderRadius:12,width:"100%"}}>
                  {zhEn("📂 加入毛孩相冊", "📂 Add to pet album")}
                </button>}
                <p style={{fontSize:10,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center"}}>{zhEn("點擊圖片可放大查看", "Tap a photo to enlarge")}</p>
              </div>;
            })()}
          </div>;
        })()}
      </BottomSheet>

      <BottomSheet open={showPay} onClose={() => setShowPay(false)} title={zhEn("付訂金", "Pay deposit")}>
        {(() => {
          const methods = (tenant?.depositMethods || []).filter(methodForCustomer);
          const activeMethod = methods.find(m => m.id === payMethodId) || methods[0];
          const shopPhone = (tenant?.phone || "").replace(/\D/g, "");
          const shopCC = (tenant?.countryCode || "+852").replace(/[^\d+]/g, "").replace(/^\+/, "");
          const waNumber = shopPhone ? shopCC + shopPhone : "";
          const svc = services.find(s => s.id === bk.svcId);
          const receiptMsg = "您好，我已轉帳訂金 $" + toPay + "\n收款方式：" + (activeMethod?.name || "") + "\n預約：" + (svc?.name || "美容服務") + "\n日期：" + (bk.date || "") + " " + (bk.time || "") + "\n毛孩：" + (selPet?.name || "") + "\n請查收附上收據截圖，謝謝！";
          const waLink = waNumber ? "https://wa.me/" + waNumber + "?text=" + encodeURIComponent(receiptMsg) : null;
          return (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:"#FFFBEB",padding:14,borderRadius:16,border:"2px solid #FEF3C7"}}>
                <p style={{fontSize:13,color:"#92400E",margin:"0 0 4px",fontWeight:800}}>💰 {i18nT("cust.book.actualDeposit")} ${toPay}</p>
                <p style={{fontSize:11,color:"#92400E",margin:0,fontWeight:700,opacity:0.85,lineHeight:1.5}}>{zhEn("用以下任一方式付款後，撳「✅ 已轉帳並提交預約」。預約成功後可再寄收據截圖給店家。", "Pay with any method below, then tap \"✅ Transferred & submit booking\". After booking, you can send the receipt screenshot to the shop.")}</p>
              </div>
              {methods.length === 0 ? (
                <div style={{padding:20,textAlign:"center",background:"#FEF2F2",borderRadius:14,fontSize:13,fontWeight:700,color:"#991B1B"}}>
                  {zhEn("店家未設定收款方式。請直接到店付款或聯絡店家。", "The shop hasn't set up payment methods. Please pay at the shop or contact them.")}
                </div>
              ) : (
                <>
                  <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:6,scrollbarWidth:"none"}}>
                    {methods.map(m => {
                      const on = m.id === activeMethod?.id;
                      return (
                        <button key={m.id} onClick={() => setPayMethodId(m.id)} style={{flexShrink:0,padding:"8px 14px",borderRadius:99,border:"1.5px solid "+(on?"#D97706":"#FDE68A"),background:on?"#D97706":"#fff",color:on?"#fff":"#92400E",fontSize:13,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>{m.name}</button>
                      );
                    })}
                  </div>
                  {activeMethod && (
                    <div style={{background:"#fff",borderRadius:12,padding:14,border:"1.5px solid #FDE68A",display:"flex",flexDirection:"column",alignItems:"center",gap:10,textAlign:"center"}}>
                      {activeMethod.qrUrl ? (
                        <a href={activeMethod.qrUrl} target="_blank" rel="noreferrer" style={{display:"block"}}>
                          <img src={activeMethod.qrUrl} alt={activeMethod.name} style={{width:200,height:200,objectFit:"contain",borderRadius:10,border:"1px solid #FEF3C7",background:"#fff"}} />
                        </a>
                      ) : (
                        <div style={{width:200,height:200,borderRadius:10,border:"1.5px dashed #FDE68A",display:"flex",alignItems:"center",justifyContent:"center",color:"#D97706",fontSize:13,fontWeight:700,textAlign:"center",padding:10}}>{zhEn("店家未上載 QR", "The shop hasn't uploaded a QR")}<br/>{activeMethod.accountNumber ? zhEn("請轉帳到：", "Transfer to: ") + activeMethod.accountNumber : ""}</div>
                      )}
                      <p style={{fontSize:15,fontWeight:900,color:"#92400E",margin:0}}>{activeMethod.name}</p>
                      {activeMethod.instructions && (
                        <p style={{fontSize:12,color:"#78350F",margin:0,fontWeight:600,whiteSpace:"pre-wrap",lineHeight:1.6}}>{activeMethod.instructions}</p>
                      )}
                    </div>
                  )}
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,borderRadius:14,background:"#25D366",color:"#fff",textDecoration:"none",fontWeight:900,fontSize:14,boxShadow:"0 6px 16px rgba(37,211,102,0.35)"}}>
                      <MessageCircle size={18} strokeWidth={2.8} /> {zhEn("WhatsApp 發送收據畀店家", "Send receipt to shop on WhatsApp")}
                    </a>
                  )}
                </>
              )}
              <Btn disabled={bookingBusy || bkBusyStatus !== "ready"} onClick={() => { if (bookingBusy || bkBusyStatus !== "ready") return; setShowPay(false); submitBooking(sig); }} style={{width:"100%",padding:16,marginTop:4}}>
                {bookingBusy ? <><Spinner /> {zhEn("提交中…", "Submitting…")}</> : i18nT("cust.book.markPaid")}
              </Btn>
              <button onClick={() => setShowPay(false)} style={{padding:"10px 14px",background:"none",border:"none",color:"#8A8A9E",fontSize:12,fontWeight:800,cursor:"pointer"}}>{i18nT("cust.book.payLater")}</button>
            </div>
          );
        })()}
      </BottomSheet>

      <BottomSheet open={!!petForm} onClose={() => setPetForm(null)} title={petForm?.id ? i18nT("cust.pets.editBtn") + " " + petForm.name : i18nT("cust.pets.addBtn")}>
        {petForm && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Input label={i18nT("pet.name") + " *"} value={petForm.name} onChange={e => setPetForm(f => ({ ...f, name:e.target.value }))} placeholder={zhEn("例: 奶茶", "e.g. Milk Tea")} />
            <Input label={i18nT("pet.weightKg")} type="number" value={petForm.weight} onChange={e => setPetForm(f => ({ ...f, weight:e.target.value }))} />
            <Input label={i18nT("pet.birthday")} type="date" max={dateNow()} min={petBirthdayMin()} value={petForm.birthday} onChange={e => setPetForm(f => ({ ...f, birthday:e.target.value }))} />
            <Sel label={i18nT("pet.type")} value={petForm.type} onChange={e => setPetForm(f => ({ ...f, type:e.target.value }))}><option value="dog">{i18nT("pet.dog")}</option><option value="cat">{i18nT("pet.cat")}</option></Sel>
          </div>
          {petWeightWarn(petForm.weight, petForm.type) && <p style={{fontSize:12, color:"#B45309", margin:"-8px 0 0", fontWeight:700}}>{petWeightWarn(petForm.weight, petForm.type)}</p>}
          <BreedCombobox label={zhEn("品種 *", "Breed *")} type={petForm.type || "dog"} lang={tenant?.lang || "zh-HK"}
            value={petForm.breedId} valueLabel={petForm.breed}
            onChange={({ breedId, breed, coatHint }) => setPetForm(f => ({ ...f, breedId, breed, coatLength: (coatHint === "long" || coatHint === "short") ? coatHint : (f.coatLength || "short") }))} />
          {/* Coat length is derived from the breed (coatHint) — no customer
              override, so it can't be flipped to "short" for a cheaper price. */}
          <Input label={i18nT("cust.pets.allergyInfo")} placeholder={i18nT("cust.pets.allergyPh")} value={petForm.allergies} onChange={e => setPetForm(f => ({ ...f, allergies:e.target.value }))} />
          <Input label={i18nT("cust.pets.vaccineDate")} type="date" value={petForm.vaccineDate} onChange={e => setPetForm(f => ({ ...f, vaccineDate:e.target.value }))} />
          <Input label={i18nT("cust.pets.vetNote")} placeholder={i18nT("cust.pets.vetNotePh")} value={petForm.vetNote} onChange={e => setPetForm(f => ({ ...f, vetNote:e.target.value }))} />
          {/* Single avatar photo only — the gallery use-case was confusing
              and most owners only wanted a profile picture. Replace, not
              append: each upload overwrites the prior one. */}
          <PhotoUpload label={i18nT("cust.pets.profilePhoto")} photos={(petForm.photos || []).slice(0, 1)} onAdd={photo => setPetForm(f => ({ ...f, photos: [photo] }))} onRemove={() => setPetForm(f => ({ ...f, photos: [] }))} maxPhotos={1} tenantId={petForm?.tenantId || user?.tenantId} scope={petForm?.id ? "pet-" + petForm.id : "pet-new"} />
          <Btn onClick={addPet} style={{width:"100%",marginTop:8,padding:"16px"}}>{petForm?.id ? i18nT("cust.pets.saveBtn") : i18nT("cust.pets.newBtn")}</Btn>
        </div>}
      </BottomSheet>

      <BottomSheet open={showRecurring} onClose={() => setShowRecurring(false)} title={zhEn("設定定期預約", "Set up recurring booking")}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <Sel label={zhEn("毛孩", "Pet")} value={recForm.petId} onChange={e => setRecForm(f => ({ ...f, petId:e.target.value }))}><option value="">{zhEn("請選擇", "Select")}</option>{myPets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Sel>
          <Sel label={zhEn("服務", "Service")} value={recForm.svcId} onChange={e => setRecForm(f => ({ ...f, svcId:e.target.value }))}><option value="">{zhEn("請選擇", "Select")}</option>{decorateServiceList(services).map(s => <option key={s.id} value={s.id}>{s.displayName}</option>)}</Sel>
          <Sel label={zhEn("美容師", "Groomer")} value={recForm.staffId} onChange={e => setRecForm(f => ({ ...f, staffId:e.target.value }))}><option value="">{zhEn("請選擇", "Select")}</option>{staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Sel>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Sel label={zhEn("週期", "Frequency")} value={recForm.interval} onChange={e => setRecForm(f => ({ ...f, interval:e.target.value }))}><option value="monthly">{zhEn("每月", "Monthly")}</option><option value="weekly">{zhEn("每週", "Weekly")}</option></Sel>
            <Sel label={zhEn("星期", "Weekday")} value={recForm.dayOfWeek} onChange={e => setRecForm(f => ({ ...f, dayOfWeek:e.target.value }))}>{DAYS.map((d, i) => <option key={i} value={String(i)}>{zhEn("星期", "Weekday") + d}</option>)}</Sel>
          </div>
          <Sel label={zhEn("時間", "Time")} value={recForm.time} onChange={e => setRecForm(f => ({ ...f, time:e.target.value }))}>{["09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map(t => <option key={t} value={t}>{t}</option>)}</Sel>
          <Btn disabled={!recForm.petId||!recForm.svcId||!recForm.staffId} onClick={saveRecurring} style={{width:"100%",marginTop:8,padding:"16px"}}>{zhEn("✨ 確認設定", "✨ Confirm")}</Btn>
        </div>
      </BottomSheet>

      <BottomSheet open={!!showPetDetail} onClose={() => setShowPetDetail(null)} title={zhEn("毛孩詳情", "Pet details")}>
        {showPetDetail && (() => {
          const p = state.pets[showPetDetail]; if (!p) return null;
          const aptH = myApts.filter(a => a.petId === p.id && a.status === "done");
          const lastGroom = aptH.length > 0 ? Math.round((new Date() - new Date(aptH.sort((a,b) => b.date.localeCompare(a.date))[0].date)) / 86400000) : null;
          return <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:32,border:"2px solid var(--c-border)",position:"relative"}}>
              {p.photos?.length ? <div style={{width:96,height:96,borderRadius:32,overflow:"hidden",margin:"0 auto 12px",border:"4px solid var(--c-border)"}}><img src={p.photos[0].url} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}} /></div> : <div style={{fontSize:64,marginBottom:12}}>{petEmoji(p)}</div>}
              <p style={{fontSize:24,fontWeight:900,margin:"0 0 6px",color:"#4A4A68"}}>{p.name}</p>
              <p style={{fontSize:15,color:"#8A8A9E",margin:0,fontWeight:700}}>{p.breed + (petAge(p.birthday) != null ? " · " + petAge(p.birthday) + zhEn("歲", " yrs") : "") + " · " + p.weight + "kg · " + (p.coatLength==="long"?zhEn("長毛", "Long coat"):zhEn("短毛", "Short coat"))}</p>
              {lastGroom !== null && <p style={{fontSize:13,color:lastGroom>60?"#EF4444":"#10B981",margin:"6px 0 0",fontWeight:800}}>{zhEn("上次美容 " + lastGroom + " 天前" + (lastGroom>60?" ⚠️":""), "Last groom " + lastGroom + " days ago" + (lastGroom>60?" ⚠️":""))}</p>}
              {p.groomingStyle?.preferred && <p style={{fontSize:13,color:"var(--c-primary)",margin:"4px 0 0",fontWeight:800}}>{zhEn("✂️ 偏好造型: ", "✂️ Preferred style: ") + p.groomingStyle.preferred}</p>}
            </div>
            {/* Behavior warnings */}
            {p.behavior && (p.behavior.bites || p.behavior.muzzleNeeded || p.behavior.anxious || p.behavior.dryerFear || p.behavior.aggressiveWithDogs) && (
              <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:20,padding:14}}>
                <p style={{fontSize:13,fontWeight:800,color:"#991B1B",margin:"0 0 8px"}}>{zhEn("⚠️ 行為注意事項", "⚠️ Behaviour notes")}</p>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {p.behavior.bites && <span style={{fontSize:12,padding:"4px 10px",borderRadius:99,background:"#FEE2E2",color:"#DC2626",fontWeight:800}}>{zhEn("🦷 會咬人", "🦷 Bites")}</span>}
                  {p.behavior.muzzleNeeded && <span style={{fontSize:12,padding:"4px 10px",borderRadius:99,background:"#FEE2E2",color:"#DC2626",fontWeight:800}}>{zhEn("🐕 需要口罩", "🐕 Needs a muzzle")}</span>}
                  {p.behavior.anxious && <span style={{fontSize:12,padding:"4px 10px",borderRadius:99,background:"#FFFBEB",color:"#92400E",fontWeight:800}}>{zhEn("😰 容易焦慮", "😰 Easily anxious")}</span>}
                  {p.behavior.dryerFear && <span style={{fontSize:12,padding:"4px 10px",borderRadius:99,background:"#FFFBEB",color:"#92400E",fontWeight:800}}>{zhEn("💨 怕吹風機", "💨 Afraid of the dryer")}</span>}
                  {p.behavior.aggressiveWithDogs && <span style={{fontSize:12,padding:"4px 10px",borderRadius:99,background:"#FFFBEB",color:"#92400E",fontWeight:800}}>{zhEn("🐕 對其他狗有攻擊性", "🐕 Aggressive with other dogs")}</span>}
                </div>
                {p.behavior.notes && <p style={{fontSize:13,color:"#78350F",margin:"8px 0 0",fontWeight:700}}>{p.behavior.notes}</p>}
              </div>
            )}
            {/* Health info */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[[zhEn("過敏", "Allergies"),p.allergies||zhEn("無", "None"),"🩺"],[zhEn("疫苗日期", "Vaccination date"),p.vaccineDate||zhEn("未記錄", "Not recorded"),"💉"]].map(([l,v,ic])=>(
                <div key={l} style={{background:"#FFFDFD",borderRadius:16,padding:"12px 14px",border:"2px solid var(--c-light)"}}>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:800}}>{ic + " " + zhEn(l, l === "過敏" ? "Allergies" : "Vaccine date")}</p>
                  <p style={{fontSize:14,fontWeight:800,color:v==="無"||v==="未記錄"?"#AFAFC4":"#4A4A68",margin:0}}>{v === "無" ? zhEn("無","None") : v === "未記錄" ? zhEn("未記錄","Not recorded") : v}</p>
                </div>
              ))}
            </div>
            {p.vetNote && <div style={{background:"#FFFBEB",borderRadius:16,padding:"12px 14px",border:"2px solid #FEF3C7"}}><p style={{fontSize:12,fontWeight:800,color:"#92400E",margin:"0 0 4px"}}>{zhEn("📋 獸醫備注", "📋 Vet notes")}</p><p style={{fontSize:14,color:"#78350F",fontWeight:700,margin:0}}>{p.vetNote}</p></div>}
            {/* Photo album — pet record photos + every grooming report photo
                for this pet, newest first. Each photo opens full-size when
                tapped. */}
            {(() => {
              const albumPhotos = [];
              (p.photos || []).forEach(ph => albumPhotos.push({ ...ph, src: "pet", sortDate: ph.date || "" }));
              aptH.forEach(a => {
                if (Array.isArray(a.report?.photos)) a.report.photos.forEach(ph => albumPhotos.push({ ...ph, src: "appt", aptDate: a.date, aptService: a.service, sortDate: ph.date || a.date || "" }));
              });
              albumPhotos.sort((a, b) => (b.sortDate || "").localeCompare(a.sortDate || ""));
              if (albumPhotos.length === 0) return null;
              return (
                <div>
                  <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("📸 相冊 (", "📸 Album (")}{albumPhotos.length})</p>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(90px, 1fr))",gap:8}}>
                    {albumPhotos.map((ph, i) => (
                      <button type="button" key={(ph.id || "") + "-" + i} onClick={() => setAlbumLb(i)} aria-label={T("放大相片","Enlarge photo")} style={{padding:0,background:"none",font:"inherit",position:"relative",display:"block",width:"100%",aspectRatio:"1",borderRadius:14,overflow:"hidden",border:"2px solid var(--c-light)",cursor:"zoom-in"}} title={ph.aptDate ? (ph.aptService || zhEn("美容", "Grooming")) + " · " + ph.aptDate : ""}>
                        <img src={ph.url} alt={ph.aptService || "pet"} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                        {ph.category && <span style={{position:"absolute",bottom:4,left:4,fontSize:9,fontWeight:800,background:"rgba(0,0,0,0.55)",color:"#fff",padding:"2px 6px",borderRadius:99}}>{ph.category === "before" ? "BEFORE" : ph.category === "after" ? "AFTER" : "SPA"}</span>}
                        {ph.src === "appt" && ph.aptDate && <span style={{position:"absolute",top:4,right:4,fontSize:9,fontWeight:800,background:"rgba(255,255,255,0.85)",color:"#4A4A68",padding:"2px 6px",borderRadius:99}}>{ph.aptDate.slice(5)}</span>}
                      </button>
                    ))}
                  </div>
                  <p style={{fontSize:10,color:"#AFAFC4",margin:"6px 0 0",fontWeight:700}}>{zhEn("點圖可放大 · 包含毛孩相冊及每次美容報告的照片", "Tap a photo to enlarge · includes the pet album and photos from every grooming report")}</p>
                  <PhotoLightbox
                    photos={albumPhotos.map(ph => ({ url: ph.url, caption: [ph.category === "before" ? "BEFORE" : ph.category === "after" ? "AFTER" : "", ph.aptService, ph.aptDate].filter(Boolean).join(" · ") }))}
                    index={albumLb != null && albumLb < albumPhotos.length ? albumLb : null}
                    onIndex={setAlbumLb} onClose={() => setAlbumLb(null)} />
                </div>
              );
            })()}
            {/* Edit + History */}
            <Btn variant="secondary" onClick={() => { setPetForm({ ...p, weight:String(p.weight) }); setShowPetDetail(null); }} style={{width:"100%"}}><Edit size={16} /> {zhEn("編輯毛孩資料", "Edit pet details")}</Btn>
            <h4 style={{fontWeight:900,margin:"4px 0 8px",color:"#4A4A68",fontSize:18}}>{zhEn("美容歷史 (" + aptH.length + "次)", "Grooming history (" + aptH.length + ")")}</h4>
            {aptH.length === 0 && <p style={{fontSize:14,color:"#AFAFC4",textAlign:"center",padding:"20px"}}>{zhEn("暫無美容記錄", "No grooming records yet")}</p>}
            {aptH.slice(0, 3).map(a => {
              const reportSummary = [
                [zhEn("皮膚","Skin"), a.report?.skin],
                [zhEn("耳","Ear"), a.report?.ear],
                [zhEn("牙","Teeth"), a.report?.teeth],
              ].filter(([, value]) => value != null && String(value).trim() !== "" && value !== "undefined" && value !== "null");
              return (
                <div key={a.id} style={{padding:16,background:"#FFFDFD",borderRadius:24,border:"2px solid var(--c-light)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}><span style={{fontSize:16,fontWeight:800,color:"#4A4A68"}}>{a.service}</span><span style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{a.date}</span></div>
                  {reportSummary.length > 0 && <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:600}}>{reportSummary.map(([label, value]) => label + ": " + value).join(" | ")}</p>}
                  {a.rating && <div style={{marginTop:8,background:"#FFFBEB",display:"inline-block",padding:"4px 10px",borderRadius:99}}><Stars value={a.rating} size={14} /></div>}
                </div>
              );
            })}
          </div>;
        })()}
      </BottomSheet>

      {/* 詢價 / Post-a-request sheet. Opens automatically when the
          visitor came in from /directory's "📢 出價搵店" button (flag
          read by the boot useEffect above). Pets list comes from the
          customer's existing per-shop pet roster; they can also
          manually fill pet info if the pet isn't registered yet. */}
      <PostRequestSheet
        open={postRfqOpen}
        onClose={() => setPostRfqOpen(false)}
        onPosted={() => { toast(zhEn("✓ 已發出 — 喺『我的詢價』可以睇到回覆", "✓ Sent — you'll see replies under \"My quote requests\""), "success"); setMyRfqOpen(true); }}
        profile={liveUser}
        pets={myPets}
      />
      <MyRequestsSheet
        open={myRfqOpen}
        onClose={() => setMyRfqOpen(false)}
        onAccepted={() => { setMyRfqOpen(false); setTab("appointments"); }}
      />
      {/* Customer-side shop news + vouchers browse sheets. Mounted at
          CustomerApp root so they overlay any home tab. */}
      <BottomSheet open={!!newsDetail} onClose={() => setNewsDetail(null)} title={zhEn("最新消息", "Latest news")} z={310}>
        {newsDetail && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {newsDetail.image_url && <img src={newsDetail.image_url} alt="" style={{width:"100%",maxHeight:220,objectFit:"cover",borderRadius:16}} />}
            <div>
              {newsDetail.pinned && <span style={{background:"var(--c-primary)",color:"#fff",fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,marginRight:6}}>{zhEn("📌 置頂", "📌 Pinned")}</span>}
              <span style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>{String(newsDetail.published_at||"").slice(0,10)}</span>
            </div>
            <h3 style={{fontSize:20,fontWeight:900,color:"#4A4A68",margin:0,lineHeight:1.3}}>{newsDetail.title}</h3>
            {newsDetail.body && (
              <p style={{fontSize:14,color:"#4A4A68",fontWeight:600,margin:0,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{newsDetail.body}</p>
            )}
            {newsDetail.cta_label && (
              <Btn onClick={() => {
                const r = newsDetail.cta_route || "";
                setNewsDetail(null);
                if (r === "book") setTab("book");
                else if (r === "wallet") setTab("rewards");
                else if (r.startsWith("url:")) window.open(r.slice(4), "_blank");
              }} style={{width:"100%",padding:14,marginTop:4}}>{newsDetail.cta_label} →</Btn>
            )}
            {shopNewsItems.length > 1 && (
              <button onClick={() => { setNewsDetail(null); setNewsListOpen(true); }}
                style={{marginTop:6,padding:12,borderRadius:12,border:"2px dashed var(--c-border)",background:"transparent",color:"var(--c-primary)",fontWeight:900,fontSize:13,cursor:"pointer"}}>
                {zhEn("查看全部", "View all")} {shopNewsItems.length} {zhEn("條消息 →", " posts →")}
              </button>
            )}
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={newsListOpen} onClose={() => setNewsListOpen(false)} title={zhEn("📣 最新消息 (", "📣 Latest news (") + shopNewsItems.length + ")"} z={305}>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {shopNewsItems.length === 0 && <p style={{fontSize:13,color:"#8A8A9E",padding:20,textAlign:"center",fontWeight:700}}>{zhEn("店舖仲未發佈消息", "The shop hasn't posted any news yet")}</p>}
          {shopNewsItems.map(n => (
            <button key={n.id} onClick={() => { setNewsListOpen(false); setTimeout(() => setNewsDetail(n), 250); }}
              style={{padding:14,background:"#fff",borderRadius:14,border:"2px solid var(--c-light)",display:"flex",gap:12,alignItems:"flex-start",cursor:"pointer",textAlign:"left"}}>
              {n.image_url && <img src={n.image_url} alt="" style={{width:56,height:56,borderRadius:10,objectFit:"cover",flexShrink:0}} />}
              <div style={{flex:1,minWidth:0}}>
                {n.pinned && <span style={{background:"var(--c-primary)",color:"#fff",fontSize:9,fontWeight:900,padding:"1px 6px",borderRadius:99,marginRight:4}}>{zhEn("置頂", "Pinned")}</span>}
                <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0,lineHeight:1.3}}>{n.title}</p>
                {n.body && <p style={{fontSize:11,color:"#8A8A9E",fontWeight:600,margin:"3px 0 0",lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{n.body}</p>}
                <p style={{fontSize:10,color:"#AFAFC4",fontWeight:700,margin:"4px 0 0"}}>{String(n.published_at||"").slice(0,10)}</p>
              </div>
            </button>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={myVouchersOpen} onClose={() => setMyVouchersOpen(false)} title={zhEn("🎫 我嘅優惠券", "🎫 My coupons")} z={305}>
        {(() => {
          const myMem = (state.memberships || []).find(m => m.userId === user.id && m.tenantId === user.tenantId);
          const rawVs = (myMem?.vouchers) || (state.users[user.id]?.vouchers) || [];
          const active = rawVs.filter(v => !v.used).sort((a,b) => String(b.createdAt||b.redeemedAt||"").localeCompare(String(a.createdAt||a.redeemedAt||"")));
          const used = rawVs.filter(v => v.used).sort((a,b) => String(b.usedAt||"").localeCompare(String(a.usedAt||"")));
          const typeLabel = t => t==="spa"||t==="free_spa"?"SPA":t==="addon"||t==="free_addon"?zhEn("加購", "Add-ons"):t==="discount"||t==="fixed"?zhEn("現金折扣", "Cash discount"):zhEn("優惠券", "Voucher");
          const row = (v, isUsed) => (
            <div key={v.id} style={{padding:12,borderRadius:12,background:isUsed?"#F3F3F8":"linear-gradient(135deg,#FFF7ED,#FFEDD5)",border:"2px "+(isUsed?"solid":"dashed")+" "+(isUsed?"#E5E7EB":"#FED7AA"),opacity:isUsed?0.7:1,display:"flex",gap:10,alignItems:"flex-start"}}>
              <div style={{width:5,alignSelf:"stretch",background:isUsed?"#CBD5E1":"linear-gradient(135deg, var(--c-grad1), var(--c-primary))",borderRadius:99,flexShrink:0}} />
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:13,fontWeight:900,color:isUsed?"#4A4A68":"#9A3412",margin:0,lineHeight:1.3}}>{v.label || zhEn("優惠券", "Voucher")}
                  {isUsed && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:"#9CA3AF",borderRadius:6,padding:"2px 6px",marginLeft:6}}>{zhEn("已使用", "Used")}</span>}
                </p>
                <p style={{fontSize:11,color:isUsed?"#8A8A9E":"#9A3412",margin:"3px 0 0",fontWeight:700,opacity:0.85}}>
                  {typeLabel(v.type)}{v.amount>0?` · $${Math.round(Number(v.amount)||0)}`:""}{v.source==="points"?zhEn(" · 積分兌換"," · Points redeemed"):v.source==="owner"?zhEn(" · 店家贈送"," · From the shop"):""}
                  {" · "}{String(v.createdAt||v.redeemedAt||"").slice(0,10)}
                </p>
              </div>
            </div>
          );
          return (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:0}}>{zhEn("可用", "Available")} <b style={{color:"var(--c-primary)"}}>{active.length}</b> {zhEn("· 已用", "· Used")} {used.length}</p>
              {active.length === 0 && used.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",padding:20,textAlign:"center",fontWeight:700,background:"var(--c-lighter)",borderRadius:12,margin:0}}>{zhEn("暫無優惠券", "No coupons yet")}</p>}
              {active.map(v => row(v, false))}
              {used.length > 0 && <p style={{fontSize:11,color:"#8A8A9E",fontWeight:900,margin:"8px 0 0"}}>{zhEn("—— 已使用 (", "—— Used (")}{used.length}) ——</p>}
              {used.map(v => row(v, true))}
            </div>
          );
        })()}
      </BottomSheet>
    </div>
  );
};

// ── Phase 4.1 — 儲存紀錄 (sync log) panel ───────────────────────────────
// Owner-facing card in 設定 → 系統 that shows the last 50 sync events
// recorded by useAppState's persist(). Lets the owner verify a save
// actually completed when they suspect "data disappeared."
// 收款 sheet — the owner enters an amount per configured payment
// method + optional wallet deduction. Sum must equal remaining balance.
// Uses recordPaymentAtomic (idempotent by entry id) + debitCustomerWallet
// so a double-tap can't double-charge and a network flake mid-write
// won't leave inconsistent state.
const CollectPaymentSheet = ({ apt, customer, tenant, user, onClose, onCollected }) => {
  // ── What is actually owed ──────────────────────────────────────────────
  // This used to be `basePrice + addonPrice + surcharges - deposit`, which
  // silently DROPPED the discount and the voucher, and ignored anything
  // already collected. On a $640 booking with 15% off it asked for $640
  // instead of $544 — and because record_payment_atomic REJECTS an entry
  // that would push collected above the booking total, those payments were
  // refused outright (a booking stuck on 未付 even after taking cash).
  // The formula below now mirrors record_payment_atomic exactly:
  //   total = subtotal − discount − voucher
  // …then subtracts the deposit and whatever the payment_log already holds.
  const surcharges = Array.isArray(apt?.surcharges) ? apt.surcharges : [];
  const surTotal = surcharges.reduce((s, x) => s + (Number(x?.amount) || 0), 0);
  const basePrice = Number(apt?.basePrice) || 0;
  const addonPrice = Number(apt?.addonPrice) || 0;
  const subtotal = basePrice + addonPrice + surTotal;
  const discountAmt = aptDiscount(apt);
  const voucherAmt = Number(apt?.voucherDiscount) || 0;
  const grandTotal = Math.max(0, subtotal - discountAmt - voucherAmt);
  const depositAmt = Number(apt?.deposit) || 0;
  const collectedAlready = Array.isArray(apt?.paymentLog)
    ? apt.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
    : 0;
  const remain = Math.max(0, grandTotal - depositAmt - collectedAlready);
  const walletBal = Number(customer?.wallet) || 0;
  // POS default: tap a method → it pays the WHOLE outstanding amount, no
  // typing. Split mode (per-method amount boxes) is opt-in.
  const [splitMode, setSplitMode] = useState(false);
  // Row order: 💵 現金 always first (configured or fallback), then 客戶錢包
  // when there's a balance, then the rest of the tenant's INTERNAL CHECKOUT
  // methods in the order the owner arranged them (設定 → 收款 → 顯示次序).
  // Customer-online-only methods (bank-transfer QR etc.) never appear here.
  const configuredMethods = checkoutMethodsOf(tenant);
  const hasCashConfigured = configuredMethods.some(isCashMethod);
  const methodRows = useMemo(() => {
    const rows = [];
    if (!hasCashConfigured) rows.push({ id: "__cash__", name: "💵 現金", presetKey: "cash" });
    const asRow = (m) => ({ id: m.id, name: m.name, presetKey: m.presetKey });
    // checkoutMethodsOf already sorted 現金 to the front, so take it first…
    for (const m of configuredMethods) if (isCashMethod(m)) rows.push(asRow(m));
    if (walletBal > 0) rows.push({ id: "__wallet__", name: "💳 客戶錢包 (餘 $" + walletBal + ")", isWallet: true, max: Math.min(walletBal, remain) });
    // …then the remainder in the owner's arranged order.
    for (const m of configuredMethods) if (!isCashMethod(m)) rows.push(asRow(m));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletBal, remain, configuredMethods.map(m => m.id).join(","), hasCashConfigured]);
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(false);
  // Stable payment-entry ids per row for THIS sheet session, so a retry after a
  // failed submit reuses the same id and record_payment_atomic treats it as a
  // duplicate instead of recording the payment twice.
  const entryIdsRef = useRef({});
  // Split-mode prefill only. In simple mode nothing is pre-entered — the
  // owner picks a method and that one tap settles the full amount.
  useEffect(() => {
    if (!splitMode) { setAmounts({}); return; }
    if (walletBal > 0 && walletBal >= remain) setAmounts({ __wallet__: String(remain) });
    else if (walletBal > 0) setAmounts({ __wallet__: String(walletBal) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apt?.id, splitMode]);

  const total = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);
  const diff = remain - total;

  const setAmt = (id, v) => setAmounts(a => ({ ...a, [id]: v }));
  const fillRow = (id, max) => {
    // Set this row to exactly what's needed to zero the remaining diff
    // (capped at the row's max if it's the wallet row).
    const need = remain - (total - (Number(amounts[id]) || 0));
    const capped = max != null ? Math.min(need, max) : need;
    setAmt(id, String(Math.max(0, capped)));
  };

  // `override` lets a one-tap method button submit without touching state
  // (a setAmounts + submit pair would race — the handler would read the
  // pre-update amounts). Split mode passes nothing and uses the inputs.
  const submit = async (override) => {
    if (!apt) return;
    const amtMap = override || amounts;
    const entered = Object.values(amtMap).reduce((s, v) => s + (Number(v) || 0), 0);
    const gap = remain - entered;
    if (Math.abs(gap) > 0.01) {
      toast(zhEn("金額未夾到 — 目前差 $" + Math.abs(gap), "Amounts don't add up — $" + Math.abs(gap) + " off"), "warning");
      return;
    }
    // ⚠️ AWAIT the writes, and RECORD BEFORE MOVING MONEY.
    //
    // This used to be optimistic + fire-and-forget: it called onCollected()
    // (which marks the appointment paid LOCALLY and closes the sheet), then
    // fired the RPCs unawaited with every failure swallowed into a
    // console.warn. Two real failures came from that on 2026-08-09:
    //   • Cash: the payment entry never reached the DB, but the UI said 已收款
    //     — then the next refetch replaced the optimistic state with the DB
    //     truth and the booking flipped back to 未付. Silent.
    //   • Wallet: the debit ran FIRST and succeeded ($450 left the customer's
    //     wallet, ledger proves it) while the payment_log write failed — so the
    //     booking still read 未付 with money already taken, i.e. one more tap
    //     would have charged the customer twice.
    // Awaiting is safe now that webFetch races its timeout (a request can no
    // longer hang forever), and correctness matters far more here than saving
    // a second on a money screen.
    //
    // ORDER IS DELIBERATE: record the payment_log entries FIRST, then debit the
    // wallet. If step 2 fails we've over-recorded (owner sees a loud warning and
    // can fix with ⚖️ 調整) — strictly safer than the reverse, which takes the
    // customer's money while the booking still shows unpaid and invites a
    // double charge.
    setBusy(true);
    const walletAmt = Number(amtMap.__wallet__) || 0;
    // Stable ids per row, generated ONCE — so retrying after a failure re-sends
    // the SAME entry id and record_payment_atomic dedupes it instead of
    // double-recording.
    const idFor = (k, prefix) => (entryIdsRef.current[k] ||= prefix + uid());
    const walletTx = walletAmt > 0
      ? { id: idFor("__wallettx__", "tx"), type: "deduct", amount: walletAmt, note: "尾款扣除 · " + (apt.service || "美容"), date: apt.date + " " + apt.time, aptId: apt.id }
      : null;
    try {
      // 1) Record every payment row (idempotent by entry id).
      let lastRes = null;
      for (const row of methodRows) {
        const amt = Number(amtMap[row.id]) || 0;
        if (amt <= 0) continue;
        const entry = {
          id: idFor(row.id, "px"),
          type: "payment",
          method: row.isWallet ? "wallet" : (row.presetKey || row.name),
          methodName: row.name.replace(/^💵 |💳 /, "").replace(/ \(餘 \$\d+\)$/, ""),
          amount: amt,
          at: new Date().toISOString(),
          collectedBy: user.id,
          by: user.id,
          purpose: "balance",
          note: "",
        };
        lastRes = await recordPaymentAtomic(apt.id, entry);
      }
      // 2) Money moves only after the record exists.
      let newWallet;
      if (walletAmt > 0) {
        try {
          const res = await debitCustomerWallet({ userId: apt.customerId, tenantId: user.tenantId, amount: walletAmt, tx: walletTx });
          if (typeof res?.newWallet === "number") newWallet = res.newWallet;
        } catch (e) {
          // Recorded as paid but the wallet didn't move — must never be silent.
          toast(zhEn("已記錄收款，但錢包扣款失敗：", "Payment recorded, but the wallet deduction failed: ") + (e?.message || e) + zhEn(" — 請用 ⚖️ 調整手動扣 $", " — use ⚖️ Adjust to deduct $") + walletAmt, "error");
        }
      }
      // Trust the server's arithmetic over an assumed "paid: true".
      onCollected(
        lastRes
          ? {
              paid: !!lastRes.paid,
              remainingBalance: Math.max(0, Number(lastRes.total || 0) - Number(lastRes.collected || 0)),
              paymentLog: Array.isArray(lastRes.log) ? lastRes.log : undefined,
            }
          : { paid: true, remainingBalance: 0 },
        walletTx ? [walletTx] : [],
        newWallet,
        apt.customerId,
      );
      toast(zhEn("已收款 $", "Collected $") + remain + " ✓");
    } catch (e) {
      // Nothing was recorded (and, thanks to the ordering, no money moved).
      toast(zhEn("收款失敗，未有扣款：", "Payment failed, nothing was charged: ") + (e?.message || e), "error");
    } finally {
      setBusy(false);
    }
  };

  if (!apt) return null;
  const Line = ({ label, amount, sub, strong, negative }) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,padding:"3px 0"}}>
      <span style={{fontSize: strong ? 14 : 12.5, fontWeight: strong ? 900 : 700, color: strong ? "#1C1C1E" : "#6A6A85", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
        {label}{sub && <span style={{fontSize:11,color:"#AFAFC4",fontWeight:700}}> {sub}</span>}
      </span>
      <span style={{fontSize: strong ? 15 : 13, fontWeight: 900, color: negative ? "#DC2626" : (strong ? "#1C1C1E" : "#4A4A68"), whiteSpace:"nowrap"}}>
        {negative ? "−$" : "$"}{Math.abs(amount).toLocaleString()}
      </span>
    </div>
  );
  // One tap = pay the whole outstanding amount with that method.
  const payAll = (row) => {
    if (busy) return;
    if (row.isWallet && walletBal < remain) {
      // Wallet can't cover it alone — fall into split mode with the wallet
      // prefilled so the owner just picks a second method for the rest.
      setSplitMode(true);
      setAmounts({ __wallet__: String(walletBal) });
      toast(zhEn("錢包餘額不足全數，請揀第二個方式付餘額", "Wallet balance isn't enough for the full amount; choose another method for the rest"), "warning");
      return;
    }
    submit({ [row.id]: String(remain) });
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,padding:"4px 2px 12px"}}>
      {/* ── Receipt breakdown ─────────────────────────────────────────── */}
      <div style={{background:"#FAFAFC",border:"1.5px solid var(--c-light)",borderRadius:14,padding:"12px 14px"}}>
        <Line label={apt.service || zhEn("美容服務", "Grooming service")} amount={basePrice} />
        {addonPrice > 0 && <Line label={zhEn("加購服務", "Add-ons")} amount={addonPrice} />}
        {/* Surcharge rows are written with `name` (sometimes also `label`). */}
        {surcharges.map((sc, i) => (
          <Line key={i} label={sc?.name || sc?.label || sc?.reason || zhEn("附加費", "Surcharge")} amount={Number(sc?.amount) || 0} />
        ))}
        {(discountAmt > 0 || voucherAmt > 0 || depositAmt > 0 || collectedAlready > 0) && (
          <div style={{borderTop:"1px dashed var(--c-border)",margin:"6px 0 4px"}} />
        )}
        {(discountAmt > 0 || voucherAmt > 0) && <Line label={zhEn("小計", "Subtotal")} amount={subtotal} />}
        {discountAmt > 0 && (
          <Line
            label={zhEn("折扣", "Discount")}
            sub={apt.discount?.type === "percent" ? "(" + (Number(apt.discount?.amount) || 0) + "% off)" : (apt.discount?.reason || "")}
            amount={discountAmt}
            negative
          />
        )}
        {voucherAmt > 0 && <Line label={zhEn("兌換券", "Coupon")} amount={voucherAmt} negative />}
        {depositAmt > 0 && <Line label={zhEn("已付訂金", "Deposit paid")} amount={depositAmt} negative />}
        {collectedAlready > 0 && <Line label={zhEn("已收款", "Paid")} amount={collectedAlready} negative />}
        <div style={{borderTop:"2px solid var(--c-border)",margin:"7px 0 5px"}} />
        <Line label={zhEn("待收金額", "Amount due")} amount={remain} strong />
      </div>

      {methodRows.length === 0 ? (
        <p style={{fontSize:12,color:"#AFAFC4",textAlign:"center",padding:"20px 0",fontWeight:700,margin:0}}>{zhEn("未設定收款方式 — 請先到「設定 → 收款」加入現金 / 其他方式", "No payment methods set — add cash / other methods under Settings → Payments first")}</p>
      ) : !splitMode ? (
        <>
          {/* ── Simple POS mode: tap a method, done. No typing. ────────── */}
          <p style={{fontSize:11,fontWeight:800,color:"#8E8E93",margin:0,letterSpacing:0.3}}>{zhEn("撳一下收款方式即完成收款", "Tap a payment method to collect")}</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {methodRows.map(row => {
              const short = row.name.replace(/ \(餘 \$[\d,]+\)$/, "");
              const walletShort = row.isWallet && walletBal < remain;
              return (
                <button key={row.id} disabled={busy} onClick={() => payAll(row)}
                  style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"14px 16px",borderRadius:14,
                    border:"2px solid " + (row.isWallet ? "#BFDBFE" : "var(--c-light)"),
                    background: row.isWallet ? "#EFF6FF" : "#fff",
                    cursor: busy ? "wait" : "pointer", textAlign:"left", opacity: busy ? 0.6 : 1}}>
                  <span style={{flex:1,minWidth:0,fontSize:14.5,fontWeight:900,color:"#1C1C1E",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {short}
                    {row.isWallet && <span style={{display:"block",fontSize:11,fontWeight:700,color: walletShort ? "#B45309" : "#2563EB",marginTop:2}}>
                      {walletShort ? zhEn("餘額 $" + walletBal + " — 不足全數，會轉去分開付款", "Balance $" + walletBal + " — not enough, switching to split payment") : zhEn("餘額 $" + walletBal, "Balance $" + walletBal)}
                    </span>}
                  </span>
                  <span style={{fontSize:15,fontWeight:900,color:"#10B981",whiteSpace:"nowrap"}}>${remain.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
          <div style={{display:"flex",gap:8,paddingTop:2}}>
            <Btn variant="ghost" onClick={onClose} style={{flex:1}}>{zhEn("取消", "Cancel")}</Btn>
            <button onClick={() => setSplitMode(true)} style={{flex:1,background:"none",border:"none",color:"var(--c-primary)",fontSize:12.5,fontWeight:900,cursor:"pointer"}}>
              {zhEn("分開付款（多個方式）→", "Split payment (multiple methods) →")}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* ── Split mode: type an amount per method. ─────────────────── */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <p style={{fontSize:11,fontWeight:800,color:"#8E8E93",margin:0,letterSpacing:0.3}}>{zhEn("分開付款 — 各方式加起來要等於 $", "Split payment — the methods must add up to $")}{remain.toLocaleString()}</p>
            <button onClick={() => { setSplitMode(false); setAmounts({}); }} style={{background:"none",border:"none",color:"var(--c-primary)",fontSize:11.5,fontWeight:900,cursor:"pointer"}}>{zhEn("← 返回一撳收款", "← Back to one-tap collect")}</button>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:800,color:"#065F46",background:"#F0FDF4",border:"1.5px solid #86EFAC",borderRadius:12,padding:"9px 12px"}}>
            <span>{zhEn("已輸入", "Entered")}</span>
            <span>${total.toLocaleString()} {diff !== 0 && <span style={{color:diff>0?"#D97706":"#DC2626",fontWeight:900}}>{"("+(diff>0?zhEn("仲差 $","Short $"):zhEn("超出 $","Over $"))+Math.abs(diff)+")"}</span>}</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {methodRows.map(row => (
              <div key={row.id} style={{display:"flex",gap:8,alignItems:"center",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-border)",background:row.isWallet?"#EFF6FF":"#fff"}}>
                <p style={{flex:1,fontSize:13,fontWeight:800,color:"#4A4A68",margin:0,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.name}</p>
                <input type="number" inputMode="decimal" value={amounts[row.id] || ""} onChange={e => setAmt(row.id, e.target.value)} placeholder="0" style={{width:82,padding:"6px 8px",border:"1.5px solid var(--c-border)",borderRadius:8,fontSize:14,fontWeight:800,textAlign:"right",outline:"none"}} />
                <button onClick={() => fillRow(row.id, row.max)} title={zhEn("填滿剩餘", "Fill remainder")} style={{background:"var(--c-lighter)",border:"none",borderRadius:6,padding:"5px 8px",fontSize:10,fontWeight:900,color:"var(--c-primary)",cursor:"pointer"}}>{zhEn("填滿", "Fill")}</button>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,paddingTop:6}}>
            <Btn variant="ghost" onClick={onClose} style={{flex:1}}>{zhEn("取消", "Cancel")}</Btn>
            <Btn onClick={() => submit()} disabled={busy || Math.abs(diff) > 0.01 || total <= 0} style={{flex:2,background:"#10B981"}}>{busy ? <><Spinner /> {zhEn("處理中…", "Processing…")}</> : zhEn("✓ 確認收款 $", "✓ Confirm payment $") + remain.toLocaleString()}</Btn>
          </div>
        </>
      )}
    </div>
  );
};

const SyncLogCard = () => {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump(x => x + 1);
    if (typeof window !== "undefined") window.addEventListener("petos:sync-log-changed", fn);
    return () => { if (typeof window !== "undefined") window.removeEventListener("petos:sync-log-changed", fn); };
  }, []);
  const log = (() => {
    try { return JSON.parse(localStorage.getItem("petos:sync-log") || "[]"); }
    catch { return []; }
  })();
  const fmtTime = (ms) => {
    const d = new Date(ms);
    const t = d.toLocaleTimeString(dateLoc(), { hour12: false });
    const dateStr = d.toLocaleDateString(dateLoc(), { month: "short", day: "numeric" });
    return dateStr + " " + t;
  };
  const okCount = log.filter(e => e.ok).length;
  const failCount = log.length - okCount;
  return (
    <Card style={{padding:20, marginBottom:16}}>
      <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("💾 儲存紀錄", "💾 Save log")}</h3>
      <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 10px",fontWeight:700}}>{zhEn("最近 50 次資料儲存事件。如果遇到「資料消失」嘅情況，可以喺呢度核對最新一次儲存有冇成功。", "The last 50 data-save events. If data seems to disappear, check here whether the latest save succeeded.")}</p>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <div style={{flex:1,padding:"8px 10px",background:"#F0FDF4",borderRadius:10,border:"1.5px solid #BBF7D0",textAlign:"center"}}>
          <p style={{fontSize:18,fontWeight:900,color:"#065F46",margin:0}}>{okCount}</p>
          <p style={{fontSize:10,color:"#065F46",margin:0,fontWeight:700,opacity:0.8}}>{zhEn("成功", "Success")}</p>
        </div>
        <div style={{flex:1,padding:"8px 10px",background:failCount?"#FEF2F2":"#F9FAFB",borderRadius:10,border:"1.5px solid "+(failCount?"#FECACA":"#E5E7EB"),textAlign:"center"}}>
          <p style={{fontSize:18,fontWeight:900,color:failCount?"#991B1B":"#9CA3AF",margin:0}}>{failCount}</p>
          <p style={{fontSize:10,color:failCount?"#991B1B":"#9CA3AF",margin:0,fontWeight:700,opacity:0.8}}>{zhEn("失敗", "Failed")}</p>
        </div>
      </div>
      {log.length === 0 ? (
        <p style={{fontSize:12,color:"#AFAFC4",margin:0,textAlign:"center",padding:"20px 0",fontWeight:700}}>{zhEn("未有任何儲存紀錄", "No save records yet")}</p>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:320,overflowY:"auto"}}>
          {log.map((e, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:8,background:e.ok ? "#F0FDF4" : "#FEF2F2",borderLeft:"3px solid "+(e.ok?"#10B981":"#EF4444")}}>
              <span style={{fontSize:11,fontWeight:900,color:e.ok?"#065F46":"#991B1B",minWidth:18}}>{e.ok?"✓":"✗"}</span>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:11,fontWeight:800,color:"#4A4A68",margin:0}}>{fmtTime(e.at)} <span style={{color:"#9CA3AF",fontWeight:600}}>· {e.ms}ms</span></p>
                {e.error && <p style={{fontSize:10,color:"#991B1B",margin:"1px 0 0",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.error}</p>}
                {!e.error && e.softCount > 0 && <p style={{fontSize:10,color:"#92400E",margin:"1px 0 0",fontWeight:700}}>{zhEn("軟性錯誤", "Soft errors")} {e.softCount} {zhEn("項（通知未送達）", " (notification not delivered)")}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:10}}>
        <button onClick={() => { try { localStorage.removeItem("petos:sync-log"); window.dispatchEvent(new CustomEvent("petos:sync-log-changed")); } catch {} }} style={{background:"transparent",border:"none",color:"#9CA3AF",fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>{zhEn("清除紀錄", "Clear log")}</button>
      </div>
    </Card>
  );
};

// ── Phase 4.3 — 認證狀態 (auth health) panel ───────────────────────────
// Surfaces the live session expiry countdown + last-refresh timestamp +
// last 401 event timestamp. Helps diagnose "why was I logged out?" or
// "why does saving fail?" without the user needing to open DevTools.
const AuthHealthCard = () => {
  const [tick, setTick] = useState(0);
  const [session, setSession] = useState(null);
  // Refresh the view every 30s so the expiry countdown stays accurate.
  useEffect(() => {
    let cancelled = false;
    const fetchSession = async () => {
      try {
        const { supabase } = await import("./lib/supabaseClient.js");
        const { data } = await supabase.auth.getSession();
        if (!cancelled) setSession(data?.session || null);
      } catch { /* ignore */ }
    };
    fetchSession();
    const iv = setInterval(() => { fetchSession(); setTick(x => x + 1); }, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  const lastForeground = (() => { try { return Number(localStorage.getItem("petos:last-foreground-at")) || 0; } catch { return 0; } })();
  const expMs = session?.expires_at ? session.expires_at * 1000 : 0;
  const msLeft = expMs ? expMs - Date.now() : 0;
  const minutesLeft = Math.max(0, Math.floor(msLeft / 60000));
  const secondsLeft = Math.max(0, Math.floor((msLeft % 60000) / 1000));
  const fmtDate = (ms) => ms ? new Date(ms).toLocaleString(dateLoc(), { hour12: false }) : "—";
  return (
    <Card style={{padding:20, marginBottom:16}}>
      <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("🔐 認證狀態", "🔐 Auth status")}</h3>
      <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{zhEn("登入 Session 嘅實時狀態。如果發現「儲存失敗」或「自動登出」，可以喺呢度檢查 Token 仲有冇效。", "Live state of your login session. If you see \"save failed\" or get logged out, check here whether the token is still valid.")}</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
        <div style={{padding:"10px 12px",background:"var(--c-lighter)",borderRadius:10}}>
          <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 2px"}}>{zhEn("SESSION 狀態", "SESSION STATE")}</p>
          <p style={{fontSize:14,fontWeight:900,color:session ? "#10B981" : "#EF4444",margin:0}}>{session ? zhEn("✓ 已登入", "✓ Logged in") : zhEn("✗ 未登入", "✗ Not logged in")}</p>
        </div>
        <div style={{padding:"10px 12px",background:"var(--c-lighter)",borderRadius:10}}>
          <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 2px"}}>{zhEn("TOKEN 剩餘時間", "TOKEN TIME LEFT")}</p>
          <p style={{fontSize:14,fontWeight:900,color:minutesLeft<5 ? "#EF4444" : minutesLeft<15 ? "#D97706" : "#10B981",margin:0}}>{expMs ? zhEn(minutesLeft + " 分 " + secondsLeft + " 秒", minutesLeft + "m " + secondsLeft + "s") : "—"}</p>
        </div>
        <div style={{padding:"10px 12px",background:"var(--c-lighter)",borderRadius:10,gridColumn:"span 2"}}>
          <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 2px"}}>{zhEn("TOKEN 到期時間", "TOKEN EXPIRES")}</p>
          <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0}}>{fmtDate(expMs)}</p>
        </div>
        <div style={{padding:"10px 12px",background:"var(--c-lighter)",borderRadius:10,gridColumn:"span 2"}}>
          <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 2px"}}>{zhEn("上次返回 App 時間", "LAST RETURN TO APP")}</p>
          <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0}}>{fmtDate(lastForeground)}</p>
        </div>
      </div>
      <p style={{fontSize:10,color:"#9CA3AF",margin:"12px 0 0",fontWeight:700,textAlign:"center"}}>{tick > 0 ? "" : ""}{zhEn("會自動每 30 秒更新", "Updates automatically every 30 seconds")}</p>
    </Card>
  );
};

// ── OWNER ONBOARDING WIZARD ────────────────────────────────────────────────
// Shown on a new owner's first dashboard load. localStorage flag per owner-id
// means it appears exactly once per device unless the owner explicitly opens
// the "再睇一次" link.
// ── Setting-template picker ────────────────────────────────────────────────
// Shown as step 0 of the first-login wizard, and re-openable forever from
// 設定 → 服務 → 套用設定模板. Applying is SAFE BY CONSTRUCTION: services are
// matched by name so their ids (and every appointment / 套票 / per-pet price
// pointing at them) survive, prices are carried across a tier change by
// weight overlap, and nothing is ever deleted. See lib/settingTemplates.js.
const TemplatePicker = ({ tenant, tenantId, setState, onClose, firstRun }) => {
  const curKey = tenant?.pricingConfig?.templateKey || null;
  const [pick, setPick] = useState(curKey || "standard");
  const [step, setStep] = useState("pick");     // pick | confirm | busy | done
  const [keepPrices, setKeepPrices] = useState(true);
  const [extraMode, setExtraMode] = useState("keep");
  const [progress, setProgress] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const tpl = getSettingTemplate(pick);
  const preview = useMemo(
    () => previewSettingTemplate({ tenant, template: tpl, extraServices: extraMode }),
    [tenant, tpl, extraMode]
  );

  // Merge what applyTemplate wrote back into client state so the 服務 tab and
  // the matrix editor repaint without a reload.
  const mergeResult = (res) => setState(s => ({
    ...s,
    tenants: s.tenants.map(tt => {
      if (tt.id !== tenantId) return tt;
      const byId = new Map((tt.services || []).map(x => [x.id, x]));
      (res.services || []).forEach(x => byId.set(x.id, x));   // includes any it disabled
      const services = [...byId.values()];
      const aById = new Map((tt.addons || []).map(x => [x.id, x]));
      (res.addons || []).forEach(x => aById.set(x.id, x));
      return {
        ...tt,
        pricingConfig: res.tenantPatch?.pricingConfig ?? tt.pricingConfig,
        specialBreeds: res.tenantPatch?.specialBreeds ?? tt.specialBreeds,
        services,
        addons: [...aById.values()],
      };
    }),
  }));

  const doApply = async () => {
    setStep("busy");
    setProgress(T("準備中…", "Preparing…"));
    const snap = snapshotTemplateState(tenant);
    try {
      const res = await applySettingTemplate({
        tenantId, tenant, template: tpl,
        keepPrices, extraServices: extraMode,
        onProgress: setProgress,
      });
      mergeResult(res);
      setSnapshot(snap);
      if (res.errors?.length) toast(T("部分項目套用失敗，請檢查服務清單", "Some items failed — check the service list"), "warning");
      setStep("done");
    } catch (e) {
      console.warn("[PettoBook] applyTemplate failed:", e);
      toast(T("套用失敗：", "Apply failed: ") + (e?.message || e), "error");
      setStep("pick");
    }
  };

  const doUndo = async () => {
    if (!snapshot) return;
    setUndoing(true);
    try {
      const res = await restoreTemplateSnapshot({ tenantId, tenant, snapshot });
      mergeResult(res);
      toast(T("已復原之前嘅設定 ✓", "Restored your previous settings ✓"));
      onClose();
    } catch (e) {
      toast(T("復原失敗：", "Undo failed: ") + (e?.message || e), "error");
    } finally { setUndoing(false); }
  };

  const card = (t) => {
    const on = pick === t.key;
    const L = templateLabels(t);
    return (
      <button key={t.key} type="button" onClick={() => setPick(t.key)}
        style={{textAlign:"left",width:"100%",cursor:"pointer",display:"flex",gap:12,alignItems:"flex-start",
          background: on ? "var(--c-lighter)" : "#fff",
          border: on ? "2.5px solid var(--c-primary)" : "2px solid var(--c-border)",
          borderRadius:18, padding:"12px 14px"}}>
        <span style={{fontSize:26,lineHeight:1.1}}>{t.emoji}</span>
        <span style={{flex:1,minWidth:0}}>
          <span style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
            <b style={{fontSize:15,fontWeight:900,color:"var(--c-dark)"}}>{L.name}</b>
            <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>{L.subtitle}</span>
            {t.recommended && <span style={{fontSize:10,fontWeight:900,color:"#fff",background:"var(--c-primary)",borderRadius:99,padding:"2px 7px"}}>{T("推薦","Best")}</span>}
            {curKey === t.key && <span style={{fontSize:10,fontWeight:900,color:"var(--c-primary)",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"1px 6px"}}>{T("目前","Current")}</span>}
          </span>
          <span style={{display:"block",fontSize:11.5,fontWeight:800,color:"var(--c-primary)",marginTop:3}}>{L.tagline}</span>
          {on && <span style={{display:"block",fontSize:12,fontWeight:600,color:"#6A6A85",lineHeight:1.5,marginTop:6}}>{L.desc}</span>}
        </span>
      </button>
    );
  };

  const shell = (children, footer) => (
    <div style={{position:"fixed",inset:0,zIndex:320,background:"rgba(74,74,104,0.55)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#FCFCFF",borderRadius:28,maxWidth:460,width:"100%",maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{padding:"20px 22px 10px",flex:"0 0 auto"}}>
          <p style={{fontSize:18,fontWeight:900,color:"var(--c-dark)",margin:0}}>{T("設定模板","Setting template")}</p>
          <p style={{fontSize:12,fontWeight:700,color:"#8A8A9E",margin:"4px 0 0",lineHeight:1.5}}>
            {T("揀一個最似你店嘅收費方式，體型分級、特殊品種同服務會即刻幫你設定好。價錢之後自己填。",
               "Pick the pricing shape closest to your shop — size tiers, special breeds and services are set up for you. You fill in the prices after.")}
          </p>
        </div>
        <div style={{padding:"4px 22px",overflowY:"auto",flex:"1 1 auto",display:"flex",flexDirection:"column",gap:10}}>{children}</div>
        <div style={{padding:"12px 22px 20px",flex:"0 0 auto",display:"flex",gap:10}}>{footer}</div>
      </div>
    </div>
  );

  if (step === "busy") {
    return shell(
      <div style={{textAlign:"center",padding:"40px 0"}}>
        <div style={{fontSize:44,marginBottom:10}}>⏳</div>
        <p style={{fontSize:14,fontWeight:800,color:"var(--c-dark)",margin:0}}>{progress || T("套用中…","Applying…")}</p>
      </div>, null);
  }

  if (step === "done") {
    return shell(
      <div style={{textAlign:"center",padding:"24px 0"}}>
        <div style={{fontSize:48,marginBottom:10}}>✅</div>
        <p style={{fontSize:16,fontWeight:900,color:"var(--c-dark)",margin:"0 0 6px"}}>{T("已套用「","Applied “") + templateLabels(tpl).name + T("」模板","”")}</p>
        <p style={{fontSize:13,fontWeight:700,color:"#6A6A85",margin:0,lineHeight:1.6}}>
          {T("下一步：去「服務」分頁填價錢。每個服務有 ","Next: open 服務 and fill in the prices — ")}
          <b>{tpl.tiers.length}{T(" 級體型","  tiers")}{tpl.coatSplit ? T(" × 短/長毛"," × short/long coat") : ""}</b>
          {tpl.breeds.length ? T("，另加 ", ", plus ") + tpl.breeds.length + T(" 組特殊品種", " breed groups") : ""}{zhEn("。", ". ")}
        </p>
      </div>,
      <>
        <Btn variant="secondary" onClick={doUndo} disabled={undoing} style={{flex:1}}>{undoing ? T("復原中…","Undoing…") : T("↩︎ 復原","↩︎ Undo")}</Btn>
        <Btn onClick={onClose} style={{flex:2}}>{T("完成 ✓","Done ✓")}</Btn>
      </>);
  }

  if (step === "confirm") {
    const rows = [];
    if (preview.tierChanged) rows.push(T("體型分級由 ","Size tiers ") + preview.tierFrom + " → " + preview.tierTo + T(" 級"," "));
    if (preview.coatSplitFrom !== preview.coatSplitTo) rows.push(preview.coatSplitTo ? T("開啟短毛／長毛分開收費","Turn ON separate short/long-coat prices") : T("關閉短毛／長毛分開收費","Turn OFF separate short/long-coat prices"));
    if (preview.breedFrom !== preview.breedTo) rows.push(T("特殊品種由 ","Special breeds ") + preview.breedFrom + " → " + preview.breedTo + T(" 組"," groups"));
    if (preview.addedServices.length) rows.push(T("新增 ","Add ") + preview.addedServices.length + T(" 個服務：","  services: ") + preview.addedServices.join("、"));
    if (preview.keptServices) rows.push(T("保留 ","Keep ") + preview.keptServices + T(" 個現有服務（預約紀錄唔受影響）","  existing services (bookings unaffected)"));
    if (preview.rebandedServices.length && preview.tierChanged) rows.push(T("將 ","Re-band ") + preview.rebandedServices.length + T(" 個你自己加嘅服務轉到新體型分級（價錢跟住搬，唔會清空）","  of your own services onto the new tiers (prices move with them, nothing is cleared)"));
    if (preview.reEnabledServices.length) rows.push("⚠️ " + T("重新啟用 ","Re-enable ") + preview.reEnabledServices.length + T(" 個你之前停用咗嘅服務：","  services you had switched off: ") + preview.reEnabledServices.join("、"));
    if (preview.willDisable.length) rows.push("⚠️ " + T("停用 ","Disable ") + preview.willDisable.length + T(" 個服務：","  services: ") + preview.willDisable.join("、"));
    return shell(
      <>
        <div style={{background:"var(--c-lighter)",border:"2px solid var(--c-border)",borderRadius:18,padding:14}}>
          <p style={{fontSize:14,fontWeight:900,color:"var(--c-dark)",margin:"0 0 8px"}}>{tpl.emoji} {T("套用「","Apply “") + templateLabels(tpl).name + T("」會…","” will…")}</p>
          {rows.length ? rows.map((r,i) => (
            <p key={i} style={{fontSize:12.5,fontWeight:700,color:"#4A4A68",margin:"0 0 5px",lineHeight:1.5}}>• {r}</p>
          )) : <p style={{fontSize:12.5,fontWeight:700,color:"#6A6A85",margin:0}}>{T("冇結構上嘅改動。","No structural change.")}</p>}
          <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:"8px 0 0",lineHeight:1.5}}>
            {T("服務永遠唔會被刪除，已完成嘅預約同收費紀錄亦唔會改變。套用後可以即刻復原。",
               "Services are never deleted, and past bookings keep the price they were charged. You can undo right after.")}
          </p>
        </div>
        <div style={{background:"#fff",border:"2px solid var(--c-border)",borderRadius:18,padding:14,display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <p style={{fontSize:12,fontWeight:900,color:"var(--c-dark)",margin:"0 0 6px"}}>{T("現有價錢","Existing prices")}</p>
            <div style={{display:"flex",gap:8}}>
              {[[true,T("保留（建議）","Keep (recommended)")],[false,T("清空重填","Clear all")]].map(([v,lbl]) => (
                <button key={String(v)} type="button" onClick={() => setKeepPrices(v)}
                  style={{flex:1,cursor:"pointer",fontSize:11.5,fontWeight:800,padding:"8px 6px",borderRadius:12,
                    background: keepPrices===v ? "var(--c-primary)" : "#fff",
                    color: keepPrices===v ? "#fff" : "#6A6A85",
                    border: keepPrices===v ? "2px solid var(--c-primary)" : "2px solid var(--c-border)"}}>{lbl}</button>
              ))}
            </div>
            {keepPrices && preview.tierChanged && (
              <p style={{fontSize:10.5,fontWeight:700,color:"#8A8A9E",margin:"6px 0 0",lineHeight:1.45}}>
                {T("體型級數改變時，每個新分級會沿用原本覆蓋同一體重嘅價錢。","When tier counts change, each new tier inherits the price of the old tier covering the same weight.")}
              </p>
            )}
          </div>
          {preview.extraServices.length > 0 && (
            <div>
              <p style={{fontSize:12,fontWeight:900,color:"var(--c-dark)",margin:"0 0 4px"}}>{T("模板以外嘅服務","Services not in the template")}</p>
              <p style={{fontSize:10.5,fontWeight:700,color:"#8A8A9E",margin:"0 0 6px",lineHeight:1.45}}>{preview.extraServices.join("、")}</p>
              <div style={{display:"flex",gap:8}}>
                {[["keep",T("保留（建議）","Keep (recommended)")],["disable",T("停用","Disable")]].map(([v,lbl]) => (
                  <button key={v} type="button" onClick={() => setExtraMode(v)}
                    style={{flex:1,cursor:"pointer",fontSize:11.5,fontWeight:800,padding:"8px 6px",borderRadius:12,
                      background: extraMode===v ? "var(--c-primary)" : "#fff",
                      color: extraMode===v ? "#fff" : "#6A6A85",
                      border: extraMode===v ? "2px solid var(--c-primary)" : "2px solid var(--c-border)"}}>{lbl}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </>,
      <>
        <Btn variant="secondary" onClick={() => setStep("pick")} style={{flex:1}}>{T("← 返回","← Back")}</Btn>
        <Btn onClick={doApply} style={{flex:2}}>{T("確認套用","Apply")}</Btn>
      </>);
  }

  // step === "pick"
  return shell(
    SETTING_TEMPLATES.map(card),
    <>
      <Btn variant="secondary" onClick={onClose} style={{flex:1}}>{firstRun ? T("稍後再揀","Later") : T("取消","Cancel")}</Btn>
      <Btn onClick={() => (firstRun ? doApply() : setStep("confirm"))} style={{flex:2}}>
        {firstRun ? T("用呢個模板 →","Use this →") : T("下一步 →","Next →")}
      </Btn>
    </>);
};

const OnboardingWizard = ({ tenant, tenantId, setState, onClose, onGoto, bookingUrlPrefix }) => {
  const [step, setStep] = useState(0);
  // Template picker opened from slide 0. Registration already seeded 標準 so
  // the shop is never empty; this is the owner's chance to swap it for a
  // shape that matches how they actually price.
  const [pickerOpen, setPickerOpen] = useState(false);
  const tplName = getSettingTemplate(tenant?.pricingConfig?.templateKey)?.name || "標準";
  const slides = [
    {
      icon: "🎉",
      title: zhEn("歡迎使用 PettoBook！", "Welcome to PettoBook!"),
      body: zhEn(`你嘅店鋪已建立，而家用緊「${tplName}」模板 — 體型分級、特殊品種、服務同加購都幫你設定好晒。

如果你嘅收費方式唔同（例如只分 3 級體型、或者要分得更細），可以而家換另一個模板。`, `Your shop is set up and currently uses the "${tplName}" template — size tiers, special breeds, services and add-ons are all set up for you.

If you price differently (e.g. only 3 size tiers, or more detailed ones), you can switch to another template now.`),
      hint: zhEn("模板隨時可以喺「設定 → 服務」再換。", "You can switch templates any time under Settings → Services."),
      cta: { label: zhEn("睇下有咩模板 →", "See templates →"), action: "picker" },
    },
    {
      icon: "✂️",
      title: zhEn("1. 完成服務定價", "1. Finish service pricing"),
      body: zhEn("前往「服務」分頁，為每個服務按體型／毛長／品種填入價錢。預設嘅體型同特殊品種已幫你預備好。", "Go to the Services tab and enter prices for each service by size / coat / breed. Default sizes and special breeds are already prepared."),
      cta: { label: zhEn("立即前往「服務」", "Go to Services now"), goto: "services" },
    },
    {
      icon: "👥",
      title: zhEn("2. 邀請員工", "2. Invite staff"),
      body: zhEn("「員工」分頁可以新增美容師。每位員工有獨立登入帳號，可以查看自己嘅排班同預約。", "Add groomers under the Staff tab. Each staff member has their own login and can see their own roster and bookings."),
      cta: { label: zhEn("立即前往「員工」", "Go to Staff now"), goto: "staff" },
    },
    {
      icon: "💬",
      title: zhEn("3. 通訊設定（選填）", "3. Messaging setup (optional)"),
      body: zhEn("「設定 → 通訊」可以連接 WhatsApp Business API，預約／提醒會自動發送畀客戶。", "Under Settings → Messaging you can connect the WhatsApp Business API so booking confirmations and reminders are sent to customers automatically."),
      cta: { label: zhEn("立即前往「設定」", "Go to Settings now"), goto: "settings" },
    },
    {
      icon: "🔗",
      title: zhEn("4. 分享預約連結", "4. Share your booking link"),
      body: zhEn(`客戶可以喺呢條連結直接預約：
${bookingUrlPrefix}${tenant?.bookingSlug || "your-shop"}
貼上 WhatsApp / Instagram / 名片上！`, `Customers can book directly through this link:
${bookingUrlPrefix}${tenant?.bookingSlug || "your-shop"}
Paste it into WhatsApp / Instagram / your business card!`),
      cta: { label: zhEn("複製連結", "Copy link"), action: () => { navigator.clipboard?.writeText(bookingUrlPrefix + tenant?.bookingSlug); toast(i18nT("toast.copyOk")); } },
    },
  ];
  const cur = slides[step];
  const isLast = step === slides.length - 1;
  if (pickerOpen) {
    return <TemplatePicker tenant={tenant} tenantId={tenantId} setState={setState} firstRun onClose={() => setPickerOpen(false)} />;
  }
  return (
    <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(74,74,104,0.55)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#FCFCFF",borderRadius:32,maxWidth:420,width:"100%",padding:28,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12,fontWeight:800,color:"#AFAFC4"}}>{step+1} / {slides.length}</span>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:12,color:"#8A8A9E",cursor:"pointer",fontWeight:800,textDecoration:"underline"}}>{zhEn("略過", "Skip")}</button>
        </div>
        <div style={{textAlign:"center",padding:"8px 0"}}>
          <div style={{fontSize:56,marginBottom:8}}>{cur.icon}</div>
          <h3 style={{fontSize:20,fontWeight:900,color:"#4A4A68",margin:"0 0 10px"}}>{cur.title}</h3>
          <p style={{fontSize:14,color:"#6A6A85",fontWeight:600,margin:0,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{cur.body}</p>
          {cur.hint && <p style={{fontSize:12,color:"var(--c-primary)",fontWeight:800,margin:"10px 0 0"}}>{cur.hint}</p>}
        </div>
        {cur.cta && (
          <Btn variant="secondary" onClick={() => {
            if (cur.cta.action === "picker") setPickerOpen(true);
            else if (typeof cur.cta.action === "function") cur.cta.action();
            else if (cur.cta.goto && onGoto) onGoto(cur.cta.goto);
          }} style={{width:"100%"}}>{cur.cta.label}</Btn>
        )}
        <div style={{display:"flex",gap:4,justifyContent:"center"}}>
          {slides.map((_, i) => <div key={i} style={{width:8,height:8,borderRadius:99,background:i===step?"var(--c-primary)":"var(--c-light)"}} />)}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="secondary" disabled={step===0} onClick={() => setStep(s => Math.max(0, s-1))} style={{flex:1,opacity:step===0?0.4:1}}>{zhEn("← 上一步", "← Back")}</Btn>
          <Btn onClick={() => isLast ? onClose() : setStep(s => s+1)} style={{flex:2}}>{isLast ? zhEn("完成 ✓", "Done ✓") : zhEn("下一步 →", "Next →")}</Btn>
        </div>
      </div>
    </div>
  );
};

// ── OWNER / STAFF APP (walk-in booking added) ──────────────────────────────
// Owner-side account deletion (Google Play account-deletion policy). Files a
// pending request via request-account-deletion; the admin completes it
// within 30 days, matching the public /delete-account page.
const OwnerDeleteAccountPanel = ({ T, onLogout }) => {
  const [pending, setPending] = useState(undefined);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    requestAccountDeletion({ check: true })
      .then(r => { if (alive) setPending(r?.pending || null); })
      .catch(() => { if (alive) setPending(null); });
    return () => { alive = false; };
  }, []);
  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") return;
    setBusy(true);
    try {
      const r = await requestAccountDeletion({ reason });
      setPending(r?.pending || { created_at: new Date().toISOString() });
      toast(T("已收到刪除申請，我哋會喺 30 日內處理","Deletion request received — we'll process it within 30 days","削除リクエストを受け付けました（30日以内に処理）"), "success");
    } catch (e) {
      toast(T("提交失敗：","Request failed: ","送信に失敗しました：") + (e?.message || e), "error");
    } finally { setBusy(false); }
  };
  const li = { margin: "2px 0" };
  return (
    <Card style={{padding:24}}>
      <p style={{fontSize:18,fontWeight:900,color:"#DC2626",margin:"0 0 8px"}}>🗑️ {T("刪除帳戶","Delete account","アカウント削除")}</p>
      <p style={{fontSize:13,color:"#4A4A68",fontWeight:600,lineHeight:1.7,margin:"0 0 12px"}}>
        {T("提交申請後，我哋會喺 30 日內永久刪除你嘅帳戶。如果你係店主，店舖資料、服務、員工帳戶、客戶名單及相簿亦會一併刪除。",
           "After you submit, we'll permanently delete your account within 30 days. For shop owners this also removes the shop profile, services, staff accounts, customer list and albums.",
           "申請後30日以内にアカウントを完全に削除します。オーナーの場合、店舗情報・サービス・スタッフ・顧客リスト・アルバムも削除されます。")}
      </p>
      <ul style={{fontSize:12,color:"#6B6B85",fontWeight:600,lineHeight:1.6,margin:"0 0 12px",paddingLeft:18}}>
        <li style={li}>{T("預約及付款紀錄會移除個人資料後保留最多 7 年（稅務要求）","Appointment and payment records are kept up to 7 years with personal details removed (tax law)","予約・支払記録は個人情報を除いて最長7年保管（税務要件）")}</li>
        <li style={li}>{T("Google Play / App Store 訂閱請另行喺商店取消","Cancel any Google Play / App Store subscription in the store","Google Play / App Store のサブスクはストアで解約してください")}</li>
        <li style={li}><a href="/delete-account" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800}}>{T("詳細說明","Details","詳細")}</a></li>
      </ul>
      {pending === undefined ? (
        <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700}}>…</p>
      ) : pending ? (
        <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:12,padding:"12px 14px"}}>
          <p style={{fontSize:13,fontWeight:800,color:"#991B1B",margin:0}}>
            {T("已提交刪除申請","Deletion requested","削除申請済み")}{pending.created_at ? " · " + String(pending.created_at).slice(0,10) : ""}
          </p>
          <p style={{fontSize:12,fontWeight:600,color:"#991B1B",margin:"4px 0 10px"}}>
            {T("我哋會喺 30 日內處理並以電郵通知你。如要取消申請，請聯絡 privacy@pettobook.com。","We'll process it within 30 days and email you. To cancel, contact privacy@pettobook.com.","30日以内に処理しメールでお知らせします。取消は privacy@pettobook.com へ。")}
          </p>
          <Btn variant="secondary" size="sm" onClick={onLogout}>{T("登出","Log out","ログアウト")}</Btn>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500}
            placeholder={T("原因（可選）","Reason (optional)","理由（任意）")}
            style={{border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:13,fontFamily:"inherit",resize:"vertical"}} />
          <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
            placeholder={T("輸入 DELETE 以確認","Type DELETE to confirm","確認のため DELETE と入力")}
            autoCapitalize="characters" autoCorrect="off"
            style={{border:"2px solid #FECACA",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800}} />
          <Btn variant="danger" disabled={busy || confirmText.trim().toUpperCase() !== "DELETE"} onClick={submit} style={{width:"100%"}}>
            {busy ? "…" : T("申請刪除帳戶","Request account deletion","アカウント削除を申請")}
          </Btn>
        </div>
      )}
    </Card>
  );
};

const OwnerApp = ({ state: rawState, setState, user, onLogout, syncStatus }) => {
  // Subscribe to locale changes so badges + nav labels re-render when
  // owner / staff toggles language.
  const _ownerLoc = i18nUseLocale();
  // ── Staff pay (owner-only tables since 2026-09-28) ──────────────────────
  // Pay settings, wage entries and payouts no longer come from `profiles`
  // (colleagues could read them there). Load them from staff_pay_* and MERGE
  // into this component's view of each staff member under the field names
  // the screens already use, so reads stay unchanged. The merge never enters
  // global state, so the background profile sync can never write pay.
  const [staffPay, setStaffPay] = useState({ settings: {}, wages: [], payouts: [], adjustments: [], loaded: false });
  const refreshStaffPay = useCallback(async () => {
    if (!user?.tenantId) return;
    try {
      const [r, adjustments] = await Promise.all([
        fetchStaffPay(user.tenantId),
        // Bonuses / deductions for the whole shop — the summary and chart
        // need them too, not just the payslip, or totals disagree.
        fetchStaffDeductions(user.tenantId).catch(() => []),
      ]);
      setStaffPay({ ...r, adjustments, loaded: true });
    }
    catch (e) { console.warn("[PettoBook] fetchStaffPay failed:", e); }
  }, [user?.tenantId]);
  useEffect(() => { refreshStaffPay(); }, [refreshStaffPay]);
  const state = useMemo(() => {
    const ids = new Set([...(rawState.tenants || []).flatMap(t => t.id === user?.tenantId ? (t.staffIds || []) : []), user?.id].filter(Boolean));
    if (!ids.size) return rawState;
    const users = { ...rawState.users };
    for (const id of ids) {
      const u = users[id];
      if (!u) continue;
      users[id] = {
        ...u,
        // Settings row, else defaults with commission OFF — a person whose pay
        // this shop can't see (e.g. another shop's owner listed here) must not
        // trip the 「佣金設定衝突」 reminder. Never the legacy profile columns.
        ...{ ...PAY_DEFAULTS, ...(staffPay.settings[id] || { commissionEnabled: false }) },
        wageLog: staffPay.wages.filter(w => w.staffId === id),
        payoutLog: staffPay.payouts.filter(p => p.staffId === id),
      };
    }
    return { ...rawState, users };
  }, [rawState, staffPay, user?.tenantId, user?.id]);
  const [tab, setTab] = useState("schedule");
  const [reportSheet, setReportSheet] = useState(null);
  // Keep the native Siri App Intent's config (shop id + Supabase) fresh so
  // "Hey Siri, book …" acts on the right shop. No-op on web.
  useEffect(() => { persistSiriConfig(user?.tenantId); }, [user?.tenantId]);
  // Toast for Stripe Checkout return. The Checkout success_url comes
  // back with ?sub=success&session_id=... so we surface the confirm
  // here once the realtime sync has flipped the plan, then strip the
  // params from the URL so refresh doesn't re-fire the toast.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const sub = q.get("sub");
      const portal = q.get("portal");
      if (sub === "success") {
        toast(zhEn("訂閱成功 ✓ 方案已升級", "Subscribed ✓ plan upgraded"), "success");
      } else if (sub === "cancel") {
        toast(zhEn("已取消訂閱付款", "Subscription payment cancelled"), "warning");
      } else if (portal === "back") {
        toast(zhEn("已返回 PettoBook", "Back in PettoBook"), "info");
      }
      if (sub || portal) {
        const u = new URL(window.location.href);
        u.searchParams.delete("sub");
        u.searchParams.delete("portal");
        u.searchParams.delete("session_id");
        window.history.replaceState({}, "", u.pathname + (u.search ? "?" + u.searchParams.toString() : "") + u.hash);
      }
    } catch { /* ignore */ }
  }, []);
  // Payment checkout sheet — { aptId, step?, discount?, method? }.
  // Step 1 (default) = line items + discount; Step 2 = method picker.
  const [paymentSheet, setPaymentSheet] = useState(null);
  const [crmUser, setCrmUser] = useState(null); const [crmTab, setCrmTab] = useState("info");
  // Opened the customer's CRM from an appointment → closing it goes back to
  // that appointment. { tab, aptId } | null
  const [crmReturn, setCrmReturn] = useState(null);
  const [clearRewardsBusy, setClearRewardsBusy] = useState(false);
  useEffect(() => { if (crmReturn && tab !== "crm") setCrmReturn(null); }, [tab, crmReturn]);
  // Owner-creates-customer: full auth account with preset password.
  // Modal state. createdAccount holds the response from the edge function
  // (email + presetPassword) so we can show the owner what to share
  // with the customer.
  const [newAcctForm, setNewAcctForm] = useState(null); // null = closed
  // CSV 匯入客戶 — { step, rows, summary }
  const [importSheet, setImportSheet] = useState(null);
  const [newAcctBusy, setNewAcctBusy] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);
  // Customer password reset — { userId, name } when reset is in flight,
  // and { presetPassword, name } when the reset has completed and we're
  // displaying the new password to the owner so they can share it.
  const [resetPwSheet, setResetPwSheet] = useState(null);
  const [resetPwBusy, setResetPwBusy] = useState(false);
  const [resetPwResult, setResetPwResult] = useState(null);
  // 📲 發送登入資料 — { name, countryCode, phone, password, shopName, loginUrl }
  const [loginShare, setLoginShare] = useState(null);
  const [loginShareBusy, setLoginShareBusy] = useState(false);
  const [expandedRecord, setExpandedRecord] = useState(null); // appointment id expanded in records tab
  // Inline edit-customer state (name / phone) opened from the detail sheet.
  const [editCustForm, setEditCustForm] = useState(null); // { id, name, phone } or null
  const [sendCouponForm, setSendCouponForm] = useState(null);   // { userId, type, label, amount, note }
  const [savingCoupon, setSavingCoupon] = useState(false);
  // Pending customer claim requests (a registered customer whose phone matches
  // a walk-in we added — owner approves to merge their old pets/history).
  const [pendingClaims, setPendingClaims] = useState([]);
  const claimNotifiedRef = useRef(false);
  const refreshClaims = useCallback(async () => {
    if (!user?.tenantId) return;
    try {
      const rows = await fetchPendingClaims(user.tenantId);
      setPendingClaims(rows);
      // Notify once per session so the owner knows to check 顧客.
      if (rows.length > 0 && !claimNotifiedRef.current) {
        claimNotifiedRef.current = true;
        toast(zhEn(`🔗 有 ${rows.length} 個客戶認領請求 — 請到「顧客」核實`, `🔗 ${rows.length} customer claim request(s) — check them in "Customers"`), "info");
      }
    } catch (e) { console.warn("[PettoBook] fetchPendingClaims failed:", e); }
  }, [user?.tenantId]);
  // Load on login (any tab) so the badge/notification shows app-wide, and
  // refresh whenever the owner opens the 顧客 tab.
  useEffect(() => { if (user?.role === "owner") refreshClaims(); }, [user?.role, user?.tenantId, refreshClaims]);
  useEffect(() => { if (tab === "crm") refreshClaims(); }, [tab, refreshClaims]);
  // Inline edit-pet state opened from the calendar's edit-apt sheet.
  const [editPet, setEditPet] = useState(null);
  // Photo lightbox — { url, name? } when shown. Used by the pet avatar
  // in EditApt and (future) any other thumbnail that should zoom on tap.
  const [photoLightbox, setPhotoLightbox] = useState(null);
  // 套票管理 tab list filter (active / expired / spent / refunded / all)
  const [packagesAdminFilter, setPackagesAdminFilter] = useState("active");
  // 儲值卡管理 tab list filter + transaction-view drawer
  const [walletsAdminFilter, setWalletsAdminFilter] = useState("withBalance");
  const [walletTxSheet, setWalletTxSheet] = useState(null); // { customerId }
  // 積分管理 tab list filter + voucher-history drawer
  const [pointsAdminFilter, setPointsAdminFilter] = useState("withPoints");
  const [pointsHistorySheet, setPointsHistorySheet] = useState(null); // { customerId }
  // Owner-side customer package management: list a customer's purchased
  // packages with refund / extend / restore-visit actions.
  const [managePkgSheet, setManagePkgSheet] = useState(null);
  // Opens a sheet showing a customer_packages usage_log (chronological).
  const [pkgUsageSheet, setPkgUsageSheet] = useState(null);
  const [managePkgList, setManagePkgList] = useState([]);
  useEffect(() => {
    if (!managePkgSheet?.customerId) { setManagePkgList([]); return; }
    fetchCustomerPackages(managePkgSheet.customerId, user.tenantId).then(setManagePkgList).catch(() => setManagePkgList([]));
  }, [managePkgSheet?.customerId, user.tenantId]);
  const [topupSheet, setTopupSheet] = useState(null); const [topupAmt, setTopupAmt] = useState("");
  // Sell/grant a multi-visit package to a customer from the CRM detail sheet
  const [sellPackageSheet, setSellPackageSheet] = useState(null); // { customerId, packageId } | null
  const [payrollPeriod, setPayrollPeriod] = useState("month");
  const [search, setSearch] = useState("");
  const [editSvc, setEditSvc] = useState(null);
  const [tierEditOpen, setTierEditOpen] = useState(false); // inline shop-tier (weight range) editor in the price grid
  const [breedEditOpen, setBreedEditOpen] = useState(false); // inline shop special-breed editor in the price grid
  const [bulkOpen, setBulkOpen] = useState(false); // full-screen inline-editable price matrix
  const [tplPickerOpen, setTplPickerOpen] = useState(false); // 套用設定模板 (lib/settingTemplates.js)
  const [overviewHtml, setOverviewHtml] = useState(null); // in-app price overview (iframe) — PWA blocks window.open
  // Print only the price-list iframe (not the whole app). The iframe's Print
  // button posts "petos-print-overview"; we focus that iframe + print it, which
  // prints just its document (the iframe's own @media print drops the toolbar).
  useEffect(() => {
    if (!overviewHtml) return;
    const onMsg = (e) => {
      if (e?.data !== "petos-print-overview") return;
      const ifr = document.querySelector('iframe[title="price-overview"]');
      try { ifr?.contentWindow?.focus(); ifr?.contentWindow?.print(); } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [overviewHtml]);
  // ID of the service currently being dragged in the services list (used
  // to fade the source row and gate dragover so non-active dnd is a no-op).
  const [dragSvcId, setDragSvcId] = useState(null);
  // Owner-customisable order of report cards on the 報表 tab. Persisted
  // per-browser via localStorage. Missing ids fall back to defaults.
  // depositPending → 🔔 通知中心 (owner confirm + dismiss alongside
  // customer claims). stats card → folded into eod's hero so the owner
  // sees ONE anchor revenue number that matches the schedule chip +
  // agenda sums.
  const REPORT_DEFAULT_ORDER = ["monthly", "payMix", "eod", "export", "weekRev", "topSvc", "packages"];
  const [reportOrder, setReportOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("petos:report-order");
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.every(x => typeof x === "string")) {
        const known = arr.filter(id => REPORT_DEFAULT_ORDER.includes(id));
        const missing = REPORT_DEFAULT_ORDER.filter(id => !known.includes(id));
        // New cards go ON TOP so they're seen (a saved order predating a
        // card would otherwise bury it at the bottom forever).
        return [...missing, ...known];
      }
    } catch { /* ignore */ }
    return REPORT_DEFAULT_ORDER;
  });
  const [dragReportId, setDragReportId] = useState(null);
  // 月度銷售 card — month being viewed (YYYY-MM) + tapped-day drilldown.
  const [reportMonth, setReportMonth] = useState(() => dateNow().slice(0, 7));
  const [monthDayPick, setMonthDayPick] = useState(null);
  // Date range for 報表 — drives the 總覽 / 匯出 / 7-日營收 cards. Cards
  // with inherent semantic scope (eod = today, packages = this-month)
  // ignore this range. Stored as { preset, from, to }; "custom" lets the
  // user pick from/to via the date inputs.
  const reportRangePresetDates = (preset) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    // FIX: localDateStr uses LOCAL year/month/day. toISOString()
    // serialises in UTC, so a HKT-evening session would format
    // "today" as yesterday (UTC was still the previous day at 00:00
    // HKT midnight). That's why the picker stuck on the wrong day
    // even after the rollover guard fired.
    const ymd = (d) => localDateStr(d);
    const start = new Date(today);
    const end = new Date(today);
    if (preset === "today") { /* both = today */ }
    else if (preset === "yesterday") { start.setDate(start.getDate()-1); end.setDate(end.getDate()-1); }
    else if (preset === "thisWeek") { const dow = (start.getDay() + 6) % 7; /* Mon=0 */ start.setDate(start.getDate() - dow); }
    else if (preset === "lastWeek") { const dow = (start.getDay() + 6) % 7; start.setDate(start.getDate() - dow - 7); end.setDate(end.getDate() - dow - 1); }
    else if (preset === "thisMonth") { start.setDate(1); }
    else if (preset === "lastMonth") { start.setMonth(start.getMonth() - 1, 1); end.setMonth(end.getMonth(), 0); }
    else if (preset === "thisYear") { start.setMonth(0, 1); }
    else if (preset === "lastYear") { start.setFullYear(start.getFullYear() - 1, 0, 1); end.setFullYear(end.getFullYear() - 1, 11, 31); }
    return { from: ymd(start), to: ymd(end) };
  };
  const [reportRange, setReportRange] = useState(() => {
    try {
      const raw = localStorage.getItem("petos:report-range");
      const obj = raw ? JSON.parse(raw) : null;
      if (obj && obj.preset && obj.from && obj.to) {
        // Recompute dates from "now" whenever the persisted preset is
        // non-custom — otherwise a "昨天" preset set yesterday keeps
        // pointing at the day-before-yesterday after midnight rolls,
        // and the report quietly disagrees with the schedule/agenda
        // that read dateNow() live.
        if (obj.preset !== "custom") {
          return { preset: obj.preset, ...reportRangePresetDates(obj.preset) };
        }
        return obj;
      }
    } catch { /* ignore */ }
    return { preset: "thisMonth", ...reportRangePresetDates("thisMonth") };
  });
  const persistReportRange = (next) => {
    setReportRange(next);
    try { localStorage.setItem("petos:report-range", JSON.stringify(next)); } catch { /* ignore */ }
  };
  const setRangePreset = (preset) => {
    const d = reportRangePresetDates(preset);
    persistReportRange({ preset, ...d });
  };
  // Date rollover guard — when midnight passes the persisted preset's
  // dates go stale (e.g. "今天" still points at yesterday). Re-derive
  // when the tab regains focus or the user opens the analytics tab.
  useEffect(() => {
    const sync = () => {
      if (!reportRange?.preset || reportRange.preset === "custom") return;
      const fresh = reportRangePresetDates(reportRange.preset);
      if (fresh.from !== reportRange.from || fresh.to !== reportRange.to) {
        persistReportRange({ preset: reportRange.preset, ...fresh });
      }
    };
    sync();
    const onVis = () => { if (document.visibilityState === "visible") sync(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", sync);
    };
  }, [reportRange.preset, reportRange.from, reportRange.to]);
  const [editAddon, setEditAddon] = useState(null);
  // Promo + Package admin state — fetched per-tenant on mount of services tab
  const [promoCodes, setPromoCodes] = useState([]);
  const [editPromo, setEditPromo] = useState(null);
  const [servicePackages, setServicePackages] = useState([]);
  const [tenantCustomerPackages, setTenantCustomerPackages] = useState([]);
  const [editPackage, setEditPackage] = useState(null);
  // Auto-prefill the per-size package matrix from the linked (or any priced)
  // service's current size-tier prices × 付費次數, so opening / switching a
  // package to 按體型 shows sensible per-tier prices instead of blanks — no need
  // to tap ⚡. Only fills when the matrix is EMPTY, so it never clobbers manual
  // edits (owner can still re-fill via ⚡). Keyed on open / mode / linked service
  // (NOT 付費次數 — re-filling mid-typing would freeze at the first digit).
  useEffect(() => {
    if (!editPackage || editPackage.pricing?.mode !== "perSize") return;
    const cur = editPackage.pricing?.perSize || {};
    if (Object.values(cur).some(c => Number(c?.shortHair) || Number(c?.longHair))) return;
    const paid = parseInt(editPackage.paidVisits ?? editPackage.visits ?? 0) || 0;
    if (paid <= 0) return;
    const services = tenant?.services || [];
    const hasNZ = (s) => svcTierPrices(s, tenant).some(r => r.shortHair > 0 || r.longHair > 0);
    const ids = editPackage.serviceIds || [];
    const src = services.find(s => ids.includes(s.id) && hasNZ(s)) || services.find(hasNZ);
    if (!src) return;
    const tiers = svcTierPrices(src, tenant);
    if (!tiers.length) return;
    const next = {};
    for (const row of tiers) {
      next[row.label] = { shortHair: row.shortHair * paid, longHair: row.longHair * paid };
    }
    // Also prefill SPECIAL-BREED prices (per tier) from the service's byBreed ×
    // 付費次數, so 泰迪/貴婦 etc. get their own package price out of the box.
    const nextBreed = {};
    for (const sb of svcPricedBreeds(src, tenant)) {
      const m = {};
      for (const r of svcBreedTierPrices(src, sb.id, tenant)) {
        if (r.shortHair > 0 || r.longHair > 0) m[r.label] = { shortHair: r.shortHair * paid, longHair: r.longHair * paid };
      }
      if (Object.keys(m).length) nextBreed[sb.id] = m;
    }
    setEditPackage(f => f ? { ...f, pricing: { ...f.pricing, mode: "perSize", perSize: next, perBreed: Object.keys(nextBreed).length ? nextBreed : (f.pricing?.perBreed || {}) } } : f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPackage?.id, editPackage?.pricing?.mode, (editPackage?.serviceIds || []).join(",")]);
  const [editInv, setEditInv] = useState(null);
  const [settingsSubtab, setSettingsSubtab] = useState("shop-basic");
  const [showReportTemplateEditor, setShowReportTemplateEditor] = useState(false);
  const [settingsSearch, setSettingsSearch] = useState(""); // filter the settings sidebar
  // Keyword synonyms so the settings search finds a subtab by CONCEPT, not just
  // its label (e.g. "合併/merge" → 積分, "錢包/wallet" → 儲值, "收款/deposit" → 付款).
  const SETTINGS_KW = {
    "shop-basic": "店舖 基本 名稱 電話 地址 shop name phone address",
    "directory": "目錄 預約連結 link booking directory boost 公開",
    "shop-brand": "品牌 logo banner 主題 顏色 theme brand image",
    "shop-hours": "營業 時間 hours 排班 schedule",
    "cancel-policy": "取消 預訂 政策 訂金 審批 approval advance cancellation deposit policy",
    "terms": "條款 聲明 免責 terms disclaimer privacy 私隱",
    "shop-policy": "功能 啟用 開關 features wallet loyalty 上門 mobile toggle",
    "payment": "收款 付款 收費 訂金 deposit stripe payme fps 現金 cash 收款方式 payment method 銀行",
    "messaging": "通知 提醒 reminder whatsapp sms email 訊息 自動 auto message notification",
    "events": "事件 活動 event 管理",
    "packages-admin": "套票 次卡 package 儲值卡",
    "wallets-admin": "錢包 儲值 加值 調整 餘額 wallet topup balance adjust",
    "points-admin": "積分 兌換 points redeem",
    "analytics": "分析 報表 收入 sales report eod 對賬 revenue 數據",
    "reviews": "評價 review 星 rating 好評",
    "points": "積分 印花 stamp loyalty 合併 merge combine 獎勵券 voucher reward 儲分 積分規則 rules",
    "language": "語言 language 繁 中文 english 日本語 ja",
    "activity": "活動記錄 log audit 記錄 history 操作",
    "support": "支援 客服 support ticket help 幫助",
    "system": "系統 備份 backup 重設 匯出 export reset danger",
  };
  // Long settings pages are split into tabs (SETTINGS_SECTIONS); the chosen
  // tab is remembered per page for the session.
  const [settingsSec, setSettingsSec] = useState({});
  const secOf = (sub) => {
    const list = settingsSectionsFor(sub, user);
    if (!list) return null;
    const want = settingsSec[sub];
    return list.some(x => x.id === want) ? want : list[0].id;
  };
  const inSec = (sub, id) => settingsSubtab === sub && secOf(sub) === id;
  const settingsMatch = (item) => {
    const q = settingsSearch.trim().toLowerCase();
    if (!q) return true;
    const secKw = (SETTINGS_SECTIONS[item.id] || []).map(x => x.label() + " " + x.kw).join(" ");
    return ((item.label || "") + " " + (SETTINGS_KW[item.id] || "") + " " + secKw + " " + item.id).toLowerCase().includes(q);
  };
  // Inline shop-page preview with tap-to-edit overlays. Lets the owner
  // see what customers see and jump straight to the right settings field
  // by tapping any section, instead of hunting through 設定 tabs.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testBookOpen, setTestBookOpen] = useState(false); // owner "試做客戶預約" sandbox
  const [upgradeConfirm, setUpgradeConfirm] = useState(null); // { key, per, go } — plan-diff sheet before paying
  const [planPicker, setPlanPicker] = useState(false);   // 方案 popup: comparison + upgrade button under each plan
  const [pickPer, setPickPer] = useState("monthly");     // Play: monthly / yearly in the popup
  const [previewTab, setPreviewTab] = useState("overview");
  // "shop-brand#gallery" also opens that tab of the page.
  const jumpToSetting = (target) => {
    const [subtab, sec] = String(target).split("#");
    setSettingsSubtab(subtab);
    if (sec) setSettingsSec(m => ({ ...m, [subtab]: sec }));
    setPreviewOpen(false);
    setTab("settings");
    toast(zhEn("已跳轉到對應設定 →", "Jumped to the matching setting →"), "info");
  };
  const openPlanPage = () => {
    setSettingsSubtab("system");
    setSettingsSec(m => ({ ...m, system: "plan" }));
    setSettingsDrilled(true);
    setTab("settings");
  };
  // Pet-type tab for the service-list page (狗 / 貓 / 小動物). Drives both
  // the filter applied to the displayed list and the default petTypes
  // assigned to new services created via the "+ 新增" button.
  const [servicePetTab, setServicePetTab] = useState("dog");
  // The "店鋪" page is long so it's further split into nested sub-tabs.
  const [shopSection, setShopSection] = useState("basic");
  // Mobile slide-out drawer (hamburger). Holds the secondary actions that
  // don't fit on the bottom nav: staff calendar filter, view mode picker.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  // 詢價 inbox — opened from the header icon. Polls request count every
  // 60s when enabled. Platform-controlled: the superadmin flips
  // platformConfig.rfqEnabled when the RFQ marketplace is ready; until then
  // every entry point (owner button + public/customer buttons) stays hidden.
  const RFQ_INBOX_ENABLED = state.platformConfig?.rfqEnabled === true;
  const [rfqInboxOpen, setRfqInboxOpen] = useState(false);
  const [rfqInboxCount, setRfqInboxCount] = useState(0);
  const canSeeRfqInbox = RFQ_INBOX_ENABLED && (user?.role === "owner" || user?.role === "admin");
  useEffect(() => {
    if (!canSeeRfqInbox) return;
    if (!user?.tenantId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const list = await fetchShopInbox(user.tenantId);
        // Badge counts only un-quoted demands that are relevant to THIS shop —
        // same region or 上門 (cross-region). The full board is still browsable;
        // this just keeps the red count from showing every platform-wide demand.
        if (!cancelled) setRfqInboxCount((list || []).filter(r => !r.alreadyQuoted && (r.regionMatch || r.mobile)).length);
      } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.tenantId]);
  // Before/after photo album sheet — opens after grooming complete or
  // when the owner taps "📸 相片分享" on a done apt. `{ aptId }` shape.
  const [photoShareSheet, setPhotoShareSheet] = useState(null);
  // Responsive flag for settings master-detail layout. Desktop ≥768px shows
  // sidebar + content side-by-side; mobile drills down from menu → content.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) { mq.addEventListener("change", handler); return () => mq.removeEventListener("change", handler); }
    mq.addListener(handler); return () => mq.removeListener(handler);
  }, []);
  // Mobile-only — track whether the user has drilled INTO a subtab. On
  // desktop this stays false because both panes are always visible.
  const [settingsDrilled, setSettingsDrilled] = useState(false);
  // First-login onboarding wizard — gated on the PERSISTED tenant.onboarded
  // flag (was localStorage, so it reappeared on new devices / cleared storage /
  // the native app). Established shops are flagged onboarded → never see it.
  const [onboardDismissed, setOnboardDismissed] = useState(false);
  const _onbTenant = state.tenants.find(t => t.id === user.tenantId);
  const showOnboarding = user?.role === "owner" && !!_onbTenant && _onbTenant.onboarded === false && !onboardDismissed;
  const dismissOnboarding = () => {
    setOnboardDismissed(true);
    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, onboarded: true } : t) }));
  };
  const [globalSearch, setGlobalSearch] = useState(""); const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [noteApt, setNoteApt] = useState(null); const [noteText, setNoteText] = useState("");
  const [noshowConfirm, setNoshowConfirm] = useState(null); // apt being marked no-show
  const [collectSheet, setCollectSheet] = useState(null); // { aptId } — 收款 flow with per-method breakdown
  // ⚡ 一按收款 — single entry point for every 收款 button. When the tenant has
  // quick-collect ON and the customer has NO wallet balance, record the full
  // remaining amount with the pre-chosen method in one tap (shops with a real
  // POS don't want to re-pick the method). WALLET CUSTOMERS ALWAYS GET THE
  // SHEET — a previous "skip the sheet" shortcut silently skipped the wallet
  // debit (topup with no matching deduct in the ledger); the guard here is
  // what makes the shortcut safe. Failure falls back to the sheet.
  const quickCollectBusyRef = useRef(false);
  // Hand an appointment to the POS: PosApp picks it up on mount and puts its
  // balance on the ticket (with the customer), ready for add-ons / retail.
  const sendAptToPos = (aptId) => {
    try { localStorage.setItem("petos:pos:incoming", aptId); localStorage.setItem("petos:pos:tab", "register"); } catch { /* private mode */ }
    setTab("pos");
  };
  const openCollect = async (aptId) => {
    const a = state.appointments.find(x => x.id === aptId);
    if (!a) return;
    const qm = quickCollectMethodOf(tenant);
    // Wallet: membership row is the source of truth; users[] overlay as backup.
    const mem = (state.memberships || []).find(m => m.userId === a.customerId && m.tenantId === user.tenantId);
    const walletBal = Number(mem ? mem.wallet : state.users?.[a.customerId]?.wallet) || 0;
    const remaining = aptRemainingBalance(a);
    if (!qm || walletBal > 0 || remaining <= 0) { setCollectSheet({ aptId }); return; }
    if (quickCollectBusyRef.current) return;   // double-tap guard on a money path
    quickCollectBusyRef.current = true;
    try {
      const entry = {
        id: "pay" + uid(), type: "payment",
        method: qm.presetKey || qm.name,
        methodName: qm.name.replace(/^💵 |💳 |🐙 /, ""),
        amount: remaining, at: new Date().toISOString(),
        collectedBy: user.id, by: user.id, purpose: "balance", note: "⚡ 一按收款",
      };
      const res = await recordPaymentAtomic(a.id, entry);
      setState(st => ({ ...st, appointments: st.appointments.map(x => x.id === a.id
        ? { ...x, paid: !!res?.paid, remainingBalance: Math.max(0, Number(res?.total || 0) - Number(res?.collected || 0)), paymentLog: Array.isArray(res?.log) ? res.log : (x.paymentLog || []) }
        : x) }));
      toast(zhEn("⚡ 已收款 ", "⚡ Collected ") + cur(tenant) + remaining + " · " + entry.methodName + " ✓");
    } catch (e) {
      toast(zhEn("收款失敗：", "Payment failed: ") + (e?.message || e), "error");
      setCollectSheet({ aptId });   // let the owner retry through the sheet
    } finally {
      quickCollectBusyRef.current = false;
    }
  };
  const [newTag, setNewTag] = useState("");
  // Photo lightbox for history-tab thumbnails. Stores the url; null = closed.
  const [aptLightboxPhoto, setAptLightboxPhoto] = useState(null);
  const [calMonth, setCalMonth] = useState(dateNow().slice(0, 7));
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(dateNow());
  const [viewMode, setViewMode] = useState("day");
  // Week view shows ONE staff's whole-week schedule at a time. Defaults
  // to the first active staff; owner cycles via ‹ / › arrows next to
  // the name in the week-view header.
  const [weekStaffId, setWeekStaffId] = useState(null);
  // Schedule zoom — px per hour. Clamped 40..200. Persisted to localStorage
  // so the owner's preference survives reloads. Default 96 (taller than the
  // old 64) so staff can read the pet name + service + addons + total
  // without zooming in every time.
  const [hourHeight, setHourHeightState] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem("petos:schedule-zoom") || "");
      return Number.isFinite(saved) && saved >= 40 && saved <= 200 ? saved : 96;
    } catch { return 96; }
  });
  // Throttle pinch updates via rAF so we don't trigger a full re-render on
  // every touchmove (60Hz × big tree = main-thread jank). And only persist
  // to localStorage when the user lifts their fingers — synchronous writes
  // mid-pinch were a measurable stutter source.
  const pendingZoomRef = useRef(null);
  const zoomRafRef = useRef(0);
  const setHourHeight = (next) => {
    const clamped = Math.max(40, Math.min(200, Math.round(next)));
    pendingZoomRef.current = clamped;
    if (zoomRafRef.current) return;
    zoomRafRef.current = requestAnimationFrame(() => {
      zoomRafRef.current = 0;
      const v = pendingZoomRef.current;
      if (v != null) {
        setHourHeightState(v);
        // Persist on every change (debounced via rAF batching above) so the
        // owner's zoom level survives a refresh even if they didn't reach
        // touch-end (e.g. they scrolled away mid-pinch).
        try { localStorage.setItem("petos:schedule-zoom", String(v)); } catch { /* ignore */ }
      }
    });
  };
  const persistHourHeight = () => {
    try { localStorage.setItem("petos:schedule-zoom", String(hourHeight)); } catch { /* ignore */ }
  };
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); return localDateStr(d);
  });
  // Jump the schedule to a specific day. ALWAYS use this instead of a bare
  // setSelectedScheduleDate when landing on a date the owner didn't pick off
  // the weekday strip: the strip renders from `weekStart`, so without the
  // re-anchor it keeps showing whatever week it was on. Tapping a
  // notification for an 8/12 booking showed the header as 8月12日 週三 while
  // the strip still listed 8/2–8/8 with 8 highlighted.
  // The ‹ › week arrows (shiftWeek) deliberately move weekStart alone —
  // paging weeks isn't picking a day.
  const goToScheduleDate = (ds) => {
    if (!ds) return;
    setSelectedScheduleDate(ds);
    const wd = new Date(ds); wd.setDate(wd.getDate() - wd.getDay());
    setWeekStart(localDateStr(wd));
    setCalMonth(String(ds).slice(0, 7));
  };
  const [hiddenStaff, setHiddenStaffRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem("petos:hidden-staff") || "{}") || {}; } catch { return {}; }
  });
  const setHiddenStaff = (updater) => {
    setHiddenStaffRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try { localStorage.setItem("petos:hidden-staff", JSON.stringify(next || {})); } catch { /* ignore */ }
      return next;
    });
  };
  const touchStartX = useRef(null);
  // Swipe lives on the weekday strip, not the staff-lanes pane — multi-staff
  // calendars need horizontal panning to switch staff columns.
  const weekSwipeX = useRef(null);
  // Day-body horizontal swipe → ±1 day. Tracks both the start touch
  // position AND the inner container's scrollLeft so we can distinguish
  // "user finger-scrolled the staff lanes" from "user swiped to change
  // the day". If scrollLeft moved during the gesture, the inner consumed
  // the touch — don't change date.
  const dayBodySwipe = useRef(null);
  // For pinch-to-zoom on the schedule grid. Snapshots the initial distance
  // between two fingers + the hourHeight at touch-start, then scales
  // proportionally on touch-move.
  const pinchRef = useRef({ startDist: 0, startHeight: 96, active: false });
  // Tick state — bumps every minute so the "current time" line on the
  // schedule grid creeps down throughout the day without needing a manual
  // refresh. The value isn't read; we just rely on the re-render.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    // Tick every 15s so the "now" line creeps smoothly enough for a glanceable
    // sense of motion without hammering React. 60s left a stale line for
    // most of the minute, which felt frozen.
    const id = setInterval(() => setNowTick(t => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);
  // NEW: walk-in booking form
  // Walk-in / appointment form draft — persisted to localStorage so a reload
  // (manual, or the auto-recovery after the app wakes from being backgrounded)
  // NEVER loses a half-entered appointment. The public booking flow already
  // does this via petos:pending-booking; the owner form previously did not,
  // which is why a dead-app reload forced re-entering the whole appointment.
  const _wkDraftKey = "petos:walkin-draft:" + (user?.tenantId || "x");
  const _wkDraft = (() => { try { return JSON.parse(localStorage.getItem(_wkDraftKey) || "null") || null; } catch { return null; } })();
  const [walkInForm, setWalkInForm] = useState(_wkDraft?.form || null);
  // Auto-save bar visibility. It used to sit permanently on the settings tab and
  // block the content. Now it only surfaces while saving / on error / offline,
  // and flashes "✓ 已儲存" for ~2.5s after a save, then disappears.
  const [saveBarShow, setSaveBarShow] = useState(false);
  useEffect(() => {
    const st = syncStatus || { state: "idle" };
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (offline || st.state === "error" || st.state === "saving") { setSaveBarShow(true); return; }
    if (st.state === "saved") { setSaveBarShow(true); const t = setTimeout(() => setSaveBarShow(false), 2500); return () => clearTimeout(t); }
    setSaveBarShow(false);
  }, [syncStatus]);
  // Tell the resume handler (useAppState) NOT to hard-reload while an input-
  // heavy sheet is open, so a long fill never loses the user's in-progress work.
  useEffect(() => {
    try { window.__petosEditingForm = !!walkInForm || testBookOpen || bulkOpen; } catch { /* ignore */ }
    return () => { try { window.__petosEditingForm = false; } catch { /* ignore */ } };
  }, [walkInForm, testBookOpen, bulkOpen]);
  // Slot-kind picker — shown after tapping an empty calendar slot so the
  // owner/staff can choose between adding a customer booking or an
  // internal event (lunch, meeting, time-off block). Holds the prefilled
  // slot info {staffId, date, time} while open; null = hidden.
  const [slotKindPick, setSlotKindPick] = useState(null);
  // Internal event sheet — both create (no id) and edit (with id) flow
  // through the same shape: { id?, staffId, date, time, durationMin,
  // label, staffNote, color }.
  const [eventForm, setEventForm] = useState(null);
  // Event template editor draft — settings 事件管理 page edits one
  // template at a time through this draft state.
  const [eventTplDraft, setEventTplDraft] = useState(null);
  const [editStaff, setEditStaff] = useState(null);
  // Per-staff monthly payslip sheet — { staffId, from, to }
  const [payslipSheet, setPayslipSheet] = useState(null);
  const [dailyWageSheet, setDailyWageSheet] = useState(false);
  // Top-toolbar date label opens a full month picker popover.
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  // Draft buffer for 店鋪資料 + 品牌頁面 settings — owners asked for an
  // explicit Save button so typing in these inputs no longer hits the DB
  // every keystroke. Initialize from `tenant`, diff against `tenant` to
  // know if dirty, commit via setState on Save.
  const SHOP_DRAFT_FIELDS = ["name","address","phone","country","currency","language","theme","logoUrl","bannerUrl","tagline","about","website","instagram","facebook"];
  const [shopDraft, setShopDraft] = useState(null);
  const [sendNotif, setSendNotif] = useState(null);
  const [editSlug, setEditSlug] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [slugErr2, setSlugErr2] = useState("");
  const [editApt, setEditApt] = useState(null);
  // Drawer view-mode tab: "detail" (default) | "history". History tab
  // lists all past visits of this pet; tapping one opens peekHistoryApt
  // as a small modal so the main drawer doesn't lose its place.
  const [aptDetailTab, setAptDetailTab] = useState("detail");
  const [peekHistoryApt, setPeekHistoryApt] = useState(null);
  // Reset tab to detail whenever a different appointment opens.
  useEffect(() => { setAptDetailTab("detail"); setPeekHistoryApt(null); }, [editApt?.id]);
  // Keep the OPEN edit-apt sheet in sync with the server-authoritative fields
  // that OTHER sheets write straight into state.appointments without touching
  // the local editApt draft — above all ReportSheet. Without this, submitting a
  // grooming report (or a status/photo change coming in over realtime) didn't
  // reflect in the open sheet until you closed + reopened the appointment
  // (`reportSubmitted = !!ea.report` read the stale snapshot). Difference-guarded
  // and limited to non-draft fields, so it never clobbers in-progress edits
  // (date/time/staff/note/etc. stay as the user typed them).
  useEffect(() => {
    const id = editApt?.id;
    if (!id) return;
    const live = state.appointments.find(a => a.id === id);
    if (!live) return;
    setEditApt(prev => {
      if (!prev || prev.id !== id) return prev;
      const FIELDS = ["report", "status", "completedAt", "startedAt", "checkedInAt", "paid", "remainingBalance", "paymentLog"];
      let patch = null;
      for (const k of FIELDS) {
        if (prev[k] !== live[k]) { (patch = patch || {})[k] = live[k]; }
      }
      return patch ? { ...prev, ...patch } : prev;
    });
  }, [state.appointments, editApt?.id]);
  const [staffDateRange, setStaffDateRange] = useState("month");
  const [staffDateFrom, setStaffDateFrom] = useState(dateNow().slice(0,7)+"-01");
  const [staffDateTo, setStaffDateTo] = useState(dateNow());
  // Reorder mode for the staff list — the up/down arrows only show when on,
  // so the cards stay clean during everyday use.
  const [staffReorder, setStaffReorder] = useState(false);
  // Staff list view: detailed cards vs a compact scan-friendly table.
  const [staffView, setStaffView] = useState("card");
  // Wage settings sheet (separate from profile editing; gated to paid plan).
  const [wageStaff, setWageStaff] = useState(null);
  // Salary history sheet — { staff, ym } where ym = "YYYY-MM"
  const [wageHistoryStaff, setWageHistoryStaff] = useState(null);
  const [staffScheduleMonth, setStaffScheduleMonth] = useState(dateNow().slice(0,7));
  const [staffScheduleIdx, setStaffScheduleIdx] = useState(0);
  // Roster edit lock — defaults to true so accidental taps on dates don't
  // toggle work days. Owner taps the 🔒 to unlock for the current session.
  const [rosterLocked, setRosterLocked] = useState(true);
  const [walkInSearch, setWalkInSearch] = useState("");
  const [walkInNewCust, setWalkInNewCust] = useState(_wkDraft?.newCust || null);
  const [walkInNewPet, setWalkInNewPet] = useState(_wkDraft?.newPet || null);
  const [walkInDiscount, setWalkInDiscount] = useState(_wkDraft?.discount || 0);
  // Persist the in-progress walk-in form so a reload restores it verbatim.
  // When the form is closed/submitted (walkInForm === null) the draft is
  // cleared, so we never resurrect a stale appointment after a real save.
  useEffect(() => {
    try {
      if (walkInForm) localStorage.setItem(_wkDraftKey, JSON.stringify({ form: walkInForm, newCust: walkInNewCust, newPet: walkInNewPet, discount: walkInDiscount }));
      else localStorage.removeItem(_wkDraftKey);
    } catch { /* ignore quota / private-mode */ }
  }, [walkInForm, walkInNewCust, walkInNewPet, walkInDiscount, _wkDraftKey]);
  const [reweighSheet, setReweighSheet] = useState(null); // { aptId, weight }
  // Free plan: this month's online-booking allowance, shown on the plan page.
  const [ownQuota, setOwnQuota] = useState(null);
  const ownPlanKey = (state.tenants.find(t => t.id === user?.tenantId)?.plan) || "free";   // `tenant` is declared further down
  useEffect(() => {
    if (!user?.tenantId || ownPlanKey !== "free") { setOwnQuota(null); return; }
    let alive = true;
    fetchOnlineBookingQuota(user.tenantId).then(q => { if (alive) setOwnQuota(q); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.tenantId, ownPlanKey, state.appointments.length]);
  const seatKey = ownPlanKey + "|" + JSON.stringify(state.platformConfig?.plans?.[ownPlanKey]?.maxStaff ?? "") + "|"
    + ((state.tenants.find(t => t.id === user?.tenantId)?.staffIds) || []).map(id => id + (state.users[id]?.enabled === false ? "0" : "1") + (state.users[id]?.leaveDate || "")).join(",");
  const staffSeats = useStaffSeats(user?.tenantId, seatKey);
  const [dragOverInfo, setDragOverInfo] = useState(null); // { staffId, top, conflict } for drop preview
  // #16: track dragged appointment so we can predict conflicts during
  // onDragOver (browsers block dataTransfer.getData() outside of drop).
  // ref instead of state so changes don't trigger re-render mid-drag.
  const draggedAptRef = useRef(null);
  // Mobile touch drag — HTML5 drag/drop doesn't fire on touch devices, so we
  // synthesize it with long-press (400ms) + touchmove. The ref holds the
  // in-flight gesture: { aptId, startX, startY, engaged, timer, targetStaff, targetTime }.
  const touchDragRef = useRef(null);
  // 設定 → 收款 — compact list state. payDrag drives the live drag preview;
  // payDragRef holds the same value for the pointerup handler (reading it out
  // of a setState updater would be a side-effect in a reducer).
  const [payDrag, setPayDrag] = useState(null);      // { id, fromIdx, overIdx, dy }
  const payDragRef = useRef(null);
  const [payDetailId, setPayDetailId] = useState(null); // method open in the ⚙️ sheet
  const [waForm, setWaForm] = useState(null); // WhatsApp settings draft (null = not loaded)
  // Whole-form saves (WhatsApp / SMS / Email / 儲存設定) must never write the
  // AI on/off switch. That switch has its own writers — the 訊息 master switch
  // and saveAi's partial patch — and it can now be flipped from the native
  // app too. This form is loaded once, so its copy of the switch goes stale
  // the moment it's changed anywhere else, and sending the whole form used to
  // switch AI back OFF every time an owner saved an unrelated setting.
  // undefined → stripUndef drops the column → the row keeps its real value.
  const waFormForSave = (f) => ({ ...f, waAiEnabled: undefined,
    // The booking-confirmation fields auto-save on change via their own
    // partial patch — same reason as the AI switch: this form is loaded once.
    waConfirmEnabled: undefined, waConfirmTemplate: undefined, waConfirmLang: undefined });
  const [waLoadErr, setWaLoadErr] = useState(null); // non-null = load FAILED (never show a blank form)
  const [waSaving, setWaSaving] = useState(false);
  // Whether the owner has expanded the "進階設定（自訂 API）" panel. Default
  // collapsed — most shops use platform-hosted sending and never touch this.
  const [messagingAdvanced, setMessagingAdvanced] = useState(false);
  const [templateTab, setTemplateTab] = useState("booking_confirmed");
  const [waTesting, setWaTesting] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Load activity logs when tab changes to activity
  useEffect(() => {
    // Activity log used to be its own tab; it's now a settings sub-tab,
    // so the trigger has to fire on either path.
    const inActivityTab = tab === "activity" || (tab === "settings" && settingsSubtab === "activity");
    if (!inActivityTab || !user.tenantId) return;
    setActivityLoading(true);
    fetchActivityLogs(user.tenantId, { limit: 100, offset: 0 })
      .then(logs => setActivityLogs(logs))
      .catch(e => { console.error("[PettoBook] fetch activity logs:", e); toast(zhEn("無法載入活動記錄", "Couldn't load the activity log"), "error"); })
      .finally(() => setActivityLoading(false));
  }, [tab, settingsSubtab, user.tenantId]);

  // Load WhatsApp credentials for this tenant.
  //
  // This used to run ONCE with no retry, and on failure it fell back to blank
  // `defaults`. Two bugs came out of that: (a) if the call never settled — an
  // aged session after resume — waForm stayed null and 設定 → 通訊 sat on
  // 「載入中…」 until the app was force-killed; (b) worse, a REJECTED fetch
  // filled the form with empty strings, so hitting 儲存 would have written
  // blanks over real WhatsApp / Twilio / WATI credentials.
  // Now: a failed load surfaces an error + 重試 (never a blank form, so the
  // save path can't wipe secrets), and it re-arms on visibility/focus.
  const loadWaSecrets = useCallback(() => {
    if (!user.tenantId || user.role !== "owner") return;
    const defaults = {
      whatsappEnabled:false, whatsappPhoneId:"", whatsappAccessToken:"", whatsappFromName:"", whatsappDefaultTemplate:"",
      smsEnabled:false, smsProvider:"twilio", smsAccountSid:"", smsAuthToken:"", smsFromNumber:"",
      emailEnabled:false, emailProvider:"resend", emailApiKey:"", emailFrom:"",
      waAiEnabled:false, waAiBookingEnabled:true, waAiKnowledge:"", waAiInstructions:"", waAiGreeting:"", waAiName:"",
      waAiHours: null, waAiWeatherPolicy: "",
      waProvider:"meta", waTwilioAccountSid:"", waTwilioAuthToken:"", waTwilioFrom:"",
      waChatwootBaseUrl:"", waChatwootApiToken:"", waChatwootAccountId:null, waChatwootInboxId:null, waChatwootHmacSecret:"",
    };
    setWaLoadErr(null);
    // A resolved-but-empty result is a real answer (tenant has no row yet) —
    // defaults are correct there. Only a REJECTION is an unknown state.
    return fetchMessagingSecrets(user.tenantId)
      .then(s => { setWaForm({ ...defaults, ...(s || {}) }); })
      .catch(e => { setWaForm(null); setWaLoadErr(e?.message || String(e)); });
  }, [user.tenantId, user.role]);
  useEffect(() => { loadWaSecrets(); }, [loadWaSecrets]);
  // The AI master switch lives in 訊息 but the row it writes is the same one
  // this form saves wholesale. Mirror the change so a later "save" here cannot
  // clobber it with a value that was already stale when the form loaded.
  useEffect(() => {
    const onAiChanged = (e) => {
      if (!e?.detail || e.detail.tenantId !== user.tenantId) return;
      setWaForm(f => (f == null ? f : { ...f, waAiEnabled: !!e.detail.waAiEnabled }));
    };
    window.addEventListener("petos:wa-ai-changed", onAiChanged);
    return () => window.removeEventListener("petos:wa-ai-changed", onAiChanged);
  }, [user.tenantId]);
  // Re-arm on reopen: if the settings never loaded, retry when the app comes
  // back to the foreground instead of making the owner force-quit.
  useEffect(() => {
    if (!user.tenantId || user.role !== "owner") return;
    const retry = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setWaForm(f => { if (f == null) loadWaSecrets(); return f; });
    };
    document.addEventListener("visibilitychange", retry);
    window.addEventListener("focus", retry);
    return () => { document.removeEventListener("visibilitychange", retry); window.removeEventListener("focus", retry); };
  }, [user.tenantId, user.role, loadWaSecrets]);
  // Placeholder shown by every 通訊 card while waForm is null. Distinguishes
  // "still loading" from "load failed" — the failed case gets a 重試 button
  // so a wedged reopen no longer needs a force-quit.
  const WaLoadState = () => waLoadErr ? (
    <div style={{background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:14,padding:12}}>
      <p style={{fontSize:13,fontWeight:900,color:"#991B1B",margin:"0 0 4px"}}>{T("載入通訊設定失敗","Couldn't load communication settings","通信設定を読み込めませんでした")}</p>
      <p style={{fontSize:11,fontWeight:700,color:"#B91C1C",margin:"0 0 10px",lineHeight:1.5,wordBreak:"break-word"}}>{T("連線可能中斷咗。撳重試，唔使強制關閉 App。","The connection may have dropped. Tap retry — no need to force-quit.","接続が切れた可能性があります。再試行してください。")}<br /><span style={{opacity:0.7}}>{String(waLoadErr).slice(0, 120)}</span></p>
      <Btn size="sm" onClick={loadWaSecrets}>{T("🔄 重試","🔄 Retry","🔄 再試行")}</Btn>
    </div>
  ) : <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{T("載入中…","Loading…","読み込み中…")}</p>;
  useEffect(() => {
    if (!user.tenantId || user.role !== "owner") return;
    // Load promo codes + service packages for the services tab admin UIs.
    // Best-effort — failures only mean the empty-state shows.
    fetchPromoCodes(user.tenantId).then(setPromoCodes).catch(() => setPromoCodes([]));
    fetchServicePackages(user.tenantId).then(setServicePackages).catch(() => setServicePackages([]));
    fetchTenantCustomerPackages(user.tenantId)
      .then(rows => { setTenantCustomerPackages(rows); console.info("[PettoBook] loaded " + (rows?.length || 0) + " customer_packages for tenant"); })
      .catch(err => { console.warn("[PettoBook] fetchTenantCustomerPackages failed:", err); setTenantCustomerPackages([]); });
  }, [user.tenantId, user.role]);
  // Keep shop package DEFINITIONS fresh across devices. `service_packages` is
  // NOT in the realtime subscription and lives in component state (not the
  // polled `state`), so an edit on one device (e.g. 10送2 → 10送1) otherwise
  // stayed stale on another until a manual refresh. Refetch on focus + every 30s
  // while visible (cheap — a single query).
  useEffect(() => {
    if (!user?.tenantId) return;
    const refetch = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      fetchServicePackages(user.tenantId).then(setServicePackages).catch(() => {});
    };
    const iv = setInterval(refetch, 30000);
    const onFocus = () => { if (typeof document === "undefined" || document.visibilityState === "visible") refetch(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("focus", onFocus); };
  }, [user?.tenantId]);

  // Auto-refetch customer packages whenever the owner opens / switches
  // CRM customer or walk-in customer — covers the case where another
  // staff member just sold / consumed a package and our cached list is
  // stale. Cheap query (small table) so we don't bother debouncing.
  useEffect(() => {
    if (!user.tenantId) return;
    if (!crmUser && !walkInForm?.customerId) return;
    fetchTenantCustomerPackages(user.tenantId).then(setTenantCustomerPackages).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmUser, walkInForm?.customerId, user.tenantId]);

  const tenant = state.tenants.find(t => t.id === user.tenantId);
  // Paid plan picked at sign-up but not paid yet (email-confirm path or the
  // Android app): open the 方案 popup once on the first visit.
  useEffect(() => {
    if (user.role !== "owner" || !tenant?.id) return;
    let want = null;
    try { want = localStorage.getItem(SIGNUP_PLAN_KEY); localStorage.removeItem(SIGNUP_PLAN_KEY); } catch { /* private mode */ }
    if (!want || (tenant.plan || "free") !== "free") return;
    openPlanPage();
    setPlanPicker(true);
  }, [tenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Initialize / re-sync the shop-settings draft buffer whenever the tenant
  // ID changes or the draft hasn't been initialized yet. We DON'T overwrite
  // an existing draft on every realtime update — that would clobber the
  // owner's in-flight edits.
  useEffect(() => {
    if (!tenant) return;
    if (shopDraft && shopDraft._tenantId === tenant.id) return;
    const init = { _tenantId: tenant.id };
    for (const k of SHOP_DRAFT_FIELDS) init[k] = tenant[k] ?? "";
    setShopDraft(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);
  const shopDirty = !!(shopDraft && tenant && SHOP_DRAFT_FIELDS.some(k => (shopDraft[k] ?? "") !== (tenant[k] ?? "")));
  const setShopField = (k, v) => setShopDraft(d => ({ ...(d || {}), _tenantId: tenant?.id, [k]: v }));
  const saveShop = () => {
    if (!shopDraft || !tenant) return;
    const patch = {};
    for (const k of SHOP_DRAFT_FIELDS) patch[k] = shopDraft[k] ?? "";
    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, ...patch } : t) }));
    toast(zhEn("店鋪資料已儲存 ✓", "Shop details saved ✓"));
  };
  const discardShop = () => {
    if (!tenant) return;
    const init = { _tenantId: tenant.id };
    for (const k of SHOP_DRAFT_FIELDS) init[k] = tenant[k] ?? "";
    setShopDraft(init);
    toast(zhEn("已放棄修改", "Changes discarded"));
  };
  const plan = PLANS[tenant?.plan || "free"];
  const canView = f => user.role === "owner" || !!(user.permissions?.[f]);
  const todayApts = state.appointments.filter(a => a.tenantId === user.tenantId && a.date === dateNow() && a.kind !== "event");
  const allApts = useMemo(() => state.appointments.filter(a => a.tenantId === user.tenantId), [state.appointments, user.tenantId]);
  const doneApts = allApts.filter(a => a.status === "done" && a.kind !== "event");
  const revenue = todayApts.filter(a => a.status === "done").reduce((s, a) => s + aptRevenue(a), 0);
  const totalRevenue = doneApts.reduce((s, a) => s + aptRevenue(a), 0);
  // Ranged subset for the 報表 tab's overview/export cards. Includes
  // EVERY apt in the date range that hasn't been cancelled / no-show'd —
  // not just `status === "done"`. Reason: owners want the report to
  // reflect what's been earned in real time the moment a booking is
  // accepted (or auto-flips to grooming / done), without waiting for
  // someone to tap 收款. Cancelled + no-show + pending_payment-expired
  // are still excluded since no revenue was generated.
  const rangedApts = useMemo(() => allApts.filter(a => {
    if (!a.date) return false;
    if (a.kind === "event") return false;
    if (a.date < reportRange.from || a.date > reportRange.to) return false;
    if (a.status === "cancelled" || a.status === "noshow") return false;
    if (a.status === "pending_payment" && a.expiresAt && new Date(a.expiresAt).getTime() < Date.now()) return false;
    return true;
  }), [allApts, reportRange.from, reportRange.to]);
  const rangedDoneCount = rangedApts.filter(a => a.status === "done").length;
  const rangedRevenue = rangedApts.reduce((s, a) => s + aptRevenue(a), 0);
  const allStaff = tenant?.staffIds.map(s => state.users[s]).filter(Boolean) || [];
  // Wage management is a paid feature (pro plan: PLANS.pro.features.payroll).
  const payrollOn = !!PLANS[tenant?.plan || "free"]?.features?.payroll;
  const staffList = allStaff.filter(s => s.enabled !== false); // active only for schedule/booking
  // Dedupe customers by normalised phone — same person who first booked as
  // visitor (synthetic profile) and later logged in via Google ends up with
  // two profile rows under one phone. We collapse them in the UI, preferring
  // the entry with a real (non-@petos.local) email so the CRM shows their
  // Google identity.
  const customers = (() => {
    const all = (tenant?.customerIds || []).map(c => state.users[c]).filter(Boolean);
    const byPhone = new Map();
    const normPhone = p => (p || "").replace(/\D+/g, "");
    for (const c of all) {
      const np = normPhone(c.phone);
      if (!np) { byPhone.set(c.id, c); continue; } // no phone → keep distinct by id
      const existing = byPhone.get(np);
      if (!existing) { byPhone.set(np, c); continue; }
      // Prefer the entry with a real (non-synthetic) email
      const cIsReal = c.email && !c.email.endsWith("@petos.local");
      const eIsReal = existing.email && !existing.email.endsWith("@petos.local");
      if (cIsReal && !eIsReal) byPhone.set(np, c);
    }
    return Array.from(byPhone.values());
  })();
  const lowStock = (tenant?.inventory || []).filter(i => i.qty <= i.lowAlert);
  // Customer search now matches name + phone + EMAIL (case-insensitive).
  // Owners often type the username portion of a saved email (e.g. "yuki"
  // for yukiwong33@163.com) instead of remembering the full name — the
  // previous name+phone-only filter returned no result and the customer
  // looked missing.
  const filteredCusts = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter(c =>
      (c.name && c.name.toLowerCase().includes(q))
      || (c.phone && c.phone.includes(search))
      || (c.email && c.email.toLowerCase().includes(q))
      // Owners often remember the PET, not the human — match pet names too.
      || (c.pets || []).some(pid => (state.pets[pid]?.name || "").toLowerCase().includes(q))
    );
  }, [customers, search, state.pets]);

  const globalResults = useMemo(() => {
    if (!globalSearch || globalSearch.length < 2) return [];
    const q = globalSearch.toLowerCase();
    const apts = allApts.filter(a => { const pet = state.pets[a.petId], cust = state.users[a.customerId]; return pet?.name.toLowerCase().includes(q) || cust?.name.toLowerCase().includes(q) || a.service.toLowerCase().includes(q); }).slice(0, 4);
    const custs = customers.filter(c =>
      (c.name && c.name.toLowerCase().includes(q))
      || (c.phone && c.phone.includes(q))
      || (c.email && c.email.toLowerCase().includes(q))
    ).slice(0, 3);
    return [...custs.map(c => ({ type:"customer", data:c })), ...apts.map(a => ({ type:"apt", data:a }))];
  }, [globalSearch, allApts, customers, state.pets, state.users]);

  // Approval mode (cp.requireApproval): accept flips approval_status →
  // 'approved' + notifies the customer; reject routes through the normal
  // cancel path (deposit refund + package-visit restore + cancel notify all
  // reuse the existing machinery) after stamping the reason.
  const approveAppointment = async (a) => {
    try {
      await upsertAppointment({ ...a, approvalStatus: "approved" });
      setState(s => ({ ...s, appointments: s.appointments.map(x => x.id === a.id ? { ...x, approvalStatus: "approved" } : x) }));
      const pet = state.pets[a.petId];
      pushNotif(setState, { tenantId: a.tenantId, userId: a.customerId, type: "booking_confirmed", title: "預約已確認 ✅", body: (pet?.name || "毛孩") + " 的預約已獲店家確認：" + a.date + " " + a.time, read: false, aptId: a.id });
      toast(zhEn("已接受預約 ✅", "Booking accepted ✅"));
    } catch (e) { toast(zhEn("接受失敗：", "Couldn't accept: ") + (e?.message || e), "error"); }
  };
  const rejectAppointment = async (a) => {
    const reason = window.prompt(zhEn("拒絕原因（會通知客戶）", "Reason for declining (the customer will be notified)"), zhEn("抱歉，此時段未能安排", "Sorry, we can't fit this time slot"));
    if (reason === null) return;
    try {
      // Cancel + reason in ONE state change so dataSync writes a single
      // cancelled row. Previously we upserted the still-pending row first for
      // the reason, whose realtime echo arrived ~1s later and resurrected the
      // appointment on the calendar.
      await updateStatus(a.id, "cancelled", { cancelReason: reason });
    } catch (e) { toast(zhEn("拒絕失敗：", "Couldn't decline: ") + (e?.message || e), "error"); }
  };
  const updateStatus = async (id, status, extraPatch = {}) => {
    const nowIso = new Date().toISOString();
    const apt = allApts.find(a => a.id === id);
    // Idempotency: if the user accidentally taps the same status they're
    // already in, no-op silently. Without this, tapping "checkedin" twice
    // (which is common — fat fingers on iPad) would re-fire the customer
    // notification and re-stamp checkedInAt.
    if (apt && apt.status === status) {
      const lbl = i18nT("status." + status, status);
      toast(i18nT("lang.name") === "English" ? ("Already \"" + lbl + "\"") : (zhEn("狀態已是「", "Already \"") + lbl + zhEn("」", "\"")), "info");
      return;
    }
    // OWNER-SIDE CANCEL: route through the same refund RPC the customer
    // path uses so wallet/points/voucher are restored. Previously owner
    // cancellation forfeited the entire deposit even when policy said
    // "100% refund inside the window".
    if (status === "cancelled" && apt && apt.deposit > 0 && !apt.refundedAt) {
      const isFree = canCancelFree(apt, tenant);
      const policy = tenant?.cancelPolicy;
      const rawFee = !isFree && policy ? Math.round((apt.deposit || 0) * (policy.lateCancelFee || 0)) : 0;
      const lateFee = Math.max(0, Math.min(apt.deposit || 0, rawFee));
      try {
        const res = await refundCancellation({ aptId: apt.id, lateFee, reason: "owner_cancelled" });
        const w = Number(res?.walletRefund) || 0;
        const p = Number(res?.pointsRefund) || 0;
        setState(s => ({
          ...s,
          appointments: s.appointments.map(a => {
            if (a.id !== apt.id) return a;
            const refundEntry = (w + p) > 0 ? {
              id: "refund" + uid(),
              at: new Date().toISOString(),
              amount: -(w + p),
              method: "refund",
              methodName: zhEn("退款", "Refund"),
              by: user.id,
              type: "refund",
              note: lateFee > 0 ? ("店家取消（扣手續費 $" + lateFee + "）") : "店家取消退款",
            } : null;
            return { ...a, status: "cancelled", cancelReason: "owner_cancelled", ...extraPatch, deposit: lateFee, depositConfirmedAt: lateFee > 0 ? a.depositConfirmedAt : null, refundedAt: new Date().toISOString(), refundAmount: w + p, paymentLog: refundEntry ? [...(a.paymentLog || []), refundEntry] : (a.paymentLog || []) };
          }),
          memberships: (s.memberships || []).map(m =>
            (m.userId === apt.customerId && m.tenantId === apt.tenantId)
              ? { ...m, wallet: (Number(m.wallet) || 0) + w, points: (Number(m.points) || 0) + p }
              : m
          ),
        }));
        if (w + p > 0) toast(zhEn("已取消並退還：", "Cancelled and refunded: ") + (w > 0 ? "$" + w : "") + (w > 0 && p > 0 ? " + " : "") + (p > 0 ? p + zhEn(" 積分", " points") : ""), "warning");
        else toast(zhEn("預約已取消", "Booking cancelled"), "warning");
        return;
      } catch (e) {
        console.warn("[PettoBook] owner-side refund failed, falling back to status-only cancel:", e);
      }
    }
    setState(s => ({ ...s, appointments: s.appointments.map(a => {
      if (a.id !== id) return a;
      const patch = { status, ...extraPatch };
      if (status === "grooming" && !a.startedAt) patch.startedAt = nowIso;
      if (status === "done" && !a.completedAt)   patch.completedAt = nowIso;
      if (status === "checkedin" && !a.checkedInAt) patch.checkedInAt = nowIso;
      // Reverting the status — clear timestamps from steps AFTER the new
      // status to honour the "會清除之後的時間戳" promise in the revert
      // confirm dialog. Without this the apt kept a stale completedAt
      // after stepping back to pending, breaking dwell-time analytics
      // and EOD report reconciliation (a "pending" apt with completedAt
      // is logically impossible).
      const order = { pending: 0, checkedin: 1, grooming: 2, done: 3 };
      const newRank = order[status];
      if (typeof newRank === "number") {
        if (newRank < 3 && a.completedAt) patch.completedAt = null;
        if (newRank < 2 && a.startedAt)   patch.startedAt   = null;
        if (newRank < 1 && a.checkedInAt) patch.checkedInAt = null;
      }
      return { ...a, ...patch };
    }) }));
    if (apt) {
      const statusLabels = { pending:i18nT("status.pending"), checkedin:i18nT("status.checkedin"), grooming:i18nT("status.grooming"), done:i18nT("status.done"), cancelled:i18nT("status.cancelled"), noshow:i18nT("status.noshow") };
      logActivity({
        tenantId: user.tenantId,
        userId: user.id,
        actionType: "appointment_" + status,
        entityType: "appointment",
        entityId: id,
        entityName: (state.users[apt.customerId]?.name||"") + " - " + (state.pets[apt.petId]?.name||""),
        description: "更新預約狀態為：" + (statusLabels[status] || status),
        severity: status === "noshow" ? "warning" : "info",
        details: { previousStatus: apt.status, newStatus: status, appointment: apt }
      }).catch(e => console.error("[PettoBook] log activity:", e));
    }
    // Insert a customer notification for any meaningful status change. The
    // notifications-insert DB trigger (trg_push_on_notification) fans the
    // row out as a Web Push automatically, so no separate sendPush call is
    // needed here — the customer gets the OS-level alert even if their
    // browser is closed.
    if (apt) {
      const petName = state.pets[apt.petId]?.name || "";
      const notifByStatus = {
        checkedin: { title: "🛁 已登到", body: petName + " 已到店，準備開始洗浴" },
        grooming:  { title: "✂️ 美容開始", body: petName + " 的美容開始了！" },
        done:      { title: "🎉 美容完成", body: petName + " 已完成美容，歡迎到店接回" },
        cancelled: { title: "❌ 預約已取消", body: petName + " 的預約已被取消" },
        noshow:    { title: "⚠️ 預約未到店", body: petName + " 的預約已標記為未到店" },
      };
      const n = notifByStatus[status];
      if (n && apt.customerId) {
        pushNotif(setState, { tenantId: apt.tenantId, userId: apt.customerId, type: status === "done" ? "grooming_done" : "schedule_reminder", title: n.title, body: n.body, read: false, aptId: id });
      }
      // Tactile confirmation for the groomer — without this, the tap feels
      // like nothing happened on an iPad. 30ms vibrate is barely perceptible
      // but enough to register; toast is the visual confirmation.
      try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(30); } catch { /* not supported on iOS Safari */ }
      const successToast = {
        checkedin: "✅ " + petName + zhEn(" 已登到", " checked in"),
        grooming:  "✂️ " + petName + zhEn(" 開始美容", " grooming started"),
        done:      "🎉 " + petName + zhEn(" 美容完成", " grooming finished"),
        cancelled: zhEn("已取消預約", "Booking cancelled"),
        noshow:    zhEn("已標記為未到店", "Marked as no-show"),
      }[status];
      if (successToast) toast(successToast);
    }
    // ── Eager-redeem migration (2026-06-11) ────────────────────────────
    // New flow: package visit deducts the moment the apt is INSERTED
    // (via trg_apt_redeem_on_insert) or its customer_package_id is
    // LINKED via UPDATE (via trg_apt_redeem_on_link_update). Cancel /
    // noshow restores via the existing trg_apt_revoke_on_cancel path.
    //
    // The block below remains as a SAFETY NET for two cases:
    //   1. Legacy apts inserted BEFORE the trigger existed — done-flip
    //      retroactively deducts them so reports stay consistent.
    //   2. FIFO fallback when the owner forgot to pick a package at
    //      booking but the customer has an eligible one — done-flip
    //      auto-picks the earliest-expiring eligible package.
    // Both paths route through redeem_package_atomic which is dedup-by-
    // aptId in usage_log, so the trigger + this fallback can never
    // double-deduct.
    // The follow-ups below (package redeem, loyalty award / revoke) are 1–3
    // network round-trips. They used to be awaited here, so the progress bar
    // and the report sheet — which wait for this function to return — sat
    // frozen for seconds on a slow Android connection after tapping 完成. The
    // status is already saved and shown above; the follow-ups run on their own
    // (each is idempotent on the booking id and keeps its own try/catch).
    void (async () => {
    if (status === "done" && apt) {
      let cpId = apt.customerPackageId || null;
      if (!cpId && apt.customerId) {
        try {
          const cps = await fetchCustomerPackages(apt.customerId, user.tenantId);
          const eligible = cps
            .filter(cp => cp.enabled && cp.remainingVisits > 0)
            .filter(cp => !cp.expiresAt || new Date(cp.expiresAt).getTime() > Date.now())
            // Pet binding — only auto-pick packages bound to THIS pet
            // (or unbound legacy ones). Without this the FIFO fallback
            // could quietly drain a package bought for the customer's
            // OTHER pet.
            .filter(cp => !cp.petId || cp.petId === apt.petId)
            .filter(cp => {
              const def = servicePackages.find(p => p.id === cp.packageId);
              const sids = def?.serviceIds || [];
              return sids.length === 0 || sids.includes(apt.svcId);
            })
            .sort((a, b) => (a.expiresAt || "9999").localeCompare(b.expiresAt || "9999"));
          if (eligible[0]) cpId = eligible[0].id;
        } catch { /* best-effort */ }
      }
      if (cpId) {
        try {
          const res = await redeemCustomerPackage(cpId, { appointmentId: apt.id });
          // Only toast the deduction when it actually happened. RPC
          // returns { dedup: true } when the apt already redeemed at
          // INSERT time — silent no-op so the owner doesn't see a
          // confusing "扣減成功" toast for a package that was already
          // deducted minutes ago.
          if (!res?.dedup) toast(zhEn("已扣減套票 1 次 📦", "1 package visit deducted 📦"));
          // H1: when the FIFO fallback chose the package (apt had no
          // customerPackageId going into "done"), persist the link
          // back onto the apt — also triggers trg_apt_redeem_on_link_update
          // for any case where the trigger missed it.
          if (!apt.customerPackageId) {
            setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === apt.id ? { ...a, customerPackageId: cpId } : a) }));
            setEditApt(prev => prev?.id === apt.id ? { ...prev, customerPackageId: cpId } : prev);
          }
        } catch (e) { console.warn("[PettoBook] package deduct failed:", e); }
      }

      // ── Loyalty awarding (stamps + 1% points + milestone vouchers) ─────
      // The old inline read-modify-write was racy across staff devices and
      // also never awarded the cash-back points it promised on the
      // booking-success page. The atomic RPC `award_loyalty_for_apt` runs
      // in a single SECURITY DEFINER TX, dedupes via apt.stamp_awarded_at
      // and apt.points_awarded_at, and mirrors milestone vouchers back to
      // customer_memberships.vouchers in the same write.
      // Loyalty earn scope (shop setting): "online" (default) only rewards the
      // customer's own online self-bookings — walk-ins (source "owner") earn
      // nothing, which keeps rewards an incentive to self-book. "all" rewards
      // every completed visit.
      const earnScope = tenant?.loyaltyEarnScope || "online";
      const isWalkin = apt.source === "owner";
      const earnsLoyalty = earnScope === "all" || !isWalkin;
      if (apt.customerId && earnsLoyalty) {
        try {
          const res = await awardLoyaltyForApt(apt.id);
          if (res && (res.stampAwarded || res.pointsAwarded > 0) && typeof res.newPoints === "number") {
            setState(s => ({
              ...s,
              memberships: (s.memberships || []).map(m =>
                (m.userId === apt.customerId && m.tenantId === user.tenantId)
                  ? { ...m, points: res.newPoints, vouchers: res.newVouchers || m.vouchers }
                  : m
              ),
              users: s.users[apt.customerId]
                ? { ...s.users, [apt.customerId]: { ...s.users[apt.customerId], points: res.newPoints } }
                : s.users,
            }));
            const earnedVoucherCount = Array.isArray(res.newVouchers)
              ? res.newVouchers.filter(v => v.sourceAptId === apt.id).length
              : 0;
            if (earnedVoucherCount > 0) {
              pushNotif(setState, {
                tenantId: user.tenantId, userId: apt.customerId, type: "system",
                title: "🎁 印花卡獎勵", body: "恭喜獲得印花卡獎勵券！", read: false,
              });
            }
          }
        } catch (e) { console.warn("[PettoBook] awardLoyaltyForApt failed:", e); }
      }
    }
    // Reverse a previous deduction if the apt is moved away from done.
    if (apt && apt.status === "done" && status !== "done") {
      if (apt.customerPackageId) {
        try { await restoreCustomerPackageVisit(apt.customerPackageId, { appointmentId: apt.id }); } catch (e) { console.warn(e); }
      }
      // Revoke stamp + cash-back points + uncirculated stamp vouchers.
      try { await revokeLoyaltyForApt(apt.id); }
      catch (e) { console.warn("[PettoBook] revokeLoyaltyForApt failed:", e); }
    }
    })().catch(e => console.warn("[PettoBook] status follow-ups failed:", e));
    toast(status === "grooming" ? zhEn("美容已開始", "Grooming started") : zhEn("狀態已更新", "Status updated"));
  };
  // Owner-side wallet top-up. Previous impl only mutated state.users —
  // dataSync deliberately strips wallet/points/walletTx from the profile
  // diff (security hardening 2026-05-23), so the credit never reached DB.
  // Fix: explicit await patchCustomerMembership (RLS allows owners) +
  // mirror canonical numbers to local state for instant UI feedback.
  const doTopup = async cid => {
    const amt = parseInt(topupAmt);
    if (!amt || amt <= 0) { toast(i18nT("val.enterAmount"), "error"); return; }
    const cu = state.users[cid];
    if (!cu) { toast(zhEn("找不到客戶", "Customer not found"), "error"); return; }
    // Atomic credit via RPC. Previously patchCustomerMembership({ wallet:
    // newAbsolute }) computed from closure-captured state.users[cid].wallet,
    // which would clobber any debit that landed between local snapshot
    // and the PATCH. credit_customer_wallet does the increment + tx
    // append in a single SQL statement.
    // 儲值送 bonus: if the amount reaches a configured tier, credit amount +
    // bonus atomically, logging the bonus as its own wallet_tx line.
    const bonus = walletBonusFor(amt, tenant);
    const total = amt + bonus;
    const topupTx = { id: "tx" + uid(), type: "topup", amount: amt, note: "店家手動加值", date: dateNow() };
    const bonusTx = bonus > 0 ? { id: "tx" + uid(), type: "bonus", amount: bonus, note: "儲值優惠 送 $" + bonus, date: dateNow() } : null;
    const txs = bonusTx ? [topupTx, bonusTx] : [topupTx];
    let newWallet;
    try {
      const res = await creditCustomerWallet({ userId: cid, tenantId: user.tenantId, amount: total, tx: txs });
      newWallet = res?.newWallet;
    } catch (e) {
      toast(i18nT("tx.topupFailed") + zhEn("：", ": ") + (e?.message || e), "error");
      return;
    }
    // Reconcile local snapshot with the authoritative server value. If
    // the server returned the new wallet, use it; otherwise optimistic
    // = local + total (best effort, realtime will correct any drift).
    setState(s => {
      const liveCu = s.users?.[cid];
      const finalWallet = typeof newWallet === "number" ? newWallet : ((Number(liveCu?.wallet) || 0) + total);
      const liveTxList = Array.isArray(liveCu?.walletTx) ? liveCu.walletTx : [];
      const finalTxList = [...liveTxList, ...txs];
      return {
        ...s,
        users: liveCu ? { ...s.users, [cid]: { ...liveCu, wallet: finalWallet, walletTx: finalTxList } } : s.users,
        memberships: (s.memberships || []).map(m =>
          (m.userId === cid && m.tenantId === user.tenantId)
            ? { ...m, wallet: finalWallet, walletTx: finalTxList }
            : m
        ),
      };
    });
    toast(zhEn("已為 ", "Topped up ") + (cu.name || "") + zhEn(" 加值 $", " $") + amt + (bonus > 0 ? zhEn("（送 $", " (bonus $") + bonus + zhEn(" = 共 $", " = total $") + total + zhEn("）", ")") : ""));
    setTopupAmt("");
    setTopupSheet(null);
  };
  // Clock-in / clock-out feature removed per owner feedback. Part-time hours
  // are now entered manually in the payslip / 員工 card.
  const saveNote = () => {
    const apt = allApts.find(a => a.id === noteApt);
    setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === noteApt ? { ...a, staffNote:noteText } : a) }));
    if (apt) {
      logActivity({
        tenantId: user.tenantId,
        userId: user.id,
        actionType: "remark_added",
        entityType: "appointment",
        entityId: noteApt,
        entityName: (state.users[apt.customerId]?.name||"") + " - " + (state.pets[apt.petId]?.name||""),
        description: "添加/更新備注：" + noteText.substring(0, 50),
        severity: "info",
        details: { remark: noteText, appointment: apt }
      }).catch(e => console.error("[PettoBook] log activity:", e));
    }
    toast(i18nT("tx.noteSaved"));
    setNoteApt(null);
  };
  const addTag = cid => { if (!newTag.trim()) return; setState(s => ({ ...s, users:{ ...s.users, [cid]:{ ...s.users[cid], tags:[...(s.users[cid].tags||[]), newTag.trim()] } } })); toast(zhEn("已新增標籤", "Tag added")); setNewTag(""); };
  const removeTag = (cid, tag) => setState(s => ({ ...s, users:{ ...s.users, [cid]:{ ...s.users[cid], tags:s.users[cid].tags.filter(t => t !== tag) } } }));

  // Save edits to a customer's name / phone / contact email — patches the
  // profile via dataSync. The email field is a CONTACT email (for receipts /
  // marketing), distinct from the synthetic auth.users.email a visitor was
  // assigned at signup. Overwriting profile.email doesn't break their phone-
  // based login (Supabase auth uses auth.users.email which we don't touch).
  const saveCustomerEdit = async () => {
    const f = editCustForm; if (!f) return;
    if (!f.name?.trim()) { toast(i18nT("val.enterPetName"), "error"); return; }
    const email = f.email?.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast(i18nT("tx.emailInvalid"), "error"); return; }
    const next = {
      name: f.name.trim(),
      phone: f.phone?.trim() || "",
      countryCode: f.countryCode || "+852",
      phone2: f.phone2?.trim() || "",
      phone2CountryCode: f.phone2CountryCode || "+852",
      address: f.address?.trim() || "",
    };
    // Owner-scoped SECURITY DEFINER RPC: works for legacy customers with no
    // membership row (old patchProfile path hit RLS → 0 rows → "權限不足"),
    // auto-links them to the shop, and still surfaces a clear toast on the
    // unique-phone-per-shop collision (23505).
    try {
      await ownerUpdateCustomer({
        userId: f.id, tenantId: user.tenantId,
        name: next.name, phone: next.phone, countryCode: next.countryCode,
        phone2: next.phone2, phone2CountryCode: next.phone2CountryCode, address: next.address,
      });
    } catch (e) {
      const msg = e?.message || String(e);
      if (/unique|duplicate|23505/i.test(msg) && /phone/i.test(msg)) {
        toast(zhEn("此電話號碼已被本店其他客戶使用", "This phone number is already used by another customer of this shop"), "error");
      } else {
        toast(msg || zhEn("儲存失敗", "Save failed"), "error");
      }
      return;
    }
    setState(s => ({ ...s, users:{ ...s.users, [f.id]:{
      ...s.users[f.id],
      ...next,
      email: email || s.users[f.id]?.email || "",
    } } }));
    toast(zhEn("已更新客戶資料 ✓", "Customer details updated ✓"));
    setEditCustForm(null);
  };

  // Soft-delete: mark profile disabled and remove from tenant.customerIds.
  // Their appointment history stays for analytics; they just can't log in or
  // appear in the active customer list. Real auth-user deletion needs an
  // admin edge function, which we can wire up later if needed.
  // Soft-disable: keeps the row but flags it disabled and removes from
  // customerIds. Reversible. Used for "I might want them back later."
  const disableCustomer = (cid) => {
    const c = state.users[cid];
    if (!c) return;
    if (!confirm(zhEn(`確定要停用客戶「${c.name}」？
預約紀錄保留，但客戶無法再登入或預約（可隨時恢復）。`, `Deactivate customer "${c.name}"?
Their bookings are kept, but they can no longer log in or book (you can restore them any time).`))) return;
    setState(s => ({
      ...s,
      users: { ...s.users, [cid]: { ...s.users[cid], enabled: false } },
      tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, customerIds: (t.customerIds||[]).filter(id => id !== cid) } : t),
    }));
    toast(zhEn("已停用客戶", "Customer deactivated"));
    setCrmUser(null);
  };

  // Hard-delete: removes the profile row entirely (and via cascade, any
  // pets / wallet records linked solely to them). Use for confirmed
  // duplicates or genuine cleanup. Appointments are kept because their
  // FK to profiles is ON DELETE SET NULL on customer_id — staff can still
  // see historical bookings, just without the linked profile.
  const removeCustomer = async (cid) => {
    const c = state.users[cid];
    if (!c) return;
    const confirmText = window.prompt(
      zhEn(`永久刪除客戶「${c.name}」？

`, `Permanently delete customer "${c.name}"?

`) +
      zhEn(`會刪除：客戶資料、毛孩、錢包紀錄、auth 帳戶（emails 釋放）
`, `Deleted: customer details, pets, wallet history, the login account (the email is freed)
`) +
      zhEn(`會保留：所有預約紀錄（但會顯示為「已離開的客戶」）

`, `Kept: all booking records (shown as "departed customer")

`) +
      zhEn(`輸入 DELETE 以確認：`, `Type DELETE to confirm:`)
    );
    if (confirmText !== "DELETE") { toast(zhEn("已取消", "Cancelled")); return; }
    try {
      // Use the delete-customer-account Edge Function so the auth.users
      // row goes too — otherwise the email stays locked and the customer
      // can never re-register / Google-login with that address again.
      const { deleteCustomerAccount } = await import("./lib/api.js");
      await deleteCustomerAccount(cid);
      setState(s => {
        const newUsers = { ...s.users };
        delete newUsers[cid];
        return {
          ...s,
          users: newUsers,
          tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, customerIds: (t.customerIds || []).filter(id => id !== cid) } : t),
        };
      });
      toast(zhEn("客戶已刪除", "Customer deleted"));
      setCrmUser(null);
    } catch (e) {
      toast(e?.message || zhEn("刪除失敗", "Delete failed"), "error");
    }
  };

  // Find the next available 15-min-aligned start time for `staffId` on
  // `date` after `afterMin` (minutes since midnight). Walks forward in
  // 15-min steps, checking each candidate against existing appointments
  // for that staff. `myDur` is the duration we need to fit. Returns
  // "HH:MM" or null if no slot fits before midnight.
  const nextAvailableSlot = (staffId, date, afterMin, myDur) => {
    const conflicts = (state.appointments || []).filter(a => a.staffId === staffId && a.date === date && !isAptDead(a));
    const stepStart = Math.ceil(afterMin / 15) * 15;
    for (let m = stepStart; m + myDur <= 24 * 60; m += 15) {
      const clash = conflicts.some(a => {
        const [oh, om] = (a.time || "00:00").split(":").map(Number);
        const oStart = oh * 60 + om;
        const oDur = getAptDuration(a, tenant);
        return m < (oStart + oDur) && (m + myDur) > oStart;
      });
      if (!clash) return String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0");
    }
    return null;
  };

  // NEW: submit walk-in appointment (handles new customer/pet creation, addons, discount, conflict check)
  // opts.bookAnother: keep the form open after save, advance `time` to
  // the next available slot for the same staff so the owner can register
  // a second walk-in (eg. same family bringing two pets) without
  // re-entering customer/svc/staff.
  // Same double-submit protection as the customer booking flow: each walk-in
  // mints a fresh uuid(), so a second tap while the write is in flight books
  // the pet twice. Ref is the real guard (synchronous); state drives the UI.
  const walkInBusyRef = useRef(false);
  const [walkInBusy, setWalkInBusy] = useState(false);
  const submitWalkIn = async (initialStatus = "pending", opts = {}) => {
    if (walkInBusyRef.current) return;
    walkInBusyRef.current = true;
    setWalkInBusy(true);
    try {
      return await submitWalkInInner(initialStatus, opts);
    } finally {
      walkInBusyRef.current = false;
      setWalkInBusy(false);
    }
  };
  const submitWalkInInner = async (initialStatus = "pending", opts = {}) => {
    if (!walkInForm) return;
    let { customerId, petId, svcId, staffId, time, addons=[], spaIds=[], discountAmount=0, discountType="percent", discountReason="", staffRemark="" } = walkInForm;

    // ── VALIDATE EVERYTHING FIRST (no side effects) ──────────────────────
    // Previously the new customer was CREATED before the pet/service/time
    // checks ran — so missing pet info left a created customer with no pet,
    // forcing the owner to re-enter everything. Validate all required fields
    // up front; only create the customer once the whole form is valid.
    if (!customerId && !walkInNewCust) { toast(i18nT("val.pickCustomer"), "error"); return; }
    let _newCustCheck = null;
    if (!customerId && walkInNewCust) {
      if (!walkInNewCust.name?.trim()) { toast(i18nT("val.enterNewCustName"), "error"); return; }
      const cc = walkInNewCust.countryCode || "+852";
      _newCustCheck = validatePhone(cc, walkInNewCust.phone || "");
      if (!_newCustCheck.valid) { toast(_newCustCheck.error, "error"); return; }
    }
    // Pet
    if (!petId && walkInNewPet) {
      if (!walkInNewPet.name || !walkInNewPet.breed || !walkInNewPet.weight) { toast(i18nT("val.fillPetInfo"), "error"); return; }
    }
    if (!petId && !walkInNewPet) { toast(i18nT("val.pickPet"), "error"); return; }
    // Service / staff / time
    if (!svcId) { toast(i18nT("val.pickService"), "error"); return; }
    if (!staffId) { toast(i18nT("val.pickStaff"), "error"); return; }
    if (!time) { toast(i18nT("val.pickTime"), "error"); return; }

    // ── SIDE EFFECTS: create the customer + assign a new pet id ───────────
    if (!customerId && walkInNewCust) {
      const cleanPhone = normalizePhoneDigits(walkInNewCust.phone);
      // Find-or-link: if this phone already belongs to a customer, reuse them
      // instead of blocking (the old "此電話已存在" dead-end).
      const existingCust = customers.find(c => normalizePhoneDigits(c.phone) === cleanPhone);
      if (existingCust) {
        customerId = existingCust.id;
        toast(zhEn("已連結現有客戶：", "Linked to existing customer: ") + (existingCust.name || cleanPhone), "info");
      } else {
        try {
          const res = await createWalkinCustomer({
            tenantId: user.tenantId,
            tenantSlug: tenant?.bookingSlug,
            name: walkInNewCust.name.trim(),
            phone: cleanPhone,
          });
          if (!res || !res.userId) {
            console.error("[PettoBook] createWalkinCustomer returned invalid response:", res);
            toast(zhEn("建立新客戶失敗：伺服器回應異常", "Couldn't create the customer: unexpected server reply"), "error");
            return;
          }
          customerId = res.userId;
        } catch (e) {
          console.error("[PettoBook] createWalkinCustomer error:", e);
          toast(e?.message || zhEn("建立新客戶失敗", "Couldn't create the customer"), "error");
          return;
        }
      }
    }
    if (!customerId) { toast(i18nT("val.pickCustomer"), "error"); return; }

    // Block-guard — refuse if this customer is blocked at this shop. The
    // server trigger trg_block_apt_insert_if_blocked will reject too, but
    // checking client-side gives a friendly toast instead of a raw error.
    const blockedMem = (state.memberships || []).find(m => m.userId === customerId && m.tenantId === user.tenantId);
    if (blockedMem?.blockedAt) {
      toast(zhEn("此客戶已被封鎖，無法建立預約。請先到 顧客 → 解除封鎖。", "This customer is blocked, so a booking can't be created. Unblock them first in Customers."), "error");
      return;
    }

    // New pet → assign id now (validation already passed above).
    if (!petId && walkInNewPet) petId = uuid();
    if (!petId) { toast(i18nT("val.pickPet"), "error"); return; }
    if (walkInNewPet) { const bErr = petBirthdayError(walkInNewPet.birthday); if (bErr) { toast(bErr, "error"); return; } }

    const svc = (tenant?.services||[]).find(s => s.id === svcId);
    if (!svc) { toast(zhEn("選擇的服務不存在", "The chosen service doesn't exist"), "error"); return; }
    const staff = state.users[staffId];
    if (!staff) { toast(zhEn("選擇的美容師不存在", "The chosen groomer doesn't exist"), "error"); return; }
    // Use the date carried on the walk-in form (set when an empty
    // calendar slot was tapped). Falls back to today for the "+"
    // button paths which don't carry a date.
    const today = walkInForm.date || dateNow();

    // Build pet object (existing or new) for pricing
    const petForPricing = walkInNewPet && !state.pets[petId] ? {
      id:petId, tenantId:user.tenantId, ownerId:customerId, name:walkInNewPet.name, breed:walkInNewPet.breed,
      breedId: walkInNewPet.breedId || null,
      weight:parseFloat(walkInNewPet.weight)||3, type:walkInNewPet.type||"dog",
      coatLength:walkInNewPet.coatLength||"short", birthday: walkInNewPet.birthday || null,
      allergies:walkInNewPet.allergies||zhEn("無", "None"), vaccineDate:"", vetNote:"", photos:[], logs:[]
    } : state.pets[petId];

    const pd = getPricingDetail(petForPricing, svc, tenant, { usesPackage: !!walkInForm.customerPackageId });
    const addonPrice = addons.reduce((s,n) => {
      const a = (tenant?.addons || []).find(x => x.name === n);
      return s + (a ? computeAddonNet(a, petForPricing, svc).price : 0);
    }, 0);
    // SPA charges — same matrix pricing as the main service.
    const allSvcs = tenant?.services || [];
    const spaPrice = spaIds.reduce((acc, sid) => {
      const sp = allSvcs.find(x => x.id === sid && x.kind === "spa");
      return acc + (sp ? getPricingDetail(petForPricing, sp, tenant).total : 0);
    }, 0);
    // (SPA labels are no longer merged into addons — spaIds + the
    // calendar/detail UI's spaIds lookup is the source of truth so SPAs
    // don't appear twice.)
    const subtotal = pd.total + addonPrice + spaPrice;
    // Clamp discount amount: percent ≤ 100, fixed ≤ subtotal. Without
    // this the priceAdjustLog records absurd values like "−150%" and the
    // owner sees a confusing audit trail even though the final total is
    // floor-clamped to 0 later.
    const clampedAmt = discountType === "percent"
      ? Math.max(0, Math.min(100, Number(discountAmount) || 0))
      : Math.max(0, Math.min(subtotal, Number(discountAmount) || 0));
    discountAmount = clampedAmt;
    const discValue = discountType === "percent" ? Math.round(subtotal * (discountAmount||0) / 100) : (discountAmount||0);
    const finalTotal = Math.max(0, subtotal - discValue);

    // Step 3: conflict check (against existing appointments for the chosen staff today)
    // Manual per-pet duration override (walkInForm.customDuration) wins over
    // the matrix/per-size duration and the service default.
    const effDur = walkInForm.customDuration || pd?.durationMin || svc?.estimatedMinutes || 60;
    const dur = effDur + (svc?.bufferMinutes||0);
    const [nh, nm] = time.split(":").map(Number);
    const newStart = nh*60 + nm;
    const conflict = state.appointments.some(a => {
      if (a.staffId !== staffId || a.date !== today) return false;
      if (isAptDead(a)) return false;
      const [oh, om] = a.time.split(":").map(Number);
      const oStart = oh*60+om;
      const oDur = getAptDuration(a, tenant);
      return newStart < (oStart + oDur) && (newStart + dur) > oStart;
    });
    if (conflict) { toast(zhEn("該美容師此時段已有預約，請選其他時間或員工", "That groomer already has a booking at this time — choose another time or staff member"), "error"); return; }

    // Leave-day guard — owner explicitly marked this staffer as away
    // (sick/personal) for this date via an event row. Block + ask for
    // confirm so a slip never becomes a missed appointment.
    const leaveKind = staffLeaveOn(allApts, staffId, today);
    if (leaveKind) {
      const label = leaveKind === "sick" ? i18nT("tx.staffLeaveLabelSick") : i18nT("tx.staffLeaveLabelPersonal");
      if (!window.confirm(i18nT("tx.staffOnLeaveConfirm").replace("{name}", state.users[staffId]?.name || i18nT("tx.staffFallback")).replace("{label}", label))) return;
    }

    // Step 4: build appointment with all required fields.
    // Walk-in can opt to deduct from the customer's wallet (per-tenant
    // customer_memberships row). Cap at the available balance; the
    // remainder stays in `remainingBalance` as the cash/FPS portion due
    // on pickup. `deposit` reflects what we've already collected via
    // wallet so the receipt math balances.
    const mem = (state.memberships || []).find(m => m.userId === customerId && m.tenantId === user.tenantId);
    const walletAvail = Number(mem?.wallet || 0);
    const walletDeduct = walkInForm.useWallet && walletAvail > 0 ? Math.min(walletAvail, finalTotal) : 0;
    const newApt = {
      id:uuid(), tenantId:user.tenantId, customerId, petId, staffId,
      service:svc?.name||"", svcId, addons, spaIds, date:today, time, status: initialStatus,
      basePrice:pd.total, addonPrice: addonPrice + spaPrice, deposit: walletDeduct, remainingBalance: finalTotal - walletDeduct,
      paid: walletDeduct >= finalTotal,
      // If the owner picked a package, the apt-insert trigger does the
      // atomic decrement server-side. NULL → no package redemption.
      customerPackageId: walkInForm.customerPackageId || null,
      // Store the FULL local ISO timestamp (not just "HH:MM") so it
      // matches startedAt / completedAt and dwell-time analytics can
      // compare them as real Date values.
      checkedInAt: (initialStatus === "checkedin" || initialStatus === "grooming") ? (today + "T" + time + ":00") : null,
      startedAt: initialStatus === "grooming" ? (today + "T" + time + ":00") : null,
      report:null, signature:null, rating:null, cancelReason:null,
      // Snapshot the customer + pet name onto the appointment so it
      // still reads meaningfully if either record is later deleted.
      customerName: state.users[customerId]?.name || walkInNewCust?.name || "",
      petName: petForPricing?.name || "",
      staffNote: [staffRemark, discountReason ? "折扣:"+discountReason : "", walletDeduct > 0 ? "儲值扣 $"+walletDeduct : ""].filter(Boolean).join(" · "),
      surcharges:[],
      discount: discountAmount > 0 ? { amount:discountAmount, type:discountType, reason:discountReason, by:user.id, at:today+" "+timeNow() } : null,
      priceAdjustLog: discountAmount > 0 ? [{ field:"discount", from:"0", to:JSON.stringify({ amount:discountAmount, type:discountType, reason:discountReason }), by:user.id, at:today+" "+timeNow() }] : [],
      customDuration: walkInForm.customDuration || pd?.durationMin || null,
    };

    // Step 5: commit state changes (new cust + new pet + appointment + optional wallet deduct)
    setState(s => {
      let newUsers = s.users;
      let newPets = s.pets;
      let newTenants = s.tenants;
      if (walkInNewCust && !s.users[customerId]) {
        newUsers = { ...newUsers, [customerId]: {
          id:customerId, role:"customer", name:walkInNewCust.name.trim(), tenantId:user.tenantId,
          password:"1234", phone:(walkInNewCust.phone||"").trim(), wallet:0, points:0, walletTx:[],
          pets:[], tags:["新客","即場"], recurringApts:[]
        } };
        newTenants = newTenants.map(t => t.id === user.tenantId ? { ...t, customerIds:[...t.customerIds, customerId] } : t);
      }
      if (walkInNewPet && !s.pets[petId]) {
        newPets = { ...newPets, [petId]: petForPricing };
        const ownerId = customerId;
        const owner = newUsers[ownerId];
        if (owner) {
          newUsers = { ...newUsers, [ownerId]: { ...owner, pets:[...(owner.pets||[]), petId] } };
        }
      }
      // Wallet deduction mirror — local snapshot. The authoritative
      // server-side write happens AFTER setState via patchCustomerMembership
      // (see below). Previously this loop hit s.memberships which is the
      // OWNER's memberships array (current user's, not customers') so the
      // local update never matched anything either; we now mirror onto
      // state.users[customerId] for owner-side display.
      let newMemberships = s.memberships || [];
      if (walletDeduct > 0) {
        const cu = newUsers[customerId];
        if (cu) {
          const prior = Array.isArray(cu.walletTx) ? cu.walletTx : [];
          const newTx = { id: "tx" + uid(), type: "deduct", amount: walletDeduct, note: "即場預約扣款 · " + (svc?.name || ""), date: today + " " + time };
          newUsers = { ...newUsers, [customerId]: { ...cu, wallet: Math.max(0, Number(cu.wallet || 0) - walletDeduct), walletTx: [...prior, newTx] } };
        }
      }
      return { ...s, users:newUsers, pets:newPets, tenants:newTenants, memberships: newMemberships, appointments:[...s.appointments, newApt] };
    });

    // Persist the wallet deduction server-side via an ATOMIC RPC.
    // Previously this did patchCustomerMembership({ wallet: newAbsolute })
    // computed from a stale local snapshot — last-write-wins clobbered
    // any concurrent debit (other staff settling another apt, customer
    // top-up landing seconds earlier) by restoring the old balance.
    // debit_customer_wallet does `wallet = greatest(0, wallet - amount)`
    // server-side so concurrent debits stack correctly. The RPC also
    // appends the tx entry atomically (no race between read-newTx and
    // write).
    if (walletDeduct > 0) {
      try {
        const tx = { id: "tx" + uid(), type: "deduct", amount: walletDeduct, note: "即場預約扣款 · " + (svc?.name || ""), date: today + " " + time, aptId: newApt.id };
        const { newWallet } = await debitCustomerWallet({ userId: customerId, tenantId: user.tenantId, amount: walletDeduct, tx });
        // Reconcile local snapshot with the authoritative server value
        // — if another debit landed between optimistic setState and the
        // RPC, our local mirror was off by that delta.
        if (typeof newWallet === "number") {
          setState(s => {
            const cu = s.users?.[customerId];
            if (!cu) return s;
            return { ...s, users: { ...s.users, [customerId]: { ...cu, wallet: newWallet } } };
          });
        }
      } catch (e) {
        console.warn("[PettoBook] walk-in wallet debit failed:", e);
        toast(i18nT("tx.walletDeductFailed"), "warning");
      }
    }

    pushNotif(setState, { userId:customerId, type:"booking_confirmed", title:"即場預約確認 ✅", body:(petForPricing?.name||"")+" 的"+(svc?.name||"")+"已登記，今天 "+time+" · 總計 $"+finalTotal, read:false, aptId:newApt.id });
    logActivity({
      tenantId: user.tenantId,
      userId: user.id,
      actionType: "appointment_created",
      entityType: "appointment",
      entityId: newApt.id,
      entityName: (state.users[customerId]?.name||"客戶") + " - " + (petForPricing?.name||"毛孩") + " - " + (svc?.name||"服務"),
      description: "建立即場預約：" + (state.users[customerId]?.name||"") + " @ " + time + " (總計 $" + finalTotal + ")",
      severity: "info",
      details: { customerId, petId, serviceId: svcId, staffId, time, totalPrice: finalTotal }
    }).catch(e => console.error("[PettoBook] log activity:", e));
    toast(zhEn("即場預約已登記！總計 $", "Walk-in booked! Total $") + finalTotal + (walkInForm.customerPackageId ? zhEn("（套票已扣 1 次 📦）", " (1 package visit deducted 📦)") : ""));
    if (opts.bookAnother) {
      // Keep the form open. Advance to the next free slot for the same
      // staff/date so user just confirms a fresh booking instead of
      // hunting for an open time. dur already includes the buffer.
      const endMin = newStart + dur;
      const nextTime = nextAvailableSlot(staffId, today, endMin, dur);
      if (!nextTime) {
        toast(zhEn("今日已無可用時段，請改日或揀其他美容師", "No slots left today — choose another day or groomer"), "warning");
        setWalkInForm(null); setWalkInNewCust(null); setWalkInNewPet(null); setWalkInSearch(""); setWalkInDiscount(0);
        return;
      }
      // Clear pet (so user re-picks a pet for the SAME customer) but
      // keep customer/svc/staff/addons. Reset transient flags so the
      // form's pricing/wallet UI starts clean.
      setWalkInForm(prev => prev ? {
        ...prev,
        petId: "",
        time: nextTime,
        addons: [],
        spaIds: [],
        discountAmount: 0,
        discountType: "percent",
        discountReason: "",
        staffRemark: "",
        useWallet: false,
        customerPackageId: null,
        customDuration: null,
      } : prev);
      setWalkInNewPet(null);
      setWalkInDiscount(0);
      return;
    }
    setWalkInForm(null); setWalkInNewCust(null); setWalkInNewPet(null); setWalkInSearch(""); setWalkInDiscount(0);
  };

  // Price overview — a printable matrix (services × size tiers + special
  // breeds) opened in a new window so the owner can see every price at once.
  const openPriceOverview = () => {
    if (!tenant) { toast(zhEn("未有店舖資料", "No shop details found"), "error"); return; }
    const en = i18nGetLocale() !== "zh-HK";
    const tiers = getShopTiers(tenant);
    const split = tenant.pricingConfig?.coatSplit === true;
    const breeds = (Array.isArray(tenant.specialBreeds) ? tenant.specialBreeds : []).filter(b => b && (b.name || (b.names||[])[0]));
    // Group order: dog services → cat services → SPA; sorted by name within each.
    const groupOf = (s) => {
      if (s.kind === "spa") return 3;
      const types = (Array.isArray(s.petTypes) && s.petTypes.length) ? s.petTypes : [s.petType || "dog"];
      return (types.includes("cat") && !types.includes("dog")) ? 2 : 1;
    };
    const groupLabel = { 1: en ? "🐶 Dogs" : "🐶 狗狗服務", 2: en ? "🐱 Cats" : "🐱 貓貓服務", 3: en ? "💆 SPA / add-ons" : "💆 SPA 水療" };
    const _wkPet = walkInForm?.petId ? state.pets[walkInForm.petId] : null;
    // customerVisible === false is the 🔒 員工專用 flag (快速護理 and friends).
    // This document is handed to customers, so a staff-only service has no
    // business on it — and it renders as an empty row anyway, because a
    // $0-base service carries its price in the add-ons, not the size matrix.
    const svcs = (tenant.services || []).filter(s => s && s.enabled !== false && s.customerVisible !== false && !(_wkPet && (_wkPet.blockedServiceIds || []).includes(s.id)))
      .sort((a, b) => (groupOf(a) - groupOf(b)) || String(a.name||"").localeCompare(String(b.name||""), "zh-Hant"));
    const esc = (x) => String(x ?? "").replace(/[&<>"]/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;"}[c]));
    const money = (v) => (v === 0 || v) ? "$" + v : "";
    const tierLabel = (t) => esc(t.label) + " <span class=w>" + t.minW + "–" + t.maxW + "kg</span>";
    // Customer-clean cell rendering: real prices only — no owner jargon (·/—/未設).
    // Normal price html for an entry; "" when blank (so the cell shows nothing).
    const priceHtml = (e) => {
      if (!e) return "";
      if (e.na) return '<span class=na>✕</span>';
      if (e.quote) return '<span class=quote>'+(en?"Quote":"面議")+'</span>';
      if (split) {
        const s = Number(e.short)||0, l = Number(e.long)||Number(e.short)||0;
        if (!s && !l) return "";
        if (s === l) return '<b>'+money(s)+'</b>';
        // Two stacked, labelled lines so customers know which is short/long hair.
        return '<div class=pl><span class=ct>'+(en?"Sh":"短")+'</span><b>'+money(s)+'</b></div><div class=pl><span class=ct>'+(en?"Lo":"長")+'</span><b>'+money(l)+'</b></div>';
      }
      const s = Number(e.short)||Number(e.long)||0;
      return s ? '<b>'+money(s)+'</b>' : "";
    };
    // 上門 (home-visit) price line for a mobile-enabled service. Uses the entry's
    // 上門 price; if none is set the 上門 price equals the shop price (同價).
    const mobileHtml = (svc, e) => {
      if (!svc.mobileAvailable || !e || e.na || e.quote) return "";
      const m = Number(e.mobile)||0;
      return m > 0 ? '<div class=mob>🚗 $'+m+'</div>' : '<div class=mob>🚗 '+(en?"same":"同價")+'</div>';
    };
    const tierCellFor = (svc, tid) => {
      const mp = svc.matrixPricing;
      if (!(svc.pricingMode === "matrix") || !mp || !mp.byTier) {
        return svc.basePrice ? '<b>'+money(svc.basePrice)+'</b>' : "";
      }
      const e = mp.byTier[tid];
      const base = priceHtml(e);
      return base ? base + mobileHtml(svc, e) : "";
    };
    const breedCellFor = (svc, b, tid) => {
      if (!breedAppliesToService(b, svc)) return ""; // wrong pet type → blank
      const e = svc.matrixPricing?.byBreed?.[b.id]?.[tid];
      if (e && (e.na || e.quote || Number(e.short) > 0 || Number(e.long) > 0)) {
        const base = priceHtml(e);
        return base ? base + mobileHtml(svc, e) : "";
      }
      // Blank breed entry → show the real general size-tier price it falls back to.
      return tierCellFor(svc, tid);
    };
    // A size tier is "served" if any service prices it (matrix non-na with a
    // price, or a static service). Unserved tiers (e.g. nobody grooms 巨型) are
    // hidden as rows.
    const served = (svc, tid) => {
      const mp = svc.matrixPricing;
      if (!(svc.pricingMode === "matrix") || !mp || !mp.byTier) return (Number(svc.basePrice)||0) > 0;
      const e = mp.byTier[tid];
      return !!e && !e.na && (e.quote || Number(e.short) > 0 || Number(e.long) > 0);
    };
    const shownTiers = tiers.filter(t => svcs.some(svc => served(svc, t.id)));
    // A special breed is "served" at a size tier when some service prices it
    // there (own entry with a price/quote, or a blank entry that falls back to
    // a served normal tier). Unserved tiers (e.g. a Poodle never reaches 巨型)
    // are hidden from THAT breed's section.
    const breedServedAt = (b, t) => svcs.some(svc => {
      if (!breedAppliesToService(b, svc)) return false; // wrong pet type for this svc
      const e = svc.matrixPricing?.byBreed?.[b.id]?.[t.id];
      if (e) { if (e.na) return false; if (e.quote || Number(e.short) > 0 || Number(e.long) > 0) return true; }
      return served(svc, t.id);
    });

    // ONE compact table per pet type, stacked vertically — a 🐶 dog table
    // (dog + SPA columns) and a 🐱 cat table (cat + SPA columns). Each table
    // only carries the columns that pet actually uses, so there are no blank
    // cross-pet columns (no wasted white space) — like a printed menu where the
    // 貓貓 block sits at the bottom with its own columns.
    const dogSvc = (svc) => serviceTypes(svc).includes("dog");
    const catSvc = (svc) => serviceTypes(svc).includes("cat");
    // A $0-base, non-matrix service has nothing to put in a tier cell, so it
    // would render as an empty column. It is listed under 加購項目 instead.
    const inMatrix = (sv) => sv.pricingMode === "matrix" || Number(sv.basePrice) > 0;
    const dogCols = svcs.filter(sv => dogSvc(sv) && inMatrix(sv));
    const catCols = svcs.filter(sv => catSvc(sv) && inMatrix(sv));
    // A size row only appears when a MAIN (non-SPA) service is offered there.
    // SPA is an add-on, so it shouldn't keep a size row alive on its own — e.g.
    // if cats aren't groomed at 大型, don't show a 大型 row just for SPA prices.
    const isMainSvc = (svc) => svc.kind !== "spa" && svc.kind !== "addon";
    const dogTiers = tiers.filter(t => dogCols.some(svc => isMainSvc(svc) && served(svc, t.id)));
    const catTiers = tiers.filter(t => catCols.some(svc => isMainSvc(svc) && served(svc, t.id)));
    const buildTable = (pet, cols, tierList) => {
      if (!cols.length || !tierList.length) return "";
      const ord = []; cols.forEach(s => { const g = groupOf(s); if (!ord.length || ord[ord.length-1].g !== g) ord.push({ g, n: 0 }); ord[ord.length-1].n++; });
      let h1 = '<th class="stick rowlab corner" rowspan="2">'+esc(pet==="dog" ? (en?"Size / Breed":"體型 / 品種") : (en?"Size":"體型"))+'</th>';
      ord.forEach(o => h1 += '<th class="band g'+o.g+'" colspan="'+o.n+'">'+esc(groupLabel[o.g])+'</th>');
      let h2 = '';
      cols.forEach(svc => h2 += '<th class="svccol g'+groupOf(svc)+'"><span class="nm">'+esc(svc.name)+(svc.mobileAvailable?' <span class="tag mob">🚗</span>':'')+'</span></th>');
      let body = '';
      tierList.forEach(t => {
        body += '<tr><td class="stick rowlab">'+tierLabel(t)+'</td>';
        cols.forEach(svc => body += '<td class="g'+groupOf(svc)+'">'+tierCellFor(svc, t.id)+'</td>');
        body += '</tr>';
      });
      // Special-breed sections belonging to THIS pet type (dog table: non-cat
      // breeds; cat table: cat breeds).
      breeds.forEach(b => {
        const bt = specialBreedType(b);
        if (pet === "dog" ? bt === "cat" : bt !== "cat") return;
        const bTiers = tiers.filter(t => breedServedAt(b, t));
        if (!bTiers.length) return;
        body += '<tr class="grouprow"><td class="stick rowlab gh">'+esc((bt==="cat"?'🐱 ':'🐩 ')+(b.name||(b.names||[])[0]))+'</td><td class="gh" colspan="'+cols.length+'"></td></tr>';
        bTiers.forEach(t => {
          body += '<tr><td class="stick rowlab breedlab">'+tierLabel(t)+'</td>';
          cols.forEach(svc => body += '<td class="breed g'+groupOf(svc)+'">'+breedCellFor(svc, b, t.id)+'</td>');
          body += '</tr>';
        });
      });
      const heading = pet==="dog" ? (en?"🐶 Dogs":"🐶 狗狗服務") : (en?"🐱 Cats":"🐱 貓貓服務");
      return '<div class="pettype" data-pet="'+pet+'"><div class="pethd">'+esc(heading)+'</div>'
        + '<div class="wrap"><table><thead><tr>'+h1+'</tr><tr>'+h2+'</tr></thead><tbody>'+body+'</tbody></table></div></div>';
    };
    const tablesHtml = buildTable("dog", dogCols, dogTiers) + buildTable("cat", catCols, catTiers);
    // Filter: All / 🐶 Dogs / 🐱 Cats — toggles whole pet-type tables.
    const hasDog = dogCols.length && dogTiers.length;
    const hasCat = catCols.length && catTiers.length;
    let filters = '<button class="fbtn active" data-f="all">'+(en?"All":"全部")+'</button>';
    if (hasDog) filters += '<button class="fbtn" data-f="dog">'+(en?"🐶 Dogs":"🐶 狗狗")+'</button>';
    if (hasCat) filters += '<button class="fbtn" data-f="cat">'+(en?"🐱 Cats":"🐱 貓貓")+'</button>';

    // ── Four themes. The card structure is identical; each theme just swaps a
    //    set of CSS variables (background, hero, group bands, accents). The
    //    owner flips themes live in the toolbar, then exports PNG/JPEG. ──
    const themes = [
      { k:"rose", n: en?"Rose":"玫瑰" },
      { k:"mono", n: en?"Minimal":"簡約" },
      { k:"dark", n: en?"Dark":"暗黑" },
      { k:"mint", n: en?"Mint":"薄荷" },
      { k:"ocean", n: en?"Ocean":"海洋" },
      { k:"sunset", n: en?"Sunset":"日落" },
      { k:"grape", n: en?"Grape":"葡萄" },
      { k:"sand", n: en?"Cream":"奶油" },
    ];
    const css = `
      *{box-sizing:border-box}
      :root{
        --bg:#FBEFF2;--bgimg:linear-gradient(135deg,#FFF7F3 0%,#F4EEFC 55%,#EAF3FF 100%);
        --card:#fff;--ink:#3A3A56;--strong:#111;--mut:#AEB3C6;--line:#F2EEF4;
        --hero:linear-gradient(135deg,#F7C9D0 0%,#C98B97 50%,#9E6B77 100%);--herofg:#fff;--accent:#B76E79;
        --paw:.12;
        --b1:linear-gradient(135deg,#8FB6F2,#6A93DE);--b2:linear-gradient(135deg,#F3A6BC,#DD7E9A);--b3:linear-gradient(135deg,#C3A6F0,#9E78DE);
        --s1bg:#F2F7FF;--s1fg:#1E40AF;--s2bg:#FFF2F7;--s2fg:#A12A5C;--s3bg:#FAF6FF;--s3fg:#6D28D9;
        --rowhead:linear-gradient(90deg,#FBF3F6,#fff);--rowheadfg:#B76E79;--breedfg:#6D28D9;--breedbg:#FCFAFF;
        --hover:#FFF7F9;--na:#E0537A;--dot:#C9B8F2;--quote:#0E7C66;
      }
      body[data-theme=mono]{
        --bg:#EEEEF1;--bgimg:linear-gradient(180deg,#F7F7F9,#E9E9ED);
        --card:#fff;--ink:#1F2430;--strong:#0E1116;--mut:#A6AAB6;--line:#ECECF0;
        --hero:linear-gradient(135deg,#15181D,#2C313B);--herofg:#fff;--accent:#15181D;--paw:.07;
        --b1:#2B2F36;--b2:#3A3F49;--b3:#22262C;
        --s1bg:#F6F6F8;--s1fg:#2B2F36;--s2bg:#F1F1F4;--s2fg:#2B2F36;--s3bg:#F6F6F8;--s3fg:#2B2F36;
        --rowhead:#FAFAFB;--rowheadfg:#15181D;--breedfg:#3A3F4A;--breedbg:#FAFAFB;
        --hover:#F6F6F8;--na:#C2415F;--dot:#C9CDD6;--quote:#0E7C66;
      }
      body[data-theme=dark]{
        --bg:#15151F;--bgimg:linear-gradient(135deg,#1B1B2A,#241B2E 60%,#15151F);
        --card:#20202E;--ink:#ECE9F5;--strong:#fff;--mut:#8A88A0;--line:#2E2E40;
        --hero:linear-gradient(135deg,#3A2E4D,#6D5A8C);--herofg:#F7F1FF;--accent:#D9B26B;--paw:.16;
        --b1:linear-gradient(135deg,#3D4F73,#2C3A57);--b2:linear-gradient(135deg,#5A3A52,#7A4D6C);--b3:linear-gradient(135deg,#473A66,#5E4D86);
        --s1bg:#23293A;--s1fg:#9DC0FF;--s2bg:#33283A;--s2fg:#F2A6C4;--s3bg:#2A2740;--s3fg:#C7B0FF;
        --rowhead:linear-gradient(90deg,#2A2438,#20202E);--rowheadfg:#D9B26B;--breedfg:#D9B26B;--breedbg:#262433;
        --hover:#282637;--na:#FF7B9B;--dot:#5B5B78;--quote:#5BD6B0;
      }
      body[data-theme=mint]{
        --bg:#E1F6EE;--bgimg:linear-gradient(135deg,#E6FBF1 0%,#FFF3E6 55%,#FDE8F0 100%);
        --card:#fff;--ink:#2C4A43;--strong:#11332C;--mut:#9FBAB1;--line:#E6F2EC;
        --hero:linear-gradient(135deg,#9BE3C6,#6FCFB0 55%,#5FB8C9);--herofg:#0E3F36;--accent:#2BA98A;--paw:.16;
        --b1:linear-gradient(135deg,#9AD0F5,#6FB7E8);--b2:linear-gradient(135deg,#FFC3D6,#FF9FBE);--b3:linear-gradient(135deg,#C7E9A8,#A6D77F);
        --s1bg:#EFF8FF;--s1fg:#1C6FA8;--s2bg:#FFF1F6;--s2fg:#C43D6E;--s3bg:#F3FBEC;--s3fg:#5A8A2E;
        --rowhead:linear-gradient(90deg,#EAFBF3,#fff);--rowheadfg:#2BA98A;--breedfg:#2BA98A;--breedbg:#F3FCF8;
        --hover:#F1FCF7;--na:#E0537A;--dot:#B9E4D4;--quote:#0E7C66;
      }
      body[data-theme=ocean]{
        --bg:#E6F0FA;--bgimg:linear-gradient(135deg,#E8F4FF 0%,#EAF0FF 55%,#E6FBFF 100%);
        --card:#fff;--ink:#1B3A57;--strong:#0B2942;--mut:#9FB3C8;--line:#E5EEF6;
        --hero:linear-gradient(135deg,#6FB1E8,#3D7FC4 55%,#2B5C9E);--herofg:#fff;--accent:#2B6CB0;--paw:.14;
        --b1:linear-gradient(135deg,#7FB2EE,#5C92DE);--b2:linear-gradient(135deg,#7FD3E0,#4FB6C9);--b3:linear-gradient(135deg,#9DB4F0,#6E86DE);
        --s1bg:#EEF6FF;--s1fg:#1C5FA8;--s2bg:#EAFAFD;--s2fg:#0E6E84;--s3bg:#F0F2FF;--s3fg:#3A4FB0;
        --rowhead:linear-gradient(90deg,#EAF4FF,#fff);--rowheadfg:#2B6CB0;--breedfg:#2B6CB0;--breedbg:#F4F9FF;
        --hover:#F1F8FF;--na:#E0537A;--dot:#B9D4EC;--quote:#0E7C66;
      }
      body[data-theme=sunset]{
        --bg:#FCEDE2;--bgimg:linear-gradient(135deg,#FFF1E6 0%,#FFE8E0 55%,#FFE3EC 100%);
        --card:#fff;--ink:#5A3320;--strong:#3D2113;--mut:#C9A892;--line:#F6E6DC;
        --hero:linear-gradient(135deg,#FFB36B,#F37D54 55%,#E85C7B);--herofg:#fff;--accent:#E8693E;--paw:.14;
        --b1:linear-gradient(135deg,#FFC078,#F59E4B);--b2:linear-gradient(135deg,#FF9FA8,#F4707F);--b3:linear-gradient(135deg,#F7A8C4,#E87BA0);
        --s1bg:#FFF4E8;--s1fg:#B45309;--s2bg:#FFF0F1;--s2fg:#BE3455;--s3bg:#FFF1F6;--s3fg:#B83B72;
        --rowhead:linear-gradient(90deg,#FFF1E6,#fff);--rowheadfg:#E8693E;--breedfg:#C0497A;--breedbg:#FFF7F3;
        --hover:#FFF6EF;--na:#D6395E;--dot:#F2C7A8;--quote:#0E7C66;
      }
      body[data-theme=grape]{
        --bg:#EFE7FA;--bgimg:linear-gradient(135deg,#F1E9FC 0%,#EDE6FB 55%,#F6E9F7 100%);
        --card:#fff;--ink:#3D2A5A;--strong:#27163F;--mut:#B0A0C8;--line:#EAE2F4;
        --hero:linear-gradient(135deg,#A78BE0,#7C5BC4 55%,#5E3F9E);--herofg:#fff;--accent:#7C3AED;--paw:.15;
        --b1:linear-gradient(135deg,#9AA8F0,#6E86DE);--b2:linear-gradient(135deg,#D49BE8,#B870D8);--b3:linear-gradient(135deg,#B79AF0,#8E6EDE);
        --s1bg:#F1F0FF;--s1fg:#4338CA;--s2bg:#FBEEFC;--s2fg:#9D2A9E;--s3bg:#F4ECFE;--s3fg:#6D28D9;
        --rowhead:linear-gradient(90deg,#F2EBFC,#fff);--rowheadfg:#7C3AED;--breedfg:#7C3AED;--breedbg:#F8F4FE;
        --hover:#F6F1FE;--na:#E0537A;--dot:#D2C2EC;--quote:#0E7C66;
      }
      body[data-theme=sand]{
        --bg:#F3ECDF;--bgimg:linear-gradient(135deg,#FAF4E8 0%,#F5EEE0 55%,#F3EFE6 100%);
        --card:#FFFDF8;--ink:#4A4036;--strong:#2E261C;--mut:#BBAE99;--line:#EDE4D4;
        --hero:linear-gradient(135deg,#D8C29A,#B89B6E 55%,#9C7F54);--herofg:#fff;--accent:#A6814C;--paw:.16;
        --b1:linear-gradient(135deg,#CDB98E,#B69C6C);--b2:linear-gradient(135deg,#D9B79A,#C2997A);--b3:linear-gradient(135deg,#C9BFA0,#A99A78);
        --s1bg:#F8F2E6;--s1fg:#8A6D3B;--s2bg:#FAF0E6;--s2fg:#9C6B45;--s3bg:#F7F3E8;--s3fg:#7E6A3C;
        --rowhead:linear-gradient(90deg,#F8F2E4,#FFFDF8);--rowheadfg:#A6814C;--breedfg:#8A6D3B;--breedbg:#FBF7EE;
        --hover:#FBF6EC;--na:#C2415F;--dot:#DBCDB0;--quote:#0E7C66;
      }
      body{margin:0;font-family:-apple-system,'PingFang HK','Microsoft JhengHei',sans-serif;color:var(--ink);
        background-color:var(--bg);background-image:var(--bgimg);padding:18px 18px 40px;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .bar{max-width:1180px;margin:0 auto 16px;display:flex;gap:10px;flex-wrap:wrap;align-items:center;justify-content:space-between}
      .seg{display:flex;gap:7px;flex-wrap:wrap;align-items:center}
      .seg .lbl{font-size:11px;font-weight:800;color:var(--ink);opacity:.6;margin-right:2px}
      .tbtn,.xbtn,.fbtn{border:none;border-radius:99px;padding:8px 14px;font-size:12.5px;font-weight:800;cursor:pointer}
      .tbtn{background:rgba(127,127,150,.16);color:var(--ink)}
      .tbtn.active{background:var(--accent);color:#fff}
      .xbtn{background:var(--accent);color:#fff;box-shadow:0 6px 16px rgba(0,0,0,.16)}
      .xbtn.ghost{background:rgba(127,127,150,.16);color:var(--ink);box-shadow:none}
      .card{max-width:1180px;margin:0 auto;background:var(--card);border-radius:28px;overflow:hidden;
        box-shadow:0 28px 70px rgba(60,40,50,.22);border:1px solid var(--line)}
      .hero{position:relative;padding:24px 28px;color:var(--herofg);overflow:hidden;background:var(--hero)}
      .hero:after{content:"🐾";position:absolute;font-size:150px;right:-12px;bottom:-46px;opacity:var(--paw);transform:rotate(-18deg)}
      .htop{display:flex;align-items:center;gap:14px}
      .logo{width:58px;height:58px;border-radius:15px;object-fit:cover;flex-shrink:0;background:#fff;box-shadow:0 6px 16px rgba(0,0,0,.20);border:2px solid rgba(255,255,255,.7)}
      .logoph{display:flex;align-items:center;justify-content:center;font-size:30px}
      .hero h1{font-size:23px;margin:0;font-weight:900;letter-spacing:.3px}
      .hero .tagline{font-size:13px;font-weight:800;opacity:.95;margin-top:4px}
      .hero .contact{display:flex;gap:16px;flex-wrap:wrap;font-size:11.5px;font-weight:700;opacity:.92;margin-top:7px}
      .hero .hours{display:flex;gap:16px;flex-wrap:wrap;font-size:11.5px;font-weight:700;opacity:.92;margin-top:5px}
      .hero .pets{font-size:18px;letter-spacing:4px;margin-top:10px;opacity:.95}
      .legend{display:flex;gap:18px;flex-wrap:wrap;font-size:11.5px;font-weight:700;color:var(--mut);padding:12px 26px;border-bottom:1px solid var(--line)}
      .legend .na{color:var(--na)}.legend .dot{color:var(--accent)}.legend .quote{color:var(--quote)}.legend b{color:var(--ink)}
      .filters{display:flex;gap:8px;flex-wrap:wrap;padding:12px 26px 2px}
      .fbtn{background:rgba(127,127,150,.14);color:var(--ink)}
      .fbtn.active{background:var(--accent);color:#fff}
      .wrap{overflow:auto;padding:10px 12px 4px}
      table{border-collapse:separate;border-spacing:0;font-size:12.5px;width:100%}
      th,td{padding:9px 11px;text-align:center;border-bottom:1px solid var(--line)}
      thead th{position:sticky;top:0;z-index:3}
      thead tr:nth-child(2) th{top:34px}
      .band{color:#fff;font-weight:900;font-size:12.5px;padding:7px 8px}
      .band.g1{background:var(--b1)} .band.g2{background:var(--b2)} .band.g3{background:var(--b3)}
      .svccol{font-weight:800;font-size:11.5px;vertical-align:top;min-width:96px;padding:8px 8px}
      .svccol.g1{color:var(--s1fg);background:var(--s1bg)} .svccol.g2{color:var(--s2fg);background:var(--s2bg)} .svccol.g3{color:var(--s3fg);background:var(--s3bg)}
      .svccol .nm{white-space:normal;line-height:1.28;word-break:break-word}
      .pettype{margin-top:6px}
      .pethd{font-size:15px;font-weight:900;color:var(--accent);padding:14px 26px 2px}
      .w{font-weight:600;color:var(--mut);font-size:9.5px}
      .stick.rowlab{position:sticky;left:0;z-index:2;background:var(--card);text-align:left;font-weight:800;min-width:180px;max-width:230px;box-shadow:3px 0 10px rgba(0,0,0,.05)}
      .corner{z-index:5;background:var(--card);border-top-left-radius:12px}
      thead .stick.rowlab{z-index:6}
      .breedlab{color:var(--breedfg)}
      .grouprow .gh{background:var(--rowhead);font-weight:900;color:var(--rowheadfg);text-align:left;font-size:12.5px;letter-spacing:.4px;border-bottom:2px solid var(--line)}
      tbody tr:hover td{background:var(--hover)}
      td.breed{background:var(--breedbg)}
      b{color:var(--strong);font-size:13px} .sl{color:var(--mut);font-weight:600} .mut{color:var(--mut);font-weight:600} .na{color:var(--na);font-weight:900} .dot{color:var(--dot);font-weight:900} .quote{color:var(--quote);font-weight:900;font-size:12px}
      .tag{font-size:9px;font-weight:900;background:rgba(127,80,200,.16);color:var(--s3fg);border-radius:5px;padding:1px 5px;vertical-align:middle}
      .tag.mob{background:rgba(3,105,161,.16);color:#0369A1}
      .ct{font-size:8.5px;color:var(--mut);font-weight:800;margin-right:2px}
      .pl{white-space:nowrap;line-height:1.3}
      .mob{color:#0EA5E9;font-weight:800;font-size:10px;margin-top:2px;white-space:nowrap}
      .legend .mob{margin:0;display:inline}
      .cta{display:flex;align-items:center;gap:18px;padding:16px 26px;border-top:1px solid var(--line);background:var(--rowhead)}
      .cta .qr{width:96px;height:96px;border-radius:10px;background:#fff;padding:5px;flex-shrink:0;box-shadow:0 4px 12px rgba(0,0,0,.12)}
      .cta .qr .qrimg{width:100%;height:100%;display:block;image-rendering:pixelated}
      .ctah{font-size:15px;font-weight:900;color:var(--accent)}
      .ctaurl{font-size:13.5px;font-weight:800;color:var(--ink);margin-top:4px;word-break:break-all}
      .ctawa{font-size:12px;font-weight:700;color:var(--mut);margin-top:5px}
      .addons{padding:16px 26px 4px;border-top:1px solid var(--line)}
      .addhd{font-size:13px;font-weight:900;color:var(--accent);margin:0 0 3px}
      .addsub{font-size:10.5px;font-weight:700;color:var(--mut);margin:0 0 10px;line-height:1.5}
      .addgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:6px 18px}
      .addrow{display:flex;align-items:baseline;gap:7px;font-size:12px;font-weight:800;color:var(--ink);padding:4px 0;border-bottom:1px dotted var(--line)}
      .addrow .an{flex:0 1 auto}
      .addrow .ad{flex:1 1 auto;border-bottom:1px dotted var(--dot);min-width:8px;transform:translateY(-3px)}
      .addrow .ap{flex:0 0 auto;color:var(--strong);font-weight:900}
      .addrow .apt{font-size:9.5px;color:var(--mut);font-weight:700}
      .addvar{font-size:10px;font-weight:700;color:var(--mut);padding-left:2px}
      .flatrow{display:flex;align-items:baseline;gap:7px;font-size:12px;font-weight:800;color:var(--ink);padding:5px 0;border-bottom:1px dotted var(--line)}
      .flatrow .fn{font-weight:900}
      .flatrow .fd{flex:1 1 auto;border-bottom:1px dotted var(--dot);transform:translateY(-3px)}
      .flatrow .fp{color:var(--quote);font-weight:900;font-size:11px}
      .note{padding:11px 26px;font-size:10.5px;color:var(--mut);font-weight:700;line-height:1.55;border-top:1px solid var(--line)}
      .foot{padding:14px 26px;font-size:11px;color:var(--mut);font-weight:700;text-align:center;border-top:1px solid var(--line)}
      .busy{position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,.4);color:#fff;font-weight:800;font-size:15px;z-index:99}
      @page{size:landscape;margin:6mm}
      @media print{.bar{display:none}body{background:#fff;padding:0}.card{box-shadow:none;border:none;border-radius:0;overflow:visible;max-width:none}.wrap{overflow:visible}thead th{position:static}.stick.rowlab{position:static;box-shadow:none}}
    `;
    let themeBtns = '';
    themes.forEach((t,i) => themeBtns += '<button class="tbtn'+(i===0?" active":"")+'" data-t="'+t.k+'">'+esc(t.n)+'</button>');
    const fname = (tenant.name||"pricelist").replace(/[\\/:*?"<>|]/g,"").trim() || "pricelist";
    // Booking call-to-action: online-booking URL + QR + WhatsApp. The QR is
    // rendered by a public image API (CORS-enabled so it survives PNG export).
    const bookingUrl = tenant.bookingSlug ? getBookingUrl(tenant.bookingSlug, state) : "";
    const bookingFull = bookingUrl ? ("https://" + bookingUrl.replace(/^https?:\/\//, "")) : "";
    const waDigits = tenant.phone ? String(tenant.phone).replace(/[^0-9]/g, "") : "";
    const now = new Date();
    const dstr = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0") + "-" + String(now.getDate()).padStart(2,"0");
    const ctaHtml = (bookingUrl || waDigits)
      ? ('<div class="cta">'
        + (bookingFull ? '<div class="qr" data-qr="'+esc(bookingFull)+'"></div>' : '')
        + '<div class="ctatxt"><div class="ctah">'+(en?"📅 Book online":"📅 網上預約")+'</div>'
        + (bookingUrl ? '<div class="ctaurl">'+esc(bookingUrl)+'</div>' : '')
        + (waDigits ? '<div class="ctawa">📱 WhatsApp / '+(en?"Tel":"電話")+'：'+esc(tenant.phone)+'</div>' : '')
        + '</div></div>')
      : '';
    // ── 加購項目 + 按項目收費嘅服務 ─────────────────────────────────────
    // Add-ons were missing from this document entirely, which is what made a
    // $0-base service like 快速護理 look broken: the row was empty because its
    // price lives HERE, not in the size matrix. Add-ons are flat name+price,
    // so they get a simple two-column list rather than a tier table.
    const petTypesOf = (a) => (Array.isArray(a.petTypes) && a.petTypes.length) ? a.petTypes : ["dog"];
    const addonList = (tenant.addons || [])
      .filter(a => a && a.enabled !== false && a.name)
      .sort((a, b) => (Number(a.price)||0) - (Number(b.price)||0) || String(a.name).localeCompare(String(b.name), "zh-Hant"));
    // A customer-visible service with a $0 base and no size matrix is priced
    // BY its add-ons. Show it as one line saying so, instead of a row of empty
    // tier cells — that row is why the table looked wrong.
    const flatSvcs = svcs.filter(sv =>
      sv.pricingMode !== "matrix" && !(Number(sv.basePrice) > 0));
    const addonPetTag = (a) => {
      const ts = petTypesOf(a);
      if (ts.includes("dog") && ts.includes("cat")) return "";
      return ts.includes("cat") ? '<span class="apt">🐱</span>' : (ts.includes("dog") ? '<span class="apt">🐶</span>' : "");
    };
    const addonPriceHtml = (a) => {
      if (a.pricingMode === "variants" && Array.isArray(a.variants) && a.variants.length) {
        const ps = a.variants.map(v => Number(v?.price) || 0).filter(n => n > 0);
        if (ps.length) {
          const lo = Math.min(...ps), hi = Math.max(...ps);
          return lo === hi ? money(lo) : money(lo) + "–" + money(hi).replace("$", "");
        }
      }
      const p = Number(a.price) || 0;
      return p > 0 ? money(p) : (en ? "Quote" : "面議");
    };
    const addonsHtml = (!addonList.length && !flatSvcs.length) ? '' :
      '<div class="addons">'
      + '<div class="addhd">'+(en ? "➕ Add-ons" : "➕ 加購項目")+'</div>'
      + '<div class="addsub">'+(en
          ? "Add any of these to a grooming service. Ear cleaning and nail trimming may already be included in some services — please ask in store."
          : "可加喺任何美容服務上。部分服務已包洗耳／剪甲，詳情請向店員查詢。")+'</div>'
      + (flatSvcs.length ? flatSvcs.map(sv =>
          '<div class="flatrow"><span class="fn">'+esc(sv.name)+'</span>'
          + '<span class="fd"></span>'
          + '<span class="fp">'+(en ? "priced by the add-ons chosen" : "按所選加購項目計價")+'</span></div>').join('') : '')
      + (addonList.length ? '<div class="addgrid">'
          + addonList.map(a =>
              '<div class="addrow"><span class="an">'+esc(a.name)+'</span>'
              + addonPetTag(a)
              + '<span class="ad"></span>'
              + '<span class="ap">'+addonPriceHtml(a)+'</span></div>').join('')
          + '</div>' : '')
      + '</div>';
    const noteHtml = '<div class="note">'+(en
        ? "Prices are for reference only; the final quote is given in-store. Severe matting, flea/tick removal, or special behavioural handling may incur additional charges."
        : "以上價格僅供參考，實際以店內報價為準。嚴重打結、去蚤／蜱、特殊行為處理可能另加費用。")+'</div>';
    // 營業時間 + 休息日 from tenant.businessHours ({ [dow]: {open,close,enabled} }).
    // Consecutive days sharing the same hours are merged into a range.
    const bh = tenant.businessHours || {};
    const dayLbl = en ? ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"] : ["週日","週一","週二","週三","週四","週五","週六"];
    const dowOrder = [1,2,3,4,5,6,0]; // Mon→Sun
    const hourSegs = []; const restDays = []; let curSeg = null;
    dowOrder.forEach(d => {
      const c = bh[d];
      const open = c && c.enabled !== false && (c.open || c.close);
      if (open) { const h = (c.open||"")+"–"+(c.close||""); if (curSeg && curSeg.h === h) curSeg.days.push(d); else { curSeg = { h, days:[d] }; hourSegs.push(curSeg); } }
      else { curSeg = null; if (c || Object.keys(bh).length) restDays.push(dayLbl[d]); }
    });
    const hoursStr = hourSegs.map(s => (s.days.length >= 2 ? dayLbl[s.days[0]]+"–"+dayLbl[s.days[s.days.length-1]] : dayLbl[s.days[0]]) + " " + s.h).join("、");
    const hoursHtml = (hoursStr || restDays.length)
      ? ('<div class="hours">'
        + (hoursStr ? '<span>⏰ '+(en?"Hours: ":"營業時間：")+esc(hoursStr)+'</span>' : '')
        + (restDays.length ? '<span>🛑 '+(en?"Closed: ":"休息日：")+esc(restDays.join("、"))+'</span>' : '')
        + '</div>')
      : '';
    const html = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">'
      + '<title>'+esc(tenant.name||"")+' — '+(en?"Price overview":"價目總覽")+'</title>'
      + '<style>'+css+'</style></head><body data-theme="rose" data-lang="'+(en?"en":"zh")+'" data-fname="'+esc(fname)+'">'
      + '<div class="bar">'
      + '<div class="seg"><span class="lbl">'+(en?"Design":"設計")+'</span>'+themeBtns+'</div>'
      + '<div class="seg"><span class="lbl">'+(en?"Show":"顯示")+'</span>'+filters+'</div>'
      + '<div class="seg"><button class="xbtn" data-x="png">⬇ PNG</button><button class="xbtn" data-x="jpeg">⬇ JPEG</button><button class="xbtn ghost" data-x="print">🖨 '+(en?"Print":"列印")+'</button></div>'
      + '</div>'
      + '<div class="card" id="card">'
      + '<div class="hero">'
      + '<div class="htop">'+(tenant.logoUrl?'<img class="logo" crossorigin="anonymous" src="'+esc(tenant.logoUrl)+'" alt=""/>':'<div class="logo logoph">🐾</div>')
      + '<div><h1>'+esc(tenant.name||"")+'</h1>'
      + (tenant.tagline ? '<div class="tagline">'+esc(tenant.tagline)+'</div>' : '')
      + ((tenant.address || tenant.phone) ? '<div class="contact">'+(tenant.serviceMode!=="mobile"&&tenant.address?'<span>📍 '+esc(tenant.address)+'</span>':'')+(tenant.phone?'<span>📞 '+esc(tenant.phone)+'</span>':'')+'</div>' : '')
      + hoursHtml
      + '</div></div>'
      + '<div class="pets">🐶 🐱 🐩 🐈 🦮 🐾</div></div>'
      + ''
        + ''
        + ''
        + ''
        + ''
      + tablesHtml
      + addonsHtml
      + noteHtml
      + ctaHtml
      + '<div class="foot">🐾 '+esc(tenant.name||"")+' · '+(en?"Updated ":"更新日期 ")+dstr+' · '+(en?"Generated by pettobook.com":"由 pettobook.com 製作")+'</div>'
      + '</div>'
      + '<div class="busy" id="busy">'+(en?"Exporting…":"匯出中…")+'</div>'
      + '<scr'+'ipt src="/html2canvas.min.js"><\/scr'+'ipt>'
      + '<scr'+'ipt src="/qrcode.min.js"><\/scr'+'ipt>'
      + '<scr'+'ipt src="/overview.js"><\/scr'+'ipt>'
      + '</body></html>';
    // Render in-app via an iframe (window.open is blocked in the PWA/native app).
    setOverviewHtml(html);
  };

  const saveSvc = () => {
    if (!editSvc?.name) { toast(i18nT("val.enterServiceName"), "error"); return; }
    // Force-set tenantId + petType on save so the sync layer's upsert
    // never sends a row with null tenant_id (would fail the services_write
    // RLS policy) or null pet_types (would fail the check constraint).
    const safe = { ...editSvc, tenantId: editSvc.tenantId || user.tenantId, petType: editSvc.petType || "dog", pricingMode: editSvc.pricingMode || "static", kind: editSvc.kind || "service" };
    setState(s => ({ ...s, tenants: s.tenants.map(t => {
      if (t.id !== user.tenantId) return t;
      // The 複製 flow pre-assigns a uuid before opening the edit sheet,
      // so we can't tell "edit vs. new" from `safe.id` alone — we have to
      // check whether that id is already in the services array. If yes
      // → update in place; if no → append. The previous code blindly took
      // the map() branch whenever safe.id was truthy, which silently
      // dropped cloned services since map() doesn't match anything.
      const exists = (t.services || []).some(sv => sv.id === safe.id);
      const services = exists
        ? t.services.map(sv => sv.id === safe.id ? safe : sv)
        : [...(t.services || []), { ...safe, id: safe.id || uuid() }];
      return { ...t, services };
    }) }));
    toast(safe.id ? zhEn("服務已更新", "Service updated") : zhEn("服務已新增", "Service added")); setEditSvc(null);
  };
  const delSvc = id => {
    // #25 Service-disable confirm: warn if this service has future bookings.
    // Cancelling a booked service silently breaks the calendar so we count
    // affected appointments and force the owner to confirm. Cancelled/done
    // bookings are excluded since they're historical.
    const today = dateNow();
    const affected = (state.appointments || []).filter(a =>
      a.tenantId === user.tenantId && a.svcId === id &&
      a.date >= today && a.status !== "cancelled" && a.status !== "done" && a.status !== "noshow"
    ).length;
    const svc = (tenant?.services || []).find(s => s.id === id);
    const svcName = svc?.name || i18nT("tx.svcFallback");
    const msg = affected > 0
      ? i18nT("tx.delSvcAffectsApts").replace("{name}", svcName).replace("{n}", String(affected))
      : i18nT("tx.delSvcConfirm").replace("{name}", svcName);
    if (!window.confirm(msg)) return;
    const removed = (tenant?.services || []).find(sv => sv.id === id);
    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, services: t.services.filter(sv => sv.id !== id) } : t) }));
    undoSnackbar({
      message: zhEn(`已刪除「${svcName}」`, `Deleted "${svcName}"`),
      onUndo: () => {
        if (!removed) return;
        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, services: [...t.services, removed] } : t) }));
        toast(zhEn("已撤銷刪除", "Deletion undone"), "success");
      },
    });
  };
  const saveAddon = () => {
    if (!editAddon?.name) { toast(i18nT("val.enterAddonName"), "error"); return; }
    const price = parseInt(editAddon.price) || 0;
    const petTypes = Array.isArray(editAddon.petTypes) && editAddon.petTypes.length
      ? editAddon.petTypes
      : (editAddon.petType ? [editAddon.petType] : ["dog"]);
    const safe = { ...editAddon, price, petTypes, tenantId: editAddon.tenantId || user.tenantId };
    delete safe.petType; // drop the legacy single field if it was lying around
    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, addons: safe.id ? (t.addons||[]).map(a => a.id === safe.id ? safe : a) : [...(t.addons||[]), { ...safe, id:uuid(), enabled:true }] } : t) }));
    toast(safe.id ? zhEn("加購已更新", "Add-on updated") : zhEn("加購已新增", "Add-on added")); setEditAddon(null);
  };
  const delAddon = id => {
    const removed = (tenant?.addons || []).find(a => a.id === id);
    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, addons: (t.addons||[]).filter(a => a.id !== id) } : t) }));
    undoSnackbar({
      message: zhEn(`已刪除「${removed?.name || "加購項目"}」`, `Deleted "${removed?.name || "加購項目"}"`),
      onUndo: () => {
        if (!removed) return;
        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, addons: [...(t.addons || []), removed] } : t) }));
        toast(zhEn("已撤銷刪除", "Deletion undone"), "success");
      },
    });
  };
  const saveInvItem = () => { if (!editInv?.name) { toast(i18nT("val.enterInvName"), "error"); return; } setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, inventory: editInv.id ? t.inventory.map(i => i.id === editInv.id ? editInv : i) : [...t.inventory, { ...editInv, id:uuid(), tenantId:user.tenantId, qty:parseInt(editInv.qty)||0, lowAlert:parseInt(editInv.lowAlert)||0 }] } : t) })); toast(editInv.id ? zhEn("庫存已更新", "Stock updated") : zhEn("耗材已新增", "Supply added")); setEditInv(null); };

  const [yr, mo] = calMonth.split("-").map(Number);
  const firstDay = new Date(yr, mo - 1, 1).getDay();
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const monthApts = allApts.filter(a => a.date.startsWith(calMonth) && a.status !== "cancelled");
  const scheduleApts = allApts.filter(a => a.date === selectedScheduleDate && a.status !== "cancelled");
  const timeAxis = useMemo(() => {
    const bh = tenant?.businessHours || {};
    // Seed with sentinels so the first enabled day actually wins. Old
    // seed (earliest=9, latest=17) made shops that opened 10:00-20:00
    // still render a 9:00 row because Math.min(9, 10) === 9.
    let earliest = 24, latest = 0;
    let anyEnabled = false;
    for (let d = 0; d < 7; d++) {
      if (bh[d]?.enabled) {
        anyEnabled = true;
        earliest = Math.min(earliest, parseInt(bh[d].open || "09"));
        latest = Math.max(latest, parseInt(bh[d].close || "17"));
      }
    }
    if (!anyEnabled) { earliest = 9; latest = 17; }
    const slots = []; for (let h = earliest; h < latest; h++) slots.push(String(h).padStart(2, "0") + ":00");
    return slots;
  }, [tenant?.businessHours]);
  const nowLabel = timeNow();
  const nowHour = parseInt(nowLabel.split(":")[0], 10);
  const nowMinute = parseInt(nowLabel.split(":")[1], 10);
  const showNowLine = selectedScheduleDate === dateNow() && nowHour >= 9 && nowHour < 17;
  const nowLineTop = ((nowHour - 9) * 64) + (nowMinute / 60) * 64;

  // Real weekly revenue from actual appointments
  const weekRevData = (() => {
    const today = new Date();
    return [0,1,2,3,4,5,6].map(offset => {
      const d = new Date(today); d.setDate(today.getDate() - (6 - offset));
      const ds = localDateStr(d);
      return doneApts.filter(a => a.date === ds).reduce((s, a) => s + aptRevenue(a), 0);
    });
  })();
  const maxRev = Math.max(...weekRevData);
  // Match by svcId first (stable through renames) and fall back to the
  // legacy `service` name string for rows from before svcId was wired.
  // Service ranking is computed inside REPORTS.topSvc against
  // rangedApts so it honours the date range picker. The legacy
  // all-time svcBreakdown was removed because owners expected the
  // ranking to refresh when they changed the period.

  // Owner bottom nav. 員工 + 套票 / 儲值卡 / 積分 管理 live on the bar (moved
  // back off the hamburger drawer); 分析 + 評價 are accessible via 設定
  // sub-tabs. The bar scrolls horizontally to fit. 庫存 + 活動記錄 stay
  // folded into settings. (packages/wallets/points are settings subtabs —
  // the bottom-nav handler routes them via settingsSubtab, see below.)
  // Unread WhatsApp count for the 訊息 tab badge. Refetched on mount, every
  // 60s, and whenever the active tab changes (so reading in the inbox then
  // leaving clears it).
  const [waUnread, setWaUnread] = useState(0);
  useEffect(() => {
    if (user.role !== "owner") return;
    let alive = true;
    const load = () => fetchWaUnread(user.tenantId).then(n => { if (alive) setWaUnread(n); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user.role, user.tenantId, tab]);
  // 收銀 (POS) is on every paid plan. Free owners see it locked, as an
  // upgrade prompt; free staff don't see it.
  const posLocked = !tenant?.posEnabled && user.role === "owner";
  const posTab = (tenant?.posEnabled || posLocked) ? [{ id:"pos",icon:ShoppingBag,label:(_ownerLoc !== "zh-HK" ? "POS" : zhEn("收銀", "POS")) + (posLocked ? " 🔒" : "") }] : [];
  const ownerTabs = [
    { id:"schedule",icon:Calendar,label:i18nT("owner.tab.schedule", _ownerLoc !== "zh-HK" ? "Calendar" : "日歷") },
    ...posTab,
    { id:"services",icon:Scissors,label:i18nT("owner.tab.services", _ownerLoc !== "zh-HK" ? "Services" : "服務") },
    { id:"crm",icon:Users,label:i18nT("owner.tab.crm", _ownerLoc !== "zh-HK" ? "Customers" : "顧客") },
    { id:"wainbox",icon:MessageCircle,label:_ownerLoc !== "zh-HK" ? "Inbox" : "訊息" },
    { id:"roster",icon:CalendarDays,label:i18nT("owner.tab.roster", _ownerLoc !== "zh-HK" ? "Roster" : "排班") },
    { id:"staff",icon:Briefcase,label:i18nT("st.title") },
    ...(PAYROLL_ENABLED ? [{ id:"salary",icon:DollarSign,label:i18nT("st.staff.payroll.title") }] : []),
    { id:"packages-admin",icon:Package,label:i18nT("lang.name") === "English" ? "Packages" : "套票" },
    { id:"wallets-admin",icon:Wallet,label:i18nT("lang.name") === "English" ? "Wallets" : "儲值卡" },
    { id:"points-admin",icon:Award,label:i18nT("lang.name") === "English" ? "Points" : "積分" },
    { id:"marketing",icon:Megaphone,label:_ownerLoc !== "zh-HK" ? "Marketing" : "推廣" },
    { id:"analytics",icon:TrendingUp,label:_ownerLoc !== "zh-HK" ? "Reports" : zhEn("報表", "Reports") },
    { id:"settings",icon:Settings,label:i18nT("owner.tab.settings") },
  ];
  const staffTabs = [
    { id:"schedule",icon:Calendar,label:i18nT("owner.tab.schedule", _ownerLoc !== "zh-HK" ? "Calendar" : "日歷") },
    ...posTab,
    { id:"crm",icon:Users,label:i18nT("owner.tab.crm", _ownerLoc !== "zh-HK" ? "Customers" : "CRM") },
    { id:"roster",icon:CalendarDays,label:i18nT("owner.tab.roster", _ownerLoc !== "zh-HK" ? "Roster" : "排班") },
  ];
  const tabs = user.role === "owner" ? ownerTabs : staffTabs;

  // 30-min-before reminder via browser Notification API. Runs only on
  // owner/staff devices where the app is open. Reschedules whenever the
  // appointment list, date, time, or status changes. We track fired IDs
  // in localStorage so refreshing the page within the 30-min window
  // doesn't re-fire the same reminder. Cancelled/done appointments are
  // skipped automatically (filter on status). Past dates are skipped.
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const apts = (state.appointments || []).filter(a =>
      a.tenantId === user.tenantId &&
      // Include shop events (kind === "event") alongside live appointments
      // — owner blocks like 員工會議 / 午餐 / 外出採購 also deserve a 30-min
      // heads-up. Events have no status field, so we match by kind.
      (a.kind === "event" || a.status === "pending" || a.status === "checked-in") &&
      a.date && a.time
    );
    const FIRED_KEY = "petos.apt.reminded";
    let fired = {};
    try { fired = JSON.parse(localStorage.getItem(FIRED_KEY) || "{}"); } catch { fired = {}; }
    const cutoff = Date.now() - 6 * 3600 * 1000;
    fired = Object.fromEntries(Object.entries(fired).filter(([, ts]) => Number(ts) > cutoff));
    const timers = [];
    const now = Date.now();
    apts.forEach(a => {
      const startMs = new Date(a.date + "T" + a.time + ":00").getTime();
      if (!startMs || isNaN(startMs)) return;
      const fireAt = startMs - 30 * 60 * 1000;
      const key = a.id + "@" + a.date + "T" + a.time;
      if (fired[key]) return;
      if (startMs < now) return;
      const delay = Math.max(0, fireAt - now);
      if (delay > 12 * 3600 * 1000) return;
      const t = setTimeout(() => {
        try {
          if (Notification.permission !== "granted") return;
          const staff = a.staffId ? state.users?.[a.staffId] : null;
          // Events get a different shape — no pet / customer, just the
          // owner-set label + staff + note. Keep the lock-screen content
          // short so the owner sees it before unlocking.
          if (a.kind === "event") {
            const leaveLbl = ({vacation:zhEn("請假", "Leave"),sick:zhEn("病假", "Sick leave"),training:zhEn("進修", "Training"),personal:zhEn("個人", "Personal")})[a.leaveType] || "";
            const label = a.label || leaveLbl || zhEn("店舖事件", "Shop event");
            const durMin = a.customDuration || 30;
            const eventTitle = zhEn("📌 30 分鐘後：", "📌 In 30 minutes: ") + label;
            const eventLines = [
              a.time + " · " + durMin + zhEn(" 分鐘", " min") + (leaveLbl && a.label ? " · " + leaveLbl : ""),
              staff?.name ? zhEn("員工：", "Staff: ") + staff.name : null,
              a.staffNote ? zhEn("備註：", "Note:") + a.staffNote : null,
            ].filter(Boolean);
            new Notification(eventTitle, {
              body: eventLines.join("\n"),
              tag: "apt-remind-" + a.id,
              icon: "/favicon.ico",
            });
            const next = { ...fired, [key]: Date.now() };
            localStorage.setItem(FIRED_KEY, JSON.stringify(next));
            return;
          }
          // BUG fix: state.customers doesn't exist — customers are in
          // state.users, keyed by id. Previous lookup was always null,
          // so the lock-screen notification only ever showed "毛孩" with
          // no owner / service name / staff context.
          const pet = state.pets?.[a.petId];
          const cust = state.users?.[a.customerId];
          // Service name + headcount of addons for at-a-glance context.
          const svcName = a.service || zhEn("美容", "Grooming");
          const addonCount = (a.addons || []).length;
          const addonChip = addonCount > 0 ? " +" + addonCount + zhEn(" 加購", " add-ons") : "";
          // Title — pet + service so the owner sees the most useful info
          // before unlocking the device.
          const titleName = pet?.name || cust?.name || zhEn("預約", "booking");
          const title = zhEn("⏰ 30 分鐘後：", "⏰ In 30 minutes: ") + titleName;
          // Body — one line per fact, easy to scan on the lock screen.
          const lines = [
            a.time + " · " + svcName + addonChip,
            cust?.name ? zhEn("主人：", "Owner: ") + cust.name + (cust?.phone ? " · " + cust.phone : "") : null,
            pet ? (pet.name || zhEn("毛孩", "Pet")) + (pet.breed ? " · " + pet.breed : "") + (pet.weight ? " · " + pet.weight + "kg" : "") : null,
            staff?.name ? zhEn("美容師：", "Groomer: ") + staff.name : null,
          ].filter(Boolean);
          new Notification(title, {
            body: lines.join("\n"),
            tag: "apt-remind-" + a.id,
            icon: "/favicon.ico",
          });
          const next = { ...fired, [key]: Date.now() };
          localStorage.setItem(FIRED_KEY, JSON.stringify(next));
        } catch (e) { console.warn("[reminder] notify failed:", e); }
      }, delay);
      timers.push(t);
    });
    localStorage.setItem(FIRED_KEY, JSON.stringify(fired));
    return () => { timers.forEach(t => clearTimeout(t)); };
  }, [state.appointments, state.pets, state.customers, user.tenantId]);

  // Inline shop-page preview overlay. Renders the same PublicShopHome the
  // customer sees, but with tap-to-edit overlays on every section. Owner
  // taps a section → we close the preview and jump straight to the right
  // settings subtab. Faster than hunting through 設定.
  const renderPreviewOverlay = () => {
    if (!previewOpen || !tenant) return null;
    const onEditSection = (target) => {
      if (target && target.startsWith("tab:")) {
        const t = target.slice(4);
        setPreviewOpen(false);
        setTab(t);
        toast(zhEn("已跳轉 →", "Jumped →"), "info");
        return;
      }
      jumpToSetting(target);
    };
    return (
      <div style={{position:"fixed", inset:0, zIndex:9000, background:"#FCFCFF", overflowY:"auto"}}>
        {/* Top banner — explains the edit mode + close button */}
        <div style={{position:"sticky", top:0, zIndex:50, background:"linear-gradient(135deg,#FF4D6D,#FF7AA2)", color:"#fff", padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:"0 6px 18px rgba(255,77,109,0.35)"}}>
          <div style={{display:"flex", alignItems:"center", gap:8, minWidth:0}}>
            <span style={{fontSize:18}}>👁️</span>
            <div style={{minWidth:0}}>
              <p style={{fontSize:13, fontWeight:900, margin:0, letterSpacing:0.3}}>{zhEn("店面預覽 — 點任何區域去設定", "Shop preview — tap any area to edit its setting")}</p>
              <p style={{fontSize:10, fontWeight:600, margin:0, opacity:0.9}}>{zhEn("客戶睇到嘅預約落地頁；店主可即點即改", "The booking page your customers see; owners can tap to edit")}</p>
            </div>
          </div>
          <button onClick={() => setPreviewOpen(false)} style={{background:"rgba(255,255,255,0.22)", border:"1px solid rgba(255,255,255,0.4)", color:"#fff", borderRadius:99, padding:"6px 14px", fontSize:13, fontWeight:900, cursor:"pointer"}}>{zhEn("✕ 關閉", "✕ Close")}</button>
        </div>
        {/* Render the actual customer-facing booking landing — the stylish
            page with hero + welcome card + 首次預約流程 + info cards. We
            pass profile=null so the auth card always shows, and editMode
            so every section has a tap-to-edit overlay. */}
        <PublicBooking
          tenant={tenant}
          state={state}
          setState={() => {}}
          profile={null}
          onDone={() => {}}
          editMode={true}
          onEditSection={onEditSection}
        />
      </div>
    );
  };


  return (
    <div style={{minHeight:"100vh",background:"#FCFCFF",paddingBottom:88}}>
      {renderPreviewOverlay()}
      <PushPermissionBanner user={user} />
      {showOnboarding && tenant && <OnboardingWizard tenant={tenant} tenantId={user.tenantId} setState={setState} onClose={dismissOnboarding} onGoto={(t) => { setTab(t); dismissOnboarding(); }} bookingUrlPrefix={state.platformConfig?.bookingUrlPrefix || ((state.platformConfig?.domain || "pettobook.com") + "/shop/")} />}
      <div style={{background:"linear-gradient(135deg, var(--c-grad1), var(--c-primary))",padding:"24px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:"0 0 32px 32px",boxShadow:"0 10px 30px var(--c-primary-30)",position:"relative",zIndex:10,overflow:"hidden"}}>
        <div style={{position:"absolute",top:8,right:80,opacity:0.12,display:"flex",gap:6}}>
          <PetIcon type="paw" size={18} /><PetIcon type="bone" size={16} /><PetIcon type="sparkle" size={14} /><PetIcon type="bath" size={16} />
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {/* Hamburger — opens the side drawer with 員工 / 活動記錄 + staff filter */}
          <button onClick={() => setDrawerOpen(true)} aria-label={zhEn("開啟選單", "Open menu")} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
            <Menu size={20} color="#fff" strokeWidth={2.5} />
          </button>
          <div style={{minWidth:0}}>
            <span style={{fontWeight:900,color:"#fff",fontSize:18,display:"block",margin:"0 0 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.name}</span>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              {user.role === "owner"
                ? <button onClick={openPlanPage} aria-label={zhEn("訂閱方案", "Subscription plan")} style={{background:"none",border:"none",padding:0,cursor:"pointer"}}>
                    <Badge color="#fff" bg="rgba(255,255,255,0.25)">{planName(tenant?.plan || "free", getPlans(state.platformConfig))}{trialTag(freeTrialOf(tenant))} ›</Badge>
                  </button>
                : <Badge color="#fff" bg="rgba(255,255,255,0.25)">{planName(tenant?.plan || "free", getPlans(state.platformConfig))}</Badge>}
              {lowStock.length > 0 && <Badge color="#fff" bg="#EF4444">{"⚠️ " + lowStock.length + zhEn("項", "lines")}</Badge>}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {/* View-mode switching now lives in the hamburger drawer; the
              header stays compact with just search / bell / logout. */}
          <button onClick={() => setShowGlobalSearch(true)} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}} title={zhEn("搜尋", "Search")}><Search size={20} color="#fff" strokeWidth={2.5} /></button>
          {/* 🔗 Share booking link — opens a quick-share sheet with the
              public URL, copy button, native share, and a printable QR
              poster. Solves "owners don't know where their customer
              page is" — previously the link was buried inside Settings
              → Shop Basic. */}
          <button onClick={() => setShareSheetOpen(true)} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}} title={zhEn("分享預約連結", "Share booking link")}><Link size={20} color="#fff" strokeWidth={2.5} /></button>
          {/* 詢價 inbox hidden until the feature is ready end-to-end
              (no notifications, no anti-spam, customer side still WIP). */}
          {canSeeRfqInbox && <button onClick={() => setRfqInboxOpen(true)} title={zhEn("詢價收件箱", "Quote request inbox")} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative"}}>
            <span style={{fontSize:20}}>📨</span>
            {rfqInboxCount > 0 && <span style={{position:"absolute",top:-2,right:-2,background:"#EF4444",color:"#fff",fontSize:10,fontWeight:900,borderRadius:99,padding:"2px 6px",border:"2px solid var(--c-primary)",minWidth:18,textAlign:"center"}}>{rfqInboxCount > 99 ? "99+" : rfqInboxCount}</span>}
          </button>}
          <NotifBell userId={user.id} state={state} setState={setState} onOpenApt={(apt) => setEditApt(apt)} onViewDay={(apt) => { goToScheduleDate(apt.date); setViewMode("day"); setTab("schedule"); }} />
          <button onClick={onLogout} style={{background:"rgba(255,255,255,0.25)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}><LogOut size={20} color="#fff" strokeWidth={2.5} /></button>
        </div>
      </div>

      {/* Top tab bar moved to bottom (see fixed bar near the end of OwnerApp).
          Keep this empty div to preserve the negative margin tweak that
          pulled the original pill row over the header gradient — without
          it the content would jump up unexpectedly. */}
      <div style={{marginTop:-10,paddingBottom:8}} />

      <div style={{padding:"16px 16px 8px",maxWidth:tab === "pos" ? 1280 : 720,margin:"0 auto"}}>
        {tab === "dashboard" && <>
          {/* #6 Today-closed quick toggle. Adds today to tenant.holidays so
              all the existing closed-day logic (booking blocking, calendar
              greying) kicks in. One tap on / off without having to visit
              the 公休日設定 page or block out individual staff slots. */}
          {(() => {
            const today = dateNow();
            const closed = (tenant?.holidays || []).includes(today);
            const toggle = () => {
              setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? {
                ...t,
                holidays: closed ? (t.holidays || []).filter(d => d !== today) : [...new Set([...(t.holidays || []), today])],
              } : t) }));
              toast(closed ? zhEn("已重新營業 ✅", "Reopened ✅") : zhEn("今日已標記休息 🏖️", "Marked closed today 🏖️"), closed ? "success" : "warning");
            };
            return (
              <div onClick={toggle} role="button" style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:16,marginBottom:12,cursor:"pointer",background: closed ? "linear-gradient(135deg,#FEF3C7,#FDE68A)" : "var(--c-lighter)",border:"2px solid " + (closed ? "#FCD34D" : "var(--c-border)")}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>{closed ? "🏖️" : "🏪"}</span>
                  <div>
                    <p style={{fontSize:13,fontWeight:900,margin:0,color: closed ? "#92400E" : "#4A4A68"}}>{closed ? i18nT("owner.shopClosed") : i18nT("owner.shopOpen")}</p>
                    <p style={{fontSize:11,fontWeight:700,margin:0,color: closed ? "#B45309" : "#8A8A9E"}}>{closed ? i18nT("owner.shopClosed.hint", _ownerLoc !== "zh-HK" ? "Tap to re-open" : "輕按重新開店") : i18nT("owner.shopOpen.hint", _ownerLoc !== "zh-HK" ? "Tap to close shop today" : "輕按一鍵設今日休息")}</p>
                  </div>
                </div>
                <Toggle on={closed} onChange={toggle} />
              </div>
            );
          })()}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <StatCard icon={Calendar} label={i18nT("owner.appointmentsToday")} value={todayApts.length} color="var(--c-primary)" />
            {canView("viewRevenue")
              ? <StatCard icon={DollarSign} label={i18nT("owner.revenueToday")} value={"$" + revenue} color="#10B981" />
              : <Card style={{padding:20,display:"flex",alignItems:"center",justifyContent:"center",background:"#F3F3F8"}}><Lock size={24} color="#AFAFC4" /></Card>
            }
          </div>
          {lowStock.length > 0 && <Card style={{padding:20,background:"#FEF2F2",border:"2px solid #FECACA",marginBottom:16}}>
            <h3 style={{fontWeight:900,color:"#991B1B",margin:"0 0 12px",display:"flex",alignItems:"center",gap:8,fontSize:16}}><AlertTriangle size={18} strokeWidth={2.5} />{i18nT("own.dash.lowStock")}</h3>
            {lowStock.map(i => <div key={i.id} style={{display:"flex",justifyContent:"space-between",fontSize:14,padding:"10px 16px",background:"#FFFDFD",borderRadius:16,marginBottom:6}}><span style={{color:"#DC2626",fontWeight:700}}>{i.name}</span><span style={{fontWeight:900,color:"#991B1B"}}>{zhEn("剩 ", "Left ") + i.qty + zhEn("（警戒: ", " (alert: ") + i.lowAlert + zhEn("）", ")")}</span></div>)}
          </Card>}
          {/* Stale-apt nag: past-date appointments still in pending / checkedin
              / grooming. Cron auto-closes nightly, but owner should clean
              these up so cash + status reflect reality. */}
          {(() => {
            const today = dateNow();
            const stale = allApts.filter(a => a.kind !== "event" && a.date && a.date < today && (a.status === "pending" || a.status === "checkedin" || a.status === "grooming") && !isAptDead(a));
            const unpaidDone = allApts.filter(a => a.status === "done" && a.date && a.date < today && !a.paid && ((a.basePrice||0) + (a.addonPrice||0) - (a.deposit||0)) > 0);
            const totalIssues = stale.length + unpaidDone.length;
            if (totalIssues === 0) return null;
            return (
              <Card onClick={() => { goToScheduleDate(stale[0]?.date || unpaidDone[0]?.date || today); setTab("schedule"); setViewMode("agenda"); }} style={{padding:18,background:"#FFFBEB",border:"2px solid #FDE68A",marginBottom:16,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                  <div style={{width:40,height:40,background:"#D97706",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><AlertTriangle size={20} color="#fff" strokeWidth={2.5} /></div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontWeight:900,fontSize:15,margin:"0 0 4px",color:"#92400E"}}>{i18nT("own.dash.staleBanner")} ({totalIssues})</p>
                    <p style={{fontSize:12,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.5,opacity:0.85}}>
                      {stale.length > 0 && <><strong>{stale.length}</strong>{i18nT("own.dash.staleBannerStale")}</>}
                      {unpaidDone.length > 0 && <> <strong>{unpaidDone.length}</strong>{i18nT("own.dash.staleBannerUnpaid")}</>}
                      {" " + i18nT("own.dash.staleBannerTap")}
                    </p>
                  </div>
                  <ChevronRight size={18} color="#92400E" strokeWidth={2.8} />
                </div>
              </Card>
            );
          })()}
          <Card style={{padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:8}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.dash.appointmentsToday") + " (" + todayApts.length + ")"} <HelpTip topic="statusFlow" /></h3>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {/* Quick-care walk-in: customer only wants 洗耳 / 剪甲 etc.
                    Looks up the 快速護理 service + matching addons. If any
                    are missing (existing shops created before this commit
                    won't have the seeded defaults), auto-creates them on
                    confirmation so the button works on first tap forever
                    after. */}
                <Btn size="sm" variant="secondary" onClick={async () => {
                  let quickSvc = (tenant?.services || []).find(s => s.name && s.name.indexOf("快速護理") === 0 && s.enabled !== false);
                  let ads = tenant?.addons || [];
                  let earAddon = ads.find(a => a.name === "洗耳" && a.enabled !== false);
                  let nailAddon = ads.find(a => a.name === "剪甲" && a.enabled !== false);
                  const needToCreate = !quickSvc || !earAddon || !nailAddon;
                  if (needToCreate) {
                    if (!window.confirm(zhEn("店舖未設定「快速護理」服務同「洗耳 / 剪甲」加購。\n按確定即時為你建立：\n• 快速護理（底價 $0，15 分鐘）\n• 洗耳 $50（10 分鐘）\n• 剪甲 $50（10 分鐘）\n\n之後可在 服務管理 修改價格 / 時間。", "The shop has no \"Quick care\" service or \"ear cleaning / nail trim\" add-ons set up.\nTap OK to create them now:\n• Quick care (base price $0, 15 min)\n• Ear cleaning $50 (10 min)\n• Nail trim $50 (10 min)\n\nYou can change the prices / times later in Service management."))) return;
                    try {
                      const newPatches = { services: [...(tenant?.services || [])], addons: [...(tenant?.addons || [])] };
                      if (!quickSvc) {
                        const created = await upsertService({
                          id: uuid(), tenantId: user.tenantId,
                          name: "快速護理 (洗耳/剪甲)", kind: "service",
                          description: "", basePrice: 0, estimatedMinutes: 15, bufferMinutes: 0,
                          pricingMode: "static", matrixPricing: null,
                          smartPricing: { enabled: false, rules: [] },
                          enabled: true, displayOrder: 99, petType: "dog",
                          customerVisible: false,
                        });
                        if (created) { quickSvc = created; newPatches.services.push(created); }
                      }
                      if (!earAddon) {
                        const ea = await upsertAddon({ id: uuid(), tenantId: user.tenantId, name: "洗耳", price: 50, pricingMode: "flat", variants: [], enabled: true, estimatedMinutes: 10 });
                        if (ea) { earAddon = ea; newPatches.addons.push(ea); }
                      }
                      if (!nailAddon) {
                        const na = await upsertAddon({ id: uuid(), tenantId: user.tenantId, name: "剪甲", price: 50, pricingMode: "flat", variants: [], enabled: true, estimatedMinutes: 10 });
                        if (na) { nailAddon = na; newPatches.addons.push(na); }
                      }
                      // Mirror into local state immediately so the walk-in
                      // form below sees the new ids.
                      setState(s => ({
                        ...s,
                        tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, services: newPatches.services, addons: newPatches.addons } : t),
                      }));
                      toast(zhEn("已自動建立快速護理服務 ✓", "Quick care service created ✓"));
                    } catch (e) {
                      console.warn("[PettoBook] quick-care seed failed:", e);
                      toast(zhEn("自動建立失敗：", "Auto-create failed: ") + (e?.message || e) + zhEn("，請手動到 服務管理 新增。", " — please add it manually in Service management."), "error");
                      return;
                    }
                  }
                  const prefAddons = [earAddon?.id, nailAddon?.id].filter(Boolean);
                  setWalkInForm({
                    customerId:"", petId:"",
                    svcId: quickSvc.id,
                    staffId:"", time:timeNow(),
                    addons: prefAddons,
                    spaIds:[],
                    discountAmount:0, discountType:"percent", discountReason:"",
                    staffRemark:"洗耳剪甲快速護理",
                  });
                }} style={{background:"#FEF3C7",color:"#92400E",border:"2px solid #FDE68A"}}>
                  🛁 {i18nT("lang.name") === "English" ? "Quick care" : "洗耳剪甲"}
                </Btn>
                <Btn size="sm" onClick={() => setWalkInForm({ customerId:"", petId:"", svcId:"", staffId:"", time:timeNow(), addons:[], spaIds:[], discountAmount:0, discountType:"percent", discountReason:"", staffRemark:"" })} style={{background:"#10B981",color:"#fff",boxShadow:"0 4px 12px rgba(16,185,129,0.3)"}}>
                  <Plus size={14} strokeWidth={3} /> {i18nT("own.dash.walkinBtn")}
                </Btn>
              </div>
            </div>
            {todayApts.length === 0 && <EmptyState icon={Calendar} title={zhEn("今日暫無預約", "No bookings today")} sub={zhEn("享受悠閒的一天 ☀️", "Enjoy a relaxed day ☀️")} />}
            {todayApts.map((a, i) => { const cust = state.users[a.customerId], pet = state.pets[a.petId]; const surTotal = (a.surcharges||[]).reduce((s,x)=>s+x.amount,0); const totalPrice = a.basePrice + a.addonPrice + surTotal; return (
              <div key={a.id} style={{padding:"16px 0",borderBottom:i===todayApts.length-1?"none":"2px solid var(--c-light)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <p style={{fontWeight:900,margin:0,fontSize:17,color:"#4A4A68"}}>{pet?.name || ""}</p>
                      <span style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{"(" + (cust?.name||"") + ")"}</span>
                    </div>
                    <p style={{fontSize:14,color:"#6A6A85",margin:"0 0 4px",fontWeight:600}}>{a.time + " · " + a.service + " · " + (state.users[a.staffId]?.name||"")}</p>
                    {/* Pricing breakdown */}
                    <div style={{fontSize:12,color:"#8A8A9E",fontWeight:700,lineHeight:1.6}}>
                      <span>{i18nT("summary.mainService") + " $" + a.basePrice}</span>
                      {a.addonPrice > 0 && <span>{zhEn(" + 加購 $", " + add-ons $") + a.addonPrice}</span>}
                      {surTotal > 0 && <span style={{color:"#D97706"}}>{zhEn(" + 附加 $", " + surcharges $") + surTotal}</span>}
                      <span style={{color:"var(--c-primary)",fontWeight:900}}>{" = $" + totalPrice}</span>
                    </div>
                    {/* #12: Always show paid vs. due so staff at the counter
                        knows exactly how much cash/card to collect on pickup
                        without flipping to the edit sheet. paid logic:
                        - a.paid=true → fully settled
                        - else deposit subtracts from total */}
                    {(() => {
                      const paid = a.paid ? totalPrice : (a.deposit || 0);
                      const due = Math.max(0, totalPrice - paid);
                      return (
                        <div style={{fontSize:12,fontWeight:800,marginTop:3,display:"flex",gap:6,alignItems:"center"}}>
                          <span style={{color:paid>0?"#10B981":"#AFAFC4"}}>{i18nT("summary.paid") + " $" + paid}</span>
                          <span style={{color:"#D1D5DB"}}>·</span>
                          <span style={{color:due>0?"#DC2626":"#10B981"}}>{due>0 ? (i18nT("summary.due") + " $" + due) : i18nT("summary.settled")}</span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const spaNames = (a.spaIds || []).map(id => (tenant?.services || []).find(sv => sv.id === id)?.name).filter(Boolean);
                      const cleanAddons = (a.addons || []).filter(n => !String(n).startsWith("💆"));
                      const parts = [...spaNames.map(n => "💆 " + n), ...cleanAddons];
                      return parts.length > 0 ? <p style={{fontSize:11,color:"#AFAFC4",margin:"2px 0 0",fontWeight:700}}>{zhEn("加購: ", "Add-ons: ") + parts.join("、")}</p> : null;
                    })()}
                    {pet && <PetAlertChips pet={pet} appointments={allApts} tenant={tenant} size="sm" />}
                    {a.staffNote && <p style={{fontSize:12,color:"var(--c-primary)",margin:"4px 0 0",background:"var(--c-lighter)",padding:"4px 8px",borderRadius:10,display:"inline-block",fontWeight:700}}>{"📝 " + a.staffNote}</p>}
                    {a.status === "grooming" && <GroomingTimer apt={a} tenant={tenant} />}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                    <StatusBadge status={a.status} cancelReason={a.cancelReason} approvalPending={a.approvalStatus === "pending"} />
                    <button onClick={() => setEditApt(a)} style={{background:"var(--c-light)",border:"none",borderRadius:99,padding:6,cursor:"pointer",display:"flex"}}><Edit size={14} color="var(--c-primary)" strokeWidth={2.5} /></button>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {/* Approval mode — accept/reject come FIRST so the shop
                      decides before any workflow buttons make sense. */}
                  {a.approvalStatus === "pending" && a.status === "pending" && <>
                    <Btn size="sm" onClick={() => approveAppointment(a)} style={{background:"#10B981",color:"#fff",boxShadow:"0 4px 12px rgba(16,185,129,0.3)"}}>{zhEn("✓ 接受預約", "✓ Accept")}</Btn>
                    <Btn variant="ghost" size="sm" onClick={() => rejectAppointment(a)} style={{color:"#EF4444",background:"#FEF2F2"}}>{zhEn("✗ 拒絕", "✗ Decline")}</Btn>
                  </>}
                  {a.status === "pending" && <Btn variant="secondary" size="sm" onClick={() => setReweighSheet({ aptId:a.id, weight:String(pet?.weight||"") })} style={{background:"#DBEAFE",color:"#2563EB"}}>{zhEn("📍 到店", "📍 Check in")}</Btn>}
                  {(a.status === "pending" || a.status === "checkedin") && <Btn variant="secondary" size="sm" onClick={() => updateStatus(a.id, "grooming")}>{zhEn("✂️ 開始美容", "✂️ Start grooming")}</Btn>}
                  {a.status === "grooming" && <Btn size="sm" onClick={async () => {
                    await updateStatus(a.id, "done");
                    if (tenant?.healthReportEnabled !== false) setTimeout(() => setReportSheet(a.id), 200);
                  }}>{tenant?.healthReportEnabled !== false ? zhEn("完成 & 報告", "Done & report") : zhEn("完成", "Done")}</Btn>}
                  {/* POS shops: one 收款 button, straight to the till. */}
                  {a.status === "done" && !a.paid && <Btn size="sm" onClick={() => tenant?.posEnabled && aptRemainingBalance(a) > 0 ? sendAptToPos(a.id) : openCollect(a.id)} style={{background:"#10B981",color:"#fff"}}>{tenant?.posEnabled ? <ShoppingBag size={14} /> : <DollarSign size={14} />} {zhEn("收款 $", "Collect $")}{aptRemainingBalance(a)}</Btn>}
                  {tenant?.posEnabled && !a.paid && a.status !== "cancelled" && a.status !== "done" && aptRemainingBalance(a) > 0 && <Btn size="sm" variant="secondary" onClick={() => sendAptToPos(a.id)}><ShoppingBag size={14} /> POS</Btn>}
                  {/* #9 Receipt button — opens a printable window with a
                      simple thermal-receipt layout. Customer can be handed
                      paper / PDF / shared via email outside the app. */}
                  {a.status === "done" && <Btn size="sm" variant="secondary" onClick={() => printReceipt(a, { tenant, customer: cust, pet })}><Printer size={14} /> {zhEn("收據", "Receipt")}</Btn>}
                  {a.status === "done" && a.paid && <span style={{fontSize:13,color:"#10B981",fontWeight:800,display:"flex",alignItems:"center",gap:4,background:"#F0FDF4",padding:"5px 10px",borderRadius:99}}><Check size={13} strokeWidth={3} />{zhEn("已收款", "Paid")}</span>}
                  {a.status === "done" && cust?.phone && <Btn variant="secondary" size="sm" onClick={async () => {
                    // Compute the album-share link IF photos exist. If a
                    // token isn't minted yet (photos uploaded via the 📸
                    // sheet but customer never sent), mint one inline so
                    // the manual-send message never goes out with photos
                    // present but no link.
                    const baCount = aptBeforeAfterPhotos(a).length;
                    let token = a.publicPhotoToken;
                    if (baCount > 0 && !token) {
                      token = "al" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
                      try {
                        await upsertAppointment({ ...a, publicPhotoToken: token });
                        setState(s => ({ ...s, appointments: s.appointments.map(x => x.id === a.id ? { ...x, publicPhotoToken: token } : x) }));
                      } catch (e) {
                        // Phase 1.2 — silent loss prevention. Previously
                        // we console.warn'd + nullified the token, then
                        // sent the message with NO album URL even though
                        // the customer was expecting one. Now block the
                        // send entirely and tell the owner what to do.
                        console.warn("[PettoBook] album token mint failed at manual-send:", e);
                        toast(zhEn("相簿連結未能建立，請先到相簿分頁 📸 試一次再發送", "Couldn't create the album link — try the album tab 📸 first, then send again"), "error");
                        return;
                      }
                    }
                    const albumUrl = (baCount > 0 && token)
                      ? ((typeof window !== "undefined" ? window.location.origin : "") + "/album/" + token)
                      : "";
                    const albumBlock = albumUrl ? "\n\n📸 美容紀念冊（前後對比）：\n" + albumUrl : "";
                    setSendNotif({ type:"grooming_done", data:{
                      shopName: tenant?.name||"",
                      customerName: cust?.name||"",
                      petName: pet?.name||"",
                      phone: cust?.phone||"",
                      skin: a.report?.skin||"正常", ear: a.report?.ear||"正常", teeth: a.report?.teeth||"正常", mood: a.report?.mood||"良好",
                      nailTrim: a.report?.nailTrim ?? a.report?.nails, nails: a.report?.nailTrim ?? a.report?.nails, analGlands: a.report?.analGlands, parasites: a.report?.parasites,
                      eyeDischarge: a.report?.eyeDischarge, matting: a.report?.matting,
                      notes: a.report?.notes||"",
                      surcharges: a.surcharges || [],
                      totalPaid: a.basePrice + a.addonPrice + (a.surcharges||[]).reduce((s,x)=>s+x.amount,0),
                      depositPaid: a.deposit,
                      nextRecommendDays: 60,
                      albumUrl,
                      albumBlock,
                    } });
                  }}><Send size={14} /></Btn>}
                  {/* #13 No-show prompt: open a confirm modal that previews
                      the shop's auto-fee and lets the owner waive it for
                      this specific case (e.g. customer rang to apologise).
                      Default action still applies the policy fee. */}
                  {a.status === "pending" && <Btn variant="ghost" size="sm" onClick={() => setNoshowConfirm(a)} style={{color:"#D97706"}}>{zhEn("未到店", "No-show")}</Btn>}
                  <Btn variant="ghost" size="sm" onClick={() => { setNoteApt(a.id); setNoteText(a.staffNote||""); }} style={{background:"var(--c-light)"}}><StickyNote size={14} strokeWidth={2.5} /></Btn>
                  {a.status === "cancelled" && <span style={{fontSize:12,color:"#EF4444",fontWeight:700}}>{a.cancelReason}</span>}
                  {a.status === "noshow" && <span style={{fontSize:12,color:"#D97706",fontWeight:700}}>{zhEn("⚠️ 未到店", "⚠️ No-show")}</span>}
                </div>
              </div>
            ); })}
          </Card>
        </>}

        {tab === "schedule" && <FeatureGate tenantId={user.tenantId} feature="scheduling" state={state} setState={setState}>
          {(() => {
            const weekDates = Array.from({length:7}, (_,i) => {
              const d = new Date(weekStart); d.setDate(d.getDate()+i); return localDateStr(d);
            });
            const shiftWeek = n => {
              const d = new Date(weekStart); d.setDate(d.getDate()+n*7);
              setWeekStart(localDateStr(d));
            };

            // Prepend an "unassigned" pseudo-lane so bookings with staffId=null
            // (visitor flow without a groomer pick) actually appear on the
            // calendar. Owner can drag-drop from this lane onto a real staff
            // column to assign. The lane id is a sentinel (no staff matches it).
            const UNASSIGNED_ID = "__unassigned__";
            const hasUnassignedApts = allApts.some(a => !a.staffId && a.date === selectedScheduleDate && a.status !== "cancelled");
            const unassignedLane = hasUnassignedApts ? [{ id: UNASSIGNED_ID, name: i18nT("own.schedule.unassigned"), isUnassigned: true }] : [];
            const visibleStaff = [...unassignedLane, ...staffList.filter(s => !hiddenStaff[s.id])];
            const timeAxis2 = timeAxis;
            const earliestHour = timeAxis2.length > 0 ? parseInt(timeAxis2[0].split(":")[0]) : 9;
            const latestHour = timeAxis2.length > 0 ? parseInt(timeAxis2[timeAxis2.length-1].split(":")[0]) + 1 : 17;
            const nowHr2 = parseInt(timeNow().split(":")[0]);
            const nowMn2 = parseInt(timeNow().split(":")[1]);
            const nowLine2 = ((nowHr2 - earliestHour) * hourHeight) + (nowMn2 / 60) * hourHeight;
            const moveAppointment = (aptId, newStaffId, newTime) => {
              const myApt = allApts.find(a => a.id === aptId);
              if (!myApt) return;
              // Translate the sentinel lane id back to null so the appointment
              // row's staff_id satisfies the FK constraint.
              const targetStaffId = newStaffId === UNASSIGNED_ID ? null : newStaffId;
              if (targetStaffId && targetStaffId !== myApt.staffId && seatLocked(staffSeats, targetStaffId)) { toast(seatLockedMsg(), "warning"); setDragOverInfo(null); return; }
              const myDur = getAptDuration(myApt, tenant);
              const [nh, nm] = newTime.split(":").map(Number);
              const newStartMin = nh*60 + nm;
              // Conflict check only applies to assigned staff; unassigned lane
              // is just a holding area so no conflict logic there.
              const others = targetStaffId
                ? allApts.filter(a => a.staffId === targetStaffId && a.date === selectedScheduleDate && a.status !== "cancelled" && a.status !== "noshow" && a.id !== aptId)
                : [];
              const conflict = others.some(o => {
                const [oh, om] = o.time.split(":").map(Number);
                const oStart = oh*60+om;
                const oDur = getAptDuration(o, tenant);
                return newStartMin < (oStart + oDur) && (newStartMin + myDur) > oStart;
              });
              if (conflict) { toast(zhEn("時段衝突，無法調整至此時段", "Time clash — can't move to this slot"), "error"); return; }
              // Confirm before committing — owners reported accidentally
              // dragging bookings and immediately losing track of the
              // original slot. Browser-native confirm() is intentional:
              // it forces a deliberate "OK" tap.
              const pet = state.pets[myApt.petId];
              const cust = state.users[myApt.customerId];
              const newStaff = state.users[newStaffId];
              const noChange = myApt.staffId === targetStaffId && myApt.time === newTime;
              if (noChange) return;
              const msg = i18nT("tx.rescheduleConfirm")
                .replace("{pet}", pet?.name || i18nT("common.petFallback","毛孩"))
                .replace("{cust}", cust?.name || i18nT("tx.custFallback"))
                .replace("{fromDate}", myApt.date || "—")
                .replace("{fromTime}", myApt.time)
                .replace("{fromStaff}", state.users[myApt.staffId]?.name || i18nT("tx.unassigned"))
                .replace("{toDate}", selectedScheduleDate)
                .replace("{toTime}", newTime)
                .replace("{toStaff}", newStaff?.name || i18nT("tx.unassigned"));
              if (!window.confirm(msg)) return;
              // Re-check conflict AFTER the dialog — another staff member
              // could have booked the slot during the ~5s confirm window.
              // setState applies the latest appointments[] snapshot so we
              // read it again from the closure-captured `state` is risky;
              // pull fresh via setState's updater for atomicity.
              setState(s => {
                const freshOthers = targetStaffId
                  ? (s.appointments || []).filter(a => a.staffId === targetStaffId && a.date === selectedScheduleDate && a.status !== "cancelled" && a.status !== "noshow" && a.id !== aptId)
                  : [];
                const freshConflict = freshOthers.some(o => {
                  const [oh2, om2] = o.time.split(":").map(Number);
                  const oStart2 = oh2*60 + om2;
                  const oDur2 = getAptDuration(o, tenant);
                  return newStartMin < (oStart2 + oDur2) && (newStartMin + myDur) > oStart2;
                });
                if (freshConflict) {
                  toast(zhEn("時段衝突 — 另一位客戶剛預約咗呢個時段，請揀其他時段", "Time clash — another customer just booked this slot; choose another"), "error");
                  return s;
                }
                return { ...s, appointments: s.appointments.map(a => a.id === aptId ? { ...a, staffId: targetStaffId, time: newTime, date: selectedScheduleDate } : a) };
              });
              logActivity({
                tenantId: user.tenantId,
                userId: user.id,
                actionType: "appointment_rescheduled",
                entityType: "appointment",
                entityId: aptId,
                entityName: (state.users[myApt.customerId]?.name||"") + " - " + (state.pets[myApt.petId]?.name||""),
                description: "改期預約：從 " + myApt.time + " (美容師: " + (state.users[myApt.staffId]?.name||"") + ") 改至 " + newTime + " (美容師: " + (state.users[newStaffId]?.name||"") + ")",
                severity: "info",
                details: { oldStaffId: myApt.staffId, newStaffId, oldTime: myApt.time, newTime, appointment: myApt }
              }).catch(e => console.error("[PettoBook] log activity:", e));
              toast(zhEn("已調整至 ", "Moved to ") + (state.users[newStaffId]?.name||"") + " " + newTime);
            };

            // Today summary — one-glance health check for the day.
            // Counts only live bookings (skips cancelled/noshow/expired
            // The chip reflects whatever date the user is VIEWING (so
            // navigating to a future / past day shows that day's totals,
            // not the now-misleading "今日 $0" when the actual today has
            // no bookings). Falls back to dateNow() for non-day views.
            const todayStr2 = viewMode === "day" ? selectedScheduleDate : dateNow();
            const isViewingToday = todayStr2 === dateNow();
            const todayLive = (allApts || []).filter(a => a.date === todayStr2 && !isAptDead(a));
            // Real-time revenue — sums EVERY non-cancelled / non-noshow
            // apt today so the chip updates the moment a booking is
            // accepted, not waiting for 收款. Matches the 報表 stats
            // card (rangedApts) + the EOD 日結 below (now also realtime).
            const doneToday2 = todayLive.filter(a => a.status === "done");
            const todayRevenue = todayLive.reduce((s, a) => s + aptRevenue(a), 0);
            const todayUnpaid = todayLive.filter(a => !a.paid && (Number(a.deposit) || 0) > 0 && !a.depositConfirmedAt).length;
            return <>
              {/* ── TOP TOOLBAR ────────────────────────────────────────────
                  Split into two rows so the today-summary chip always
                  has guaranteed real estate on the right of row 1 —
                  before this, staff filter (flex:1) could push the chip
                  off-screen on narrow viewports / many staff. View
                  switching is in the drawer (日/週/月/議程); date
                  navigation uses swipe left/right on the grid; zoom
                  uses pinch on the grid; walk-in is the green floating
                  action button bottom-right. */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:6,position:"relative"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flexShrink:1}}>
                  <button onClick={() => setShowMonthPicker(v => !v)} style={{display:"inline-flex",alignItems:"center",gap:6,background:"transparent",border:"none",cursor:"pointer",padding:0,minWidth:0,flexShrink:1}}>
                    <h2 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {viewMode === "day"
                        ? new Date(selectedScheduleDate).toLocaleDateString(dateLoc(), { month:"long", day:"numeric", weekday:"short" })
                        : viewMode === "month"
                          ? fmtYM(calMonth)
                          : viewMode === "agenda"
                            ? zhEn("議程", "Agenda")
                            : zhEn((new Date(weekDates[0]).getMonth()+1) + "月 " + new Date(weekDates[0]).getFullYear(), new Date(weekDates[0]).toLocaleDateString("en-GB", { month: "long", year: "numeric" }))}
                    </h2>
                    <ChevronDown size={16} color="var(--c-primary)" strokeWidth={2.5} style={{transform: showMonthPicker ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s",flexShrink:0}} />
                  </button>
                  {/* 今日 — quick jump button right next to the date heading
                      so the owner can re-anchor with one tap. Greys out when
                      already on today. */}
                  {!isViewingToday && (
                    <button onClick={() => {
                      goToScheduleDate(dateNow());
                      if (viewMode === "agenda") setViewMode("day");
                    }} title={zhEn("跳到今日", "Jump to today")} style={{padding:"4px 12px",borderRadius:99,background:"var(--c-primary)",color:"#fff",border:"none",fontSize:11,fontWeight:900,cursor:"pointer",flexShrink:0,boxShadow:"0 2px 6px var(--c-primary-30)"}}>
                      {zhEn("今日", "Today")}
                    </button>
                  )}
                </div>
                {/* Today summary chip — first-class real estate on the
                    right of the date row. Always rendered so the owner
                    can see today's revenue at a glance even when 0
                    bookings; tap jumps to agenda view scoped to today. */}
                <button onClick={() => setViewMode("agenda")} title={zhEn("點擊查看今日清單", "Tap to see today's list")} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,background:todayLive.length > 0 ? "linear-gradient(135deg,#ECFDF5,#D1FAE5)" : "#F3F4F6",border:"1.5px solid " + (todayLive.length > 0 ? "#A7F3D0" : "#E5E7EB"),color:todayLive.length > 0 ? "#166534" : "#6B7280",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,boxShadow:todayLive.length > 0 ? "0 2px 6px rgba(16,185,129,0.18)" : "none"}}>
                  📅 {isViewingToday ? zhEn("今日", "Today") : zhEn("選日", "Selected day")} {canView("viewRevenue") ? "$" + todayRevenue.toLocaleString() + " · " : ""}{todayLive.length}{zhEn("單", "Sale")}
                  {todayUnpaid > 0 && <span style={{background:"#FEF2F2",color:"#991B1B",borderRadius:6,padding:"1px 6px",fontWeight:900,fontSize:11}}>{todayUnpaid}{zhEn("待付", "due")}</span>}
                </button>
                {/* Month picker popover — full grid, tap any date to jump.
                    Closes after pick. */}
                {showMonthPicker && (() => {
                  const anchor = viewMode === "month" ? calMonth + "-01" : selectedScheduleDate;
                  const aDate = new Date(anchor);
                  const py = aDate.getFullYear(), pm = aDate.getMonth();
                  const monthStart = new Date(py, pm, 1);
                  const startWeekday = monthStart.getDay();
                  const daysInMonth = new Date(py, pm + 1, 0).getDate();
                  const monthApts = allApts.filter(a => {
                    const d = new Date(a.date);
                    return d.getFullYear() === py && d.getMonth() === pm && a.status !== "cancelled";
                  });
                  return (
                    <>
                      <div onClick={() => setShowMonthPicker(false)} style={{position:"fixed",inset:0,zIndex:140,background:"transparent"}} />
                      <div style={{position:"absolute",top:34,left:0,zIndex:150,background:"#FFFDFD",borderRadius:18,padding:14,boxShadow:"0 16px 40px rgba(31,41,55,0.15)",border:"1px solid var(--c-light)",minWidth:300}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <button onClick={() => {
                            const d = new Date(py, pm - 1, 1);
                            const ym = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0");
                            goToScheduleDate(ym + "-01");
                          }} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={14} color="var(--c-primary)" strokeWidth={3}/></button>
                          <p style={{fontWeight:900,fontSize:15,margin:0,color:"#4A4A68"}}>{zhEn(py + " 年 " + (pm+1) + " 月", new Date(py, pm, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }))}</p>
                          <button onClick={() => {
                            const d = new Date(py, pm + 1, 1);
                            const ym = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0");
                            goToScheduleDate(ym + "-01");
                          }} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={14} color="var(--c-primary)" strokeWidth={3}/></button>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
                          {DAYS.map(d => <div key={d} style={{textAlign:"center",fontSize:10,color:"#AFAFC4",fontWeight:800,padding:"3px 0"}}>{d}</div>)}
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                          {Array(startWeekday).fill(null).map((_,i)=><div key={"e"+i}/>)}
                          {Array(daysInMonth).fill(null).map((_,i) => {
                            const day = i + 1;
                            const ds = py + "-" + String(pm+1).padStart(2,"0") + "-" + String(day).padStart(2,"0");
                            const cnt = monthApts.filter(a => a.date === ds).length;
                            const isToday = ds === dateNow();
                            const isSel = ds === selectedScheduleDate;
                            return (
                              <button key={day} onClick={() => {
                                goToScheduleDate(ds);
                                setShowMonthPicker(false);
                              }} style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderRadius:10,background:isSel?"var(--c-primary)":isToday?"var(--c-lighter)":cnt>0?"#FFFDFD":"transparent",cursor:"pointer",border:"none",padding:0,boxShadow:isSel?"0 4px 12px var(--c-primary-30)":"none"}}>
                                <span style={{fontSize:13,fontWeight:isSel||isToday?900:700,color:isSel?"#fff":isToday?"var(--c-primary)":"#4A4A68",lineHeight:1}}>{day}</span>
                                {cnt > 0 && !isSel && <div style={{width:4,height:4,borderRadius:99,background:isToday?"var(--c-primary)":"var(--c-grad2)",marginTop:2}}/>}
                                {cnt > 0 && isSel && <div style={{width:4,height:4,borderRadius:99,background:"#fff",marginTop:2}}/>}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{display:"flex",gap:6,marginTop:10,paddingTop:10,borderTop:"1px solid var(--c-light)"}}>
                          <Btn size="sm" variant="secondary" style={{flex:1}} onClick={() => {
                            goToScheduleDate(dateNow());
                            setShowMonthPicker(false);
                          }}>{zhEn("今日", "Today")}</Btn>
                          <Btn size="sm" variant="secondary" style={{flex:1}} onClick={() => setShowMonthPicker(false)}>{zhEn("關閉", "Close")}</Btn>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* Row 2 — view-mode toggle + 今日 jump + (separator) +
                   staff filter pills. All controls that operate on the
                   schedule live together so the date-heading row stays
                   clean. The | separator visually splits the time/view
                   group from the staff-filter group. */}
              <div style={{display:"flex",gap:6,alignItems:"center",overflowX:"auto",paddingBottom:6,marginBottom:8,scrollbarWidth:"none"}}>
                {/* View-mode pill toggle: 日 / 週 / 月 / 議程. Week view
                    only shows ONE staff at a time — owner can switch via
                    the staff picker that appears when 週 is active. */}
                <div style={{display:"flex",gap:3,padding:"3px",background:"#F3F4F6",borderRadius:99,flexShrink:0}}>
                  {[["day",zhEn("日", "Day")],["week",zhEn("週", "Week")],["month",zhEn("月", "Month")],["agenda",zhEn("議程", "Agenda")]].map(([k, l]) => (
                    <button key={k} onClick={() => setViewMode(k)} style={{padding:"4px 10px",borderRadius:99,border:"none",background:viewMode===k?"#fff":"transparent",color:viewMode===k?"var(--c-primary)":"#6B7280",fontSize:11,fontWeight:900,cursor:"pointer",boxShadow:viewMode===k?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>{l}</button>
                  ))}
                </div>
                {/* Separator — only when there's a staff filter to divide. */}
                {staffList.length > 1 && (
                  <div aria-hidden style={{flexShrink:0,width:1,height:18,background:"#E5E7EB",margin:"0 4px"}} />
                )}
                {/* Staff filter pills (≥2 staff). */}
                {staffList.length > 1 && staffList.map(s => (
                  <button key={s.id} onClick={()=>setHiddenStaff(h=>({...h,[s.id]:!h[s.id]}))} title={s.name} style={{display:"flex",alignItems:"center",gap:3,padding:"4px 10px",borderRadius:99,border:"1.5px solid "+(hiddenStaff[s.id]?"var(--c-light)":"var(--c-primary)"),background:hiddenStaff[s.id]?"var(--c-lighter)":"var(--c-primary)15",cursor:"pointer",whiteSpace:"nowrap",fontWeight:700,fontSize:11,color:hiddenStaff[s.id]?"#AFAFC4":"var(--c-primary)",flexShrink:0}}>
                    {hiddenStaff[s.id]?<EyeOff size={10} strokeWidth={2.5}/>:<Eye size={10} strokeWidth={2.5}/>}
                    {s.name}
                  </button>
                ))}
              </div>

              {/* Month view */}
              {viewMode==="month" && <Card style={{padding:24,marginBottom:16,backdropFilter:"none",WebkitBackdropFilter:"none",background:"#FFFDFD"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <button onClick={()=>{const d=new Date(yr,mo-2,1);setCalMonth(d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"));}} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={16} color="var(--c-primary)" strokeWidth={3}/></button>
                  <p style={{fontWeight:900,fontSize:17,margin:0,color:"#4A4A68"}}>{fmtYM(calMonth)}</p>
                  <button onClick={()=>{const d=new Date(yr,mo,1);setCalMonth(d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"));}} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={16} color="var(--c-primary)" strokeWidth={3}/></button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:8}}>
                  {DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:12,color:"#AFAFC4",fontWeight:800}}>{d}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
                  {Array(firstDay).fill(null).map((_,i)=><div key={"e"+i}/>)}
                  {Array(daysInMonth).fill(null).map((_,i)=>{
                    const day=i+1,ds=calMonth+"-"+String(day).padStart(2,"0");
                    // Pull all bookings for this day, sort by time so the
                    // mini list shows the earliest 3 first. Mirrors how a
                    // physical desk calendar reads — "10:00 奶茶, 11:30 芝麻…"
                    const dayApts = monthApts.filter(a=>a.date===ds).sort((a,b)=>(a.time||"").localeCompare(b.time||""));
                    const cnt=dayApts.length;
                    const isToday=ds===dateNow(),isSel=ds===selectedScheduleDate;
                    const ph = getHolidayForDate(tenant?.country || "HK", ds);
                    const isClosed = (tenant?.holidays || []).includes(ds);
                    const bg = isSel ? "var(--c-primary)"
                      : isClosed ? "#FEE2E2"
                      : isToday ? "var(--c-light)"
                      : cnt>0 ? "var(--c-lighter)" : "transparent";
                    const textOnSel = isSel ? "#fff" : null;
                    return <div key={day} title={ph ? ph.name : ""} onClick={()=>{goToScheduleDate(ds);setViewMode("day");}} style={{minHeight:84,display:"flex",flexDirection:"column",alignItems:"stretch",borderRadius:10,background:bg,cursor:"pointer",transition:"all 0.2s",boxShadow:isSel?"0 6px 16px var(--c-primary-40)":"none",position:"relative",overflow:"hidden",padding:"4px 4px 3px"}}>
                      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:2}}>
                        <span style={{fontSize:12,fontWeight:isToday||isSel||ph||isClosed?900:700,color:textOnSel || (isClosed?"#991B1B":(cnt>0||isToday?"var(--c-primary)":"#4A4A68")),lineHeight:1}}>{day}</span>
                        {cnt > 0 && <span style={{fontSize:9,fontWeight:800,color:textOnSel || "#8A8A9E",lineHeight:1}}>{cnt}</span>}
                      </div>
                      {ph && <span style={{fontSize:9,fontWeight:800,color:textOnSel || "#6B21A8",marginTop:2,lineHeight:1.1,maxWidth:"100%",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{ph.name.length > 4 ? ph.name.slice(0,3)+"…" : ph.name}</span>}
                      {/* Mini booking list — top 3 by time, with "+N" overflow.
                          Keeps the cell scannable at a glance without making
                          the month grid scroll. */}
                      {!isClosed && dayApts.slice(0, 3).map(a => {
                        const pet = state.pets[a.petId];
                        const petName = pet?.name || "—";
                        const t = (a.time || "").slice(0, 5);
                        return (
                          <div key={a.id} style={{fontSize:9,fontWeight:700,lineHeight:1.15,marginTop:2,padding:"1px 4px",borderRadius:4,background:isSel?"rgba(255,255,255,0.18)":"#fff",color:textOnSel || "#4A4A68",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            <span style={{fontWeight:800,opacity:0.85}}>{t}</span> {petName}
                          </div>
                        );
                      })}
                      {!isClosed && dayApts.length > 3 && (
                        <div style={{fontSize:9,fontWeight:800,color:textOnSel || "var(--c-primary)",marginTop:2,lineHeight:1.15}}>{"+" + (dayApts.length - 3) + zhEn(" 個", " more")}</div>
                      )}
                    </div>;
                  })}
                </div>
              </Card>}

              {/* Week view — one staff, full 7 days, hourly time-grid like
                  the day view. Apts are absolutely positioned by start time
                  and duration. Tap an apt → editApt; tap day header → drop
                  into 日 view; ‹ › arrows cycle staff. Now-line on today's
                  column. */}
              {viewMode === "week" && (() => {
                const activeStaff = staffList.find(s => s.id === weekStaffId)
                  || staffList[0]
                  || null;
                if (!activeStaff) {
                  return <Card style={{padding:30,textAlign:"center",marginBottom:16}}>
                    <p style={{fontSize:14,fontWeight:800,color:"#AFAFC4",margin:0}}>{zhEn("未有可顯示嘅美容師", "No groomers to show")}</p>
                  </Card>;
                }
                const curIdx = Math.max(0, staffList.findIndex(s => s.id === activeStaff.id));
                const prevStaff = () => setWeekStaffId(staffList[(curIdx - 1 + staffList.length) % staffList.length]?.id || null);
                const nextStaff = () => setWeekStaffId(staffList[(curIdx + 1) % staffList.length]?.id || null);
                // Same time-axis math as day view.
                const weekEarliestHour = timeAxis.length > 0 ? parseInt(timeAxis[0].split(":")[0]) : 9;
                const weekLatestHour = timeAxis.length > 0 ? parseInt(timeAxis[timeAxis.length-1].split(":")[0]) + 1 : 17;
                const gridHeight = (weekLatestHour - weekEarliestHour) * hourHeight;
                const nowDateStr = dateNow();
                const nowMinutesFromTop = ((nowHour - weekEarliestHour) * hourHeight) + (nowMinute / 60) * hourHeight;
                const inHoursNow = nowHour >= weekEarliestHour && nowHour < weekLatestHour;
                return (
                  <Card style={{padding:0,overflow:"hidden",marginBottom:16,background:"#FFFDFD"}}>
                    {/* Staff switcher header */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",background:"linear-gradient(135deg, var(--c-lighter), #fff)",borderBottom:"1px solid var(--c-light)"}}>
                      <button onClick={prevStaff} disabled={staffList.length < 2} style={{background:"transparent",border:"none",cursor:staffList.length<2?"default":"pointer",padding:6,opacity:staffList.length<2?0.3:1}}>
                        <ChevronLeft size={20} color="var(--c-primary)" strokeWidth={2.5} />
                      </button>
                      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:10,minWidth:0}}>
                        <div style={{width:32,height:32,borderRadius:99,overflow:"hidden",background:"var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"var(--c-primary)",flexShrink:0}}>
                          {activeStaff.avatar
                            ? <img src={activeStaff.avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                            : (activeStaff.name?.[0] || "?")}
                        </div>
                        <div style={{minWidth:0,textAlign:"center"}}>
                          <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{activeStaff.name}</p>
                          <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{curIdx + 1} / {staffList.length}</p>
                        </div>
                      </div>
                      <button onClick={nextStaff} disabled={staffList.length < 2} style={{background:"transparent",border:"none",cursor:staffList.length<2?"default":"pointer",padding:6,opacity:staffList.length<2?0.3:1}}>
                        <ChevronRight size={20} color="var(--c-primary)" strokeWidth={2.5} />
                      </button>
                    </div>
                    {/* Single scroll container with sticky day-header row
                        (top) + sticky time-axis column (left). Lets the
                        owner scroll horizontally through 7 days on a
                        narrow viewport while keeping both axes anchored. */}
                    <div style={{overflow:"auto",maxHeight:"70vh",position:"relative"}}>
                      <div style={{display:"grid",gridTemplateColumns:"48px repeat(7,minmax(96px,1fr))",position:"relative"}}>
                        {/* Top-left empty corner — sticky both axes so it
                            doesn't disappear under the time column or the
                            header row. zIndex highest. */}
                        <div style={{position:"sticky",top:0,left:0,zIndex:6,background:"var(--c-lighter)",borderBottom:"2px solid var(--c-light)",borderRight:"1px solid var(--c-light)"}} />
                        {/* Sticky day headers — top:0 keeps them visible
                            when scrolling vertically. */}
                        {weekDates.map(ds => {
                          const d = new Date(ds);
                          const isToday = ds === nowDateStr;
                          const isSel = ds === selectedScheduleDate;
                          return (
                            <button key={"hdr-"+ds} onClick={() => { goToScheduleDate(ds); setViewMode("day"); }} style={{position:"sticky",top:0,zIndex:5,padding:"8px 4px",border:"none",borderLeft:"1px solid var(--c-light)",borderBottom:"2px solid var(--c-light)",background:isSel?"var(--c-primary)":isToday?"#FFF7F0":"var(--c-lighter)",cursor:"pointer",textAlign:"center"}}>
                              <p style={{fontSize:11,fontWeight:800,color:isSel?"rgba(255,255,255,0.85)":isToday?"var(--c-primary)":"#8A8A9E",margin:0,letterSpacing:0.3}}>{DAYS[d.getDay()]}</p>
                              <p style={{fontSize:16,fontWeight:900,color:isSel?"#fff":isToday?"var(--c-primary)":"#4A4A68",margin:"2px 0 0"}}>{d.getDate()}</p>
                            </button>
                          );
                        })}
                        {/* Time axis column — sticky left so the times
                            stay anchored when scrolling horizontally
                            through 7 days. Solid background to avoid
                            overlap with apt blocks. */}
                        <div style={{position:"sticky",left:0,zIndex:4,background:"#fff",borderRight:"1px solid var(--c-light)"}}>
                          {timeAxis.map(t => (
                            <div key={t} style={{height:hourHeight,display:"flex",alignItems:"flex-start",justifyContent:"flex-end",padding:"2px 4px 0 0",borderBottom:"1px dashed var(--c-light)",fontSize:10,fontWeight:700,color:"#AFAFC4"}}>{t}</div>
                          ))}
                        </div>
                        {/* Day columns */}
                        {weekDates.map(ds => {
                          const isToday = ds === nowDateStr;
                          const dayApts = (allApts || [])
                            .filter(a => a.tenantId === user.tenantId && a.date === ds && a.staffId === activeStaff.id && a.status !== "cancelled");
                          return (
                            <div key={ds} style={{position:"relative",borderLeft:"1px solid var(--c-light)",height:gridHeight,background:isToday?"#FFFBF5":"transparent"}}>
                              {/* Hour grid lines */}
                              {timeAxis.map((t, i) => (
                                <div key={t} style={{position:"absolute",left:0,right:0,top:i*hourHeight,height:hourHeight,borderBottom:"1px dashed var(--c-light)"}} />
                              ))}
                              {/* Now line on today */}
                              {isToday && inHoursNow && (
                                <div style={{position:"absolute",left:0,right:0,top:nowMinutesFromTop,height:2,background:"#EF4444",zIndex:5,boxShadow:"0 0 0 1px rgba(239,68,68,0.3)"}} />
                              )}
                              {/* Apt blocks */}
                              {dayApts.map(a => {
                                const isEvent = a.kind === "event";
                                const [ah, am] = (a.time || "00:00").split(":").map(Number);
                                const top = ((ah - weekEarliestHour) * hourHeight) + (am / 60) * hourHeight;
                                const dur = getAptDuration(a, tenant) || 30;
                                const height = Math.max(20, (dur / 60) * hourHeight);
                                if (top < 0 || top >= gridHeight) return null;
                                const pet = state.pets?.[a.petId];
                                const statusColor = isEvent ? "#9CA3AF"
                                  : a.status === "done" ? "#10B981"
                                  : a.status === "grooming" ? "#3B82F6"
                                  : a.status === "checkedin" ? "#F59E0B"
                                  : a.status === "noshow" ? "#EF4444"
                                  : "var(--c-primary)";
                                return (
                                  <button
                                    key={a.id}
                                    onClick={() => { if (isEvent) { setEventForm({ id:a.id, staffId:a.staffId, date:a.date, time:a.time, durationMin:a.customDuration||30, label:a.label||"", staffNote:a.staffNote||"", color:a.color||"", leaveType:a.leaveType||"" }); } else { setEditApt(a); } }}
                                    style={{position:"absolute",left:2,right:2,top:top+1,height:height-2,padding:"3px 5px",borderRadius:6,border:"none",borderLeft:"3px solid "+statusColor,background:statusColor+"22",cursor:"pointer",textAlign:"left",overflow:"hidden",display:"flex",flexDirection:"column",gap:1,zIndex:2}}
                                  >
                                    <p style={{fontSize:9,fontWeight:900,color:statusColor,margin:0,letterSpacing:0.3,whiteSpace:"nowrap"}}>{a.time}{height > 36 && (" · " + dur + "m")}</p>
                                    <p style={{fontSize:10,fontWeight:800,color:"#4A4A68",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{isEvent ? (a.label || zhEn("事件", "Event")) : (pet?.name || zhEn("毛孩", "Pet"))}</p>
                                    {!isEvent && a.service && height > 50 && <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.service}</p>}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Card>
                );
              })()}

              {user.role === "owner" && (() => {
                const tr = freeTrialOf(tenant);
                if (!tr) return null;
                const soon = tr.daysLeft <= 3;
                return (
                  <div style={{background: soon ? "linear-gradient(135deg,#FEF3C7,#FDE68A)" : "linear-gradient(135deg,#F5F3FF,#EDE9FE)", border: "2px solid " + (soon ? "#F59E0B" : "#C4B5FD"), borderRadius:14, padding:"10px 14px", marginBottom:10, display:"flex", alignItems:"center", gap:10}}>
                    <span style={{fontSize:20}}>🎁</span>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{fontSize:13, fontWeight:900, color: soon ? "#92400E" : "#5B21B6", margin:0}}>{zhEn("專業版免費試用 · 仲有 " + tr.daysLeft + " 日", "Free Pro trial · " + tr.daysLeft + (tr.daysLeft === 1 ? " day" : " days") + " left")}</p>
                      <p style={{fontSize:11, fontWeight:700, color: soon ? "#92400E" : "#6D28D9", margin:"2px 0 0", opacity:0.85}}>{zhEn("試用完自動轉免費版，唔會收費。升級即可保留收銀、無限網上預約同多位員工。", "Afterwards you move to Free with no charge. Upgrade to keep POS, unlimited online booking and more staff.")}</p>
                    </div>
                    <Btn size="sm" onClick={openPlanPage}>{zhEn("升級", "Upgrade")}</Btn>
                  </div>
                );
              })()}
              {/* Shop-closed-day banner — fires for weekly off-days
                  (businessHours[dow].enabled=false, eg. 逢週一休息) AND
                  one-off holidays (tenant.holidays). Bookings can still
                  be created (the empty-slot picker isn't blocked) so the
                  owner can do an exception walk-in; the banner just
                  communicates that the shop is officially closed. */}
              {viewMode === "day" && (() => {
                const dStr = selectedScheduleDate;
                if (!dStr || !tenant) return null;
                const dow = new Date(dStr).getDay();
                const bh = tenant.businessHours?.[dow];
                const closedByHours = bh && bh.enabled === false;
                const onHoliday = (tenant.holidays || []).includes(dStr);
                if (!closedByHours && !onHoliday) return null;
                const dayName = [zhEn("日", "Sun"),zhEn("一", "Mon"),zhEn("二", "Tue"),zhEn("三", "Wed"),zhEn("四", "Thu"),zhEn("五", "Fri"),zhEn("六", "Sat")][dow];
                const reason = onHoliday ? zhEn("公眾／自訂假期", "Public / custom holiday") : (zhEn("逢週", "Closed every ") + dayName + zhEn("休息", ""));
                return (
                  <div style={{background:"linear-gradient(135deg,#FEF3C7,#FDE68A)",border:"2px solid #F59E0B",borderRadius:14,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:20}}>🏖️</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("店舖今日休息 ·", "Shop closed today ·")} {reason}</p>
                      <p style={{fontSize:11,color:"#92400E",margin:"2px 0 0",fontWeight:700,opacity:0.85}}>{zhEn("客戶端不會見到此日為可預約日；店家仍可手動加單（例：補登已收訂金）。", "Customers won't see this day as bookable; the shop can still add bookings manually (e.g. to log a deposit already received).")}</p>
                    </div>
                  </div>
                );
              })()}
              {/* Day view — staff-column grid + a 7-day strip on top so the
                  owner can jump to nearby days without leaving day mode. */}
              {viewMode === "day" && <Card style={{padding:0,overflow:"clip",marginBottom:16,backdropFilter:"none",WebkitBackdropFilter:"none",background:"#FFFDFD"}}>
                <div
                  style={{display:"flex",alignItems:"stretch",borderBottom:"2px solid var(--c-light)",touchAction:"pan-y",position:"sticky",top:0,zIndex:8,background:"#FFFDFD",willChange:"transform",contain:"layout paint"}}
                  onTouchStart={e => { if (e.touches.length === 1) weekSwipeX.current = e.touches[0].clientX; }}
                  onTouchEnd={e => {
                    if (weekSwipeX.current == null) return;
                    const dx = e.changedTouches[0].clientX - weekSwipeX.current;
                    weekSwipeX.current = null;
                    if (Math.abs(dx) > 50) {
                      const d = new Date(selectedScheduleDate);
                      d.setDate(d.getDate() + (dx < 0 ? 1 : -1));
                      const next = localDateStr(d);
                      goToScheduleDate(next);
                    }
                  }}
                >
                  {/* Previous-week arrow — flanks the strip so it's tap-easy */}
                  <button onClick={() => shiftWeek(-1)} aria-label={i18nT("own.schedule.prevWeek")} style={{width:32,background:"var(--c-light)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <ChevronLeft size={16} color="var(--c-primary)" strokeWidth={3} />
                  </button>
                  <div style={{flex:1,display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:0}}>
                    {weekDates.map(ds=>{
                      const d=new Date(ds),isToday=ds===dateNow(),isSel=ds===selectedScheduleDate;
                      const cnt=allApts.filter(a=>a.date===ds&&a.status!=="cancelled"&&a.kind!=="event").length;
                      return <button key={ds} onClick={()=>goToScheduleDate(ds)} style={{padding:"10px 4px",textAlign:"center",background:isSel?"var(--c-primary)":isToday?"var(--c-lighter)":"transparent",border:"none",cursor:"pointer",borderLeft:"1px solid var(--c-light)",transition:"all 0.2s"}}>
                        <p style={{fontSize:11,fontWeight:700,color:isSel?"rgba(255,255,255,0.8)":isToday?"var(--c-primary)":"#AFAFC4",margin:"0 0 2px"}}>{DAYS[d.getDay()]}</p>
                        <p style={{fontSize:16,fontWeight:900,margin:"0 0 2px",color:isSel?"#fff":isToday?"var(--c-primary)":"#4A4A68"}}>{d.getDate()}</p>
                        {cnt>0&&<div style={{width:6,height:6,borderRadius:99,background:isSel?"#fff":"var(--c-grad2)",margin:"0 auto"}}/>}
                      </button>;
                    })}
                  </div>
                  <button onClick={() => shiftWeek(1)} aria-label={i18nT("own.schedule.nextWeek")} style={{width:32,background:"var(--c-light)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <ChevronRight size={16} color="var(--c-primary)" strokeWidth={3} />
                  </button>
                </div>
                {/* Staff lanes. Two-finger pinch zooms hourHeight; everything
                    else is native scroll (touchAction allows both axes). The
                    pinch activates only after the finger distance changes
                    by >20px so a normal two-finger scroll doesn't keep
                    snapping into zoom mode. */}
                <div
                  // Horizontal scroll for the staff lanes; vertical scrolls the
                  // PAGE (owner shell is minHeight:100vh, document-scrolled).
                  // The trap this avoids: `overflowX:auto` alone makes overflowY
                  // COMPUTE to `auto` (CSS spec), turning this into a vertical
                  // scroll container with content==height — it then swallowed the
                  // vertical gesture instead of chaining to the page, so only the
                  // gutter beside the calendar scrolled. Setting overflowY:hidden
                  // EXPLICITLY keeps it non-user-vertically-scrollable, so pan-y
                  // chains to the document and every hour is reachable via normal
                  // page scroll — no height cap needed, so the page isn't
                  // shortened (which was displacing the fixed bottom nav). Card
                  // stays overflow:clip, touchAction stays pan-x pan-y (memory).
                  style={{overflowX:"auto",overflowY:"hidden",touchAction:"pan-x pan-y"}}
                  onTouchStart={e => {
                    if (e.touches.length === 2) {
                      const dx = e.touches[0].clientX - e.touches[1].clientX;
                      const dy = e.touches[0].clientY - e.touches[1].clientY;
                      pinchRef.current = { startDist: Math.sqrt(dx*dx + dy*dy), startHeight: hourHeight, active: false };
                      dayBodySwipe.current = null; // cancel any pending swipe
                      return;
                    }
                    if (e.touches.length === 1) {
                      dayBodySwipe.current = {
                        x: e.touches[0].clientX,
                        y: e.touches[0].clientY,
                        sl: e.currentTarget.scrollLeft,
                        cancelled: false,
                      };
                    }
                  }}
                  onTouchMove={e => {
                    if (e.touches.length === 2 && pinchRef.current.startDist > 0) {
                      const dx = e.touches[0].clientX - e.touches[1].clientX;
                      const dy = e.touches[0].clientY - e.touches[1].clientY;
                      const dist = Math.sqrt(dx*dx + dy*dy);
                      const delta = Math.abs(dist - pinchRef.current.startDist);
                      if (!pinchRef.current.active && delta < 20) return;
                      pinchRef.current.active = true;
                      const scale = dist / pinchRef.current.startDist;
                      setHourHeight(pinchRef.current.startHeight * scale);
                      return;
                    }
                    // Cancel pending swipe if the user grew vertical-dominant
                    // (they're scrolling time, not switching day).
                    if (e.touches.length === 1 && dayBodySwipe.current && !dayBodySwipe.current.cancelled) {
                      const dx = Math.abs(e.touches[0].clientX - dayBodySwipe.current.x);
                      const dy = Math.abs(e.touches[0].clientY - dayBodySwipe.current.y);
                      if (dy > 30 && dy > dx) dayBodySwipe.current.cancelled = true;
                    }
                  }}
                  onTouchEnd={e => {
                    if (e.touches.length < 2 && pinchRef.current.startDist > 0) {
                      const wasActive = pinchRef.current.active;
                      pinchRef.current = { startDist: 0, startHeight: hourHeight, active: false };
                      if (wasActive) persistHourHeight();
                      dayBodySwipe.current = null;
                      return;
                    }
                    const start = dayBodySwipe.current;
                    dayBodySwipe.current = null;
                    if (!start || start.cancelled) return;
                    const dx = e.changedTouches[0].clientX - start.x;
                    const dy = e.changedTouches[0].clientY - start.y;
                    const scrolled = Math.abs(e.currentTarget.scrollLeft - start.sl) > 8;
                    // Trigger ONLY when: clearly horizontal, big enough,
                    // and the inner staff-lane scroll didn't consume it.
                    if (scrolled) return;
                    if (Math.abs(dx) < 80) return;
                    if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
                    const d = new Date(selectedScheduleDate);
                    d.setDate(d.getDate() + (dx < 0 ? 1 : -1));
                    const next = localDateStr(d);
                    goToScheduleDate(next);
                  }}
                >
                  <div style={{minWidth:Math.max(420, visibleStaff.length*140+48)}}>
                    {visibleStaff.length===0 ? <div style={{padding:32,textAlign:"center",color:"#AFAFC4",fontWeight:700}}>{i18nT("own.schedule.noStaff")}</div> : <>
                      {/* Staff header — name only; appointment count was
                          noisy and took an extra line in the sticky bar. */}
                      <div style={{display:"grid",gridTemplateColumns:"48px repeat("+visibleStaff.length+",1fr)",borderBottom:"2px solid var(--c-light)",background:"var(--c-lighter)",position:"sticky",top:0,zIndex:7,willChange:"transform",contain:"layout paint"}}>
                        <div/>
                        {visibleStaff.map(s => (
                          <div key={s.id} style={{padding:"6px 4px",textAlign:"center",borderLeft:"1px solid var(--c-light)",background:s.isUnassigned?"#FEF3C7":"transparent"}}>
                            <p style={{fontSize:13,fontWeight:900,color:s.isUnassigned?"#92400E":seatLocked(staffSeats, s.id)?"#9CA3AF":"var(--c-primary)",margin:0}}>{s.isUnassigned ? "⚠ " : seatLocked(staffSeats, s.id) ? "🔒 " : ""}{s.name}</p>
                            {seatLocked(staffSeats, s.id) && <button onClick={openPlanPage} style={{marginTop:2,background:"#4B5563",color:"#fff",border:"none",borderRadius:99,padding:"2px 8px",fontSize:10,fontWeight:900,cursor:"pointer"}}>{zhEn("超出名額 · 升級", "Over limit · Upgrade")}</button>}
                          </div>
                        ))}
                      </div>
                      {/* Time grid */}
                      <div style={{position:"relative"}}>
                        {/* "Now" line — red horizontal across the day grid
                            with a time pill. Only shown on today's date and
                            within business hours. Auto-updates every minute
                            via the setNowTick interval. */}
                        {selectedScheduleDate===dateNow()&&nowHr2>=earliestHour&&nowHr2<latestHour&&<div style={{position:"absolute",left:0,right:0,top:nowLine2,zIndex:5,pointerEvents:"none",display:"flex",alignItems:"center"}}>
                          <div style={{background:"#FF4D6D",color:"#fff",fontSize:10,fontWeight:900,borderRadius:99,padding:"3px 8px",marginLeft:4,whiteSpace:"nowrap",boxShadow:"0 4px 12px rgba(255,77,109,0.5)"}}>● {timeNow()}</div>
                          <div style={{height:2,background:"#FF4D6D",flex:1,marginRight:4,boxShadow:"0 0 6px rgba(255,77,109,0.4)"}}/>
                        </div>}
                        <div style={{display:"grid",gridTemplateColumns:"48px repeat("+visibleStaff.length+",1fr)"}}>
                          {/* Time labels */}
                          <div>{timeAxis2.map(t=><div key={t} style={{height:hourHeight,borderBottom:"1px dashed var(--c-light)",display:"flex",alignItems:"flex-start",justifyContent:"flex-end",paddingRight:6,paddingTop:4}}><span style={{fontSize:11,fontWeight:700,color:"#AFAFC4"}}>{t}</span></div>)}</div>
                          {/* Staff columns */}
                          {visibleStaff.map(s=>{
                            const dayApts = s.isUnassigned
                              ? allApts.filter(a=>!a.staffId && a.date===selectedScheduleDate && !isAptDead(a))
                              : allApts.filter(a=>a.staffId===s.id&&a.date===selectedScheduleDate&&!isAptDead(a));
                            const isDropTarget = dragOverInfo?.staffId === s.id;
                            return <div key={s.id} data-staff-col={s.id} style={{position:"relative",borderLeft:"1px solid var(--c-light)",background:isDropTarget?"var(--c-lighter)":"transparent",transition:"background 0.15s"}}
                              onDragOver={e => {
                                e.preventDefault();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const offsetY = e.clientY - rect.top;
                                const snappedMin = Math.max(0, Math.round(offsetY / hourHeight * 60 / 15) * 15);
                                // #16: predict conflict during dragover so the
                                // drop preview turns red BEFORE the user
                                // releases. Without this the apt visually
                                // snaps to the new spot, then jumps back when
                                // the error toast fires — disorienting.
                                const dragged = draggedAptRef.current;
                                let conflict = false;
                                if (dragged) {
                                  const newStart = earliestHour * 60 + snappedMin;
                                  const newEnd = newStart + (dragged.duration || 60);
                                  conflict = dayApts.some(a => {
                                    if (a.id === dragged.id) return false;
                                    const [ah, am] = (a.time || "00:00").split(":").map(Number);
                                    const aDur = getAptDuration(a, tenant);
                                    const aStart = ah * 60 + am;
                                    const aEnd = aStart + aDur;
                                    return newStart < aEnd && newEnd > aStart;
                                  });
                                }
                                // Always allow drop — we used to set dropEffect:"none"
                                // on conflict, but that prevented onDrop from
                                // firing so the conflict toast never showed.
                                // Now we keep "move", let onDrop reject + toast.
                                e.dataTransfer.dropEffect = "move";
                                setDragOverInfo({ staffId: s.id, top: (snappedMin / 60) * hourHeight, conflict });
                              }}
                              onDragLeave={e => {
                                // Only clear when the cursor actually leaves
                                // the staff column, not when crossing inner
                                // cells (which fires onDragLeave/onDragEnter
                                // pairs in Chrome/Firefox and caused flicker).
                                const next = e.relatedTarget;
                                if (next && e.currentTarget.contains(next)) return;
                                setDragOverInfo(null);
                              }}
                              onDrop={e => {
                                e.preventDefault();
                                const aptId = e.dataTransfer.getData("aptId");
                                const draggedSnap = draggedAptRef.current;
                                draggedAptRef.current = null;
                                if (!aptId) { setDragOverInfo(null); return; }
                                // Recompute conflict at the actual drop
                                // position rather than trusting the
                                // closure-captured `dragOverInfo.conflict`
                                // — the last `onDragOver` may have been
                                // batched and the previewed preview state
                                // may lag the cursor on fast drops.
                                const rect = e.currentTarget.getBoundingClientRect();
                                const offsetY = e.clientY - rect.top;
                                const snappedMin = Math.max(0, Math.round(offsetY / hourHeight * 60 / 15) * 15);
                                const newStart = earliestHour * 60 + snappedMin;
                                const newDur = draggedSnap?.duration || 60;
                                const newEnd = newStart + newDur;
                                const liveConflict = dayApts.some(a => {
                                  if (a.id === aptId) return false;
                                  const [ah, am] = (a.time || "00:00").split(":").map(Number);
                                  const aDur = getAptDuration(a, tenant);
                                  const aStart = ah * 60 + am;
                                  const aEnd = aStart + aDur;
                                  return newStart < aEnd && newEnd > aStart;
                                });
                                if (liveConflict) {
                                  setDragOverInfo(null);
                                  toast(zhEn("時段衝突，未能移動", "Time clash — couldn't move"), "error");
                                  return;
                                }
                                const newH = earliestHour + Math.floor(snappedMin/60);
                                const newM = snappedMin % 60;
                                const newTime = String(newH).padStart(2,"0") + ":" + String(newM).padStart(2,"0");
                                moveAppointment(aptId, s.id, newTime);
                                setDragOverInfo(null);
                              }}
                            >
                              {/* Staff non-office overlay. Three layers, all
                                  visual-only (cells underneath stay clickable
                                  so the owner can override and book during
                                  off-hours when needed):
                                  1. Whole-day shading + 🏖️ label when staff is
                                     OFF today via fixed weekly schedule
                                     (staffHours[dow].enabled=false), custom
                                     timeOff range, or employment window
                                     (joinDate / leaveDate).
                                  2. Top/bottom bands shading time outside the
                                     staff's narrower hours when their weekly
                                     hours intersect the shop window.
                                  3. Break bands inside their hours. */}
                              {seatLocked(staffSeats, s.id) && <div aria-hidden style={{position:"absolute",inset:0,background:"repeating-linear-gradient(45deg, rgba(75,85,99,0.14) 0 8px, rgba(75,85,99,0.24) 8px 16px)",pointerEvents:"none",zIndex:1}}>
                                <div style={{position:"sticky",top:8,margin:"8px auto 0",width:"fit-content",background:"rgba(55,65,81,0.92)",color:"#fff",borderRadius:99,padding:"4px 12px",fontSize:11,fontWeight:900,whiteSpace:"nowrap"}}>🔒 {zhEn("超出方案員工名額", "Over plan staff limit")}</div>
                              </div>}
                              {/* Shop closed hours for THIS day. The time axis spans the
                                  union of every day's hours (e.g. Mon–Fri 10–20), so a
                                  short day (Sat 10–15) needs the rest shaded or staff
                                  book customers into hours the shop is shut. */}
                              {(() => {
                                const win = shopWindowOn(tenant, selectedScheduleDate);
                                if (!win) return null;
                                const eMin = earliestHour * 60, lMin = latestHour * 60;
                                const band = (key, from, to) => (
                                  <div key={key} aria-hidden style={{position:"absolute",left:0,right:0,top:((from - eMin) / 60) * hourHeight,height:((to - from) / 60) * hourHeight,background:"repeating-linear-gradient(45deg, rgba(107,114,128,0.12) 0 8px, rgba(107,114,128,0.20) 8px 16px)",pointerEvents:"none",zIndex:1,display:"flex",alignItems:"flex-start",justifyContent:"center"}}>
                                    {to - from >= 45 && <span style={{marginTop:6,background:"rgba(75,85,99,0.85)",color:"#fff",borderRadius:99,padding:"2px 10px",fontSize:10,fontWeight:900,whiteSpace:"nowrap"}}>{zhEn("非營業時間", "Closed")}</span>}
                                  </div>
                                );
                                const out = [];
                                if (win.open > eMin) out.push(band("shopPre", eMin, Math.min(win.open, lMin)));
                                if (win.close < lMin) out.push(band("shopPost", Math.max(win.close, eMin), lMin));
                                return out.length ? <>{out}</> : null;
                              })()}
                              {!s.isUnassigned && (() => {
                                const dow = new Date(selectedScheduleDate).getDay();
                                const sh = s.staffHours?.[dow] || s.staffHours?.[String(dow)];
                                const hasSched = s.staffHours && Object.values(s.staffHours).some(d => d && d.enabled);
                                const isFixedDayOff = hasSched && (!sh || !sh.enabled);
                                const isTimeOff = staffOffOn(s, selectedScheduleDate);
                                const beforeJoin = s.joinDate && selectedScheduleDate < s.joinDate;
                                const afterLeave = s.leaveDate && selectedScheduleDate > s.leaveDate;
                                const isFullOff = isFixedDayOff || isTimeOff || beforeJoin || afterLeave;
                                const offLabel = beforeJoin ? zhEn("未入職", "Not yet joined")
                                  : afterLeave ? zhEn("已離職", "Left")
                                  : isTimeOff ? (staffOffOn(s, selectedScheduleDate) === "sick" ? zhEn("🤒 病假", "🤒 Sick leave") : zhEn("🏖️ 休假", "🏖️ On leave"))
                                  : zhEn("🏖️ 固定休息", "🏖️ Regular day off");
                                if (isFullOff) {
                                  // Full diagonal-stripe overlay + label sticker at top.
                                  return (
                                    <>
                                      <div aria-hidden style={{position:"absolute",inset:0,background:"repeating-linear-gradient(45deg, rgba(156,163,175,0.10) 0 8px, rgba(156,163,175,0.18) 8px 16px)",pointerEvents:"none",zIndex:1}} />
                                      <div style={{position:"absolute",top:6,left:"50%",transform:"translateX(-50%)",background:"rgba(75,85,99,0.92)",color:"#fff",borderRadius:99,padding:"4px 12px",fontSize:11,fontWeight:900,whiteSpace:"nowrap",pointerEvents:"none",zIndex:1,boxShadow:"0 2px 6px rgba(0,0,0,0.18)"}}>{offLabel}</div>
                                    </>
                                  );
                                }
                                // Partial schedule: shade the time bands the
                                // staff doesn't work. Only renders when the
                                // staffer has explicit weekly hours that
                                // narrow the shop window.
                                if (!hasSched || !sh) return null;
                                const overlays = [];
                                const [shopOpenH, shopOpenM] = (tenant?.businessHours?.[dow]?.open || "09:00").split(":").map(Number);
                                const [shopCloseH, shopCloseM] = (tenant?.businessHours?.[dow]?.close || "18:00").split(":").map(Number);
                                const shopOpenMin = shopOpenH*60 + shopOpenM;
                                const shopCloseMin = shopCloseH*60 + shopCloseM;
                                const shOpenMin = sh.open ? (() => { const [h,m] = sh.open.split(":").map(Number); return h*60+m; })() : shopOpenMin;
                                const shCloseMin = sh.close ? (() => { const [h,m] = sh.close.split(":").map(Number); return h*60+m; })() : shopCloseMin;
                                const earliestMin = earliestHour * 60;
                                const latestMin = latestHour * 60;
                                if (shOpenMin > earliestMin) {
                                  const top = 0;
                                  const height = ((shOpenMin - earliestMin) / 60) * hourHeight;
                                  overlays.push(<div key="pre" aria-hidden style={{position:"absolute",left:0,right:0,top,height,background:"repeating-linear-gradient(45deg, rgba(156,163,175,0.08) 0 8px, rgba(156,163,175,0.14) 8px 16px)",pointerEvents:"none",zIndex:1}} />);
                                }
                                if (shCloseMin < latestMin) {
                                  const top = ((shCloseMin - earliestMin) / 60) * hourHeight;
                                  const height = ((latestMin - shCloseMin) / 60) * hourHeight;
                                  overlays.push(<div key="post" aria-hidden style={{position:"absolute",left:0,right:0,top,height,background:"repeating-linear-gradient(45deg, rgba(156,163,175,0.08) 0 8px, rgba(156,163,175,0.14) 8px 16px)",pointerEvents:"none",zIndex:1}} />);
                                }
                                // Break bands within working hours
                                const breaks = staffDayBreaks(s, dow);
                                for (const b of (breaks || [])) {
                                  const [bsh, bsm] = (b.start || "00:00").split(":").map(Number);
                                  const [beh, bem] = (b.end || "00:00").split(":").map(Number);
                                  const bs = bsh*60 + bsm;
                                  const be = beh*60 + bem;
                                  if (be <= earliestMin || bs >= latestMin) continue;
                                  const top = ((Math.max(bs, earliestMin) - earliestMin) / 60) * hourHeight;
                                  const height = ((Math.min(be, latestMin) - Math.max(bs, earliestMin)) / 60) * hourHeight;
                                  overlays.push(<div key={"br_" + bs} aria-hidden style={{position:"absolute",left:0,right:0,top,height,background:"repeating-linear-gradient(135deg, rgba(217,119,6,0.08) 0 8px, rgba(217,119,6,0.16) 8px 16px)",pointerEvents:"none",zIndex:1}} />);
                                }
                                return overlays.length ? <>{overlays}</> : null;
                              })()}
                              {(() => {
                                // 30-min click-to-add cells. A 1.5hr booking now
                                // greys exactly 3 half-hour cells (not a whole
                                // extra hour), and tapping snaps to the half hour.
                                const cells = [];
                                for (let M = earliestHour*60; M < latestHour*60; M += 30) cells.push(M);
                                return cells.map(M => {
                                  const h = Math.floor(M/60), m = M%60;
                                  const timeStr = String(h).padStart(2,"0") + ":" + String(m).padStart(2,"0");
                                  const hasConflict = dayApts.some(a => {
                                    const [ah, am] = a.time.split(":").map(Number);
                                    const aStart = ah*60+am;
                                    const aEnd = aStart + getAptDuration(a, tenant);
                                    return M < aEnd && (M+30) > aStart;
                                  });
                                  const shopWin = shopWindowOn(tenant, selectedScheduleDate);
                                  const offHours = !!shopWin && (M < shopWin.open || M >= shopWin.close);
                                  return <div key={timeStr} onClick={() => {
                                    if (hasConflict) return;
                                    if (seatLocked(staffSeats, s.id)) { toast(seatLockedMsg(), "warning"); return; }
                                    if (offHours && !window.confirm(zhEn("呢個時段唔喺營業時間（" + shopWin.openStr + "–" + shopWin.closeStr + "）。仍然要加單？", "This time is outside opening hours (" + shopWin.openStr + "–" + shopWin.closeStr + "). Add a booking anyway?"))) return;
                                    setSlotKindPick({ staffId:s.id, date:selectedScheduleDate, time:timeStr });
                                  }} style={{height:hourHeight/2,borderBottom:m===30?"1px dashed var(--c-light)":"1px dotted #EFEFF4",cursor:hasConflict?"not-allowed":"pointer",background:hasConflict?"#F3F4F6":"transparent",opacity:hasConflict?0.5:1,transition:"background 0.15s",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:10,color:hasConflict?"#AFAFC4":"var(--c-primary)",fontWeight:700,opacity:hasConflict?0.3:0.35}}>{hasConflict?"":"+"}</span></div>;
                                });
                              })()}
                              {isDropTarget && <div style={{position:"absolute",left:4,right:4,top:dragOverInfo.top,height:2,background:dragOverInfo.conflict?"#EF4444":"var(--c-primary)",zIndex:6,pointerEvents:"none",boxShadow:dragOverInfo.conflict?"0 0 8px rgba(239,68,68,0.6)":"0 0 8px var(--c-primary)"}}/>}
                              {isDropTarget && dragOverInfo.conflict && <div style={{position:"absolute",left:4,right:4,top:Math.max(0, dragOverInfo.top-12),background:"#EF4444",color:"#fff",fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,zIndex:6,pointerEvents:"none",width:"fit-content"}}>{zhEn("⚠ 時段衝突", "⚠ Time clash")}</div>}
                              {dayApts.map(a=>{
                                const [hr,mn]=a.time.split(":").map(Number);
                                const top=((hr-earliestHour)*hourHeight)+(mn/60)*hourHeight;
                                if (a.kind === "event") {
                                  const evDur = getAptDuration(a, tenant, false);
                                  const evH = Math.max((evDur/60)*hourHeight-4, 28);
                                  const evColor = a.color || "#9CA3AF";
                                  return <div key={a.id}
                                    onClick={() => setEventForm({ id:a.id, staffId:a.staffId, date:a.date, time:a.time, durationMin:a.customDuration||30, label:a.label||"", staffNote:a.staffNote||"", color:a.color||"", leaveType:a.leaveType||"" })}
                                    style={{position:"absolute",left:4,right:4,top:top+2,height:evH,borderRadius:14,background:`repeating-linear-gradient(45deg, ${evColor}22, ${evColor}22 8px, ${evColor}11 8px, ${evColor}11 16px)`,border:`1.5px dashed ${evColor}`,padding:"4px 10px",boxSizing:"border-box",overflow:"hidden",zIndex:2,cursor:"pointer",display:"flex",flexDirection:"column",gap:2}}
                                    title={`📌 ${a.label||zhEn("事件", "Event")}\n${a.time} · ${evDur}min${a.staffNote?"\n"+a.staffNote:""}`}>
                                    <div style={{fontSize:12,fontWeight:900,color:"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📌 {a.label || zhEn("事件", "Event")}</div>
                                    <div style={{fontSize:10,fontWeight:700,color:"#6B7280"}}>{a.time} · {evDur}min</div>
                                    {a.staffNote && evH > 50 && <div style={{fontSize:10,color:"#6B7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.staffNote}</div>}
                                  </div>;
                                }
                                const pet=state.pets[a.petId];
                                const dur=getAptDuration(a, tenant, false);
                                const cardH=Math.max((dur/60)*hourHeight-4,32);
                                // Pale rainbow background per appointment so
                                // each booking is visually distinct on a busy
                                // day. Status is still conveyed by the border
                                // colour + the status pill, so no info is
                                // lost. Hue is stable per appointment id.
                                const PALE_BG = [
                                  "rgba(255, 159, 178, 0.2)",  // pink
                                  "rgba(255, 196, 152, 0.2)",  // peach
                                  "rgba(255, 232, 152, 0.2)",  // butter
                                  "rgba(196, 232, 188, 0.22)", // sage
                                  "rgba(170, 213, 245, 0.22)", // sky
                                  "rgba(190, 178, 245, 0.22)", // lavender
                                  "rgba(232, 188, 245, 0.22)", // orchid
                                ];
                                let hash = 0; const sid = String(a.id || "");
                                for (let i = 0; i < sid.length; i++) hash = (hash * 31 + sid.charCodeAt(i)) | 0;
                                const bg = PALE_BG[Math.abs(hash) % PALE_BG.length];
                                // Border still encodes status so the eye can
                                // still scan pending / checked-in / grooming /
                                // done at a glance.
                                const statusFg = ({pending:"#FF7AA2",checkedin:"#2563EB",grooming:"#3B82F6",done:"#7C3AED",cancelled:"#9CA3AF",noshow:"#EF4444"})[a.status] || "#FF7AA2";
                                const fg = statusFg;
                                // Merge spaIds-derived SPA names with regular
                                // addons, and drop any legacy SPA labels that
                                // older bookings still have inside `addons`.
                                const spaNamesC = (a.spaIds || []).map(id => (tenant?.services || []).find(sv => sv.id === id)?.name).filter(Boolean);
                                const cleanAddonsC = (a.addons || []).filter(n => !String(n).startsWith("💆"));
                                const addons=[...spaNamesC.map(n => "💆 " + n), ...cleanAddonsC];
                                // Treat all forms of "no allergy" the same:
                                // empty, "無", "None", "none", "无", etc. — language
                                // mixing can leave any of these on the pet row.
                                const NO_ALLERGY = new Set(["", "無", "无", "None", "none", "NONE", "n/a", "N/A", "nil", "Nil"]);
                                const allergy = pet?.allergies && !NO_ALLERGY.has(String(pet.allergies).trim()) ? pet.allergies : null;
                                const vetNote=pet?.vetNote||null;
                                const ownerNote=pet?.behavior?.notes||pet?.groomingStyle?.notes||null;
                                const staffNote=a.staffNote||null;
                                // Severity badges (very compact, 1 char each)
                                const badges=[];
                                if (pet?.behavior?.bites) badges.push({c:"#DC2626",t:"🦷"});
                                if (pet?.behavior?.dryerFear) badges.push({c:"#D97706",t:"🌪️"});
                                if (pet?.behavior?.anxious) badges.push({c:"#D97706",t:"😰"});
                                if (pet?.behavior?.aggressiveWithDogs) badges.push({c:"#D97706",t:"🐕"});
                                if (pet?.medicalFlags?.heart||pet?.medicalFlags?.seizures) badges.push({c:"#DC2626",t:"⚕️"});
                                return <div
                                  key={a.id}
                                  draggable
                                  onDragStart={e => { e.dataTransfer.setData("aptId", a.id); e.dataTransfer.effectAllowed = "move"; draggedAptRef.current = { id: a.id, duration: getAptDuration(a, tenant) }; }}
                                  onDragEnd={() => setDragOverInfo(null)}
                                  onClick={() => {
                                    // Drag-just-finished guard: only suppress when THIS card's drag
                                    // engaged. A stale ref from a prior captured-by-scroll touch was
                                    // making every appointment unclickable. Also self-heal: anything
                                    // older than 1s is stale; clear it.
                                    const r = touchDragRef.current;
                                    const ageMs = r?.startedAt ? (Date.now() - r.startedAt) : 0;
                                    if (r && ageMs > 1500) { touchDragRef.current = null; }
                                    if (r?.engaged && r?.aptId === a.id && ageMs < 1500) return;
                                    if (a.kind === "event") { setEventForm({ id:a.id, staffId:a.staffId, date:a.date, time:a.time, durationMin:a.customDuration||30, label:a.label||"", staffNote:a.staffNote||"", color:a.color||"", leaveType:a.leaveType||"" }); return; }
                                    setEditApt(a);
                                  }}
                                  onTouchStart={e => {
                                    // New touch wipes any stale ref from a prior gesture that
                                    // never got a clean touchend (page scroll captured it,
                                    // browser cancelled mid-drag, etc).
                                    const prev = touchDragRef.current;
                                    if (prev) { clearTimeout(prev.timer); touchDragRef.current = null; setDragOverInfo(null); }
                                    const t = e.touches[0];
                                    const ref = { aptId: a.id, startX: t.clientX, startY: t.clientY, engaged: false, timer: null, targetStaff: null, targetTime: null, startedAt: Date.now() };
                                    ref.timer = setTimeout(() => {
                                      if (touchDragRef.current === ref) {
                                        ref.engaged = true;
                                        if (navigator.vibrate) navigator.vibrate(15);
                                      }
                                    }, 400);
                                    touchDragRef.current = ref;
                                  }}
                                  onTouchMove={e => {
                                    const ref = touchDragRef.current;
                                    if (!ref) return;
                                    const t = e.touches[0];
                                    if (!ref.engaged) {
                                      const dy = Math.abs(t.clientY - ref.startY);
                                      const dx = Math.abs(t.clientX - ref.startX);
                                      if (dy > 4 || (dy > dx && dy > 2)) {
                                        clearTimeout(ref.timer);
                                        touchDragRef.current = null;
                                        return;
                                      }
                                      if (Math.hypot(dx, dy) > 10) {
                                        clearTimeout(ref.timer);
                                        touchDragRef.current = null;
                                      }
                                      return;
                                    }
                                    const under = document.elementFromPoint(t.clientX, t.clientY);
                                    let col = under;
                                    while (col && !col.dataset?.staffCol) col = col.parentElement;
                                    if (!col) return;
                                    const rect = col.getBoundingClientRect();
                                    const offsetY = t.clientY - rect.top;
                                    const snappedMin = Math.max(0, Math.round(offsetY / hourHeight * 60 / 15) * 15);
                                    ref.targetStaff = col.dataset.staffCol;
                                    ref.targetTime = String(earliestHour + Math.floor(snappedMin/60)).padStart(2,"0") + ":" + String(snappedMin%60).padStart(2,"0");
                                    // Conflict prediction so the touch drag
                                    // preview turns red on conflict (matched
                                    // mouse-drag behaviour). Unassigned lane
                                    // skips this since it's a holding area.
                                    const myDur2 = getAptDuration(a, tenant);
                                    const newStart2 = earliestHour * 60 + snappedMin;
                                    const newEnd2 = newStart2 + myDur2;
                                    const targetIsUnassigned = ref.targetStaff === UNASSIGNED_ID;
                                    const conflictApts = targetIsUnassigned ? [] : allApts.filter(x =>
                                      x.staffId === ref.targetStaff &&
                                      x.date === selectedScheduleDate &&
                                      !isAptDead(x) &&
                                      x.id !== a.id
                                    );
                                    const touchConflict = conflictApts.some(o => {
                                      const [oh, om] = (o.time || "00:00").split(":").map(Number);
                                      const oStart = oh * 60 + om;
                                      const oDur = getAptDuration(o, tenant);
                                      return newStart2 < (oStart + oDur) && newEnd2 > oStart;
                                    });
                                    ref.conflict = touchConflict;
                                    setDragOverInfo({ staffId: ref.targetStaff, top: (snappedMin / 60) * hourHeight, conflict: touchConflict });
                                  }}
                                  onTouchEnd={() => {
                                    const ref = touchDragRef.current;
                                    if (ref) {
                                      clearTimeout(ref.timer);
                                      if (ref.engaged && ref.targetStaff && ref.targetTime) {
                                        if (ref.conflict) {
                                          toast(zhEn("時段衝突，未能移動", "Time clash — couldn't move"), "error");
                                        } else {
                                          moveAppointment(ref.aptId, ref.targetStaff, ref.targetTime);
                                        }
                                      }
                                    }
                                    // Identity-check the 0-tick clear: a follow-up
                                    // touchstart that arrived before this timer
                                    // fires would otherwise have its fresh ref
                                    // wiped, leaving the next drag dead-on-arrival.
                                    // Only null if it's still OUR ref.
                                    setTimeout(() => { if (touchDragRef.current === ref) touchDragRef.current = null; }, 0);
                                    setDragOverInfo(null);
                                  }}
                                  onTouchCancel={() => {
                                    const ref = touchDragRef.current;
                                    if (ref) clearTimeout(ref.timer);
                                    touchDragRef.current = null;
                                    setDragOverInfo(null);
                                  }}
                                  style={{position:"absolute",left:4,right:4,top:top+2,height:cardH,borderRadius:18,background:bg,border:"none",boxShadow:"0 2px 8px rgba(31,41,55,0.06)",padding:"6px 10px",boxSizing:"border-box",overflow:"hidden",zIndex:2,cursor:"grab",userSelect:"none",WebkitUserSelect:"none",WebkitTouchCallout:"none",touchAction:"pan-y",display:"flex",flexDirection:"column",gap:2}}
                                  title={[
                                    a.time+" "+SL[a.status]+" "+dur+"min",
                                    pet?.name+" / "+a.service,
                                    addons.length?zhEn("加購: ", "Add-ons: ")+addons.join("、"):"",
                                    allergy?zhEn("⚠️ 過敏: ", "⚠️ Allergy: ")+allergy:"",
                                    vetNote?zhEn("🩺 獸醫備註: ", "🩺 Vet note: ")+vetNote:"",
                                    ownerNote?zhEn("🏠 主人備註: ", "🏠 Owner note: ")+ownerNote:"",
                                    staffNote?zhEn("📝 員工備註: ", "📝 Staff note: ")+staffNote:"",
                                  ].filter(Boolean).join("\n")}
                                >
                                  {/* Round pet avatar — top-right corner. Uses
                                      first uploaded photo when available, else
                                      a type-appropriate emoji (🐕 / 🐈 / 🐇).
                                      Absolute-positioned so it doesn't disturb
                                      the existing flex layout. z-index sits
                                      below the payment bubble (which is z:3)
                                      since the bubble lives bottom-right and
                                      they don't overlap. */}
                                  {(() => {
                                    const photoUrl = pet?.photos?.[0]?.url || null;
                                    const fallbackEmoji = pet?.type === "cat" ? "🐈" : pet?.type === "rabbit" ? "🐇" : pet?.type === "hamster" ? "🐹" : pet?.type === "guinea_pig" ? "🐹" : "🐕";
                                    return (
                                      <div aria-hidden style={{position:"absolute",top:4,right:4,width:26,height:26,borderRadius:"50%",overflow:"hidden",background:"#fff",border:"2px solid #fff",boxShadow:"0 1px 4px rgba(0,0,0,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,zIndex:2,pointerEvents:"none"}}>
                                        {photoUrl
                                          ? <img src={photoUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} />
                                          : <span>{fallbackEmoji}</span>}
                                      </div>
                                    );
                                  })()}
                                  {/* Top line: duration + price pill (time prefix
                                      was removed per owner feedback — the card's
                                      vertical position already encodes the
                                      time, so duplicating it on the card was
                                      noise). Pet remarks (red chips) live just
                                      under the name for instant awareness. */}
                                  {/* Badges-only top row — kept only if there
                                      are medical/behavior flags to surface.
                                      Right-padded so the round avatar doesn't
                                      overlap. */}
                                  {badges.length>0 && (
                                    <div style={{fontSize:10,display:"inline-flex",gap:1,alignSelf:"flex-end",paddingRight:32}}>
                                      {badges.slice(0,3).map((b,i)=><span key={i}>{b.t}</span>)}
                                    </div>
                                  )}
                                  {/* Pet name + total price on the SAME line.
                                      Price moves inline so the card top reads
                                      "name … $price" at a glance. Breed name
                                      shown as a small parenthetical under the
                                      pet name for quick groomer reference. */}
                                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:6,paddingRight:32}}>
                                    <span style={{fontSize:15,fontWeight:900,color:"#1F2937",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.2,flex:1,minWidth:0}}>{pet?.name||""}</span>
                                    {(() => {
                                      const sub = (a.basePrice||0) + (a.addonPrice||0) + (a.surcharges||[]).reduce((s,x)=>s+(x.amount||0),0);
                                      const disc = a.discount ? (a.discount.type === "percent"
                                        ? Math.round(sub * (a.discount.amount || 0) / 100)
                                        : (a.discount.amount || 0)) : 0;
                                      const total = Math.max(0, sub - disc);
                                      // Net collected from payment_log so the chip reflects
                                      // ACTUAL money in the till, not just the paid boolean
                                      // (which may lag for cash bookings before the owner
                                      // taps 收款).
                                      const usesPackage = !!a.customerPackageId;
                                      // Package bookings: basePrice is covered by the package,
                                      // so the cash-due is just the addon/SPA/surcharge portion
                                      // (with discount/voucher applied proportionally). aptRevenue
                                      // already does that math.
                                      const cashDue = usesPackage ? aptRevenue(a) : total;
                                      const collected = Array.isArray(a.paymentLog) ? a.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0) : 0;
                                      const isPaid = a.paid || (cashDue > 0 && collected >= cashDue) || (usesPackage && cashDue === 0);
                                      const isPartial = !isPaid && collected > 0;
                                      const cancelled = a.status === "cancelled" || a.status === "noshow";
                                      // Pill: green=paid, amber=partial, red=未付/cancelled, slate=normal
                                      const pillBg = cancelled ? "#6B7280" : (isPaid ? "#10B981" : (isPartial ? "#F59E0B" : "rgba(31,41,55,0.85)"));
                                      const pillLabel = cancelled ? "—" : (isPaid ? zhEn("✓ 已付", "✓ Paid") : (isPartial ? zhEn("部分已付 $", "Part paid $") + collected : zhEn("未付", "Unpaid")));
                                      const canTap = !cancelled && !isPaid && cashDue > 0;
                                      const onPillTap = async (ev) => {
                                        ev.stopPropagation();
                                        if (!canTap) return;
                                        const remaining = Math.max(0, cashDue - collected);
                                        const entry = { id:"pay"+uid(), at:new Date().toISOString(), amount: remaining, method:"manual", methodName:"店家系統", by:user.id, type:"payment", note:"" };
                                        try {
                                          const res = await recordPaymentAtomic(a.id, entry);
                                          setState(st => ({ ...st, appointments: st.appointments.map(x => x.id === a.id ? { ...x, paid: !!res?.paid, remainingBalance: Math.max(0, Number(res?.total||0) - Number(res?.collected||0)), paymentLog: Array.isArray(res?.log) ? res.log : (x.paymentLog || []) } : x) }));
                                          toast(zhEn("已收款 ", "Collected ") + cur(tenant) + remaining + " ✓");
                                        } catch (e) { toast(zhEn("收款失敗：", "Payment failed: ") + (e?.message || e), "error"); }
                                      };
                                      // ── Single consolidated bubble (bottom-right). ──
                                      // Replaces the previous 2-pill stack
                                      // (price + payment status). One chip
                                      // showing dollar+status, colour-coded.
                                      const bubbleBg = cancelled
                                        ? "#6B7280"
                                        : (usesPackage && cashDue === 0)
                                          ? "linear-gradient(135deg,#F59E0B,#D97706)"
                                          : isPaid ? "linear-gradient(135deg,#10B981,#059669)"
                                          : isPartial ? "linear-gradient(135deg,#F59E0B,#D97706)"
                                          : "linear-gradient(135deg,#DC2626,#B91C1C)";
                                      const bubbleText = cancelled
                                        ? "—"
                                        : (usesPackage && cashDue === 0)
                                          ? zhEn("📦 套票", "📦 Package")
                                          : usesPackage
                                            ? (isPaid ? "📦 ✓ $" + collected : isPartial ? "📦 $" + collected + "/$" + cashDue : "📦 +$" + cashDue + zhEn(" 未付", " unpaid"))
                                            : (isPaid ? "✓ $" + collected + zhEn(" 已付", " paid") : isPartial ? "$" + collected + "/$" + total + zhEn(" 部分", " part") : "$" + total + zhEn(" 未付", " unpaid"));
                                      return (
                                        <button
                                          onClick={onPillTap}
                                          disabled={!canTap}
                                          title={canTap ? zhEn("撳一下收剩餘 $", "Tap to collect the remaining $") + Math.max(0, cashDue - collected) : (usesPackage && cashDue===0 ? zhEn("使用套票結算", "Settled with a package") : "")}
                                          style={{position:"absolute",right:6,bottom:6,zIndex:3,display:"inline-flex",alignItems:"center",gap:3,background:bubbleBg,color:"#fff",border:"none",borderRadius:99,padding:"3px 10px",fontSize:10,fontWeight:900,letterSpacing:0.2,lineHeight:1.2,cursor:canTap?"pointer":"default",boxShadow:"0 2px 8px rgba(0,0,0,0.18)",whiteSpace:"nowrap"}}
                                        >
                                          {bubbleText}
                                        </button>
                                      );
                                    })()}
                                  </div>
                                  {(pet?.breed || pet?.weight) && (
                                    <div style={{fontSize:10,color:"#8E8E93",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.2}}>({[pet?.breed, pet?.weight ? pet.weight + "kg" : ""].filter(Boolean).join(" · ")})</div>
                                  )}
                                  <div style={{fontSize:13,color:"#374151",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.3}}>{a.service}</div>
                                  {/* Addons — neutral grey chip, no purple. */}
                                  {addons.length>0 && cardH>50 && (
                                    <div style={{fontSize:11,fontWeight:700,color:"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",background:"rgba(31,41,55,0.06)",padding:"2px 8px",borderRadius:99,alignSelf:"flex-start"}}>
                                      {"+ " + addons.map(n => String(n).replace(/\s*[·•]\s*\$\d+/g, "")).join("、")}
                                    </div>
                                  )}
                                  {/* Allergy (high priority) */}
                                  {allergy && cardH>70 && (
                                    <div style={{fontSize:10,fontWeight:900,color:"#DC2626",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{"⚠️ "+allergy}</div>
                                  )}
                                  {/* Owner note (pet behavior / grooming style notes from owner) */}
                                  {ownerNote && cardH>90 && (
                                    <div style={{fontSize:10,fontWeight:700,color:"#5B21B6",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",background:"rgba(91,33,182,0.08)",padding:"1px 5px",borderRadius:6}}>{"🏠 "+ownerNote}</div>
                                  )}
                                  {/* Vet note */}
                                  {vetNote && cardH>130 && (
                                    <div style={{fontSize:10,fontWeight:700,color:"#92400E",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",background:"#FFFBEB",padding:"1px 5px",borderRadius:6}}>{"🩺 "+vetNote}</div>
                                  )}
                                  {/* Remark chips — pinned to the BOTTOM. Pet staff_note
                                      (red), customer grooming_request (blue), staff
                                      booking note (purple). marginTop:auto pushes them
                                      to the card's bottom edge regardless of content
                                      above. */}
                                  {/* Remark chips flow inline (no marginTop:auto).
                                      Payment bubble owns the bottom-right corner
                                      on its own row, so chips stay higher up
                                      and never overlap. */}
                                  {(pet?.staffNote || a.groomingRequest || staffNote) && cardH>50 && (
                                    <div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:24}}>
                                      {String(pet?.staffNote || "").split(/[、,，|\n]+/).map(s => s.trim()).filter(Boolean).slice(0, 3).map((n, i) => (
                                        <span key={"p"+i} style={{background:"#EF4444",color:"#fff",borderRadius:4,padding:"0 5px",fontSize:9,fontWeight:900,whiteSpace:"nowrap"}}>{n}</span>
                                      ))}
                                      {a.groomingRequest && (
                                        <span style={{background:"#3B82F6",color:"#fff",borderRadius:4,padding:"0 5px",fontSize:9,fontWeight:900,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>✂ {a.groomingRequest}</span>
                                      )}
                                      {staffNote && (
                                        <span style={{background:"#7C3AED",color:"#fff",borderRadius:4,padding:"0 5px",fontSize:9,fontWeight:900,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📝 {staffNote}</span>
                                      )}
                                    </div>
                                  )}
                                  {/* Label pill — top-right corner of the calendar
                                      card. Matches Setmore's tag affordance so staff
                                      can pin 已付款 / 遲到 / VIP etc visibly. The
                                      26px pet avatar lives in the same corner at
                                      right:4, so the label sits to its LEFT
                                      (right:36) to avoid overlap. */}
                                  {a.label && (
                                    <span style={{position:"absolute",right:36,top:6,background:"rgba(255,255,255,0.92)",color:"#1C1C1E",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:900,boxShadow:"0 1px 2px rgba(0,0,0,0.15)",letterSpacing:0.2,maxWidth:"calc(100% - 78px)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.label}</span>
                                  )}
                                  {/* Deposit-pending indicator. Dot turns
                                      green when the customer tapped "I've
                                      paid" so the shop can prioritise
                                      verification. Disappears once shop
                                      confirms (depositConfirmedAt set). */}
                                  {(a.deposit > 0 && !a.depositConfirmedAt && a.status !== "cancelled" && a.status !== "noshow") && (
                                    <span title={a.depositClaimedAt ? zhEn("客戶已通知，待你確認訂金 $", "The customer says they've paid — confirm the deposit $") + a.deposit : zhEn("訂金待確認 $", "Deposit to confirm $") + a.deposit}
                                          style={{position:"absolute",left:6,top:6,background: a.depositClaimedAt ? "#10B981" : "rgba(245,158,11,0.95)",color:"#fff",borderRadius:99,padding:"2px 8px",fontSize:9,fontWeight:900,letterSpacing:0.2,boxShadow:"0 2px 6px rgba(0,0,0,0.18)",display:"inline-flex",alignItems:"center",gap:3}}>
                                      {a.depositClaimedAt ? "🔔" : "⏳"} {zhEn("訂金 $", "Deposit $")}{a.deposit}
                                    </span>
                                  )}
                                </div>;
                              })}
                            </div>;
                          })}
                        </div>
                      </div>
                    </>}
                  </div>
                </div>
              </Card>}

              {/* ── AGENDA VIEW — chronological list grouped by date ─── */}
              {viewMode === "agenda" && (() => {
                const today = dateNow();
                // Status + range filters live on window so they survive re-renders
                // without adding state plumbing for this self-contained view.
                const ranges = [["upcoming",zhEn("即將", "Upcoming")],["today",zhEn("今日", "Today")],["week",zhEn("本週", "This week")],["past",zhEn("已過", "Past")],["all",zhEn("全部", "All")]];
                const statuses = [["all",zhEn("全部", "All")],["pending",zhEn("等候", "Waiting")],["checkedin",zhEn("已登到", "Checked in")],["grooming",zhEn("美容中", "Grooming")],["done",zhEn("完成", "Done")],["cancelled",zhEn("取消", "Cancel")],["noshow",zhEn("未到店", "No-show")],["event",zhEn("📌 事件", "📌 Events")]];
                const fr = window.__agendaRange || "upcoming";
                const fs = window.__agendaStatus || "all";
                const setFR = (v) => { window.__agendaRange = v; setNowTick(t => t + 1); };
                const setFS = (v) => { window.__agendaStatus = v; setNowTick(t => t + 1); };
                // 7 days from today (start of week → end of week)
                const weekStartDate = new Date(today); weekStartDate.setDate(weekStartDate.getDate() - weekStartDate.getDay());
                const weekStartStr = localDateStr(weekStartDate);
                const weekEndDate = new Date(weekStartDate); weekEndDate.setDate(weekEndDate.getDate() + 6);
                const weekEndStr = localDateStr(weekEndDate);
                const filtered = allApts
                  // Events are hidden from agenda by default — they're an
                  // internal calendar block, not a customer-facing booking.
                  // Owner can opt in via the dedicated 📌 事件 chip.
                  .filter(a => fs === "event" ? a.kind === "event" : a.kind !== "event")
                  .filter(a => fs === "all" || fs === "event" ? a.status !== "cancelled" || fr === "past" || fr === "all" : a.status === fs)
                  .filter(a => fr === "upcoming" ? a.date >= today
                             : fr === "today" ? a.date === today
                             : fr === "week" ? (a.date >= weekStartStr && a.date <= weekEndStr)
                             : fr === "past" ? a.date <= today
                             : true)
                  .sort((a, b) => (b.date.localeCompare(a.date) * (fr === "past" ? 1 : -1)) || (a.time || "").localeCompare(b.time || ""))
                  .sort((a, b) => fr === "past" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""));
                // Stats summary
                const totalRev = filtered.filter(a => a.status === "done").reduce((s,a) => s + aptRevenue(a), 0);
                const expectRev = filtered.filter(a => a.status !== "done" && a.status !== "cancelled" && a.status !== "noshow").reduce((s,a) => s + aptRevenue(a), 0);
                // Smart grouping label
                const groupLabel = (date) => {
                  if (date === today) return zhEn("今日", "Today");
                  const t = new Date(today); const d = new Date(date);
                  const diff = Math.round((d - t) / 86400000);
                  if (diff === 1) return zhEn("明日", "Tomorrow");
                  if (diff === -1) return zhEn("昨日", "Yesterday");
                  if (diff > 1 && diff <= 7) return zhEn("本週 · ", "This week · ") + d.toLocaleDateString(dateLoc(),{month:"long",day:"numeric",weekday:"short"});
                  if (diff > 7 && diff <= 14) return zhEn("下週 · ", "Next week · ") + d.toLocaleDateString(dateLoc(),{month:"long",day:"numeric",weekday:"short"});
                  return d.toLocaleDateString(dateLoc(),{month:"long",day:"numeric",weekday:"short"});
                };
                const byDate = {};
                for (const a of filtered) { (byDate[a.date] = byDate[a.date] || []).push(a); }
                const statusColor = {pending:["#FFF7ED","#9A3412"],checkedin:["#DBEAFE","#1D4ED8"],grooming:["#E0F2FE","#0369A1"],done:["#EDE9FE","#5B21B6"],cancelled:["#F3F4F6","#6B7280"],noshow:["#FEE2E2","#991B1B"]};
                const statusLabel = {pending:zhEn("等候", "Waiting"),checkedin:zhEn("已登到", "Checked in"),grooming:zhEn("美容中", "Grooming"),done:zhEn("完成", "Done"),cancelled:zhEn("取消", "Cancel"),noshow:zhEn("未到店", "No-show")};
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    {/* Summary */}
                    <Card style={{padding:14}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                        <div style={{textAlign:"center"}}>
                          <p style={{fontSize:18,fontWeight:900,color:"var(--c-primary)",margin:0}}>{filtered.length}</p>
                          <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("預約數", "Bookings")}</p>
                        </div>
                        <div style={{textAlign:"center"}}>
                          <p style={{fontSize:18,fontWeight:900,color:"#10B981",margin:0}}>${totalRev}</p>
                          <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("已完成收入", "Completed revenue")}</p>
                        </div>
                        <div style={{textAlign:"center"}}>
                          <p style={{fontSize:18,fontWeight:900,color:"#D97706",margin:0}}>${expectRev}</p>
                          <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("未完成預期", "Expected (not done)")}</p>
                        </div>
                      </div>
                    </Card>
                    {/* Range filter */}
                    <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                      {ranges.map(([v,l]) => (
                        <button key={v} onClick={() => setFR(v)} style={{padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:800,border:"none",background:fr===v?"var(--c-primary)":"var(--c-light)",color:fr===v?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap"}}>{l}</button>
                      ))}
                    </div>
                    {/* Status filter */}
                    <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                      {statuses.map(([v,l]) => (
                        <button key={v} onClick={() => setFS(v)} style={{padding:"6px 12px",borderRadius:99,fontSize:11,fontWeight:700,border:"2px solid "+(fs===v?"var(--c-primary)":"var(--c-light)"),background:fs===v?"var(--c-lighter)":"#fff",color:fs===v?"var(--c-primary)":"#8A8A9E",cursor:"pointer",whiteSpace:"nowrap"}}>{l}</button>
                      ))}
                    </div>
                    {/* Empty state */}
                    {Object.keys(byDate).length === 0 && (
                      <Card style={{padding:32,textAlign:"center"}}>
                        <Calendar size={32} color="#AFAFC4" strokeWidth={2} style={{margin:"0 auto 8px"}} />
                        <p style={{fontSize:14,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("沒有符合條件嘅預約", "No matching bookings")}</p>
                      </Card>
                    )}
                    {Object.entries(byDate).map(([date, apts]) => (
                      <Card key={date} style={{padding:0,overflow:"hidden"}}>
                        <div style={{padding:"10px 16px",background:"var(--c-lighter)",borderBottom:"2px solid var(--c-light)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:13,fontWeight:900,color:"#4A4A68"}}>{groupLabel(date)}</span>
                          <span style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>{apts.filter(a=>a.kind!=="event").length} {zhEn("個預約 · $", "bookings · $")}{apts.reduce((s,a)=>s+aptRevenue(a),0)}</span>
                        </div>
                        {apts.map((a, i) => {
                          const pet = state.pets[a.petId];
                          const cust = state.users[a.customerId];
                          const staff = state.users[a.staffId];
                          // Use the shared revenue helper so discount +
                          //   package redemption are applied identically
                          //   to the group sum, dashboard tiles + EOD.
                          const total = aptRevenue(a);
                          const petRemarks = (pet?.staffNote || "").split(/[、,，|\n]+/).map(s=>s.trim()).filter(Boolean);
                          const [bg, fg] = statusColor[a.status] || statusColor.pending;
                          const lbl = statusLabel[a.status] || a.status;
                          return (
                            <div key={a.id} onClick={() => { if (a.kind === "event") { setEventForm({ id:a.id, staffId:a.staffId, date:a.date, time:a.time, durationMin:a.customDuration||30, label:a.label||"", staffNote:a.staffNote||"", color:a.color||"", leaveType:a.leaveType||"" }); return; } setEditApt(a); }} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:i===apts.length-1?"none":"1px solid var(--c-light)",cursor:"pointer",background:a.kind==="event"?"#F9FAFB":undefined}}>
                              <div style={{flexShrink:0,width:54,textAlign:"center"}}>
                                <p style={{fontSize:14,fontWeight:900,color:"var(--c-primary)",margin:0}}>{a.time}</p>
                                <p style={{fontSize:10,color:"#AFAFC4",margin:"2px 0 0",fontWeight:700}}>{getAptDuration(a, tenant)}m</p>
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                {a.kind === "event" ? <>
                                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                                    <p style={{fontSize:15,fontWeight:900,color:"#374151",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📌 {a.label || zhEn("事件", "Event")}</p>
                                    <span style={{background:"#E5E7EB",color:"#374151",fontSize:9,fontWeight:900,borderRadius:99,padding:"1px 7px"}}>{zhEn("內部", "Internal")}</span>
                                  </div>
                                  <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(staff?.name || zhEn("全店", "Storewide")) + (a.staffNote ? " · " + a.staffNote : "")}</p>
                                </> : <>
                                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                                    <p style={{fontSize:15,fontWeight:900,color:"#1F2937",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pet?.name || "(?)"}</p>
                                    <span style={{background:bg,color:fg,fontSize:9,fontWeight:900,borderRadius:99,padding:"1px 7px",whiteSpace:"nowrap"}}>{lbl}</span>
                                    {petRemarks.slice(0,2).map((n,j) => (
                                      <span key={j} style={{background:"#EF4444",color:"#fff",fontSize:9,fontWeight:900,borderRadius:4,padding:"0 5px",whiteSpace:"nowrap"}}>{n}</span>
                                    ))}
                                  </div>
                                  <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                    {(cust?.name || zhEn("(訪客)", "(guest)")) + " · " + (a.service || "") + (staff ? " · " + staff.name : "")}
                                  </p>
                                </>}
                              </div>
                              {a.kind !== "event" && (() => {
                                const sub = (a.basePrice||0) + (a.addonPrice||0) + (a.surcharges||[]).reduce((s,x)=>s+(x.amount||0),0);
                                const disc = a.discount ? (a.discount.type === "percent" ? Math.round(sub * (a.discount.amount || 0) / 100) : (a.discount.amount || 0)) : 0;
                                const aptTotal = Math.max(0, sub - disc);
                                const usesPackageA = !!a.customerPackageId;
                                // basePrice is covered by package, so the cash-due is the
                                // addon/SPA/surcharge portion only. aptRevenue handles that.
                                const cashDueA = usesPackageA ? aptRevenue(a) : aptTotal;
                                const aptCollected = Array.isArray(a.paymentLog) ? a.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0) : 0;
                                const aptPaid = a.paid || (cashDueA > 0 && aptCollected >= cashDueA) || (usesPackageA && cashDueA === 0);
                                const aptPartial = !aptPaid && aptCollected > 0;
                                const cancelledA = a.status === "cancelled" || a.status === "noshow";
                                const payLabel = cancelledA ? "—" : (aptPaid ? zhEn("✓ 已付", "✓ Paid") : (aptPartial ? zhEn("部分已付", "Part paid") : zhEn("未付", "Unpaid")));
                                const canTapA = !cancelledA && !aptPaid && cashDueA > 0;
                                const pillBgA = canTapA ? "#DC2626" : (aptPaid ? "#10B981" : (aptPartial ? "#F59E0B" : "#9CA3AF"));
                                const onPillTapA = async (ev) => {
                                  ev.stopPropagation();
                                  if (!canTapA) return;
                                  const remaining = Math.max(0, cashDueA - aptCollected);
                                  const entry = { id:"pay"+uid(), at:new Date().toISOString(), amount: remaining, method:"manual", methodName:"店家系統", by:user.id, type:"payment", note:"" };
                                  try {
                                    const res = await recordPaymentAtomic(a.id, entry);
                                    setState(st => ({ ...st, appointments: st.appointments.map(x => x.id === a.id ? { ...x, paid: !!res?.paid, remainingBalance: Math.max(0, Number(res?.total||0) - Number(res?.collected||0)), paymentLog: Array.isArray(res?.log) ? res.log : (x.paymentLog || []) } : x) }));
                                    toast(zhEn("已收款 ", "Collected ") + cur(tenant) + remaining + " ✓");
                                  } catch (e) { toast(zhEn("收款失敗：", "Payment failed: ") + (e?.message || e), "error"); }
                                };
                                // Single consolidated bubble (agenda row right edge).
                                const _bg = cancelledA ? "#6B7280"
                                  : (usesPackageA && cashDueA === 0) ? "linear-gradient(135deg,#F59E0B,#D97706)"
                                  : aptPaid ? "linear-gradient(135deg,#10B981,#059669)"
                                  : aptPartial ? "linear-gradient(135deg,#F59E0B,#D97706)"
                                  : "linear-gradient(135deg,#DC2626,#B91C1C)";
                                const _txt = cancelledA ? "—"
                                  : (usesPackageA && cashDueA === 0) ? zhEn("📦 套票", "📦 Package")
                                  : usesPackageA
                                    ? (aptPaid ? "📦 ✓ $" + aptCollected : aptPartial ? "📦 $" + aptCollected + "/$" + cashDueA : "📦 +$" + cashDueA + zhEn(" 未付", " unpaid"))
                                    : (aptPaid ? "✓ $" + aptCollected + zhEn(" 已付", " paid") : aptPartial ? "$" + aptCollected + "/$" + total + zhEn(" 部分", " part") : "$" + total + zhEn(" 未付", " unpaid"));
                                return (
                                  <button onClick={onPillTapA} disabled={!canTapA}
                                    style={{flexShrink:0,display:"inline-flex",alignItems:"center",gap:3,background:_bg,color:"#fff",border:"none",borderRadius:99,padding:"5px 12px",fontSize:11,fontWeight:900,letterSpacing:0.2,cursor:canTapA?"pointer":"default",boxShadow:"0 2px 8px rgba(0,0,0,0.18)",whiteSpace:"nowrap"}}>
                                    {_txt}
                                  </button>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </Card>
                    ))}
                  </div>
                );
              })()}

            </>;
          })()}
          {/* Floating walk-in FAB — primary action moved off the toolbar
              into the bottom-right corner. Sits above the global tab bar. */}
          <button
            onClick={() => setWalkInForm({ customerId:"", petId:"", svcId:"", staffId:"", time:timeNow(), addons:[], spaIds:[], discountAmount:0, discountType:"percent", discountReason:"", staffRemark:"" })}
            title={i18nT("own.walkin.title")}
            style={{position:"fixed",bottom:90,right:18,zIndex:25,width:56,height:56,borderRadius:99,background:"#10B981",color:"#fff",border:"none",cursor:"pointer",boxShadow:"0 12px 32px rgba(16,185,129,0.5)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:0,fontWeight:900,fontSize:10}}
          >
            <Plus size={20} strokeWidth={3} />
            <span>{zhEn("即場", "Walk-in")}</span>
          </button>
        </FeatureGate>}

        {/* ── ROSTER (員工月排班) TAB ─── */}
        {tab === "roster" && (() => {
          const allActive = allStaff.filter(s => s.enabled !== false);
          // Non-owner staff only sees / edits their own row
          const activeStaff = user.role === "owner" ? allActive : allActive.filter(s => s.id === user.id);
          if (activeStaff.length === 0) return (
            <EmptyState icon={CalendarDays} title={zhEn("暫無員工", "No staff")} sub={user.role === "owner" ? zhEn("先到「員工」頁面新增員工", "Add staff in the \"Staff\" page first") : zhEn("你的帳號未啟用", "Your account isn't active")} />
          );
          const idx = Math.min(staffScheduleIdx, activeStaff.length - 1);
          const s = activeStaff[idx];
          const hasEditRole = user.role === "owner" || s.id === user.id;
          const canEdit = hasEditRole && !rosterLocked;
          const [smY, smM] = staffScheduleMonth.split("-").map(Number);
          const smDays = new Date(smY, smM, 0).getDate();
          const daysArr = (s.workDays || {})[staffScheduleMonth] || [];
          const setWorkDays = (next) => {
            setState(st => ({...st, users:{...st.users, [s.id]:{...st.users[s.id], workDays:{...(st.users[s.id].workDays||{}), [staffScheduleMonth]:next}}}}));
          };
          // Date-range guard — block roster edits outside the staff's
          // employed window. Owners shouldn't be able to schedule a
          // staffer before they joined or after they left.
          const employmentJoin = s.joinDate || "";
          const employmentLeave = s.leaveDate || "";
          const inEmployment = (ds) => {
            if (employmentJoin && ds < employmentJoin) return false;
            if (employmentLeave && ds > employmentLeave) return false;
            return true;
          };
          const toggleDay = (day) => {
            if (!canEdit) return;
            const ds = staffScheduleMonth + "-" + String(day).padStart(2,"0");
            if (!inEmployment(ds)) {
              toast(employmentJoin && ds < employmentJoin ? zhEn("此日期早於入職日 ", "This date is before the join date ") + employmentJoin : zhEn("此日期晚於離職日 ", "This date is after the leave date ") + employmentLeave, "warning");
              return;
            }
            setWorkDays(daysArr.includes(ds) ? daysArr.filter(x=>x!==ds) : [...daysArr, ds]);
          };
          const fillAll = () => { if (!canEdit) return; setWorkDays(
            Array.from({length:smDays}, (_,i) => staffScheduleMonth + "-" + String(i+1).padStart(2,"0"))
              .filter(ds => inEmployment(ds))
          ); };
          const clearAll = () => { if (!canEdit) return; setWorkDays([]); };
          const copyWeekdays = () => { if (!canEdit) return; setWorkDays(
            Array.from({length:smDays}, (_,i) => {
              const dow = new Date(smY, smM-1, i+1).getDay();
              const ds = staffScheduleMonth + "-" + String(i+1).padStart(2,"0");
              if (dow === 0 || dow === 6) return null;
              if (!inEmployment(ds)) return null;
              return ds;
            }).filter(Boolean)
          ); };
          const prevStaff = () => setStaffScheduleIdx(i => (i - 1 + activeStaff.length) % activeStaff.length);
          const nextStaff = () => setStaffScheduleIdx(i => (i + 1) % activeStaff.length);

          // Count today's confirmed appointments per staff (today only, for chip badge)
          const todayApts = state.appointments.filter(a => a.tenantId === user.tenantId && a.date === dateNow() && a.status !== "cancelled" && a.status !== "noshow");

          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><CalendarDays size={22} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("員工排班", "Staff roster")} <HelpTip topic="shifts" /></h2>
                {hasEditRole ? (
                  <button
                    onClick={() => setRosterLocked(v => !v)}
                    style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,border:"1.5px solid " + (rosterLocked ? "#E5E5EA" : "#10B981"),background: rosterLocked ? "#F2F2F7" : "#ECFDF5",color: rosterLocked ? "#6A6A85" : "#047857",fontWeight:900,fontSize:12,cursor:"pointer"}}
                    title={rosterLocked ? zhEn("解鎖以編輯排班", "Unlock to edit the roster") : zhEn("鎖定，防止誤觸", "Lock to prevent accidental taps")}
                  >
                    {rosterLocked ? <><Lock size={13} strokeWidth={2.5} /> {zhEn("已鎖定（點此解鎖）", "Locked (tap to unlock)")}</> : <><Edit size={13} strokeWidth={2.5} /> {zhEn("編輯中（點此鎖定）", "Editing (tap to lock)")}</>}
                  </button>
                ) : (
                  <span style={{fontSize:12,color:"#8A8A9E",fontWeight:700}}>{zhEn("唯讀檢視", "View only")}</span>
                )}
              </div>

              <Card style={{padding:18}}>
                {/* Staff chips strip */}
                {activeStaff.length > 1 && (
                  <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:8,marginBottom:12}}>
                    {activeStaff.map((st2, i) => {
                      const cnt = ((st2.workDays||{})[staffScheduleMonth]||[]).length;
                      const todayCnt = todayApts.filter(a => a.staffId === st2.id).length;
                      const on = i === idx;
                      return (
                        <button key={st2.id} onClick={() => setStaffScheduleIdx(i)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-primary)":"var(--c-lighter)",color:on?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap",fontWeight:800,fontSize:13,transition:"all 0.2s",flexShrink:0}}>
                          <span>{st2.name}</span>
                          <span style={{fontSize:11,opacity:0.9,padding:"1px 7px",borderRadius:99,background:on?"rgba(255,255,255,0.25)":"#fff"}}>{cnt}{zhEn("天", " days")}</span>
                          {todayCnt > 0 && <span style={{fontSize:10,padding:"1px 6px",borderRadius:99,background:on?"#FBBF24":"#FEF3C7",color:on?"#fff":"#92400E"}}>{zhEn("今", "Today")} {todayCnt}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Month nav */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                  <button onClick={()=>{const d=new Date(staffScheduleMonth+"-01");d.setMonth(d.getMonth()-1);setStaffScheduleMonth(localDateStr(d).slice(0,7));}} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={18} color="var(--c-primary)" strokeWidth={3}/></button>
                  <p style={{fontWeight:900,fontSize:17,margin:0,color:"#4A4A68"}}>{fmtYM(staffScheduleMonth)}</p>
                  <button onClick={()=>{const d=new Date(staffScheduleMonth+"-01");d.setMonth(d.getMonth()+1);setStaffScheduleMonth(localDateStr(d).slice(0,7));}} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={18} color="var(--c-primary)" strokeWidth={3}/></button>
                </div>

                {/* (Removed redundant per-staff header — staff chips
                    strip above already shows selection + day counts.) */}

                {/* Quick actions (owner only or self) */}
                {canEdit && (
                  <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                    <button disabled={!canEdit} onClick={fillAll} style={{background:"var(--c-light)",border:"none",borderRadius:99,padding:"6px 14px",fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor: canEdit ? "pointer" : "not-allowed",opacity: canEdit ? 1 : 0.4}}>{zhEn("全選本月", "Select the whole month")}</button>
                    <button disabled={!canEdit} onClick={copyWeekdays} style={{background:"#EFF6FF",border:"none",borderRadius:99,padding:"6px 14px",fontSize:12,fontWeight:800,color:"#2563EB",cursor: canEdit ? "pointer" : "not-allowed",opacity: canEdit ? 1 : 0.4}}>{zhEn("只選週一至五", "Weekdays only")}</button>
                    <button disabled={!canEdit} onClick={clearAll} style={{background:"#FEF2F2",border:"none",borderRadius:99,padding:"6px 14px",fontSize:12,fontWeight:800,color:"#DC2626",cursor: canEdit ? "pointer" : "not-allowed",opacity: canEdit ? 1 : 0.4}}>{zhEn("清空", "Clear")}</button>
                  </div>
                )}

                {/* Day labels */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
                  {DAYS.map((d,i)=><div key={d} style={{textAlign:"center",fontSize:11,color:i===0||i===6?"#EF4444":"#AFAFC4",fontWeight:800}}>{d}</div>)}
                </div>
                {/* Grid */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
                  {Array(new Date(smY,smM-1,1).getDay()).fill(null).map((_,i)=><div key={"e"+i}/>)}
                  {Array.from({length:smDays},(_,i)=>{
                    const day=i+1, ds=staffScheduleMonth+"-"+String(day).padStart(2,"0");
                    const isWork=daysArr.includes(ds);
                    const isToday=ds===dateNow();
                    const beforeJoin = employmentJoin && ds < employmentJoin;
                    const afterLeave = employmentLeave && ds > employmentLeave;
                    const outOfRange = beforeJoin || afterLeave;
                    const dayApts = state.appointments.filter(a => a.staffId === s.id && a.date === ds && a.status !== "cancelled" && a.status !== "noshow").length;
                    const ph = getHolidayForDate(tenant?.country || "HK", ds);
                    return (
                      <button key={day} onClick={()=>toggleDay(day)} disabled={!canEdit || outOfRange} title={outOfRange ? (beforeJoin ? zhEn("入職前 ", "Before joining ") + employmentJoin : zhEn("離職後 ", "After leaving ") + employmentLeave) : (ph ? ph.name : "")} style={{aspectRatio:"1",borderRadius:10,border:isToday?"2px solid var(--c-grad1)":ph&&!isWork&&!outOfRange?"2px solid #C4B5FD":"none",background:outOfRange?"#F3F4F6":(isWork?"var(--c-primary)":ph?"#F5F3FF":"var(--c-lighter)"),color:outOfRange?"#D1D5DB":(isWork?"#fff":ph?"#6B21A8":"#8A8A9E"),fontSize:14,fontWeight:outOfRange?500:(isWork||isToday||ph?900:600),cursor:(canEdit && !outOfRange)?"pointer":"not-allowed",opacity:outOfRange?0.55:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative",transition:"all 0.15s",padding:"2px",overflow:"hidden"}}>
                        <span style={{lineHeight:1,textDecoration:outOfRange?"line-through":"none"}}>{day}</span>
                        {outOfRange && <span style={{fontSize:7,fontWeight:800,lineHeight:1,marginTop:2,color:"#9CA3AF"}}>{beforeJoin ? zhEn("未入職", "Not yet joined") : zhEn("已離職", "Left")}</span>}
                        {!outOfRange && ph && <span style={{fontSize:8,fontWeight:800,lineHeight:1,marginTop:2,maxWidth:"100%",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:isWork?"rgba(255,255,255,0.95)":"#6B21A8"}}>{ph.name.length > 3 ? ph.name.slice(0,2)+"…" : ph.name}</span>}
                        {!outOfRange && dayApts > 0 && !ph && <span style={{position:"absolute",bottom:2,fontSize:9,fontWeight:800,padding:"0 4px",borderRadius:99,background:isWork?"rgba(255,255,255,0.3)":"var(--c-primary)",color:isWork?"#fff":"#fff"}}>{dayApts}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Employment dates pill — surface the join/leave range so
                    the boss knows why some days are greyed out. */}
                {(employmentJoin || employmentLeave) && (
                  <div style={{marginTop:12,padding:"8px 12px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,fontWeight:900,color:"#92400E"}}>{zhEn("📅 在職期間：", "📅 Employed: ")}</span>
                    <span style={{fontSize:12,fontWeight:800,color:"#78350F"}}>
                      {employmentJoin ? zhEn("入職 ", "Joined ") + employmentJoin : "—"}
                      {employmentJoin && employmentLeave ? " · " : ""}
                      {employmentLeave ? zhEn("離職 ", "Left ") + employmentLeave : (employmentJoin ? zhEn(" · 至今", " · to date") : "")}
                    </span>
                  </div>
                )}

                {/* Legend */}
                <div style={{display:"flex",gap:14,justifyContent:"center",marginTop:14,fontSize:11,color:"#8A8A9E",fontWeight:700,flexWrap:"wrap"}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,background:"var(--c-primary)",borderRadius:4,display:"inline-block"}}/>{zhEn("上班日", "Working day")}</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,background:"var(--c-lighter)",borderRadius:4,display:"inline-block"}}/>{zhEn("休息", "Day off")}</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,background:"#F3F4F6",borderRadius:4,display:"inline-block"}}/>{zhEn("不適用", "N/A")}</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,border:"2px solid var(--c-grad1)",borderRadius:4,display:"inline-block"}}/>{zhEn("今天", "Today")}</span>
                </div>
              </Card>
            </div>
          );
        })()}

        {/* ── STAFF MANAGEMENT TAB (profile / services / hours / time-off) ─── */}
        {tab === "staff" && user.role === "owner" && <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("員工管理 👥", "Staff 👥")} <HelpTip topic="staffOrder" /></h2>
            <Btn size="sm" onClick={() => {
              if (staffSeats && staffSeats.max > 0 && staffSeats.used >= staffSeats.max) {
                toast(zhEn("已達方案員工上限（" + staffSeats.max + " 位，包括店主）。升級方案即可新增員工。", "Your plan allows " + staffSeats.max + " staff (including the owner). Upgrade to add more."), "warning");
                openPlanPage();
                return;
              }
              setEditStaff({});
            }}><Plus size={16} strokeWidth={3} /> {i18nT("staff.add")}</Btn>
          </div>
          {staffSeats && staffSeats.max > 0 && (() => {
            const over = staffSeats.used > staffSeats.max;
            return (
              <div style={{background: over ? "#FEF2F2" : "var(--c-lighter)", border: "1.5px solid " + (over ? "#FECACA" : "var(--c-border)"), borderRadius: 14, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10}}>
                <div style={{flex: 1, minWidth: 0}}>
                  <p style={{margin: 0, fontSize: 13, fontWeight: 900, color: over ? "#B91C1C" : "#4A4A68"}}>{zhEn("方案員工名額 ", "Plan staff seats ")}{staffSeats.used} / {staffSeats.max}</p>
                  <p style={{margin: "2px 0 0", fontSize: 11.5, fontWeight: 700, color: "#8A8A9E", lineHeight: 1.5}}>{over
                    ? zhEn("超出名額嘅員工（🔒）保留喺日曆，舊預約照做，但唔可以接新預約。升級方案，或停用其他員工就會解鎖。", "Staff over the limit (🔒) stay on the calendar and keep their existing bookings, but can't take new ones. Upgrade, or disable someone, to unlock them.")
                    : zhEn("包括店主。停用嘅員工唔計名額。", "Includes the owner. Disabled staff don't use a seat.")}</p>
                </div>
                {over && <Btn size="sm" onClick={openPlanPage}>{zhEn("升級", "Upgrade")}</Btn>}
              </div>
            );
          })()}
          {allStaff.length === 0 && <EmptyState icon={Users} title={zhEn("尚未新增員工", "No staff yet")} sub={zhEn("點右上新增第一位美容師", "Tap the button at the top right to add your first groomer")} />}
          {allStaff.map(s => {
            const isEnabled = s.enabled !== false;
            const DAYS_ZH = [zhEn("日", "Sun"),zhEn("一", "Mon"),zhEn("二", "Tue"),zhEn("三", "Wed"),zhEn("四", "Thu"),zhEn("五", "Fri"),zhEn("六", "Sat")];
            const svcIds = Array.isArray(s.canServiceIds) ? s.canServiceIds : [];
            const svcNames = svcIds.length === 0 ? zhEn("所有服務", "All services") : svcIds.map(id => (tenant?.services||[]).find(sv => sv.id===id)?.name).filter(Boolean).join("、");
            const enabledDays = [0,1,2,3,4,5,6].filter(d => ((s.staffHours||{})[d] || (s.staffHours||{})[String(d)])?.enabled);
            const daysLabel = enabledDays.length ? enabledDays.map(d => DAYS_ZH[d]).join(" ") : zhEn("依店鋪營業時間", "Follows shop hours");
            const offToday = staffOffOn(s, dateNow());
            const upcomingOff = (s.timeOff||[]).filter(p => p && p.to && p.to >= dateNow()).length;
            const row = (icon, label, value, color) => (
              <div style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderTop:"1px solid #F0F0F5"}}>
                <span style={{fontSize:13,width:18,flexShrink:0}}>{icon}</span>
                <span style={{fontSize:12,fontWeight:700,color:"#9A9AAE",width:64,flexShrink:0}}>{label}</span>
                <span style={{fontSize:13,fontWeight:700,color:color||"#4A4A68",flex:1,minWidth:0}}>{value}</span>
              </div>
            );
            return <Card key={s.id} style={{padding:16,opacity:isEnabled?1:0.55,border:isEnabled?"none":"1.5px dashed #E5E5EE"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                <div style={{width:46,height:46,background:isEnabled?"linear-gradient(135deg,var(--c-light),var(--c-grad2))":"#F3F3F8",borderRadius:15,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:isEnabled?"var(--c-primary)":"#AFAFC4",fontSize:19,flexShrink:0}}>{(s.name||"?")[0]}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap"}}>
                    <p style={{fontWeight:800,fontSize:16,margin:0,color:isEnabled?"#1D1D2E":"#AFAFC4"}}>{s.name}</p>
                    <Badge color={s.jobType==="fulltime"?"#1D4ED8":"#92400E"} bg={s.jobType==="fulltime"?"#EFF6FF":"#FFFBEB"}>{s.jobType==="fulltime"?zhEn("全職", "Full-time"):zhEn("兼職", "Part-time")}</Badge>
                    {!isEnabled && <Badge color="#EF4444" bg="#FEF2F2">{zhEn("停用", "Off")}</Badge>}
                    {isEnabled && seatLocked(staffSeats, s.id) && <Badge color="#fff" bg="#4B5563">🔒 {zhEn("超出名額", "Over limit")}</Badge>}
                    {offToday && <Badge color="#EA580C" bg="#FFF7ED">{zhEn("休假中", "On leave")}</Badge>}
                  </div>
                  <p style={{fontSize:12,color:"#9A9AAE",margin:0,fontWeight:600}}>{(s.phone||"—") + zhEn(" · 入職 ", " · joined ") + (s.joinDate||"—")}</p>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
                  <Btn size="sm" variant="secondary" onClick={() => setEditStaff(s)}><Edit size={15} strokeWidth={2.5} /> {zhEn("編輯", "Edit ")}</Btn>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      const next = !isEnabled;
                      if (next && staffSeats && staffSeats.max > 0 && staffSeats.used >= staffSeats.max) {
                        toast(zhEn("已達方案員工上限（" + staffSeats.max + " 位）。先停用另一位員工，或升級方案。", "Your plan allows " + staffSeats.max + " staff. Disable someone else first, or upgrade."), "warning");
                        return;
                      }
                      const msg = (next ? i18nT("tx.enableStaffConfirm") : i18nT("tx.disableStaffConfirm")).replace("{name}", s.name);
                      if (!window.confirm(msg)) return;
                      try {
                        await patchProfile(s.id, { enabled: next });
                      } catch (e) {
                        toast(i18nT("tx.updateFailed") + zhEn("：", ": ") + (e?.message || e), "error");
                        return;
                      }
                      setState(st => ({ ...st, users: { ...st.users, [s.id]: { ...st.users[s.id], enabled: next } } }));
                      toast(next ? zhEn("已啟用 ", "Enabled ") + s.name + " ✓" : zhEn("已停用 ", "Disabled ") + s.name + " ✓");
                    }}
                    style={{background:isEnabled?"#FEF2F2":"#ECFDF5",color:isEnabled?"#B91C1C":"#047857",borderColor:"transparent"}}
                  >
                    {isEnabled ? zhEn("停用", "Off") : zhEn("啟用", "On")}
                  </Btn>
                </div>
              </div>
              {row("✂️",zhEn("可提供服務", "Services offered"), svcNames)}
              {row("⏰",zhEn("工作日", "Working days"), daysLabel)}
              {row("🏖️",zhEn("休假", "Leave"), offToday ? zhEn("休假中", "On leave") : (upcomingOff ? (upcomingOff + zhEn(" 段排期", " scheduled period(s)")) : "—"), offToday ? "#EA580C" : undefined)}
              {/* Customer-page visibility toggle. Separate from 啟用/停用 —
                  the owner can still assign a hidden staffer internally;
                  customers just don't see them in the groomer picker. */}
              {isEnabled && (() => {
                const isCustVis = s.customerVisible !== false;
                return (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0 2px",borderTop:"1px solid #F0F0F5"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:1}}>
                      <span style={{fontSize:14,width:18,flexShrink:0}}>👁️</span>
                      <span style={{fontSize:12,fontWeight:700,color:"#9A9AAE",width:64,flexShrink:0}}>{zhEn("客戶可見", "Visible to customers")}</span>
                      <span style={{fontSize:12,color:"#8A8A9E",fontWeight:600,flex:1,minWidth:0}}>
                        {isCustVis ? zhEn("顯示喺客戶預約頁", "Shown on the customer booking page") : zhEn("隱藏 — 只限店家排程指派", "Hidden — shop scheduling only")}
                      </span>
                    </div>
                    <Toggle on={isCustVis} onChange={async v => {
                      try { await patchProfile(s.id, { customerVisible: v }); }
                      catch (e) { toast(i18nT("tx.updateFailed") + zhEn("：", ": ") + (e?.message || e), "error"); return; }
                      setState(st => ({ ...st, users: { ...st.users, [s.id]: { ...st.users[s.id], customerVisible: v } } }));
                      toast(v ? (zhEn("已顯示 ", "Now showing ") + s.name + zhEn(" 於客戶預約頁", " on the customer booking page")) : (zhEn("已從客戶預約頁隱藏 ", "Hidden from the customer booking page: ") + s.name));
                    }} />
                  </div>
                );
              })()}
            </Card>;
          })}
          <BottomSheet open={editStaff !== null} onClose={() => setEditStaff(null)} title={editStaff?.id ? (i18nT("staff.edit") + (editStaff.name ? " — " + editStaff.name : "")) : i18nT("staff.add")}>
            {editStaff !== null && <StaffEditor staff={editStaff?.id ? editStaff : null} state={state} setState={setState} tenantId={user.tenantId} onClose={() => setEditStaff(null)} section="staff" onPaySaved={refreshStaffPay} />}
          </BottomSheet>
        </div>}

        {/* ── SALARY TAB (payroll: cards / table / chart / payslip / payouts) ─── */}
        {tab === "salary" && PAYROLL_ENABLED && user.role === "owner" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>薪金管理 💰 <HelpTip topic="staffOrder" /></h2>

          {/* ── ACTION INBOX — overdue payouts / config conflicts / cycle due ── */}
          {payrollOn && (() => {
            const today = dateNow();
            const todayDate = new Date(today + "T00:00:00");
            // Previous month range
            const prevMonthStart = (() => { const d = new Date(todayDate); d.setDate(1); d.setMonth(d.getMonth() - 1); return localDateStr(d); })();
            const prevMonthEnd = (() => { const d = new Date(todayDate); d.setDate(0); return localDateStr(d); })();
            const monthEnd = (() => { const d = new Date(todayDate); d.setMonth(d.getMonth() + 1); d.setDate(0); return localDateStr(d); })();
            const daysUntilCycleEnd = Math.ceil((new Date(monthEnd + "T00:00:00") - todayDate) / 86400000);

            const inboxItems = [];
            const activeStaff = allStaff.filter(s => s && s.enabled !== false);
            for (const st of activeStaff) {
              // 1. Overdue payout — previous month unpaid, today > prevMonthEnd + 5 days grace
              const graceDay = new Date(prevMonthEnd + "T00:00:00"); graceDay.setDate(graceDay.getDate() + 5);
              const inGrace = todayDate <= graceDay;
              const prevPay = staffPeriodPay(st, allApts, prevMonthStart, prevMonthEnd, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments });
              if (!inGrace && prevPay.total > 0 && !payoutCovering(st, prevMonthStart, prevMonthEnd)) {
                inboxItems.push({
                  severity: "high",
                  icon: "🔴",
                  title: `${st.name} 上月薪金未發放`,
                  detail: `$${prevPay.total.toLocaleString()} · ${prevMonthStart.slice(5)} — ${prevMonthEnd.slice(5)}`,
                  actionLabel: "結算",
                  onAction: () => setPayslipSheet({ staffId: st.id, from: prevMonthStart, to: prevMonthEnd }),
                });
                continue;
              }
              // 2. Cycle due soon (-3 days)
              if (inGrace) {
                // already inside grace of last month — covered above
              } else if (daysUntilCycleEnd <= 3) {
                const thisMonthStart = today.slice(0,7) + "-01";
                const curPay = staffPeriodPay(st, allApts, thisMonthStart, today, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments });
                if (curPay.total > 0 && !payoutCovering(st, thisMonthStart, monthEnd)) {
                  inboxItems.push({
                    severity: "med",
                    icon: "🟠",
                    title: `${st.name} 本月即將結算（${daysUntilCycleEnd} 日內）`,
                    detail: `預估 $${curPay.total.toLocaleString()}`,
                    actionLabel: "預覽",
                    onAction: () => setPayslipSheet({ staffId: st.id, from: thisMonthStart, to: monthEnd }),
                  });
                }
              }
              // (Pre-3) Pending deposits older than 24h — flagged once
              // per active staff so the inbox shows a single combined card
              // per shop instead of N copies per staffer. Done OUTSIDE the
              // per-staff loop later. Skip per-staff iteration here.
              // 3. Salary config conflict
              if (st.commissionEnabled !== false && Number(st.commissionRate || 0) === 0) {
                inboxItems.push({
                  severity: "med",
                  icon: "🟠",
                  title: `${st.name} 佣金設定衝突`,
                  detail: "已啟用佣金但比率為 0%",
                  actionLabel: "修復",
                  onAction: () => setEditStaff(st),
                });
              }
              // 4. Part-time: scheduled-but-unfilled / unpaid past days
              if (st.jobType === "parttime") {
                const hasStaffSched = st.staffHours && Object.values(st.staffHours).some(d => d && d.enabled);
                const startScan = (() => {
                  const d = new Date(todayDate); d.setDate(d.getDate() - 30);
                  return localDateStr(d);
                })();
                const stWageMap = {};
                for (const w of (st.wageLog || [])) { if (w && w.date) stWageMap[w.date] = w; }
                const stPayouts = (st.payoutLog || []);
                const leaveDates = new Set();
                for (const e of state.appointments || []) {
                  if (e.kind === "event" && e.staffId === st.id && e.leaveType && e.tenantId === user.tenantId) {
                    leaveDates.add(e.date);
                  }
                }
                let unfilledDays = 0, unpaidDays = 0;
                const cursor = new Date(startScan + "T00:00:00");
                while (localDateStr(cursor) < today) {
                  const ds = localDateStr(cursor);
                  cursor.setDate(cursor.getDate() + 1);
                  if (st.joinDate && ds < st.joinDate) continue;
                  if (st.leaveDate && ds > st.leaveDate) continue;
                  // Part-timers without an explicit schedule shouldn't be
                  // nagged about every shop-open day.
                  if (!hasStaffSched) continue;
                  const dow = new Date(ds + "T00:00:00").getDay();
                  const scheduled = !!(st.staffHours[dow]?.enabled || st.staffHours[String(dow)]?.enabled);
                  if (!scheduled || staffOffOn(st, ds) || leaveDates.has(ds)) continue;
                  const r = stWageMap[ds];
                  if (!r) { unfilledDays++; continue; }
                  const paidDaily = !!r.paidMethod;
                  const paidMonthly = stPayouts.some(p => p && p.from && p.to && p.from <= ds && p.to >= ds);
                  if (!paidDaily && !paidMonthly) unpaidDays++;
                }
                if (unfilledDays > 0) {
                  inboxItems.push({
                    severity: "med",
                    icon: "📝",
                    title: `${st.name} 過去 30 日有 ${unfilledDays} 個排班日未填工時`,
                    detail: "排班咗但無 wageLog 紀錄",
                    actionLabel: "補填",
                    onAction: () => setWageHistoryStaff({ staff: st, ym: today.slice(0,7) }),
                  });
                }
                if (unpaidDays > 0) {
                  inboxItems.push({
                    severity: "med",
                    icon: "💰",
                    title: `${st.name} ${unpaidDays} 個工作日未出薪`,
                    detail: "已填工時但無出薪方式 / 月結覆蓋",
                    actionLabel: "出薪",
                    onAction: () => setWageHistoryStaff({ staff: st, ym: today.slice(0,7) }),
                  });
                }
              }
            }

            // 5. Deposits pending owner confirmation > 24h. Customer
            // already tapped "我已付款" (depositClaimedAt) but the owner
            // hasn't verified the receipt. Surface so they can either
            // confirm or refund instead of leaving the slot in limbo.
            const now = Date.now();
            const pendingDeposits = allApts.filter(a => {
              if (!a || a.kind === "event") return false;
              if (!(Number(a.deposit) > 0)) return false;
              if (a.depositConfirmedAt) return false;
              if (a.status === "cancelled" || a.status === "noshow") return false;
              if (!a.depositClaimedAt) return false;
              const claimedAt = new Date(a.depositClaimedAt).getTime();
              return !isNaN(claimedAt) && (now - claimedAt) > 24 * 60 * 60 * 1000;
            });
            if (pendingDeposits.length > 0) {
              const apt = pendingDeposits[0];
              const cust = state.users[apt.customerId];
              inboxItems.push({
                severity: "high",
                icon: "💸",
                title: pendingDeposits.length === 1
                  ? zhEn(`${cust?.name || "客戶"} 訂金未確認超過 24 小時`, `${cust?.name || "客戶"}'s deposit has been unconfirmed for over 24 hours`)
                  : zhEn(`有 ${pendingDeposits.length} 筆訂金未確認（>24h）`, `${pendingDeposits.length} deposit(s) unconfirmed (>24h)`),
                detail: zhEn(`${apt.date} ${apt.time} · 訂金 $${Number(apt.deposit).toLocaleString()}`, `${apt.date} ${apt.time} · deposit $${Number(apt.deposit).toLocaleString()}`),
                actionLabel: zhEn("確認", "Confirm "),
                onAction: () => setEditApt(apt),
              });
            }

            // 6. 套票即將過期 — within 14 days AND remaining visits > 0.
            // Aggregated into a single card so the inbox doesn't drown
            // when 20 customers' packages all expire in the same window.
            const pkgCutoffMs = now + 14 * 24 * 60 * 60 * 1000;
            const expiringPkgs = (tenantCustomerPackages || []).filter(cp => {
              if (!cp || !cp.expiresAt) return false;
              if (!(Number(cp.remainingVisits) > 0)) return false;
              if (cp.refundedAt) return false;
              const t = new Date(cp.expiresAt).getTime();
              return !isNaN(t) && t >= now && t <= pkgCutoffMs;
            });
            if (expiringPkgs.length > 0) {
              const soonest = expiringPkgs.sort((a, b) => (a.expiresAt || "").localeCompare(b.expiresAt || ""))[0];
              const soonestCustName = state.users[soonest.userId]?.name || zhEn("客戶", "Customer");
              inboxItems.push({
                severity: "med",
                icon: "📦",
                title: expiringPkgs.length === 1
                  ? zhEn(`${soonestCustName} 套票 14 日內過期`, `${soonestCustName}'s package expires within 14 days`)
                  : zhEn(`${expiringPkgs.length} 張套票 14 日內過期`, `${expiringPkgs.length} packages expire within 14 days`),
                detail: zhEn(`${(soonest.expiresAt || "").slice(0, 10)} · 剩 ${soonest.remainingVisits} 次（首張）`, `${(soonest.expiresAt || "").slice(0, 10)} · ${soonest.remainingVisits} left (first one)`),
                actionLabel: zhEn("查看", "View"),
                onAction: () => { setSettingsSubtab("packages-admin"); setTab("settings"); },
              });
            }

            // 7. 低庫存 — qty ≤ lowAlert AND lowAlert > 0 (item has alert
            // configured). Single inbox card showing the worst-hit item.
            const lowStockItems = (tenant?.inventory || []).filter(i => i && Number(i.lowAlert) > 0 && Number(i.qty) <= Number(i.lowAlert));
            if (lowStockItems.length > 0) {
              const worst = lowStockItems.sort((a, b) => (Number(a.qty) - Number(a.lowAlert)) - (Number(b.qty) - Number(b.lowAlert)))[0];
              inboxItems.push({
                severity: "med",
                icon: "📦",
                title: lowStockItems.length === 1
                  ? zhEn(`${worst.name} 庫存偏低`, `${worst.name} is low on stock`)
                  : zhEn(`${lowStockItems.length} 項庫存偏低`, `${lowStockItems.length} items are low on stock`),
                detail: zhEn(`${worst.name}：剩 ${worst.qty} ${worst.unit || ""}（警戒 ${worst.lowAlert}）`, `${worst.name}: ${worst.qty} ${worst.unit || ""} left (alert ${worst.lowAlert})`),
                actionLabel: zhEn("查看", "View"),
                onAction: () => { setSettingsSubtab("inventory"); setTab("settings"); },
              });
            }

            // 8. 流失客戶 — customer has ≥3 lifetime done bookings but
            // hasn't booked in the last 90 days. Surfaces only ONE card
            // (count) so it nudges without spamming.
            const ninetyDaysAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 90); return localDateStr(d); })();
            const churnCust = (() => {
              const byCust = new Map();
              for (const a of allApts) {
                if (!a.customerId || a.status !== "done") continue;
                const cur = byCust.get(a.customerId) || { count: 0, lastDate: "" };
                cur.count++;
                if (!cur.lastDate || a.date > cur.lastDate) cur.lastDate = a.date;
                byCust.set(a.customerId, cur);
              }
              const out = [];
              for (const [cid, info] of byCust) {
                if (info.count >= 3 && info.lastDate && info.lastDate < ninetyDaysAgo) {
                  out.push({ cid, ...info });
                }
              }
              return out;
            })();
            if (churnCust.length > 0) {
              const sample = churnCust.slice().sort((a, b) => a.lastDate.localeCompare(b.lastDate))[0];
              const sampleName = state.users[sample.cid]?.name || zhEn("客戶", "Customer");
              inboxItems.push({
                severity: "low",
                icon: "👋",
                title: zhEn(`${churnCust.length} 位流失客戶（${sample.count >= 5 ? "高價值" : "活躍"}），90+ 日未預約`, `${churnCust.length} lapsed customers (${sample.count >= 5 ? "high-value" : "active"}) — no booking in 90+ days`),
                detail: zhEn(`最久：${sampleName} 上次 ${sample.lastDate}`, `Longest: ${sampleName}, last visit ${sample.lastDate}`),
                actionLabel: zhEn("查看", "View"),
                onAction: () => setTab("customers"),
              });
            }

            // 9. Stripe subscription — flag past_due / canceled hard, and
            // 7-day expiry soft. Without payment the shop loses access
            // to paid features at period end.
            const sub = tenant?.subscription;
            if (sub && typeof sub === "object") {
              const cpe = sub.current_period_end;
              const cpeMs = (() => {
                if (!cpe) return null;
                if (typeof cpe === "number") return cpe < 1e12 ? cpe * 1000 : cpe;
                const t = Date.parse(cpe);
                return isNaN(t) ? null : t;
              })();
              if (sub.status === "past_due" || sub.status === "unpaid") {
                inboxItems.push({
                  severity: "high",
                  icon: "💳",
                  title: zhEn("訂閱付款逾期 — 立即處理", "Subscription payment overdue — act now"),
                  detail: zhEn(`狀態：${sub.status === "past_due" ? "past_due（信用卡扣款失敗）" : "unpaid（多次扣款失敗）"}`, `Status: ${sub.status === "past_due" ? "past_due (card payment failed)" : "unpaid (repeated payment failures)"}`),
                  actionLabel: zhEn("去 Stripe", "Go to Stripe"),
                  onAction: () => { setSettingsSubtab("payment"); setTab("settings"); },
                });
              } else if (sub.status === "active" && sub.cancel_at_period_end && cpeMs) {
                const daysLeft = Math.ceil((cpeMs - now) / 86400000);
                if (daysLeft >= 0 && daysLeft <= 14) {
                  inboxItems.push({
                    severity: "med",
                    icon: "💳",
                    title: zhEn(`訂閱將於 ${daysLeft} 日後到期`, `Subscription expires in ${daysLeft} days`),
                    detail: zhEn("已設定到期不續訂 — 確認意願或取消", "Set not to renew — confirm or cancel"),
                    actionLabel: zhEn("管理", "Manage"),
                    onAction: () => { setSettingsSubtab("payment"); setTab("settings"); },
                  });
                }
              } else if (sub.status === "trialing" && cpeMs) {
                const daysLeft = Math.ceil((cpeMs - now) / 86400000);
                if (daysLeft >= 0 && daysLeft <= 3) {
                  inboxItems.push({
                    severity: "med",
                    icon: "💳",
                    title: zhEn(`試用 ${daysLeft} 日後結束`, `Trial ends in ${daysLeft} days`),
                    detail: zhEn("綁卡先續用，否則自動降級到 free plan", "Add a card to continue, otherwise it falls back to the free plan"),
                    actionLabel: zhEn("管理", "Manage"),
                    onAction: () => { setSettingsSubtab("payment"); setTab("settings"); },
                  });
                }
              }
            }

            // 10. Pending-payment bookings > 48h — customer reserved a
            // slot but never tapped "I've paid" within the 30-min hold
            // window. Even after the hold expires the row remains as
            // `pending_payment`, blocking the slot visually. Surface so
            // the owner can cancel + free the time.
            const fortyEightHrsAgo = now - 48 * 60 * 60 * 1000;
            const staleHolds = allApts.filter(a => {
              if (!a || a.kind === "event") return false;
              if (a.status !== "pending_payment") return false;
              const created = a.createdAt ? Date.parse(a.createdAt) : 0;
              return created > 0 && created < fortyEightHrsAgo;
            });
            if (staleHolds.length > 0) {
              const sampleHold = staleHolds[0];
              const holdCust = state.users[sampleHold.customerId];
              inboxItems.push({
                severity: "med",
                icon: "⏸️",
                title: staleHolds.length === 1
                  ? `${holdCust?.name || "客戶"} 預約待付款超過 48 小時`
                  : `${staleHolds.length} 個未繳訂金預約 (>48h)`,
                detail: `${sampleHold.date} ${sampleHold.time} · 訂金 $${Number(sampleHold.deposit || 0).toLocaleString()}`,
                actionLabel: "處理",
                onAction: () => setEditApt(sampleHold),
              });
            }

            // 11. Over-worked staff — defines as "7 consecutive scheduled
            // days with no day off". Helps the owner spot burnout. Only
            // counted for staff with a personal schedule.
            const overworkedStaff = [];
            for (const st of activeStaff) {
              const hasStaffSched = st.staffHours && Object.values(st.staffHours).some(d => d && d.enabled);
              if (!hasStaffSched) continue;
              let consec = 0;
              const cursor2 = new Date(today + "T00:00:00");
              cursor2.setDate(cursor2.getDate() - 14);
              while (localDateStr(cursor2) < today) {
                const ds = localDateStr(cursor2);
                cursor2.setDate(cursor2.getDate() + 1);
                if (st.joinDate && ds < st.joinDate) { consec = 0; continue; }
                if (st.leaveDate && ds > st.leaveDate) { consec = 0; continue; }
                const dow = new Date(ds + "T00:00:00").getDay();
                const scheduled = !!(st.staffHours[dow]?.enabled || st.staffHours[String(dow)]?.enabled);
                const offDay = staffOffOn(st, ds) || staffLeaveOn(state.appointments, st.id, ds);
                if (scheduled && !offDay) consec++;
                else consec = 0;
                if (consec >= 7) { overworkedStaff.push({ name: st.name, days: consec }); break; }
              }
            }
            if (overworkedStaff.length > 0) {
              inboxItems.push({
                severity: "med",
                icon: "🥵",
                title: overworkedStaff.length === 1
                  ? `${overworkedStaff[0].name} 連續工作 ${overworkedStaff[0].days} 日無休`
                  : `${overworkedStaff.length} 位員工連續無休 ≥7 日`,
                detail: "建議安排休假以免過勞",
                actionLabel: "排班",
                onAction: () => setTab("roster"),
              });
            }

            // 12. Orphan customer_packages — service the package was
            // bound to has been deleted from the catalog (or is
            // disabled). Existing remaining visits can't be redeemed
            // until the owner re-binds or refunds.
            const allSvcIds = new Set((tenant?.services || []).filter(sv => sv && sv.id).map(sv => sv.id));
            const orphanPkgs = (tenantCustomerPackages || []).filter(cp => {
              if (!cp || cp.refundedAt) return false;
              if (!(Number(cp.remainingVisits) > 0)) return false;
              const boundIds = Array.isArray(cp.serviceIds) ? cp.serviceIds : (cp.serviceId ? [cp.serviceId] : []);
              if (boundIds.length === 0) return false;
              return boundIds.every(id => !allSvcIds.has(id));
            });
            if (orphanPkgs.length > 0) {
              const sampleOrphan = orphanPkgs[0];
              const orphanCustName = state.users[sampleOrphan.userId]?.name || "客戶";
              inboxItems.push({
                severity: "high",
                icon: "🔗",
                title: orphanPkgs.length === 1
                  ? `${orphanCustName} 套票綁定嘅服務已刪除`
                  : `${orphanPkgs.length} 張套票綁定嘅服務唔再存在`,
                detail: `首張：${orphanCustName} · 剩 ${sampleOrphan.remainingVisits} 次無法兌換`,
                actionLabel: "查看",
                onAction: () => { setSettingsSubtab("packages-admin"); setTab("settings"); },
              });
            }

            if (inboxItems.length === 0) {
              return (
                <Card style={{padding:"16px 20px",background:"linear-gradient(135deg,#ECFDF5 0%,#D1FAE5 100%)",border:"2px solid #A7F3D0",display:"flex",alignItems:"center",gap:14}}>
                  <span style={{fontSize:28}}>✅</span>
                  <div style={{flex:1}}>
                    <p style={{fontSize:14,fontWeight:900,color:"#065F46",margin:0}}>所有員工已結算 — 你做得好！</p>
                    <p style={{fontSize:11,color:"#047857",margin:"3px 0 0",fontWeight:700}}>本月暫無需要處理嘅薪金事項</p>
                  </div>
                </Card>
              );
            }
            return (
              <Card style={{padding:0,overflow:"hidden",border:"2px solid #FED7AA"}}>
                <div style={{padding:"12px 16px",background:"linear-gradient(135deg,#FFF7ED 0%,#FFEDD5 100%)",borderBottom:"1px solid #FED7AA",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>⏰</span>
                  <p style={{fontSize:14,fontWeight:900,color:"#9A3412",margin:0}}>需要處理 ({inboxItems.length})</p>
                </div>
                {inboxItems.map((it, i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderTop:i===0?"none":"1px solid #FED7AA",background:"#FFFBF5"}}>
                    <span style={{fontSize:16,flexShrink:0}}>{it.icon}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#1F2937",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.title}</p>
                      <p style={{fontSize:11,color:"#6B7280",margin:"2px 0 0",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.detail}</p>
                    </div>
                    <Btn size="sm" onClick={it.onAction} style={{background:it.severity==="high"?"#DC2626":"#F59E0B",color:"#fff",flexShrink:0,borderColor:"transparent"}}>{it.actionLabel} →</Btn>
                  </div>
                ))}
              </Card>
            );
          })()}

          <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:2}}>
            {/* 「紀錄今日薪金」入口統一移到每位兼職員工嘅卡片上 —
                之前同時喺頂部 + 卡片 + 月曆三個位置，owner 反映入口太多
                難搵到自己需要嘅員工。月結單 button 保留作快速 PDF
                出單入口。 */}
            {payrollOn && <Btn size="sm" variant="secondary" onClick={() => allStaff.length && setPayslipSheet({ staffId: allStaff[0].id, from: staffDateFrom, to: staffDateTo })} style={{flexShrink:0}}><FileText size={16} strokeWidth={2.5} /> 月結單</Btn>}
          </div>

          {/* Pay period — ONE month stepper (‹ 2026年9月 ›). It replaced three
              overlapping pickers (本月/上月/自訂 buttons, a 12-month chip row
              and a date-range row). A custom range stays behind 自訂. */}
          {(() => {
            const today = dateNow();
            const anchor = new Date((staffDateFrom || today).slice(0, 7) + "-01T00:00:00");
            const monthRange = (d) => {
              const y = d.getFullYear(), mo = d.getMonth();
              const ym = y + "-" + String(mo + 1).padStart(2, "0");
              const last = String(new Date(y, mo + 1, 0).getDate()).padStart(2, "0");
              return { key: "m:" + ym, from: ym + "-01", to: ym === today.slice(0, 7) ? today : ym + "-" + last, label: `${y}年${mo + 1}月` };
            };
            const cur = monthRange(anchor);
            const custom = staffDateRange === "custom" || staffDateFrom !== cur.from || staffDateTo !== cur.to;
            const go = (n) => { const r = monthRange(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1)); setStaffDateRange(r.key); setStaffDateFrom(r.from); setStaffDateTo(r.to); };
            const nextIsFuture = monthRange(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)).from > today;
            const stepBtn = (disabled) => ({ width:40, height:40, borderRadius:12, border:"1px solid #E5E7EB", background:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor: disabled ? "default" : "pointer", flexShrink:0 });
            const dateBox = { flex:1, minWidth:0, border:"1px solid #E5E7EB", borderRadius:12, padding:"9px 8px", fontSize:13, fontWeight:800, outline:"none", color:"#374151", background:"#fff", textAlign:"center" };
            return (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <button title="上一個月" onClick={() => go(-1)} style={stepBtn(false)}><ChevronLeft size={18} color="#374151" strokeWidth={3}/></button>
                  <div style={{flex:1,minWidth:0,textAlign:"center",fontSize:16,fontWeight:900,color:"#111827"}}>{custom ? `${staffDateFrom} ～ ${staffDateTo}` : cur.label}</div>
                  <button title="下一個月" disabled={nextIsFuture} onClick={() => !nextIsFuture && go(1)} style={stepBtn(nextIsFuture)}><ChevronRight size={18} color={nextIsFuture ? "#D1D5DB" : "#374151"} strokeWidth={3}/></button>
                  <button onClick={() => custom ? go(0) : setStaffDateRange("custom")} style={{height:40,padding:"0 14px",borderRadius:12,border:"1px solid #E5E7EB",background: custom ? "#111827" : "#fff",color: custom ? "#fff" : "#374151",fontSize:12,fontWeight:800,cursor:"pointer",flexShrink:0}}>{custom ? "按月" : "自訂"}</button>
                </div>
                {custom && (
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="date" value={staffDateFrom} onChange={e=>{if(e.target.value){setStaffDateFrom(e.target.value);setStaffDateRange("custom");}}} style={dateBox} />
                    <span style={{color:"#9CA3AF",fontWeight:800,flexShrink:0}}>—</span>
                    <input type="date" value={staffDateTo} onChange={e=>{if(e.target.value){setStaffDateTo(e.target.value);setStaffDateRange("custom");}}} style={dateBox} />
                  </div>
                )}
              </div>
            );
          })()}

          {/* Wage management is a paid (pro) feature — upgrade nudge */}
          {!payrollOn && (
            <Card style={{padding:16,background:"linear-gradient(135deg,#FFFBEB,#FEF3C7)",border:"2px solid #FDE68A",display:"flex",alignItems:"center",gap:12}}>
              <Crown size={22} color="#D97706" strokeWidth={2.5} style={{flexShrink:0}} />
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:14,fontWeight:900,color:"#92400E",margin:0}}>薪金管理為專業版功能</p>
                <p style={{fontSize:12,color:"#B45309",margin:"2px 0 0",fontWeight:700}}>升級即可記薪金、抽成、出薪、月結單同薪金圖表。</p>
              </div>
            </Card>
          )}
          {/* Overview card — 3 payroll numbers + progress (paid feature).
              Apple-style hero: the boss sees what to do, not analytics. */}
          {payrollOn && (() => {
            const activeStaff = allStaff.filter(s=>s.enabled!==false);
            const totalPayroll = activeStaff.reduce((sum,s) => sum + staffPeriodPay(s, allApts, staffDateFrom, staffDateTo, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments }).total, 0);
            // 已發放 = every live payout (salary + 補發/調整) whose period
            // overlaps the viewed range. Counting only a payout that COVERS the
            // whole range hid partial payments and top-ups (showed $0 paid
            // after a $500 half-month payout).
            const totalPaid = activeStaff.reduce((sum,s) => sum + (s.payoutLog || [])
              .filter(p => p && !p.voidedAt && p.from <= staffDateTo && staffDateFrom <= p.to)
              .reduce((a,p) => a + (Number(p.amount)||0), 0), 0);
            const totalDue = Math.max(0, totalPayroll - totalPaid);
            const paidPct = totalPayroll > 0 ? Math.round((totalPaid / totalPayroll) * 100) : 0;
            const allSettled = totalPayroll > 0 && totalDue === 0;
            return <Card style={{padding:20,background:"linear-gradient(135deg,#FFFDFD 0%,var(--c-lighter) 100%)",border:"1.5px solid rgba(255,122,162,0.12)"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,alignItems:"end"}}>
                <div>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700,letterSpacing:0.3}}>應發</p>
                  <p style={{fontSize:24,fontWeight:900,color:"#1F2937",margin:0,letterSpacing:"-0.5px"}}>{"$"+totalPayroll.toLocaleString()}</p>
                </div>
                <div style={{textAlign:"center",borderLeft:"1px solid #F0F0F5",borderRight:"1px solid #F0F0F5",padding:"0 4px"}}>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700,letterSpacing:0.3}}>已發放</p>
                  <p style={{fontSize:22,fontWeight:900,color:"#10B981",margin:0,letterSpacing:"-0.5px"}}>{"$"+totalPaid.toLocaleString()}</p>
                </div>
                <div style={{textAlign:"right"}}>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700,letterSpacing:0.3}}>待發放</p>
                  <p style={{fontSize:22,fontWeight:900,color:totalDue>0?"#F59E0B":"#9CA3AF",margin:0,letterSpacing:"-0.5px"}}>{"$"+totalDue.toLocaleString()}</p>
                </div>
              </div>
              {/* Progress bar — green progress on light grey track */}
              {totalPayroll > 0 && <div style={{marginTop:16,position:"relative",height:8,background:"#F3F4F6",borderRadius:99,overflow:"hidden"}}>
                <div style={{position:"absolute",left:0,top:0,bottom:0,width:paidPct+"%",background:"linear-gradient(90deg,#10B981 0%,#34D399 100%)",borderRadius:99,transition:"width 0.4s ease"}} />
              </div>}
              <div style={{marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,fontWeight:700}}>
                <span style={{color:"#9CA3AF"}}>{allSettled ? "✅ 全部已發放" : `已發放 ${paidPct}%`}</span>
                <span style={{color:"#6B7280"}}>{activeStaff.length} 位員工 · {staffDateFrom.slice(5)} — {staffDateTo.slice(5)}</span>
              </div>
            </Card>;
          })()}

          {/* Past-12-months payroll chart — tap a bar to jump to that month (paid) */}
          {payrollOn && allStaff.length > 0 && (() => {
            const now = new Date();
            const data = [];
            for (let i=11;i>=0;i--){
              const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
              const y = d.getFullYear(), mo = d.getMonth();
              const ym = y + "-" + String(mo+1).padStart(2,"0");
              const from = ym + "-01";
              const last = new Date(y, mo+1, 0).getDate();
              const to = i===0 ? dateNow() : ym + "-" + String(last).padStart(2,"0");
              const total = allStaff.filter(s=>s.enabled!==false).reduce((sum,s)=>sum+staffPeriodPay(s, allApts, from, to, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments }).total, 0);
              data.push({ ym, mo: mo+1, from, to, total });
            }
            const max = Math.max(...data.map(x=>x.total), 1);
            const peak = data.reduce((a,b)=>b.total>a.total?b:a, data[0]);
            return <Card style={{padding:"18px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
                <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>過去 12 個月薪金</p>
                <p style={{fontSize:11,color:"#9A9AAE",margin:0,fontWeight:700}}>最高 ${peak.total.toLocaleString()}</p>
              </div>
              <div style={{display:"flex",alignItems:"flex-end",gap:3}}>
                {data.map(x => {
                  const sel = staffDateRange === "m:"+x.ym;
                  // No pay that month (or nobody employed yet) → no bar, no number.
                  const hasPay = x.total > 0;
                  const barH = hasPay ? Math.max(6, Math.round((x.total/max)*92)) : 0;
                  return (
                    <button key={x.ym} onClick={()=>{setStaffDateRange("m:"+x.ym);setStaffDateFrom(x.from);setStaffDateTo(x.to);}} title={x.ym+(hasPay?"：$"+x.total.toLocaleString():"：—")} style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",alignItems:"center",gap:4,background:"none",border:"none",cursor:"pointer",padding:0}}>
                      <span style={{fontSize:9,fontWeight:800,color:sel?"var(--c-primary)":"#C4C4D4",whiteSpace:"nowrap"}}>{hasPay ? (x.total>=1000?Math.round(x.total/1000)+"k":x.total) : ""}</span>
                      <div style={{width:"100%",display:"flex",justifyContent:"center",alignItems:"flex-end",height:92}}>
                        {hasPay
                          ? <div style={{width:"66%",borderRadius:"6px 6px 2px 2px",height:barH,background:sel?"linear-gradient(180deg,var(--c-grad1),var(--c-primary))":"#E2E2EE",transition:"height 0.3s"}} />
                          : <div style={{width:"66%",height:2,borderRadius:99,background:"#EFEFF4"}} />}
                      </div>
                      <span style={{fontSize:9.5,fontWeight:700,color:sel?"var(--c-primary)":"#9A9AAE"}}>{x.mo}月</span>
                    </button>
                  );
                })}
              </div>
            </Card>;
          })()}

          {/* Per-staff cards */}
          {allStaff.length === 0 && <EmptyState icon={Users} title="尚未新增員工" sub="點擊上方按鈕新增第一位美容師" />}
          {allStaff.length > 0 && (
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:-2}}>
              {/* View toggle — segmented control */}
              <div style={{display:"inline-flex",background:"var(--c-light)",borderRadius:12,padding:3,gap:2}}>
                {[["card","卡片"],["table","表格"]].map(([v,l])=>(
                  <button key={v} onClick={()=>{setStaffView(v);if(v==="table")setStaffReorder(false);}} style={{padding:"6px 16px",borderRadius:9,fontSize:13,fontWeight:800,border:"none",background:staffView===v?"#fff":"transparent",color:staffView===v?"var(--c-primary)":"#9A9AAE",cursor:"pointer",boxShadow:staffView===v?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{l}</button>
                ))}
              </div>
              {staffView==="card" && allStaff.length > 1 && (
                <button onClick={() => setStaffReorder(v => !v)} style={{background:"none",border:"none",color:staffReorder?"var(--c-primary)":"#9A9AAE",fontWeight:800,fontSize:13,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5,padding:"4px 6px"}}>
                  {staffReorder ? <><Check size={14} strokeWidth={3}/> 完成</> : <><GripVertical size={14} strokeWidth={2.5}/> 排序</>}
                </button>
              )}
            </div>
          )}
          {(() => {
            // Helper to reorder staff. We persist the new order on
            // tenant.staffOrder (uuid[]); dataLoader.attachTenantChildren
            // applies it on next reload, but we ALSO patch tenant.staffIds
            // here so the UI reflects the change instantly without waiting
            // for the realtime round-trip.
            window.__petosMoveStaff = (id, dir) => {
              setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => {
                if (t.id !== user.tenantId) return t;
                const ids = [...(t.staffIds || [])];
                const idx = ids.indexOf(id);
                if (idx < 0) return t;
                const j = idx + dir;
                if (j < 0 || j >= ids.length) return t;
                [ids[idx], ids[j]] = [ids[j], ids[idx]];
                return { ...t, staffIds: ids, staffOrder: ids };
              }) }));
            };
            return null;
          })()}
          {staffView === "card" && allStaff.map((s, idx) => {
            const isEnabled = s.enabled !== false;
            const rangeApts2 = allApts.filter(a => a.staffId === s.id && a.status === "done" && a.date >= staffDateFrom && a.date <= staffDateTo);
            // Shared engine (payroll.js) — same numbers as the payslip.
            const cardPay = staffPeriodPay(s, allApts, staffDateFrom, staffDateTo, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments });
            const orderRev = cardPay.rev;
            const commEnabled = s.commissionEnabled !== false;
            const comm = cardPay.comm;
            // Work days / hours
            // Clock-in/out removed. Owner enters worked hours manually in
            // the payslip sheet. For the 員工 summary card we just show 0.
            // Part-time pay is summed from the daily wage-log (entered via
            // 每日入數) within the selected date range. `pay` is the source of
            // truth (may be a day-wage override, not just hours × rate).
            const wl = sumWageLog(s, staffDateFrom, staffDateTo);
            const workDays = wl.days;
            const workHours = wl.hours;
            const hPay = cardPay.hPay;
            const basePay = cardPay.basePay;
            const totalPay = cardPay.total;
            const reviews = state.reviews.filter(r => r.staffId === s.id);
            const avg = reviews.length ? (reviews.reduce((a,r)=>a+r.rating,0)/reviews.length).toFixed(1) : null;

            // Stat cells — monochrome numbers, muted labels. 上班日 is dropped
            // for full-timers (always 0, it read as broken). Part-time 工時
            // shows an em-dash until the owner records a day.
            // Free/basic plans see NO pay-derived figures (收費 / 抽成).
            const stats = (s.jobType === "fulltime"
              ? [["訂單", rangeApts2.length], ["收費", "$"+orderRev.toLocaleString()], ["抽成", commEnabled ? "$"+comm.toLocaleString() : "—"]]
              : [["訂單", rangeApts2.length], ["收費", "$"+orderRev.toLocaleString()], ["工時", (workHours>0||workDays>0) ? workHours+"h" : "—"], ["抽成", commEnabled ? "$"+comm.toLocaleString() : "—"]]
            ).filter(([l]) => payrollOn || (l !== "收費" && l !== "抽成"));
            // One quiet caption line instead of the loud commission box.
            const payParts = [];
            if (s.jobType === "fulltime") payParts.push("底薪 $"+basePay.toLocaleString());
            else payParts.push("兼職工資 $"+hPay.toLocaleString()+((workHours>0||workDays>0)?"（"+workHours+"h）":""));
            if (commEnabled || comm > 0) payParts.push("抽成 $"+comm.toLocaleString()+(s.commissionMode==="percent"?"（"+s.commissionRate+"%）":""));
            // The total is the engine's net, so the caption must show every
            // part that went into it or it won't add up.
            if (cardPay.tips > 0) payParts.push("小費 $"+cardPay.tips.toLocaleString());
            if (cardPay.bonus > 0) payParts.push("獎金 $"+cardPay.bonus.toLocaleString());
            let payCaption = payParts.join("  +  ");
            if (cardPay.deductions > 0) payCaption += "  −  扣減 $"+cardPay.deductions.toLocaleString();
            const paid = payoutCovering(s, staffDateFrom, staffDateTo);

            // Soft, rounded name band — tinted by job type (Apple-style
            // inset fill). Kept low-saturation so the card still reads calm.
            const nameBandBg = !isEnabled
              ? "#F4F4F7"
              : (s.jobType === "fulltime"
                  ? "linear-gradient(135deg,#EEF3FF 0%,#F6F9FF 100%)"
                  : "linear-gradient(135deg,#FFF6E8 0%,#FFFBF4 100%)");
            return <Card key={s.id} style={{padding:14,opacity:isEnabled?1:0.55,border:isEnabled?"none":"1.5px dashed #E5E5EE"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,background:nameBandBg,border:"1px solid rgba(120,120,160,0.08)",borderRadius:20,padding:"12px 14px"}}>
                {staffReorder && (
                  <div style={{display:"flex",flexDirection:"column",gap:3,flexShrink:0}}>
                    <button onClick={() => window.__petosMoveStaff?.(s.id, -1)} disabled={idx === 0} title="上移" style={{background:idx===0?"#F7F7FB":"var(--c-light)",border:"none",borderRadius:8,width:30,height:24,display:"flex",alignItems:"center",justifyContent:"center",cursor:idx===0?"not-allowed":"pointer",opacity:idx===0?0.35:1}}><ChevronUp size={15} color="var(--c-primary)" strokeWidth={3}/></button>
                    <button onClick={() => window.__petosMoveStaff?.(s.id, +1)} disabled={idx === allStaff.length - 1} title="下移" style={{background:idx===allStaff.length-1?"#F7F7FB":"var(--c-light)",border:"none",borderRadius:8,width:30,height:24,display:"flex",alignItems:"center",justifyContent:"center",cursor:idx===allStaff.length-1?"not-allowed":"pointer",opacity:idx===allStaff.length-1?0.35:1}}><ChevronDown size={15} color="var(--c-primary)" strokeWidth={3}/></button>
                  </div>
                )}
                <div style={{width:48,height:48,background:isEnabled?"linear-gradient(135deg,var(--c-light),var(--c-grad2))":"#F3F3F8",borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:isEnabled?"var(--c-primary)":"#AFAFC4",fontSize:20,flexShrink:0}}>{s.name[0]}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                    <p style={{fontWeight:800,fontSize:16,margin:0,color:isEnabled?"#1D1D2E":"#AFAFC4",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</p>
                    {!isEnabled&&<Badge color="#EF4444" bg="#FEF2F2">停用</Badge>}
                    <Badge color={s.jobType==="fulltime"?"#1D4ED8":"#92400E"} bg={s.jobType==="fulltime"?"#EFF6FF":"#FFFBEB"}>{s.jobType==="fulltime"?"全職":"兼職"}</Badge>
                  </div>
                  {/* Quick-glance metadata — phone + join date on one line
                      so the owner can verify identity at a glance; the
                      pay-rate chip below is for cross-checking salary. */}
                  <p style={{fontSize:12,color:"#9A9AAE",margin:0,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(s.phone||"") + " · 入職 " + (s.joinDate||"—")}{s.leaveDate?" · 離職 "+s.leaveDate:""}</p>
                  {payrollOn && (() => {
                    const isFT = s.jobType === "fulltime";
                    const base = Number(s.baseSalary) || 0;
                    const hourly = Number(s.hourlyRate) || 0;
                    const commEnabled = s.commissionEnabled !== false;
                    const rateBits = [];
                    if (isFT) rateBits.push("月薪 $" + base.toLocaleString());
                    else rateBits.push("時薪 $" + hourly);
                    if (commEnabled) {
                      if (s.commissionMode === "percent") rateBits.push("抽 " + (s.commissionRate || 0) + "%");
                      else rateBits.push("每單 $" + (s.commissionRate || 0));
                    }
                    return <div style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:5,background:"#fff",border:"1px solid rgba(120,120,160,0.18)",borderRadius:99,padding:"3px 10px"}}>
                      <DollarSign size={11} color="#B45309" strokeWidth={2.8} />
                      <span style={{fontSize:11,fontWeight:800,color:"#92400E"}}>{rateBits.join(" + ")}</span>
                    </div>;
                  })()}
                  {avg&&<div style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:3,marginLeft:6}}><Stars value={Math.round(parseFloat(avg))} size={11}/><span style={{fontSize:11,color:"#9A9AAE",fontWeight:700}}>{avg}</span></div>}
                </div>
                {/* Action column — labelled pills, right of the name. White
                    chips lift cleanly off the tinted band. */}
                {!staffReorder && (() => {
                  const pill = { display:"flex",alignItems:"center",gap:6,width:"100%",padding:"8px 10px",borderRadius:12,background:"#fff",border:"1px solid rgba(120,120,160,0.12)",boxShadow:"0 1px 3px rgba(40,40,80,0.06)",fontWeight:800,fontSize:12,cursor:"pointer",whiteSpace:"nowrap",boxSizing:"border-box"};
                  // Primary settle button — only when there's actually pay
                  // owed AND it hasn't been paid yet. Becomes a filled
                  // green pill so the boss's eye lands on it first.
                  const unpaid = payrollOn && totalPay > 0 && !paid;
                  const settlePrimary = unpaid ? { ...pill, background: "#10B981", color: "#fff", border: "1px solid transparent", boxShadow: "0 4px 10px rgba(16,185,129,0.25)" } : { ...pill, color: "var(--c-primary)" };
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0,width:124}}>
                      {payrollOn && <button onClick={() => setPayslipSheet({ staffId: s.id, from: staffDateFrom, to: staffDateTo })} style={settlePrimary}><FileText size={14} strokeWidth={2.5}/> {unpaid ? "結算" : "月結單"}</button>}
                      <button onClick={() => payrollOn ? setWageStaff(s) : toast("薪金管理需要專業版方案", "error")} style={{...pill,color:payrollOn?"#B45309":"#C4C4D4"}}><DollarSign size={14} strokeWidth={2.5}/> 薪金設定{payrollOn?"":" 🔒"}</button>
                      {payrollOn && <button onClick={() => setWageHistoryStaff({ staff: s, ym: dateNow().slice(0,7) })} style={{...pill,color:"#15803D"}}><CalendarDays size={14} strokeWidth={2.5}/> 薪金紀錄</button>}
                      {payrollOn && s.jobType === "parttime" && <button onClick={() => setDailyWageSheet({ focusStaffId: s.id })} style={{...pill,color:"#B45309"}}><DollarSign size={14} strokeWidth={2.5}/> {(s.settleMode || "daily") === "monthly" ? "記錄工時" : "紀錄今日薪金"}</button>}
                    </div>
                  );
                })()}
              </div>

              {/* Stats — uniform dark numbers, hairline separators */}
              <div style={{display:"flex",marginTop:16}}>
                {stats.map(([l,v],i)=>(
                  <div key={i} style={{flex:1,textAlign:"center",borderLeft:i?"1px solid #F0F0F5":"none"}}>
                    <p style={{fontSize:16,fontWeight:800,color:"#1D1D2E",margin:"0 0 2px",letterSpacing:"-0.3px"}}>{v}</p>
                    <p style={{fontSize:11,color:"#9A9AAE",margin:0,fontWeight:600}}>{l}</p>
                  </div>
                ))}
              </div>

              {/* Pay — the one place colour earns its keep (paid feature) */}
              {payrollOn && <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid #F0F0F5"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8}}>
                  <span style={{display:"flex",alignItems:"center",gap:7}}>
                    <span style={{fontSize:14,fontWeight:700,color:"#6A6A85"}}>應付薪資</span>
                    {paid && <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:11,fontWeight:800,color:"#16A34A",background:"#DCFCE7",borderRadius:99,padding:"2px 8px"}}><Check size={11} strokeWidth={3.5}/> 已出薪</span>}
                  </span>
                  <span style={{fontSize:24,fontWeight:900,color:"#10B981",letterSpacing:"-0.5px"}}>{"$"+totalPay.toLocaleString()}</span>
                </div>
                <p style={{fontSize:11.5,color:"#AFAFC4",margin:"3px 0 0",fontWeight:600,textAlign:"right"}}>{paid ? (paid.paidAt + " · " + ({fps:"FPS",cash:"現金",bank:"銀行轉賬",cheque:"支票",other:"其他"}[paid.method]||paid.method) + " 出薪 $" + (Number(paid.amount)||0).toLocaleString()) : payCaption}</p>
              </div>}

            </Card>;
          })}

          {/* Table view — one tidy row per staff, tap to open the payslip.
              Scrolls horizontally so extra columns (入職 / 評分 / 工時…)
              never crush the layout on a phone. */}
          {staffView === "table" && allStaff.length > 0 && (() => {
            // Transposed table: each STAFF is a column, each metric a row —
            // so one staff's full data reads top-to-bottom. As detailed as
            // possible; the wage rows only show on a paid plan.
            const cols = allStaff.map(s => {
              const isEnabled = s.enabled !== false;
              const sd = allApts.filter(a => a.staffId === s.id && a.status === "done" && a.date >= staffDateFrom && a.date <= staffDateTo);
              // Shared engine (payroll.js) — same numbers as the payslip.
              const rowPay = staffPeriodPay(s, allApts, staffDateFrom, staffDateTo, { packages: tenantCustomerPackages, adjustments: staffPay.adjustments });
              const orderRev = rowPay.rev;
              const commEnabled = s.commissionEnabled !== false;
              const comm = rowPay.comm;
              const wl = sumWageLog(s, staffDateFrom, staffDateTo);
              const isFT = s.jobType === "fulltime";
              const hPay = rowPay.hPay;
              const basePay = rowPay.basePay;
              const tips = rowPay.tips;
              const totalPay = rowPay.total;
              const reviews = state.reviews.filter(r => r.staffId === s.id);
              const avg = reviews.length ? (reviews.reduce((a,r)=>a+r.rating,0)/reviews.length).toFixed(1) : null;
              const paid = payoutCovering(s, staffDateFrom, staffDateTo);
              return { s, isEnabled, isFT, orders: sd.length, orderRev, comm, commEnabled, wl, hPay, basePay, tips, totalPay, avg, paid };
            });
            const metrics = [
              { label:"職位", render: c => <span style={{fontSize:11,fontWeight:800,color:c.isFT?"#1D4ED8":"#D97706",background:c.isFT?"#EFF6FF":"#FFFBEB",borderRadius:99,padding:"2px 8px"}}>{c.isFT?"全職":"兼職"}</span> },
              { label:"入職", render: c => <span style={{fontSize:12,fontWeight:700,color:"#8A8A9E"}}>{c.s.joinDate||"—"}</span> },
              { label:"評分", render: c => <span style={{fontSize:13,fontWeight:800,color:c.avg?"#F59E0B":"#C4C4D4"}}>{c.avg?"★"+c.avg:"—"}</span> },
              { label:"完成訂單", render: c => <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{c.orders}</span> },
              { label:"訂單收費", payroll:true, render: c => <span style={{fontSize:13,fontWeight:700,color:"#4A4A68"}}>${c.orderRev.toLocaleString()}</span> },
              { label:"工時", payroll:true, render: c => <span style={{fontSize:13,fontWeight:700,color:"#4A4A68"}}>{c.isFT?"—":((c.wl.hours>0||c.wl.days>0)?c.wl.hours+"h":"—")}</span> },
              { label:"上班日", payroll:true, render: c => <span style={{fontSize:13,fontWeight:700,color:"#4A4A68"}}>{c.isFT?"—":(c.wl.days?c.wl.days+"日":"—")}</span> },
              { label:"底薪 / 時薪", payroll:true, render: c => <span style={{fontSize:12,fontWeight:800,color:"#4A4A68"}}>{c.isFT?("$"+(c.s.baseSalary||0).toLocaleString()+"/月"):("$"+(c.s.hourlyRate||0)+"/時")}</span> },
              { label:"服務抽成", payroll:true, render: c => <span style={{fontSize:13,fontWeight:700,color:"#D97706"}}>{c.commEnabled?"$"+c.comm.toLocaleString():"—"}</span> },
              { label:"小費", payroll:true, render: c => <span style={{fontSize:13,fontWeight:700,color:"#4A4A68"}}>{c.tips>0?"$"+c.tips.toLocaleString():"—"}</span> },
              { label:"應付薪資", payroll:true, hero:true, render: c => <span style={{fontSize:15,fontWeight:900,color:"#10B981"}}>${c.totalPay.toLocaleString()}</span> },
              { label:"出薪", payroll:true, render: c => c.paid ? <span style={{display:"inline-flex",alignItems:"center",gap:2,fontSize:11,fontWeight:800,color:"#16A34A",background:"#DCFCE7",borderRadius:99,padding:"2px 7px"}}><Check size={10} strokeWidth={3.5}/>已出</span> : <span style={{fontSize:12,color:"#C4C4D4",fontWeight:700}}>未出</span> },
            ].filter(m => payrollOn || !m.payroll);
            const LABELW = 90, COLW = 124;
            const labelCell = (extra) => ({position:"sticky",left:0,zIndex:1,width:LABELW,flexShrink:0,background:"#FCFCFF",padding:"10px 12px",fontSize:12,fontWeight:800,color:"#6A6A85",borderRight:"1px solid #EEEEF4",...extra});
            return (
              <Card style={{padding:0,overflow:"hidden"}}>
                <div style={{overflowX:"auto"}}>
                  <div style={{minWidth:LABELW + cols.length*COLW}}>
                    {/* Header: staff names (tap → payslip) */}
                    <div style={{display:"flex",borderBottom:"2px solid #F0F0F5",background:"#FCFCFF"}}>
                      <span style={labelCell({display:"flex",alignItems:"center",fontSize:11,color:"#9A9AAE"})}>員工</span>
                      {cols.map(c => (
                        <button key={c.s.id} onClick={() => payrollOn && setPayslipSheet({ staffId: c.s.id, from: staffDateFrom, to: staffDateTo })} style={{width:COLW,flexShrink:0,padding:"10px 8px",border:"none",borderLeft:"1px solid #F5F5FA",background:"transparent",cursor:payrollOn?"pointer":"default",textAlign:"center",opacity:c.isEnabled?1:0.5}}>
                          <span style={{display:"block",fontSize:14,fontWeight:900,color:"#1D1D2E",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.s.name}</span>
                        </button>
                      ))}
                    </div>
                    {metrics.map((m, ri) => (
                      <div key={ri} style={{display:"flex",borderTop:ri?"1px solid #F5F5FA":"none",background:m.hero?"#F0FDF4":"transparent"}}>
                        <span style={labelCell(m.hero?{background:"#F0FDF4",color:"#166534"}:{})}>{m.label}</span>
                        {cols.map(c => (
                          <span key={c.s.id} style={{width:COLW,flexShrink:0,padding:"10px 8px",borderLeft:"1px solid #F5F5FA",display:"flex",alignItems:"center",justifyContent:"center",textAlign:"center",opacity:c.isEnabled?1:0.5}}>{m.render(c)}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{display:"flex",gap:14,padding:"9px 16px",borderTop:"1px solid #F0F0F5",fontSize:11,fontWeight:700,color:"#9A9AAE"}}>
                  <span>← 左右滑動睇更多員工</span>
                  {payrollOn && <span style={{marginLeft:"auto"}}>點員工開月結單</span>}
                </div>
              </Card>
            );
          })()}

          <BottomSheet open={editStaff !== null} onClose={() => setEditStaff(null)} title={editStaff?.id ? (i18nT("staff.edit") + (editStaff.name ? " — " + editStaff.name : "")) : i18nT("staff.add")}>
            {editStaff !== null && <StaffEditor staff={editStaff?.id ? editStaff : null} state={state} setState={setState} tenantId={user.tenantId} onClose={() => setEditStaff(null)} section="staff" onPaySaved={refreshStaffPay} />}
          </BottomSheet>
          {/* Bridge so the wage sheet's "改名 / 編輯資料" link can hand
              off to the full StaffEditor without prop-drilling. */}
          {(() => { try { window.__petosOpenFullStaffEdit = (s) => setEditStaff(s); } catch { /* ignore */ } return null; })()}
          <BottomSheet open={!!wageStaff} onClose={() => setWageStaff(null)} title={"薪金設定 💰" + (wageStaff?.name ? " — " + wageStaff.name : "")}>
            {wageStaff && <StaffEditor staff={wageStaff} state={state} setState={setState} tenantId={user.tenantId} onClose={() => setWageStaff(null)} section="wage" onPaySaved={refreshStaffPay} />}
          </BottomSheet>

          {/* Salary history sheet — month picker + 1–31 day table.
              Full-time staff settle by monthly salary, so the calendar
              table is hidden for them (they only see payout history).
              Part-time staff get an inline editor: 工時 / 出薪 / 方式 /
              核實 — all entered in one row that expands when tapped. */}
          <BottomSheet open={!!wageHistoryStaff} onClose={() => setWageHistoryStaff(null)} title={"薪金紀錄 📒" + (wageHistoryStaff?.staff?.name ? " — " + wageHistoryStaff.staff.name : "")}>
            {wageHistoryStaff && (() => {
              const s = wageHistoryStaff.staff;
              const ym = wageHistoryStaff.ym;
              const editingDay = wageHistoryStaff.editingDay || null;
              const editDraft = wageHistoryStaff.editDraft || null;
              const [yStr, mStr] = ym.split("-");
              const yNum = parseInt(yStr), mNum = parseInt(mStr);
              const daysInMonth = new Date(yNum, mNum, 0).getDate();
              const joinDate = s?.joinDate || "";
              const leaveDate = s?.leaveDate || "";
              const isFT = s?.jobType === "fulltime";
              const hourlyRate = Number(s?.hourlyRate) || 0;
              const baseSalary = Number(s?.baseSalary) || 0;
              const wageMap = {};
              for (const r of (s?.wageLog || [])) { if (r && r.date) wageMap[r.date] = r; }
              const payouts = (s?.payoutLog || []).filter(p => p && p.from && p.to && p.from <= (ym + "-" + String(daysInMonth).padStart(2, "0")) && p.to >= (ym + "-01"));
              const methodLabel = { fps: "FPS", cash: "現金", bank: "銀行轉賬", cheque: "支票", other: "其他", "": "未付" };
              const methodOptions = [["", "未付"], ["cash", "現金"], ["fps", "FPS"], ["bank", "銀行轉賬"], ["cheque", "支票"], ["other", "其他"]];
              const prevMonth = (() => { const d = new Date(yNum, mNum - 2, 1); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"); })();
              const nextMonth = (() => { const d = new Date(yNum, mNum, 1); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"); })();
              const today = dateNow();
              let monthWageTotal = 0, monthHoursTotal = 0, monthPaidTotal = 0;
              // Schedule + leave attendance counters (full-time & part-time)
              const hasStaffSchedSheet = s.staffHours && Object.values(s.staffHours).some(d => d && d.enabled);
              let monthScheduledDays = 0, monthWorkedDays = 0, monthSickDays = 0, monthPersonalDays = 0;
              // Pull leave events for this staff in this month from state.appointments
              const monthEvents = (state.appointments || []).filter(a =>
                a.kind === "event" && a.tenantId === user.tenantId && a.staffId === s.id && a.leaveType && a.date && a.date.slice(0,7) === ym
              );
              const leaveByDate = {};
              for (const e of monthEvents) {
                const fraction = (Number(e.customDuration) || 0) >= 360 ? 1 : 0.5; // >=6h treated as whole day
                leaveByDate[e.date] = (leaveByDate[e.date] || { sick: 0, personal: 0 });
                if (e.leaveType === "sick") leaveByDate[e.date].sick += fraction;
                else if (e.leaveType === "personal") leaveByDate[e.date].personal += fraction;
              }
              for (let d = 1; d <= daysInMonth; d++) {
                const ds = ym + "-" + String(d).padStart(2, "0");
                if (joinDate && ds < joinDate) continue;
                if (leaveDate && ds > leaveDate) continue;
                if (ds > today) continue;
                const dow = new Date(ds + "T00:00:00").getDay();
                // Same per-jobType rule as the inline table.
                const scheduledThatDay = hasStaffSchedSheet
                  ? !!(s.staffHours[dow]?.enabled || s.staffHours[String(dow)]?.enabled)
                  : (isFT ? !!tenant?.businessHours?.[dow]?.enabled : false);
                const lv = leaveByDate[ds];
                if (scheduledThatDay && !staffOffOn(s, ds)) {
                  monthScheduledDays++;
                  if (lv) {
                    monthSickDays += lv.sick || 0;
                    monthPersonalDays += lv.personal || 0;
                  }
                }
                const r = wageMap[ds];
                if (r) {
                  monthHoursTotal += Number(r.hours) || 0;
                  monthWageTotal += Number(r.amount) || 0;
                  // Only count as a worked day if NOT a full-day leave.
                  // Half-day leave still allows half a worked day; we
                  // count the wage row regardless so wages aren't lost,
                  // but don't double-count in monthWorkedDays.
                  const fullDayLeave = lv && (lv.sick + lv.personal) >= 1;
                  if (Number(r.hours) > 0 && !fullDayLeave) monthWorkedDays++;
                  if (r.paidMethod) {
                    monthPaidTotal += Number(r.amount) || 0;
                  } else if (payouts.some(p => p.from <= ds && p.to >= ds)) {
                    monthPaidTotal += Number(r.amount) || 0;
                  }
                }
              }
              const monthAbsentLeaveDays = monthSickDays + monthPersonalDays;
              // Save draft → patch profile + sync local state
              const saveDraft = async () => {
                if (!editingDay || !editDraft) return;
                const ds = editingDay;
                const hRaw = String(editDraft.hours ?? "").trim();
                const existing = wageMap[ds];
                const others = (s.wageLog || []).filter(r => r && r.date !== ds);
                let nextLog;
                const amtRaw = String(editDraft.amount ?? "").trim();
                const amtOverride = amtRaw !== "" && !isNaN(parseFloat(amtRaw)) ? Math.round(parseFloat(amtRaw)) : null;
                const hoursOnly = hRaw === "" || isNaN(parseFloat(hRaw));
                if (hoursOnly && amtOverride == null) {
                  // No hours AND no amount — clear the row.
                  nextLog = others;
                } else {
                  const hours = hoursOnly ? 0 : parseFloat(hRaw);
                  // Precedence: explicit owner-entered amount > hours×rate >
                  // existing amount. Flat-day-wage shops (rate=0) MUST
                  // supply an amount via the override; we no longer fall
                  // back to existing silently.
                  let amount;
                  if (amtOverride != null) amount = amtOverride;
                  else if (hourlyRate > 0 && hours > 0) amount = Math.round(hours * hourlyRate);
                  else amount = 0;
                  nextLog = [...others, {
                    date: ds, hours, amount,
                    paidMethod: editDraft.paidMethod || "",
                    verified: !!editDraft.verified,
                    note: existing?.note || editDraft.note || "",
                    at: new Date().toISOString(),
                  }];
                }
                try {
                  // One row per work day in staff_wage_entries (owner-only).
                  const row = nextLog.find(r => r && r.date === ds);
                  if (row) await upsertWageEntry({ tenantId: user.tenantId, staffId: s.id, date: ds, hours: row.hours, amount: row.amount, note: row.note, verified: row.verified, paidMethod: row.paidMethod });
                  else await deleteWageEntry({ staffId: s.id, date: ds });
                  await refreshStaffPay();
                  setWageHistoryStaff(prev => prev ? { ...prev, staff: { ...prev.staff, wageLog: nextLog }, editingDay: null, editDraft: null } : prev);
                  toast(hRaw === "" ? "已刪除 " + ds : "已儲存 " + ds);
                } catch (e) {
                  toast("更新失敗：" + (e?.message || e), "error");
                }
              };
              // Toggle 核實 in-place (no full edit needed)
              const toggleVerify = async (ds) => {
                const existing = wageMap[ds];
                if (!existing) { toast("呢日未有薪金紀錄", "warning"); return; }
                const others = (s.wageLog || []).filter(r => r && r.date !== ds);
                const nextLog = [...others, { ...existing, verified: !existing.verified, at: new Date().toISOString() }];
                try {
                  await upsertWageEntry({ tenantId: user.tenantId, staffId: s.id, date: ds, hours: existing.hours, amount: existing.amount, note: existing.note, verified: !existing.verified, paidMethod: existing.paidMethod });
                  await refreshStaffPay();
                  setWageHistoryStaff(prev => prev ? { ...prev, staff: { ...prev.staff, wageLog: nextLog } } : prev);
                  toast(existing.verified ? "已取消核實" : "已核實 ✓");
                } catch (e) {
                  toast("更新失敗：" + (e?.message || e), "error");
                }
              };
              const openEditor = (ds, existing) => {
                if (joinDate && ds < joinDate) { toast("此日期早於入職日 " + joinDate, "warning"); return; }
                if (leaveDate && ds > leaveDate) { toast("此日期晚於離職日 " + leaveDate, "warning"); return; }
                setWageHistoryStaff(prev => prev ? {
                  ...prev,
                  editingDay: ds,
                  editDraft: {
                    hours: existing ? String(existing.hours || "") : "",
                    amount: existing ? String(existing.amount || "") : "",
                    paidMethod: existing?.paidMethod || "",
                    verified: !!existing?.verified,
                  },
                } : prev);
              };
              return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {/* Month nav */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                    <button onClick={() => setWageHistoryStaff(prev => ({ ...prev, ym: prevMonth, editingDay: null }))} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                      <ChevronLeft size={18} color="var(--c-primary)" strokeWidth={3} />
                    </button>
                    <div style={{textAlign:"center",flex:1}}>
                      <p style={{fontSize:18,fontWeight:900,color:"#1F2937",margin:0}}>{yNum} 年 {mNum} 月</p>
                      <p style={{fontSize:11,color:"#6B7280",margin:"2px 0 0",fontWeight:700}}>{isFT ? "全職 · 月薪 $" + baseSalary.toLocaleString() : "兼職 · 時薪 $" + hourlyRate}{joinDate ? " · 入職 " + joinDate : ""}</p>
                    </div>
                    <button onClick={() => setWageHistoryStaff(prev => ({ ...prev, ym: nextMonth, editingDay: null }))} disabled={ym >= today.slice(0, 7)} style={{background:ym >= today.slice(0, 7) ? "#F3F4F6" : "var(--c-light)",border:"none",borderRadius:99,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:ym >= today.slice(0, 7) ? "not-allowed" : "pointer",opacity:ym >= today.slice(0, 7) ? 0.4 : 1}}>
                      <ChevronRight size={18} color="var(--c-primary)" strokeWidth={3} />
                    </button>
                  </div>

                  {/* Month summary — always shown */}
                  <Card style={{padding:"12px 14px",background:"linear-gradient(135deg,#FFFDFD 0%,var(--c-lighter) 100%)"}}>
                    <div style={{display:"grid",gridTemplateColumns:isFT?"1fr 1fr":"1fr 1fr 1fr",gap:8}}>
                      {!isFT && <div><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>工時</p><p style={{fontSize:18,fontWeight:900,color:"#1F2937",margin:"2px 0 0"}}>{monthHoursTotal}h</p></div>}
                      <div style={{textAlign:isFT?"left":"center",borderLeft:isFT?"none":"1px solid #F0F0F5",borderRight:isFT?"none":"1px solid #F0F0F5"}}><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>應付薪金</p><p style={{fontSize:18,fontWeight:900,color:"#1F2937",margin:"2px 0 0"}}>${isFT ? baseSalary.toLocaleString() : monthWageTotal.toLocaleString()}</p></div>
                      <div style={{textAlign:"right"}}><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>已出薪</p><p style={{fontSize:18,fontWeight:900,color:(isFT?payouts.length:monthPaidTotal)?"#10B981":"#9CA3AF",margin:"2px 0 0"}}>${isFT ? payouts.reduce((sm, p) => sm + (Number(p.amount) || 0), 0).toLocaleString() : monthPaidTotal.toLocaleString()}</p></div>
                    </div>
                  </Card>

                  {/* Attendance breakdown — 上班 / 病假 / 事假.
                      When the join / leave date falls mid-month, surface
                      that explicitly so the boss isn't confused why "31"
                      becomes "12" (they joined on the 20th, etc). */}
                  {(() => {
                    const monthStart = ym + "-01";
                    const monthEnd = ym + "-" + String(daysInMonth).padStart(2, "0");
                    const joinMid = joinDate && joinDate > monthStart && joinDate <= monthEnd;
                    const leaveMid = leaveDate && leaveDate >= monthStart && leaveDate < monthEnd;
                    const partialRange = (() => {
                      if (joinMid && leaveMid) return joinDate + " — " + leaveDate;
                      if (joinMid) return "自 " + joinDate + " 起（入職）";
                      if (leaveMid) return "至 " + leaveDate + " 止（離職）";
                      const futureCutoff = today < monthEnd ? "至今 " + today : null;
                      return futureCutoff;
                    })();
                    return (
                      <Card style={{padding:"12px 14px"}}>
                        <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:8,gap:8}}>
                          <p style={{fontSize:11,fontWeight:900,color:"#6B7280",margin:0,letterSpacing:0.3}}>📅 本月出勤</p>
                          {partialRange && <span style={{fontSize:10,fontWeight:700,color:"#92400E",background:"#FFFBEB",borderRadius:99,padding:"2px 8px"}}>{partialRange}</span>}
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:isFT?"1fr 1fr 1fr 1fr":"1fr 1fr 1fr",gap:8}}>
                          <div><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>應上班</p><p style={{fontSize:18,fontWeight:900,color:"#1F2937",margin:"2px 0 0"}}>{monthScheduledDays} 日</p></div>
                          {isFT && <div style={{textAlign:"center"}}><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>實際上班</p><p style={{fontSize:18,fontWeight:900,color:"#10B981",margin:"2px 0 0"}}>{Math.max(0, monthScheduledDays - monthAbsentLeaveDays)} 日</p></div>}
                          <div style={{textAlign:"center",borderLeft:"1px solid #F0F0F5"}}><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>🤒 病假</p><p style={{fontSize:18,fontWeight:900,color:monthSickDays>0?"#F59E0B":"#9CA3AF",margin:"2px 0 0"}}>{monthSickDays} 日</p></div>
                          <div style={{textAlign:"right",borderLeft:"1px solid #F0F0F5"}}><p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>📌 事假</p><p style={{fontSize:18,fontWeight:900,color:monthPersonalDays>0?"#F59E0B":"#9CA3AF",margin:"2px 0 0"}}>{monthPersonalDays} 日</p></div>
                        </div>
                      </Card>
                    );
                  })()}

                  {/* Full-time view — payout history only, no daily table */}
                  {isFT && (
                    <div style={{background:"#F0FDF4",borderRadius:16,padding:16,border:"1.5px solid #BBF7D0"}}>
                      <p style={{fontSize:12,fontWeight:900,color:"#15803D",margin:"0 0 4px"}}>📒 出薪紀錄</p>
                      <p style={{fontSize:11,color:"#15803D",margin:"0 0 10px",fontWeight:700,opacity:0.85}}>全職員工以月薪結算 — 無需逐日記錄。下面係呢個月嘅出薪：</p>
                      {payouts.length === 0 ? (
                        <p style={{fontSize:12,color:"#15803D",margin:"8px 0 0",fontWeight:700,opacity:0.8,textAlign:"center",padding:"12px 0"}}>呢個月仲未發放薪金</p>
                      ) : (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {payouts.map((p, idx) => (
                            <div key={idx} style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1px solid #BBF7D0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                              <div style={{minWidth:0,flex:1}}>
                                <p style={{fontSize:13,fontWeight:900,color:"#15803D",margin:0}}>{(p.from || "?") + " — " + (p.to || "?")}</p>
                                <p style={{fontSize:11,color:"#16A34A",margin:"2px 0 0",fontWeight:700}}>{(methodLabel[p.method] || p.method || "—") + " · " + (p.paidAt || "—")}</p>
                              </div>
                              <span style={{fontSize:15,fontWeight:900,color:"#15803D",flexShrink:0}}>${(Number(p.amount) || 0).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Part-time view — 1–31 day table with inline editor */}
                  {!isFT && (
                    <div style={{border:"1.5px solid var(--c-light)",borderRadius:14,overflow:"hidden"}}>
                      {/* Header */}
                      <div style={{display:"grid",gridTemplateColumns:"54px 64px 1fr 92px 36px",gap:0,padding:"10px 0",background:"#F9FAFB",borderBottom:"1.5px solid var(--c-light)"}}>
                        <span style={{fontSize:11,fontWeight:900,color:"#6B7280",textAlign:"center"}}>日期</span>
                        <span style={{fontSize:11,fontWeight:900,color:"#6B7280",textAlign:"center"}}>工時</span>
                        <span style={{fontSize:11,fontWeight:900,color:"#6B7280",textAlign:"center"}}>應付 / 方式</span>
                        <span style={{fontSize:11,fontWeight:900,color:"#6B7280",textAlign:"center"}}>狀態</span>
                        <span style={{fontSize:11,fontWeight:900,color:"#6B7280",textAlign:"center"}}>核實</span>
                      </div>
                      {(() => {
                        const hasStaffSchedule = s.staffHours && Object.values(s.staffHours).some(d => d && d.enabled);
                        return Array.from({length:daysInMonth}, (_, i) => i + 1).map(d => {
                        const ds = ym + "-" + String(d).padStart(2, "0");
                        const isFuture = ds > today;
                        const beforeJoin = joinDate && ds < joinDate;
                        const afterLeave = leaveDate && ds > leaveDate;
                        const dow = new Date(ds + "T00:00:00").getDay();
                        // Schedule check: staffHours wins when defined,
                        // otherwise fall back to the shop's business hours
                        // for that weekday. If the staffer is on time-off,
                        // override to off regardless of schedule.
                        const shopOpenThatDay = !!tenant?.businessHours?.[dow]?.enabled;
                        // Part-timers MUST have an explicit schedule —
                        // don't silently inherit shop hours, otherwise every
                        // shop-open day is treated as "scheduled" even when
                        // the owner hasn't told the system this staffer
                        // actually works those days. Full-timers still
                        // inherit shop hours (default contract).
                        const staffScheduledThatDay = hasStaffSchedule
                          ? !!(s.staffHours[dow]?.enabled || s.staffHours[String(dow)]?.enabled)
                          : (isFT ? shopOpenThatDay : false);
                        const onTimeOff = staffOffOn(s, ds);
                        const r = wageMap[ds];
                        // Days WITHOUT a schedule are locked UNLESS the
                        // owner already recorded wage for that day (e.g.
                        // staff covered an emergency shift) — in that
                        // case keep the row editable so the figures stay
                        // accurate.
                        const noSchedule = (!staffScheduledThatDay || onTimeOff) && !r;
                        const locked = beforeJoin || afterLeave || noSchedule;
                        const isWeekend = [0, 6].includes(dow);
                        const rowBg = locked ? "#F3F4F6" : (editingDay === ds ? "#FEF3C7" : (isWeekend ? "#FAFAFC" : "#fff"));
                        const lockHint = beforeJoin ? "入職前" : afterLeave ? "離職後" : onTimeOff ? "休假" : noSchedule ? "無排班" : "";
                        const matchedPayout = payouts.find(p => p.from <= ds && p.to >= ds);
                        const isPaidDaily = !!(r && r.paidMethod);
                        const isPaid = isPaidDaily || !!matchedPayout;
                        const paidLabel = r?.paidMethod ? methodLabel[r.paidMethod] : (matchedPayout ? (methodLabel[matchedPayout.method] || matchedPayout.method) : "");
                        const isVerified = r && r.verified;
                        if (editingDay === ds) {
                          // Inline editor row
                          return (
                            <div key={d} style={{background:rowBg,padding:"12px 12px",borderTop:"1px solid #F3F4F6",display:"flex",flexDirection:"column",gap:10}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <span style={{fontSize:13,fontWeight:900,color:"#92400E"}}>📝 編輯 {ds}{isWeekend ? " (週末)" : ""}</span>
                                <div style={{flex:1}} />
                                <button onClick={() => setWageHistoryStaff(prev => prev ? { ...prev, editingDay: null, editDraft: null } : prev)} style={{background:"none",border:"none",color:"#6B7280",fontSize:12,fontWeight:700,cursor:"pointer"}}>取消</button>
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                                <div>
                                  <p style={{fontSize:10,color:"#92400E",margin:"0 0 4px",fontWeight:700}}>工時 (h)</p>
                                  <input type="number" inputMode="decimal" placeholder="例：5" value={editDraft?.hours ?? ""} onChange={e => setWageHistoryStaff(prev => prev ? { ...prev, editDraft: { ...prev.editDraft, hours: e.target.value } } : prev)} style={{width:"100%",border:"1.5px solid #FDE68A",borderRadius:10,padding:"8px 10px",fontSize:14,fontWeight:800,outline:"none",color:"#92400E",background:"#FFFBEB",textAlign:"right",boxSizing:"border-box"}} />
                                </div>
                                <div>
                                  <p style={{fontSize:10,color:"#92400E",margin:"0 0 4px",fontWeight:700}}>應付 ${hourlyRate > 0 ? <span style={{opacity:0.7}}>· 留空 = 自動</span> : <span style={{color:"#DC2626"}}>· 必填（無時薪）</span>}</p>
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    placeholder={(Number(editDraft?.hours) > 0 && hourlyRate > 0) ? String(Math.round(Number(editDraft.hours) * hourlyRate)) : "0"}
                                    value={editDraft?.amount ?? ""}
                                    onChange={e => setWageHistoryStaff(prev => prev ? { ...prev, editDraft: { ...prev.editDraft, amount: e.target.value } } : prev)}
                                    style={{width:"100%",border:"1.5px solid #FDE68A",borderRadius:10,padding:"8px 10px",fontSize:14,fontWeight:900,outline:"none",color:"#92400E",background:"#FFFBEB",textAlign:"right",boxSizing:"border-box"}}
                                  />
                                </div>
                              </div>
                              {/* Monthly-settle staff: paidMethod is set at
                                  month-end via the 月結單 payout flow, not
                                  per-day. Hide the picker so owner can't
                                  accidentally mix daily + monthly. */}
                              {(s.settleMode || "daily") === "monthly" ? (
                                <div style={{background:"#EFF6FF",border:"1.5px solid #BFDBFE",borderRadius:10,padding:"8px 10px"}}>
                                  <p style={{fontSize:11,fontWeight:800,color:"#1E40AF",margin:0}}>🗓️ 月結模式 — 出薪喺月結單一次過處理</p>
                                </div>
                              ) : (
                                <div>
                                  <p style={{fontSize:10,color:"#92400E",margin:"0 0 4px",fontWeight:700}}>出薪方式</p>
                                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                                    {methodOptions.map(([v, l]) => (
                                      <button key={v} onClick={() => setWageHistoryStaff(prev => prev ? { ...prev, editDraft: { ...prev.editDraft, paidMethod: v } } : prev)} style={{padding:"6px 12px",borderRadius:99,fontSize:11,fontWeight:800,border:"1.5px solid " + (editDraft?.paidMethod === v ? "var(--c-primary)" : "#FDE68A"),background:editDraft?.paidMethod === v ? "var(--c-primary)" : "#fff",color:editDraft?.paidMethod === v ? "#fff" : "#92400E",cursor:"pointer"}}>{l}</button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                                <input type="checkbox" checked={!!editDraft?.verified} onChange={e => setWageHistoryStaff(prev => prev ? { ...prev, editDraft: { ...prev.editDraft, verified: e.target.checked } } : prev)} style={{width:18,height:18,cursor:"pointer"}} />
                                <span style={{fontSize:12,fontWeight:800,color:"#92400E"}}>✓ 核實此筆紀錄（confirm 金額無誤）</span>
                              </label>
                              <div style={{display:"flex",gap:8}}>
                                <Btn size="sm" variant="secondary" onClick={() => setWageHistoryStaff(prev => prev ? { ...prev, editDraft: { ...prev.editDraft, hours: "" } } : prev)} style={{flex:1}}>清空</Btn>
                                <Btn size="sm" onClick={saveDraft} style={{flex:2,background:"#10B981",color:"#fff",borderColor:"transparent"}}>儲存</Btn>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={d}
                            onClick={() => !locked && !isFuture && openEditor(ds, r)}
                            style={{display:"grid",gridTemplateColumns:"54px 64px 1fr 92px 36px",gap:0,padding:"10px 0",borderTop:"1px solid #F3F4F6",background:rowBg,cursor:(locked || isFuture) ? "default" : "pointer",alignItems:"center"}}>
                            <span style={{fontSize:13,fontWeight:800,color:locked ? "#9CA3AF" : (isFuture ? "#9CA3AF" : "#1F2937"),textAlign:"center"}}>{d}{isWeekend ? "*" : ""}</span>
                            <span style={{fontSize:13,fontWeight:700,color:locked ? "#9CA3AF" : "#374151",textAlign:"center"}}>
                              {locked ? <span style={{fontSize:10,fontStyle:"italic"}}>{lockHint}</span> : (r?.hours ? Number(r.hours) + "h" : (isFuture ? "—" : "·"))}
                            </span>
                            <span style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                              <span style={{fontSize:13,fontWeight:900,color:locked ? "#9CA3AF" : "#1F2937"}}>{r?.amount ? "$" + Number(r.amount).toLocaleString() : (locked ? "—" : "—")}</span>
                              {r && <span style={{fontSize:10,fontWeight:700,color:isPaid?"#10B981":"#D97706",marginTop:1}}>{isPaid ? paidLabel : "未付"}</span>}
                            </span>
                            <span style={{textAlign:"center"}}>
                              {r && isPaid ? <span style={{display:"inline-flex",alignItems:"center",gap:2,fontSize:10,fontWeight:900,color:"#10B981",background:"#DCFCE7",borderRadius:99,padding:"2px 7px"}}>{isPaidDaily ? "日結" : "月結"}</span> : (r ? <span style={{fontSize:10,fontWeight:800,color:"#D97706"}}>未付</span> : <span style={{fontSize:11,color:"#C4C4D4"}}>—</span>)}
                            </span>
                            <span style={{textAlign:"center"}}>
                              {r ? (
                                <button onClick={(e) => { e.stopPropagation(); toggleVerify(ds); }} title={isVerified ? "已核實 — 撳取消" : "撳一下核實"} style={{background:"none",border:"none",cursor:"pointer",padding:0,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
                                  {isVerified ? <Check size={18} color="#10B981" strokeWidth={3.5} /> : <Check size={16} color="#D1D5DB" strokeWidth={2} />}
                                </button>
                              ) : <span style={{fontSize:11,color:"#C4C4D4"}}>—</span>}
                            </span>
                          </div>
                        );
                      });
                      })()}
                    </div>
                  )}

                  <p style={{fontSize:10,color:"#9CA3AF",margin:0,fontWeight:700,textAlign:"center",lineHeight:1.5}}>
                    {isFT ? "全職員工：月薪喺月底自動結算" : "點擊日期可輸入工時 / 出薪方式 / 核實 · 灰色 = 無排班"}
                    {!isFT && " · * = 週末"}
                  </p>
                </div>
              );
            })()}
          </BottomSheet>
          {/* 員工月結單 — full payslip breakdown for the chosen date range
              with CSV / print export. Uses paired clock-in/out hours +
              tiered commission + tips + deductions. */}
          <DailyWageSheet
            open={!!dailyWageSheet}
            focusStaffId={dailyWageSheet && typeof dailyWageSheet === "object" ? dailyWageSheet.focusStaffId : null}
            onClose={() => setDailyWageSheet(false)}
            staffList={allStaff.filter(s => s.role !== "owner" && s.jobType !== "fulltime" && s.enabled !== false)}
            onPatched={() => refreshStaffPay()}
          />
          <BottomSheet open={!!payslipSheet} onClose={() => setPayslipSheet(null)} title="員工薪金單">
            {payslipSheet && (() => {
              const s = (tenant?.staffIds || []).map(id => state.users[id]).find(u => u?.id === payslipSheet.staffId);
              if (!s) return <p style={{fontSize:13,color:"#AFAFC4"}}>員工不存在</p>;
              const from = payslipSheet.from, to = payslipSheet.to;
              // Lazy fetch deductions when sheet opens / range changes — cached on payslipSheet itself.
              if (!payslipSheet._loadedFor || payslipSheet._loadedFor !== (s.id + from + to)) {
                fetchStaffDeductions(user.tenantId, { from, to, staffId: s.id })
                  .then(ds => setPayslipSheet(f => f && f.staffId === s.id ? { ...f, _loadedFor: s.id + from + to, deductions: ds } : f))
                  .catch(() => setPayslipSheet(f => f ? { ...f, _loadedFor: s.id + from + to, deductions: [] } : f));
              }
              const deductions = payslipSheet.deductions || [];
              // ONE engine (src/lib/payroll.js) — the same call the summary,
              // cards, table, chart and reminders use, so the payslip can never
              // disagree with them. Everything below just names its parts.
              const slip = staffPeriodPay(s, allApts, from, to, { packages: tenantCustomerPackages, adjustments: deductions });
              const wage = sumWageLog(s, from, to);
              const workHours = wage.hours;
              const workDays = wage.days;
              const hPay = slip.hPay;
              const basePay = slip.basePay;
              const rangeApts = allApts.filter(a => a.staffId === s.id && a.status === "done" && a.date >= from && a.date <= to);
              const orderRev = slip.rev;
              const svcComm = slip.serviceCommission + slip.redemptionCommission;
              const commLabel = (slip.serviceCommissionLabel || "") + (slip.redemptionCommission > 0 ? (slip.serviceCommissionLabel ? " + " : "") + "套票 " + slip.redemptionRate + "%" : "");
              const tipsTotal = slip.tips;
              const pkgsSold = (tenantCustomerPackages || []).filter(c => c.soldByUserId === s.id && !c.refundedAt && c.purchasedAt
                && localDateStr(new Date(c.purchasedAt)) >= from && localDateStr(new Date(c.purchasedAt)) <= to);
              const pkgRev = slip.packageSales.revenue;
              const pkgRate = slip.packageSales.rate;
              const pkgComm = slip.packageSales.commission;
              const totalComm = svcComm + pkgComm;
              const bonusTotal = slip.bonus;
              const deductTotal = slip.deductions;
              const gross = slip.gross;
              const mpf = null; // not computed — shown as 未計強積金 on the payslip
              const net = slip.net;
              const exportCsv = () => {
                const lines = [
                  ["員工", s.name],
                  ["期間", from + " ～ " + to],
                  ["職位", s.jobType === "fulltime" ? "全職" : "兼職"],
                  ["底薪", "$" + basePay],
                  ["兼職工資", "$" + hPay + "（" + workHours + " h / " + workDays + " 日）"],
                  ["工作日", workDays + " 日"],
                  ["完成訂單", rangeApts.length],
                  ["訂單收費", "$" + orderRev],
                  ["服務抽成 (" + commLabel + ")", "$" + svcComm],
                  ["小費總額", "$" + tipsTotal],
                  ["售出套票", pkgsSold.length],
                  ["套票銷售額", "$" + pkgRev],
                  ["套票抽成 (" + pkgRate + "%)", "$" + pkgComm],
                  ["獎金 / 額外津貼", "+$" + bonusTotal],
                  ["合計應付（含小費+獎金）", "$" + gross],
                  ["扣減 (" + deductions.filter(d => d.kind !== "bonus").length + " 項)", "-$" + deductTotal],
                  ["實付", "$" + net],
                  ["備註", "未計強積金（MPF）"],
                ].map(r => r.join(",")).join("\n");
                const blob = new Blob(["﻿" + lines], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "payslip_" + s.name + "_" + from + "_" + to + ".csv";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast("已下載 CSV ✓");
              };
              // ── Payout (已出薪) — record an actual salary payment with
              //    method + date. Stored on profile.payoutLog (JSONB).
              const PAY_METHODS = [["fps","FPS"],["cash","現金"],["bank","銀行轉賬"],["cheque","支票"],["other","其他"]];
              const methodLabel = (m) => (PAY_METHODS.find(x => x[0] === m) || [m, m])[1];
              const payMethod = payslipSheet.payMethod || "fps";
              const payDate = payslipSheet.payDate || dateNow();
              const payAmt = payslipSheet.payAmt ?? net;
              const payouts = (s.payoutLog || []).slice().sort((a,b) => (b.paidAt||"").localeCompare(a.paidAt||""));
              // A payout already covering this exact-or-wider period → the
              // period is settled; we hide the form and show a paid panel.
              const paidThis = payoutCovering(s, from, to);
              // Overlapping-but-not-fully-covering payouts → the period is only
              // partially settled (a remainder may be owed), so we keep the form
              // but surface the clash upfront instead of only warning on submit.
              const partialClashes = paidThis ? [] : payoutsOverlapping(s, from, to);
              const confirmPayout = async () => {
                // Validate before submit.
                if (!payDate) { toast("請選擇出薪日期", "error"); return; }
                const amount = Math.round(Number(payAmt) || 0);
                if (!Number.isFinite(amount) || amount <= 0) { toast("請輸入有效金額（需大於 0）", "error"); return; }
                if (!PAY_METHODS.some(x => x[0] === payMethod)) { toast("請選擇出薪方式", "error"); return; }
                let kind = "salary";
                let note = String(payslipSheet.payNote || "").trim();
                // The DB refuses a second salary payout for an overlapping
                // period (staff_payouts_no_overlap). A genuine top-up or
                // correction is recorded as an explicit 補發 / 調整 — and must
                // say why — instead of the old "are you sure?" override.
                const clashes = payoutsOverlapping(s, from, to);
                if (clashes.length) {
                  const lines = clashes.slice().sort((a,b) => (a.from||"").localeCompare(b.from||""))
                    .map(p => `• ${p.from} ~ ${p.to}｜$${Math.round(Number(p.amount)||0).toLocaleString()}（${p.paidAt||"?"}）`).join("\n");
                  const reason = window.prompt(`⚠️ 呢段期間已經出過薪：\n${lines}\n\n如要「補發 / 調整」，請輸入原因（會記錄喺出薪紀錄）：`, note);
                  if (!reason || !reason.trim()) return;
                  kind = "adjustment"; note = reason.trim();
                } else if (amount !== Math.round(net) && !note) {
                  // Paying something other than the payslip's 實付 leaves an
                  // unexplained gap in the record — ask for the reason.
                  const reason = window.prompt(`金額 $${amount.toLocaleString()} 同月結單「實付」$${Math.round(net).toLocaleString()} 唔同。請輸入原因：`);
                  if (!reason || !reason.trim()) return;
                  note = reason.trim();
                }
                // Review step — a payout can't be edited or deleted afterwards.
                const ok = window.confirm(
                  `確認出薪？\n\n${s.name || "員工"}\n期間：${from} ～ ${to}\n金額：$${amount.toLocaleString()}（${methodLabel(payMethod)}）\n出薪日期：${payDate}` +
                  (kind === "adjustment" ? "\n類型：補發 / 調整" : "") + (note ? `\n備註：${note}` : "") +
                  "\n\n※ 出薪紀錄唔可以修改或刪除，只可以作廢（會保留紀錄）。"
                );
                if (!ok) return;
                try {
                  // Freeze exactly what was paid: the engine result for this
                  // payslip, so later booking edits can't rewrite history.
                  const breakdown = { ...slip, staffName: s.name || "", computedAt: new Date().toISOString(), mpfIncluded: false };
                  await recordPayout({ tenantId: user.tenantId, staffId: s.id, kind, from, to, paidOn: payDate, amount, method: payMethod, note, breakdown });
                  await refreshStaffPay();
                  setPayslipSheet(f => ({ ...f, payAmt: undefined, payNote: "" }));
                  toast(i18nT("tx.payoutToast").replace("{amount}", amount.toLocaleString()));
                } catch (e) { toast(e?.message || i18nT("tx.payoutFailed"), "error"); }
              };
              // Payouts are never deleted — voiding keeps the row with who /
              // when / why, and frees the period to be paid again.
              const removePayout = async (p) => {
                if (!p?.id) return;
                const reason = window.prompt(`作廢 ${p.from} ～ ${p.to} 嘅出薪 $${Math.round(Number(p.amount)||0).toLocaleString()}？\n紀錄會保留。請輸入作廢原因：`);
                if (!reason || !reason.trim()) return;
                try {
                  await voidPayout(p.id, reason.trim());
                  await refreshStaffPay();
                  toast("已作廢出薪紀錄");
                } catch (e) { toast(e?.message || "失敗", "error"); }
              };
              return (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {/* Staff selector */}
                  <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                    {(tenant?.staffIds || []).map(id => state.users[id]).filter(Boolean).map(st => {
                      const on = st.id === payslipSheet.staffId;
                      return <button key={st.id} onClick={() => setPayslipSheet(f => ({...f, staffId: st.id}))} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:on?"var(--c-primary)":"var(--c-light)",color:on?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                        <span style={{width:20,height:20,borderRadius:99,background:on?"rgba(255,255,255,0.25)":"var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900}}>{(st.name||"?")[0]}</span>
                        {st.name}
                      </button>;
                    })}
                  </div>
                  <div style={{textAlign:"center",padding:"14px 8px",background:"var(--c-lighter)",borderRadius:16}}>
                    <p style={{fontSize:18,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{s.name}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{from + " ～ " + to + " · " + (s.jobType === "fulltime" ? "全職" : "兼職")}</p>
                  </div>
                  {/* Pay period — one month stepper. Pay is monthly; a custom
                      range stays available behind 自訂 (the old 今日 / 昨天 chips
                      made a "payslip" for a single day). */}
                  {(() => {
                    const today = dateNow();
                    const anchor = new Date((from || today).slice(0, 7) + "-01T00:00:00");
                    const monthRange = (d) => {
                      const y = d.getFullYear(), mo = d.getMonth();
                      const ym = y + "-" + String(mo + 1).padStart(2, "0");
                      const last = String(new Date(y, mo + 1, 0).getDate()).padStart(2, "0");
                      return { from: ym + "-01", to: ym === today.slice(0, 7) ? today : ym + "-" + last, label: `${y}年${mo + 1}月` };
                    };
                    const cur = monthRange(anchor);
                    const isWholeMonth = from === cur.from && to === cur.to;
                    const custom = !!payslipSheet.customRange || !isWholeMonth;
                    const go = (n) => { const r = monthRange(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1)); setPayslipSheet(f => ({ ...f, from: r.from, to: r.to, customRange: false })); };
                    const nextIsFuture = monthRange(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)).from > today;
                    const stepBtn = (disabled) => ({ width:38, height:38, borderRadius:12, border:"1px solid #E5E7EB", background:"#fff", color: disabled ? "#D1D5DB" : "#374151", fontSize:18, fontWeight:900, cursor: disabled ? "default" : "pointer", flexShrink:0 });
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <button onClick={() => go(-1)} aria-label="上一個月" style={{...stepBtn(false),display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronLeft size={18} color="#374151" strokeWidth={3}/></button>
                          <div style={{flex:1,minWidth:0,textAlign:"center",fontSize:15,fontWeight:900,color:"#111827"}}>{custom ? `${from} ～ ${to}` : cur.label}</div>
                          <button onClick={() => !nextIsFuture && go(1)} disabled={nextIsFuture} aria-label="下一個月" style={{...stepBtn(nextIsFuture),display:"flex",alignItems:"center",justifyContent:"center"}}><ChevronRight size={18} color={nextIsFuture ? "#D1D5DB" : "#374151"} strokeWidth={3}/></button>
                          <button onClick={() => custom ? go(0) : setPayslipSheet(f => ({ ...f, customRange: true }))} style={{padding:"0 12px",height:38,borderRadius:12,border:"1px solid #E5E7EB",background: custom ? "#111827" : "#fff",color: custom ? "#fff" : "#374151",fontSize:12,fontWeight:800,cursor:"pointer",flexShrink:0}}>{custom ? "按月" : "自訂"}</button>
                        </div>
                        {custom && (
                          <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:8}}>
                            <Input type="date" value={from} onChange={e => setPayslipSheet(f => ({...f, from: e.target.value, customRange: true}))} />
                            <Input type="date" value={to} onChange={e => setPayslipSheet(f => ({...f, to: e.target.value, customRange: true}))} />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Daily wage-log breakdown (auto-summed from 每日入數) */}
                  {s.jobType === "parttime" && (() => {
                    const settleMode = s.settleMode || "daily";
                    const paidDailyEntries = (wage.entries || []).filter(r => r && r.paidMethod);
                    const paidDailyTotal = paidDailyEntries.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
                    const unpaidDailyEntries = (wage.entries || []).filter(r => r && !r.paidMethod);
                    const unpaidDailyTotal = unpaidDailyEntries.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
                    return (
                    <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:14,padding:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
                        <p style={{fontSize:11,fontWeight:800,color:"#92400E",margin:0}}>每日工資紀錄 · {workDays} 日 / {workHours}h / ${hPay.toLocaleString()}</p>
                        <span style={{fontSize:10,fontWeight:900,padding:"2px 8px",borderRadius:99,background: settleMode === "monthly" ? "#DBEAFE" : "#FED7AA", color: settleMode === "monthly" ? "#1E40AF" : "#9A3412"}}>{settleMode === "monthly" ? "月結" : "日結"}</span>
                      </div>
                      {/* Paid-daily tally — surface how much has already been
                          settled per-day so the owner doesn't double-pay at
                          month-end. Was missing entirely; owners had to
                          mentally sum the green check marks below. */}
                      {(paidDailyTotal > 0 || unpaidDailyTotal > 0) && (
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8,padding:"6px 8px",background:"#fff",border:"1px dashed #FDE68A",borderRadius:10}}>
                          <div>
                            <p style={{fontSize:10,color:"#16A34A",margin:0,fontWeight:800}}>已付日結</p>
                            <p style={{fontSize:13,color:"#16A34A",margin:"2px 0 0",fontWeight:900}}>${paidDailyTotal.toLocaleString()} · {paidDailyEntries.length} 日</p>
                          </div>
                          <div>
                            <p style={{fontSize:10,color:"#B45309",margin:0,fontWeight:800}}>未付（{settleMode === "monthly" ? "月結時支付" : "尚待出薪"}）</p>
                            <p style={{fontSize:13,color:"#B45309",margin:"2px 0 0",fontWeight:900}}>${unpaidDailyTotal.toLocaleString()} · {unpaidDailyEntries.length} 日</p>
                          </div>
                        </div>
                      )}
                      {wage.entries.length === 0 && <p style={{fontSize:11,color:"#92400E",opacity:0.75,fontWeight:700,margin:0}}>此期間未有紀錄。到員工頁「{settleMode === "monthly" ? "記錄工時" : "紀錄今日薪金"}」記錄。</p>}
                      {wage.entries.map((r, i) => {
                        const isPaid = !!r.paidMethod;
                        return (
                        <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:700,color:"#92400E",padding:"5px 0",borderTop:i?"1px dashed #FDE68A":"none",alignItems:"center",gap:8}}>
                          <span>{isPaid ? "✅ " : "⏳ "}{r.date}{r.note ? " · " + r.note : ""}</span>
                          <span>{(Number(r.hours)||0)}h · ${Math.round(Number(r.amount)||0).toLocaleString()}</span>
                        </div>);
                      })}
                    </div>
                    );
                  })()}
                  {/* Earnings breakdown — only lines that carry money, grouped
                      收入 → 合計應付 → 扣減 → 實付. Orders / revenue are the
                      commission BASIS, shown as a caption, not a pay line. */}
                  {(() => {
                    const line = (label, value, opts = {}) => (
                      <div key={opts.k || label} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"6px 0",fontSize:13,fontWeight:700,color:opts.color||"#4A4A68",borderTop:opts.first?"none":"1px dashed var(--c-light)"}}>
                        <span style={{minWidth:0}}>{label}{opts.sub && <span style={{display:"block",fontSize:11,fontWeight:600,color:"#8A8A9E",marginTop:2}}>{opts.sub}</span>}</span>
                        <span style={{flexShrink:0,fontVariantNumeric:"tabular-nums"}}>{value}</span>
                      </div>
                    );
                    const money = (n) => "$" + Math.round(n).toLocaleString();
                    const prorated = s.jobType === "fulltime" && slip.base.employedDays > 0 && slip.base.amount !== Math.round(Number(s.baseSalary) || 0);
                    const earnings = [];
                    if (s.jobType === "fulltime") earnings.push(line("底薪", money(basePay), { first: true, sub: prorated ? `按受僱日數計（${slip.base.employedDays} 日）· 月薪 ${money(Number(s.baseSalary)||0)}` : null }));
                    else earnings.push(line("兼職工資", money(hPay), { first: true, sub: `${workHours}h · ${workDays} 日` }));
                    if (svcComm > 0 || rangeApts.length > 0) earnings.push(line(`服務抽成${commLabel ? "（" + commLabel + "）" : ""}`, "+" + money(svcComm), { color: svcComm > 0 ? "#047857" : "#4A4A68", sub: `${rangeApts.length} 張完成訂單 · 收費 ${money(orderRev)}` }));
                    if (tipsTotal > 0) earnings.push(line("小費", "+" + money(tipsTotal), { color: "#047857", sub: `${rangeApts.filter(a => (Number(a.tipAmount)||0) > 0).length} 單` }));
                    if (pkgComm > 0) earnings.push(line(`套票銷售抽成（${pkgRate}%）`, "+" + money(pkgComm), { color: "#047857", sub: `售出 ${pkgsSold.length} 張 · ${money(pkgRev)}` }));
                    if (bonusTotal > 0) earnings.push(line("獎金 / 津貼", "+" + money(bonusTotal), { color: "#047857", sub: `${deductions.filter(d => d.kind === "bonus").length} 項` }));
                    return (
                      <div style={{background:"#FFFFFF",borderRadius:16,padding:14,border:"1px solid #E5E7EB"}}>
                        <p style={{fontSize:11,fontWeight:800,color:"#6B7280",margin:"0 0 2px",letterSpacing:0.3}}>收入</p>
                        {earnings}
                        <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 4px",marginTop:6,fontSize:14,fontWeight:800,color:"#1F2937",borderTop:"1px solid #D1D5DB"}}><span>合計應付</span><span style={{fontVariantNumeric:"tabular-nums"}}>{money(gross)}</span></div>
                        {deductTotal > 0 && <>
                          <p style={{fontSize:11,fontWeight:800,color:"#6B7280",margin:"10px 0 2px",letterSpacing:0.3}}>扣減</p>
                          {line("扣減項目", "−" + money(deductTotal), { first: true, color: "#B91C1C", sub: `${deductions.filter(d => d.kind !== "bonus").length} 項` })}
                        </>}
                        <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 2px",marginTop:8,fontSize:17,fontWeight:900,color:"#111827",borderTop:"2px solid #111827"}}><span>實付</span><span style={{fontVariantNumeric:"tabular-nums"}}>{money(net)}</span></div>
                        <p style={{fontSize:11,color:"#6B7280",margin:"8px 0 0",fontWeight:700,lineHeight:1.5}}>※ 未計強積金（MPF）。如員工需要供款，請另行計算及扣除。</p>
                      </div>
                    );
                  })()}
                  {/* Adjustments manager — bonuses (+) and deductions (-).
                      Replaces the old prompt() chain with an inline picker
                      so owners can type / tap on iPad without three modal
                      hops. Kinds with positive sign live in POSITIVE_KINDS. */}
                  {(() => {
                    const ADJ_KINDS = [
                      { k: "bonus",    l: "🎁 獎金 / 津貼",  sign: +1 },
                      { k: "late",     l: "⏰ 遲到罰款",     sign: -1 },
                      { k: "accident", l: "⚠️ 意外 / 賠償", sign: -1 },
                      { k: "uniform",  l: "👕 制服費",      sign: -1 },
                      { k: "advance",  l: "💵 預支",        sign: -1 },
                      { k: "fine",     l: "🚫 罰款",        sign: -1 },
                      { k: "other",    l: "📝 其他扣減",    sign: -1 },
                    ];
                    const labelOf = (k) => ADJ_KINDS.find(x => x.k === k)?.l || k;
                    const signOf  = (k) => ADJ_KINDS.find(x => x.k === k)?.sign || -1;
                    const draft = payslipSheet.adjDraft || { open: false, kind: "bonus", amount: "", note: "" };
                    const setDraft = (patch) => setPayslipSheet(f => ({ ...f, adjDraft: { ...(f.adjDraft || draft), ...patch } }));
                    const submit = async () => {
                      const amt = parseFloat(draft.amount);
                      if (isNaN(amt) || amt <= 0) { toast("請輸入正確金額", "error"); return; }
                      try {
                        await addStaffDeduction({ tenantId: user.tenantId, staffId: s.id, date: dateNow(), kind: draft.kind, amount: amt, note: draft.note || "", createdBy: user.id });
                        const ds = await fetchStaffDeductions(user.tenantId, { from, to, staffId: s.id });
                        setPayslipSheet(f => ({ ...f, deductions: ds, adjDraft: null }));
                        toast(signOf(draft.kind) > 0 ? "已加獎金 +$" + amt : "已加扣減 -$" + amt);
                      } catch (e) { toast(e?.message || "失敗", "error"); }
                    };
                    return (
                      <div style={{background:"#FFFDFD",borderRadius:14,border:"2px solid var(--c-light)",padding:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0}}>薪金調整（獎金 / 扣減）</p>
                          {!draft.open && <Btn size="sm" variant="secondary" onClick={() => setDraft({ open: true })}>+ 新增</Btn>}
                        </div>
                        {draft.open && (
                          <div style={{background:"var(--c-lighter)",borderRadius:12,padding:12,marginBottom:10,display:"flex",flexDirection:"column",gap:8}}>
                            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                              {ADJ_KINDS.map(opt => (
                                <button key={opt.k} onClick={() => setDraft({ kind: opt.k })} style={{padding:"6px 10px",borderRadius:99,fontSize:12,fontWeight:800,border:"1.5px solid " + (draft.kind === opt.k ? "var(--c-primary)" : "var(--c-light)"),background:draft.kind === opt.k ? "var(--c-primary)" : "#fff",color:draft.kind === opt.k ? "#fff" : "#4A4A68",cursor:"pointer"}}>{opt.l}</button>
                              ))}
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:"110px 1fr",gap:8}}>
                              <Input type="number" placeholder="金額" value={draft.amount} onChange={e => setDraft({ amount: e.target.value })} />
                              <Input placeholder="備註（選填）" value={draft.note || ""} onChange={e => setDraft({ note: e.target.value })} />
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <Btn size="sm" variant="secondary" onClick={() => setPayslipSheet(f => ({ ...f, adjDraft: null }))} style={{flex:1}}>取消</Btn>
                              <Btn size="sm" onClick={submit} style={{flex:2}}>{signOf(draft.kind) > 0 ? "+ 加獎金" : "− 加扣減"}</Btn>
                            </div>
                          </div>
                        )}
                        {deductions.length === 0 && <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:0,padding:"6px 0"}}>未有調整項目</p>}
                        {deductions.map(d => {
                          const sign = signOf(d.kind);
                          return (
                            <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderTop:"1px dashed var(--c-light)"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{labelOf(d.kind) + (d.note ? " · " + d.note : "")}</p>
                                <p style={{fontSize:10,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{d.date}</p>
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <span style={{fontSize:13,fontWeight:900,color:sign > 0 ? "#16A34A" : "#EF4444"}}>{sign > 0 ? "+" : "-"}${d.amount}</span>
                                <button onClick={async () => {
                                  if (!window.confirm(i18nT("tx.delDeductConfirm"))) return;
                                  try {
                                    await deleteStaffDeduction(d.id);
                                    const ds = await fetchStaffDeductions(user.tenantId, { from, to, staffId: s.id });
                                    setPayslipSheet(f => ({ ...f, deductions: ds }));
                                    toast(i18nT("toast.deleted"));
                                  } catch (e) { toast(e?.message || "失敗", "error"); }
                                }} title="刪除" style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"4px 6px",cursor:"pointer"}}><Trash2 size={12} color="#EF4444" strokeWidth={2.5}/></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* Sold packages list */}
                  {pkgsSold.length > 0 && (
                    <details style={{background:"#FFFDFD",borderRadius:14,border:"2px solid var(--c-light)",padding:"10px 12px"}}>
                      <summary style={{fontSize:12,fontWeight:800,color:"#4A4A68",cursor:"pointer"}}>📦 套票銷售明細（{pkgsSold.length} 張）</summary>
                      <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4}}>
                        {pkgsSold.map(c => {
                          const def = servicePackages.find(p => p.id === c.packageId);
                          const cust = state.users[c.customerId];
                          return (
                            <div key={c.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#8A8A9E",fontWeight:700,padding:"4px 0"}}>
                              <span>{(c.purchasedAt||"").slice(0,10) + " · " + (cust?.name || "") + " · " + (def?.name || "")}</span>
                              <span>${c.amountPaid || 0}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                  {/* If this period is already paid → show a settled panel
                      instead of the form. Otherwise show the payout form. */}
                  {paidThis ? (
                    <div style={{background:"#F0FDF4",borderRadius:14,border:"2px solid #BBF7D0",padding:14}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                        <div style={{minWidth:0}}>
                          <p style={{fontSize:14,fontWeight:900,color:"#166534",margin:0,display:"flex",alignItems:"center",gap:6}}><Check size={16} strokeWidth={3.5}/> 已出薪</p>
                          <p style={{fontSize:12,color:"#166534",opacity:0.85,margin:"4px 0 0",fontWeight:700}}>{paidThis.paidAt + " · " + methodLabel(paidThis.method) + (paidThis.note ? " · " + paidThis.note : "")}</p>
                          <p style={{fontSize:11,color:"#166534",opacity:0.6,margin:"2px 0 0",fontWeight:700}}>{"期間 " + (paidThis.from||"—") + " ~ " + (paidThis.to||"—")}</p>
                        </div>
                        <span style={{fontSize:22,fontWeight:900,color:"#16A34A",whiteSpace:"nowrap"}}>{"$" + (Number(paidThis.amount)||0).toLocaleString()}</span>
                      </div>
                      <button onClick={() => removePayout(paidThis)} style={{marginTop:10,background:"#fff",border:"1.5px solid #BBF7D0",borderRadius:10,padding:"8px 12px",fontSize:12,fontWeight:800,color:"#166534",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}><Trash2 size={13} strokeWidth={2.5}/> 作廢出薪</button>
                    </div>
                  ) : (
                    <div style={{background:"#F0FDF4",borderRadius:14,border:"2px solid #BBF7D0",padding:12}}>
                      {partialClashes.length > 0 && (
                        <div style={{background:"#FEF3C7",border:"1.5px solid #FDE68A",borderRadius:10,padding:"9px 11px",marginBottom:10}}>
                          <p style={{fontSize:12,fontWeight:900,color:"#92400E",margin:0,display:"flex",alignItems:"center",gap:6}}>⚠️ 此期間已有重疊出薪</p>
                          {partialClashes.slice().sort((a,b) => (a.from||"").localeCompare(b.from||"")).map(p => (
                            <p key={p.at} style={{fontSize:11,color:"#92400E",opacity:0.9,margin:"4px 0 0",fontWeight:700}}>{(p.from||"—") + " ~ " + (p.to||"—") + "｜$" + (Number(p.amount)||0).toLocaleString() + "（" + (p.paidAt||"?") + (p.note ? "，"+p.note : "") + "）"}</p>
                          ))}
                          <p style={{fontSize:10.5,color:"#92400E",opacity:0.75,margin:"6px 0 0",fontWeight:700}}>只補未出部分，避免重覆出薪。</p>
                        </div>
                      )}
                      <p style={{fontSize:13,fontWeight:800,color:"#166534",margin:"0 0 10px"}}>💸 確認出薪</p>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                        {PAY_METHODS.map(([v,l]) => {
                          const on = payMethod === v;
                          return <button key={v} onClick={() => setPayslipSheet(f => ({...f, payMethod: v}))} style={{padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:800,border:"none",background:on?"#16A34A":"#fff",color:on?"#fff":"#166534",cursor:"pointer",boxShadow:on?"none":"inset 0 0 0 1.5px #BBF7D0"}}>{l}</button>;
                        })}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                        <div>
                          <p style={{fontSize:10,color:"#166534",margin:"0 0 3px",fontWeight:700,opacity:0.8}}>出薪日期</p>
                          <input type="date" value={payDate} onChange={e => setPayslipSheet(f => ({...f, payDate: e.target.value}))} style={{width:"100%",border:"1.5px solid #BBF7D0",borderRadius:10,padding:"9px 10px",fontSize:13,fontWeight:800,outline:"none",color:"#166534",background:"#fff",boxSizing:"border-box"}} />
                        </div>
                        <div>
                          <p style={{fontSize:10,color:"#166534",margin:"0 0 3px",fontWeight:700,opacity:0.8}}>金額 $（預設實付）</p>
                          <input type="number" inputMode="numeric" value={payAmt} onChange={e => setPayslipSheet(f => ({...f, payAmt: e.target.value === "" ? 0 : Number(e.target.value)}))} style={{width:"100%",border:"1.5px solid #BBF7D0",borderRadius:10,padding:"9px 10px",fontSize:14,fontWeight:900,outline:"none",color:"#166534",background:"#fff",textAlign:"right",boxSizing:"border-box"}} />
                        </div>
                      </div>
                      <input value={payslipSheet.payNote || ""} onChange={e => setPayslipSheet(f => ({...f, payNote: e.target.value}))} placeholder="備註（選填，例：5 月人工）" style={{width:"100%",border:"1.5px solid #BBF7D0",borderRadius:10,padding:"9px 10px",fontSize:12,fontWeight:700,outline:"none",color:"#166534",background:"#fff",boxSizing:"border-box",marginBottom:8}} />
                      <Btn variant="success" style={{width:"100%"}} onClick={confirmPayout}><Check size={15} strokeWidth={3}/> 確認出薪 ${Math.round(Number(payAmt)||0).toLocaleString()}（{methodLabel(payMethod)}）</Btn>
                    </div>
                  )}
                  {/* Payout history */}
                  {payouts.length > 0 && (
                    <div style={{background:"#FFFDFD",borderRadius:14,border:"2px solid var(--c-light)",padding:12}}>
                      <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>已出薪紀錄（{payouts.length}）</p>
                      {payouts.map(p => (
                        <div key={p.id || p.at} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderTop:"1px dashed var(--c-light)",opacity:p.voidedAt?0.55:1}}>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0,textDecoration:p.voidedAt?"line-through":"none"}}>
                              {p.kind === "adjustment" && <span style={{fontSize:10,fontWeight:900,color:"#B45309",background:"#FEF3C7",borderRadius:6,padding:"1px 6px",marginRight:6}}>補發 / 調整</span>}
                              {p.paidAt + " · " + methodLabel(p.method) + (p.note ? " · " + p.note : "")}
                            </p>
                            <p style={{fontSize:10,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{"期間 " + (p.from||"—") + " ~ " + (p.to||"—")}</p>
                            {p.voidedAt && <p style={{fontSize:10,color:"#DC2626",margin:"2px 0 0",fontWeight:800}}>{"已作廢 · " + String(p.voidedAt).slice(0,10) + (p.voidReason ? " · " + p.voidReason : "")}</p>}
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:13,fontWeight:900,color:p.voidedAt?"#9CA3AF":"#16A34A",textDecoration:p.voidedAt?"line-through":"none"}}>{"$" + (Number(p.amount)||0).toLocaleString()}</span>
                            {!p.voidedAt && <button onClick={() => removePayout(p)} title="作廢（保留紀錄）" style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"4px 8px",cursor:"pointer",fontSize:11,fontWeight:800,color:"#EF4444"}}>作廢</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{display:"flex",gap:8}}>
                    <Btn variant="secondary" style={{flex:1}} onClick={() => window.print()}>🖨️ 列印 / PDF</Btn>
                    <Btn style={{flex:1}} onClick={exportCsv}><Save size={14} strokeWidth={2.5}/> 匯出 CSV</Btn>
                  </div>
                </div>
              );
            })()}
          </BottomSheet>
        </div>}

        {tab === "crm" && <>
          {/* Pending customer claim requests — approve to merge their old records */}
          {pendingClaims.length > 0 && (
            <div style={{background:"#FFF7ED",border:"1.5px solid #FED7AA",borderRadius:16,padding:"12px 14px",marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,margin:"0 0 8px"}}>
                <p style={{fontSize:14,fontWeight:900,color:"#9A3412",margin:0,display:"flex",alignItems:"center",gap:6}}>{zhEn("🔗 認領請求 (", "🔗 Claim requests (")}{pendingClaims.length})</p>
                {/* Configure how a merge handles stamps / reward vouchers before
                    approving — jumps to the 積分 subtab's merge toggle. */}
                <button onClick={() => jumpToSetting("points#rules")} title={T("合併設定：印花／獎勵券點處理","Merge settings: stamps / vouchers","統合設定")} style={{display:"inline-flex",alignItems:"center",gap:4,background:"#fff",border:"1.5px solid #FED7AA",borderRadius:99,padding:"5px 10px",fontSize:11,fontWeight:800,color:"#9A3412",cursor:"pointer",flexShrink:0}}>
                  <Settings size={13} strokeWidth={2.5} /> {T("合併設定","Settings","設定")}
                </button>
              </div>
              <p style={{fontSize:11,fontWeight:700,color:"#B45309",margin:"0 0 10px",lineHeight:1.5}}>{zhEn("以下客戶用相同電話註冊咗帳戶。核實係本人後，按「合併」會將佢哋喺你舖頭嘅舊寵物、預約、套票同積分連結到佢嘅新帳戶。", "These customers signed up with a phone number that matches an existing record. After you confirm it's really them, tap \"Merge\" to link their old pets, bookings, packages and points to their new account.")}</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {pendingClaims.map(c => {
                  const targets = Array.isArray(c.targetNames) ? c.targetNames : [];
                  const pets    = Array.isArray(c.petNames)    ? c.petNames    : [];
                  const petShown  = pets.slice(0, 6);
                  const petExtra  = Math.max(0, pets.length - petShown.length);
                  const tgtShown  = targets.slice(0, 3);
                  const tgtExtra  = Math.max(0, targets.length - tgtShown.length);
                  return (
                    <div key={c.id} style={{background:"#fff",borderRadius:14,padding:"12px 14px",display:"flex",flexDirection:"column",gap:10}}>
                      {/* Merge preview: [incoming account] → [target walk-in(s)] */}
                      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                        <div style={{background:"#EFF6FF",border:"1.5px solid #BFDBFE",borderRadius:12,padding:"6px 10px",flex:1,minWidth:130}}>
                          <p style={{fontSize:10,fontWeight:800,color:"#1E40AF",margin:"0 0 2px",letterSpacing:0.3}}>{zhEn("新帳戶", "New account")}</p>
                          <p style={{fontSize:13,fontWeight:900,color:"#1E3A8A",margin:0,lineHeight:1.3}}>{c.requesterName || zhEn("（未命名）", "(unnamed)")}</p>
                          <p style={{fontSize:11,fontWeight:700,color:"#3B82F6",margin:"2px 0 0"}}>📞 {c.phone}</p>
                        </div>
                        <div style={{color:"#F59E0B",fontSize:20,fontWeight:900,flexShrink:0}}>→</div>
                        <div style={{background:"#F0FDF4",border:"1.5px solid #A7F3D0",borderRadius:12,padding:"6px 10px",flex:1,minWidth:130}}>
                          <p style={{fontSize:10,fontWeight:800,color:"#166534",margin:"0 0 2px",letterSpacing:0.3}}>{zhEn("合併到 · 舊客紀錄 (", "Merge into · existing records (")}{c.walkinCount})</p>
                          {tgtShown.length === 0 ? (
                            <p style={{fontSize:12,fontWeight:800,color:"#166534",margin:0,fontStyle:"italic"}}>{zhEn("（無姓名嘅 walk-in 紀錄）", "(unnamed walk-in record)")}</p>
                          ) : (
                            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                              {tgtShown.map((n, i) => (
                                <span key={i} style={{fontSize:12,fontWeight:900,color:"#166534",background:"#DCFCE7",padding:"2px 8px",borderRadius:99}}>{n}</span>
                              ))}
                              {tgtExtra > 0 && (
                                <span style={{fontSize:11,fontWeight:800,color:"#166534",background:"#DCFCE7",padding:"2px 8px",borderRadius:99}}>+{tgtExtra}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Pets that will link to the incoming account */}
                      {pets.length > 0 && (
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>{zhEn("🐾 寵物：", "🐾 Pets: ")}</span>
                          {petShown.map((n, i) => (
                            <span key={i} style={{fontSize:12,fontWeight:800,color:"#4A4A68",background:"#FFF7ED",border:"1px solid #FED7AA",padding:"2px 8px",borderRadius:99}}>{n}</span>
                          ))}
                          {petExtra > 0 && (
                            <span style={{fontSize:11,fontWeight:800,color:"#9A3412",background:"#FFF7ED",border:"1px solid #FED7AA",padding:"2px 8px",borderRadius:99}}>+{petExtra}</span>
                          )}
                        </div>
                      )}
                      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                        <Btn size="sm" variant="secondary" onClick={async () => { try { await rejectCustomerClaim(c.id); await refreshClaims(); } catch (e) { toast(e?.message || zhEn("操作失敗", "Action failed"), "error"); } }}>{zhEn("拒絕", "Reject")}</Btn>
                        <Btn size="sm" onClick={async () => {
                          const preview = (targets.length ? targets.slice(0, 3).join("、") + (tgtExtra ? zhEn(` 等 ${tgtExtra + tgtShown.length} 位`, ` and ${tgtExtra + tgtShown.length} more`) : "") : zhEn("walk-in 紀錄", "walk-in record"));
                          if (!window.confirm(zhEn(`確定將「${c.requesterName || c.phone}」合併到 ${preview} 嘅舊紀錄？此操作不可撤銷。`, `Merge "${c.requesterName || c.phone}" into ${preview}'s existing records? This cannot be undone.`))) return;
                          try { const r = await approveCustomerClaim(c.id); toast(zhEn(`已合併 ✓（${r?.pets||0} 寵物 · ${r?.appointments||0} 預約）`, `Merged ✓ (${r?.pets||0} pets · ${r?.appointments||0} bookings)`)); await refreshClaims(); }
                          catch (e) { toast(e?.message || zhEn("合併失敗", "Merge failed"), "error"); }
                        }} style={{background:"#10B981",color:"#fff"}}>{zhEn("合併", "Merge")}</Btn>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:8,marginBottom:16}}>
            <div style={{position:"relative",flex:1}}>
              <Search size={18} strokeWidth={2.5} style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#AFAFC4"}} />
              <input style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:20,padding:"14px 14px 14px 44px",fontSize:15,fontWeight:600,boxSizing:"border-box",outline:"none",background:"#FFFDFD",color:"#4A4A68",transition:"border 0.3s"}} placeholder={i18nT("ph.searchCustomer")} value={search} onChange={e => setSearch(e.target.value)} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-border)"} />
            </div>
            {/* Open the new-account modal. Preselects last-6-of-phone as
                the default password; owner can override or just let it
                stand. */}
            <Btn size="sm" onClick={() => setNewAcctForm({ name:"", email:"", phone:"", countryCode:"+852", password:"" })} style={{background:"var(--c-primary)",color:"#fff",whiteSpace:"nowrap",padding:"12px 14px",flexShrink:0}}>
              <UserPlus size={14} strokeWidth={2.5} /> {zhEn("新增", "Add")}
            </Btn>
            <Btn size="sm" variant="ghost" onClick={() => setImportSheet({ step: "upload", rows: [], summary: null })} style={{whiteSpace:"nowrap",padding:"12px 14px",flexShrink:0,background:"#FFFAFC",border:"2px solid var(--c-light)",color:"#4A4A68"}} title={zhEn("從 CSV 批量匯入客戶名單", "Bulk-import customers from CSV")}>
              {zhEn("📥 匯入", "📥 Import")}
            </Btn>
          </div>
          {filteredCusts.length === 0 && <EmptyState icon={Users} title={zhEn("找不到客戶", "Customer not found")} />}
          {filteredCusts.map(c => { const cu = state.users[c.id];
            // Avatar resolution: profile.avatar > first pet's first photo > initial.
            const firstPet = (cu?.pets || []).map(pid => state.pets[pid]).find(Boolean);
            const avatarUrl = cu?.avatar || firstPet?.photos?.[0]?.url || null;
            return (
            <Card key={c.id} style={{padding:20,marginBottom:12,cursor:"pointer",transition:"all 0.2s"}} onClick={() => { setCrmUser(c.id); setCrmTab("info"); }}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:16}}>
                  <div style={{width:52,height:52,background:"linear-gradient(135deg,var(--c-light),var(--c-grad2))",borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"var(--c-primary)",fontSize:22,overflow:"hidden"}}>
                    {avatarUrl ? <img src={avatarUrl} alt={c.name} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : c.name[0]}
                  </div>
                  <div>
                    <p style={{fontWeight:900,margin:"0 0 2px",fontSize:17,color:"#4A4A68"}}>{c.name}</p>
                    {cu?.email && !cu.email.endsWith("@petos.local") && (
                      <p style={{fontSize:11,color:"#AFAFC4",margin:"0 0 4px",fontWeight:700,wordBreak:"break-all"}}>{cu.email}</p>
                    )}
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{(c.countryCode ? c.countryCode + " " : "") + c.phone + zhEn(" · 錢包 $", " · Wallet $") + (cu?.wallet||0) + " · " + (cu?.points||0) + "pt"}</p>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end",maxWidth:120}}>
                  {(c.tags||[]).slice(0,2).map(t => <Badge key={t} color="var(--c-primary)" bg="var(--c-light)">{t}</Badge>)}
                </div>
              </div>
            </Card>
          ); })}
          {/* ── NEW: Owner-creates-customer-account modal ────────────
              Collects name + email + phone + (optional) password and
              calls the create-customer-account Edge Function. On
              success swaps in a "credentials to share" panel so the
              owner can copy the email + password and read them out to
              the customer (or paste into WhatsApp). */}
          <BottomSheet open={!!newAcctForm || !!createdAccount} onClose={() => { setNewAcctForm(null); setCreatedAccount(null); }} title={createdAccount ? zhEn("帳戶已建立 ✅", "Account created ✅") : (newAcctForm?.mode === "account" ? zhEn("新增客戶帳戶", "New customer account") : zhEn("新增客戶", "New customer"))}>
            {createdAccount ? (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{background:"#F0FDF4",borderRadius:20,padding:16,border:"2px solid #BBF7D0"}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#166534",margin:"0 0 10px"}}>{zhEn("請告訴客戶以下登入資料：", "Give the customer these login details:")}</p>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1px solid #BBF7D0"}}>
                      <p style={{fontSize:11,fontWeight:700,color:"#166534",margin:"0 0 4px",opacity:0.7}}>{zhEn("電郵", "Email")}</p>
                      <p style={{fontSize:14,fontWeight:800,color:"#166534",margin:0,wordBreak:"break-all",fontFamily:"ui-monospace,SF Mono,monospace"}}>{createdAccount.email}</p>
                    </div>
                    <div style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1px solid #BBF7D0"}}>
                      <p style={{fontSize:11,fontWeight:700,color:"#166534",margin:"0 0 4px",opacity:0.7}}>{zhEn("初始密碼", "Initial password")}</p>
                      <p style={{fontSize:18,fontWeight:900,color:"#166534",margin:0,fontFamily:"ui-monospace,SF Mono,monospace",letterSpacing:2}}>{createdAccount.presetPassword}</p>
                    </div>
                  </div>
                  <p style={{fontSize:11,fontWeight:700,color:"#166534",margin:"10px 0 0",opacity:0.85,lineHeight:1.5}}>{zhEn("客戶首次登入後系統會要求佢設定新密碼 🔒", "The customer will be asked to set a new password on first login 🔒")}</p>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <Btn variant="secondary" onClick={() => {
                    const text = zhEn(`登入電郵：${createdAccount.email}
初始密碼：${createdAccount.presetPassword}`, `Login email: ${createdAccount.email}
Initial password: ${createdAccount.presetPassword}`);
                    try { navigator.clipboard.writeText(text); toast(zhEn("已複製登入資料", "Login details copied")); } catch { toast(i18nT("toast.copyFail"), "error"); }
                  }} style={{flex:1}}><Copy size={14} /> {zhEn("複製", "Copy")}</Btn>
                  <Btn onClick={() => { setCreatedAccount(null); setNewAcctForm(null); reload && reload(user); }} style={{flex:1}}>{zhEn("完成", "Done")}</Btn>
                </div>
              </div>
            ) : newAcctForm && newAcctForm.mode !== "account" ? (
              /* QUICK ADD (default) — a customer record, NOT a login account.
                 Name + phone only, via create-walkin-customer (synthetic email +
                 pending_claim, same as the walk-in flow). The owner asked for
                 exactly this: 「add customer should not equal to create an
                 account for that customer」. Account creation stays available
                 behind the switch link below. */
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{background:"#F0FDF4",borderRadius:16,padding:"12px 14px",border:"2px solid #BBF7D0",fontSize:12,fontWeight:700,color:"#166534",lineHeight:1.5}}>
                  {zhEn("💡 快速新增 — 只需姓名＋電話，毋須開登入帳戶。客戶日後想用 app，可自行認領或由你再建立帳戶。", "💡 Quick add — just a name and phone, no login account needed. If the customer wants to use the app later, they can claim the record or you can create an account for them.")}
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("姓名 *", "Name *")}</p>
                  <input value={newAcctForm.name} onChange={e => setNewAcctForm(f => ({ ...f, name:e.target.value }))} placeholder={zhEn("客戶姓名", "Customer name")} style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("電話 *", "Phone *")}</p>
                  <div style={{display:"flex",gap:8}}>
                    <select value={newAcctForm.countryCode} onChange={e => setNewAcctForm(f => ({ ...f, countryCode:e.target.value }))} style={{border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 10px",fontSize:14,fontWeight:700,outline:"none",color:"#4A4A68",background:"#fff"}}>
                      <option value="+852">+852 HK</option>
                      <option value="+886">+886 TW</option>
                      <option value="+86">+86 CN</option>
                      <option value="+65">+65 SG</option>
                      <option value="+60">+60 MY</option>
                      <option value="+1">+1 US</option>
                      <option value="+44">+44 UK</option>
                      <option value="+61">+61 AU</option>
                    </select>
                    <input value={newAcctForm.phone} onChange={e => { const parsed = parseCopiedPhone(e.target.value); setNewAcctForm(f => ({ ...f, phone: parsed.digits, ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {}) })); }} placeholder={zhEn("只輸入數字", "Digits only")} style={{flex:1,border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
                  </div>
                </div>
                <Btn onClick={async () => {
                  if (!newAcctForm.name.trim()) { toast(zhEn("請輸入姓名", "Please enter a name"), "error"); return; }
                  const phoneV = validatePhone(newAcctForm.countryCode, newAcctForm.phone);
                  if (!phoneV.valid) { toast(phoneV.error, "error"); return; }
                  setNewAcctBusy(true);
                  try {
                    const res = await createWalkinCustomer({
                      tenantId: user.tenantId,
                      tenantSlug: tenant?.bookingSlug,
                      name: newAcctForm.name.trim(),
                      phone: normalizePhoneDigits(newAcctForm.phone),
                    });
                    if (!res?.userId) throw new Error(zhEn("伺服器回應異常", "Unexpected server response"));
                    toast(res.linked ? (zhEn("已連結現有客戶：", "Linked to existing customer: ") + (res.name || newAcctForm.name)) : zhEn("已新增客戶 ✓", "Customer added ✓"), "success");
                    setNewAcctForm(null);
                    reload && reload(user);
                  } catch (e) {
                    toast(e?.message || zhEn("新增客戶失敗", "Failed to add customer"), "error");
                  } finally {
                    setNewAcctBusy(false);
                  }
                }} disabled={newAcctBusy} style={{width:"100%",opacity:newAcctBusy?0.6:1}}>
                  {newAcctBusy ? zhEn("新增中...", "Adding...") : zhEn("➕ 新增客戶", "➕ Add customer")}
                </Btn>
                <button onClick={() => setNewAcctForm(f => ({ ...f, mode:"account" }))} style={{background:"none",border:"none",color:"var(--c-primary)",fontSize:12.5,fontWeight:800,cursor:"pointer",padding:4}}>
                  {zhEn("需要幫客戶開登入帳戶（電郵＋密碼）？→", "Need to create a login account (email + password) for the customer? →")}
                </button>
              </div>
            ) : newAcctForm && (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{background:"#FFFBEB",borderRadius:16,padding:"12px 14px",border:"2px solid #FEF3C7",fontSize:12,fontWeight:700,color:"#92400E",lineHeight:1.5}}>
                  {zhEn("💡 系統會用以下資料建立完整帳戶。客戶可立即用電郵 + 密碼登入，首次登入後須自設新密碼。", "💡 A full account will be created with these details. The customer can log in right away with email + password and must set a new password on first login.")}
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("姓名 *", "Name *")}</p>
                  <input value={newAcctForm.name} onChange={e => setNewAcctForm(f => ({ ...f, name:e.target.value }))} placeholder={zhEn("客戶姓名", "Customer name")} style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("電郵 *", "Email *")}</p>
                  <input type="email" value={newAcctForm.email} onChange={e => setNewAcctForm(f => ({ ...f, email:e.target.value.trim() }))} placeholder="customer@example.com" style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("電話 *", "Phone *")}</p>
                  <div style={{display:"flex",gap:8}}>
                    <select value={newAcctForm.countryCode} onChange={e => setNewAcctForm(f => ({ ...f, countryCode:e.target.value }))} style={{border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 10px",fontSize:14,fontWeight:700,outline:"none",color:"#4A4A68",background:"#fff"}}>
                      <option value="+852">+852 HK</option>
                      <option value="+886">+886 TW</option>
                      <option value="+86">+86 CN</option>
                      <option value="+65">+65 SG</option>
                      <option value="+60">+60 MY</option>
                      <option value="+1">+1 US</option>
                      <option value="+44">+44 UK</option>
                      <option value="+61">+61 AU</option>
                    </select>
                    <input value={newAcctForm.phone} onChange={e => { const parsed = parseCopiedPhone(e.target.value); setNewAcctForm(f => ({ ...f, phone: parsed.digits, ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {}) })); }} placeholder={zhEn("只輸入數字", "Digits only")} style={{flex:1,border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68"}} />
                  </div>
                </div>
                <div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("初始密碼", "Initial password")} <span style={{fontWeight:700,color:"#AFAFC4",fontSize:11}}>{zhEn("（留空 = 電話最後 6 位）", "(blank = last 6 digits of phone)")}</span></p>
                  <input value={newAcctForm.password} onChange={e => setNewAcctForm(f => ({ ...f, password:e.target.value }))} placeholder={newAcctForm.phone.length >= 6 ? zhEn(`預設：${newAcctForm.phone.slice(-6)}`, `Default: ${newAcctForm.phone.slice(-6)}`) : zhEn("至少 6 位", "At least 6 characters")} style={{width:"100%",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px",fontSize:14,fontWeight:700,boxSizing:"border-box",outline:"none",color:"#4A4A68",fontFamily:"ui-monospace,SF Mono,monospace"}} />
                </div>
                <Btn onClick={async () => {
                  // Client-side validation mirrors server-side checks so
                  // the owner gets immediate feedback instead of waiting
                  // for an Edge Function round-trip.
                  if (!newAcctForm.name.trim()) { toast(zhEn("請輸入姓名", "Please enter a name"), "error"); return; }
                  const emailV = validateEmail(newAcctForm.email);
                  if (!emailV.valid) { toast(emailV.error, "error"); return; }
                  const phoneV = validatePhone(newAcctForm.countryCode, newAcctForm.phone);
                  if (!phoneV.valid) { toast(phoneV.error, "error"); return; }
                  setNewAcctBusy(true);
                  try {
                    const res = await createCustomerAccount({
                      tenantId: user.tenantId,
                      name: newAcctForm.name.trim(),
                      email: emailV.value,
                      phone: newAcctForm.phone,
                      countryCode: newAcctForm.countryCode,
                      password: newAcctForm.password || null,
                    });
                    setCreatedAccount(res);
                    setNewAcctForm(null);
                  } catch (e) {
                    toast(e?.message || zhEn("建立失敗", "Creation failed"), "error");
                  } finally {
                    setNewAcctBusy(false);
                  }
                }} disabled={newAcctBusy} style={{width:"100%",opacity:newAcctBusy?0.6:1}}>
                  {newAcctBusy ? zhEn("建立中...", "Creating...") : zhEn("建立帳戶", "Create account")}
                </Btn>
              </div>
            )}
          </BottomSheet>
          {/* ─── 發送登入資料 ──────────────────────────────────
              Result of sendCustomerLogin. The password is already set, so
              closing this sheet without sending just means the owner taps
              the button again later (which issues a fresh password). */}
          <BottomSheet open={!!loginShare} onClose={() => setLoginShare(null)} title={zhEn("📲 發送登入資料", "📲 Send login details")} z={300}>
            {loginShare && (() => {
              const msg = `${loginShare.name ? loginShare.name + " " : ""}你好！呢度係你喺「${loginShare.shopName}」嘅網上預約帳戶 🐾\n\n` +
                `🔗 登入：${loginShare.loginUrl}\n📱 電話：${loginShare.phone}\n🔑 密碼：${loginShare.password}\n\n` +
                `首次登入後請設定你自己嘅密碼。之後可以隨時網上預約、睇返毛孩紀錄、積分同儲值。`;
              const cc = (String(loginShare.countryCode || "+852").replace(/\D/g, "") || "852");
              const waUrl = "https://wa.me/" + cc + loginShare.phone + "?text=" + encodeURIComponent(msg);
              return (
                <div style={{display:"flex",flexDirection:"column",gap:12,paddingBottom:6}}>
                  <div style={{background:"var(--c-lighter)",border:"2px solid var(--c-border)",borderRadius:14,padding:"12px 14px"}}>
                    <p style={{fontSize:12.5,color:"#4A4A68",margin:0,fontWeight:700,lineHeight:1.65,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{msg}</p>
                  </div>
                  <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={() => toast(zhEn("WhatsApp 已開啟 💬", "WhatsApp opened 💬"))}
                    style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"13px 14px",borderRadius:14,background:"#25D366",color:"#fff",fontWeight:900,fontSize:15,textDecoration:"none"}}>
                    <MessageCircle size={18} strokeWidth={2.6} /> {zhEn("用 WhatsApp 發送", "Send via WhatsApp")}
                  </a>
                  <Btn variant="secondary" onClick={() => {
                    try { navigator.clipboard?.writeText(msg); toast(i18nT("common.copied")); }
                    catch { toast(zhEn("複製失敗", "Copy failed"), "error"); }
                  }} style={{width:"100%"}}>{zhEn("📋 複製訊息", "📋 Copy message")}</Btn>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6}}>
                    {zhEn("※ 密碼已即時生效。客戶首次登入時會被要求改密碼（可按「稍後再改」略過 — 你自己用呢個密碼登入睇客戶畫面都唔會令密碼失效）。再按一次「發送登入資料」會產生新密碼，舊密碼即時失效。", "※ The password is active immediately. The customer will be asked to change it on first login (they can tap \"Later\" to skip — logging in yourself with it to view the customer screen won't invalidate it). Tapping \"Send login details\" again generates a new password and the old one stops working immediately.")}
                  </p>
                </div>
              );
            })()}
          </BottomSheet>
          {/* ─── 重設客戶密碼 ──────────────────────────────────
              Confirm sheet + result sheet, modelled on the
              create-customer-account flow above. Owner triggers, sees
              the new password, copies/shares it with the customer. */}
          <BottomSheet open={!!resetPwSheet || !!resetPwResult} onClose={() => { setResetPwSheet(null); setResetPwResult(null); }} title={resetPwResult ? zhEn("密碼已重設 ✅", "Password reset ✅") : zhEn("重設客戶密碼", "Reset customer password")} z={300}>
            {resetPwResult ? (
              <div style={{display:"flex",flexDirection:"column",gap:14,paddingBottom:6}}>
                <p style={{fontSize:14,color:"#4A4A68",margin:0,fontWeight:700,lineHeight:1.6}}>
                  {zhEn("已為", "Password reset for")} <span style={{fontWeight:900,color:"var(--c-primary)"}}>{resetPwResult.name || zhEn("客戶", "Customer")}</span> {zhEn("重設密碼。請將以下臨時密碼發送給客戶，並提醒佢首次登入時必須改密碼。", ". Send the customer this temporary password and remind them they must change it on first login.")}
                </p>
                <div style={{background:"var(--c-lighter)",border:"2px solid var(--c-border)",borderRadius:14,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                  <div style={{minWidth:0,flex:1}}>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:800}}>{zhEn("臨時密碼", "Temporary password")}</p>
                    <p style={{fontSize:20,fontFamily:"ui-monospace,SF Mono,monospace",color:"#4A4A68",margin:0,fontWeight:900,letterSpacing:1}}>{resetPwResult.presetPassword}</p>
                  </div>
                  <Btn size="sm" onClick={() => {
                    try { navigator.clipboard?.writeText(resetPwResult.presetPassword); toast(i18nT("common.copied")); }
                    catch { toast(zhEn("複製失敗", "Copy failed"), "error"); }
                  }}>{i18nT("common.copy")}</Btn>
                </div>
                <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6}}>
                  {zhEn("※ 客戶下次登入會被強制更改密碼，舊密碼即時失效。", "※ The customer will be forced to change their password at next login; the old password stops working immediately.")}
                </p>
                <Btn onClick={() => { setResetPwSheet(null); setResetPwResult(null); }} style={{width:"100%"}}>{zhEn("完成", "Done")}</Btn>
              </div>
            ) : resetPwSheet && (
              <div style={{display:"flex",flexDirection:"column",gap:14,paddingBottom:6}}>
                <p style={{fontSize:14,color:"#4A4A68",margin:0,fontWeight:700,lineHeight:1.6}}>
                  {zhEn("確定要為", "Reset the password for")} <span style={{fontWeight:900,color:"var(--c-primary)"}}>{resetPwSheet.name || zhEn("此客戶", "this customer")}</span> {zhEn("重設密碼？", " ?")}
                </p>
                <div style={{background:"#FFFBEB",borderRadius:12,padding:"10px 14px",border:"2px solid #FEF3C7"}}>
                  <p style={{fontSize:12,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.6}}>
                    {resetPwSheet.customPassword
                      ? zhEn("將使用你輸入嘅自訂密碼。", "The custom password you entered will be used.")
                      : zhEn("預設密碼 = 客戶電話號碼最後 6 位。", "Default password = last 6 digits of the customer's phone number.")}
                    <br />{zhEn("客戶下次登入會被強制更改密碼。", "The customer will be forced to change their password at next login.")}
                  </p>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <Btn variant="secondary" onClick={() => setResetPwSheet(null)} disabled={resetPwBusy} style={{flex:1}}>{zhEn("取消", "Cancel")}</Btn>
                  <Btn onClick={async () => {
                    setResetPwBusy(true);
                    try {
                      const res = await resetCustomerPassword({
                        tenantId: user.tenantId,
                        userId: resetPwSheet.userId,
                        password: resetPwSheet.customPassword || null,
                      });
                      setResetPwResult({ presetPassword: res.presetPassword, name: resetPwSheet.name });
                      setResetPwSheet(null);
                    } catch (e) {
                      toast(e?.message || zhEn("重設失敗", "Reset failed"), "error");
                    } finally {
                      setResetPwBusy(false);
                    }
                  }} disabled={resetPwBusy} style={{flex:1,opacity:resetPwBusy?0.6:1}}>
                    {resetPwBusy ? zhEn("處理中...", "Processing...") : zhEn("確認重設", "Confirm reset")}
                  </Btn>
                </div>
              </div>
            )}
          </BottomSheet>
          {/* ─── CSV 匯入客戶 ──────────────────────────────────
              Template column order is fixed (10 columns) so the visitor
              doesn't have to map fields — they fill the same template,
              paste into Excel, save as CSV (UTF-8), upload. Breed is
              matched against BREEDS via the loose-match below; unknown
              breeds are kept as free-text so the import never blocks. */}
          <BottomSheet open={!!importSheet} onClose={() => setImportSheet(null)} title={zhEn("📥 匯入客戶名單", "📥 Import customers")}>
            {importSheet && (() => {
              const TEMPLATE_HEADERS = ["客戶姓名","電話","電郵","地址","標籤","寵物名","品種","體重(kg)","類型(dog/cat)","毛長(short/long)"];
              const TEMPLATE_SAMPLE = ["陳大文","91234567","tai@example.com","旺角彌敦道 100 號","VIP,常客","毛毛","貴婦犬","4.5","dog","long"];
              // Reuse the shared breed matcher so import + walk-in use
              // identical heuristics. Returns the canonical zh label as
              // `breed` (so the saved row reads consistently), or falls
              // back to the raw CSV text when no match.
              const matchBreed = (str, type) => {
                if (!str || !String(str).trim()) return { breedId: null, breed: "" };
                const id = inferBreedId(str, type);
                if (id) {
                  const b = getBreedById(id);
                  return { breedId: id, breed: b?.zh || b?.en || String(str).trim(), coatHint: b?.coatHint };
                }
                return { breedId: null, breed: String(str).trim() };
              };
              // Minimal CSV parser — handles quoted fields with commas /
              // doubled-quotes. Enough for Excel "Save as CSV" output.
              const parseCSV = (text) => {
                const rows = [];
                let i = 0, field = "", row = [], inQ = false;
                const t = text.replace(/^﻿/, "");
                while (i < t.length) {
                  const c = t[i];
                  if (inQ) {
                    if (c === '"' && t[i+1] === '"') { field += '"'; i += 2; continue; }
                    if (c === '"') { inQ = false; i++; continue; }
                    field += c; i++; continue;
                  }
                  if (c === '"') { inQ = true; i++; continue; }
                  if (c === ",") { row.push(field); field = ""; i++; continue; }
                  if (c === "\r") { i++; continue; }
                  if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
                  field += c; i++;
                }
                if (field !== "" || row.length) { row.push(field); rows.push(row); }
                return rows.filter(r => r.some(c => c.trim() !== ""));
              };
              const downloadTemplate = () => {
                const csv = [TEMPLATE_HEADERS.join(","), TEMPLATE_SAMPLE.join(",")].join("\n");
                const a = document.createElement("a");
                a.href = "data:text/csv;charset=utf-8,﻿" + encodeURIComponent(csv);
                a.download = "PettoBook_客戶匯入範本.csv";
                a.click();
              };
              const onFile = (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  try {
                    const raw = parseCSV(String(reader.result || ""));
                    if (raw.length < 2) { toast(zhEn("CSV 沒有資料 — 請至少填一行客戶", "The CSV has no data — add at least one customer row"), "error"); return; }
                    const header = raw[0].map(s => s.trim());
                    // Map by first-cell-of-header → column index, so the
                    // template order is the canonical one. If the visitor's
                    // headers don't match we still try positional fallback.
                    const idx = (k) => {
                      const i = header.findIndex(h => h === k);
                      return i >= 0 ? i : TEMPLATE_HEADERS.indexOf(k);
                    };
                    const cols = {
                      name: idx("客戶姓名"), phone: idx("電話"), email: idx("電郵"),
                      address: idx("地址"), tags: idx("標籤"),
                      petName: idx("寵物名"), breed: idx("品種"),
                      weight: idx("體重(kg)"), type: idx("類型(dog/cat)"),
                      coat: idx("毛長(short/long)"),
                    };
                    const existingPhones = new Set(
                      (tenant?.customerIds || [])
                        .map(id => state.users[id]?.phone || "")
                        .map(p => p.replace(/\D+/g, ""))
                        .filter(Boolean)
                    );
                    // Track phones we've already seen within this CSV so
                    // two rows with the same phone in the file flag the
                    // second one as duplicate — otherwise the second
                    // createWalkinCustomer would error mid-import.
                    const seenInCsv = new Set();
                    const parsedRows = raw.slice(1).map((r, ri) => {
                      const get = (k) => (cols[k] >= 0 ? (r[cols[k]] || "").trim() : "");
                      const name = get("name");
                      const phone = get("phone").replace(/\D+/g, "");
                      const type = (get("type").toLowerCase() === "cat") ? "cat" : "dog";
                      const coat = (get("coat").toLowerCase() === "long") ? "long" : "short";
                      const breedRaw = get("breed");
                      const matched = matchBreed(breedRaw, type);
                      const weight = parseFloat(get("weight")) || 0;
                      const issues = [];
                      if (!name) issues.push(zhEn("缺客戶姓名", "Missing customer name"));
                      if (!phone) issues.push(zhEn("缺電話", "Missing phone"));
                      if (phone && existingPhones.has(phone)) issues.push(zhEn("電話已存在", "Phone already exists"));
                      if (phone && seenInCsv.has(phone)) issues.push(zhEn("檔案內電話重覆", "Duplicate phone in file"));
                      if (phone) seenInCsv.add(phone);
                      if (get("petName") && weight <= 0) issues.push(zhEn("寵物體重缺", "Pet weight missing"));
                      return {
                        n: ri + 2, // CSV line number for error display
                        name, phone, email: get("email"), address: get("address"),
                        tags: get("tags").split(/[、,，|]+/).map(s => s.trim()).filter(Boolean),
                        petName: get("petName"), breedRaw,
                        breedId: matched.breedId, breed: matched.breed,
                        coatLength: coat,
                        weight, type, issues,
                      };
                    });
                    setImportSheet(s => ({ ...s, step: "preview", rows: parsedRows }));
                  } catch (err) {
                    toast(zhEn("CSV 解析失敗：", "Failed to parse CSV: ") + (err?.message || err), "error");
                  }
                };
                reader.readAsText(file, "utf-8");
              };
              const importable = importSheet.rows.filter(r => r.issues.length === 0 || (r.issues.length === 1 && r.issues[0] === "電話已存在"));
              const newCount = importSheet.rows.filter(r => !r.issues.length).length;
              const petCount = importSheet.rows.filter(r => !r.issues.length && r.petName).length;
              const doImport = async () => {
                setImportSheet(s => ({ ...s, busy: true }));
                let okCust = 0, okPet = 0, fail = 0;
                // Accumulate inserts then mirror to local state once at
                // the end — avoids 50 partial setState calls when a big
                // CSV is imported, and keeps the new customer / pet
                // visible in the CRM list immediately (instead of
                // waiting for realtime to backfill).
                const newCustEntries = [];   // { id, profile }
                const newPetEntries = [];    // { id, pet, ownerId }
                for (const r of importSheet.rows.filter(x => !x.issues.length)) {
                  try {
                    const res = await createWalkinCustomer({
                      tenantId: tenant.id,
                      tenantSlug: tenant.bookingSlug || tenant.id,
                      name: r.name,
                      phone: r.phone,
                    });
                    const newUserId = res?.userId;
                    if (!newUserId) { fail += 1; continue; }
                    okCust += 1;
                    // Best-effort: patch the extras CSV had (email,
                    // address, tags). RLS may reject — if so we keep the
                    // customer with just name + phone and log the error.
                    const extras = {};
                    if (r.email) extras.email = r.email;
                    if (r.address) extras.address = r.address;
                    if (r.tags?.length) extras.tags = r.tags;
                    if (Object.keys(extras).length) {
                      try { await patchProfile(newUserId, extras); }
                      catch (pe) { console.warn("[import] patchProfile failed:", pe); }
                    }
                    newCustEntries.push({
                      id: newUserId,
                      profile: {
                        id: newUserId, role: "customer", tenantId: tenant.id,
                        name: r.name, phone: r.phone, countryCode: "+852",
                        email: r.email || "", address: r.address || "",
                        tags: r.tags || [],
                        wallet: 0, points: 0, walletTx: [],
                        pets: [], recurringApts: [],
                      },
                    });
                    if (r.petName && r.weight > 0) {
                      const petId = uuid();
                      const newPet = {
                        id: petId,
                        tenantId: tenant.id,
                        ownerId: newUserId,
                        name: r.petName,
                        breed: r.breed || r.breedRaw || "未知",
                        breedId: r.breedId || null,
                        weight: r.weight,
                        type: r.type,
                        coatLength: r.coatLength,
                        allergies: zhEn("無", "None"),
                        photos: [], logs: [],
                      };
                      try {
                        await upsertPet(newPet);
                        okPet += 1;
                        newPetEntries.push({ id: petId, pet: newPet, ownerId: newUserId });
                      } catch (pe) { console.warn("[import] pet failed:", pe); }
                    }
                  } catch (e) {
                    fail += 1;
                    console.warn("[import] customer failed:", e);
                  }
                }
                // Mirror to local state once — same pattern walk-in uses
                // so the CRM list shows the new rows without a reload.
                if (newCustEntries.length || newPetEntries.length) {
                  setState(s => {
                    const users = { ...s.users };
                    for (const e of newCustEntries) {
                      users[e.id] = { ...(users[e.id] || {}), ...e.profile };
                    }
                    const pets = { ...s.pets };
                    for (const e of newPetEntries) {
                      pets[e.id] = e.pet;
                      const owner = users[e.ownerId];
                      if (owner) users[e.ownerId] = { ...owner, pets: [...(owner.pets || []), e.id] };
                    }
                    const tenants = s.tenants.map(t => t.id === tenant.id
                      ? { ...t, customerIds: [...(t.customerIds || []), ...newCustEntries.map(e => e.id).filter(id => !(t.customerIds || []).includes(id))] }
                      : t);
                    return { ...s, users, pets, tenants };
                  });
                }
                setImportSheet(s => ({ ...s, busy: false, summary: { okCust, okPet, fail } }));
                toast(zhEn("已匯入 ", "Imported ") + okCust + zhEn(" 個客戶、", " customers, ") + okPet + zhEn(" 隻毛孩", " pets") + (fail ? zhEn("（", " (") + fail + zhEn(" 個失敗）", " failed)") : ""));
              };

              if (importSheet.summary) {
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:14,padding:"10px 0"}}>
                    <div style={{textAlign:"center"}}>
                      <span style={{fontSize:54}}>🎉</span>
                      <p style={{fontSize:18,fontWeight:900,color:"#10B981",margin:"8px 0"}}>{zhEn("匯入完成", "Import complete")}</p>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                      <div style={{background:"#F0FDF4",border:"1.5px solid #BBF7D0",borderRadius:12,padding:"12px 8px",textAlign:"center"}}>
                        <p style={{fontSize:24,fontWeight:900,color:"#10B981",margin:0}}>{importSheet.summary.okCust}</p>
                        <p style={{fontSize:11,color:"#166534",margin:0,fontWeight:700}}>{zhEn("客戶", "Customer")}</p>
                      </div>
                      <div style={{background:"#EFF6FF",border:"1.5px solid #BFDBFE",borderRadius:12,padding:"12px 8px",textAlign:"center"}}>
                        <p style={{fontSize:24,fontWeight:900,color:"#3B82F6",margin:0}}>{importSheet.summary.okPet}</p>
                        <p style={{fontSize:11,color:"#1E3A8A",margin:0,fontWeight:700}}>{zhEn("毛孩", "Pet")}</p>
                      </div>
                      <div style={{background:importSheet.summary.fail>0?"#FEE2E2":"#FFFDFD",border:"1.5px solid "+(importSheet.summary.fail>0?"#FECACA":"#E5E5EA"),borderRadius:12,padding:"12px 8px",textAlign:"center"}}>
                        <p style={{fontSize:24,fontWeight:900,color:importSheet.summary.fail>0?"#EF4444":"#9CA3AF",margin:0}}>{importSheet.summary.fail}</p>
                        <p style={{fontSize:11,color:importSheet.summary.fail>0?"#991B1B":"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("失敗", "Failed")}</p>
                      </div>
                    </div>
                    <Btn onClick={() => setImportSheet(null)}>{zhEn("關閉", "Close")}</Btn>
                  </div>
                );
              }

              if (importSheet.step === "preview") {
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <div style={{background:"#FFF0F5",border:"1.5px solid #FFD8E8",borderRadius:12,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                      <div>
                        <p style={{fontSize:13,fontWeight:900,color:"#FF4D6D",margin:0}}>{zhEn("共", "Total")} {importSheet.rows.length} {zhEn("行 · 可匯入", " rows · can import ")} {newCount} {zhEn("個客戶（", " customers (")}{petCount} {zhEn("隻毛孩）", " pets)")}</p>
                        {(importSheet.rows.length - newCount) > 0 && (
                          <p style={{fontSize:11,color:"#92400E",margin:"2px 0 0",fontWeight:700}}>{zhEn("⚠ 跳過", "⚠ Skipping")} {importSheet.rows.length - newCount} {zhEn("行（有錯誤或重覆）", " rows (errors or duplicates)")}</p>
                        )}
                      </div>
                      <button onClick={() => setImportSheet({ step:"upload", rows:[], summary:null })} style={{background:"none",border:"1.5px solid #FFE0EC",borderRadius:99,padding:"6px 12px",fontSize:11,fontWeight:800,color:"#FF4D6D",cursor:"pointer"}}>{zhEn("重新上載", "Upload again")}</button>
                    </div>
                    <div style={{maxHeight:340,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                      {importSheet.rows.map((r, i) => {
                        const ok = !r.issues.length;
                        return (
                          <div key={i} style={{padding:"10px 12px",borderRadius:10,border:"1.5px solid " + (ok ? "#A7F3D0" : "#FECACA"),background: ok ? "#F0FDF4" : "#FEF2F2",display:"flex",alignItems:"flex-start",gap:8}}>
                            <span style={{fontSize:14}}>{ok ? "✓" : "⚠"}</span>
                            <div style={{flex:1,minWidth:0,fontSize:12}}>
                              <p style={{margin:0,fontWeight:900,color: ok ? "#065F46" : "#991B1B"}}>
                                {zhEn("行", "rows")} {r.n} · {r.name || zhEn("(無姓名)", "(no name)")} · {r.phone || zhEn("(無電話)", "(no phone)")}
                              </p>
                              {r.petName && (
                                <p style={{margin:"2px 0 0",fontWeight:700,color: ok ? "#047857" : "#7F1D1D",opacity:0.85}}>
                                  🐾 {r.petName} · {r.breed || r.breedRaw || "?"}
                                  {r.breedId ? <span style={{color:"#10B981",fontSize:10,marginLeft:6}}>{zhEn("✓ 已匹配", "✓ Matched")}</span>
                                              : r.breedRaw ? <span style={{color:"#D97706",fontSize:10,marginLeft:6}}>{zhEn("⚠ 用原文 (", "⚠ Using original text (")}{r.breedRaw})</span> : null}
                                  {" · " + (r.weight || "—") + "kg · " + (r.type === "cat" ? "🐱" : "🐶") + " · " + r.coatLength}
                                </p>
                              )}
                              {r.issues.length > 0 && (
                                <p style={{margin:"4px 0 0",fontSize:11,fontWeight:800,color:"#991B1B"}}>
                                  ✗ {r.issues.join(" · ")}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Btn onClick={doImport} disabled={!newCount || importSheet.busy} style={{opacity: !newCount || importSheet.busy ? 0.6 : 1}}>
                      {importSheet.busy ? zhEn("匯入中…", "Importing…") : zhEn("確認匯入 ", "Confirm import of ") + newCount + zhEn(" 個客戶", " customers")}
                    </Btn>
                  </div>
                );
              }

              // step === "upload"
              return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{background:"#FFFAFC",border:"1.5px solid #FFE0EC",borderRadius:14,padding:"14px 16px"}}>
                    <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("步驟", "Steps")}</p>
                    <ol style={{margin:0,paddingLeft:18,fontSize:12,color:"#6A6A85",fontWeight:700,lineHeight:1.7}}>
                      <li>{zhEn("下載範本 CSV → 用 Excel / Numbers 開", "Download the template CSV → open it in Excel / Numbers")}</li>
                      <li>{zhEn("填好客戶 + 寵物資料（每行 = 一隻寵物）", "Fill in customer + pet details (one row = one pet)")}</li>
                      <li>{zhEn("另存為", "Save as ")} <b>UTF-8 CSV</b> {zhEn("格式", " format")}</li>
                      <li>{zhEn("上載 → 系統自動匹配品種 + 預覽", "Upload → breeds are matched automatically + preview")}</li>
                    </ol>
                  </div>
                  <Btn variant="ghost" onClick={downloadTemplate} style={{background:"#fff",border:"2px solid var(--c-light)",color:"var(--c-primary)"}}>
                    {zhEn("📄 下載範本 CSV", "📄 Download template CSV")}
                  </Btn>
                  <label style={{padding:"14px",borderRadius:14,background:"linear-gradient(135deg,var(--c-grad1),var(--c-primary))",color:"#fff",fontWeight:900,fontSize:14,textAlign:"center",cursor:"pointer",boxShadow:"0 8px 20px var(--c-primary-30)"}}>
                    {zhEn("📤 選擇 CSV 上載", "📤 Choose CSV to upload")}
                    <input type="file" accept=".csv,text/csv" onChange={onFile} style={{display:"none"}} />
                  </label>
                  <div style={{background:"#FEF3C7",border:"1.5px solid #FDE68A",borderRadius:12,padding:"10px 12px"}}>
                    <p style={{fontSize:11,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("⚠ 注意事項", "⚠ Notes")}</p>
                    <ul style={{margin:"4px 0 0",paddingLeft:16,fontSize:11,color:"#92400E",fontWeight:700,lineHeight:1.55}}>
                      <li>{zhEn("電話重複嘅客戶會自動跳過（避免重覆）", "Customers with a duplicate phone number are skipped automatically")}</li>
                      <li>{zhEn("品種會 fuzzy-match 我哋嘅品種庫（貴婦犬、Poodle、貴婦…都認）", "Breeds are fuzzy-matched against our breed list (e.g. Poodle, Toy Poodle…)")}</li>
                      <li>{zhEn("未匹配嘅品種會用 CSV 入面嘅原文，唔會 block 匯入", "Breeds that don't match keep the text from your CSV and won't block the import")}</li>
                      <li>{zhEn("類型只接受", "Type accepts only")} <b>dog</b> {zhEn("或", "or")} <b>cat</b>{zhEn("；毛長只接受", "; coat length accepts only")} <b>short</b> {zhEn("或", "or")} <b>long</b></li>
                    </ul>
                  </div>
                </div>
              );
            })()}
          </BottomSheet>
          <BottomSheet open={!!crmUser} onClose={() => {
            setCrmUser(null);
            if (crmReturn) {
              const back = crmReturn; setCrmReturn(null);
              setTab(back.tab);
              const apt = back.aptId && state.appointments.find(x => x.id === back.aptId);
              if (apt) setEditApt(apt);
            }
          }} title={state.users[crmUser]?.name || ""}>
            {crmUser && (() => {
              const _cuBase = state.users[crmUser];
              // Wallet is PER-SHOP (customer_memberships) — the source of truth,
              // same as the 錢包 tab. state.users[...].wallet is the profile
              // column (often 0/stale), so overlay the membership balance +
              // history so the customer detail matches the 錢包 view.
              const _cuMem = (state.memberships || []).find(m => m.userId === crmUser && m.tenantId === user.tenantId);
              const cu = _cuMem ? { ..._cuBase, wallet: _cuMem.wallet, walletTx: _cuMem.walletTx } : _cuBase;
              const cuPets = (cu.pets || []).map(pid => state.pets[pid]).filter(Boolean);
              const cuApts = state.appointments.filter(a => a.customerId === crmUser).sort((a, b) => b.date.localeCompare(a.date));
              const totalSpent = cuApts.filter(a => a.status === "done").reduce((s, a) => s + aptRevenue(a), 0);
              return <div>
                <div style={{display:"flex",gap:6,marginBottom:24,background:"var(--c-light)",padding:6,borderRadius:99,overflowX:"auto"}}>
                  {["info","records",...(tenant?.posEnabled && planPosRank(getPlans(state.platformConfig)[tenant?.plan || "free"]) >= 2 ? ["purchases"] : []),"wallet","stamps","ledger"].map(t => (
                    <button key={t} onClick={() => setCrmTab(t)} style={{flexShrink:0,padding:"10px 14px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:crmTab===t?"var(--c-primary)":"transparent",color:crmTab===t?"#fff":"var(--c-grad1)",cursor:"pointer",transition:"all 0.3s",whiteSpace:"nowrap",boxShadow:crmTab===t?"0 4px 12px var(--c-primary-30)":"none"}}>
                      {t==="info"?i18nT("own.crm.tab.info"):t==="records"?i18nT("own.crm.tab.records"):t==="purchases"?T("購物","Purchases","購入"):t==="wallet"?i18nT("own.crm.tab.wallet"):t==="stamps"?T("積分卡","Stamp card","スタンプカード"):i18nT("own.crm.tab.ledger")}
                    </button>
                  ))}
                </div>
                {crmTab === "info" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div style={{background:"var(--c-lighter)",borderRadius:24,padding:16,textAlign:"center",border:"2px solid var(--c-border)"}}><p style={{fontSize:13,color:"var(--c-primary)",margin:"0 0 4px",fontWeight:800}}>{i18nT("own.crm.totalSpent")}</p><p style={{fontSize:22,fontWeight:900,color:"var(--c-dark)",margin:0}}>{"$" + totalSpent}</p></div>
                    <div style={{background:"#EFF6FF",borderRadius:24,padding:16,textAlign:"center",border:"2px solid #DBEAFE"}}><p style={{fontSize:13,color:"#3B82F6",margin:"0 0 4px",fontWeight:800}}>{i18nT("own.crm.groomCount")}</p><p style={{fontSize:22,fontWeight:900,color:"#1D4ED8",margin:0}}>{cuApts.filter(a=>a.status==="done").length}</p></div>
                  </div>

                  {/* Contact info — phones + email + address */}
                  <div style={{background:"#FFFDFD",borderRadius:16,padding:14,border:"2px solid var(--c-light)"}}>
                    <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>{zhEn("📞 聯絡資料", "📞 Contact details")}</p>
                    <p style={{fontSize:14,fontWeight:700,color:"#4A4A68",margin:"0 0 4px"}}>{cu.phone ? ((cu.countryCode || "+852") + " " + cu.phone) : "—"}</p>
                    {cu.phone2 && <p style={{fontSize:13,fontWeight:700,color:"#6A6A85",margin:"0 0 4px"}}>📱 {(cu.phone2CountryCode || "+852") + " " + cu.phone2}</p>}
                    {cu.address && <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>📍 {cu.address}</p>}
                    {cu.email && !cu.email.endsWith("@petos.local") && <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,wordBreak:"break-all"}}>📧 {cu.email}</p>}
                    {cu.email && cu.email.endsWith("@petos.local") && <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700,fontStyle:"italic"}}>{zhEn("訪客帳戶（無 Email 登入）", "Guest account (no email login)")}</p>}
                  </div>

                  {/* Walk-in / 訪客 customer? Offer a one-tap shortcut to
                      provision a real auth account for them. Opens the
                      existing 新增帳戶 form pre-filled with the walk-in's
                      name + phone. Hidden once they already have a real
                      email account. */}
                  {(!cu.email || cu.email.endsWith("@petos.local")) && (
                    <button
                      onClick={() => {
                        setCrmUser(null);
                        setNewAcctForm({
                          name: cu.name || "",
                          email: "",
                          phone: cu.phone || "",
                          countryCode: cu.countryCode || "+852",
                          password: "",
                        });
                      }}
                      style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px 14px",borderRadius:14,background:"linear-gradient(135deg,#5AC8FA,#007AFF)",color:"#fff",border:"none",fontWeight:900,fontSize:14,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,122,255,0.3)"}}
                    >
                      {zhEn("🔐 建立登入帳戶（電郵 + 密碼）", "🔐 Create login account (email + password)")}
                    </button>
                  )}
                  {/* Customers the shop created have no login of their own. One tap
                      gives them a phone + 6-digit password and opens WhatsApp
                      with the message ready. Hidden for anyone with a real
                      email/Google account; the edge fn enforces the same rule. */}
                  {user.role === "owner" && cu?.email?.endsWith("@petos.local") && (
                    <button
                      disabled={loginShareBusy}
                      onClick={async () => {
                        if (!cu.phone) { toast(zhEn("請先為客戶補回電話號碼", "Add the customer's phone number first"), "error"); return; }
                        setLoginShareBusy(true);
                        try {
                          const r = await sendCustomerLogin({ tenantId: user.tenantId, userId: crmUser });
                          setLoginShare({ name: cu.name || "", countryCode: cu.countryCode || "+852", ...r });
                        } catch (e) {
                          toast(e?.message || zhEn("發送失敗", "Failed to send"), "error");
                        } finally {
                          setLoginShareBusy(false);
                        }
                      }}
                      style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px 14px",borderRadius:14,background:"linear-gradient(135deg,#34D399,#25D366)",color:"#fff",border:"none",fontWeight:900,fontSize:14,cursor:"pointer",boxShadow:"0 4px 12px rgba(37,211,102,0.3)",opacity:loginShareBusy?0.6:1}}
                    >
                      {loginShareBusy ? zhEn("處理中…", "Processing…") : zhEn("📲 發送登入資料（電話 + 密碼）", "📲 Send login details (phone + password)")}
                    </button>
                  )}
                  {/* Before handing a shop-made customer their login: walk-ins the
                      shop booked may have earned stamps / coupons the shop
                      doesn't want them to see. One tap resets the stamp card
                      and removes UNUSED coupons (used ones stay as history). */}
                  {user.role === "owner" && cu?.email?.endsWith("@petos.local") && (() => {
                    const mem = (state.memberships || []).find(m => m.userId === crmUser && m.tenantId === user.tenantId);
                    const stampsNow = Number(mem?.stamps || 0);
                    const unused = (mem?.vouchers || []).filter(v => !v.used);
                    if (!mem || (stampsNow === 0 && unused.length === 0)) return null;
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      <p style={{fontSize:11.5,color:"#92400E",fontWeight:700,margin:0,lineHeight:1.55,background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"7px 10px"}}>
                        {zhEn("ℹ️ 此客戶現有", "ℹ️ This customer currently has")} <b>{stampsNow}</b> {zhEn("個印章、", " stamps, ")}<b>{unused.length}</b> {zhEn("張未使用優惠券（由店舖代約嘅預約累積）。發送登入資料後客戶會見到；如唔想佢見到，可以先清除。", " unused coupons (earned from bookings made by the shop). The customer will see them after you send login details; clear them first if you don't want that.")}
                      </p>
                      <button
                        disabled={clearRewardsBusy}
                        onClick={async () => {
                          const parts = [stampsNow ? stampsNow + zhEn(" 個印章", " stamps") : "", unused.length ? unused.length + zhEn(" 張未使用優惠券", " unused coupons") : ""].filter(Boolean).join(" 及 ");
                          if (!window.confirm(zhEn("清除「", "Clear \"") + (cu.name || zhEn("客戶", "Customer")) + zhEn("」嘅 ", "\"'s ") + parts + zhEn("？\n\n印章歸零，未使用優惠券會被刪除（已使用嘅保留作紀錄）。此動作無法還原。", "?\n\nStamps will be reset to zero and unused coupons deleted (used ones are kept for the record). This cannot be undone."))) return;
                          setClearRewardsBusy(true);
                          try {
                            const kept = (mem.vouchers || []).filter(v => v.used);
                            await patchCustomerMembership({ userId: crmUser, tenantId: user.tenantId, stamps: 0, vouchers: kept });
                            setState(s => ({ ...s, memberships: (s.memberships || []).map(m => m.userId === crmUser && m.tenantId === user.tenantId ? { ...m, stamps: 0, vouchers: kept } : m) }));
                            try { logActivity({ tenantId: user.tenantId, userId: user.id, actionType: "customer_rewards_cleared", entityType: "customer", entityId: crmUser, entityName: cu.name || "", description: "清除印章及優惠券：" + parts }).catch(() => {}); } catch { /* audit is best-effort */ }
                            toast(zhEn("已清除 ", "Cleared ") + parts + " ✓");
                          } catch (e) {
                            toast(zhEn("清除失敗：", "Clear failed: ") + (e?.message || e), "error");
                          } finally { setClearRewardsBusy(false); }
                        }}
                        style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"9px 14px",borderRadius:14,background:"#fff",color:"#B45309",border:"2px solid #FCD34D",fontWeight:900,fontSize:13,cursor:"pointer"}}
                      >
                        {clearRewardsBusy ? zhEn("處理中…", "Processing…") : zhEn("🧹 清除印章及優惠券", "🧹 Clear stamps & coupons")}
                      </button>
                      </div>
                    );
                  })()}

                  {/* Customer notes — free-form remarks. Persists on blur
                      via patchProfile. Previously the onChange handler only
                      updated local React state and the "※ 自動儲存" hint
                      was misleading — a refresh wiped everything the owner
                      typed. */}
                  <div>
                    <p style={{fontSize:14,color:"#8A8A9E",fontWeight:800,marginBottom:8}}>{zhEn("📝 客戶備註", "📝 Customer notes")}</p>
                    <textarea
                      placeholder={zhEn("記下客戶喜好、過敏、行為等...", "Note the customer's preferences, allergies, behaviour, etc...")}
                      value={cu.notes || ""}
                      onChange={e => setState(s => ({...s, users:{...s.users, [crmUser]:{...s.users[crmUser], notes: e.target.value}}}))}
                      onBlur={async e => {
                        const next = e.target.value || "";
                        // Compare against the freshest snapshot at blur time,
                        // not the closure-captured `cu`. Skip the network call
                        // when nothing actually changed (common when the user
                        // focuses out without typing).
                        const live = state.users?.[crmUser];
                        if ((live?.notes || "") === next) return;
                        try {
                          await patchProfile(crmUser, { notes: next });
                        } catch (err) {
                          console.warn("[PettoBook] CRM notes save failed:", err);
                          toast(i18nT("toast.saveFailed", "儲存失敗") + zhEn("：", ": ") + (err?.message || err), "error");
                        }
                      }}
                      style={{width:"100%",minHeight:80,padding:"10px 14px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",background:"#FFFDFD",color:"#4A4A68",boxSizing:"border-box"}}
                    />
                    <p style={{fontSize:10,color:"#AFAFC4",margin:"4px 0 0",fontWeight:700}}>{zhEn("※ 失去焦點時自動儲存", "※ Saved automatically when you click away")}</p>
                  </div>

                  {/* Pets section */}
                  <div>
                    <p style={{fontSize:14,color:"#8A8A9E",fontWeight:800,marginBottom:8}}>{zhEn("🐾 毛孩 (", "🐾 Pets (")}{cuPets.length})</p>
                    {cuPets.length === 0 ? (
                      <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,padding:"12px",textAlign:"center",background:"var(--c-lighter)",borderRadius:12,margin:0}}>{zhEn("尚未登記毛孩", "No pets registered yet")}</p>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {cuPets.map(p => (
                          <div key={p.id} style={{display:"flex",alignItems:"center",gap:12,background:"#FFFDFD",borderRadius:12,padding:12,border:"2px solid var(--c-light)"}}>
                            <div style={{width:42,height:42,borderRadius:99,background:"var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{p.type === "cat" ? "🐈" : p.type === "rabbit" ? "🐇" : "🐕"}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{p.name}</p>
                              <p style={{fontSize:11,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{p.breed || "—"} · {p.weight || "?"}kg · {p.coatLength === "long" ? zhEn("長毛", "Long coat") : p.coatLength === "medium" ? zhEn("中毛", "Medium coat") : zhEn("短毛", "Short coat")}{p.allergies && p.allergies !== "無" ? " · ⚠️ " + p.allergies : ""}</p>
                            </div>
                            <button onClick={() => setEditPet({ ...p, weight: p.weight || "" })} title={i18nT("edit.editPet","編輯毛孩")}
                              style={{flexShrink:0,display:"flex",alignItems:"center",gap:5,background:"var(--c-lighter)",border:"2px solid var(--c-light)",borderRadius:99,padding:"7px 12px",cursor:"pointer",color:"var(--c-primary)",fontWeight:800,fontSize:12}}>
                              <Edit size={13} strokeWidth={2.5} /> {T("編輯","Edit","編集")}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* App-installed badge — cu.appInstalled is set by
                      fetchTenantProfiles from app_installs. Nothing shows
                      when the customer hasn't installed the native app. */}
                  {cu.appInstalled && (
                    <div style={{marginBottom:12,padding:"10px 14px",background:"linear-gradient(135deg,var(--c-lighter),#FFF)",borderRadius:12,border:"1.5px solid var(--c-light)",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:20}}>📱</span>
                      <div style={{flex:1}}>
                        <p style={{fontSize:12,fontWeight:900,color:"var(--c-primary)",margin:0}}>{zhEn("已安裝 PettoBook App", "PettoBook app installed")}</p>
                        {cu.appLastSeen && (
                          <p style={{fontSize:10,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{zhEn("最近開啟：", "Last opened: ")}{String(cu.appLastSeen).slice(0,10)}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <p style={{fontSize:14,color:"#8A8A9E",fontWeight:800,marginBottom:10}}>{zhEn("標籤管理 🏷️", "Manage tags 🏷️")}</p>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
                      {(cu.tags||[]).map(t => (
                        <div key={t} style={{display:"flex",alignItems:"center",gap:6,background:"var(--c-light)",borderRadius:99,padding:"6px 12px"}}>
                          <span style={{fontSize:13,fontWeight:800,color:"var(--c-primary)"}}>{t}</span>
                          <button onClick={() => removeTag(crmUser, t)} style={{background:"var(--c-grad2)",border:"none",cursor:"pointer",padding:4,display:"flex",borderRadius:99}}><X size={10} color="#fff" strokeWidth={3} /></button>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <input style={{flex:1,border:"2px solid var(--c-light)",borderRadius:99,padding:"10px 16px",fontSize:14,fontWeight:600,outline:"none",background:"#FFFDFD",color:"#4A4A68"}} placeholder={zhEn("輸入新標籤...", "Enter a new tag...")} value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key==="Enter"&&addTag(crmUser)} />
                      <Btn onClick={() => addTag(crmUser)} style={{padding:"10px 20px"}}><Tag size={16} strokeWidth={2.5} />{zhEn("新增", "Add")}</Btn>
                    </div>
                  </div>
                  {canView("manageCRM") && tenant?.walletEnabled !== false && <Btn onClick={() => setTopupSheet(crmUser)} style={{width:"100%",marginTop:8}}><Wallet size={18} strokeWidth={2.5} />{i18nT("own.crm.addWallet")}</Btn>}
                  {canView("manageCRM") && <Btn variant="secondary" onClick={() => setManagePkgSheet({ customerId: crmUser })} style={{width:"100%",marginTop:4}}>{i18nT("own.crm.managePackage")}</Btn>}
                  {canView("manageCRM") && <Btn variant="secondary" onClick={() => setSendCouponForm({ userId: crmUser, type: "discount", label: "", amount: "", note: "" })} style={{width:"100%",marginTop:4,background:"#FEF3C7",color:"#92400E"}}>{zhEn("🎫 送贈優惠券", "🎫 Give coupon")}</Btn>}

                  {/* Edit / Disable / Delete controls.
                      停用 = soft (reversible), 刪除 = hard delete via RLS policy
                      `profiles_delete_owner_or_admin`. Cascades remove pets +
                      wallet rows; appointments are kept for audit. */}
                  {canView("manageCRM") && (() => {
                    const mem = (state.memberships || []).find(m => m.userId === crmUser && m.tenantId === user.tenantId);
                    const isBlocked = !!mem?.blockedAt;
                    const toggleBlock = async () => {
                      if (!mem) { toast(i18nT("lang.name") === "English" ? "Membership not found" : zhEn("找不到該客戶會籍紀錄", "Customer membership record not found"), "error"); return; }
                      let reason = mem.blockedReason || "";
                      if (!isBlocked) {
                        const r = window.prompt(i18nT("own.crm.confirmBlock").replace("{name}", cu.name), "");
                        if (r === null) return;
                        reason = r;
                      } else {
                        if (!confirm(i18nT("own.crm.confirmUnblock"))) return;
                      }
                      try {
                        await setCustomerBlock({ userId: crmUser, tenantId: user.tenantId, blocked: !isBlocked, reason });
                        setState(s => ({
                          ...s,
                          memberships: (s.memberships || []).map(m =>
                            (m.userId === crmUser && m.tenantId === user.tenantId)
                              ? { ...m, blockedAt: isBlocked ? null : new Date().toISOString(), blockedReason: isBlocked ? "" : reason }
                              : m
                          ),
                        }));
                        toast(isBlocked ? i18nT("own.crm.unblockedToast") : i18nT("own.crm.blockedToast"));
                      } catch (e) { toast(e?.message || (i18nT("lang.name") === "English" ? "Action failed" : zhEn("操作失敗", "Action failed")), "error"); }
                    };
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
                        {isBlocked && <div style={{padding:"10px 14px",background:"#FEE2E2",border:"2px solid #FCA5A5",borderRadius:14,fontSize:12,fontWeight:800,color:"#991B1B"}}>{i18nT("own.crm.blocked")}{mem.blockedReason ? zhEn("（", " (") + i18nT("own.crm.blockReason") + mem.blockedReason + zhEn("）", ")") : ""}</div>}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          <Btn variant="secondary" onClick={() => setEditCustForm({ id: crmUser, name: cu.name || "", phone: cu.phone || "", countryCode: cu.countryCode || "+852", phone2: cu.phone2 || "", phone2CountryCode: cu.phone2CountryCode || "+852", address: cu.address || "", email: (cu.email && !cu.email.endsWith("@petos.local")) ? cu.email : "" })}>
                            <Edit size={16} strokeWidth={2.5}/> {zhEn("編輯資料", "Edit details")}
                          </Btn>
                          <Btn variant={isBlocked ? "secondary" : "ghost"} onClick={toggleBlock}>
                            {isBlocked ? i18nT("own.crm.unblockBtn") : i18nT("own.crm.blockBtn")}
                          </Btn>
                        </div>
                        {/* Re-entry point for the gift/sell package sheet — the
                            sheet existed but nothing opened it after an earlier
                            cleanup removed the button. */}
                        <Btn onClick={() => setSellPackageSheet({ customerId: crmUser, packageId: "", petId: (cu.pets || [])[0] || "", isGift: false, paymentMethod: "cash", manualAmount: "" })} style={{width:"100%",background:"linear-gradient(135deg,#FB923C,#F97316)",color:"#fff",boxShadow:"0 6px 18px rgba(249,115,22,0.35)"}}>
                          📦 {T("送贈／販售套票","Gift / Sell package","パッケージを贈る／販売")}
                        </Btn>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {/* Reset password — Edge Function generates a new
                              preset (default = last 6 digits of phone) and
                              forces the customer to set their own on next
                              login. Owner sees the new password in a result
                              sheet so they can share it. */}
                          <Btn variant="secondary" onClick={() => {
                            if (!cu.phone) {
                              const custom = window.prompt(zhEn("此客戶沒有電話號碼，請手動輸入新密碼（至少 6 位）：", "This customer has no phone number. Enter a new password manually (at least 6 characters):"));
                              if (!custom || custom.length < 6) return;
                              setResetPwSheet({ userId: crmUser, name: cu.name || "", customPassword: custom });
                            } else {
                              setResetPwSheet({ userId: crmUser, name: cu.name || "", customPassword: null });
                            }
                          }}>
                            {zhEn("🔑 重設密碼", "🔑 Reset password")}
                          </Btn>
                          <Btn variant="ghost" onClick={() => disableCustomer(crmUser)}>
                            <UserX size={16} strokeWidth={2.5}/> {i18nT("own.crm.disableBtn")}
                          </Btn>
                          <Btn variant="danger" onClick={() => removeCustomer(crmUser)}>
                            <Trash2 size={16} strokeWidth={2.5}/> {i18nT("own.crm.deleteBtn")}
                          </Btn>
                        </div>
                      </div>
                    );
                  })()}
                </div>}
                {crmTab === "purchases" && tenant?.posEnabled && <CustomerPurchases tenant={tenant} customerId={crmUser} customerName={cu.name} sym={cur(tenant)} />}
                {crmTab === "records" && <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {cuApts.length === 0 && <EmptyState icon={Calendar} title={zhEn("暫無美容紀錄", "No grooming history yet")} />}
                  {cuApts.map(a => {
                    const expanded = expandedRecord === a.id;
                    const r = a.report;
                    const staffName = state.users?.[a.staffId]?.name || (a.staffId ? zhEn("已離職員工", "Former staff") : zhEn("未指派", "Unassigned"));
                    const allPhotos = Array.isArray(r?.photos) ? r.photos : [];
                    const surcharges = Array.isArray(a.surcharges) ? a.surcharges : [];
                    return (
                      <div key={a.id} style={{borderRadius:20,background:"#FFFDFD",border:"2px solid "+(expanded?"var(--c-primary)":"var(--c-light)"),overflow:"hidden",transition:"border 0.2s"}}>
                        <button onClick={() => setExpandedRecord(expanded ? null : a.id)} style={{width:"100%",padding:14,background:"transparent",border:"none",cursor:"pointer",textAlign:"left"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,gap:8}}>
                            <p style={{fontSize:15,fontWeight:900,margin:0,color:"#4A4A68"}}>{((state.pets[a.petId]?.name || a.petName) || zhEn("(已刪除毛孩)", "(deleted pet)")) + " — " + a.service}</p>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <StatusBadge status={a.status} cancelReason={a.cancelReason} approvalPending={a.approvalStatus === "pending"} />
                              <ChevronDown size={16} color="#8A8A9E" style={{transform:expanded?"rotate(180deg)":"none",transition:"transform 0.2s"}} />
                            </div>
                          </div>
                          <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{a.date + " " + a.time + " · " + staffName + " · $" + (a.basePrice + a.addonPrice + (surcharges.reduce((s,x)=>s+(Number(x.amount)||0),0)))}</p>
                          {a.rating && !expanded && <div style={{marginTop:6}}><Stars value={a.rating} size={13} /></div>}
                        </button>
                        {expanded && (
                          <div style={{padding:"0 14px 14px",borderTop:"1px solid var(--c-light)"}}>
                            {/* Pricing breakdown */}
                            <div style={{display:"flex",flexDirection:"column",gap:3,padding:"10px 0",borderBottom:"1px solid var(--c-light)",fontSize:12,fontWeight:700,color:"#6A6A85"}}>
                              <div style={{display:"flex",justifyContent:"space-between"}}><span>{i18nT("summary.mainService")}</span><span>${a.basePrice}</span></div>
                              {a.addonPrice > 0 && <div style={{display:"flex",justifyContent:"space-between"}}><span>{zhEn("加購", "Add-ons")} {(a.addons||[]).join("、")}</span><span>${a.addonPrice}</span></div>}
                              {surcharges.map((s, i) => <div key={i} style={{display:"flex",justifyContent:"space-between",color:"#991B1B"}}><span>+ {s.name || s.reason || T("額外項目","Extra item","追加項目")}</span><span>${s.amount}</span></div>)}
                              {a.deposit > 0 && (() => {
                                const dW = Number(a.depositPaidWallet) || 0;
                                const dP = Number(a.depositPaidPoints) || 0;
                                const dV = Number(a.depositPaidVoucher) || 0;
                                const dCash = Math.max(0, a.deposit - dW - dP - dV);
                                const parts = [];
                                if (dCash > 0) parts.push(T("現金","Cash","現金") + " $" + dCash);
                                if (dW > 0) parts.push(T("錢包","Wallet","ウォレット") + " $" + dW);
                                if (dP > 0) parts.push(T("積分","Points","ポイント") + " $" + dP);
                                if (dV > 0) parts.push(T("券","Voucher","クーポン") + " $" + dV);
                                return (
                                  <div style={{display:"flex",justifyContent:"space-between",color:"#92400E",gap:8}}>
                                    <span style={{flex:1,minWidth:0}}>{T("訂金","Deposit","内金")}{zhEn("（", " (")}{parts.length?parts.join(" + "):T("已收","Received","受領済み")}{zhEn("）", ")")}</span>
                                    <span style={{flexShrink:0}}>− ${a.deposit}</span>
                                  </div>
                                );
                              })()}
                              <div style={{display:"flex",justifyContent:"space-between",fontWeight:900,color:"#4A4A68",marginTop:4,paddingTop:4,borderTop:"1px dashed var(--c-light)"}}><span>{a.paid ? (i18nT("lang.name") === "English" ? "Paid ✓" : zhEn("已付款 ✓", "Paid ✓")) : (i18nT("lang.name") === "English" ? "Balance" : zhEn("結餘", "Balance"))}</span><span>${a.remainingBalance ?? (a.basePrice + a.addonPrice + surcharges.reduce((s,x)=>s+(Number(x.amount)||0),0) - (a.deposit||0))}</span></div>
                            </div>

                            {/* Grooming report */}
                            {r ? <div style={{padding:"10px 0",borderBottom:"1px solid var(--c-light)"}}>
                              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>📝 {T("美容報告","Grooming report","トリミングレポート")}</p>
                              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))",gap:6}}>
                                {[[i18nT("ea.report.skin"),r.skin],[i18nT("ea.report.ear"),r.ear],[i18nT("ea.report.teeth"),r.teeth],[i18nT("ea.report.mood"),r.mood],[i18nT("ea.report.nails"),r.nailTrim ?? r.nails],[i18nT("ea.report.analGlands"),r.analGlands],[i18nT("ea.report.parasites"),r.parasites],[i18nT("ea.report.eyeDischarge"),r.eyeDischarge],[i18nT("ea.report.matting"),r.matting]].filter(([,v]) => v).map(([k,v]) => (
                                  <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:11,fontWeight:700,padding:"4px 8px",background:"var(--c-lighter)",borderRadius:8}}>
                                    <span style={{color:"#8A8A9E"}}>{k}</span>
                                    <span style={{color:(v==="正常"||v==="良好"||v==="無"||v==="無發現"||v==="已修剪"||v==="已擠")?"#10B981":"#F59E0B"}}>{v}</span>
                                  </div>
                                ))}
                              </div>
                              {r.notes && <div style={{background:"#FFFBEB",borderRadius:10,padding:"8px 10px",marginTop:8,fontSize:12,color:"#78350F",fontWeight:700}}>{r.notes}</div>}
                            </div> : <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,padding:"10px 0",margin:0}}>{T("尚未填寫美容報告","Grooming report not filled in yet","トリミングレポート未記入")}</p>}

                            {/* Photos */}
                            {allPhotos.length > 0 && <div style={{padding:"10px 0",borderBottom:"1px solid var(--c-light)"}}>
                              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>📸 {T("照片","Photos","写真")} ({allPhotos.length})</p>
                              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px, 1fr))",gap:6}}>
                                {allPhotos.map((ph, i) => (
                                  <a key={(ph.id || "") + i} href={ph.url} target="_blank" rel="noopener noreferrer" style={{position:"relative",aspectRatio:"1",borderRadius:10,overflow:"hidden",border:"1px solid var(--c-light)",cursor:"zoom-in"}}>
                                    <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                                    {ph.category && <span style={{position:"absolute",bottom:3,left:3,fontSize:9,fontWeight:800,background:"rgba(0,0,0,0.55)",color:"#fff",padding:"1px 5px",borderRadius:99}}>{ph.category === "before" ? T("前","Before","施術前") : ph.category === "after" ? T("後","After","施術後") : "SPA"}</span>}
                                  </a>
                                ))}
                              </div>
                            </div>}

                            {/* Rating + comment */}
                            {a.rating && <div style={{padding:"10px 0"}}>
                              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{T("客戶評價","Customer rating","お客様の評価")}</p>
                              <Stars value={a.rating} size={16} />
                              {a.ratingComment && <p style={{fontSize:12,color:"#6A6A85",margin:"6px 0 0",fontStyle:"italic",fontWeight:700}}>{zhEn("「", "\"")}{a.ratingComment}{zhEn("」", "\"")}</p>}
                            </div>}

                            {a.cancelReason && <div style={{padding:"10px 0"}}>
                              <p style={{fontSize:12,fontWeight:800,color:"#991B1B",margin:"0 0 4px"}}>{T("取消原因","Cancellation reason","キャンセル理由")}</p>
                              <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700}}>{a.cancelReason}</p>
                            </div>}

                            {a.staffNote && <div style={{padding:"10px 0",background:"var(--c-lighter)",borderRadius:10,marginTop:6}}>
                              <p style={{fontSize:11,fontWeight:800,color:"var(--c-primary)",margin:"0 0 4px",padding:"0 8px"}}>🔒 {T("員工內部備註","Internal staff note","スタッフ内部メモ")}</p>
                              <p style={{fontSize:12,color:"#6A6A85",margin:0,padding:"0 8px 4px",fontWeight:700}}>{a.staffNote}</p>
                            </div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>}
                {crmTab === "wallet" && <div style={{display:"flex",flexDirection:"column",gap:0}}>
                  {(cu.walletTx||[]).length === 0
                    ? <EmptyState icon={Wallet} title={i18nT("own.crm.noWalletTx")} sub={i18nT("own.crm.noWalletTxSub")} />
                    : (cu.walletTx||[]).slice().reverse().map((tx, i) => (
                      <div key={tx.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 0",borderBottom:i===(cu.walletTx||[]).length-1?"none":"2px solid var(--c-light)"}}>
                        <div style={{display:"flex",alignItems:"center",gap:14}}>
                          <div style={{width:40,height:40,borderRadius:99,background:tx.type==="topup"?"#F0FDF4":"#FEF2F2",display:"flex",alignItems:"center",justifyContent:"center"}}>{tx.type==="topup"?<ArrowUpRight size={18} color="#10B981" strokeWidth={3}/>:<ArrowDownLeft size={18} color="#EF4444" strokeWidth={3}/>}</div>
                          <div><p style={{fontSize:15,margin:"0 0 2px",color:"#4A4A68",fontWeight:800}}>{tx.note}</p><p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:600}}>{tx.date}</p></div>
                        </div>
                        <span style={{fontWeight:900,fontSize:16,color:tx.type==="topup"?"#10B981":"#EF4444"}}>{(tx.type==="topup"?"+":"-")+"$"+tx.amount}</span>
                      </div>
                    ))
                  }
                </div>}
                {crmTab === "stamps" && (() => {
                  const sc = tenant?.stampCard || {};
                  const mem = (state.memberships || []).find(m => m.userId === crmUser && m.tenantId === user.tenantId);
                  if (!sc.enabled) return <Card style={{padding:24,textAlign:"center"}}><p style={{fontSize:14,fontWeight:800,color:"#8A8A9E",margin:0}}>📇 {T("未啟用電子積分卡 — 可到 設定 → 積分卡 開啟","Stamp card not enabled — turn it on in Settings","スタンプカード未設定")}</p></Card>;
                  const totalSlots = Math.max(1, Number(sc.totalSlots) || 10);
                  const milestones = Array.isArray(sc.milestones) ? sc.milestones : [];
                  const rawStamps = Number(mem?.stamps || 0);
                  const stamps = Math.min(rawStamps, totalSlots);
                  const nextMs = milestones.find(m => (m.atSlot || 0) > stamps);
                  const adjustStamps = async (delta) => {
                    const next = Math.max(0, Math.min(totalSlots, rawStamps + delta));
                    if (next === rawStamps) return;
                    try {
                      // Upsert so a customer with no membership row yet still gets one.
                      const saved = await setCustomerStamps({ userId: crmUser, tenantId: user.tenantId, stamps: next });
                      const savedStamps = typeof saved?.stamps === "number" ? saved.stamps : next;
                      setState(s => {
                        const list = s.memberships || [];
                        const exists = list.some(m => m.userId === crmUser && m.tenantId === user.tenantId);
                        return {
                          ...s,
                          memberships: exists
                            ? list.map(m => m.userId === crmUser && m.tenantId === user.tenantId ? { ...m, stamps: savedStamps } : m)
                            : [...list, (saved || { userId: crmUser, tenantId: user.tenantId, stamps: savedStamps })],
                        };
                      });
                      toast((delta > 0 ? zhEn("+1 印章 ✅ ", "+1 stamp ✅ ") : zhEn("−1 印章 ", "−1 stamp ")) + savedStamps + "/" + totalSlots);
                    } catch (e) { toast(zhEn("更新失敗：", "Update failed: ") + (e?.message || e), "error"); }
                  };
                  return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    <div style={{background:"linear-gradient(135deg,#FB923C,#F97316)",borderRadius:20,padding:"16px 18px",color:"#fff",display:"flex",alignItems:"center",gap:14}}>
                      <span style={{fontSize:28}}>📇</span>
                      <div style={{flex:1}}>
                        <p style={{fontSize:20,fontWeight:900,margin:0}}>{stamps} / {totalSlots}</p>
                        <p style={{fontSize:12,fontWeight:700,margin:"2px 0 0",opacity:0.9}}>{nextMs ? zhEn("再 " + ((nextMs.atSlot||0) - stamps) + " 個印章獲贈「" + milestoneLiveLabel(nextMs, tenant) + "」🎁", ((nextMs.atSlot||0) - stamps) + " more stamps to earn \"" + milestoneLiveLabel(nextMs, tenant) + "\" 🎁") : zhEn("已集滿 🎉", "Card complete 🎉")}</p>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={() => adjustStamps(-1)} title={zhEn("減 1 印章", "Remove 1 stamp")} style={{width:38,height:38,borderRadius:99,border:"none",background:"rgba(255,255,255,0.25)",color:"#fff",fontSize:18,fontWeight:900,cursor:"pointer"}}>−</button>
                        <button onClick={() => adjustStamps(1)} title={zhEn("加 1 印章", "Add 1 stamp")} style={{width:38,height:38,borderRadius:99,border:"none",background:"#fff",color:"#F97316",fontSize:18,fontWeight:900,cursor:"pointer"}}>＋</button>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(totalSlots, 5)}, minmax(0, 1fr))`,gap:8}}>
                      {Array.from({length: totalSlots}, (_, i) => {
                        const slot = i + 1;
                        const filled = slot <= stamps;
                        const ms = milestones.find(m => m.atSlot === slot);
                        return <div key={i} style={{aspectRatio:"1",borderRadius:12,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,padding:"4px 2px",background: filled ? (ms ? "linear-gradient(135deg,#34D399,#10B981)" : "linear-gradient(135deg,#FB923C,#F97316)") : (ms ? "#fff" : "#FFF7ED"),border: ms ? (filled ? "2px solid #10B981" : "2px dashed #10B981") : (filled ? "2px solid #F97316" : "2px dashed #FED7AA")}}>
                          {ms
                            ? <><span style={{fontSize:14,lineHeight:1}}>🎁</span><span style={{fontSize:9,fontWeight:800,textAlign:"center",lineHeight:1.15,color: filled ? "#fff" : "#059669",overflow:"hidden"}}>{milestoneLiveLabel(ms, tenant)}</span></>
                            : <span style={{fontSize:13,fontWeight:900,color: filled ? "#fff" : "#FDBA74"}}>{filled ? "🐾" : slot}</span>}
                        </div>;
                      })}
                    </div>
                    <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:0,lineHeight:1.6}}>{zhEn("＋/− 手動調整印章數（例如門市補印）。里程碑獎賞券由系統喺完成預約時自動發放；手動加印章唔會補發獎賞券。", "+/− manually adjusts the stamp count (e.g. in-shop top-ups). Milestone reward coupons are issued automatically when a booking is completed; adding stamps by hand won't issue them.")}</p>
                  </div>;
                })()}
                {crmTab === "ledger" && (() => {
                  const mem = (state.memberships || []).find(m => m.userId === crmUser && m.tenantId === user.tenantId);
                  const walletTx = (mem?.walletTx || cu.walletTx || []).map(t => ({...t, _kind:"wallet"}));
                  const pointsTx = (mem?.pointsTx || []).map(t => ({...t, _kind:"points"}));
                  const allVouchers = (mem?.vouchers || []).map(v => ({
                    ...v,
                    _kind:"voucher",
                    amount: Number(v?.voucher?.amount || v?.amount) || 0,
                    at: v.usedAt || v.redeemedAt || "",
                    date: (v.usedAt || v.redeemedAt || "").slice(0,10),
                  }));
                  const combined = [...walletTx, ...pointsTx, ...allVouchers].sort((a,b) => String(b.at||b.date||"").localeCompare(String(a.at||a.date||"")));
                  const totalWalletUsed  = walletTx.filter(t => t.type==="deduct").reduce((s,t)=>s+(Number(t.amount)||0),0);
                  const totalPointsUsed  = pointsTx.filter(t => t.type==="deduct").reduce((s,t)=>s+(Number(t.amount)||0),0);
                  const totalVouchersUsed= allVouchers.filter(v=>v.used).length;
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                        <div style={{background:"linear-gradient(135deg,#EFF6FF,#DBEAFE)",border:"1.5px solid #BFDBFE",borderRadius:14,padding:"10px 8px",textAlign:"center"}}>
                          <p style={{fontSize:10,fontWeight:800,color:"#1E40AF",margin:"0 0 2px"}}>{i18nT("own.crm.walletDeducted")}</p>
                          <p style={{fontSize:15,fontWeight:900,color:"#1E40AF",margin:0}}>${totalWalletUsed}</p>
                        </div>
                        <div style={{background:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",border:"1.5px solid #DDD6FE",borderRadius:14,padding:"10px 8px",textAlign:"center"}}>
                          <p style={{fontSize:10,fontWeight:800,color:"#6D28D9",margin:"0 0 2px"}}>{i18nT("own.crm.pointsDeducted")}</p>
                          <p style={{fontSize:15,fontWeight:900,color:"#6D28D9",margin:0}}>${totalPointsUsed}</p>
                        </div>
                        <div style={{background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)",border:"1.5px solid #FED7AA",borderRadius:14,padding:"10px 8px",textAlign:"center"}}>
                          <p style={{fontSize:10,fontWeight:800,color:"#9A3412",margin:"0 0 2px"}}>{i18nT("own.crm.vouchersUsed")}</p>
                          <p style={{fontSize:15,fontWeight:900,color:"#9A3412",margin:0}}>{totalVouchersUsed}</p>
                        </div>
                      </div>
                      {combined.length === 0 ? <EmptyState icon={Activity} title={i18nT("own.crm.ledger.empty")} sub={i18nT("own.crm.ledger.emptySub")} />
                       : combined.map(t => {
                          const dollar = Number(t.amount)||0;
                          const isDeduct = t.type === "deduct" || t._kind === "voucher" && t.used;
                          const color = t._kind === "wallet" ? "#1D4ED8" : t._kind === "points" ? "#7C3AED" : "#D97706";
                          const bg    = t._kind === "wallet" ? "#EFF6FF" : t._kind === "points" ? "#F5F3FF" : "#FFF7ED";
                          const kindLabel = t._kind === "wallet" ? "💳 "+T("錢包","Wallet","ウォレット") : t._kind === "points" ? "⭐ "+T("積分","Points","ポイント") : (t.used?"🎟️ "+T("已用券","Voucher used","クーポン使用済み"):"🎟️ "+T("已發券","Voucher issued","クーポン発行済み"));
                          const aptId = t.aptId || t.sourceAptId;
                          const apt = aptId ? state.appointments.find(a => a.id === aptId) : null;
                          const aptLabel = apt ? T("預約","Booking","予約") + " " + apt.date + " " + apt.time : "";
                          return (
                            <div key={t.id || t._kind+"-"+t.at} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:12,background:bg,gap:10}}>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontSize:12,fontWeight:900,color,margin:"0 0 2px"}}>{kindLabel} · {t.note || t.label || (t._kind==="voucher"?T("獎勵券","Reward voucher","リワードクーポン"):T("交易","Transaction","取引"))}</p>
                                <p style={{fontSize:10,color,margin:0,fontWeight:700,opacity:0.7}}>{(t.at||t.date||"").slice(0,16).replace("T"," ")}{aptLabel?" · "+aptLabel:""}</p>
                              </div>
                              <span style={{fontWeight:900,fontSize:14,color,whiteSpace:"nowrap"}}>
                                {t._kind === "voucher"
                                  ? (t.used?T("已用","Used","使用済み")+" $"+dollar:T("未用","Unused","未使用")+" $"+dollar)
                                  : (isDeduct?"−$":"+$")+dollar}
                              </span>
                            </div>
                          );
                        })
                      }
                    </div>
                  );
                })()}
              </div>;
            })()}
          </BottomSheet>
          <BottomSheet open={!!sellPackageSheet} onClose={() => setSellPackageSheet(null)} title={T("送贈／販售套票","Gift / Sell package","パッケージを贈る／販売")}>
            {sellPackageSheet && (() => {
              const cust = state.users[sellPackageSheet.customerId];
              const available = servicePackages.filter(p => p.enabled);
              const selected = available.find(p => p.id === sellPackageSheet.packageId);
              const custPets = (cust?.pets || []).map(pid => state.pets[pid]).filter(Boolean);
              const pickedPet = state.pets[sellPackageSheet.petId] || null;
              // Resolve the actual price: flat → pkg.price; perSize → look up
              // the matching matrix row by pet weight + coat. Falls back to
              // pkg.price if the row has no number set.
              const resolvePrice = (pkg, pet) => {
                if (!pkg) return 0;
                if (pkg.pricing?.mode !== "perSize") return pkg.price || 0;
                if (!pet) return pkg.price || 0;
                const perSize = pkg.pricing.perSize || {};
                const perBreed = pkg.pricing.perBreed || {};
                const isLong = pet.coatLength === "long";
                const pick = (row) => Number(isLong ? row.longHair : row.shortHair) || Number(row.shortHair) || Number(row.longHair) || 0;
                const w = parseFloat(pet.weight) || 0;
                const services = tenant?.services || [];
                const linked = services.find(s => (pkg.serviceIds || []).includes(s.id) && s.matrixPricing) || services.find(s => s.matrixPricing);
                const legacyRow = (Array.isArray(linked?.matrixPricing?.sizes) ? linked.matrixPricing.sizes : []).find(r => w >= (r.minW ?? 0) && w <= (r.maxW ?? 999));
                const tier = petSizeTier(pet, tenant);
                const labels = [tier?.label, legacyRow?.label].filter(Boolean);
                // Special breed price overrides the size price when set.
                const sb = matchSpecialBreed(pet, tenant);
                if (sb && perBreed[sb.id]) { for (const lb of labels) { const br = perBreed[sb.id][lb]; if (br) { const bp = pick(br); if (bp > 0) return bp; } } }
                for (const lb of labels) { const row = perSize[lb]; if (row) return pick(row); }
                return pkg.price || 0;
              };
              const finalPrice = selected ? resolvePrice(selected, pickedPet) : 0;
              return (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("為","Add a package for","")} <strong style={{color:"#4A4A68"}}>{cust?.name || T("客戶","customer","お客様")}</strong> {T("加入套票。客戶會在 App 看到剩餘次數及到期日。","— the customer will see remaining visits and expiry in the App.","を追加。お客様はアプリで残り回数と有効期限を確認できます。")}</p>
                  {custPets.length > 0 && (
                    <div>
                      <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{T("毛孩（按體型計價會用到）","Pet (used for size-based pricing)","ペット（サイズ別料金に使用）")}</p>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {custPets.map(p => {
                          const sel = sellPackageSheet.petId === p.id;
                          return <button key={p.id} onClick={() => setSellPackageSheet(f => ({...f, petId: p.id}))} style={{padding:"6px 12px",borderRadius:99,border:"2px solid "+(sel ? "var(--c-primary)" : "var(--c-light)"),background:sel ? "var(--c-lighter)" : "#fff",color:sel ? "var(--c-primary)" : "#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{(p.type === "cat" ? "🐱 " : "🐶 ") + p.name + " · " + p.weight + "kg"}</button>;
                        })}
                      </div>
                    </div>
                  )}
                  {available.length === 0 ? (
                    <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,padding:"12px",textAlign:"center",background:"var(--c-lighter)",borderRadius:12,margin:0}}>{T("未設定套票。請先到「服務」分頁新增。","No packages set up. Add one in the Services tab first.","パッケージ未設定です。まず「サービス」タブで追加してください。")}</p>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {available.map(p => {
                        const tier = resolvePrice(p, pickedPet);
                        const totalV = (p.paidVisits || p.visits || 0) + (p.bonusVisits || 0);
                        const bonus = p.bonusVisits || 0;
                        return (
                          <label key={p.id} style={{display:"flex",alignItems:"center",gap:12,padding:12,background:sellPackageSheet.packageId===p.id?"var(--c-lighter)":"#FFFDFD",borderRadius:12,cursor:"pointer",border:"2px solid "+(sellPackageSheet.packageId===p.id?"var(--c-primary)":"var(--c-light)")}}>
                            <input type="radio" name="pkg" checked={sellPackageSheet.packageId===p.id} onChange={() => setSellPackageSheet(f => ({...f, packageId: p.id}))} style={{width:18,height:18,accentColor:"var(--c-primary)"}} />
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{p.name}{bonus > 0 && <span style={{fontSize:10,fontWeight:800,color:"#92400E",background:"#FEF3C7",borderRadius:6,padding:"2px 6px",marginLeft:6}}>{T("贈","+","+") + " " + bonus + " " + T("次","free visits","回サービス")}</span>}</p>
                              <p style={{fontSize:12,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{totalV + " " + T("次","visits","回") + " · $" + tier + " · " + (p.validityDays > 0 ? p.validityDays + " " + T("日有效","days valid","日間有効") : T("永久","Permanent","無期限"))}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {/* Payment + bookkeeping */}
                  {selected && (
                    <div style={{background:"#FFFDFD",border:"2px solid var(--c-light)",borderRadius:14,padding:12,display:"flex",flexDirection:"column",gap:10}}>
                      <label style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:10,background:sellPackageSheet.isGift?"#FEF3C7":"transparent",cursor:"pointer"}}>
                        <input type="checkbox" checked={!!sellPackageSheet.isGift} onChange={e => setSellPackageSheet(f => ({...f, isGift: e.target.checked}))} style={{transform:"scale(1.2)"}} />
                        <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{T("免費贈送（無需收款）","Free gift (no payment)","無料プレゼント（支払い不要）")}</span>
                      </label>
                      {!sellPackageSheet.isGift && <>
                        <div>
                          <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{T("付款方式","Payment method","支払い方法")}</p>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            {[["cash","💵 "+T("現金","Cash","現金")],["card","💳 "+T("信用卡","Card","クレジットカード")],["fps","📱 FPS"],["bank","🏦 "+T("銀行轉帳","Bank transfer","銀行振込")],["other",T("其他","Other","その他")]].map(([v,l]) => {
                              const sel = (sellPackageSheet.paymentMethod || "cash") === v;
                              return <button key={v} onClick={() => setSellPackageSheet(f => ({...f, paymentMethod: v}))} style={{padding:"6px 12px",borderRadius:99,border:"2px solid "+(sel?"var(--c-primary)":"var(--c-light)"),background:sel?"var(--c-lighter)":"#fff",color:sel?"var(--c-primary)":"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{l}</button>;
                            })}
                          </div>
                        </div>
                        <Input label={T("實收金額 (留空 = 套票價格)","Amount received (blank = package price)","受領金額（空欄＝パッケージ価格）")} type="number" value={sellPackageSheet.manualAmount} onChange={e => setSellPackageSheet(f => ({...f, manualAmount: e.target.value}))} placeholder={"$" + finalPrice} hint={T("可填促銷折扣價，留空會用上面套票價","Enter a promo price, or leave blank to use the package price above","プロモ価格を入力、空欄なら上記パッケージ価格を使用")} />
                      </>}
                    </div>
                  )}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <Btn variant="secondary" onClick={() => setSellPackageSheet(null)}>{T("取消","Cancel","キャンセル")}</Btn>
                    <Btn disabled={!selected || (!sellPackageSheet.isGift && finalPrice <= 0) || (custPets.length > 0 && !sellPackageSheet.petId)} onClick={async () => {
                      try {
                        if (custPets.length > 0 && !sellPackageSheet.petId) {
                          toast(T("請揀邊隻毛孩用呢張套票","Please pick which pet to use this package for","このパッケージを使うペットを選んでください"), "error");
                          return;
                        }
                        const totalV = (selected.paidVisits || selected.visits || 0) + (selected.bonusVisits || 0);
                        const amountPaid = sellPackageSheet.isGift ? 0 : (parseFloat(sellPackageSheet.manualAmount) || finalPrice);
                        await purchaseCustomerPackage({
                          tenantId: user.tenantId,
                          customerId: sellPackageSheet.customerId,
                          packageId: selected.id,
                          visits: totalV,
                          validityDays: selected.validityDays,
                          amountPaid,
                          paymentMethod: sellPackageSheet.isGift ? null : (sellPackageSheet.paymentMethod || "cash"),
                          soldByUserId: user.id,
                          isGift: !!sellPackageSheet.isGift,
                          // Snapshot the bound pet so the package row
                          // remembers what size/coat the customer
                          // bought it for, even if the pet later
                          // changes weight.
                          petId: pickedPet?.id || null,
                          petBreed: pickedPet?.breed || null,
                          petWeight: pickedPet ? Number(pickedPet.weight) : null,
                          petCoat: pickedPet?.coatLength || null,
                        });
                        // Notify customer
                        pushNotif(setState, {
                          userId: sellPackageSheet.customerId,
                          type: "system",
                          title: sellPackageSheet.isGift ? T("新套票 🎁","New package 🎁","新しいパッケージ 🎁") : T("套票購買成功","Package purchased","パッケージ購入完了"),
                          body: (sellPackageSheet.isGift ? T("您已獲贈「","You've received the 「","「") : T("您已購買「","You've purchased the 「","「")) + selected.name + T("」套票，共 ","」 package, ","」パッケージを") + totalV + " " + T("次","visits","回") + (selected.validityDays > 0 ? T("，有效期 ",", valid for ","、有効期間 ") + selected.validityDays + " " + T("日","days","日間") : ""),
                          read: false,
                        });
                        toast(sellPackageSheet.isGift ? T("已送出套票 ✓","Package sent ✓","パッケージを送りました ✓") : (T("已收款","Payment received","入金済み") + " $" + amountPaid + " ✓"));
                        setSellPackageSheet(null);
                      } catch (e) { toast(e?.message || T("失敗","Failed","失敗しました"), "error"); }
                    }}><Check size={16}/> {selected && finalPrice > 0 ? (T("確認","Confirm","確認") + " $" + finalPrice) : T("確認送出","Confirm","送信を確認")}</Btn>
                  </div>
                </div>
              );
            })()}
          </BottomSheet>
          <BottomSheet open={!!managePkgSheet} onClose={() => setManagePkgSheet(null)} title={i18nT("own.crm.managePackage")}>
            {managePkgSheet && (() => {
              const reload = () => fetchCustomerPackages(managePkgSheet.customerId, user.tenantId).then(setManagePkgList).catch(() => {});
              return (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {managePkgList.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:14,margin:0}}>{i18nT("own.pkg.empty")}</p>}
                  {managePkgList.map(cp => {
                    const pkg = servicePackages.find(p => p.id === cp.packageId);
                    const expired = cp.expiresAt && new Date(cp.expiresAt).getTime() < Date.now();
                    const isRefunded = !!cp.refundedAt;
                    return (
                      <div key={cp.id} style={{padding:14,background:isRefunded?"#F3F3F8":"#FFFDFD",borderRadius:16,border:"2px solid "+(isRefunded?"#E5E7EB":"var(--c-light)"),opacity:isRefunded?0.7:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{pkg?.name || (i18nT("lang.name") === "English" ? "Package" : zhEn("套票", "Package"))}{isRefunded && <span style={{fontSize:10,fontWeight:800,color:"#991B1B",background:"#FEE2E2",borderRadius:6,padding:"2px 6px",marginLeft:6}}>{i18nT("own.pkg.refunded")}</span>}</p>
                            <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>
                              {(i18nT("lang.name") === "English" ? cp.remainingVisits + " / " + (cp.totalVisits || cp.remainingVisits) + i18nT("own.pkg.visits") : zhEn("剩 ", "Left ") + cp.remainingVisits + " / " + (cp.totalVisits || cp.remainingVisits) + zhEn(" 次", " times"))}
                              {cp.expiresAt && <span style={{color:expired?"#EF4444":"#8A8A9E"}}> · {expired ? i18nT("own.pkg.expired") : zhEn(cp.expiresAt.slice(0,10) + " 到期", i18nT("own.pkg.expires") + cp.expiresAt.slice(0,10))}</span>}
                              {cp.isGift ? " · " + i18nT("own.pkg.gift") : (cp.amountPaid > 0 ? " · $" + cp.amountPaid : "")}
                            </p>
                          </div>
                        </div>
                        {!isRefunded && <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <Btn size="sm" variant="secondary" onClick={() => setPkgUsageSheet(cp)}>{i18nT("own.pkg.usageLog")}</Btn>
                          <Btn size="sm" variant="secondary" onClick={async () => {
                            try { await extendCustomerPackage(cp.id, { addDays: 30 }); toast(i18nT("own.pkg.extendedToast").replace("{days}", "30")); reload(); } catch (e) { toast(e?.message || (i18nT("lang.name") === "English" ? "Failed" : zhEn("失敗", "Failed")), "error"); }
                          }}>{i18nT("own.pkg.extend30")}</Btn>
                          <Btn size="sm" variant="secondary" onClick={async () => {
                            const days = parseInt(window.prompt(i18nT("own.pkg.extendPrompt"), "60"));
                            if (!days || days <= 0) return;
                            try { await extendCustomerPackage(cp.id, { addDays: days }); toast(i18nT("own.pkg.extendedToast").replace("{days}", String(days))); reload(); } catch (e) { toast(e?.message || (i18nT("lang.name") === "English" ? "Failed" : zhEn("失敗", "Failed")), "error"); }
                          }}>{i18nT("own.pkg.extendCustom")}</Btn>
                          <Btn size="sm" variant="secondary" disabled={cp.remainingVisits >= (cp.totalVisits || cp.remainingVisits)} onClick={async () => {
                            if (!window.confirm(i18nT("own.pkg.confirmRestore"))) return;
                            try { await adjustCustomerPackageVisit(cp.id, 1, i18nT("own.pkg.restore1")); toast(i18nT("own.pkg.restoredToast")); reload(); } catch (e) { toast(e?.message || (i18nT("lang.name") === "English" ? "Failed" : zhEn("失敗", "Failed")), "error"); }
                          }}>{i18nT("own.pkg.restore1")}</Btn>
                          <Btn size="sm" variant="secondary" disabled={cp.remainingVisits <= 0} onClick={async () => {
                            if (!window.confirm(i18nT("own.pkg.confirmDeduct"))) return;
                            try { await adjustCustomerPackageVisit(cp.id, -1, i18nT("own.pkg.deduct1")); toast(i18nT("own.pkg.deductedToast")); reload(); } catch (e) { toast(e?.message || (i18nT("lang.name") === "English" ? "Failed" : zhEn("失敗", "Failed")), "error"); }
                          }}>{i18nT("own.pkg.deduct1")}</Btn>
                          <Btn size="sm" variant="secondary" onClick={async () => {
                            const raw = window.prompt(T("調整次數（正數加，負數扣）：","Adjust visits (positive = add, negative = deduct):","回数を調整（正＝追加、負＝差引）："), "-1");
                            if (raw == null) return;
                            const delta = parseInt(raw, 10);
                            if (!Number.isFinite(delta) || delta === 0) { toast(T("請輸入非零整數","Please enter a non-zero integer","0以外の整数を入力してください"), "error"); return; }
                            const reason = window.prompt(T("原因（會記錄）：","Reason (will be logged):","理由（記録されます）："), delta > 0 ? T("手動加","Manually added","手動追加") + " " + delta + " " + T("次","visits","回") : T("手動扣","Manually deducted","手動差引") + " " + Math.abs(delta) + " " + T("次","visits","回")) || "";
                            try {
                              const res = await adjustCustomerPackageVisit(cp.id, delta, reason);
                              toast(T("已調整：剩","Adjusted: remaining","調整しました：残り") + " " + (res?.newRemaining ?? "?") + " " + T("次 ✓","visits ✓","回 ✓"));
                              reload();
                            } catch (e) { toast(e?.message || T("失敗","Failed","失敗しました"), "error"); }
                          }}>⚖️ {T("調整","Adjust","調整")}</Btn>
                          <Btn size="sm" variant="danger" onClick={async () => {
                            const reason = window.prompt(T("退款原因（會記錄）：","Refund reason (will be logged):","返金理由（記録されます）："), T("客戶要求取消","Customer requested cancellation","お客様のキャンセル希望"));
                            if (reason == null) return;
                            const amt = parseFloat(window.prompt(T("退款金額：","Refund amount:","返金額："), String(cp.amountPaid || 0)));
                            if (isNaN(amt)) return;
                            try { await refundCustomerPackage(cp.id, { amount: amt, reason }); toast(T("已標記退款","Marked as refunded","返金として記録") + " $" + amt); reload(); } catch (e) { toast(e?.message || T("失敗","Failed","失敗しました"), "error"); }
                          }}>💸 {T("退款","Refund","返金")}</Btn>
                        </div>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </BottomSheet>
          <BottomSheet open={!!pkgUsageSheet} onClose={() => setPkgUsageSheet(null)} title={T("套票使用紀錄","Package usage log","パッケージ利用履歴")}>
            {pkgUsageSheet && (() => {
              const cp = pkgUsageSheet;
              const log = Array.isArray(cp.usageLog) ? cp.usageLog : [];
              const sorted = [...log].sort((a, b) => String(b?.at || "").localeCompare(String(a?.at || "")));
              const pkgName = servicePackages.find(p => p.id === cp.packageId)?.name || T("套票","Package","パッケージ");
              const custName = state.users[cp.customerId]?.name || T("(未知客戶)","(Unknown customer)","(不明なお客様)");
              return (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{background:"var(--c-lighter)",borderRadius:14,padding:12}}>
                    <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{pkgName}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:"4px 0 0",fontWeight:700}}>{custName} · {T("剩","Remaining","残り")} {cp.remainingVisits} / {(cp.totalVisits || cp.remainingVisits)} {T("次","visits","回")}</p>
                  </div>
                  {sorted.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",margin:0}}>{T("暫無使用紀錄","No usage records yet","利用履歴はまだありません")}</p>}
                  {sorted.map((e, i) => {
                    const isManual = e?.source === "manual_adjust";
                    const at = String(e?.at || "").slice(0, 16).replace("T", " ");
                    const delta = Number(e?.delta);
                    const aptId = e?.aptId;
                    const apt = aptId ? state.appointments.find(a => a.id === aptId) : null;
                    const byName = e?.by ? (state.users[e.by]?.name || T("員工","Staff","スタッフ")) : null;
                    return (
                      <div key={i} style={{padding:12,borderRadius:12,border:"1px solid var(--c-light)",background:"#FFFDFD",display:"flex",flexDirection:"column",gap:4}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                          <span style={{fontSize:12,fontWeight:800,color:"#8A8A9E"}}>{at || "—"}</span>
                          <span style={{fontSize:13,fontWeight:900,color: (Number.isFinite(delta) && delta > 0) ? "#10B981" : "#DC2626"}}>
                            {isManual ? (delta > 0 ? "+" + delta : String(delta)) + " " + T("次","visits","回") : "−1 " + T("次","visit","回")}
                          </span>
                        </div>
                        <p style={{fontSize:13,fontWeight:700,color:"#4A4A68",margin:0}}>
                          {isManual ? ("✋ " + T("手動調整","Manual adjustment","手動調整") + (byName ? zhEn("（", " (") + byName + zhEn("）", ")") : "")) : ("📅 " + T("預約扣減","Booking deduction","予約による差引") + (apt ? zhEn("（", " (") + apt.date + " " + apt.time + (apt.service ? " · " + apt.service : "") + zhEn("）", ")") : (aptId ? zhEn("（apt#", " (apt#") + String(aptId).slice(0,6) + zhEn("）", ")") : "")))}
                        </p>
                        {e?.reason && <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700}}>{T("原因：","Reason: ","理由：")}{e.reason}</p>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </BottomSheet>
          <BottomSheet open={!!topupSheet} onClose={() => setTopupSheet(null)} title={T("手動加值錢包","Manual wallet top-up","ウォレット手動チャージ")}>
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <input type="number" style={{border:"2px solid var(--c-light)",borderRadius:24,padding:"20px",fontSize:32,fontWeight:900,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box",color:"var(--c-primary)",background:"#FFFDFD",transition:"border 0.3s"}} placeholder={T("輸入金額","Enter amount","金額を入力")} value={topupAmt} onChange={e => setTopupAmt(e.target.value)} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-light)"} />
              <div style={{display:"flex",gap:10}}>
                {[100,200,500].map(v => <button key={v} onClick={() => setTopupAmt(String(v))} style={{flex:1,padding:"14px 8px",borderRadius:16,border:"2px solid var(--c-light)",background:topupAmt===String(v)?"var(--c-lighter)":"#FFFDFD",cursor:"pointer",fontWeight:800,fontSize:16,color:topupAmt===String(v)?"var(--c-primary)":"#8A8A9E",transition:"all 0.2s"}}>{"$" + v}</button>)}
              </div>
              {(() => {
                const tiers = getWalletBonusTiers(tenant).filter(t => (Number(t?.spend) || 0) > 0).sort((a, b) => (Number(a.spend) || 0) - (Number(b.spend) || 0));
                if (!tiers.length) return null;
                const amt = parseInt(topupAmt) || 0;
                const b = walletBonusFor(amt, tenant);
                return (
                  <div style={{background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:14,padding:"10px 12px"}}>
                    {b > 0
                      ? <p style={{fontSize:14,fontWeight:900,color:"#166534",margin:"0 0 4px"}}>{zhEn("🎁 送 $"+b+" — 實際到帳 $"+(amt+b), "🎁 Bonus $"+b+" — you receive $"+(amt+b))}</p>
                      : <p style={{fontSize:12,fontWeight:800,color:"#166534",margin:"0 0 4px"}}>{zhEn("🎁 儲值優惠","🎁 Top-up bonus")}</p>}
                    <p style={{fontSize:11,color:"#059669",fontWeight:700,margin:0}}>{tiers.map(t => zhEn("儲 $" + (Number(t.spend) || 0) + " 送 $" + (Number(t.bonus) || 0), "Top up $" + (Number(t.spend) || 0) + ", get $" + (Number(t.bonus) || 0))).join(" · ")}</p>
                  </div>
                );
              })()}
              <Btn onClick={() => doTopup(topupSheet)} style={{width:"100%",marginTop:8,padding:"18px"}}>{T("確認加值","Confirm top-up","チャージを確認")}</Btn>
            </div>
          </BottomSheet>
        </>}

        {tab === "wainbox" && user.role === "owner" && <WaInboxView user={user} state={state} onOpenCustomer={(id) => { setCrmUser(id); setCrmTab("info"); setTab("crm"); }} onOpenAiSettings={() => { setSettingsSubtab("messaging"); setSettingsSec(m => ({ ...m, messaging: "channels" })); setMessagingAdvanced(true); setTab("settings"); }} />}

        {tab === "pos" && (tenant?.posEnabled || posLocked) && (
          <PosApp state={state} setState={setState} user={user} tenant={tenant} sym={cur(tenant)} toast={toast} plans={getPlans(state.platformConfig)} onUpgrade={user.role === "owner" ? openPlanPage : null}
            helpers={{ aptRemainingBalance, aptSubtotal, aptDiscount, checkoutMethodsOf, isCashMethod, getPricingDetail, computeAddonPrice, walletBonusFor, saveAppointment: upsertAppointment }} />
        )}

        {tab === "marketing" && user.role === "owner" && (
          <MarketingTab tenant={tenant} user={user} state={state} setState={setState} />
        )}

        {tab === "analytics" && <FeatureGate tenantId={user.tenantId} feature="analytics" state={state} setState={setState}>
          {(() => {
            // Each report block is keyed by an id so the owner can reorder
            // them via drag handles. The order is persisted to
            // localStorage (`petos:report-order`). Reordering rewrites the
            // array and immediately re-renders.
            const REPORTS = {};
            REPORTS.packages = (() => {
              const cps = tenantCustomerPackages || [];
              const today = dateNow();
              const startOfMonth = today.slice(0, 7) + "-01";
              // `purchasedAt` is stored as full UTC timestamp. Bucket
              // by tenant-LOCAL date so a 00:30 UTC purchase (08:30 HKT
              // on the 1st) isn't mis-counted to the previous month.
              const sold = cps.filter(c => {
                if (c.refundedAt || !c.purchasedAt) return false;
                const localDate = localDateStr(new Date(c.purchasedAt));
                return localDate >= startOfMonth;
              });
              const monthRevenue = sold.reduce((s, c) => s + (Number(c.amountPaid) || 0), 0);
              const activeCount = cps.filter(c => c.enabled && c.remainingVisits > 0 && !c.refundedAt).length;
              const expiringSoon = cps.filter(c => c.enabled && c.expiresAt && !c.refundedAt && new Date(c.expiresAt).getTime() - Date.now() < 14 * 86400000 && new Date(c.expiresAt).getTime() > Date.now()).length;
              const lowBalance = cps.filter(c => c.enabled && c.remainingVisits > 0 && !c.refundedAt && c.remainingVisits <= (servicePackages.find(p => p.id === c.packageId)?.lowBalanceThreshold || 2)).length;
              const visitsUsed = cps.reduce((s, c) => s + (Array.isArray(c.usageLog) ? c.usageLog.length : 0), 0);
              const visitsRemaining = cps.filter(c => c.enabled && !c.refundedAt).reduce((s, c) => s + (Number(c.remainingVisits) || 0), 0);
              return (
                <Card style={{padding:20}}>
                  <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 14px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📦 {T("套票概況","Package overview","パッケージ概況")} <HelpTip topic="packages" /></h3>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>{sold.length}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("本月銷售","Sold this month","今月の販売")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>${monthRevenue}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("本月收入","Revenue this month","今月の収入")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#7C3AED",margin:"0 0 2px"}}>{activeCount}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("有效套票","Active packages","有効パッケージ")}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                    <div style={{textAlign:"center",padding:"10px 6px",background:"#FFFDFD",borderRadius:12,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#10B981",margin:"0 0 2px"}}>{visitsUsed}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("已使用次數","Visits used","利用済み回数")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 6px",background:"#FFFDFD",borderRadius:12,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#3B82F6",margin:"0 0 2px"}}>{visitsRemaining}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("剩餘總次數","Visits remaining","残り回数合計")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 6px",background:expiringSoon+lowBalance>0?"#FEE2E2":"#FFFDFD",borderRadius:12,border:"1px solid "+(expiringSoon+lowBalance>0?"#FECACA":"var(--c-light)")}}>
                      <p style={{fontSize:16,fontWeight:900,color:expiringSoon+lowBalance>0?"#991B1B":"#9CA3AF",margin:"0 0 2px"}}>{expiringSoon + lowBalance}</p>
                      <p style={{fontSize:10,color:expiringSoon+lowBalance>0?"#991B1B":"#8A8A9E",margin:0,fontWeight:700}}>{T("需跟進","Need follow-up","要フォロー")}</p>
                    </div>
                  </div>
                </Card>
              );
            })();
            REPORTS.eod = (() => {
              // ZERO drift: use the EXACT same array the schedule chip +
              // agenda per-group sum read from. If those two screens
              // agree (they do, both read rangedApts), the EOD card now
              // mathematically cannot disagree with them.
              const liveToday  = rangedApts;
              const allInRange = allApts.filter(a => a.date && a.date >= reportRange.from && a.date <= reportRange.to);
              const doneToday  = liveToday.filter(a => a.status === "done");
              const noShow     = allInRange.filter(a => a.status === "noshow");
              const cancelled  = allInRange.filter(a => a.status === "cancelled");
              const sum = (arr, fn) => arr.reduce((s,a) => s + fn(a), 0);
              const grossToday    = sum(liveToday, aptRevenue);
              const totalDiscount = sum(liveToday, aptDiscount);
              // ── Deposits collected today: only money ACTUALLY received.
              //   Was previously summing `a.deposit > 0` across all apts,
              //   which double-counted owed-but-uncollected deposits AND
              //   bundled cancelled-late-fee deposits into 訂金總額. Now
              //   uses aptDepositReceived which returns 0 for cancelled /
              //   noshow and excludes unconfirmed external deposits.
              const depositsToday = sum(liveToday, aptDepositReceived);
              // M1: when payment_log has entries, balance collected =
              // net sum (payment − refund), excluding the deposit portion
              // already counted above. Falls back to the derived formula
              // for legacy apts that pre-date the log.
              const balanceColl   = sum(doneToday.filter(a => a.paid), a => {
                const logged = aptCollectedFromLog(a);
                if (logged != null) return Math.max(0, logged);
                return Math.max(0, aptRevenue(a) - aptDepositReceived(a));
              });
              // Unpaid = revenue − deposit-received − collected-via-log.
              const unpaid        = sum(doneToday.filter(a => !a.paid), a => {
                const logged = aptCollectedFromLog(a) || 0;
                return Math.max(0, aptRevenue(a) - aptDepositReceived(a) - logged);
              });
              // 罰款 — combine no-show surcharges with retained
              // late-fees on cancelled apts. Previously no-show "fee
              // surcharges" overlapped with their deposit field; we now
              // pick whichever is larger so a single fee isn't counted
              // twice when the owner stored it both ways.
              const noShowFees = sum(noShow, a => {
                const surTotal = (a.surcharges||[]).reduce((p,q)=>p+(Number(q.amount)||0),0);
                const retained = Number(a.deposit) || 0;
                return Math.max(surTotal, retained);
              });
              const cancelFees = sum(cancelled, a => Number(a.deposit) || 0);
              // 套票使用 — informational. Count of apts that redeemed
              // a package in range + total realized value (already excluded
              // from grossToday since aptRevenue zeros the package portion).
              const pkgUsedToday = liveToday.filter(a => a.customerPackageId);
              const pkgRealizedToday = pkgUsedToday.reduce((s, a) => s + aptPackageRealizedValue(a, tenantCustomerPackages), 0);
              const isSingleDay = reportRange.from === reportRange.to;
              const rangeLabel = isSingleDay ? reportRange.from : (reportRange.from + " → " + reportRange.to);
              const avgTicket = liveToday.length ? Math.round(grossToday / liveToday.length) : 0;
              return (
                <Card style={{padding:20,background:"linear-gradient(135deg,#FFFFFF,var(--c-lighter))"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8,flexWrap:"wrap"}}>
                    <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📅 {isSingleDay ? T("日結對帳","Daily reconciliation","日次精算") : T("期間對帳","Period reconciliation","期間精算")} — {rangeLabel} <HelpTip topic="analytics" /></h3>
                    <span style={{fontSize:12,fontWeight:800,color:"var(--c-primary)"}}>{doneToday.length} {T("已完成","done","完了")} · {liveToday.length} {T("總預約","total","予約合計")}</span>
                  </div>
                  {/* Hero row — total revenue stands alone so the owner
                       gets one obvious anchor number that matches the
                       schedule chip + agenda group sums. */}
                  <div style={{background:"linear-gradient(135deg,#10B981,#059669)",color:"#fff",borderRadius:16,padding:"14px 18px",marginBottom:12,boxShadow:"0 8px 20px rgba(16,185,129,0.25)"}}>
                    <p style={{fontSize:12,fontWeight:800,margin:"0 0 2px",opacity:0.92}}>{isSingleDay ? T("當日總收入","Total revenue today","当日総収入") : T("區間總收入","Total revenue (range)","期間総収入")}</p>
                    <p style={{fontSize:30,fontWeight:900,margin:0,letterSpacing:-0.5,fontVariantNumeric:"tabular-nums"}}>${grossToday.toLocaleString()}</p>
                    <p style={{fontSize:11,fontWeight:700,margin:"4px 0 0",opacity:0.85}}>{T("平均客單","Avg ticket","平均客単価")} ${avgTicket.toLocaleString()} · {T("客戶","Customers","お客様")} {customers.length}</p>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {[
                      [T("已收訂金","Deposits collected","受領済み内金"),          "$"+depositsToday, "#3B82F6"],
                      [T("已收金額","Collected","受領済み"),          "$"+balanceColl,   "#7C3AED"],
                      [T("待收金額","Owed","未収"),          "$"+unpaid,         unpaid>0?"#EF4444":"#10B981"],
                      [T("折扣總額","Total discount","割引合計"),          "−$"+totalDiscount,"#D97706"],
                      [T("罰款合計","Total penalties","罰金合計"),          "$"+(noShowFees + cancelFees), "#92400E"],
                      [T("套票使用","Package usage","パッケージ利用"),          pkgUsedToday.length + " " + T("次","visits","回") + " · $" + Math.round(pkgRealizedToday), "#0369A1"],
                    ].map(([l,v,c],i) => (
                      <div key={i} style={{background:"#FFFDFD",borderRadius:14,padding:"10px 14px",border:"1px solid var(--c-light)"}}>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 2px",fontWeight:700}}>{l}</p>
                        <p style={{fontSize:16,fontWeight:900,color:c,margin:0,fontVariantNumeric:"tabular-nums"}}>{v}</p>
                      </div>
                    ))}
                  </div>
                  {(noShow.length > 0 || cancelled.length > 0) && (
                    <p style={{fontSize:11,color:"#8A8A9E",margin:"10px 0 0",fontWeight:700}}>{"⚠️ no-show " + noShow.length + " " + T("單","·","件") + " " + T("取消","cancelled","キャンセル") + " " + cancelled.length + " " + T("單","","件")}</p>
                  )}
                  {pkgUsedToday.length > 0 && (
                    <p style={{fontSize:10,color:"#0369A1",margin:"8px 0 0",fontWeight:700,opacity:0.8,lineHeight:1.4}}>📦 {T("套票銷售時已入帳，本次認列","Already booked at point of sale; recognized here","パッケージ販売時に計上済み。今回計上")} ${Math.round(pkgRealizedToday)} {T("不重複計入區間收入","not double-counted in period revenue","は期間収入に重複計上しません")}</p>
                  )}
                </Card>
              );
            })();
            // depositPending → 通知中心. Separate stats card removed — its
            // 4 tiles are now folded into REPORTS.eod's hero + sub-grid.
            REPORTS.export = (
              <Btn variant="secondary" onClick={() => {
              // CSV 金額 column uses the same aptRevenue helper as the
              // dashboard tiles — including surcharges and subtracting
              // discount — so exports reconcile with on-screen totals.
              const rows = [[T("日期","Date","日付"),T("服務","Service","サービス"),T("金額","Amount","金額"),T("狀態","Status","ステータス"),T("員工","Staff","スタッフ"),T("客戶","Customer","お客様")],...rangedApts.map(a => [a.date, a.service, aptRevenue(a), a.status, state.users[a.staffId]?.name||"", state.users[a.customerId]?.name||""])];
              const csv = rows.map(r => r.join(",")).join("\n");
              const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csv); a.download = "PettoBook_" + reportRange.from + "_" + reportRange.to + ".csv"; a.click();
              toast(T("已匯出 CSV 報表","CSV report exported","CSVレポートを出力しました"));
            }} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%"}}><Package size={16} strokeWidth={2.5} /> {T("匯出 CSV","Export CSV","CSVエクスポート")} ({reportRange.from} → {reportRange.to})</Btn>
            );
            REPORTS.weekRev = (
            <Card style={{padding:24}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 20px",color:"#4A4A68"}}>{T("近7日每日營收","Daily revenue (last 7 days)","直近7日間の日次売上")} 📊</h3>
              <div style={{display:"flex",alignItems:"flex-end",gap:8,height:140}}>
                {weekRevData.map((v, i) => {
                  const d = new Date(); d.setDate(d.getDate() - (6 - i));
                  const dayLabel = DAYS[d.getDay()];
                  const isToday = i === 6;
                  return (
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                      <span style={{fontSize:11,color:isToday?"var(--c-primary)":"#8A8A9E",fontWeight:800}}>{v > 0 ? "$"+v : ""}</span>
                      <div style={{width:"100%",background:isToday?"linear-gradient(to top,var(--c-grad2),var(--c-primary))":"linear-gradient(to top,#E0E0F0,#C0C0D8)",borderRadius:99,minHeight:8,height:maxRev > 0 ? ((v/maxRev)*90)+"px" : "8px",boxShadow:isToday?"0 4px 10px var(--c-primary-30)":"none",transition:"height 0.3s"}} />
                      <span style={{fontSize:12,color:isToday?"var(--c-primary)":"#8A8A9E",fontWeight:isToday?900:700}}>{dayLabel}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
            );
            REPORTS.monthly = (() => {
              // 月度銷售 — full month by date WITHOUT a 31-row wall: a 7-col
              // calendar heat-grid (max 6 rows) + tap-a-day inline drilldown.
              // Own month scope (like eod/packages), independent of the range
              // picker above.
              const [yy, mm] = reportMonth.split("-").map(Number);
              const daysInMonth = new Date(yy, mm, 0).getDate();
              const firstDow = new Date(yy, mm - 1, 1).getDay();
              const monthApts = allApts.filter(a =>
                a.date && a.date.startsWith(reportMonth)
                && a.kind !== "event"
                && a.status !== "cancelled" && a.status !== "noshow");
              const byDay = {};
              for (const a of monthApts) {
                const d = Number(a.date.slice(8, 10));
                (byDay[d] = byDay[d] || { rev: 0, apts: [] });
                byDay[d].rev += aptRevenue(a);
                byDay[d].apts.push(a);
              }
              // Future dates hold UNCONFIRMED income (bookings not yet done) —
              // summarize + colour them separately from realized revenue.
              const todayStr = dateNow();
              const todayDayNum = todayStr.startsWith(reportMonth) ? Number(todayStr.slice(8, 10)) : (reportMonth < todayStr.slice(0, 7) ? 32 : 0);
              const confirmedEntries = Object.entries(byDay).filter(([d]) => Number(d) <= todayDayNum);
              const monthRev = confirmedEntries.reduce((s, [, d]) => s + d.rev, 0);
              const futureRev = Object.entries(byDay).filter(([d]) => Number(d) > todayDayNum).reduce((s, [, d]) => s + d.rev, 0);
              const activeDays = confirmedEntries.filter(([, d]) => d.rev > 0).length;
              const maxDayRev = Math.max(...Object.values(byDay).map(d => d.rev), 1);
              const bestDay = confirmedEntries.sort((a, b) => b[1].rev - a[1].rev)[0];
              const fmt$ = (v) => v >= 10000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + v;
              const shiftMonth = (delta) => {
                const d = new Date(yy, mm - 1 + delta, 1);
                setReportMonth(d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"));
                setMonthDayPick(null);
              };
              const pickedApts = monthDayPick && monthDayPick.startsWith(reportMonth)
                ? (byDay[Number(monthDayPick.slice(8, 10))]?.apts || []).slice().sort((a, b) => (a.time || "").localeCompare(b.time || ""))
                : null;
              const isCurrentMonth = reportMonth === dateNow().slice(0, 7);
              return (
                <Card style={{padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("月度銷售","Monthly sales","月次売上")} 🗓️</h3>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <button onClick={() => shiftMonth(-1)} style={{width:30,height:30,borderRadius:99,border:"none",background:"var(--c-light)",color:"var(--c-primary)",fontWeight:900,cursor:"pointer"}}>‹</button>
                      <span style={{fontSize:14,fontWeight:900,color:"#4A4A68",minWidth:76,textAlign:"center"}}>{fmtYM(yy+"-"+mm)}</span>
                      <button onClick={() => shiftMonth(1)} disabled={isCurrentMonth} style={{width:30,height:30,borderRadius:99,border:"none",background:isCurrentMonth?"#F3F4F6":"var(--c-light)",color:isCurrentMonth?"#C7C7CC":"var(--c-primary)",fontWeight:900,cursor:isCurrentMonth?"default":"pointer"}}>›</button>
                    </div>
                  </div>
                  {/* Summary chips */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0,1fr))",gap:8,marginBottom:14}}>
                    {[[T("營收","Revenue","売上"), "$" + monthRev.toLocaleString()],[T("單數","Bookings","件数"), String(monthApts.length)],[T("日均","Daily avg","日平均"), activeDays ? fmt$(Math.round(monthRev / activeDays)) : "$0"],[T("最高日","Best day","最高日"), bestDay && bestDay[1].rev > 0 ? zhEn(bestDay[0] + "日 ", "Day " + bestDay[0] + " ") + fmt$(bestDay[1].rev) : "—"]].map(([l, v]) => (
                      <div key={l} style={{background:"var(--c-lighter)",borderRadius:14,padding:"10px 8px",textAlign:"center"}}>
                        <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:0}}>{l}</p>
                        <p style={{fontSize:14,fontWeight:900,color:"var(--c-primary)",margin:"2px 0 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</p>
                      </div>
                    ))}
                  </div>
                  {/* Calendar heat-grid */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(7, minmax(0,1fr))",gap:4,marginBottom:4}}>
                    {DAYS.map(d => <div key={d} style={{textAlign:"center",fontSize:10,fontWeight:800,color:"#AFAFC4",padding:"2px 0"}}>{d}</div>)}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(7, minmax(0,1fr))",gap:4}}>
                    {Array.from({length: firstDow}, (_, i) => <div key={"b"+i} />)}
                    {Array.from({length: daysInMonth}, (_, i) => {
                      const day = i + 1;
                      const ds = reportMonth + "-" + String(day).padStart(2, "0");
                      const rev = byDay[day]?.rev || 0;
                      const heat = rev / maxDayRev; // 0..1
                      const picked = monthDayPick === ds;
                      const isToday = ds === todayStr;
                      const isFuture = ds > todayStr;
                      // Realized income = pink; FUTURE (unconfirmed) = blue.
                      const bg = rev > 0
                        ? (isFuture ? `rgba(59,130,246,${0.10 + heat * 0.45})` : `rgba(255,107,139,${0.12 + heat * 0.55})`)
                        : "#FFFDFD";
                      const strong = heat > 0.55;
                      return (
                        <button key={day} onClick={() => setMonthDayPick(picked ? null : ds)} style={{
                          border: picked ? "2px solid var(--c-primary)" : (isToday ? "2px solid var(--c-primary)" : "1px solid var(--c-light)"),
                          boxShadow: isToday ? "0 0 0 2px var(--c-primary-30)" : "none",
                          borderRadius:10, padding:"5px 2px 4px", cursor:"pointer", minWidth:0, position:"relative",
                          background: bg,
                          display:"flex", flexDirection:"column", alignItems:"center", gap:1,
                        }}>
                          {isToday
                            ? <span style={{fontSize:10,fontWeight:900,lineHeight:1,background:"var(--c-primary)",color:"#fff",borderRadius:99,padding:"1px 5px"}}>{day}</span>
                            : <span style={{fontSize:10,fontWeight:800,color: strong ? "#fff" : "#8A8A9E",lineHeight:1}}>{day}</span>}
                          <span style={{fontSize:9,fontWeight:900,color: strong ? "#fff" : (rev > 0 ? (isFuture ? "#2563EB" : "var(--c-primary)") : "#D9D9E3"),lineHeight:1.1,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rev > 0 ? fmt$(rev) : "·"}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Legend — realized vs unconfirmed-future + today ring */}
                  <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginTop:8}}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#8A8A9E"}}><span style={{width:10,height:10,borderRadius:3,background:"rgba(255,107,139,0.55)"}} /> {T("已實現","Realized","確定")}</span>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#8A8A9E"}}><span style={{width:10,height:10,borderRadius:3,background:"rgba(59,130,246,0.45)"}} /> {T("未來預約（未確認）","Upcoming (unconfirmed)","未確定（今後の予約）")}</span>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,color:"#8A8A9E"}}><span style={{width:10,height:10,borderRadius:99,border:"2px solid var(--c-primary)"}} /> {T("今天","Today","今日")}</span>
                    {futureRev > 0 && <span style={{marginLeft:"auto",fontSize:10,fontWeight:900,color:"#2563EB"}}>{T("未來預約","Upcoming","今後")} +${futureRev.toLocaleString()}</span>}
                  </div>
                  {/* Tap-a-day drilldown — renders ONLY when a day is picked */}
                  {pickedApts && (
                    <div style={{marginTop:12,background:"var(--c-lighter)",borderRadius:16,padding:"12px 14px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0}}>{monthDayPick} · {pickedApts.length} {T("單","bookings","件")} · ${(byDay[Number(monthDayPick.slice(8,10))]?.rev || 0).toLocaleString()}</p>
                        <button onClick={() => setMonthDayPick(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#AFAFC4",fontWeight:900,fontSize:14}}>✕</button>
                      </div>
                      {pickedApts.length === 0
                        ? <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,margin:0}}>{T("呢日冇預約","No bookings this day","この日は予約なし")}</p>
                        : <div style={{maxHeight:220,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                            {pickedApts.map(a => {
                              const pet = state.pets[a.petId];
                              return (
                                <div key={a.id} onClick={() => setEditApt(a)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,background:"#fff",borderRadius:12,padding:"8px 10px",cursor:"pointer"}}>
                                  <div style={{minWidth:0,flex:1}}>
                                    <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(a.time || "") + " · " + (pet?.name || state.users[a.customerId]?.name || zhEn("客戶", "Customer")) + " · " + (a.service || "")}</p>
                                    <p style={{fontSize:10,fontWeight:700,color:"#8A8A9E",margin:"1px 0 0"}}>{(state.users[a.staffId]?.name || "") + (a.status === "done" ? "" : " · " + (SL[a.status] || a.status))}</p>
                                  </div>
                                  <span style={{fontSize:13,fontWeight:900,color:"var(--c-primary)",fontVariantNumeric:"tabular-nums"}}>${aptRevenue(a)}</span>
                                </div>
                              );
                            })}
                          </div>}
                    </div>
                  )}
                </Card>
              );
            })();
            REPORTS.payMix = (() => {
              // 收款方式月報 — how much actually came IN through each payment
              // method for the month. Source is每張預約嘅 payment_log (money
              // really collected), NOT the billed price — so this is what you
              // reconcile the cash drawer / card terminal against. Shares
              // reportMonth with the 月度銷售 card, with its own ‹ › so the
              // card still works wherever the owner drags it.
              const [pyy, pmm] = reportMonth.split("-").map(Number);
              const monthApts = allApts.filter(a =>
                a.date && a.date.startsWith(reportMonth)
                && a.kind !== "event"
                && a.status !== "cancelled" && a.status !== "noshow");
              const nameById = Object.fromEntries((tenant?.depositMethods || []).map(m => [m.id, m.name]));
              const FALLBACK = { cash:zhEn("💵 現金","💵 Cash"), card:zhEn("💳 信用卡","💳 Card"), fps:"FPS", wallet:zhEn("💳 客戶錢包","💳 Customer wallet"), manual:T("店家系統","In-shop","店舗システム"), octopus:zhEn("🐙 八達通","🐙 Octopus"), eps:"EPS", other:T("其他","Other","その他") };
              const rows = {};
              let totalIn = 0, totalOut = 0, payCount = 0;
              for (const a of monthApts) {
                for (const e of (Array.isArray(a.paymentLog) ? a.paymentLog : [])) {
                  const amt = Number(e?.amount) || 0;
                  if (!amt) continue;
                  const key = String(e?.methodId || e?.method || "other");
                  const label0 = nameById[e?.methodId] || nameById[e?.method] || e?.methodName || FALLBACK[e?.method] || String(e?.method || FALLBACK.other);
                  const label = zhEn(label0, ({ "現金": "Cash", "錢包": "Wallet", "積分": "Points", "💵 現金": "💵 Cash" })[label0] ?? label0);
                  const r = (rows[key] = rows[key] || { label, in: 0, out: 0, count: 0 });
                  r.label = label;
                  // Refund entries are stored positive with type:"refund" —
                  // same sign convention as aptCollectedNet (line ~804).
                  if (e?.type === "refund") { r.out += amt; totalOut += amt; }
                  else { r.in += amt; r.count++; totalIn += amt; payCount++; }
                }
              }
              const list = Object.values(rows).map(r => ({ ...r, net: r.in - r.out })).sort((a, b) => b.net - a.net);
              const netTotal = totalIn - totalOut;
              const shiftPayMonth = (delta) => {
                const d = new Date(pyy, pmm - 1 + delta, 1);
                setReportMonth(d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0"));
                setMonthDayPick(null);
              };
              const isCurMonth = reportMonth === dateNow().slice(0, 7);
              const PALETTE = ["#FF6B8B","#3B82F6","#10B981","#F59E0B","#8B5CF6","#EC4899","#14B8A6","#F97316"];
              return (
                <Card style={{padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,gap:8}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("收款方式","Payment methods","支払方法")} 💳</h3>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <button onClick={() => shiftPayMonth(-1)} style={{width:30,height:30,borderRadius:99,border:"none",background:"var(--c-light)",color:"var(--c-primary)",fontWeight:900,cursor:"pointer"}}>‹</button>
                      <span style={{fontSize:14,fontWeight:900,color:"#4A4A68",minWidth:76,textAlign:"center"}}>{fmtYM(pyy+"-"+pmm)}</span>
                      <button onClick={() => shiftPayMonth(1)} disabled={isCurMonth} style={{width:30,height:30,borderRadius:99,border:"none",background:isCurMonth?"#F3F4F6":"var(--c-light)",color:isCurMonth?"#C7C7CC":"var(--c-primary)",fontWeight:900,cursor:isCurMonth?"default":"pointer"}}>›</button>
                    </div>
                  </div>
                  <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:"0 0 14px",lineHeight:1.5}}>{T("按預約日期統計實際收到嘅錢（收款紀錄），已扣退款。","By appointment date, from the recorded payments (refunds deducted).","予約日ベースの実収金額（返金控除後）。")}</p>
                  {list.length === 0 ? (
                    <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"20px 0",margin:0}}>{T("呢個月未有收款紀錄","No payments recorded this month","今月の入金記録なし")}</p>
                  ) : (
                    <>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3, minmax(0,1fr))",gap:8,marginBottom:14}}>
                        {[[T("實收","Collected","実収"), "$" + netTotal.toLocaleString()],[T("筆數","Payments","件数"), String(payCount)],[T("退款","Refunds","返金"), totalOut > 0 ? "-$" + totalOut.toLocaleString() : "$0"]].map(([l, v]) => (
                          <div key={l} style={{background:"var(--c-lighter)",borderRadius:14,padding:"10px 8px",textAlign:"center"}}>
                            <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:0}}>{l}</p>
                            <p style={{fontSize:14,fontWeight:900,color:"var(--c-primary)",margin:"2px 0 0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</p>
                          </div>
                        ))}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {list.map((r, i) => {
                          const pct = netTotal > 0 ? Math.round((r.net / netTotal) * 100) : 0;
                          const color = PALETTE[i % PALETTE.length];
                          return (
                            <div key={r.label + i}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8,marginBottom:4}}>
                                <span style={{fontSize:13,fontWeight:900,color:"#4A4A68",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.label}</span>
                                <span style={{fontSize:13,fontWeight:900,color,whiteSpace:"nowrap"}}>${r.net.toLocaleString()} <span style={{fontSize:10,fontWeight:800,color:"#AFAFC4"}}>{pct}%</span></span>
                              </div>
                              <div style={{height:8,borderRadius:99,background:"var(--c-lighter)",overflow:"hidden"}}>
                                <div style={{width: Math.max(2, Math.min(100, Math.abs(pct))) + "%",height:"100%",borderRadius:99,background:color}} />
                              </div>
                              <p style={{fontSize:10,fontWeight:700,color:"#AFAFC4",margin:"3px 0 0"}}>
                                {r.count} {T("筆","payments","件")}
                                {r.out > 0 && " · " + T("退款","refunded","返金") + " -$" + r.out.toLocaleString()}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card>
              );
            })();
            REPORTS.topSvc = (() => {
              // Service ranking — count every booking in range that
              // referenced this service (by id OR legacy name fallback)
              // and isn't cancelled / noshow. Sorted desc, top-only
              // services with 0 bookings are hidden so a fresh shop
              // doesn't see a wall of empty bars. SPAs + add-ons are
              // shown separately below with their own bars so the owner
              // can see what add-on attaches most often.
              const liveRanged = rangedApts;
              const svcs = (tenant?.services || []).filter(s => s.kind !== "spa");
              const spas = (tenant?.services || []).filter(s => s.kind === "spa");
              const addons = tenant?.addons || [];
              const mainRows = svcs.map(svc => {
                const count = liveRanged.filter(a => a.svcId ? a.svcId === svc.id : a.service === svc.name).length;
                return { id: svc.id, name: svc.name, count };
              }).sort((a, b) => b.count - a.count);
              const spaRows = spas.map(sp => {
                const count = liveRanged.filter(a => Array.isArray(a.spaIds) && a.spaIds.includes(sp.id)).length;
                return { id: sp.id, name: "💆 " + sp.name, count };
              }).sort((a, b) => b.count - a.count);
              const addonRows = addons.map(ad => {
                const count = liveRanged.filter(a => Array.isArray(a.addons) && a.addons.includes(ad.name)).length;
                return { id: ad.id || ad.name, name: "✨ " + ad.name, count };
              }).sort((a, b) => b.count - a.count);
              const all = [...mainRows, ...spaRows, ...addonRows].filter(r => r.count > 0);
              const maxSvc2 = Math.max(...all.map(s => s.count), 1);
              return (
                <Card style={{padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14,gap:8,flexWrap:"wrap"}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("熱門服務排行","Top services","人気サービスランキング")} 🔥</h3>
                    <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>{reportRange.from === reportRange.to ? reportRange.from : (reportRange.from + " → " + reportRange.to)}</span>
                  </div>
                  {all.length === 0 ? (
                    <p style={{fontSize:13,color:"#AFAFC4",textAlign:"center",padding:"16px 0",margin:0,fontWeight:700}}>{T("此期間冇預約紀錄","No bookings in this period","この期間の予約記録はありません")}</p>
                  ) : all.map(s => (
                    <div key={s.id} style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:14,marginBottom:6}}>
                        <span style={{color:"#4A4A68",fontWeight:800}}>{s.name}</span>
                        <span style={{fontWeight:900,color:"#F59E0B",fontVariantNumeric:"tabular-nums"}}>{s.count + " " + T("單","","件")}</span>
                      </div>
                      <div style={{height:8,background:"var(--c-light)",borderRadius:99}}>
                        <div style={{height:8,borderRadius:99,background:"linear-gradient(to right,#FDE68A,#F59E0B)",width:((s.count/maxSvc2)*100)+"%"}} />
                      </div>
                    </div>
                  ))}
                </Card>
              );
            })();

            const persistOrder = (nextArr) => {
              setReportOrder(nextArr);
              try { localStorage.setItem("petos:report-order", JSON.stringify(nextArr)); } catch { /* ignore */ }
            };
            const reorderReport = (draggedId, targetId) => {
              if (!draggedId || !targetId || draggedId === targetId) return;
              const nextArr = [...reportOrder];
              const di = nextArr.indexOf(draggedId);
              const ti = nextArr.indexOf(targetId);
              if (di < 0 || ti < 0) return;
              const [moved] = nextArr.splice(di, 1);
              nextArr.splice(ti, 0, moved);
              persistOrder(nextArr);
            };
            const orderedIds = [
              ...reportOrder.filter(id => REPORTS[id]),
              ...Object.keys(REPORTS).filter(id => !reportOrder.includes(id)),
            ];
            const PRESETS = [
              ["today",T("今天","Today","今日")], ["yesterday",T("昨天","Yesterday","昨日")],
              ["thisWeek",T("本週","This week","今週")], ["lastWeek",T("上週","Last week","先週")],
              ["thisMonth",T("本月","This month","今月")], ["lastMonth",T("上月","Last month","先月")],
              ["thisYear",T("本年","This year","今年")], ["lastYear",T("上年","Last year","昨年")],
            ];
            return (
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {/* Date range picker — presets + custom from/to. Drives
                     stats + 匯出 CSV. eod / packages / weekRev cards
                     keep their own time scope. */}
                <Card style={{padding:14}}>
                  <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 8px",display:"flex",alignItems:"center",gap:6}}>
                    📅 {T("報表時段","Report period","レポート期間")} <span style={{fontSize:10,fontWeight:700,color:"#8A8A9E"}}>({T("總覽 / 匯出","Overview / Export","概要 / 出力")})</span>
                  </p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                    {PRESETS.map(([k, label]) => {
                      const on = reportRange.preset === k;
                      return (
                        <button key={k} onClick={() => setRangePreset(k)} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid " + (on ? "var(--c-primary)" : "var(--c-light)"),background: on ? "var(--c-primary)" : "#fff",color: on ? "#fff" : "#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{label}</button>
                      );
                    })}
                    <button onClick={() => persistReportRange({ ...reportRange, preset: "custom" })} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid " + (reportRange.preset === "custom" ? "var(--c-primary)" : "var(--c-light)"),background: reportRange.preset === "custom" ? "var(--c-primary)" : "#fff",color: reportRange.preset === "custom" ? "#fff" : "#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{T("自選","Custom","カスタム")}</button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <input type="date" value={reportRange.from} onChange={e => persistReportRange({ ...reportRange, preset: "custom", from: e.target.value })} style={{padding:"8px 10px",borderRadius:10,border:"1.5px solid var(--c-light)",fontSize:13,fontWeight:700,color:"#4A4A68",outline:"none"}} />
                    <input type="date" value={reportRange.to} onChange={e => persistReportRange({ ...reportRange, preset: "custom", to: e.target.value })} style={{padding:"8px 10px",borderRadius:10,border:"1.5px solid var(--c-light)",fontSize:13,fontWeight:700,color:"#4A4A68",outline:"none"}} />
                  </div>
                </Card>
                <p style={{fontSize:11,color:"#8A8A9E",margin:"0 4px",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                  <GripVertical size={12} strokeWidth={2.5} /> {T("拖動手柄重新排序","Drag the handle to reorder","ハンドルをドラッグして並べ替え")}
                </p>
                {orderedIds.map((id, idx) => (
                  <div
                    key={id}
                    draggable
                    onDragStart={(e) => { setDragReportId(id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch {} }}
                    onDragOver={(e) => { if (dragReportId && dragReportId !== id) { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch {} } }}
                    onDrop={(e) => { e.preventDefault(); reorderReport(dragReportId, id); setDragReportId(null); }}
                    onDragEnd={() => setDragReportId(null)}
                    style={{display:"flex",alignItems:"stretch",gap:8,opacity:dragReportId===id?0.4:1,transition:"opacity 0.15s"}}
                  >
                    <div title={T("拖動排序","Drag to reorder","ドラッグして並べ替え")} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"4px 2px",color:"#C7C7CC",cursor:"grab",touchAction:"none",flexShrink:0}}>
                      <GripVertical size={18} strokeWidth={2.5} />
                    </div>
                    <div style={{flex:1,minWidth:0}}>{REPORTS[id]}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </FeatureGate>}

        {tab === "inventory" && <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{T("庫存耗材管理","Inventory & supplies","在庫・消耗品管理")} <HelpTip content={{title:T("庫存管理 📦","Inventory 📦","在庫管理 📦"),body:T("· 追蹤洗毛精 / 護毛素 / 棉花 等耗材庫存。\n· 設定低庫存警告（≤ N 件時自動提示）。\n· 用嚟做訂貨提醒。","· Track supplies like shampoo / conditioner / cotton.\n· Set low-stock alerts (auto-notify at ≤ N units).\n· Use it as a reorder reminder.","· シャンプー／コンディショナー／コットンなどの在庫を管理。\n· 在庫僅少アラートを設定（N個以下で自動通知）。\n· 発注リマインダーに活用。")}} /></h2>
            <Btn size="sm" onClick={() => setEditInv({ name:"", qty:"", lowAlert:"" })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
          </div>
          {(tenant?.inventory||[]).map(i => { const low = i.qty <= i.lowAlert; const pct = Math.min((i.qty / Math.max(i.qty, i.lowAlert*3)) * 100, 100); return (
            <Card key={i.id} style={{padding:20,marginBottom:12,border:"2px solid "+(low?"#FECACA":"var(--c-light)"),background:low?"#FFF5F5":"#FFFDFD"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <p style={{fontWeight:800,fontSize:16,margin:0,color:"#4A4A68"}}>{i.name}</p>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {low && <Badge color="#DC2626" bg="#FEF2F2">{T("低庫存","Low stock","在庫僅少")}</Badge>}
                  <button onClick={() => setEditInv({ ...i, qty:String(i.qty), lowAlert:String(i.lowAlert) })} style={{background:"var(--c-light)",border:"none",borderRadius:12,padding:"6px 8px",cursor:"pointer",display:"flex"}}><Edit size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1,height:10,background:"var(--c-light)",borderRadius:99}}><div style={{height:10,borderRadius:99,background:low?"linear-gradient(to right,#FCA5A5,#EF4444)":"linear-gradient(to right,#6EE7B7,#10B981)",width:pct+"%"}} /></div>
                <span style={{fontSize:15,fontWeight:900,color:low?"#DC2626":"#4A4A68",minWidth:40,textAlign:"right"}}>{i.qty}</span>
              </div>
              <p style={{fontSize:12,color:"#8A8A9E",margin:"8px 0 0",fontWeight:700}}>{T("警戒線","Alert level","警告ライン") + ": " + i.lowAlert}</p>
            </Card>
          ); })}
          <BottomSheet open={!!editInv} onClose={() => setEditInv(null)} title={editInv?.id ? T("編輯庫存","Edit item","在庫を編集") : T("新增耗材","Add supply","消耗品を追加")}>
            {editInv && <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <Input label={T("耗材名稱","Item name","品名")} value={editInv.name} onChange={e => setEditInv(i => ({ ...i, name:e.target.value }))} placeholder={T("例: 洗毛精 (ml)","e.g. Shampoo (ml)","例：シャンプー (ml)")} />
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Input label={T("現有數量","Current qty","現在数量")} type="number" value={editInv.qty} onChange={e => setEditInv(i => ({ ...i, qty:e.target.value }))} />
                <Input label={T("警戒數量","Alert qty","警告数量")} type="number" value={editInv.lowAlert} onChange={e => setEditInv(i => ({ ...i, lowAlert:e.target.value }))} />
              </div>
              <div style={{display:"flex",gap:12,marginTop:8}}>
                {editInv.id && <Btn variant="danger" style={{flex:1}} onClick={() => { setState(s => ({ ...s, tenants:s.tenants.map(t => t.id===user.tenantId ? { ...t, inventory:t.inventory.filter(i=>i.id!==editInv.id) } : t) })); toast(T("耗材已刪除","Item deleted","品目を削除しました"),"warning"); setEditInv(null); }}>{T("刪除","Delete","削除")}</Btn>}
                <Btn style={{flex:1}} onClick={saveInvItem}>{T("儲存","Save","保存")}</Btn>
              </div>
            </div>}
          </BottomSheet>
        </>}

        {tab === "reviews" && <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <StatCard icon={Star} label={T("平均評分","Avg rating","平均評価")} value={state.reviews.length ? (state.reviews.reduce((a,r)=>a+r.rating,0)/state.reviews.length).toFixed(1)+"★" : "—"} color="#D97706" />
            <StatCard icon={ThumbsUp} label={T("評價數","Reviews","レビュー数")} value={state.reviews.length} color="#10B981" />
          </div>
          {staffList.map(s => {
            const rs = state.reviews.filter(r => r.staffId === s.id);
            const avg = rs.length ? (rs.reduce((a, r) => a + r.rating, 0) / rs.length).toFixed(1) : null;
            return <Card key={s.id} style={{padding:24,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:16}}>
                <div style={{width:56,height:56,background:"linear-gradient(135deg,#E0F2FE,#BAE6FD)",borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"#2563EB",fontSize:24}}>{s.name[0]}</div>
                <div><p style={{fontWeight:900,fontSize:18,margin:"0 0 6px",color:"#4A4A68"}}>{s.name}</p>{avg ? <div style={{display:"flex",alignItems:"center",gap:8}}><div style={{background:"#FFFBEB",padding:"4px 10px",borderRadius:99}}><Stars value={Math.round(parseFloat(avg))} size={14} /></div><span style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{avg + " (" + rs.length + T("則)",")",")")}</span></div> : <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700}}>{T("尚無評價","No reviews yet","レビューはまだありません")}</p>}</div>
              </div>
              {rs.length === 0 && <EmptyState icon={Star} title={T("暫無評價","No reviews yet","レビューはまだありません")} sub={T("完成美容後客戶可以留下評價","Customers can leave a review after grooming","トリミング後にお客様がレビューを残せます")} />}
              {rs.map((r, i) => (
                <div key={r.id} style={{padding:"16px 0",borderTop:i===0?"2px solid var(--c-light)":"1px dashed var(--c-light)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,alignItems:"center"}}><Stars value={r.rating} size={14} /><span style={{fontSize:12,color:"#AFAFC4",fontWeight:700}}>{r.date}</span></div>
                  <p style={{fontSize:15,color:"#4A4A68",margin:"0 0 8px",fontWeight:700,lineHeight:1.5}}>{r.comment}</p>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:800}}>{T("來自","From","送信者") + ": " + (state.users[r.customerId]?.name||"")}</p>
                </div>
              ))}
            </Card>;
          })}
        </>}

        {tab === "services" && user.role === "owner" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
            <h2 style={{fontWeight:900,fontSize:20,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{T("服務管理","Service management","サービス管理")} ✂️ <HelpTip topic="pricing" /></h2>
            <div style={{display:"flex",gap:8,flexShrink:0,flexWrap:"wrap"}}>
              <button onClick={() => setBulkOpen(true)} style={{display:"inline-flex",alignItems:"center",gap:6,background:"var(--c-primary)",color:"#fff",border:"none",borderRadius:99,padding:"8px 14px",fontSize:13,fontWeight:800,cursor:"pointer"}}>✏️ {T("批量定價","Bulk edit")}</button>
              <button onClick={openPriceOverview} style={{display:"inline-flex",alignItems:"center",gap:6,background:"var(--c-lighter)",color:"var(--c-primary)",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"8px 14px",fontSize:13,fontWeight:800,cursor:"pointer"}}>📊 {T("預覽及生成價目表","Preview & generate")}</button>
              <button onClick={() => setTplPickerOpen(true)} style={{display:"inline-flex",alignItems:"center",gap:6,background:"#fff",color:"#6A6A85",border:"1.5px solid var(--c-border)",borderRadius:99,padding:"8px 14px",fontSize:13,fontWeight:800,cursor:"pointer"}}>🧩 {T("套用設定模板","Apply template")}</button>
            </div>
          </div>

          {(() => {
            const allSvcs = tenant?.services || [];
            const matchesTab = (s) => {
              const types = (Array.isArray(s.petTypes) && s.petTypes.length) ? s.petTypes : [s.petType || "dog"];
              return types.includes(servicePetTab);
            };
            const byOrder = (a, b) => (Number(a.displayOrder)||0) - (Number(b.displayOrder)||0);
            const mainSvcs = allSvcs.filter(s => s.kind !== "spa" && matchesTab(s)).sort(byOrder);
            // SPA section is now ALWAYS visible (independent of the petType
            // tab) per user request — the previous "SPA as its own tab"
            // design made owners flip back and forth when comparing 主要服務
            // with SPA pricing. Pulls ALL spas across pet types since SPA
            // is typically pet-agnostic (same warm-water bath / machines).
            const spaSvcs = allSvcs.filter(s => s.kind === "spa").sort(byOrder);
            // Drag-and-drop reorder. Drop target id is set on dragover so we
            // can highlight the row; on drop we move the dragged service to
            // the target's position within its own kind/tab subset, then
            // rewrite displayOrder for every service in that subset so the
            // sort persists after sync.
            const reorderSvc = (draggedId, targetId, subset) => {
              if (!draggedId || !targetId || draggedId === targetId) return;
              const ids = subset.map(s => s.id);
              const di = ids.indexOf(draggedId);
              const ti = ids.indexOf(targetId);
              if (di < 0 || ti < 0) return;
              const next = [...subset];
              const [moved] = next.splice(di, 1);
              next.splice(ti, 0, moved);
              const orderMap = new Map(next.map((s, i) => [s.id, i + 1]));
              setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId
                ? { ...t, services: (t.services || []).map(sv => orderMap.has(sv.id) ? { ...sv, displayOrder: orderMap.get(sv.id) } : sv) }
                : t
              )}));
            };
            // Deep-clone a service into a fresh new row. Reuses matrix pricing,
            // duration, smart-pricing rules, and kind so the owner doesn't have
            // to rebuild the full体型 × 毛長 grid every time they need a
            // variant ("沖涼全套 + SPA", "沖涼剪毛 全手剪 — 高級版" etc.). The
            // clone opens immediately in the edit sheet so the owner can rename
            // and tweak price before saving.
            const copySvc = (s) => {
              const cloned = JSON.parse(JSON.stringify(s));
              cloned.id = uuid();
              cloned.name = (s.name || zhEn("服務", "Service")) + zhEn(" (複製)", " (copy)");
              // Bump displayOrder so the new row sits at the bottom of its
              // list section. Use the current max + 1 across BOTH service
              // and SPA kinds since they're stored on the same array.
              const maxOrder = Math.max(0, ...(tenant?.services || []).map(x => Number(x.displayOrder) || 0));
              cloned.displayOrder = maxOrder + 1;
              // Open in the edit sheet — same shape that the "+ 新增" CTA uses
              // so save handler works without modification.
              setEditSvc({
                ...cloned,
                basePrice: String(cloned.basePrice || 0),
                smartPricing: cloned.smartPricing || { enabled: false, rules: [] },
                pricingMode: cloned.pricingMode || "static",
                matrixPricing: cloned.matrixPricing || null,
                kind: cloned.kind || "service",
                description: cloned.description || "",
              });
              toast(T("已複製，請修改名稱及價錢後儲存","Copied — edit the name and price, then save","コピーしました。名前と価格を編集して保存してください"));
            };
            const renderRow = (s, i, last, subset) => (
              <div
                key={s.id}
                draggable
                onDragStart={(e) => { setDragSvcId(s.id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", s.id); } catch {} }}
                onDragOver={(e) => { if (dragSvcId && dragSvcId !== s.id) { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch {} } }}
                onDrop={(e) => { e.preventDefault(); reorderSvc(dragSvcId, s.id, subset); setDragSvcId(null); }}
                onDragEnd={() => setDragSvcId(null)}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 0",borderBottom:last?"none":"2px solid var(--c-light)",opacity:dragSvcId===s.id?0.4:1,transition:"opacity 0.15s",cursor:dragSvcId?"grabbing":"default"}}
              >
                <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                  <span title={T("拖動排序","Drag to reorder","ドラッグして並べ替え")} style={{cursor:"grab",color:"#C7C7CC",display:"flex",alignItems:"center",padding:"4px 2px",touchAction:"none"}}>
                    <GripVertical size={18} strokeWidth={2.5} />
                  </span>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:16,fontWeight:800,margin:"0 0 6px",color:"#4A4A68",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {s.name}
                      {s.customerVisible === false && <span style={{fontSize:10,fontWeight:800,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:6,padding:"2px 6px"}}>🔒 {T("員工專用","Staff only","スタッフ専用")}</span>}
                      {/* Pet-type chips — single small chip per applicable
                          type. Lets the owner see at a glance that a SPA
                          row covers both dogs and cats. */}
                      {(() => {
                        const types = (Array.isArray(s.petTypes) && s.petTypes.length) ? s.petTypes : [s.petType || "dog"];
                        const LBL = { dog: "🐶", cat: "🐱", small: "🐇" };
                        if (types.length === 1) return null; // single-type rows render the type via the tab they're in — chip would be noise
                        return types.map(t => (
                          <span key={t} style={{fontSize:11,fontWeight:800,color:"#5B21B6",background:"#EDE9FE",border:"1px solid #DDD6FE",borderRadius:99,padding:"1px 7px"}}>{LBL[t] || t}</span>
                        ));
                      })()}
                    </p>
                    {s.description && <p style={{fontSize:12,color:"#6A6A85",margin:"0 0 4px",fontWeight:600,lineHeight:1.4}}>{s.description}</p>}
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>
                      {T("底價","Base","基本料金") + " $" + s.basePrice + " · " + (s.pricingMode==="matrix" ? T("⚡ 體型/毛長矩陣計價","⚡ Size/coat matrix pricing","⚡ サイズ/被毛マトリクス料金") : s.smartPricing?.enabled ? T("⚡智能加價","⚡ Smart pricing","⚡ スマート加算") : T("固定價格","Fixed price","固定料金"))}
                    </p>
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={() => copySvc(s)} title={T("複製服務","Copy service","サービスをコピー")} style={{background:"var(--c-lighter)",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Copy size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                  <button onClick={() => setEditSvc({ ...s, basePrice:String(s.basePrice), smartPricing:s.smartPricing||{enabled:false,rules:[]}, pricingMode:s.pricingMode||"static", matrixPricing:s.matrixPricing||null, kind:s.kind||"service", description:s.description||"" })} style={{background:"var(--c-light)",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Edit size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                  <button onClick={() => delSvc(s.id)} style={{background:"#FEF2F2",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
                </div>
              </div>
            );
            // Default petType for a new service = current tab. The
            // "settings" tab is the parking spot for shop-wide rules
            // (max weight, blocked breeds) so they're discoverable in
            // the same screen as 服務.
            const newSvcPetType = (servicePetTab === "settings") ? "dog" : servicePetTab;
            // SPA pulled OUT of the tab strip — it's pet-agnostic and
            // each service can target dog + cat simultaneously, so
            // gating it behind a tab forced owners to flip back and
            // forth. Now the SPA card is always visible below the
            // per-pet-type 主要服務 section.
            const TABS = [["dog","🐶 "+T("狗","Dog","犬")],["cat","🐱 "+T("貓","Cat","猫")],["small","🐇 "+T("小動物","Small pets","小動物")],["settings","⚙️ "+T("設定","Settings","設定")]];
            const showSpa = servicePetTab !== "settings";
            const showServiceCards = servicePetTab !== "settings";
            const showSettings = servicePetTab === "settings";
            return <>
              <div style={{display:"flex",gap:6,padding:4,background:"#F2F2F7",borderRadius:99}}>
                {TABS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setServicePetTab(k)} style={{flex:1,padding:"8px 12px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:servicePetTab===k?"#fff":"transparent",color:servicePetTab===k?"#1C1C1E":"#8E8E93",cursor:"pointer",boxShadow:servicePetTab===k?"0 1px 2px rgba(0,0,0,0.08)":"none"}}>{label}</button>
                ))}
              </div>
              {/* Shop-wide policy lives in its own tab so the dog/cat/小動物
                  tabs stay focused on service editing. */}
              {showSettings && (
                <>
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"#FFFBEB",border:"2px solid #FEF3C7",borderRadius:14}}>
                    <span style={{fontSize:18}}>⚖️</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"0 0 2px"}}>{T("最大接受體重","Max accepted weight","受入れ最大体重")}</p>
                      <p style={{fontSize:11,fontWeight:700,color:"#92400E",margin:0,opacity:0.85,lineHeight:1.4}}>{T("客戶毛孩超此體重 → 無法預約。空白 / 0 = 冇限制。","Pets above this weight can't book. Blank / 0 = no limit.","この体重を超えるペットは予約不可。空欄 / 0 = 制限なし。")}</p>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                      <input type="number" min="0" step="1" value={tenant?.maxPetWeightKg || ""} placeholder="∞" onChange={e => {
                        const v = parseFloat(e.target.value);
                        const next = isNaN(v) || v <= 0 ? null : v;
                        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, maxPetWeightKg: next } : t) }));
                      }} style={{width:70,border:"2px solid #FEF3C7",borderRadius:10,padding:"8px 10px",fontSize:15,fontWeight:900,outline:"none",textAlign:"center",color:"#92400E",background:"#fff"}} />
                      <span style={{fontSize:13,fontWeight:800,color:"#92400E"}}>kg</span>
                    </div>
                  </div>

                  {(() => {
                    const blocked = Array.isArray(tenant?.blockedBreedIds) ? tenant.blockedBreedIds : [];
                    const setBlocked = (next) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, blockedBreedIds: next } : t) }));
                    return (
                      <div style={{padding:"12px 16px",background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:14,display:"flex",flexDirection:"column",gap:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <span style={{fontSize:18}}>🚫</span>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:13,fontWeight:900,color:"#991B1B",margin:"0 0 2px"}}>{T("不接受的品種","Breeds not accepted","受入れ不可の犬種・猫種")}</p>
                            <p style={{fontSize:11,fontWeight:700,color:"#991B1B",margin:0,opacity:0.85,lineHeight:1.4}}>{T("客戶選此品種的毛孩 → 無法預約。可加多個。","Pets of these breeds can't book. You can add several.","これらの品種のペットは予約不可。複数追加できます。")}</p>
                          </div>
                        </div>
                        {blocked.length > 0 && (
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {blocked.map(bid => {
                              const b = getBreedById(bid);
                              const name = b ? (((tenant?.lang || "zh-HK").startsWith("zh") ? b.zh : b.en) || b.en) : bid;
                              return (
                                <span key={bid} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px 4px 10px",borderRadius:99,background:"#fff",border:"1px solid #FECACA",color:"#991B1B",fontSize:12,fontWeight:800}}>
                                  {name}
                                  <button onClick={() => setBlocked(blocked.filter(x => x !== bid))} title={T("移除","Remove")} style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"flex",color:"#991B1B"}}>
                                    <X size={12} strokeWidth={2.5} />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <BreedCombobox
                          type="dog"
                          lang={tenant?.lang || "zh-HK"}
                          value=""
                          valueLabel=""
                          placeholder={T("搜尋品種加入黑名單…","Search a breed to block…","ブロックする品種を検索…")}
                          onChange={({ breedId }) => {
                            if (!breedId || blocked.includes(breedId)) return;
                            setBlocked([...blocked, breedId]);
                          }}
                        />
                      </div>
                    );
                  })()}
                </>
              )}
              {showServiceCards && (
                <Card style={{padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("主要服務（客戶必選一個）","Main services (customer must pick one)","メインサービス（お客様は1つ選択必須）")}</h3>
                    <Btn size="sm" onClick={() => setEditSvc({ name:"", basePrice:"", smartPricing:{ enabled:true, rules:[] }, kind:"service", description:"", petType: newSvcPetType })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
                  </div>
                  {mainSvcs.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"12px 0"}}>{T("尚未設置服務項目，按右上「新增」開始","No services yet — tap Add (top right) to start","サービスがまだありません。右上の「追加」から始めてください")}</p>}
                  {mainSvcs.map((s, i) => renderRow(s, i, i === mainSvcs.length - 1, mainSvcs))}
                </Card>
              )}
              {showSpa && (
                <Card style={{padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("SPA 服務（客戶可選，預設不選）","SPA services (optional, off by default)","SPAサービス（任意・既定はオフ）")}</h3>
                    <Btn size="sm" onClick={() => setEditSvc({ name:"", basePrice:"", smartPricing:{ enabled:false, rules:[] }, kind:"spa", description:"", pricingMode:"matrix", petType: "dog", petTypes: ["dog", "cat"] })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
                  </div>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>{T("SPA 顯示喺主要服務同加購之間，使用同主要服務一樣嘅動態定價（按體重 / 毛長 / 特殊品種）。","SPA appears between main services and add-ons, using the same dynamic pricing as main services (by weight / coat / special breed).","SPAはメインサービスと追加オプションの間に表示され、メインサービスと同じ動的料金（体重／被毛／特別品種）を使用します。")}</p>
                  {spaSvcs.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"12px 0"}}>{T("暫無 SPA，按右上「新增」開始","No SPA yet — tap Add (top right) to start","SPAがまだありません。右上の「追加」から始めてください")}</p>}
                  {spaSvcs.map((s, i) => renderRow(s, i, i === spaSvcs.length - 1, spaSvcs))}
                </Card>
              )}
            </>;
          })()}
          {(() => {
            // Addons only render on the pet-type tabs (dog / cat / small).
            // The 設定 / SPA tabs are dedicated views.
            if (servicePetTab === "settings" || servicePetTab === "spa") return null;
            // Filter addons by the active pet-type tab — show any row whose
            // petTypes array INCLUDES the active tab. So a "剪指甲" row
            // tagged ["dog","cat"] appears in BOTH the dog and cat tabs.
            const addonMatchesTab = (a) => {
              const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
              return pts.includes(servicePetTab);
            };
            const tabAddons = (tenant?.addons || []).filter(addonMatchesTab);
            return (
              <Card style={{padding:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                  <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>{T("加購服務","Add-on services","追加オプション")} ✨</h3>
                  <Btn size="sm" onClick={() => setEditAddon({ name:"", price:"", petTypes: [servicePetTab] })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
                </div>
                <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{T("客戶預約時可選擇的額外項目（例：剪指甲、洗耳朵、香水噴霧）","Extra items customers can choose when booking (e.g. nail trim, ear cleaning, perfume spray)","予約時にお客様が選べる追加項目（例：爪切り、耳掃除、香水スプレー）")}</p>
                {tabAddons.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"12px 0"}}>{T("此寵物種類暫無加購項目，按右上「新增」開始","No add-ons for this pet type — tap Add (top right) to start","このペット種別の追加項目はありません。右上の「追加」から")}</p>}
                {tabAddons.map((a, i) => (
                  <div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",borderBottom:i===tabAddons.length-1?"none":"2px solid var(--c-light)"}}>
                    <div>
                      <p style={{fontSize:15,fontWeight:800,margin:"0 0 4px",color:"#4A4A68"}}>{a.name}{a.pricingMode==="variants" && (a.variants||[]).length > 0 ? <span style={{fontSize:11,fontWeight:700,color:"var(--c-primary)",marginLeft:8,padding:"2px 8px",background:"var(--c-light)",borderRadius:99}}>⚡ {(a.variants||[]).length} {T("個變體","variants","バリエーション")}</span> : null}</p>
                      <p style={{fontSize:13,color:"var(--c-primary)",margin:0,fontWeight:800}}>{a.pricingMode==="variants" && (a.variants||[]).length > 0 ? "$" + Math.min(...a.variants.map(v=>Number(v.price)||0)) + " – $" + Math.max(...a.variants.map(v=>Number(v.price)||0)) : "$" + a.price}</p>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={() => setEditAddon({ ...a, price:String(a.price), pricingMode:a.pricingMode||"flat", variants:a.variants||[] })} style={{background:"var(--c-light)",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Edit size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                      <button onClick={() => delAddon(a.id)} style={{background:"#FEF2F2",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
                    </div>
                  </div>
                ))}
              </Card>
            );
          })()}

          {/* Service packages admin — moved out of the petTabs so 套票 is
              always reachable. Sits between 加購 and 全店價格調整. */}
          <Card style={{padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:8,flexWrap:"wrap"}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📦 {T("套票","Packages","パッケージ")} <HelpTip topic="packages" /></h3>
              <div style={{display:"flex",gap:6}}>
                <Btn size="sm" variant="secondary" onClick={() => jumpToSetting("cancel-policy")} title={T("套票取消 / 失約政策","Package cancel / no-show policy","パッケージのキャンセル/無断ポリシー")}><Settings size={15} strokeWidth={2.5} /> {T("設定","Settings","設定")}</Btn>
                <Btn size="sm" onClick={() => setEditPackage({ name:"", description:"", serviceIds:[], visits:10, price:1800, validityDays:365, enabled:true })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
              </div>
            </div>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{T("例：10 次洗護 $1800，有效期 1 年。客戶購買後預約時自動扣減","e.g. 10 grooming visits for $1800, valid 1 year. Auto-deducted when the customer books.","例：トリミング10回 $1800、有効期間1年。お客様の予約時に自動で差し引かれます。")}</p>
            {servicePackages.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"12px 0"}}>{T("暫無套票","No packages yet","パッケージはまだありません")}</p>}
            {servicePackages.map((p, i) => {
              const isPerSize = p.pricing?.mode === "perSize";
              let priceStr = "$" + (Number(p.price) || 0);
              if (isPerSize) {
                const sizes = p.pricing?.perSize || {};
                const all = Object.values(sizes).flatMap(r => [Number(r.shortHair) || 0, Number(r.longHair) || 0]).filter(n => n > 0);
                if (all.length) {
                  const mn = Math.min(...all), mx = Math.max(...all);
                  priceStr = mn === mx ? "$" + mn : "$" + mn + " – $" + mx;
                } else {
                  priceStr = T("未設價","Not priced","価格未設定");
                }
              }
              const visitsLabel = (p.paidVisits || p.visits || 0) + (p.bonusVisits ? " " + T("送","+","+") + " " + p.bonusVisits : "") + " " + T("次","visits","回");
              const boundSvc = (p.serviceIds || []).map(id => (tenant?.services || []).find(s => s.id === id)).filter(Boolean);
              const boundLabel = boundSvc.length === 0
                ? T("⚠ 未綁定服務","⚠ No service linked","⚠ サービス未連携")
                : boundSvc.length === 1
                  ? ((boundSvc[0].kind === "spa" ? "💆 " : "🛁 ") + boundSvc[0].name)
                  : "🛁 " + boundSvc[0].name + " +" + (boundSvc.length - 1);
              return (
                <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",borderBottom:i===servicePackages.length-1?"none":"2px solid var(--c-light)"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:15,fontWeight:900,margin:"0 0 4px",color:"#4A4A68"}}>{p.name}{!p.enabled && <span style={{fontSize:10,padding:"2px 8px",background:"#F3F4F6",color:"#6B7280",borderRadius:99,marginLeft:8}}>{T("停用","Disabled","無効")}</span>}</p>
                    <p style={{fontSize:11,color: boundSvc.length === 0 ? "#DC2626" : "#0369A1",margin:"0 0 3px",fontWeight:800,background: boundSvc.length === 0 ? "#FEF2F2" : "#F0F9FF",padding:"2px 8px",borderRadius:99,display:"inline-block"}}>{boundLabel}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{visitsLabel} · {priceStr}{p.validityDays > 0 ? " · " + p.validityDays + " " + T("日有效","days valid","日間有効") : " · " + T("永久","Permanent","無期限")}</p>
                  </div>
                  <div style={{display:"flex",gap:8,marginLeft:12}}>
                    <button aria-label={T("編輯","Edit","編集")} onClick={() => setEditPackage({...p, visits:String(p.visits), price:String(p.price), validityDays:String(p.validityDays)})} style={{background:"var(--c-light)",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Edit size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                    <button aria-label={T("刪除","Delete","削除")} onClick={async () => { if (!confirm(T("確定刪除套票「","Delete package 「","パッケージ「") + p.name + T("」？","」?","」を削除しますか？"))) return; try { await deleteServicePackage(p.id); setServicePackages(arr => arr.filter(x => x.id !== p.id)); toast(i18nT("toast.deleted")); } catch (e) { toast(e?.message || T("刪除失敗","Delete failed","削除に失敗しました"), "error"); } }} style={{background:"#FEF2F2",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
                  </div>
                </div>
              );
            })}
          </Card>

          {/* Shop-wide percentage adjustment campaigns. Moved to the
              bottom of 服務管理 so the per-pet-type service editor is
              the first thing the owner sees. */}
          {(() => {
            const list = tenant?.priceAdjustments || [];
            const update = (next) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, priceAdjustments: next } : t) }));
            const today = (() => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); })();
            const active = list.find(c => c && c.enabled !== false && c.percent && (!c.startDate || c.startDate <= today) && (!c.endDate || c.endDate >= today));
            const add = () => update([...list, { id: "adj"+uid(), label: zhEn("新春加價", "Lunar New Year surcharge"), percent: 10, startDate: today, endDate: today, enabled: true }]);
            const patch = (idx, k, v) => update(list.map((c, i) => i === idx ? { ...c, [k]: v } : c));
            const del = (idx) => update(list.filter((_, i) => i !== idx));
            return (
              <Card style={{padding:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>🎉 {T("全店價格調整","Shop-wide price adjustment","全店価格調整")}</h3>
                  <Btn size="sm" variant="secondary" onClick={add}><Plus size={14} strokeWidth={3} /> {T("新增活動","Add campaign","キャンペーン追加")}</Btn>
                </div>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700,lineHeight:1.5}}>{T("設定全店百分比加價／減價，自動於開始至結束日期生效。例：新春加價 +10%，新開張優惠 −20%。多個活動，第一個符合日期者生效。","Set a shop-wide percentage markup/discount that auto-applies between start and end dates. e.g. New Year +10%, Grand Opening −20%. With multiple campaigns, the first matching the date wins.","全店の割増／割引（％）を設定し、開始〜終了日に自動適用。例：新春割増 +10%、開店記念 −20%。複数ある場合は日付が最初に一致したものが有効。")}</p>
                {active && (
                  <div style={{background: active.percent >= 0 ? "#FEF3C7" : "#D1FAE5",borderRadius:12,padding:"8px 12px",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:18}}>{active.percent >= 0 ? "📈" : "🎊"}</span>
                    <p style={{margin:0,fontSize:12,fontWeight:800,color: active.percent >= 0 ? "#92400E" : "#065F46"}}>{T("現正生效：「","Active now: 「","現在有効：「")}{active.label}{zhEn("」", "\"")}{active.percent >= 0 ? "+" : ""}{active.percent}%</p>
                  </div>
                )}
                {list.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:12,margin:0}}>{T("未設定價格調整活動","No price adjustment campaigns set","価格調整キャンペーン未設定")}</p>}
                {list.map((c, idx) => (
                  <div key={c.id} style={{padding:12,background:"#FFFDFD",borderRadius:14,border:"2px solid var(--c-light)",marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,gap:8}}>
                      <Input value={c.label || ""} onChange={e => patch(idx, "label", e.target.value)} placeholder={T("活動名稱（例：新春加價）","Campaign name (e.g. New Year markup)","キャンペーン名（例：新春割増）")} />
                      <Toggle on={c.enabled !== false} onChange={v => patch(idx, "enabled", v)} />
                      <button onClick={() => del(idx)} title={T("刪除","Delete","削除")} style={{background:"#FEF2F2",border:"none",borderRadius:10,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Trash2 size={14} color="#EF4444" strokeWidth={2.5}/></button>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                      <div>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>{T("調整 %（+加價／−減價）","Adjust % (+ markup / − discount)","調整 %（+割増／−割引）")}</p>
                        <input type="number" value={c.percent ?? ""} onChange={e => patch(idx, "percent", parseFloat(e.target.value) || 0)} placeholder="±%" style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px",fontSize:14,fontWeight:800,outline:"none",boxSizing:"border-box",color: (c.percent || 0) >= 0 ? "#D97706" : "#10B981",textAlign:"center"}} />
                      </div>
                      <div>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>{T("開始日期","Start date","開始日")}</p>
                        <Input type="date" value={c.startDate || ""} onChange={e => patch(idx, "startDate", e.target.value)} />
                      </div>
                      <div>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>{T("結束日期","End date","終了日")}</p>
                        <Input type="date" value={c.endDate || ""} onChange={e => patch(idx, "endDate", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            );
          })()}

          {/* Promo codes admin — at the very bottom of 服務管理. */}
          <Card style={{padding:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>🎁 {T("優惠碼","Promo codes","プロモコード")}</h3>
              <Btn size="sm" onClick={() => setEditPromo({ code:"", description:"", discountType:"percent", discountValue:10, maxDiscount:"", maxUses:"", perCustomerLimit:1, validFrom:"", validTo:"", newCustomersOnly:false, minSubtotal:0, enabled:true })}><Plus size={16} strokeWidth={3} /> {T("新增","Add","追加")}</Btn>
            </div>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{T("客戶喺預約最後一步輸入碼即可獲得折扣，可設使用次數、有效期、新客戶限定等","Customers enter the code at the last booking step to get a discount. You can set usage limits, validity, new-customer-only, and more.","お客様は予約の最後にコードを入力すると割引を受けられます。利用回数、有効期間、新規限定などを設定できます。")}</p>
            {promoCodes.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"12px 0"}}>{T("暫無優惠碼","No promo codes yet","プロモコードはまだありません")}</p>}
            {promoCodes.map((p, i) => (
              <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 0",borderBottom:i===promoCodes.length-1?"none":"2px solid var(--c-light)"}}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:15,fontWeight:900,margin:"0 0 4px",color:"#4A4A68",fontFamily:"ui-monospace, SF Mono, monospace",letterSpacing:1}}>{p.code}{!p.enabled && <span style={{fontSize:10,padding:"2px 8px",background:"#F3F4F6",color:"#6B7280",borderRadius:99,marginLeft:8}}>{T("停用","Disabled","無効")}</span>}</p>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>
                    {p.discountType === "percent" ? p.discountValue + "% " + T("折扣","off","オフ") : T("減","−","−") + " $" + p.discountValue}
                    {p.validTo ? " · " + T("至","until","まで") + " " + p.validTo : ""}
                    {p.maxUses ? " · " + p.usedCount + "/" + p.maxUses : ""}
                    {p.newCustomersOnly ? " · " + T("新客限定","New customers only","新規限定") : ""}
                  </p>
                  {p.description && <p style={{fontSize:11,color:"#AFAFC4",margin:"3px 0 0",fontWeight:700}}>{p.description}</p>}
                </div>
                <div style={{display:"flex",gap:8,marginLeft:12}}>
                  <button aria-label={T("編輯","Edit","編集")} onClick={() => setEditPromo({...p, discountValue:String(p.discountValue), maxDiscount:p.maxDiscount ? String(p.maxDiscount) : "", maxUses:p.maxUses ? String(p.maxUses) : "", minSubtotal:String(p.minSubtotal || 0), validFrom:p.validFrom || "", validTo:p.validTo || ""})} style={{background:"var(--c-light)",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Edit size={16} color="var(--c-primary)" strokeWidth={2.5} /></button>
                  <button aria-label={T("刪除","Delete","削除")} onClick={async () => { if (!confirm(T("確定刪除優惠碼「","Delete promo code 「","プロモコード「") + p.code + T("」？","」?","」を削除しますか？"))) return; try { await deletePromoCode(p.id); setPromoCodes(arr => arr.filter(x => x.id !== p.id)); toast(i18nT("toast.deleted")); } catch (e) { toast(e?.message || T("刪除失敗","Delete failed","削除に失敗しました"), "error"); } }} style={{background:"#FEF2F2",border:"none",borderRadius:12,padding:"8px 10px",cursor:"pointer",display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
                </div>
              </div>
            ))}
          </Card>

        </div>}

        {tab === "activity" && user.role === "owner" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <Card>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📋 {T("活動記錄","Activity log","アクティビティ記録")}</h3>
              <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{T("共","Total","合計")} {activityLogs.length} {T("項記錄","records","件")}</p>
            </div>
            {activityLoading ? (
              <p style={{fontSize:13,color:"#8A8A9E",fontStyle:"italic",margin:0}}>{T("載入中…","Loading…","読み込み中…")}</p>
            ) : activityLogs.length === 0 ? (
              <p style={{fontSize:13,color:"#AFAFC4",fontStyle:"italic",margin:0}}>{T("暫無活動記錄","No activity records yet","アクティビティ記録はまだありません")}</p>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {activityLogs.map(log => {
                  const severityColors = {
                    info: { bg:"#F3F4F6", border:"#E5E7EB", icon:"ℹ️", textColor:"#6B7280" },
                    warning: { bg:"#FFFBEB", border:"#FEF3C7", icon:"⚠️", textColor:"#92400E" },
                    error: { bg:"#FEF2F2", border:"#FECACA", icon:"❌", textColor:"#991B1B" },
                  };
                  const sev = severityColors[log.severity] || severityColors.info;
                  const timeStr = new Date(log.createdAt).toLocaleString(dateLoc());
                  const userName = state.users[log.userId]?.name || T("系統","System","システム");
                  return (
                    <div key={log.id} style={{padding:12,borderRadius:12,border:"2px solid "+sev.border,background:sev.bg,display:"flex",gap:12,alignItems:"flex-start"}}>
                      <span style={{fontSize:18,flexShrink:0}}>{sev.icon}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <p style={{fontWeight:800,fontSize:13,margin:0,color:"#4A4A68"}}>{log.description || log.actionType}</p>
                          {log.severity !== "info" && <span style={{fontSize:10,padding:"2px 8px",borderRadius:6,background:sev.textColor+"20",color:sev.textColor,fontWeight:800}}>
                            {log.severity === "warning" ? T("警告","Warning","警告") : log.severity === "error" ? T("錯誤","Error","エラー") : log.severity}
                          </span>}
                        </div>
                        <div style={{fontSize:11,color:"#8A8A9E",fontWeight:700,lineHeight:1.4}}>
                          {log.entityName && <p style={{margin:"0 0 2px"}}>📌 {log.entityName}</p>}
                          <p style={{margin:"0 0 2px"}}>👤 {userName}</p>
                          <p style={{margin:0}}>🕐 {timeStr}</p>
                        </div>
                        {log.details && <div style={{fontSize:11,color:"#666",marginTop:6,fontStyle:"italic"}}>
                          {typeof log.details === "object" ? JSON.stringify(log.details).substring(0, 100) : String(log.details).substring(0, 100)}
                        </div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>}

        {tab === "settings" && user.role === "owner" && <div style={{display:"flex",flexDirection:isMobile?"column":"row",alignItems:"flex-start",gap:isMobile?12:0,minHeight:isMobile?undefined:"calc(100vh - 280px)"}}>
          {/* ── SIDEBAR (master) ── */}
          {(!isMobile || !settingsDrilled) && <div style={{width:isMobile?"100%":240,flexShrink:0,background:"#FFFDFD",borderRight:isMobile?"none":"2px solid var(--c-light)",borderRadius:isMobile?16:0,padding:"12px 0"}}>
            <p style={{fontSize:18,fontWeight:900,color:"#4A4A68",margin:"4px 16px 12px"}}>{i18nT("settings.title")}</p>
            <div style={{padding:"0 12px 10px",position:"relative"}}>
              <input
                value={settingsSearch}
                onChange={e => setSettingsSearch(e.target.value)}
                placeholder={T("搜尋設定…（例：合併、錢包、收款）","Search settings…","設定を検索…")}
                style={{width:"100%",boxSizing:"border-box",border:"2px solid var(--c-light)",borderRadius:99,padding:"9px 32px 9px 14px",fontSize:13,fontWeight:600,outline:"none",background:"#FFFDFD",color:"#4A4A68"}}
              />
              {settingsSearch && <button onClick={() => setSettingsSearch("")} title={T("清除","Clear","クリア")} style={{position:"absolute",right:20,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:2,display:"flex",color:"#AFAFC4"}}><X size={15} strokeWidth={3} /></button>}
            </div>
            {[
              { header: null, items: [
                { id: "__plan", icon: Crown, label: T("訂閱方案","Subscription plan","サブスクリプション"), tag: planName(tenant?.plan || "free", getPlans(state.platformConfig)) },
                { id: "__help", icon: BookOpen, label: T("使用手冊 / 常見問題","User manual / FAQ","使い方ガイド / FAQ") },
              ]},
              { header: i18nT("settings.group.shop"), items: [
                { id: "shop-basic",  icon: Store,    label: i18nT("settings.shopBasic") },
                { id: "directory",   icon: Globe,    label: T("公開目錄 / 預約連結","Public directory / Booking link","公開ディレクトリ / 予約リンク") },
                { id: "shop-brand",  icon: Image,    label: i18nT("settings.shopBrand") },
                { id: "shop-hours",  icon: CalendarClock, label: i18nT("settings.shopHours") },
                { id: "cancel-policy", icon: AlertTriangle, label: T("預訂及取消政策","Booking & cancellation policy","予約・キャンセルポリシー") },
                { id: "terms",     icon: StickyNote,    label: i18nT("settings.terms") },
                { id: "shop-policy", icon: Settings, label: T("啟用功能","Features","機能設定") },
              ]},
              { header: i18nT("settings.group.ops"), items: [
                { id: "payment",   icon: DollarSign,    label: i18nT("settings.payment") },
                { id: "messaging", icon: MessageCircle, label: "WhatsApp / SMS / Email" },
                { id: "events",    icon: CalendarDays,  label: i18nT("settings.events") },
              ]},
              // 套票 / 儲值卡 / 積分 管理 ALSO live as bottom-nav shortcuts,
              // but with 11 nav items the wallet entry often scrolls off-
              // screen on phones. List them here too so the owner can find
              // them under 設定 — no duplicate-confusion penalty in practice
              // since the sidebar is the "I know where to look" entry point.
              { header: i18nT("settings.group.customerAssets"), items: [
                { id: "packages-admin", icon: Package, label: i18nT("settings.packagesAdmin") },
                { id: "wallets-admin",  icon: Wallet,  label: i18nT("settings.walletsAdmin") },
                { id: "points-admin",   icon: Award,   label: i18nT("settings.pointsAdmin") },
              ]},
              { header: i18nT("settings.group.reports"), items: [
                { id: "analytics", icon: TrendingUp,    label: i18nT("settings.analytics") },
                { id: "reviews",   icon: Star,          label: i18nT("settings.reviews") },
              ]},
              { header: i18nT("settings.group.stamp"), items: [
                { id: "points",    icon: Award,         label: i18nT("settings.pointsSetup") },
              ]},
              { header: i18nT("settings.group.advanced"), items: [
                { id: "language",  icon: Globe,         label: i18nT("settings.language") },
                { id: "activity",  icon: Activity,      label: i18nT("settings.activity") },
                { id: "support",   icon: MessageCircle, label: i18nT("settings.support") },
                { id: "system",    icon: Settings,      label: i18nT("settings.systemBackup") },
                { id: "delete-account", icon: Trash2,   label: T("刪除帳戶","Delete account","アカウント削除") },
              ]},
            ].map(group => ({ ...group, items: (group.items || []).filter(settingsMatch) })).filter(group => group.items.length > 0).map((group, gi) => (
              <div key={gi} style={{marginBottom:14}}>
                {group.header && <p style={{fontSize:11,fontWeight:800,color:"#AFAFC4",margin:"4px 16px 6px",letterSpacing:0.5}}>{group.header}</p>}
                {group.items.map(item => {
                  const active = (item.id === "__plan"
                    ? settingsSubtab === "system" && secOf("system") === "plan"
                    : settingsSubtab === item.id && !(item.id === "system" && secOf("system") === "plan")) && (!isMobile || settingsDrilled);
                  const Ic = item.icon;
                  return (
                    <button key={item.id} onClick={() => {
                      if (item.id === "__plan") { openPlanPage(); return; }
                      if (item.id === "__help") { window.location.hash = "#/help"; return; }
                      // 分析 + 評價 keep their existing top-level pages;
                      // clicking them from settings just navigates the
                      // user there. Everything else stays in settings.
                      if (item.id === "analytics" || item.id === "reviews") {
                        setTab(item.id);
                        return;
                      }
                      setSettingsSubtab(item.id);
                      const q = settingsSearch.trim().toLowerCase();
                      const hit = q && (settingsSectionsFor(item.id, user) || []).find(x => (x.label() + " " + x.kw).toLowerCase().includes(q));
                      if (hit) setSettingsSec(m => ({ ...m, [item.id]: hit.id }));
                      if (isMobile) setSettingsDrilled(true);
                    }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",padding:"10px 16px",border:"none",borderLeft:active && !isMobile ? "3px solid var(--c-primary)" : "3px solid transparent",background:active ? "var(--c-lighter)" : "transparent",color:active ? "var(--c-primary)" : "#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer",textAlign:"left"}}>
                      <span style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                        <Ic size={16} strokeWidth={2.5} color={active ? "var(--c-primary)" : "#6A6A85"} />
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.label}</span>
                        {item.tag && <span style={{fontSize:10.5,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"var(--c-primary-20)",color:"var(--c-primary)",flexShrink:0}}>{item.tag}</span>}
                      </span>
                      {isMobile && <ChevronRight size={14} color="#AFAFC4" strokeWidth={2.5} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>}

          {/* ── CONTENT (detail) ── */}
          {(!isMobile || settingsDrilled) && <div style={{flex:1,minWidth:0,padding:isMobile?"0":"16px 0 16px 20px",display:"flex",flexDirection:"column",gap:16,width:isMobile?"100%":"auto"}}>
            {isMobile && (
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <button onClick={() => setSettingsDrilled(false)} aria-label={T("返回","Back","戻る")} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                  <ChevronLeft size={16} color="var(--c-primary)" strokeWidth={3} />
                </button>
                <h2 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68"}}>
                  {(settingsSubtab === "system" && secOf("system") === "plan") ? T("訂閱方案","Subscription plan","サブスクリプション") : { "shop-basic":i18nT("settings.shopBasic"), "directory":T("公開目錄 / 預約連結","Public directory / Booking link","公開ディレクトリ / 予約リンク"), "shop-brand":i18nT("settings.shopBrand"), "shop-hours":i18nT("settings.shopHours"), "cancel-policy":T("預訂及取消政策","Booking & cancellation policy","予約・キャンセルポリシー"), "shop-policy":T("啟用功能","Features","機能設定"), payment:i18nT("settings.payment"), analytics:i18nT("settings.analytics"), reviews:i18nT("settings.reviews"), messaging:i18nT("settings.messaging"), events:T("📌 事件管理","📌 Event management","📌 イベント管理"), terms:i18nT("settings.terms"), activity:i18nT("settings.activity"), support:T("💬 客戶支援","💬 Customer support","💬 カスタマーサポート"), language:i18nT("settings.language"), system:i18nT("settings.system") }[settingsSubtab] || i18nT("settings.title")}
                </h2>
              </div>
            )}
            {/* Long pages: one tab per group of cards instead of one long scroll. */}
            <SettingsSectionTabs sections={settingsSectionsFor(settingsSubtab, user)} active={secOf(settingsSubtab)}
              onPick={(id) => {
                setSettingsSec(m => ({ ...m, [settingsSubtab]: id }));
                // The 進階 tab is just the collapsed credentials panel — open it.
                if (settingsSubtab === "messaging" && id === "channels") setMessagingAdvanced(true);
              }} />
          {/* ── WhatsApp 訊息 ─────────────────────────────────────────── */}
          {/* ── Simple-mode panel: 1 toggle per channel + display name.
              Tap any channel toggle and PettoBook sends on your behalf using
              its shared sender. Owners who need a custom WhatsApp number
              or branded Email domain use the 「WhatsApp 號碼 / 進階」 tab. */}
          {inSec("messaging","auto") && <Card style={{padding:20,background:"linear-gradient(135deg,#FFFAFC 0%,#FFEEF2 100%)",border:"2px solid #FFE0EC"}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.set.msg.autoCommTitle")}</h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700,lineHeight:1.5}}>
              {T("預約確認、改期、完成通知會自動由 PettoBook 代你發送。客戶見到「","Booking confirmations, reschedules and completion notices are sent automatically by PettoBook on your behalf. Customers see the name \"","予約確認・変更・完了通知はPettoBookが自動で代理送信します。お客様には「")}<strong>{tenant?.name || T("您的店鋪","your shop","あなたの店舗")}</strong>{T("」嘅名字，無需自設 API。WhatsApp 唔會自動發；如想用嚟人手聯絡，請喺上面「WhatsApp 號碼 / 進階」分頁設定店鋪 WhatsApp 號碼。","\", no API setup needed. WhatsApp is not sent automatically; to use it for manual contact, set your shop's WhatsApp number in the \"WhatsApp number / Advanced\" tab above.","」と表示され、API設定は不要です。WhatsAppは自動送信されません。手動連絡に使う場合は、上の「WhatsApp番号 / 詳細」タブで店舗のWhatsApp番号を設定してください。")}
            </p>
            {!waForm ? <WaLoadState /> : <>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {(() => {
                  const planF = PLANS[tenant?.plan||"free"];
                  const smsAvail = !!planF?.features?.autoNotify;
                  // SMS is hidden platform-wide until the superadmin turns it on
                  // (admin → 平台設定 → 開放 SMS). Not ready yet → Email only.
                  const smsGloballyOn = state.platformConfig?.smsEnabled === true;
                  return [
                    { key: "emailEnabled", icon: "📧", label: "Email",     desc: T("建議：收據、提醒、報告（永遠免費）","Recommended: receipts, reminders, reports (always free)","推奨：レシート、リマインダー、レポート（常に無料）"), locked: false },
                    ...(smsGloballyOn ? [{ key: "smsEnabled", icon: "📱", label: "SMS", desc: smsAvail ? T("長輩客戶都收得到，每則約 $0.05","Even older customers receive it, about $0.05 each","年配のお客様にも届きます、1通約$0.05") : T("🔒 需要基本版或專業版方案","🔒 Requires Basic or Pro plan","🔒 ベーシックまたはプロプランが必要"), locked: !smsAvail }] : []),
                  ].map(({key, icon, label, desc, locked}) => (
                    <label key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:14,background:"#fff",border:"2px solid "+(waForm[key]?"var(--c-primary)":"var(--c-light)"),cursor:locked?"not-allowed":"pointer",opacity:locked?0.6:1}}>
                      <div style={{width:36,height:36,borderRadius:10,background:waForm[key]?"var(--c-lighter)":"var(--c-light)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{label}</p>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{desc}</p>
                      </div>
                      <Toggle on={!!waForm[key]} onChange={v => {
                        if (locked) { toast(T("此功能需要付費版，請先升級","This feature needs a paid plan — please upgrade first","この機能は有料プランが必要です。まずアップグレードしてください"), "error"); return; }
                        setWaForm(f => ({...f, [key]: v}));
                      }} />
                    </label>
                  ));
                })()}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginTop:14,padding:"10px 14px",background:"rgba(255,255,255,0.6)",borderRadius:12}}>
                <span style={{fontSize:18}}>✨</span>
                <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700,lineHeight:1.5}}>
                  {T("Email 同 SMS 由 PettoBook 共享寄件人代發。如要設定店鋪自家嘅 ","Email and SMS are sent via PettoBook's shared sender. To set your shop's own ","EmailとSMSはPettoBookの共有送信元から送られます。店舗独自の")}<strong>{T("WhatsApp Business 號碼","WhatsApp Business number","WhatsApp Business番号")}</strong>{T("，請去上面「WhatsApp 號碼 / 進階」分頁。",", open the 「WhatsApp number / Advanced」 tab above.","を設定するには、上の「WhatsApp番号 / 詳細」タブを開いてください。")}
                </p>
              </div>
              <Btn size="sm" disabled={waSaving} onClick={async () => {
                setWaSaving(true);
                try { await upsertMessagingSecrets(user.tenantId, waFormForSave(waForm)); toast(T("通訊設定已儲存","Communication settings saved","通信設定を保存しました")); }
                catch (e) { toast(e?.message || T("儲存失敗","Save failed","保存に失敗しました"), "error"); }
                finally { setWaSaving(false); }
              }} style={{width:"100%",marginTop:12}}>{waSaving ? T("儲存中…","Saving…","保存中…") : T("💾 儲存通訊設定","💾 Save communication settings","💾 通信設定を保存")}</Btn>
              {/* Manual push enable + self-test. On iOS this fires the OS
                  permission prompt, stores the device token, and sends a test
                  push so the owner can confirm end-to-end. */}
              <Btn size="sm" variant="secondary" onClick={async () => {
                try {
                  const nat = await import("./lib/native.js");
                  if (!nat.isNative?.()) { toast(T("此功能只喺 App 內有效","Only works inside the app","アプリ内のみ有効"), "info"); return; }
                  let got = false, lastEvent = "";
                  await nat.registerForPush(async (token) => {
                    got = true;
                    try { await registerDeviceToken({ userId: user.id, token, platform: "ios", tenantId: user.tenantId }); } catch { /* ignore */ }
                    toast(T("已開啟通知 ✅ 發送測試中…","Enabled ✅ sending test…","有効化 ✅ テスト送信中…"), "success");
                    try { await sendPush({ userIds: [user.id], title: "PettoBook", body: T("測試通知 🔔","Test notification 🔔","テスト通知 🔔") }); } catch { /* ignore */ }
                  }, (stage, detail) => {
                    lastEvent = stage + (detail ? ":" + detail : "");
                    if (/error|fail|denied|not-native|import/.test(stage)) toast(zhEn("推送：", "Push: ") + lastEvent, "error");
                  });
                  setTimeout(() => { if (!got) toast(T("未收到通知權杖，請稍後再試","No device token — please retry","トークン未取得"), "error"); }, 6000);
                } catch (e) { toast((e?.message || "error").slice(0,120), "error"); }
              }} style={{width:"100%",marginTop:8,background:"#EEF2FF",border:"2px solid #C7D2FE",color:"#4338CA"}}>{T("🔔 開啟並測試推送通知","🔔 Enable & test push","🔔 プッシュ通知を有効化")}</Btn>
            </>}
          </Card>}

          {/* Per-trigger settings — complements the top 客戶自動通訊 card.
              Top card = which channels (Email / SMS) are enabled at the
              tenant_messaging_secrets level. THIS card = which channel each
              specific trigger uses + whether each trigger fires at all. */}
          {inSec("messaging","triggers") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.set.msg.perTriggerCard")}</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{T("逐個事件揀 channel 同開關（預約確認、改期、提醒……）","Pick a channel and toggle for each event (booking confirm, reschedule, reminder…)","イベントごとにチャネルとオン/オフを選択（予約確認、変更、リマインダー…）")}</p>
            {(() => {
              const mc = tenant?.messagingConfig || {};
              const setMc = (k,v) => setState(s => ({...s, tenants:s.tenants.map(t=>t.id===user.tenantId?{...t,messagingConfig:{...t.messagingConfig,[k]:v}}:t)}));
              const TRIGGERS = [
                ["booking_created",    "autoSendBookingConfirm",T("📅 預約確認","📅 Booking confirmed","📅 予約確認"),T("客戶成功預約後","After a customer books","お客様の予約完了後")],
                ["booking_rescheduled","",                       T("🔁 改期通知","🔁 Reschedule notice","🔁 変更通知"),T("客戶／店家改期後","After customer/shop reschedules","お客様／店舗の変更後")],
                ["booking_cancelled",  "autoSendCancel",         T("❌ 取消通知","❌ Cancellation notice","❌ キャンセル通知"),T("預約取消後","After a booking is cancelled","予約キャンセル後")],
                ["day_before",         "autoSendReminder",       T("⏰ 前一日提醒","⏰ Day-before reminder","⏰ 前日リマインダー"),T("預約前 24 小時","24 hours before the booking","予約の24時間前")],
                ["grooming_done",      "autoSendGroomingDone",   T("🎉 美容完成","🎉 Grooming done","🎉 トリミング完了"),T("完成 + 健康報告","Completion + health report","完了 + 健康レポート")],
              ];
              const setChannel = (trig, v) => setState(s => ({
                ...s,
                tenants: s.tenants.map(t => t.id === user.tenantId
                  ? { ...t, messagingConfig: { ...(t.messagingConfig || {}), channels: { ...(t.messagingConfig?.channels || {}), [trig]: v } } }
                  : t
                ),
              }));
              const planF = PLANS[tenant?.plan||"free"];
              const smsAvail = !!planF?.features?.autoNotify;
              const smsGloballyOn = state.platformConfig?.smsEnabled === true;
              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <Sel label={i18nT("own.set.msg.defaultChannel")} value={mc.defaultChannel||"email"} onChange={e=>setMc("defaultChannel",e.target.value)}>
                  <option value="email">📧 Email{T("（建議）","（recommended）","（推奨）")}</option>
                  {smsGloballyOn && <option value="sms" disabled={!smsAvail}>📱 SMS {smsAvail?"":T("（需付費版）","（paid plan）","（有料プラン）")}</option>}
                  {smsGloballyOn && <option value="both" disabled={!smsAvail}>📧 + 📱 {T("兩者都寄","Send both","両方送信")} {smsAvail?"":T("（需付費版）","（paid plan）","（有料プラン）")}</option>}
                </Sel>
                <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14,border:"2px solid var(--c-border)"}}>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 4px"}}>{i18nT("own.set.msg.perEvent")}</p>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"0 0 10px",fontWeight:700}}>{T("左邊 toggle：開關呢個通知；右邊：揀 channel（留空＝跟預設）","Left toggle: turn this notice on/off; right: pick a channel (blank = use default)","左のトグル：この通知のオン/オフ；右：チャネル選択（空欄＝既定を使用）")}</p>
                  {TRIGGERS.map(([trig, enableKey, label, desc]) => (
                    <div key={trig} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--c-light)",gap:10}}>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 2px"}}>{label}</p>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{desc}</p>
                      </div>
                      {enableKey && <Toggle on={mc[enableKey]!==false} onChange={v=>setMc(enableKey,v)} />}
                      <select value={mc.channels?.[trig] || ""} onChange={e => setChannel(trig, e.target.value)} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 10px",fontSize:12,fontWeight:800,background:"#fff",color:"#4A4A68"}}>
                        <option value="">{T("（預設）","（Default）","（既定）")}</option>
                        <option value="email">📧 Email</option>
                        {smsGloballyOn && <option value="sms" disabled={!smsAvail}>📱 SMS</option>}
                        {smsGloballyOn && <option value="both" disabled={!smsAvail}>📧 + 📱</option>}
                      </select>
                    </div>
                  ))}
                </div>
                {smsGloballyOn && !smsAvail && <div style={{background:"#FFFBEB",borderRadius:16,padding:"12px 16px",border:"2px solid #FEF3C7",display:"flex",gap:10,alignItems:"center"}}><Crown size={16} color="#D97706" /><p style={{fontSize:12,color:"#92400E",margin:0,fontWeight:700}}>{T("SMS 通知需要基本版或專業版方案","SMS notifications require the Basic or Pro plan","SMS通知にはベーシックまたはプロプランが必要です")}</p></div>}
                {/* WhatsApp template auto-notify — match an approved template
                    to each event; sent via the WhatsApp Business API. Pro+ (has
                    the whatsapp feature) + a WhatsApp number configured below. */}
                {planF?.features?.whatsapp && (user.role === "owner" || !!user.permissions?.manageSettings) && <WaTemplateMatcher user={user} tenant={tenant} setState={setState} />}
                {/* When a human takes over an AI chat, send a one-time
                    "X is now assisting you" notice (no per-message name).
                    Owner/manager (manageSettings) only — a general staff must
                    not be able to change the shop-wide display name. */}
                {planF?.features?.whatsapp && (user.role === "owner" || !!user.permissions?.manageSettings) && (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    <label style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:14,background:"#FFFDFD",border:"2px solid var(--c-light)",cursor:"pointer"}}>
                      <Toggle on={mc.waSignStaff !== false} onChange={v => setMc("waSignStaff", v)} />
                      <span style={{fontSize:13,fontWeight:800,color:"#4A4A68",lineHeight:1.4}}>{T("由 AI 轉真人時，發一句「XXX 現在為你服務」通知客戶（唔會喺每句加名）","When a human takes over from the AI, send a one-time “X is now assisting you” notice (no name on each reply)","AIから担当者に切替時に「担当者が対応します」通知を送信")}</span>
                    </label>
                    {mc.waSignStaff !== false && (
                      <Input
                        label={T("通知顯示名稱（留空＝用登入同事嘅名）","Notice display name (blank = the logged-in staff's name)","通知の表示名（空欄＝ログイン中のスタッフ名）")}
                        value={mc.waHandoffName || ""}
                        onChange={e => setMc("waHandoffName", e.target.value)}
                        placeholder={T("例：客服 / 同事 / Ada（唔想顯示真名就填「客服」）","e.g. Support / Ada (type a label to hide the real name)","例：サポート / Ada")}
                      />
                    )}
                    <p style={{fontSize:10.5,color:"#AFAFC4",fontWeight:700,margin:0}}>{T("只有店主 / 有「設定」權限嘅同事先可以改呢個名。","Only the owner / staff with the Settings permission can change this name.","オーナー／設定権限のあるスタッフのみ変更可能。")}</p>
                  </div>
                )}
              </div>;
            })()}
          </Card>}

          {/* ── Advanced (BYOK) section ─────────────────────────────────
              Hidden by default behind 進階設定 expander. Inside: the three
              original cards (WhatsApp / SMS / Email API keys). Most shops
              never touch this. Owner/manager (manageSettings) only — general
              staff must not see or change API credentials / provider / AI. */}
          {inSec("messaging","channels") && (user.role === "owner" || !!user.permissions?.manageSettings) && <Card style={{padding:0,overflow:"hidden"}}>
            <button
              onClick={() => setMessagingAdvanced(v => !v)}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"14px 20px",background:"#FFFDFD",border:"none",cursor:"pointer",borderBottom:messagingAdvanced?"2px solid var(--c-light)":"none"}}
            >
              <span style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:18}}>🛠️</span>
                <span style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>{T("進階設定（自訂 API 憑證）","Advanced settings (custom API credentials)","詳細設定（カスタムAPI認証情報）")}</span>
              </span>
              <ChevronDown size={16} color="#8A8A9E" strokeWidth={2.5} style={{transform:messagingAdvanced?"rotate(180deg)":"none",transition:"transform 0.2s"}} />
            </button>
            {messagingAdvanced && (
              <div style={{padding:"14px 16px 18px",background:"#FFFAFC",display:"flex",flexDirection:"column",gap:14}}>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5,padding:"8px 12px",background:"#FFFBEB",borderRadius:10,border:"1px solid #FEF3C7"}}>
                  {T("⚠️ Email 同 SMS 通知由 PettoBook 平台代寄，唔需要設定。下面係","⚠️ Email and SMS notices are sent by the PettoBook platform — no setup needed. Below is your ","⚠️ EmailとSMS通知はPettoBookプラットフォームが代理送信するため設定不要です。下記は")}<strong>{T("店鋪自家 WhatsApp Business 號碼","shop's own WhatsApp Business number","店舗独自のWhatsApp Business番号")}</strong>{T("，畀客戶喺預約頁撳「WhatsApp 聯絡」即可同你直接傾。", " — customers can tap 「Contact via WhatsApp」 on the booking page to chat with you directly.","です。お客様は予約ページの「WhatsAppで連絡」をタップして直接やり取りできます。")}
                </p>

          {/* WhatsApp BYOK — shop's own WhatsApp Business Number for manual
              messaging / wa.me deep links. PLATFORM DOES NOT auto-send
              WhatsApp; this is purely so the shop has a clickable contact
              channel that uses their verified WhatsApp business profile. */}
          {inSec("messaging","channels") && <Card style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.set.msg.waBYOK")}</h3>
              {waForm?.whatsappEnabled && <span style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:"#D1FAE5",color:"#065F46",fontWeight:800}}>{T("已啟用","Enabled","有効")}</span>}
            </div>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>
              {T("店鋪自己嘅 WhatsApp Business 號碼。","Your shop's own WhatsApp Business number. ","店舗独自のWhatsApp Business番号。")}<strong>{T("平台唔會自動代你發 WhatsApp","The platform does not auto-send WhatsApp for you","プラットフォームはWhatsAppを自動送信しません")}</strong>{T("（自動通訊只用 Email / SMS）。但你可以喺呢度設定你嘅號碼，等員工人手分享美容報告／提醒、或者係客戶撳「WhatsApp 聯絡」直接傳訊息畀你。如要做模板自動發送，需先到 ","（auto-messaging uses Email / SMS only）. But you can set your number here so staff can manually share grooming reports/reminders, or customers can tap 「Contact via WhatsApp」 to message you directly. For automated template sending, first register at ","（自動通信はEmail / SMSのみ）。ただし、ここで番号を設定すれば、スタッフが手動でトリミングレポート／リマインダーを共有したり、お客様が「WhatsAppで連絡」をタップして直接メッセージを送れます。テンプレート自動送信を行うには、まず")}<a href="https://business.facebook.com/" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800}}>Meta Business</a> {T("註冊。","to register.","に登録してください。")}
            </p>
            {!waForm ? <WaLoadState /> : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,background:waForm.whatsappEnabled?"var(--c-lighter)":"#FFFDFD",border:"2px solid "+(waForm.whatsappEnabled?"var(--c-primary)":"var(--c-light)"),cursor:"pointer"}}>
                <input type="checkbox" checked={!!waForm.whatsappEnabled} onChange={e => setWaForm(f => ({ ...f, whatsappEnabled: e.target.checked }))} style={{transform:"scale(1.2)"}} />
                <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{T("啟用 WhatsApp 自動通知","Enable WhatsApp auto-notifications","WhatsApp自動通知を有効化")}</span>
              </label>

              {/* Provider choice. Meta Cloud API = cheaper (service msgs free)
                  but you must create a Meta developer app. Twilio = small
                  per-message fee, but no Meta app to build (easier onboarding).
                  The AI concierge + inbox work identically on either. */}
              <div>
                <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:6}}>{T("WhatsApp 供應商","WhatsApp provider","WhatsAppプロバイダー")}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {[
                    { k:"meta", label:"Meta Cloud API", desc:T("服務訊息免費，但要自行喺 Meta 建立 App","Service messages free; needs Meta app setup","サービスメッセージ無料、Metaアプリ設定が必要") },
                    { k:"twilio", label:"Twilio", desc:T("每則約 $0.005，免建 Meta App，設定較易","~$0.005/msg, no Meta app, easier setup","1通約$0.005、Metaアプリ不要で簡単") },
                    { k:"wati", label:"WATI", desc:T("有現成收件箱，貼上 API 端點同權杖即用","Has its own inbox; paste API endpoint + token","専用受信箱あり、APIエンドポイントとトークンを貼付") },
                    { k:"chatwoot", label:"Chatwoot", desc:T("最平 $19/月・支援 Coexistence，手機 App 同時可用","Cheapest $19/mo · Coexistence supported","最安$19/月・Coexistence対応") },
                  ].map(o => (
                    <button key={o.k} type="button" onClick={() => setWaForm(f => ({ ...f, waProvider: o.k }))}
                      style={{flex:1,textAlign:"left",padding:"10px 12px",borderRadius:12,cursor:"pointer",border:"2px solid "+(waForm.waProvider===o.k?"var(--c-primary)":"var(--c-light)"),background:waForm.waProvider===o.k?"var(--c-lighter)":"#fff"}}>
                      <div style={{fontSize:13,fontWeight:900,color:"#4A4A68"}}>{o.label}</div>
                      <div style={{fontSize:10.5,color:"#8A8A9E",fontWeight:700,marginTop:2,lineHeight:1.4}}>{o.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {waForm.waProvider === "meta" && <>
              {/* Step-by-step help — where the Phone Number ID + Access Token
                  come from in Meta. Collapsed by default to avoid clutter. */}
              <details style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:12,padding:"0"}}>
                <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:13,fontWeight:900,color:"#0369A1",display:"flex",alignItems:"center",gap:8}}>
                  <HelpCircle size={16} strokeWidth={2.5} /> {T("點樣攞 Phone Number ID 同 Access Token？","How do I get the Phone Number ID & Access Token?","Phone Number IDとAccess Tokenの取得方法は？")}
                </summary>
                <div style={{padding:"0 14px 14px",fontSize:12.5,color:"#075985",fontWeight:600,lineHeight:1.65}}>
                  <ol style={{margin:"4px 0 0",paddingLeft:18,display:"flex",flexDirection:"column",gap:7}}>
                    <li>{T("去 ","Go to ","")}<a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" style={{color:"#0284C7",fontWeight:800}}>developers.facebook.com/apps</a>{T("，撳「建立應用程式」，類型揀「商業 / Business」。","→ Create App → choose the 「Business」 type.","でアプリを作成し、「ビジネス」タイプを選択。")}</li>
                    <li>{T("喺應用程式入面加入「WhatsApp」產品，並連結你嘅 Meta Business 帳戶。","Add the 「WhatsApp」 product to the app and link your Meta Business account.","アプリに「WhatsApp」製品を追加し、Meta Businessアカウントを連携。")}</li>
                    <li>{T("開 WhatsApp → 「API Setup」。「From」欄下面就係 ","Open WhatsApp → 「API Setup」. Under the 「From」 field you'll see the ","WhatsApp →「API設定」を開く。「From」欄の下に ")}<strong>Phone number ID</strong>{T("（一串數字），複製過嚟上面第一格。","(a number) — copy it into the first field above.","（数字）が表示されるので、上の最初の欄にコピー。")}</li>
                    <li>{T("同一頁有一個臨時 ","The same page has a temporary ","同じページに一時的な ")}<strong>Access Token</strong>{T("（24 小時），可以先用嚟測試。","(24 hours) you can use to test first.","（24時間）があり、まずテストに使えます。")}</li>
                    <li>{T("要長期用，去 Business 設定 → 使用者 → ","For long-term use, go to Business Settings → Users → ","長期利用は、ビジネス設定 → ユーザー → ")}<strong>System user</strong>{T("，建立一個系統使用者，指派你嘅 App，然後「產生權杖」，勾選 ","→ create a system user, assign your App, then Generate Token with the ","でシステムユーザーを作成し、アプリを割り当てて「トークンを生成」、")}<code style={{background:"#E0F2FE",padding:"1px 5px",borderRadius:4}}>whatsapp_business_messaging</code>{T(" 同 ","and ","と")}<code style={{background:"#E0F2FE",padding:"1px 5px",borderRadius:4}}>whatsapp_business_management</code>{T(" 權限，攞永久 Token 貼落上面第二格。","permissions to get a permanent token for the second field above.","権限を付与して永続トークンを取得し、上の2番目の欄に貼り付け。")}</li>
                    <li>{T("最後喺 WhatsApp → API Setup「Add phone number」加入並驗證你嘅店鋪電話號碼。","Finally, under WhatsApp → API Setup 「Add phone number」, add and verify your shop's phone number.","最後にWhatsApp → API設定の「電話番号を追加」で店舗の番号を追加・認証。")}</li>
                  </ol>
                  <p style={{margin:"10px 0 0",padding:"8px 10px",background:"#FEF9C3",borderRadius:8,color:"#854D0E",fontWeight:700}}>
                    {T("⚠️ 用嚟接收訊息嘅號碼唔可以已經裝咗普通 WhatsApp App。AI 客服要收到客人訊息，仲需要平台幫你完成 webhook 設定 — 設定好號碼後聯絡我哋。","⚠️ The number you use here must NOT already be active on the regular WhatsApp app. For the AI concierge to receive customer messages, the platform also needs to finish the webhook setup — contact us after saving your number.","⚠️ ここで使う番号は通常のWhatsAppアプリで使用中でないこと。AIコンシェルジュが受信するには、プラットフォーム側でwebhook設定も必要です。番号保存後にご連絡ください。")}
                  </p>
                </div>
              </details>

              <Input label="WhatsApp Phone Number ID" placeholder={T("例：123456789012345","e.g. 123456789012345","例：123456789012345")} value={waForm.whatsappPhoneId} onChange={e => setWaForm(f => ({ ...f, whatsappPhoneId: e.target.value }))} hint={T("Meta Business → WhatsApp → API Setup 取得","Get it from Meta Business → WhatsApp → API Setup","Meta Business → WhatsApp → API Setup で取得")} />
              <Input label="Access Token" type="password" placeholder="EAAG..." value={waForm.whatsappAccessToken} onChange={e => setWaForm(f => ({ ...f, whatsappAccessToken: e.target.value }))} hint={T("建議使用 System User 永久 Token","Recommended: use a System User permanent token","System Userの永続トークンの使用を推奨")} />
              <Input label={T("顯示名稱（選填）","Display name (optional)","表示名（任意）")} placeholder={T("例：毛孩天堂美容院","e.g. Furry Paradise Grooming","例：もふもふパラダイス美容院")} value={waForm.whatsappFromName} onChange={e => setWaForm(f => ({ ...f, whatsappFromName: e.target.value }))} />
              <Input label={T("預設訊息範本名（選填）","Default template name (optional)","既定テンプレート名（任意）")} placeholder="例：booking_confirm" value={waForm.whatsappDefaultTemplate} onChange={e => setWaForm(f => ({ ...f, whatsappDefaultTemplate: e.target.value }))} hint={T("若已批准模板，填名稱以模板模式發送；否則使用純文字（限 24 小時客戶回覆窗）","If you have an approved template, enter its name to send in template mode; otherwise plain text is used (limited to the 24-hour customer reply window)","承認済みテンプレートがある場合は名前を入力してテンプレートモードで送信。なければプレーンテキスト（24時間の返信ウィンドウ内のみ）")} />
              </>}

              {waForm.waProvider === "twilio" && <>
                {/* Twilio setup help — where the SID / token / sender come from. */}
                <details style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:12,padding:"0"}}>
                  <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:13,fontWeight:900,color:"#0369A1",display:"flex",alignItems:"center",gap:8}}>
                    <HelpCircle size={16} strokeWidth={2.5} /> {T("點樣攞 Twilio 憑證？","How do I get the Twilio credentials?","Twilioの認証情報の取得方法は？")}
                  </summary>
                  <div style={{padding:"0 14px 14px",fontSize:12.5,color:"#075985",fontWeight:600,lineHeight:1.65}}>
                    <ol style={{margin:"4px 0 0",paddingLeft:18,display:"flex",flexDirection:"column",gap:7}}>
                      <li>{T("去 ","Go to ","")}<a href="https://www.twilio.com/try-twilio" target="_blank" rel="noreferrer" style={{color:"#0284C7",fontWeight:800}}>twilio.com</a>{T(" 註冊帳戶。Console 首頁就有 ","and sign up. The Console homepage shows your ","で登録。Consoleのトップに ")}<strong>Account SID</strong>{T(" 同 ","and ","と")}<strong>Auth Token</strong>{T("，複製落下面。","— copy both below.","が表示されるので、下にコピー。")}</li>
                      <li>{T("Console → Messaging → 「Try it out」→「Send a WhatsApp message」，跟指示連接你嘅 WhatsApp Sender。Twilio 會幫你經 Meta 驗證商業號碼（唔使自己建 Meta App）。","Console → Messaging → 「Try it out」 → 「Send a WhatsApp message」, follow the steps to connect a WhatsApp Sender. Twilio handles the Meta business verification for you (no Meta app to build).","Console → Messaging →「Try it out」→「Send a WhatsApp message」でWhatsApp Senderを接続。TwilioがMetaのビジネス認証を代行します（Metaアプリ不要）。")}</li>
                      <li>{T("批核後，喺 Messaging → Senders → WhatsApp senders 見到你嘅號碼，填落下面「WhatsApp 寄件號碼」（含國碼）。","Once approved, find your number under Messaging → Senders → WhatsApp senders and enter it below as the sender number (with country code).","承認後、Messaging → Senders → WhatsApp sendersで番号を確認し、下の送信番号欄に国番号付きで入力。")}</li>
                      <li>{T("要收到客人訊息，喺該 Sender 嘅「Configuration」將 incoming-message webhook 設為平台提供嘅網址 — 儲存號碼後聯絡我哋攞含密鑰嘅 URL。","To receive customer messages, set that Sender's incoming-message webhook to the platform URL — contact us after saving for the URL with its key.","受信するには、Senderの「Configuration」で受信メッセージwebhookをプラットフォームのURLに設定 — 保存後にご連絡ください。")}</li>
                    </ol>
                    <p style={{margin:"10px 0 0",padding:"8px 10px",background:"#FEF9C3",borderRadius:8,color:"#854D0E",fontWeight:700}}>
                      {T("⚠️ Twilio 沙盒號碼只可用嚟測試；正式收發需完成 WhatsApp Sender 註冊。","⚠️ The Twilio sandbox number is for testing only; live use needs a registered WhatsApp Sender.","⚠️ Twilioサンドボックス番号はテスト専用。本番利用にはWhatsApp Sender登録が必要です。")}
                    </p>
                  </div>
                </details>
                <Input label="Twilio Account SID" placeholder="AC..." value={waForm.waTwilioAccountSid} onChange={e => setWaForm(f => ({ ...f, waTwilioAccountSid: e.target.value }))} hint={T("Twilio Console 首頁","Twilio Console homepage","Twilio Consoleトップ")} />
                <Input label="Twilio Auth Token" type="password" placeholder="••••••••" value={waForm.waTwilioAuthToken} onChange={e => setWaForm(f => ({ ...f, waTwilioAuthToken: e.target.value }))} hint={T("Twilio Console 首頁（撳「顯示」）","Twilio Console homepage (click Show)","Twilio Consoleトップ（「表示」をクリック）")} />
                <Input label={T("WhatsApp 寄件號碼","WhatsApp sender number","WhatsApp送信番号")} placeholder="+14155238886" value={waForm.waTwilioFrom} onChange={e => setWaForm(f => ({ ...f, waTwilioFrom: e.target.value }))} hint={T("你喺 Twilio 批核咗嘅 WhatsApp 號碼（含國碼）","Your approved Twilio WhatsApp number (with country code)","Twilioで承認されたWhatsApp番号（国番号付き）")} />
              </>}

              {waForm.waProvider === "wati" && (() => {
                const watiWebhookUrl = "https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/whatsapp-webhook?provider=wati&tenant=" + (tenant?.id || user?.tenantId || "");
                return (<>
                  <details style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:12,padding:"0"}}>
                    <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:13,fontWeight:900,color:"#0369A1",display:"flex",alignItems:"center",gap:8}}>
                      <HelpCircle size={16} strokeWidth={2.5} /> {T("點樣攞 WATI 端點同權杖？","How do I get the WATI endpoint & token?","WATIのエンドポイントとトークンの取得方法は？")}
                    </summary>
                    <div style={{padding:"0 14px 14px",fontSize:12.5,color:"#075985",fontWeight:600,lineHeight:1.65}}>
                      <ol style={{margin:"4px 0 0",paddingLeft:18,display:"flex",flexDirection:"column",gap:7}}>
                        <li>{T("登入 ","Log in to ","")}<a href="https://app.wati.io" target="_blank" rel="noreferrer" style={{color:"#0284C7",fontWeight:800}}>app.wati.io</a>{T("，確認你嘅 WhatsApp 號碼已連接。","and confirm your WhatsApp number is connected.","でWhatsApp番号が接続済みか確認。")}</li>
                        <li>{T("去 「API Docs」（或 設定 → API Endpoint）。頁頂嘅網址（例：https://live-server-xxxxx.wati.io）就係 ","Go to 「API Docs」 (or Settings → API Endpoint). The URL at the top (e.g. https://live-server-xxxxx.wati.io) is the ","「API Docs」（または設定 → API Endpoint）へ。上部のURL（例：https://live-server-xxxxx.wati.io）が ")}<strong>{T("API 端點","API endpoint","APIエンドポイント")}</strong>{T("。","。","。")}</li>
                        <li>{T("同一頁嘅 「Authorization」 就係 ","The 「Authorization」 value on that page is your ","同ページの「Authorization」が ")}<strong>{T("存取權杖","access token","アクセストークン")}</strong>{T("，複製整串貼落下面。","— copy the whole string below.","です。全体をコピーして下に貼付。")}</li>
                        <li>{T("最後，複製下面嘅 Webhook 網址，貼入 WATI → 設定 → Webhooks，事件揀「收到訊息」。","Finally copy the webhook URL below into WATI → Settings → Webhooks, and select the 「Message Received」 event.","最後に下のWebhook URLをWATI → 設定 → Webhooksに貼付し、「メッセージ受信」イベントを選択。")}</li>
                      </ol>
                    </div>
                  </details>
                  <Input label={T("WATI API 端點","WATI API endpoint","WATI APIエンドポイント")} placeholder="https://live-server-12345.wati.io" value={waForm.waWatiEndpoint} onChange={e => setWaForm(f => ({ ...f, waWatiEndpoint: e.target.value }))} hint={T("WATI → API Docs 頂部嘅網址","The base URL at the top of WATI → API Docs","WATI → API Docs 上部のURL")} />
                  <Input label={T("WATI 存取權杖","WATI access token","WATIアクセストークン")} type="password" placeholder="eyJhbGciOi..." value={waForm.waWatiToken} onChange={e => setWaForm(f => ({ ...f, waWatiToken: e.target.value }))} hint={T("WATI → API Docs → Authorization（可連 Bearer 一齊貼）","WATI → API Docs → Authorization (may include 「Bearer」)","WATI → API Docs → Authorization（「Bearer」付きでも可）")} />
                  <Input label={T("WhatsApp 號碼（選填）","WhatsApp number (optional)","WhatsApp番号（任意）")} placeholder="+85291234567" value={waForm.waWatiNumber} onChange={e => setWaForm(f => ({ ...f, waWatiNumber: e.target.value }))} hint={T("你喺 WATI 連接咗嘅號碼（含國碼）","Your connected WATI number (with country code)","WATIで接続した番号（国番号付き）")} />
                  <div style={{background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:12,padding:"11px 14px"}}>
                    <div style={{fontSize:12,fontWeight:900,color:"#166534",marginBottom:6}}>{T("收訊 Webhook 網址（貼入 WATI）","Incoming webhook URL (paste into WATI)","受信Webhook URL（WATIに貼付）")}</div>
                    <code style={{display:"block",fontSize:11,wordBreak:"break-all",background:"#fff",padding:"8px 10px",borderRadius:8,color:"#166534",border:"1px solid #DCFCE7"}}>{watiWebhookUrl}</code>
                    <button type="button" onClick={() => { try { navigator.clipboard?.writeText(watiWebhookUrl); } catch { /* */ } toast(T("已複製 Webhook 網址","Webhook URL copied","Webhook URLをコピーしました")); }} style={{marginTop:8,fontSize:12,fontWeight:800,color:"#166534",background:"#DCFCE7",border:"none",borderRadius:99,padding:"6px 14px",cursor:"pointer"}}>{T("複製網址","Copy URL","URLをコピー")}</button>
                    <p style={{fontSize:11,color:"#166534",fontWeight:700,margin:"8px 0 0",lineHeight:1.5}}>{T("喺 WATI → 設定 → Webhooks 貼上此網址，事件揀「Message Received / 收到訊息」，AI 客服就會自動回覆客人。","In WATI → Settings → Webhooks, paste this URL and select the 「Message Received」 event so the AI concierge can auto-reply.","WATI → 設定 → Webhooks でこのURLを貼付し、「メッセージ受信」イベントを選択すると、AIが自動返信します。")}</p>
                  </div>
                </>);
              })()}

              {waForm.waProvider === "chatwoot" && (() => {
                const origin = (typeof window !== "undefined" && window.location.origin) || "";
                const chatwootWebhookUrl = origin
                  ? `${(state.platformConfig?.supabaseUrl || 'https://oiiuarooqzbqtuqtshzx.supabase.co')}/functions/v1/whatsapp-webhook?provider=chatwoot&tenant=${encodeURIComponent(user.tenantId)}`
                  : "";
                return (<>
                  <details style={{background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:12,padding:"0"}}>
                    <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:13,fontWeight:900,color:"#166534",display:"flex",alignItems:"center",gap:8}}>
                      <HelpCircle size={16} strokeWidth={2.5} /> {T("點樣連接 Chatwoot？","How do I connect Chatwoot?","Chatwootの接続方法は？")}
                    </summary>
                    <div style={{padding:"0 14px 14px",fontSize:12.5,color:"#166534",fontWeight:600,lineHeight:1.65}}>
                      <ol style={{margin:"4px 0 0",paddingLeft:18,display:"flex",flexDirection:"column",gap:7}}>
                        <li>{T("去 ","Go to ","")}<a href="https://www.chatwoot.com/pricing" target="_blank" rel="noreferrer" style={{color:"#059669",fontWeight:800}}>chatwoot.com</a>{T("，開一個 Startups($19/月) 或以上帳戶。","and open a Startups ($19/mo) or higher account.","でStartups($19/月)以上のアカウントを開設。")}</li>
                        <li>{T("Chatwoot → Settings → Inboxes → + Add Inbox → WhatsApp → 「Connect with WhatsApp Business」，跟 Meta 嘅嵌入式登入。可保留 WhatsApp Business App 一齊用（Coexistence）。","In Chatwoot: Settings → Inboxes → + Add Inbox → WhatsApp → 「Connect with WhatsApp Business」, follow Meta's embedded signup. Your phone can keep the WhatsApp Business App (Coexistence).","Chatwoot: Settings → Inboxes → + Add Inbox → WhatsApp → 「Connect with WhatsApp Business」でMetaの埋込サインアップ。電話のWhatsApp Business Appは併用可能（Coexistence）。")}</li>
                        <li>{T("Profile → Access Tokens 建立一個 API token，複製落下面。","Profile → Access Tokens: create an API token and paste it below.","Profile → Access Tokensでトークンを作成し、下に貼付。")}</li>
                        <li>{T("Chatwoot URL 係 ","Your Chatwoot URL is ","Chatwoot URLは ")}<code>https://app.chatwoot.com/app/accounts/<b>ACCOUNT_ID</b>/…</code>{T("，抽出 ACCOUNT_ID 填下面。Inbox ID 喺 Settings → Inboxes 每個 inbox 嘅 URL 找到。","— extract ACCOUNT_ID for the field below. Inbox ID appears in the URL of each inbox in Settings → Inboxes.","のACCOUNT_IDを下に入力。Inbox IDはSettings → Inboxesの各inboxのURLで確認。")}</li>
                        <li>{T("最後喺該 inbox 嘅「Configuration」 → Webhooks 貼上下面 Webhook 網址（Chatwoot 會轉發客戶訊息返 PettoBook 畀 AI 回覆）。","Finally, under that inbox's 「Configuration」 → Webhooks, paste the URL below (Chatwoot forwards customer messages to PettoBook for the AI to reply).","最後、そのinboxの「Configuration」→ Webhooksで下記URLを貼付（Chatwootが顧客メッセージをPettoBookに転送し、AIが返信）。")}</li>
                      </ol>
                    </div>
                  </details>
                  <Input label="Chatwoot Base URL" placeholder="https://app.chatwoot.com" value={waForm.waChatwootBaseUrl || ""} onChange={e => setWaForm(f => ({ ...f, waChatwootBaseUrl: e.target.value }))} hint={T("Cloud 用 app.chatwoot.com，自架用你嘅網址","Cloud users: app.chatwoot.com. Self-hosted: your URL","Cloud: app.chatwoot.com、セルフホスト: あなたのURL")} />
                  <Input label={T("API Token","API Token","APIトークン")} type="password" placeholder="…" value={waForm.waChatwootApiToken || ""} onChange={e => setWaForm(f => ({ ...f, waChatwootApiToken: e.target.value }))} hint={T("Profile → Access Tokens","Profile → Access Tokens","Profile → Access Tokens")} />
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <Input label={T("Account ID","Account ID","アカウントID")} type="number" placeholder="1" value={waForm.waChatwootAccountId || ""} onChange={e => setWaForm(f => ({ ...f, waChatwootAccountId: parseInt(e.target.value) || null }))} hint={T("URL /app/accounts/<b>N</b>/…","URL /app/accounts/<b>N</b>/…","URL /app/accounts/<b>N</b>/…")} />
                    <Input label={T("Inbox ID","Inbox ID","Inbox ID")} type="number" placeholder="5" value={waForm.waChatwootInboxId || ""} onChange={e => setWaForm(f => ({ ...f, waChatwootInboxId: parseInt(e.target.value) || null }))} hint={T("Settings → Inboxes → your WA inbox","Settings → Inboxes → your WA inbox","Settings → Inboxes → WA inbox")} />
                  </div>
                  <Input label={T("Webhook HMAC 密鑰（選填）","Webhook HMAC secret (optional)","Webhook HMAC 秘密（任意）")} type="password" placeholder="…" value={waForm.waChatwootHmacSecret || ""} onChange={e => setWaForm(f => ({ ...f, waChatwootHmacSecret: e.target.value }))} hint={T("Chatwoot Inbox → Configuration → HMAC 內定義","Set in Chatwoot Inbox → Configuration → HMAC","Chatwoot Inbox → Configuration → HMACで設定")} />
                  <div style={{background:"#F0FDF4",border:"1px dashed #A7F3D0",borderRadius:12,padding:"12px 14px",marginTop:6}}>
                    <p style={{fontSize:12,fontWeight:900,color:"#166534",margin:"0 0 6px"}}>📡 {T("Webhook URL","Webhook URL","Webhook URL")}</p>
                    <code style={{display:"block",background:"#fff",padding:"8px 10px",borderRadius:8,fontSize:11,color:"#166534",wordBreak:"break-all",lineHeight:1.5}}>{chatwootWebhookUrl}</code>
                    <button type="button" onClick={() => { try { navigator.clipboard?.writeText(chatwootWebhookUrl); } catch { /* */ } toast(T("已複製 Webhook 網址","Webhook URL copied","Webhook URLをコピーしました")); }} style={{marginTop:8,fontSize:12,fontWeight:800,color:"#166534",background:"#DCFCE7",border:"none",borderRadius:99,padding:"6px 14px",cursor:"pointer"}}>{T("複製網址","Copy URL","URLをコピー")}</button>
                    <p style={{fontSize:11,color:"#166534",fontWeight:700,margin:"8px 0 0",lineHeight:1.5}}>{T("Chatwoot → Inbox → Configuration → Webhooks 貼上呢個網址。AI 客服會處理客人嘅訊息。","In Chatwoot → Inbox → Configuration → Webhooks, paste this URL. The AI concierge handles customer messages.","Chatwoot → Inbox → Configuration → WebhooksでこのURLを貼付。AIが顧客メッセージを処理。")}</p>
                  </div>
                </>);
              })()}

              {/* ── WhatsApp 預約確認 ────────────────────────────────────────
                  Sent server-side (booking-confirmation edge function, driven
                  by a DB trigger) so it works for the online bookings that
                  arrive while nobody is at the dashboard. Online bookings
                  only; bookings awaiting approval are confirmed on approval.
                  Business-initiated, so Meta requires an APPROVED template. */}
              {(() => {
                const saveConfirm = async (patch) => {
                  setWaForm(f => ({ ...f, ...patch }));
                  try { await upsertMessagingSecrets(user.tenantId, { ...patch }); }
                  catch (e) { toast((e?.message || T("儲存失敗","Save failed","保存失敗")), "error"); }
                };
                const waOn = !!waForm.whatsappEnabled;
                const isTwilio = waForm.waProvider === "twilio";
                const tplText = "✅ 預約確認\n\n{{1}} 你好！{{2}} 嘅美容預約已經確認：\n\n📅 日期：{{3}}\n⏰ 時間：{{4}}\n✂️ 服務：{{5}}\n\n如需改期或取消，請直接回覆此訊息，我哋會盡快處理。\n— {{6}} 🐾";
                return (
                  <div style={{background:"#fff",border:"2px solid var(--c-light)",borderRadius:20,padding:16,marginTop:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>📅 {T("WhatsApp 預約確認","WhatsApp booking confirmation","WhatsApp予約確認")}</div>
                        <div style={{fontSize:12,color:"#8A8A9E",fontWeight:700,marginTop:4,lineHeight:1.5}}>
                          {T("客人網上預約後，自動用你嘅 WhatsApp 號碼發確認。店內／電話預約唔會發；要你批核嘅預約，批核後先發。",
                             "When a customer books online, a confirmation goes out from your WhatsApp number. Counter / phone bookings don't get one; bookings awaiting approval are confirmed once you approve.",
                             "オンライン予約時にお店のWhatsAppから確認を自動送信します。")}
                        </div>
                      </div>
                      <input type="checkbox" checked={!!waForm.waConfirmEnabled} disabled={!waOn || isTwilio}
                        onChange={e => saveConfirm({ waConfirmEnabled: e.target.checked })}
                        style={{transform:"scale(1.3)",marginTop:4,flexShrink:0}} />
                    </div>
                    {!waOn && <p style={{fontSize:12,fontWeight:800,color:"#B45309",margin:"10px 0 0"}}>{T("⚠️ 請先喺上面開啟 WhatsApp。","⚠️ Turn on WhatsApp above first.","⚠️ 先にWhatsAppを有効にしてください。")}</p>}
                    {isTwilio && <p style={{fontSize:12,fontWeight:800,color:"#B45309",margin:"10px 0 0"}}>{T("⚠️ Twilio 暫時未支援範本訊息，無法發送預約確認。請用 Meta、WATI 或 Chatwoot。","⚠️ Twilio can't send templates here yet — use Meta, WATI or Chatwoot.","⚠️ Twilioはテンプレート未対応です。")}</p>}
                    {waOn && !isTwilio && <>
                      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:10,marginTop:12}}>
                        <Input label={T("已批核範本名稱","Approved template name","承認済みテンプレート名")} placeholder="booking_confirmation"
                          value={waForm.waConfirmTemplate || ""}
                          onChange={e => setWaForm(f => ({ ...f, waConfirmTemplate: e.target.value }))}
                          onBlur={e => saveConfirm({ waConfirmTemplate: e.target.value.trim() })} />
                        <div>
                          <div style={{fontSize:12,fontWeight:800,color:"#6A6A85",marginBottom:6}}>{T("語言","Language","言語")}</div>
                          <select value={waForm.waConfirmLang || "zh_HK"} onChange={e => saveConfirm({ waConfirmLang: e.target.value })}
                            style={{width:"100%",padding:"12px 10px",borderRadius:14,border:"2px solid var(--c-light)",fontSize:14,fontWeight:700,background:"#FFFDFD"}}>
                            <option value="zh_HK">繁中（香港）zh_HK</option>
                            <option value="zh_TW">繁中（台灣）zh_TW</option>
                            <option value="en">English en</option>
                          </select>
                        </div>
                      </div>
                      {waForm.waConfirmEnabled && !String(waForm.waConfirmTemplate || "").trim() && (
                        <p style={{fontSize:12,fontWeight:800,color:"#B91C1C",margin:"8px 0 0"}}>{T("⚠️ 已開啟但未填範本名稱 — 未有範本唔會發送。","⚠️ On, but no template name — nothing will send until you add one.","⚠️ テンプレート名が未入力です。")}</p>
                      )}
                      <details style={{marginTop:12}}>
                        <summary style={{fontSize:12,fontWeight:900,color:"var(--c-primary)",cursor:"pointer"}}>{T("📋 點樣建立範本（一次過）","📋 How to create the template (one-off)","📋 テンプレートの作り方")}</summary>
                        <ol style={{fontSize:12,color:"#4A4A68",fontWeight:700,lineHeight:1.7,paddingLeft:18,margin:"8px 0"}}>
                          <li>{T("去 Meta WhatsApp Manager → 訊息範本 → 建立範本","Go to Meta WhatsApp Manager → Message templates → Create","Meta WhatsApp Manager → テンプレート作成")}</li>
                          <li>{T("類別揀「Utility（公用事業）」，名稱填 booking_confirmation，語言揀 Chinese (HKG)","Category: Utility · Name: booking_confirmation · Language: Chinese (HKG)","カテゴリ: Utility")}</li>
                          <li>{T("內容貼上以下文字（{{1}}–{{6}} 次序唔好改）","Paste this body — keep {{1}}–{{6}} in this order","以下を本文に貼り付け")}</li>
                          <li>{T("批核後（通常幾分鐘），將範本名稱填返上面","Once approved (usually minutes), enter the name above","承認後、上に名前を入力")}</li>
                        </ol>
                        <pre style={{whiteSpace:"pre-wrap",background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:10,fontSize:12,color:"#374151",margin:0,fontFamily:"inherit"}}>{tplText}</pre>
                        <button type="button" onClick={() => { try { navigator.clipboard?.writeText(tplText); } catch { /* */ } toast(T("已複製範本內容","Template copied","コピーしました")); }}
                          style={{marginTop:8,fontSize:12,fontWeight:800,color:"var(--c-primary)",background:"#fff",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"6px 12px",cursor:"pointer"}}>📋 {T("複製範本內容","Copy template text","コピー")}</button>
                        <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"8px 0 0",lineHeight:1.5}}>
                          {T("{{1}} 客人名　{{2}} 毛孩名　{{3}} 日期　{{4}} 時間　{{5}} 服務　{{6}} 店名。Meta 要求每個變數填範例值，例如：陳小姐、豆豆、2026-10-01（三）、14:30、沖涼全套、你嘅店名。",
                             "{{1}} customer · {{2}} pet · {{3}} date · {{4}} time · {{5}} service · {{6}} shop. Meta asks for a sample value for each.",
                             "{{1}}顧客 {{2}}ペット {{3}}日付 {{4}}時間 {{5}}サービス {{6}}店名")}
                        </p>
                      </details>
                    </>}
                  </div>
                );
              })()}

              {/* ── AI 自動回覆 (Gemini concierge) — needs the shop's own
                  WhatsApp number above so inbound webhooks can be routed.
                  Gated: platform kill switch (aiConciergeEnabled) + the
                  shop's plan must grant aiConcierge (Enterprise). ── */}
              {(() => {
                const platformOn = state.platformConfig?.aiConciergeEnabled === true;
                const planHasAi = !!getPlans(state.platformConfig)[tenant?.plan || "free"]?.features?.aiConcierge;
                const cap = Number(state.platformConfig?.aiConciergeMonthlyCap) || 1500;
                if (!platformOn) return null; // hidden platform-wide
                if (!planHasAi) return (
                  <div style={{marginTop:8,padding:"14px",borderRadius:14,background:"#F5F3FF",border:"2px solid #DDD6FE",display:"flex",gap:12,alignItems:"center"}}>
                    <Lock size={22} color="#7C3AED" style={{flexShrink:0}} />
                    <div>
                      <div style={{fontSize:14,fontWeight:900,color:"#5B21B6"}}>🤖 {T("WhatsApp AI 客服","WhatsApp AI concierge","WhatsApp AIコンシェルジュ")}</div>
                      <div style={{fontSize:12,color:"#7C6FD6",fontWeight:700,marginTop:2,lineHeight:1.5}}>{T("升級至旗艦版即可開啟：AI 自動回覆客戶查詢並代客預約。","Upgrade to Enterprise to unlock AI auto-reply and in-chat booking.","旗艦プランにアップグレードでAI自動返信とチャット予約が利用可能。")}</div>
                    </div>
                  </div>
                );
                // AI fields auto-save on change/blur (owners kept forgetting
                // to scroll down + tap 儲存設定, then losing their work on
                // force-close). Credentials above still use the Save button
                // because half-typed keys shouldn't hit the DB.
                const saveAi = async (patch) => {
                  setWaForm(f => ({ ...f, ...patch }));
                  try {
                    await upsertMessagingSecrets(user.tenantId, { ...patch });
                    if (Object.prototype.hasOwnProperty.call(patch, "waAiEnabled")) {
                      try { window.dispatchEvent(new CustomEvent("petos:wa-ai-changed", { detail: { tenantId: user.tenantId, waAiEnabled: !!patch.waAiEnabled } })); } catch { /* ignore */ }
                    }
                  }
                  catch (e) { toast((e?.message || T("儲存失敗","Save failed","保存失敗")), "error"); }
                };
                return <>
              <div style={{marginTop:8,padding:"12px 14px",borderRadius:14,background:"#F5F3FF",border:"2px solid #DDD6FE",display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>🤖 {T("AI 自動回覆助手","AI auto-reply assistant","AI自動返信アシスタント")}</div>
                    <div style={{fontSize:11,color:"#8A8A9E",fontWeight:700,marginTop:2}}>{T("客戶 WhatsApp 查詢由 AI 即時回覆，並可代客建立預約（需先設定上方店鋪 WhatsApp 號碼）","AI instantly answers customer WhatsApp inquiries and can create bookings (requires your shop WhatsApp number above)","お客様のWhatsApp問い合わせにAIが即時返信し、予約も作成できます（上の店舗WhatsApp番号の設定が必要）")}</div>
                  </div>
                  <Toggle on={!!waForm.waAiEnabled} onChange={v => saveAi({ waAiEnabled: v })} />
                </div>
                {waForm.waAiEnabled && <>
                  <AiUsageBadge tenantId={user.tenantId} cap={cap} />
                  {/* Read-only preview of the live data the AI answers from, so
                      the owner can spot-check prices/hours/policies and fix the
                      source setting if anything is wrong. */}
                  {(() => {
                    const dayL = [zhEn("日", "Sun"),zhEn("一", "Mon"),zhEn("二", "Tue"),zhEn("三", "Wed"),zhEn("四", "Thu"),zhEn("五", "Fri"),zhEn("六", "Sat")];
                    const svcs = (tenant?.services || []).filter(s => s.enabled !== false && s.customerVisible !== false && s.kind !== "spa");
                    const tiers = tenant?.pricingConfig?.tiers || [];
                    const bh = tenant?.businessHours || {};
                    const cp = tenant?.cancellationPolicy || {};
                    const sbs = (tenant?.specialBreeds || []).map(b => b.name || (b.names || [])[0]).filter(Boolean);
                    const priceOf = (s) => {
                      const mp = s.matrixPricing || {};
                      if (s.pricingMode !== "matrix" || !mp.byTier) return "$" + (s.basePrice || 0);
                      const parts = tiers.map(t => { const c = mp.byTier?.[t.id]; if (!c || c.na) return null; const p = c.short ?? c.long; return p != null ? `${t.label}$${p}` : null; }).filter(Boolean);
                      return parts.length ? parts.join(" · ") : ("$" + (s.basePrice || 0) + zhEn("起", "+"));
                    };
                    return (
                      <details style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:12,padding:0}}>
                        <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:12.5,fontWeight:900,color:"#5B21B6",display:"flex",alignItems:"center",gap:8}}>🔎 {T("AI 會用嘅資料（點開檢查）","What the AI knows (tap to review)","AIが使う情報（確認）")}</summary>
                        <div style={{padding:"0 14px 14px",display:"flex",flexDirection:"column",gap:9}}>
                          <p style={{fontSize:10.5,color:"#7C6FD6",fontWeight:700,margin:"0 0 2px",lineHeight:1.5}}>{T("如有錯，去對應設定（服務／營業時間／店鋪政策）修改，即時生效。","If anything is wrong, fix it in the matching setting (Services / Hours / Policies) — it applies instantly.","誤りは対応する設定で修正すると即反映されます。")}</p>
                          <div>
                            <div style={{fontSize:11,fontWeight:900,color:"#5B21B6",marginBottom:3}}>{T("服務同價錢","Services & prices","サービスと料金")}</div>
                            {svcs.length ? svcs.map(s => <div key={s.id} style={{fontSize:11.5,lineHeight:1.65,color:"#4A4A68",fontWeight:600}}><b>{s.name}</b> — {priceOf(s)}</div>) : <div style={{fontSize:11.5,color:"#AFAFC4"}}>{T("未設定服務","No services","なし")}</div>}
                            {sbs.length ? <div style={{fontSize:10.5,color:"#8A8A9E",marginTop:3}}>{T("特別品種另計","Special breeds priced separately","特別犬種は別料金")}{zhEn("：", ": ")}{sbs.join("、")}</div> : null}
                          </div>
                          <div>
                            <div style={{fontSize:11,fontWeight:900,color:"#5B21B6",marginBottom:3}}>{T("營業時間","Hours","営業時間")}</div>
                            <div style={{lineHeight:1.7}}>{[0,1,2,3,4,5,6].map(d => { const v = bh[d] ?? bh[String(d)]; return <span key={d} style={{fontSize:11,color:"#4A4A68",fontWeight:600,marginRight:8,whiteSpace:"nowrap"}}>{dayL[d]} {v?.enabled ? `${v.open||"09:00"}-${v.close||"18:00"}` : T("休","off","休")}</span>; })}</div>
                          </div>
                          <div>
                            <div style={{fontSize:11,fontWeight:900,color:"#5B21B6",marginBottom:3}}>{T("政策","Policies","ポリシー")}</div>
                            <div style={{fontSize:11.5,color:"#4A4A68",fontWeight:600,lineHeight:1.6}}>{T("預約","Booking","予約")}{zhEn("：", ": ")}{cp.requireApproval ? T("需店家確認","needs approval","要確認") : T("自動接受","auto-accept","自動")}{Number(cp.minAdvanceHours) > 0 ? ` · ${T("需提前","min advance","最低")} ${cp.minAdvanceHours}h` : ""}</div>
                          </div>
                        </div>
                      </details>
                    );
                  })()}
                  <label style={{display:"flex",alignItems:"center",gap:10,fontSize:13,fontWeight:800,color:"#4A4A68",cursor:"pointer"}}>
                    <input type="checkbox" checked={waForm.waAiBookingEnabled !== false} onChange={e => saveAi({ waAiBookingEnabled: e.target.checked })} style={{transform:"scale(1.2)"}} />
                    {T("允許 AI 喺對話入面直接建立預約（會以「待確認」狀態出現喺日歷）","Allow the AI to create bookings in-chat (appear on your calendar as pending)","AIがチャット内で予約を作成できるようにする（カレンダーに「確認待ち」で表示）")}
                  </label>
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4}}>{T("店鋪知識庫（AI 會根據呢度回答）","Shop knowledge base (the AI answers from this)","店舗ナレッジ（AIはこれを基に回答）")}</div>
                    <textarea value={waForm.waAiKnowledge || ""}
                      onChange={e => setWaForm(f => ({ ...f, waAiKnowledge: e.target.value }))}
                      onBlur={e => saveAi({ waAiKnowledge: e.target.value })}
                      rows={4}
                      placeholder={T("例：泊車資訊、接送安排、貓狗以外唔接、農曆新年休息安排…","e.g. parking info, pickup arrangements, dogs & cats only, CNY holiday schedule…","例：駐車場情報、送迎、対応動物、年末年始の休業案内…")}
                      style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4}}>{T("語氣／額外指示（選填）","Tone / extra instructions (optional)","トーン／追加指示（任意）")}</div>
                    <textarea value={waForm.waAiInstructions || ""}
                      onChange={e => setWaForm(f => ({ ...f, waAiInstructions: e.target.value }))}
                      onBlur={e => saveAi({ waAiInstructions: e.target.value })}
                      rows={2}
                      placeholder={T("例：語氣活潑啲、多用 emoji、自稱「小毛」","e.g. be playful, use emojis, call yourself \"Momo\"","例：明るい口調で、絵文字を使い、「モモ」と名乗る")}
                      style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4}}>{T("AI 助手名稱（選填）","AI assistant name (optional)","AIアシスタント名（任意）")}</div>
                    <input value={waForm.waAiName || ""}
                      onChange={e => setWaForm(f => ({ ...f, waAiName: e.target.value }))}
                      onBlur={e => saveAi({ waAiName: e.target.value })}
                      placeholder={T("例：小毛（AI 會自稱呢個名，並講明係 AI 助手）","e.g. Momo (the AI uses this name and discloses it's an AI)","例：モモ")}
                      style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}} />
                    <div style={{fontSize:11,color:"#9A93B8",fontWeight:600,marginTop:4,lineHeight:1.5}}>
                      {T("為咗保障你，AI 一定會表明自己係 AI 助手（唔會扮真人），客人可隨時講「人工」搵真人客服。","To protect you, the AI always discloses that it's an AI (never pretends to be human); customers can say 「人工」 to reach a real person anytime.","AIは常にAIであることを明示し、人間のふりはしません。お客様は「人工」と言えばいつでもスタッフに繋がります。")}
                    </div>
                  </div>
                  {/* AI service hours — 24h always-on, or a daily window. Outside
                      the window the AI stops auto-replying (you handle manually). */}
                  {(() => {
                    // Three modes so shops can pick the common cases without
                    // hand-typing a window:
                    //   null / {enabled:false}                → always-on
                    //   {enabled:true, mode:"off_hours"}      → AI takes over only when the shop is closed
                    //   {enabled:true, mode:"custom", start,end} → bespoke window
                    // Legacy rows with start/end but no mode are treated as
                    // custom (see edge-fn withinAiHours).
                    const hrs = waForm.waAiHours || {};
                    const enabled = hrs.enabled === true;
                    const mode = !enabled ? "always" : (hrs.mode === "off_hours" ? "off_hours" : "custom");
                    const setMode = (m) => {
                      if (m === "always") return saveAi({ waAiHours: null });
                      if (m === "off_hours") return saveAi({ waAiHours: { enabled: true, mode: "off_hours" } });
                      return saveAi({ waAiHours: { enabled: true, mode: "custom", start: hrs.start || "10:00", end: hrs.end || "20:00" } });
                    };
                    const setWindow = (patch) => saveAi({ waAiHours: { enabled: true, mode: "custom", start: hrs.start || "10:00", end: hrs.end || "20:00", ...patch } });
                    const opt = (m, label) => (
                      <button key={m} type="button" onClick={() => setMode(m)} style={{flex:1,padding:"10px",borderRadius:12,border:"2px solid "+(mode===m?"var(--c-primary)":"var(--c-light)"),background:mode===m?"var(--c-lighter)":"#fff",color:mode===m?"var(--c-primary)":"#8A8A9E",fontWeight:800,fontSize:13,cursor:"pointer"}}>{label}</button>
                    );
                    return (
                      <div>
                        <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:6}}>{T("AI 自動回覆時間","AI auto-reply hours","AI自動返信の時間")}</div>
                        <div style={{display:"flex",gap:8,marginBottom:mode==="custom"?10:0,flexWrap:"wrap"}}>
                          {opt("always",    T("🕐 24 小時","🕐 24 hours","🕐 24時間"))}
                          {opt("off_hours", T("🌙 只喺非營業時間","🌙 Only when closed","🌙 営業時間外のみ"))}
                          {opt("custom",    T("⏰ 自訂時間","⏰ Set hours","⏰ 時間指定"))}
                        </div>
                        {mode === "custom" && (
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:13,fontWeight:700,color:"#8A8A9E"}}>{T("由","From","")}</span>
                            <input type="time" value={hrs.start || "10:00"} onChange={e => setWindow({ start: e.target.value })} style={{flex:1,padding:"9px 10px",borderRadius:10,border:"2px solid var(--c-light)",fontSize:14,fontWeight:700,outline:"none"}} />
                            <span style={{fontSize:13,fontWeight:700,color:"#8A8A9E"}}>{T("至","to","〜")}</span>
                            <input type="time" value={hrs.end || "20:00"} onChange={e => setWindow({ end: e.target.value })} style={{flex:1,padding:"9px 10px",borderRadius:10,border:"2px solid var(--c-light)",fontSize:14,fontWeight:700,outline:"none"}} />
                          </div>
                        )}
                        <div style={{fontSize:11,color:"#9A93B8",fontWeight:600,marginTop:6,lineHeight:1.5}}>
                          {mode === "always"
                            ? T("AI 全日 24 小時自動回覆。","AI auto-replies 24 hours a day.","AIは24時間自動返信します。")
                            : mode === "off_hours"
                              ? T("營業時間內客人訊息由你人手回覆；打烊、假期、非營業日就由 AI 接手。","During business hours you reply manually; after hours, holidays and days-off the AI takes over.","営業時間中はスタッフが返信、時間外・休業日はAIが対応。")
                              : T("指定時段內 AI 自動回覆；時段外你人手跟進。","AI auto-replies within the window; outside it you handle manually.","指定時間内のみAIが返信。")}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Extreme weather policy — AI checks live HK Observatory
                      warnings + applies this policy. */}
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4}}>{T("極端天氣政策（選填，AI 會查即時天文台警告）","Extreme weather policy (optional; AI checks live HK Observatory warnings)","悪天候ポリシー（任意）")}</div>
                    <textarea value={waForm.waAiWeatherPolicy || ""}
                      onChange={e => setWaForm(f => ({ ...f, waAiWeatherPolicy: e.target.value }))}
                      onBlur={e => saveAi({ waAiWeatherPolicy: e.target.value })}
                      rows={3}
                      placeholder={T("例：天文台發出 8 號或以上風球、或紅／黑色暴雨警告期間暫停營業，我哋會主動聯絡你改期，訂金會保留。","e.g. We close when a T8+ typhoon signal or Red/Black rainstorm is in force; we'll contact you to reschedule and keep your deposit.","例：8号以上または赤/黒の大雨警報発令中は休業し、振替のご連絡をします。")}
                      style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                    <div style={{fontSize:11,color:"#9A93B8",fontWeight:600,marginTop:4,lineHeight:1.5}}>
                      {T("客人問打風／落雨開唔開、或想改期時，AI 會即時查香港天文台警告（8 號風球 / 紅黑雨），再按呢個政策回覆。","When a customer asks about a typhoon/rainstorm or rescheduling, the AI checks live HKO warnings (T8 / red-black rain) and answers per this policy.","悪天候時、AIは天文台の警報を確認してこのポリシーで回答します。")}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:900,color:"#4A4A68",marginBottom:4}}>{T("開場選單（客人開始對話時顯示，選填）","Opening menu (shown when a customer starts a chat, optional)","開始メニュー（会話開始時に表示、任意）")}</div>
                    <textarea value={waForm.waAiGreeting || ""}
                      onChange={e => setWaForm(f => ({ ...f, waAiGreeting: e.target.value }))}
                      onBlur={e => saveAi({ waAiGreeting: e.target.value })}
                      rows={5}
                      placeholder={T("留空 = 用預設美容選單。想加零售／其他選項就自訂，例如：\n你好！我可以幫你：\n1️⃣ 預約美容 / SPA\n2️⃣ 查詢服務同價錢\n3️⃣ 選購產品（我幫你轉店員）\n請問想我幫你邊樣？","Leave blank = default grooming menu. Customize to add retail / other options, e.g.:\nHi! I can help you with:\n1️⃣ Book grooming / SPA\n2️⃣ Services & prices\n3️⃣ Shop products (I'll pass you to staff)\nWhat would you like?","空欄=デフォルトのトリミングメニュー。物販など追加する場合はカスタマイズ")}
                      style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-light)",fontSize:13,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                    <div style={{fontSize:11,color:"#9A93B8",fontWeight:600,marginTop:4,lineHeight:1.5}}>
                      {T("AI 只處理美容預約；客人揀零售／其他非美容項目時，會自動轉交你人手跟進。","The AI only handles grooming bookings; if a customer picks retail / other non-grooming items, it hands the chat to you automatically.","AIはトリミング予約のみ対応。物販など以外を選ぶと自動でスタッフに引き継ぎます。")}
                    </div>
                  </div>
                  <div style={{fontSize:11,color:"#7C6FD6",fontWeight:700,lineHeight:1.5}}>
                    {T("💡 收到嘅對話會出現喺底部「訊息」分頁；你隨時可以喺該處暫停 AI、人手接管。","💡 Conversations appear in the 「Inbox」 tab below; you can pause the AI and take over anytime.","💡 会話は下部の「メッセージ」タブに表示されます。いつでもAIを止めてスタッフが対応できます。")}
                  </div>
                </>}
              </div>
                </>;
              })()}
              <div style={{display:"flex",gap:10,marginTop:4}}>
                <Btn size="sm" disabled={waSaving} onClick={async () => {
                  setWaSaving(true);
                  try {
                    await upsertMessagingSecrets(user.tenantId, waFormForSave(waForm));
                    toast(i18nT("tx.waSettingsSaved"));
                  } catch (e) { toast(e?.message || T("儲存失敗","Save failed","保存に失敗しました"), "error"); }
                  finally { setWaSaving(false); }
                }} style={{flex:1}}>{waSaving ? T("儲存中…","Saving…","保存中…") : T("儲存設定","Save settings","設定を保存")}</Btn>
                <Btn variant="secondary" size="sm" disabled={waTesting || !waForm.whatsappEnabled || !user.phone} onClick={async () => {
                  if (!user.phone) { toast(T("請先在你的帳號設定電話","Please set a phone number in your account first","まずアカウントに電話番号を設定してください"), "error"); return; }
                  setWaTesting(true);
                  try {
                    const res = await sendWhatsApp({
                      tenantId: user.tenantId,
                      to: user.phone,
                      text: "【" + (tenant?.name||"PettoBook") + "】" + T("這是測試訊息 ✅\n如果你收到了，WhatsApp 設定正確！","This is a test message ✅\nIf you received it, your WhatsApp settings are correct!","これはテストメッセージです ✅\n受信できれば、WhatsApp設定は正しいです！"),
                    });
                    if (res?.ok) toast(i18nT("tx.testMsgSent"));
                    else toast(T("發送失敗：","Send failed: ","送信失敗：") + (res?.raw?.error?.message || T("請檢查設定","Please check your settings","設定を確認してください")), "error");
                  } catch (e) { toast(e?.message || T("發送失敗","Send failed","送信に失敗しました"), "error"); }
                  finally { setWaTesting(false); }
                }} style={{flex:1}}>{waTesting ? T("發送中…","Sending…","送信中…") : T("📲 寄測試到自己","📲 Send a test to myself","📲 自分にテスト送信")}</Btn>
              </div>
            </div>}
          </Card>}

          {/* SMS BYOK card hidden — platform uses its own Twilio account.
              Re-enable here if BYO SMS comes back as a paid feature. */}
          {/* ── SMS (Twilio) ──────────────────────────────────────────── */}
          {false && settingsSubtab === "messaging" && <Card style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📱 SMS（Twilio）", "📱 SMS (Twilio)")}</h3>
              {waForm?.smsEnabled && <span style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:"#D1FAE5",color:"#065F46",fontWeight:800}}>{T("已啟用","Enabled","有効")}</span>}
            </div>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>
              {T("對唔用 WhatsApp 嘅客戶（特別係長輩）發 SMS 確認。需到 ","Send SMS confirmations to customers who don't use WhatsApp (especially older ones). Open an account at ","WhatsAppを使わないお客様（特に年配の方）にSMS確認を送信。")}<a href="https://www.twilio.com" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800}}>Twilio</a> {T("開戶取得 Account SID、Auth Token 同發送號碼。","to get an Account SID, Auth Token and sender number.","でアカウントを開設し、Account SID、Auth Token、送信番号を取得してください。")}
            </p>
            {!waForm ? <WaLoadState /> : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,background:waForm.smsEnabled?"var(--c-lighter)":"#FFFDFD",border:"2px solid "+(waForm.smsEnabled?"var(--c-primary)":"var(--c-light)"),cursor:"pointer"}}>
                <input type="checkbox" checked={!!waForm.smsEnabled} onChange={e => setWaForm(f => ({ ...f, smsEnabled: e.target.checked }))} style={{transform:"scale(1.2)"}} />
                <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{T("啟用 SMS 通知","Enable SMS notifications","SMS通知を有効化")}</span>
              </label>
              <Input label="Twilio Account SID" placeholder="AC..." value={waForm.smsAccountSid || ""} onChange={e => setWaForm(f => ({ ...f, smsAccountSid: e.target.value }))} />
              <Input label="Auth Token" type="password" placeholder="••••" value={waForm.smsAuthToken || ""} onChange={e => setWaForm(f => ({ ...f, smsAuthToken: e.target.value }))} />
              <Input label={T("發送號碼","Sender number","送信番号")} placeholder="+15551234567" type="tel" value={waForm.smsFromNumber || ""} onChange={e => setWaForm(f => ({ ...f, smsFromNumber: e.target.value }))} hint={T("格式 E.164（含國碼）","E.164 format (with country code)","E.164形式（国番号付き）")} />
              <div style={{display:"flex",gap:10,marginTop:4}}>
                <Btn size="sm" disabled={waSaving} onClick={async () => {
                  setWaSaving(true);
                  try { await upsertMessagingSecrets(user.tenantId, waFormForSave(waForm)); toast(i18nT("tx.smsSettingsSaved")); }
                  catch (e) { toast(e?.message || T("儲存失敗","Save failed","保存に失敗しました"), "error"); }
                  finally { setWaSaving(false); }
                }} style={{flex:1}}>{waSaving ? T("儲存中…","Saving…","保存中…") : T("儲存設定","Save settings","設定を保存")}</Btn>
                <Btn variant="secondary" size="sm" disabled={!waForm.smsEnabled || !user.phone} onClick={async () => {
                  if (!user.phone) { toast(T("請先在你的帳號設定電話","Please set a phone number in your account first","まずアカウントに電話番号を設定してください"), "error"); return; }
                  try {
                    const res = await sendSms({ tenantId: user.tenantId, to: user.phone, text: "【" + (tenant?.name || "PettoBook") + "】" + T("SMS 測試 ✅","SMS test ✅","SMSテスト ✅") });
                    if (res?.ok) toast(i18nT("tx.testSmsSent")); else toast(i18nT("own.set.msg.testFailed"), "error");
                  } catch (e) { toast(e?.message || T("發送失敗","Send failed","送信に失敗しました"), "error"); }
                }} style={{flex:1}}>{T("📲 寄測試到自己","📲 Send a test to myself","📲 自分にテスト送信")}</Btn>
              </div>
            </div>}
          </Card>}

          {/* Email BYOK card hidden — platform uses its own Resend account.
              Re-enable here if BYO Email comes back as a paid feature. */}
          {/* ── Email (Resend) ───────────────────────────────────────── */}
          {false && settingsSubtab === "messaging" && <Card style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📧 Email（Resend）", "📧 Email (Resend)")}</h3>
              {waForm?.emailEnabled && <span style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:"#D1FAE5",color:"#065F46",fontWeight:800}}>{T("已啟用","Enabled","有効")}</span>}
            </div>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>
              {T("發送收據、預約確認及行銷訊息嘅 Email 通道。需到 ","Email channel for sending receipts, booking confirmations and marketing messages. Register at ","レシート、予約確認、マーケティングメッセージを送るEmailチャネル。")}<a href="https://resend.com" target="_blank" rel="noreferrer" style={{color:"var(--c-primary)",fontWeight:800}}>Resend</a> {T("註冊並驗證寄件域名。","and verify your sending domain.","に登録し、送信ドメインを認証してください。")}
            </p>
            {!waForm ? <WaLoadState /> : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,background:waForm.emailEnabled?"var(--c-lighter)":"#FFFDFD",border:"2px solid "+(waForm.emailEnabled?"var(--c-primary)":"var(--c-light)"),cursor:"pointer"}}>
                <input type="checkbox" checked={!!waForm.emailEnabled} onChange={e => setWaForm(f => ({ ...f, emailEnabled: e.target.checked }))} style={{transform:"scale(1.2)"}} />
                <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{zhEn("啟用 Email 通知", "Enable email notifications")}</span>
              </label>
              <Input label="Resend API Key" type="password" placeholder="re_..." value={waForm.emailApiKey || ""} onChange={e => setWaForm(f => ({ ...f, emailApiKey: e.target.value }))} />
              <Input label={zhEn("寄件人 Email", "Sender email")} placeholder="bookings@your-shop.com" type="email" value={waForm.emailFrom || ""} onChange={e => setWaForm(f => ({ ...f, emailFrom: e.target.value }))} hint={zhEn("必須喺 Resend 驗證過嘅域名", "Must be a domain verified in Resend")} />
              <div style={{display:"flex",gap:10,marginTop:4}}>
                <Btn size="sm" disabled={waSaving} onClick={async () => {
                  setWaSaving(true);
                  try { await upsertMessagingSecrets(user.tenantId, waFormForSave(waForm)); toast(i18nT("tx.emailSettingsSaved")); }
                  catch (e) { toast(e?.message || zhEn("儲存失敗", "Save failed"), "error"); }
                  finally { setWaSaving(false); }
                }} style={{flex:1}}>{waSaving ? zhEn("儲存中…", "Saving…") : zhEn("儲存設定", "Save settings")}</Btn>
                <Btn variant="secondary" size="sm" disabled={!waForm.emailEnabled || !user.email} onClick={async () => {
                  if (!user.email) { toast(zhEn("請先設定你的 Email", "Set your email first"), "error"); return; }
                  try {
                    const res = await sendEmail({ tenantId: user.tenantId, to: user.email, subject: "[" + (tenant?.name || "PettoBook") + zhEn("] Email 測試", "] Email test"), text: zhEn("這是測試訊息 ✅", "This is a test message ✅") });
                    if (res?.ok) toast(i18nT("tx.testEmailSent")); else toast(i18nT("own.set.msg.testFailed"), "error");
                  } catch (e) { toast(e?.message || zhEn("發送失敗", "Failed to send"), "error"); }
                }} style={{flex:1}}>{zhEn("📨 寄測試到自己", "📨 Send a test to myself")}</Btn>
              </div>
            </div>}
          </Card>}
              </div>
            )}
          </Card>}

          {/* ── Editable message templates per trigger ───────────────── */}
          {inSec("messaging","templates") && (() => {
            const triggers = [
              { key: "booking_confirmed", label: zhEn("預約確認", "Booking confirmation"),   desc: zhEn("客戶剛預約完即時收到", "Sent as soon as a customer books"), icon: "✅" },
              { key: "reschedule",        label: zhEn("改期通知", "Reschedule notice"),   desc: zhEn("客戶／店家改期後發送", "Sent after the customer or shop reschedules"),   icon: "📅" },
              { key: "reminder",          label: zhEn("前一日提醒", "Day-before reminder"), desc: zhEn("預約前 24 小時自動發送", "Sent automatically 24 hours before the booking"), icon: "📅" },
              { key: "checkin",           label: zhEn("到店通知", "Check-in notice"),   desc: zhEn("員工標記「到店」時發送", "Sent when staff mark \"Arrived\""), icon: "🛁" },
              { key: "grooming_started",  label: zhEn("美容開始", "Grooming started"),   desc: zhEn("員工標記「美容中」時發送", "Sent when staff mark \"Grooming\""), icon: "✂️" },
              { key: "grooming_done",     label: zhEn("美容完成", "Grooming finished"),   desc: zhEn("員工提交報告時發送（含健康報告 + 帳單）", "Sent when staff submit the report (incl. health report + bill)"), icon: "🎉" },
              { key: "cancel",            label: zhEn("預約取消", "Booking cancelled"),   desc: zhEn("客戶／店家取消預約後發送", "Sent after the customer or shop cancels"), icon: "❌" },
            ];
            const mc = tenant?.messagingConfig || {};
            const tpls = mc.templates || {};
            const subjs = mc.subjects || {};
            // Local DOM-ref map for the uncontrolled textareas. Each
            // render creates a fresh map; the textareas remount only
            // when `key` changes (we include `isCustom` in the key) so
            // the refs are valid for the entire typing session.
            const tpltRefs = { current: {} };
            const subjRefs = { current: {} };
            const update = (key, val) => {
              setState(s => ({
                ...s,
                tenants: s.tenants.map(t => t.id === user.tenantId
                  ? { ...t, messagingConfig: { ...(t.messagingConfig || {}), templates: { ...(t.messagingConfig?.templates || {}), [key]: val } } }
                  : t
                ),
              }));
            };
            const updateSubj = (key, val) => {
              setState(s => ({
                ...s,
                tenants: s.tenants.map(t => t.id === user.tenantId
                  ? { ...t, messagingConfig: { ...(t.messagingConfig || {}), subjects: { ...(t.messagingConfig?.subjects || {}), [key]: val } } }
                  : t
                ),
              }));
            };
            return (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📝 自訂訊息範本", "📝 Custom message templates")} <HelpTip topic="templates" /></h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700,lineHeight:1.5}}>
                  {zhEn("每個事件嘅自動訊息可以自訂內容。空白格表示使用預設範本。\n                  支援以下動態欄位：", "You can customise the automatic message for each event. A blank box uses the default template.\n                  Supported placeholders:")}
                </p>
                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:14,padding:"8px 10px",background:"var(--c-lighter)",borderRadius:10}}>
                  {["{shopName}","{customerName}","{petName}","{service}","{date}","{time}","{deposit}","{address}","{reason}","{skin}","{ear}","{teeth}","{mood}","{nails}","{analGlands}","{parasites}","{eyeDischarge}","{matting}","{notes}","{totalPaid}"].map(t => (
                    <code key={t} style={{fontSize:10,fontWeight:800,color:"var(--c-primary)",background:"#fff",padding:"2px 6px",borderRadius:6,border:"1px solid var(--c-border)"}}>{t}</code>
                  ))}
                </div>
                <div style={{display:"flex",gap:6,overflowX:"auto",padding:"4px 2px 12px",marginBottom:4,borderBottom:"2px solid var(--c-light)"}}>
                  {triggers.map(t => {
                    const active = templateTab === t.key;
                    const customized = !!tpls[t.key] || !!subjs[t.key];
                    return (
                      <button key={t.key} onClick={() => setTemplateTab(t.key)} style={{flexShrink:0,padding:"8px 14px",borderRadius:99,border:"2px solid "+(active?"var(--c-primary)":"var(--c-light)"),background:active?"var(--c-primary)":"#fff",color:active?"#fff":"#4A4A68",fontSize:12,fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap"}}>
                        <span>{t.icon}</span>
                        <span>{t.label}</span>
                        {customized && <span style={{width:6,height:6,borderRadius:99,background:active?"#fff":"var(--c-primary)"}}/>}
                      </button>
                    );
                  })}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {triggers.filter(t => t.key === templateTab).map(t => {
                    const value = tpls[t.key] ?? "";
                    const defaultTpl = DEFAULT_TEMPLATE_STRINGS[t.key] || "";
                    const defaultSubj = DEFAULT_SUBJECT_STRINGS[t.key] || "";
                    const subjValue = subjs[t.key] ?? "";
                    const isCustom = !!tpls[t.key];
                    const isSubjCustom = !!subjs[t.key];
                    // Click-to-edit-existing: the textarea is PRE-FILLED
                    // with either the custom template or the platform
                    // default, so tapping doesn't show an empty field.
                    // Owner edits in place, then taps 儲存. Typing is
                    // uncontrolled (no setState per keystroke) so input
                    // stays smooth on older iPads.
                    return (
                      <div key={t.key}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                          <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0}}>
                            {t.icon} {t.label}
                            {isCustom && <span style={{fontSize:10,padding:"2px 6px",background:"var(--c-light)",color:"var(--c-primary)",borderRadius:99,marginLeft:6,fontWeight:800}}>{zhEn("已自訂", "Customised")}</span>}
                          </p>
                          {isCustom && (
                            <button onClick={() => update(t.key, "")} style={{background:"none",border:"none",fontSize:11,color:"#8A8A9E",fontWeight:800,cursor:"pointer",textDecoration:"underline"}}>{zhEn("還原預設", "Reset to default")}</button>
                          )}
                        </div>
                        <p style={{fontSize:11,color:"#AFAFC4",margin:"0 0 6px",fontWeight:700}}>{t.desc}</p>
                        <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 4px"}}>{zhEn("📧 Email 主旨", "📧 Email subject")}{isSubjCustom && <span style={{fontSize:9,padding:"1px 5px",background:"var(--c-light)",color:"var(--c-primary)",borderRadius:99,marginLeft:6,fontWeight:800}}>{zhEn("已自訂", "Customised")}</span>}</p>
                        <input
                          key={t.key+":subj:"+(isSubjCustom?"c":"d")+":"+subjValue.length}
                          defaultValue={subjValue || defaultSubj}
                          ref={el => { if (el) { subjRefs.current[t.key] = el; } }}
                          style={{width:"100%",border:"2px solid "+(isSubjCustom?"var(--c-primary)":"var(--c-light)"),borderRadius:12,padding:"8px 12px",fontSize:12,fontWeight:700,fontFamily:"inherit",outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box",marginBottom:8}}
                        />
                        <p style={{fontSize:10,fontWeight:800,color:"#8A8A9E",margin:"0 0 4px"}}>{zhEn("💬 訊息內文", "💬 Message body")}</p>
                        <textarea
                          key={t.key + ":" + (isCustom ? "c" : "d") + ":" + value.length}
                          defaultValue={value || defaultTpl}
                          ref={el => { if (el) { tpltRefs.current[t.key] = el; } }}
                          style={{width:"100%",minHeight:120,border:"2px solid "+(isCustom?"var(--c-primary)":"var(--c-light)"),borderRadius:14,padding:"10px 12px",fontSize:12,fontWeight:600,fontFamily:"inherit",lineHeight:1.6,resize:"vertical",outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box"}}
                        />
                        <div style={{display:"flex",gap:6,marginTop:6,justifyContent:"flex-end"}}>
                          <Btn size="sm" variant="secondary" onClick={() => {
                            const elB = tpltRefs.current[t.key];
                            const elS = subjRefs.current[t.key];
                            if (elB) elB.value = defaultTpl;
                            if (elS) elS.value = defaultSubj;
                          }}>{zhEn("還原預設", "Reset to default")}</Btn>
                          <Btn size="sm" onClick={async () => {
                            const elB = tpltRefs.current[t.key];
                            const elS = subjRefs.current[t.key];
                            const nextBody = (elB?.value || "").trim();
                            const nextSubj = (elS?.value || "").trim();
                            // Saving the platform default verbatim is
                            // equivalent to "no custom" — write empty so
                            // future default-changes still flow through.
                            const normBody = (nextBody === "" || nextBody === defaultTpl.trim()) ? "" : nextBody;
                            const normSubj = (nextSubj === "" || nextSubj === defaultSubj.trim()) ? "" : nextSubj;
                            update(t.key, normBody);
                            updateSubj(t.key, normSubj);
                            // Persist IMMEDIATELY via patchTenant rather
                            // than waiting 600ms for dataSync's debounced
                            // diff. The debounce window meant a tap-save-
                            // then-refresh sequence (which is common when
                            // owners verify "did it stick?") lost the
                            // write entirely — pagehide fired before the
                            // timer, and the persist promise never
                            // completed before unload. Toast said "已儲存"
                            // but the row was never written.
                            try {
                              const nextMc = {
                                ...(tenant?.messagingConfig || {}),
                                templates: { ...((tenant?.messagingConfig?.templates) || {}), [t.key]: normBody },
                                subjects:  { ...((tenant?.messagingConfig?.subjects)  || {}), [t.key]: normSubj },
                              };
                              await patchTenant(user.tenantId, { messagingConfig: nextMc });
                              toast(zhEn("已儲存 ✓", "Saved ✓"));
                            } catch (e) {
                              console.warn("[PettoBook] messaging template save failed:", e);
                              toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message || e), "error");
                            }
                          }}>{zhEn("儲存", "Save")}</Btn>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })()}

          {/* ── 事件管理 ───────────────────────────────────────────── */}
          {settingsSubtab === "events" && (() => {
            const templates = tenant?.eventTemplates || [];
            const COLORS = [["#9CA3AF",zhEn("灰", "Grey")],["#3B82F6",zhEn("藍", "Blue")],["#F59E0B",zhEn("橙", "Orange")],["#10B981",zhEn("綠", "Green")],["#EF4444",zhEn("紅", "Red")],["#8B5CF6",zhEn("紫", "Purple")]];
            const DURATIONS = [15,30,45,60,90,120,180,240];
            const saveTemplates = (next) => {
              setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, eventTemplates: next } : t) }));
            };
            const startNew = () => setEventTplDraft({ id:null, name:"", durationMin:30, staffIds:[], color:"#9CA3AF", enabled:true });
            const startEdit = (tpl) => setEventTplDraft({ ...tpl, staffIds: Array.isArray(tpl.staffIds) ? tpl.staffIds : [] });
            const removeTpl = (id) => {
              if (!window.confirm(i18nT("tx.delEventTplConfirm"))) return;
              saveTemplates(templates.filter(t => t.id !== id));
              toast(i18nT("tx.deleted"));
            };
            return (
              <>
                <Card style={{padding:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:6}}>
                    <h3 style={{fontWeight:900,fontSize:18,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📌 事件範本", "📌 Event templates")}</h3>
                    <Btn size="sm" onClick={startNew} style={{background:"#10B981",color:"#fff"}}><Plus size={14} strokeWidth={3} /> {zhEn("新增", "Add")}</Btn>
                  </div>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>
                    {zhEn("範本可由日歷快速套用，自動為所選員工排上同一個內部事件（例：員工會議、午餐、設備保養）。客人公開預約時，該時段將自動被阻擋。", "Templates can be applied quickly from the calendar to put the same internal event on the selected staff (e.g. staff meeting, lunch, equipment maintenance). That time is blocked automatically from public online booking.")}
                  </p>
                  {templates.length === 0 ? (
                    <div style={{padding:"30px 16px",textAlign:"center",color:"#8A8A9E",fontSize:13,fontWeight:700,background:"var(--c-lighter)",borderRadius:14}}>
                      {zhEn("暫無事件範本。按「新增」建立第一個。", "No event templates yet. Tap \"Add\" to create the first one.")}
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {templates.map(tpl => {
                        const staffNames = (tpl.staffIds || []).map(id => state.users[id]?.name).filter(Boolean);
                        const dur = Number(tpl.durationMin) || 30;
                        return (
                          <div key={tpl.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",border:"1.5px solid var(--c-light)",borderRadius:14,background:"#fff"}}>
                            <span style={{width:10,height:36,borderRadius:4,background:tpl.color||"#9CA3AF",flexShrink:0}} />
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                                <span style={{fontSize:14,fontWeight:900,color:"#1F2937"}}>{tpl.name || zhEn("(無名稱)", "(no name)")}</span>
                                <span style={{background:"#F3F4F6",color:"#6B7280",fontSize:10,fontWeight:800,borderRadius:99,padding:"1px 7px"}}>{dur < 60 ? dur + zhEn(" 分", " pts") : (dur/60) + zhEn(" 小時", " hours") + (dur%60 ? " " + (dur%60) + zhEn(" 分", " pts") : "")}</span>
                                {tpl.enabled === false && <span style={{background:"#FEE2E2",color:"#B91C1C",fontSize:10,fontWeight:800,borderRadius:99,padding:"1px 7px"}}>{zhEn("已停用", "Disabled")}</span>}
                              </div>
                              <p style={{fontSize:11,color:"#6B7280",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {zhEn("適用員工：", "Applies to: ")}{staffNames.length === 0 ? zhEn("(未指定)", "(not set)") : staffNames.join("、")}
                              </p>
                            </div>
                            <button onClick={() => startEdit(tpl)} aria-label={zhEn("編輯", "Edit ")} style={{background:"var(--c-light)",border:"none",borderRadius:99,padding:8,cursor:"pointer",display:"flex"}}>
                              <Edit size={14} color="var(--c-primary)" strokeWidth={2.5} />
                            </button>
                            <button onClick={() => removeTpl(tpl.id)} aria-label={zhEn("刪除", "Delete")} style={{background:"#FEE2E2",border:"none",borderRadius:99,padding:8,cursor:"pointer",display:"flex"}}>
                              <Trash2 size={14} color="#B91C1C" strokeWidth={2.5} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>

                <BottomSheet open={!!eventTplDraft} onClose={() => setEventTplDraft(null)} title={eventTplDraft?.id ? zhEn("編輯事件範本", "Edit event template") : zhEn("新增事件範本", "New event template")}>
                  {eventTplDraft && (() => {
                    const d = eventTplDraft;
                    const setD = (u) => setEventTplDraft(prev => ({ ...prev, ...u }));
                    const toggleStaff = (sid) => {
                      const cur = d.staffIds || [];
                      setD({ staffIds: cur.includes(sid) ? cur.filter(x => x !== sid) : [...cur, sid] });
                    };
                    const onSave = () => {
                      if (!d.name.trim()) { toast(zhEn("請輸入事件名稱", "Please enter an event name"), "warning"); return; }
                      if ((d.staffIds || []).length === 0) { toast(zhEn("請選擇至少一位適用員工", "Select at least one staff member"), "warning"); return; }
                      const out = {
                        id: d.id || uuid(),
                        name: d.name.trim(),
                        durationMin: Number(d.durationMin) || 30,
                        staffIds: d.staffIds || [],
                        color: d.color || "#9CA3AF",
                        leaveType: d.leaveType || "",
                        enabled: d.enabled !== false,
                      };
                      const next = d.id
                        ? templates.map(t => t.id === d.id ? out : t)
                        : [...templates, out];
                      saveTemplates(next);
                      toast(d.id ? zhEn("已更新 📌", "Updated 📌") : zhEn("已新增 📌", "Added 📌"));
                      setEventTplDraft(null);
                    };
                    return (
                      <div style={{padding:"4px 4px 16px",display:"flex",flexDirection:"column",gap:14}}>
                        <div>
                          <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("類型", "Type")}</label>
                          <div style={{display:"flex",gap:6}}>
                            {[["", zhEn("一般事件", "General event")], ["sick", zhEn("🤒 病假", "🤒 Sick leave")], ["personal", zhEn("📌 事假", "📌 Personal leave")]].map(([v, l]) => (
                              <button key={v} onClick={() => setD({ leaveType: v })} style={{flex:1,padding:"8px 10px",borderRadius:12,border:"1.5px solid " + ((d.leaveType || "") === v ? "var(--c-primary)" : "var(--c-light)"),background:(d.leaveType || "") === v ? "var(--c-primary)" : "#fff",color:(d.leaveType || "") === v ? "#fff" : "#374151",fontSize:12,fontWeight:800,cursor:"pointer"}}>{l}</button>
                            ))}
                          </div>
                          {d.leaveType && <p style={{fontSize:10,color:"#92400E",margin:"6px 0 0",fontWeight:700}}>{zhEn("💡 病假 / 事假類型嘅事件會喺薪金紀錄入面分類顯示", "💡 Sick / personal leave events are categorised in payroll records")}</p>}
                        </div>
                        <div>
                          <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("事件名稱 *", "Event name *")}</label>
                          <input value={d.name} onChange={e => setD({ name: e.target.value })} placeholder={zhEn("例：員工會議、午餐時間", "e.g. Staff meeting, lunch break")} style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:14,fontWeight:700,boxSizing:"border-box"}} />
                        </div>
                        <div>
                          <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("時長", "Duration")}</label>
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {DURATIONS.map(x => (
                              <button key={x} onClick={() => setD({ durationMin: x })} style={{padding:"6px 12px",borderRadius:99,border:"1px solid var(--c-light)",background:Number(d.durationMin)===x?"var(--c-primary)":"#fff",color:Number(d.durationMin)===x?"#fff":"#374151",fontSize:12,fontWeight:800,cursor:"pointer"}}>
                                {x < 60 ? x + zhEn(" 分", " pts") : (x/60) + zhEn(" 小時", " hours") + (x%60 ? " " + (x%60) + zhEn(" 分", " pts") : "")}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                            <label style={{fontSize:11,fontWeight:900,color:"#6B7280"}}>{zhEn("適用員工 *（可多選）", "Applies to staff * (multi-select)")}</label>
                            <button onClick={() => setD({ staffIds: (d.staffIds||[]).length === allStaff.length ? [] : allStaff.map(s=>s.id) })} style={{background:"none",border:"none",color:"var(--c-primary)",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                              {(d.staffIds||[]).length === allStaff.length ? zhEn("清空", "Clear") : zhEn("全選", "Select all")}
                            </button>
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {allStaff.length === 0 && <p style={{fontSize:12,color:"#8A8A9E",margin:0}}>{zhEn("(店鋪暫無員工)", "(no staff in this shop yet)")}</p>}
                            {allStaff.map(s => {
                              const on = (d.staffIds||[]).includes(s.id);
                              return (
                                <button key={s.id} onClick={() => toggleStaff(s.id)} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"#374151",fontSize:12,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                                  {on && <Check size={12} strokeWidth={3} />} {s.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("顏色", "Colour")}</label>
                          <div style={{display:"flex",gap:8}}>
                            {COLORS.map(([c,name]) => (
                              <button key={c} onClick={() => setD({ color: c })} title={name} style={{width:32,height:32,borderRadius:99,background:c,border:d.color===c?"3px solid #1F2937":"2px solid #fff",cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}} />
                            ))}
                          </div>
                        </div>
                        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                          <input type="checkbox" checked={d.enabled !== false} onChange={e => setD({ enabled: e.target.checked })} style={{width:18,height:18,cursor:"pointer"}} />
                          <span style={{fontSize:13,fontWeight:700,color:"#374151"}}>{zhEn("啟用（停用後不會出現喺日歷快速套用清單）", "Enabled (when off, it won't appear in the calendar quick-apply list)")}</span>
                        </label>
                        <div style={{display:"flex",gap:8,marginTop:4,justifyContent:"flex-end"}}>
                          <Btn onClick={() => setEventTplDraft(null)} variant="secondary">{zhEn("取消", "Cancel")}</Btn>
                          <Btn onClick={onSave} style={{background:"#10B981",color:"#fff"}}>{d.id ? zhEn("儲存", "Save") : zhEn("新增", "Add")}</Btn>
                        </div>
                      </div>
                    );
                  })()}
                </BottomSheet>
              </>
            );
          })()}

          {/* ── 條款 / 聲明 ────────────────────────────────────────── */}
          {settingsSubtab === "terms" && (() => {
            // Uncontrolled-with-manual-save pattern for all three
            // terms sections. Reasons:
            //   (1) controlled textarea + onChange writing to state
            //       caused per-keystroke setState which fed the
            //       background sync layer and made typing feel laggy on
            //       iPads,
            //   (2) the previous placeholder-only display made clicking
            //       on the default text wipe it, forcing the owner to
            //       retype everything they wanted to tweak.
            // Now the textarea is PRE-FILLED with either the saved
            // custom value or the platform default; the owner edits in
            // place and taps 儲存 when done.
            const termRefs = { current: {} };
            const saveTerm = (field, defaultStr) => {
              const el = termRefs.current[field];
              const next = (el?.value || "").trim();
              const value = next === "" || next === defaultStr.trim() ? "" : next;
              setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, [field]: value } : t) }));
              toast(zhEn("已儲存 ✓", "Saved ✓"));
            };
            const TermBlock = ({ title, hint, field, defaultStr, current, minHeight = 200 }) => {
              const isCustom = !!current && current.trim() !== defaultStr.trim();
              return (
                <Card style={{padding:24}}>
                  <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{title}{isCustom && <span style={{fontSize:10,padding:"2px 8px",background:"var(--c-light)",color:"var(--c-primary)",borderRadius:99,fontWeight:800}}>{zhEn("已自訂", "Customised")}</span>}</h3>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 10px",fontWeight:700}}>{hint}</p>
                  <textarea
                    key={field + ":" + (isCustom ? "c" : "d") + ":" + (current || "").length}
                    defaultValue={current || defaultStr}
                    ref={el => { if (el) termRefs.current[field] = el; }}
                    style={{width:"100%",minHeight,border:"2px solid "+(isCustom?"var(--c-primary)":"var(--c-light)"),borderRadius:16,padding:"12px 14px",fontSize:13,fontWeight:600,outline:"none",color:"#4A4A68",resize:"vertical",fontFamily:"inherit",lineHeight:1.6,boxSizing:"border-box"}}
                  />
                  <div style={{display:"flex",gap:8,marginTop:8,justifyContent:"flex-end"}}>
                    <Btn size="sm" variant="secondary" onClick={() => {
                      const el = termRefs.current[field];
                      if (el) el.value = defaultStr;
                    }}>{zhEn("還原預設", "Reset to default")}</Btn>
                    <Btn size="sm" onClick={() => saveTerm(field, defaultStr)}>{zhEn("儲存", "Save")}</Btn>
                  </div>
                </Card>
              );
            };
            return (
              <>
                {inSec("terms","booking") && TermBlock({
                  title: <><StickyNote size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("預約條款", "Booking terms")} <HelpTip topic="terms" /></>,
                  hint: zhEn("客戶喺預約最後一步必須勾選同意。文字會預填預設模板，可直接編輯。", "Customers must tick to agree on the last booking step. The text is pre-filled with a default template that you can edit."),
                  field: "termsBooking",
                  defaultStr: DEFAULT_TERMS_BOOKING,
                  current: tenant?.termsBooking || "",
                  minHeight: 200,
                })}
                {inSec("terms","senior") && <Card style={{padding:20}}>
                  <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68"}}>{zhEn("🎂 高齡毛孩年齡門檻", "🎂 Senior pet age threshold")}</h3>
                  <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 10px",fontWeight:700}}>{zhEn("當預約毛孩年齡 ≥ 呢個歲數時，會喺預約流程顯示免責聲明並要求家長簽名。", "When a pet's age is ≥ this number, the booking flow shows a disclaimer and asks the owner to sign.")}</p>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      defaultValue={seniorAgeFor(tenant)}
                      onBlur={e => {
                        const raw = Number(e.target.value);
                        const n = Number.isFinite(raw) && raw > 0 ? Math.min(30, Math.max(1, Math.round(raw))) : 10;
                        e.target.value = String(n);
                        if (n === seniorAgeFor(tenant)) return;
                        setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, seniorAgeYears: n } : t) }));
                        toast(zhEn("已儲存 · 高齡門檻改為 ", "Saved · senior age threshold set to ") + n + zhEn(" 歲", " years"));
                      }}
                      style={{width:96,border:"2px solid var(--c-light)",borderRadius:12,padding:"8px 12px",fontSize:15,fontWeight:800,outline:"none",color:"#4A4A68",textAlign:"center"}}
                    />
                    <span style={{fontSize:13,color:"#8A8A9E",fontWeight:700}}>{zhEn("歲以上", "years or older")}</span>
                  </div>
                </Card>}
                {inSec("terms","senior") && TermBlock({
                  title: <>{zhEn("🐾 老犬／患病免責聲明", "🐾 Senior / ill pet disclaimer")}</>,
                  hint: zhEn("當預約毛孩年齡 ≥ ", "When a pet's age is ≥ ") + seniorAgeFor(tenant) + zhEn(" 歲時，會喺預約流程顯示此聲明並要求家長確認。", " years, this notice is shown in the booking flow and the owner must confirm."),
                  field: "disclaimerSenior",
                  defaultStr: DEFAULT_DISCLAIMER_SENIOR,
                  current: tenant?.disclaimerSenior || "",
                  minHeight: 240,
                })}
                {inSec("terms","cat") && TermBlock({
                  title: <>{zhEn("🐱 貓貓美容預約須知", "🐱 Cat grooming booking notice")}</>,
                  hint: zhEn("當預約毛孩種類係貓時，會喺預約流程顯示呢段須知並要求家長確認。", "When the pet is a cat, this notice is shown in the booking flow and the owner must confirm."),
                  field: "termsCat",
                  defaultStr: DEFAULT_TERMS_CAT,
                  current: tenant?.termsCat || "",
                  minHeight: 200,
                })}
              </>
            );
          })()}

          {/* 💬 Support tickets — shop owner contacts PettoBook platform team */}
          {settingsSubtab === "support" && (
            <SupportPanel tenantId={user.tenantId} mode="shop" />
          )}

          {settingsSubtab === "delete-account" && <OwnerDeleteAccountPanel T={T} onLogout={onLogout} />}

          {/* Language picker — owner-side parallel of the one in customer
              profile. Same i18n state; switching here flips everything. Lives
              in its own 語言 subtab (owners couldn't find it under 系統). */}
          {settingsSubtab === "language" && (
            <Card style={{padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>🌐</span>
                <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0}}>{i18nT("settings.language")}</p>
              </div>
              <div style={{display:"flex",gap:6}}>
                {i18nListLocales().map(l => (
                  <button key={l.code} onClick={() => i18nSetLocale(l.code)} style={{padding:"6px 12px",borderRadius:99,border: l.code === _ownerLoc ? "2px solid var(--c-primary)" : "2px solid var(--c-border)",background: l.code === _ownerLoc ? "var(--c-primary)" : "#fff",color: l.code === _ownerLoc ? "#fff" : "#4A4A68",fontWeight:800,fontSize:12,cursor:"pointer"}}>{l.name}</button>
                ))}
              </div>
            </Card>
          )}

          {/* ── 資料備份 / 還原 ───────────────────────────────────────── */}
          {inSec("system","backup") && <Card style={{padding:20}}>
            <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 10px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("💾 資料備份", "💾 Data backup")}</h3>
            {/* #7 Backup info — Supabase auto-backs up daily and PITR is on
                for 7 days. Owners couldn't see any UI indicator that their
                data was safe; this panel makes the guarantee visible. */}
            <div style={{background:"#F0FDF4",borderRadius:14,padding:"12px 14px",border:"2px solid #BBF7D0",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:20,flexShrink:0}}>🛡️</span>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:13,fontWeight:900,color:"#166534",margin:0}}>{zhEn("雲端自動備份已啟用", "Automatic cloud backup is on")}</p>
                <p style={{fontSize:11,fontWeight:700,color:"#166534",margin:"2px 0 0",opacity:0.85}}>{zhEn("Supabase 每日自動備份 · 過去 7 天可即時還原（PITR）", "Supabase backs up daily · restore to any point in the last 7 days (PITR)")}</p>
              </div>
            </div>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{zhEn("匯出整店資料為 JSON，可用於審計或備份。還原會覆寫雲端資料，請小心使用。", "Export all shop data as JSON for auditing or backup. Restoring overwrites cloud data, so use it carefully.")}</p>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="secondary" size="sm" style={{flex:1}} onClick={() => {
                const snapshot = {
                  exportedAt: new Date().toISOString(),
                  tenantId: user.tenantId,
                  tenants: state.tenants.filter(t => t.id === user.tenantId),
                  users: Object.fromEntries(Object.entries(state.users).filter(([,u]) => u.tenantId === user.tenantId || u.id === user.id)),
                  pets: Object.fromEntries(Object.entries(state.pets).filter(([,p]) => p.tenantId === user.tenantId)),
                  appointments: state.appointments.filter(a => a.tenantId === user.tenantId),
                  reviews: state.reviews.filter(r => r.tenantId === user.tenantId),
                  notifications: state.notifications.filter(n => n.tenantId === user.tenantId),
                };
                const json = JSON.stringify(snapshot, null, 2);
                const a = document.createElement("a");
                a.href = "data:application/json;charset=utf-8," + encodeURIComponent(json);
                a.download = "petos_backup_" + dateNow() + ".json";
                a.click();
                toast(zhEn("已匯出 JSON 備份", "JSON backup exported"));
              }}><Package size={14} strokeWidth={2.5} /> {zhEn("匯出 JSON", "Export JSON")}</Btn>
              <Btn variant="secondary" size="sm" style={{flex:1}} onClick={() => {
                const rows = [[zhEn("日期", "Date"),zhEn("時間", "Time"),zhEn("狀態", "Status"),zhEn("服務", "Service"),zhEn("員工", "Staff"),zhEn("客戶", "Customer"),zhEn("毛孩", "Pet"),zhEn("金額", "Amount"),zhEn("訂金", "Deposit"),zhEn("已付", "Paid")]];
                const apts = state.appointments.filter(a => a.tenantId === user.tenantId);
                for (const a of apts) {
                  rows.push([
                    a.date, a.time, SL[a.status]||a.status, a.service,
                    state.users[a.staffId]?.name||"", state.users[a.customerId]?.name||"",
                    state.pets[a.petId]?.name||"",
                    aptRevenue(a),
                    aptDepositReceived(a), a.paid?zhEn("是", "yes"):zhEn("否", "no"),
                  ]);
                }
                const csv = rows.map(r => r.join(",")).join("\n");
                const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8,﻿" + encodeURIComponent(csv); a.download = "petos_appointments_" + dateNow() + ".csv"; a.click();
                toast(zhEn("已匯出預約 CSV", "Bookings CSV exported"));
              }}>{zhEn("📊 匯出 CSV", "📊 Export CSV")}</Btn>
            </div>
          </Card>}

          {/* ── 平台管理切換 (僅雙重身份用戶) ──────────────────────── */}
          {inSec("system","backup") && user.isSuper && <Card style={{padding:20,background:"linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)",border:"2px solid #FBBF24"}}>
            <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 6px",color:"#78350F",display:"flex",alignItems:"center",gap:8}}>{zhEn("👑 平台超級管理員", "👑 Platform super admin")}</h3>
            <p style={{fontSize:12,color:"#92400E",margin:"0 0 12px",fontWeight:700}}>{zhEn("你同時擁有平台管理權限。隨時可切換至平台總控台管理所有商戶。", "You also have platform admin access. Switch to the platform console at any time to manage all shops.")}</p>
            <Btn variant="primary" size="sm" style={{width:"100%"}} onClick={() => {
              try { window.localStorage.setItem("petos:view-as", "super"); } catch { /* ignore */ }
              window.location.reload();
            }}><Crown size={14} strokeWidth={2.5} /> {zhEn("切換到平台管理", "Switch to platform admin")}</Btn>
          </Card>}

          {/* ── 操作紀錄 (Audit log) ──────────────────────────────────── */}
          {/* Subscription card — shows the active plan + buttons that
              kick off Stripe Checkout (upgrade) or Stripe Customer
              Portal (manage payment / cancel). Only renders for owner
              role; staff don't bill. The Checkout success_url returns
              here with ?sub=success which we toast in App.jsx. */}
          {inSec("system","plan") && user.role === "owner" && (() => {
            // During the free Pro trial the shop has paid for nothing, so the
            // buy buttons treat it as Free (Pro is purchasable, not "current").
            const freeTrial = freeTrialOf(tenant);
            const tplan = freeTrial ? "free" : (tenant?.plan || "free");
            const activePlans = getPlans(state.platformConfig);
            const planInfo = activePlans[tplan] || activePlans.free;
            const trialingUntil = tenant?.subscription?.currentPeriodEnd;
            const isTrialing = tenant?.subscription?.status === "trialing";
            const isActive = tenant?.subscription?.status === "active" || isTrialing;
            const hasStripeCustomer = !!tenant?.subscription?.stripeCustomerId && !isNativeApp();
            // Google requires Play Billing for digital subscriptions bought
            // inside the Android app, so Stripe is never offered there.
            const androidApp = window.Capacitor?.getPlatform?.() === "android";
            // Play buttons only once RevenueCat is configured for Android;
            // until then Android owners get the "暫未開放" notice, never Stripe.
            const playBilling = androidApp && !!import.meta.env.VITE_RC_GOOGLE_KEY;
            const billingLive = stripeBillingLive(state.platformConfig);
            const buyPlay = async (plan, period) => {
              if (window.__playBuyBusy) return;
              window.__playBuyBusy = true;
              try {
                const r = await rcPurchaseTier(plan, period);
                toast(r?.changeMode === "DEFERRED"
                  ? zhEn("已安排轉換，新方案會喺現有計費期完結後生效", "Change scheduled — the new plan starts when the current billing period ends")
                  : zhEn("購買成功，方案稍後會自動更新", "Purchase complete — your plan will update shortly"), "success");
              } catch (e) {
                if (!e?.userCancelled) toast(e?.message || zhEn("購買失敗，請稍後再試", "Purchase failed, please try again later"), "error");
              } finally { window.__playBuyBusy = false; }
            };
            const restorePlay = async () => {
              try { await rcRestore(); toast(zhEn("已還原購買，方案稍後會自動更新", "Purchases restored — your plan will update shortly"), "success"); }
              catch (e) { toast(e?.message || zhEn("還原失敗", "Restore failed"), "error"); }
            };
            const askUpgrade = (key, per, go) => (key === tplan ? go() : setUpgradeConfirm({ key, per, go }));
            const startCheckout = async (plan) => {
              try {
                const res = await createSubscriptionCheckout({
                  tenantId: tenant.id,
                  plan,
                  returnUrl: window.location.href,
                  customerEmail: user.email,
                  // What this card is showing — the server refuses to charge
                  // anything else.
                  expectedAmount: Number(activePlans[plan]?.price),
                });
                const url = res?.url;
                if (res?.changed) { setPlanPicker(false); toast(zhEn("已升級 ✓ 差價已即時收取，方案稍後更新", "Upgraded ✓ The difference was charged now; your plan will update shortly"), "success"); return; }
                if (url) { window.location.href = url; return; }
                toast(zhEn("無法建立付款連結", "Couldn't create the payment link"), "error");
              } catch (e) { toast(e?.message || zhEn("升級失敗，請稍後再試", "Upgrade failed, please try again later"), "error"); }
            };
            const openPortal = async () => {
              try {
                const { url } = await createStripePortalSession({ tenantId: tenant.id, returnUrl: window.location.href });
                if (url) window.location.href = url;
              } catch (e) { toast(e?.message || zhEn("開啟管理頁失敗", "Couldn't open the billing page"), "error"); }
            };
            return (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>💎 {zhEn("訂閱方案", "Subscription plan")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>{zhEn("升級解鎖更多員工、自動通知、進階報表等功能。可隨時取消，無綁約。", "Upgrade for more staff, automatic notifications, advanced reports and more. Cancel anytime, no lock-in.")}</p>
                <div style={{background: isActive ? "#F0FDF4" : "#FFFBEB",border:"2px solid " + (isActive ? "#BBF7D0" : "#FDE68A"),borderRadius:14,padding:"12px 14px",marginBottom:12}}>
                  <p style={{fontSize:14,fontWeight:900,color: isActive ? "#166534" : "#92400E",margin:0,display:"flex",alignItems:"center",gap:6}}>
                    {freeTrial ? planName("pro", activePlans) + zhEn("（免費試用）", " (free trial)") : planName(tplan, activePlans)} {isActive && "✓"}
                  </p>
                  {freeTrial && (
                    <p style={{fontSize:11.5,color:"#92400E",margin:"4px 0 0",fontWeight:700,lineHeight:1.5}}>{zhEn("試用至 " + freeTrial.ends + "（仲有 " + freeTrial.daysLeft + " 日）。之後自動轉回免費版，唔會收費；資料全部保留。想繼續用專業版功能就喺下面升級。", "Trial until " + freeTrial.ends + " (" + freeTrial.daysLeft + " days left). Then you move to Free automatically — no charge, all data kept. Upgrade below to keep Pro features.")}</p>
                  )}
                  {isTrialing && trialingUntil && (
                    <p style={{fontSize:11,color:"#166534",margin:"4px 0 0",fontWeight:700,opacity:0.9}}>{zhEn("試用中 · 至 ", "Trial · until ")}{String(trialingUntil).slice(0,10)}</p>
                  )}
                  {!isTrialing && isActive && tenant?.subscription?.cancelAtPeriodEnd && (
                    <p style={{fontSize:11,color:"#92400E",margin:"4px 0 0",fontWeight:700}}>{zhEn("已取消續訂 · ", "Renewal cancelled · ")}{trialingUntil ? zhEn("用到 " + String(trialingUntil).slice(0,10) + "，之後轉回免費版", "active until " + String(trialingUntil).slice(0,10) + ", then back to Free") : zhEn("本期完結後轉回免費版", "back to Free when this period ends")}</p>
                  )}
                </div>
                {/* Every tier ABOVE the current one, derived from the ladder
                    rather than hand-written per plan. The hand-written version
                    covered free→basic/pro and basic→pro and simply had no
                    pro→旗艦版 branch, so a 專業版 shop could not buy the most
                    expensive plan on the platform — and a paid shop with no
                    Stripe customer (plan set by hand, as every current shop
                    was) got a card with nothing in it at all. */}
                {billingLive && (() => {
                  // Bought in the Android / iOS app: changing it on the web
                  // would start a second (Stripe) subscription on top.
                  const appSub = tenant?.subscription?.source === "revenuecat" && tplan !== "free";
                  if (appSub) {
                    return <p style={{fontSize:13,fontWeight:700,color:"#6A6A85",lineHeight:1.6,margin:0}}>
                      {tenant.subscription.store === "APP_STORE"
                        ? zhEn("你嘅訂閱係經 Apple App Store 購買，請喺 iPhone 設定 → 訂閱 更改或取消。", "You subscribed through the Apple App Store. Change or cancel it in iPhone Settings → Subscriptions.")
                        : zhEn("你嘅訂閱係經 Google Play 購買，請喺 PettoBook Android App 或 Google Play → 訂閱 更改或取消。", "You subscribed through Google Play. Change or cancel it in the PettoBook Android app or Google Play → Subscriptions.")}
                    </p>;
                  }
                  const LADDER = ["free", "basic", "pro", "enterprise"];
                  const here = LADDER.indexOf(tplan);
                  const upgrades = LADDER.slice(here + 1);
                  if (!upgrades.length) {
                    return hasStripeCustomer
                      ? <Btn variant="secondary" onClick={openPortal} style={{width:"100%"}}>{zhEn("管理訂閱 / 取消 →", "Manage / cancel subscription →")}</Btn>
                      : null;
                  }
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {tplan === "free" && ownQuota && !ownQuota.unlimited && (
                        <div style={{background: ownQuota.open ? "var(--c-lighter)" : "#FEF2F2", border:"1.5px solid " + (ownQuota.open ? "var(--c-border)" : "#FECACA"), borderRadius:14, padding:"10px 12px"}}>
                          <p style={{margin:0,fontSize:13,fontWeight:900,color: ownQuota.open ? "#4A4A68" : "#B91C1C"}}>{zhEn("本月網上預約 ", "Online bookings this month ")}{ownQuota.used} / {ownQuota.limit}</p>
                          <p style={{margin:"2px 0 0",fontSize:11.5,fontWeight:700,color:"#8A8A9E",lineHeight:1.5}}>{ownQuota.open ? zhEn("免費版每月可接 " + ownQuota.limit + " 個客人網上預約，試用完整功能。你自己入嘅預約唔計。", "Free includes " + ownQuota.limit + " customer online bookings a month so you can try it. Bookings you enter yourself don't count.") : zhEn("今個月名額已用完，客人暫時只能聯絡你。升級基本版即可不限次數。", "This month's places are used up — customers can only contact you. Upgrade to Basic for unlimited bookings.")}</p>
                        </div>
                      )}
                      <Btn onClick={() => setPlanPicker(true)} style={{width:"100%"}}><Crown size={15} strokeWidth={2.5} /> {zhEn("升級方案", "Upgrade plan")}</Btn>
                      {hasStripeCustomer && <Btn variant="ghost" size="sm" onClick={openPortal}>{zhEn("管理訂閱 / 取消 →", "Manage / cancel subscription →")}</Btn>}
                    </div>
                  );
                })()}
                {playBilling && (() => {
                  // Every plan/period except the one already owned. A shop
                  // with a Play subscription CHANGES it (Play replaces the old
                  // one — see purchaseTier); it never buys a second one.
                  const LADDER = ["free", "basic", "pro", "enterprise"];
                  const sub = tenant?.subscription || {};
                  const onPlay = sub.source === "revenuecat" && sub.store === "PLAY_STORE" && tplan !== "free";
                  const onOtherStore = sub.source === "revenuecat" && sub.store && sub.store !== "PLAY_STORE" && tplan !== "free";
                  const curPeriod = onPlay ? (sub.period || "monthly") : null;
                  const rank = k => LADDER.indexOf(k);
                  const options = ["basic", "pro", "enterprise"].flatMap(key => ["monthly", "yearly"].map(per => [key, per]))
                    // Upgrades only: a higher tier, or the same tier monthly → yearly.
                    // Lower plans are reached by cancelling in Google Play.
                    .filter(([key, per]) => onPlay
                      ? (rank(key) > rank(tplan) || (key === tplan && curPeriod === "monthly" && per === "yearly"))
                      : rank(key) > rank(tplan));
                  if (onOtherStore) {
                    return <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6}}>
                      {zhEn("你嘅訂閱係經 " + (sub.store === "APP_STORE" ? "Apple App Store" : sub.store) + " 購買，請喺原本嘅裝置更改或取消方案。", "You subscribed through " + (sub.store === "APP_STORE" ? "the Apple App Store" : sub.store) + ". Change or cancel it on that device.")}
                    </p>;
                  }
                  // A shop already paying through Stripe (web) must not buy again
                  // on Play: revenuecat-webhook deliberately ignores Play purchases
                  // while a Stripe subscription is active, so the shop would be
                  // charged twice and its plan would not change.
                  const onStripe = sub.source !== "revenuecat" && !!sub.stripeCustomerId
                    && (sub.status === "active" || sub.status === "trialing");
                  if (onStripe) {
                    return <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6}}>
                      {zhEn("你嘅訂閱係經網頁版（信用卡）購買，請用電腦或手機瀏覽器登入 pettobook.com 更改或取消方案。", "You subscribed on the website (card). Sign in at pettobook.com in a browser to change or cancel it.")}
                    </p>;
                  }
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {onPlay && sub.currentPeriodEnd && (
                        <p style={{fontSize:12,color:"#4A4A68",margin:"0 0 2px",fontWeight:700}}>
                          {curPeriod === "yearly" ? zhEn("年費", "Yearly") : zhEn("月費", "Monthly")} · {sub.willRenew === false ? zhEn("到期日", "Ends") : zhEn("下次續期", "Renews")} {String(sub.currentPeriodEnd).slice(0,10)}
                        </p>
                      )}
                      {options.length > 0 && <Btn onClick={() => { setPickPer(curPeriod === "monthly" && rank(tplan) >= 3 ? "yearly" : "monthly"); setPlanPicker(true); }} style={{width:"100%"}}><Crown size={15} strokeWidth={2.5} /> {onPlay ? zhEn("升級 / 轉方案", "Upgrade / change plan") : zhEn("升級方案", "Upgrade plan")}</Btn>}
                      {onPlay && <Btn variant="ghost" size="sm" onClick={() => window.open(rcPlayManageUrl(`pettobook_${tplan}_${curPeriod}`), "_blank")}>{zhEn("喺 Google Play 管理 / 取消訂閱 →", "Manage / cancel in Google Play →")}</Btn>}
                      <Btn variant="ghost" size="sm" onClick={restorePlay}>{zhEn("還原購買", "Restore purchases")}</Btn>
                      <p style={{fontSize:11,color:"#AFAFC4",margin:"4px 0 0",fontWeight:700,lineHeight:1.5}}>
                        {zhEn("由 Google Play 收費，可隨時喺 Google Play「付款與訂閱」取消。升級即時生效，只收差價。如想轉低方案，請喺 Google Play 取消，計費期完結後會轉返免費版。實際價錢以 Google Play 顯示為準。", "Billed by Google Play; cancel anytime in Google Play → Payments & subscriptions. Upgrades apply now and only charge the difference. To move to a lower plan, cancel in Google Play and you return to Free when the period ends. Google Play shows the final price.")}
                      </p>
                    </div>
                  );
                })()}
                {/* Online billing isn't switched on yet. An existing paying
                    shop still gets its portal link — cancelling must never
                    depend on a platform toggle — but nobody is offered a
                    checkout that would only fail. */}
                {!billingLive && !playBilling && (
                  <div style={{background:"#F9FAFB",border:"1.5px dashed #E5E7EB",borderRadius:14,padding:"14px 16px"}}>
                    <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 4px"}}>{zhEn("線上升級暫未開放", "Online upgrades aren't available yet")}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6}}>
                      {freeTrial ? zhEn("試用期間所有專業版功能照用。", "Every Pro feature works during the trial.") : zhEn("你而家用緊" + planName(tplan, activePlans) + "，所有已開通嘅功能照用。", "You're on " + planName(tplan, activePlans) + "; everything already enabled keeps working.")}{!isNativeApp() && zhEn("想轉方案請直接聯絡我哋。", " Contact us to change plan.")}
                    </p>
                    {hasStripeCustomer && (
                      <div style={{marginTop:10}}>
                        <Btn variant="ghost" size="sm" onClick={openPortal}>{zhEn("管理現有訂閱 →", "Manage subscription →")}</Btn>
                      </div>
                    )}
                  </div>
                )}
                {billingLive && <p style={{fontSize:11,color:"#AFAFC4",margin:"10px 0 0",fontWeight:700,lineHeight:1.5}}>{zhEn("付款由 Stripe 處理。我哋唔會儲存你嘅信用卡資料。可隨時取消，用到該期完結為止。", "Payments are handled by Stripe; we never store your card. Cancel anytime and keep your plan until the period ends.")}</p>}
                <Btn variant="ghost" size="sm" onClick={() => setPlanPicker(true)} style={{width:"100%",marginTop:10}}>{zhEn("比較所有方案 →", "Compare all plans →")}</Btn>
                {planPicker && (() => {
                  // 方案 popup: the comparison table with the upgrade button
                  // UNDER each plan. Same rules as before — web = Stripe
                  // (higher tiers only), Android = Google Play (higher tiers,
                  // or monthly → yearly on the same tier); a shop paying
                  // through the other channel gets no buy buttons here.
                  const LADDER = ["free", "basic", "pro", "enterprise"];
                  const rank = k => LADDER.indexOf(k);
                  const cols = LADDER.filter(k => activePlans[k]);
                  const sub = tenant?.subscription || {};
                  const appSub = sub.source === "revenuecat" && tplan !== "free";
                  const onPlay = appSub && sub.store === "PLAY_STORE";
                  const curPeriod = onPlay ? (sub.period || "monthly") : null;
                  const stripeActive = sub.source !== "revenuecat" && !!sub.stripeCustomerId && (sub.status === "active" || sub.status === "trialing");
                  const webBuy = billingLive && !appSub;
                  const playBuy = playBilling && !stripeActive && (!appSub || onPlay);
                  const per = playBuy ? pickPer : "monthly";
                  const mult = per === "yearly" ? 10 : 1;
                  const cur = state.platformConfig?.basePlanCurrency || "HKD";
                  const money = (n) => (cur === "HKD" ? "$" : cur + " ") + Number(n).toLocaleString();
                  const priceOf = (c) => webBuy
                    ? (() => { const pp = getPlanPrice(c, tenant?.currency || "HKD", state.platformConfig); return pp.symbol + pp.amount + perMonth(); })()
                    : money(Number(activePlans[c].price) * mult) + (per === "yearly" ? perYear() : perMonth());
                  const rows = PLAN_COMPARE_ROWS.filter(r => cols.some(c => planRowOn(r, activePlans[c])));
                  const cell = (c) => ({ padding:"8px 2px", textAlign:"center", verticalAlign:"top", background: c === tplan ? "var(--c-primary-20)" : "transparent", ...(c === tplan ? { borderLeft:"2px solid var(--c-primary)", borderRight:"2px solid var(--c-primary)" } : null) });
                  const tick = (on) => <span style={{color: on ? "#10B981" : "#D4D4DE", fontWeight:900}}>{on ? "✓" : "—"}</span>;
                  const buyFor = (c) => {
                    if (webBuy && rank(c) > rank(tplan)) return { label: zhEn("升級", "Upgrade"), go: () => askUpgrade(c, null, () => startCheckout(c)) };
                    if (playBuy && (rank(c) > rank(tplan) || (onPlay && c === tplan && curPeriod === "monthly" && per === "yearly"))) {
                      return { label: rank(c) > rank(tplan) ? zhEn("升級", "Upgrade") : zhEn("轉年費", "Go yearly"), go: () => askUpgrade(c, per, () => { setPlanPicker(false); buyPlay(c, per); }) };
                    }
                    return null;
                  };
                  const btnCell = (c) => {
                    if (c === tplan && !buyFor(c)) return <span style={{fontSize:11,fontWeight:900,color:"var(--c-primary)"}}>{zhEn("目前方案", "Current")}</span>;
                    const b = buyFor(c);
                    if (!b) return null;
                    return <button onClick={b.go} style={{width:"100%",border:"none",borderRadius:10,padding:"9px 2px",background:"var(--c-primary)",color:"#fff",fontSize:12.5,fontWeight:900,cursor:"pointer",boxShadow:"0 4px 10px var(--c-primary-40)"}}>{b.label}</button>;
                  };
                  const btnRow = (
                    <tr>
                      <td style={{padding:"10px 8px"}}></td>
                      {cols.map(c => <td key={c} style={{...cell(c),padding:"10px 3px"}}>{btnCell(c)}</td>)}
                    </tr>
                  );
                  return createPortal(<div style={THEMES[tenant?.theme] || THEMES.peach}><BottomSheet open onClose={() => setPlanPicker(false)} title={zhEn("選擇方案", "Choose a plan")} z={250}>
                    {playBuy && (
                      <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
                        <div style={{display:"inline-flex",background:"var(--c-lighter)",borderRadius:99,padding:4,gap:4}}>
                          {[["monthly",zhEn("月繳","Monthly")],["yearly",zhEn("年繳 · 慳 2 個月","Yearly · 2 months free")]].map(([k, l]) => (
                            <button key={k} onClick={() => setPickPer(k)} style={{background: pickPer === k ? "#fff" : "transparent", color: pickPer === k ? "var(--c-primary)" : "#8A8A9E", border:"none", borderRadius:99, padding:"7px 16px", fontSize:13, fontWeight:900, cursor:"pointer", boxShadow: pickPer === k ? "0 2px 8px rgba(0,0,0,0.1)" : "none"}}>{l}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{overflowX:"auto",border:"1.5px solid var(--c-light)",borderRadius:14}}>
                      <table style={{width:"100%",minWidth:330,tableLayout:"fixed",borderCollapse:"collapse",fontSize:11.5,fontWeight:700,color:"#4A4A68"}}>
                        <colgroup><col style={{width:"25%"}} />{cols.map(c => <col key={c} />)}</colgroup>
                        <thead>
                          <tr style={{borderBottom:"1.5px solid var(--c-light)"}}>
                            <th style={{padding:"10px 8px"}}></th>
                            {cols.map(c => (
                              <th key={c} style={{...cell(c),padding:"10px 2px",fontWeight:900,color: c === tplan ? "var(--c-primary)" : "#4A4A68"}}>
                                {planName(c, activePlans)}
                                <span style={{display:"block",fontSize:10,fontWeight:900,color: c === tplan ? "var(--c-primary)" : "#8A8A9E",marginTop:2,whiteSpace:"nowrap"}}>{Number(activePlans[c].price) > 0 ? priceOf(c) : zhEn("免費", "Free")}</span>
                                {c === tplan
                                  ? <span style={{display:"inline-block",marginTop:4,fontSize:9.5,fontWeight:900,color:"#fff",background:"var(--c-primary)",borderRadius:99,padding:"1px 7px",whiteSpace:"nowrap"}}>{zhEn("目前方案", "Your plan")}</span>
                                  : c === "pro" ? <span style={{display:"inline-block",marginTop:4,fontSize:9.5,fontWeight:900,color:"var(--c-primary)",background:"var(--c-lighter)",borderRadius:99,padding:"1px 7px",whiteSpace:"nowrap"}}>{zhEn("推薦", "Popular")}</span> : null}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {btnRow}
                          <tr style={{borderTop:"1.5px solid var(--c-light)",borderBottom:"1px solid var(--c-light)"}}>
                            <td style={{padding:"8px",color:"#8A8A9E"}}>{zhEn("員工人數", "Staff")}</td>
                            {cols.map(c => <td key={c} style={cell(c)}>{activePlans[c].maxStaff}{zhEn(" 位", "")}</td>)}
                          </tr>
                          {rows.map(r => (
                            <tr key={r.k} style={{borderBottom:"1px solid var(--c-light)"}}>
                              <td style={{padding:"8px",color:"#8A8A9E",lineHeight:1.35}}>{planRowLabel(r)}</td>
                              {cols.map(c => <td key={c} style={cell(c)}>{r.val && r.val(activePlans[c], !zhEn(1, 0)) ? <span style={{fontSize:10.5,fontWeight:900,color:"#10B981"}}>{r.val(activePlans[c], !zhEn(1, 0))}</span> : tick(planRowOn(r, activePlans[c]))}</td>)}
                            </tr>
                          ))}
                          {btnRow}
                        </tbody>
                      </table>
                    </div>
                    <p style={{fontSize:11,color:"#AFAFC4",margin:"12px 2px 0",fontWeight:700,lineHeight:1.6}}>
                      {webBuy ? (stripeActive ? zhEn("升級會即時生效，只收今期差價（用你已儲存嘅信用卡）。轉低方案請喺「管理訂閱」取消，計費期完結後轉返免費版。", "Upgrades apply now and only charge the difference for this period (on your saved card). To move down, cancel in Manage subscription and you return to Free when the period ends.") : zhEn("付款由 Stripe 處理，可隨時取消，用到該期完結為止。", "Payments by Stripe. Cancel anytime and keep your plan until the period ends."))
                        : playBuy ? zhEn("由 Google Play 收費。升級即時生效，只收差價。轉低方案請喺 Google Play 取消。實際價錢以 Google Play 顯示為準。", "Billed by Google Play. Upgrades apply now and only charge the difference. To move down, cancel in Google Play. Google Play shows the final price.")
                        : appSub ? zhEn("你嘅訂閱係經 App Store / Google Play 購買，請喺原本嘅裝置更改方案。", "You subscribed through the App Store / Google Play. Change your plan on that device.")
                        : stripeActive ? zhEn("你嘅訂閱係經網頁版購買，請用瀏覽器登入 pettobook.com 更改方案。", "You subscribed on the website. Sign in at pettobook.com in a browser to change plan.")
                        : zhEn("線上升級暫未開放，想轉方案請聯絡我哋。", "Online upgrades aren't available yet. Contact us to change plan.")}
                    </p>
                  </BottomSheet></div>, document.body);
                })()}
                {(() => {
                  const uc = upgradeConfirm;
                  const to = uc && activePlans[uc.key];
                  if (!to) return null;
                  const gained = PLAN_COMPARE_ROWS.filter(r => planRowOn(r, to) && !planRowOn(r, planInfo));
                  const staffUp = Number(to.maxStaff) > Number(planInfo?.maxStaff);
                  const showPrice = !isNativeApp();
                  const fromP = getPlanPrice(tplan, tenant?.currency || "HKD", state.platformConfig);
                  const toP = getPlanPrice(uc.key, tenant?.currency || "HKD", state.platformConfig);
                  const yearly = uc.per === "yearly";
                  const mult = yearly ? 10 : 1;
                  const planBox = (name, price, unit, accent) => (
                    <div style={{flex:1,minWidth:0,textAlign:"center",padding:"12px 8px",borderRadius:14,
                      border: accent ? "2px solid var(--c-primary)" : "1.5px solid var(--c-light)",
                      background: accent ? "var(--c-primary-20)" : "#FAFAFC"}}>
                      <p style={{fontSize:11,fontWeight:800,color: accent ? "var(--c-primary)" : "#8A8A9E",margin:"0 0 2px"}}>{accent ? zhEn("升級至", "Upgrade to") : zhEn("目前方案", "Current plan")}</p>
                      <p style={{fontSize:15,fontWeight:900,color:"#4A4A68",margin:0}}>{name}</p>
                      {showPrice && <p style={{fontSize:12,fontWeight:800,color: accent ? "var(--c-primary)" : "#8A8A9E",margin:"2px 0 0"}}>{price}</p>}
                    </div>
                  );
                  const fromPrice = Number(fromP.amount) > 0 ? fromP.symbol + fromP.amount + perMonth() : zhEn("免費", "Free");
                  const toPrice = toP.symbol + (Number(toP.amount) * mult).toLocaleString() + (yearly ? perYear() : perMonth());
                  return (
                    createPortal(<div style={THEMES[tenant?.theme] || THEMES.peach}><BottomSheet open onClose={() => setUpgradeConfirm(null)} title={zhEn("確認升級方案", "Confirm upgrade")} z={300}>
                      <div style={{display:"flex",flexDirection:"column",gap:16}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {planBox(planName(tplan, activePlans), fromPrice, "", false)}
                          <ChevronRight size={20} color="var(--c-primary)" strokeWidth={3} style={{flexShrink:0}} />
                          {planBox(planName(uc.key, activePlans), toPrice, "", true)}
                        </div>
                        {(gained.length > 0 || staffUp) && (
                          <div>
                            <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("升級後你會獲得", "You'll get")}</p>
                            <div style={{display:"flex",flexDirection:"column",gap:2,border:"1.5px solid var(--c-light)",borderRadius:14,padding:"4px 12px"}}>
                              {staffUp && (
                                <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom: gained.length ? "1px solid var(--c-light)" : "none"}}>
                                  <span style={{width:20,height:20,borderRadius:99,background:"#10B981",color:"#fff",fontSize:12,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✓</span>
                                  <span style={{flex:1,fontSize:13,fontWeight:700,color:"#4A4A68"}}>{zhEn("員工人數", "Staff")}</span>
                                  <span style={{fontSize:12.5,fontWeight:800,color:"#8A8A9E"}}>{planInfo?.maxStaff}{zhEn(" 位", "")} <span style={{color:"var(--c-primary)"}}>→ {to.maxStaff}{zhEn(" 位", "")}</span></span>
                                </div>
                              )}
                              {gained.map((r, idx) => (
                                <div key={r.k} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom: idx < gained.length - 1 ? "1px solid var(--c-light)" : "none"}}>
                                  <span style={{width:20,height:20,borderRadius:99,background:"#10B981",color:"#fff",fontSize:12,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✓</span>
                                  <span style={{flex:1,fontSize:13,fontWeight:700,color:"#4A4A68",lineHeight:1.4}}>{planRowLabel(r)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.6,textAlign:"center"}}>
                          {isNativeApp()
                            ? zhEn("由 Google Play 收費，升級即時生效，只收差價。可隨時喺 Google Play「付款與訂閱」取消。", "Billed by Google Play. Upgrades apply now and only charge the difference. Cancel anytime in Google Play → Payments & subscriptions.")
                            : zhEn("付款由 Stripe 處理，升級後立即生效。可隨時取消。", "Payments by Stripe. Your upgrade applies right away. Cancel anytime.")}
                        </p>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          <Btn onClick={() => { const go = uc.go; setUpgradeConfirm(null); go(); }} style={{width:"100%"}}>
                            {zhEn("確認升級至", "Upgrade to ")}{planName(uc.key, activePlans)}{yearly ? zhEn("（年費）", " (yearly)") : uc.per === "monthly" ? zhEn("（月費）", " (monthly)") : ""}
                          </Btn>
                          <Btn variant="ghost" size="sm" onClick={() => setUpgradeConfirm(null)}>{zhEn("稍後再說", "Not now")}</Btn>
                        </div>
                      </div>
                    </BottomSheet></div>, document.body)
                  );
                })()}
              </Card>
            );
          })()}

          {/* Google Calendar real-time sync — per-staff OAuth. Pushes
              every booking change to Google within ~15 seconds (vs. the
              4-24h Google takes to re-poll the iCal feed). */}
          {inSec("system","calendar") && (() => {
            const me = state.users[user.id] || user;
            const connected = !!me.googleSyncEnabled;
            const connect = async () => {
              try {
                const url = await getGoogleAuthorizeUrl(window.location.href);
                if (!url) { toast(zhEn("無法取得授權連結", "Couldn't get the authorisation link"), "error"); return; }
                window.location.href = url;
              } catch (e) {
                toast(i18nT("tx.googleAuthFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              }
            };
            const disconnect = async () => {
              if (!window.confirm(i18nT("tx.gcalDisconnectConfirm"))) return;
              try {
                await disconnectGoogleCalendar(user.id);
                setState(s => ({ ...s, users: { ...s.users, [user.id]: { ...(s.users[user.id] || {}), googleSyncEnabled: false, googleEmail: null } } }));
                toast(i18nT("tx.gcalDisconnectedToast"));
              } catch (e) {
                toast(i18nT("tx.disconnectFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              }
            };
            return (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("tx.gcalTitle")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700,lineHeight:1.5}}>{i18nT("tx.gcalSub")}</p>
                {connected ? (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    <div style={{background:"#F0FDF4",borderRadius:14,padding:"10px 12px",border:"2px solid #BBF7D0",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:14,fontWeight:900,color:"#166534"}}>{i18nT("tx.gcalConnected")}</span>
                      {me.googleEmail && <span style={{fontSize:12,fontWeight:700,color:"#166534",opacity:0.8}}>{me.googleEmail}</span>}
                    </div>
                    <Btn variant="ghost" size="sm" style={{color:"#DC2626"}} onClick={disconnect}>{i18nT("tx.gcalDisconnect")}</Btn>
                  </div>
                ) : (
                  <Btn onClick={connect} style={{width:"100%",background:"#4285F4",color:"#fff"}}>{i18nT("tx.gcalConnect")}</Btn>
                )}
              </Card>
            );
          })()}

          {/* #10 Staff iCal subscription — each owner/admin/staff can copy
              a unique calendar URL into Apple Calendar / Google Calendar
              and have their PettoBook bookings appear on their phone. Token
              lives in profiles.ical_token; "regenerate" rotates it so
              the previous URL stops working. */}
          {inSec("system","calendar") && (() => {
            const me = state.users[user.id] || user;
            const baseUrl = "https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/staff-ical-feed";
            const fullUrl = me.icalToken ? (baseUrl + "?token=" + me.icalToken) : "";
            const generate = async () => {
              const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "").slice(0, 16);
              try {
                await patchProfile(user.id, { icalToken: token });
                setState(s => ({ ...s, users: { ...s.users, [user.id]: { ...(s.users[user.id] || {}), icalToken: token } } }));
                toast(i18nT("tx.icalGenToast"));
              } catch (e) {
                toast(i18nT("tx.icalGenFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              }
            };
            const revoke = async () => {
              if (!window.confirm(i18nT("tx.icalRevokeConfirm"))) return;
              try {
                await patchProfile(user.id, { icalToken: null });
                setState(s => ({ ...s, users: { ...s.users, [user.id]: { ...(s.users[user.id] || {}), icalToken: "" } } }));
                toast(i18nT("tx.icalRevokedToast"));
              } catch (e) {
                toast(i18nT("tx.icalRevokeFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              }
            };
            return (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📅 月曆訂閱（iCal）", "📅 Calendar subscription (iCal)")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700,lineHeight:1.5}}>{zhEn("將 PettoBook 排程同步到手機月曆 App（Apple Calendar / Google Calendar）。訂閱後預約會自動更新，無需手動匯入。", "Sync your PettoBook schedule to your phone's calendar app (Apple Calendar / Google Calendar). Once subscribed, bookings update automatically — no manual import needed.")}</p>
                {fullUrl ? (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    <div style={{background:"#F0FDF4",borderRadius:14,padding:"10px 12px",border:"2px solid #BBF7D0"}}>
                      <p style={{fontSize:11,fontWeight:800,color:"#166534",margin:"0 0 4px",opacity:0.7}}>{zhEn("您嘅訂閱連結（請保密，相當於密碼）", "Your subscription link (keep it private — it works like a password)")}</p>
                      <p style={{fontSize:11,fontWeight:700,color:"#166534",margin:0,wordBreak:"break-all",fontFamily:"ui-monospace,SF Mono,monospace"}}>{fullUrl}</p>
                    </div>
                    <details style={{background:"var(--c-lighter)",borderRadius:12,padding:"10px 12px",border:"1px solid var(--c-border)"}}>
                      <summary style={{fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor:"pointer"}}>{zhEn("📱 訂閱步驟", "📱 How to subscribe")}</summary>
                      <div style={{marginTop:8,fontSize:12,color:"#4A4A68",lineHeight:1.7,fontWeight:600}}>
                        <p style={{margin:"0 0 6px",fontWeight:900}}>{zhEn("iPhone：", "iPhone: ")}</p>
                        <ol style={{margin:"0 0 8px",paddingLeft:20}}>
                          <li>{zhEn("「設定」→「行事曆」→「帳號」", "Settings → Calendar → Accounts")}</li>
                          <li>{zhEn("「加入帳號」→「其他」→「加入已訂閱的行事曆」", "Add Account → Other → Add Subscribed Calendar")}</li>
                          <li>{zhEn("貼上以上連結 → 完成", "Paste the link above → done")}</li>
                        </ol>
                        <p style={{margin:"0 0 6px",fontWeight:900}}>{zhEn("Google Calendar：", "Google Calendar: ")}</p>
                        <ol style={{margin:0,paddingLeft:20}}>
                          <li>{zhEn("左側「其他日曆」旁邊 +", "Click + next to \"Other calendars\" on the left")}</li>
                          <li>{zhEn("「透過網址加入」→ 貼上連結", "\"From URL\" → paste the link")}</li>
                        </ol>
                      </div>
                    </details>
                    <div style={{display:"flex",gap:8}}>
                      <Btn variant="secondary" size="sm" style={{flex:1}} onClick={() => { try { navigator.clipboard.writeText(fullUrl); toast(i18nT("toast.copyOk")); } catch { toast(i18nT("toast.copyFail"), "error"); } }}>
                        <Copy size={14} strokeWidth={2.5} /> {zhEn("複製連結", "Copy link")}
                      </Btn>
                      <Btn variant="ghost" size="sm" style={{flex:1,color:"#DC2626"}} onClick={revoke}>{zhEn("撤銷重設", "Revoke & reset")}</Btn>
                    </div>
                  </div>
                ) : (
                  <Btn onClick={generate} style={{width:"100%"}}>
                    <Plus size={14} strokeWidth={3} /> {zhEn("產生訂閱連結", "Generate subscription link")}
                  </Btn>
                )}
              </Card>
            );
          })()}

          {inSec("system","diag") && <SyncLogCard />}
          {inSec("system","diag") && <AuthHealthCard />}

          {inSec("system","diag") && <Card style={{padding:20}}>
            <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("📋 操作紀錄", "📋 Activity log")}</h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{zhEn("最近的預約調價／折扣／狀態變更紀錄（取自所有預約的 priceAdjustLog）", "Recent booking price / discount / status changes (from every booking's priceAdjustLog)")}</p>
            {(() => {
              const logs = (state.appointments || [])
                .flatMap(a => (a.priceAdjustLog||[]).map(l => ({...l, aptId:a.id, petName:state.pets[a.petId]?.name||"(unknown)", custName:state.users[a.customerId]?.name||"(unknown)"})))
                .filter(l => l.at)
                .sort((a,b) => (b.at||"").localeCompare(a.at||""))
                .slice(0, 30);
              if (logs.length === 0) return <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",margin:0}}>{zhEn("暫無紀錄", "No records yet")}</p>;
              return logs.map((l, i) => (
                <div key={i} style={{padding:"8px 0",borderBottom:i===logs.length-1?"none":"1px dashed var(--c-light)",fontSize:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontWeight:800,color:"#4A4A68"}}>{l.petName} · {l.field}</span>
                    <span style={{color:"#AFAFC4",fontWeight:700}}>{l.at}</span>
                  </div>
                  <div style={{color:"#6A6A85",fontWeight:600}}>
                    {state.users[l.by]?.name || l.by || zhEn("未知", "Unknown")}
                    {l.field === "discount" ? zhEn(" 已更新折扣", " updated the discount") : (" " + l.from + " → " + l.to)}
                    {l.reason ? " · " + l.reason : ""}
                  </div>
                </div>
              ));
            })()}
          </Card>}

          {/* Branding Card */}
          {inSec("shop-brand","page") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Image size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("品牌頁面", "Brand page")}</h3>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Logo Upload */}
              <div>
                <p style={{fontSize:13,fontWeight:700,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("📸 店鋪標誌", "📸 Shop logo")}</p>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  {tenant?.logoUrl && <img src={tenant.logoUrl} alt="logo" style={{width:80,height:80,borderRadius:12,objectFit:"cover"}} />}
                  <button onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const url = await uploadTenantMedia(user.tenantId, file, "logo");
                          setState(s2 => ({...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? {...t, logoUrl: url} : t)}));
                          toast(zhEn("標誌已上傳", "Logo uploaded"));
                        } catch (err) {
                          console.error("Logo upload failed:", err);
                          toast(i18nT("tx.uploadFailedToast"), "error");
                        }
                      }
                    };
                    input.click();
                  }} style={{flex:1,padding:"10px 16px",borderRadius:12,border:"2px dashed var(--c-border)",background:"#FFFDFD",cursor:"pointer",fontWeight:800,fontSize:13,color:"var(--c-primary)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    <Upload size={16} /> {tenant?.logoUrl ? zhEn("更新標誌", "Update logo") : zhEn("上傳標誌", "Upload logo")}
                  </button>
                </div>
                {shopHasBrandedApp(tenant) && <p style={{fontSize:11,color:"#7C3AED",margin:"6px 0 0",fontWeight:700,lineHeight:1.5}}>{zhEn("📱 （旗艦版）此標誌亦會用作你嘅", "📱 (Enterprise) This logo is also used as your ")}<strong>{zhEn("專屬 App 圖示", "custom app icon")}</strong>{zhEn("。想用唔同圖案，可喺下方上傳專屬 App 圖示。", ". To use a different design, upload a custom app icon below.")}</p>}
              </div>

              {/* App Icon Upload — 旗艦版 only. Used as the home-screen icon when
                  a customer installs the shop's branded PWA. */}
              {shopHasBrandedApp(tenant) && (
              <div>
                <p style={{fontSize:13,fontWeight:700,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("📱 專屬 App 圖示", "📱 Custom app icon")} <span style={{fontSize:11,fontWeight:700,color:"#7C3AED",background:"#F3EEFF",borderRadius:99,padding:"1px 8px",marginLeft:4}}>{zhEn("旗艦版", "Enterprise")}</span></p>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  {(tenant?.appIcon || tenant?.logoUrl) && <img src={tenant.appIcon || tenant.logoUrl} alt="app icon" style={{width:80,height:80,borderRadius:18,objectFit:"cover",border:"1px solid var(--c-border)"}} />}
                  <button onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/png,image/jpeg,image/webp";
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const url = await uploadTenantMedia(user.tenantId, file, "app-icon");
                          setState(s2 => ({...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? {...t, appIcon: url} : t)}));
                          toast(zhEn("App 圖示已上傳", "App icon uploaded"));
                        } catch (err) {
                          console.error("App icon upload failed:", err);
                          toast(i18nT("tx.uploadFailedToast"), "error");
                        }
                      }
                    };
                    input.click();
                  }} style={{flex:1,padding:"10px 16px",borderRadius:12,border:"2px dashed var(--c-border)",background:"#FFFDFD",cursor:"pointer",fontWeight:800,fontSize:13,color:"var(--c-primary)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    <Upload size={16} /> {tenant?.appIcon ? zhEn("更新 App 圖示", "Update app icon") : zhEn("上傳 App 圖示", "Upload app icon")}
                  </button>
                </div>
                <p style={{fontSize:11,color:"#8A8A9E",margin:"6px 0 0",fontWeight:700,lineHeight:1.5}}>{zhEn("客戶將你嘅預約頁「加入主畫面」時，會用呢個圖示同店名（似一個專屬 App）。建議正方形 512×512 PNG；冇上傳就用店鋪標誌。", "When customers add your booking page to their home screen, this icon and your shop name are used (like a dedicated app). A square 512×512 PNG is recommended; if none is uploaded, the shop logo is used.")}</p>
              </div>
              )}

              {/* Banner Upload */}
              <div>
                <p style={{fontSize:13,fontWeight:700,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("🎨 橫幅圖片", "🎨 Banner image")}</p>
                {tenant?.bannerUrl && <img src={tenant.bannerUrl} alt="banner" style={{width:"100%",height:120,borderRadius:12,objectFit:"cover",marginBottom:8}} />}
                <button onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const url = await uploadTenantMedia(user.tenantId, file, "banner");
                        setState(s2 => ({...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? {...t, bannerUrl: url} : t)}));
                        toast(zhEn("橫幅已上傳", "Banner uploaded"));
                      } catch (err) {
                        console.error("Banner upload failed:", err);
                        toast(i18nT("tx.uploadFailedToast"), "error");
                      }
                    }
                  };
                  input.click();
                }} style={{width:"100%",padding:"12px 16px",borderRadius:12,border:"2px dashed var(--c-border)",background:"#FFFDFD",cursor:"pointer",fontWeight:800,fontSize:13,color:"var(--c-primary)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <Upload size={16} /> {tenant?.bannerUrl ? zhEn("更新橫幅", "Update banner") : zhEn("上傳橫幅", "Upload banner")}
                </button>
              </div>

              {/* Tagline */}
              <Input label={zhEn("短語（Tagline）", "Tagline")} value={tenant?.tagline || ""} onChange={e => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, tagline: e.target.value } : t) }))} placeholder={zhEn("例：為您的毛孩提供最好的服務", "e.g. The best care for your pet")} />

              {/* About */}
              <div>
                <label style={{fontSize:13,fontWeight:700,color:"#4A4A68",display:"block",marginBottom:6}}>{zhEn("關於本店", "About the shop")}</label>
                <textarea value={tenant?.about || ""} onChange={e => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, about: e.target.value } : t) }))} placeholder={zhEn("介紹您的店鋪...", "Tell customers about your shop...")} style={{width:"100%",minHeight:100,padding:"12px 14px",borderRadius:12,border:"2px solid var(--c-border)",fontSize:13,fontFamily:"inherit",resize:"none",boxSizing:"border-box"}} />
              </div>

              {/* Social Links */}
              <Input label={zhEn("網站", "Website")} value={tenant?.website || ""} onChange={e => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, website: e.target.value } : t) }))} placeholder="https://..." />
              <Input label="Instagram" value={tenant?.instagram || ""} onChange={e => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, instagram: e.target.value } : t) }))} placeholder="@yourinstagram" />
              <Input label="Facebook" value={tenant?.facebook || ""} onChange={e => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, facebook: e.target.value } : t) }))} placeholder="yourpage" />

              {/* Preview & save action */}
              <div style={{display:"flex",gap:8,marginTop:8}}>
                <Btn onClick={() => { toast(zhEn("變更已自動保存 ✓", "Changes are saved automatically ✓")); }} style={{flex:1}}>
                  <Save size={16}/> {zhEn("確認已保存", "Confirm saved")}
                </Btn>
                <Btn variant="ghost" onClick={() => {
                  const slug = tenant?.bookingSlug;
                  if (!slug) { toast(zhEn("請先設定預約連結", "Set your booking link first"), "error"); return; }
                  window.open(window.location.origin + "/shop/" + slug, "_blank");
                }} style={{flex:1}}>
                  <Eye size={16}/> {zhEn("預覽公開頁面", "Preview public page")}
                </Btn>
              </div>
            </div>
          </Card>}

          {/* Gallery Card — owner-curated photos shown on /shop/{slug}/gallery */}
          {inSec("shop-brand","gallery") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Image size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("作品集", "Portfolio")}</h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700}}>{zhEn("上傳您嘅得意作品，會顯示喺公開店鋪頁面嘅「作品集」分頁。建議上傳清晰、構圖好嘅成果照。", "Upload your best work — it appears in the \"Portfolio\" tab of your public shop page. Clear, well-composed result photos work best.")}</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
              {(tenant?.shopGallery || []).map((g, i) => (
                <div key={g.id || i} style={{position:"relative",aspectRatio:"1",borderRadius:12,overflow:"hidden",border:"2px solid var(--c-border)"}}>
                  <img src={g.url} alt={g.caption || "gallery " + (i+1)} style={{width:"100%",height:"100%",objectFit:"cover"}} />
                  <button
                    onClick={() => setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, shopGallery: (t.shopGallery || []).filter((_, idx) => idx !== i) } : t) }))}
                    style={{position:"absolute",top:4,right:4,width:24,height:24,borderRadius:99,background:"rgba(0,0,0,0.6)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                    aria-label={zhEn("移除", "Remove")}
                  ><X size={14} color="#fff" strokeWidth={3} /></button>
                </div>
              ))}
              <button
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.multiple = true;
                  input.onchange = async (e) => {
                    const files = Array.from(e.target.files || []);
                    // Resize each photo to a 1200px-on-the-long-edge JPEG
                    // at 0.9 quality before upload. Raw iPhone photos are
                    // 4-8 MB and would frequently fail when 6 uploaded
                    // back-to-back — the storage layer times out or hits
                    // size limits. Same compression PhotoUpload uses.
                    const compress = (file) => new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const img = new window.Image();
                        img.onload = () => {
                          const canvas = document.createElement("canvas");
                          const maxSize = 1200;
                          let w = img.width, h = img.height;
                          if (w > maxSize || h > maxSize) { const r = Math.min(maxSize/w, maxSize/h); w = Math.round(w*r); h = Math.round(h*r); }
                          canvas.width = w; canvas.height = h;
                          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
                          // Phase 3.1 — try q=0.85 first; if the blob is
                          // still >2MB (unusually busy photo / pre-resized
                          // input), recompress at q=0.7.
                          canvas.toBlob(b => {
                            if (!b) return reject(new Error("compress failed"));
                            if (b.size <= 2_000_000) return resolve(b);
                            canvas.toBlob(b2 => b2 ? resolve(b2) : reject(new Error("compress failed (retry)")), "image/jpeg", 0.7);
                          }, "image/jpeg", 0.85);
                        };
                        img.onerror = () => reject(new Error("image decode failed"));
                        img.src = ev.target.result;
                      };
                      reader.onerror = () => reject(new Error("file read failed"));
                      reader.readAsDataURL(file);
                    });
                    // Phase 1.6 — explicit patchTenant after EACH successful
                    // upload. Previously we just setState'd and relied on the
                    // 600ms debounced sync to mirror it; if the owner closed
                    // Settings within that window (or unmount raced the
                    // microtask queue), the photo URL was orphaned in Storage
                    // with no DB row pointing to it. Inline patchTenant makes
                    // each photo durable the moment it succeeds.
                    let ok = 0, fail = 0;
                    // Snapshot current gallery so we can append + patch
                    // cumulatively across the loop without state-closure races.
                    const tnow = state.tenants.find(t => t.id === user.tenantId);
                    let nextGallery = Array.isArray(tnow?.shopGallery) ? [...tnow.shopGallery] : [];
                    for (const file of files) {
                      try {
                        const blob = await compress(file);
                        const url = await uploadTenantMedia(user.tenantId, blob, "gallery");
                        const item = { id: uuid(), url, caption: "", displayOrder: 0 };
                        nextGallery = [...nextGallery, item];
                        // Durable write first — if this throws, the photo is
                        // already in Storage but we WON'T add it to local
                        // state, so the orphan is visible only as an unused
                        // Storage file (recoverable) instead of a phantom
                        // tile that vanishes on refresh.
                        await patchTenant(user.tenantId, { shopGallery: nextGallery });
                        setState(s2 => ({ ...s2, tenants: s2.tenants.map(t => t.id === user.tenantId ? { ...t, shopGallery: nextGallery } : t) }));
                        ok++;
                      } catch (err) {
                        console.error("Gallery upload failed:", file?.name, err);
                        fail++;
                      }
                    }
                    if (ok > 0 && fail === 0) toast(zhEn(ok + " 張相片已加入作品集", ok + (ok === 1 ? " photo" : " photos") + " added to portfolio"));
                    else if (ok > 0 && fail > 0) toast(zhEn(ok + " 張成功 · " + fail + " 張失敗", ok + " succeeded · " + fail + " failed"), "warning");
                    else if (fail > 0) toast(zhEn(fail + " 張上載失敗，請重試", fail + " failed to upload, please retry"), "error");
                  };
                  input.click();
                }}
                style={{aspectRatio:"1",borderRadius:12,border:"2px dashed var(--c-border)",background:"#FFFDFD",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,color:"var(--c-primary)",fontWeight:800,fontSize:12}}
              >
                <Plus size={20} strokeWidth={2.5} /> {zhEn("加入", "Add")}
              </button>
            </div>
            <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700}}>{zhEn("※ 一張張按 X 移除，新照片會排喺最後。需要重新排序？拖拉功能稍後推出。", "※ Tap X on each photo to remove it; new photos go at the end. Need to reorder? Drag-and-drop is coming soon.")}</p>
          </Card>}

          {/* Shop Info Card — draft mode: typing only mutates the local
              draft; nothing hits the DB until the owner clicks 儲存. */}
          {settingsSubtab === "shop-basic" && shopDraft && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Store size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("店鋪資料", "Shop details")} <HelpTip topic="shopBranding" /></h3>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <Input label={zhEn("店鋪名稱", "Shop name")} value={shopDraft.name || ""} onChange={e => setShopField("name", e.target.value)} placeholder={zhEn("例：毛孩天堂美容院", "e.g. Happy Paws Grooming")} />
              <Input label={zhEn("店鋪地址", "Shop address")} value={shopDraft.address || ""} onChange={e => setShopField("address", e.target.value)} placeholder={zhEn("例：香港銅鑼灣...", "e.g. Causeway Bay, Hong Kong...")} />
              <Input label={zhEn("店鋪電話", "Shop phone")} value={shopDraft.phone || ""} onChange={e => setShopField("phone", parseCopiedPhone(e.target.value).digits)} placeholder={zhEn("例：2345 6789", "e.g. 2345 6789")} type="tel" inputMode="tel" />
              <Sel label={zhEn("所在地區（用於匯入公眾假期）", "Region (used to import public holidays)")} value={shopDraft.country || "HK"} onChange={e => setShopField("country", e.target.value)}>
                {SUPPORTED_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Sel>
              <Sel label={zhEn("貨幣（用於收費 / 報表 / 收據）", "Currency (used for charges / reports / receipts)")} value={shopDraft.currency || "HKD"} onChange={e => setShopField("currency", e.target.value)}>
                {SUPPORTED_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Sel>
              <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700}}>{i18nT("set.basic.langMovedHint")}</p>
              <div style={{display:"flex",gap:8,marginTop:8,paddingTop:14,borderTop:"2px solid var(--c-light)",alignItems:"center"}}>
                {shopDirty
                  ? <span style={{fontSize:12,fontWeight:800,color:"#D97706",marginRight:"auto"}}>{zhEn("● 未儲存修改", "● Unsaved changes")}</span>
                  : <span style={{fontSize:12,fontWeight:700,color:"#9CA3AF",marginRight:"auto"}}>{zhEn("已儲存", "Saved")}</span>}
                <Btn variant="secondary" size="sm" disabled={!shopDirty} onClick={discardShop}>{zhEn("放棄", "Discard")}</Btn>
                <Btn size="sm" disabled={!shopDirty} onClick={saveShop}><Save size={14} strokeWidth={2.5}/> {zhEn("儲存", "Save")}</Btn>
              </div>
            </div>
          </Card>}

          {/* Booking Link Card */}
          {inSec("directory","link") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Globe size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("客戶預約連結", "Customer booking link")} <HelpTip topic="bookingSlug" /></h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("分享此連結給客人，讓他們線上預約", "Share this link so customers can book online")}</p>
            {(() => {
              const slug = tenant?.bookingSlug || "";
              const link = getBookingUrl(slug, state);
              const saveSlug = () => {
                const s = newSlug.toLowerCase().replace(/[^a-z0-9]/g,"");
                if (!s||s.length<3){setSlugErr2(zhEn("至少需3個字元", "At least 3 characters"));return;}
                if (state.tenants.some(t=>t.id!==user.tenantId&&t.bookingSlug===s)){setSlugErr2(zhEn("此連結已被使用", "This link is already taken"));return;}
                setState(s2=>({...s2,tenants:s2.tenants.map(t=>t.id===user.tenantId?{...t,bookingSlug:s}:t)}));
                toast(zhEn("預約連結已更新", "Booking link updated")); setEditSlug(false);
              };
              return <>
                <div style={{background:"var(--c-lighter)",borderRadius:20,padding:"16px 20px",border:"2px solid var(--c-border)",marginBottom:12}}>
                  <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:"0 0 6px"}}>{zhEn("你的專屬連結", "Your link")}</p>
                  <p style={{fontSize:16,fontWeight:900,color:"var(--c-primary)",margin:0,wordBreak:"break-all"}}>{link}</p>
                </div>
                {editSlug ? <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",background:"var(--c-light)",borderRadius:16,padding:"10px 14px",gap:6,border:"2px solid "+(slugErr2?"#FF4D6D":"var(--c-border)")}}>
                      <span style={{fontSize:13,color:"#8A8A9E",fontWeight:700,whiteSpace:"nowrap"}}>{(state.platformConfig?.domain||"pettobook.com")+"/shop/"}</span>
                      <input style={{flex:1,background:"none",border:"none",outline:"none",fontSize:15,fontWeight:800,color:"var(--c-primary)"}} value={newSlug} onChange={e=>{setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,""));setSlugErr2("");}} />
                    </div>
                    {slugErr2&&<p style={{fontSize:12,color:"#FF4D6D",margin:"4px 0 0",fontWeight:700}}>{slugErr2}</p>}
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <Btn variant="secondary" style={{flex:1}} onClick={()=>setEditSlug(false)}>{zhEn("取消", "Cancel")}</Btn>
                    <Btn style={{flex:1}} onClick={saveSlug}>{zhEn("儲存連結", "Save link")}</Btn>
                  </div>
                </div> : <div style={{display:"flex",gap:10}}>
                  <Btn variant="secondary" style={{flex:1}} onClick={()=>{setEditSlug(true);setNewSlug(slug);}}><Edit size={14}/>{zhEn("修改連結", "Edit link")}</Btn>
                  <Btn style={{flex:1}} onClick={()=>{navigator.clipboard?.writeText("https://"+link).then(()=>toast(zhEn("連結已複製！", "Link copied!"))).catch(()=>toast(zhEn("連結：", "Link: ")+link));}}>
                    <Link size={14}/>{zhEn("複製連結", "Copy link")}
                  </Btn>
                </div>}
                <Btn style={{width:"100%",marginTop:10,background:"linear-gradient(135deg,#FF7AA2,#FF4D6D)",color:"#fff",fontWeight:900}} onClick={() => { setPreviewTab("overview"); setPreviewOpen(true); }}>
                  <Eye size={14} strokeWidth={2.5}/>{zhEn("👁️ 預覽店面（即點即改）", "👁️ Preview storefront (edit in place)")}
                </Btn>
                <Btn variant="secondary" style={{width:"100%",marginTop:8,background:"#F0FDF4",color:"#10B981"}} onClick={()=>{ window.open(window.location.origin + "/shop/" + slug, "_blank"); }}>
                  <Eye size={14} strokeWidth={2.5}/>{zhEn("喺新分頁打開", "Open in new tab")}
                </Btn>
                {/* PRIVATE, unlisted price-check link — customers see exact price
                    + live openings WITHOUT registering; the register wall appears
                    only when they book. NOT shown anywhere on the public page. */}
                <div style={{marginTop:12,padding:"12px 14px",borderRadius:14,background:"#F5F3FF",border:"1.5px solid #DDD6FE"}}>
                  <p style={{fontSize:12.5,fontWeight:900,color:"#5B21B6",margin:"0 0 4px"}}>{zhEn("🔒 私密查價連結（免註冊睇價 · 睇空檔）", "🔒 Private price-check link (see prices & free slots without signing up)")}</p>
                  <p style={{fontSize:11,color:"#6D28D9",margin:"0 0 8px",fontWeight:700,lineHeight:1.5}}>{zhEn("分享畀想先睇價錢同即時空檔嘅客人。此連結唔會出現喺你嘅公開頁面；客人落訂先需要註冊。", "Share with customers who want to see prices and live availability first. This link doesn't appear on your public page; customers only sign up when they book.")}</p>
                  <div style={{display:"flex",gap:8}}>
                    <Btn size="sm" style={{flex:1}} onClick={()=>{ const q = "https://"+link+"?quote=1"; navigator.clipboard?.writeText(q).then(()=>toast(zhEn("私密連結已複製！", "Private link copied!"))).catch(()=>toast(zhEn("連結：", "Link: ")+q)); }}><Link size={13}/>{zhEn("複製私密連結", "Copy private link")}</Btn>
                    <Btn size="sm" variant="secondary" style={{flex:1}} onClick={()=>{ window.open(window.location.origin + "/shop/" + slug + "?quote=1", "_blank"); }}><Eye size={13}/>{zhEn("預覽", "Preview")}</Btn>
                  </div>
                </div>
              </>;
            })()}
          </Card>}

          {/* Public directory opt-in card. Lives next to the booking-link
              card on shop-basic so the owner sees both discovery options
              together. Opt-in is OFF by default — shops have to flip it
              before they appear in /directory. */}
          {inSec("directory","dir") && tenant && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>
              <Globe size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("公開店舖目錄", "Public shop directory")}
            </h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>{zhEn("揀選你嘅店舖點樣喺 pettobook.com/directory 出現。", "Choose how your shop appears at pettobook.com/directory.")}</p>
            {(() => {
              const canBook = shopHasOnlineBooking(tenant); // paid plans only
              // Free shops can't pick "booking"; if their stored flag still says
              // booking, treat it as listing-only in the UI.
              const mode = !tenant.directoryListed ? "off" : ((tenant.directoryBooking === false || !canBook) ? "listing" : "booking");
              const setMode = (m) => {
                if (m === "booking" && !canBook) { toast(T("網上預約係付費功能，請先升級店舖計劃","Online booking is a paid feature — please upgrade the plan first"), "warning"); return; }
                setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, directoryListed: m !== "off", directoryBooking: m === "booking" } : t) }));
              };
              const opts = [
                { id:"booking", title:zhEn("列入目錄，同時接受網上預約", "Listed, and accepting online bookings"), desc:zhEn("客戶可搜尋到你，並可即時網上預約 📅", "Customers can find you and book online instantly 📅"), locked: !canBook },
                { id:"listing", title:zhEn("列入目錄，不接受網上預約", "Listed, not accepting online bookings"), desc:zhEn("客戶可搜尋到你，但只顯示聯絡方式（隱藏預約按鈕）", "Customers can find you but only see contact details (booking button hidden)") },
                { id:"off",     title:zhEn("不列入目錄", "Not listed"), desc:zhEn("唔公開 — 只有你嘅 QR / 連結客戶可以見到", "Private — only customers with your QR / link can see you") },
              ];
              return (
                <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
                  {opts.map(o => {
                    const sel = mode === o.id;
                    return (
                      <button key={o.id} onClick={() => setMode(o.id)} disabled={o.locked} style={{textAlign:"left",display:"flex",gap:12,alignItems:"flex-start",background: sel ? "var(--c-lighter)" : "#fff",border:"2px solid " + (sel ? "var(--c-primary)" : "var(--c-border)"),borderRadius:14,padding:"12px 14px",cursor: o.locked ? "not-allowed" : "pointer", opacity: o.locked ? 0.55 : 1}}>
                        <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,marginTop:1,border:"2px solid " + (sel ? "var(--c-primary)" : "#C4C4D4"),display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {sel && <div style={{width:10,height:10,borderRadius:"50%",background:"var(--c-primary)"}} />}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{o.title}{o.locked ? " 🔒" : ""}</p>
                          <p style={{fontSize:11.5,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.4}}>{o.locked ? T("付費功能 — 升級後即可開啟網上預約","Paid feature — upgrade to enable online booking") : o.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <div style={{marginBottom:6}}>
              <p style={{fontSize:13,color:"#8A8A9E",fontWeight:800,margin:"0 0 6px"}}>{zhEn("地區 (顯示喺目錄卡片同篩選用)", "Area (shown on directory cards and used for filtering)")}</p>
              <Sel value={tenant.region || ""} onChange={e => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, region: e.target.value || null } : t) }))}>
                <option value="">{zhEn("— 未設定 —", "— Not set —")}</option>
                {HK_REGION_GROUPS.map(g => (
                  <optgroup key={g.id} label={g.zh + " / " + g.en}>
                    {HK_REGIONS.filter(r => r.group === g.id).map(r => (
                      <option key={r.id} value={r.id}>{r.zh}</option>
                    ))}
                  </optgroup>
                ))}
              </Sel>
            </div>
            {tenant.directoryListed && !tenant.region && (
              <p style={{fontSize:11,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"6px 10px",margin:"6px 0 0",fontWeight:700}}>{zhEn("⚠️ 未揀地區嘅店舖只會喺「全部地區」入面出現，搜尋率會低好多。", "⚠️ Shops with no area only appear under \"All areas\", so far fewer people will find you.")}</p>
            )}

            {/* Service mode — fixed shop / mobile (上門) / both */}
            <div style={{marginTop:16}}>
              <p style={{fontSize:13,color:"#8A8A9E",fontWeight:800,margin:"0 0 2px"}}>{T("服務模式","Service mode")}</p>
              <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:"0 0 8px"}}>{T("客人會喺目錄睇到你係實體店定上門服務","Customers see in the directory whether you're in-shop or home-visit")}</p>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[["in_shop","🏠 "+T("實體店","In-shop")],["mobile","🚗 "+T("上門服務","Home visit")],["both","🏠🚗 "+T("兩者皆可","Both")]].map(([val,label]) => {
                  const on = (tenant.serviceMode || "in_shop") === val;
                  return (
                    <button key={val} onClick={() => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, serviceMode: val } : t) }))}
                      style={{fontSize:13, fontWeight:800, padding:"8px 14px", borderRadius:99, cursor:"pointer", border:"2px solid " + (on ? "var(--c-primary)" : "var(--c-light)"), background: on ? "var(--c-primary)" : "#fff", color: on ? "#fff" : "#6A6A85"}}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Directory tags */}
            <div style={{marginTop:16}}>
              <p style={{fontSize:13,color:"#8A8A9E",fontWeight:800,margin:"0 0 2px"}}>{zhEn("店舖標籤", "Shop tags")}</p>
              <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:"0 0 8px"}}>{zhEn("顯示喺目錄卡片，等客人一眼睇到你接收咩寵物 / 特色", "Shown on directory cards so customers can see at a glance which pets / specialities you handle")}</p>
              <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:10}}>
                {PRESET_DIRECTORY_TAGS.map(tag => {
                  const on = (tenant.directoryTags || []).includes(tag);
                  return (
                    <button key={tag} onClick={() => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, directoryTags: on ? (t.directoryTags || []).filter(x => x !== tag) : [ ...(t.directoryTags || []), tag ] } : t) }))}
                      style={{fontSize:12.5, fontWeight:800, padding:"6px 12px", borderRadius:99, cursor:"pointer", border:"1.5px solid " + (on ? "var(--c-primary)" : "var(--c-border)"), background: on ? "var(--c-primary)" : "#fff", color: on ? "#fff" : "#6A6A85"}}>
                      {on ? "✓ " : "+ "}{tag}
                    </button>
                  );
                })}
              </div>
              {/* Custom tags (not in presets) */}
              {(tenant.directoryTags || []).filter(t => !PRESET_DIRECTORY_TAGS.includes(t)).length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:10}}>
                  {(tenant.directoryTags || []).filter(t => !PRESET_DIRECTORY_TAGS.includes(t)).map(tag => (
                    <span key={tag} style={{fontSize:12.5, fontWeight:800, padding:"6px 12px", borderRadius:99, background:"var(--c-primary)", color:"#fff", display:"inline-flex", alignItems:"center", gap:6}}>
                      {tag}
                      <span onClick={() => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, directoryTags: (t.directoryTags || []).filter(x => x !== tag) } : t) }))} style={{cursor:"pointer", opacity:0.85}}>×</span>
                    </span>
                  ))}
                </div>
              )}
              <input placeholder={zhEn("自訂標籤後㩒 Enter 加入…", "Type a custom tag and press Enter...")} onKeyDown={e => {
                if (e.key === "Enter") {
                  const v = e.target.value.trim();
                  if (v) setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, directoryTags: Array.from(new Set([ ...(t.directoryTags || []), v ])) } : t) }));
                  e.target.value = "";
                  e.preventDefault();
                }
              }} style={{width:"100%", border:"2px solid var(--c-light)", borderRadius:14, padding:"10px 14px", fontSize:14, fontWeight:600, outline:"none", boxSizing:"border-box", background:"#FFFDFD", color:"#4A4A68"}} />
            </div>

            <Btn variant="secondary" style={{width:"100%",marginTop:14,background:"#F0FDF4",color:"#10B981"}} onClick={()=>{ window.open(window.location.origin + "/directory", "_blank"); }}>
              <Eye size={14} strokeWidth={2.5}/> {zhEn("查看公開目錄", "View public directory")}
            </Btn>
          </Card>}

          {inSec("shop-brand","theme") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 20px",color:"#4A4A68"}}>{zhEn("店鋪專屬主題色 🎨", "Shop theme colour 🎨")}</h3>
            <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
              {Object.entries(THEME_NAMES).map(([k, name]) => {
                const isActive = (tenant?.theme || "peach") === k;
                const tVars = THEMES[k];
                return (
                  <div key={k} onClick={() => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, theme:k } : t) }))} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,cursor:"pointer"}}>
                    <div style={{width:52,height:52,borderRadius:99,background:`linear-gradient(135deg,${tVars['--c-grad1']},${tVars['--c-primary']})`,border:isActive?`3px solid ${tVars['--c-primary']}`:"3px solid transparent",boxShadow:isActive?`0 6px 16px ${tVars['--c-primary-40']}`:"0 4px 12px rgba(0,0,0,0.05)",transition:"all 0.2s",transform:isActive?"scale(1.1)":"scale(1)"}} />
                    <span style={{fontSize:13,fontWeight:800,color:isActive?"var(--c-primary)":"#8A8A9E"}}>{name}</span>
                  </div>
                );
              })}
            </div>
          </Card>}
          {/* White-label branding toggle — Pro plan only. Free / Basic see
              a locked card prompting upgrade so they know the feature exists. */}
          {inSec("shop-brand","theme") && (() => {
            const plan = PLANS[tenant?.plan || "free"];
            const unlocked = !!plan?.features?.removeBranding;
            return (
              <Card style={{padding:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
                  <div style={{flex:1}}>
                    <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.set.policy.brandRemove")}</h3>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.brandRemoveSub")}</p>
                    {!unlocked && <p style={{fontSize:11,color:"#92400E",margin:"6px 0 0",fontWeight:800,padding:"6px 10px",background:"#FFFBEB",borderRadius:8,border:"1px solid #FEF3C7",display:"inline-block"}}>{i18nT("own.set.policy.brandRemoveProBadge")}</p>}
                  </div>
                  <Toggle on={unlocked && tenant?.hideBranding === true} disabled={!unlocked} onChange={v => {
                    if (!unlocked) { toast(i18nT("own.set.msg.upgradeRequired"), "error"); return; }
                    setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, hideBranding: v } : t) }));
                  }} />
                </div>
              </Card>
            );
          })()}
          {/* 員工權限 settings card REMOVED — permissions are now edited
              inline in the StaffEditor (員工 bottom tab). This duplicate
              surface was causing confusion. */}
          {inSec("shop-hours","holidays") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("公休日設定 🏖️", "Days off 🏖️")} <HelpTip topic="holidays" /></h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700}}>{zhEn("公眾假期會喺月曆顯示假期名（紫色標籤）作參考，", "Public holidays appear in the calendar (purple labels) for reference and ")}<strong>{zhEn("並唔會自動休息", "do not close the shop automatically")}</strong>{zhEn("。如某個假期想休假，請喺下方手動加日期。", ". To close on a holiday, add the date manually below.")}</p>
            {(() => {
              const yr = new Date().getFullYear();
              const cc = tenant?.country || "HK";
              const phs = getHolidaysForYear(cc, yr);
              const future = phs.filter(h => h.date >= dateNow());
              const already = new Set(tenant?.holidays || []);
              return future.length > 0 ? (
                <div style={{background:"var(--c-lighter)",borderRadius:16,padding:14,border:"2px solid var(--c-border)",marginBottom:14}}>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{SUPPORTED_COUNTRIES.find(c=>c.code===cc)?.name || cc} {yr} {zhEn("公眾假期（", "Public holidays (")}{future.length} {zhEn("個）— 點擊以標記為休假", ") — tap to mark as closed")}</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:120,overflowY:"auto"}}>
                    {future.map(h => {
                      const closed = already.has(h.date);
                      const toggle = () => {
                        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, holidays: closed ? (t.holidays||[]).filter(d => d !== h.date) : [...new Set([...(t.holidays||[]), h.date])] } : t) }));
                        toast(zhEn(closed ? h.name + " 已恢復營業" : h.name + " 已標記為休假", closed ? h.name + " reopened" : h.name + " marked as closed"));
                      };
                      return (
                        <button key={h.date} onClick={toggle} title={h.date + zhEn(" · 點擊切換", " · tap to toggle") } style={{fontSize:11,padding:"4px 10px",borderRadius:99,background:closed?"#FEE2E2":"#F5F3FF",color:closed?"#991B1B":"#6B21A8",fontWeight:700,border:"1px solid "+(closed?"#FCA5A5":"#C4B5FD"),cursor:"pointer"}}>
                          {closed ? "🛑 " : "✨ "}{h.date.slice(5)} · {h.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })()}
            <div style={{display:"flex",gap:10,marginBottom:16}}>
              <input type="date" id="hdate" style={{flex:1,border:"2px solid var(--c-light)",borderRadius:20,padding:"12px 16px",fontSize:15,fontWeight:600,outline:"none",color:"#4A4A68"}} />
              <Btn onClick={() => { const d = document.getElementById("hdate").value; if (!d) { toast(i18nT("val.pickDate"), "error"); return; } setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, holidays:[...new Set([...(t.holidays||[]), d])] } : t) })); toast(d + zhEn(" 已設為公休日", " set as a day off")); }}>{zhEn("手動新增", "Add manually")}</Btn>
            </div>
            {(tenant?.holidays||[]).slice().sort().map(d => {
              const ph = getHolidayForDate(tenant?.country || "HK", d);
              return (
                <div key={d} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"#FFFDFD",border:"2px solid var(--c-light)",borderRadius:16,marginBottom:8}}>
                  <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{d}{ph ? <span style={{fontSize:12,fontWeight:700,color:"var(--c-primary)",marginLeft:8}}>· {ph.name}</span> : null}</span>
                  <button onClick={() => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, holidays: t.holidays.filter(h => h !== d) } : t) }))} style={{background:"#FEF2F2",border:"none",cursor:"pointer",padding:6,borderRadius:99,display:"flex"}}><X size={16} color="#EF4444" strokeWidth={3} /></button>
                </div>
              );
            })}
          </Card>}
          {inSec("shop-hours","hours") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><CalendarClock size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("營業時間設定", "Opening hours")} <HelpTip topic="businessHours" /></h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("設定每天的營業時間，系統會根據此設定顯示可預約時段", "Set opening hours for each day; bookable slots are shown based on this")}</p>
            <BusinessHoursEditor
              hours={tenant?.businessHours || {0:{enabled:false},1:{enabled:true,open:"09:00",close:"18:00"},2:{enabled:true,open:"09:00",close:"18:00"},3:{enabled:true,open:"09:00",close:"18:00"},4:{enabled:true,open:"09:00",close:"18:00"},5:{enabled:true,open:"09:00",close:"18:00"},6:{enabled:true,open:"10:00",close:"17:00"}}}
              onChange={hours => {
                setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, businessHours: hours } : t) }));
                toast(zhEn("營業時間已更新 ⏰", "Opening hours updated ⏰"));
              }}
            />
          </Card>}
          {/* Tax / VAT config */}
          {/* Deposit Policy */}
          {inSec("payment","deposit") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><DollarSign size={20} color="#10B981" strokeWidth={2.5} /> {zhEn("訂金設定", "Deposit settings")}</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("客戶預約時需支付的訂金金額", "Deposit customers pay when booking")}</p>
            {(() => {
              const dp = tenant?.depositPolicy || { mode:"percent", percent:30, fixedAmount:0, minDeposit:50, enabled:false, newCustomersOnly:false };
              const enabled = dp.enabled === true;
              const setDp = (k,v) => setState(s => ({...s, tenants:s.tenants.map(t=>t.id===user.tenantId?{...t,depositPolicy:{...dp,[k]:v}}:t)}));
              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Master toggle */}
                <label style={{display:"flex",alignItems:"center",gap:12,padding:12,background:enabled?"var(--c-lighter)":"#F3F3F8",borderRadius:12,cursor:"pointer"}}>
                  <input type="checkbox" checked={enabled} onChange={e=>setDp("enabled",e.target.checked)} style={{width:20,height:20,accentColor:"var(--c-primary)"}} />
                  <div style={{flex:1}}>
                    <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{zhEn("啟用訂金", "Require deposit")}</p>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{zhEn("關閉後預約不會顯示訂金，店家現場收取全額", "When off, bookings show no deposit and the full amount is collected in the shop")}</p>
                  </div>
                </label>

                {enabled && <>
                  {/* New-customers-only toggle */}
                  <label style={{display:"flex",alignItems:"center",gap:12,padding:12,background:"var(--c-lighter)",borderRadius:12,cursor:"pointer"}}>
                    <input type="checkbox" checked={!!dp.newCustomersOnly} onChange={e=>setDp("newCustomersOnly",e.target.checked)} style={{width:20,height:20,accentColor:"var(--c-primary)"}} />
                    <div style={{flex:1}}>
                      <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{zhEn("只向新客戶要求訂金", "Deposit for new customers only")}</p>
                      <p style={{fontSize:11,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{zhEn("已有預約紀錄的客戶毋須再付訂金", "Returning customers with past bookings don't pay a deposit")}</p>
                    </div>
                  </label>

                  <div style={{display:"flex",gap:8,background:"var(--c-light)",borderRadius:99,padding:4}}>
                    {[["percent",zhEn("按百分比 %", "By percentage %")],["fixed",zhEn("固定金額 $", "Fixed amount $")]].map(([v,l])=>(
                      <button key={v} onClick={()=>setDp("mode",v)} style={{flex:1,padding:"10px 0",borderRadius:99,fontSize:14,fontWeight:800,border:"none",background:dp.mode===v?"var(--c-primary)":"transparent",color:dp.mode===v?"#fff":"var(--c-grad1)",cursor:"pointer"}}>{l}</button>
                    ))}
                  </div>
                  {dp.mode==="percent" ? (
                    <div>
                      <Input label={zhEn("訂金比例 (目前: "+dp.percent+"%)", "Deposit % (now: "+dp.percent+"%)")} type="number" value={dp.percent} onChange={e=>setDp("percent",Math.max(0,Math.min(100,parseInt(e.target.value)||0)))} />
                      <p style={{fontSize:12,color:"#8A8A9E",margin:"8px 0 0",fontWeight:700}}>{zhEn("例：服務費 $500 → 訂金 $", "e.g. a $500 service → deposit $")}{Math.round(500*(dp.percent||30)/100)}</p>
                    </div>
                  ) : (
                    <Input label={zhEn("固定訂金金額 ($)", "Fixed deposit amount ($)")} type="number" value={dp.fixedAmount} onChange={e=>setDp("fixedAmount",parseInt(e.target.value)||0)} />
                  )}
                  <Input label={zhEn("最低訂金 ($)", "Minimum deposit ($)")} type="number" value={dp.minDeposit} onChange={e=>setDp("minDeposit",parseInt(e.target.value)||0)} />
                </>}
              </div>;
            })()}
          </Card>}

          {/* Payment methods — one list, but each method carries a SCOPE:
              客戶線上 (customer booking/deposit page) and/or 店內結帳 (the
              owner's 收款 sheet at the counter). They are genuinely
              different sets: a credit-card terminal is counter-only, a
              bank-transfer QR is customer-only, and FPS is often both.
              Legacy rows with no scope flags count as BOTH — see
              methodForCustomer / methodForCheckout. */}
          {inSec("payment","methods") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Wallet size={20} color="#3B82F6" strokeWidth={2.5} /> {zhEn("收款方式", "Payment methods")}</h3>
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>{zhEn("每個方式可以分開設定用喺邊度：", "Each method can be set separately for where it's used: ")}<b>{zhEn("客戶線上", "Customer online")}</b>{zhEn("（客戶預約／付訂金頁面，可上載 QR + 說明）定係", " (customer booking / deposit page; you can upload a QR + instructions) or ")} <b>{zhEn("店內結帳", "In-shop checkout")}</b>{zhEn("（老闆收款表單，例如信用卡機、八達通）。可以兩樣都揀。", " (the owner's payment form, e.g. card terminal, Octopus). You can pick both.")}</p>
            {(() => {
              // Preset catalogue. `scope` only seeds a NEW row — once added
              // the owner can flip the chips inside the ⚙️ detail sheet.
              const PRESETS = [
                { key:"fps",     name:zhEn("FPS（轉數快）", "FPS (Faster Payment System)"),        scope:"customer" },
                { key:"payme",   name:"PayMe",               scope:"customer" },
                { key:"bank",    name:"銀行轉帳",             scope:"customer" },
                { key:"wechat",  name:zhEn("WeChat Pay 微信支付", "WeChat Pay"),  scope:"customer" },
                { key:"line",    name:"LINE Pay",            scope:"customer" },
                { key:"cash",    name:"💵 現金",              scope:"checkout" },
                { key:"card",    name:zhEn("💳 信用卡 / 信用卡機", "💳 Card / card terminal"),  scope:"checkout" },
                { key:"octopus", name:zhEn("🐙 八達通", "🐙 Octopus"),            scope:"checkout" },
                { key:"eps",     name:"EPS",                 scope:"checkout" },
              ];
              const methods = Array.isArray(tenant?.depositMethods) ? tenant.depositMethods : [];
              const setMethods = (next) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, depositMethods: next } : t) }));
              const patchMethod = (id, patch) => setMethods(methods.map(m => m.id === id ? { ...m, ...patch } : m));
              const addPreset = (preset) => setMethods([...methods, { id: uuid(), presetKey: preset.key, name: preset.name, qrUrl: "", instructions: "", enabled: true, forCustomer: preset.scope === "customer", forCheckout: preset.scope === "checkout" }]);
              const addCustom = () => {
                const row = { id: uuid(), name: zhEn("新收款方式", "New payment method"), qrUrl: "", instructions: "", enabled: true, forCustomer: true, forCheckout: true };
                setMethods([...methods, row]);
                setPayDetailId(row.id);   // open its sheet straight away
              };
              const removeMethod = (id) => {
                if (!window.confirm(i18nT("tx.delPayMethodConfirm"))) return;
                setMethods(methods.filter(m => m.id !== id));
                setPayDetailId(null);
              };
              const onQrUpload = async (id, file) => {
                if (!file) return;
                try {
                  const url = await uploadTenantMedia(user.tenantId, file, "deposit-qr");
                  patchMethod(id, { qrUrl: url });
                  toast(i18nT("tx.qrUploaded"));
                } catch (e) {
                  toast(i18nT("tx.uploadFailed") + zhEn("：", ": ") + (e?.message || e), "error");
                }
              };
              // A method must be usable SOMEWHERE — a row that shows up in
              // neither place is just a confusing dead entry.
              const setScope = (m, key, on) => {
                const next = { forCustomer: m.forCustomer !== false, forCheckout: m.forCheckout !== false, [key]: on };
                if (!next.forCustomer && !next.forCheckout) { toast(zhEn("最少要揀一個用途（客戶線上 或 店內結帳）", "Pick at least one use (customer online or in-shop checkout)"), "warning"); return; }
                patchMethod(m.id, next);
              };

              // ── Drag to reorder ────────────────────────────────────────
              // Pointer Events, not HTML5 drag-and-drop: the latter simply
              // doesn't fire on iPad/touch, and this list is mostly used on a
              // tablet at the counter. Array order IS the display order, so a
              // drop is just a splice.
              const ROW_H = 56;
              const startDrag = (e, idx) => {
                if (e.button != null && e.button !== 0) return;
                e.preventDefault();
                const startY = e.clientY;
                const seed = { id: methods[idx].id, fromIdx: idx, overIdx: idx, dy: 0 };
                payDragRef.current = seed;
                setPayDrag(seed);
                const move = (ev) => {
                  const dy = ev.clientY - startY;
                  const overIdx = Math.max(0, Math.min(methods.length - 1, idx + Math.round(dy / ROW_H)));
                  const next = { ...payDragRef.current, dy, overIdx };
                  payDragRef.current = next;
                  setPayDrag(next);
                };
                const up = () => {
                  window.removeEventListener("pointermove", move);
                  window.removeEventListener("pointerup", up);
                  window.removeEventListener("pointercancel", up);
                  const d = payDragRef.current;
                  payDragRef.current = null;
                  setPayDrag(null);
                  if (d && d.overIdx !== d.fromIdx) {
                    const arr = [...methods];
                    const [moved] = arr.splice(d.fromIdx, 1);
                    arr.splice(d.overIdx, 0, moved);
                    setMethods(arr);
                  }
                };
                window.addEventListener("pointermove", move);
                window.addEventListener("pointerup", up);
                window.addEventListener("pointercancel", up);
              };

              const unusedPresets = PRESETS.filter(p => !methods.some(m => m.presetKey === p.key));
              const detail = methods.find(m => m.id === payDetailId) || null;
              // ⚡ 一按收款 config — persisted on tenant.quick_collect (jsonb).
              const qc = tenant?.quickCollect || {};
              const qcRows = [{ id: "__cash__", name: "💵 現金" }, ...checkoutMethodsOf(tenant).filter(m => !isCashMethod(m))];
              const setQuickCollect = (patch) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, quickCollect: { enabled: !!qc.enabled, methodId: qc.methodId || "__cash__", ...patch } } : t) }));
              return (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {/* ⚡ 一按收款 — for shops running a separate POS: 收款 records the
                      full amount with the method below in ONE tap, no method sheet.
                      Wallet customers still get the sheet (deduction must never be
                      bypassed) — that's enforced in openCollect, stated here so the
                      owner isn't surprised. */}
                  <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:16,padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                      <div>
                        <p style={{fontSize:13.5,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("⚡ 一按收款", "⚡ One-tap collect")}</p>
                        <p style={{fontSize:11.5,color:"#B45309",fontWeight:700,margin:"2px 0 0"}}>{zhEn("撳「收款」即以下面方式收全數，唔使再揀（適合另有 POS 機嘅店）。客戶有錢包餘額時仍會開選單，方便扣錢包。", "Tapping \"Collect\" takes the full amount with the method below, with no menu (good for shops with a separate POS terminal). If the customer has wallet balance, the menu still opens so you can deduct from the wallet.")}</p>
                      </div>
                      <Toggle on={!!qc.enabled} onChange={v => setQuickCollect({ enabled: v })} />
                    </div>
                    {!!qc.enabled && (
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10}}>
                        <span style={{fontSize:12,fontWeight:800,color:"#92400E",whiteSpace:"nowrap"}}>{zhEn("入帳方式", "Record as")}</span>
                        <select
                          value={qc.methodId || "__cash__"}
                          onChange={e => setQuickCollect({ methodId: e.target.value })}
                          style={{flex:1,border:"2px solid #FDE68A",borderRadius:12,padding:"8px 10px",fontSize:13,fontWeight:800,color:"#92400E",background:"#fff",outline:"none"}}
                        >
                          {qcRows.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  {methods.length === 0 && (
                    <p style={{fontSize:12,color:"#8A8A9E",fontStyle:"italic",margin:0,padding:"6px 0",fontWeight:700}}>{zhEn("未有收款方式 — 喺下面加入。", "No payment methods yet — add one below.")}</p>
                  )}
                  {/* Compact rows: grip · name · scope · on/off · ⚙️.
                      Everything else lives in the detail sheet. */}
                  {methods.map((m, idx) => {
                    const dragging = payDrag?.id === m.id;
                    const off = m.enabled === false;
                    // Rows the dragged item is passing over slide out of the way.
                    let shift = 0;
                    if (payDrag && !dragging) {
                      const { fromIdx, overIdx } = payDrag;
                      if (fromIdx < overIdx && idx > fromIdx && idx <= overIdx) shift = -ROW_H;
                      else if (fromIdx > overIdx && idx >= overIdx && idx < fromIdx) shift = ROW_H;
                    }
                    return (
                      <div key={m.id} style={{
                        display:"flex",alignItems:"center",gap:8,
                        background: off ? "#F9FAFB" : "#fff",
                        border:"1.5px solid " + (dragging ? "var(--c-primary)" : (off ? "#E5E5EA" : "var(--c-light)")),
                        borderRadius:14,padding:"9px 10px",minHeight:44,
                        opacity: off ? 0.6 : 1,
                        transform: dragging ? "translateY(" + payDrag.dy + "px) scale(1.02)" : (shift ? "translateY(" + shift + "px)" : "none"),
                        transition: dragging ? "none" : "transform 0.16s ease",
                        boxShadow: dragging ? "0 8px 20px rgba(0,0,0,0.14)" : "none",
                        // touchAction stays on the GRIP only — putting it on the
                        // whole row blocks page scroll wherever you touch it.
                        position:"relative", zIndex: dragging ? 5 : 1,
                      }}>
                        <div onPointerDown={(e) => startDrag(e, idx)} title={zhEn("拖動排序", "Drag to reorder")}
                          style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"4px 2px",color:"#C7C7CC",cursor:"grab",touchAction:"none",flexShrink:0}}>
                          <GripVertical size={18} strokeWidth={2.5} />
                        </div>
                        <span style={{flex:1,minWidth:0,fontSize:13.5,fontWeight:800,color:"#4A4A68",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name || "—"}</span>
                        <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E",whiteSpace:"nowrap"}}>{[m.forCustomer !== false ? "👤" : null, m.forCheckout !== false ? "🏪" : null].filter(Boolean).join("")}</span>
                        <Toggle on={!off} onChange={v => patchMethod(m.id, { enabled: v })} />
                        <button onClick={() => setPayDetailId(m.id)} title={zhEn("設定", "Settings")}
                          style={{width:34,height:34,borderRadius:10,border:"1.5px solid var(--c-light)",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                          <Settings size={15} color="var(--c-primary)" strokeWidth={2.5} />
                        </button>
                      </div>
                    );
                  })}
                  <p style={{fontSize:11,color:"#AFAFC4",margin:"2px 0 0",fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
                    <GripVertical size={12} strokeWidth={2.5} /> {zhEn("拖動左邊手柄排次序 · 👤 客戶線上 · 🏪 店內結帳（結帳時 💵 現金 永遠第一）", "Drag the left handle to reorder · 👤 Customer online · 🏪 In-shop checkout (💵 Cash is always first at checkout)")}
                  </p>
                  {/* Add — presets not yet in the list, then a custom one. */}
                  {unusedPresets.length > 0 && <>
                    <p style={{fontSize:11,fontWeight:800,color:"#8E8E93",margin:"8px 0 -2px",letterSpacing:0.4}}>{zhEn("加入常用方式", "Add a common method")}</p>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {unusedPresets.map(p => (
                        <button key={p.key} onClick={() => addPreset(p)}
                          style={{padding:"7px 12px",borderRadius:99,border:"1.5px dashed var(--c-light)",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}>
                          <Plus size={12} strokeWidth={3} /> {p.name} <span style={{fontSize:10,color:"#AFAFC4"}}>{p.scope === "customer" ? "👤" : "🏪"}</span>
                        </button>
                      ))}
                    </div>
                  </>}
                  <button onClick={addCustom} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px",borderRadius:12,background:"var(--c-lighter)",border:"1.5px dashed var(--c-primary)",color:"var(--c-primary)",fontSize:13,fontWeight:900,cursor:"pointer",marginTop:4}}>
                    <Plus size={14} strokeWidth={3} /> {zhEn("自訂收款方式", "Custom payment method")}
                  </button>

                  {/* ── Per-method detail sheet ───────────────────────── */}
                  <BottomSheet open={!!detail} onClose={() => setPayDetailId(null)} title={detail?.name || zhEn("收款方式", "Payment methods")}>
                    {detail && (() => {
                      const isCustom = !detail.presetKey;
                      return (
                        <div style={{display:"flex",flexDirection:"column",gap:14}}>
                          {isCustom && (
                            <Input label={zhEn("名稱", "Name")} value={detail.name || ""} onChange={e => patchMethod(detail.id, { name: e.target.value })} placeholder={zhEn("例：PayMe、信用卡機", "e.g. PayMe, card terminal")} />
                          )}
                          <div>
                            <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("用喺邊度", "Used for")}</p>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              {[["forCustomer",zhEn("👤 客戶線上", "👤 Customer online"),zhEn("客戶預約／付訂金頁面", "Customer booking / deposit page")],["forCheckout",zhEn("🏪 店內結帳", "🏪 In-shop checkout"),zhEn("老闆收款表單", "Owner's payment form")]].map(([k, label, hint]) => {
                                const on = detail[k] !== false;
                                return (
                                  <button key={k} onClick={() => setScope(detail, k, !on)} title={hint}
                                    style={{flex:1,minWidth:130,textAlign:"left",padding:"10px 12px",borderRadius:12,border:"2px solid " + (on ? "var(--c-primary)" : "var(--c-light)"),background: on ? "var(--c-lighter)" : "#fff",cursor:"pointer"}}>
                                    <span style={{display:"block",fontSize:13,fontWeight:900,color:"#4A4A68"}}>{label}</span>
                                    <span style={{display:"block",fontSize:10.5,fontWeight:700,color:"#8A8A9E",marginTop:2}}>{hint}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:"var(--c-lighter)",cursor:"pointer"}}>
                            <Toggle on={detail.enabled !== false} onChange={v => patchMethod(detail.id, { enabled: v })} />
                            <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{detail.enabled === false ? zhEn("已停用", "Disabled") : zhEn("啟用中", "Active")}</span>
                          </label>
                          {/* QR + 說明 only matter when the customer pays remotely. */}
                          {detail.forCustomer !== false ? <>
                            <div>
                              <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("付款說明", "Payment instructions")}</p>
                              <textarea
                                value={detail.instructions || ""}
                                onChange={e => patchMethod(detail.id, { instructions: e.target.value })}
                                placeholder={zhEn("選填，例：手機號碼 9123-4567 / 戶口號碼 / 備註", "Optional, e.g. phone number 9123-4567 / account number / note")}
                                style={{width:"100%",boxSizing:"border-box",border:"1.5px solid var(--c-light)",borderRadius:10,padding:"9px 12px",fontSize:13,fontWeight:700,outline:"none",color:"#4A4A68",background:"#fff",resize:"vertical",minHeight:70,fontFamily:"inherit"}}
                              />
                            </div>
                            <div>
                              <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("收款 QR Code", "Payment QR code")}</p>
                              <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                                {detail.qrUrl ? (
                                  <div style={{width:96,height:96,borderRadius:12,overflow:"hidden",border:"1.5px solid var(--c-light)",background:"#fff",flexShrink:0}}>
                                    <img src={detail.qrUrl} alt="QR" style={{width:"100%",height:"100%",objectFit:"contain"}} />
                                  </div>
                                ) : (
                                  <div style={{width:96,height:96,borderRadius:12,border:"1.5px dashed #C7C7CC",display:"flex",alignItems:"center",justifyContent:"center",color:"#AFAFC4",fontSize:11,fontWeight:700,flexShrink:0}}>{zhEn("無 QR", "No QR")}</div>
                                )}
                                <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                                  <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"9px 13px",borderRadius:10,background:"#fff",border:"1.5px solid var(--c-light)",color:"var(--c-primary)",fontSize:12.5,fontWeight:800,cursor:"pointer",width:"fit-content"}}>
                                    <Upload size={13} strokeWidth={2.5} /> {detail.qrUrl ? zhEn("更換 QR", "Replace QR") : zhEn("上載 QR", "Upload QR")}
                                    <input type="file" accept="image/*" style={{display:"none"}} onChange={e => onQrUpload(detail.id, e.target.files?.[0])} />
                                  </label>
                                  {detail.qrUrl && (
                                    <button onClick={() => patchMethod(detail.id, { qrUrl: "" })} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:99,background:"transparent",border:"none",color:"#DC2626",fontSize:11.5,fontWeight:700,cursor:"pointer",width:"fit-content"}}><X size={11} strokeWidth={2.5} /> {zhEn("移除 QR", "Remove QR")}</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </> : (
                            <p style={{fontSize:11.5,color:"#8A8A9E",fontWeight:700,margin:0,lineHeight:1.6,background:"var(--c-lighter)",borderRadius:12,padding:"10px 12px"}}>
                              {zhEn("呢個方式淨係用喺店內結帳，唔需要 QR / 付款說明。想加 QR？開返「👤 客戶線上」。", "This method is only used for in-shop checkout, so no QR / instructions are needed. Want a QR? Turn on \"👤 Customer online\".")}
                            </p>
                          )}
                          <button onClick={() => removeMethod(detail.id)}
                            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px",borderRadius:12,background:"#FEF2F2",border:"1.5px solid #FECACA",color:"#DC2626",fontSize:13,fontWeight:900,cursor:"pointer"}}>
                            <Trash2 size={14} strokeWidth={2.5} /> {zhEn("刪除呢個方式", "Delete this method")}
                          </button>
                        </div>
                      );
                    })()}
                  </BottomSheet>
                </div>
              );
            })()}
            <div style={{marginTop:16,padding:"12px 14px",background:"#EFF6FF",border:"1.5px solid #BFDBFE",borderRadius:12,display:"flex",gap:10,alignItems:"flex-start"}}>
              <Info size={16} color="#2563EB" strokeWidth={2.5} style={{flexShrink:0,marginTop:2}} />
              <p style={{fontSize:12,color:"#1E40AF",margin:0,fontWeight:700,lineHeight:1.6}}>
                {zhEn("客戶預訂成功後會顯示你嘅 QR + 說明。佢哋付款後，會用 WhatsApp 將收據截圖 send 比你；你喺預約詳情手動確認「已收訂金」。", "After booking, customers see your QR + instructions. After paying, they send you a receipt screenshot on WhatsApp; you confirm \"Deposit received\" manually in the booking details.")}
              </p>
            </div>
          </Card>}

          {/* Tax Settings */}
          {inSec("payment","tax") && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><DollarSign size={20} color="#8B5CF6" strokeWidth={2.5} /> {zhEn("稅務設定", "Tax settings")} <HelpTip topic="payment" /></h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("跨市場稅務支援（HK 服務費 / SG GST / TW VAT / 其他）", "Multi-market tax support (HK service charge / SG GST / TW VAT / other)")}</p>
            {(() => {
              const tc = tenant?.taxConfig || { enabled: false, rate: 0, label: zhEn("稅", "Tax"), mode: "exclusive" };
              const setTc = (k, v) => setState(s => ({...s, tenants: s.tenants.map(t => t.id === user.tenantId ? {...t, taxConfig: {...tc, [k]: v}} : t)}));
              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <label style={{display:"flex",alignItems:"center",gap:12,padding:12,background:tc.enabled?"var(--c-lighter)":"#F3F3F8",borderRadius:12,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!tc.enabled} onChange={e => setTc("enabled", e.target.checked)} style={{width:20,height:20,accentColor:"var(--c-primary)"}} />
                  <div style={{flex:1}}>
                    <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>{zhEn("啟用稅務計算", "Enable tax calculation")}</p>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{zhEn("關閉後預約總計不會顯示稅項", "When off, booking totals don't show tax")}</p>
                  </div>
                </label>
                {tc.enabled && <>
                  <Input label={zhEn("稅項名稱", "Tax name")} value={tc.label || ""} onChange={e => setTc("label", e.target.value)} placeholder={zhEn("例：服務費 / GST / VAT / 稅", "e.g. Service charge / GST / VAT / Tax")} />
                  <Input label={zhEn("稅率 (%)", "Tax rate (%)")} type="number" step="0.1" value={tc.rate ?? 0} onChange={e => setTc("rate", Math.max(0, parseFloat(e.target.value) || 0))} placeholder={zhEn("例：10", "e.g. 10")} />
                  <div style={{display:"flex",gap:8,background:"var(--c-light)",borderRadius:99,padding:4}}>
                    {[["exclusive",zhEn("另加 (Exclusive)", "Added on top (Exclusive)")],["inclusive",zhEn("已含 (Inclusive)", "Included (Inclusive)")]].map(([v,l]) => (
                      <button key={v} onClick={() => setTc("mode", v)} style={{flex:1,padding:"10px 0",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:tc.mode===v?"var(--c-primary)":"transparent",color:tc.mode===v?"#fff":"var(--c-grad1)",cursor:"pointer"}}>{l}</button>
                    ))}
                  </div>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>
                    {tc.mode === "inclusive"
                      ? zhEn("「已含」：服務價格已包含稅項，收據上將拆分顯示", "\"Included\": service prices already include tax; receipts show it split out")
                      : zhEn("「另加」：稅項會加在小計之上", "\"Added on top\": tax is added on top of the subtotal")}
                  </p>
                </>}
              </div>;
            })()}
          </Card>}

          {/* Cancellation Policy — its own 取消政策 subtab (owner request:
              split policy rules from the 啟用功能 feature toggles). */}
          {settingsSubtab === "cancel-policy" && <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={20} color="#EF4444" strokeWidth={2.5} /> {zhEn("預訂及取消政策", "Booking & cancellation policy")}</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>{zhEn("設定客戶取消預約的規則和退款條件", "Set the rules and refund terms for customer cancellations")}</p>
            {(() => {
              const cp = tenant?.cancellationPolicy || { deadlineHours:24, lateCancelFeePercent:100, freeCancelRefundPercent:100 };
              const setCp = (k,v) => setState(s => ({...s, tenants:s.tenants.map(t=>t.id===user.tenantId?{...t,cancellationPolicy:{...cp,[k]:v}}:t)}));
              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Approval mode — enforced server-side by guard_appointment_abuse
                    (customer inserts land approval_status='pending' when on). */}
                <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background:cp.requireApproval?"#FEF3C7":"var(--c-lighter)",border:"2px solid "+(cp.requireApproval?"#FDE68A":"var(--c-border)"),borderRadius:16,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!cp.requireApproval} onChange={e=>setCp("requireApproval", e.target.checked)} style={{transform:"scale(1.25)",marginTop:2,accentColor:"#D97706"}} />
                  <span style={{flex:1}}>
                    <span style={{display:"block",fontSize:13,fontWeight:900,color:"#4A4A68"}}>{zhEn("🔒 網上預約需店家確認", "🔒 Online bookings need shop approval")}</span>
                    <span style={{display:"block",fontSize:11,fontWeight:700,color:"#8A8A9E",marginTop:2,lineHeight:1.5}}>{zhEn("開啟後，客戶自助預約會先顯示「⏳ 待確認」，喺預約列表撳「✓ 接受」先生效；「✗ 拒絕」會自動取消並通知客戶。門市 / 員工建立嘅預約唔受影響。", "When on, customer self-bookings show \"⏳ Pending\" until you tap \"✓ Accept\" in the booking list; \"✗ Reject\" cancels automatically and notifies the customer. Bookings created by the shop / staff are unaffected.")}</span>
                  </span>
                </label>
                {/* Minimum advance-booking notice — enforced in generateTimeSlots
                    (slots inside the window are shown crossed-out + unbookable). */}
                <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:16,padding:14}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#1E40AF",margin:"0 0 2px"}}>{zhEn("⏳ 最少提前預約時間", "⏳ Minimum booking notice")}</p>
                  <p style={{fontSize:11,color:"#2563EB",fontWeight:700,margin:"0 0 10px",lineHeight:1.5}}>{zhEn("客戶網上預約時須喺服務時間前預留幾耐？揀「允許即日」＝接受即日 / walk-in；揀「1 日前」＝當日唔接受新網上預約。門市 / 員工建立嘅預約唔受限制。", "How far ahead must customers book online? \"Allow same-day\" accepts same-day / walk-in; \"1 day ahead\" accepts no new online bookings for today. Bookings made by the shop / staff aren't restricted.")}</p>
                  <Sel label={zhEn("提前時間", "Notice period")} value={String(cp.minAdvanceHours ?? 0)} onChange={e=>setCp("minAdvanceHours", parseInt(e.target.value)||0)}>
                    <option value="0">{zhEn("允許即日 / walk-in", "Allow same-day / walk-in")}</option>
                    <option value="2">{zhEn("2 小時前", "2 hours ahead")}</option>
                    <option value="4">{zhEn("4 小時前", "4 hours ahead")}</option>
                    <option value="12">{zhEn("12 小時前", "12 hours ahead")}</option>
                    <option value="24">{zhEn("1 日前（24 小時）", "1 day ahead (24 hours)")}</option>
                    <option value="48">{zhEn("2 日前（48 小時）", "2 days ahead (48 hours)")}</option>
                  </Sel>
                </div>
                <Input label={zhEn("免費取消截止 (預約前幾小時)", "Free-cancellation cut-off (hours before booking)")} type="number" value={cp.deadlineHours} onChange={e=>setCp("deadlineHours",parseInt(e.target.value)||24)} />
                <Input label={zhEn("免費取消退款比例 (%)", "Free-cancellation refund (%)")} type="number" value={cp.freeCancelRefundPercent} onChange={e=>setCp("freeCancelRefundPercent",Math.max(0,Math.min(100,parseInt(e.target.value)||100)))} />
                <Input label={zhEn("逾期取消扣款比例 (% 訂金)", "Late-cancellation charge (% of deposit)")} type="number" value={cp.lateCancelFeePercent} onChange={e=>setCp("lateCancelFeePercent",Math.max(0,Math.min(100,parseInt(e.target.value)||100)))} />
                <div style={{background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:16,padding:14}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#166534",margin:"0 0 2px"}}>{zhEn("📦 套票次數處理", "📦 Package visit handling")}</p>
                  <p style={{fontSize:11,color:"#059669",fontWeight:700,margin:"0 0 10px",lineHeight:1.5}}>{zhEn("當預約用咗套票，取消 / 失約時：退還次數（唔當使用）定係扣除次數（當已使用，作為罰則）。", "When a booking used a package and is cancelled / missed: return the visit (not counted as used) or deduct it (counted as used, as a penalty).")}</p>
                  <Sel label={zhEn("取消時", "On cancellation")} value={cp.packageOnCancel || "return"} onChange={e=>setCp("packageOnCancel", e.target.value)}>
                    <option value="return">{zhEn("退還次數（唔扣）", "Return the visit (don't deduct)")}</option>
                    <option value="deduct">{zhEn("扣除次數（當已使用）", "Deduct the visit (count as used)")}</option>
                  </Sel>
                  <div style={{height:10}} />
                  <Sel label={zhEn("失約時 No-show", "On no-show")} value={cp.packageOnNoShow || "deduct"} onChange={e=>setCp("packageOnNoShow", e.target.value)}>
                    <option value="return">{zhEn("退還次數（唔扣）", "Return the visit (don't deduct)")}</option>
                    <option value="deduct">{zhEn("扣除次數（當已使用）", "Deduct the visit (count as used)")}</option>
                  </Sel>
                </div>
                <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("政策說明預覽", "Policy preview")}</p>
                  <p style={{fontSize:13,color:"#6A6A85",margin:0,fontWeight:700,lineHeight:1.6}}>
                    {zhEn("預約前 " + cp.deadlineHours + " 小時取消：退還 " + cp.freeCancelRefundPercent + "% 訂金", "Cancel more than " + cp.deadlineHours + "h before: " + cp.freeCancelRefundPercent + "% of the deposit refunded")}
                    <br/>
                    {zhEn("預約前 " + cp.deadlineHours + " 小時內取消：扣除 " + cp.lateCancelFeePercent + "% 訂金", "Cancel within " + cp.deadlineHours + "h: " + cp.lateCancelFeePercent + "% of the deposit kept")}
                  </p>
                </div>
              </div>;
            })()}
          </Card>}

          {/* 每日服務上限 card removed — capacity is now governed entirely
              by staff schedules + working hours. The dailyLimit field is
              still on tenants for backwards-compat but no longer surfaces
              to the owner. Re-enable here if needed. */}

          {/* Wallet feature toggle — when disabled, hides the 錢包 tab on
              the customer side, the manual top-up button in CRM, and the
              wallet-deduct path in booking. Existing balances remain on
              record (auto-deduct still works) so no money disappears. */}
          {settingsSubtab === "shop-policy" && <Card style={{padding:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
              <div style={{flex:1}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Wallet size={18} color="var(--c-primary)" strokeWidth={2.5} /> {i18nT("own.set.policy.wallet")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.walletSub")}</p>
              </div>
              <Toggle on={tenant?.walletEnabled !== false} onChange={v => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, walletEnabled: v } : t) }))} />
            </div>
          </Card>}

          {settingsSubtab === "shop-policy" && (
              // WooCommerce store integration — enables the 網店 tab in
              // the customer app. Test-connection button hits the ping
              // op on the proxy edge fn so the owner sees green/red
              // right after saving.
              <Card style={{padding:20,display:"flex",flexDirection:"column",gap:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
                  <div style={{flex:1}}>
                    <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>
                      🛍️ {T("網店 (WooCommerce)", "Store (WooCommerce)", "ショップ (WooCommerce)")}
                    </h3>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>
                      {T("啟用後客戶 App 會出現「網店」分頁，展示你 WooCommerce 網站嘅產品。付款經 WC 現有嘅 Stripe / PayPal 進行。Apple 唔會抽任何佣金。",
                          "When on, the customer app shows a 「Shop」 tab with your WooCommerce products. Checkout uses your WC's existing Stripe / PayPal. Apple takes 0% on physical goods.",
                          "有効時、顧客アプリに「ショップ」タブが表示されます。支払いはWCの既存Stripe/PayPal経由、Appleは物販に手数料0%。")}
                    </p>
                  </div>
                  <Toggle on={tenant?.wcEnabled === true}
                    onChange={v => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcEnabled: v } : t) }))} />
                </div>
                {tenant?.wcEnabled && (
                  <div style={{display:"flex",flexDirection:"column",gap:10,paddingTop:8,borderTop:"1px solid var(--c-light)"}}>
                    <details style={{background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:10,padding:0}}>
                      <summary style={{cursor:"pointer",listStyle:"none",padding:"10px 12px",fontSize:12,fontWeight:900,color:"#166534"}}>
                        📖 {T("點樣攞 WooCommerce API 憑證？","How do I get WooCommerce API keys?","WooCommerce APIキーの取得方法")}
                      </summary>
                      <div style={{padding:"0 12px 12px",fontSize:12,color:"#166534",fontWeight:600,lineHeight:1.6}}>
                        <ol style={{margin:"4px 0 0",paddingLeft:18}}>
                          <li>{T("登入你嘅 WordPress 網站 → WooCommerce → Settings → Advanced → REST API。","Log in to WordPress → WooCommerce → Settings → Advanced → REST API.","WordPress → WooCommerce → Settings → Advanced → REST API")}</li>
                          <li>{T("撳「Add key」，Description 填「PettoBook」，User 揀店主帳戶，Permissions 揀「Read/Write」。","Click 「Add key」. Description = 「PettoBook」, User = shop owner, Permissions = 「Read/Write」.","「Add key」→ Description「PettoBook」、User=店主、Permissions=Read/Write")}</li>
                          <li>{T("撳「Generate API key」，會出現 ck_… 同 cs_…，複製落下面（呢頁一離開就唔會再顯示）。","Click 「Generate API key」 — copy the ck_… + cs_… below (they won't appear again after you leave the page).","「Generate API key」→ ck_…とcs_…をコピー（画面を離れると再表示不可）")}</li>
                          <li>{T("Store URL 填你嘅 WordPress 網站首頁 URL，例如 https://pawsin.com（唔使 /wp-json）。","Store URL is your WP homepage — e.g. https://pawsin.com (no /wp-json).","Store URL = WPホームページURL（/wp-json不要）")}</li>
                        </ol>
                      </div>
                    </details>
                    <Input label="Store URL" placeholder="https://pawsin.com"
                      value={tenant?.wcStoreUrl || ""}
                      onChange={e => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcStoreUrl: e.target.value.trim() } : t) }))} />
                    <Input label="Consumer Key (ck_...)" type="password" placeholder={T("ck_…（已儲存嘅唔會再顯示，貼新嘅就會取代）","ck_… (a saved key stays hidden — paste a new one to replace it)","ck_…（保存済みは非表示・新しい値で置換）")}
                      value={tenant?.wcConsumerKey || ""}
                      onChange={e => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcConsumerKey: e.target.value.trim() } : t) }))} />
                    <Input label="Consumer Secret (cs_...)" type="password" placeholder={T("cs_…（已儲存嘅唔會再顯示，貼新嘅就會取代）","cs_… (a saved secret stays hidden — paste a new one to replace it)","cs_…（保存済みは非表示・新しい値で置換）")}
                      value={tenant?.wcConsumerSecret || ""}
                      onChange={e => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcConsumerSecret: e.target.value.trim() } : t) }))} />
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <Btn size="sm" onClick={async () => {
                        try {
                          await patchTenant(user.tenantId, {
                            wcEnabled: true,
                            wcStoreUrl: tenant?.wcStoreUrl,
                            wcConsumerKey: tenant?.wcConsumerKey,
                            wcConsumerSecret: tenant?.wcConsumerSecret,
                          });
                          const { data: sess } = await supabaseDirect.auth.getSession();
                          const r = await fetch(`https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/woocommerce`, {
                            method:"POST",
                            headers:{
                              Authorization:`Bearer ${sess?.session?.access_token || ""}`,
                              "Content-Type":"application/json",
                              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
                            },
                            body: JSON.stringify({ tenant: user.tenantId, op:"ping" }),
                          });
                          const j = await r.json();
                          if (j.ok) toast(zhEn(`✅ 連線成功 · 幣別 ${j.currency || "HKD"}`, `✅ Connected · currency ${j.currency || "HKD"}`));
                          else toast(zhEn("❌ 連線失敗：", "❌ Connection failed: ") + (j.error || `status ${r.status}`), "error");
                        } catch (e) {
                          toast(zhEn("❌ 連線失敗：", "❌ Connection failed: ") + (e?.message || ""), "error");
                        }
                      }} style={{background:"#10B981",color:"#fff"}}>🧪 {T("測試連線","Test connection","接続テスト")}</Btn>
                      <span style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>{T("先儲存憑證，再測試","Save credentials first, then test","認証情報を保存してからテスト")}</span>
                    </div>

                    {/* App display rules — what the customer app shows from
                        this shop's WC catalog. Filter runs server-side in the
                        woocommerce edge fn so the client can't override. */}
                    <div style={{height:1,background:"#EEE",margin:"6px 0"}} />
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:900,fontSize:13,color:"#4A4A68"}}>
                          {T("隱藏售罄產品","Hide out-of-stock products","在庫切れ商品を隠す")}
                        </div>
                        <div style={{fontSize:11,color:"#8A8A9E",fontWeight:700,marginTop:2}}>
                          {T("接受預訂嘅產品仍然會顯示。","Pre-order (backorder) items still show.","予約受付中の商品は表示されます。")}
                        </div>
                      </div>
                      <Toggle on={tenant?.wcHideOutOfStock === true}
                        onChange={async v => {
                          setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcHideOutOfStock: v } : t) }));
                          try { await patchTenant(user.tenantId, { wcHideOutOfStock: v }); toast(zhEn("已更新", "Updated")); }
                          catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message||""), "error"); }
                        }} />
                    </div>

                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      <div style={{fontWeight:900,fontSize:13,color:"#4A4A68"}}>
                        {T("隱藏 App 分類","Hide categories from the app","アプリで非表示にするカテゴリー")}
                      </div>
                      <div style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>
                        {T("空白 = 顯示全部。揀選要「隱藏」嘅分類，客戶就見唔到。","Empty = show all. Pick categories to HIDE from customers.","空 = すべて表示。選択したカテゴリーはお客様に非表示。")}
                      </div>
                      {(() => {
                        // Cache lives in a module-level Map (see __wcCatsCache
                        // below) so a background tenant refetch doesn't wipe
                        // it — the DB has no wcCatsCache column, so stashing
                        // it on the tenant object lost it on every reload.
                        const cats = __wcCatsCache.get(user.tenantId) || [];
                        const setCats = (arr) => {
                          __wcCatsCache.set(user.tenantId, arr);
                          setState(s => ({ ...s })); // nudge a rerender
                        };
                        const selected = new Set((tenant?.wcHiddenCategories || []).map(Number));
                        return (
                          <>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              <Btn size="sm" onClick={async () => {
                                try {
                                  const { data: sess } = await supabaseDirect.auth.getSession();
                                  const r = await fetch(`https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/woocommerce?tenant=${user.tenantId}&op=categories&all=1`, {
                                    headers: {
                                      Authorization:`Bearer ${sess?.session?.access_token || ""}`,
                                      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
                                    }
                                  });
                                  const j = await r.json();
                                  if (Array.isArray(j.categories)) { setCats(j.categories); toast(zhEn(`已載入 ${j.categories.length} 個分類`, `Loaded ${j.categories.length} categories`)); }
                                  else toast(zhEn("載入失敗：", "Failed to load: ") + (j.error || `status ${r.status}`), "error");
                                } catch (e) { toast(zhEn("載入失敗：", "Failed to load: ") + (e?.message||""), "error"); }
                              }} style={{background:"#0EA5E9",color:"#fff"}}>📥 {T("載入分類","Load categories","カテゴリー読込")}</Btn>
                              {selected.size > 0 && (
                                <Btn size="sm" onClick={async () => {
                                  setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcHiddenCategories: [] } : t) }));
                                  try { await patchTenant(user.tenantId, { wcHiddenCategories: [] }); toast(zhEn("已重設 · 顯示全部", "Reset · showing all")); }
                                  catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message||""), "error"); }
                                }}>♻️ {T("重設 · 顯示全部","Reset · show all","リセット")}</Btn>
                              )}
                            </div>
                            {cats.length > 0 && (
                              <div style={{display:"flex",gap:6,flexWrap:"wrap",padding:8,background:"#FAFAFC",borderRadius:10}}>
                                {cats.map(c => {
                                  const on = selected.has(Number(c.id));
                                  return (
                                    <button key={c.id} type="button"
                                      onClick={async () => {
                                        const next = new Set(selected);
                                        if (on) next.delete(Number(c.id)); else next.add(Number(c.id));
                                        const arr = Array.from(next);
                                        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, wcHiddenCategories: arr } : t) }));
                                        try { await patchTenant(user.tenantId, { wcHiddenCategories: arr }); }
                                        catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message||""), "error"); }
                                      }}
                                      style={{
                                        border:"none", cursor:"pointer", fontWeight:800, fontSize:12,
                                        padding:"6px 10px", borderRadius:999,
                                        background: on ? "var(--c-primary)" : "#fff",
                                        color: on ? "#fff" : "#4A4A68",
                                        boxShadow: on ? "none" : "inset 0 0 0 1.5px #E5E5EE",
                                      }}>
                                      {c.name}{c.count != null ? ` · ${c.count}` : ""}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </Card>
          )}

          {/* Loyalty / points feature — owner-configurable earn + redeem
              rules. Toggle hides points UI everywhere customer-facing if
              off (per-tenant). Backend keeps existing points balances safe. */}
          {settingsSubtab === "shop-policy" && <Card style={{padding:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14,marginBottom:12}}>
              <div style={{flex:1}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Award size={18} color="var(--c-primary)" strokeWidth={2.5} /> {i18nT("own.set.policy.loyalty")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.loyaltySub")}</p>
              </div>
              <Toggle on={tenant?.loyaltyEnabled === true} onChange={v => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, loyaltyEnabled: v } : t) }))} />
            </div>
            {tenant?.loyaltyEnabled && (() => {
              const earn = Number(tenant?.loyaltyEarnPer) || 100;
              const val  = Number(tenant?.loyaltyRedeemValue) || 1;
              const ex100 = Math.floor(100 / earn);
              const example = ex100 > 0
                ? i18nT("own.set.policy.example").replace("{n}", String(ex100)).replace("{cash}", String(ex100 * val))
                : (i18nT("lang.name") === "English" ? ("Spend $" + earn + " → earn 1 point → redeem $" + val + " cash") : (zhEn("消費 $" + earn + " → 賺 1 分 → 抵現金 $" + val, "Spend $" + earn + " → earn 1 point → redeem $" + val + " cash")));
              return (
                <div style={{background:"var(--c-lighter)",borderRadius:14,padding:14,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <div>
                      <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 4px"}}>{i18nT("own.set.policy.earnRule")}</p>
                      <input type="number" min="1" step="1" value={tenant?.loyaltyEarnPer || 100} onChange={e => {
                        const v = Math.max(1, parseInt(e.target.value) || 100);
                        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, loyaltyEarnPer: v } : t) }));
                      }} style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box"}} />
                    </div>
                    <div>
                      <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 4px"}}>{i18nT("own.set.policy.redeemRule")}</p>
                      <input type="number" min="0.01" step="0.1" value={tenant?.loyaltyRedeemValue || 1} onChange={e => {
                        const v = Math.max(0.01, parseFloat(e.target.value) || 1);
                        setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, loyaltyRedeemValue: v } : t) }));
                      }} style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,outline:"none",color:"#4A4A68",background:"#FFFDFD",boxSizing:"border-box"}} />
                    </div>
                  </div>
                  <p style={{fontSize:11,color:"#7C3AED",fontWeight:800,margin:0,background:"#F5F3FF",padding:"8px 10px",borderRadius:10,border:"1px solid #DDD6FE"}}>{zhEn("💡 例：", "💡 e.g. ")}{example}</p>

                  {/* Where the redeemed voucher can be applied. Booking +
                      POS flows gate on this. Default 'grooming' = existing
                      behaviour (only groom services). */}
                  <div style={{marginTop:4}}>
                    <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{zhEn("兌換券可用於", "Redeemed coupons can be used for")}</p>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                      {[["grooming",zhEn("✂️ 美容服務", "✂️ Grooming services")],["products",zhEn("🛍️ 產品商品", "🛍️ Products")],["both",zhEn("兩者皆可", "Both")]].map(([v,l]) => {
                        const on = (tenant?.redeemScope || "grooming") === v;
                        return (
                          <button key={v} onClick={() => {
                            setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, redeemScope: v } : t) }));
                          }} style={{padding:"9px 6px",borderRadius:10,fontSize:12,fontWeight:900,cursor:"pointer",border:"2px solid "+(on?"#7C3AED":"var(--c-light)"),background:on?"#7C3AED":"#fff",color:on?"#fff":"#4A4A68"}}>{l}</button>
                        );
                      })}
                    </div>
                    <p style={{fontSize:10,color:"#8A8A9E",fontWeight:700,margin:"6px 0 0",lineHeight:1.5}}>
                      {zhEn("決定積分兌換到嘅折扣券可以喺邊度用。冇售賣產品嘅店舖建議保持「美容服務」。", "Decides where discount coupons redeemed with points can be used. Shops that don't sell products should keep \"Grooming services\".")}
                    </p>
                  </div>
                </div>
              );
            })()}
          </Card>}

          {/* Reviews feature toggle — controls the 評價 tab visibility AND
              the customer-side rating prompt after a 美容完成 booking. */}
          {settingsSubtab === "shop-policy" && <Card style={{padding:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
              <div style={{flex:1}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Star size={18} color="var(--c-primary)" strokeWidth={2.5} /> {i18nT("own.set.policy.review")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.reviewSub")}</p>
              </div>
              <Toggle on={tenant?.reviewsEnabled !== false} onChange={v => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, reviewsEnabled: v } : t) }))} />
            </div>
          </Card>}

          {/* Health report feature toggle — controls whether tapping the
              「完成」step in the appointment stepper opens the report sheet.
              Turn off for solo / express shops that don't need a written
              health summary per booking. */}
          {settingsSubtab === "shop-policy" && <Card style={{padding:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
              <div style={{flex:1}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><FileText size={18} color="var(--c-primary)" strokeWidth={2.5} /> {i18nT("own.set.policy.healthReport")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.healthReportSub")}</p>
              </div>
              <Toggle on={tenant?.healthReportEnabled !== false} onChange={v => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, healthReportEnabled: v } : t) }))} />
            </div>
            {tenant?.healthReportEnabled !== false && (
              <div style={{marginTop:12,paddingTop:12,borderTop:"2px solid var(--c-light)"}}>
                <button
                  onClick={() => setShowReportTemplateEditor(true)}
                  style={{
                    display:"flex",alignItems:"center",gap:8,
                    padding:"10px 14px",borderRadius:12,border:"2px solid var(--c-light)",
                    background:"var(--c-lighter)",color:"var(--c-primary)",
                    fontWeight:800,fontSize:13,cursor:"pointer",width:"100%",justifyContent:"space-between",
                  }}>
                  <span>{zhEn("⚙️ 自訂健康報告欄位", "⚙️ Customise health report fields")}</span>
                  <span style={{fontSize:11,color:"#8A8A9E",fontWeight:700}}>
                    {(tenant?.reportTemplate && tenant.reportTemplate.length)
                      ? zhEn(`已自訂 · ${(tenant.reportTemplate.filter(f=>f.enabled!==false)).length} 項`, `Customised · ${(tenant.reportTemplate.filter(f=>f.enabled!==false)).length} fields`)
                      : zhEn("使用預設 9 項","Using the default 9 fields")}
                  </span>
                </button>
                <p style={{fontSize:11,color:"#8A8A9E",margin:"6px 2px 0",fontWeight:600,lineHeight:1.4}}>
                  {zhEn("可增加自訂欄位、刪除唔用嘅、將皮膚等改為自由輸入。", "Add custom fields, remove unused ones, or switch fields like skin to free text.")}
                </p>
              </div>
            )}
          </Card>}

          {/* Maximum pet weight policy — customer-side booking blocks any
              pet whose recorded weight exceeds this value. 0 / blank = no
              limit. Owner uses this when they physically can't groom heavy
              dogs (table size, single-staff lift, etc). */}
          {settingsSubtab === "shop-policy" && <Card style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <div style={{flex:1}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{i18nT("own.set.policy.maxWeight")}</h3>
                <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("own.set.policy.maxWeightSub")}</p>
              </div>
              <input type="number" min="0" step="1" value={tenant?.maxPetWeightKg || ""} placeholder="∞" onChange={e => {
                const v = parseFloat(e.target.value);
                const next = isNaN(v) || v <= 0 ? null : v;
                setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, maxPetWeightKg: next } : t) }));
              }} style={{width:80,border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:15,fontWeight:900,outline:"none",textAlign:"center",color:"#4A4A68"}} />
            </div>
          </Card>}

          {/* 積分兌換項目 — owner defines what customers can swap points for.
              Stored as tenant.pointRedeemables jsonb. Customer-side UI lives
              in CustomerApp's profile tab. */}
          {/* 套票管理 — unified view of every issued customer package, stats
              card, filters, and per-row refund / extend / restore actions. */}
          {settingsSubtab === "packages-admin" && (() => {
            const cps = tenantCustomerPackages || [];
            // Compute stats
            const today = dateNow();
            const startOfMonth = today.slice(0, 7) + "-01";
            const sold = cps.filter(c => {
              if (c.refundedAt || !c.purchasedAt) return false;
              return localDateStr(new Date(c.purchasedAt)) >= startOfMonth;
            });
            const monthRevenue = sold.reduce((s, c) => s + (Number(c.amountPaid) || 0), 0);
            const allRevenue = cps.filter(c => !c.refundedAt).reduce((s, c) => s + (Number(c.amountPaid) || 0), 0);
            const activeCps = cps.filter(c => c.enabled && c.remainingVisits > 0 && !c.refundedAt);
            const refundedCps = cps.filter(c => c.refundedAt);
            const expiredCps = cps.filter(c => !c.refundedAt && c.expiresAt && new Date(c.expiresAt).getTime() < Date.now());
            const visitsUsed = cps.reduce((s, c) => s + (Array.isArray(c.usageLog) ? c.usageLog.length : 0), 0);
            const visitsRemaining = activeCps.reduce((s, c) => s + (Number(c.remainingVisits) || 0), 0);
            const filter = packagesAdminFilter;
            const setFilter = setPackagesAdminFilter;
            const visible = cps
              .filter(c => filter === "active" ? (c.enabled && c.remainingVisits > 0 && !c.refundedAt && (!c.expiresAt || new Date(c.expiresAt).getTime() > Date.now()))
                         : filter === "expired" ? !!(!c.refundedAt && c.expiresAt && new Date(c.expiresAt).getTime() < Date.now())
                         : filter === "spent"   ? (c.remainingVisits === 0 && !c.refundedAt)
                         : filter === "refunded" ? !!c.refundedAt
                         : true)
              .sort((a, b) => (b.purchasedAt || "").localeCompare(a.purchasedAt || ""));
            const reload = () => fetchTenantCustomerPackages(user.tenantId).then(setTenantCustomerPackages).catch(() => {});
            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <Card style={{padding:20}}>
                  <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Package size={20} color="var(--c-primary)" strokeWidth={2.5}/> {zhEn("套票管理", "Package management")} <HelpTip topic="packages" /></h3>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>{sold.length}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月銷售", "Sold this month")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>${monthRevenue}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月收入", "Revenue this month")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#7C3AED",margin:"0 0 2px"}}>{activeCps.length}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("有效張數", "Active packages")}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#10B981",margin:"0 0 2px"}}>{visitsUsed}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("已使用", "Used")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#3B82F6",margin:"0 0 2px"}}>{visitsRemaining}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("剩餘次數", "Visits left")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#D97706",margin:"0 0 2px"}}>${allRevenue}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("累計收入", "Total revenue")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:refundedCps.length>0?"#FEE2E2":"#FFFDFD",borderRadius:10,border:"1px solid "+(refundedCps.length>0?"#FECACA":"var(--c-light)")}}>
                      <p style={{fontSize:16,fontWeight:900,color:refundedCps.length>0?"#991B1B":"#9CA3AF",margin:"0 0 2px"}}>{refundedCps.length}</p>
                      <p style={{fontSize:9,color:refundedCps.length>0?"#991B1B":"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("退款", "Refund")}</p>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
                    <Btn size="sm" variant="secondary" onClick={() => {
                      // CSV export of all customer packages
                      const headers = [zhEn("購買日", "Purchased"),zhEn("客戶", "Customer"),zhEn("套票", "Package"),zhEn("總次數", "Total visits"),zhEn("剩餘", "Left"),zhEn("已用", "Used"),zhEn("到期", "Expires"),zhEn("金額", "Amount"),zhEn("付款方式", "Payment method"),zhEn("贈送", "Gift"),zhEn("狀態", "Status")];
                      const rows = cps.map(c => {
                        const pkg = servicePackages.find(p => p.id === c.packageId);
                        const cust = state.users[c.customerId];
                        const used = Array.isArray(c.usageLog) ? c.usageLog.length : 0;
                        const status = c.refundedAt ? zhEn("已退款", "Refunded") : (c.remainingVisits === 0 ? zhEn("已用完", "Used up") : (c.expiresAt && new Date(c.expiresAt) < new Date() ? zhEn("已過期", "Expired") : zhEn("有效", "Active")));
                        return [
                          (c.purchasedAt || "").slice(0,10),
                          (cust?.name || "").replace(/,/g," "),
                          (pkg?.name || "").replace(/,/g," "),
                          c.totalVisits || c.remainingVisits,
                          c.remainingVisits,
                          used,
                          (c.expiresAt || "").slice(0,10),
                          c.amountPaid || 0,
                          c.paymentMethod || "",
                          c.isGift ? zhEn("是", "yes") : zhEn("否", "no"),
                          status,
                        ].join(",");
                      });
                      const csv = headers.join(",") + "\n" + rows.join("\n");
                      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = "packages_" + dateNow() + ".csv";
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                      toast(zhEn("已下載 CSV ✓", "CSV downloaded ✓"));
                    }}>{zhEn("📥 匯出 CSV", "📥 Export CSV")}</Btn>
                    <Btn size="sm" variant="secondary" onClick={() => reload()}>{zhEn("🔄 重新整理", "🔄 Refresh")}</Btn>
                  </div>
                </Card>
                {/* Filter tabs */}
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                  {[["active",zhEn("有效", "Active"),activeCps.length],["expired",zhEn("已過期", "Expired"),expiredCps.length],["spent",zhEn("已用完", "Used up"),cps.filter(c=>c.remainingVisits===0&&!c.refundedAt).length],["refunded",zhEn("已退款", "Refunded"),refundedCps.length],["all",zhEn("全部", "All"),cps.length]].map(([v,l,n]) => {
                    const isSel = filter === v;
                    return <button key={v} onClick={() => setFilter(v)} style={{padding:"8px 14px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:isSel?"var(--c-primary)":"var(--c-light)",color:isSel?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap"}}>{l + " (" + n + ")"}</button>;
                  })}
                </div>
                {visible.length === 0 && <Card style={{padding:24}}><p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0}}>{zhEn("呢個分類冇套票", "No packages in this category")}</p></Card>}
                {visible.map(cp => {
                  const pkg = servicePackages.find(p => p.id === cp.packageId);
                  const cust = state.users[cp.customerId];
                  const used = Array.isArray(cp.usageLog) ? cp.usageLog.length : 0;
                  const total = cp.totalVisits || cp.remainingVisits;
                  const pctUsed = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                  const expired = cp.expiresAt && new Date(cp.expiresAt).getTime() < Date.now();
                  const isRefunded = !!cp.refundedAt;
                  const soldBy = cp.soldByUserId ? (state.users[cp.soldByUserId]?.name || "—") : "—";
                  return (
                    <Card key={cp.id} style={{padding:14,opacity:isRefunded?0.6:1}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
                            <p style={{fontSize:15,fontWeight:900,color:"#4A4A68",margin:0}}>{cust?.name || zhEn("(未知客戶)", "(unknown customer)")}</p>
                            <span style={{fontSize:10,color:"#8A8A9E",fontWeight:700}}>·</span>
                            <p style={{fontSize:13,fontWeight:800,color:"var(--c-primary)",margin:0}}>{pkg?.name || zhEn("套票", "Package")}</p>
                            {isRefunded && <span style={{fontSize:10,fontWeight:800,color:"#991B1B",background:"#FEE2E2",borderRadius:6,padding:"2px 6px"}}>{zhEn("已退款", "Refunded")}</span>}
                            {!isRefunded && cp.isGift && <span style={{fontSize:10,fontWeight:800,color:"#92400E",background:"#FEF3C7",borderRadius:6,padding:"2px 6px"}}>{zhEn("🎁 贈送", "🎁 Gift")}</span>}
                            {!isRefunded && expired && <span style={{fontSize:10,fontWeight:800,color:"#991B1B",background:"#FEE2E2",borderRadius:6,padding:"2px 6px"}}>{zhEn("已過期", "Expired")}</span>}
                          </div>
                          <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>
                            {zhEn("購買 ", "Purchased ") + (cp.purchasedAt || "").slice(0,10)}
                            {cp.expiresAt && zhEn(" · 到期 ", " · expires ") + cp.expiresAt.slice(0,10)}
                            {cp.paymentMethod && " · " + cp.paymentMethod}
                            {zhEn(" · 銷售員 ", " · Sold by ") + soldBy}
                          </p>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <p style={{fontSize:18,fontWeight:900,color:cp.remainingVisits===0?"#EF4444":"var(--c-primary)",margin:0,lineHeight:1}}>{cp.remainingVisits}</p>
                          <p style={{fontSize:10,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{"/ " + total}</p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div style={{height:6,background:"var(--c-light)",borderRadius:99,overflow:"hidden",marginBottom:8}}>
                        <div style={{height:"100%",width:pctUsed+"%",background:isRefunded?"#9CA3AF":(pctUsed>80?"#EF4444":pctUsed>50?"#F59E0B":"#10B981"),transition:"width 0.3s"}} />
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("已用 " + used + " 次"+(cp.amountPaid>0?" · 收 $"+cp.amountPaid:""), "Used " + used + (used === 1 ? " time" : " times")+(cp.amountPaid>0?" · received $"+cp.amountPaid:""))}</p>
                        {!isRefunded && <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <Btn size="sm" variant="secondary" onClick={() => setPkgUsageSheet(cp)}>{zhEn("📜 紀錄", "📜 History")}</Btn>
                          <Btn size="sm" variant="secondary" onClick={async () => {
                            try { await extendCustomerPackage(cp.id, { addDays: 30 }); toast(i18nT("tx.pkgExtend30Toast")); reload(); } catch (e) { toast(e?.message||i18nT("tx.opFailed"),"error"); }
                          }}>{i18nT("tx.pkgExtend30Btn")}</Btn>
                          <Btn size="sm" variant="secondary" disabled={cp.remainingVisits >= total} onClick={async () => {
                            if (!window.confirm(i18nT("tx.pkgRestore1Confirm"))) return;
                            try { await adjustCustomerPackageVisit(cp.id, 1, i18nT("tx.pkgRestoreReason")); toast(i18nT("tx.pkgRestoredToast")); reload(); } catch (e) { toast(e?.message||i18nT("tx.opFailed"),"error"); }
                          }}>{i18nT("tx.pkgRestoreBtn")}</Btn>
                          <Btn size="sm" variant="secondary" disabled={cp.remainingVisits <= 0} onClick={async () => {
                            if (!window.confirm(i18nT("tx.pkgDeduct1Confirm"))) return;
                            try { await adjustCustomerPackageVisit(cp.id, -1, i18nT("tx.pkgDeductReason")); toast(i18nT("tx.pkgDeductedToast")); reload(); } catch (e) { toast(e?.message||i18nT("tx.opFailed"),"error"); }
                          }}>{i18nT("tx.pkgDeductBtn")}</Btn>
                          <Btn size="sm" variant="secondary" onClick={async () => {
                            const raw = window.prompt(zhEn("調整次數（正數加，負數扣）：", "Adjust visits (positive adds, negative deducts):"), "-1");
                            if (raw == null) return;
                            const delta = parseInt(raw, 10);
                            if (!Number.isFinite(delta) || delta === 0) { toast(zhEn("請輸入非零整數", "Enter a non-zero whole number"), "error"); return; }
                            const reason = window.prompt(zhEn("原因（會記錄）：", "Reason (will be recorded):"), delta > 0 ? "手動加 " + delta + zhEn(" 次", " times") : "手動扣 " + Math.abs(delta) + zhEn(" 次", " times")) || "";
                            try { const res = await adjustCustomerPackageVisit(cp.id, delta, reason); toast(zhEn("已調整：剩 " + (res?.newRemaining ?? "?") + " 次 ✓", "Adjusted: " + (res?.newRemaining ?? "?") + " left ✓")); reload(); } catch (e) { toast(e?.message||zhEn("失敗", "Failed"),"error"); }
                          }}>{zhEn("⚖️ 調整", "⚖️ Adjust")}</Btn>
                          <Btn size="sm" variant="danger" onClick={async () => {
                            const reason = window.prompt(zhEn("退款原因：", "Refund reason:"), "客戶要求取消");
                            if (reason == null) return;
                            const amt = parseFloat(window.prompt(zhEn("退款金額：", "Refund amount:"), String(cp.amountPaid || 0)));
                            if (isNaN(amt)) return;
                            try { await refundCustomerPackage(cp.id, { amount: amt, reason }); toast(zhEn("已退款", "Refunded")); reload(); } catch (e) { toast(e?.message||zhEn("失敗", "Failed"),"error"); }
                          }}>{zhEn("💸 退款", "💸 Refund")}</Btn>
                        </div>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            );
          })()}

          {/* 儲值卡管理 — every customer's wallet balance + transactions in one
              place. Top-up, manual adjust, refund, CSV export. */}
          {settingsSubtab === "wallets-admin" && (() => {
            // Source: customer_memberships rows for this tenant (wallet lives
            // there in the multi-shop model). Map to a flat list joined with
            // profile for the name + phone display.
            const today = dateNow();
            const startOfMonth = today.slice(0, 7) + "-01";
            // Build a per-customer wallet list from state.memberships if
            // present, else fall back to legacy users[].wallet.
            const list = customers.map(c => {
              const mem = (state.memberships || []).find(m => m.userId === c.id && m.tenantId === user.tenantId);
              const wallet = mem ? (Number(mem.wallet) || 0) : (Number(c.wallet) || 0);
              const tx = (mem ? mem.walletTx : c.walletTx) || [];
              const lastTx = tx.length ? tx[tx.length - 1] : null;
              return { id: c.id, name: c.name || zhEn("(未命名)", "(unnamed)"), phone: c.phone || "", wallet, tx, lastTx };
            });
            const totalBalance = list.reduce((s, r) => s + r.wallet, 0);
            const allTx = list.flatMap(r => (r.tx || []).map(t => ({ ...t, customerId: r.id, customerName: r.name })));
            const monthTopups = allTx.filter(t => t.type === "topup" && (t.date || "").slice(0,10) >= startOfMonth);
            const monthSpend = allTx.filter(t => t.type === "deduct" && (t.date || "").slice(0,10) >= startOfMonth);
            const monthRefunds = allTx.filter(t => t.type === "topup" && (t.note || "").includes("退") && (t.date || "").slice(0,10) >= startOfMonth);
            const topupSum = monthTopups.reduce((s, t) => s + (Number(t.amount) || 0), 0);
            const spendSum = monthSpend.reduce((s, t) => s + (Number(t.amount) || 0), 0);
            const visible = list
              .filter(r => walletsAdminFilter === "withBalance" ? r.wallet > 0
                         : walletsAdminFilter === "lowBalance" ? (r.wallet > 0 && r.wallet < 100)
                         : walletsAdminFilter === "noBalance" ? r.wallet === 0
                         : true)
              .sort((a, b) => b.wallet - a.wallet);
            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <Card style={{padding:20}}>
                  <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Wallet size={20} color="var(--c-primary)" strokeWidth={2.5}/> {zhEn("儲值卡管理", "Wallet management")} <HelpTip topic="wallets" /></h3>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>${totalBalance.toLocaleString()}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("總餘額", "Total balance")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#10B981",margin:"0 0 2px"}}>${topupSum.toLocaleString()}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月加值", "Top-ups this month")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#D97706",margin:"0 0 2px"}}>${spendSum.toLocaleString()}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月消費", "Spent this month")}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#7C3AED",margin:"0 0 2px"}}>{list.filter(r => r.wallet > 0).length}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("有餘額客戶", "Customers with balance")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#3B82F6",margin:"0 0 2px"}}>{monthTopups.length}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月加值筆數", "Top-ups (count)")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#EF4444",margin:"0 0 2px"}}>${monthRefunds.reduce((s,t) => s + (Number(t.amount)||0), 0)}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月退款", "Refunds this month")}</p>
                    </div>
                  </div>
                  {/* 儲值優惠 — top-up bonus tiers (儲 $X 送 $Y). The shared,
                      correctly-priced multi-pet prepay: any of the customer's
                      pets draws from one balance at its own real service price. */}
                  <div style={{marginTop:14,padding:"12px",background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:12}}>
                    <p style={{fontSize:13,fontWeight:900,color:"#166534",margin:"0 0 2px"}}>{zhEn("🎁 儲值優惠（儲 $X 送 $Y）", "🎁 Top-up bonus (top up $X, get $Y)")}</p>
                    <p style={{fontSize:10.5,color:"#059669",fontWeight:700,margin:"0 0 8px",lineHeight:1.5}}>{zhEn("客戶加值到指定金額自動送額外餘額。多隻毛孩共用一個錢包，按每次實際服務價扣款 — 唔會少收。", "Customers automatically get bonus balance when they top up to the set amounts. Multiple pets share one wallet and are charged the actual service price each time — you never under-collect.")}</p>
                    {(() => {
                      const tiers = getWalletBonusTiers(tenant);
                      const setTiers = (next) => setState(s => ({ ...s, tenants: s.tenants.map(tt => tt.id === user.tenantId ? { ...tt, pricingConfig: { ...(tt.pricingConfig || {}), walletBonus: next } } : tt) }));
                      const upd = (i, k, v) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [k]: parseInt(v) || 0 } : t));
                      const inp = { border:"1.5px solid #A7F3D0", borderRadius:8, padding:"6px 8px", fontSize:13, fontWeight:800, textAlign:"center", outline:"none", width:"100%", boxSizing:"border-box", background:"#fff", color:"#166534" };
                      return (
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {tiers.length > 0 && (
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 30px",gap:6}}>
                              <span style={{fontSize:10,color:"#166534",fontWeight:800,textAlign:"center"}}>{zhEn("儲值 $", "Top up $")}</span>
                              <span style={{fontSize:10,color:"#166534",fontWeight:800,textAlign:"center"}}>{zhEn("送 $", "Bonus $")}</span>
                              <span />
                            </div>
                          )}
                          {tiers.map((t, i) => (
                            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr 30px",gap:6,alignItems:"center"}}>
                              <input type="number" value={t.spend ?? ""} onChange={e => upd(i, "spend", e.target.value)} placeholder="3000" style={inp} />
                              <input type="number" value={t.bonus ?? ""} onChange={e => upd(i, "bonus", e.target.value)} placeholder="300" style={inp} />
                              <button onClick={() => setTiers(tiers.filter((_, idx) => idx !== i))} style={{width:28,height:28,borderRadius:8,border:"1px solid #FCA5A5",background:"#fff",color:"#EF4444",cursor:"pointer",fontWeight:900,padding:0}}>✕</button>
                            </div>
                          ))}
                          <button onClick={() => setTiers([...tiers, { spend: 0, bonus: 0 }])} style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,background:"#fff",color:"#166534",border:"1px dashed #6EE7B7",borderRadius:10,padding:"8px",fontSize:12,fontWeight:800,cursor:"pointer",marginTop:2}}>{zhEn("＋ 新增優惠級別", "＋ Add bonus tier")}</button>
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
                    <Btn size="sm" variant="secondary" onClick={() => {
                      // CSV export — one row per transaction
                      const headers = [zhEn("日期", "Date"),zhEn("客戶", "Customer"),zhEn("類型", "Type"),zhEn("金額", "Amount"),zhEn("備註", "Note")];
                      const rows = allTx
                        .sort((a,b) => (b.date||"").localeCompare(a.date||""))
                        .map(t => [
                          (t.date || "").slice(0,10),
                          (t.customerName || "").replace(/,/g," "),
                          t.type === "topup" ? zhEn("加值", "Top-up") : t.type === "deduct" ? zhEn("扣減", "Deduction") : (t.type || ""),
                          t.amount || 0,
                          (t.note || "").replace(/,/g," "),
                        ].join(","));
                      const csv = headers.join(",") + "\n" + rows.join("\n");
                      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = "wallets_" + dateNow() + ".csv";
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                      toast(zhEn("已下載 CSV ✓", "CSV downloaded ✓"));
                    }}>{zhEn("📥 匯出 CSV", "📥 Export CSV")}</Btn>
                  </div>
                </Card>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                  {[["withBalance",zhEn("有餘額", "Has balance"),list.filter(r=>r.wallet>0).length],["lowBalance",zhEn("少於 $100", "Under $100"),list.filter(r=>r.wallet>0&&r.wallet<100).length],["noBalance",zhEn("零餘額", "Zero balance"),list.filter(r=>r.wallet===0).length],["all",zhEn("全部", "All"),list.length]].map(([v,l,n]) => {
                    const isSel = walletsAdminFilter === v;
                    return <button key={v} onClick={() => setWalletsAdminFilter(v)} style={{padding:"8px 14px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:isSel?"var(--c-primary)":"var(--c-light)",color:isSel?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap"}}>{l + " (" + n + ")"}</button>;
                  })}
                </div>
                {visible.length === 0 && <Card style={{padding:24}}><p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0}}>{zhEn("呢個分類冇客戶", "No customers in this category")}</p></Card>}
                {visible.map(r => (
                  <Card key={r.id} style={{padding:14}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:15,fontWeight:900,color:"#4A4A68",margin:0}}>{r.name}</p>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{r.phone || "—"} · {r.tx.length} {zhEn("筆交易", " transactions")}{r.lastTx ? zhEn(" · 最近 ", " · latest ") + (r.lastTx.date || "").slice(0,10) : ""}</p>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <p style={{fontSize:20,fontWeight:900,color:r.wallet===0?"#9CA3AF":"var(--c-primary)",margin:0,lineHeight:1}}>{"$" + r.wallet.toLocaleString()}</p>
                        <p style={{fontSize:9,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{zhEn("錢包餘額", "Wallet balance")}</p>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                      <Btn size="sm" variant="secondary" onClick={() => setTopupSheet(r.id)}>{zhEn("💵 加值", "💵 Top up")}</Btn>
                      <Btn size="sm" variant="secondary" onClick={() => setWalletTxSheet({ customerId: r.id })}>{zhEn("📋 交易紀錄", "📋 Transactions")}</Btn>
                      <Btn size="sm" variant="secondary" disabled={r.wallet <= 0} onClick={async () => {
                        const reason = window.prompt(zhEn("調整原因（會記入紀錄）：", "Adjustment reason (will be logged):"), "手動調整");
                        if (reason == null) return;
                        const v = parseFloat(window.prompt(zhEn("調整金額（正數加 / 負數減）：", "Adjustment amount (positive adds / negative deducts):"), "0"));
                        if (isNaN(v) || v === 0) return;
                        const adjTx = { id:"tx"+uid(), type: v > 0 ? "topup" : "deduct", amount: Math.abs(v), note: "調整：" + reason, date: dateNow() };
                        let newWallet;
                        try {
                          // Atomic, ledger-backed adjust (credit/debit RPC → the
                          // shadow trigger records it). No more last-write-wins on
                          // the wallet column.
                          const res = v > 0
                            ? await creditCustomerWallet({ userId: r.id, tenantId: user.tenantId, amount: v, tx: [adjTx] })
                            : await debitCustomerWallet({ userId: r.id, tenantId: user.tenantId, amount: -v, tx: [adjTx] });
                          newWallet = typeof res?.newWallet === "number" ? res.newWallet : Math.max(0, r.wallet + v);
                        } catch (e) {
                          toast(zhEn("調整失敗：", "Adjustment failed: ") + (e?.message || e), "error");
                          return;
                        }
                        const cu = state.users[r.id];
                        const prior = Array.isArray(cu?.walletTx) ? cu.walletTx : [];
                        const tx = [...prior, adjTx];
                        setState(s => ({
                          ...s,
                          memberships: (s.memberships || []).map(m => (m.userId === r.id && m.tenantId === user.tenantId) ? { ...m, wallet: newWallet, walletTx: tx } : m),
                          users: { ...s.users, [r.id]: { ...s.users[r.id], wallet: newWallet, walletTx: tx } },
                        }));
                        toast(zhEn("已調整 ", "Adjusted ") + (v > 0 ? "+" : "") + "$" + v);
                      }}>{zhEn("⚖️ 調整", "⚖️ Adjust")}</Btn>
                    </div>
                  </Card>
                ))}
              </div>
            );
          })()}
          {/* Wallet transactions drawer */}
          <BottomSheet open={!!walletTxSheet} onClose={() => setWalletTxSheet(null)} title={zhEn("交易紀錄", "Transactions")}>
            {walletTxSheet && (() => {
              const cust = state.users[walletTxSheet.customerId];
              const mem = (state.memberships || []).find(m => m.userId === walletTxSheet.customerId && m.tenantId === user.tenantId);
              const tx = (mem ? mem.walletTx : cust?.walletTx) || [];
              const sorted = [...tx].sort((a,b) => (b.date||"").localeCompare(a.date||""));
              return (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:0}}>{cust?.name || zhEn("客戶", "Customer")} {zhEn("· 共", "· ")} {sorted.length} {zhEn("筆", " total")}</p>
                  {sorted.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:12,margin:0}}>{zhEn("暫無交易", "No transactions yet")}</p>}
                  {sorted.map(t => {
                    const isIn = t.type === "topup";
                    return (
                      <div key={t.id} style={{padding:12,borderRadius:12,background:"#FFFDFD",border:"2px solid var(--c-light)"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0}}>{t.note || (isIn ? zhEn("加值", "Top-up") : zhEn("扣減", "Deduction"))}</p>
                            <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{(t.date || "").slice(0, 10)}</p>
                          </div>
                          <p style={{fontSize:15,fontWeight:900,color:isIn?"#10B981":"#EF4444",margin:0,whiteSpace:"nowrap"}}>{(isIn ? "+" : "-") + "$" + (Number(t.amount) || 0)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </BottomSheet>

          {/* 積分管理 — every customer's points balance, redeemed vouchers,
              adjustment audit. Mirrors 儲值卡管理 layout. */}
          {settingsSubtab === "points-admin" && (() => {
            const list = customers.map(c => {
              const mem = (state.memberships || []).find(m => m.userId === c.id && m.tenantId === user.tenantId);
              const points = mem ? (Number(mem.points) || 0) : (Number(c.points) || 0);
              const vouchers = (mem ? mem.vouchers : c.vouchers) || [];
              const used = vouchers.filter(v => v.used);
              const unused = vouchers.filter(v => !v.used);
              return { id: c.id, name: c.name || zhEn("(未命名)", "(unnamed)"), phone: c.phone || "", points, vouchers, used, unused };
            });
            const totalPoints = list.reduce((s, r) => s + r.points, 0);
            const today = dateNow();
            const startOfMonth = today.slice(0, 7) + "-01";
            const allVouchers = list.flatMap(r => (r.vouchers || []).map(v => ({ ...v, customerId: r.id, customerName: r.name })));
            const monthRedeems = allVouchers.filter(v => (v.redeemedAt || "").slice(0,10) >= startOfMonth);
            const monthRedeemedPoints = monthRedeems.reduce((s, v) => s + (Number(v.pointsCost) || 0), 0);
            const monthUsed = allVouchers.filter(v => v.used && (v.usedAt || "").slice(0,10) >= startOfMonth);
            const visible = list
              .filter(r => pointsAdminFilter === "withPoints" ? r.points > 0
                         : pointsAdminFilter === "highPoints" ? r.points >= 500
                         : pointsAdminFilter === "noPoints" ? r.points === 0
                         : true)
              .sort((a, b) => b.points - a.points);
            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <Card style={{padding:20}}>
                  <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 12px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Award size={20} color="var(--c-primary)" strokeWidth={2.5}/> {zhEn("積分管理", "Points management")} <HelpTip topic="pointsAdmin" vars={{ earnPer: Number(tenant?.loyaltyEarnPer) || 100 }} /></h3>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"var(--c-primary)",margin:"0 0 2px"}}>{totalPoints.toLocaleString()}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("流通積分", "Points in circulation")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#10B981",margin:"0 0 2px"}}>{monthRedeems.length}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月兌換筆數", "Redemptions this month")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"12px 6px",background:"var(--c-lighter)",borderRadius:12}}>
                      <p style={{fontSize:20,fontWeight:900,color:"#D97706",margin:"0 0 2px"}}>{monthRedeemedPoints.toLocaleString()}</p>
                      <p style={{fontSize:10,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月已用積分", "Points used this month")}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#7C3AED",margin:"0 0 2px"}}>{list.filter(r => r.points > 0).length}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("有積分客戶", "Customers with points")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#3B82F6",margin:"0 0 2px"}}>{list.reduce((s,r)=>s+r.unused.length,0)}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("未用兌換券", "Unused coupons")}</p>
                    </div>
                    <div style={{textAlign:"center",padding:"10px 4px",background:"#FFFDFD",borderRadius:10,border:"1px solid var(--c-light)"}}>
                      <p style={{fontSize:16,fontWeight:900,color:"#EF4444",margin:"0 0 2px"}}>{monthUsed.length}</p>
                      <p style={{fontSize:9,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("本月已使用券", "Coupons used this month")}</p>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
                    <Btn size="sm" variant="secondary" onClick={() => {
                      const headers = [zhEn("客戶", "Customer"),zhEn("電話", "Phone"),zhEn("當前積分", "Current points"),zhEn("已兌換次數", "Redemptions"),zhEn("未用券", "Unused coupons"),zhEn("已用券", "Used coupons")];
                      const rows = list.map(r => [r.name.replace(/,/g," "), r.phone, r.points, r.vouchers.length, r.unused.length, r.used.length].join(","));
                      const csv = headers.join(",") + "\n" + rows.join("\n");
                      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url; a.download = "points_" + dateNow() + ".csv";
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                      toast(zhEn("已下載 CSV ✓", "CSV downloaded ✓"));
                    }}>{zhEn("📥 匯出 CSV", "📥 Export CSV")}</Btn>
                    <Btn size="sm" variant="secondary" onClick={() => jumpToSetting("points#redeem")}>{zhEn("⚙️ 編輯兌換項目", "⚙️ Edit rewards")}</Btn>
                  </div>
                </Card>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
                  {[["withPoints",zhEn("有積分", "Has points"),list.filter(r=>r.points>0).length],["highPoints",zhEn("≥ 500 分", "≥ 500 pts"),list.filter(r=>r.points>=500).length],["noPoints",zhEn("零積分", "Zero points"),list.filter(r=>r.points===0).length],["all",zhEn("全部", "All"),list.length]].map(([v,l,n]) => {
                    const isSel = pointsAdminFilter === v;
                    return <button key={v} onClick={() => setPointsAdminFilter(v)} style={{padding:"8px 14px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:isSel?"var(--c-primary)":"var(--c-light)",color:isSel?"#fff":"var(--c-primary)",cursor:"pointer",whiteSpace:"nowrap"}}>{l + " (" + n + ")"}</button>;
                  })}
                </div>
                {visible.length === 0 && <Card style={{padding:24}}><p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0}}>{zhEn("呢個分類冇客戶", "No customers in this category")}</p></Card>}
                {visible.map(r => (
                  <Card key={r.id} style={{padding:14}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:15,fontWeight:900,color:"#4A4A68",margin:0}}>{r.name}</p>
                        <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>
                          {r.phone || "—"}
                          {r.unused.length > 0 && <span style={{color:"#D97706",marginLeft:6}}>🎟️ {r.unused.length} {zhEn("張未用", " unused")}</span>}
                          {r.used.length > 0 && <span style={{marginLeft:6}}>· {r.used.length} {zhEn("張已用", " used")}</span>}
                        </p>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <p style={{fontSize:20,fontWeight:900,color:r.points===0?"#9CA3AF":"var(--c-primary)",margin:0,lineHeight:1}}>{r.points.toLocaleString()}</p>
                        <p style={{fontSize:9,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{zhEn("積分", "Points")}</p>
                      </div>
                    </div>
                    {/* Per-customer stamp card so the shop can check progress. */}
                    {shopHasStampCard(tenant) && tenant?.stampCard?.enabled && (() => {
                      const cmem = (state.memberships || []).find(m => m.userId === r.id && m.tenantId === user.tenantId);
                      const total = tenant.stampCard.totalSlots || 10;
                      const stamps = Math.min(Number(cmem?.stamps || 0), total);
                      return (
                        <div style={{marginTop:10,background:"#FFF7ED",borderRadius:12,padding:"8px 12px",border:"1px solid #FED7AA"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                            <span style={{fontSize:11,fontWeight:900,color:"#9A3412"}}>{zhEn("📇 印花卡", "📇 Stamp card")}</span>
                            <span style={{fontSize:12,fontWeight:900,color:"#9A3412"}}>{stamps} / {total}</span>
                          </div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            {Array.from({length: total}, (_, i) => (
                              <span key={i} style={{width:18,height:18,borderRadius:99,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,background: i < stamps ? "#F97316" : "#FFE4C4"}}>{i < stamps ? "🐾" : ""}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                      <Btn size="sm" variant="secondary" onClick={() => setPointsHistorySheet({ customerId: r.id })}>{zhEn("📋 兌換紀錄", "📋 Redemptions")}</Btn>
                      <Btn size="sm" variant="secondary" onClick={async () => {
                        const reason = window.prompt(zhEn("調整原因（會記入紀錄）：", "Adjustment reason (will be logged):"), "手動調整");
                        if (reason == null) return;
                        const v = parseInt(window.prompt(zhEn("調整積分（正數加 / 負數減）：", "Adjust points (positive adds / negative deducts):"), "0"));
                        if (isNaN(v) || v === 0) return;
                        const newPts = Math.max(0, r.points + v);
                        // patchCustomerMembership persists server-side
                        // (dataSync no longer ships financial fields).
                        try {
                          await patchCustomerMembership({ userId: r.id, tenantId: user.tenantId, points: newPts });
                        } catch (e) {
                          toast(zhEn("調整失敗：", "Adjustment failed: ") + (e?.message || e), "error");
                          return;
                        }
                        setState(s => ({
                          ...s,
                          memberships: (s.memberships || []).map(m => (m.userId === r.id && m.tenantId === user.tenantId) ? { ...m, points: newPts } : m),
                          users: { ...s.users, [r.id]: { ...s.users[r.id], points: newPts } },
                        }));
                        // Log into activity for audit
                        logActivity({
                          tenantId: user.tenantId, userId: user.id,
                          actionType: "customer_updated", entityType: "profile", entityId: r.id,
                          entityName: r.name,
                          description: "手動調整積分 " + (v > 0 ? "+" : "") + v + "（" + reason + "）",
                          severity: "info", details: { delta: v, reason, newBalance: newPts },
                        }).catch(() => {});
                        toast(zhEn("已調整 ", "Adjusted ") + (v > 0 ? "+" : "") + v + zhEn(" 積分", " points"));
                      }}>{zhEn("⚖️ 調整", "⚖️ Adjust")}</Btn>
                    </div>
                  </Card>
                ))}
              </div>
            );
          })()}
          {/* Voucher / redeem history drawer */}
          <BottomSheet open={!!pointsHistorySheet} onClose={() => setPointsHistorySheet(null)} title={zhEn("兌換紀錄", "Redemptions")}>
            {pointsHistorySheet && (() => {
              const cust = state.users[pointsHistorySheet.customerId];
              const mem = (state.memberships || []).find(m => m.userId === pointsHistorySheet.customerId && m.tenantId === user.tenantId);
              const vs = (mem ? mem.vouchers : cust?.vouchers) || [];
              const sorted = [...vs].sort((a,b) => (b.redeemedAt||"").localeCompare(a.redeemedAt||""));
              return (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:0}}>{cust?.name || zhEn("客戶", "Customer")} {zhEn("· 共", "· ")} {sorted.length} {zhEn("張兌換券", " coupons")}</p>
                  {sorted.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:12,margin:0}}>{zhEn("暫無兌換紀錄", "No redemptions yet")}</p>}
                  {sorted.map(v => (
                    <div key={v.id} style={{padding:12,borderRadius:12,background:v.used?"#F3F3F8":"linear-gradient(135deg,#FFF7ED,#FFEDD5)",border:"2px "+(v.used?"solid":"dashed")+" "+(v.used?"#E5E7EB":"#FED7AA"),opacity:v.used?0.75:1}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:13,fontWeight:900,color:v.used?"#4A4A68":"#9A3412",margin:0}}>{v.label}{v.used && <span style={{fontSize:10,fontWeight:800,color:"#fff",background:"#9CA3AF",borderRadius:6,padding:"2px 6px",marginLeft:6}}>{zhEn("已使用", "Used")}</span>}</p>
                          <p style={{fontSize:11,color:v.used?"#8A8A9E":"#9A3412",margin:"3px 0 0",fontWeight:700,opacity:0.85}}>
                            {(() => {
                              const spec = v.voucher || v;
                              const t = spec.type || v.type;
                              const kind = t === "spa" || t === "free_spa" ? "SPA"
                                : t === "addon" || t === "free_addon" ? zhEn("加購", "Add-ons")
                                : t === "discount" || t === "fixed" ? zhEn("現金折扣", "Cash discount")
                                : t === "percent" ? zhEn("百分比折扣", "Percentage discount")
                                : zhEn("兌換券", "Coupon");
                              const origin = v.pointsCost
                                ? zhEn(v.pointsCost + " 積分 · 兌換 " + (v.redeemedAt || "").slice(0,10), v.pointsCost + " pts · redeemed " + (v.redeemedAt || "").slice(0,10))
                                : v.source === "stampcard"
                                  ? zhEn("印花卡贈送 · " + (v.redeemedAt || "").slice(0,10), "Stamp card reward · " + (v.redeemedAt || "").slice(0,10))
                                  : (v.redeemedAt || "").slice(0,10);
                              return kind + " · " + origin;
                            })()}
                            {v.used && v.usedAt && zhEn(" · 使用 " + v.usedAt.slice(0,10), " · used " + v.usedAt.slice(0,10))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </BottomSheet>

          {inSec("points","rules") && (() => {
            const scope = tenant?.loyaltyEarnScope || "online";
            const mergeRewards = !!tenant?.loyaltyMergeRewards;
            const setTenantField = (patch) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, ...patch } : t) }));
            return (
            <Card style={{padding:20,marginBottom:12,background:"linear-gradient(135deg,#FFF7ED,#FFFBF5)",border:"2px solid #FFE4C4"}}>
              <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 6px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("🎁 獎勵發放規則", "🎁 Reward rules")}</h3>
              <p style={{fontSize:12.5,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.55}}>{zhEn("呢度決定「邊啲預約先可以儲積分同印花」，以及合併客戶資料時點處理獎勵。", "Decide which bookings earn points and stamps, and how rewards are handled when customer records are merged.")}</p>

              <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 8px"}}>{zhEn("邊啲預約可以儲分 / 印花？", "Which bookings earn points / stamps?")}</p>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                {[["online",zhEn("只限網上自助預約", "Online self-bookings only"),zhEn("推薦", "Recommended")],["all",zhEn("所有預約（含 walk-in）", "All bookings (incl. walk-in)"),""]].map(([v,label,tag]) => {
                  const sel = scope === v;
                  return (
                    <button key={v} onClick={() => setTenantField({ loyaltyEarnScope: v })} style={{flex:1,textAlign:"left",padding:"12px 14px",borderRadius:14,border:"2px solid "+(sel?"var(--c-primary)":"var(--c-light)"),background:sel?"var(--c-lighter)":"#fff",cursor:"pointer"}}>
                      <p style={{fontSize:13,fontWeight:900,color:sel?"var(--c-primary)":"#4A4A68",margin:0}}>{label}{tag && <span style={{fontSize:10,fontWeight:900,color:"#fff",background:"#10B981",borderRadius:99,padding:"1px 7px",marginLeft:6}}>{tag}</span>}</p>
                    </button>
                  );
                })}
              </div>
              <div style={{background:"#fff",borderRadius:12,padding:"10px 12px",border:"1px solid #FFE4C4",marginBottom:16}}>
                <p style={{fontSize:11.5,color:"#92400E",margin:"0 0 4px",fontWeight:700,lineHeight:1.55}}><strong>{zhEn("只限網上自助預約：", "Online self-bookings only:")}</strong>{zhEn("只有客戶", "Points and stamps are earned only when customers ")}<strong>{zhEn("自己用網上預約", "book online themselves")}</strong>{zhEn("、完成服務後先會儲積分同印花。店員代落單嘅 walk-in 唔會儲分 — 令獎勵成為客戶自己網上預約嘅誘因。", " and the service is completed. Walk-ins entered by staff earn nothing — making rewards an incentive for customers to book online.")}</p>
                <p style={{fontSize:11.5,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.55}}><strong>{zhEn("所有預約：", "All bookings:")}</strong>{zhEn("每次完成服務都會儲分，無論網上定 walk-in。", "Points are earned every time a service is completed, online or walk-in.")}</p>
              </div>

              <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"12px 14px",borderRadius:14,background:"#fff",border:"2px solid "+(mergeRewards?"var(--c-primary)":"var(--c-light)"),cursor:"pointer"}}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:0}}>{zhEn("合併客戶資料時轉移印花及獎勵券", "Transfer stamps & reward coupons when merging customers")}</p>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700,lineHeight:1.5}}>{zhEn("關閉（預設）：合併 walk-in 記錄時只轉移錢包同積分，唔會合併印花同未用獎勵券（避免客戶一次過攞太多優惠）。開啟：連印花進度同獎勵券都一併合併。", "Off (default): merging a walk-in record transfers only wallet and points, not stamps or unused reward coupons (so customers don't get too many perks at once). On: stamp progress and reward coupons are merged too.")}</p>
                </div>
                <Toggle on={mergeRewards} onChange={v => setTenantField({ loyaltyMergeRewards: v })} />
              </label>

              <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"16px 0 4px"}}>{zhEn("用錢包 / 積分付款嘅部分賺唔賺積分？", "Do wallet / points-paid portions earn points?")}</p>
              <div style={{background:"#fff",borderRadius:14,padding:"2px 12px",border:"1px solid #FFE4C4"}}>
                <PointsEarnSettings tenant={tenant} setState={setState} sym={cur(tenant)} compact />
              </div>
            </Card>
            );
          })()}

          {/* 積分兌換項目 — owner defines what customers can swap points for.
              Stored as tenant.pointRedeemables jsonb. Read by iOS
              PointsRedeemView + customer-web 我嘅優惠券 flow. */}
          {inSec("points","redeem") && (() => {
            const list = Array.isArray(tenant?.pointRedeemables) ? tenant.pointRedeemables : [];
            const allSpas = (tenant?.services || []).filter(s => s.kind === "spa" && s.enabled !== false);
            const allAddons = (tenant?.addons || []).filter(a => a.enabled !== false);
            const setList = (next) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, pointRedeemables: next } : t) }));
            const add = (type) => {
              const id = "rd" + Math.random().toString(36).slice(2, 10);
              const base = { id, type, pointsCost: 100, label: "" };
              if (type === "discount") setList([...list, { ...base, amount: 50, label: zhEn("$50 折扣券", "$50 off coupon") }]);
              else if (type === "spa") setList([...list, { ...base, refId: allSpas[0]?.id || "", label: allSpas[0] ? zhEn("免費 " + allSpas[0].name, "Free " + allSpas[0].name) : zhEn("免費 SPA", "Free SPA") }]);
              else if (type === "addon") setList([...list, { ...base, refId: allAddons[0]?.id || allAddons[0]?.name || "", label: allAddons[0] ? zhEn("免費 " + allAddons[0].name, "Free " + allAddons[0].name) : zhEn("免費 加購", "Free add-on") }]);
            };
            const patch = (id, delta) => setList(list.map(r => r.id === id ? { ...r, ...delta } : r));
            const del = (id) => { if (window.confirm(zhEn("確定刪除呢個兌換獎賞？", "Delete this reward?"))) setList(list.filter(r => r.id !== id)); };
            return (
              <Card style={{padding:20}}>
                <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 6px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>{zhEn("🎁 積分兌換獎賞", "🎁 Points rewards")} <HelpTip topic="points" vars={{ earnPer: Number(tenant?.loyaltyEarnPer) || 100 }} /></h3>
                <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>{zhEn("客戶用積分換嘅獎賞。喺 App「積分」頁面顯示，客戶撳「兌換」即即刻扣分並收到優惠券。", "Rewards customers can redeem with points. They appear on the app's Points page; when a customer taps \"Redeem\", points are deducted instantly and they receive a coupon.")}</p>

                {list.length === 0 && (
                  <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:20,background:"var(--c-lighter)",borderRadius:12,margin:"0 0 12px"}}>{zhEn("尚未設定獎賞 — 撳下面「+ 新增」開始", "No rewards yet — tap \"+ Add\" below to start")}</p>
                )}

                <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
                  {list.map((r) => {
                    const typeLabel = r.type === "discount" ? zhEn("💰 現金折扣", "💰 Cash discount") : r.type === "spa" ? zhEn("💆 免費 SPA", "💆 Free SPA") : zhEn("➕ 免費加購", "➕ Free add-on");
                    return (
                      <div key={r.id} style={{background:"#fff",border:"2px solid var(--c-light)",borderRadius:14,padding:12,display:"flex",flexDirection:"column",gap:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,fontWeight:900,color:"#4A4A68",background:"var(--c-lighter)",padding:"3px 8px",borderRadius:99}}>{typeLabel}</span>
                          <input value={r.label || ""} onChange={e => patch(r.id, { label: e.target.value })} placeholder={zhEn("標籤（客戶會見到）", "Label (customers see this)")}
                            style={{flex:1,border:"2px solid var(--c-light)",borderRadius:10,padding:"7px 10px",fontSize:13,fontWeight:800,color:"#4A4A68",outline:"none"}} />
                          <button onClick={() => del(r.id)} style={{background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:8,padding:"6px 10px",fontSize:12,fontWeight:900,cursor:"pointer"}}>{zhEn("刪除", "Delete")}</button>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {r.type === "discount" ? (
                            <div>
                              <p style={{fontSize:10,fontWeight:900,color:"#8A8A9E",margin:"0 0 3px"}}>{zhEn("折扣金額 ($)", "Discount amount ($)")}</p>
                              <input type="number" min="1" value={r.amount || ""} onChange={e => patch(r.id, { amount: Number(e.target.value) || 0 })}
                                style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:8,padding:"6px 10px",fontSize:13,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
                            </div>
                          ) : (
                            <div>
                              <p style={{fontSize:10,fontWeight:900,color:"#8A8A9E",margin:"0 0 3px"}}>{zhEn("指定", "Choose")}{r.type === "spa" ? "SPA" : zhEn("加購", "Add-ons")}</p>
                              <select value={r.refId || ""} onChange={e => {
                                const src = r.type === "spa" ? allSpas.find(x => x.id === e.target.value) : allAddons.find(x => (x.id || x.name) === e.target.value);
                                patch(r.id, { refId: e.target.value, label: src ? zhEn("免費 " + src.name, "Free " + src.name) : r.label });
                              }} style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:8,padding:"6px 10px",fontSize:13,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box",background:"#fff"}}>
                                <option value="">{zhEn("— 揀 —", "— Choose —")}</option>
                                {(r.type === "spa" ? allSpas : allAddons).map(x => (
                                  <option key={x.id || x.name} value={x.id || x.name}>{x.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div>
                            <p style={{fontSize:10,fontWeight:900,color:"#8A8A9E",margin:"0 0 3px"}}>{zhEn("兌換所需積分", "Points needed")}</p>
                            <input type="number" min="1" value={r.pointsCost || ""} onChange={e => patch(r.id, { pointsCost: Number(e.target.value) || 0 })}
                              style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:8,padding:"6px 10px",fontSize:13,fontWeight:800,color:"#7C3AED",outline:"none",boxSizing:"border-box"}} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <Btn size="sm" variant="secondary" onClick={() => add("discount")}>{zhEn("+ 現金折扣券", "+ Cash coupon")}</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => add("addon")} disabled={allAddons.length === 0}>{zhEn("+ 免費加購", "+ Free add-on")}</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => add("spa")} disabled={allSpas.length === 0}>{zhEn("+ 免費 SPA", "+ Free SPA")}</Btn>
                </div>
                {(allSpas.length === 0 && allAddons.length === 0) && (
                  <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"8px 0 0"}}>{zhEn("💡 先喺「服務」加 SPA / 加購服務，先可以做免費 SPA / 加購獎賞。", "💡 Add SPA / add-on services under \"Services\" first, then you can offer free SPA / add-on rewards.")}</p>
                )}
              </Card>
            );
          })()}

          {inSec("points","stamp") && <Card style={{padding:20}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 6px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Award size={20} color="var(--c-primary)" strokeWidth={2.5} /> {zhEn("印花卡設定", "Stamp card settings")} <HelpTip topic="points" vars={{ earnPer: Number(tenant?.loyaltyEarnPer) || 100 }} /></h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 14px",fontWeight:700,lineHeight:1.5}}>{zhEn("每次預約完成（done）自動為客戶加一個印章。達到指定格數時自動發送獎勵券。客戶可喺個人頁面睇到自己嘅印花卡進度。", "A stamp is added automatically each time a booking is completed. A reward coupon is sent when a milestone is reached. Customers can see their stamp card progress on their profile page.")}</p>
            {(() => {
              // 電子積分卡 is a 專業版/旗艦版 feature — gate the whole editor.
              const stampAllowed = !!getPlans(state.platformConfig)[tenant?.plan || "free"]?.features?.stampCard;
              if (!stampAllowed) return (
                <div style={{background:"#FFFBEB",border:"2px solid #FEF3C7",borderRadius:16,padding:"16px 18px",display:"flex",gap:12,alignItems:"center"}}>
                  <Crown size={20} color="#D97706" strokeWidth={2.5} />
                  <div>
                    <p style={{fontSize:14,fontWeight:900,color:"#92400E",margin:"0 0 2px"}}>{zhEn("電子積分卡需要專業版或以上", "The e-stamp card needs the Pro plan or above")}</p>
                    <p style={{fontSize:12.5,color:"#B45309",margin:0,fontWeight:700,lineHeight:1.5}}>{zhEn("升級即可開啟「集印換禮遇」，自動儲分留住熟客。", "Upgrade to unlock \"collect stamps, get rewards\" and keep regulars coming back automatically.")}</p>
                  </div>
                </div>
              );
              const sc = tenant?.stampCard || { enabled: false, totalSlots: 10, milestones: [], resetOnFull: true };
              const allSpas = (tenant?.services || []).filter(s => s.kind === "spa");
              const allAddons = tenant?.addons || [];
              const update = (patch) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, stampCard: { ...sc, ...patch } } : t) }));
              const updateMilestones = (next) => update({ milestones: next });
              // Owner used to type a free-text reward label, but the actual
              // redemption pulled from voucher.refId — so a label of "免費刷牙"
              // paired with refId pointing at 解結 would silently comp 解結.
              // We now derive the label from the voucher shape so they can
              // never disagree. Called every time type / refId / amount
              // changes, and applied to both m.rewardLabel + voucher.label
              // (the booking UI reads voucher.label).
              // Drop everything from the first separator onward (dash,
              // colon, paren) and cap length. The owner is expected to
              // rename SPAs to descriptive names ("舒緩 SPA" / "去味 SPA")
              // rather than relying on -A/-B/-C variants for the tile.
              const shortName = (full) => {
                const head = String(full || "").split(/[\-：(（]/)[0].trim();
                return head.length > 10 ? head.slice(0, 10) + "…" : head;
              };
              const deriveRewardLabel = (vc) => {
                const t = vc?.type || "discount";
                if (t === "discount") return zhEn("$" + (Number(vc?.amount) || 0) + " 折扣券", "$" + (Number(vc?.amount) || 0) + " off coupon");
                if (t === "percent")  return zhEn((Number(vc?.amount) || 0) + "% 折扣券", (Number(vc?.amount) || 0) + "% off coupon");
                if (t === "free_spa") {
                  const sp = (tenant?.services || []).find(s => s.id === vc?.refId);
                  return sp ? zhEn("免費 " + shortName(sp.name), "Free " + shortName(sp.name)) : zhEn("免費 SPA", "Free SPA");
                }
                if (t === "free_addon") {
                  const ad = (tenant?.addons || []).find(a => a.id === vc?.refId);
                  return ad ? zhEn("免費 " + shortName(ad.name), "Free " + shortName(ad.name)) : zhEn("免費加購", "Free add-on");
                }
                return zhEn("兌換券", "Coupon");
              };
              const addMs = () => {
                const vc = { type: "discount", amount: 50 };
                updateMilestones([...(sc.milestones || []), { id: "ms"+uid(), atSlot: Math.min((sc.totalSlots||10), (sc.milestones||[]).length ? Math.max(...sc.milestones.map(m => m.atSlot||0)) + 5 : 5), rewardLabel: deriveRewardLabel(vc), voucher: { ...vc, label: deriveRewardLabel(vc) } }]);
              };
              const patchMs = (idx, k, v) => updateMilestones((sc.milestones || []).map((m, i) => i === idx ? { ...m, [k]: v } : m));
              const patchMsVoucher = (idx, k, v) => updateMilestones((sc.milestones || []).map((m, i) => {
                if (i !== idx) return m;
                const nextVc = { ...(m.voucher || {}), [k]: v };
                const lbl = deriveRewardLabel(nextVc);
                return { ...m, rewardLabel: lbl, voucher: { ...nextVc, label: lbl } };
              }));
              const delMs = (idx) => updateMilestones((sc.milestones || []).filter((_, i) => i !== idx));
              const sorted = [...(sc.milestones || [])].sort((a, b) => (a.atSlot || 0) - (b.atSlot || 0));
              return <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--c-lighter)",padding:"10px 14px",borderRadius:14,marginBottom:12}}>
                  <p style={{margin:0,fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("啟用印花卡", "Enable stamp card")}</p>
                  <Toggle on={sc.enabled} onChange={v => update({ enabled: v })} />
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                  <Input label={zhEn("一張卡格數", "Stamps per card")} type="number" value={sc.totalSlots || 10} onChange={e => {
                    const nextTotal = Math.max(1, parseInt(e.target.value) || 10);
                    // Also clamp any existing milestone whose atSlot now
                    // exceeds the new total — otherwise the reward is
                    // unreachable and silently never fires.
                    const nextMs = (sc.milestones || []).map(m => ({ ...m, atSlot: Math.max(1, Math.min(nextTotal, Number(m.atSlot) || 1)) }));
                    update({ totalSlots: nextTotal, milestones: nextMs });
                  }} hint={zhEn("例：10 = 集 10 個印一張", "e.g. 10 = collect 10 stamps per card")} />
                  <div>
                    <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("裝滿後", "When full:")}</p>
                    <div style={{background:"var(--c-lighter)",padding:"14px 16px",borderRadius:20,fontWeight:700,color:"#4A4A68",fontSize:14}}>{zhEn("自動重設，開新卡", "resets automatically and starts a new card")}</div>
                  </div>
                </div>
                {/* Live preview — faithful copy of the customer-side stamp
                    tile design so the owner sees exactly what the customer
                    will see while editing milestones. Demo state: 2 stamps
                    earned out of totalSlots. */}
                <div style={{padding:14,background:"linear-gradient(135deg,#FFF7ED,#FFEDD5)",borderRadius:16,marginBottom:14,border:"2px dashed #FB923C"}}>
                  <p style={{fontSize:11,fontWeight:800,color:"#9A3412",margin:"0 0 10px"}}>{zhEn("客戶睇到嘅樣子（已集 2 個印示範）", "What customers see (demo with 2 stamps)")}</p>
                  <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(sc.totalSlots||10, 5)}, 1fr)`,gap:8}}>
                    {Array.from({length: sc.totalSlots || 10}, (_, i) => {
                      const slot = i + 1;
                      const filled = slot <= 2;
                      const ms = sorted.find(m => m.atSlot === slot);
                      const reached = ms && slot <= 2;
                      const labelLen = ms?.rewardLabel?.length || 0;
                      // clamp() so the tile font grows on iPad (≥768px) but
                      // stays compact on phones. Min: phone-fit. Max: tablet.
                      // Three buckets by label length so long labels still
                      // shrink to fit.
                      const fontPx = ms
                        ? (labelLen > 10 ? "clamp(11px, 1.6vw, 18px)" : labelLen > 7 ? "clamp(12px, 1.9vw, 20px)" : labelLen > 4 ? "clamp(14px, 2.2vw, 24px)" : "clamp(16px, 2.6vw, 28px)")
                        : "clamp(16px, 2.6vw, 28px)";
                      return (
                        <div key={i} style={{
                          aspectRatio:"1",
                          borderRadius:14,
                          padding:"6px 4px",
                          background: filled
                            ? (ms ? "linear-gradient(135deg,#34D399,#10B981)" : "linear-gradient(135deg,#FB923C,#F97316)")
                            : (ms ? "#fff" : "#FFF7ED"),
                          border: ms
                            ? (filled ? "2px solid #10B981" : "2px dashed #10B981")
                            : (filled ? "2px solid #F97316" : "2px dashed #FED7AA"),
                          display:"flex",
                          flexDirection:"column",
                          alignItems:"center",
                          justifyContent:"space-between",
                          boxShadow: filled
                            ? (ms ? "0 6px 18px rgba(16,185,129,0.45)" : "0 4px 12px rgba(249,115,22,0.4)")
                            : "inset 0 2px 4px rgba(0,0,0,0.04)",
                          position:"relative",
                          overflow:"hidden",
                        }}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
                            <span style={{fontSize:9,fontWeight:900,color: filled ? "rgba(255,255,255,0.85)" : (ms ? "#10B981" : "#FB923C"),letterSpacing:0.5}}>#{slot}</span>
                            {filled && <span style={{fontSize:"clamp(13px, 2vw, 20px)",color:"#fff"}}>{reached ? "🎉" : "✓"}</span>}
                          </div>
                          {ms ? (
                            <div style={{textAlign:"center",lineHeight:1.15,padding:"0 1px",flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                              <div style={{fontSize:"clamp(13px, 1.8vw, 20px)",marginBottom:2,lineHeight:1}}>🎁</div>
                              {(() => {
                                const lbl = ms.rewardLabel || deriveRewardLabel(ms.voucher) || "—";
                                const [line1, line2] = splitMilestoneLabel(lbl);
                                const sty = {margin:0,fontSize:fontPx,fontWeight:900,color: filled ? "#fff" : "#065F46",wordBreak:"break-word",textShadow: filled ? "0 1px 2px rgba(0,0,0,0.2)" : "none",lineHeight:1.2};
                                return line2 ? (<><p style={{...sty,opacity:0.95}}>{line1}</p><p style={sty}>{line2}</p></>) : (<p style={sty}>{line1}</p>);
                              })()}
                            </div>
                          ) : (
                            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                              {filled ? <span style={{fontSize:18}}>🐾</span> : <span style={{fontSize:14,fontWeight:900,color:"#FB923C"}}>{slot}</span>}
                            </div>
                          )}
                          <span style={{height:6}} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p style={{fontSize:13,color:"#4A4A68",fontWeight:800,margin:"0 0 8px"}}>{zhEn("獎勵里程碑", "Reward milestones")}</p>
                {sorted.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",background:"var(--c-lighter)",borderRadius:14,margin:0}}>{zhEn("未設定獎勵", "No rewards set")}</p>}
                {sorted.map((m, idx) => {
                  const realIdx = (sc.milestones || []).findIndex(x => x.id === m.id);
                  const voucher = m.voucher || {};
                  const voucherType = voucher.type || "discount";
                  const refOptions = voucherType === "free_spa" ? allSpas : voucherType === "free_addon" ? allAddons : [];
                  return <div key={m.id} style={{padding:14,background:"#FFFDFD",borderRadius:16,border:"2px solid var(--c-light)",marginBottom:10}}>
                    <div style={{display:"grid",gridTemplateColumns:"110px 1fr auto",gap:8,alignItems:"end",marginBottom:10}}>
                      <Input label={zhEn("第幾印", "At stamp #")} type="number" value={m.atSlot || 0} onChange={e => patchMs(realIdx, "atSlot", Math.max(1, Math.min(sc.totalSlots || 10, parseInt(e.target.value) || 1)))} />
                      {/* Auto-derived from voucher type + amount/refId below.
                          Read-only so the displayed name can never diverge
                          from the actual comp-target (the old "type whatever"
                          field caused 免費刷牙 to redeem as 免費解結 when the
                          refId dropdown pointed elsewhere). */}
                      <div>
                        <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("獎勵名稱（自動）", "Reward name (auto)")}</p>
                        <div style={{background:"var(--c-lighter)",padding:"14px 16px",borderRadius:20,fontWeight:800,color:"#4A4A68",fontSize:14,minHeight:20}}>{m.rewardLabel || deriveRewardLabel(m.voucher) || "—"}</div>
                      </div>
                      <button onClick={() => delMs(realIdx)} title={zhEn("刪除", "Delete")} style={{background:"#FEF2F2",border:"none",borderRadius:10,padding:"8px 10px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={14} color="#EF4444" strokeWidth={2.5}/></button>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"150px 1fr",gap:8,alignItems:"end"}}>
                      <Sel label={zhEn("獎勵類型", "Reward type")} value={voucherType} onChange={e => patchMsVoucher(realIdx, "type", e.target.value)}>
                        <option value="discount">{zhEn("$ 現金折扣券", "$ Cash coupon")}</option>
                        <option value="percent">{zhEn("% 折扣券", "% off coupon")}</option>
                        <option value="free_spa">{zhEn("免費 SPA 服務", "Free SPA service")}</option>
                        <option value="free_addon">{zhEn("免費加購", "Free add-on")}</option>
                      </Sel>
                      {(voucherType === "discount" || voucherType === "percent") ? (
                        <Input label={voucherType === "discount" ? zhEn("折扣金額 ($)", "Discount amount ($)") : zhEn("折扣百分比 (%)", "Discount percentage (%)")} type="number" value={voucher.amount || 0} onChange={e => patchMsVoucher(realIdx, "amount", parseInt(e.target.value) || 0)} />
                      ) : (
                        <Sel label={zhEn("服務", "Service")} value={voucher.refId || ""} onChange={e => patchMsVoucher(realIdx, "refId", e.target.value)}>
                          <option value="">{zhEn("請選擇", "Select")}</option>
                          {refOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </Sel>
                      )}
                    </div>
                  </div>;
                })}
                <Btn variant="secondary" onClick={addMs} style={{width:"100%",marginTop:6}}><Plus size={16} strokeWidth={3}/> {zhEn("新增獎勵里程碑", "Add reward milestone")}</Btn>
              </>;
            })()}
          </Card>}

          {/* 活動記錄 subtab — moved into settings per owner feedback */}
          {settingsSubtab === "activity" && (
            <Card style={{padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <h3 style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68"}}>{zhEn("📋 操作紀錄", "📋 Activity log")}</h3>
                <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("共", "Total")} {activityLogs.length} {zhEn("項", "lines")}</p>
              </div>
              {activityLoading ? (
                <p style={{fontSize:13,color:"#AFAFC4",margin:0,textAlign:"center",padding:"30px 0",fontWeight:700}}>{zhEn("載入中…", "Loading…")}</p>
              ) : activityLogs.length === 0 ? (
                <p style={{fontSize:13,color:"#AFAFC4",margin:0,textAlign:"center",padding:"30px 0",fontWeight:700}}>{zhEn("暫無紀錄", "No records yet")}</p>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:520,overflowY:"auto"}}>
                  {activityLogs.slice(0, 100).map(log => {
                    const userName = state.users[log.userId]?.name || zhEn("系統", "System");
                    const timeStr = log.createdAt ? new Date(log.createdAt).toLocaleString(dateLoc(), { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                    const sevColor = log.severity === "warning" ? "#D97706" : log.severity === "error" ? "#DC2626" : "#6B7280";
                    return (
                      <div key={log.id} style={{padding:"10px 12px",borderRadius:12,background:"#FFFDFD",border:"1px solid var(--c-light)"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:4}}>
                          <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0,flex:1}}>{log.description || log.actionType}</p>
                          <span style={{fontSize:10,padding:"2px 6px",borderRadius:99,background:sevColor+"20",color:sevColor,fontWeight:800,flexShrink:0}}>{log.severity || "info"}</span>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#8A8A9E",fontWeight:700}}>
                          <span>👤 {userName}</span>
                          <span>🕐 {timeStr}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}
          </div>}
        </div>}
      </div>

      {/* ── SAVE STATUS / OFFLINE BAR ──────────────────────────────────
          Pinned above the bottom nav. Always shown on settings tab; on
          other tabs only shown when there's an active error/saving/offline
          state worth surfacing — so the owner sees problems immediately
          without the bar being permanently in the way. */}
      {(() => {
        const st = syncStatus || { state: "idle" };
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        // Only show transiently (saving / error / offline / just-saved) — see
        // saveBarShow effect. No longer permanently pinned on the settings tab.
        if (!saveBarShow) return null;
        const secAgo = st.lastSavedAt ? Math.max(1, Math.round((Date.now() - st.lastSavedAt) / 1000)) : null;
        const palette = offline
          ? { bg:"#FEE2E2", border:"#FCA5A5", fg:"#991B1B", icon:"📡", text:zhEn("離線中 · 變更會於連線後同步", "Offline · changes sync when you reconnect") }
          : st.state === "saving"
          ? { bg:"#FEF3C7", border:"#FCD34D", fg:"#92400E", icon:"⏳", text:zhEn("正在儲存…", "Saving…") }
          : st.state === "error"
            ? { bg:"#FEE2E2", border:"#FCA5A5", fg:"#991B1B", icon:"❌", text:zhEn("儲存失敗：" + (st.error || "請重試"), "Save failed: " + (st.error || "please retry")) }
            : st.state === "saved"
              ? { bg:"#D1FAE5", border:"#86EFAC", fg:"#065F46", icon:"✓", text:zhEn("已儲存" + (secAgo ? "（" + secAgo + " 秒前）" : ""), "Saved" + (secAgo ? " (" + secAgo + "s ago)" : "")) }
              : { bg:"#F3F4F6", border:"#E5E7EB", fg:"#6B7280", icon:"💾", text:zhEn("變更會自動儲存", "Changes save automatically") };
        return (
          <div style={{position:"fixed",bottom:"calc(82px + env(safe-area-inset-bottom, 0px))",left:0,right:0,zIndex:25,display:"flex",justifyContent:"center",pointerEvents:"none",padding:"0 16px"}}>
            <div style={{pointerEvents:"auto",display:"flex",alignItems:"center",gap:10,background:palette.bg,border:"2px solid "+palette.border,borderRadius:99,padding:"8px 16px",boxShadow:"0 8px 24px rgba(0,0,0,0.08)",maxWidth:560,width:"100%",justifyContent:"space-between"}}>
              <span style={{display:"flex",alignItems:"center",gap:8,color:palette.fg,fontWeight:800,fontSize:13,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                <span style={{fontSize:16,flexShrink:0}}>{palette.icon}</span>
                <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{palette.text}</span>
              </span>
              {/* Manual save button — flushes any pending changes immediately.
                  Most edits already auto-save; this is for owner reassurance. */}
              <button
                onClick={() => {
                  // Trigger an explicit no-op setState — this fires the sync
                  // pipeline and updates the status. If nothing changed it's
                  // a no-op but the UI still flashes "saving → saved".
                  setState(s => ({ ...s, _saveBump: (s._saveBump || 0) + 1 }));
                }}
                disabled={st.state === "saving"}
                style={{flexShrink:0,padding:"6px 14px",borderRadius:99,background:palette.fg,color:"#fff",border:"none",fontWeight:900,fontSize:12,cursor:st.state === "saving" ? "not-allowed" : "pointer",opacity:st.state === "saving" ? 0.5 : 1}}
              >
                {st.state === "saving" ? zhEn("處理中", "Processing") : zhEn("立即儲存", "Save now")}
              </button>
            </div>
          </div>
        );
      })()}

      <ReportSheet open={!!reportSheet} aptId={reportSheet} state={state} setState={setState} onClose={() => setReportSheet(null)} />
      <ReportTemplateEditor
        open={showReportTemplateEditor}
        tenant={tenant}
        onClose={() => setShowReportTemplateEditor(false)}
        onSave={async (fields) => {
          const cleaned = Array.isArray(fields) ? fields : [];
          setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === user.tenantId ? { ...t, reportTemplate: cleaned } : t) }));
          try { await patchTenant({ id: user.tenantId, reportTemplate: cleaned }); toast(zhEn("報告欄位已儲存 ✅", "Report fields saved ✅")); }
          catch (e) { toast(zhEn("儲存失敗：", "Save failed: ") + (e?.message || ""), "error"); }
          setShowReportTemplateEditor(false);
        }}
      />
      <BottomSheet open={!!photoShareSheet} onClose={() => setPhotoShareSheet(null)} title={zhEn("📸 前後對比照 / 美容紀念冊", "📸 Before / after photos & grooming album")} z={300}>
        {photoShareSheet && (() => {
          const apt = (state.appointments || []).find(a => a.id === photoShareSheet.aptId);
          if (!apt) return <p style={{padding:20,textAlign:"center",color:"#8A8A9E",fontWeight:700}}>{zhEn("找不到此預約", "Booking not found")}</p>;
          const pet = state.pets[apt.petId];
          const cust = state.users[apt.customerId];
          // Reuse apt.report.photos (existing field) instead of a parallel
          // public_photos column. before/after entries belong to the same
          // photo array that the health-report sheet already manages —
          // there's no good reason to keep two copies. `currentBA` is the
          // filtered list this sheet manages; "avatar" entries stay
          // untouched so the CRM portrait isn't disturbed.
          const allReportPhotos = Array.isArray(apt.report?.photos) ? apt.report.photos : [];
          const currentBA = allReportPhotos.filter(p => p.category === "before" || p.category === "after");
          const token = apt.publicPhotoToken || null;
          const origin = (typeof window !== "undefined" && window.location.origin) || "";
          const shareUrl = token ? (origin + "/album/" + token) : "";
          // All writes below go through queueAptWrite and spread `cur` (the
          // latest written apt), never the render-time `apt`, so a token
          // write can't wipe photos saved a moment earlier and vice versa.
          const ensureToken = () => token ? Promise.resolve(token) : queueAptWrite(apt, async (cur, setCur) => {
            if (cur.publicPhotoToken) return cur.publicPhotoToken;
            // 14-char random base36 — long enough that brute-forcing the
            // unique index is impractical, short enough to fit in a QR /
            // SMS body.
            const fresh = "al" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
            try {
              // Spread the full apt object — supabase-js upsert runs the
              // INSERT phase before ON CONFLICT, so a partial row like
              // { id, publicPhotoToken } trips NOT NULL on tenant_id /
              // date / time.
              await upsertAppointment({ ...cur, publicPhotoToken: fresh });
              setCur({ ...cur, publicPhotoToken: fresh });
              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === apt.id ? { ...a, publicPhotoToken: fresh } : a) }));
              return fresh;
            } catch (e) { toast(zhEn("產生連結失敗：", "Failed to create link: ") + (e?.message || e), "error"); return null; }
          });
          // Rebuilds report.photos (avatar + before/after subset) from the
          // latest apt and persists it. Bootstraps `report` for apts that
          // don't have one yet (shops with health-report disabled).
          const writePhotos = (mutateBA) => queueAptWrite(apt, async (cur, setCur) => {
            const all = Array.isArray(cur.report?.photos) ? cur.report.photos : [];
            const isBA = p => p && (p.category === "before" || p.category === "after");
            const nextBA = mutateBA(all.filter(isBA));
            if (!nextBA) return;
            const nextReport = { ...(cur.report || {}), photos: [...all.filter(p => !isBA(p)), ...nextBA] };
            try {
              await upsertAppointment({ ...cur, report: nextReport });
              setCur({ ...cur, report: nextReport });
              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === apt.id ? { ...a, report: nextReport } : a) }));
            } catch (e) { toast(zhEn("儲存照片失敗", "Failed to save photo"), "error"); throw e; }
          });
          const addPhoto = async (photoObj) => {
            try {
              await writePhotos(ba => {
                if (ba.length >= MAX_APT_BA_PHOTOS) { toast(zhEn("每個預約最多 " + MAX_APT_BA_PHOTOS + " 張照片", "Max " + MAX_APT_BA_PHOTOS + " photos per booking"), "warning"); return null; }
                return [...ba, photoObj];
              });
              // Auto-mint token on first photo so the owner can share
              // immediately without an extra tap.
              await ensureToken();
            } catch { /* writePhotos already toasted */ }
          };
          const removePhoto = async (id) => {
            try { await writePhotos(ba => ba.filter(p => (p.id || p.url) !== id)); } catch { /* toasted */ }
          };
          const beforeCount = currentBA.filter(p => p.category === "before").length;
          const afterCount = currentBA.length - beforeCount;
          const sendLink = async () => {
            const t = await ensureToken();
            if (!t) return;
            const url = origin + "/album/" + t;
            const msg = (pet?.name || "毛孩") + " 的美容紀念冊已準備好啦 🐾✨\n" + url;
            // Open WhatsApp DIRECTLY to the customer with the link prefilled, if
            // we have their phone. wa.me needs full international digits.
            const digits = ((cust?.countryCode || "") + (cust?.phone || "")).replace(/\D/g, "");
            if (digits.length >= 8) {
              const wa = "https://wa.me/" + digits + "?text=" + encodeURIComponent(msg);
              try { window.open(wa, "_blank"); } catch { try { window.location.href = wa; } catch { /* ignore */ } }
              return;
            }
            // No phone on file → fall back to the native share sheet / copy.
            try {
              if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
                await navigator.share({ title: tenant?.name || "PettoBook", text: msg, url });
              } else {
                navigator.clipboard?.writeText(url);
                toast(zhEn("已複製連結 ✓ 可貼到 WhatsApp / Messenger / IG 傳俾客戶", "Link copied ✓ paste it into WhatsApp / Messenger / IG to send to the customer"));
              }
            } catch (e) { /* user cancelled */ }
          };
          // Build + save/share the watermarked before-after composite image so
          // the shop (or customer) can post it online to promote the brand.
          const downloadComposite = async () => {
            const hasPair = currentBA.some(p => p.category === "before") && currentBA.some(p => p.category === "after");
            if (!hasPair) { toast(zhEn("需要一張「美容前」同一張「美容後」照片", "You need one \"before\" and one \"after\" photo"), "error"); return; }
            try {
              const prefix = state.platformConfig?.bookingUrlPrefix || ((state.platformConfig?.domain || "pettobook.com") + "/shop/");
              const bookingUrl = tenant?.bookingSlug ? (prefix + tenant.bookingSlug) : "";
              // Multi-pair grid — mirrors the on-screen preview layout so
              // what the shop sees IS what they download.
              const blob = await buildBeforeAfterGrid({
                photos: currentBA, shopName: tenant?.name || "", petName: pet?.name || "",
                bookingUrl, phone: tenant?.phone || "", platformDomain: state.platformConfig?.domain || "pettobook.com",
                hideBranding: tenantHidesBranding(tenant),
              });
              await shareImageBlob({ blob, text: (pet?.name ? pet.name + " " : "") + "美容前後對比 ✨ " + (tenant?.name || ""), filename: (pet?.name || "pet") + "-before-after.jpg" });
            } catch (e) { toast(zhEn("下載失敗：", "Download failed: ") + (e?.message || e), "error"); }
          };
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14,paddingBottom:8}}>
              <div style={{background:"linear-gradient(135deg,#FAF5FF,#EDE9FE)",border:"2px solid #DDD6FE",borderRadius:14,padding:"10px 14px"}}>
                <p style={{fontSize:12,fontWeight:800,color:"#5B21B6",margin:0,lineHeight:1.5}}>
                  {zhEn("上載「美容前」+「美容後」照片，建立一個無需登入嘅分享連結俾客戶。客戶可即時瀏覽 + 分享到社交媒體。", "Upload \"before\" + \"after\" photos to create a no-login share link for the customer. They can view it instantly and share it to social media.")}
                </p>
              </div>
              {/* Upload sections — two stacked PhotoUpload widgets, each
                  tagged with kind so BeforeAfterGallery pairs them up. */}
              <div>
                <p style={{fontSize:13,fontWeight:900,color:"#DC2626",margin:"0 0 8px"}}>{zhEn("美容前 BEFORE", "BEFORE")}</p>
                <PhotoUpload
                  photos={currentBA.filter(p => p.category === "before")}
                  onAdd={(p) => addPhoto({ ...p })}
                  onRemove={(id) => removePhoto(id)}
                  maxPhotos={MAX_APT_BA_PHOTOS - afterCount}
                  category="before"
                  tenantId={user.tenantId}
                  scope={"album-" + apt.id}
                />
              </div>
              <div>
                <p style={{fontSize:13,fontWeight:900,color:"#059669",margin:"0 0 8px"}}>{zhEn("美容後 AFTER", "AFTER")}</p>
                <PhotoUpload
                  photos={currentBA.filter(p => p.category === "after")}
                  onAdd={(p) => addPhoto({ ...p })}
                  onRemove={(id) => removePhoto(id)}
                  maxPhotos={MAX_APT_BA_PHOTOS - beforeCount}
                  category="after"
                  tenantId={user.tenantId}
                  scope={"album-" + apt.id}
                />
              </div>
              {/* Preview + share */}
              {currentBA.length > 0 && (
                <div style={{background:"#FFFDFD",borderRadius:16,padding:14,border:"2px solid var(--c-border)"}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 10px"}}>{zhEn("📲 客戶會睇到嘅樣", "📲 What the customer sees")}</p>
                  <BeforeAfterGallery photos={currentBA} onPhotoTap={(ph) => setPhotoLightbox({ url: ph.url, name: pet?.name })} />
                </div>
              )}
              {currentBA.length === 0 ? (
                <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:"4px 0 0"}}>{zhEn("未上載照片。先添加幾張，分享連結會自動產生。", "No photos yet. Add a few and the share link is created automatically.")}</p>
              ) : (
                <div style={{background:"linear-gradient(135deg,#FAF5FF,#EDE9FE)",border:"2px solid #DDD6FE",borderRadius:14,padding:"12px 14px"}}>
                  <p style={{fontSize:11,fontWeight:800,color:"#5B21B6",margin:"0 0 6px"}}>{zhEn("客戶嘅專屬分享連結", "Customer's private share link")}</p>
                  <div style={{display:"flex",gap:8,alignItems:"center",background:"#fff",borderRadius:10,padding:"8px 12px",marginBottom:10}}>
                    <p style={{fontSize:11,fontFamily:"ui-monospace,SF Mono,monospace",color:"#4A4A68",margin:0,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shareUrl || zhEn("(產生中…)", "(creating…)")}</p>
                    <Btn size="sm" variant="secondary" onClick={async () => {
                      const t = await ensureToken();
                      if (!t) return;
                      try { navigator.clipboard?.writeText(origin + "/album/" + t); toast(i18nT("common.copied")); } catch { toast(zhEn("複製失敗", "Copy failed"), "error"); }
                    }}>{i18nT("common.copy")}</Btn>
                  </div>
                  <Btn onClick={sendLink} style={{width:"100%",background:"linear-gradient(135deg,#25D366,#128C7E)",color:"#fff"}}>
                    {zhEn("💬 用 WhatsApp 傳俾", "💬 Send via WhatsApp")} {cust?.name || zhEn("客戶", "Customer")}
                  </Btn>
                  <Btn variant="secondary" onClick={downloadComposite} style={{width:"100%",marginTop:8}}>
                    {zhEn("⬇️ 下載前後對比相（可出 IG / FB 宣傳）", "⬇️ Download before/after image (for IG / FB promo)")}
                  </Btn>
                </div>
              )}
              <Btn variant="ghost" onClick={() => setPhotoShareSheet(null)} style={{width:"100%"}}>{zhEn("完成", "Done")}</Btn>
            </div>
          );
        })()}
      </BottomSheet>

      {/* PaymentSheet — 2-step checkout: (1) line items + discount,
          (2) payment method (Cash / Card / FPS / Wallet). On confirm
          we set paid + append a payment_log entry so 收款紀錄 tab
          reflects what was paid, by whom, how, and when. */}
      <BottomSheet open={!!paymentSheet} onClose={() => {
        // M4: drop any empty surcharge rows the owner appended via the
        // "+ 加附加費" button but never filled in. Without this, every
        // accidental tap leaves a {name:"", amount:0} zombie row on the
        // apt that clutters the breakdown card and receipt.
        if (paymentSheet?.aptId) {
          const apt = state.appointments.find(a => a.id === paymentSheet.aptId);
          if (apt) {
            const cleaned = (apt.surcharges || []).filter(s => (s?.name || "").trim() !== "" || Number(s?.amount) > 0);
            if (cleaned.length !== (apt.surcharges || []).length) {
              setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === apt.id ? { ...a, surcharges: cleaned } : a) }));
              setEditApt(prev => prev?.id === apt.id ? { ...prev, surcharges: cleaned } : prev);
            }
          }
        }
        setPaymentSheet(null);
      }} title={i18nT("pay.title")} z={300}>
        {paymentSheet && (() => {
          const apt = state.appointments.find(a => a.id === paymentSheet.aptId);
          if (!apt) return <p>{i18nT("pay.notFound")}</p>;
          // Currency symbol resolved once for the whole sheet so all
          // amounts honour tenant.currency (HK$ / US$ / ¥ / etc).
          const cs = cur(tenant);
          const pet = state.pets[apt.petId];
          const surTot = (apt.surcharges || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
          // C1: when the booking redeems a package, basePrice is covered
          // by the package; charge ONLY addons + surcharges. Mirror the
          // same proportional-discount logic aptRevenue uses so a $50
          // discount on a $660 booking with $480 package + $180 addons
          // takes $13.6 ($50 × 180/660) off the cash portion only.
          const usesPackage = !!apt.customerPackageId;
          const cashBase = usesPackage
            ? (Number(apt.addonPrice) || 0) + surTot
            : (Number(apt.basePrice) || 0) + (Number(apt.addonPrice) || 0) + surTot;
          const fullSubtotal = (Number(apt.basePrice) || 0) + (Number(apt.addonPrice) || 0) + surTot;
          const draft = paymentSheet.discount !== undefined ? paymentSheet.discount : (apt.discount || null);
          const dPct = draft?.type === "percent" ? (Number(draft.amount) || 0) : (Number(paymentSheet._dPctMem) || 0);
          const dFix = draft?.type === "fixed" ? (Number(draft.amount) || 0) : (Number(paymentSheet._dFixMem) || 0);
          const fullDisc = draft ? (draft.type === "percent" ? Math.round(fullSubtotal * (Number(draft.amount) || 0) / 100) : (Number(draft.amount) || 0)) : 0;
          // Prorate discount onto the cash portion when a package is in use
          // (matches aptRevenue's calc so EOD + receipt agree).
          const discVal = usesPackage && fullSubtotal > 0
            ? Math.round(fullDisc * cashBase / fullSubtotal)
            : fullDisc;
          const subtotal = cashBase; // what's shown as "小計" in the sheet
          const total = Math.max(0, subtotal - discVal);
          const deposit = Number(apt.deposit) || 0;
          const balanceDue = Math.max(0, total - deposit);
          const step = paymentSheet.step || "details";
          // C3: once paid the sheet should reflect that — disable confirm
          // and show a stamped-paid state instead of letting the owner
          // append duplicate log entries.
          const alreadyPaid = !!apt.paid;
          if (step === "details") {
            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:99,background:"var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{petEmoji(pet)}</div>
                  <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:0,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(pet?.name || i18nT("pay.pet")) + " · " + (apt.service || "")}</p>
                </div>
                <div style={{borderTop:"1px solid var(--c-light)",paddingTop:10,display:"flex",flexDirection:"column",gap:8}}>
                  {(() => {
                    const petForPricing = pet || null;
                    const spaSvcs = (tenant?.services || []).filter(s => s.kind === "spa");
                    const tenantAddons = tenant?.addons || [];
                    const spaLines = (apt.spaIds || []).map(id => {
                      const sp = spaSvcs.find(s => s.id === id);
                      if (!sp) return null;
                      const pd = petForPricing ? getPricingDetail(petForPricing, sp, tenant) : { total: sp.basePrice };
                      return { id, name: sp.name, price: pd.total };
                    }).filter(Boolean);
                    const addonLines = (apt.addons || []).filter(n => !String(n).startsWith("💆")).map(name => {
                      const a = tenantAddons.find(x => x.name === name);
                      const price = a ? computeAddonPrice(a, petForPricing).price : 0;
                      return { name, price };
                    });
                    // Recompute apt.addonPrice deterministically based on
                    // current spaIds + addons so any add/remove flows
                    // through the rest of the receipt.
                    const recomputeAddonPrice = (nextSpaIds, nextAddons) => {
                      const sps = (nextSpaIds || []).reduce((s, id) => {
                        const sp = spaSvcs.find(x => x.id === id);
                        if (!sp) return s;
                        const pd = petForPricing ? getPricingDetail(petForPricing, sp, tenant) : { total: sp.basePrice };
                        return s + (Number(pd.total) || 0);
                      }, 0);
                      const ads = (nextAddons || []).filter(n => !String(n).startsWith("💆")).reduce((s, name) => {
                        const a = tenantAddons.find(x => x.name === name);
                        return s + (a ? computeAddonPrice(a, petForPricing).price : 0);
                      }, 0);
                      return sps + ads;
                    };
                    const patchApt = (patch) => {
                      setState(st => ({ ...st, appointments: st.appointments.map(x => x.id === apt.id ? { ...x, ...patch } : x) }));
                      setEditApt(prev => prev?.id === apt.id ? { ...prev, ...patch } : prev);
                    };
                    const removeSpa = (id) => {
                      const nextSpaIds = (apt.spaIds || []).filter(x => x !== id);
                      patchApt({ spaIds: nextSpaIds, addonPrice: recomputeAddonPrice(nextSpaIds, apt.addons) });
                    };
                    const removeAddon = (name) => {
                      const nextAddons = (apt.addons || []).filter(x => x !== name);
                      patchApt({ addons: nextAddons, addonPrice: recomputeAddonPrice(apt.spaIds, nextAddons) });
                    };
                    const removeSur = (i) => patchApt({ surcharges: (apt.surcharges || []).filter((_, j) => j !== i) });
                    const patchSur = (i, patch) => patchApt({ surcharges: (apt.surcharges || []).map((s, j) => j === i ? { ...s, ...patch } : s) });
                    // Append an empty surcharge row that the owner fills in
                    // line — same inline-edit feel as the discount controls
                    // below. No modal, no confirm button.
                    const addSur = () => patchApt({ surcharges: [...(apt.surcharges || []), { name: "", amount: 0 }] });
                    const addItem = () => setPaymentSheet(s => ({ ...s, _addItem: true }));
                    const showActionBar = !paymentSheet._addItem;
                    return (
                      <>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:14,fontWeight:700,color:"#4A4A68"}}><span>{apt.service || i18nT("pay.svcDefault")}</span><span>{cs}{apt.basePrice}</span></div>
                        {spaLines.map(l => (
                          <div key={"spa"+l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,fontWeight:700,color:"#6A6A85"}}>
                            <span style={{display:"inline-flex",alignItems:"center",gap:6}}>💆 {l.name}
                              <button onClick={() => removeSpa(l.id)} title={i18nT("pay.remove")} style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",padding:0,display:"inline-flex"}}><X size={12} strokeWidth={2.5} /></button>
                            </span>
                            <span>{cs}{l.price}</span>
                          </div>
                        ))}
                        {addonLines.map((l, i) => (
                          <div key={"ad"+i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,fontWeight:700,color:"#6A6A85"}}>
                            <span style={{display:"inline-flex",alignItems:"center",gap:6}}>{l.name}
                              <button onClick={() => removeAddon(l.name)} title={i18nT("pay.remove")} style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",padding:0,display:"inline-flex"}}><X size={12} strokeWidth={2.5} /></button>
                            </span>
                            <span>{cs}{l.price}</span>
                          </div>
                        ))}
                        {(apt.surcharges || []).map((s, i) => (
                          <div key={"sur"+i} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:"#92400E"}}>
                            <span style={{color:"#92400E",fontWeight:900}}>+</span>
                            <input
                              defaultValue={s.name || ""}
                              onBlur={e => patchSur(i, { name: e.target.value.trim() })}
                              placeholder={i18nT("pay.surchargePh")}
                              style={{flex:1,border:"1.5px solid transparent",borderRadius:8,padding:"6px 8px",fontSize:13,fontWeight:700,outline:"none",color:"#92400E",background:"rgba(255,251,235,0.6)"}}
                              onFocus={e => e.target.style.borderColor = "#FCD34D"}
                            />
                            <div style={{display:"inline-flex",alignItems:"center",gap:2,padding:"4px 8px",borderRadius:8,border:"1.5px solid #FCD34D",background:"#FFFBEB"}}>
                              <span style={{fontSize:11,fontWeight:800,color:"#92400E"}}>{cs}</span>
                              <input
                                type="number" min="0"
                                defaultValue={s.amount || 0}
                                onBlur={e => patchSur(i, { amount: Math.max(0, Number(e.target.value) || 0) })}
                                style={{width:48,border:"none",fontSize:13,fontWeight:900,color:"#92400E",outline:"none",background:"transparent",textAlign:"right"}}
                              />
                            </div>
                            <button onClick={() => removeSur(i)} title={i18nT("pay.remove")} style={{background:"none",border:"none",cursor:"pointer",color:"#D97706",padding:2,display:"inline-flex"}}><X size={14} strokeWidth={2.5} /></button>
                          </div>
                        ))}
                        {showActionBar && (
                          <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                            <button onClick={addItem} style={{flex:"1 1 130px",padding:"8px 10px",borderRadius:10,border:"1.5px dashed #C7C7CC",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}>{i18nT("pay.addSpaAddon")}</button>
                            <button onClick={addSur} style={{flex:"1 1 130px",padding:"8px 10px",borderRadius:10,border:"1.5px dashed #FDBA74",background:"#FFFBEB",color:"#92400E",fontSize:12,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}>{i18nT("pay.addSurcharge")}</button>
                          </div>
                        )}
                        {paymentSheet._addItem && (() => {
                          const availSpa = spaSvcs.filter(sp => sp.enabled !== false && !((apt.spaIds || []).includes(sp.id)));
                          const availAddons = tenantAddons.filter(a => {
                            if (a.enabled === false) return false;
                            const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
                            if (petForPricing?.type && !pts.includes(petForPricing.type)) return false;
                            return !((apt.addons || []).includes(a.name));
                          });
                          return (
                            <div style={{background:"#F8F8FC",border:"1.5px solid #E5E5EA",borderRadius:12,padding:10,display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <p style={{margin:0,fontSize:12,fontWeight:900,color:"#4A4A68"}}>{i18nT("pay.addSpaAddon")}</p>
                                <button onClick={() => setPaymentSheet(s => ({ ...s, _addItem: false }))} style={{background:"none",border:"none",cursor:"pointer",color:"#8E8E93",padding:0,display:"inline-flex"}}><X size={14} strokeWidth={2.5} /></button>
                              </div>
                              {availSpa.length === 0 && availAddons.length === 0 && (
                                <p style={{fontSize:12,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"8px 0"}}>{i18nT("pay.noAvailable")}</p>
                              )}
                              {availSpa.length > 0 && <p style={{fontSize:10,color:"#8E8E93",margin:0,fontWeight:800,letterSpacing:0.4,textTransform:"uppercase"}}>SPA</p>}
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {availSpa.map(sp => {
                                  const pd = petForPricing ? getPricingDetail(petForPricing, sp, tenant) : { total: sp.basePrice };
                                  return (
                                    <button key={sp.id} onClick={() => {
                                      const nextSpaIds = [...(apt.spaIds || []), sp.id];
                                      patchApt({ spaIds: nextSpaIds, addonPrice: recomputeAddonPrice(nextSpaIds, apt.addons) });
                                    }} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-light)",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>💆 {sp.name} · {cs}{pd.total}</button>
                                  );
                                })}
                              </div>
                              {availAddons.length > 0 && <p style={{fontSize:10,color:"#8E8E93",margin:"4px 0 0",fontWeight:800,letterSpacing:0.4,textTransform:"uppercase"}}>{i18nT("pay.addonLabel")}</p>}
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {availAddons.map(a => {
                                  const ap = computeAddonPrice(a, petForPricing);
                                  return (
                                    <button key={a.id || a.name} onClick={() => {
                                      const nextAddons = [...(apt.addons || []), a.name];
                                      patchApt({ addons: nextAddons, addonPrice: recomputeAddonPrice(apt.spaIds, nextAddons) });
                                    }} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-light)",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{a.name} · {cs}{ap.price}</button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
                <div style={{borderTop:"1px solid var(--c-light)",paddingTop:10,display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,color:"#4A4A68"}}><span>{i18nT("pay.subtotal")}</span><span>{cs}{subtotal}</span></div>
                  {/* M5: each pill keeps the OTHER type's last value
                       in paymentSheet._dPctMem / _dFixMem so toggling
                       % ↔ $ doesn't silently reset to 0. */}
                  <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"space-between"}}>
                    <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>{i18nT("pay.discount")}</span>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",borderRadius:10,border:"1.5px solid " + (draft?.type === "percent" ? "#1C1C1E" : "var(--c-light)"),background: draft?.type === "percent" ? "#F2F2F7" : "#fff",cursor:"pointer"}}
                           onClick={() => setPaymentSheet(s => ({ ...s, _dFixMem: dFix, discount: { type:"percent", amount: dPct } }))}>
                        <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>−%</span>
                        <input type="number" min="0" max="100" value={dPct || 0} onChange={e => setPaymentSheet(s => ({ ...s, _dFixMem: dFix, discount: { type:"percent", amount: Number(e.target.value) || 0 } }))} style={{width:34,border:"none",fontSize:13,fontWeight:900,color:"#1C1C1E",outline:"none",background:"transparent",textAlign:"right"}} />
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",borderRadius:10,border:"1.5px solid " + (draft?.type === "fixed" ? "#1C1C1E" : "var(--c-light)"),background: draft?.type === "fixed" ? "#F2F2F7" : "#fff",cursor:"pointer"}}
                           onClick={() => setPaymentSheet(s => ({ ...s, _dPctMem: dPct, discount: { type:"fixed", amount: dFix } }))}>
                        <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>− $</span>
                        <input type="number" min="0" value={dFix || 0} onChange={e => setPaymentSheet(s => ({ ...s, _dPctMem: dPct, discount: { type:"fixed", amount: Number(e.target.value) || 0 } }))} style={{width:44,border:"none",fontSize:13,fontWeight:900,color:"#1C1C1E",outline:"none",background:"transparent",textAlign:"right"}} />
                      </div>
                    </div>
                  </div>
                  {deposit > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#10B981"}}><span>{i18nT("pay.depositPaid")}</span><span>−{cs}{deposit}</span></div>}
                </div>
                <div style={{borderTop:"2px solid var(--c-border)",paddingTop:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:16,fontWeight:900,color:"#1C1C1E"}}>{i18nT("pay.balanceDue")}</span>
                  <span style={{fontSize:22,fontWeight:900,color:"#1C1C1E"}}>{cs}{balanceDue}</span>
                </div>
                <div style={{display:"flex",gap:8,marginTop:4}}>
                  <Btn variant="secondary" style={{flex:1}} onClick={() => setPaymentSheet(null)}>{i18nT("pay.cancel")}</Btn>
                  {/* C2: 繼續 → just advances the step. Discount stays as
                       transient draft inside paymentSheet; it commits to
                       apt.discount ONLY at 確認收款 below. Cancelling
                       (or X-ing the sheet) leaves apt.discount untouched. */}
                  <Btn style={{flex:1,background:"#1C1C1E",color:"#fff"}} onClick={() => {
                    setPaymentSheet(s => ({ ...s, step: "method" }));
                  }}>{i18nT("pay.continue")}</Btn>
                </div>
              </div>
            );
          }
          // step === "method"
          // Methods come from tenant.depositMethods (managed at 設定 →
          // 收款 → 收款方式), limited to the ones marked 店內結帳 and with
          // 現金 first (it's the default selection below). If nothing is
          // enabled yet, fall back to 現金 so the checkout still works.
          const tenantMethods = checkoutMethodsOf(tenant);
          const METHODS = tenantMethods.length > 0
            ? tenantMethods.map(m => [m.id, m.name || i18nT("pay.methodFallback")])
            : [["cash", i18nT("pay.cash")]];
          const method = paymentSheet.method || METHODS[0][0];
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <button onClick={() => setPaymentSheet(s => ({ ...s, step: "details" }))} style={{background:"none",border:"none",color:"#8A8A9E",fontSize:13,fontWeight:700,cursor:"pointer",padding:0,alignSelf:"flex-start"}}>{i18nT("pay.back")}</button>
              <p style={{fontSize:15,fontWeight:900,color:"#1C1C1E",margin:0}}>{i18nT("pay.pickMethod")}</p>
              {tenantMethods.length === 0 && (
                <p style={{fontSize:11,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:10,padding:"8px 12px",margin:0,fontWeight:700,lineHeight:1.5}}>{i18nT("pay.noMethods")}</p>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {METHODS.map(([v, l]) => {
                  const sel = method === v;
                  return (
                    <button key={v} onClick={() => setPaymentSheet(s => ({ ...s, method: v }))}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:14,border:"2px solid " + (sel ? "#1C1C1E" : "var(--c-light)"),background: sel ? "#F2F2F7" : "#fff",cursor:"pointer",textAlign:"left"}}>
                      <span style={{width:18,height:18,borderRadius:99,border:"2px solid " + (sel ? "#1C1C1E" : "#C7C7CC"),background: sel ? "#1C1C1E" : "#fff",flexShrink:0}} />
                      <span style={{fontSize:14,fontWeight:800,color:"#1C1C1E"}}>{l}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{borderTop:"2px solid var(--c-border)",paddingTop:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:15,fontWeight:900,color:"#1C1C1E"}}>{i18nT("pay.due")}</span>
                <span style={{fontSize:22,fontWeight:900,color:"#1C1C1E"}}>${balanceDue}</span>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <Btn variant="secondary" style={{flex:1}} onClick={() => setPaymentSheet(null)}>{i18nT("pay.cancel")}</Btn>
                {/* C3: alreadyPaid OR _submitting both gate this button so a
                     double-tap / network lag can't append two log entries
                     for the same checkout. */}
                <Btn
                  disabled={alreadyPaid || !!paymentSheet._submitting}
                  style={{flex:1,background: alreadyPaid ? "#10B981" : "#1C1C1E", color:"#fff", opacity: alreadyPaid || paymentSheet._submitting ? 0.6 : 1}}
                  onClick={async () => {
                    if (alreadyPaid || paymentSheet._submitting) return;
                    setPaymentSheet(s => ({ ...s, _submitting: true }));
                    const discountToCommit = draft && (dPct > 0 || dFix > 0)
                      ? { type: draft.type, amount: draft.type === "percent" ? dPct : dFix, reason: apt.discount?.reason || "" }
                      : (apt.discount && (dPct === 0 && dFix === 0) ? null : apt.discount);
                    const methodMeta = METHODS.find(([v]) => v === method) || [method, method];
                    const tenantRow = (tenant?.depositMethods || []).find(m => m.id === method) || null;
                    const entry = {
                      id: "pay" + uid(),
                      at: new Date().toISOString(),
                      amount: balanceDue,
                      method,
                      methodId: tenantRow ? tenantRow.id : null,
                      presetKey: tenantRow?.presetKey || null,
                      methodName: methodMeta[1],
                      by: user.id,
                      type: "payment",
                      note: "",
                    };
                    // H2: persist via atomic RPC FIRST so multi-device
                    // appends are race-safe + idempotent. Discount is
                    // sent through the regular setState/dataSync since
                    // the RPC re-derives paid from the canonical log.
                    let rpcResult = null;
                    try {
                      rpcResult = await recordPaymentAtomic(apt.id, entry);
                    } catch (e) {
                      toast(i18nT("pay.failed") + (e?.message || e), "error");
                      setPaymentSheet(s => ({ ...s, _submitting: false }));
                      return;
                    }
                    if (rpcResult?.skipped === "duplicate") {
                      toast(i18nT("pay.dup"), "info");
                    } else {
                      toast(i18nT("pay.collected") + " $" + balanceDue + " ✓");
                    }
                    // Mirror canonical log + paid state to client.
                    const aptPatch = (a) => ({
                      ...a,
                      paid: !!rpcResult?.paid,
                      remainingBalance: Math.max(0, Number(rpcResult?.total || 0) - Number(rpcResult?.collected || 0)),
                      discount: discountToCommit,
                      paymentLog: Array.isArray(rpcResult?.log) ? rpcResult.log : (a.paymentLog || []),
                    });
                    setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === apt.id ? aptPatch(a) : a) }));
                    setEditApt(prev => prev?.id === apt.id ? aptPatch(prev) : prev);
                    setPaymentSheet(null);
                  }}
                >{alreadyPaid ? i18nT("pay.paidTick") : i18nT("pay.confirm")}</Btn>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Slot-kind picker — appears when tapping an empty calendar slot.
          Lets the owner/staff pick between adding a customer appointment
          and adding an internal event (lunch, meeting, time-off block). */}
      <BottomSheet open={!!slotKindPick} onClose={() => setSlotKindPick(null)} title={zhEn("新增到此時段", "Add to this time slot")}>
        {slotKindPick && (() => {
          const sp = slotKindPick;
          const staffName = state.users[sp.staffId]?.name || zhEn("員工", "Staff");
          return (
            <div style={{padding:"4px 4px 12px",display:"flex",flexDirection:"column",gap:12}}>
              <p style={{fontSize:12,color:"#6B7280",margin:0,fontWeight:700}}>{staffName} · {sp.date} · {sp.time}</p>
              <button
                onClick={() => {
                  setWalkInForm({ customerId:"", petId:"", svcId:"", staffId:sp.staffId, date:sp.date, time:sp.time, addons:[], spaIds:[], discountAmount:0, discountType:"percent", discountReason:"", staffRemark:"", _prefilledSlot:true });
                  setSlotKindPick(null);
                }}
                style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px",borderRadius:18,border:"1.5px solid var(--c-light)",background:"#fff",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:30}}>🐾</span>
                <span style={{flex:1,minWidth:0}}>
                  <span style={{display:"block",fontSize:15,fontWeight:900,color:"#1F2937"}}>{zhEn("新增預約", "New booking")}</span>
                  <span style={{display:"block",fontSize:11,color:"#6B7280",fontWeight:700,marginTop:2}}>{zhEn("客戶 · 寵物 · 服務", "Customer · pet · service")}</span>
                </span>
              </button>
              <button
                onClick={() => {
                  setEventForm({ id:null, staffId:sp.staffId, date:sp.date, time:sp.time, durationMin:30, label:"", staffNote:"", color:"#9CA3AF" });
                  setSlotKindPick(null);
                }}
                style={{display:"flex",alignItems:"center",gap:14,padding:"16px 18px",borderRadius:18,border:"1.5px solid var(--c-light)",background:"#fff",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:30}}>📌</span>
                <span style={{flex:1,minWidth:0}}>
                  <span style={{display:"block",fontSize:15,fontWeight:900,color:"#374151"}}>{zhEn("新增事件", "New event")}</span>
                  <span style={{display:"block",fontSize:11,color:"#6B7280",fontWeight:700,marginTop:2}}>{zhEn("內部用 · 阻擋客人預約此時段", "Internal · blocks customers from booking this slot")}</span>
                </span>
              </button>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Internal event sheet — create or edit a non-customer block on
          the calendar (員工會議, 私人時間, 設備保養 etc). Acts as a busy
          slot so public booking can't reserve over it. */}
      <BottomSheet open={!!eventForm} onClose={() => setEventForm(null)} title={eventForm?.id ? zhEn("編輯事件", "Edit event") : zhEn("新增事件", "New event")}>
        {eventForm && (() => {
          const ef = eventForm;
          const setEf = (u) => setEventForm(f => ({ ...f, ...u }));
          const QUICK_LABELS = [zhEn("員工休息", "Staff break"),zhEn("會議", "Meeting"),zhEn("設備保養", "Equipment maintenance"),zhEn("私人時間", "Personal time"),zhEn("培訓", "Training"),zhEn("盤點", "Stock-take"),zhEn("🤒 病假", "🤒 Sick leave"),zhEn("📌 事假", "📌 Personal leave")];
          const LEAVE_TYPES = [["", zhEn("一般事件", "General event")], ["sick", zhEn("🤒 病假", "🤒 Sick leave")], ["personal", zhEn("📌 事假", "📌 Personal leave")]];
          const DURATIONS = [15,30,45,60,90,120,180,240,360,480];
          // Compute end time from start + duration for the user-facing
          // helper line. Caps at 23:59 so it doesn't roll into the next
          // day visually (the event itself can still span the full day).
          const endTimeStr = (() => {
            const [h, m] = String(ef.time || "00:00").split(":").map(Number);
            const total = (h * 60 + m) + (Number(ef.durationMin) || 0);
            const eh = Math.min(23, Math.floor(total / 60));
            const em = Math.min(59, total % 60);
            return String(eh).padStart(2, "0") + ":" + String(em).padStart(2, "0");
          })();
          // Whole-day quick action: use the shop's business hours for the
          // event's weekday when available, otherwise fall back to
          // 00:00–24:00. Sets BOTH start time and duration at once.
          const applyWholeDay = () => {
            const day = new Date(ef.date).getDay();
            const bh = tenant?.businessHours?.[day];
            if (bh && bh.enabled && bh.open && bh.close) {
              const [oh, om] = bh.open.split(":").map(Number);
              const [ch, cm] = bh.close.split(":").map(Number);
              const dur = Math.max(15, (ch * 60 + cm) - (oh * 60 + om));
              setEf({ time: bh.open, durationMin: dur });
            } else {
              setEf({ time: "00:00", durationMin: 1440 });
            }
          };
          const COLORS = [["#9CA3AF",zhEn("灰", "Grey")],["#3B82F6",zhEn("藍", "Blue")],["#F59E0B",zhEn("橙", "Orange")],["#10B981",zhEn("綠", "Green")],["#EF4444",zhEn("紅", "Red")]];
          const eventTemplates = (tenant?.eventTemplates || []).filter(t => t.enabled !== false);
          const buildEvt = (id, staffId) => ({
            id: id || uuid(),
            tenantId: user.tenantId,
            kind: "event",
            customerId: null,
            petId: null,
            staffId: staffId || null,
            svcId: null,
            service: "",
            addons: [],
            spaIds: [],
            date: ef.date,
            time: ef.time,
            status: "pending",
            basePrice: 0,
            addonPrice: 0,
            deposit: 0,
            remainingBalance: 0,
            paid: false,
            surcharges: [],
            discount: null,
            source: "owner",
            customDuration: Number(ef.durationMin) || 30,
            label: ef.label.trim(),
            color: ef.color || null,
            leaveType: ef.leaveType || null,
            staffNote: ef.staffNote || "",
            createdBy: user.id,
          });
          const onSave = async () => {
            if (!ef.label.trim()) { toast(zhEn("請輸入事件標題", "Please enter an event title"), "warning"); return; }
            // Edit mode → single event update.
            if (ef.id) {
              const evt = buildEvt(ef.id, ef.staffId);
              try { await upsertAppointment(evt); }
              catch (e) { toast(i18nT("tx.eventSaveFailed") + zhEn("：", ": ") + (e?.message || e), "error"); return; }
              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ef.id ? { ...a, ...evt } : a) }));
              toast(i18nT("tx.eventUpdated"));
              setEventForm(null);
              return;
            }
            // Create mode → if a template with multiple staff was picked,
            // fan out one event per applicable staff so the block lands on
            // every selected groomer's calendar at the same slot.
            const targetStaff = Array.isArray(ef._templateStaffIds) && ef._templateStaffIds.length > 0
              ? ef._templateStaffIds
              : [ef.staffId || null];
            const newEvts = targetStaff.map(sid => buildEvt(null, sid));
            try {
              for (const evt of newEvts) await upsertAppointment(evt);
            } catch (e) {
              toast(i18nT("tx.eventSaveFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              return;
            }
            setState(s => ({ ...s, appointments: [...s.appointments, ...newEvts] }));
            toast(newEvts.length > 1 ? zhEn(`已為 ${newEvts.length} 位員工新增事件 📌`, `Event added for ${newEvts.length} staff 📌`) : zhEn("事件已新增 📌","Event added 📌"));
            setEventForm(null);
          };
          const onDelete = async () => {
            if (!ef.id) return;
            if (!window.confirm(i18nT("tx.delEventConfirm"))) return;
            try {
              await deleteAppointment(ef.id, { hard: true });
            } catch (e) {
              toast(i18nT("tx.deleteFailed") + zhEn("：", ": ") + (e?.message || e), "error");
              return;
            }
            setState(s => ({ ...s, appointments: s.appointments.filter(a => a.id !== ef.id) }));
            toast(i18nT("tx.eventDeleted"));
            setEventForm(null);
          };
          return (
            <div style={{padding:"4px 4px 16px",display:"flex",flexDirection:"column",gap:14}}>
              {!ef.id && eventTemplates.length > 0 && (
                <div style={{padding:"10px 12px",background:"var(--c-lighter)",borderRadius:14}}>
                  <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("從範本快速套用", "Quick-apply from template")}</label>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {eventTemplates.map(t => (
                      <button key={t.id} onClick={() => setEf({
                        label: t.name,
                        durationMin: Number(t.durationMin) || 30,
                        color: t.color || "#9CA3AF",
                        leaveType: t.leaveType || "",
                        _templateStaffIds: Array.isArray(t.staffIds) ? t.staffIds : [],
                        staffId: (Array.isArray(t.staffIds) && t.staffIds.length === 1) ? t.staffIds[0] : ef.staffId,
                      })} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-light)",background:"#fff",fontSize:12,fontWeight:800,color:"#374151",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}>
                        <span style={{width:8,height:8,borderRadius:99,background:t.color||"#9CA3AF",display:"inline-block"}} />
                        {t.name}
                        <span style={{color:"#9CA3AF",fontWeight:700}}>· {(t.staffIds||[]).length}{zhEn("人"," staff")}</span>
                      </button>
                    ))}
                  </div>
                  {Array.isArray(ef._templateStaffIds) && ef._templateStaffIds.length > 1 && (
                    <p style={{fontSize:11,color:"#10B981",margin:"8px 0 0",fontWeight:800}}>
                      {zhEn("✓ 將為", "✓ Will add this event for")} {ef._templateStaffIds.length} {zhEn("位員工同時新增此事件", "staff at the same time")}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("類型", "Type")}</label>
                <div style={{display:"flex",gap:6}}>
                  {LEAVE_TYPES.map(([v, l]) => (
                    <button key={v} onClick={() => {
                      // Auto-fill label when picking 病假/事假; pick a
                      // sensible duration (whole-day) so the row blocks
                      // the staff's full day on the calendar by default.
                      if (v === "sick") setEf({ leaveType: v, label: ef.label || zhEn("🤒 病假", "🤒 Sick leave"), durationMin: ef.durationMin && ef.durationMin >= 360 ? ef.durationMin : 480 });
                      else if (v === "personal") setEf({ leaveType: v, label: ef.label || zhEn("📌 事假", "📌 Personal leave"), durationMin: ef.durationMin && ef.durationMin >= 360 ? ef.durationMin : 480 });
                      else setEf({ leaveType: "" });
                    }} style={{flex:1,padding:"8px 10px",borderRadius:12,border:"1.5px solid " + (ef.leaveType === v ? "var(--c-primary)" : "var(--c-light)"),background:ef.leaveType === v ? "var(--c-primary)" : "#fff",color:ef.leaveType === v ? "#fff" : "#374151",fontSize:12,fontWeight:800,cursor:"pointer"}}>{l}</button>
                  ))}
                </div>
                {ef.leaveType && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={() => setEf({ durationMin: 240 })} style={{flex:1,padding:"6px 10px",borderRadius:99,border:"1.5px solid " + (Number(ef.durationMin) === 240 ? "var(--c-primary)" : "var(--c-light)"),background:Number(ef.durationMin) === 240 ? "var(--c-primary)" : "#fff",color:Number(ef.durationMin) === 240 ? "#fff" : "#374151",fontSize:11,fontWeight:800,cursor:"pointer"}}>{zhEn("半日 (4 小時)", "Half day (4 hours)")}</button>
                    <button onClick={() => setEf({ durationMin: 480 })} style={{flex:1,padding:"6px 10px",borderRadius:99,border:"1.5px solid " + (Number(ef.durationMin) >= 480 ? "var(--c-primary)" : "var(--c-light)"),background:Number(ef.durationMin) >= 480 ? "var(--c-primary)" : "#fff",color:Number(ef.durationMin) >= 480 ? "#fff" : "#374151",fontSize:11,fontWeight:800,cursor:"pointer"}}>{zhEn("全日 (8 小時)", "Full day (8 hours)")}</button>
                  </div>
                )}
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("事件標題 *", "Event title *")}</label>
                <input value={ef.label} onChange={e => setEf({ label: e.target.value, _templateStaffIds: null })} placeholder={zhEn("例：員工會議", "e.g. Staff meeting")} style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:14,fontWeight:700,boxSizing:"border-box"}} />
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                  {QUICK_LABELS.map(q => (
                    <button key={q} onClick={() => setEf({ label: q, _templateStaffIds: null })} style={{padding:"4px 10px",borderRadius:99,border:"1px solid var(--c-light)",background:ef.label===q?"var(--c-primary)":"#fff",color:ef.label===q?"#fff":"#374151",fontSize:11,fontWeight:800,cursor:"pointer"}}>{q}</button>
                  ))}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("日期", "Date")}</label>
                  <input type="date" value={ef.date} onChange={e => setEf({ date: e.target.value })} style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:14,fontWeight:700,boxSizing:"border-box"}} />
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{i18nT("own.walkin.startTime")}</label>
                  <input type="time" value={ef.time} onChange={e => setEf({ time: e.target.value })} style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:14,fontWeight:700,boxSizing:"border-box"}} />
                </div>
              </div>
              <div>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:6}}>
                  <label style={{fontSize:11,fontWeight:900,color:"#6B7280"}}>{zhEn("時長", "Duration")}</label>
                  <span style={{fontSize:11,fontWeight:700,color:"var(--c-primary)"}}>{zhEn("結束", "End")} {endTimeStr}</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
                  <button onClick={applyWholeDay} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-primary)",background:Number(ef.durationMin)>=480?"var(--c-primary)":"#fff",color:Number(ef.durationMin)>=480?"#fff":"var(--c-primary)",fontSize:12,fontWeight:900,cursor:"pointer"}}>{zhEn("📅 全日", "📅 All day")}</button>
                  {DURATIONS.map(d => (
                    <button key={d} onClick={() => setEf({ durationMin: d })} style={{padding:"6px 12px",borderRadius:99,border:"1px solid var(--c-light)",background:Number(ef.durationMin)===d?"var(--c-primary)":"#fff",color:Number(ef.durationMin)===d?"#fff":"#374151",fontSize:12,fontWeight:800,cursor:"pointer"}}>
                      {d < 60 ? d + zhEn(" 分", " pts") : (d/60) + zhEn(" 小時", " hours") + (d%60 ? " " + (d%60) + zhEn(" 分", " pts") : "")}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:11,fontWeight:700,color:"#8A8A9E"}}>{zhEn("自訂", "Custom")}</span>
                  <input
                    type="number"
                    min="5"
                    max="1440"
                    step="5"
                    value={ef.durationMin || ""}
                    onChange={e => {
                      const v = parseInt(e.target.value) || 0;
                      setEf({ durationMin: Math.max(0, Math.min(1440, v)) });
                    }}
                    placeholder={zhEn("分鐘", "min")}
                    style={{flex:1,maxWidth:120,padding:"6px 10px",borderRadius:10,border:"1.5px solid var(--c-light)",fontSize:13,fontWeight:800,boxSizing:"border-box",textAlign:"center"}}
                  />
                  <span style={{fontSize:11,fontWeight:700,color:"#8A8A9E"}}>{zhEn("分鐘", "min")}</span>
                </div>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("指派員工", "Assign staff")}</label>
                <Sel value={ef.staffId || ""} onChange={e => setEf({ staffId: e.target.value || null })}>
                  <option value="">{zhEn("— 選擇員工 —", "— Select staff —")}</option>
                  {allStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Sel>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("顏色", "Colour")}</label>
                <div style={{display:"flex",gap:8}}>
                  {COLORS.map(([c,name]) => (
                    <button key={c} onClick={() => setEf({ color: c })} title={name} style={{width:32,height:32,borderRadius:99,background:c,border:ef.color===c?"3px solid #1F2937":"2px solid #fff",cursor:"pointer",boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}} />
                  ))}
                </div>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:900,color:"#6B7280",marginBottom:6}}>{zhEn("備註（可選）", "Notes (optional)")}</label>
                <textarea value={ef.staffNote} onChange={e => setEf({ staffNote: e.target.value })} rows={2} placeholder={zhEn("詳細備註…", "Details…")} style={{width:"100%",padding:"10px 12px",borderRadius:12,border:"1.5px solid var(--c-light)",fontSize:13,fontWeight:600,boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}} />
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                {ef.id && <Btn onClick={onDelete} style={{background:"#FEE2E2",color:"#B91C1C"}}>{zhEn("刪除", "Delete")}</Btn>}
                <div style={{flex:1}} />
                <Btn onClick={() => setEventForm(null)} variant="secondary">{zhEn("取消", "Cancel")}</Btn>
                <Btn onClick={onSave} style={{background:"#10B981",color:"#fff"}}>{ef.id ? zhEn("儲存", "Save") : zhEn("新增", "Add")}</Btn>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* NEW: Walk-in booking sheet — full rebuild */}
      <BottomSheet open={!!walkInForm} onClose={() => { setWalkInForm(null); setWalkInNewCust(null); setWalkInNewPet(null); setWalkInSearch(""); }} title={walkInForm?._prefilledSlot && walkInForm?.date !== dateNow() ? zhEn("新增預約", "New booking") : i18nT("wi.title")}>
        {walkInForm && (() => {
          const wf = walkInForm;
          const setWf = (updates) => setWalkInForm(f => ({ ...f, ...updates }));
          const selectedCust = wf.customerId ? state.users[wf.customerId] : null;

          // Customer search filter — matches customer name / phone / email AND
          // any of their pets' name or breed, so the shop can find a client by
          // the pet they remember ("旺財") not just the owner.
          const q = walkInSearch.trim().toLowerCase();
          const petsOf = (c) => (c.pets || []).map(pid => state.pets?.[pid]).filter(Boolean);
          const filteredCusts = q
            ? customers.filter(c =>
                (c.name && c.name.toLowerCase().includes(q))
                || (c.phone && c.phone.includes(q))
                || (c.email && c.email.toLowerCase().includes(q))
                || petsOf(c).some(p => (p.name && p.name.toLowerCase().includes(q)) || (p.breed && p.breed.toLowerCase().includes(q)))
              )
            : customers.slice(0, 8);

          // Pet for pricing (existing or staged new)
          const petForPricing = wf.petId ? state.pets[wf.petId] :
            (walkInNewPet && walkInNewPet.name ? {
              ...walkInNewPet,
              weight: parseFloat(walkInNewPet.weight)||0,
              coatLength: walkInNewPet.coatLength||"short"
            } : null);

          const selectedSvc = (tenant?.services||[]).find(s => s.id === wf.svcId);
          const pd = petForPricing && selectedSvc ? getPricingDetail(petForPricing, selectedSvc, tenant, { usesPackage: !!wf.customerPackageId }) : null;
          const addonPrice = (wf.addons||[]).reduce((s,n) => {
            const a = (tenant?.addons || []).find(x => x.name === n);
            return s + (a ? computeAddonPrice(a, petForPricing).price : 0);
          }, 0);
          const subtotal = (pd?.total||0) + addonPrice;
          const discValue = wf.discountType === "percent" ? Math.round(subtotal * (wf.discountAmount||0) / 100) : (wf.discountAmount||0);
          const finalTotal = Math.max(0, subtotal - discValue);

          // Walk-in time slots — every 15 minutes, conflict-aware, BOUNDED
          // by the shop's business hours for that day-of-week. The previous
          // version generated from 00:00 → 24:00 ignoring opening hours,
          // which surfaced midnight slots when the prefilled `wf.time` was
          // unset or when the device clock was overnight. Pre-selection
          // still works: if `wf.time` (set by the calendar tap) falls inside
          // business hours and is on the 15-min grid, the matching slot is
          // highlighted in the grid below.
          // Date the booking is being created for. Pre-set by the
          // calendar slot tap; falls back to today for the "+" button.
          const todayStr = wf.date || dateNow();
          const slots = (() => {
            if (!wf.staffId || !wf.svcId) return [];
            const svc = (tenant?.services || []).find(s => s.id === wf.svcId);
            const dur = (wf.customDuration || svc?.estimatedMinutes || 60) + (svc?.bufferMinutes || 0);
            const dow = new Date(todayStr).getDay();
            const bh = tenant?.businessHours?.[dow];
            // Default to 09:00-18:00 if the day has no hours configured.
            const [openH, openM] = (bh?.open || "09:00").split(":").map(Number);
            const [closeH, closeM] = (bh?.close || "18:00").split(":").map(Number);
            let openMin = openH * 60 + openM;
            let closeMin = closeH * 60 + closeM;
            // Per-staff hours override the shop window. If the staff has
            // any per-day hours configured but this day is off, no slots.
            const staffProfile = state.users?.[wf.staffId];
            if (staffProfile && staffOffOn(staffProfile, todayStr)) return [];
            const dayBreaks = staffProfile ? staffDayBreaks(staffProfile, dow) : [];
            const sh = staffProfile?.staffHours?.[dow] || staffProfile?.staffHours?.[String(dow)];
            const hasStaffSchedule = staffProfile?.staffHours && Object.values(staffProfile.staffHours).some(d => d && d.enabled);
            if (hasStaffSchedule) {
              if (!sh || !sh.enabled) return [];
              if (sh.open) {
                const [h, m] = sh.open.split(":").map(Number);
                openMin = Math.max(openMin, h * 60 + m);
              }
              if (sh.close) {
                const [h, m] = sh.close.split(":").map(Number);
                closeMin = Math.min(closeMin, h * 60 + m);
              }
              if (closeMin <= openMin) return [];
            }
            const staffApts = state.appointments.filter(a =>
              a.staffId === wf.staffId && a.date === todayStr && !isAptDead(a)
            );
            const out = [];
            for (let m = Math.floor(openMin / 15) * 15; m + dur <= closeMin; m += 15) {
              const time = String(Math.floor(m/60)).padStart(2,"0") + ":" + String(m%60).padStart(2,"0");
              const onBreak = overlapsBreak(m, m + dur, dayBreaks);
              const conflict = onBreak || staffApts.some(a => {
                const [ah, am] = a.time.split(":").map(Number);
                const aStart = ah*60 + am;
                const aDur = getAptDuration(a, tenant);
                return m < (aStart + aDur) && (m + dur) > aStart;
              });
              out.push({ time, available: !conflict, past: false });
            }
            return out;
          })();

          // A calendar slot on another day is a normal booking, not a walk-in:
          // no "walk-in, start now" banner or button for it.
          const futureSlot = walkInForm?._prefilledSlot && walkInForm?.date !== dateNow();
          return <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {!futureSlot && <div style={{background:"#F0FDF4",borderRadius:20,padding:"12px 16px",display:"flex",gap:10,alignItems:"center",border:"2px solid #BBF7D0"}}>
              <Check size={18} color="#10B981" strokeWidth={3} />
              <p style={{fontSize:13,color:"#166534",margin:0,fontWeight:700}}>{zhEn("即場到店無需訂金，直接登記開始美容", "Walk-ins need no deposit — check in and start grooming")}</p>
            </div>}

            {/* ── 1. CUSTOMER ── */}
            <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("① 客戶", "① Customer")}</p>
              {selectedCust ? (() => {
                // Risk chips — surface customer history that affects the
                // booking decision. No-show + frequent-cancel counts in
                // the last 180 days. ≥3 no-shows triggers a red border
                // so the owner can ask for an upfront deposit.
                const recentCutoff = (() => { const d = new Date(); d.setDate(d.getDate() - 180); return localDateStr(d); })();
                const custApts = allApts.filter(a => a.customerId === selectedCust.id && a.date >= recentCutoff);
                const noShowCount = custApts.filter(a => a.status === "noshow").length;
                const cancelCount = custApts.filter(a => a.status === "cancelled").length;
                const highRisk = noShowCount >= 3;
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:8,padding:14,borderRadius:20,border:"2px solid " + (highRisk ? "#EF4444" : "var(--c-primary)"),background:highRisk ? "#FEF2F2" : "var(--c-lighter)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <div style={{width:40,height:40,borderRadius:99,background:"var(--c-light)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"var(--c-primary)"}}>{selectedCust.name[0]}</div>
                      <div style={{flex:1}}>
                        <p style={{fontWeight:900,fontSize:15,margin:0,color:"#4A4A68"}}>{selectedCust.name}</p>
                        <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{selectedCust.phone}</p>
                      </div>
                      <button onClick={() => { setWf({ customerId:"", petId:"" }); setWalkInNewPet(null); }} style={{background:"none",border:"none",cursor:"pointer",color:"#AFAFC4",padding:6}}><X size={16} strokeWidth={3} /></button>
                    </div>
                    {(noShowCount > 0 || cancelCount >= 3) && (
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {highRisk && <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:99,background:"#FEE2E2",color:"#991B1B",fontSize:11,fontWeight:900}}>{zhEn("⚠️ 高風險：過去半年", "⚠️ High risk: last 6 months")} {noShowCount} {zhEn("次 no-show", "no-shows")}</span>}
                        {!highRisk && noShowCount > 0 && <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:99,background:"#FEF3C7",color:"#92400E",fontSize:11,fontWeight:900}}>{noShowCount} {zhEn("次 no-show", "no-shows")}</span>}
                        {cancelCount >= 3 && <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:99,background:"#FEF3C7",color:"#92400E",fontSize:11,fontWeight:900}}>{cancelCount} {zhEn("次取消", " cancellations")}</span>}
                      </div>
                    )}
                  </div>
                );
              })() : walkInNewCust ? (
                <div style={{display:"flex",flexDirection:"column",gap:10,padding:14,borderRadius:20,border:"2px dashed #10B981",background:"#F0FDF4"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <p style={{fontSize:13,fontWeight:800,color:"#10B981",margin:0}}>+ {i18nT("wi.newCust")}</p>
                    <button onClick={() => setWalkInNewCust(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#AFAFC4",padding:4}}><X size={14} strokeWidth={3} /></button>
                  </div>
                  <Input placeholder={i18nT("wi.fullName")} value={walkInNewCust.name} onChange={e => setWalkInNewCust(c => ({...c, name:e.target.value}))} />
                  <Input placeholder={i18nT("wi.phone")} type="tel" inputMode="tel" value={walkInNewCust.phone} onChange={e => {
                    // Paste from WhatsApp often arrives as "+852 9123 4567"
                    // — smart-strip the country code (and update the picker)
                    // so the owner doesn't have to manually clean it up.
                    const parsed = parseCopiedPhone(e.target.value);
                    setWalkInNewCust(c => ({
                      ...c,
                      phone: parsed.digits,
                      ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {}),
                    }));
                  }} />
                  {/* Phone fuzzy-match — surface possible existing
                      customer when the entered number matches anyone in
                      this shop (last 7 digits). Avoids accidentally
                      creating a duplicate profile + losing history. */}
                  {(() => {
                    const enteredDigits = (walkInNewCust.phone || "").replace(/\D/g, "");
                    if (enteredDigits.length < 7) return null;
                    const tail = enteredDigits.slice(-7);
                    const dupes = customers.filter(c => {
                      const cd = (c.phone || "").replace(/\D/g, "");
                      return cd.length >= 7 && cd.slice(-7) === tail;
                    });
                    if (dupes.length === 0) return null;
                    return (
                      <div style={{padding:"10px 12px",background:"#FEF3C7",border:"1.5px solid #FDE68A",borderRadius:14,display:"flex",flexDirection:"column",gap:6}}>
                        <p style={{fontSize:12,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("⚠️ 已有相同電話客戶 — 是否同一位？", "⚠️ A customer with the same phone already exists — is it the same person?")}</p>
                        {dupes.slice(0,3).map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setWf({ customerId: c.id, petId: "" });
                              setWalkInNewCust(null);
                              setWalkInNewPet(null);
                              toast(zhEn("已選擇現有客戶：", "Existing customer selected: ") + c.name);
                            }}
                            style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:"#fff",border:"1px solid #FDE68A",borderRadius:10,cursor:"pointer",textAlign:"left",width:"100%"}}
                          >
                            <span style={{fontSize:13,fontWeight:800,color:"#1F2937"}}>{c.name}</span>
                            <span style={{fontSize:11,fontWeight:700,color:"#92400E"}}>{c.phone} →</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <div style={{position:"relative",marginBottom:8}}>
                    <Search size={16} strokeWidth={2.5} style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#AFAFC4"}} />
                    <input style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:99,padding:"12px 12px 12px 40px",fontSize:14,fontWeight:700,outline:"none",boxSizing:"border-box",color:"#4A4A68"}} placeholder={T("搜尋姓名 / 電話 / 寵物名","Search name / phone / pet name")} value={walkInSearch} onChange={e => setWalkInSearch(e.target.value)} />
                  </div>
                  <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:6,marginBottom:8}}>
                    {filteredCusts.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",textAlign:"center",padding:"12px 0",fontWeight:700,margin:0}}>{zhEn("找不到客戶 🥺", "No customer found 🥺")}</p>}
                    {filteredCusts.map(c => (
                      <button key={c.id} onClick={() => { setWf({ customerId:c.id, petId:"" }); setWalkInSearch(""); }} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,border:"2px solid var(--c-light)",background:"#FFFDFD",cursor:"pointer",textAlign:"left"}}>
                        <div style={{width:32,height:32,borderRadius:99,background:"var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"var(--c-primary)",fontSize:13}}>{c.name[0]}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontWeight:800,fontSize:13,margin:0,color:"#4A4A68"}}>{c.name}</p>
                          <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{c.phone || "—"}</p>
                          {petsOf(c).length > 0
                            ? <p style={{fontSize:11,color:"var(--c-primary)",margin:"2px 0 0",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🐾 {petsOf(c).map(p => p.name + (p.breed ? zhEn("（", " (") + p.breed + zhEn("）", ")") : "")).join("、")}</p>
                            : <p style={{fontSize:11,color:"#C4C4D0",margin:"2px 0 0",fontWeight:700}}>🐾 {T("未有寵物資料","No pets yet")}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                  <Btn variant="ghost" size="sm" onClick={() => setWalkInNewCust({ name:walkInSearch, phone:"" })} style={{width:"100%",background:"#F0FDF4",color:"#10B981",border:"2px dashed #10B981"}}>
                    <UserPlus size={14} strokeWidth={2.5} /> {i18nT("wi.newCust")}
                  </Btn>
                </>
              )}
            </div>

            {/* ── 2. PET ── */}
            {(selectedCust || walkInNewCust) && <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("② 毛孩", "② Pet")}</p>
              {wf.petId && state.pets[wf.petId] ? (() => {
                const p = state.pets[wf.petId];
                return <div style={{display:"flex",alignItems:"center",gap:12,padding:14,borderRadius:20,border:"2px solid var(--c-primary)",background:"var(--c-lighter)"}}>
                  <div style={{fontSize:28}}>{petEmoji(p)}</div>
                  <div style={{flex:1}}>
                    <p style={{fontWeight:900,fontSize:15,margin:0,color:"#4A4A68"}}>{p.name}</p>
                    <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{p.breed + " · " + p.weight + "kg · " + (p.coatLength==="long"?zhEn("長毛", "Long coat"):zhEn("短毛", "Short coat"))}</p>
                  </div>
                  <button onClick={() => setWf({ petId:"" })} style={{background:"none",border:"none",cursor:"pointer",color:"#AFAFC4",padding:6}}><X size={16} strokeWidth={3} /></button>
                </div>;
              })() : walkInNewPet ? (
                <div style={{display:"flex",flexDirection:"column",gap:10,padding:14,borderRadius:20,border:"2px dashed var(--c-primary)",background:"var(--c-lighter)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <p style={{fontSize:13,fontWeight:800,color:"var(--c-primary)",margin:0}}>+ {i18nT("wi.addPet")}</p>
                    <button onClick={() => setWalkInNewPet(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#AFAFC4",padding:4}}><X size={14} strokeWidth={3} /></button>
                  </div>
                  {/* 「同主人名」捷徑 — 客戶有時用毛孩名做暱稱 (e.g. 主人
                       + 毛孩同叫「毛毛」)，呢個 button 一按複製 owner name
                       過嚟。只喺新客戶 + 已填名 + 毛孩名仲未同 owner name
                       一致 嘅時候顯示，避免重覆 click 嘅 noise。 */}
                  {walkInNewCust?.name?.trim() && walkInNewPet.name !== walkInNewCust.name.trim() && (
                    <button
                      onClick={() => setWalkInNewPet(p => ({...p, name: walkInNewCust.name.trim()}))}
                      style={{alignSelf:"flex-start",padding:"5px 12px",borderRadius:99,border:"1.5px dashed var(--c-primary)",background:"rgba(255,182,193,0.15)",color:"var(--c-primary)",fontSize:11,fontWeight:900,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}
                    >{zhEn("✓ 同主人名 —", "✓ Same as owner —")} {walkInNewCust.name.trim()}</button>
                  )}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <Input label={zhEn("名字 *", "Name *")} placeholder={zhEn("例：奶茶", "e.g. Milk Tea")} value={walkInNewPet.name} onChange={e => setWalkInNewPet(p => ({...p, name:e.target.value}))} />
                    <Input label={zhEn("體重 (kg) *", "Weight (kg) *")} placeholder="4.5" type="number" step="0.1" value={walkInNewPet.weight} onChange={e => setWalkInNewPet(p => ({...p, weight:e.target.value}))} />
                    <Sel label={zhEn("種類", "Type")} value={walkInNewPet.type||"dog"} onChange={e => setWalkInNewPet(p => ({...p, type:e.target.value, breedId: null, breed: ""}))}>
                      <option value="dog">{i18nT("wi.dog")}</option><option value="cat">{i18nT("wi.cat")}</option><option value="other">{i18nT("common.other")}</option>
                    </Sel>
                    <Sel label={zhEn("毛長", "Coat length")} value={walkInNewPet.coatLength||"short"} onChange={e => setWalkInNewPet(p => ({...p, coatLength:e.target.value}))}>
                      <option value="short">{i18nT("wi.coatShort")}</option><option value="long">{i18nT("wi.coatLong")}</option>
                    </Sel>
                    <Input label={zhEn("生日 / 年齡", "Birthday / age")} type="date" value={walkInNewPet.birthday||""} max={dateNow()} min={petBirthdayMin()} onChange={e => setWalkInNewPet(p => ({...p, birthday:e.target.value}))} />
                    <Input label={zhEn("過敏 / 健康備註", "Allergies / health notes")} placeholder={zhEn("無 / 對洗毛精過敏…", "None / allergic to shampoo…")} value={walkInNewPet.allergies||""} onChange={e => setWalkInNewPet(p => ({...p, allergies:e.target.value}))} />
                  </div>
                  {petWeightWarn(walkInNewPet.weight, walkInNewPet.type) && <p style={{fontSize:12, color:"#B45309", margin:0, fontWeight:700}}>{petWeightWarn(walkInNewPet.weight, walkInNewPet.type)}</p>}
                  <div style={{marginTop:4}}>
                    <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("品種 *", "Breed *")}</p>
                    <BreedCombobox type={walkInNewPet.type === "cat" ? "cat" : "dog"} lang={tenant?.lang || "zh-HK"}
                      value={walkInNewPet.breedId} valueLabel={walkInNewPet.breed} placeholder={i18nT("wi.breed")}
                      onChange={({ breedId, breed, coatHint }) => setWalkInNewPet(p => ({ ...p, breedId, breed, coatLength: p.coatLength || coatHint || "short" }))} />
                  </div>
                </div>
              ) : (
                <>
                  {selectedCust && (selectedCust.pets||[]).length > 0 && <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:8}}>
                    {(selectedCust.pets||[]).map(pid => {
                      const p = state.pets[pid]; if (!p) return null;
                      return <button key={pid} onClick={() => setWf({ petId:pid })} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:14,border:"2px solid var(--c-light)",background:"#FFFDFD",cursor:"pointer",textAlign:"left"}}>
                        <div style={{fontSize:24}}>{petEmoji(p)}</div>
                        <div style={{flex:1}}>
                          <p style={{fontWeight:800,fontSize:13,margin:0,color:"#4A4A68"}}>{p.name}</p>
                          <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{p.breed + " · " + p.weight + "kg · " + (p.coatLength==="long"?zhEn("長毛", "Long coat"):zhEn("短毛", "Short coat"))}</p>
                        </div>
                      </button>;
                    })}
                  </div>}
                  <Btn variant="ghost" size="sm" onClick={() => setWalkInNewPet({ name:"", breed:"", weight:"", type:"dog", coatLength:"short", allergies:"" })} style={{width:"100%",background:"var(--c-lighter)",color:"var(--c-primary)",border:"2px dashed var(--c-primary)"}}>
                    <Plus size={14} strokeWidth={3} /> {i18nT("wi.addPet")}
                  </Btn>
                </>
              )}
            </div>}

            {/* ── 3. SERVICE ── */}
            {petForPricing && petForPricing.weight > 0 && <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("③ 服務（依毛孩體型自動報價）", "③ Service (price quoted automatically by pet size)")}</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {(tenant?.services||[])
                  .filter(s => s.kind !== "spa" && serviceTypes(s).includes(petForPricing.type || "dog"))
                  .map(svc => ({ svc, sd: getPricingDetail(petForPricing, svc, tenant) }))
                  .sort((a,b) => (Number(a.sd?.total ?? a.svc.basePrice ?? 0)) - (Number(b.sd?.total ?? b.svc.basePrice ?? 0)))
                  .map(({ svc, sd }) => {
                  const sel = wf.svcId === svc.id;
                  // A $0-base service (e.g. 快速護理) IS its add-ons — so on select,
                  // jump the sheet straight to ⑤ 加購服務 instead of making the
                  // owner scroll past SPA to find them.
                  const jumpToAddons = Number(svc.basePrice) === 0;
                  return <button key={svc.id} onClick={() => { setWf({ svcId:svc.id }); if (jumpToAddons) setTimeout(() => document.getElementById("walkin-addons")?.scrollIntoView({ behavior:"smooth", block:"start" }), 80); }} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",borderRadius:16,border:"2px solid "+(sel?"var(--c-primary)":"var(--c-light)"),background:sel?"var(--c-lighter)":"#FFFDFD",cursor:"pointer",textAlign:"left"}}>
                    <div style={{flex:1}}>
                      <p style={{fontWeight:800,fontSize:14,margin:0,color:"#4A4A68"}}>{svc.name}</p>
                      <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{sd.baseLabel + " · " + (svc.estimatedMinutes||60) + "min"}</p>
                      {sd.extra > 0 && <p style={{fontSize:11,color:"#D97706",margin:"2px 0 0",fontWeight:700}}>{"+ " + sd.ruleLabel}</p>}
                    </div>
                    <span style={{fontWeight:900,fontSize:18,color:"var(--c-primary)"}}>{"$" + sd.total}</span>
                  </button>;
                })}
              </div>
            </div>}

            {/* ── 4a. SPA SERVICES (optional, multi-select) ──
                Hidden for small animals (rabbits / hamsters / guinea pigs)
                since the shop only offers SPA on dogs + cats. */}
            {wf.svcId && petForPricing?.type !== "small" && (tenant?.services||[]).some(s => s.kind === "spa") && <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("④ SPA 服務（可選，多項） ✨", "④ SPA services (optional, multiple) ✨")}</p>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {(tenant?.services||[]).filter(s => s.kind === "spa" && (s.petType || "dog") === (petForPricing?.type || "dog")).map(sp => {
                  const on = (wf.spaIds||[]).includes(sp.id);
                  const sPd = petForPricing ? getPricingDetail(petForPricing, sp, tenant) : { total: sp.basePrice };
                  return <button key={sp.id} onClick={() => setWf({ spaIds: on ? (wf.spaIds||[]).filter(x=>x!==sp.id) : [...(wf.spaIds||[]), sp.id] })} title={sp.description || ""} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-border)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    {on ? "✓ " : "+ "}{sp.name} ${sPd.total}
                  </button>;
                })}
              </div>
            </div>}

            {/* ── 4b. ADDONS ── (hidden for small animals) */}
            {(() => {
              const petType = petForPricing?.type || "dog";
              const wfAddonMatches = (a) => {
                const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
                return pts.includes(petType);
              };
              return wf.svcId && (tenant?.addons||[]).some(wfAddonMatches);
            })() && <div id="walkin-addons" style={{scrollMarginTop:12}}>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("⑤ 加購服務（可多選）", "⑤ Add-ons (multi-select)")}</p>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {(tenant?.addons||[]).filter(a => {
                  const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
                  return pts.includes(petForPricing?.type || "dog");
                }).map(ad => {
                  const on = (wf.addons||[]).includes(ad.name);
                  const ap = computeAddonPrice(ad, petForPricing);
                  return <button key={ad.id} onClick={() => setWf({ addons: on ? wf.addons.filter(x=>x!==ad.name) : [...(wf.addons||[]), ad.name] })} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-border)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    {on ? "✓ " : "+ "}{ad.name}{ap.variantLabel ? " (" + ap.variantLabel + ")" : ""} ${ap.price}
                  </button>;
                })}
              </div>
            </div>}

            {/* ── 5. STAFF + TIME + REMARKS ── */}
            {wf.svcId && <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("⑤ 美容師（單選）", "⑤ Groomer (pick one)")}</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {staffList.filter(s => staffCanDo(s, wf.svcId) && !seatLocked(staffSeats, s.id)).length === 0 ? (
                    <p style={{fontSize:12,color:"#AFAFC4",fontStyle:"italic",margin:0}}>{zhEn("暫無可提供此服務嘅美容師", "No groomer offers this service")}</p>
                  ) : (
                    staffList.filter(s => staffCanDo(s, wf.svcId) && !seatLocked(staffSeats, s.id)).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setWf({ staffId:s.id })}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",borderRadius:12,border:"2px solid "+(wf.staffId===s.id?"var(--c-primary)":"var(--c-light)"),background:wf.staffId===s.id?"var(--c-lighter)":"#FFFDFD",cursor:"pointer",textAlign:"left"}}
                      >
                        <span style={{width:18,height:18,borderRadius:99,border:"2px solid "+(wf.staffId===s.id?"var(--c-primary)":"#D4D4DC"),background:wf.staffId===s.id?"var(--c-primary)":"#fff",flexShrink:0}} />
                        <span style={{fontWeight:800,fontSize:13,color:"#4A4A68",flex:1}}>{s.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {wf.staffId && <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,gap:8,flexWrap:"wrap"}}>
                  <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("日期 / 時間", "Date / time")}</p>
                  <input
                    type="date"
                    value={wf.date || dateNow()}
                    onChange={e => setWf({ date: e.target.value })}
                    style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 10px",fontSize:13,fontWeight:800,color:"#4A4A68",outline:"none"}}
                  />
                </div>
                {/* Collapsed view — when a calendar slot was tapped we
                    already know the time. Show it as a single pill +
                    「編輯」 to expand the full grid only on demand. */}
                {wf._prefilledSlot && wf.time ? (
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 14px",borderRadius:14,background:"var(--c-lighter)",border:"2px solid var(--c-primary)"}}>
                    <span style={{fontSize:18,fontWeight:900,color:"var(--c-primary)"}}>{wf.time}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#8A8A9E",flex:1}}>{zhEn("已預選時段", "Slot preselected")}</span>
                    <button
                      type="button"
                      onClick={() => setWf({ _prefilledSlot: false })}
                      style={{background:"#fff",border:"1px solid var(--c-border)",borderRadius:99,padding:"6px 12px",fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor:"pointer"}}
                    >✏️ {i18nT("common.edit")}</button>
                  </div>
                ) : (
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(80px, 1fr))",gap:6}}>
                    {slots.length === 0 ? (
                      <p style={{fontSize:12,color:"#AFAFC4",fontStyle:"italic",margin:0,gridColumn:"1/-1"}}>{zhEn("暫無可用時段", "No available slots")}</p>
                    ) : (
                      slots.map(slot => {
                        const selected = wf.time === slot.time;
                        return (
                          <button
                            key={slot.time}
                            type="button"
                            title={!slot.available ? i18nT("wi.slotTaken") : ""}
                            disabled={!slot.available}
                            onClick={() => slot.available && setWf({ time:slot.time })}
                            style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"10px 10px",borderRadius:10,border:"2px solid "+(selected?"var(--c-primary)":(slot.available?"var(--c-light)":"#E5E7EB")),background:selected?"var(--c-primary)":(slot.available?"#FFFDFD":"#F9FAFB"),cursor:slot.available?"pointer":"not-allowed",opacity:slot.available?1:0.6,fontWeight:900,fontSize:12,color:selected?"#fff":(slot.available?"#4A4A68":"#AFAFC4"),boxShadow:selected?"0 4px 12px var(--c-primary-30)":"none"}}
                          >
                            {slot.time}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>}

              {selectedSvc && (() => {
                const svcDur = selectedSvc.estimatedMinutes || 60;
                const eff = wf.customDuration || svcDur;
                const fmt = (m) => (m % 30 === 0 && m >= 60) ? (m/60) + "hr" : m + "min";
                return (
                  <div>
                    <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{i18nT("wi.duration")}{wf.customDuration ? <span style={{color:"var(--c-primary)"}}> · {i18nT("wi.customDur")}</span> : <span style={{color:"#AFAFC4"}}> · {svcDur}m</span>}</p>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                      {[30,45,60,75,90,120,150,180].map(m => {
                        const on = eff === m;
                        return <button key={m} onClick={() => setWf({ customDuration: m === svcDur ? null : m })} style={{padding:"8px 13px",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:on?"var(--c-primary)":"var(--c-light)",color:on?"#fff":"var(--c-primary)",cursor:"pointer"}}>{fmt(m)}</button>;
                      })}
                      <input type="number" min="15" step="15" value={wf.customDuration || ""} placeholder={i18nT("wi.custom")} onChange={e => { const v = parseInt(e.target.value); setWf({ customDuration: (!v || v === svcDur) ? null : v }); }} style={{width:64,border:"2px solid var(--c-light)",borderRadius:10,padding:"7px 8px",fontSize:13,fontWeight:800,outline:"none",color:"#4A4A68",textAlign:"center",boxSizing:"border-box"}} />
                      {wf.customDuration && <button onClick={() => setWf({ customDuration: null })} style={{padding:"7px 10px",borderRadius:99,fontSize:12,fontWeight:800,border:"none",background:"#FEF2F2",color:"#DC2626",cursor:"pointer"}}>{zhEn("↺ 還原", "↺ Reset")}</button>}
                    </div>
                    {selectedSvc.bufferMinutes ? <p style={{fontSize:11,color:"#AFAFC4",margin:"6px 0 0",fontWeight:700}}>{zhEn("另加", "Plus ")} {selectedSvc.bufferMinutes} {zhEn("分鐘準備時間", " min prep time")}</p> : null}
                  </div>
                );
              })()}

              <div>
                <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("備註（選填）", "Notes (optional)")}</p>
                <textarea placeholder={i18nT("wi.allergiesEg")} value={wf.staffRemark||""} onChange={e => setWf({ staffRemark:e.target.value })} style={{width:"100%",minHeight:60,border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:13,fontWeight:600,outline:"none",color:"#4A4A68",resize:"vertical",fontFamily:"inherit",lineHeight:1.4,boxSizing:"border-box"}} />
              </div>
            </div>}

            {/* ── 6. DISCOUNT ── */}
            {wf.svcId && <div style={{background:"#FFFBEB",borderRadius:16,padding:12,border:"2px solid #FEF3C7"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <p style={{fontSize:12,fontWeight:800,color:"#92400E",margin:0}}>{zhEn("💰 折扣（選填）", "💰 Discount (optional)")}</p>
                {discValue > 0 && <span style={{fontSize:13,fontWeight:900,color:"#D97706"}}>{"−$" + discValue}</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"90px 1fr 1fr",gap:6}}>
                <select value={wf.discountType} onChange={e => setWf({ discountType:e.target.value })} style={{border:"2px solid #FEF3C7",borderRadius:10,padding:"6px",fontSize:12,fontWeight:800,outline:"none",background:"#fff",color:"#4A4A68"}}>
                  <option value="percent">{zhEn("% 百分", "% percent")}</option>
                  <option value="fixed">{zhEn("$ 固定", "$ fixed")}</option>
                </select>
                <input type="number" min="0" placeholder="0" value={wf.discountAmount||""} onChange={e => setWf({ discountAmount:parseInt(e.target.value)||0 })} style={{border:"2px solid #FEF3C7",borderRadius:10,padding:"6px 10px",fontSize:13,fontWeight:800,outline:"none",width:"100%",boxSizing:"border-box",color:"#4A4A68"}} />
                <select value={wf.discountReason} onChange={e => setWf({ discountReason:e.target.value })} style={{border:"2px solid #FEF3C7",borderRadius:10,padding:"6px",fontSize:12,fontWeight:700,outline:"none",background:"#fff",color:"#4A4A68"}}>
                  <option value="">{zhEn("原因…", "Reason…")}</option>
                  <option value="VIP老客">{i18nT("wi.tagVip")}</option>
                  <option value="朋友介紹">{i18nT("wi.tagReferral")}</option>
                  <option value="補償">{i18nT("wi.tagCompensation")}</option>
                  <option value="試做">{i18nT("wi.tagTrial")}</option>
                  <option value="員工親友">{i18nT("wi.tagFriend")}</option>
                  <option value="活動">{i18nT("wi.tagEvent")}</option>
                </select>
              </div>
            </div>}

            {/* ── PACKAGE PICKER ──
                When an existing customer has eligible packages (active +
                visits remaining + not expired + service matches the
                package's serviceIds) the owner can pick one to redeem.
                The pick lives on wf.customerPackageId; the apt insert
                trigger does the actual decrement so this stays
                client-side display only. */}
            {(() => {
              if (!selectedCust || !wf.svcId) return null;
              const allCps = (tenantCustomerPackages || []).filter(c =>
                c.customerId === selectedCust.id &&
                c.enabled !== false &&
                !c.refundedAt &&
                Number(c.remainingVisits) > 0 &&
                (!c.expiresAt || new Date(c.expiresAt).getTime() > Date.now())
              );
              if (allCps.length === 0) return null;
              // Annotate every package with WHY it's ineligible so the
              // owner sees the full inventory and understands the
              // mismatch instead of wondering "why is my customer's
              // 10 送 2 not showing up?". Reasons rank by user-visible
              // severity: wrong pet → wrong service → fine.
              const apt = wf.petId ? state.pets[wf.petId] : null;
              const annotated = allCps.map(cp => {
                const def = servicePackages.find(p => p.id === cp.packageId);
                const sids = def?.serviceIds || [];
                let reason = null;
                if (cp.petId && wf.petId && cp.petId !== wf.petId) {
                  const boundPet = state.pets[cp.petId];
                  reason = zhEn("綁定「" + (boundPet?.name || "另一隻") + "」(" + (boundPet?.breed || "") + (boundPet?.weight ? " " + boundPet.weight + "kg" : "") + ")", "Tied to \"" + (boundPet?.name || "another pet") + "\" (" + (boundPet?.breed || "") + (boundPet?.weight ? " " + boundPet.weight + "kg" : "") + ")");
                } else if (sids.length > 0 && !sids.includes(wf.svcId)) {
                  const svc = (tenant?.services || []).find(s => sids.includes(s.id));
                  reason = zhEn("只適用「" + (svc?.name || "其他服務") + "」", "Only valid for \"" + (svc?.name || "another service") + "\"");
                }
                return { cp, def, reason };
              });
              const picked = wf.customerPackageId;
              const hasEligible = annotated.some(a => !a.reason);
              return (
                <div style={{background:"#F0FDF4",borderRadius:16,padding:"12px 14px",border:"2px solid #BBF7D0",display:"flex",flexDirection:"column",gap:8}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#166534",margin:0}}>📦 {i18nT("wi.package")}{apt ? " · " + apt.name : ""}</p>
                  {!hasEligible && (
                    <p style={{fontSize:11,color:"#92400E",background:"#FFFBEB",padding:"6px 10px",borderRadius:8,margin:0,fontWeight:700,border:"1px solid #FDE68A"}}>{zhEn("⚠ 此寵物 / 服務暫無可用套票", "⚠ No package available for this pet / service")}</p>
                  )}
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {annotated.map(({ cp, def, reason }) => {
                      const sel = picked === cp.id;
                      const ineligible = !!reason;
                      return (
                        <button
                          key={cp.id}
                          type="button"
                          onClick={() => {
                            if (ineligible) { toast("⚠ " + def?.name + " · " + reason, "warning"); return; }
                            setWf({ customerPackageId: sel ? null : cp.id });
                          }}
                          title={reason || ""}
                          style={{padding:"6px 12px",borderRadius:99,border:"2px solid " + (sel ? "#059669" : ineligible ? "#E5E7EB" : "#BBF7D0"),background: sel ? "#059669" : ineligible ? "#F9FAFB" : "#fff",color: sel ? "#fff" : ineligible ? "#9CA3AF" : "#166534",fontSize:12,fontWeight:800,cursor:"pointer",textDecoration: ineligible ? "line-through" : "none",position:"relative"}}
                        >
                          {sel ? "✓ " : ""}{def?.name || zhEn("套票", "Package")} {zhEn("· 剩", "· ")} {cp.remainingVisits} {zhEn("次", "times")}
                          {ineligible && <span style={{marginLeft:6,fontSize:10,fontWeight:900,color:"#DC2626",background:"#FEF2F2",padding:"1px 6px",borderRadius:99,textDecoration:"none",display:"inline-block"}}>{zhEn("不適用", "N/A")}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {picked && <p style={{fontSize:11,color:"#166534",margin:0,fontWeight:700,opacity:0.85}}>{zhEn("主要服務由套票抵；只收 SPA / 加購 / 附加費部分。", "The main service is covered by the package; only SPA / add-ons / surcharges are charged.")}</p>}
                </div>
              );
            })()}

            {/* ── WALLET DEDUCT TOGGLE ──
                Only shown when an existing customer is picked AND they
                have a wallet balance for this tenant. Defaults off so
                the staff doesn't accidentally drain a wallet. */}
            {(() => {
              if (!selectedCust || !pd) return null;
              const mem = (state.memberships || []).find(m => m.userId === selectedCust.id && m.tenantId === user.tenantId);
              const wallet = Number(mem?.wallet || 0);
              if (wallet <= 0) return null;
              const useW = !!wf.useWallet;
              const deduct = useW ? Math.min(wallet, finalTotal) : 0;
              return (
                <div style={{background:"#EFF6FF",borderRadius:16,padding:"12px 14px",border:"2px solid #BFDBFE",display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:13,fontWeight:800,color:"#1E40AF",margin:0}}>💳 {i18nT("walkin.useWallet")}</p>
                    <p style={{fontSize:11,fontWeight:700,color:"#1E40AF",margin:"2px 0 0",opacity:0.85}}>
                      {zhEn("餘額 $", "Balance $")}{wallet}{useW ? zhEn(` · 扣 $${deduct}，尾款 $${finalTotal - deduct}`, ` · deduct $${deduct}, remaining $${finalTotal - deduct}`) : ""}
                    </p>
                  </div>
                  <Toggle on={useW} onChange={v => setWf({ useWallet: v })} />
                </div>
              );
            })()}

            {/* ── TOTAL ── */}
            {pd && (() => {
              const mem = selectedCust ? (state.memberships || []).find(m => m.userId === selectedCust.id && m.tenantId === user.tenantId) : null;
              const wallet = Number(mem?.wallet || 0);
              const walletDeduct = wf.useWallet && wallet > 0 ? Math.min(wallet, finalTotal) : 0;
              const balanceDue = finalTotal - walletDeduct;
              return (
                <div style={{background:"var(--c-lighter)",borderRadius:16,padding:14,border:"2px solid var(--c-border)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6A6A85",fontWeight:700}}><span>{i18nT("summary.mainService")}</span><span>{"$"+pd.total}</span></div>
                  {addonPrice > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6A6A85",fontWeight:700,marginTop:2}}><span>{i18nT("wi.addonsRow") + (wf.addons?.length||0)}</span><span>{"$"+addonPrice}</span></div>}
                  {discValue > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#D97706",fontWeight:800,marginTop:2}}><span>{i18nT("walkin.discount")+(wf.discountReason?zhEn("（", " (")+wf.discountReason+zhEn("）", ")"):"")}</span><span>{"−$"+discValue}</span></div>}
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:17,fontWeight:900,marginTop:8,paddingTop:8,borderTop:"2px dashed var(--c-border)"}}><span>{i18nT("wi.subtotal")}</span><span style={{color:"var(--c-primary)"}}>{"$"+finalTotal}</span></div>
                  {walletDeduct > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#1E40AF",fontWeight:800,marginTop:4}}><span>{i18nT("wi.walletDeduct")}</span><span>{"−$"+walletDeduct}</span></div>}
                  {walletDeduct > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:900,marginTop:4,paddingTop:4,borderTop:"1px dashed var(--c-border)"}}><span style={{color:"#4A4A68"}}>{i18nT("wi.balance")}</span><span style={{color:"#D97706"}}>{"$"+balanceDue}</span></div>}
                </div>
              );
            })()}

            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:4}}>
              <Btn disabled={walkInBusy} onClick={() => submitWalkIn("pending")} style={{width:"100%",padding:"16px",background:"#10B981",boxShadow:"0 8px 24px rgba(16,185,129,0.3)"}}>
                {walkInBusy ? <><Spinner /> {zhEn("儲存中…", "Saving…")}</> : <><Check size={18} strokeWidth={3} /> {i18nT("walkin.submit")}{pd?" · $"+finalTotal:""}</>}
              </Btn>
              {/* #4 Shortcut: customer already standing at the counter so the
                  most common follow-up action ("登記 → 即刻開始美容") is
                  one tap instead of two. status="grooming" + startedAt set,
                  so the grooming timer kicks in immediately. */}
              {walkInForm?.date === dateNow() || !walkInForm?._prefilledSlot ? <Btn variant="secondary" disabled={walkInBusy} onClick={() => submitWalkIn("grooming")} style={{width:"100%",padding:"14px"}}>
                {walkInBusy ? <><Spinner /> {zhEn("儲存中…", "Saving…")}</> : <>✂️ {i18nT("walkin.submitStart")}</>}
              </Btn> : null}
              {/* Save + book another for the SAME customer (eg. family
                  brought two pets). Auto-advances `time` to the next
                  available slot for the chosen staff so the user isn't
                  re-presented with the now-occupied original time. */}
              <Btn variant="ghost" disabled={walkInBusy} onClick={() => submitWalkIn("pending", { bookAnother: true })} style={{width:"100%",padding:"12px",fontSize:13,color:"var(--c-primary)"}}>
                {walkInBusy ? <><Spinner /> {zhEn("儲存中…", "Saving…")}</> : <>➕ {i18nT("walkin.submitAndAnother")}</>}
              </Btn>
            </div>
          </div>;
        })()}
      </BottomSheet>

      {/* Price overview — rendered in-app in an iframe (PWA blocks window.open).
          The iframe's own 列印 button prints just the table. */}
      {overviewHtml && (
        <div style={{position:"fixed",inset:0,zIndex:650,background:"#fff",display:"flex",flexDirection:"column"}}>
          {/* Own header bar so the close button never overlaps the price-list
              toolbar inside the iframe (it was getting hidden behind it). */}
          <div style={{flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 14px",background:"var(--c-primary)",color:"#fff",boxShadow:"0 2px 10px rgba(0,0,0,0.15)"}}>
            <span style={{fontSize:15,fontWeight:900}}>📊 {T("價目表預覽","Price list preview")}</span>
            <button onClick={() => setOverviewHtml(null)} style={{background:"rgba(255,255,255,0.22)",border:"1px solid rgba(255,255,255,0.45)",color:"#fff",borderRadius:99,padding:"7px 18px",fontSize:14,fontWeight:900,cursor:"pointer"}}>✕ {T("關閉","Close")}</button>
          </div>
          <iframe title="price-overview" srcDoc={overviewHtml} allow="web-share" style={{flex:1,width:"100%",border:"none"}} />
        </div>
      )}

      {/* Bulk price matrix — inline-editable grid (services × size tiers).
          Edits save straight to state (auto-synced). Breeds / 上門 / 不接 stay
          in the per-service editor. */}
      {tplPickerOpen && tenant && (
        <TemplatePicker tenant={tenant} tenantId={user.tenantId} setState={setState} onClose={() => setTplPickerOpen(false)} />
      )}
      {bulkOpen && (() => {
        const tiers = getShopTiers(tenant);
        const split = tenant?.pricingConfig?.coatSplit === true;
        const groupOf = (s) => { if (s.kind === "spa") return 3; const ty = (Array.isArray(s.petTypes)&&s.petTypes.length)?s.petTypes:[s.petType||"dog"]; return (ty.includes("cat") && !ty.includes("dog")) ? 2 : 1; };
        const gLabel = { 1:zhEn("🐶 狗狗", "🐶 Dogs"), 2:zhEn("🐱 貓貓", "🐱 Cats"), 3:"💆 SPA" };
        const svcs = (tenant?.services||[]).filter(s => s && s.enabled !== false && (s.pricingMode === "matrix" || s.matrixPricing?.byTier))
          .sort((a,b) => (groupOf(a)-groupOf(b)) || String(a.name||"").localeCompare(String(b.name||""),"zh-Hant"));
        const num = (v) => v === "" ? "" : (parseInt(v) || 0);
        const breeds = (Array.isArray(tenant?.specialBreeds) ? tenant.specialBreeds : []).filter(b => b && (b.name || (b.names||[])[0]));
        const setBT = (svcId, tid, k, v) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id !== user.tenantId ? t : { ...t, services: (t.services||[]).map(sv => sv.id !== svcId ? sv : { ...sv, pricingMode:"matrix", matrixPricing: { ...(sv.matrixPricing||{}), byTier: { ...((sv.matrixPricing||{}).byTier||{}), [tid]: { ...(((sv.matrixPricing||{}).byTier||{})[tid]||{}), [k]: v } } } }) }) }));
        const setBB = (svcId, bid, tid, k, v) => setState(s => ({ ...s, tenants: s.tenants.map(t => t.id !== user.tenantId ? t : { ...t, services: (t.services||[]).map(sv => { if (sv.id !== svcId) return sv; const mp = sv.matrixPricing||{}; const bb = mp.byBreed||{}; const br = bb[bid]||{}; return { ...sv, pricingMode:"matrix", matrixPricing: { ...mp, byBreed: { ...bb, [bid]: { ...br, [tid]: { ...(br[tid]||{}), [k]: v } } } } }; }) }) }));
        const setPair = (kind, svcId, idA, tid, v) => kind === "tier" ? setBT(svcId, tid, "short", v) : setBB(svcId, idA, tid, "short", v); // single-coat helper
        const cellInputs = (kind, svcId, idA, tid, e) => {
          if (e && e.na) return <span style={{color:"#DC2626",fontWeight:900}}>✕</span>;
          if (e && e.quote) return <span style={{color:"#0E7C66",fontWeight:900,fontSize:12}}>{T("面議","Quote")}</span>;
          const onCh = (k, val) => kind === "tier" ? setBT(svcId, tid, k, num(val)) : setBB(svcId, idA, tid, k, num(val));
          const onSingle = (val) => { const v = num(val); if (kind === "tier") setBT(svcId, tid, "short", v), setBT(svcId, tid, "long", v); else setBB(svcId, idA, tid, "short", v), setBB(svcId, idA, tid, "long", v); };
          const ph = kind === "breed" ? T("一般","—") : "$";
          if (split) return <div style={{display:"flex",gap:3}}>
            <input value={e?.short ?? ""} onChange={ev=>onCh("short",ev.target.value)} inputMode="numeric" placeholder={ph} style={inp} />
            <input value={e?.long ?? ""} onChange={ev=>onCh("long",ev.target.value)} inputMode="numeric" placeholder={ph} style={inp} />
          </div>;
          return <input value={e?.short ?? ""} onChange={ev=>onSingle(ev.target.value)} inputMode="numeric" placeholder={ph} style={inp} />;
        };
        const inp = { width:"100%",border:"1.5px solid var(--c-light)",borderRadius:8,padding:"5px 4px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",boxSizing:"border-box" };
        const colMin = split ? 96 : 64;
        let lastG = null;
        return (
          <div style={{position:"fixed",inset:0,zIndex:600,background:"rgba(0,0,0,0.4)",display:"flex",flexDirection:"column"}}>
            <div style={{background:"#fff",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,boxShadow:"0 2px 10px rgba(0,0,0,0.08)"}}>
              <div>
                <p style={{fontSize:17,fontWeight:900,color:"#3A3A56",margin:0}}>✏️ {T("批量定價","Bulk pricing")}</p>
                <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:"2px 0 0"}}>{T("直接喺格仔改價，自動儲存。特殊品種 / 上門 / 不接喺服務內設定。","Edit prices in-cell — auto-saved. Breeds / home-visit / N-A in the service editor.")}</p>
              </div>
              <button onClick={() => setBulkOpen(false)} style={{background:"var(--c-primary)",color:"#fff",border:"none",borderRadius:99,padding:"9px 18px",fontWeight:800,fontSize:14,cursor:"pointer",flexShrink:0}}>{T("完成","Done")}</button>
            </div>
            <div style={{flex:1,overflow:"auto",padding:"12px",background:"var(--c-light)"}}>
              <table style={{borderCollapse:"separate",borderSpacing:0,background:"#fff",borderRadius:14,overflow:"hidden",fontSize:13,minWidth:"100%"}}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{position:"sticky",left:0,top:0,zIndex:4,background:"#F3F8FF",textAlign:"left",padding:"8px 12px",minWidth:170,fontSize:12,color:"#1E40AF",fontWeight:900}}>{T("服務","Service")}</th>
                    <th colSpan={tiers.length} style={{position:"sticky",top:0,background:"#EAF2FF",color:"#1E40AF",fontWeight:900,fontSize:12,padding:"6px"}}>{T("一般體型","By size")}</th>
                    {breeds.map(b => <th key={b.id} colSpan={tiers.length} style={{position:"sticky",top:0,background:"#F3EEFF",color:"#6D28D9",fontWeight:900,fontSize:12,padding:"6px",borderLeft:"2px solid #EDE7FA"}}>{specialBreedType(b)==="cat"?"🐱":"🐩"} {b.name || (b.names||[])[0]}</th>)}
                  </tr>
                  <tr>
                    {tiers.map(t => <th key={t.id} style={{position:"sticky",top:32,background:"#F3F8FF",padding:"5px 6px",fontSize:11,color:"#1E40AF",fontWeight:800,minWidth:colMin,textAlign:"center"}}>{t.label}<div style={{fontSize:9,color:"#9AA0B4",fontWeight:600}}>{t.minW}–{t.maxW}kg{split?zhEn(" · 短/長"," · short/long"):""}</div></th>)}
                    {breeds.map(b => tiers.map(t => <th key={b.id+t.id} style={{position:"sticky",top:32,background:"#FAF7FF",padding:"5px 6px",fontSize:11,color:"#6D28D9",fontWeight:800,minWidth:colMin,textAlign:"center",borderLeft: t.id===tiers[0].id?"2px solid #EDE7FA":undefined}}>{t.label}<div style={{fontSize:9,color:"#B7A7DE",fontWeight:600}}>{split?zhEn("短/長","S/L"):"$"}</div></th>))}
                  </tr>
                </thead>
                <tbody>
                  {svcs.map(svc => {
                    const g = groupOf(svc); const header = g !== lastG ? (lastG = g, gLabel[g]) : null;
                    const bt = svc.matrixPricing?.byTier || {};
                    const bb = svc.matrixPricing?.byBreed || {};
                    const totalCols = 1 + tiers.length + breeds.length*tiers.length;
                    return (
                      <Fragment key={svc.id}>
                        {header && <tr><td colSpan={totalCols} style={{background:"linear-gradient(90deg,#FBF3F6,#fff)",fontWeight:900,color:"var(--c-primary)",fontSize:12.5,padding:"7px 12px",position:"sticky",left:0}}>{header}</td></tr>}
                        <tr>
                          <td style={{position:"sticky",left:0,background:"#fff",textAlign:"left",fontWeight:800,color:"#4A4A68",padding:"6px 12px",borderBottom:"1px solid #F1EEF4",boxShadow:"2px 0 6px rgba(0,0,0,0.03)",fontSize:12.5}}>{svc.kind==="spa"?"💆 ":""}{svc.name}</td>
                          {tiers.map(t => <td key={t.id} style={{padding:"5px 6px",borderBottom:"1px solid #F1EEF4"}}>{cellInputs("tier", svc.id, null, t.id, bt[t.id])}</td>)}
                          {breeds.map(b => { const applies = breedAppliesToService(b, svc); return tiers.map(t => <td key={b.id+t.id} style={{padding:"5px 6px",borderBottom:"1px solid #F1EEF4",background: applies?"#FCFAFF":"#F6F6F8",borderLeft: t.id===tiers[0].id?"2px solid #EDE7FA":undefined}}>{applies ? cellInputs("breed", svc.id, b.id, t.id, bb[b.id]?.[t.id]) : <span style={{color:"#CBCBD6",fontWeight:700}}>—</span>}</td>); })}
                        </tr>
                      </Fragment>
                    );
                  })}
                  {svcs.length === 0 && <tr><td colSpan={1+tiers.length+breeds.length*tiers.length} style={{textAlign:"center",padding:"30px",color:"#AFAFC4",fontWeight:700}}>{T("未有矩陣定價服務","No tier-priced services")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <BottomSheet open={!!editSvc} onClose={() => setEditSvc(null)} title={
        editSvc?.kind === "spa"
          ? (editSvc?.id ? i18nT("svc.sheet.spaEdit") : i18nT("svc.sheet.spaAdd"))
          : (editSvc?.id ? i18nT("svc.sheet.edit") : i18nT("svc.sheet.add"))
      }>
        {editSvc && (() => {
          const pm = editSvc.pricingMode || "static";
          // Contiguous from 0.1kg, chained at +0.1 — same convention as
          // seedDefaults so any pet weight falls into some band by default.
          const defaultSizes = [
            { id:"sz"+uid(), label:zhEn("小型", "Small"),  minW:0.1,  maxW:5,   shortHair:0, longHair:0, durationMin:60 },
            { id:"sz"+uid(), label:zhEn("中型", "Medium"),  minW:5.1,  maxW:10,  shortHair:0, longHair:0, durationMin:75 },
            { id:"sz"+uid(), label:zhEn("大型", "Large"),  minW:10.1, maxW:15,  shortHair:0, longHair:0, durationMin:90 },
            { id:"sz"+uid(), label:zhEn("超大", "XL"),  minW:15.1, maxW:20,  shortHair:0, longHair:0, durationMin:120 },
            { id:"sz"+uid(), label:zhEn("巨型", "XXL"),  minW:20.1, maxW:999, shortHair:0, longHair:0, durationMin:150 },
          ];
          const mp = editSvc.matrixPricing || { sizes:defaultSizes, hugeExtra:{ baseW:35, perStep:5, stepPrice:100 }, specialBreeds:[] };
          const setSvcField = (k,v) => setEditSvc(s=>({...s,[k]:v}));
          const setMatrix = fn => setEditSvc(s=>({...s,matrixPricing:{...mp,...fn(mp)}}));
          // Auto-chain helper: when row i's maxW changes, the next row's minW
          // is bumped to maxW + 0.1 so the owner doesn't have to think about
          // chaining ranges manually.
          const patchRow = (rows, i, k, v) => rows.map((r, ri) => {
            if (ri === i) return { ...r, [k]: v };
            if (ri === i + 1 && k === "maxW" && typeof v === "number" && v > 0) {
              const newMin = Math.round((v + 0.1) * 10) / 10;
              if (r.minW < newMin || r.minW === 0) return { ...r, minW: newMin };
            }
            return r;
          });
          const setSizeRow = (i, k, v) => setMatrix(m => ({ sizes: patchRow(m.sizes, i, k, v) }));
          const addSizeRow = () => setMatrix(m => {
            const last = m.sizes[m.sizes.length - 1];
            const seedMin = last && last.maxW > 0 ? Math.round((last.maxW + 0.1) * 10) / 10 : 0;
            const seedMax = seedMin > 0 ? seedMin + 4.9 : 0;
            return { sizes: [...m.sizes, { id:"sz"+uid(), label:zhEn("新增體型", "New size"), minW:seedMin, maxW:seedMax, shortHair:0, longHair:0, durationMin:60 }] };
          });
          const delSizeRow = (i) => setMatrix(m=>({sizes:m.sizes.filter((_,ri)=>ri!==i)}));
          const sbList = mp.specialBreeds || [];
          const addBreed = () => setMatrix(m=>({specialBreeds:[...(m.specialBreeds||[]),{id:"sb"+uid(),name:zhEn("新品種", "New breed"),extraNote:"",sizes:[{id:"sz"+uid(),label:zhEn("預設", "Default"),minW:1,maxW:999,shortHair:0,longHair:0,durationMin:60}]}]}));
          const delBreed = (idx) => setMatrix(m=>({specialBreeds:(m.specialBreeds||[]).filter((_,bi)=>bi!==idx)}));
          const setBreedField = (idx,k,v) => setMatrix(m=>({specialBreeds:(m.specialBreeds||[]).map((b,bi)=>bi===idx?{...b,[k]:v}:b)}));
          const setBreedSize = (bidx, sidx, k, v) => setMatrix(m => ({
            specialBreeds: (m.specialBreeds || []).map((b, bi) =>
              bi === bidx ? { ...b, sizes: patchRow(b.sizes || [], sidx, k, v) } : b
            ),
          }));
          const addBreedSize = (bidx) => setMatrix(m => ({
            specialBreeds: (m.specialBreeds || []).map((b, bi) => {
              if (bi !== bidx) return b;
              const sizes = b.sizes || [];
              const last = sizes[sizes.length - 1];
              const seedMin = last && last.maxW > 0 ? Math.round((last.maxW + 0.1) * 10) / 10 : 0;
              const seedMax = seedMin > 0 ? seedMin + 4.9 : 0;
              return { ...b, sizes: [...sizes, { id:"sz"+uid(), label:zhEn("新增體型", "New size"), minW:seedMin, maxW:seedMax, shortHair:0, longHair:0, durationMin:60 }] };
            }),
          }));
          const delBreedSize = (bidx,sidx) => setMatrix(m=>({specialBreeds:(m.specialBreeds||[]).map((b,bi)=>bi===bidx?{...b,sizes:(b.sizes||[]).filter((_,ri)=>ri!==sidx)}:b)}));
          // Validate a list of {minW,maxW,label} — returns array of warning strings.
          const validateSizes = (rows) => {
            const out = [];
            if (!rows || rows.length === 0) return out;
            for (let i = 0; i < rows.length; i++) {
              const r = rows[i];
              const lbl = r.label || zhEn("第" + (i + 1) + "行", "Row " + (i + 1));
              if (r.minW < 0 || r.maxW < 0) { out.push(zhEn("⚠️ 「" + lbl + "」體重不可為負數", "⚠️ \"" + lbl + "\": weight can't be negative")); continue; }
              if (r.maxW > 0 && r.minW > r.maxW) { out.push(zhEn("⚠️ 「" + lbl + "」最小值大於最大值，請對調", "⚠️ \"" + lbl + "\": minimum is greater than maximum, please swap")); continue; }
            }
            const sorted = [...rows].sort((a, b) => a.minW - b.minW);
            for (let i = 0; i < sorted.length - 1; i++) {
              const a = sorted[i], b = sorted[i + 1];
              if (a.maxW >= b.minW && a.maxW > 0 && b.minW > 0) {
                out.push(zhEn("⚠️ 「" + a.label + "」(" + a.minW + "-" + a.maxW + ") 同「" + b.label + "」(" + b.minW + "-" + b.maxW + ") 重疊", "⚠️ \"" + a.label + "\" (" + a.minW + "-" + a.maxW + ") overlaps \"" + b.label + "\" (" + b.minW + "-" + b.maxW + ")"));
              } else if (b.minW - a.maxW > 0.2 && a.maxW > 0 && b.minW > 0) {
                out.push(zhEn("⚠️ " + (a.maxW + 0.1).toFixed(1) + "-" + (b.minW - 0.1).toFixed(1) + "kg 冇對應價，會用底價 $" + (editSvc.basePrice || 0), "⚠️ " + (a.maxW + 0.1).toFixed(1) + "-" + (b.minW - 0.1).toFixed(1) + "kg has no price; the base price $" + (editSvc.basePrice || 0) + " will be used"));
              }
            }
            return out;
          };
          return <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <Input label={editSvc.kind === "spa" ? zhEn("SPA 名稱", "SPA name") : zhEn("服務名稱", "Service name")} value={editSvc.name} onChange={e=>setSvcField("name",e.target.value)} />
            {editSvc.kind === "spa" && (
              <div>
                <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("說明（顯示喺客戶預約畫面）", "Description (shown on the customer booking screen)")}</p>
                <textarea value={editSvc.description || ""} onChange={e=>setSvcField("description",e.target.value)} placeholder={zhEn("例：舒緩肌膚 SPA：溫泉鹽浴 + 深層滋潤護理，約 30 分鐘。", "e.g. Soothing skin SPA: hot-spring salt bath + deep moisturising treatment, about 30 min.")} style={{width:"100%",minHeight:80,border:"2px solid var(--c-light)",borderRadius:16,padding:"12px 14px",fontSize:13,fontWeight:600,outline:"none",color:"#4A4A68",resize:"vertical",fontFamily:"inherit",lineHeight:1.5,boxSizing:"border-box"}} />
              </div>
            )}
            {/* Included basic-care add-ons — free with this main service so
                they're never double-charged (e.g. 沖涼全套 includes 剪甲). */}
            {editSvc.kind !== "spa" && (() => {
              const _svcTypes = (Array.isArray(editSvc.petTypes) && editSvc.petTypes.length) ? editSvc.petTypes : [editSvc.petType || "dog"];
              const addonList = (tenant?.addons || []).filter(a => { const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"]; return pts.some(t => _svcTypes.includes(t)); });
              if (!addonList.length) return null;
              const inc = editSvc.includedAddonIds || [];
              const toggle = (id) => setEditSvc(s => { const cur = s.includedAddonIds || []; return { ...s, includedAddonIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] }; });
              return (
                <div style={{marginTop:4}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{T("已包含嘅基本護理（選填）","Included basic care (optional)","含まれる基本ケア（任意）")}</p>
                  <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"0 0 8px",lineHeight:1.5}}>{T("勾選呢個服務已包（免費）嘅加購，例如「沖涼全套」已含剪甲。客人／AI 揀到呢啲項目時唔會另外收費。","Tick add-ons already included free with this service (e.g. full bath includes nail trim); they won't be charged again.","この服務に含まれる加購を選択（別途課金しません）。")}</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {addonList.map(a => { const on = inc.includes(a.id); return (
                      <button key={a.id} type="button" onClick={() => toggle(a.id)} style={{padding:"7px 12px",borderRadius:99,border:"2px solid "+(on?"#10B981":"var(--c-light)"),background:on?"#ECFDF5":"#fff",color:on?"#065F46":"#8A8A9E",fontWeight:800,fontSize:12.5,cursor:"pointer"}}>{on?"✓ ":""}{a.name}</button>
                    ); })}
                  </div>
                </div>
              );
            })()}
            {/* Pet-type targeting. Regular services stay single-select
                (dog OR cat OR small, with distinct pricing). SPA services
                are multi-select — same warm-water bath / machine setup
                works for both dogs and cats, so the owner picks one row
                that covers both. Small animals don't get SPA at all.  */}
            {(() => {
              const isSpa = editSvc.kind === "spa";
              const currentTypes = Array.isArray(editSvc.petTypes) && editSvc.petTypes.length
                ? editSvc.petTypes
                : [editSvc.petType || "dog"];
              const OPTS = isSpa
                ? [["dog",zhEn("🐶 狗", "🐶 Dog")],["cat",zhEn("🐱 貓", "🐱 Cat")]]
                : [["dog",zhEn("🐶 狗", "🐶 Dog")],["cat",zhEn("🐱 貓", "🐱 Cat")],["small",zhEn("🐇 小動物", "🐇 Small animal")]];
              const togglePetType = (k) => {
                if (!isSpa) {
                  // Single-select: write both for migration + back-compat.
                  setEditSvc(d => ({ ...(d || {}), petType: k, petTypes: [k] }));
                  return;
                }
                // Multi-select: toggle this type in/out, but never let
                // the set drop to empty (a service with no pet types
                // would be invisible everywhere — confusing).
                const has = currentTypes.includes(k);
                const next = has ? currentTypes.filter(x => x !== k) : [...currentTypes, k];
                const safe = next.length === 0 ? [k] : next;
                setEditSvc(d => ({ ...(d || {}), petTypes: safe, petType: safe[0] }));
              };
              return (
                <div>
                  <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>
                    {zhEn("適用寵物種類", "Applies to pet types")}{isSpa && <span style={{fontSize:11,fontWeight:700,color:"#7C3AED",marginLeft:6}}>{zhEn("· 可多選", "· multi-select")}</span>}
                  </p>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {OPTS.map(([k, label]) => {
                      const on = currentTypes.includes(k);
                      return (
                        <button key={k} type="button" onClick={() => togglePetType(k)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>{on?"● ":""}{label}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <Input label={zhEn("底價 / 未設定時顯示價格 ($)", "Base price / price shown when not set ($)")} type="number" value={editSvc.basePrice} onChange={e=>setSvcField("basePrice",parseInt(e.target.value)||0)} />
            <div style={{display:"flex",gap:10}}>
              <div style={{flex:1}}>
                <Input label={T("預約時段長度 (分鐘)","Slot length (min)")} type="number" inputMode="numeric" value={editSvc.estimatedMinutes ?? ""} placeholder="60" onChange={e=>setSvcField("estimatedMinutes", e.target.value === "" ? null : (parseInt(e.target.value)||0))} />
              </div>
              <div style={{flex:1}}>
                <Input label={T("每節之間緩衝 (分鐘)","Buffer between (min)")} type="number" inputMode="numeric" value={editSvc.bufferMinutes ?? ""} placeholder="0" onChange={e=>setSvcField("bufferMinutes", e.target.value === "" ? null : (parseInt(e.target.value)||0))} />
              </div>
            </div>
            <p style={{fontSize:11,color:"#AFAFC4",margin:"-4px 0 4px",fontWeight:700,lineHeight:1.4}}>{T("「預約時段長度」決定每個預約佔用嘅時間。緩衝時間會自動加喺每個預約之後，預留清潔／準備（或上門車程）時間，避免預約太密。","'Slot length' sets how long each booking occupies. Buffer is added after each booking for cleanup/prep (or travel), so slots aren't packed too tight.")}</p>
            {(tenant?.serviceMode === "mobile" || tenant?.serviceMode === "both") && (
              <button type="button" onClick={() => setSvcField("mobileAvailable", !editSvc.mobileAvailable)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",textAlign:"left",background: editSvc.mobileAvailable ? "var(--c-lighter)" : "#fff",border:"2px solid "+(editSvc.mobileAvailable ? "var(--c-primary)" : "var(--c-light)"),borderRadius:14,padding:"12px 14px",cursor:"pointer",margin:"2px 0 4px"}}>
                <div style={{width:22,height:22,borderRadius:7,flexShrink:0,border:"2px solid "+(editSvc.mobileAvailable?"var(--c-primary)":"#C4C4D4"),background: editSvc.mobileAvailable?"var(--c-primary)":"#fff",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:900,fontSize:13}}>{editSvc.mobileAvailable ? "✓" : ""}</div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:0}}>🚗 {T("可上門服務","Available for home visit")}</p>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700,lineHeight:1.4}}>{T("客戶揀「上門」時，只會見到開咗呢個掣嘅服務","When a customer chooses Home visit, only services with this on are shown")}</p>
                </div>
              </button>
            )}
            <Sel label={zhEn("計價方式", "Pricing method")} value={pm} onChange={e=>setSvcField("pricingMode",e.target.value)}>
              <option value="static">{zhEn("固定價格", "Fixed price")}</option>
              <option value="matrix">{zhEn("體型 × 毛長矩陣計價 ⚡", "Size × coat-length matrix ⚡")}</option>
            </Sel>

            {pm==="matrix" && <>
              {/* ── Shared-tier price grid (preferred) ──────────────────────
                  Size tiers are defined once for the whole shop (defaults
                  until customised). Owner only types prices per tier:
                  短毛/長毛 (+ 上門 when the 可上門 toggle is on) + 時長.
                  Stored in matrixPricing.byTier[tierId]. */}
              {(() => {
                const tiers = getShopTiers(tenant);
                const split = tenant?.pricingConfig?.coatSplit === true || mp.coatSplit === true;
                const showMobileCol = !!editSvc.mobileAvailable;
                const bt = mp.byTier && typeof mp.byTier === "object" ? mp.byTier : {};
                const setTier = (tid, k, v) => setMatrix(m => ({ byTier: { ...(m.byTier || {}), [tid]: { ...((m.byTier || {})[tid] || {}), [k]: v } } }));
                const num = (v) => v === "" ? "" : (parseInt(v) || 0);
                const fnum = (v) => v === "" ? "" : (parseFloat(v) || 0);
                const cols = `minmax(60px,1.2fr) ${split ? "1fr 1fr" : "1fr"}${showMobileCol ? " 1fr" : ""} 52px 30px`;
                // Shop-level tiers (weight ranges) live on tenant.pricingConfig — shared by every service.
                // Cut-point model: the owner types ONLY each tier's max. Every min auto-derives from
                // the previous tier's max (+0.1) and the LAST tier's max is the shop cap
                // (tenant.maxPetWeightKg). This makes gaps/overlaps impossible by construction and
                // removes fiddly decimal (.1) typing. dataSync persists the tenant change.
                const TIER_FLOOR = 0.1;
                const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
                const shopMaxKg = Number(tenant?.maxPetWeightKg) || 0; // 0 = 未設 (∞)
                const normalizeTiers = (rows) => rows.map((t, i, arr) => {
                  const minW = i === 0 ? TIER_FLOOR : round1((Number(arr[i - 1].maxW) || 0) + 0.1);
                  const isLast = i === arr.length - 1;
                  const maxW = isLast ? (shopMaxKg || 999) : (Number(t.maxW) || 0);
                  return { ...t, minW, maxW };
                });
                const setTiers = (next) => setState(s => ({ ...s, tenants: s.tenants.map(tt => tt.id === user.tenantId ? { ...tt, pricingConfig: { ...(tt.pricingConfig || {}), tiers: normalizeTiers(next) } } : tt) }));
                // Normalise on open too — self-heals any legacy hand-typed tiers into a contiguous
                // chain and snaps the last tier to the current shop max.
                const openTierEdit = () => { setTiers(tiers.map(t => ({ ...t }))); setTierEditOpen(true); };
                const setTierLabel = (i, v) => setTiers(tiers.map((t, ti) => ti === i ? { ...t, label: v } : t));
                const setTierMax = (i, v) => setTiers(tiers.map((t, ti) => ti === i ? { ...t, maxW: v } : t));
                const addTier = () => { const last = tiers[tiers.length - 1]; const seedMax = round1((Number(last?.maxW) || 0) + 5) || 5; setTiers([...tiers, { id: "t" + uid(), label: zhEn("新分級", "New tier"), minW: 0, maxW: seedMax }]); };
                const delTier = (i) => setTiers(tiers.filter((_, ti) => ti !== i));
                // Contiguity is guaranteed, so the only residual typo is a non-ascending max (type 10
                // then 8 → its auto min 10.1 > 8) or a max at/above the shop cap. Warn on those.
                const tierWarnings = (() => {
                  const out = [];
                  normalizeTiers(tiers).forEach((t, i, norm) => {
                    if (i === norm.length - 1) return; // last tier's max is the auto shop cap
                    const lbl = t.label || (T("第", "#") + (i + 1));
                    if (!(Number(t.maxW) > Number(t.minW))) out.push(zhEn("⚠️ 「", "⚠️ \"") + lbl + zhEn("」", "\"") + T("最大 ", "max ") + t.maxW + T(" 必須大於 ", " must exceed ") + round1(t.minW) + "kg");
                    else if (shopMaxKg && Number(t.maxW) >= shopMaxKg) out.push(zhEn("⚠️ 「", "⚠️ \"") + lbl + zhEn("」", "\"") + t.maxW + T("kg 已達／超過全店最大體重 ", "kg reaches/exceeds shop max ") + shopMaxKg + "kg");
                  });
                  return out;
                })();
                return (
                  <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,gap:8,flexWrap:"wrap"}}>
                      <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:0}}>{T("體型定價（共用分級）","Size pricing (shared tiers)")}</p>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <button type="button" onClick={() => { const v = window.prompt(T("所有體型同一價錢，輸入金額：","One price for all sizes — enter amount:")); if (v === null) return; const n = parseInt(v) || 0; setMatrix(m => { const nx = {}; tiers.forEach(t => { const cur = (m.byTier||{})[t.id] || {}; nx[t.id] = cur.na ? cur : { ...cur, short:n, long:n }; }); return { byTier: nx }; }); }} style={{fontSize:11,fontWeight:800,color:"var(--c-primary)",background:"#fff",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"4px 10px",cursor:"pointer"}}>{T("全部同價","All same")}</button>
                        <button type="button" onClick={() => tierEditOpen ? setTierEditOpen(false) : openTierEdit()} style={{fontSize:11,fontWeight:800,color:"var(--c-primary)",background:"#fff",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"4px 10px",cursor:"pointer"}}>{tierEditOpen ? T("完成","Done") : T("編輯分級","Edit tiers")}</button>
                        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,color:"#6A6A85",cursor:"pointer"}}>
                          <input type="checkbox" checked={split} onChange={e => setMatrix(() => ({ coatSplit: e.target.checked }))} style={{width:16,height:16,accentColor:"var(--c-primary)"}} />
                          {T("分長短毛","Split coat")}
                        </label>
                      </div>
                    </div>
                    <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"0 0 10px",lineHeight:1.4}}>{T("體型分級（體重範圍）全店共用，喺「編輯分級」一次過設定；其他服務會跟住用。","Size tiers (weight ranges) are shared shop-wide — set once via Edit tiers; all services follow.")}{showMobileCol ? T("「上門」留空 = 用正價。"," Blank 上門 = use normal price.") : T("開咗上面「可上門」就會多一個上門價欄."," Turn on 可上門 above to add a home-visit price column.")}</p>
                    {tierEditOpen && (
                      <div style={{background:"#fff",border:"2px solid var(--c-border)",borderRadius:14,padding:12,marginBottom:12}}>
                        <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>{T("體型分級（全店共用 · 改一次所有服務通用）","Size tiers (shop-wide · edit once, applies to all services)")}</p>
                        <div style={{display:"grid",gridTemplateColumns:"minmax(60px,1.2fr) 64px 64px 36px",gap:6,marginBottom:4}}>
                          <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800}}>{T("名稱","Name")}</span>
                          <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{T("最小kg","Min kg")}</span>
                          <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{T("最大kg","Max kg")}</span>
                          <span />
                        </div>
                        {tiers.map((t, i) => {
                          const isLast = i === tiers.length - 1;
                          const roBox = { border:"2px solid transparent",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",width:"100%",boxSizing:"border-box",background:"#F3F4F6",color:"#9AA0AE",display:"flex",alignItems:"center",justifyContent:"center",height:32 };
                          return (
                          <div key={t.id} style={{display:"grid",gridTemplateColumns:"minmax(60px,1.2fr) 64px 64px 36px",gap:6,marginBottom:6,alignItems:"center"}}>
                            <input value={t.label} onChange={ev=>setTierLabel(i,ev.target.value)} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 8px",fontSize:13,fontWeight:700,outline:"none",width:"100%",boxSizing:"border-box"}} />
                            <div style={roBox} title={T("自動：上一級最大 +0.1","Auto: previous max +0.1")}>{round1(t.minW)}</div>
                            {isLast ? (
                              <div style={roBox} title={T("自動：全店最大體重（設定 → 店鋪政策）","Auto: shop max weight (Settings → Shop policy)")}>{shopMaxKg || "∞"}</div>
                            ) : (
                              <input value={t.maxW ?? ""} onChange={ev=>setTierMax(i,fnum(ev.target.value))} inputMode="decimal" placeholder="0" style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                            )}
                            <button type="button" onClick={()=>delTier(i)} disabled={tiers.length<=1} style={{background:"#FEF2F2",border:"none",borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",cursor:tiers.length<=1?"not-allowed":"pointer",opacity:tiers.length<=1?0.4:1}}><Trash2 size={13} color="#EF4444" strokeWidth={2.5}/></button>
                          </div>
                          );
                        })}
                        <Btn size="sm" variant="secondary" onClick={addTier}><Plus size={14} strokeWidth={3} /> {T("新增分級","Add tier")}</Btn>
                        {tierWarnings.length > 0 && (
                          <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:4}}>
                            {tierWarnings.map((w, wi) => <div key={wi} style={{fontSize:11,fontWeight:700,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"6px 8px"}}>{w}</div>)}
                          </div>
                        )}
                        <p style={{fontSize:10,color:"#AFAFC4",fontWeight:700,margin:"8px 0 0",lineHeight:1.5}}>{T("只需輸入每級「最大kg」。下一級最小值自動 +0.1；最後一級去到全店最大體重。","Type only each tier's max kg. The next min auto +0.1; the last tier extends to the shop max weight.")}{!shopMaxKg && T(" 未設最大體重 = 無上限 ∞（設定 → 店鋪政策）。"," No shop max set = unlimited ∞ (Settings → Shop policy).")}</p>
                      </div>
                    )}
                    <div style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:4}}>
                      <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800}}>{T("體型","Size")}</span>
                      {split ? <><span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("短毛","Short")}$</span><span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("長毛","Long")}$</span></> : <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("價格","Price")}$</span>}
                      {showMobileCol && <span style={{fontSize:11,color:"#7C3AED",fontWeight:800,textAlign:"center"}}>🚗{T("上門","Visit")}$</span>}
                      <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("時長","Time")}</span>
                      <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{T("狀態","State")}</span>
                    </div>
                    {tiers.map(t => {
                      const e = bt[t.id] || {};
                      const na = !!e.na; // 此體型不適用此服務
                      const quote = !na && !!e.quote; // 面議 — price settled in person
                      const off = na || quote;
                      const setState3 = (st) => setMatrix(m => { const cur = (m.byTier||{})[t.id]||{}; return { byTier: { ...(m.byTier||{}), [t.id]: { ...cur, na: st==="na", quote: st==="quote" } } }; });
                      const dim = off ? { opacity:0.4, pointerEvents:"none", background:"#F3F4F6" } : {};
                      const numInput = { border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box" };
                      return (
                        <div key={t.id} style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:6,alignItems:"center"}}>
                          <span style={{fontSize:13,fontWeight:800,color: off ? "#AFAFC4" : "#4A4A68", textDecoration: na ? "line-through" : "none"}}>{t.label}<span style={{fontSize:10,color:"#AFAFC4",fontWeight:700}}> {t.minW}-{Number(t.maxW) >= 999 ? "∞" : t.maxW}kg</span>{quote && <span style={{fontSize:10,color:"#0E7C66",fontWeight:800}}> · {T("面議","Quote")}</span>}</span>
                          {split ? (
                            <>
                              <input disabled={off} value={e.short ?? ""} onChange={ev=>setTier(t.id,"short",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":"0"} style={{...numInput,...dim}} />
                              <input disabled={off} value={e.long ?? ""} onChange={ev=>setTier(t.id,"long",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":"0"} style={{...numInput,...dim}} />
                            </>
                          ) : (
                            <input disabled={off} value={e.short ?? ""} onChange={ev=>{const v=num(ev.target.value); setMatrix(m=>({byTier:{...(m.byTier||{}),[t.id]:{...((m.byTier||{})[t.id]||{}),short:v,long:v}}}));}} inputMode="numeric" placeholder={off?"—":"0"} style={{...numInput,...dim}} />
                          )}
                          {showMobileCol && <input disabled={off} value={e.mobile ?? ""} onChange={ev=>setTier(t.id,"mobile",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":T("正價","Normal")} style={{...numInput,border:"2px solid #DDD6FE",background:"#FAF5FF",...dim}} />}
                          <input disabled={off} value={e.durationMin ?? ""} onChange={ev=>setTier(t.id,"durationMin",num(ev.target.value))} inputMode="numeric" placeholder={String(editSvc.estimatedMinutes||60)} style={{...numInput,...dim}} />
                          <button type="button" title={na ? T("不接此體型（按此切換：正常）","Not offered (tap to cycle: normal)") : quote ? T("面議（按此切換：不接）","Quote price (tap to cycle: not offered)") : T("正常（按此切換：面議）","Normal (tap to cycle: quote price)")} onClick={()=>setState3(na ? "normal" : quote ? "na" : "quote")} style={{border:"none",borderRadius:8,height:30,cursor:"pointer",fontSize:13,fontWeight:900,background: na ? "#FEE2E2" : quote ? "#D1FAE5" : "var(--c-light)", color: na ? "#DC2626" : quote ? "#0E7C66" : "#AFAFC4"}}>{na ? "✕" : quote ? zhEn("議","Quote") : "○"}</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ── Shared special-breed pricing ────────────────────────────
                  Breeds are defined once for the whole shop (tenant.specialBreeds);
                  each service stores only a price per breed (matrixPricing.byBreed).
                  Blank price = use the size-tier price; 不接 = not offered. */}
              {(() => {
                const breeds = Array.isArray(tenant?.specialBreeds) ? tenant.specialBreeds : [];
                const split = tenant?.pricingConfig?.coatSplit === true || mp.coatSplit === true;
                const showMobileCol = !!editSvc.mobileAvailable;
                const bb = mp.byBreed && typeof mp.byBreed === "object" ? mp.byBreed : {};
                const tiers = getShopTiers(tenant); // breeds price per the same shared 體型 tiers
                const num = (v) => v === "" ? "" : (parseInt(v) || 0);
                // Per-breed, per-tier price: byBreed[breedId][tierId] = {short,long,mobile,durationMin,na}
                const setBreedTier = (bid, tid, k, v) => setMatrix(m => { const cur = (m.byBreed || {})[bid] || {}; return { byBreed: { ...(m.byBreed || {}), [bid]: { ...cur, [tid]: { ...(cur[tid] || {}), [k]: v } } } }; });
                const setBreeds = (next) => setState(s => ({ ...s, tenants: s.tenants.map(tt => tt.id === user.tenantId ? { ...tt, specialBreeds: next } : tt) }));
                const setBreedDef = (i, k, v) => setBreeds(breeds.map((b, bi) => bi === i ? { ...b, [k]: v } : b));
                const addBreed = () => setBreeds([...breeds, { id: "sb" + uid(), name: "", names: [], catalogBreedIds: [], excludeHugeExtra: false }]);
                const delBreed = (i) => setBreeds(breeds.filter((_, bi) => bi !== i));
                // Add / remove a catalog breed to a shop-breed row (a row can map
                // several catalog breeds that share one price entry).
                const breedNamesOf = (b) => Array.isArray(b.names) ? b.names : (b.name ? [b.name] : []);
                const breedIdsOf = (b) => Array.isArray(b.catalogBreedIds) ? b.catalogBreedIds : (b.catalogBreedId ? [b.catalogBreedId] : []);
                const addCatalogBreed = (i, { breedId, breed }) => {
                  if (!breed) return;
                  setBreeds(breeds.map((b, bi) => {
                    if (bi !== i) return b;
                    const names = breedNamesOf(b); const ids = breedIdsOf(b);
                    if (names.includes(breed)) return b;
                    const nextNames = [...names, breed]; const nextIds = breedId ? [...ids, breedId] : ids;
                    return { ...b, names: nextNames, catalogBreedIds: nextIds, catalogBreedId: nextIds[0] || "", name: nextNames.join(" / ") };
                  }));
                };
                const removeCatalogBreed = (i, ni) => setBreeds(breeds.map((b, bi) => {
                  if (bi !== i) return b;
                  const names = breedNamesOf(b).filter((_, k) => k !== ni);
                  const ids = breedIdsOf(b).filter((_, k) => k !== ni);
                  return { ...b, names, catalogBreedIds: ids, catalogBreedId: ids[0] || "", name: names.join(" / ") };
                }));
                const cols = `minmax(70px,1.3fr) ${split ? "1fr 1fr" : "1fr"}${showMobileCol ? " 1fr" : ""} 52px 30px`;
                return (
                  <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)",marginTop:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,gap:8,flexWrap:"wrap"}}>
                      <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:0}}>{T("特殊品種定價（共用）","Special-breed pricing (shared)")}</p>
                      <button type="button" onClick={() => setBreedEditOpen(v => !v)} style={{fontSize:11,fontWeight:800,color:"var(--c-primary)",background:"#fff",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"4px 10px",cursor:"pointer"}}>{breedEditOpen ? T("完成","Done") : T("編輯品種","Edit breeds")}</button>
                    </div>
                    <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"0 0 10px",lineHeight:1.4}}>{T("品種名單全店共用，喺「編輯品種」設定；其他服務跟住用。冇填價 = 用體型價。","Breed list is shared shop-wide via Edit breeds; all services follow. Blank price = use the size-tier price.")}</p>
                    {breedEditOpen && (
                      <div style={{background:"#fff",border:"2px solid var(--c-border)",borderRadius:14,padding:12,marginBottom:12}}>
                        <p style={{fontSize:11,fontWeight:800,color:"#8A8A9E",margin:"0 0 8px"}}>{T("品種名單（全店共用）· 由品種庫揀選；一行可揀多個品種共用同一價","Breed list (shop-wide) · pick from the catalog; one row can hold several breeds sharing a price")}</p>
                        {breeds.map((b, i) => {
                          const names = breedNamesOf(b);
                          return (
                            <div key={b.id} style={{display:"grid",gridTemplateColumns:"1fr 36px",gap:6,marginBottom:8,alignItems:"start",paddingBottom:8,borderBottom:"1px dashed var(--c-light)"}}>
                              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                                {names.length > 0 && (
                                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                    {names.map((n, ni) => (
                                      <span key={ni} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px 3px 10px",borderRadius:99,background:"var(--c-lighter)",border:"1px solid var(--c-border)",color:"var(--c-dark)",fontSize:11,fontWeight:800}}>
                                        {n}
                                        <button type="button" onClick={()=>removeCatalogBreed(i, ni)} title={T("移除","Remove")} style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"flex",color:"var(--c-dark)"}}><X size={10} strokeWidth={3} /></button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <BreedCombobox type={editSvc.petType || "dog"} lang={tenant?.lang || "zh-HK"} value="" valueLabel="" placeholder={names.length ? T("再加多一個品種…","Add another breed…") : T("搜尋並揀選品種…","Search & pick a breed…")} onChange={(sel) => addCatalogBreed(i, sel)} />
                                <label style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,color:"#6A6A85",cursor:"pointer",whiteSpace:"nowrap"}}>
                                  <input type="checkbox" checked={!!b.excludeHugeExtra} onChange={ev=>setBreedDef(i,"excludeHugeExtra",ev.target.checked)} style={{width:15,height:15,accentColor:"var(--c-primary)"}} />{T("免超重加價","No surcharge")}
                                </label>
                              </div>
                              <button type="button" onClick={()=>delBreed(i)} title={T("刪除整行","Delete row")} style={{background:"#FEF2F2",border:"none",borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}><Trash2 size={13} color="#EF4444" strokeWidth={2.5}/></button>
                            </div>
                          );
                        })}
                        <Btn size="sm" variant="secondary" onClick={addBreed}><Plus size={14} strokeWidth={3} /> {T("新增品種","Add breed")}</Btn>
                      </div>
                    )}
                    {(() => {
                      // Only price breeds that match this service's pet type — a
                      // 貓 breed shouldn't appear under a 狗 service, and vice-versa.
                      const applicableBreeds = breeds.filter(b => breedAppliesToService(b, editSvc));
                      if (breeds.length === 0) return (
                      <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"8px 0",margin:0}}>{T("未設定特殊品種（按「編輯品種」新增）","No special breeds yet (use Edit breeds to add)")}</p>
                      );
                      if (applicableBreeds.length === 0) return (
                      <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"8px 0",margin:0}}>{T("此服務無對應品種（品種類型與服務不符）","No matching breeds for this service's pet type")}</p>
                      );
                      return (
                      <>
                        {applicableBreeds.map(b => {
                          const sub = (bb[b.id] && typeof bb[b.id] === "object") ? bb[b.id] : {};
                          const numInput = { border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box" };
                          return (
                            <div key={b.id} style={{border:"1px solid var(--c-border)",borderRadius:12,padding:"10px 12px",marginBottom:8,background:"#fff"}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,margin:"0 0 6px",flexWrap:"wrap"}}>
                                <p style={{fontSize:12.5,fontWeight:900,color:"#4A4A68",margin:0}}>🐩 {b.name || "—"} <span style={{fontSize:10,color:"#AFAFC4",fontWeight:700}}>· {T("按體型定價（留空＝用一般體型價）","priced by size (blank = use the normal size price)")}</span></p>
                                <button type="button" onClick={() => setMatrix(m => { const bt0 = m.byTier || {}; const nx = {}; tiers.forEach(t => { const e = bt0[t.id]; if (e && !e.na) { const o = {}; if (e.short != null) o.short = e.short; if (e.long != null) o.long = e.long; if (e.mobile != null) o.mobile = e.mobile; if (e.durationMin != null) o.durationMin = e.durationMin; nx[t.id] = o; } }); return { byBreed: { ...(m.byBreed||{}), [b.id]: nx } }; })} style={{fontSize:10.5,fontWeight:800,color:"var(--c-primary)",background:"#fff",border:"1.5px solid var(--c-primary)",borderRadius:99,padding:"3px 9px",cursor:"pointer",flexShrink:0}}>{T("同一般體型價","Copy size prices")}</button>
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:4}}>
                                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800}}>{T("體型","Size")}</span>
                                {split ? <><span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("短毛","Short")}$</span><span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("長毛","Long")}$</span></> : <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("價格","Price")}$</span>}
                                {showMobileCol && <span style={{fontSize:11,color:"#7C3AED",fontWeight:800,textAlign:"center"}}>🚗{T("上門","Visit")}$</span>}
                                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{T("時長","Time")}</span>
                                <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{T("狀態","State")}</span>
                              </div>
                              {tiers.map(t => {
                                const e = sub[t.id] || {};
                                const na = !!e.na;
                                const quote = !na && !!e.quote;
                                const off = na || quote;
                                const setBState3 = (st) => setMatrix(m => { const cur = (m.byBreed||{})[b.id]||{}; return { byBreed: { ...(m.byBreed||{}), [b.id]: { ...cur, [t.id]: { ...(cur[t.id]||{}), na: st==="na", quote: st==="quote" } } } }; });
                                const dim = off ? { opacity:0.4, pointerEvents:"none", background:"#F3F4F6" } : {};
                                return (
                                  <div key={t.id} style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:6,alignItems:"center"}}>
                                    <span style={{fontSize:12,fontWeight:800,color: off ? "#AFAFC4" : "#4A4A68", textDecoration: na ? "line-through" : "none"}}>{t.label}{quote && <span style={{fontSize:9.5,color:"#0E7C66",fontWeight:800}}> · {T("面議","Quote")}</span>}</span>
                                    {split ? (
                                      <>
                                        <input disabled={off} value={e.short ?? ""} onChange={ev=>setBreedTier(b.id,t.id,"short",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":T("一般","Normal")} style={{...numInput,...dim}} />
                                        <input disabled={off} value={e.long ?? ""} onChange={ev=>setBreedTier(b.id,t.id,"long",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":T("一般","Normal")} style={{...numInput,...dim}} />
                                      </>
                                    ) : (
                                      <input disabled={off} value={e.short ?? ""} onChange={ev=>setMatrix(m=>{const cur=(m.byBreed||{})[b.id]||{}; const v=num(ev.target.value); return {byBreed:{...(m.byBreed||{}),[b.id]:{...cur,[t.id]:{...(cur[t.id]||{}),short:v,long:v}}}};})} inputMode="numeric" placeholder={off?"—":T("一般","Normal")} style={{...numInput,...dim}} />
                                    )}
                                    {showMobileCol && <input disabled={off} value={e.mobile ?? ""} onChange={ev=>setBreedTier(b.id,t.id,"mobile",num(ev.target.value))} inputMode="numeric" placeholder={off?"—":T("正價","Normal")} style={{...numInput,border:"2px solid #DDD6FE",background:"#FAF5FF",...dim}} />}
                                    <input disabled={off} value={e.durationMin ?? ""} onChange={ev=>setBreedTier(b.id,t.id,"durationMin",num(ev.target.value))} inputMode="numeric" placeholder={String(editSvc.estimatedMinutes||60)} style={{...numInput,...dim}} />
                                    <button type="button" title={na ? T("不接此體型（按此切換：正常）","Not offered (tap to cycle: normal)") : quote ? T("面議（按此切換：不接）","Quote (tap to cycle: not offered)") : T("正常（按此切換：面議）","Normal (tap to cycle: quote)")} onClick={()=>setBState3(na ? "normal" : quote ? "na" : "quote")} style={{border:"none",borderRadius:8,height:30,cursor:"pointer",fontSize:13,fontWeight:900,background: na ? "#FEE2E2" : quote ? "#D1FAE5" : "var(--c-light)", color: na ? "#DC2626" : quote ? "#0E7C66" : "#AFAFC4"}}>{na ? "✕" : quote ? zhEn("議","Quote") : "○"}</button>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* ── Legacy per-service size editor — only shown for services
                  still holding legacy data (un-migrated). Once a shop is
                  migrated to shared tiers (no sizes/specialBreeds), it hides. ─ */}
              {((mp.sizes||[]).length > 0 || (mp.specialBreeds||[]).length > 0) && <>
              <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)",marginTop:8}}>
                <p style={{fontSize:11,color:"#AFAFC4",fontWeight:800,margin:"0 0 8px"}}>{T("進階：自訂體型表 / 特殊品種 / 超重加價","Advanced: custom size table / special breeds / over-weight surcharge")}</p>
                {/* coatSplit toggle: off = one $ column (most shops). On =
                    short / long columns. Legacy services with differing
                    shortHair/longHair are auto-detected as split so the
                    owner doesn't lose data — detection happens at render
                    time without mutating state. */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:8,flexWrap:"wrap"}}>
                  <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:0}}>{mp.coatSplit ? zhEn("體型 × 毛髮長度定價", "Size × coat-length pricing") : zhEn("按體型定價", "Pricing by size")}</p>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,color:"#6A6A85",cursor:"pointer"}}>
                      <input type="checkbox" checked={mp.coatSplit === true || (mp.coatSplit === undefined && (mp.sizes || []).some(r => Number(r.longHair || 0) > 0 && Number(r.longHair) !== Number(r.shortHair || 0)))} onChange={e => setMatrix(() => ({ coatSplit: e.target.checked }))} style={{width:16,height:16,accentColor:"var(--c-primary)"}} />
                      {zhEn("分長短毛", "Split short / long coat")}
                    </label>
                    <Btn size="sm" variant="secondary" onClick={addSizeRow}><Plus size={14} strokeWidth={3} /> {zhEn("新增", "Add")}</Btn>
                  </div>
                </div>
                {(() => {
                  const split = mp.coatSplit === true || (mp.coatSplit === undefined && (mp.sizes || []).some(r => Number(r.longHair || 0) > 0 && Number(r.longHair) !== Number(r.shortHair || 0)));
                  // Grid template depends on whether long-hair column is shown.
                  // First column needs ≥72px so "小型犬" / "中型犬" don't squish
                  // to invisible — previously `1fr` collapsed when the other
                  // fixed columns took all the row width on a phone.
                  const cols = split
                    ? "minmax(72px,1.4fr) 50px 50px 56px 56px 56px 28px"
                    : "minmax(72px,1.4fr) 50px 50px 64px 56px 28px";
                  return (
                    <>
                      <div style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:6}}>
                        <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800}}>{zhEn("體型", "Size")}</span>
                        <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("最小kg", "Min kg")}</span>
                        <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("最大kg", "Max kg")}</span>
                        {split ? (
                          <>
                            <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("短毛$", "Short $")}</span>
                            <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("長毛$", "Long $")}</span>
                          </>
                        ) : (
                          <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("價格$", "Price $")}</span>
                        )}
                        <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("時長(分)", "Duration (min)")}</span>
                        <span />
                      </div>
                      {mp.sizes.map((row,i)=>(
                        <div key={row.id||i} style={{display:"grid",gridTemplateColumns:cols,gap:6,marginBottom:6,alignItems:"center"}}>
                          <input value={row.label} onChange={e=>setSizeRow(i,"label",e.target.value)} placeholder={zhEn("例：小型", "e.g. Small")} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 8px",fontSize:13,fontWeight:700,outline:"none",color:"#4A4A68",width:"100%",boxSizing:"border-box"}} />
                          <NumCell value={row.minW} onCommit={v => setSizeRow(i,"minW",v)} parse={parseFloat} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                          <NumCell value={row.maxW} onCommit={v => setSizeRow(i,"maxW",v)} parse={parseFloat} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                          {split ? (
                            <>
                              <NumCell value={row.shortHair} onCommit={v => setSizeRow(i,"shortHair",v)} parse={parseInt} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                              <NumCell value={row.longHair} onCommit={v => setSizeRow(i,"longHair",v)} parse={parseInt} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                            </>
                          ) : (
                            // Single price column — writes BOTH shortHair and longHair
                            // to keep the data model consistent, so flipping the split
                            // toggle later doesn't surprise the owner with empty cells.
                            <NumCell value={row.shortHair} parse={parseInt} onCommit={(v) => {
                              setMatrix(m => ({ sizes: m.sizes.map((r, ri) => ri === i ? { ...r, shortHair: v, longHair: v } : r) }));
                            }} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                          )}
                          <NumCell value={row.durationMin || 60} onCommit={v => setSizeRow(i,"durationMin",v)} parse={parseInt} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                          <button onClick={()=>delSizeRow(i)} title={zhEn("刪除", "Delete")} style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"6px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12} color="#EF4444" strokeWidth={2.5} /></button>
                        </div>
                      ))}
                    </>
                  );
                })()}
                {mp.sizes.length === 0 && <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0,padding:"8px 0"}}>{zhEn("暫無體型，按上方「新增」", "No sizes yet — tap \"Add\" above")}</p>}
                {(() => {
                  const warnings = validateSizes(mp.sizes);
                  if (warnings.length === 0) return null;
                  return <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:8}}>
                    {warnings.map((w, i) => (
                      <div key={i} style={{fontSize:11,fontWeight:700,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"6px 10px"}}>{w}</div>
                    ))}
                  </div>;
                })()}
              </div>
              {/* SPA + add-on services skip this section entirely — too-
                  large dogs are excluded from SPA via the matrix tier cap
                  instead of a surcharge. Regular services keep it but it's
                  now an opt-in toggle with auto-derived baseW. */}
              {editSvc.kind !== "spa" && editSvc.kind !== "addon" && (() => {
                const sortedTiers = [...(mp.sizes || [])].sort((a, b) => (Number(a.minW) || 0) - (Number(b.minW) || 0));
                const lastTier = sortedTiers[sortedTiers.length - 1];
                // baseW is AUTO-DERIVED from the LAST tier's maxW so the
                // surcharge fires for any pet beyond the tier-priced range.
                const autoBaseW = lastTier
                  ? (Number(lastTier.maxW) || 0)
                  : 0;
                const shopMaxWeight = Number(tenant?.maxPetWeightKg) || 0;
                const he = mp.hugeExtra || {};
                // maxW: editable per-service cap. Default = shop's max
                // (read from tenant). Owner can override per service.
                const capW = Number(he.maxW) || shopMaxWeight || 0;
                const enabled = he.enabled === true;
                const setHe = (patch) => setMatrix(m => ({...m, hugeExtra: {...(m.hugeExtra || {}), ...patch}}));
                const rangeLabel = capW > 0 ? `${autoBaseW}kg → ${capW}kg` : `>${autoBaseW}kg`;
                return (
                  <div style={{background:enabled?"#FFFBEB":"#F9FAFB",borderRadius:20,padding:16,border:"2px solid " + (enabled?"#FEF3C7":"#E5E7EB"),opacity:enabled?1:0.7}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <p style={{fontSize:13,fontWeight:800,color:enabled?"#92400E":"#6B7280",margin:0}}>{zhEn("巨型超重加價","Oversize surcharge")} ({rangeLabel})</p>
                      <Toggle on={enabled} onChange={v => setHe({enabled: v})} />
                    </div>
                    <p style={{fontSize:11,color:enabled?"#8A8A9E":"#9CA3AF",fontWeight:700,margin:"0 0 10px"}}>
                      {zhEn("起計體重自動為「"+autoBaseW+"kg」（接上一個體型上限）。超過上限體重的毛孩無法預約此服務。", "The starting weight is automatically "+autoBaseW+"kg (the previous size's upper limit). Pets above the maximum weight can't book this service.")}
                    </p>
                    {enabled && (
                      <>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                          <div>
                            <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 4px"}}>{zhEn("上限體重 (kg)", "Max weight (kg)")}</p>
                            <input type="number" min={autoBaseW + 1} value={capW || ""} onChange={e=>{
                              const v = parseInt(e.target.value) || 0;
                              setHe({maxW: v});
                            }} placeholder={shopMaxWeight ? String(shopMaxWeight) : zhEn("店鋪最大", "Shop max")} style={{border:"2px solid #FEF3C7",borderRadius:12,padding:"8px 12px",fontSize:14,fontWeight:800,outline:"none",width:"100%",boxSizing:"border-box"}} />
                          </div>
                          <div>
                            <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 4px"}}>{zhEn("每步距 (kg)", "Step (kg)")}</p>
                            <input type="number" value={he.perStep || 5} onChange={e=>setHe({perStep: parseInt(e.target.value) || 5})} style={{border:"2px solid #FEF3C7",borderRadius:12,padding:"8px 12px",fontSize:14,fontWeight:800,outline:"none",width:"100%",boxSizing:"border-box"}} />
                          </div>
                          <div>
                            <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 4px"}}>{zhEn("每步加收 ($)", "Charge per step ($)")}</p>
                            <input type="number" value={he.stepPrice || 100} onChange={e=>setHe({stepPrice: parseInt(e.target.value) || 100})} style={{border:"2px solid #FEF3C7",borderRadius:12,padding:"8px 12px",fontSize:14,fontWeight:800,outline:"none",width:"100%",boxSizing:"border-box"}} />
                          </div>
                        </div>
                        {shopMaxWeight > 0 && capW > shopMaxWeight && (
                          <div style={{marginTop:8,fontSize:11,fontWeight:700,color:"#B91C1C",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,padding:"6px 10px"}}>
                            {zhEn("⚠️ 此服務上限 ("+capW+"kg) 超過店鋪接受最大體重 ("+shopMaxWeight+"kg)，超出店鋪上限部分仍無法預約。", "⚠️ This service's limit ("+capW+"kg) exceeds the shop's maximum accepted weight ("+shopMaxWeight+"kg); weight above the shop limit still can't be booked.")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
              <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:0}}>{zhEn("特殊品種專屬定價 🐩", "Breed-specific pricing 🐩")}</p>
                  <Btn size="sm" variant="secondary" onClick={addBreed}><Plus size={14} strokeWidth={3} /> {T("新增品種","Add breed")}</Btn>
                </div>
                <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"0 0 12px"}}>{zhEn("品種名稱會以「包含」匹配毛孩品種欄；可為每個品種設定獨立的體重區間及價格", "Breed names are matched by \"contains\" against the pet's breed field; each breed can have its own weight ranges and prices")}</p>
                {sbList.length === 0 && <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0,padding:"8px 0"}}>{zhEn("暫無特殊品種，按上方「新增品種」", "No special breeds yet — tap \"Add breed\" above")}</p>}
                {sbList.map((b,bi) => {
                  // Normalize to arrays so the multi-breed UI works for both
                  // new rows (names[] / catalogBreedIds[]) and legacy rows
                  // (single name / catalogBreedId).
                  const breedIds = Array.isArray(b.catalogBreedIds)
                    ? b.catalogBreedIds
                    : (b.catalogBreedId ? [b.catalogBreedId] : []);
                  const breedNames = Array.isArray(b.names)
                    ? b.names
                    : (b.name ? [b.name] : []);
                  const addBreedToRow = ({ breedId, breed }) => {
                    if (!breed) return;
                    setMatrix(m => ({
                      specialBreeds: (m.specialBreeds || []).map((row, ri) => {
                        if (ri !== bi) return row;
                        const nextIds = Array.isArray(row.catalogBreedIds) ? [...row.catalogBreedIds] : (row.catalogBreedId ? [row.catalogBreedId] : []);
                        const nextNames = Array.isArray(row.names) ? [...row.names] : (row.name ? [row.name] : []);
                        if (breed && nextNames.includes(breed)) return row;
                        if (breedId) nextIds.push(breedId);
                        nextNames.push(breed);
                        // Keep the single-value mirrors in sync so the
                        // legacy matchBreed / display paths still work
                        // before this code is unloaded.
                        return { ...row, catalogBreedIds: nextIds, names: nextNames, catalogBreedId: nextIds[0] || "", name: nextNames.join(" / ") };
                      }),
                    }));
                  };
                  const removeBreedFromRow = (idx) => {
                    setMatrix(m => ({
                      specialBreeds: (m.specialBreeds || []).map((row, ri) => {
                        if (ri !== bi) return row;
                        const nextIds = (Array.isArray(row.catalogBreedIds) ? row.catalogBreedIds : (row.catalogBreedId ? [row.catalogBreedId] : [])).filter((_, i) => i !== idx);
                        const nextNames = (Array.isArray(row.names) ? row.names : (row.name ? [row.name] : [])).filter((_, i) => i !== idx);
                        return { ...row, catalogBreedIds: nextIds, names: nextNames, catalogBreedId: nextIds[0] || "", name: nextNames.join(" / ") };
                      }),
                    }));
                  };
                  return (
                  <div key={b.id||bi} style={{background:"#FFFDFD",borderRadius:16,padding:12,border:"1px dashed var(--c-border)",marginBottom:10}}>
                    {/* Multi-breed picker — pick from the breed catalog
                        and keep adding; chips below show what this price
                        matrix applies to. Same matrix → multiple breeds. */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 28px",gap:6,marginBottom:6,alignItems:"start"}}>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {breedNames.length > 0 && (
                          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                            {breedNames.map((n, ni) => (
                              <span key={ni} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px 3px 10px",borderRadius:99,background:"var(--c-lighter)",border:"1px solid var(--c-border)",color:"var(--c-dark)",fontSize:11,fontWeight:800}}>
                                {n}
                                <button onClick={() => removeBreedFromRow(ni)} title={zhEn("移除此品種", "Remove this breed")} style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"flex",color:"var(--c-dark)"}}>
                                  <X size={10} strokeWidth={3} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <BreedCombobox
                          type={editSvc.petType || "dog"}
                          lang={tenant?.lang || "zh-HK"}
                          value=""
                          valueLabel=""
                          placeholder={breedNames.length ? zhEn("再加多一個品種…", "Add another breed…") : zhEn("搜尋並加入品種…", "Search and add a breed…")}
                          onChange={addBreedToRow}
                        />
                      </div>
                      <button onClick={()=>delBreed(bi)} title={T("刪除整行","Delete row")} style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"6px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12} color="#EF4444" strokeWidth={2.5} /></button>
                    </div>
                    <input value={b.extraNote||""} onChange={e=>setBreedField(bi,"extraNote",e.target.value)} placeholder={zhEn("備註（選填）", "Notes (optional)")} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 8px",fontSize:12,fontWeight:700,outline:"none",color:"#6A6A85",width:"100%",boxSizing:"border-box",marginBottom:8}} />
                    {/* Per-breed exemption from the 巨型超重加價 surcharge.
                        On = surcharge skipped for this breed (price row is
                        treated as final). Off = surcharge stacks normally. */}
                    <label style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,cursor:"pointer",marginBottom:8}}>
                      <input type="checkbox" checked={!!b.excludeHugeExtra} onChange={e => setBreedField(bi, "excludeHugeExtra", e.target.checked)} style={{width:14,height:14,accentColor:"#D97706"}} />
                      <span style={{fontSize:11,fontWeight:700,color:"#92400E"}}>{zhEn("此品種不適用「巨型超重加價」", "The oversize surcharge doesn't apply to this breed")}</span>
                    </label>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <p style={{fontSize:11,color:"#8A8A9E",fontWeight:800,margin:0}}>{zhEn("體型 × 毛長 × 價格", "Size × coat length × price")}</p>
                      <button onClick={()=>addBreedSize(bi)} style={{background:"var(--c-light)",border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:800,color:"var(--c-primary)",cursor:"pointer"}}>{zhEn("+ 體型", "+ Size")}</button>
                    </div>
                    {(() => {
                      const split = mp.coatSplit === true || (mp.coatSplit === undefined && (b.sizes || []).some(r => Number(r.longHair || 0) > 0 && Number(r.longHair) !== Number(r.shortHair || 0)));
                      const cols = split ? "1.2fr 50px 50px 60px 60px 60px 24px" : "1.2fr 50px 50px 70px 60px 24px";
                      return (
                        <>
                          {/* Column headers — matches the main matrix table
                              so staff have the same mental model for both. */}
                          <div style={{display:"grid",gridTemplateColumns:cols,gap:5,marginBottom:4,paddingLeft:2}}>
                            <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800}}>{zhEn("體型名", "Size name")}</span>
                            <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("最小kg", "Min kg")}</span>
                            <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("最大kg", "Max kg")}</span>
                            {split ? (
                              <>
                                <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("短毛$", "Short $")}</span>
                                <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("長毛$", "Long $")}</span>
                              </>
                            ) : (
                              <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("價格$", "Price $")}</span>
                            )}
                            <span style={{fontSize:10,color:"#AFAFC4",fontWeight:800,textAlign:"center"}}>{zhEn("時長(分)", "Duration (min)")}</span>
                            <span />
                          </div>
                          {(b.sizes||[]).map((row,si)=>(
                            <div key={row.id||si} style={{display:"grid",gridTemplateColumns:cols,gap:5,marginBottom:5,alignItems:"center"}}>
                              <input value={row.label} onChange={e=>setBreedSize(bi,si,"label",e.target.value)} placeholder={zhEn("體型名", "Size name")} style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px 7px",fontSize:12,fontWeight:700,outline:"none",color:"#4A4A68",width:"100%",boxSizing:"border-box"}} />
                              <NumCell value={row.minW} onCommit={v => setBreedSize(bi,si,"minW",v)} parse={parseFloat} placeholder="min" style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                              <NumCell value={row.maxW} onCommit={v => setBreedSize(bi,si,"maxW",v)} parse={parseFloat} placeholder="max" style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                              {split ? (
                                <>
                                  <NumCell value={row.shortHair} onCommit={v => setBreedSize(bi,si,"shortHair",v)} parse={parseInt} placeholder={zhEn("短","S")} style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                                  <NumCell value={row.longHair} onCommit={v => setBreedSize(bi,si,"longHair",v)} parse={parseInt} placeholder={zhEn("長","L")} style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                                </>
                              ) : (
                                <NumCell value={row.shortHair} parse={parseInt} onCommit={(v) => {
                                  setMatrix(m => ({ specialBreeds: (m.specialBreeds || []).map((bb, bbi) => bbi === bi ? { ...bb, sizes: (bb.sizes || []).map((r, ri) => ri === si ? { ...r, shortHair: v, longHair: v } : r) } : bb) }));
                                }} placeholder="$" style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                              )}
                              <NumCell value={row.durationMin || 60} onCommit={v => setBreedSize(bi,si,"durationMin",v)} parse={parseInt} placeholder={zhEn("分", "pts")} style={{border:"2px solid var(--c-light)",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                              <button onClick={()=>delBreedSize(bi,si)} title={zhEn("刪除", "Delete")} style={{background:"#FEF2F2",border:"none",borderRadius:6,padding:"5px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={10} color="#EF4444" strokeWidth={2.5} /></button>
                            </div>
                          ))}
                        </>
                      );
                    })()}
                    {(() => {
                      const warnings = validateSizes(b.sizes || []);
                      if (warnings.length === 0) return null;
                      return <div style={{display:"flex",flexDirection:"column",gap:3,marginTop:6}}>
                        {warnings.map((w, i) => (
                          <div key={i} style={{fontSize:10,fontWeight:700,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:6,padding:"4px 8px"}}>{w}</div>
                        ))}
                      </div>;
                    })()}
                  </div>
                  );
                })}
              </div>
              </>}
            </>}

            <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--c-lighter)",borderRadius:12,cursor:"pointer",fontSize:13,fontWeight:700}}>
              <input type="checkbox" checked={editSvc.customerVisible !== false} onChange={e => setSvcField("customerVisible", e.target.checked)} style={{transform:"scale(1.2)"}} />
              <div style={{flex:1}}>
                <div>{zhEn("客戶可見", "Visible to customers")}</div>
                <div style={{fontSize:11,fontWeight:600,color:"#6B6B85",marginTop:2}}>{zhEn("關閉後，顧客在預約頁睇唔到呢個服務，但員工仍可即場預約使用。", "When off, customers can't see this service on the booking page, but staff can still book it on the spot.")}</div>
              </div>
            </label>
            <div style={{display:"flex",gap:12,marginTop:8}}>
              {editSvc.id && <Btn variant="danger" style={{flex:1}} onClick={() => { delSvc(editSvc.id); setEditSvc(null); }}>{i18nT("svc.del")}</Btn>}
              <Btn style={{flex:2}} onClick={() => { setEditSvc(s=>({...s,matrixPricing:pm==="matrix"?mp:null})); saveSvc(); }}>{i18nT("common.save")}</Btn>
            </div>
          </div>;
        })()}
      </BottomSheet>

      <BottomSheet open={!!editAddon} onClose={() => setEditAddon(null)} title={editAddon?.id ? zhEn("編輯加購", "Edit add-on") : zhEn("新增加購", "New add-on")}>
        {editAddon && (() => {
          const pm = editAddon.pricingMode || "flat";
          const variants = editAddon.variants || [];
          const setField = (k,v) => setEditAddon(a=>({...a,[k]:v}));
          // Auto-chain: setting row i's maxW bumps row i+1's minW to maxW+0.1
          const setVariant = (i, k, v) => setEditAddon(a => ({
            ...a,
            variants: a.variants.map((row, ri) => {
              if (ri === i) return { ...row, [k]: v };
              if (ri === i + 1 && k === "maxW" && typeof v === "number" && v > 0) {
                const newMin = Math.round((v + 0.1) * 10) / 10;
                if (row.minW < newMin || row.minW === 0) return { ...row, minW: newMin };
              }
              return row;
            }),
          }));
          const addVariant = () => setEditAddon(a => {
            const list = a.variants || [];
            const last = list[list.length - 1];
            const seedMin = last && last.maxW > 0 ? Math.round((last.maxW + 0.1) * 10) / 10 : 0;
            const seedMax = seedMin > 0 ? seedMin + 4.9 : 0;
            return { ...a, variants: [...list, { id:"av"+uid(), label:zhEn("新增體型", "New size"), minW:seedMin, maxW:seedMax, price:0 }] };
          });
          const delVariant = (i) => setEditAddon(a => ({ ...a, variants: a.variants.filter((_,ri) => ri!==i) }));
          // Validation — returns array of warning strings for overlaps / gaps / inverted ranges.
          const validateVariants = (rows) => {
            const out = [];
            if (!rows || rows.length === 0) return out;
            for (let i = 0; i < rows.length; i++) {
              const r = rows[i];
              const lbl = r.label || zhEn("第" + (i + 1) + "行", "Row " + (i + 1));
              if (r.minW < 0 || r.maxW < 0) { out.push(zhEn("⚠️「" + lbl + "」體重不可為負數", "⚠️ \"" + lbl + "\": weight can't be negative")); continue; }
              if (r.maxW > 0 && r.minW > r.maxW) { out.push(zhEn("⚠️「" + lbl + "」最小值大於最大值，請對調", "⚠️ \"" + lbl + "\": minimum is greater than maximum, please swap")); continue; }
            }
            const sorted = [...rows].sort((a, b) => a.minW - b.minW);
            for (let i = 0; i < sorted.length - 1; i++) {
              const a = sorted[i], b = sorted[i + 1];
              if (a.maxW >= b.minW && a.maxW > 0 && b.minW > 0) {
                out.push(zhEn("⚠️「" + a.label + "」(" + a.minW + "-" + a.maxW + ") 同「" + b.label + "」(" + b.minW + "-" + b.maxW + ") 重疊", "⚠️ \"" + a.label + "\" (" + a.minW + "-" + a.maxW + ") overlaps \"" + b.label + "\" (" + b.minW + "-" + b.maxW + ")"));
              } else if (b.minW - a.maxW > 0.2 && a.maxW > 0 && b.minW > 0) {
                out.push(zhEn("⚠️ " + (a.maxW + 0.1).toFixed(1) + "-" + (b.minW - 0.1).toFixed(1) + "kg 冇對應變體，會用備用價 $" + (parseFloat(editAddon.price) || 0), "⚠️ " + (a.maxW + 0.1).toFixed(1) + "-" + (b.minW - 0.1).toFixed(1) + "kg has no variant; the fallback price $" + (parseFloat(editAddon.price) || 0) + " will be used"));
              }
            }
            return out;
          };
          return <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Input label={zhEn("加購名稱", "Add-on name")} value={editAddon.name} onChange={e=>setField("name",e.target.value)} placeholder={zhEn("例：剪指甲 / SPA", "e.g. Nail trim / SPA")} />
            {(() => {
              // Multi-select: a single addon row can apply to dog + cat
              // (e.g. 剪指甲, 香水噴霧). At least one type stays selected.
              const cur = Array.isArray(editAddon.petTypes) && editAddon.petTypes.length
                ? editAddon.petTypes
                : (editAddon.petType ? [editAddon.petType] : ["dog"]);
              const OPTS = [["dog",zhEn("🐶 狗", "🐶 Dog")],["cat",zhEn("🐱 貓", "🐱 Cat")],["small",zhEn("🐇 小動物", "🐇 Small animal")]];
              const toggle = (k) => {
                const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
                setField("petTypes", next.length ? next : [k]);
              };
              return (
                <div>
                  <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("適用寵物種類（可多選）", "Applies to pet types (multi-select)")}</p>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {OPTS.map(([k, label]) => {
                      const on = cur.includes(k);
                      return (
                        <button key={k} type="button" onClick={() => toggle(k)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>{on?"✓ ":""}{label}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <Sel label={zhEn("計價方式", "Pricing method")} value={pm} onChange={e=>setField("pricingMode",e.target.value)}>
              <option value="flat">{zhEn("固定價格", "Fixed price")}</option>
              <option value="variants">{zhEn("按體重變體 ⚡（例：SPA 按體型）", "By weight variant ⚡ (e.g. SPA by size)")}</option>
            </Sel>
            {pm === "flat" && <Input label={zhEn("價格 ($)", "Price ($)")} type="number" value={editAddon.price} onChange={e=>setField("price",e.target.value)} placeholder={zhEn("例：50", "e.g. 50")} />}
            {pm === "variants" && <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14,border:"2px solid var(--c-border)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <p style={{fontSize:13,fontWeight:800,color:"var(--c-dark)",margin:0}}>{zhEn("體重變體", "Weight variants")}</p>
                <Btn size="sm" variant="secondary" onClick={addVariant}><Plus size={14} strokeWidth={3} /> {zhEn("新增", "Add")}</Btn>
              </div>
              <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"0 0 8px"}}>{zhEn("系統會按毛孩體重自動匹配；無匹配時使用上面「價格」欄作備用。", "Matched automatically by the pet's weight; if none match, the \"Price\" field above is used as a fallback.")}</p>
              <div style={{display:"grid",gridTemplateColumns:"1.2fr 56px 56px 64px 28px",gap:6,marginBottom:6}}>
                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800}}>{zhEn("變體名", "Variant name")}</span>
                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("最小kg", "Min kg")}</span>
                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("最大kg", "Max kg")}</span>
                <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("價格 $", "Price $")}</span>
                <span />
              </div>
              {variants.map((row,i) => (
                <div key={row.id||i} style={{display:"grid",gridTemplateColumns:"1.2fr 56px 56px 64px 28px",gap:6,marginBottom:6,alignItems:"center"}}>
                  <input value={row.label} onChange={e=>setVariant(i,"label",e.target.value)} placeholder={zhEn("例：小型SPA", "e.g. Small SPA")} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px 8px",fontSize:13,fontWeight:700,outline:"none",color:"#4A4A68",width:"100%",boxSizing:"border-box"}} />
                  <input type="number" value={row.minW} onChange={e=>setVariant(i,"minW",parseFloat(e.target.value)||0)} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                  <input type="number" value={row.maxW} onChange={e=>setVariant(i,"maxW",parseFloat(e.target.value)||0)} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                  <input type="number" value={row.price} onChange={e=>setVariant(i,"price",parseFloat(e.target.value)||0)} style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                  <button onClick={()=>delVariant(i)} title={zhEn("刪除", "Delete")} style={{background:"#FEF2F2",border:"none",borderRadius:8,padding:"6px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12} color="#EF4444" strokeWidth={2.5} /></button>
                </div>
              ))}
              {variants.length === 0 && <p style={{fontSize:12,color:"#AFAFC4",fontWeight:700,textAlign:"center",margin:0,padding:"8px 0"}}>{zhEn("暫無變體，按上方「新增」", "No variants yet — tap \"Add\" above")}</p>}
              {(() => {
                const warnings = validateVariants(variants);
                if (warnings.length === 0) return null;
                return <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:8,marginBottom:8}}>
                  {warnings.map((w, i) => (
                    <div key={i} style={{fontSize:11,fontWeight:700,color:"#92400E",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"6px 10px"}}>{w}</div>
                  ))}
                </div>;
              })()}
              <Input label={zhEn("備用價格 / 無匹配時 ($)", "Fallback price / when none match ($)")} type="number" value={editAddon.price} onChange={e=>setField("price",e.target.value)} placeholder={zhEn("例：50", "e.g. 50")} />
            </div>}
            <div style={{display:"flex",gap:12,marginTop:8}}>
              {editAddon.id && <Btn variant="danger" style={{flex:1}} onClick={() => { delAddon(editAddon.id); setEditAddon(null); }}>{zhEn("刪除加購", "Delete add-on")}</Btn>}
              <Btn style={{flex:2}} onClick={saveAddon}>{zhEn("儲存加購", "Save add-on")}</Btn>
            </div>
          </div>;
        })()}
      </BottomSheet>

      {/* Promo code editor */}
      <BottomSheet open={!!editPromo} onClose={() => setEditPromo(null)} title={editPromo?.id ? zhEn("編輯優惠碼", "Edit promo code") : zhEn("新增優惠碼", "New promo code")}>
        {editPromo && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Input label={zhEn("優惠碼 (大寫英數)", "Promo code (uppercase letters / digits)")} value={editPromo.code} onChange={e => setEditPromo(f => ({...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")}))} placeholder={zhEn("例：WELCOME10", "e.g. WELCOME10")} />
            <Input label={zhEn("描述（內部備註）", "Description (internal note)")} value={editPromo.description} onChange={e => setEditPromo(f => ({...f, description: e.target.value}))} placeholder={zhEn("例：新客戶 10% 折扣", "e.g. 10% off for new customers")} />
            <div style={{display:"flex",gap:8,background:"var(--c-light)",borderRadius:99,padding:4}}>
              {[["percent",zhEn("百分比 %", "Percentage %")],["fixed",zhEn("固定金額 $", "Fixed amount $")]].map(([v,l]) => (
                <button key={v} onClick={() => setEditPromo(f => ({...f, discountType: v}))} style={{flex:1,padding:"10px 0",borderRadius:99,fontSize:13,fontWeight:800,border:"none",background:editPromo.discountType===v?"var(--c-primary)":"transparent",color:editPromo.discountType===v?"#fff":"var(--c-grad1)",cursor:"pointer"}}>{l}</button>
              ))}
            </div>
            <Input label={editPromo.discountType === "percent" ? zhEn("折扣比例 %", "Discount %") : zhEn("減免金額 $", "Amount off $")} type="number" value={editPromo.discountValue} onChange={e => setEditPromo(f => ({...f, discountValue: e.target.value}))} />
            {editPromo.discountType === "percent" && <Input label={zhEn("最高折扣 $ (選填)", "Max discount $ (optional)")} type="number" value={editPromo.maxDiscount} onChange={e => setEditPromo(f => ({...f, maxDiscount: e.target.value}))} placeholder={zhEn("例：100（百分比折扣上限）", "e.g. 100 (cap for percentage discounts)")} />}
            <Input label={zhEn("最低消費 $ (選填)", "Minimum spend $ (optional)")} type="number" value={editPromo.minSubtotal} onChange={e => setEditPromo(f => ({...f, minSubtotal: e.target.value}))} placeholder={zhEn("0 = 無限制", "0 = no limit")} />
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Input label={zhEn("有效起", "Valid from")} type="date" value={editPromo.validFrom} onChange={e => setEditPromo(f => ({...f, validFrom: e.target.value}))} />
              <Input label={zhEn("有效至", "Valid until")} type="date" value={editPromo.validTo} onChange={e => setEditPromo(f => ({...f, validTo: e.target.value}))} />
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Input label={zhEn("總使用次數 (選填)", "Total uses (optional)")} type="number" value={editPromo.maxUses} onChange={e => setEditPromo(f => ({...f, maxUses: e.target.value}))} placeholder={zhEn("無限制", "Unlimited")} />
              <Input label={zhEn("每客戶限用", "Limit per customer")} type="number" value={editPromo.perCustomerLimit} onChange={e => setEditPromo(f => ({...f, perCustomerLimit: parseInt(e.target.value) || 1}))} />
            </div>
            <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:"var(--c-lighter)",cursor:"pointer"}}>
              <input type="checkbox" checked={!!editPromo.newCustomersOnly} onChange={e => setEditPromo(f => ({...f, newCustomersOnly: e.target.checked}))} style={{transform:"scale(1.2)"}} />
              <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("只限新客戶使用", "New customers only")}</span>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:editPromo.enabled?"#ECFDF5":"#FEF2F2",cursor:"pointer"}}>
              <input type="checkbox" checked={!!editPromo.enabled} onChange={e => setEditPromo(f => ({...f, enabled: e.target.checked}))} style={{transform:"scale(1.2)"}} />
              <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("啟用", "On")}</span>
            </label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
              <Btn variant="secondary" onClick={() => setEditPromo(null)}>{zhEn("取消", "Cancel")}</Btn>
              <Btn onClick={async () => {
                if (!editPromo.code) { toast(i18nT("val.enterPromoCode"), "error"); return; }
                try {
                  const saved = await upsertPromoCode({
                    ...editPromo,
                    tenantId: user.tenantId,
                    discountValue: parseFloat(editPromo.discountValue) || 0,
                    maxDiscount: editPromo.maxDiscount ? parseFloat(editPromo.maxDiscount) : null,
                    maxUses: editPromo.maxUses ? parseInt(editPromo.maxUses) : null,
                    minSubtotal: parseFloat(editPromo.minSubtotal) || 0,
                    validFrom: editPromo.validFrom || null,
                    validTo: editPromo.validTo || null,
                  });
                  setPromoCodes(arr => {
                    const i = arr.findIndex(x => x.id === saved.id);
                    if (i >= 0) { const next = arr.slice(); next[i] = saved; return next; }
                    return [saved, ...arr];
                  });
                  toast(editPromo.id ? zhEn("已更新", "Updated") : zhEn("已新增", "Added")); setEditPromo(null);
                } catch (e) { toast(e?.message || zhEn("儲存失敗", "Save failed"), "error"); }
              }}><Save size={16}/> {zhEn("儲存", "Save")}</Btn>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Service package editor */}
      <BottomSheet open={!!editPackage} onClose={() => setEditPackage(null)} title={editPackage?.id ? zhEn("編輯套票", "Edit package") : zhEn("新增套票", "New package")}>
        {editPackage && (() => {
          const allServices = tenant?.services || [];
          const pricingMode = editPackage.pricing?.mode || "flat";
          // Help tip rendered inline at the top of the editor body.
          // (BottomSheet's title prop is plain text only.)
          // Per-size pricing reuses the size matrix from one of the linked
          // services (or any service with prices) so the owner doesn't have
          // to re-enter weight ranges twice. Prefer a service that actually
          // has non-zero prices set — picking an unconfigured service makes
          // auto-calc return all zeros which is useless.
          // Format-agnostic: reads prices from `sizes` (dog) OR `byTier` (cat) —
          // otherwise cat services looked "unpriced" and the matrix stayed blank.
          const hasNonZero = (svc) => svcTierPrices(svc, tenant).some(r => r.shortHair > 0 || r.longHair > 0);
          const linkedIds = editPackage.serviceIds || [];
          const linkedPriced = allServices.find(s => linkedIds.includes(s.id) && hasNonZero(s));
          const linkedAny = allServices.find(s => linkedIds.includes(s.id) && s.matrixPricing);
          const anyPriced = allServices.find(hasNonZero);
          const anyMatrix = allServices.find(s => s.matrixPricing);
          const primaryService = linkedPriced || linkedAny || anyPriced || anyMatrix || null;
          const sizeRows = (() => { const r = svcTierPrices(primaryService, tenant); return r.length ? r : getShopTiers(tenant); })();
          const perSize = editPackage.pricing?.perSize || {};
          const setPerSize = (label, coat, val) => setEditPackage(f => ({
            ...f,
            pricing: { ...f.pricing, mode: "perSize", perSize: { ...(f.pricing?.perSize || {}), [label]: { ...((f.pricing?.perSize || {})[label] || {}), [coat]: parseFloat(val) || 0 } } },
          }));
          // Special-breed package prices (per tier, per coat) — keyed by breedId
          // then tier label, mirroring perSize. Only breeds the linked service
          // actually prices are shown.
          const perBreed = editPackage.pricing?.perBreed || {};
          const pricedBreeds = svcPricedBreeds(primaryService, tenant);
          const setPerBreed = (breedId, label, coat, val) => setEditPackage(f => {
            const pb = f.pricing?.perBreed || {};
            const br = pb[breedId] || {};
            return { ...f, pricing: { ...f.pricing, mode: "perSize", perBreed: { ...pb, [breedId]: { ...br, [label]: { ...(br[label] || {}), [coat]: parseFloat(val) || 0 } } } } };
          });
          return <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:-4}}>
              <span style={{fontSize:12,color:"#8A8A9E",fontWeight:700}}>{zhEn("填寫套票資料", "Package details")}</span>
              <HelpTip topic="packageEditor" />
            </div>
            {/* Single-service binding — each package now applies to
                 exactly one service (matches owner's usual workflow:
                 「沖涼全套 10送2」, 「貓貓沖涼 5送1」, 「SPA-A 月卡」).
                 The previous multi-select gave confusing UX where one
                 package could quietly redeem against any service. */}
            <Sel
              label={zhEn("綁定服務 *", "Linked service *")}
              value={(editPackage.serviceIds && editPackage.serviceIds[0]) || ""}
              onChange={e => setEditPackage(f => ({ ...f, serviceIds: e.target.value ? [e.target.value] : [] }))}
            >
              <option value="">{zhEn("— 請揀一項服務 —", "— Choose a service —")}</option>
              <optgroup label={zhEn("🛁 主服務", "🛁 Main services")}>
                {allServices.filter(s => (s.kind || "service") !== "spa").map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.petType === "cat" ? " 🐱" : s.petType === "small" ? " 🐰" : ""}</option>
                ))}
              </optgroup>
              <optgroup label={zhEn("💆 SPA 服務", "💆 SPA services")}>
                {allServices.filter(s => s.kind === "spa").map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </optgroup>
            </Sel>
            <Input label={zhEn("套票名稱", "Package name")} value={editPackage.name} onChange={e => setEditPackage(f => ({...f, name: e.target.value}))} placeholder={zhEn("例：沖涼全套 10 送 2", "e.g. Full groom, buy 10 get 2")} />
            <Input label={zhEn("描述（選填）", "Description (optional)")} value={editPackage.description} onChange={e => setEditPackage(f => ({...f, description: e.target.value}))} placeholder={zhEn("例：適合每月一次的常客", "e.g. For regulars who come monthly")} />
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Input label={zhEn("付費次數", "Paid visits")} type="number" value={editPackage.paidVisits ?? editPackage.visits ?? 10} onChange={e => setEditPackage(f => ({...f, paidVisits: e.target.value}))} hint={zhEn("客戶實際支付嘅次數", "Number of visits the customer actually pays for")} />
              <Input label={zhEn("贈送次數", "Free visits")} type="number" value={editPackage.bonusVisits ?? 0} onChange={e => setEditPackage(f => ({...f, bonusVisits: e.target.value}))} hint={zhEn("例：填 2 即「10 送 2」", "e.g. enter 2 for \"buy 10 get 2\"")} />
            </div>
            <div style={{background:"var(--c-lighter)",borderRadius:14,padding:"10px 12px",fontSize:12,fontWeight:700,color:"var(--c-primary)"}}>
              {(() => {
                const p = parseInt(editPackage.paidVisits ?? editPackage.visits ?? 10) || 0;
                const b = parseInt(editPackage.bonusVisits ?? 0) || 0;
                const total = p + b;
                const pct = total > 0 ? Math.round((b / total) * 100) : 0;
                return zhEn("客戶共獲得 " + total + " 次" + (b > 0 ? "（" + p + " 付費 + " + b + " 贈送）· 相當於 " + pct + "% 折扣" : ""), "Customer gets " + total + " visits in total" + (b > 0 ? " (" + p + " paid + " + b + " free) · equivalent to " + pct + "% off" : ""));
              })()}
            </div>
            <Sel label={zhEn("計價方式", "Pricing method")} value={pricingMode} onChange={e => {
              const mode = e.target.value;
              setEditPackage(f => {
                let perSize = f.pricing?.perSize || {};
                let perBreed = f.pricing?.perBreed || {};
                // First-time switch to perSize and no cells filled yet:
                // pre-populate size AND special-breed prices from a priced
                // service × paidVisits so nothing is left blank.
                const empty = Object.keys(perSize).length === 0
                  || Object.values(perSize).every(c => !Number(c?.shortHair) && !Number(c?.longHair));
                if (mode === "perSize" && empty) {
                  const paid = parseInt(f.paidVisits ?? f.visits ?? 0) || 0;
                  const source = (linkedPriced || anyPriced || primaryService);
                  if (paid > 0 && source && hasNonZero(source)) {
                    const next = {};
                    for (const row of svcTierPrices(source, tenant)) {
                      next[row.label] = { shortHair: row.shortHair * paid, longHair: row.longHair * paid };
                    }
                    perSize = next;
                    const nb = {};
                    for (const sb of svcPricedBreeds(source, tenant)) {
                      const m = {};
                      for (const r of svcBreedTierPrices(source, sb.id, tenant)) {
                        if (r.shortHair > 0 || r.longHair > 0) m[r.label] = { shortHair: r.shortHair * paid, longHair: r.longHair * paid };
                      }
                      if (Object.keys(m).length) nb[sb.id] = m;
                    }
                    perBreed = nb;
                  }
                }
                return { ...f, pricing: { mode, perSize, perBreed } };
              });
            }}>
              <option value="flat">{zhEn("固定價格", "Fixed price")}</option>
              <option value="perSize">{zhEn("按體型 × 毛長 ⚡", "By size × coat ⚡")}</option>
            </Sel>
            <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"10px 12px",fontSize:11,fontWeight:700,color:"#92400E",lineHeight:1.55}}>
              <p style={{margin:"0 0 4px",fontWeight:900}}>{zhEn("💡 套票說明", "💡 About package pricing")}</p>
              <p style={{margin:0}}>
                · <strong>{zhEn("固定價格", "Fixed price")}</strong>{zhEn("：所有毛孩同一個套票價。", ": one package price for every pet.")}<br/>
                · <strong>{zhEn("按體型 × 毛長", "By size × coat")}</strong>{zhEn("：依照「服務管理」嘅體型矩陣自動換算，每個體型可以有獨立套票價。", ": calculated from the size matrix in Services, so each size can have its own package price.")}<br/>
                · <strong>{zhEn("付費 + 贈送", "Paid + Free")}</strong>{zhEn("：填", ": enter ")} <code>{zhEn("付費 10 / 贈送 2", "Paid 10 / Free 2")}</code> {zhEn("代表「10 送 2」，客戶共得 12 次。", " for \"buy 10 get 2\" — the customer gets 12 visits in total.")}<br/>
                · <strong>{zhEn("自動計算", "Auto-calculate")}</strong>{zhEn("：按一下 ⚡ 即會用「服務價 × 付費次數」填滿矩陣，再可以手動調整。", ": tap ⚡ to fill the matrix with \"service price × paid visits\"; you can then adjust it by hand.")}
              </p>
            </div>
            {pricingMode === "flat" && (
              <Input label={zhEn("套票售價 $", "Package price $")} type="number" value={editPackage.price} onChange={e => setEditPackage(f => ({...f, price: e.target.value}))} hint={zhEn("客戶不論毛孩體型都付同樣價", "Customers pay the same regardless of pet size")} />
            )}
            {pricingMode === "perSize" && (
              <div style={{background:"var(--c-lighter)",borderRadius:16,padding:12,border:"2px solid var(--c-border)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:800,color:"#4A4A68",margin:0}}>{zhEn("體型 × 毛長套票價（依"+(primaryService ? "「"+primaryService.name+"」的" : "預設")+"體型）", "Size × coat package price (by "+(primaryService ? "\""+primaryService.name+"\" size tiers" : "default size tiers")+")")}</p>
                  {svcTierPrices(primaryService, tenant).length > 0 && (() => {
                    const paid = parseInt(editPackage.paidVisits ?? editPackage.visits ?? 0) || 0;
                    return (
                      <button
                        onClick={() => {
                          if (paid <= 0) { toast(zhEn("請先輸入付費次數", "Enter the paid visits first"), "error"); return; }
                          // Pick the priced service for the multiplication —
                          // if the package's linked service has all-zero
                          // matrix prices, fall through to any service that
                          // actually has prices set.
                          const source = (linkedPriced || anyPriced || primaryService);
                          if (!source || !hasNonZero(source)) {
                            toast(zhEn("找不到已設定價格的服務 — 請先到「服務管理」填入體型價", "No service with prices found — fill in size prices under Services first"), "error");
                            return;
                          }
                          const next = {};
                          for (const row of svcTierPrices(source, tenant)) {
                            next[row.label] = {
                              shortHair: row.shortHair * paid,
                              longHair:  row.longHair  * paid,
                            };
                          }
                          // Also compute SPECIAL-BREED prices (per tier) so ⚡
                          // fills 泰迪/貴婦 etc. too, not just the size tiers.
                          const nextBreed = {};
                          for (const sb of svcPricedBreeds(source, tenant)) {
                            const m = {};
                            for (const r of svcBreedTierPrices(source, sb.id, tenant)) {
                              if (r.shortHair > 0 || r.longHair > 0) m[r.label] = { shortHair: r.shortHair * paid, longHair: r.longHair * paid };
                            }
                            if (Object.keys(m).length) nextBreed[sb.id] = m;
                          }
                          setEditPackage(f => ({ ...f, pricing: { mode: "perSize", perSize: next, perBreed: nextBreed } }));
                          const bc = Object.keys(nextBreed).length;
                          toast(zhEn("已用「" + source.name + "」× " + paid + " 次自動計算" + (bc ? "（含 " + bc + " 個特殊品種）" : ""), "Calculated from \"" + source.name + "\" × " + paid + (bc ? " (incl. " + bc + " special breeds)" : "")));
                        }}
                        style={{padding:"6px 12px",borderRadius:99,border:"none",background:"var(--c-primary)",color:"#fff",fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:4}}
                      >{zhEn("⚡ 自動計算", "⚡ Auto-calculate")}</button>
                    );
                  })()}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 46px 54px 60px 60px",gap:6,marginBottom:6}}>
                  <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800}}>{zhEn("體型", "Size")}</span>
                  <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>kg</span>
                  <span style={{fontSize:10,color:"#B0B0C0",fontWeight:800,textAlign:"center"}}>{zhEn("原價", "List price")}</span>
                  <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("短毛 $", "Short $")}</span>
                  <span style={{fontSize:11,color:"#8A8A9E",fontWeight:800,textAlign:"center"}}>{zhEn("長毛 $", "Long $")}</span>
                </div>
                {sizeRows.map(row => (
                  <div key={row.id || row.label} style={{display:"grid",gridTemplateColumns:"1fr 46px 54px 60px 60px",gap:6,marginBottom:5,alignItems:"center"}}>
                    <span style={{fontSize:13,fontWeight:700,color:"#4A4A68"}}>{row.label}</span>
                    <span style={{fontSize:11,fontWeight:700,color:"#8A8A9E",textAlign:"center"}}>{row.minW}–{row.maxW}</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#B0B0C0",textAlign:"center",lineHeight:1.2}}>{(() => { const s = Number(row.shortHair) || 0, l = Number(row.longHair) || 0; if (!s && !l) return "—"; return (!l || s === l) ? "$" + s : "$" + s + "/" + l; })()}</span>
                    <input type="number" value={perSize[row.label]?.shortHair ?? ""} onChange={e => setPerSize(row.label, "shortHair", e.target.value)} placeholder="—" style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                    <input type="number" value={perSize[row.label]?.longHair ?? ""} onChange={e => setPerSize(row.label, "longHair", e.target.value)} placeholder="—" style={{border:"2px solid var(--c-light)",borderRadius:10,padding:"6px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box"}} />
                  </div>
                ))}
                <p style={{fontSize:11,color:"#AFAFC4",fontWeight:700,margin:"8px 0 0"}}>{zhEn("※ 售出時系統會按毛孩體重 + 毛長自動套用對應價格", "※ When sold, the matching price is applied automatically by the pet's weight + coat length")}</p>
                {pricedBreeds.length > 0 && (
                  <div style={{marginTop:12,background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:14,padding:12}}>
                    <p style={{fontSize:12,fontWeight:900,color:"#9A3412",margin:"0 0 4px"}}>{zhEn("🐩 特殊品種套票價", "🐩 Special-breed package prices")}</p>
                    <p style={{fontSize:10.5,color:"#C2410C",fontWeight:700,margin:"0 0 8px",lineHeight:1.5}}>{zhEn("依「", "Calculated from \"")}{primaryService?.name || zhEn("服務", "Service")}{zhEn("」嘅品種價自動換算（× 付費次數）。呢啲品種嘅套票會用呢度嘅價，唔會用上面嘅體型價。", "\" breed prices (× paid visits). Packages for these breeds use the prices here instead of the size prices above.")}</p>
                    {pricedBreeds.map(sb => {
                      const bid = sb.id;
                      const bname = sb.name || (sb.names || [])[0] || zhEn("特殊品種","Special breed");
                      const bm = perBreed[bid] || {};
                      return (
                        <div key={bid} style={{marginBottom:10}}>
                          <p style={{fontSize:12,fontWeight:800,color:"#7C2D12",margin:"0 0 4px"}}>{bname}</p>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 42px 50px 58px 58px",gap:6,marginBottom:4}}>
                            <span style={{fontSize:10,color:"#9A3412",fontWeight:800}}>{zhEn("體型", "Size")}</span>
                            <span style={{fontSize:10,color:"#9A3412",fontWeight:800,textAlign:"center"}}>kg</span>
                            <span style={{fontSize:9.5,color:"#C89B84",fontWeight:800,textAlign:"center"}}>{zhEn("原價", "List price")}</span>
                            <span style={{fontSize:10,color:"#9A3412",fontWeight:800,textAlign:"center"}}>{zhEn("短毛$", "Short $")}</span>
                            <span style={{fontSize:10,color:"#9A3412",fontWeight:800,textAlign:"center"}}>{zhEn("長毛$", "Long $")}</span>
                          </div>
                          {(() => { const bo = {}; svcBreedTierPrices(primaryService, bid, tenant).forEach(r => { bo[r.label] = r; }); return sizeRows.map(row => {
                            const o = bo[row.label] || {};
                            return (
                            <div key={row.id || row.label} style={{display:"grid",gridTemplateColumns:"1fr 42px 50px 58px 58px",gap:6,marginBottom:4,alignItems:"center"}}>
                              <span style={{fontSize:12,fontWeight:700,color:"#7C2D12"}}>{row.label}</span>
                              <span style={{fontSize:10,fontWeight:700,color:"#C2410C",textAlign:"center"}}>{row.minW}–{row.maxW}</span>
                              <span style={{fontSize:10,fontWeight:700,color:"#C89B84",textAlign:"center",lineHeight:1.2}}>{(() => { const s = Number(o.shortHair) || 0, l = Number(o.longHair) || 0; if (!s && !l) return "—"; return (!l || s === l) ? "$" + s : "$" + s + "/" + l; })()}</span>
                              <input type="number" value={bm[row.label]?.shortHair ?? ""} onChange={e => setPerBreed(bid, row.label, "shortHair", e.target.value)} placeholder="—" style={{border:"1.5px solid #FED7AA",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box",background:"#fff"}} />
                              <input type="number" value={bm[row.label]?.longHair ?? ""} onChange={e => setPerBreed(bid, row.label, "longHair", e.target.value)} placeholder="—" style={{border:"1.5px solid #FED7AA",borderRadius:8,padding:"5px",fontSize:12,fontWeight:800,textAlign:"center",outline:"none",width:"100%",boxSizing:"border-box",background:"#fff"}} />
                            </div>
                          ); }); })()}
                        </div>
                      );
                    })}
                  </div>
                )}
                {(() => {
                  // Value + savings per tier. Each cell = the package price the
                  // customer PAYS (service price × 付費次數). The VALUE = the same
                  // per-visit price × total visits (付費 + 贈送) — so the 贈送 次數
                  // are free. Only shown when there's a bonus to save.
                  const paid = parseInt(editPackage.paidVisits ?? editPackage.visits ?? 0) || 0;
                  const bonus = parseInt(editPackage.bonusVisits ?? 0) || 0;
                  const total = paid + bonus;
                  if (paid <= 0 || bonus <= 0) return null;
                  const val = (v) => Math.round((Number(v) || 0) * total / paid);
                  const rows = sizeRows.map(row => {
                    const c = perSize[row.label] || {};
                    const s = Number(c.shortHair) || 0, l = Number(c.longHair) || 0;
                    return (s || l) ? { label: row.label, s, l } : null;
                  }).filter(Boolean);
                  if (!rows.length) return null;
                  return (
                    <div style={{marginTop:10,background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:12,padding:"10px 12px"}}>
                      <p style={{fontSize:11,fontWeight:900,color:"#166534",margin:"0 0 6px"}}>{zhEn("💰 客戶價值（付 "+paid+" 次 → 用 "+total+" 次；"+bonus+" 次免費）", "💰 Customer value (pay for "+paid+" → use "+total+"; "+bonus+" free)")}</p>
                      {rows.map(r => (
                        <div key={r.label} style={{display:"flex",justifyContent:"space-between",gap:8,fontSize:11,fontWeight:700,color:"#166534",padding:"2px 0"}}>
                          <span style={{flexShrink:0}}>{r.label}</span>
                          <span style={{textAlign:"right"}}>{r.s ? zhEn("短 付$" + r.s + "→值$" + val(r.s), "S pay $" + r.s + "→worth $" + val(r.s)) : ""}{r.s && r.l ? " · " : ""}{r.l ? zhEn("長 付$" + r.l + "→值$" + val(r.l), "L pay $" + r.l + "→worth $" + val(r.l)) : ""}</span>
                        </div>
                      ))}
                      <p style={{fontSize:10,color:"#059669",fontWeight:700,margin:"6px 0 0"}}>{zhEn("付 = 客戶實付套票價 · 值 = 用足", "Pay = package price the customer pays · Worth = full-use value of ")} {total} {zhEn("次嘅等值服務價", " visits at normal prices")}</p>
                    </div>
                  );
                })()}
              </div>
            )}
            <Input label={zhEn("有效日數", "Valid for (days)")} type="number" value={editPackage.validityDays} onChange={e => setEditPackage(f => ({...f, validityDays: e.target.value}))} hint={zhEn("0 = 永久有效", "0 = never expires")} />
            <Input label={zhEn("低餘量提醒（剩餘 N 次以下會提示客戶）", "Low-balance alert (customers are notified when N visits or fewer remain)")} type="number" value={editPackage.lowBalanceThreshold ?? 2} onChange={e => setEditPackage(f => ({...f, lowBalanceThreshold: parseInt(e.target.value) || 0}))} hint={zhEn("預設 2。設 0 = 唔提示", "Default 2. Set 0 = no alert")} />
            <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:editPackage.enabled?"#ECFDF5":"#FEF2F2",cursor:"pointer"}}>
              <input type="checkbox" checked={!!editPackage.enabled} onChange={e => setEditPackage(f => ({...f, enabled: e.target.checked}))} style={{transform:"scale(1.2)"}} />
              <span style={{fontSize:13,fontWeight:800,color:"#4A4A68"}}>{zhEn("啟用（公開銷售）", "Active (on sale publicly)")}</span>
            </label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
              <Btn variant="secondary" onClick={() => setEditPackage(null)}>{zhEn("取消", "Cancel")}</Btn>
              <Btn onClick={async () => {
                if (!editPackage.name) { toast(i18nT("val.enterPackageName"), "error"); return; }
                if (!Array.isArray(editPackage.serviceIds) || editPackage.serviceIds.length === 0) {
                  toast(zhEn("請揀一項綁定服務", "Choose a service to link"), "error");
                  return;
                }
                const paid = parseInt(editPackage.paidVisits ?? editPackage.visits) || 1;
                const bonus = parseInt(editPackage.bonusVisits) || 0;
                try {
                  const saved = await upsertServicePackage({
                    ...editPackage,
                    tenantId: user.tenantId,
                    paidVisits: paid,
                    bonusVisits: bonus,
                    visits: paid + bonus,
                    price: parseFloat(editPackage.price) || 0,
                    pricing: editPackage.pricing || { mode: "flat" },
                    validityDays: parseInt(editPackage.validityDays) || 0,
                    // SPA / addon comp removed — packages cover only the
                    // main service. Force-disable on every save so any
                    // legacy `true` value gets normalized.
                    coversSpas: false,
                    coversAddons: false,
                  });
                  setServicePackages(arr => {
                    const i = arr.findIndex(x => x.id === saved.id);
                    if (i >= 0) { const next = arr.slice(); next[i] = saved; return next; }
                    return [saved, ...arr];
                  });
                  toast(editPackage.id ? zhEn("已更新", "Updated") : zhEn("已新增", "Added")); setEditPackage(null);
                } catch (e) { toast(e?.message || zhEn("儲存失敗", "Save failed"), "error"); }
              }}><Save size={16}/> {zhEn("儲存", "Save")}</Btn>
            </div>
          </div>;
        })()}
      </BottomSheet>

      <BottomSheet open={!!noteApt} onClose={() => setNoteApt(null)} title={zhEn("員工內部備注", "Staff internal note")}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:"var(--c-lighter)",padding:12,borderRadius:16,display:"flex",alignItems:"center",gap:8}}>
            <Lock size={16} color="var(--c-primary)" /><p style={{fontSize:13,color:"var(--c-primary)",margin:0,fontWeight:700}}>{zhEn("此備注僅員工可見，不會顯示給客戶", "Only staff can see this note; it is never shown to customers")}</p>
          </div>
          <textarea style={{border:"2px solid var(--c-light)",borderRadius:20,padding:"16px",fontSize:15,fontWeight:600,height:120,resize:"none",outline:"none",boxSizing:"border-box",width:"100%",color:"#4A4A68",transition:"border 0.3s"}} placeholder={zhEn("輸入備注事項...", "Enter a note...")} value={noteText} onChange={e => setNoteText(e.target.value)} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-light)"} />
          <Btn onClick={saveNote} style={{width:"100%",padding:"16px"}}><Save size={18} strokeWidth={2.5} /> {zhEn("儲存備注", "Save note")}</Btn>
        </div>
      </BottomSheet>

      <BottomSheet open={showGlobalSearch} onClose={() => { setShowGlobalSearch(false); setGlobalSearch(""); }} title={zhEn("全局搜尋", "Global search")}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{position:"relative"}}>
            <Search size={18} strokeWidth={2.5} style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",color:"#AFAFC4"}} />
            <input style={{width:"100%",border:"2px solid var(--c-grad1)",borderRadius:99,padding:"14px 14px 14px 44px",fontSize:15,fontWeight:700,outline:"none",boxSizing:"border-box",color:"#4A4A68"}} placeholder={i18nT("ph.searchGlobal")} value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} autoFocus />
          </div>
          {globalSearch.length >= 2 && globalResults.length === 0 && <p style={{fontSize:14,color:"#AFAFC4",textAlign:"center",padding:"32px 0",fontWeight:700}}>{zhEn("找不到相關結果 🥺", "No results found 🥺")}</p>}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {globalResults.map((r, i) => (
              <div key={i} onClick={() => { if (r.type === "customer") { setCrmUser(r.data.id); setCrmTab("info"); setTab("crm"); } else if (r.type === "apt") { setEditApt(r.data); } setShowGlobalSearch(false); setGlobalSearch(""); }} style={{padding:16,borderRadius:20,background:"#FFFDFD",border:"2px solid var(--c-light)",cursor:"pointer",display:"flex",gap:14,alignItems:"center"}}>
                <div style={{width:44,height:44,borderRadius:16,background:r.type==="customer"?"var(--c-light)":"#EFF6FF",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {r.type === "customer" ? <Users size={20} color="var(--c-primary)" strokeWidth={2.5} /> : <Calendar size={20} color="#3B82F6" strokeWidth={2.5} />}
                </div>
                <div>
                  <p style={{fontSize:15,fontWeight:800,margin:"0 0 2px",color:"#4A4A68"}}>{r.type==="customer" ? r.data.name : (state.pets[r.data.petId]?.name||"") + " — " + r.data.service}</p>
                  <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:600}}>{r.type==="customer" ? r.data.phone : r.data.date + " · " + (state.users[r.data.customerId]?.name||"")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </BottomSheet>

      {/* SMS/WhatsApp Notification Sender */}
      {/* Edit Appointment — draft pattern: changes only commit on save */}
      {/* Customer + pet edit sheets — top-level so they work from any tab
          (calendar's edit-apt sheet links here too). z=250 lifts them above
          the editApt sheet that's at the default z=200. */}
      <BottomSheet open={!!sendCouponForm} onClose={() => setSendCouponForm(null)} title={zhEn("🎫 送贈優惠券", "🎫 Give coupon")} z={260}>
        {sendCouponForm && (() => {
          const target = state.users[sendCouponForm.userId];
          const trimmedLabel = String(sendCouponForm.label || "").trim();
          const amtNum = Number(sendCouponForm.amount);
          const requiresAmount = sendCouponForm.type === "discount";
          const requiresRef    = sendCouponForm.type === "free_addon" || sendCouponForm.type === "free_spa";
          // Service pool for the type-specific picker. SPA services are
          // marked kind === "spa"; addons are entries in tenant.addons.
          const spaServices  = (tenant?.services || []).filter(s => s.kind === "spa");
          const addonEntries = tenant?.addons || [];
          const canSave = !savingCoupon
            && trimmedLabel.length > 0
            && (!requiresAmount || (amtNum > 0))
            && (!requiresRef    || !!sendCouponForm.refId);
          const send = async () => {
            setSavingCoupon(true);
            try {
              const n = await ownerGrantVoucher({
                userIds: [sendCouponForm.userId],
                tenantId: user.tenantId,
                voucher: {
                  type: sendCouponForm.type,
                  label: trimmedLabel,
                  amount: requiresAmount ? Math.round(amtNum) : 0,
                  // refId links the voucher to the specific service/addon
                  // so the booking flow can auto-attach it at checkout.
                  // Nil for pure cash discounts.
                  refId: requiresRef ? (sendCouponForm.refId || null) : null,
                  note: (sendCouponForm.note || "").trim() || undefined,
                  expiresAt: sendCouponForm.expiresAt || undefined,
                },
              });
              if (n > 0) {
                // Push notification alongside the in-app notification the
                // RPC already inserted. Silent-fails if no device token.
                sendPush({
                  userIds: [sendCouponForm.userId],
                  title: "🎫 你收到一張優惠券",
                  body: trimmedLabel + (requiresAmount ? " · $" + Math.round(amtNum) : ""),
                  tag: "voucher-grant",
                }).catch(err => { console.warn("[coupon-grant] push failed:", err); });
                toast(zhEn(`已送出優惠券 ✓ (${target?.name || "客戶"})`, `Coupon sent ✓ (${target?.name || "客戶"})`));
                setSendCouponForm(null);
              } else {
                toast(zhEn("送出失敗：客戶未加入你店舖", "Failed to send: the customer hasn't joined your shop"), "error");
              }
            } catch (e) {
              toast(zhEn("送出失敗：", "Failed to send: ") + (e?.message || ""), "error");
            }
            setSavingCoupon(false);
          };
          return (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:"var(--c-lighter)",padding:"10px 14px",borderRadius:14,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:99,background:"var(--c-primary)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900}}>{(target?.name || "?").charAt(0).toUpperCase()}</div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0}}>{target?.name || zhEn("客戶", "Customer")}</p>
                  <p style={{fontSize:11,fontWeight:700,color:"#8A8A9E",margin:0}}>📞 {target?.phone || "—"}</p>
                </div>
              </div>
              <div>
                <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("券種類型", "Coupon type")}</p>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
                  {[["discount",zhEn("💰 現金折扣", "💰 Cash discount")],["free_addon",zhEn("➕ 免費加購", "➕ Free add-on")],["free_spa",zhEn("💆 免費 SPA", "💆 Free SPA")]].map(([v,l]) => (
                    <button key={v} onClick={() => setSendCouponForm(f => ({...f, type: v, refId: null, label: f.type===v ? f.label : ""}))}
                      style={{padding:"10px 8px",borderRadius:12,fontSize:12,fontWeight:900,cursor:"pointer",border:"2px solid "+(sendCouponForm.type===v?"var(--c-primary)":"var(--c-light)"),background:sendCouponForm.type===v?"var(--c-primary)":"#fff",color:sendCouponForm.type===v?"#fff":"#4A4A68"}}>{l}</button>
                  ))}
                </div>
              </div>
              {requiresRef && (
                <div>
                  <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("指定", "Choose")}{sendCouponForm.type==="free_spa"?"SPA":zhEn("加購", "Add-ons")} *</p>
                  <select value={sendCouponForm.refId || ""} onChange={e => {
                    const id = e.target.value;
                    if (sendCouponForm.type === "free_spa") {
                      const s = spaServices.find(x => x.id === id);
                      setSendCouponForm(f => ({...f, refId: id, label: s ? zhEn("免費 " + s.name, "Free " + s.name) : f.label}));
                    } else {
                      const a = addonEntries.find(x => (x.id || x.name) === id);
                      setSendCouponForm(f => ({...f, refId: id, label: a ? zhEn("免費 " + a.name, "Free " + a.name) : f.label}));
                    }
                  }} style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box",background:"#fff"}}>
                    <option value="">{zhEn("— 揀一項 —", "— Choose one —")}</option>
                    {(sendCouponForm.type === "free_spa" ? spaServices : addonEntries).map(x => (
                      <option key={x.id || x.name} value={x.id || x.name}>{x.name}</option>
                    ))}
                  </select>
                  <p style={{fontSize:10,color:"#8A8A9E",fontWeight:700,margin:"4px 0 0",lineHeight:1.4}}>
                    {zhEn("揀咗之後，客戶預約時可以一鍵綁定 — 揀優惠券後自動加落預約，唔使人手加。", "Once chosen, customers can attach it in one tap when booking — the coupon is added to the booking automatically, no manual adding.")}
                  </p>
                </div>
              )}
              <div>
                <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("標籤（客戶會見到）", "Label (customers see this)")}</p>
                <input value={sendCouponForm.label} onChange={e => setSendCouponForm(f => ({...f, label: e.target.value}))}
                  placeholder={sendCouponForm.type==="discount"?zhEn("例：$50 折扣券","e.g. $50 off coupon"):sendCouponForm.type==="spa"?zhEn("例：免費 炭酸泉 SPA","e.g. Free carbonated-spring SPA"):zhEn("例：免費刷牙服務","e.g. Free teeth brushing")}
                  style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
              </div>
              {requiresAmount && (
                <div>
                  <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("折扣金額 ($)", "Discount amount ($)")}</p>
                  <input type="number" min="1" value={sendCouponForm.amount} onChange={e => setSendCouponForm(f => ({...f, amount: e.target.value}))}
                    placeholder="50"
                    style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
                </div>
              )}
              <div>
                <p style={{fontSize:12,fontWeight:900,color:"#4A4A68",margin:"0 0 6px"}}>{zhEn("備註（可選）", "Notes (optional)")}</p>
                <input value={sendCouponForm.note || ""} onChange={e => setSendCouponForm(f => ({...f, note: e.target.value}))}
                  placeholder={zhEn("例：新春優惠 · 情人節禮物", "e.g. Lunar New Year offer · Valentine's gift")}
                  style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:12,padding:"10px 12px",fontSize:14,fontWeight:800,color:"#4A4A68",outline:"none",boxSizing:"border-box"}} />
              </div>
              <p style={{fontSize:11,color:"#8A8A9E",fontWeight:700,margin:"4px 0",lineHeight:1.5}}>
                {zhEn("客戶會即刻收到通知，可以喺 App 內「我嘅優惠券」睇到，並喺下次預約時使用。", "The customer is notified immediately, can see it under \"My coupons\" in the app, and can use it on their next booking.")}
              </p>
              <Btn onClick={send} disabled={!canSave} style={{width:"100%",padding:14,opacity:canSave?1:0.5}}>
                {savingCoupon ? zhEn("送出中…", "Sending…") : zhEn("送出優惠券", "Send coupon")}
              </Btn>
            </div>
          );
        })()}
      </BottomSheet>

      <BottomSheet open={!!editCustForm} onClose={() => setEditCustForm(null)} title={i18nT("cust.edit")} z={250}>
        {editCustForm && (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Input label={i18nT("cust.name")} value={editCustForm.name} onChange={e => setEditCustForm(f => ({...f, name: e.target.value}))} placeholder={i18nT("cust.name")} />
            <div>
              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{i18nT("cust.phone")}</p>
              <div style={{display:"grid",gridTemplateColumns:"130px 1fr",gap:8}}>
                <Sel value={editCustForm.countryCode || "+852"} onChange={e => setEditCustForm(f => ({...f, countryCode: e.target.value}))}>
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </Sel>
                <Input value={editCustForm.phone} onChange={e => { const parsed = parseCopiedPhone(e.target.value); setEditCustForm(f => ({...f, phone: parsed.digits, ...(parsed.countryCode ? { countryCode: parsed.countryCode } : {})})); }} placeholder="9123 4567" type="tel" inputMode="tel" />
              </div>
            </div>
            <div>
              <p style={{fontSize:12,fontWeight:800,color:"#8A8A9E",margin:"0 0 6px"}}>{i18nT("cust.phone2")}</p>
              <div style={{display:"grid",gridTemplateColumns:"130px 1fr",gap:8}}>
                <Sel value={editCustForm.phone2CountryCode || "+852"} onChange={e => setEditCustForm(f => ({...f, phone2CountryCode: e.target.value}))}>
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </Sel>
                <Input value={editCustForm.phone2 || ""} onChange={e => { const parsed = parseCopiedPhone(e.target.value); setEditCustForm(f => ({...f, phone2: parsed.digits, ...(parsed.countryCode ? { phone2CountryCode: parsed.countryCode } : {})})); }} placeholder={zhEn("例：家人 / 公司電話", "e.g. family / office phone")} type="tel" inputMode="tel" />
              </div>
            </div>
            <Input label={i18nT("cust.address")} value={editCustForm.address || ""} onChange={e => setEditCustForm(f => ({...f, address: e.target.value}))} placeholder={i18nT("cust.address")} />
            <Input label={i18nT("cust.email")} value={editCustForm.email || ""} onChange={e => setEditCustForm(f => ({...f, email: e.target.value}))} placeholder="customer@example.com" type="email" />
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
              <Btn variant="secondary" onClick={() => setEditCustForm(null)}>{i18nT("common.cancel")}</Btn>
              <Btn onClick={saveCustomerEdit}><Save size={16} strokeWidth={2.5}/> {i18nT("common.save")}</Btn>
            </div>
          </div>
        )}
      </BottomSheet>
      <BottomSheet open={!!editPet} onClose={() => setEditPet(null)} title={zhEn("編輯毛孩資料", "Edit pet details")} z={250}>
        {editPet && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Input label={zhEn("名字", "Name")} value={editPet.name || ""} onChange={e => setEditPet(p => ({...p, name: e.target.value}))} />
            <Sel label={zhEn("種類", "Type")} value={editPet.type || "dog"} onChange={e => setEditPet(p => ({...p, type: e.target.value, breedId: null, breed: ""}))}>
              <option value="dog">{zhEn("🐶 狗", "🐶 Dog")}</option>
              <option value="cat">{zhEn("🐱 貓", "🐱 Cat")}</option>
            </Sel>
            {/* Breed picker — typeahead combobox keyed off pet type. Same
                 component the walk-in form uses, so users get the same
                 fuzzy matching (zh / en / aliases) + auto coat hint. */}
            <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("品種", "Breed")}</p>
              <BreedCombobox
                type={editPet.type === "cat" ? "cat" : "dog"}
                lang={tenant?.lang || "zh-HK"}
                value={editPet.breedId}
                valueLabel={editPet.breed}
                placeholder={zhEn("例：貴婦犬 / Poodle", "e.g. Poodle")}
                onChange={({ breedId, breed, coatHint }) => setEditPet(p => ({
                  ...p,
                  breedId,
                  breed,
                  coatLength: coatHint || p.coatLength || "short",
                }))}
              />
            </div>
            <Input label={i18nT("pet.weightKg")} type="number" value={editPet.weight || ""} onChange={e => setEditPet(p => ({...p, weight: parseFloat(e.target.value) || 0}))} />
            <Sel label={zhEn("毛長", "Coat length")} value={editPet.coatLength || "short"} onChange={e => setEditPet(p => ({...p, coatLength: e.target.value}))}>
              <option value="short">{zhEn("短毛", "Short coat")}</option>
              <option value="long">{zhEn("長毛", "Long coat")}</option>
            </Sel>
            <Input label={i18nT("pet.birthday")} type="date" max={dateNow()} min={petBirthdayMin()} value={editPet.birthday || ""} onChange={e => setEditPet(p => ({...p, birthday: e.target.value}))} />
            <Input label={zhEn("疫苗日期", "Vaccination date")} type="date" value={editPet.vaccineDate || ""} onChange={e => setEditPet(p => ({...p, vaccineDate: e.target.value}))} />
            <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("過敏 / 健康備註", "Allergies / health notes")}</p>
              <textarea value={editPet.allergies || ""} onChange={e => setEditPet(p => ({...p, allergies: e.target.value}))} placeholder={zhEn("例：對洗毛精過敏 / 心臟病注意", "e.g. Allergic to shampoo / heart condition")} style={{width:"100%",minHeight:60,border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px",fontSize:13,fontWeight:600,outline:"none",color:"#4A4A68",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}} />
            </div>
            <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{i18nT("apt.staffNote")}</p>
              <textarea value={editPet.staffNote || ""} onChange={e => setEditPet(p => ({...p, staffNote: e.target.value}))} placeholder={zhEn("例：咬腳、No Show 不接", "e.g. Bites feet, no-show — don't accept")} style={{width:"100%",minHeight:60,border:"2px solid var(--c-light)",borderRadius:14,padding:"10px 12px",fontSize:13,fontWeight:600,outline:"none",color:"#4A4A68",resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}} />
            </div>
            {/* Per-dog SPECIAL / legacy price (舊客優惠). Set a fixed price for a
                specific service on THIS dog — used everywhere (AI, online booking,
                walk-in). Blank = normal price. */}
            {(() => {
              const _pt = editPet.type || "dog";
              const svcList = (tenant?.services || []).filter(s => s.kind !== "spa" && s.customerVisible !== false && serviceTypes(s).includes(_pt));
              if (!svcList.length) return null;
              const sp = editPet.specialPrices || {};
              const blocked = editPet.blockedServiceIds || [];
              const toggleBlock = (id) => setEditPet(p => { const cur = p.blockedServiceIds || []; const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]; return { ...p, blockedServiceIds: next }; });
              return (
                <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:14,padding:"12px 14px"}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"0 0 2px"}}>{zhEn("💰 特別價 / 不提供（選填）", "💰 Special price / not offered (optional)")}</p>
                  <p style={{fontSize:11,color:"#A16207",fontWeight:700,margin:"0 0 10px",lineHeight:1.5}}>{zhEn("為呢隻寵物設定個別服務嘅固定價（留空 = 正常價），或勾「不提供」隱藏唔做嘅服務。AI 助手、網上預約、即場都會跟住做。", "Set a fixed price for this pet on individual services (blank = normal price), or tick \"Not offered\" to hide services you don't do for it. The AI assistant, online booking and walk-ins all follow this.")}</p>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {svcList.map(s => { const isBlocked = blocked.includes(s.id); return (
                      <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,opacity:isBlocked?0.55:1}}>
                        <span style={{flex:1,minWidth:0,fontSize:12.5,fontWeight:700,color:"#4A4A68",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textDecoration:isBlocked?"line-through":"none"}}>{s.name}</span>
                        <span style={{fontSize:13,fontWeight:800,color:"#A16207"}}>$</span>
                        <input type="number" inputMode="numeric" placeholder={zhEn("正常價", "Normal price")} disabled={isBlocked} value={sp[s.id] ?? ""}
                          onChange={e => { const v = e.target.value; setEditPet(p => { const next = { ...(p.specialPrices || {}) }; if (v === "" || v == null) delete next[s.id]; else next[s.id] = Number(v); return { ...p, specialPrices: next }; }); }}
                          style={{width:76,padding:"8px 8px",borderRadius:10,border:"2px solid #FDE68A",fontSize:13,fontWeight:800,outline:"none",textAlign:"right",background:isBlocked?"#F3F4F6":"#fff",color:"#92400E"}} />
                        <button type="button" onClick={() => toggleBlock(s.id)} title={zhEn("呢隻寵物唔做呢項服務", "This pet doesn't get this service")}
                          style={{flexShrink:0,padding:"6px 8px",borderRadius:8,border:"2px solid "+(isBlocked?"#EF4444":"#E5E7EB"),background:isBlocked?"#FEE2E2":"#fff",color:isBlocked?"#B91C1C":"#9CA3AF",fontSize:11,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>{isBlocked?zhEn("不提供 ✓","Not offered ✓"):zhEn("不提供","Not offered")}</button>
                      </div>
                    ); })}
                  </div>
                </div>
              );
            })()}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
              <Btn variant="secondary" onClick={() => setEditPet(null)}>{zhEn("取消", "Cancel")}</Btn>
              <Btn onClick={() => {
                if (!editPet.name?.trim()) { toast(i18nT("val.enterPetNameRequired"), "error"); return; }
                const bErr = petBirthdayError(editPet.birthday); if (bErr) { toast(bErr, "error"); return; }
                setState(s => ({ ...s, pets: { ...s.pets, [editPet.id]: { ...s.pets[editPet.id], ...editPet } } }));
                toast(zhEn("毛孩資料已更新 ✓", "Pet details updated ✓"));
                setEditPet(null);
              }}><Save size={16} strokeWidth={2.5}/> {zhEn("儲存", "Save")}</Btn>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Photo lightbox — tap anywhere to dismiss. Image is contained
           inside the viewport so portrait + landscape both fit. Higher
           z than any sheet so it overlays whatever opened it. */}
      {photoLightbox && (
        <div
          onClick={() => setPhotoLightbox(null)}
          style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px",cursor:"zoom-out",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)"}}
        >
          <button
            onClick={e => { e.stopPropagation(); setPhotoLightbox(null); }}
            aria-label={zhEn("關閉", "Close")}
            style={{position:"absolute",top:18,right:18,width:40,height:40,borderRadius:99,background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",fontSize:20,fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)"}}
          >✕</button>
          <img
            src={photoLightbox.url}
            alt={photoLightbox.name || ""}
            onClick={e => e.stopPropagation()}
            style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:16,boxShadow:"0 24px 60px rgba(0,0,0,0.5)"}}
          />
          {photoLightbox.name && (
            <div style={{position:"absolute",bottom:24,left:0,right:0,textAlign:"center",color:"#fff",fontSize:14,fontWeight:800,letterSpacing:0.3,textShadow:"0 2px 8px rgba(0,0,0,0.6)"}}>{photoLightbox.name}</div>
          )}
        </div>
      )}

      <BottomSheet open={!!editApt} onClose={() => { if (editApt && editApt._dirty) { if (!window.confirm(i18nT("apt.unsaved"))) return; } setEditApt(null); }} title={editApt?._mode === "edit" ? (i18nT("apt.edit") + " — " + (state.pets[editApt.petId]?.name||"")) : i18nT("apt.detail")}>
        {editApt && (() => {
          const ea = editApt;
          const isView = ea._mode !== "edit";
          const original = state.appointments.find(a => a.id === ea.id) || ea;
          const pet = state.pets[ea.petId]; const cust = state.users[ea.customerId];
          // Local draft mutation — NEVER writes to state until save
          const setDraft = (updates) => setEditApt(prev => ({ ...prev, ...updates, _dirty: true }));
          const tenantAddons = tenant?.addons || [];
          // Combined SPA + addon total. Previously calcAddonPrice only
          // summed addons, so toggling an addon (and the 清空 button) would
          // overwrite addonPrice with addon-only — silently dropping the
          // SPA portion the SPA-toggle handler had baked in. Always pass
          // both current spaIds + addons so no caller can accidentally
          // clobber the other half.
          const calcAddonPrice = (addons, spaIds = ea.spaIds || []) => {
            const ads = (addons || []).reduce((s, n) => {
              const a = tenantAddons.find(x => x.name === n);
              return s + (a ? computeAddonPrice(a, pet).price : 0);
            }, 0);
            const sps = (spaIds || []).reduce((s, id) => {
              const sp = (tenant?.services || []).find(x => x.id === id && x.kind === "spa");
              if (!sp) return s;
              const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice };
              return s + (Number(pd.total) || 0);
            }, 0);
            return ads + sps;
          };
          const toggleAddon = (name) => {
            const has = (ea.addons||[]).includes(name);
            const next = has ? ea.addons.filter(x=>x!==name) : [...(ea.addons||[]), name];
            setDraft({ addons: next, addonPrice: calcAddonPrice(next, ea.spaIds) });
          };
          const surTotal = (ea.surcharges||[]).reduce((s,x)=>s+x.amount,0);
          const subtotal = ea.basePrice + ea.addonPrice + surTotal;
          const disc = ea.discount || { amount:0, type:"percent", reason:"" };
          const discValue = disc.type === "percent" ? Math.round(subtotal * (disc.amount||0) / 100) : (disc.amount||0);
          const total = Math.max(0, subtotal - discValue);
          const setDiscount = (next) => setDraft({ discount: { ...disc, ...next } });

          // Commit draft to state, building audit log entries from diff
          const commitDraft = () => {
            // 1) Discount cap enforcement (non-owners)
            const isOwner = user.role === "owner";
            const capPct = isOwner ? 100 : (user.permissions?.discountCapPercent ?? 0);
            const effectivePct = disc.type === "percent" ? (disc.amount||0) : (subtotal > 0 ? Math.round(((discValue||0) / subtotal) * 100) : 0);
            if (!isOwner && effectivePct > capPct) {
              toast(i18nT("edit.discountOverCap1") + effectivePct + i18nT("edit.discountOverCap2") + capPct + i18nT("edit.discountOverCap3"), "error");
              return;
            }
            // 2) Reschedule conflict check (date/time/staff changed)
            if (ea.date !== original.date || ea.time !== original.time || ea.staffId !== original.staffId || ea.customDuration !== original.customDuration) {
              const dur = getAptDuration(ea, tenant);
              const [nh, nm] = ea.time.split(":").map(Number);
              const newStart = nh*60 + nm;
              const others = (state.appointments||[]).filter(a => a.id !== ea.id && a.staffId === ea.staffId && a.date === ea.date && a.status !== "cancelled" && a.status !== "noshow");
              const conflict = others.some(o => {
                const [oh, om] = o.time.split(":").map(Number);
                const oStart = oh*60+om;
                const oDur = getAptDuration(o, tenant);
                return newStart < (oStart + oDur) && (newStart + dur) > oStart;
              });
              if (conflict) {
                toast(i18nT("edit.staffConflict1") + ea.date + " " + ea.time + i18nT("edit.staffConflict2"), "error");
                return;
              }
            }
            // 3) Build log diff
            const log = [...(original.priceAdjustLog||[])];
            const stamp = dateNow()+" "+timeNow();
            const changes = [];
            if (ea.basePrice !== original.basePrice) {
              log.push({ field:"basePrice", from:original.basePrice, to:ea.basePrice, reason:i18nT("edit.manualAdj"), by:user.id, at:stamp });
              changes.push(i18nT("edit.svcChanged1")+original.basePrice+"→$"+ea.basePrice);
            }
            const oldDisc = JSON.stringify(original.discount||null);
            const newDisc = JSON.stringify(ea.discount||null);
            if (oldDisc !== newDisc) {
              log.push({ field:"discount", from:oldDisc, to:newDisc, by:user.id, at:stamp });
              changes.push(i18nT("edit.discAdj"));
            }
            if (ea.date !== original.date || ea.time !== original.time) {
              log.push({ field:"reschedule", from: original.date+" "+original.time, to: ea.date+" "+ea.time, by:user.id, at:stamp });
              changes.push(i18nT("edit.reschedTo") + ea.date + " " + ea.time);
            }
            if (ea.staffId !== original.staffId) {
              log.push({ field:"staff", from: original.staffId, to: ea.staffId, by:user.id, at:stamp });
              changes.push(i18nT("edit.staffChanged"));
            }
            const finalApt = { ...ea, priceAdjustLog: log };
            delete finalApt._dirty;
            delete finalApt._mode;
            const petStaffNoteDraft = ea._petStaffNoteDraft;
            delete finalApt._petStaffNoteDraft;
            setState(s => {
              const next = { ...s, appointments: s.appointments.map(a => a.id === ea.id ? finalApt : a) };
              // Persist the pet-level staff note alongside if it changed.
              if (pet && petStaffNoteDraft != null && petStaffNoteDraft !== (pet.staffNote || "")) {
                next.pets = { ...s.pets, [pet.id]: { ...s.pets[pet.id], staffNote: petStaffNoteDraft } };
                changes.push(i18nT("edit.petNoteUpdated"));
              }
              return next;
            });
            // Notify customer if rescheduled — in-app push + auto email/WhatsApp.
            if ((ea.date !== original.date || ea.time !== original.time) && ea.customerId) {
              pushNotif(setState, { tenantId: ea.tenantId, userId: ea.customerId, type:"schedule_reminder", title:i18nT("edit.rescheduledTitle"), body:i18nT("edit.rescheduledBody") + ea.date + " " + ea.time, read:false, aptId: ea.id });
              const cust = state.users?.[ea.customerId];
              const petName = state.pets?.[ea.petId]?.name || "";
              autoSendMessage("booking_rescheduled", {
                shopName: tenant?.name || "",
                customerName: cust?.name || "",
                petName,
                service: ea.service || "",
                date: ea.date,
                time: ea.time,
                phone: cust?.phone || "",
                email: cust?.email || "",
                aptId: ea.id,
                customerId: ea.customerId,
              }, state, tenant).catch(e => console.warn("autoSendMessage(reschedule) failed:", e));
            }
            toast(changes.length > 0 ? (i18nT("common.saved") + " " + changes.join("、")) : i18nT("common.saved"));
            setEditApt(null);
          };
          const discardDraft = () => {
            if (ea._dirty && !window.confirm(i18nT("edit.discardPrompt"))) return;
            setEditApt(null);
          };

          // ─────────────────────────────────────────────────────────────
          // VIEW mode — read-only summary. Owner clicks 編輯 to switch to
          // the editable form below.
          // ─────────────────────────────────────────────────────────────
          if (isView) {
            // 報告 (grooming report) sits between 美容 and 接回 — it's an action
            // step rather than a status change. Tapping it opens the report
            // sheet without flipping `status`; the existing save handler
            // inside the sheet sets status="done" after the report is
            // submitted, which makes the stepper auto-advance to 接回.
            // Simplified 3-step flow: 到店 → 美容中 → 完成.
            // pending = booking exists but pet not yet arrived → all 3
            // circles dim, tapping 到店 = first action. paid + report are
            // surfaced as independent chips beside the stepper (not steps).
            const healthReportEnabled = tenant?.healthReportEnabled !== false;
            // Split "完成" and "報告" into separate steps — user can mark
            // done first, fill the report later (the previous flow tied
            // them together and pushed the report sheet immediately).
            // The "report" step is a UI-only pseudo-step: it doesn't
            // change apt.status; it just visualizes whether apt.report
            // has been submitted yet.
            const STATUS_FLOW = [
              { id: "checkedin", label: i18nT("edit.checkin"),   icon: MapPin },
              { id: "grooming",  label: i18nT("edit.grooming"),  icon: Scissors },
              { id: "done",      label: i18nT("edit.done"),      icon: CheckCheck },
              ...(healthReportEnabled ? [{ id: "report", label: zhEn("報告","Report"), icon: FileText, pseudo: true }] : []),
            ];
            const reportSubmitted = !!ea.report;
            const stepIdx = (() => {
              if (ea.status === "done") {
                // Report step counts as reached only when report actually saved.
                return (healthReportEnabled && reportSubmitted) ? 3 : 2;
              }
              if (ea.status === "grooming") return 1;
              if (ea.status === "checkedin") return 0;
              return -1; // pending — no step active yet
            })();
            const fullSpecial = ea.status === "cancelled" || ea.status === "noshow";
            // Past-date lock: appointments scheduled for a previous day
            // should not change status anymore (they're either done or
            // no-show). The stepper greys out and the goTo handler
            // refuses. Report editing is STILL allowed via the dedicated
            // 報告 step so groomers can upload late photos / fix typos.
            const isPastDate = ea.date && ea.date < dateNow();
            // Owner can override the past-date lock via the 🔓 unlock button
            // (banner appears at the top of the detail sheet). Override is
            // session-only — set `_unlocked` flag on the editApt draft.
            const lockStatus = isPastDate && !ea._unlocked;
            const staff = state.users[ea.staffId];
            const tenantAddons2 = tenant?.addons || [];
            const addonLines = (ea.addons || [])
              // Filter out legacy "💆 SPA …" labels that older appointments
              // wrote into addons — SPA is rendered separately from spaIds
              // and we don't want it appearing twice (as a phantom $0 row).
              .filter(name => !String(name).startsWith("💆"))
              .map(name => {
                const a = tenantAddons2.find(x => x.name === name);
                const price = a ? computeAddonPrice(a, pet).price : 0;
                return { name, price };
              });
            const spaLines = (ea.spaIds || []).map(id => {
              const sp = (tenant?.services || []).find(s => s.id === id);
              if (!sp) return null;
              const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice };
              return { id, name: sp.name, price: pd.total };
            }).filter(Boolean);
            const surchargeLines = (ea.surcharges || []).map(s => ({ name: s.label || i18nT("apt.surcharge"), price: s.amount }));
            const viewSubtotal = ea.basePrice + ea.addonPrice + (ea.surcharges || []).reduce((s, x) => s + x.amount, 0);
            const viewDisc = ea.discount ? (ea.discount.type === "percent"
              ? Math.round(viewSubtotal * (ea.discount.amount || 0) / 100)
              : (ea.discount.amount || 0)) : 0;
            // Voucher discount written by customer_settle_booking RPC.
            // Cap at the remaining subtotal so a $500 voucher on a $300
            // booking only reduces by $300 — wasted overage stays gone.
            const voucherDisc = Math.max(0, Math.min(Number(ea.voucherDiscount) || 0, Math.max(0, viewSubtotal - viewDisc)));
            const total = Math.max(0, viewSubtotal - viewDisc - voucherDisc);
            const cc = (cust?.countryCode || "+852").replace(/[^\d+]/g, "");
            const digits = (cust?.phone || "").replace(/\D/g, "");
            const fullTel = digits ? cc + digits : "";
            const waNumber = fullTel.replace(/^\+/, "");
            const phoneDisplay = digits ? (cust?.countryCode || "+852") + " " + cust.phone : "";
            // Preset labels — sit between status and free-text. Pick a
            // preset or type a custom one. Custom values persist as-is and
            // re-appear in the dropdown next time as recently-used.
            const LABEL_PRESETS = [i18nT("apt.labelPending"),i18nT("apt.labelConfirmed"),i18nT("apt.labelPaid"),i18nT("apt.labelLate"),i18nT("apt.labelWait"),i18nT("apt.labelVip"),i18nT("apt.labelRush"),i18nT("apt.labelOther")];
            return (
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Past-date lock banner — appears for any appointment
                    scheduled before today. Owner can tap 🔓 解鎖 to
                    temporarily enable status / payment edits (e.g. backfill
                    a missed 收款). Override is in-session only; closing
                    the sheet re-locks. */}
                {isPastDate && (
                  <div style={{background:ea._unlocked?"#FEF3C7":"#F3F4F6",border:"2px solid "+(ea._unlocked?"#FDE68A":"#E5E7EB"),borderRadius:14,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:1}}>
                      <span style={{fontSize:18}}>{ea._unlocked?"🔓":"🔒"}</span>
                      <div style={{minWidth:0}}>
                        <p style={{fontSize:12,fontWeight:900,color:ea._unlocked?"#92400E":"#374151",margin:0}}>{ea._unlocked?i18nT("ea.unlock.unlocked"):i18nT("ea.unlock.locked")}</p>
                        <p style={{fontSize:10,color:ea._unlocked?"#92400E":"#6B7280",margin:"2px 0 0",fontWeight:700,opacity:0.85,lineHeight:1.3}}>{ea._unlocked?i18nT("ea.unlock.unlockedSub"):i18nT("ea.unlock.lockedSub")}</p>
                      </div>
                    </div>
                    <button onClick={() => { setEditApt(prev => prev ? { ...prev, _unlocked: !prev._unlocked } : prev); toast(ea._unlocked ? i18nT("ea.unlock.relockedToast") : i18nT("ea.unlock.unlockedToast"), "info"); }} style={{flexShrink:0,padding:"7px 14px",borderRadius:99,border:"none",background:ea._unlocked?"#92400E":"#4A4A68",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}>{ea._unlocked?i18nT("ea.unlock.relock"):i18nT("ea.unlock.btn")}</button>
                  </div>
                )}
                {/* Top toolbar — transparent, single row. Action buttons
                    pinned to the left; 🏷️ label dropdown sits in the
                    top-right corner per latest UX request. */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginTop:-8,flexWrap:"nowrap",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                  <div style={{position:"relative",order:2}}>
                    <select
                      value={ea.label && LABEL_PRESETS.includes(ea.label) ? ea.label : (ea.label ? "__custom__" : "")}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom__") {
                          const cur = window.prompt(i18nT("edit.customLabelPrompt"), ea.label && !LABEL_PRESETS.includes(ea.label) ? ea.label : "");
                          if (cur !== null) {
                            const next = cur.trim() || null;
                            setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, label: next } : a) }));
                            setEditApt(prev => prev ? { ...prev, label: next } : prev);
                          }
                          return;
                        }
                        const next = v || null;
                        setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, label: next } : a) }));
                        setEditApt(prev => prev ? { ...prev, label: next } : prev);
                      }}
                      style={{padding:"6px 28px 6px 12px",borderRadius:99,border:"1px solid #E5E5EA",background: ea.label ? "#FEF3C7" : "#fff",color: ea.label ? "#92400E" : "#6A6A85",fontSize:12,fontWeight:800,outline:"none",cursor:"pointer",appearance:"none",WebkitAppearance:"none",backgroundImage:"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'><path fill='%236A6A85' d='M0 0l4 5 4-5z'/></svg>\")",backgroundRepeat:"no-repeat",backgroundPosition:"right 10px center",backgroundSize:"8px 5px"}}
                    >
                      <option value="">{i18nT("edit.noLabel")}</option>
                      {LABEL_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                      {ea.label && !LABEL_PRESETS.includes(ea.label) && (
                        <option value={ea.label}>{ea.label}</option>
                      )}
                      <option value="__custom__">{i18nT("edit.customLabel")}</option>
                    </select>
                  </div>
                  {/* Action buttons inline — the previous ⋯ More menu was
                      one extra tap that obscured what was inside. Surfaced
                      directly: 報告 (when filled or in progress), 再次預約
                      (when done), 編輯 (always). 編輯預約 is no longer
                      duplicated in a dropdown. */}
                  <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",order:1}}>
                    {(ea.status === "done" || ea.status === "grooming" || ea.report) && (
                      <button
                        onClick={() => setReportSheet(ea.id)}
                        style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,background:"#F2F2F7",border:"1px solid #E5E5EA",fontSize:12,fontWeight:800,color:"#1C1C1E",cursor:"pointer"}}
                      >
                        <FileText size={13} strokeWidth={2.5} /> {ea.report ? i18nT("edit.report") : i18nT("edit.fillReport")}
                      </button>
                    )}
                    {/* 📸 前後對比照 — reads from apt.report.photos (the
                        same array the health-report sheet already manages)
                        so we never duplicate storage. Surfaced as its own
                        button so shops that disable the report still
                        have a clean path. */}
                    {(ea.status === "done" || ea.status === "grooming") && (() => {
                      const baCount = aptBeforeAfterPhotos(ea).length;
                      return (
                        <button
                          onClick={() => setPhotoShareSheet({ aptId: ea.id })}
                          style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,background: baCount ? "linear-gradient(135deg,#A78BFA,#7C3AED)" : "#F2F2F7",border: baCount ? "none" : "1px solid #E5E5EA",fontSize:12,fontWeight:800,color: baCount ? "#fff" : "#1C1C1E",cursor:"pointer",boxShadow: baCount ? "0 4px 12px rgba(124,58,237,0.3)" : "none"}}
                        >
                          📸 {baCount ? zhEn("管理紀念冊", "Manage album") : zhEn("上載前後照", "Upload before/after photos")}
                        </button>
                      );
                    })()}
                    {ea.status !== "cancelled" && pet && (() => {
                      // 已完成 → 28 日後再嚟（returning customer 行為）
                      // 仍在進行中 → 同日同時段空白 petId（同客戶帶第 2 隻嚟，
                      //   owner 立即開單，唔需要重新揀客戶）
                      const isDone = ea.status === "done";
                      const otherCustPets = (state.users[ea.customerId]?.pets || [])
                        .map(pid => state.pets[pid]).filter(p => p && p.id !== ea.petId);
                      const suggestPetId = isDone ? ea.petId : (otherCustPets[0]?.id || "");
                      const nextDate = (() => {
                        if (!isDone) return ea.date || dateNow();
                        const d = new Date(ea.date || dateNow());
                        d.setDate(d.getDate() + 28);
                        return localDateStr(d);
                      })();
                      const label = isDone ? i18nT("edit.bookAgain") : (otherCustPets.length > 0 ? zhEn("+ 為「" + otherCustPets[0].name + "」加單", "+ Add booking for " + otherCustPets[0].name) : zhEn("+ 加多一個", "+ Add another"));
                      return (
                        <button
                          onClick={() => {
                            // Carry the intended date as `date` (NOT the dead
                            // `_suggestedDate`, which submitWalkIn never read —
                            // so the booking silently landed on TODAY instead of
                            // the same date as the original apt for a 2nd pet, or
                            // the +28d follow-up for a returning customer). The
                            // slot picker then shows free times for that date so
                            // the owner can pick a non-conflicting slot.
                            setWalkInForm({ customerId: ea.customerId, petId: suggestPetId, svcId: isDone ? ea.svcId : "", staffId: ea.staffId, date: nextDate, time: ea.time, addons: [], spaIds: isDone ? (ea.spaIds || []) : [], discountAmount:0, discountType:"percent", discountReason:"", staffRemark:"" });
                            setEditApt(null);
                            toast((isDone ? i18nT("edit.preFilled") : zhEn("已預填客戶資料 · ", "Customer details pre-filled · ")) + nextDate + " " + ea.time);
                          }}
                          style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,background:isDone ? "#F2F2F7" : "#ECFDF5",border:"1px solid " + (isDone ? "#E5E5EA" : "#A7F3D0"),fontSize:12,fontWeight:800,color:isDone ? "#1C1C1E" : "#166534",cursor:"pointer",whiteSpace:"nowrap"}}
                        >
                          <Repeat size={13} strokeWidth={2.5} /> {label}
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => setEditApt(prev => prev ? { ...prev, _mode: "edit" } : prev)}
                      style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:99,background:"#007AFF",border:"none",fontSize:13,fontWeight:900,color:"#fff",cursor:"pointer",boxShadow:"0 3px 8px rgba(0,122,255,0.3)"}}
                    >
                      <Edit size={14} strokeWidth={2.8} /> {i18nT("edit.editBtn")}
                    </button>
                  </div>
                </div>
                {/* Approval-mode banner (view side) — customer's online
                    booking is pending until owner accepts. Shows in the
                    DETAIL view so tapping any apt in the list surfaces
                    the accept/reject buttons without having to hit 編輯
                    first. */}
                {ea.approvalStatus === "pending" && ea.status === "pending" && (
                  <div style={{background:"#FEF3C7",border:"2px solid #FDE68A",borderRadius:16,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12}}>
                    <p style={{flex:1,minWidth:160,fontSize:13,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("⏳ 客戶預約待確認 — 接受後先會生效", "⏳ Customer booking pending — takes effect once you accept")}</p>
                    <Btn size="sm" onClick={() => { approveAppointment(ea); setEditApt(prev => prev ? { ...prev, approvalStatus: "approved" } : prev); }} style={{background:"#10B981",color:"#fff"}}>{zhEn("✓ 接受", "✓ Accept")}</Btn>
                    <Btn variant="ghost" size="sm" onClick={() => { setEditApt(null); rejectAppointment(ea); }} style={{color:"#EF4444",background:"#FEF2F2"}}>{zhEn("✗ 拒絕", "✗ Decline")}</Btn>
                  </div>
                )}
                {/* Status stepper at the top — owner sees current progress
                    immediately on opening. Payment chip on the right when
                    status === 完成. */}
                {!fullSpecial && (
                  <div style={{background:"#FFFDFD",borderRadius:20,padding:"16px 14px",border:"2px solid var(--c-border)",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <div style={{display:"flex",alignItems:"center",gap:14}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",position:"relative",minWidth:280,gap:0,flex:1}}>
                        {STATUS_FLOW.map((s, i) => {
                          // Report step: green when apt.report is saved,
                          // otherwise dim + tappable regardless of grooming
                          // progress. Non-pseudo steps use the linear rule.
                          const done = s.pseudo && s.id === "report"
                            ? reportSubmitted
                            : (stepIdx >= 0 && i <= stepIdx);
                          const active = i === stepIdx;
                          const Ic = s.icon;
                          const goTo = async () => {
                            if (lockStatus) { toast(i18nT("edit.pastLockedToast"), "warning"); return; }
                            // The 報告 pseudo-step doesn't change apt.status
                            // — tapping it just opens the report sheet. If
                            // grooming isn't done yet, warn the owner but
                            // still allow (report + done can be filled in
                            // whichever order suits the workflow).
                            if (s.pseudo && s.id === "report") {
                              if (ea.status !== "done") {
                                toast(zhEn("提示：預約仲未標記「完成」，可以先填報告再標記完成。", "Note: the booking isn't marked \"Done\" yet; you can fill in the report first and mark it done afterwards."), "info");
                              }
                              setReportSheet(ea.id);
                              return;
                            }
                            if (i < stepIdx) {
                              if (!window.confirm(i18nT("edit.statusBackPrompt1") + s.label + i18nT("edit.statusBackPrompt2"))) return;
                              // Extra hard-warn when reverting a paid apt — the
                              // payment log is intentionally preserved (audit
                              // trail) but EOD will keep counting this booking
                              // until the owner manually voids via 取消收款.
                              const collectedNet = Array.isArray(ea.paymentLog)
                                ? ea.paymentLog.reduce((acc, e) => acc + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                                : 0;
                              if (collectedNet > 0) {
                                if (!window.confirm(i18nT("edit.statusBackPaidWarn").replace("{amt}", String(collectedNet)))) return;
                              }
                            }
                            if (s.id === ea.status) return;
                            await updateStatus(ea.id, s.id);
                            setEditApt(prev => ({ ...prev, status: s.id }));
                            if (ea.customerId && s.id !== "done") {
                              try {
                                const petName = pet?.name || i18nT("apt.yourPet");
                                const trigger = s.id === "checkedin" ? "checkin" : s.id === "grooming" ? "grooming_started" : null;
                                if (trigger) autoSendMessage(trigger, { shopName: tenant?.name || "", customerName: cust?.name || "", petName, phone: cust?.phone || "", date: ea.date, time: ea.time, service: ea.service || "" }, state, tenant);
                              } catch (e) { console.warn("autoSendMessage failed:", e); }
                            }
                            if (s.id === "done" && !healthReportEnabled && aptBeforeAfterPhotos(ea).length === 0) {
                              // Only auto-prompt the photo sheet when the
                              // health report is DISABLED. With report on,
                              // the 報告 step gives the owner a dedicated
                              // affordance — no auto-popup.
                              setTimeout(() => setPhotoShareSheet({ aptId: ea.id }), 200);
                            } else if (s.id === "done" && healthReportEnabled) {
                              toast(zhEn("已標記完成 · 點「報告」步驟隨時填寫健康報告", "Marked done · tap the \"Report\" step any time to fill in the health report"), "success");
                            } else {
                              toast(i18nT("edit.statusUpdated1") + s.label + i18nT("edit.statusUpdated2"));
                            }
                          };
                          return (
                            <Fragment key={s.id}>
                              <button onClick={goTo} title={lockStatus ? i18nT("edit.pastLocked") : (i18nT("edit.markAs") + s.label + i18nT("edit.andNotify"))} disabled={lockStatus} style={{background:"transparent",border:"none",cursor: lockStatus ? "not-allowed" : "pointer",padding:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,position:"relative",zIndex:2,opacity: (lockStatus && !done) ? 0.4 : 1}}>
                                <div style={{width:44,height:44,borderRadius:99,background: active ? "#1F2937" : done ? "var(--c-primary)" : "var(--c-lighter)",display:"flex",alignItems:"center",justifyContent:"center",border: active ? "3px solid #1F2937" : "2px solid " + (done ? "var(--c-primary)" : "var(--c-border)"),boxShadow: active ? "0 6px 16px rgba(31,41,55,0.3)" : "none"}}>
                                  <Ic size={18} color={done || active ? "#fff" : "#AFAFC4"} strokeWidth={2.5} />
                                </div>
                                <span style={{fontSize:11,fontWeight:800,color: active ? "#1F2937" : done ? "var(--c-primary)" : "#AFAFC4"}}>{s.label}</span>
                              </button>
                              {i < STATUS_FLOW.length - 1 && (
                                <div style={{flex:1,height:3,margin:"0 -6px",marginTop:-18,background: i < stepIdx ? "var(--c-primary)" : "var(--c-border)",borderRadius:99,position:"relative",zIndex:1}} />
                              )}
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {/* Hero card — pet identity + alerts + main contact actions
                    all in one. First thing the groomer reads after the
                    top-button row. Phone + 📞 + 💬 inline at the right so
                    there's no separate contact strip. */}
                <div style={{display:"flex",alignItems:"center",gap:14,padding:"16px 16px",borderRadius:20,background:"linear-gradient(135deg, #FFE4F0 0%, #FFD1E3 40%, #C8E8FF 100%)",border:"1px solid rgba(255,255,255,0.6)",boxShadow:"0 8px 24px rgba(255,122,162,0.12), 0 2px 4px rgba(0,0,0,0.04)"}}>
                  {/* Pet avatar — when there's a photo, tap opens a full
                       lightbox; the 📷 badge stays as the upload affordance
                       so re-uploading is still one tap. When no photo, tap
                       the avatar itself opens the file picker. */}
                  {(() => {
                    if (!pet) {
                      return (
                        <div style={{width:92,height:92,borderRadius:99,overflow:"hidden",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:48,flexShrink:0,boxShadow:"0 8px 20px rgba(0,0,0,0.10)",border:"3px solid rgba(255,255,255,0.85)"}}>
                          {petEmoji(null)}
                        </div>
                      );
                    }
                    const hasPhoto = !!pet?.photos?.[0]?.url;
                    const photoUrl = pet?.photos?.[0]?.url;
                    const inputId = "pet-avatar-input-" + pet.id;
                    const handleFile = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const { url } = await uploadGroomingMedia({ tenantId: user.tenantId, blob: file, scope: "pet-" + pet.id });
                        const newPhoto = { id: "ph" + uid(), url, addedAt: new Date().toISOString(), category: "avatar" };
                        const nextPhotos = [newPhoto, ...((pet.photos || []).filter(p => p.category !== "avatar"))];
                        setState(s => ({ ...s, pets: { ...s.pets, [pet.id]: { ...s.pets[pet.id], photos: nextPhotos } } }));
                        toast(i18nT("edit.avatarUploaded"));
                      } catch (err) {
                        toast(i18nT("edit.uploadFail") + (err?.message || err), "error");
                      }
                      e.target.value = "";
                    };
                    return (
                      <div style={{position:"relative",flexShrink:0}}>
                        <input id={inputId} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile} />
                        <div
                          onClick={() => {
                            if (hasPhoto) setPhotoLightbox({ url: photoUrl, name: pet.name });
                            else document.getElementById(inputId)?.click();
                          }}
                          title={hasPhoto ? zhEn("點擊放大", "Tap to enlarge") : zhEn("上載頭像", "Upload photo")}
                          style={{width:92,height:92,borderRadius:99,overflow:"hidden",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:48,boxShadow:"0 8px 20px rgba(0,0,0,0.10)",cursor:"pointer",border:"3px solid rgba(255,255,255,0.85)"}}
                        >
                          {hasPhoto ? <img src={photoUrl} alt={pet.name} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : petEmoji(pet)}
                        </div>
                        {/* Zoom hint — top-right only when there's a photo to enlarge. */}
                        {hasPhoto && (
                          <button
                            onClick={() => setPhotoLightbox({ url: photoUrl, name: pet.name })}
                            title={zhEn("放大頭像", "Enlarge photo")}
                            style={{position:"absolute",top:-2,right:-2,width:28,height:28,borderRadius:99,background:"rgba(28,28,30,0.85)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,border:"2px solid #fff",cursor:"pointer",padding:0,boxShadow:"0 4px 10px rgba(0,0,0,0.15)"}}
                          >🔍</button>
                        )}
                        {/* Camera / plus badge — always present so re-uploading
                              is still one tap regardless of photo state. */}
                        <button
                          onClick={() => document.getElementById(inputId)?.click()}
                          title={hasPhoto ? zhEn("更換頭像", "Change photo") : zhEn("上載頭像", "Upload photo")}
                          style={{position:"absolute",bottom:0,right:0,width:28,height:28,borderRadius:99,background:hasPhoto ? "linear-gradient(135deg,#FF6B8B,#FF4D6D)" : "#1C1C1E",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:hasPhoto ? 13 : 14,fontWeight:900,border:"2px solid #fff",cursor:"pointer",padding:0,boxShadow:"0 4px 10px rgba(0,0,0,0.15)"}}
                        >{hasPhoto ? "📷" : "＋"}</button>
                      </div>
                    );
                  })()}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,margin:"0 0 2px",flexWrap:"wrap"}}>
                      <p style={{fontSize:22,fontWeight:900,color:"#1C1C1E",margin:0,lineHeight:1.1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pet?.name || i18nT("common.unnamed")}</p>
                      {pet && (
                        <button onClick={() => setEditPet({ ...pet, weight: pet.weight || "" })} title={i18nT("edit.editPet")} style={{background:"none",border:"none",padding:2,cursor:"pointer",display:"flex",color:"#AFAFC4"}}>
                          <Edit size={14} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,margin:"0 0 4px"}}>
                      <p style={{fontSize:12,color:"#6A6A85",margin:0,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:"0 1 auto",minWidth:0}}>
                        {(pet?.breed || "")}{pet?.weight ? " · " + pet.weight + "kg" : ""}
                        {/* Age — full years from 1+, otherwise show months
                             so newborn puppies don't read as "0 歲". */}
                        {pet?.birthday ? (() => {
                          const m = petAgeMonths(pet.birthday);
                          if (m == null) return "";
                          if (m >= 12) return " · " + Math.floor(m / 12) + zhEn(" 歲", " years");
                          return " · " + m + zhEn(" 個月", m === 1 ? " month" : " months");
                        })() : ""}
                        {cust?.name ? (
                          <>
                            {zhEn(" · 主人 "," · Owner ")}
                            {/* Tap the customer name → open their full CRM
                                profile (not the quick-edit form; the pencil
                                beside it still opens edit). */}
                            <span
                              onClick={(e) => { e.stopPropagation(); setCrmReturn({ tab, aptId: editApt?.id }); setCrmUser(cust.id); setCrmTab("info"); setTab("crm"); setEditApt(null); }}
                              style={{ color: "var(--c-primary)", fontWeight: 900, cursor: "pointer", textDecoration: "underline" }}
                            >{cust.name}</span>
                          </>
                        ) : ""}
                      </p>
                      {cust && (
                        <button onClick={() => { setCrmReturn({ tab, aptId: editApt?.id }); setCrmUser(cust.id); setCrmTab("info"); setTab("crm"); setEditApt(null); }} title={i18nT("edit.editCust")} style={{background:"none",border:"none",padding:2,cursor:"pointer",display:"flex",color:"#AFAFC4",flexShrink:0}}>
                          <Edit size={12} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                    {/* Phone shown in-card so the user doesn't have to tap
                        the WhatsApp / call buttons to see it. tappable in
                        case staff prefer tap-to-call. */}
                    {phoneDisplay && (
                      <a href={fullTel ? "tel:" + fullTel : undefined} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:99,background:"rgba(255,255,255,0.7)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.8)",fontSize:12,fontWeight:800,color:"#1C1C1E",fontVariantNumeric:"tabular-nums",textDecoration:"none",boxShadow:"0 1px 2px rgba(0,0,0,0.04)"}}>
                        📞 {phoneDisplay}
                      </a>
                    )}
                    {/* Customer balances — wallet / points / active packages
                         pulled from state.memberships + state.customerPackages.
                         Shown as 3 chips so the owner sees what's available
                         at a glance before tapping 收款. */}
                    {cust && (() => {
                      const mem = (state.memberships || []).find(m => m.userId === cust.id && m.tenantId === ea.tenantId);
                      const wallet = Number(mem?.wallet || 0);
                      const points = Number(mem?.points || 0);
                      const activePkgs = (tenantCustomerPackages || []).filter(c =>
                        c.customerId === cust.id &&
                        c.enabled !== false &&
                        !c.refundedAt &&
                        Number(c.remainingVisits) > 0 &&
                        (!c.expiresAt || new Date(c.expiresAt).getTime() > Date.now())
                      );
                      const totalVisits = activePkgs.reduce((s, c) => s + Number(c.remainingVisits || 0), 0);
                      const chip = (icon, label, color) => (
                        <span style={{display:"inline-flex",alignItems:"center",gap:3,padding:"3px 7px",borderRadius:99,background:"rgba(255,255,255,0.75)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.8)",fontSize:10,fontWeight:900,color,fontVariantNumeric:"tabular-nums",boxShadow:"0 1px 2px rgba(0,0,0,0.04)",whiteSpace:"nowrap"}}>
                          <span style={{fontSize:11}}>{icon}</span>{label}
                        </span>
                      );
                      const cs = cur(tenant);
                      // Stamp-card count — only when the shop's plan + config
                      // enable the stamp card. Shows 印花 progress (e.g. 3/10).
                      const stampOn = tenant?.stampCard?.enabled && shopHasStampCard(tenant);
                      const stampSlots = Number(tenant?.stampCard?.totalSlots) || 10;
                      const stamps = stampOn ? Math.min(Number(mem?.stamps || 0), stampSlots) : 0;
                      return (
                        <div style={{display:"flex",flexWrap:"nowrap",gap:3,marginTop:4,alignItems:"center",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                          {chip("💰", cs + wallet, wallet > 0 ? "#0369A1" : "#9CA3AF")}
                          {chip("💎", points + "pt", points > 0 ? "#7C3AED" : "#9CA3AF")}
                          {stampOn && chip("🎯", stamps + "/" + stampSlots, stamps > 0 ? "#E11D48" : "#9CA3AF")}
                          {chip("📦", totalVisits > 0 ? (totalVisits + i18nT("apt.pkgVisits") + activePkgs.length + i18nT("apt.pkgRemain")) : i18nT("apt.noPkg"), totalVisits > 0 ? "#059669" : "#9CA3AF")}
                        </div>
                      );
                    })()}
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,alignItems:"center",marginTop:8}}>
                      {pet && (
                        <button
                          onClick={() => {
                            const v = window.prompt(i18nT("edit.addNotePrompt"), "");
                            if (!v) return;
                            const cur = (pet.staffNote || "").trim();
                            const next = cur ? cur + zhEn("、", ", ") + v.trim() : v.trim();
                            setState(s => ({ ...s, pets: { ...s.pets, [pet.id]: { ...s.pets[pet.id], staffNote: next } } }));
                            toast(i18nT("edit.noteAdded"));
                          }}
                          title={i18nT("edit.addNoteTitle")}
                          style={{display:"inline-flex",alignItems:"center",gap:3,padding:"3px 10px",borderRadius:99,background:"rgba(255,255,255,0.7)",border:"1px dashed #EF4444",color:"#EF4444",fontSize:11,fontWeight:800,cursor:"pointer"}}
                        >{i18nT("edit.addNote")}</button>
                      )}
                      {(pet?.staffNote || "").split(/[、,，|\n]+/).map(s => s.trim()).filter(Boolean).slice(0, 5).map((note, i) => {
                        const remove = () => {
                          if (!pet) return;
                          const next = (pet.staffNote || "").split(/[、,，|\n]+/).map(x => x.trim()).filter(Boolean).filter((x, j) => !(x === note && j === i)).join("、");
                          setState(s => ({ ...s, pets: { ...s.pets, [pet.id]: { ...s.pets[pet.id], staffNote: next } } }));
                        };
                        return (
                          <span key={"pn"+i} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 4px 3px 10px",borderRadius:99,background:"#EF4444",color:"#fff",fontSize:11,fontWeight:800}}>
                            {note}
                            <button onClick={remove} title={i18nT("edit.removeNote")} style={{background:"rgba(255,255,255,0.25)",border:"none",borderRadius:99,width:16,height:16,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",padding:0}}><X size={10} color="#fff" strokeWidth={3} /></button>
                          </span>
                        );
                      })}
                      {(cust?.tags || []).filter(t => !["即場","未認領","Walk-in","Unclaimed"].includes(t)).slice(0, 4).map(t => <Badge key={"ct"+t} color="var(--c-primary)" bg="var(--c-light)">{t}</Badge>)}
                      {fullSpecial && <Badge color="#fff" bg={ea.status === "cancelled" ? "#9CA3AF" : "#EF4444"}>{ea.status === "cancelled" ? i18nT("edit.cancelled") : i18nT("edit.noshow")}</Badge>}
                    </div>
                  </div>
                  {fullTel && (
                    <div style={{display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>
                      <a href={"tel:" + fullTel} title={phoneDisplay} style={{width:40,height:40,borderRadius:99,background:"linear-gradient(135deg,#5AC8FA,#007AFF)",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none",boxShadow:"0 4px 10px rgba(0,122,255,0.35)"}}>
                        <Phone size={17} color="#fff" strokeWidth={2.5} />
                      </a>
                      <a href={"https://wa.me/" + waNumber} target="_blank" rel="noopener" title="WhatsApp" style={{width:40,height:40,borderRadius:99,background:"linear-gradient(135deg,#34C759,#25D366)",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none",boxShadow:"0 4px 10px rgba(37,211,102,0.35)"}}>
                        <MessageCircle size={17} color="#fff" strokeWidth={2.5} />
                      </a>
                    </div>
                  )}
                </div>

                {/* Drawer view tabs — 詳情 / 過往紀錄. The 過往 tab swaps
                    the body to a full chronological list; tapping a row
                    opens peekHistoryApt as a small modal so the user can
                    inspect without losing place. */}
                {(() => {
                  if (!pet) return null;
                  const pastCount = state.appointments.filter(a => a.petId === pet.id && a.id !== ea.id && !isAptDead(a)).length;
                  const payCount = (ea.paymentLog || []).length;
                  return (
                    <div style={{display:"flex",gap:4,padding:3,background:"#F2F2F7",borderRadius:99}}>
                      {[["detail",i18nT("apt.detail")],["payments",i18nT("edit.records") + (payCount ? " ("+payCount+")" : "")],["history",i18nT("edit.history") + (pastCount ? " ("+pastCount+")" : "")]].map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setAptDetailTab(k)} style={{flex:1,padding:"7px 10px",borderRadius:99,fontSize:12,fontWeight:800,border:"none",background:aptDetailTab===k?"#fff":"transparent",color:aptDetailTab===k?"#1C1C1E":"#8E8E93",cursor:"pointer",boxShadow:aptDetailTab===k?"0 1px 2px rgba(0,0,0,0.08)":"none"}}>{label}</button>
                      ))}
                    </div>
                  );
                })()}

                {/* Payments tab body */}
                {aptDetailTab === "payments" && (() => {
                  const log = ea.paymentLog || [];
                  const tenantMethodMap = Object.fromEntries((tenant?.depositMethods || []).map(m => [m.id, m.name]));
                  const FALLBACK = { cash:i18nT("apt.cash"), card:i18nT("apt.card"), fps:i18nT("apt.fps"), wallet:i18nT("apt.walletM") };
                  const METHOD_LABEL = { ...FALLBACK, ...tenantMethodMap };
                  return (
                    <div style={{background:"#FAFAFC",borderRadius:20,padding:14,border:"1px solid #E5E5EA",display:"flex",flexDirection:"column",gap:10}}>
                      {log.length === 0 && <p style={{fontSize:13,color:"#AFAFC4",fontWeight:700,textAlign:"center",padding:"16px 0",margin:0}}>{i18nT("edit.no記錄")}</p>}
                      {log.slice().sort((a,b) => String(b?.at || "").localeCompare(String(a?.at || ""))).map((p, i) => {
                        const at = String(p?.at || "").slice(0, 16).replace("T", " ");
                        const byName = p?.by ? (state.users[p.by]?.name || "—") : "—";
                        return (
                          <div key={p?.id || i} style={{padding:12,borderRadius:12,background: p?.type === "refund" ? "#FEF2F2" : "#FFFDFD",border:"1px solid " + (p?.type === "refund" ? "#FECACA" : "var(--c-light)"),display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:14,fontWeight:900,color: p?.type === "refund" ? "#991B1B" : "#1C1C1E",margin:0}}>
                                {p?.type === "refund" && <span style={{background:"#EF4444",color:"#fff",fontSize:10,fontWeight:900,padding:"2px 6px",borderRadius:99,marginRight:6}}>{zhEn("退款", "Refund")}</span>}
                                {METHOD_LABEL[p?.method] || p?.method || i18nT("common.other")}
                              </p>
                              <p style={{fontSize:11,color:"#8A8A9E",margin:"3px 0 0",fontWeight:700}}>{at} · {i18nT("apt.via")}{byName}</p>
                              {p?.note && <p style={{fontSize:11,color:"#6A6A85",margin:"3px 0 0",fontWeight:700}}>{p.note}</p>}
                            </div>
                            <span style={{fontSize:18,fontWeight:900,color: p?.type === "refund" ? "#EF4444" : "#10B981",whiteSpace:"nowrap"}}>
                              {p?.type === "refund" ? "-" : ""}${Number(p?.amount) || 0}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* History tab body — shown only when 過往紀錄 is active. */}
                {aptDetailTab === "history" && (() => {
                  if (!pet) return null;
                  const past = state.appointments
                    .filter(a => a.petId === pet.id && a.id !== ea.id && !isAptDead(a))
                    .sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.time || "").localeCompare(a.time || ""));
                  if (past.length === 0) {
                    return <div style={{background:"#FAFAFC",borderRadius:20,padding:14,border:"1px solid #E5E5EA"}}><p style={{fontSize:13,color:"#8E8E93",fontWeight:700,textAlign:"center",padding:"24px 0",margin:0}}>{i18nT("edit.noOtherHist")}</p></div>;
                  }
                  // Render every row as a small key-value table so date /
                  // time / duration / service / staff / 美容要求 / 內部備註 /
                  // 報告叮嚀 are all visible without an extra tap. The whole
                  // card is still tappable → opens the peek modal for the
                  // full breakdown.
                  return (
                    <div style={{background:"#FAFAFC",borderRadius:20,padding:14,border:"1px solid #E5E5EA",display:"flex",flexDirection:"column",gap:10}}>
                      {past.map(h => {
                        const hStaff = state.users[h.staffId];
                        const hTotal = (Number(h.basePrice) || 0) + (Number(h.addonPrice) || 0) + ((h.surcharges || []).reduce((s, x) => s + (x.amount || 0), 0));
                        const statusPalette = h.status === "done" ? { bg:"#ECFDF5", fg:"#166534" } : h.status === "cancelled" ? { bg:"#FEF2F2", fg:"#991B1B" } : { bg:"#F2F2F7", fg:"#4A4A68" };
                        const dur = getAptDuration(h, tenant);
                        const reportNotes = h.report?.notes || "";
                        const rowStyle = { display:"grid", gridTemplateColumns:"68px 1fr", gap:"4px 10px", fontSize:12, fontWeight:700, color:"#4A4A68", alignItems:"baseline" };
                        const labelStyle = { color:"#8E8E93", fontWeight:800, fontSize:11 };
                        const Row = ({ label, children }) => (
                          <>
                            <span style={labelStyle}>{label}</span>
                            <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{children}</span>
                          </>
                        );
                        // Health-report chip summary — pulled from h.report.
                        // Each category renders as "label: value" so the
                        // groomer can scan abnormals at a glance.
                        const REPORT_CAT = [
                          ["skin",i18nT("ea.report.skin")],["ear",i18nT("ea.report.ear")],["teeth",i18nT("ea.report.teeth")],["mood",i18nT("ea.report.mood")],
                          ["nailTrim",i18nT("lang.name") === "English" ? "Nail trim" : "剪甲"],["analGlands",i18nT("ea.report.analGlands")],["parasites",i18nT("ea.report.parasites")],
                          ["eyeDischarge",i18nT("ea.report.eyeDischarge")],["matting",i18nT("ea.report.matting")],
                        ];
                        const reportChips = h.report
                          ? REPORT_CAT.map(([k, label]) => h.report[k] ? { label, val: h.report[k] } : null).filter(Boolean)
                          : [];
                        const photos = Array.isArray(h.report?.photos) ? h.report.photos : [];
                        return (
                          <button key={h.id} onClick={() => setPeekHistoryApt(h)} style={{padding:"12px 14px",borderRadius:14,border:"1px solid var(--c-light)",background:"#fff",cursor:"pointer",textAlign:"left",display:"flex",flexDirection:"column",gap:8}}>
                            {/* Header strip — status pill + total only. The
                                bold "date / time" line was moved into the
                                table below per UX request. */}
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                              <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:99,background:statusPalette.bg,color:statusPalette.fg}}>{({pending:i18nT("apt.statusPending"),checkedin:i18nT("apt.statusCheckedin"),grooming:i18nT("apt.statusGrooming"),done:i18nT("apt.statusDone"),cancelled:i18nT("apt.statusCancelled"),noshow:i18nT("apt.statusNoshow")})[h.status] || h.status}</span>
                              <span style={{fontSize:15,fontWeight:900,color:"var(--c-primary)"}}>${hTotal}</span>
                            </div>
                            <div style={rowStyle}>
                              <Row label={i18nT("edit.col.date")}>{h.date || "—"}</Row>
                              <Row label={i18nT("edit.col.slot")}>{(() => {
                                if (!h.time) return "—";
                                const [hr, mn] = h.time.split(":").map(Number);
                                const total = hr * 60 + mn + (dur || 0);
                                const end = String(Math.floor((total % (24*60)) / 60)).padStart(2,"0") + ":" + String(total % 60).padStart(2,"0");
                                return `${h.time} – ${end}${dur ? zhEn(` (${dur}分)`, ` (${dur} min)`) : ""}`;
                              })()}</Row>
                              <Row label={i18nT("edit.col.svc")}>{h.service || "—"}</Row>
                              <Row label={i18nT("edit.col.staff")}>{hStaff?.name || i18nT("common.unassigned")}</Row>
                              {h.groomingRequest && <Row label={i18nT("edit.col.groomingReq")}><span style={{color:"#1E40AF"}}>{h.groomingRequest}</span></Row>}
                              {h.staffNote && <Row label={i18nT("edit.col.intNote")}><span style={{color:"#4A4A68"}}>{h.staffNote}</span></Row>}
                              {reportNotes && <Row label={i18nT("edit.col.groomerNote")}><span style={{color:"#166534"}}>{reportNotes}</span></Row>}
                              {h.cancelReason && <Row label={i18nT("edit.col.cancelReason")}><span style={{color:"#991B1B"}}>{h.cancelReason}</span></Row>}
                            </div>
                            {reportChips.length > 0 && (
                              <div style={{paddingTop:8,borderTop:"1px dashed var(--c-light)"}}>
                                <p style={{fontSize:10,fontWeight:800,color:"#8E8E93",margin:"0 0 4px",letterSpacing:0.3}}>{i18nT("edit.healthReport")}</p>
                                <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                  {reportChips.map(({ label, val }) => {
                                    const GOOD = new Set(["正常","良好","無","已修剪","已擠","很少","無發現","不需要"]);
                                    const HIGH = new Set(["發炎","紅腫發炎","建議洗牙","攻擊性","斷裂","發炎堵塞","跳蚤","蜱蟲","過多分泌","嚴重"]);
                                    const palette = GOOD.has(val) ? { bg:"#ECFDF5", fg:"#166534" } : HIGH.has(val) ? { bg:"#FEF2F2", fg:"#991B1B" } : { bg:"#FFFBEB", fg:"#92400E" };
                                    return (
                                      <span key={label} style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,background:palette.bg,color:palette.fg,whiteSpace:"nowrap"}}>{label}: {REPORT_CHIP_LABEL(val)}</span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {photos.length > 0 && (() => {
                              // Group by category so groomers can see the
                              // BEFORE shots side-by-side with AFTER. Older
                              // photos without a category default to 美容後
                              // (the historical assumption).
                              const groups = {
                                before: photos.filter(p => p.category === "before"),
                                after:  photos.filter(p => (p.category || "after") === "after"),
                                spa:    photos.filter(p => p.category === "spa"),
                              };
                              const GROUP_META = [
                                ["before", i18nT("edit.beforeShot"), "#FEF3C7", "#92400E"],
                                ["after",  i18nT("edit.afterShot"), "#DCFCE7", "#166534"],
                                ["spa",    i18nT("edit.spaShot"),   "#DBEAFE", "#1D4ED8"],
                              ];
                              return (
                                <div style={{paddingTop:8,borderTop:"1px dashed var(--c-light)",display:"flex",flexDirection:"column",gap:8}}>
                                  {GROUP_META.map(([k, label, bg, fg]) => {
                                    const arr = groups[k];
                                    if (arr.length === 0) return null;
                                    return (
                                      <div key={k}>
                                        <p style={{fontSize:10,fontWeight:800,color:fg,background:bg,display:"inline-block",padding:"2px 8px",borderRadius:99,margin:"0 0 6px"}}>{label} ({arr.length})</p>
                                        <div style={{display:"flex",gap:6,flexWrap:"nowrap",overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:2}}>
                                          {arr.map((ph, pi) => (
                                            <div
                                              key={ph.id || pi}
                                              role="button"
                                              tabIndex={0}
                                              onClick={(e) => { e.stopPropagation(); setAptLightboxPhoto(ph.url); }}
                                              style={{flexShrink:0,width:64,height:64,borderRadius:8,border:"1px solid var(--c-light)",cursor:"zoom-in",overflow:"hidden"}}
                                              title={i18nT("common.zoomIn")}
                                            >
                                              <img src={ph.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block",pointerEvents:"none"}} />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                {/* Detail tab body — wrapped in a single tinted card so
                    all the inline-editable sections (時間 / 服務 / 備註 /
                    報告) read as one grouped panel rather than a stream of
                    loose cards on the page. */}
                {aptDetailTab === "detail" && <div style={{background:"#FAFAFC",borderRadius:20,padding:14,border:"1px solid #E5E5EA",display:"flex",flexDirection:"column",gap:12}}>
                {/* 上門 service — where the groomer travels to, plus a 尋找路線
                    deep-link. Only shown for home-visit appointments. */}
                {ea.isMobile && (
                  <div style={{background:"#F5F3FF",border:"2px solid #DDD6FE",borderRadius:14,padding:"12px 14px"}}>
                    <p style={{fontSize:12,fontWeight:900,color:"#5B21B6",margin:"0 0 6px"}}>{zhEn("🚗 上門服務", "🚗 Mobile service")}</p>
                    {ea.serviceAddress ? (
                      <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"0 0 8px",lineHeight:1.4}}>📍 {ea.serviceAddress}</p>
                    ) : (
                      <p style={{fontSize:13,fontWeight:700,color:"#9CA3AF",margin:"0 0 8px"}}>{zhEn("未提供地址", "No address provided")}</p>
                    )}
                    {ea.serviceNotes && <p style={{fontSize:12,fontWeight:700,color:"#6A6A85",margin:"0 0 8px"}}>📝 {ea.serviceNotes}</p>}
                    {ea.serviceAddress && <a href={"https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(ea.serviceAddress)} target="_blank" rel="noreferrer" style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:13,fontWeight:800,color:"#fff",background:"#7C3AED",borderRadius:99,padding:"7px 14px",textDecoration:"none"}}>{zhEn("🧭 尋找路線", "🧭 Get directions")}</a>}
                  </div>
                )}
                {/* Header banner removed — 日期 / 時段 are already visible
                    in the 時間 card below, and the 過往紀錄 tab is the only
                    place a non-current booking can show up. */}
                {/* Past-date banner — informational so the owner understands
                    why the stepper is locked. */}
                {isPastDate && !fullSpecial && (
                  <div style={{background:"#F3F4F6",borderRadius:14,padding:"10px 14px",border:"2px solid #E5E7EB",display:"flex",alignItems:"center",gap:8,fontSize:12,fontWeight:700,color:"#4B5563"}}>
                    🔒 {i18nT("edit.lockedPast")}
                  </div>
                )}
                {/* Package redemption error banner — surfaces the
                    trigger's RAISE WARNING so owners know the apt was
                    saved but its package linkage failed (refunded /
                    disabled / expired / race). 點 ✕ 即清，已修正咗
                    嘅可以撳「重試扣套票」用 client redeem RPC. */}
                {ea.packageRedeemError && (
                  <div style={{background:"#FEF2F2",borderRadius:14,padding:"12px 14px",border:"2px solid #FECACA",display:"flex",alignItems:"flex-start",gap:10}}>
                    <span style={{fontSize:18,lineHeight:1}}>⚠️</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#991B1B",margin:"0 0 4px"}}>{i18nT("edit.packageFail")}</p>
                      <p style={{fontSize:12,color:"#7F1D1D",margin:0,fontWeight:700,wordBreak:"break-word"}}>{ea.packageRedeemError}</p>
                      <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                        <button
                          onClick={async () => {
                            if (!ea.customerPackageId) { toast(i18nT("edit.notBoundToPkg"), "error"); return; }
                            try {
                              await redeemCustomerPackage(ea.customerPackageId, { appointmentId: ea.id });
                              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, packageRedeemError: null } : a) }));
                              setEditApt(prev => prev?.id === ea.id ? { ...prev, packageRedeemError: null } : prev);
                              toast(i18nT("edit.retryRedeemOk"));
                            } catch (e) {
                              const msg = e?.message || String(e);
                              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, packageRedeemError: msg } : a) }));
                              setEditApt(prev => prev?.id === ea.id ? { ...prev, packageRedeemError: msg } : prev);
                              toast(i18nT("edit.retryFail") + msg, "error");
                            }
                          }}
                          style={{padding:"4px 10px",borderRadius:99,background:"#DC2626",border:"none",color:"#fff",fontSize:11,fontWeight:900,cursor:"pointer"}}
                        >{i18nT("edit.retryRedeem")}</button>
                        <button
                          onClick={() => {
                            setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, packageRedeemError: null } : a) }));
                            setEditApt(prev => prev?.id === ea.id ? { ...prev, packageRedeemError: null } : prev);
                          }}
                          style={{padding:"4px 10px",borderRadius:99,background:"transparent",border:"1px solid #FECACA",color:"#991B1B",fontSize:11,fontWeight:800,cursor:"pointer"}}
                        >{i18nT("edit.clearAlert")}</button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Inline 再次預約 removed — the same action is in the
                    top-row button strip (with the 報告 / 預約 buttons). */}

                {/* Status stepper moved to the bottom of the tab — see
                    the bottom of this conditional block. */}

                {/* 時間 — inline-editable. Tap any row to change without
                    entering 編輯 mode. Date is a native date input, time is
                    a slot picker (uses generateTimeSlots so already-booked
                    slots grey out), staff is a Sel. All changes persist
                    immediately via setState + the sync layer. */}
                <p style={{fontSize:11,fontWeight:800,color:"#8E8E93",margin:"4px 0 -4px",letterSpacing:0.4,textTransform:"uppercase"}}>{i18nT("edit.time")}</p>
                <div style={{borderRadius:14,border:"1px solid var(--c-light)",overflow:"hidden",background:"#fff"}}>
                  <div style={{display:"flex",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid var(--c-light)",gap:10}}>
                    <span style={{fontSize:13,color:"#8E8E93",fontWeight:700,flex:1}}>📅 {i18nT("edit.date")}</span>
                    <input type="date" value={ea.date} onChange={e => {
                      const v = e.target.value;
                      setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, date: v } : a) }));
                      setEditApt(prev => prev ? { ...prev, date: v } : prev);
                    }} style={{border:"none",fontSize:14,fontWeight:800,color:"#1C1C1E",background:"transparent",textAlign:"right",outline:"none",cursor:"pointer"}} />
                  </div>
                  <div style={{display:"flex",alignItems:"center",padding:"10px 14px",borderBottom:"1px solid var(--c-light)",gap:10}}>
                    <span style={{fontSize:13,color:"#8E8E93",fontWeight:700,flex:1}}>⏰ {i18nT("edit.slot")}</span>
                    {(() => {
                      // Show start - end - duration so the groomer sees
                      // when the chair frees up at a glance. Duration falls
                      // back to the matched service's estimate when no
                      // custom override is set.
                      const dur = getAptDuration(ea, tenant);
                      const end = (() => {
                        if (!ea.time) return "";
                        const [h, m] = ea.time.split(":").map(Number);
                        const total = h * 60 + m + (dur || 0);
                        return String(Math.floor((total % (24*60)) / 60)).padStart(2,"0") + ":" + String(total % 60).padStart(2,"0");
                      })();
                      return (
                        <div style={{display:"flex",alignItems:"center",gap:6,position:"relative"}}>
                          {/* Plain-text 24h display. The native iOS time
                              picker shows 上午/下午 + truncates leading
                              zeros which the owner found confusing. We
                              render text in 24h and overlay an invisible
                              time input on top so tapping still opens the
                              system wheel for editing. */}
                          <span style={{fontSize:14,fontWeight:800,color:"#1C1C1E"}}>{ea.time || "--:--"}{end ? " – " + end : ""}</span>
                          {dur > 0 && <span style={{fontSize:11,color:"#8E8E93",fontWeight:700,padding:"2px 8px",borderRadius:99,background:"#F2F2F7"}}>{dur}{zhEn("分", "pts")}{ea.customDuration ? " ✏️" : ""}</span>}
                          <input
                            type="time"
                            step={900}
                            value={ea.time || ""}
                            onChange={e => {
                              const v = e.target.value;
                              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, time: v } : a) }));
                              setEditApt(prev => prev ? { ...prev, time: v } : prev);
                            }}
                            style={{position:"absolute",inset:0,opacity:0,border:"none",background:"transparent",cursor:"pointer",fontSize:14}}
                            aria-label={i18nT("apt.editStart")}
                          />
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{display:"flex",alignItems:"center",padding:"10px 14px",gap:10}}>
                    <span style={{fontSize:13,color:"#8E8E93",fontWeight:700,flex:1}}>👤 {i18nT("edit.staff")}</span>
                    <select value={ea.staffId || ""} onChange={e => {
                      const v = e.target.value;
                      setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, staffId: v } : a) }));
                      setEditApt(prev => prev ? { ...prev, staffId: v } : prev);
                    }} style={{border:"none",fontSize:14,fontWeight:800,color:"#1C1C1E",background:"transparent",textAlign:"right",outline:"none",cursor:"pointer",appearance:"none",WebkitAppearance:"none"}}>
                      <option value="">{i18nT("common.unassigned")}</option>
                      {(state.users ? Object.values(state.users) : []).filter(u => u && u.tenantId === ea.tenantId && (u.role === "staff" || u.role === "owner") && u.enabled !== false).map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Services breakdown */}
                <p style={{fontSize:11,fontWeight:800,color:"#8E8E93",margin:"4px 0 -4px",letterSpacing:0.4,textTransform:"uppercase"}}>{i18nT("edit.service")}</p>
                {(() => {
                  // Package context — shown as a banner above the breakdown
                  // and used to strike-through the main service price.
                  const usedCp = ea.customerPackageId ? (tenantCustomerPackages || []).find(c => c.id === ea.customerPackageId) : null;
                  const usedPkg = usedCp ? servicePackages.find(p => p.id === usedCp.packageId) : null;
                  const realized = usedCp ? aptPackageRealizedValue(ea, tenantCustomerPackages) : 0;
                  const cashOwed = aptRevenue(ea); // cash-basis (excludes basePrice when usedCp)
                  const cs = cur(tenant);
                  return (
                    <div style={{background:"#fff",borderRadius:18,padding:"16px 18px",border:"1px solid #ECECF0",boxShadow:"0 1px 2px rgba(0,0,0,0.02)"}}>
                      <p style={{display:"none"}}>{zhEn("服務內容", "Service details")}</p>
                      {usedCp && (
                        <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:"#F0F9FF",border:"1px solid #BAE6FD",marginBottom:8}}>
                          <span style={{fontSize:13,fontWeight:900,color:"#0369A1"}}>📦</span>
                          <div style={{flex:1,minWidth:0}}>
                            <p style={{fontSize:12,fontWeight:900,color:"#0369A1",margin:0}}>{usedPkg?.name || zhEn("套票", "Package")} {zhEn("· 抵此次美容", "· applied to this groom")}</p>
                            <p style={{fontSize:10,color:"#0369A1",margin:"2px 0 0",fontWeight:700,opacity:0.8}}>
                              {zhEn("剩 "+Math.max(0, (usedCp.remainingVisits || 0))+" / "+(usedCp.totalVisits || "—")+" 次 · 認列 "+cs+Math.round(realized), Math.max(0, (usedCp.remainingVisits || 0))+" / "+(usedCp.totalVisits || "—")+" left · recognised "+cs+Math.round(realized))}
                              {usedCp.petId && state.pets[usedCp.petId] && <> · 🐾 {state.pets[usedCp.petId].name}</>}
                              {usedCp.petBreedAtPurchase && <> · {usedCp.petBreedAtPurchase}{usedCp.petWeightAtPurchase ? " " + usedCp.petWeightAtPurchase + "kg" : ""}</>}
                            </p>
                          </div>
                        </div>
                      )}
                      {(() => {
                        // Shared right-column for all price lines.
                        // Reserves a fixed-width slot so the column
                        // visually aligns regardless of whether the
                        // amount is editable or static.
                        const PRICE_COL = { display: "flex", justifyContent: "flex-end", alignItems: "baseline", minWidth: 90, flexShrink: 0, gap: 3, fontVariantNumeric: "tabular-nums" };
                        const ROW = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", fontSize: 14, fontWeight: 700 };
                        const PILL_BASE = { padding:"6px 12px", borderRadius:99, border:"1.5px dashed", background:"transparent", fontSize:12, fontWeight:800, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4, transition:"all 0.15s" };
                        return (<>
                          {/* Service price — inline editable. Hero row,
                              slightly larger + bolder. */}
                          <div style={{...ROW, color:"#1C1C1E", fontSize:15, paddingBottom:10}}>
                            <span style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:800}}>
                              {ea.service || i18nT("pay.svcDefault")}
                              {usedCp && <span style={{fontSize:10,fontWeight:800,color:"#0369A1",background:"#E0F2FE",padding:"2px 6px",borderRadius:6,flexShrink:0}}>{zhEn("📦 套票", "📦 Package")}</span>}
                            </span>
                            <span style={PRICE_COL}>
                              <span style={{fontSize:11,color:"#AFAFC4",fontWeight:800,letterSpacing:-0.3}}>{cs}</span>
                              <input
                                type="number" min="0"
                                defaultValue={Number(ea.basePrice) || 0}
                                key={"bp-"+ea.id+"-"+ea.basePrice}
                                onBlur={e => {
                                  const v = Math.max(0, Number(e.target.value) || 0);
                                  if (v === Number(ea.basePrice)) return;
                                  setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, basePrice: v } : a) }));
                                  setEditApt(prev => prev ? { ...prev, basePrice: v } : prev);
                                }}
                                style={{width:56,border:"1.5px solid transparent",borderRadius:8,padding:"2px 6px",fontSize:15,fontWeight:900,outline:"none",color: usedCp ? "#9CA3AF" : "#1C1C1E",background:"transparent",textAlign:"right",textDecoration: usedCp ? "line-through" : "none"}}
                                onFocus={e => { e.target.style.borderColor = "#E5E5EA"; e.target.style.background = "#FAFAFC"; }}
                                onMouseDown={e => { /* allow scroll-to-edit */ }}
                              />
                            </span>
                          </div>
                          {/* SPA / addon lines — static price + ✕ to remove. */}
                          {spaLines.map((l, i) => (
                            <div key={"spa"+i} style={{...ROW, fontSize:13, color:"#6A6A85"}}>
                              <span style={{display:"inline-flex",alignItems:"center",gap:6,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                💆 {l.name}
                                <button onClick={() => {
                                  const nextSpaIds = (ea.spaIds || []).filter(x => x !== l.id);
                                  const tenantAddons = tenant?.addons || [];
                                  const spaSvcs = (tenant?.services || []).filter(sv => sv.kind === "spa");
                                  const sps = nextSpaIds.reduce((s, id) => { const sp = spaSvcs.find(x => x.id === id); if (!sp) return s; const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice }; return s + (Number(pd.total) || 0); }, 0);
                                  const ads = (ea.addons || []).filter(n => !String(n).startsWith("💆")).reduce((s, name) => { const a = tenantAddons.find(x => x.name === name); return s + (a ? computeAddonPrice(a, pet).price : 0); }, 0);
                                  const next = { spaIds: nextSpaIds, addonPrice: sps + ads };
                                  setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, ...next } : a) }));
                                  setEditApt(prev => prev?.id === ea.id ? { ...prev, ...next } : prev);
                                }} title={i18nT("pay.remove")} style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",padding:0,display:"inline-flex"}}><X size={12} strokeWidth={2.5} /></button>
                              </span>
                              <span style={PRICE_COL}>{cs}{l.price}</span>
                            </div>
                          ))}
                          {addonLines.map((l, i) => (
                            <div key={"ad"+i} style={{...ROW, fontSize:13, color:"#6A6A85"}}>
                              <span style={{display:"inline-flex",alignItems:"center",gap:6,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {l.name}
                                <button onClick={() => {
                                  const nextAddons = (ea.addons || []).filter(n => n !== l.name);
                                  const tenantAddons = tenant?.addons || [];
                                  const spaSvcs = (tenant?.services || []).filter(sv => sv.kind === "spa");
                                  const sps = (ea.spaIds || []).reduce((s, id) => { const sp = spaSvcs.find(x => x.id === id); if (!sp) return s; const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice }; return s + (Number(pd.total) || 0); }, 0);
                                  const ads = nextAddons.filter(n => !String(n).startsWith("💆")).reduce((s, name) => { const a = tenantAddons.find(x => x.name === name); return s + (a ? computeAddonPrice(a, pet).price : 0); }, 0);
                                  const next = { addons: nextAddons, addonPrice: sps + ads };
                                  setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, ...next } : a) }));
                                  setEditApt(prev => prev?.id === ea.id ? { ...prev, ...next } : prev);
                                }} title={i18nT("pay.remove")} style={{background:"none",border:"none",cursor:"pointer",color:"#9CA3AF",padding:0,display:"inline-flex"}}><X size={12} strokeWidth={2.5} /></button>
                              </span>
                              <span style={PRICE_COL}>{cs}{l.price}</span>
                            </div>
                          ))}
                          {/* Surcharges — only render FILLED entries
                              (i.e. with name OR amount > 0). Empty
                              rows never persist; "+ 附加費" pill
                              expands a transient draft form below. */}
                          {(ea.surcharges || []).map((s, i) => {
                            const filled = (s.name || "").trim() !== "" || Number(s.amount) > 0;
                            if (!filled) return null;
                            return (
                              <div key={"sur"+i} style={{...ROW, fontSize:13, color:"#92400E"}}>
                                <span style={{display:"inline-flex",alignItems:"center",gap:6,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  + {s.name || i18nT("apt.surcharge")}
                                  <button
                                    onClick={() => {
                                      const next = (ea.surcharges || []).filter((_, j) => j !== i);
                                      setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, surcharges: next } : a) }));
                                      setEditApt(prev => prev ? { ...prev, surcharges: next } : prev);
                                    }}
                                    title={i18nT("pay.remove")}
                                    style={{background:"none",border:"none",cursor:"pointer",color:"#D97706",padding:0,display:"inline-flex"}}
                                  ><X size={12} strokeWidth={2.5} /></button>
                                </span>
                                <span style={PRICE_COL}>{cs}{Number(s.amount) || 0}</span>
                              </div>
                            );
                          })}
                          {/* Surcharge draft form (transient, not persisted until ✓). */}
                          {ea._surDraft && (
                            <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 0",fontSize:13,fontWeight:700,color:"#92400E",background:"#FFFBEB",border:"1.5px solid #FCD34D",borderRadius:8,paddingLeft:8,paddingRight:8}}>
                              <span style={{fontWeight:900,flexShrink:0}}>+</span>
                              <input
                                value={ea._surDraft.name}
                                onChange={e => setEditApt(prev => prev ? { ...prev, _surDraft: { ...prev._surDraft, name: e.target.value } } : prev)}
                                placeholder={i18nT("apt.surchargePh")}
                                autoFocus
                                style={{flex:1,minWidth:0,border:"1.5px solid transparent",borderRadius:6,padding:"4px 6px",fontSize:13,fontWeight:700,outline:"none",color:"#92400E",background:"#fff"}}
                                onFocus={e => e.target.style.borderColor = "#FCD34D"}
                              />
                              <span style={{display:"inline-flex",alignItems:"center",gap:2,padding:"2px 6px",borderRadius:6,border:"1.5px solid #FCD34D",background:"#fff",flexShrink:0}}>
                                <span style={{fontSize:11,fontWeight:800,color:"#92400E"}}>{cs}</span>
                                <input
                                  type="number" min="0"
                                  value={ea._surDraft.amount}
                                  onChange={e => setEditApt(prev => prev ? { ...prev, _surDraft: { ...prev._surDraft, amount: e.target.value } } : prev)}
                                  placeholder="0"
                                  style={{width:50,border:"none",fontSize:13,fontWeight:900,color:"#92400E",outline:"none",background:"transparent",textAlign:"right"}}
                                />
                              </span>
                              <button
                                onClick={() => {
                                  const nm = (ea._surDraft.name || "").trim();
                                  const amt = Math.max(0, Number(ea._surDraft.amount) || 0);
                                  if (!nm || amt <= 0) { toast(zhEn("請輸入名稱及金額", "Enter a name and amount"), "error"); return; }
                                  const next = [...(ea.surcharges || []), { name: nm, amount: amt }];
                                  setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, surcharges: next } : a) }));
                                  setEditApt(prev => prev ? { ...prev, surcharges: next, _surDraft: null } : prev);
                                }}
                                style={{background:"#D97706",border:"none",borderRadius:99,color:"#fff",fontSize:11,fontWeight:900,padding:"4px 10px",cursor:"pointer",flexShrink:0}}
                              >{zhEn("✓ 加入", "✓ Add")}</button>
                              <button
                                onClick={() => setEditApt(prev => prev ? { ...prev, _surDraft: null } : prev)}
                                style={{background:"none",border:"none",cursor:"pointer",color:"#92400E",padding:2,display:"inline-flex"}}
                              ><X size={14} strokeWidth={2.5} /></button>
                            </div>
                          )}
                          {/* Quick-add pills row — SPA + 加購 + 附加費 inline.
                              All three default to collapsed; tap to expand a
                              picker (SPA / 加購) or a transient form (附加費). */}
                          {(() => {
                            const spaSvcs = (tenant?.services || []).filter(sv => sv.kind === "spa" && sv.enabled !== false);
                            const tenantAddons = tenant?.addons || [];
                            const recomputeAddonPrice = (nextSpaIds, nextAddons) => {
                              const sps = (nextSpaIds || []).reduce((s, id) => {
                                const sp = spaSvcs.find(x => x.id === id);
                                if (!sp) return s;
                                const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice };
                                return s + (Number(pd.total) || 0);
                              }, 0);
                              const ads = (nextAddons || []).filter(n => !String(n).startsWith("💆")).reduce((s, name) => {
                                const a = tenantAddons.find(x => x.name === name);
                                return s + (a ? computeAddonPrice(a, pet).price : 0);
                              }, 0);
                              return sps + ads;
                            };
                            const patchApt = (patch) => {
                              setState(st => ({ ...st, appointments: st.appointments.map(x => x.id === ea.id ? { ...x, ...patch } : x) }));
                              setEditApt(prev => prev?.id === ea.id ? { ...prev, ...patch } : prev);
                            };
                            const showSpa = !!ea._showSpaPicker;
                            const showAddon = !!ea._showAddonPicker;
                            const availSpa = spaSvcs.filter(sp => !((ea.spaIds || []).includes(sp.id)));
                            const availAddons = tenantAddons.filter(a => {
                              if (a.enabled === false) return false;
                              const pts = Array.isArray(a.petTypes) && a.petTypes.length ? a.petTypes : [a.petType || "dog"];
                              if (pet?.type && !pts.includes(pet.type)) return false;
                              return !((ea.addons || []).includes(a.name));
                            });
                            return (
                              <>
                                {/* Picker: SPA */}
                                {showSpa && (
                                  <div style={{background:"#F8F8FC",border:"1.5px solid #E5E5EA",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:6,margin:"4px 0"}}>
                                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                      <p style={{margin:0,fontSize:12,fontWeight:900,color:"#4A4A68"}}>{zhEn("💆 加 SPA", "💆 Add SPA")}</p>
                                      <button onClick={() => setEditApt(p => ({...p, _showSpaPicker: false}))} style={{background:"none",border:"none",cursor:"pointer",color:"#8E8E93",padding:0}}><X size={14} strokeWidth={2.5} /></button>
                                    </div>
                                    {availSpa.length === 0 && <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"4px 0"}}>{zhEn("暫無可加 SPA", "No SPA available to add")}</p>}
                                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                      {availSpa.map(sp => {
                                        const pd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice };
                                        return (
                                          <button key={sp.id} onClick={() => {
                                            const nextSpaIds = [...(ea.spaIds || []), sp.id];
                                            patchApt({ spaIds: nextSpaIds, addonPrice: recomputeAddonPrice(nextSpaIds, ea.addons) });
                                          }} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-light)",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>💆 {sp.name} · {cs}{pd.total}</button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {/* Picker: 加購 */}
                                {showAddon && (
                                  <div style={{background:"#F8F8FC",border:"1.5px solid #E5E5EA",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:6,margin:"4px 0"}}>
                                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                      <p style={{margin:0,fontSize:12,fontWeight:900,color:"#4A4A68"}}>{zhEn("✨ 加 加購項目", "✨ Add an add-on")}</p>
                                      <button onClick={() => setEditApt(p => ({...p, _showAddonPicker: false}))} style={{background:"none",border:"none",cursor:"pointer",color:"#8E8E93",padding:0}}><X size={14} strokeWidth={2.5} /></button>
                                    </div>
                                    {availAddons.length === 0 && <p style={{fontSize:11,color:"#AFAFC4",margin:0,fontWeight:700,textAlign:"center",padding:"4px 0"}}>{zhEn("暫無可加項目", "Nothing available to add")}</p>}
                                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                      {availAddons.map(a => {
                                        const ap = computeAddonPrice(a, pet);
                                        return (
                                          <button key={a.id || a.name} onClick={() => {
                                            const nextAddons = [...(ea.addons || []), a.name];
                                            patchApt({ addons: nextAddons, addonPrice: recomputeAddonPrice(ea.spaIds, nextAddons) });
                                          }} style={{padding:"6px 12px",borderRadius:99,border:"1.5px solid var(--c-light)",background:"#fff",color:"#4A4A68",fontSize:12,fontWeight:800,cursor:"pointer"}}>{a.name} · {cs}{ap.price}</button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {/* Unified quick-add pills row — same neutral
                                    border + color so the row reads as one
                                    "+ quick-add" cluster. Action-specific
                                    color is reserved for confirmed line
                                    items in the breakdown. */}
                                {/* Pinned 2x2 grid so 加SPA/加購 sit on row 1,
                                    附加費/折扣 on row 2, all equal width — even
                                    when a button is hidden, the cell stays
                                    reserved so neighbors don't shift around. */}
                                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:8}}>
                                  {!showSpa ? (
                                    <button
                                      onClick={() => setEditApt(p => ({...p, _showSpaPicker: true, _showAddonPicker: false}))}
                                      style={{...PILL_BASE,width:"100%",borderColor:"#E5E5EA",color:"#6A6A85"}}
                                    >{zhEn("💆 加 SPA", "💆 Add SPA")}</button>
                                  ) : <span />}
                                  {!showAddon ? (
                                    <button
                                      onClick={() => setEditApt(p => ({...p, _showAddonPicker: true, _showSpaPicker: false}))}
                                      style={{...PILL_BASE,width:"100%",borderColor:"#E5E5EA",color:"#6A6A85"}}
                                    >{zhEn("✨ 加購", "✨ Add-on")}</button>
                                  ) : <span />}
                                  {!ea._surDraft ? (
                                    <button
                                      onClick={() => setEditApt(prev => prev ? { ...prev, _surDraft: { name: "", amount: "" } } : prev)}
                                      style={{...PILL_BASE,width:"100%",borderColor:"#E5E5EA",color:"#6A6A85"}}
                                    >{zhEn("➕ 附加費", "➕ Surcharge")}</button>
                                  ) : <span />}
                                  {!ea._showDisc && !ea.discount ? (
                                    <button
                                      onClick={() => setEditApt(prev => prev ? { ...prev, _showDisc: true } : prev)}
                                      style={{...PILL_BASE,width:"100%",borderColor:"#E5E5EA",color:"#6A6A85"}}
                                    >{zhEn("🏷️ 折扣", "🏷️ Discount")}</button>
                                  ) : <span />}
                                </div>
                              </>
                            );
                          })()}
                          {/* Discount — readonly badge when set, "+ 折扣"
                              pill otherwise. Click to edit toggles
                              ea._showDisc which renders the inline
                              %/$ editor. */}
                          {(() => {
                            const d = ea.discount || null;
                            const setDiscount = (next) => {
                              setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, discount: next } : a) }));
                              setEditApt(prev => prev ? { ...prev, discount: next } : prev);
                            };
                            const editing = !!ea._showDisc || !!d;
                            // Collapsed state — the "🏷️ 折扣" pill is now
                            // part of the unified pills row above, so the
                            // editor block here renders nothing until
                            // editing kicks in.
                            if (!editing) return null;
                            // Editor + summary row.
                            const isPct = d?.type === "percent";
                            const isFix = d?.type === "fixed";
                            return (
                              <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 0",fontSize:13,fontWeight:700,color:"#D97706",flexWrap:"wrap"}}>
                                <span style={{flex:"0 0 auto"}}>{zhEn("折扣", "Discount")}</span>
                                <div style={{display:"inline-flex",alignItems:"center",gap:2,padding:"2px 6px",borderRadius:6,border:"1.5px solid "+(isPct?"#D97706":"#FCD34D"),background: isPct?"#FEF3C7":"#FFFBEB",cursor:"pointer"}}
                                     onClick={() => !isPct && setDiscount({ type:"percent", amount: (Number(d?.amount)||0), reason: d?.reason||"" })}>
                                  <span style={{fontSize:11,fontWeight:800,color:"#92400E"}}>−%</span>
                                  <input
                                    type="number" min="0" max="100"
                                    defaultValue={isPct ? (Number(d?.amount) || 0) : 0}
                                    key={"dpct-"+ea.id+"-"+(isPct?d.amount:"x")}
                                    onBlur={e => {
                                      const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                      if (v === 0 && isPct) { setDiscount(null); return; }
                                      if (v === 0) return;
                                      setDiscount({ type:"percent", amount: v, reason: d?.reason || "" });
                                    }}
                                    style={{width:36,border:"none",fontSize:13,fontWeight:900,color:"#92400E",outline:"none",background:"transparent",textAlign:"right"}}
                                  />
                                </div>
                                <div style={{display:"inline-flex",alignItems:"center",gap:2,padding:"2px 6px",borderRadius:6,border:"1.5px solid "+(isFix?"#D97706":"#FCD34D"),background: isFix?"#FEF3C7":"#FFFBEB",cursor:"pointer"}}
                                     onClick={() => !isFix && setDiscount({ type:"fixed", amount: (Number(d?.amount)||0), reason: d?.reason||"" })}>
                                  <span style={{fontSize:11,fontWeight:800,color:"#92400E"}}>−{cs}</span>
                                  <input
                                    type="number" min="0"
                                    defaultValue={isFix ? (Number(d?.amount) || 0) : 0}
                                    key={"dfix-"+ea.id+"-"+(isFix?d.amount:"x")}
                                    onBlur={e => {
                                      const v = Math.max(0, Number(e.target.value) || 0);
                                      if (v === 0 && isFix) { setDiscount(null); return; }
                                      if (v === 0) return;
                                      setDiscount({ type:"fixed", amount: v, reason: d?.reason || "" });
                                    }}
                                    style={{width:44,border:"none",fontSize:13,fontWeight:900,color:"#92400E",outline:"none",background:"transparent",textAlign:"right"}}
                                  />
                                </div>
                                <button onClick={() => { setDiscount(null); setEditApt(prev => prev ? { ...prev, _showDisc: false } : prev); }} title={zhEn("移除折扣", "Remove discount")} style={{background:"none",border:"none",cursor:"pointer",color:"#D97706",padding:2,display:"inline-flex"}}><X size={14} strokeWidth={2.5} /></button>
                                <span style={{...PRICE_COL, marginLeft:"auto", fontSize:13, fontWeight:900, color:"#D97706"}}>{viewDisc > 0 ? "−" + cs + viewDisc : "—"}</span>
                              </div>
                            );
                          })()}
                        </>);
                      })()}
                  {/* Voucher line — read-only; the customer redeemed it at
                       booking time via customer_settle_booking RPC. Show
                       it inline so the owner understands why the total
                       dropped at checkout. */}
                  {voucherDisc > 0 && (
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",fontSize:14,fontWeight:700,gap:8}}>
                      <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:99,background:"#ECFDF5",border:"1px solid #86EFAC",color:"#166534",fontSize:12,fontWeight:900}}>{zhEn("🎟️ 兌換券折抵", "🎟️ Voucher applied")}</span>
                      <span style={{display:"flex",justifyContent:"flex-end",alignItems:"baseline",minWidth:90,flexShrink:0,gap:3,fontVariantNumeric:"tabular-nums",marginLeft:"auto",fontSize:13,fontWeight:900,color:"#10B981"}}>−{cs}{voucherDisc}</span>
                    </div>
                  )}
                  <div style={{borderTop:"1px solid #ECECF0",marginTop:12,paddingTop:14,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                    <span style={{fontSize:13,fontWeight:800,color:"#8E8E93",letterSpacing:0.3}}>{usedCp ? zhEn("原價", "List price") : zhEn("總收費", "Total charge")}</span>
                    <span style={{minWidth:110,textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize: usedCp ? 16 : 26,fontWeight:900,color: usedCp ? "#9CA3AF" : "var(--c-primary)", textDecoration: usedCp ? "line-through" : "none",letterSpacing:-0.5}}>
                      <span style={{fontSize: usedCp ? 12 : 14,fontWeight:800,color: usedCp ? "#9CA3AF" : "var(--c-primary)",opacity:0.7,marginRight:2,letterSpacing:-0.3}}>{cs}</span>{total}
                    </span>
                  </div>
                  {usedCp && (
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:6}}>
                      <span style={{fontSize:13,fontWeight:800,color:"#8E8E93",letterSpacing:0.3}}>{zhEn("實收", "Collected")}</span>
                      <span style={{minWidth:110,textAlign:"right",fontVariantNumeric:"tabular-nums",fontSize:26,fontWeight:900,color:"var(--c-primary)",letterSpacing:-0.5}}>
                        <span style={{fontSize:14,fontWeight:800,color:"var(--c-primary)",opacity:0.7,marginRight:2,letterSpacing:-0.3}}>{cs}</span>{cashOwed}
                      </span>
                    </div>
                  )}
                  {ea.deposit > 0 && (() => {
                    const confirmed = !!ea.depositConfirmedAt;
                    const dW = Number(ea.depositPaidWallet) || 0;
                    const dP = Number(ea.depositPaidPoints) || 0;
                    const dV = Number(ea.depositPaidVoucher) || 0;
                    const dCash = Math.max(0, ea.deposit - dW - dP - dV);
                    const bd = [];
                    if (dCash > 0) bd.push(zhEn("現金 $" + dCash, "Cash $" + dCash));
                    if (dW > 0) bd.push(zhEn("錢包 $" + dW, "Wallet $" + dW));
                    if (dP > 0) bd.push(zhEn("積分 $" + dP, "Points $" + dP));
                    if (dV > 0) bd.push(zhEn("券 $" + dV, "Voucher $" + dV));
                    return (
                      <div style={{display:"flex",flexDirection:"column",marginTop:8,padding:"8px 10px",borderRadius:10,background: confirmed ? "#F0FDF4" : "#FFFBEB",border:"1px solid " + (confirmed ? "#BBF7D0" : "#FDE68A"),gap:6}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                            <span style={{fontSize:12,fontWeight:900,color: confirmed ? "#166534" : "#92400E"}}>
                              {confirmed ? i18nT("ea.depositPaid") : i18nT("ea.depositPending")}
                            </span>
                            <span style={{fontSize:12,fontWeight:800,color: confirmed ? "#166534" : "#92400E"}}>${ea.deposit}</span>
                            {confirmed && ea.depositConfirmedAt && (
                              <span style={{fontSize:10,fontWeight:700,color:"#166534",opacity:0.7}}>· {String(ea.depositConfirmedAt).slice(0, 10)}</span>
                            )}
                          </div>
                        {!confirmed ? (
                          <button
                            onClick={() => {
                              const stamp = new Date().toISOString();
                              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, depositConfirmedAt: stamp } : a) }));
                              setEditApt(prev => prev ? { ...prev, depositConfirmedAt: stamp } : prev);
                              toast(i18nT("ea.depositPaid"));
                            }}
                            style={{padding:"5px 12px",borderRadius:99,background:"#10B981",border:"none",color:"#fff",fontSize:11,fontWeight:900,cursor:"pointer",whiteSpace:"nowrap"}}
                          >{i18nT("ea.depositConfirm")}</button>
                        ) : (
                          <button
                            onClick={() => {
                              if (!window.confirm(i18nT("ea.cancelDepositConfirm"))) return;
                              setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, depositConfirmedAt: null } : a) }));
                              setEditApt(prev => prev ? { ...prev, depositConfirmedAt: null } : prev);
                            }}
                            style={{padding:"4px 10px",borderRadius:99,background:"transparent",border:"1px solid #BBF7D0",color:"#166534",fontSize:10,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}
                          >{i18nT("ea.depositUncancel")}</button>
                        )}
                        </div>
                        {bd.length > 0 && (
                          <div style={{fontSize:10,fontWeight:700,color:confirmed?"#166534":"#92400E",opacity:0.85}}>
                            {i18nT("ea.depositCompose") + bd.join(" + ")}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Single rounded payment CTA below 總收費. Opens the
                      PaymentSheet (line items → method → pay) instead of
                      the previous inline 折扣 / 附加費 mini-forms. */}
                  {(() => {
                    // Use the canonical aptRevenue helper so the 收款
                    // button shows the cash-basis amount: when a package
                    // covers basePrice (customerPackageId set), only the
                    // addon + surcharge − discount portion is "due".
                    // Voucher discount + per-package proportional split
                    // are all handled inside aptRevenue.
                    const cashOwed = aptRevenue(ea);
                    // Net cash already in payment_log (payments − refunds).
                    // Computed BEFORE `computedRemain` because the due amount
                    // must subtract it: without that, a booking whose `paid`
                    // flag is stale or false (price edited after a payment,
                    // or a sync race) showed the FULL price on the 收款
                    // button and a second tap charged it all over again —
                    // PAWSIN 2026-06 collected 520+600, 290+280, 570+570,
                    // 940+940 that way, so 已收金額 exceeded 區間總收入.
                    const netCollected = Array.isArray(ea.paymentLog)
                      ? ea.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                      : 0;
                    const computedRemain = Math.max(0, cashOwed
                      - (Number(ea.depositPaidWallet) || 0)
                      - (Number(ea.depositPaidPoints) || 0)
                      - (Number(ea.depositPaidVoucher) || 0)
                      - Math.max(0, netCollected));
                    const dueDisplay = ea.paid ? 0 : computedRemain;
                    // ── Live reconciliation banner ─────────────────────
                    // Recompute expected vs net collected on every render
                    // so the banner reflects ANY change (service swap,
                    // discount edit, refund) instantly. The cached
                    // `paid` / `remainingBalance` may lag the JS state by
                    // a tick — the formula here is the only truth.
                    const expected = cashOwed; // already factors package / voucher / discount
                    const delta = expected - netCollected; // >0: short; <0: over
                    const isShort = delta > 0;
                    const isOver = delta < 0;
                    const isReconciled = expected > 0 && delta === 0;
                    return (
                      <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
                        {(netCollected > 0 || isShort || isOver || isReconciled) && (
                          <div style={{background:isReconciled?"#ECFDF5":(isOver?"#FFFBEB":"#FEF2F2"),border:"1.5px solid "+(isReconciled?"#A7F3D0":(isOver?"#FDE68A":"#FECACA")),borderRadius:12,padding:"10px 12px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8}}>
                              <span style={{fontSize:11,fontWeight:800,color:"#4A4A68"}}>{zhEn("應收", "Amount due")}</span>
                              <span style={{fontSize:14,fontWeight:900,color:"#1F2937"}}>${expected.toLocaleString()}</span>
                            </div>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8,marginTop:2}}>
                              <span style={{fontSize:11,fontWeight:800,color:"#4A4A68"}}>{zhEn("已收", "Received")}</span>
                              <span style={{fontSize:14,fontWeight:900,color:"#10B981"}}>${netCollected.toLocaleString()}</span>
                            </div>
                            <div style={{marginTop:6,paddingTop:6,borderTop:"1px dashed "+(isReconciled?"#A7F3D0":(isOver?"#FDE68A":"#FECACA")),display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                              {isReconciled && <span style={{fontSize:12,fontWeight:900,color:"#047857"}}>{zhEn("✅ 已對齊 · 無需處理", "✅ Reconciled · nothing to do")}</span>}
                              {isShort && <span style={{fontSize:12,fontWeight:900,color:"#B91C1C"}}>{zhEn("⚠️ 仲差 $", "⚠️ Still owed $")}{delta.toLocaleString()}</span>}
                              {isOver && <span style={{fontSize:12,fontWeight:900,color:"#B45309"}}>{zhEn("⚠️ 多收 $", "⚠️ Overpaid $")}{(-delta).toLocaleString()}</span>}
                              {(isShort || isOver) && (
                                <button
                                  disabled={lockStatus}
                                  onClick={async () => {
                                    if (lockStatus) { toast(zhEn("已鎖定", "Locked"), "warning"); return; }
                                    const isRefund = isOver;
                                    const amt = Math.abs(delta);
                                    if (!window.confirm((isRefund ? zhEn(`退款 $${amt}？`, `Refund $${amt}?`) : zhEn(`補收 $${amt}？`, `Collect $${amt} more?`)))) return;
                                    const entry = {
                                      id: (isRefund ? "ref" : "pay") + uid(),
                                      at: new Date().toISOString(),
                                      amount: amt,
                                      method: "manual",
                                      methodName: "店家系統",
                                      type: isRefund ? "refund" : "payment",
                                      note: isRefund ? "對賬退款（多收）" : "對賬補收（仲差）",
                                      by: user.id,
                                    };
                                    let rpcRes = null;
                                    try { rpcRes = await recordPaymentAtomic(ea.id, entry); }
                                    catch (err) { toast(zhEn((isRefund?"退款":"補收")+"失敗：" + (err?.message || err), (isRefund?"Refund":"Collection")+" failed: " + (err?.message || err)), "error"); return; }
                                    const newLog = rpcRes?.log || [...(ea.paymentLog || []), entry];
                                    const newPaid = rpcRes?.paid ?? (!isRefund);
                                    setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, paymentLog: newLog, paid: newPaid, remainingBalance: rpcRes?.total ? Math.max(0, rpcRes.total - (rpcRes.collected || 0)) : 0 } : a) }));
                                    setEditApt(prev => prev?.id === ea.id ? { ...prev, paymentLog: newLog, paid: newPaid } : prev);
                                    toast(isRefund ? zhEn(`已退 $${amt}`, `Refunded $${amt}`) : zhEn(`已補收 $${amt}`, `Collected $${amt}`));
                                  }}
                                  style={{background:isShort?"#10B981":"#F59E0B",color:"#fff",border:"none",borderRadius:99,padding:"5px 14px",fontSize:12,fontWeight:900,cursor:lockStatus?"not-allowed":"pointer",opacity:lockStatus?0.5:1,boxShadow:"0 2px 6px rgba(0,0,0,0.12)"}}
                                >
                                  {isShort ? zhEn(`補收 $${delta}`, `Collect $${delta}`) : zhEn(`退 $${-delta}`, `Refund $${-delta}`)}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        <button
                          onClick={async () => {
                            if (lockStatus) { toast(i18nT("ea.payLockedToast"), "warning"); return; }
                            if (ea.paid) {
                              // Paid → require explicit confirmation before
                              // any reversal. Previously a single tap toggled
                              // straight to refund which was easy to do by
                              // accident (especially on a tap-prone iPad).
                              if (!window.confirm(i18nT("ea.paidFullConfirm"))) return;
                              // Reversal — record a refund entry equal to the
                              // net cash already collected so the till math
                              // stays auditable. Previously the code only
                              // flipped the boolean, leaving the old payment
                              // row in the log. A subsequent 收款 click then
                              // added a SECOND payment row → revenue counted
                              // twice. Now: refund first, re-pay later, net
                              // collected = sum(payments) − sum(refunds).
                              const net = Array.isArray(ea.paymentLog)
                                ? ea.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                                : 0;
                              if (net <= 0) {
                                // Nothing to reverse — just flip the flag.
                                setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, paid: false, remainingBalance: dueDisplay } : a) }));
                                setEditApt(prev => prev?.id === ea.id ? { ...prev, paid: false, remainingBalance: dueDisplay } : prev);
                                toast(i18nT("ea.cancelPayMarkToast"));
                                return;
                              }
                              if (!window.confirm(i18nT("ea.cancelPayConfirm").replace("{net}", String(net)))) return;
                              const refundEntry = {
                                id: "ref" + uid(),
                                at: new Date().toISOString(),
                                amount: net,
                                method: "manual",
                                methodName: "店家系統",
                                type: "refund",
                                note: "取消收款 — 沖銷舊紀錄",
                                by: user.id,
                              };
                              let rpcRes = null;
                              try {
                                rpcRes = await recordPaymentAtomic(ea.id, refundEntry);
                              } catch (e) {
                                toast(i18nT("tx.refundCancelFailed") + zhEn("：", ": ") + (e?.message || e), "error");
                                return;
                              }
                              const newLog = rpcRes?.log || [...(ea.paymentLog || []), refundEntry];
                              setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, paid: false, paymentLog: newLog, remainingBalance: dueDisplay } : a) }));
                              setEditApt(prev => prev?.id === ea.id ? { ...prev, paid: false, paymentLog: newLog, remainingBalance: dueDisplay } : prev);
                              // If any of this payment came from the customer's WALLET, credit
                              // it back. The payment-log refund above is only till/revenue
                              // bookkeeping — the wallet balance is separate and used to stay
                              // debited on undo (the bug). Ledger-backed via creditCustomerWallet.
                              const walletCollected = Array.isArray(ea.paymentLog)
                                ? ea.paymentLog.reduce((s, e) => s + ((String(e?.method) === "wallet") ? ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)) : 0), 0)
                                : 0;
                              if (walletCollected > 0 && ea.customerId) {
                                try {
                                  const refTx = { id: "tx" + uid(), type: "topup", amount: walletCollected, note: "取消收款 · 退回錢包", date: dateNow(), aptId: ea.id };
                                  const cr = await creditCustomerWallet({ userId: ea.customerId, tenantId: user.tenantId, amount: walletCollected, tx: [refTx] });
                                  const nb = typeof cr?.newWallet === "number" ? cr.newWallet : null;
                                  setState(st => ({
                                    ...st,
                                    memberships: (st.memberships || []).map(m => (m.userId === ea.customerId && m.tenantId === user.tenantId) ? { ...m, wallet: nb != null ? nb : (Number(m.wallet) || 0) + walletCollected, walletTx: [...(m.walletTx || []), refTx] } : m),
                                    users: st.users[ea.customerId] ? { ...st.users, [ea.customerId]: { ...st.users[ea.customerId], wallet: nb != null ? nb : (Number(st.users[ea.customerId].wallet) || 0) + walletCollected } } : st.users,
                                  }));
                                  toast(zhEn("已取消收款 · 退回錢包 $" + walletCollected, "Payment cancelled · refunded to wallet $" + walletCollected));
                                  return;
                                } catch (e) { toast(zhEn("錢包退款失敗，請用 ⚖️ 調整手動退回：", "Wallet refund failed — use ⚖️ Adjust to refund manually: ") + (e?.message || e), "warning"); }
                              }
                              toast(zhEn("已取消收款 · 記低 $" + net + " refund", "Payment cancelled · $" + net + " refund noted"));
                              return;
                            }
                            // Nothing left to collect — the log already covers
                            // the bill even though `paid` says otherwise. Post
                            // no entry (a $0 or duplicate one would only skew
                            // the EOD card); tell the owner to reconcile with
                            // the banner above instead.
                            if (dueDisplay <= 0) {
                              toast(zhEn("此單已全數收妥，無需再收款", "This booking is fully paid; nothing more to collect"), "info");
                              return;
                            }
                            // Open the FULL checkout sheet (客戶錢包 + per-method
                            // split) — the same one the Dashboard 收款 button
                            // uses.
                            //
                            // ⚠️ This used to be a one-tap checkout that
                            // hardcoded `method:"manual" / 店家系統` and never
                            // looked at the wallet, on the assumption that
                            // "most pet salons run a separate POS / cash
                            // drawer". That silently broke wallet customers:
                            // an owner topped a customer up $2800, tapped 收款
                            // for a $450 groom here, and the wallet was NEVER
                            // debited (ledger showed the topup with no matching
                            // deduct) — while the very same action from the
                            // Dashboard list DID deduct, because that entry
                            // point already opened CollectPaymentSheet. Two
                            // money paths that disagreed. Now both go through
                            // the sheet, which is ledger-backed
                            // (debitCustomerWallet) and idempotent
                            // (recordPaymentAtomic).
                            //
                            // Shops with POS settle at the till by default, so
                            // the money lands on a sale (receipt, shift, retail
                            // add-ons). 直接收款 below still opens the sheet.
                            if (tenant?.posEnabled) { sendAptToPos(ea.id); setEditApt(null); return; }
                            openCollect(ea.id);
                          }}
                          disabled={lockStatus || ea.paid}
                          style={{width:"100%",padding:"16px 18px",borderRadius:16,border:"none",background: ea.paid ? "linear-gradient(135deg,#10B981,#059669)" : "linear-gradient(135deg,#FF7AA2 0%,#FF5C8A 50%,#F472B6 100%)",color:"#fff",fontSize:15,fontWeight:900,cursor: (lockStatus || ea.paid) ? "default" : "pointer",opacity: lockStatus ? 0.5 : 1,display:"flex",alignItems:"center",justifyContent:"center",gap:10,boxShadow: ea.paid ? "0 8px 20px rgba(16,185,129,0.35)" : "0 10px 24px rgba(255,92,138,0.40)",letterSpacing:0.2,transition:"transform 0.1s"}}
                        >
                          {ea.paid ? <Check size={18} strokeWidth={3} /> : <DollarSign size={18} strokeWidth={2.8} />}
                          {ea.paid ? i18nT("ea.payDoneBtn")
                            : (tenant?.posEnabled && dueDisplay > 0)
                              ? (_ownerLoc !== "zh-HK" ? "Checkout at POS · " + cur(tenant) + dueDisplay : "收銀台結帳 " + cur(tenant) + dueDisplay)
                              : (i18nT("ea.payButton") + cur(tenant) + dueDisplay)}
                        </button>
                        {/* POS shops: the button above goes to the till. Keep
                            the plain 收款 sheet one tap away (POS down, quick
                            wallet charge) without making it the default. */}
                        {tenant?.posEnabled && !ea.paid && !lockStatus && dueDisplay > 0 && (
                          <button onClick={() => openCollect(ea.id)}
                            style={{width:"100%",marginTop:6,padding:"8px 12px",border:"none",background:"transparent",color:"#8A8A9E",fontSize:13,fontWeight:800,cursor:"pointer",textDecoration:"underline"}}>
                            {_ownerLoc !== "zh-HK" ? "Collect without POS" : "直接收款（唔經收銀台）"}
                          </button>
                        )}
                        {ea.paid && !lockStatus && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(i18nT("ea.refundConfirm"))) return;
                              const net = Array.isArray(ea.paymentLog)
                                ? ea.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                                : 0;
                              if (net <= 0) {
                                setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, paid: false, remainingBalance: dueDisplay } : a) }));
                                setEditApt(prev => prev?.id === ea.id ? { ...prev, paid: false, remainingBalance: dueDisplay } : prev);
                                toast(i18nT("ea.cancelPayMarkToast"));
                                return;
                              }
                              const refundEntry = {
                                id: "ref" + uid(), at: new Date().toISOString(),
                                amount: net, method: "manual", methodName: "店家系統",
                                type: "refund", note: "店家手動退款／重開收款", by: user.id,
                              };
                              let rpcRes = null;
                              try { rpcRes = await recordPaymentAtomic(ea.id, refundEntry); }
                              catch (e) { toast(zhEn("退款失敗：", "Refund failed: ") + (e?.message || e), "error"); return; }
                              const newLog = rpcRes?.log || [...(ea.paymentLog || []), refundEntry];
                              setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, paid: false, paymentLog: newLog, remainingBalance: dueDisplay } : a) }));
                              setEditApt(prev => prev?.id === ea.id ? { ...prev, paid: false, paymentLog: newLog, remainingBalance: dueDisplay } : prev);
                              toast(i18nT("ea.refundedToast").replace("{amount}", String(net)));
                            }}
                            style={{width:"100%",padding:"8px 12px",borderRadius:12,border:"1.5px solid #FEE2E2",background:"transparent",color:"#B91C1C",fontSize:12,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:8}}
                          >
                            {i18nT("ea.refundReopen")}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {/* Removed: inline 折扣 / 附加費 mini-forms — discount is
                      now entered inside the PaymentSheet. Surcharges, if
                      needed, can still be added via the 編輯 mode. */}
                  <div style={{display:"none"}}>
                    <button
                      onClick={() => setEditApt(prev => prev ? {
                        ...prev,
                        _quickDisc: prev._quickDisc ? null : {
                          type: prev.discount?.type || "percent",
                          amount: prev.discount?.amount != null ? String(prev.discount.amount) : "",
                          reason: prev.discount?.reason || "",
                        },
                        _quickSur: null,
                      } : prev)}
                      style={{flex:"1 1 140px",padding:"10px 12px",borderRadius:14,border:"1.5px solid #FCD34D",background: ea._quickDisc ? "#FEF3C7" : "#FFFBEB",color:"#92400E",fontSize:13,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}
                    >
                      💰 {ea.discount ? zhEn("編輯折扣", "Edit discount") : zhEn("加折扣", "Add discount")}
                    </button>
                    <button
                      onClick={() => setEditApt(prev => prev ? {
                        ...prev,
                        _quickSur: prev._quickSur ? null : { name: "", amount: "" },
                        _quickDisc: null,
                      } : prev)}
                      style={{flex:"1 1 140px",padding:"10px 12px",borderRadius:14,border:"1.5px solid #FDBA74",background: ea._quickSur ? "#FFEDD5" : "#FFFBEB",color:"#92400E",fontSize:13,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}
                    >
                      {zhEn("➕ 加附加費", "➕ Add surcharge")}
                    </button>
                  </div>
                  {ea._quickDisc && (
                    <div style={{marginTop:10,background:"#FFFBEB",border:"1.5px solid #FCD34D",borderRadius:14,padding:12,display:"flex",flexDirection:"column",gap:8}}>
                      <p style={{margin:0,fontSize:12,fontWeight:900,color:"#92400E"}}>{zhEn("💰 設定折扣", "💰 Set discount")}</p>
                      <div style={{display:"flex",gap:6}}>
                        <button
                          onClick={() => setEditApt(prev => prev ? { ...prev, _quickDisc: { ...prev._quickDisc, type: "percent" } } : prev)}
                          style={{flex:1,padding:"8px",borderRadius:10,border:"1.5px solid " + (ea._quickDisc.type === "percent" ? "#D97706" : "#FDE68A"),background: ea._quickDisc.type === "percent" ? "#D97706" : "#fff",color: ea._quickDisc.type === "percent" ? "#fff" : "#92400E",fontWeight:800,fontSize:12,cursor:"pointer"}}
                        >{zhEn("% 百分比", "% percentage")}</button>
                        <button
                          onClick={() => setEditApt(prev => prev ? { ...prev, _quickDisc: { ...prev._quickDisc, type: "fixed" } } : prev)}
                          style={{flex:1,padding:"8px",borderRadius:10,border:"1.5px solid " + (ea._quickDisc.type === "fixed" ? "#D97706" : "#FDE68A"),background: ea._quickDisc.type === "fixed" ? "#D97706" : "#fff",color: ea._quickDisc.type === "fixed" ? "#fff" : "#92400E",fontWeight:800,fontSize:12,cursor:"pointer"}}
                        >{zhEn("$ 固定金額", "$ fixed amount")}</button>
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={ea._quickDisc.amount}
                        onChange={e => setEditApt(prev => prev ? { ...prev, _quickDisc: { ...prev._quickDisc, amount: e.target.value } } : prev)}
                        placeholder={ea._quickDisc.type === "percent" ? zhEn("例：10", "e.g. 10") : zhEn("例：50", "e.g. 50")}
                        style={{border:"1.5px solid #FDE68A",borderRadius:10,padding:"10px 12px",fontSize:15,fontWeight:800,outline:"none",color:"#92400E",background:"#fff",textAlign:"right"}}
                      />
                      <select
                        value={ea._quickDisc.reason}
                        onChange={e => setEditApt(prev => prev ? { ...prev, _quickDisc: { ...prev._quickDisc, reason: e.target.value } } : prev)}
                        style={{border:"1.5px solid #FDE68A",borderRadius:10,padding:"10px 12px",fontSize:13,fontWeight:700,outline:"none",color:"#92400E",background:"#fff"}}
                      >
                        <option value="">{zhEn("原因（選填）", "Reason (optional)")}</option>
                        <option value="VIP">{zhEn("VIP / 熟客", "VIP / regular")}</option>
                        <option value="補償">{zhEn("補償", "Compensation")}</option>
                        <option value="試做">{zhEn("新客試做", "New-customer trial")}</option>
                        <option value="員工親友">{zhEn("員工親友", "Staff friends & family")}</option>
                        <option value="活動">{zhEn("活動優惠", "Promotion")}</option>
                        <option value="其他">{zhEn("其他", "Other")}</option>
                      </select>
                      <div style={{display:"flex",gap:6}}>
                        <button
                          onClick={() => setEditApt(prev => prev ? { ...prev, _quickDisc: null } : prev)}
                          style={{flex:1,padding:"10px",borderRadius:10,border:"1.5px solid #FDE68A",background:"#fff",color:"#92400E",fontWeight:800,fontSize:13,cursor:"pointer"}}
                        >{zhEn("取消", "Cancel")}</button>
                        <button
                          onClick={() => {
                            const num = parseFloat(String(ea._quickDisc.amount).replace(/[%$\s]/g, ""));
                            if (!Number.isFinite(num) || num < 0) { toast(zhEn("請輸入有效金額", "Enter a valid amount"), "error"); return; }
                            const next = { type: ea._quickDisc.type, amount: num, reason: ea._quickDisc.reason || "" };
                            setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, discount: next } : a) }));
                            setEditApt(prev => prev ? { ...prev, discount: next, _quickDisc: null } : prev);
                            toast(zhEn("折扣已更新", "Discount updated"));
                          }}
                          style={{flex:2,padding:"10px",borderRadius:10,border:"none",background:"#D97706",color:"#fff",fontWeight:900,fontSize:13,cursor:"pointer"}}
                        >{zhEn("儲存折扣", "Save discount")}</button>
                      </div>
                    </div>
                  )}
                  {ea._quickSur && (
                    <div style={{marginTop:10,background:"#FFFBEB",border:"1.5px solid #FDBA74",borderRadius:14,padding:12,display:"flex",flexDirection:"column",gap:8}}>
                      <p style={{margin:0,fontSize:12,fontWeight:900,color:"#92400E"}}>{zhEn("➕ 加附加費", "➕ Add surcharge")}</p>
                      <input
                        value={ea._quickSur.name}
                        onChange={e => setEditApt(prev => prev ? { ...prev, _quickSur: { ...prev._quickSur, name: e.target.value } } : prev)}
                        placeholder={zhEn("名稱（例：打結處理 / 大型犬 / 安撫費）", "Name (e.g. de-matting / large dog / calming fee)")}
                        style={{border:"1.5px solid #FED7AA",borderRadius:10,padding:"10px 12px",fontSize:14,fontWeight:700,outline:"none",color:"#92400E",background:"#fff"}}
                      />
                      <input
                        type="number"
                        inputMode="decimal"
                        value={ea._quickSur.amount}
                        onChange={e => setEditApt(prev => prev ? { ...prev, _quickSur: { ...prev._quickSur, amount: e.target.value } } : prev)}
                        placeholder={zhEn("金額 ($)", "Amount ($)")}
                        style={{border:"1.5px solid #FED7AA",borderRadius:10,padding:"10px 12px",fontSize:15,fontWeight:800,outline:"none",color:"#92400E",background:"#fff",textAlign:"right"}}
                      />
                      <div style={{display:"flex",gap:6}}>
                        <button
                          onClick={() => setEditApt(prev => prev ? { ...prev, _quickSur: null } : prev)}
                          style={{flex:1,padding:"10px",borderRadius:10,border:"1.5px solid #FED7AA",background:"#fff",color:"#92400E",fontWeight:800,fontSize:13,cursor:"pointer"}}
                        >{zhEn("取消", "Cancel")}</button>
                        <button
                          onClick={() => {
                            const name = String(ea._quickSur.name || "").trim();
                            if (!name) { toast(zhEn("請輸入附加費名稱", "Enter a surcharge name"), "error"); return; }
                            const amt = parseFloat(String(ea._quickSur.amount).replace(/[$\s,]/g, ""));
                            if (!Number.isFinite(amt) || amt <= 0) { toast(zhEn("請輸入有效金額", "Enter a valid amount"), "error"); return; }
                            const row = { name, label: name, amount: Math.round(amt) };
                            const next = [...(ea.surcharges || []), row];
                            setState(st => ({ ...st, appointments: st.appointments.map(a => a.id === ea.id ? { ...a, surcharges: next } : a) }));
                            setEditApt(prev => prev ? { ...prev, surcharges: next, _quickSur: null } : prev);
                            toast(zhEn("已加附加費 +$", "Surcharge added +$") + Math.round(amt));
                          }}
                          style={{flex:2,padding:"10px",borderRadius:10,border:"none",background:"#EA580C",color:"#fff",fontWeight:900,fontSize:13,cursor:"pointer"}}
                        >{zhEn("加入附加費", "Add surcharge")}</button>
                      </div>
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* Inline-edit notes. Click into the textarea to edit;
                    onBlur commits to state + sync. No edit-mode round-trip
                    for what's usually a one-line tweak. The customer-side
                    grooming request still shows but is read-only for staff
                    (they can edit it but we keep the blue 客戶-supplied
                    visual cue). */}
                <div style={{background:"#EFF6FF",borderRadius:20,padding:16,border:"2px solid #DBEAFE"}}>
                  <p style={{fontSize:13,fontWeight:900,color:"#1D4ED8",margin:"0 0 6px",display:"flex",alignItems:"center",gap:6}}>
                    <Scissors size={14} color="#1D4ED8" strokeWidth={2.5} /> {zhEn("美容要求 (Grooming Request)", "Grooming request")}
                  </p>
                  <textarea
                    defaultValue={ea.groomingRequest || ""}
                    placeholder={zhEn("客戶想點剪？（直接輸入即可）", "How does the customer want the cut? (just type)")}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if ((ea.groomingRequest || "") === v) return;
                      setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, groomingRequest: v } : a) }));
                      setEditApt(prev => prev ? { ...prev, groomingRequest: v } : prev);
                    }}
                    style={{width:"100%",minHeight:48,border:"1.5px solid transparent",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.6,color:"#1E40AF",background:"rgba(255,255,255,0.55)",boxSizing:"border-box"}}
                    onFocus={(e) => e.target.style.borderColor = "#1D4ED8"}
                  />
                </div>
                <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
                  <p style={{fontSize:12,fontWeight:900,color:"var(--c-primary)",margin:"0 0 6px"}}>{zhEn("📝 內部備註 (員工)", "📝 Internal note (staff)")}</p>
                  <textarea
                    defaultValue={ea.staffNote || ""}
                    placeholder={zhEn("員工內部備註（客戶睇唔到）", "Staff internal note (customers can't see it)")}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if ((ea.staffNote || "") === v) return;
                      setState(s => ({ ...s, appointments: s.appointments.map(a => a.id === ea.id ? { ...a, staffNote: v } : a) }));
                      setEditApt(prev => prev ? { ...prev, staffNote: v } : prev);
                    }}
                    style={{width:"100%",minHeight:48,border:"1.5px solid transparent",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.6,color:"#4A4A68",background:"rgba(255,255,255,0.55)",boxSizing:"border-box"}}
                    onFocus={(e) => e.target.style.borderColor = "var(--c-primary)"}
                  />
                </div>

                {/* Pet-level staff note — persists ACROSS all bookings
                    for this dog (stored on pets.staff_note). Mirrors the
                    field in 編輯 mode so the groomer can adjust the
                    "always-true-about-this-dog" reminders without
                    leaving 詳情. */}
                {pet && (
                  <div style={{background:"#FEF2F2",borderRadius:20,padding:16,border:"2px solid #FECACA"}}>
                    <p style={{fontSize:12,fontWeight:900,color:"#B91C1C",margin:"0 0 6px",display:"flex",alignItems:"center",gap:6}}>{zhEn("🐶 寵物備註 (員工，每次預約共用)", "🐶 Pet note (staff, shared across bookings)")}</p>
                    <textarea
                      defaultValue={pet.staffNote || ""}
                      placeholder={zhEn("例：怕風筒、尾巴要剪短、容易激動", "e.g. Afraid of the dryer, tail trimmed short, gets excited easily")}
                      key={"petnote-" + pet.id + "-" + (pet.staffNote || "")}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if ((pet.staffNote || "") === v) return;
                        setState(s => ({ ...s, pets: { ...s.pets, [pet.id]: { ...s.pets[pet.id], staffNote: v } } }));
                      }}
                      style={{width:"100%",minHeight:48,border:"1.5px solid transparent",borderRadius:10,padding:"8px 10px",fontSize:13,fontWeight:700,outline:"none",resize:"vertical",fontFamily:"inherit",lineHeight:1.6,color:"#7F1D1D",background:"rgba(255,255,255,0.7)",boxSizing:"border-box"}}
                      onFocus={(e) => e.target.style.borderColor = "#B91C1C"}
                    />
                  </div>
                )}

                {ea.cancelReason && (
                  <div style={{background:"#FEE2E2",borderRadius:20,padding:16,border:"2px solid #FECACA"}}>
                    <p style={{fontSize:13,fontWeight:900,color:"#991B1B",margin:"0 0 4px"}}>{zhEn("取消原因", "Cancellation reason")}</p>
                    <p style={{fontSize:13,color:"#991B1B",margin:0,fontWeight:700}}>{ea.cancelReason}</p>
                  </div>
                )}

                {/* Senior-pet waiver — if the customer signed at booking
                    time, show a preview of the signature + a button to
                    open the printable PDF-ready view. Owner uses browser
                    Print → Save as PDF to keep a signed record on file. */}
                {ea.signature && (
                  <div style={{background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:16,padding:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <p style={{fontSize:13,fontWeight:900,color:"#92400E",margin:0,display:"flex",alignItems:"center",gap:6}}>{zhEn("🖊️ 高齡寵物免責聲明 (已簽署)", "🖊️ Senior pet disclaimer (signed)")}</p>
                      <Btn size="sm" onClick={() => printSeniorWaiver(ea, { tenant, customer: state.users[ea.customerId], pet: state.pets[ea.petId] })} style={{background:"#D97706",color:"#fff"}}>{zhEn("📄 開啟 / 列印 PDF", "📄 Open / print PDF")}</Btn>
                    </div>
                    <div style={{background:"#fff",borderRadius:10,padding:8,border:"1.5px solid #FDE68A"}}>
                      <img src={ea.signature} alt="signature" style={{maxWidth:"100%",maxHeight:120,display:"block",margin:"0 auto"}} />
                    </div>
                    <p style={{fontSize:10,color:"#92400E",margin:"6px 0 0",fontWeight:700,textAlign:"center",opacity:0.75}}>{zhEn("客戶已閱讀免責條款並電子簽署", "The customer read the disclaimer and signed electronically")}</p>
                  </div>
                )}

                {/* View-mode cancel / no-show — owner shouldn't have to
                     tap 編輯 first just to mark someone as a no-show.
                     Hidden once the apt is already cancelled / no-show /
                     done so the buttons can't be tapped twice. */}
                {ea.status !== "cancelled" && ea.status !== "noshow" && ea.status !== "done" && (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:4}}>
                    <Btn variant="ghost" style={{background:"#FFFBEB",color:"#D97706",border:"2px solid #FDE68A",fontWeight:900}} onClick={async () => {
                      if (!window.confirm(i18nT("ea.noshowConfirm").replace("{pet}", state.pets[ea.petId]?.name || i18nT("common.petFallback","毛孩")).replace("{date}", ea.date).replace("{time}", ea.time))) return;
                      try {
                        await refundCancellation({ aptId: ea.id, lateFee: Math.round((ea.deposit||0) * ((tenant?.cancellationPolicy?.noShowFeePercent||100)/100)), reason: "店家標記為未到店" });
                      } catch (e) { console.warn("[PettoBook] noshow refund failed:", e); }
                      await updateStatus(ea.id, "noshow");
                      setEditApt(null);
                    }}>{i18nT("ea.markNoShowBtn")}</Btn>
                    <Btn variant="ghost" style={{background:"#FEF2F2",color:"#DC2626",border:"2px solid #FECACA",fontWeight:900}} onClick={async () => {
                      const reason = window.prompt(i18nT("ea.cancelAptPromptTitle"), i18nT("ea.cancelAptPromptDefault"));
                      if (reason === null) return;
                      const isLate = !canCancelFree(ea, tenant);
                      const fee = isLate ? Math.round((ea.deposit||0) * ((tenant?.cancellationPolicy?.lateCancelFeePercent||0)/100)) : 0;
                      const feeLine = fee > 0 ? i18nT("ea.cancelAptFeeLine").replace("{fee}", String(fee)) : i18nT("ea.cancelAptFullRefund");
                      if (!window.confirm(i18nT("ea.cancelAptConfirm").replace("{pet}", state.pets[ea.petId]?.name || i18nT("common.petFallback","毛孩")).replace("{date}", ea.date).replace("{time}", ea.time).replace("{feeLine}", feeLine))) return;
                      try {
                        await refundCancellation({ aptId: ea.id, lateFee: fee, reason: (reason || "").trim() || i18nT("ea.cancelAptPromptDefault") });
                      } catch (e) { console.warn("[PettoBook] cancel refund failed:", e); }
                      await updateStatus(ea.id, "cancelled");
                      setEditApt(null);
                    }}>{i18nT("ea.cancelAptBtn")}</Btn>
                  </div>
                )}
                {/* WOW album card — pinned to the bottom of the detail view so
                    the customer-facing showcase is the final visual beat after
                    every operational button. Mirrors PublicAlbumPage as closely
                    as we can inside a card: 72px logo, 22px shop name, tagline,
                    decorative paws, magazine-feel gallery, full-bleed share CTA. */}
                {(() => {
                  const ba = aptBeforeAfterPhotos(ea);
                  if (ba.length === 0) return null;
                  const themeVars = THEMES[tenant?.theme] || THEMES.peach;
                  const primary = themeVars["--c-primary"] || "#FF758F";
                  const grad1 = themeVars["--c-grad1"] || "#FFB6C1";
                  const grad2 = themeVars["--c-grad2"] || primary;
                  const origin = (typeof window !== "undefined" && window.location.origin) || "";
                  const shareUrl = ea.publicPhotoToken ? (origin + "/album/" + ea.publicPhotoToken) : "";
                  return (
                    <div style={{borderRadius:28, overflow:"hidden", boxShadow:"0 20px 48px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.06)", background:"#fff", marginTop:6, position:"relative"}}>
                      {/* Customer-mirror banner stripe */}
                      <div style={{background:"linear-gradient(90deg,#FEF3C7,#FCE7F3)", padding:"6px 12px", fontSize:11, fontWeight:800, color:"#7C2D12", textAlign:"center", letterSpacing:0.4}}>
                        {zhEn("✨ 客戶分享預覽 · Customer share preview ✨", "✨ Customer share preview ✨")}
                      </div>
                      {/* Hero — same vibe as PublicAlbumPage */}
                      <div style={{background:`linear-gradient(135deg, ${grad1} 0%, ${grad2} 60%, ${primary} 100%)`, padding:"32px 20px 36px", color:"#fff", textAlign:"center", position:"relative", overflow:"hidden"}}>
                        {/* Decorative paws — bigger, more scattered, like a magazine cover */}
                        <span aria-hidden style={{position:"absolute", left:"-10px", top:"8px", fontSize:84, opacity:0.14, transform:"rotate(-22deg)"}}>🐾</span>
                        <span aria-hidden style={{position:"absolute", right:"-12px", top:"-14px", fontSize:90, opacity:0.14, transform:"rotate(20deg)"}}>🐾</span>
                        <span aria-hidden style={{position:"absolute", left:"18%", bottom:"-18px", fontSize:54, opacity:0.12, transform:"rotate(8deg)"}}>🐾</span>
                        <span aria-hidden style={{position:"absolute", right:"22%", bottom:"-10px", fontSize:46, opacity:0.12, transform:"rotate(-14deg)"}}>🐾</span>
                        {tenant?.logoUrl ? (
                          <img src={tenant.logoUrl} alt="" style={{width:72, height:72, borderRadius:22, objectFit:"cover", border:"3px solid #fff", boxShadow:"0 10px 28px rgba(0,0,0,0.28)", marginBottom:12, position:"relative", zIndex:1}} />
                        ) : (
                          <div style={{width:72, height:72, borderRadius:22, background:"#fff", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12, fontSize:34, boxShadow:"0 10px 28px rgba(0,0,0,0.28)", position:"relative", zIndex:1}}>🐾</div>
                        )}
                        <p style={{fontSize:22, fontWeight:900, margin:"4px 0 4px", letterSpacing:0.4, textShadow:"0 2px 10px rgba(0,0,0,0.28)", position:"relative", zIndex:1}}>{tenant?.name || zhEn("美容院", "Grooming salon")}</p>
                        {tenant?.tagline && (
                          <p style={{fontSize:12, fontWeight:600, margin:"0 0 12px", opacity:0.92, position:"relative", zIndex:1, textShadow:"0 1px 4px rgba(0,0,0,0.2)"}}>{tenant.tagline}</p>
                        )}
                        <div style={{marginTop:8, background:"rgba(255,255,255,0.22)", backdropFilter:"blur(10px)", borderRadius:99, padding:"6px 14px", display:"inline-block", border:"1px solid rgba(255,255,255,0.35)", position:"relative", zIndex:1}}>
                          <p style={{fontSize:13, fontWeight:800, margin:0, color:"#fff", textShadow:"0 1px 3px rgba(0,0,0,0.18)"}}>🐾 {pet?.name || zhEn("毛孩", "Pet")} · {ea.service || zhEn("美容", "Grooming")} · {ea.date}</p>
                        </div>
                      </div>
                      {/* Body — heading + gallery */}
                      <div style={{padding:"20px 16px 8px", background:"linear-gradient(180deg, #FFFAF7 0%, #fff 100%)"}}>
                        <div style={{textAlign:"center", marginBottom:14}}>
                          <p style={{fontSize:18, fontWeight:900, color:"#4A4A68", margin:"0 0 4px", letterSpacing:0.3}}>✂️ Before & After</p>
                          <p style={{fontSize:12, color:"#9CA3AF", margin:0, fontWeight:600}}>{zhEn("美容紀念冊 · "+ba.length+" 張靚相", "Grooming album · "+ba.length+" photos")}</p>
                        </div>
                        <BeforeAfterGallery photos={ba} onPhotoTap={(ph) => setPhotoLightbox({ url: ph.url, name: pet?.name })} />
                      </div>
                      {/* Footer — manage + share CTAs */}
                      <div style={{padding:"4px 16px 18px", display:"flex", flexDirection:"column", gap:10}}>
                        {shareUrl && (
                          <button onClick={async () => {
                            try {
                              if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
                                await navigator.share({ title: tenant?.name || "PettoBook", text: (pet?.name || "毛孩") + " 的美容紀念冊 🐾", url: shareUrl });
                              } else {
                                navigator.clipboard?.writeText(shareUrl);
                                toast(zhEn("已複製連結 ✓", "Link copied ✓"));
                              }
                            } catch { /* cancelled */ }
                          }} style={{width:"100%", padding:"14px 18px", background:`linear-gradient(135deg, ${grad1}, ${primary})`, color:"#fff", border:"none", borderRadius:99, fontWeight:900, fontSize:15, cursor:"pointer", boxShadow:`0 10px 28px ${primary}55`, letterSpacing:0.3}}>
                            {zhEn("📲 分享俾", "📲 Share with")} {cust?.name || zhEn("客戶", "Customer")} →
                          </button>
                        )}
                        <button onClick={() => setPhotoShareSheet({ aptId: ea.id })} style={{width:"100%", padding:"10px 14px", background:"#fff", color:"#4A4A68", border:"2px solid #F3F4F6", borderRadius:99, fontWeight:800, fontSize:13, cursor:"pointer"}}>
                          {zhEn("🖼️ 管理相簿", "🖼️ Manage album")}
                        </button>
                      </div>
                    </div>
                  );
                })()}
                </div>}
              </div>
            );
          }

          return <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Back to view button */}
            <div style={{display:"flex",justifyContent:"flex-start",marginTop:-8}}>
              <Btn size="sm" variant="ghost" onClick={() => {
                if (ea._dirty && !window.confirm(i18nT("ea.discardEditsBack"))) return;
                setEditApt(prev => { const { _mode, _dirty, ...rest } = prev; return rest; });
              }}>{i18nT("ea.backToDetail")}</Btn>
            </div>
            {ea._dirty && <div style={{background:"#FFFBEB",borderRadius:14,padding:"8px 14px",border:"2px solid #FEF3C7",display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>✏️</span>
              <p style={{fontSize:12,color:"#92400E",margin:0,fontWeight:800}}>{i18nT("ea.unsavedBanner")}</p>
            </div>}
            {/* Customer card — name, phone, quick contact buttons. Placed
                ABOVE the pet card because the owner usually opens an
                appointment to call/message the customer first. */}
            {cust && (() => {
              const cc = (cust.countryCode || "+852").replace(/[^\d+]/g, "");
              const digits = (cust.phone || "").replace(/\D/g, "");
              const fullTel = digits ? cc + digits : "";
              const waNumber = fullTel.replace(/^\+/, "");
              const phoneDisplay = digits ? (cust.countryCode || "+852") + " " + cust.phone : "";
              return (
                <div style={{background:"#FFFDFD",borderRadius:20,padding:14,border:"2px solid var(--c-border)",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:44,height:44,borderRadius:99,background:"linear-gradient(135deg,var(--c-light),var(--c-grad2))",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:900,color:"var(--c-primary)",flexShrink:0,overflow:"hidden"}}>
                    {cust.avatar ? <img src={cust.avatar} alt={cust.name} style={{width:"100%",height:"100%",objectFit:"cover"}} /> : (cust.name?.[0] || "?")}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontWeight:900,fontSize:15,margin:"0 0 2px",color:"#4A4A68",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cust.name || zhEn("(未命名客戶)", "(unnamed customer)")}</p>
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{phoneDisplay || <span style={{color:"#FF4D6D"}}>{zhEn("未設定電話", "No phone set")}</span>}</p>
                    {cust.email && !cust.email.endsWith("@petos.local") && (
                      <p style={{fontSize:11,color:"#AFAFC4",margin:"2px 0 0",fontWeight:700,wordBreak:"break-all",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cust.email}</p>
                    )}
                  </div>
                  {fullTel && (
                    <>
                      <a href={"tel:" + fullTel} title={zhEn("致電", "Call")} style={{width:36,height:36,borderRadius:99,background:"#DBEAFE",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none",flexShrink:0}}>
                        <Phone size={16} color="#2563EB" strokeWidth={2.5} />
                      </a>
                      <a href={"https://wa.me/" + waNumber} target="_blank" rel="noopener" title="WhatsApp" style={{width:36,height:36,borderRadius:99,background:"#DCFCE7",display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none",flexShrink:0}}>
                        <MessageCircle size={16} color="#16A34A" strokeWidth={2.5} />
                      </a>
                    </>
                  )}
                </div>
              );
            })()}
            <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14,border:"2px solid var(--c-border)",display:"flex",alignItems:"center",gap:12}}>
              <div style={{fontSize:32}}>{petEmoji(pet)}</div>
              <div style={{flex:1}}><p style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#4A4A68"}}>{pet?.name}</p><p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{pet?.breed + " · " + pet?.weight + "kg · " + (pet?.coatLength==="long"?zhEn("長毛", "Long coat"):zhEn("短毛", "Short coat"))}</p></div>
              <button onClick={() => setReweighSheet({ aptId:ea.id, weight:String(pet?.weight||"") })} style={{background:"var(--c-light)",border:"none",borderRadius:99,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:800,color:"var(--c-primary)"}}>{zhEn("⚖️ 重秤", "⚖️ Re-weigh")}</button>
            </div>
            {/* Approval-mode banner — the accept also patches the DRAFT so a
                subsequent 儲存 doesn't write the stale 'pending' back. */}
            {ea.approvalStatus === "pending" && ea.status === "pending" && (
              <div style={{background:"#FEF3C7",border:"2px solid #FDE68A",borderRadius:16,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <p style={{flex:1,minWidth:160,fontSize:13,fontWeight:900,color:"#92400E",margin:0}}>{zhEn("⏳ 客戶預約待確認 — 接受後先會生效", "⏳ Customer booking pending — takes effect once you accept")}</p>
                <Btn size="sm" onClick={() => { approveAppointment(allApts.find(x => x.id === ea.id) || ea); setDraft({ approvalStatus: "approved" }); }} style={{background:"#10B981",color:"#fff"}}>{zhEn("✓ 接受", "✓ Accept")}</Btn>
                <Btn variant="ghost" size="sm" onClick={() => { setEditApt(null); rejectAppointment(allApts.find(x => x.id === ea.id) || ea); }} style={{color:"#EF4444",background:"#FEF2F2"}}>{zhEn("✗ 拒絕", "✗ Decline")}</Btn>
              </div>
            )}
            <Sel label={zhEn("狀態", "Status")} value={ea.status} onChange={e => setDraft({ status:e.target.value })}>
              <option value="pending">{zhEn("待開始", "Not started")}</option><option value="checkedin">{zhEn("已到店", "Arrived")}</option><option value="grooming">{zhEn("美容中", "Grooming")}</option><option value="done">{zhEn("已完成", "Done")}</option><option value="noshow">{zhEn("未到店", "No-show")}</option><option value="cancelled">{zhEn("已取消", "Cancelled")}</option>
            </Sel>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Input label={zhEn("日期", "Date")} type="date" value={ea.date} onChange={e => setDraft({ date:e.target.value })} />
              <Sel label={zhEn("時間", "Time")} value={ea.time} onChange={e => setDraft({ time:e.target.value })}>
                {(() => {
                  const slots = [];
                  for (let h = (timeAxis[0]?parseInt(timeAxis[0]):9); h < (timeAxis.length>0?parseInt(timeAxis[timeAxis.length-1])+1:18); h++) {
                    slots.push(String(h).padStart(2,"0")+":00");
                    slots.push(String(h).padStart(2,"0")+":30");
                  }
                  return slots.map(t => <option key={t} value={t}>{t}</option>);
                })()}
              </Sel>
            </div>
            <Sel label={zhEn("美容師", "Groomer")} value={ea.staffId} onChange={e => setDraft({ staffId:e.target.value })}>
              {staffList.filter(s => s.id === ea.staffId || !seatLocked(staffSeats, s.id)).map(s => <option key={s.id} value={s.id}>{s.name}{seatLocked(staffSeats, s.id) ? " 🔒" : ""}</option>)}
            </Sel>
            {/* Duration override — staff can extend or shorten the appointment regardless of service preset */}
            {(() => {
              const svcDur = (tenant?.services||[]).find(sv => sv.name === ea.service || sv.id === ea.svcId)?.estimatedMinutes || 60;
              const eff = ea.customDuration || svcDur;
              return (
                <div>
                  <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{zhEn("預計時長（分鐘）", "Estimated duration (min)")}{ea.customDuration ? <span style={{color:"var(--c-primary)"}}> {zhEn("· 已自訂", "· customised")}</span> : <span style={{color:"#AFAFC4"}}> {zhEn("· 服務預設", "· service default")} {svcDur} {zhEn("分鐘", "min")}</span>}</p>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                    {[30,45,60,75,90,120,150,180].map(m => {
                      const on = eff === m;
                      const fmt = (x) => (x % 30 === 0 && x >= 60) ? (x/60) + "hr" : x + "min";
                      return <button key={m} onClick={() => setDraft({ customDuration: m === svcDur ? null : m })} style={{padding:"8px 13px",borderRadius:99,border:"none",background:on?"var(--c-primary)":"var(--c-light)",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>{fmt(m)}</button>;
                    })}
                    <input type="number" min="15" step="15" value={ea.customDuration || ""} placeholder={zhEn("自訂 " + svcDur, "Custom " + svcDur)} onChange={e => { const v = parseInt(e.target.value); setDraft({ customDuration: (!v || v === svcDur) ? null : v }); }} style={{width:72,border:"2px solid var(--c-light)",borderRadius:10,padding:"7px 8px",fontSize:13,fontWeight:800,textAlign:"center",outline:"none",color:"#4A4A68",boxSizing:"border-box"}} />
                    {ea.customDuration && <button onClick={() => setDraft({ customDuration: null })} style={{padding:"7px 10px",borderRadius:99,border:"none",background:"#FEF2F2",color:"#DC2626",fontWeight:800,fontSize:12,cursor:"pointer"}}>{zhEn("↺ 還原", "↺ Reset")}</button>}
                  </div>
                </div>
              );
            })()}
            <Sel label={zhEn("服務", "Service")} value={ea.service} onChange={e => {
              const svc = (tenant?.services||[]).filter(s => s.kind !== "spa").find(s => s.name === e.target.value);
              const pd = svc && pet ? getPricingDetail(pet, svc, tenant) : null;
              setDraft({ service:e.target.value, svcId:svc?.id || ea.svcId, basePrice:pd?.total || ea.basePrice, addons:[], spaIds:[], addonPrice:0 });
            }}>
              {(() => {
                // Show only services for THIS pet's species (dog/cat/…), keeping
                // the currently-selected one visible so the dropdown never blanks;
                // fall back to all if nothing matches. Then sort by price low→high.
                const petType = pet?.type || "dog";
                const all = decorateServiceList((tenant?.services||[]).filter(s => s.kind !== "spa"));
                let list = all.filter(s => serviceTypes(s).includes(petType) || s.name === ea.service || s.id === ea.svcId);
                if (!list.length) list = all;
                const priceOf = (s) => { const pd = pet ? getPricingDetail(pet, s, tenant) : null; return Number(pd?.total ?? s.basePrice ?? 0); };
                return [...list].sort((a,b) => priceOf(a) - priceOf(b)).map(s => {
                  const pd = pet ? getPricingDetail(pet, s, tenant) : null;
                  return <option key={s.id} value={s.name}>{s.displayName + " — $" + (pd?.total || s.basePrice) + (pd?.baseLabel ? zhEn("（", " (")+pd.baseLabel.replace("主服務 ","").replace("主服務","")+zhEn("）", ")") : "")}</option>;
                });
              })()}
            </Sel>

            {/* SPA services multi-select */}
            {(tenant?.services||[]).some(s => s.kind === "spa") && <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("SPA 服務（可多選） ✨", "SPA services (multi-select) ✨")}</p>
                <span style={{fontSize:10,color:"#AFAFC4",fontWeight:700}}>{zhEn("價格請至「服務」頁編輯", "Edit prices on the Services page")}</span>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {(tenant?.services||[]).filter(s => s.kind === "spa").map(sp => {
                  const on = (ea.spaIds||[]).includes(sp.id);
                  const sPd = pet ? getPricingDetail(pet, sp, tenant) : { total: sp.basePrice };
                  return <button key={sp.id} onClick={() => {
                    const nextIds = on ? (ea.spaIds||[]).filter(x => x !== sp.id) : [...(ea.spaIds||[]), sp.id];
                    // spaIds is the single source of truth for SPA services
                    // (the detail view + receipts read it directly). Strip
                    // any legacy "💆 …" labels that older appointments may
                    // have written into addons so they don't render twice.
                    const cleanedAddons = (ea.addons || []).filter(n => !String(n).startsWith("💆"));
                    const addonsTotal = cleanedAddons.reduce((s,n) => {
                      const a = tenantAddons.find(x => x.name === n);
                      return s + (a && pet ? computeAddonPrice(a, pet).price : (a?.price || 0));
                    }, 0);
                    const spaTotal = nextIds.reduce((s,sid) => {
                      const sv = (tenant?.services||[]).find(x => x.id === sid && x.kind === "spa");
                      return s + (sv && pet ? getPricingDetail(pet, sv, tenant).total : 0);
                    }, 0);
                    setDraft({ spaIds:nextIds, addons:cleanedAddons, addonPrice: addonsTotal + spaTotal });
                  }} title={sp.description || ""} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-border)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    {on ? "✓ " : "+ "}{sp.name} ${sPd.total}
                  </button>;
                })}
              </div>
            </div>}

            {/* Addons multi-select */}
            {tenantAddons.length > 0 && <div>
              <p style={{fontSize:13,color:"#8A8A9E",marginBottom:8,fontWeight:700}}>{zhEn("加購服務（可多選）", "Add-ons (multi-select)")}</p>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {tenantAddons.map(ad => {
                  const on = (ea.addons||[]).includes(ad.name);
                  const ap = computeAddonPrice(ad, pet);
                  return <button key={ad.id} onClick={() => toggleAddon(ad.name)} style={{padding:"8px 14px",borderRadius:99,border:"2px solid "+(on?"var(--c-primary)":"var(--c-border)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer",transition:"all 0.2s"}}>
                    {on ? "✓ " : "+ "}{ad.name}{ap.variantLabel ? " (" + ap.variantLabel + ")" : ""} ${ap.price}
                  </button>;
                })}
                {(ea.addons||[]).length > 0 && <button onClick={() => setDraft({ addons:[], addonPrice: calcAddonPrice([], ea.spaIds) })} style={{padding:"8px 14px",borderRadius:99,border:"2px solid #FECACA",background:"#FFF1F2",color:"#DC2626",fontWeight:800,fontSize:13,cursor:"pointer"}}>{zhEn("✕ 清空", "✕ Clear")}</button>}
              </div>
            </div>}

            {/* 主服務費 / 訂金 inputs removed — both values are auto-computed
                from the matrix on save. Owner overrides should go through the
                discount or surcharge sections below so they leave an audit
                trail instead of silently mutating the headline price. */}

            {/* Surcharges — owner can add a $-amount or %-of-subtotal extra
                with a reason. List is editable inline; each row leaves an
                audit trail via the saved `surcharges[]` array on the
                appointment (mode + percent preserved for re-rendering). */}
            {(() => {
              const surForm = ea._surForm || { name: "", amount: "", percent: "", mode: "amount", open: false };
              const setSurForm = (next) => setDraft({ _surForm: { ...surForm, ...next } });
              const base = Number(ea.basePrice || 0) + Number(ea.addonPrice || 0);
              const preview = surForm.mode === "percent" ? Math.round(base * (parseFloat(surForm.percent) || 0) / 100) : (parseInt(surForm.amount) || 0);
              const addSurcharge = () => {
                if (!surForm.name?.trim()) return;
                if (preview <= 0) return;
                const row = { name: surForm.name.trim(), amount: preview, mode: surForm.mode, percent: surForm.mode === "percent" ? (parseFloat(surForm.percent) || 0) : null };
                setDraft({ surcharges: [...(ea.surcharges || []), row], _surForm: { name: "", amount: "", percent: "", mode: surForm.mode, open: false } });
              };
              return (
                <div style={{background:"#FFFBEB",borderRadius:20,padding:14,border:"2px solid #FEF3C7"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <p style={{fontSize:13,fontWeight:800,color:"#92400E",margin:0}}>➕ {i18nT("apt.surcharge")}</p>
                    {!surForm.open && <button onClick={() => setSurForm({ open: true })} style={{background:"#FDE68A",border:"none",borderRadius:99,padding:"4px 12px",fontSize:12,fontWeight:800,color:"#92400E",cursor:"pointer"}}>{zhEn("+ 加收費用", "+ Add charge")}</button>}
                  </div>
                  {(ea.surcharges || []).map((s, i) => (
                    <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom: i < (ea.surcharges.length - 1) ? "1px dashed #FEF3C7" : "none"}}>
                      <span style={{fontSize:13,fontWeight:700,color:"#92400E"}}>{s.name}{s.mode === "percent" && s.percent ? zhEn("（", " (") + s.percent + zhEn("%）", "%)") : ""}</span>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:14,fontWeight:900,color:"#D97706"}}>+${s.amount}</span>
                        <button onClick={() => setDraft({ surcharges: (ea.surcharges || []).filter((_, j) => j !== i) })} style={{background:"none",border:"none",cursor:"pointer",padding:2}}><X size={14} color="#D97706" /></button>
                      </div>
                    </div>
                  ))}
                  {surForm.open && (
                    <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
                      <div style={{display:"flex",gap:8}}>
                        <input placeholder={zhEn("原因（如：嚴重打結、超大體型）", "Reason (e.g. severe matting, extra-large size)")} value={surForm.name} onChange={e => setSurForm({ name: e.target.value })} style={{flex:1,border:"2px solid #FDE68A",borderRadius:12,padding:"8px 12px",fontSize:13,fontWeight:700,outline:"none",color:"#92400E"}} />
                        <div style={{display:"flex",border:"2px solid #FDE68A",borderRadius:12,overflow:"hidden"}}>
                          <button onClick={() => setSurForm({ mode: "amount" })} style={{padding:"6px 12px",fontSize:13,fontWeight:900,border:"none",cursor:"pointer",background:surForm.mode==="amount"?"#F59E0B":"transparent",color:surForm.mode==="amount"?"#fff":"#92400E"}}>$</button>
                          <button onClick={() => setSurForm({ mode: "percent" })} style={{padding:"6px 12px",fontSize:13,fontWeight:900,border:"none",cursor:"pointer",background:surForm.mode==="percent"?"#F59E0B":"transparent",color:surForm.mode==="percent"?"#fff":"#92400E"}}>%</button>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {surForm.mode === "amount" ? (
                          <input type="number" placeholder={zhEn("例：100", "e.g. 100")} value={surForm.amount} onChange={e => setSurForm({ amount: e.target.value })} style={{flex:1,border:"2px solid #FDE68A",borderRadius:12,padding:"8px 10px",fontSize:13,fontWeight:800,outline:"none",textAlign:"center",color:"#92400E"}} />
                        ) : (
                          <>
                            <input type="number" placeholder={zhEn("例：15", "e.g. 15")} value={surForm.percent} onChange={e => setSurForm({ percent: e.target.value })} style={{flex:1,border:"2px solid #FDE68A",borderRadius:12,padding:"8px 10px",fontSize:13,fontWeight:800,outline:"none",textAlign:"center",color:"#92400E"}} />
                            <span style={{fontSize:11,fontWeight:800,color:"#92400E",whiteSpace:"nowrap"}}>≈ ${preview}</span>
                          </>
                        )}
                        <Btn size="sm" onClick={addSurcharge} style={{background:"#F59E0B"}}><Check size={14} /></Btn>
                        <button onClick={() => setSurForm({ open: false, name: "", amount: "", percent: "" })} style={{background:"none",border:"none",cursor:"pointer",padding:6,fontSize:12,fontWeight:800,color:"#92400E"}}>{zhEn("取消", "Cancel")}</button>
                      </div>
                    </div>
                  )}
                  {surTotal > 0 && <p style={{fontSize:13,fontWeight:900,color:"#D97706",margin:"8px 0 0",textAlign:"right"}}>{zhEn("附加小計：+$"+surTotal, "Surcharges: +$"+surTotal)}</p>}
                </div>
              );
            })()}

            {/* Discount */}
            <div style={{background:"#FFFBEB",borderRadius:20,padding:14,border:"2px solid #FEF3C7"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <p style={{fontSize:13,fontWeight:800,color:"#92400E",margin:0}}>💰 {i18nT("apt.discount")}</p>
                {discValue > 0 && <span style={{fontSize:13,fontWeight:900,color:"#D97706"}}>{"−$" + discValue}</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"100px 1fr",gap:8,marginBottom:8}}>
                <select value={disc.type} onChange={e => setDiscount({ type:e.target.value })} style={{border:"2px solid #FEF3C7",borderRadius:12,padding:"8px",fontSize:13,fontWeight:800,outline:"none",background:"#fff",color:"#4A4A68"}}>
                  <option value="percent">{zhEn("百分比 %", "Percentage %")}</option>
                  <option value="fixed">{zhEn("固定 $", "Fixed $")}</option>
                </select>
                <input type="number" min="0" placeholder="0" value={disc.amount||""} onChange={e => setDiscount({ amount:parseInt(e.target.value)||0 })} style={{border:"2px solid #FEF3C7",borderRadius:12,padding:"8px 12px",fontSize:14,fontWeight:800,outline:"none",width:"100%",boxSizing:"border-box",color:"#4A4A68"}} />
              </div>
              <select value={disc.reason||""} onChange={e => setDiscount({ reason:e.target.value })} style={{width:"100%",border:"2px solid #FEF3C7",borderRadius:12,padding:"8px 12px",fontSize:13,fontWeight:700,outline:"none",background:"#fff",color:"#4A4A68"}}>
                <option value="">{zhEn("選擇折扣原因…", "Choose discount reason…")}</option>
                <option value="VIP老客">{zhEn("VIP 老客", "VIP regular")}</option>
                <option value="朋友介紹">{zhEn("朋友介紹", "Referral")}</option>
                <option value="補償">{zhEn("補償（上次體驗欠佳）", "Compensation (poor last experience)")}</option>
                <option value="試做">{zhEn("新客試做", "New-customer trial")}</option>
                <option value="員工親友">{zhEn("員工親友", "Staff friends & family")}</option>
                <option value="活動">{zhEn("活動優惠", "Promotion")}</option>
                <option value="其他">{zhEn("其他", "Other")}</option>
              </select>
            </div>

            <div style={{background:"var(--c-lighter)",borderRadius:16,padding:"12px 16px",border:"2px solid var(--c-border)"}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6A6A85",fontWeight:700}}><span>{zhEn("主服務 + 加購" + (surTotal>0?" + 附加":""), "Main service + add-ons" + (surTotal>0?" + surcharges":""))}</span><span>{"$" + subtotal}</span></div>
              {discValue > 0 && <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#D97706",fontWeight:800,marginTop:2}}><span>{zhEn("折扣", "Discount") + (disc.reason?zhEn("（", " (")+disc.reason+zhEn("）", ")"):"")}</span><span>{"−$" + discValue}</span></div>}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:900,marginTop:6,paddingTop:6,borderTop:"1px dashed var(--c-border)"}}><span>{zhEn("總計", "Total")}</span><span style={{color:"var(--c-primary)"}}>{"$" + total}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#8A8A9E",fontWeight:700,marginTop:4}}><span>{zhEn("尾款", "Balance due")}</span><span style={{color:"#D97706"}}>{"$" + Math.max(0, total - ea.deposit)}</span></div>
            </div>
            {/* Pet-level staff note — persists ACROSS bookings for this dog.
                Stored on pets.staff_note. Use for traits the groomer always
                wants to remember (e.g. "尾巴要剪短", "怕風筒"). */}
            {pet && (
              <div>
                <p style={{fontSize:12,color:"#8A8A9E",marginBottom:4,fontWeight:700}}>{zhEn("🐶 寵物備註（員工，每次預約共用）", "🐶 Pet note (staff, shared across bookings)")}</p>
                <textarea
                  style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:16,padding:"10px 14px",fontSize:14,fontWeight:600,height:60,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68",background:"#FFFDFD"}}
                  placeholder={zhEn("例：怕風筒、尾巴要剪短、容易激動", "e.g. Afraid of the dryer, tail trimmed short, gets excited easily")}
                  value={ea._petStaffNoteDraft != null ? ea._petStaffNoteDraft : (pet?.staffNote || "")}
                  onChange={e => setDraft({ _petStaffNoteDraft: e.target.value })}
                />
              </div>
            )}
            {/* Customer-supplied grooming request (this visit). Editable here
                so staff can correct typos / clarify what the customer wrote. */}
            <div>
              <p style={{fontSize:12,color:"#1D4ED8",marginBottom:4,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>{zhEn("✂️ 美容要求（客戶填寫，可選）", "✂️ Grooming request (filled in by customer, optional)")}</p>
              <textarea
                style={{width:"100%",border:"2px solid #DBEAFE",borderRadius:16,padding:"10px 14px",fontSize:14,fontWeight:600,height:60,resize:"none",boxSizing:"border-box",outline:"none",color:"#1E40AF",background:"#EFF6FF"}}
                placeholder={zhEn("例：盡量剪短，腳部留少少毛", "e.g. Trim as short as possible, leave a little fur on the legs")}
                value={ea.groomingRequest||""}
                onChange={e => setDraft({ groomingRequest:e.target.value })}
              />
            </div>
            {(ea.priceAdjustLog||[]).length > 0 && <details style={{background:"#FFFDFD",borderRadius:16,padding:"10px 14px",border:"2px solid var(--c-light)"}}>
              <summary style={{fontSize:12,fontWeight:800,color:"#8A8A9E",cursor:"pointer"}}>{zhEn("📋 調價紀錄 (" + ea.priceAdjustLog.length + ")", "📋 Price adjustments (" + ea.priceAdjustLog.length + ")")}</summary>
              <div style={{marginTop:8,maxHeight:160,overflowY:"auto"}}>
                {ea.priceAdjustLog.slice().reverse().map((log,i) => (
                  <div key={i} style={{fontSize:11,color:"#6A6A85",fontWeight:700,padding:"4px 0",borderBottom:"1px dashed var(--c-light)"}}>
                    <span style={{color:"var(--c-primary)"}}>{log.at}</span> · {(state.users[log.by]?.name||log.by)} · {log.field} {log.field === "discount" ? zhEn("已更新", "Updated") : (log.from + " → " + log.to)} {log.reason ? "("+log.reason+")" : ""}
                  </div>
                ))}
              </div>
            </details>}

            {/* 內部備註 — appointment-level staff note. Moved to the bottom
                (just above the save bar) so the more frequently-used fields
                (date/time/service/price) stay above the fold. */}
            <div>
              <p style={{fontSize:12,color:"#8A8A9E",marginBottom:4,fontWeight:700}}>📝 {i18nT("edit.note")}</p>
              <textarea
                style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:16,padding:"10px 14px",fontSize:14,fontWeight:600,height:60,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68",background:"#FFFDFD"}}
                placeholder={zhEn("例：今日要趕時間、客戶會晚到 15 分鐘", "e.g. In a rush today, customer will be 15 minutes late")}
                value={ea.staffNote||""}
                onChange={e => setDraft({ staffNote:e.target.value })}
              />
            </div>

            {/* Owner-side cancel / no-show. Uses the same refundCancellation
                 RPC as the customer flow so points/wallet/voucher/stamp/
                 package all roll back through one canonical path. The
                 cancel/noshow trigger on appointments fires the existing
                 apt_revoke_on_cancel to undo stamp/points awards. */}
            {(() => {
              // Compute net cash collected via payment_log so cancel
              // flows can refund it explicitly. The deposit-only path
              // (refundCancellation RPC) covers wallet/points/voucher
              // only — cash payments need a refund entry of their own.
              const netCash = Array.isArray(ea.paymentLog)
                ? ea.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                : 0;
              // Unified cancel handler:
              //  * Asks the owner what to do with collected cash (refund all
              //    / late fee / keep as no-show fee)
              //  * Records a matching refund entry so payment_log audit
              //    trail stays intact (no "money disappears" bug)
              //  * Calls refundCancellation for the deposit portion
              //  * Then flips status. Trigger keeps paid/remaining sane.
              const handleCancelOrNoShow = async (mode /* 'cancel'|'noshow' */) => {
                const targetStatus = mode === "noshow" ? "noshow" : "cancelled";
                let cashRefund = netCash;
                if (netCash > 0) {
                  const choice = window.prompt(
                    zhEn(`此預約已收 $${netCash}，請選擇處理方式：\n\n  1. 全額退款 — 記低 $${netCash} refund\n  2. 全部留為手續費（no-show 政策）\n  3. 部分退款 — 輸入要退嘅金額\n\n輸入 1 / 2 / 數字（0 至 ${netCash}）：`, `$${netCash} has been collected for this booking. Choose how to handle it:\n\n  1. Full refund — note $${netCash} refund\n  2. Keep all as a fee (no-show policy)\n  3. Partial refund — enter the amount to refund\n\nEnter 1 / 2 / a number (0 to ${netCash}):`),
                    "1"
                  );
                  if (choice === null) return;
                  const c = String(choice).trim();
                  if (c === "1") cashRefund = netCash;
                  else if (c === "2") cashRefund = 0;
                  else {
                    const n = parseFloat(c);
                    if (isNaN(n) || n < 0 || n > netCash) { toast(zhEn("輸入嘅金額無效", "The amount entered is invalid"), "error"); return; }
                    cashRefund = Math.round(n);
                  }
                }
                const finalReason = mode === "noshow" ? "店家標記為未到店" : (window.prompt(zhEn("取消原因（會通知客戶）：","Cancellation reason (the customer will be notified):"), "店家取消") ?? null);
                if (mode === "cancel" && finalReason === null) return;
                const summary = (state.pets[ea.petId]?.name || zhEn("毛孩", "Pet")) + " · " + ea.date + " " + ea.time;
                const moneyLine = netCash > 0
                  ? (cashRefund > 0 ? zhEn(`\n• 退款 $${cashRefund}（payment log 加 refund entry）`, `\n• Refund $${cashRefund} (adds a refund entry to the payment log)`) + (netCash - cashRefund > 0 ? zhEn(`\n• 留 $${netCash - cashRefund} 作手續費`, `\n• Keep $${netCash - cashRefund} as a fee`) : "") : zhEn(`\n• 全部 $${netCash} 留作手續費`, `\n• Keep all $${netCash} as a fee`))
                  : "";
                if (!window.confirm(zhEn(`確認${mode === "noshow" ? "標記未到店" : "取消預約"}？\n\n${summary}${moneyLine}\n\n• 套票次數退還\n• 印花 / 積分撤銷\n• 訂金按店鋪政策處理`, `Confirm ${mode === "noshow" ? "marking as no-show" : "cancelling the booking"}?\n\n${summary}${moneyLine}\n\n• Package visits are returned\n• Stamps / points are reversed\n• The deposit is handled per shop policy`))) return;
                // Step 1: refund cash via payment_log so reports stay honest
                if (cashRefund > 0) {
                  try {
                    await recordPaymentAtomic(ea.id, {
                      id: "ref" + uid(),
                      at: new Date().toISOString(),
                      amount: cashRefund,
                      method: "manual",
                      methodName: "店家系統",
                      type: "refund",
                      note: (mode === "noshow" ? "未到店退款" : "取消退款") + (cashRefund < netCash ? `（扣 $${netCash - cashRefund}）` : ""),
                      by: user.id,
                    });
                  } catch (e) { toast(zhEn("退款記錄失敗：", "Failed to record refund: ") + (e?.message || e), "error"); return; }
                }
                // Step 2: deposit-side refund (wallet/points/voucher)
                const depFee = mode === "noshow"
                  ? Math.round((ea.deposit || 0) * ((tenant?.cancellationPolicy?.noShowFeePercent || 100) / 100))
                  : (canCancelFree(ea, tenant) ? 0 : Math.round((ea.deposit || 0) * ((tenant?.cancellationPolicy?.lateCancelFeePercent || 0) / 100)));
                try {
                  await refundCancellation({ aptId: ea.id, lateFee: depFee, reason: (finalReason || "").trim() || (mode === "noshow" ? "店家標記為未到店" : "店家取消") });
                } catch (e) { console.warn("[PettoBook] deposit refund failed:", e); }
                // Step 3: flip status — trigger keeps paid/remaining in sync
                await updateStatus(ea.id, targetStatus);
                setEditApt(null);
                toast(mode === "noshow" ? zhEn("已標記未到店", "Marked as no-show") : zhEn("預約已取消", "Booking cancelled"));
              };
              if (ea.status === "cancelled" || ea.status === "noshow") return null;
              const isDoneAndPaid = ea.status === "done" && netCash > 0;
              return (
                <div style={{borderTop:"1px dashed var(--c-border)",marginTop:4,paddingTop:14,display:"grid",gridTemplateColumns:ea.status === "done" ? "1fr" : "1fr 1fr",gap:8}}>
                  {ea.status !== "done" && (
                    <Btn variant="ghost" style={{background:"#FFFBEB",color:"#D97706",border:"2px solid #FDE68A"}} onClick={() => handleCancelOrNoShow("noshow")}>{zhEn("⚠️ 標記為未到店", "⚠️ Mark as no-show")}</Btn>
                  )}
                  <Btn variant="ghost" style={{background:"#FEF2F2",color:"#DC2626",border:"2px solid #FECACA"}} onClick={() => handleCancelOrNoShow("cancel")}>
                    {isDoneAndPaid ? zhEn("↩️ 退款並取消（已完成）","↩️ Refund & cancel (completed)") : zhEn("❌ 取消預約","❌ Cancel booking")}
                  </Btn>
                </div>
              );
            })()}

            {/* Danger zone — permanent delete. Distinct from 取消預約
                (which keeps the row with status='cancelled' for audit);
                delete removes the row entirely. Owners use this for
                booking entries that should never have existed (test
                rows, duplicates, mis-clicks). */}
            <div style={{borderTop:"1px dashed var(--c-border)",marginTop:4,paddingTop:14}}>
              <Btn variant="danger" style={{width:"100%"}} onClick={() => {
                // Financial-safety guard — never let hard-delete vaporise
                // collected cash. Forces owner to cancel + refund first so
                // payment_log carries the full audit trail.
                const netCashD = Array.isArray(ea.paymentLog)
                  ? ea.paymentLog.reduce((s, e) => s + ((e?.type === "refund" ? -1 : 1) * (Number(e?.amount) || 0)), 0)
                  : 0;
                if (netCashD > 0 && ea.status !== "cancelled" && ea.status !== "noshow") {
                  toast(zhEn(`此預約已收 $${netCashD}，請先用「取消預約」處理退款，再刪除。`, `$${netCashD} has been collected for this booking. Use "Cancel booking" to handle the refund first, then delete it.`), "error");
                  return;
                }
                if (!window.confirm(i18nT("apt.delConfirm") + (netCashD > 0 ? zhEn("\n\n注意：此預約 payment log 仲有 $" + netCashD + " 紀錄，刪除後 audit trail 會消失。", "\n\nNote: this booking still has $" + netCashD + " in its payment log; deleting it removes the audit trail.") : ""))) return;
                setState(s => ({ ...s, appointments: s.appointments.filter(a => a.id !== ea.id) }));
                logActivity({
                  tenantId: ea.tenantId,
                  userId: user.id,
                  actionType: "appointment_deleted",
                  entityType: "appointment",
                  entityId: ea.id,
                  entityName: (state.users[ea.customerId]?.name || ea.customerName || zhEn("客戶", "Customer")) + " - " + (state.pets[ea.petId]?.name || ea.petName || zhEn("毛孩", "Pet")) + " - " + (ea.service || ""),
                  description: "永久刪除預約：" + ea.date + " " + ea.time,
                  severity: "warning",
                  details: { aptSnapshot: ea },
                }).catch(e => console.error("[PettoBook] log activity:", e));
                toast(zhEn("預約已刪除", "Booking deleted"), "warning");
                setEditApt(null);
              }}>
                <Trash2 size={16} strokeWidth={2.5} /> {i18nT("apt.del")}
              </Btn>
            </div>

            {/* Save / Cancel action bar */}
            <div style={{display:"flex",gap:10,marginTop:4,position:"sticky",bottom:0,background:"#FCFCFF",paddingTop:8}}>
              <Btn variant="secondary" style={{flex:1}} onClick={discardDraft}>{i18nT("common.cancel")}</Btn>
              <Btn style={{flex:2}} onClick={commitDraft} disabled={!ea._dirty}>
                <Save size={16} strokeWidth={2.5} /> {ea._dirty ? i18nT("common.save") : i18nT("edit.noChange", "未變更")}
              </Btn>
            </div>
          </div>;
        })()}
      </BottomSheet>

      {/* Peek modal — small detail card for a single past appointment.
          Opens from the 過往紀錄 tab when the user taps a history row,
          so they can inspect dates / services / price breakdown without
          losing the parent drawer's place. */}
      <BottomSheet open={!!peekHistoryApt} onClose={() => setPeekHistoryApt(null)} title={peekHistoryApt ? `${peekHistoryApt.date} · ${peekHistoryApt.time}` : ""} z={300}>
        {peekHistoryApt && (() => {
          const h = peekHistoryApt;
          const hPet = state.pets[h.petId];
          const hStaff = state.users[h.staffId];
          const tenantSvcs = tenant?.services || [];
          const tenantAddons2 = tenant?.addons || [];
          const hSpaLines = (h.spaIds || []).map(id => {
            const sp = tenantSvcs.find(s => s.id === id);
            if (!sp) return null;
            const pd = hPet ? getPricingDetail(hPet, sp, tenant) : { total: sp.basePrice };
            return { name: sp.name, price: pd.total };
          }).filter(Boolean);
          const hAddonLines = (h.addons || []).filter(n => !String(n).startsWith("💆")).map(name => {
            const a = tenantAddons2.find(x => x.name === name);
            const price = a && hPet ? computeAddonPrice(a, hPet).price : (a?.price || 0);
            return { name, price };
          });
          const hSurLines = (h.surcharges || []).map(s => ({ name: s.label || zhEn("附加費","Surcharge"), price: s.amount }));
          const hTotal = (Number(h.basePrice) || 0) + (Number(h.addonPrice) || 0) + ((h.surcharges || []).reduce((s, x) => s + (x.amount || 0), 0));
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"var(--c-lighter)",borderRadius:12,border:"1px solid var(--c-light)"}}>
                <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,background: h.status === "done" ? "#ECFDF5" : "#F2F2F7", color: h.status === "done" ? "#166534" : "#4A4A68"}}>{i18nT("status." + h.status, h.status)}</span>
                <span style={{flex:1,fontSize:13,fontWeight:700,color:"#6A6A85"}}>{i18nT("lang.name") === "English" ? "Groomer: " : zhEn("員工：", "Staff: ")}{hStaff?.name || "—"}</span>
              </div>
              <div style={{background:"#fff",borderRadius:14,padding:14,border:"1px solid var(--c-light)"}}>
                <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:14,fontWeight:700,color:"#4A4A68"}}>
                  <span>{h.service || zhEn("美容服務", "Grooming service")}</span>
                  <span>${h.basePrice}</span>
                </div>
                {hSpaLines.map((l, i) => (
                  <div key={"spa"+i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,fontWeight:700,color:"#6A6A85"}}>
                    <span>💆 {l.name}</span><span>+${l.price}</span>
                  </div>
                ))}
                {hAddonLines.map((l, i) => (
                  <div key={"ad"+i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,fontWeight:700,color:"#6A6A85"}}>
                    <span>{l.name}</span><span>+${l.price}</span>
                  </div>
                ))}
                {hSurLines.map((l, i) => (
                  <div key={"sur"+i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,fontWeight:700,color:"#92400E"}}>
                    <span>+ {l.name}</span><span>+${l.price}</span>
                  </div>
                ))}
                <div style={{borderTop:"1px solid var(--c-light)",marginTop:6,paddingTop:8,display:"flex",justifyContent:"space-between"}}>
                  <span style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>{zhEn("總收費", "Total charge")}</span>
                  <span style={{fontSize:18,fontWeight:900,color:"var(--c-primary)"}}>${hTotal}</span>
                </div>
              </div>
              {h.report && (
                <button onClick={() => { setPeekHistoryApt(null); setReportSheet(h.id); }} style={{padding:"10px",borderRadius:12,border:"1px dashed var(--c-primary)",background:"var(--c-lighter)",color:"var(--c-primary)",fontWeight:800,fontSize:13,cursor:"pointer"}}>{zhEn("📋 查看健康報告", "📋 View health report")}</button>
              )}
              <Btn variant="secondary" onClick={() => { setPeekHistoryApt(null); setEditApt(h); }}>{zhEn("開啟完整預約詳情 →", "Open full booking details →")}</Btn>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Collect-payment sheet — per-method breakdown + wallet deduction.
          Replaces the old one-shot "已收取尾款 $X" toast. Owner enters
          amount per configured payment method + optional wallet deduct
          from the customer's balance; sum must equal outstanding.
          recordPaymentAtomic appends each entry to appointments.payment_log
          idempotently, then the wallet debit + paid flag land. */}
      <BottomSheet open={!!collectSheet} onClose={() => setCollectSheet(null)} title={zhEn("💵 收款 · 分項記錄", "💵 Collect payment · itemised")} z={310}>
        {collectSheet && <CollectPaymentSheet
          apt={state.appointments.find(a => a.id === collectSheet.aptId)}
          customer={(() => {
            const _cid = state.appointments.find(a => a.id === collectSheet.aptId)?.customerId;
            const _base = state.users?.[_cid];
            // Wallet is PER-SHOP (customer_memberships) — the source of truth.
            // Overlay the membership balance so 收款 offers wallet deduction even
            // when state.users[...].wallet is stale (top-ups via the 錢包 view
            // update only state.memberships).
            const _mem = (state.memberships || []).find(m => m.userId === _cid && m.tenantId === user.tenantId);
            return _mem ? { ..._base, wallet: _mem.wallet, walletTx: _mem.walletTx } : _base;
          })()}
          tenant={tenant}
          user={user}
          onClose={() => setCollectSheet(null)}
          onCollected={(patch, walletTxEntries, newWallet, customerId) => {
            setState(s => {
              const nextUsers = { ...s.users };
              if (customerId && nextUsers[customerId]) {
                const cu = nextUsers[customerId];
                nextUsers[customerId] = {
                  ...cu,
                  wallet: typeof newWallet === "number" ? newWallet : cu.wallet,
                  walletTx: walletTxEntries.length ? [...(cu.walletTx || []), ...walletTxEntries] : cu.walletTx,
                };
              }
              return {
                ...s,
                users: nextUsers,
                // Wallet is per-shop — mirror the deduction into the membership
                // (the source of truth) so the balance is consistent everywhere
                // (收款, 錢包 view, customer app) without waiting on realtime.
                memberships: (customerId && walletTxEntries.length)
                  ? (s.memberships || []).map(m =>
                      (m.userId === customerId && m.tenantId === user.tenantId)
                        ? { ...m,
                            wallet: typeof newWallet === "number" ? newWallet : m.wallet,
                            walletTx: [...(m.walletTx || []), ...walletTxEntries] }
                        : m)
                  : s.memberships,
                appointments: s.appointments.map(x => x.id === collectSheet.aptId ? { ...x, ...patch } : x),
              };
            });
            // The appointment-detail sheet can be open UNDERNEATH this one (its
            // 收款 button now opens this sheet), and it renders from `editApt`,
            // not from state.appointments — without this it would still show
            // 未付 / the 收款 button after a successful collection.
            setEditApt(prev => prev?.id === collectSheet.aptId ? { ...prev, ...patch } : prev);
            setCollectSheet(null);
          }}
        />}
      </BottomSheet>

      {/* Re-weigh sheet (on check-in or manual) */}
      <BottomSheet open={!!reweighSheet} onClose={() => setReweighSheet(null)} title={zhEn("⚖️ 到店秤重", "⚖️ Weigh-in at arrival")}>
        {reweighSheet && (() => {
          const apt = state.appointments.find(a => a.id === reweighSheet.aptId);
          if (!apt) return null;
          const pet = state.pets[apt.petId];
          const oldW = pet?.weight || 0;
          const newW = parseFloat(reweighSheet.weight)||0;
          const svc = (tenant?.services||[]).find(s => s.name === apt.service || s.id === apt.svcId);
          const newPet = { ...pet, weight: newW };
          const pdOld = pet && svc ? getPricingDetail(pet, svc, tenant) : null;
          const pdNew = newW > 0 && svc ? getPricingDetail(newPet, svc, tenant) : null;
          const priceChanged = pdNew && pdOld && pdNew.total !== pdOld.total;
          const confirmReweigh = (alsoCheckIn) => {
            if (newW <= 0) { toast(zhEn("請輸入有效體重", "Enter a valid weight"), "error"); return; }
            const weightHistory = [...(pet.weightHistory||[]), { weight: newW, date: dateNow()+" "+timeNow(), by: user.id, from: oldW }];
            setState(s => {
              let newApts = s.appointments;
              if (priceChanged) {
                const log = [...(apt.priceAdjustLog||[]), { field:"basePrice", from: apt.basePrice, to: pdNew.total, reason:"到店重秤 "+oldW+"→"+newW+"kg", by:user.id, at:dateNow()+" "+timeNow() }];
                newApts = s.appointments.map(a => a.id === apt.id ? { ...a, basePrice: pdNew.total, priceAdjustLog: log } : a);
              }
              if (alsoCheckIn) {
                newApts = newApts.map(a => a.id === apt.id ? { ...a, status:"checkedin", checkedInAt: dateNow() + "T" + timeNow() + ":00" } : a);
              }
              return { ...s, pets:{ ...s.pets, [pet.id]:{ ...s.pets[pet.id], weight:newW, weightHistory } }, appointments: newApts };
            });
            if (editApt && editApt.id === apt.id) setEditApt(a => ({ ...a, basePrice: priceChanged ? pdNew.total : a.basePrice }));
            toast(priceChanged ? zhEn("已更新體重，報價 $"+pdOld.total+" → $"+pdNew.total, "Weight updated, quote $"+pdOld.total+" → $"+pdNew.total) : zhEn("已更新體重","Weight updated"));
            setReweighSheet(null);
          };
          return <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{background:"var(--c-lighter)",borderRadius:20,padding:14,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:13,fontWeight:700,color:"#6A6A85",margin:"0 0 4px"}}>{pet?.name + " · " + pet?.breed}</p>
              <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{zhEn("客戶填報體重：", "Customer-reported weight: ") + oldW + "kg"}</p>
            </div>
            <Input label={zhEn("實際體重 (kg) — 以店內磅為準", "Actual weight (kg) — as measured in the shop")} type="number" step="0.1" value={reweighSheet.weight} onChange={e => setReweighSheet(r => ({ ...r, weight:e.target.value }))} autoFocus />
            {priceChanged && <div style={{background:"#FFFBEB",borderRadius:16,padding:"12px 16px",border:"2px solid #FEF3C7"}}>
              <p style={{fontSize:13,fontWeight:800,color:"#92400E",margin:"0 0 6px"}}>{zhEn("⚠️ 報價將會調整", "⚠️ The quote will change")}</p>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,color:"#4A4A68"}}>
                <span>{zhEn("原報價 $"+pdOld.total, "Original quote $"+pdOld.total)}</span>
                <span>→</span>
                <span style={{color:"#D97706"}}>{zhEn("新報價 $", "New quote $")+pdNew.total + (pdNew.total>pdOld.total?" (+$"+(pdNew.total-pdOld.total)+")":" (-$"+(pdOld.total-pdNew.total)+")")}</span>
              </div>
              <p style={{fontSize:11,color:"#8A8A9E",margin:"6px 0 0",fontWeight:700}}>{pdNew.baseLabel}</p>
            </div>}
            {pdNew && !priceChanged && newW>0 && <p style={{fontSize:12,color:"#10B981",fontWeight:700,textAlign:"center"}}>{zhEn("✓ 體重在原計價區間，報價不變", "✓ Weight is within the original price band; quote unchanged")}</p>}
            <div style={{display:"flex",gap:10}}>
              <Btn variant="secondary" style={{flex:1}} onClick={() => confirmReweigh(false)}>{zhEn("只更新體重", "Update weight only")}</Btn>
              {apt.status === "pending" && <Btn style={{flex:1,background:"#10B981"}} onClick={() => confirmReweigh(true)}>{zhEn("更新並到店 📍", "Update & check in 📍")}</Btn>}
            </div>
          </div>;
        })()}
      </BottomSheet>

      <BottomSheet open={!!sendNotif} onClose={() => setSendNotif(null)} title={zhEn("發送通知 💬", "Send notification 💬")}>
        {sendNotif && <NotifSender type={sendNotif.type} tenant={tenant} data={sendNotif.data} onSend={() => {}} onClose={() => setSendNotif(null)} />}
        {/* Photo lightbox — tap-to-dismiss, used by the history tab thumbnails. */}
        {aptLightboxPhoto && (
          <div onClick={() => setAptLightboxPhoto(null)} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,cursor:"zoom-out"}}>
            <img src={aptLightboxPhoto} alt="full" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:8}} />
            <button onClick={(e) => { e.stopPropagation(); setAptLightboxPhoto(null); }} style={{position:"absolute",top:16,right:16,background:"rgba(255,255,255,0.18)",backdropFilter:"blur(8px)",border:"none",color:"#fff",borderRadius:99,width:44,height:44,fontSize:20,fontWeight:900,cursor:"pointer"}}>×</button>
          </div>
        )}
        {/* #13 No-show confirm with policy preview + waive option. */}
        {noshowConfirm && (() => {
          const a = noshowConfirm;
          const cp = tenant?.cancellationPolicy || {};
          const subtotal = a.basePrice + a.addonPrice;
          const feePct = cp.noShowFeePercent ?? 100;
          const feeAmt = Math.round(subtotal * feePct / 100);
          const petName = state.pets[a.petId]?.name || zhEn("毛孩", "Pet");
          const applyNoshow = (waive) => {
            const stamp = dateNow() + " " + timeNow();
            setState(s => ({ ...s, appointments: s.appointments.map(x => x.id === a.id ? {
              ...x, status:"noshow",
              surcharges: (!waive && feeAmt > 0) ? [...(x.surcharges||[]), { name:"未到店罰款 ("+feePct+"%)", amount: feeAmt, at: stamp, by: user.id }] : (x.surcharges||[]),
              priceAdjustLog: (!waive && feeAmt > 0) ? [...(x.priceAdjustLog||[]), { field:"surcharge.noshow", from:0, to:feeAmt, reason:"no-show 罰款 "+feePct+"%", by:user.id, at:stamp }] : (x.priceAdjustLog||[]),
            } : x) }));
            toast(waive ? zhEn("已標記未到店（豁免罰款）","Marked as no-show (fee waived)") : (feeAmt > 0 ? zhEn("已標記未到店，自動扣 $"+feeAmt, "Marked as no-show, $"+feeAmt+" charged automatically") : zhEn("已標記未到店","Marked as no-show")), "warning");
            setNoshowConfirm(null);
          };
          return (
            <div style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(74,74,104,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <div style={{background:"#fff",borderRadius:24,padding:24,maxWidth:380,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
                <div style={{fontSize:42,textAlign:"center",marginBottom:6}}>⚠️</div>
                <h3 style={{fontWeight:900,fontSize:18,color:"#4A4A68",margin:"0 0 6px",textAlign:"center"}}>{zhEn("標記為未到店？", "Mark as no-show?")}</h3>
                <p style={{fontSize:13,color:"#8A8A9E",fontWeight:700,margin:"0 0 16px",textAlign:"center",lineHeight:1.6}}>{petName} · {a.time}</p>
                {feeAmt > 0 && (
                  <div style={{background:"#FFFBEB",borderRadius:14,padding:"12px 14px",marginBottom:14,border:"2px solid #FEF3C7"}}>
                    <p style={{fontSize:12,fontWeight:800,color:"#92400E",margin:"0 0 4px"}}>{zhEn("店舖政策罰款","Shop policy fee")} ({feePct}%)</p>
                    <p style={{fontSize:20,fontWeight:900,color:"#D97706",margin:0}}>${feeAmt}</p>
                  </div>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {feeAmt > 0 ? (
                    <>
                      <Btn onClick={() => applyNoshow(false)} style={{width:"100%",background:"#D97706",color:"#fff"}}>{zhEn("保留訂金 · 加收 $"+feeAmt, "Keep deposit · charge $"+feeAmt)}</Btn>
                      <Btn variant="secondary" onClick={() => applyNoshow(true)} style={{width:"100%"}}>豁免罰款（仁慈處理）</Btn>
                    </>
                  ) : (
                    <Btn onClick={() => applyNoshow(true)} style={{width:"100%",background:"#D97706",color:"#fff"}}>{zhEn("標記未到店", "Mark no-show")}</Btn>
                  )}
                  <Btn variant="ghost" onClick={() => setNoshowConfirm(null)} style={{width:"100%"}}>{zhEn("取消", "Cancel")}</Btn>
                </div>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* ── SIDE DRAWER ────────────────────────────────────────────────
          Slides in from the left when the header hamburger is tapped.
          Holds the actions that don't fit on the bottom nav: 員工,
          活動記錄, and the staff calendar filter (show A / B / C / All
          in the schedule grid). */}
      {/* ─── 🔗 Quick-share booking link ───────────────────────────
          Discoverability fix: owners couldn't find their customer page
          (it was buried in Settings → Shop Basic). Now one tap from the
          top-right opens this sheet with the URL + Copy + native Share
          + a beautifully-styled QR poster the owner can print and put
          on their counter. */}
      <OwnerInboxSheet
        open={rfqInboxOpen}
        onClose={() => setRfqInboxOpen(false)}
        tenantId={user.tenantId}
        services={tenant?.services || []}
      />
      {shareSheetOpen && (() => {
        const slug = tenant?.bookingSlug || "";
        const link = slug ? getBookingUrl(slug, state) : "";
        const themeVars = THEMES[tenant?.theme] || THEMES.peach;
        const primaryColor = themeVars["--c-primary"] || "#FF758F";
        const gradColor = themeVars["--c-grad1"] || "#FFB6C1";
        return (
          <div style={{position:"fixed",inset:0,zIndex:400,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
            <div onClick={() => setShareSheetOpen(false)} style={{position:"absolute",inset:0,background:"rgba(31,41,55,0.55)",backdropFilter:"blur(8px)"}} />
            <div style={{position:"relative",width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",background:"#FFFDFD",borderRadius:"28px 28px 0 0",boxShadow:"0 -16px 60px rgba(0,0,0,0.25)",animation:"slideUp 0.3s cubic-bezier(0.32,0.72,0,1)"}}>
              <style>{`@keyframes slideUp { 0% { transform: translateY(100%); } 100% { transform: translateY(0); } }`}</style>
              {/* Header */}
              <div style={{position:"sticky",top:0,zIndex:2,background:"#FFFDFD",borderBottom:"1px solid var(--c-border)",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <p style={{fontWeight:900,fontSize:17,color:"#4A4A68",margin:0}}>{zhEn("🔗 分享你的預約連結", "🔗 Share your booking link")}</p>
                <button onClick={() => setShareSheetOpen(false)} style={{background:"transparent",border:"none",cursor:"pointer",padding:6}}><X size={20} color="#8A8A9E" strokeWidth={2.5} /></button>
              </div>
              <div style={{padding:"18px 20px 24px"}}>
                {!slug ? (
                  <div style={{padding:"22px 18px",background:"#FFFBEB",border:"2px solid #FDE68A",borderRadius:16,textAlign:"center"}}>
                    <p style={{fontSize:14,color:"#92400E",fontWeight:800,margin:"0 0 6px"}}>{zhEn("未設定預約連結", "Booking link not set")}</p>
                    <p style={{fontSize:12,color:"#92400E",margin:"0 0 14px",fontWeight:700}}>{zhEn("請先到設定 → 店鋪基本資料設定一個短連結（例：pawsin）", "First set a short link under Settings → Shop details (e.g. pawsin)")}</p>
                    <Btn size="sm" onClick={() => { setShareSheetOpen(false); setSettingsSubtab("shop-basic"); setTab("settings"); }}>{zhEn("前往設定 →", "Go to Settings →")}</Btn>
                  </div>
                ) : (
                  <>
                    {/* Poster preview — what the printed QR poster looks like.
                        Pure-React so it always reflects current branding. */}
                    {/* Free plan: the link only takes 10 customer bookings a month —
                        say so where the owner is about to share it. */}
                    {(tenant?.plan || "free") === "free" && ownQuota && !ownQuota.unlimited && (() => {
                      const left = Math.max(0, ownQuota.limit - ownQuota.used);
                      const tone = !ownQuota.open ? ["#FEF2F2", "#FECACA", "#B91C1C"] : left <= 2 ? ["#FFFBEB", "#FDE68A", "#92400E"] : ["var(--c-lighter)", "var(--c-border)", "#4A4A68"];
                      return (
                        <div style={{background:tone[0],border:"1.5px solid "+tone[1],borderRadius:16,padding:"12px 14px",marginBottom:14,textAlign:"left"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                            <span style={{fontSize:13,fontWeight:900,color:tone[2]}}>{zhEn("本月網上預約名額", "Online bookings this month")}</span>
                            <span style={{fontSize:14,fontWeight:900,color:tone[2]}}>{ownQuota.used} / {ownQuota.limit}</span>
                          </div>
                          <div style={{height:6,borderRadius:99,background:"rgba(0,0,0,0.08)",margin:"8px 0",overflow:"hidden"}}>
                            <div style={{width:Math.min(100, Math.round(ownQuota.used / Math.max(1, ownQuota.limit) * 100)) + "%",height:"100%",background:tone[2],borderRadius:99}} />
                          </div>
                          <p style={{margin:0,fontSize:12,fontWeight:700,color:"#6A6A85",lineHeight:1.55}}>
                            {!ownQuota.open
                              ? zhEn("名額已用完 — 客人打開呢條連結只見到「致電 / WhatsApp」。升級基本版即可不限次數。", "All places used — customers opening this link only see Call / WhatsApp. Upgrade to Basic for unlimited bookings.")
                              : left <= 2
                                ? zhEn("只剩 " + left + " 個名額，用完客人就約唔到。升級基本版即可不限次數。", "Only " + left + " left this month; after that customers can't book. Upgrade to Basic for unlimited.")
                                : zhEn("免費版每月可接 " + ownQuota.limit + " 個客人網上預約，仲有 " + left + " 個。", "Free includes " + ownQuota.limit + " online bookings a month — " + left + " left.")}
                          </p>
                          {(!ownQuota.open || left <= 2) && <button onClick={() => { setShareSheetOpen(false); openPlanPage(); }} style={{marginTop:8,border:"none",borderRadius:99,padding:"7px 14px",background:"var(--c-primary)",color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer"}}>{zhEn("升級方案 →", "Upgrade →")}</button>}
                        </div>
                      );
                    })()}
                    <div id="petos-qr-poster" style={{width:"100%",aspectRatio:"3/4",borderRadius:24,background:`linear-gradient(160deg, ${gradColor} 0%, ${primaryColor} 100%)`,padding:"32px 24px",boxShadow:"0 16px 40px rgba(0,0,0,0.18)",position:"relative",overflow:"hidden",display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",color:"#fff"}}>
                      {/* Decorative paws */}
                      <span aria-hidden style={{position:"absolute",top:14,left:18,fontSize:42,opacity:0.18,transform:"rotate(-20deg)"}}>🐾</span>
                      <span aria-hidden style={{position:"absolute",top:24,right:14,fontSize:50,opacity:0.16,transform:"rotate(18deg)"}}>🐾</span>
                      <span aria-hidden style={{position:"absolute",bottom:60,left:10,fontSize:46,opacity:0.14,transform:"rotate(8deg)"}}>🦴</span>
                      <span aria-hidden style={{position:"absolute",bottom:30,right:16,fontSize:38,opacity:0.16,transform:"rotate(-12deg)"}}>✨</span>
                      {/* Logo */}
                      {tenant?.logoUrl ? (
                        <img src={tenant.logoUrl} alt="" style={{width:64,height:64,borderRadius:20,objectFit:"cover",border:"3px solid #fff",boxShadow:"0 6px 18px rgba(0,0,0,0.22)",marginBottom:8}} />
                      ) : (
                        <div style={{width:64,height:64,borderRadius:20,background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8,boxShadow:"0 6px 18px rgba(0,0,0,0.22)"}}>
                          <PawPrint size={32} color={primaryColor} strokeWidth={2.5} />
                        </div>
                      )}
                      <p style={{fontWeight:900,fontSize:22,margin:0,letterSpacing:0.3,textShadow:"0 2px 8px rgba(0,0,0,0.25)"}}>{tenant?.name || zhEn("我的寵物店", "My pet shop")}</p>
                      {tenant?.tagline && <p style={{fontSize:13,fontWeight:700,opacity:0.92,margin:"4px 0 0",textShadow:"0 1px 4px rgba(0,0,0,0.2)"}}>{tenant.tagline}</p>}
                      {/* QR — img with data URL from qrcode lib. Falls back to a
                          loading placeholder while generating. */}
                      <QrImage url={link} size={220} />
                      <p style={{fontSize:13,fontWeight:800,margin:"10px 0 4px",opacity:0.92}}>{zhEn("掃描即可線上預約 ✂️🐾", "Scan to book online ✂️🐾")}</p>
                      <p style={{fontSize:11,fontWeight:700,opacity:0.85,wordBreak:"break-all",fontFamily:"ui-monospace,SF Mono,monospace",margin:0}}>{link.replace(/^https?:\/\//,"")}</p>
                    </div>
                    {/* URL + copy */}
                    <div style={{display:"flex",gap:8,alignItems:"center",background:"var(--c-lighter)",borderRadius:14,padding:"10px 14px",marginTop:16,border:"2px solid var(--c-border)"}}>
                      <p style={{fontSize:12,fontFamily:"ui-monospace,SF Mono,monospace",color:"#4A4A68",margin:0,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:700}}>{link}</p>
                      <Btn size="sm" onClick={() => { try { navigator.clipboard?.writeText(link); toast(i18nT("common.copied")); } catch { toast(zhEn("複製失敗", "Copy failed"), "error"); } }}><Copy size={14} strokeWidth={2.5} /> {i18nT("common.copy")}</Btn>
                    </div>
                    {/* Actions */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:14}}>
                      <Btn variant="secondary" onClick={async () => {
                        const shareData = { title: tenant?.name || "PettoBook", text: zhEn("歡迎使用線上預約：", "Book online with us: ") + link, url: link };
                        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
                          try { await navigator.share(shareData); }
                          catch (e) { if (e?.name !== "AbortError") { try { navigator.clipboard?.writeText(link); toast(zhEn("已複製連結 ✓", "Link copied ✓")); } catch {} } }
                        } else {
                          try { navigator.clipboard?.writeText(link); toast(zhEn("已複製連結 ✓", "Link copied ✓")); } catch { toast(zhEn("複製失敗", "Copy failed"), "error"); }
                        }
                      }}><Send size={15} strokeWidth={2.5} /> {zhEn("立即分享", "Share now")}</Btn>
                      <Btn onClick={() => downloadQrPoster({ tenant, link, primaryColor, gradColor })}>
                        <Upload size={15} strokeWidth={2.5} style={{transform:"rotate(180deg)"}} /> {zhEn("下載海報", "Download poster")}
                      </Btn>
                    </div>
                    <Btn onClick={() => { setShareSheetOpen(false); setPreviewTab("overview"); setPreviewOpen(true); }} style={{width:"100%",marginTop:10,background:"linear-gradient(135deg,#FF7AA2,#FF4D6D)",color:"#fff",fontWeight:900}}>
                      {zhEn("👁️ 預覽店面（即點即改）", "👁️ Preview storefront (edit in place)")}
                    </Btn>
                    <Btn variant="ghost" onClick={() => { setShareSheetOpen(false); setSettingsSubtab("shop-basic"); setTab("settings"); }} style={{width:"100%",marginTop:8,fontSize:13}}>
                      {zhEn("⚙️ 修改連結 / 上載店鋪 LOGO 設計更靚海報", "⚙️ Edit link / upload your shop logo for a nicer poster")}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {drawerOpen && (
        <div style={{position:"fixed",inset:0,zIndex:300,display:"flex"}}>
          <div onClick={() => setDrawerOpen(false)} style={{position:"absolute",inset:0,background:"rgba(74,74,104,0.4)",backdropFilter:"blur(6px)"}} />
          <div style={{position:"relative",width:"82%",maxWidth:320,height:"100%",background:"#FFFDFD",boxShadow:"8px 0 32px rgba(0,0,0,0.16)",display:"flex",flexDirection:"column",animation:"slideRight 0.25s cubic-bezier(0.32,0.72,0,1)"}}>
            <style>{`@keyframes slideRight { 0% { transform: translateX(-100%); } 100% { transform: translateX(0); } }`}</style>
            {/* Drawer header */}
            <div style={{padding:"20px 20px 14px",borderBottom:"2px solid var(--c-light)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:99,background:"linear-gradient(135deg,var(--c-grad1),var(--c-primary))",display:"flex",alignItems:"center",justifyContent:"center"}}><Star size={18} color="#fff" strokeWidth={3} /></div>
                <div style={{minWidth:0}}>
                  <p style={{fontSize:14,fontWeight:900,color:"#4A4A68",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tenant?.name || user.name}</p>
                  <p style={{fontSize:11,color:"#8A8A9E",margin:"2px 0 0",fontWeight:700}}>{user.email || ""}</p>
                </div>
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label={zhEn("關閉", "Close")} style={{background:"var(--c-light)",border:"none",borderRadius:99,width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                <X size={16} color="var(--c-primary)" strokeWidth={3} />
              </button>
            </div>

            <div style={{flex:1,overflowY:"auto",padding:"16px 12px"}}>
              {/* View-mode picker for the schedule. Jumps to 日歷 + sets mode. */}
              <p style={{fontSize:11,fontWeight:800,color:"#AFAFC4",margin:"0 8px 6px",letterSpacing:0.5}}>{zhEn("日歷顯示模式", "Calendar view")}</p>
              {[
                { id: "day",    icon: Clock,        label: zhEn("日 (Day)", "Day") },
                { id: "month",  icon: Calendar,     label: zhEn("月 (Month)", "Month") },
                { id: "agenda", icon: Activity,     label: zhEn("議程 (Agenda)", "Agenda") },
              ].map(item => {
                const isActive = tab === "schedule" && viewMode === item.id;
                return (
                  <button key={item.id} onClick={() => { setViewMode(item.id); setTab("schedule"); setDrawerOpen(false); }} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"10px 14px",borderRadius:14,border:"none",background:isActive ? "var(--c-lighter)" : "transparent",color:isActive ? "var(--c-primary)" : "#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer",marginBottom:2}}>
                    <item.icon size={16} strokeWidth={2.5} color={isActive ? "var(--c-primary)" : "#6A6A85"} />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* 管理 (員工 / 套票 / 儲值卡 / 積分) moved back to the bottom
                  nav bar — no longer duplicated here in the drawer. */}

              {/* Staff calendar filter — quick way to show one staff's column
                  (A / B / C / All). Mirrors / replaces the chip strip above
                  the calendar so it's reachable from any tab. */}
              {staffList.length > 0 && (
                <>
                  <p style={{fontSize:11,fontWeight:800,color:"#AFAFC4",margin:"18px 8px 6px",letterSpacing:0.5}}>{zhEn("日歷顯示", "Calendar display")}</p>
                  <button onClick={() => { setHiddenStaff({}); }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,width:"100%",padding:"10px 14px",borderRadius:14,border:"none",background:Object.values(hiddenStaff).every(v=>!v) ? "var(--c-lighter)" : "transparent",color:Object.values(hiddenStaff).every(v=>!v) ? "var(--c-primary)" : "#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    <span style={{display:"flex",alignItems:"center",gap:10}}>
                      <Users size={16} strokeWidth={2.5} color={Object.values(hiddenStaff).every(v=>!v) ? "var(--c-primary)" : "#6A6A85"} />
                      {zhEn("全部美容師", "All groomers")}
                    </span>
                    {Object.values(hiddenStaff).every(v=>!v) && <Check size={14} color="var(--c-primary)" strokeWidth={3} />}
                  </button>
                  {staffList.map(s => {
                    // "Show only this staff" = hide everyone else.
                    const isOnly = !hiddenStaff[s.id] && staffList.filter(x => x.id !== s.id).every(x => hiddenStaff[x.id]);
                    return (
                      <button key={s.id} onClick={() => {
                        const next = {};
                        for (const x of staffList) { if (x.id !== s.id) next[x.id] = true; }
                        setHiddenStaff(next);
                      }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,width:"100%",padding:"10px 14px",borderRadius:14,border:"none",background:isOnly ? "var(--c-lighter)" : "transparent",color:isOnly ? "var(--c-primary)" : "#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                        <span style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:24,height:24,borderRadius:99,background:isOnly ? "var(--c-primary)" : "var(--c-light)",color:isOnly ? "#fff" : "var(--c-primary)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:11}}>{(s.name || "?")[0]}</div>
                          {s.name}
                        </span>
                        {isOnly && <Check size={14} color="var(--c-primary)" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div style={{padding:"12px 14px",borderTop:"2px solid var(--c-light)"}}>
              <button onClick={() => { setDrawerOpen(false); window.location.hash = "#/help"; }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"12px 14px",borderRadius:14,border:"none",background:"var(--c-lighter)",color:"var(--c-primary)",fontWeight:800,fontSize:14,cursor:"pointer",marginBottom:8}}>
                <BookOpen size={16} strokeWidth={2.5} /> {zhEn("使用說明 / FAQ", "Help / FAQ")}
              </button>
              {/* Support — jumps to 設定 → 客戶支援 (SupportPanel). */}
              <button onClick={() => { setDrawerOpen(false); jumpToSetting("support"); }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"12px 14px",borderRadius:14,border:"none",background:"var(--c-lighter)",color:"var(--c-primary)",fontWeight:800,fontSize:14,cursor:"pointer",marginBottom:8}}>
                <MessageCircle size={16} strokeWidth={2.5} /> {T("聯絡支援","Contact support","サポート")}
              </button>
              {/* Language — inline pills (the header pill was removed; this is
                  the always-reachable switcher alongside 設定 → 語言). */}
              <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px 12px"}}>
                <span style={{fontSize:13}}>🌐</span>
                {i18nListLocales().map(l => (
                  <button key={l.code} onClick={() => i18nSetLocale(l.code)} style={{flex:1,padding:"7px 0",borderRadius:99,border:"none",background: l.code === _ownerLoc ? "var(--c-primary)" : "var(--c-light)",color: l.code === _ownerLoc ? "#fff" : "#4A4A68",fontWeight:800,fontSize:12,cursor:"pointer"}}>{l.name}</button>
                ))}
              </div>
              <button onClick={() => { setDrawerOpen(false); onLogout(); }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"12px 14px",borderRadius:14,border:"none",background:"#FEF2F2",color:"#DC2626",fontWeight:800,fontSize:14,cursor:"pointer"}}>
                <LogOut size={16} strokeWidth={2.5} /> {zhEn("登出", "Log out")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BOTTOM TAB BAR ─────────────────────────────────────────────
          Fixed bottom navigation — thumb-reachable on mobile. Horizontally
          scrollable for the 11 owner tabs; staff sees just 4 so they fit
          without scrolling. Active tab gets the primary fill, others stay
          subtle. */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:30,background:"#FFFDFD",borderTop:"1px solid var(--c-border)",boxShadow:"0 -8px 24px rgba(0,0,0,0.06)",paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
        <div style={{maxWidth:720,margin:"0 auto",padding:"8px 12px",display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none"}}>
          {tabs.map(t => {
            // Report shortcuts route through settings + a subtab — light up
            // the button when that subtab is the active one.
            const isReportShortcut = ["packages-admin","wallets-admin","points-admin"].includes(t.id);
            const active = isReportShortcut
              ? (tab === "settings" && settingsSubtab === t.id)
              : (tab === t.id && !["packages-admin","wallets-admin","points-admin"].includes(settingsSubtab));
            return (
              <button key={t.id} onClick={() => {
                // Report shortcuts (套票 / 儲值卡 / 積分) live under the
                // settings tab via a subtab. The previous behavior left the
                // subtab pinned to the shortcut even after the user tapped
                // 設定 next — making 設定 feel broken because clicking it
                // didn't visibly change anything. Now: tapping 設定
                // explicitly resets the subtab back to its default landing,
                // while tapping the shortcut sets it to that report.
                if (["packages-admin","wallets-admin","points-admin"].includes(t.id)) {
                  setSettingsSubtab(t.id);
                  setSettingsDrilled(true);
                  setTab("settings");
                } else if (t.id === "settings") {
                  setSettingsSubtab("shop-basic");
                  setSettingsDrilled(false);
                  setTab("settings");
                } else {
                  setTab(t.id);
                }
              }} style={{flexShrink:0,padding:"8px 12px",borderRadius:99,background:active?"var(--c-primary)":"transparent",color:active?"#fff":"#6A6A85",fontWeight:800,fontSize:12,border:"none",whiteSpace:"nowrap",display:"flex",flexDirection:"column",alignItems:"center",gap:2,cursor:"pointer",transition:"all 0.2s",minWidth:60,boxShadow:active?"0 6px 16px var(--c-primary-30)":"none"}}>
                <span style={{position:"relative",display:"flex"}}>
                  <t.icon size={18} strokeWidth={active?3:2.2} color={active?"#fff":"var(--c-primary)"} />
                  {t.id === "wainbox" && waUnread > 0 && (
                    <span style={{position:"absolute",top:-6,right:-10,minWidth:16,height:16,padding:"0 4px",borderRadius:99,background:"#FF4D6D",color:"#fff",fontSize:10,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",border:"1.5px solid #FFFDFD",boxSizing:"border-box",lineHeight:1}}>{waUnread > 99 ? "99+" : waUnread}</span>
                  )}
                  {/* 認領請求 waited unseen in the 顧客 tab because a one-off
                      toast was its only signal — a pending merge needs a badge
                      the owner can find on any screen. */}
                  {t.id === "crm" && pendingClaims.length > 0 && (
                    <span style={{position:"absolute",top:-6,right:-10,minWidth:16,height:16,padding:"0 4px",borderRadius:99,background:"#F97316",color:"#fff",fontSize:10,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",border:"1.5px solid #FFFDFD",boxSizing:"border-box",lineHeight:1}}>{pendingClaims.length > 99 ? "99+" : pendingClaims.length}</span>
                  )}
                </span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Floating shop-owner AI assistant (專業/旗艦). Server re-checks the plan. */}
      {user.role === "owner" && !!getPlans(state.platformConfig)[tenant?.plan || "free"]?.features?.shopAssistant && (
        <ShopAssistantWidget user={user} tenant={tenant} />
      )}
    </div>
  );
};

// ── SUPER ADMIN ────────────────────────────────────────────────────────────
const SuperAdminApp = ({ state, setState, user, onLogout }) => {
  const [tab, setTab] = useState("dashboard");
  const [upgradeSheet, setUpgradeSheet] = useState(null);
  const [announceForm, setAnnounceForm] = useState(null);
  const [announceSending, setAnnounceSending] = useState(false);
  const [editAutoNotif, setEditAutoNotif] = useState(null);
  const [billingSheet, setBillingSheet] = useState(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantFilter, setTenantFilter] = useState("all");
  const tabs = [{ id:"dashboard",icon:BarChart2,label:"總覽" },{ id:"tenants",icon:Users,label:"商戶" },{ id:"followup",icon:Megaphone,label:"跟進" },{ id:"claims",icon:Store,label:"認領" },{ id:"saleschat",icon:MessageCircle,label:"查詢" },{ id:"support",icon:MessageCircle,label:"工單" },{ id:"automations",icon:Zap,label:"自動通知" },{ id:"announce",icon:Megaphone,label:"公告" },{ id:"plans",icon:Package,label:"方案" },{ id:"platform",icon:Settings,label:"平台設定" }];

  // Auto-notification settings (stored on platform level)
  const autoNotifDefaults = [
    { id:"an1", name:"預約確認通知", trigger:"booking_created", channel:"email", enabled:true, paidOnly:false, template:"booking_confirmed", timing:"即時", description:"客戶預約成功後自動發送確認訊息" },
    { id:"an2", name:"預約提醒 (前一天)", trigger:"day_before", channel:"email", enabled:true, paidOnly:true, template:"reminder", timing:"預約前 24 小時", description:"預約前一天自動提醒客戶" },
    { id:"an3", name:"美容完成通知", trigger:"grooming_done", channel:"email", enabled:true, paidOnly:false, template:"grooming_done", timing:"即時", description:"美容完成後自動發送健康報告給客戶" },
    { id:"an4", name:"取消通知", trigger:"booking_cancelled", channel:"email", enabled:true, paidOnly:false, template:"cancel", timing:"即時", description:"預約取消後自動通知客戶" },
    { id:"an5", name:"定期美容提醒", trigger:"recurring_reminder", channel:"sms", enabled:false, paidOnly:true, template:"reminder", timing:"根據定期預約週期", description:"提醒客戶預約下一次定期美容" },
    { id:"an6", name:"疫苗到期提醒", trigger:"vaccine_expiry", channel:"sms", enabled:false, paidOnly:true, template:"custom", timing:"疫苗到期前 30 天", description:"寵物疫苗即將到期時提醒家長" },
    { id:"an7", name:"生日祝福", trigger:"pet_birthday", channel:"email", enabled:false, paidOnly:true, template:"custom", timing:"寵物生日當天", description:"寵物生日當天發送祝福訊息" },
    { id:"an8", name:"久未到店提醒", trigger:"inactive_customer", channel:"sms", enabled:false, paidOnly:true, template:"custom", timing:"最後到店 60 天後", description:"超過60天未到店的客戶自動發送關懷訊息" },
  ];
  const autoNotifs = state.autoNotifications || autoNotifDefaults;

  const up = (tid, plan) => { setState(s => ({ ...s, tenants: s.tenants.map(t => t.id === tid ? { ...t, plan } : t) })); toast("方案已更新為 " + PLANS[plan].name); setUpgradeSheet(null); };
  const saveAnnounce = async () => {
    if (!announceForm?.title) { toast("請填寫公告標題","error"); return; }
    const audience = announceForm.audience || "both"; // both | shop | customer
    const ann = { id:uuid(), title:announceForm.title, body:announceForm.body, active:true, date:dateNow(), audience };
    // Email leg first: if the admin cancels the confirm, nothing is published.
    const wantEmail = audience !== "customer" && announceForm.emailOn !== false;
    if (wantEmail) {
      const email = effectiveAnnounceEmail(announceForm.email, { title: ann.title, body: ann.body });
      let n = "所有";
      try { n = String((await announceEmail("count")).count); } catch { /* fall back to wording */ }
      if (!window.confirm(`發布公告，並寄電郵「${email.subject}」給 ${n} 位店主？\n\n電郵寄出後無法收回。`)) return;
      setAnnounceSending(true);
      try {
        const r = await announceEmail("send", email, ann.id);
        toast(`📧 已寄出 ${r.sent} 封電郵` + (r.failed ? `（${r.failed} 封失敗）` : ""), r.failed ? "error" : undefined);
      } catch (e) {
        toast("電郵寄送失敗：" + (e?.message || e) + "（App 內公告照常發布）", "error");
      } finally { setAnnounceSending(false); }
    }
    setState(s => {
      const mk = (tenantId, userId) => ({ id:uuid(), tenantId, userId, type:"announcement", title:"📢 "+ann.title, body:ann.body, read:false, time:timeNow(), createdAt: new Date().toISOString(), aptId:null });
      const notifs = [];
      // Customers (audience both | customer)
      if (audience !== "shop") s.tenants.forEach(t => (t.customerIds || []).forEach(cid => notifs.push(mk(t.id, cid))));
      // Shop owners (audience both | shop)
      if (audience !== "customer") s.tenants.forEach(t => { if (t.ownerId) notifs.push(mk(t.id, t.ownerId)); });
      return { ...s, announcements:[...(s.announcements||[]), ann], notifications:[...s.notifications, ...notifs] };
    });
    toast(i18nT("tx.announcementPublished")); setAnnounceForm(null);
  };

  const savePlatformConfig = async () => {
    try {
      await upsertPlatformConfig({
        config: state.platformConfig || {},
        autoNotifications: autoNotifs || []
      });
      toast("平台設定已儲存 ✅");
    } catch (e) {
      console.error("[PettoBook] save platform config failed:", e);
      toast("儲存失敗：" + (e?.message || e), "error");
    }
  };

  const allApts = state.appointments;
  const totalRev = allApts.filter(a => a.status === "done").reduce((s, a) => s + aptRevenue(a), 0);

  return (
    <div style={{minHeight:"100vh",background:"#FCFCFF",paddingBottom:120}}>
      <div style={{background:"linear-gradient(135deg,#1E1B4B 0%,#4C1D95 100%)",padding:"24px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",borderRadius:"0 0 32px 32px",boxShadow:"0 12px 40px rgba(76,29,149,0.3)",position:"relative",zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{background:"rgba(255,255,255,0.1)",borderRadius:16,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center"}}><Crown size={24} color="#FBBF24" strokeWidth={2.5} /></div>
          <div><span style={{fontWeight:900,color:"#fff",fontSize:18,display:"block",margin:"0 0 2px"}}>PettoBook 總控台</span><Badge color="#fff" bg="rgba(255,255,255,0.15)">Super Admin</Badge></div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <NotifBell userId={user.id} state={state} setState={setState} />
          {/* Dual-role: if this superadmin also owns a shop (is_super=true with role=owner/staff),
              offer a one-click way back to the merchant dashboard. */}
          {user.isSuper && user.role !== "superadmin" && user.tenantId && (
            <button
              onClick={() => {
                try { window.localStorage.setItem("petos:view-as", "owner"); } catch { /* ignore */ }
                window.location.reload();
              }}
              title="切換至商戶後台"
              style={{background:"rgba(255,255,255,0.15)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,height:44,padding:"0 14px",display:"flex",alignItems:"center",gap:6,cursor:"pointer",color:"#fff",fontWeight:800,fontSize:12}}
            >
              <Store size={16} color="#fff" strokeWidth={2.5} /> 商戶
            </button>
          )}
          <button onClick={onLogout} style={{background:"rgba(255,255,255,0.15)",backdropFilter:"blur(10px)",border:"none",borderRadius:99,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}><LogOut size={20} color="#fff" strokeWidth={2.5} /></button>
        </div>
      </div>

      <div style={{position:"fixed",bottom:20,left:20,right:20,background:"rgba(255,255,255,0.85)",backdropFilter:"blur(24px)",borderRadius:36,display:"flex",boxShadow:"0 16px 40px rgba(76,29,149,0.15)",border:"1px solid rgba(255,255,255,0.6)",padding:"8px 12px",zIndex:100}}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",gap:4,background:tab===t.id?"#F5F3FF":"transparent",borderRadius:24,border:"none",cursor:"pointer",color:tab===t.id?"#7C3AED":"#AFAFC4",transition:"all 0.3s",transform:tab===t.id?"scale(1.05)":"scale(1)"}}>
            <t.icon size={22} strokeWidth={tab===t.id?2.5:2} /><span style={{fontSize:11,fontWeight:tab===t.id?900:700}}>{t.label}</span>
          </button>
        ))}
      </div>

      <div style={{padding:20,maxWidth:720,margin:"0 auto"}}>
        {tab === "dashboard" && <>
          {/* ── Needs-attention feed ────────────────────────────────────
              Surfaces shops / accounts that need an operator touch today.
              Each card is tappable → switches to 商戶 tab with a search
              filter pre-applied. Keeps the dashboard from being a static
              vanity board. */}
          {(() => {
            const todayStr = dateNow();
            const sevenDaysAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return localDateStr(d); })();
            const threeDaysFromNow = (() => { const d = new Date(); d.setDate(d.getDate() + 3); return localDateStr(d); })();
            const apptByTenant = (() => {
              const m = new Map();
              for (const a of allApts) {
                const v = m.get(a.tenantId) || { all: 0, week: 0, lastDate: "" };
                v.all += 1;
                if (a.date >= sevenDaysAgo) v.week += 1;
                if (a.date > v.lastDate) v.lastDate = a.date;
                m.set(a.tenantId, v);
              }
              return m;
            })();

            const noActivation = state.tenants.filter(t => {
              const created = (t.createdAt || t.subscription?.joinDate || "").slice(0, 10);
              const totalApts = apptByTenant.get(t.id)?.all || 0;
              return created && created < sevenDaysAgo && totalApts === 0;
            });
            const pastDue = state.tenants.filter(t => t.subscription?.status === "past_due");
            const dormant = state.tenants.filter(t => {
              const v = apptByTenant.get(t.id);
              if (!v) return false;
              return v.all > 0 && v.week === 0;
            });
            const trialEnding = state.tenants.filter(t => {
              const ft = freeTrialOf(t);
              if (ft) return ft.daysLeft <= 3;
              if (t.subscription?.status !== "trialing") return false;
              const renewal = (t.subscription?.nextRenewalDate || "").slice(0, 10);
              return renewal && renewal <= threeDaysFromNow && renewal >= todayStr;
            });
            const stuckOwners = Object.values(state.users || {}).filter(u =>
              u && u.role === "owner" && !u.tenantId && u.enabled !== false
            );

            const sections = [
              {key:"noActivation", label:"🆕 註冊 7+ 日但未有預約", count: noActivation.length, color:"#F59E0B", bg:"#FFFBEB", border:"#FDE68A", items: noActivation.slice(0,5).map(t => t.name), filterTerm: noActivation[0]?.name},
              {key:"pastDue",      label:"⚠️ 賬單逾期", count: pastDue.length, color:"#DC2626", bg:"#FEF2F2", border:"#FECACA", items: pastDue.slice(0,5).map(t => t.name), filterTerm: pastDue[0]?.name},
              {key:"dormant",      label:"💤 本週 0 預約 (有過往紀錄)", count: dormant.length, color:"#7C3AED", bg:"#F5F3FF", border:"#DDD6FE", items: dormant.slice(0,5).map(t => t.name), filterTerm: dormant[0]?.name},
              {key:"trialEnding",  label:"🎯 試用即將到期 (3 日內)", count: trialEnding.length, color:"#2563EB", bg:"#EFF6FF", border:"#BFDBFE", items: trialEnding.slice(0,5).map(t => t.name), filterTerm: trialEnding[0]?.name},
              {key:"stuckOwners",  label:"🚨 老闆已註冊但店鋪未建立", count: stuckOwners.length, color:"#991B1B", bg:"#FEE2E2", border:"#FCA5A5", items: stuckOwners.slice(0,5).map(u => u.email || u.name || u.id?.slice(0,8))},
            ].filter(s => s.count > 0);

            if (sections.length === 0) {
              return (
                <Card style={{padding:16,marginBottom:16,background:"#F0FDF4",border:"2px solid #BBF7D0"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:22}}>✨</span>
                    <div>
                      <p style={{fontSize:14,fontWeight:900,color:"#166534",margin:0}}>一切正常</p>
                      <p style={{fontSize:12,color:"#15803D",margin:"2px 0 0",fontWeight:700,opacity:0.85}}>暫無需立即處理嘅店鋪 / 帳號</p>
                    </div>
                  </div>
                </Card>
              );
            }
            return (
              <Card style={{padding:16,marginBottom:16,background:"#FFFDFD",border:"2px solid #F3F3F8"}}>
                <h4 style={{fontWeight:900,fontSize:14,margin:"0 0 10px",color:"#4A4A68",display:"flex",alignItems:"center",gap:6}}>
                  🔔 需要關注 <span style={{fontSize:11,fontWeight:800,color:"#8A8A9E"}}>(共 {sections.reduce((s,x)=>s+x.count,0)} 項)</span>
                </h4>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {sections.map(s => (
                    <button
                      key={s.key}
                      onClick={() => { if (s.filterTerm) setTenantSearch(s.filterTerm); setTab("tenants"); }}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:s.bg,border:"1.5px solid "+s.border,borderRadius:12,cursor:"pointer",textAlign:"left",width:"100%"}}
                    >
                      <div style={{minWidth:32,height:32,borderRadius:99,background:"#fff",border:"1.5px solid "+s.border,color:s.color,fontSize:14,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{s.count}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:12,fontWeight:900,color:s.color,margin:0}}>{s.label}</p>
                        {s.items.length > 0 && (
                          <p style={{fontSize:11,color:s.color,margin:"2px 0 0",fontWeight:700,opacity:0.8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {s.items.join(" · ")}{s.count > s.items.length ? " · …+" + (s.count - s.items.length) : ""}
                          </p>
                        )}
                      </div>
                      <ChevronRight size={14} color={s.color} strokeWidth={2.5} />
                    </button>
                  ))}
                </div>
              </Card>
            );
          })()}

          {/* ── KPI Row ─── */}
          {(() => {
            // MRR stays in the platform's base plan currency so the
            // superadmin sees a single coherent number. Each tenant's
            // plan price uses the live (editable) plans dict.
            const livePlans = getPlans(state.platformConfig);
            const mrr = state.tenants.reduce((s,t) => s + (livePlans[t.plan]?.price||0), 0);
            const arr = mrr * 12;
            const totalLifetimePaid = state.tenants.reduce((s,t) => s + (t.subscription?.totalPaid||0), 0);
            // 活躍 = either explicitly subscribed "active" OR has any
            // appointment in the last 30 days. The previous count only
            // recognised paid subscriptions, so free-plan shops that were
            // clearly using PettoBook read as inactive.
            const thirtyDaysAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return localDateStr(d); })();
            const activeTenantIds = new Set(allApts.filter(a => a.date >= thirtyDaysAgo).map(a => a.tenantId));
            const activeCount = state.tenants.filter(t => t.subscription?.status==="active" || activeTenantIds.has(t.id)).length;
            const freeCount = state.tenants.filter(t => t.plan==="free").length;
            const paidCount = state.tenants.length - freeCount;
            const convRate = state.tenants.length > 0 ? Math.round((paidCount / state.tenants.length) * 100) : 0;
            const totalCustomers = state.tenants.reduce((s,t) => s + (t.customerIds?.length||0), 0);
            const totalStaff = state.tenants.reduce((s,t) => s + (t.staffIds?.length||0), 0);
            const avgRevPerTenant = paidCount > 0 ? Math.round(totalLifetimePaid / paidCount) : 0;
            // Uses the module-level `aptRevenue` helper (base + addons +
            // surcharges − discount) so every place shows the same number.
            const aptRevenue2 = aptRevenue;
            const thisMonthKey = dateNow().slice(0,7);
            const lastMonthKey = (() => {
              const d = new Date(thisMonthKey + "-01");
              d.setMonth(d.getMonth() - 1);
              return localDateStr(d).slice(0,7);
            })();
            const thisMonthApts = allApts.filter(a => a.date?.startsWith(thisMonthKey));
            const lastMonthApts = allApts.filter(a => a.date?.startsWith(lastMonthKey));
            const thisMonthDone = thisMonthApts.filter(a => a.status==="done");
            const lastMonthDone = lastMonthApts.filter(a => a.status==="done");
            const thisMonthRev = thisMonthDone.reduce((s,a) => s + aptRevenue2(a), 0);
            const lastMonthRev = lastMonthDone.reduce((s,a) => s + aptRevenue2(a), 0);
            // Last 12 weeks of platform revenue → sparkline under MRR.
            const weeklyRev = (() => {
              const out = [];
              for (let w = 11; w >= 0; w--) {
                const end = new Date(); end.setDate(end.getDate() - w * 7);
                const start = new Date(end); start.setDate(start.getDate() - 6);
                const s = localDateStr(start), e = localDateStr(end);
                const rev = allApts
                  .filter(a => a.status === "done" && a.date >= s && a.date <= e)
                  .reduce((acc, a) => acc + aptRevenue2(a), 0);
                out.push(rev);
              }
              return out;
            })();
            const fmtDelta = (cur, prev) => {
              if (!prev) return cur > 0 ? { txt: "新", up: true, color: "#10B981" } : null;
              const pct = Math.round(((cur - prev) / prev) * 100);
              if (pct === 0) return { txt: "0%", up: null, color: "#8A8A9E" };
              const up = pct > 0;
              return { txt: (up ? "▲" : "▼") + " " + Math.abs(pct) + "%", up, color: up ? "#10B981" : "#EF4444" };
            };
            const revDelta = fmtDelta(thisMonthRev, lastMonthRev);
            const aptDelta = fmtDelta(thisMonthApts.length, lastMonthApts.length);

            return <>
              {/* Hero MRR banner */}
              <div style={{background:"linear-gradient(135deg,#4C1D95,#7C3AED,#A78BFA)",borderRadius:32,padding:"28px 24px",color:"#fff",marginBottom:16,position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:-20,right:-20,width:120,height:120,borderRadius:99,background:"rgba(255,255,255,0.06)"}} />
                <div style={{position:"absolute",bottom:-30,left:40,width:80,height:80,borderRadius:99,background:"rgba(255,255,255,0.04)"}} />
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",position:"relative"}}>
                  <div>
                    <p style={{fontSize:13,color:"rgba(255,255,255,0.7)",margin:"0 0 6px",fontWeight:700}}>每月經常性收入 (MRR)</p>
                    <p style={{fontSize:42,fontWeight:900,margin:"0 0 4px",letterSpacing:"-1px"}}>{"$" + mrr.toLocaleString()}</p>
                    <p style={{fontSize:14,color:"rgba(255,255,255,0.8)",margin:0,fontWeight:700}}>{"年化收入 ARR $" + arr.toLocaleString()}</p>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <p style={{fontSize:13,color:"rgba(255,255,255,0.7)",margin:"0 0 4px",fontWeight:700}}>付費轉換率</p>
                    <p style={{fontSize:28,fontWeight:900,margin:0}}>{convRate + "%"}</p>
                  </div>
                </div>
                {/* Weekly platform-revenue sparkline (last 12 weeks) so
                    the operator sees momentum, not just a static MRR. */}
                {(() => {
                  const max = Math.max(...weeklyRev, 1);
                  return (
                    <div style={{marginTop:18,position:"relative"}}>
                      <div style={{display:"flex",alignItems:"flex-end",gap:3,height:36}}>
                        {weeklyRev.map((v, i) => {
                          const h = max > 0 ? Math.max(2, Math.round((v / max) * 36)) : 2;
                          const isCurrent = i === weeklyRev.length - 1;
                          return (
                            <div key={i} style={{flex:1,height:h,background: isCurrent ? "#FBBF24" : "rgba(255,255,255,0.45)",borderRadius:3,transition:"height 0.4s"}} title={"週 " + (12 - i) + "：$" + v.toLocaleString()} />
                          );
                        })}
                      </div>
                      <p style={{fontSize:10,color:"rgba(255,255,255,0.55)",margin:"6px 0 0",fontWeight:700,letterSpacing:0.5}}>近 12 週平台已完成預約收入（黃色=本週）</p>
                    </div>
                  );
                })()}
              </div>

              {/* Stats grid — delta chips on appointment-derived tiles
                  compare this month vs. last month. Plan-based numbers
                  (商戶 / 付費 / 累計收入) don't have a historical snapshot
                  to compare against, so they stay un-decorated. */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  {icon:Users,label:"總商戶",value:state.tenants.length,color:"#7C3AED"},
                  {icon:Crown,label:"付費商戶",value:paidCount,color:"#D97706"},
                  {icon:UserCheck,label:"30 日活躍",value:activeCount,color:"#10B981"},
                  {icon:DollarSign,label:"平台累計收入",value:"$"+totalLifetimePaid.toLocaleString(),color:"#10B981"},
                  {icon:TrendingUp,label:"本月營業額",value:"$"+thisMonthRev.toLocaleString(),color:"#3B82F6",delta:revDelta},
                  {icon:Calendar,label:"本月預約",value:thisMonthApts.length,color:"#F97316",delta:aptDelta},
                ].map((s,i)=>(
                  <div key={i} style={{background:"rgba(255,255,255,0.95)",borderRadius:20,padding:"14px 12px",textAlign:"center",border:"1px solid #F3F3F8",boxShadow:"0 4px 16px rgba(0,0,0,0.04)",position:"relative"}}>
                    <div style={{width:36,height:36,borderRadius:12,background:s.color+"15",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 8px"}}><s.icon size={18} color={s.color} strokeWidth={2.5} /></div>
                    <p style={{fontSize:18,fontWeight:900,color:"#4A4A68",margin:"0 0 2px"}}>{s.value}</p>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{s.label}</p>
                    {s.delta && (
                      <span style={{display:"inline-block",marginTop:4,padding:"1px 7px",borderRadius:99,background:s.delta.color+"15",color:s.delta.color,fontSize:10,fontWeight:900}} title="vs. 上月">
                        {s.delta.txt}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Plan distribution + revenue share */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                <Card style={{padding:20}}>
                  <h4 style={{fontWeight:900,fontSize:15,margin:"0 0 14px",color:"#4A4A68"}}>方案分佈</h4>
                  {["free","basic","pro","enterprise"].map(p => {
                    const count = state.tenants.filter(t => t.plan === p).length;
                    const pct = Math.round((count / Math.max(state.tenants.length, 1)) * 100);
                    const colors = { free:"#AFAFC4", basic:"#3B82F6", pro:"#7C3AED" };
                    return <div key={p} style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}><span style={{color:"#4A4A68",fontWeight:800}}>{PLANS[p].name}</span><span style={{color:"#8A8A9E",fontWeight:700}}>{count + " (" + pct + "%)"}</span></div>
                      <div style={{height:8,background:"#F3F3F8",borderRadius:99}}><div style={{height:8,borderRadius:99,background:colors[p],width:pct+"%",transition:"width 0.5s"}} /></div>
                    </div>;
                  })}
                </Card>
                <Card style={{padding:20}}>
                  <h4 style={{fontWeight:900,fontSize:15,margin:"0 0 14px",color:"#4A4A68"}}>平台概覽</h4>
                  {[["總客戶數",totalCustomers,"👥"],["總員工數",totalStaff,"✂️"],["平均客單價","$"+avgRevPerTenant,"💰"],["總預約量",allApts.length,"📅"]].map(([l,v,ic])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #F3F3F8"}}>
                      <span style={{fontSize:13,color:"#6A6A85",fontWeight:700}}>{ic + " " + l}</span>
                      <span style={{fontSize:14,fontWeight:900,color:"#4A4A68"}}>{v}</span>
                    </div>
                  ))}
                </Card>
              </div>

              {/* Top shops by revenue */}
              <Card style={{padding:20,marginBottom:16}}>
                <h4 style={{fontWeight:900,fontSize:15,margin:"0 0 14px",color:"#4A4A68"}}>商戶營收排行 🏆</h4>
                {/* Revenue formula matches the per-shop EOD report:
                    base + addons + surcharges − discount. The old version
                    used base + addons only, which inflated discounted
                    bookings and ignored surcharges entirely. */}
                {(() => {
                  const revByTenant = new Map();
                  for (const a of allApts) {
                    if (a.status !== "done") continue;
                    revByTenant.set(a.tenantId, (revByTenant.get(a.tenantId) || 0) + aptRevenue(a));
                  }
                  return state.tenants.slice().sort((a,b) => (revByTenant.get(b.id) || 0) - (revByTenant.get(a.id) || 0)).slice(0,5).map((t,i) => {
                    const rev = revByTenant.get(t.id) || 0;
                    const medals = ["🥇","🥈","🥉","4️⃣","5️⃣"];
                  return <div key={t.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:i<4?"1px solid #F3F3F8":"none"}}>
                    <span style={{fontSize:18,width:28,textAlign:"center"}}>{medals[i]}</span>
                    <div style={{flex:1}}>
                      <p style={{fontWeight:800,fontSize:14,margin:"0 0 2px",color:"#4A4A68"}}>{t.name}</p>
                      <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{PLANS[t.plan].name + " · " + (t.staffIds?.length||0) + "員 · " + (t.customerIds?.length||0) + "客"}</p>
                    </div>
                    <span style={{fontWeight:900,fontSize:16,color:"#10B981"}}>{"$" + rev.toLocaleString()}</span>
                  </div>;
                  });
                })()}
              </Card>
            </>;
          })()}
        </>}

        {tab === "tenants" && <>
          {/* Search + Filter */}
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <div style={{position:"relative",flex:1}}><Search size={16} style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#AFAFC4"}} /><input style={{width:"100%",border:"2px solid #E0E0F0",borderRadius:20,padding:"12px 12px 12px 40px",fontSize:14,fontWeight:600,boxSizing:"border-box",outline:"none",background:"#FFFDFD",color:"#4A4A68"}} placeholder={i18nT("ph.searchTenant")} value={tenantSearch} onChange={e=>setTenantSearch(e.target.value)} /></div>
            <Sel value={tenantFilter} onChange={e=>setTenantFilter(e.target.value)} style={{width:110}}>
              <option value="all">全部方案</option><option value="free">免費版</option><option value="basic">基本版</option><option value="pro">專業版</option><option value="enterprise">旗艦版</option>
            </Sel>
          </div>

          {state.tenants
            .filter(t => (tenantFilter==="all" || t.plan===tenantFilter) && (!tenantSearch || t.name.toLowerCase().includes(tenantSearch.toLowerCase())))
            .map(t => {
            const plan = PLANS[t.plan]; const sub = t.subscription || {};
            const tApts = allApts.filter(a => a.tenantId === t.id);
            const tRev = tApts.filter(a => a.status === "done").reduce((s, a) => s + aptRevenue(a), 0);
            const daysSinceJoin = sub.joinDate ? Math.round((new Date() - new Date(sub.joinDate)) / 86400000) : null;
            const daysToRenewal = sub.nextRenewalDate ? Math.round((new Date(sub.nextRenewalDate) - new Date()) / 86400000) : null;
            const statusColors = { active:["#10B981","#F0FDF4","#BBF7D0"], cancelled:["#EF4444","#FEF2F2","#FECACA"], past_due:["#F59E0B","#FFFBEB","#FEF3C7"], trialing:["#3B82F6","#EFF6FF","#BFDBFE"] };
            const [sc,sbg,sborder] = statusColors[sub.status] || statusColors.active;
            const statusLabel = { active:"活躍", cancelled:"已取消", past_due:"逾期未付", trialing:"試用中" };

            return <Card key={t.id} style={{padding:0,marginBottom:16,overflow:"hidden"}}>
              {/* Header */}
              <div style={{padding:"20px 20px 16px",borderBottom:"2px solid #F3F3F8"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:52,height:52,borderRadius:18,background:"linear-gradient(135deg,#F3E8FF,#E0E7FF)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,color:"#7C3AED",fontSize:24}}>{t.name[0]}</div>
                    <div>
                      <p style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68"}}>{t.name}</p>
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <Badge color={plan.color} bg={t.plan==="pro"?"#F5F3FF":t.plan==="basic"?"#EFF6FF":"#F3F3F8"}>{plan.name}</Badge>
                        <span style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:sbg,color:sc,fontWeight:800,border:"1px solid "+sborder}}>{statusLabel[sub.status]||"活躍"}</span>
                      </div>
                    </div>
                  </div>
                  <span style={{fontSize:24,fontWeight:900,color:"#10B981"}}>{"$" + (sub.totalPaid||0).toLocaleString()}</span>
                </div>
                <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700,display:"flex",alignItems:"center",gap:6}}><MapPin size={14} strokeWidth={2.5} />{t.address || "未提供地址"}</p>
              </div>

              {/* Subscription timeline */}
              <div style={{padding:"16px 20px",background:"#FAFAFE"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                  {[
                    ["加入日期", sub.joinDate || "—", "📅", daysSinceJoin !== null ? daysSinceJoin + " 天前" : ""],
                    ["帳期起始", sub.currentPeriodStart || "—", "▶️", ""],
                    ["帳期結束", sub.currentPeriodEnd || "—", "⏹️", ""],
                    ["下次續約", sub.nextRenewalDate || "—", "🔄", daysToRenewal !== null ? (daysToRenewal > 0 ? daysToRenewal + " 天後" : "已到期 ⚠️") : ""],
                  ].map(([label,val,ic,hint],i)=>(
                    <div key={i} style={{textAlign:"center"}}>
                      <p style={{fontSize:11,color:"#AFAFC4",margin:"0 0 4px",fontWeight:700}}>{ic + " " + label}</p>
                      <p style={{fontSize:13,fontWeight:800,color:"#4A4A68",margin:"0 0 2px"}}>{val}</p>
                      {hint && <p style={{fontSize:10,color:hint.includes("⚠️")?"#EF4444":"#8A8A9E",margin:0,fontWeight:700}}>{hint}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats row */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:0,borderTop:"2px solid #F3F3F8"}}>
                {[
                  ["預約", tApts.length, "#7C3AED"],
                  ["營業額", "$"+tRev.toLocaleString(), "#10B981"],
                  ["員工", (t.staffIds?.length||0)+"人", "#3B82F6"],
                  ["客戶", (t.customerIds?.length||0)+"人", "#F97316"],
                ].map(([l,v,c],i)=>(
                  <div key={i} style={{padding:"14px 8px",textAlign:"center",borderRight:i<3?"1px solid #F3F3F8":"none"}}>
                    <p style={{fontSize:16,fontWeight:900,color:c,margin:"0 0 2px"}}>{v}</p>
                    <p style={{fontSize:11,color:"#8A8A9E",margin:0,fontWeight:700}}>{l}</p>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{padding:"12px 20px",display:"flex",gap:8,borderTop:"2px solid #F3F3F8"}}>
                <Btn variant="secondary" size="sm" style={{flex:1}} onClick={() => setUpgradeSheet(t.id)}><Crown size={14} /> 方案</Btn>
                <Btn variant="secondary" size="sm" style={{flex:1}} onClick={() => setBillingSheet(t.id)}><DollarSign size={14} /> 帳單</Btn>
                <Btn variant="ghost" size="sm" style={{flex:1}} onClick={() => {
                  const newStatus = sub.status === "active" ? "cancelled" : "active";
                  setState(s => ({...s, tenants:s.tenants.map(x=>x.id===t.id?{...x,subscription:{...x.subscription,status:newStatus,cancelledAt:newStatus==="cancelled"?dateNow():null}}:x)}));
                  toast(newStatus==="cancelled"?"商戶已標記為取消":"商戶已重新啟用");
                }}>{sub.status==="active"?<><UserX size={14}/>停用</>:<><UserCheck size={14}/>啟用</>}</Btn>
                <Btn variant="danger" size="sm" style={{flex:1}} onClick={async () => {
                  const c1 = window.prompt(`永久刪除商戶「${t.name}」？\n所有預約、客戶、毛孩、員工資料將連帶刪除。\n\n輸入 DELETE 以確認：`);
                  if (c1 !== "DELETE") { toast("已取消"); return; }
                  try {
                    const { supabase } = await import("./lib/supabaseClient.js");
                    const { error } = await supabase.from("tenants").delete().eq("id", t.id);
                    if (error) throw error;
                    setState(s => ({ ...s, tenants: s.tenants.filter(x => x.id !== t.id) }));
                    toast(i18nT("tx.shopDeleted"));
                  } catch (e) { toast(e?.message || "刪除失敗（可能需要超級管理員 RLS 權限）", "error"); }
                }}><Trash2 size={14}/> 刪除</Btn>
              </div>
            </Card>;
          })}

          {/* Billing history bottom sheet */}
          <BottomSheet open={!!billingSheet} onClose={() => setBillingSheet(null)} title="帳單記錄">
            {billingSheet && (() => {
              const t = state.tenants.find(x => x.id === billingSheet);
              const bills = t?.subscription?.billingHistory || [];
              return <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{background:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",borderRadius:24,padding:20,border:"2px solid #DDD6FE",textAlign:"center",marginBottom:8}}>
                  <p style={{fontSize:13,color:"#7C3AED",margin:"0 0 4px",fontWeight:800}}>累計付款</p>
                  <p style={{fontSize:36,fontWeight:900,color:"#4C1D95",margin:"0 0 4px"}}>{"$" + (t?.subscription?.totalPaid||0).toLocaleString()}</p>
                  <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{"共 " + (t?.subscription?.lifetimeMonths||0) + " 個月"}</p>
                </div>
                {bills.length === 0 ? <EmptyState icon={DollarSign} title="暫無帳單記錄" /> : bills.map(b => (
                  <div key={b.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:20,background:"#FFFDFD",border:"2px solid #F3F3F8"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <div style={{width:40,height:40,borderRadius:14,background:b.status==="paid"?"#F0FDF4":b.status==="free"?"#F3F3F8":"#FEF2F2",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {b.status==="paid"?<Check size={18} color="#10B981" strokeWidth={3}/>:b.status==="free"?<Tag size={18} color="#AFAFC4" strokeWidth={2.5}/>:<AlertTriangle size={18} color="#EF4444" strokeWidth={2.5}/>}
                      </div>
                      <div>
                        <p style={{fontWeight:800,fontSize:14,margin:"0 0 2px",color:"#4A4A68"}}>{PLANS[b.plan]?.name || b.plan}</p>
                        <p style={{fontSize:12,color:"#8A8A9E",margin:0,fontWeight:700}}>{b.date + " · " + b.method}</p>
                      </div>
                    </div>
                    <span style={{fontWeight:900,fontSize:16,color:b.amount>0?"#10B981":"#AFAFC4"}}>{"$" + b.amount}</span>
                  </div>
                ))}
              </div>;
            })()}
          </BottomSheet>

          <BottomSheet open={!!upgradeSheet} onClose={() => setUpgradeSheet(null)} title="調整商戶方案">
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {Object.entries(getPlans(state.platformConfig)).map(([key, p]) => {
                const t = state.tenants.find(x => x.id === upgradeSheet); const isCurrent = t?.plan === key;
                const price = getPlanPrice(key, t?.currency || "HKD", state.platformConfig);
                return <div key={key} onClick={() => up(upgradeSheet, key)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",borderRadius:24,border:"2px solid "+(isCurrent?"#7C3AED":"var(--c-light)"),background:isCurrent?"#F5F3FF":"#FFFDFD",cursor:"pointer",transition:"all 0.2s"}}>
                  <div><p style={{fontWeight:900,fontSize:16,margin:"0 0 4px",color:isCurrent?"#5B21B6":"#4A4A68"}}>{p.name}</p><p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>{price.symbol+price.amount+"/月 · 上限 "+p.maxStaff+" 員"}</p></div>
                  {isCurrent ? <Badge color="#7C3AED" bg="#E0E7FF">目前方案</Badge> : <ChevronRight size={20} color="#AFAFC4" strokeWidth={3} />}
                </div>;
              })}
              {/* 收銀 POS add-on — platform switch (a DB trigger ignores anyone but super). */}
              {(() => {
                const t = state.tenants.find(x => x.id === upgradeSheet);
                if (!t) return null;
                return (
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"16px 20px",borderRadius:24,border:"2px solid "+(t.posEnabled?"#10B981":"var(--c-light)"),background:t.posEnabled?"#ECFDF5":"#FFFDFD",marginTop:4}}>
                    <ShoppingBag size={22} color={t.posEnabled ? "#10B981" : "#AFAFC4"} />
                    <div style={{flex:1}}>
                      <p style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#4A4A68"}}>收銀 POS</p>
                      <p style={{fontSize:12.5,color:"#8A8A9E",margin:0,fontWeight:700}}>{t.posEnabled ? "已開通 — 店主見到「收銀」" : "未開通"}{t.plan && t.plan !== "free" ? "（付費方案已包含；轉做免費版會自動關閉）" : "（免費版手動開通 = 基本收銀）"}</p>
                    </div>
                    <Toggle on={!!t.posEnabled} onChange={async (v) => {
                      try {
                        await setTenantPosEnabled(t.id, v);
                        setState(s => ({ ...s, tenants: s.tenants.map(x => x.id === t.id ? { ...x, posEnabled: v } : x) }));
                        toast(v ? "已開通收銀 POS" : "已關閉收銀 POS");
                      } catch (e) { toast("更新失敗：" + (e?.message || e), "error"); }
                    }} />
                  </div>
                );
              })()}
            </div>
          </BottomSheet>
        </>}

        {/* ── SUPPORT TICKETS TAB (all tenants) ─── */}
        {tab === "claims" && <ShopClaimsPanel />}
        {tab === "followup" && <ShopFollowupPanel currentUserId={user?.id} />}
        {tab === "saleschat" && <SalesChatPanel />}
        {tab === "support" && <SupportPanel mode="platform" />}

        {/* ── AUTO-NOTIFICATIONS TAB ─── */}
        {tab === "automations" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <h2 style={{fontWeight:900,fontSize:20,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>自動通知設定 ⚡ <HelpTip topic="messaging" /></h2>
              <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700}}>設定各類自動通知的觸發條件和發送方式</p>
            </div>
          </div>

          <div style={{background:"linear-gradient(135deg,#F5F3FF,#EDE9FE)",borderRadius:24,padding:20,border:"2px solid #DDD6FE"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <div style={{width:44,height:44,background:"#7C3AED",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center"}}><Zap size={22} color="#FBBF24" strokeWidth={2.5} /></div>
              <div>
                <p style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#4C1D95"}}>通知引擎概覽</p>
                <p style={{fontSize:13,color:"#6D28D9",margin:0,fontWeight:700}}>{autoNotifs.filter(n=>n.enabled).length + " / " + autoNotifs.length + " 個通知已啟用"}</p>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
              <div style={{background:"rgba(255,255,255,0.8)",borderRadius:16,padding:"12px 10px",textAlign:"center"}}>
                <p style={{fontSize:11,color:"#7C3AED",margin:"0 0 2px",fontWeight:800}}>Email</p>
                <p style={{fontSize:18,fontWeight:900,color:"#4C1D95",margin:0}}>{autoNotifs.filter(n=>n.enabled&&n.channel==="email").length}</p>
              </div>
              <div style={{background:"rgba(255,255,255,0.8)",borderRadius:16,padding:"12px 10px",textAlign:"center"}}>
                <p style={{fontSize:11,color:"#3B82F6",margin:"0 0 2px",fontWeight:800}}>SMS</p>
                <p style={{fontSize:18,fontWeight:900,color:"#1D4ED8",margin:0}}>{autoNotifs.filter(n=>n.enabled&&n.channel==="sms").length}</p>
              </div>
              <div style={{background:"rgba(255,255,255,0.8)",borderRadius:16,padding:"12px 10px",textAlign:"center"}}>
                <p style={{fontSize:11,color:"#D97706",margin:"0 0 2px",fontWeight:800}}>付費限定</p>
                <p style={{fontSize:18,fontWeight:900,color:"#92400E",margin:0}}>{autoNotifs.filter(n=>n.paidOnly).length}</p>
              </div>
            </div>
          </div>

          {autoNotifs.map((n, idx) => (
            <Card key={n.id} style={{padding:20,border:n.enabled?"2px solid var(--c-border)":"2px dashed #EAEAF2",opacity:n.enabled?1:0.7}}>
              {(() => {
                // Legacy "whatsapp" channel migrated to "email" — platform
                // no longer auto-sends WhatsApp.
                const ch = n.channel === "whatsapp" ? "email" : n.channel;
                const isEmail = ch === "email";
                const bg = isEmail ? "#FEF3F2" : "#EFF6FF";
                const fg = isEmail ? "#9F1239" : "#1E40AF";
                const label = isEmail ? "Email" : "SMS";
                return (
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,flex:1}}>
                      <div style={{width:44,height:44,borderRadius:14,background:bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {isEmail ? <Mail size={22} color="#E11D48" strokeWidth={2.5} /> : <Phone size={22} color="#3B82F6" strokeWidth={2.5} />}
                      </div>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <p style={{fontWeight:900,fontSize:16,margin:0,color:"#4A4A68"}}>{n.name}</p>
                          {n.paidOnly && <Badge color="#D97706" bg="#FFFBEB">付費限定 💎</Badge>}
                        </div>
                        <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 4px",fontWeight:700}}>{n.description}</p>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:11,fontWeight:800,padding:"3px 8px",borderRadius:99,background:bg,color:fg}}>{label}</span>
                          <span style={{fontSize:11,fontWeight:800,padding:"3px 8px",borderRadius:99,background:"var(--c-lighter)",color:"var(--c-primary)"}}>{n.timing}</span>
                        </div>
                      </div>
                    </div>
                    <Toggle on={n.enabled} onChange={v => {
                      const updated = autoNotifs.map((x,i) => i===idx ? {...x, enabled:v} : x);
                      setState(s => ({...s, autoNotifications: updated}));
                      toast(v ? n.name + " 已啟用 ✅" : n.name + " 已停用");
                    }} />
                  </div>
                );
              })()}
              <div style={{display:"flex",gap:8}}>
                <Btn variant="secondary" size="sm" onClick={() => setEditAutoNotif(n)}><Edit size={14} /> 編輯設定</Btn>
                <Btn variant="ghost" size="sm" onClick={() => {
                  const cur = n.channel === "whatsapp" ? "email" : n.channel;
                  const next = cur === "email" ? "sms" : "email";
                  const updated = autoNotifs.map((x,i) => i===idx ? {...x, channel: next} : x);
                  setState(s => ({...s, autoNotifications: updated}));
                  toast("已切換為 " + (next === "email" ? "Email" : "SMS"));
                }}><MessageCircle size={14} /> {(n.channel==="whatsapp"||n.channel==="email")?"切換 SMS":"切換 Email"}</Btn>
              </div>
            </Card>
          ))}

          <Card style={{padding:24,background:"linear-gradient(135deg,#FFFBEB,#FEF3C7)",border:"2px solid #FDE68A"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <Crown size={24} color="#D97706" strokeWidth={2.5} />
              <div>
                <p style={{fontWeight:900,fontSize:16,margin:"0 0 2px",color:"#92400E"}}>付費方案限定功能</p>
                <p style={{fontSize:13,color:"#B45309",margin:0,fontWeight:700}}>以下自動通知僅限基本版及專業版使用</p>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {autoNotifs.filter(n=>n.paidOnly).map(n => (
                <div key={n.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"rgba(255,255,255,0.7)",borderRadius:16}}>
                  <span style={{fontSize:18}}>{n.trigger==="day_before"?"📅":n.trigger==="recurring_reminder"?"🔄":n.trigger==="vaccine_expiry"?"💉":n.trigger==="pet_birthday"?"🎂":"💌"}</span>
                  <div style={{flex:1}}>
                    <p style={{fontSize:14,fontWeight:800,margin:0,color:"#92400E"}}>{n.name}</p>
                    <p style={{fontSize:12,color:"#B45309",margin:0,fontWeight:600}}>{n.timing}</p>
                  </div>
                  <span style={{fontSize:12,fontWeight:800,color:n.enabled?"#10B981":"#AFAFC4"}}>{n.enabled?"啟用":"停用"}</span>
                </div>
              ))}
            </div>
          </Card>

          <BottomSheet open={!!editAutoNotif} onClose={() => setEditAutoNotif(null)} title={"編輯通知 — " + (editAutoNotif?.name || "")}>
            {editAutoNotif && <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:"var(--c-lighter)",borderRadius:20,padding:16,border:"2px solid var(--c-border)"}}>
                <p style={{fontSize:14,fontWeight:800,color:"#4A4A68",margin:"0 0 8px"}}>觸發條件</p>
                <p style={{fontSize:15,color:"var(--c-primary)",margin:0,fontWeight:700}}>{editAutoNotif.trigger === "booking_created" ? "客戶成功預約時" : editAutoNotif.trigger === "day_before" ? "預約前一天" : editAutoNotif.trigger === "grooming_done" ? "美容完成後" : editAutoNotif.trigger === "booking_cancelled" ? "預約取消後" : editAutoNotif.trigger === "recurring_reminder" ? "定期預約提醒日" : editAutoNotif.trigger === "vaccine_expiry" ? "疫苗到期前 30 天" : editAutoNotif.trigger === "pet_birthday" ? "寵物生日當天" : "最後到店 60 天後"}</p>
              </div>
              <Sel label="發送方式" value={editAutoNotif.channel === "whatsapp" ? "email" : editAutoNotif.channel} onChange={e => setEditAutoNotif(n => ({...n, channel:e.target.value}))}>
                <option value="email">Email 📧</option>
                <option value="sms">SMS 📱</option>
              </Sel>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:20,border:"2px solid " + (editAutoNotif.paidOnly ? "#FDE68A" : "var(--c-border)"),background:editAutoNotif.paidOnly ? "#FFFBEB" : "var(--c-lighter)"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <Crown size={18} color={editAutoNotif.paidOnly?"#D97706":"#AFAFC4"} strokeWidth={2.5} />
                  <span style={{fontSize:14,fontWeight:800,color:editAutoNotif.paidOnly?"#92400E":"#8A8A9E"}}>限付費方案</span>
                </div>
                <Toggle on={editAutoNotif.paidOnly} onChange={v => setEditAutoNotif(n => ({...n, paidOnly:v}))} />
              </div>
              {/* #27 Available merge tags — owner sees which dynamic fields
                  the template engine will substitute for THIS trigger so
                  they know what content the customer will receive. */}
              <details style={{background:"var(--c-lighter)",borderRadius:14,padding:"10px 14px",border:"2px solid var(--c-border)"}}>
                <summary style={{fontSize:12,fontWeight:800,color:"var(--c-primary)",cursor:"pointer"}}>📋 可用模板標籤（{({"booking_created":"預約確認","day_before":"提醒","grooming_done":"完成","booking_cancelled":"取消","vaccine_expiry":"疫苗","pet_birthday":"生日"})[editAutoNotif.trigger] || "通用"}）</summary>
                <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4}}>
                  {(() => {
                    const COMMON = [
                      ["{shopName}", "店舖名稱"],
                      ["{customerName}", "客戶姓名"],
                      ["{petName}", "毛孩名稱"],
                      ["{phone}", "客戶電話"],
                    ];
                    const PER_TRIGGER = {
                      booking_created: [["{date}","預約日期"],["{time}","預約時間"],["{service}","服務名稱"],["{deposit}","訂金金額"],["{address}","店舖地址"]],
                      booking_rescheduled: [["{date}","新日期"],["{time}","新時間"]],
                      day_before: [["{date}","明日日期"],["{time}","預約時間"],["{service}","服務名稱"]],
                      checkin: [["{time}","到店時間"]],
                      grooming_started: [["{time}","開始時間"]],
                      grooming_done: [["{skin}","皮膚"],["{ear}","耳朵"],["{teeth}","牙齒"],["{mood}","情緒"],["{notes}","美容師叮嚀"],["{totalPaid}","應收金額"]],
                      booking_cancelled: [["{date}","原日期"],["{cancelReason}","取消原因"]],
                      vaccine_expiry: [["{vaccineDate}","上次疫苗日"]],
                      pet_birthday: [["{petAge}","年齡"]],
                    };
                    const list = [...COMMON, ...(PER_TRIGGER[editAutoNotif.trigger] || [])];
                    return list.map(([tag, desc]) => (
                      <div key={tag} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 8px",background:"#fff",borderRadius:8}}>
                        <code style={{fontSize:11,fontWeight:900,color:"var(--c-primary)",fontFamily:"ui-monospace,SF Mono,monospace"}}>{tag}</code>
                        <span style={{fontSize:11,fontWeight:700,color:"#6A6A85"}}>{desc}</span>
                      </div>
                    ));
                  })()}
                </div>
                <p style={{fontSize:10,fontWeight:700,color:"#8A8A9E",margin:"8px 0 0",lineHeight:1.5}}>系統會自動將以上標籤替換為客戶／預約嘅實際資料</p>
              </details>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:20,border:"2px solid " + (editAutoNotif.enabled ? "#BBF7D0" : "#FECACA"),background:editAutoNotif.enabled ? "#F0FDF4" : "#FEF2F2"}}>
                <span style={{fontSize:14,fontWeight:800,color:editAutoNotif.enabled?"#166534":"#991B1B"}}>{editAutoNotif.enabled?"通知已啟用":"通知已停用"}</span>
                <Toggle on={editAutoNotif.enabled} onChange={v => setEditAutoNotif(n => ({...n, enabled:v}))} />
              </div>
              <Btn onClick={() => {
                const updated = autoNotifs.map(x => x.id === editAutoNotif.id ? editAutoNotif : x);
                setState(s => ({...s, autoNotifications: updated}));
                toast("通知設定已儲存 ✅");
                setEditAutoNotif(null);
              }} style={{width:"100%",padding:"16px"}}><Save size={18} strokeWidth={2.5} /> 儲存設定</Btn>
            </div>}
          </BottomSheet>
        </div>}

        {tab === "announce" && <>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>全站公告管理 <HelpTip content={{title:"全站公告 📢",body:"超級管理員可以發佈系統公告，所有店主 / 員工會喺登入時見到。用嚟發佈版本更新、系統維護、推廣等訊息。"}} /></h2>
            <Btn size="sm" onClick={() => setAnnounceForm({ title:"", body:"" })}><Plus size={16} strokeWidth={3} /> 發布公告</Btn>
          </div>
          {(state.announcements||[]).length === 0 && <EmptyState icon={Megaphone} title="暫無公告" sub="發布公告後會推送通知給所有客戶" />}
          {(state.announcements||[]).slice().reverse().map(a => (
            <Card key={a.id} style={{padding:20,marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{flex:1}}><p style={{fontWeight:900,fontSize:16,margin:"0 0 6px",color:"#4A4A68"}}>{a.title}</p><p style={{fontSize:14,color:"#6A6A85",margin:"0 0 8px",fontWeight:600,lineHeight:1.5}}>{a.body}</p><p style={{fontSize:12,color:"#AFAFC4",margin:0,fontWeight:800}}>{a.date}</p></div>
                <button onClick={() => setState(s => ({ ...s, announcements: s.announcements.filter(x => x.id !== a.id) }))} style={{background:"#FEF2F2",border:"none",cursor:"pointer",padding:8,borderRadius:99,display:"flex"}}><Trash2 size={16} color="#EF4444" strokeWidth={2.5} /></button>
              </div>
            </Card>
          ))}
          <BottomSheet open={!!announceForm} onClose={() => setAnnounceForm(null)} title="發布系統公告">
            {announceForm && <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <Input label="公告標題 📢" value={announceForm.title} onChange={e => setAnnounceForm(f => ({ ...f, title:e.target.value }))} placeholder="例：系統維護通知" />
              <div><p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>公告內容 📝</p><textarea style={{width:"100%",border:"2px solid var(--c-light)",borderRadius:20,padding:"16px",fontSize:15,fontWeight:600,height:120,resize:"none",boxSizing:"border-box",outline:"none",color:"#4A4A68",transition:"border 0.3s"}} placeholder="輸入公告內容..." value={announceForm.body} onChange={e => setAnnounceForm(f => ({ ...f, body:e.target.value }))} onFocus={e=>e.target.style.borderColor="var(--c-grad1)"} onBlur={e=>e.target.style.borderColor="var(--c-light)"} /></div>
              <div>
                <p style={{fontSize:13,color:"#8A8A9E",marginBottom:6,fontWeight:700}}>{T("發送對象","Audience")} 🎯</p>
                <div style={{display:"flex",gap:8}}>
                  {[["both",T("全部","All")],["shop",T("店主","Shops")],["customer",T("客戶","Customers")]].map(([val,label]) => {
                    const on = (announceForm.audience || "both") === val;
                    return <button key={val} onClick={() => setAnnounceForm(f => ({ ...f, audience: val }))} style={{flex:1,padding:"10px",borderRadius:12,border:"2px solid "+(on?"var(--c-primary)":"var(--c-light)"),background:on?"var(--c-primary)":"#fff",color:on?"#fff":"#4A4A68",fontWeight:800,fontSize:14,cursor:"pointer"}}>{label}</button>;
                  })}
                </div>
              </div>
              {/* 📧 Email leg — owners also get the announcement by email. */}
              {(announceForm.audience || "both") !== "customer" && (
                <div style={{border:"2px solid var(--c-light)",borderRadius:18,padding:14,display:"flex",flexDirection:"column",gap:12}}>
                  <label style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,cursor:"pointer"}}>
                    <span>
                      <span style={{display:"block",fontSize:14,fontWeight:900,color:"#4A4A68"}}>📧 同時 Email 通知店主</span>
                      <span style={{display:"block",fontSize:12,fontWeight:700,color:"#8A8A9E",marginTop:2}}>除咗 App 內通知，亦寄一封設計好嘅電郵</span>
                    </span>
                    <Toggle on={announceForm.emailOn !== false} onChange={v => setAnnounceForm(f => ({ ...f, emailOn: v }))} />
                  </label>
                  {announceForm.emailOn !== false && (
                    <AnnouncementEmailEditor value={announceForm.email} onChange={email => setAnnounceForm(f => ({ ...f, email }))}
                      fallback={{ title: announceForm.title, body: announceForm.body }} toast={toast} />
                  )}
                </div>
              )}
              <Btn onClick={saveAnnounce} disabled={announceSending} style={{width:"100%",padding:"16px",marginTop:8}}><Megaphone size={18} strokeWidth={2.5} /> {announceSending ? "寄送中…" : ((announceForm.audience || "both") !== "customer" && announceForm.emailOn !== false ? "確認發布 + 寄出電郵" : "確認發布")}</Btn>
            </div>}
          </BottomSheet>
        </>}

        {tab === "plans" && (() => {
          const activePlans = getPlans(state.platformConfig);
          const baseCurrency = state.platformConfig?.basePlanCurrency || "HKD";
          const baseSymbol = CURRENCY_SYMBOLS[baseCurrency] || "$";
          const patchPlan = (key, patch) => {
            setState(s => {
              const cur = s.platformConfig?.plans?.[key] || {};
              return { ...s, platformConfig: { ...(s.platformConfig || {}), plans: { ...(s.platformConfig?.plans || {}), [key]: { ...cur, ...patch } } } };
            });
          };
          return (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:"#EFF6FF",border:"2px solid #BFDBFE",borderRadius:14,padding:"12px 14px"}}>
                <p style={{fontSize:13,fontWeight:800,color:"#1E40AF",margin:"0 0 4px"}}>💱 計價基礎貨幣：{baseCurrency} ({baseSymbol})</p>
                <p style={{fontSize:11,color:"#1E40AF",margin:0,fontWeight:700,opacity:0.85}}>下方輸入嘅價錢以呢個貨幣為準。店家睇到嘅價格會用「平台設定 → 匯率」自動換算成佢哋自己嘅貨幣。</p>
              </div>
              <div style={{background:"#F0FDF4",border:"2px solid #BBF7D0",borderRadius:14,padding:"12px 14px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{fontSize:13,fontWeight:800,color:"#166534"}}>🗓 年繳優惠：每年免費</span>
                <input
                  type="number" min="0" max="12"
                  value={state.platformConfig?.annualDiscountMonths ?? 2}
                  onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), annualDiscountMonths: Math.max(0, Math.min(12, Number(e.target.value) || 0)) } }))}
                  style={{width:56,border:"1.5px solid #BBF7D0",borderRadius:8,padding:"4px 8px",fontSize:14,fontWeight:800,textAlign:"right",outline:"none",color:"#166534"}}
                />
                <span style={{fontSize:13,fontWeight:800,color:"#166534"}}>個月</span>
                <span style={{fontSize:11,fontWeight:700,color:"#16A34A",opacity:0.85}}>（年繳 = 月費 × (12 − 此數)。設 0 即無年繳優惠）</span>
              </div>
              {Object.entries(activePlans).map(([key, p]) => (
                <Card key={key} style={{padding:24,border:key==="pro"?"2px solid #D8B4FE":"1px solid rgba(255,255,255,0.8)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,gap:12,flexWrap:"wrap"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flex:"1 1 200px"}}>
                      {key === "pro" && <Crown size={24} color="#D97706" strokeWidth={2.5} />}
                      <input
                        value={p.name}
                        onChange={e => patchPlan(key, { name: e.target.value })}
                        style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",border:"1.5px solid transparent",borderRadius:8,padding:"4px 8px",outline:"none",background:"transparent",width:"100%"}}
                        onFocus={e => e.target.style.borderColor = "var(--c-light)"}
                        onBlur={e => e.target.style.borderColor = "transparent"}
                      />
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:18,fontWeight:800,color:"#AFAFC4"}}>{baseSymbol}</span>
                      <input
                        type="number" min="0"
                        value={p.price}
                        onChange={e => patchPlan(key, { price: Number(e.target.value) || 0 })}
                        style={{fontSize:24,fontWeight:900,color:key==="pro"?"var(--c-primary)":"#4A4A68",border:"1.5px solid var(--c-light)",borderRadius:10,padding:"4px 8px",width:90,textAlign:"right",outline:"none"}}
                      />
                      <span style={{fontSize:14,fontWeight:700,color:"#AFAFC4"}}>/月</span>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,padding:"8px 12px",background:"var(--c-lighter)",borderRadius:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,fontWeight:800,color:"#6A6A85"}}>最多員工</span>
                    <input
                      type="number" min="1"
                      value={p.maxStaff || 1}
                      onChange={e => patchPlan(key, { maxStaff: Math.max(1, Number(e.target.value) || 1) })}
                      style={{width:60,border:"1.5px solid var(--c-light)",borderRadius:8,padding:"4px 8px",fontSize:14,fontWeight:800,textAlign:"right",outline:"none",color:"#4A4A68"}}
                    />
                    <span style={{fontSize:12,fontWeight:700,color:"#AFAFC4"}}>人</span>
                    <span style={{fontSize:12,fontWeight:800,color:"#6A6A85",marginLeft:8}}>· 超額每位 {baseSymbol}</span>
                    <input
                      type="number" min="0"
                      value={p.seatOverage || 0}
                      onChange={e => patchPlan(key, { seatOverage: Math.max(0, Number(e.target.value) || 0) })}
                      style={{width:60,border:"1.5px solid var(--c-light)",borderRadius:8,padding:"4px 8px",fontSize:14,fontWeight:800,textAlign:"right",outline:"none",color:"#4A4A68"}}
                    />
                    <span style={{fontSize:12,fontWeight:700,color:"#AFAFC4"}}>/月</span>
                  </div>
                  <div style={{background:"#FFFDFD",borderRadius:20,padding:"0 16px",border:"2px solid var(--c-light)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap",fontSize:14,padding:"14px 0",borderBottom:"2px solid var(--c-light)"}}>
                      <span style={{color:"#6A6A85",fontWeight:700}}>收銀 POS</span>
                      <span style={{display:"flex",alignItems:"center",gap:6}}>
                        <select value={({1:"lite",2:"standard",3:"full"})[planPosRank(p)] || "off"}
                          onChange={e => patchPlan(key, { features: { ...p.features, pos: e.target.value === "off" ? false : e.target.value } })}
                          style={{border:"1.5px solid var(--c-light)",borderRadius:8,padding:"5px 8px",fontSize:13,fontWeight:800,color:"#4A4A68",background:"#fff"}}>
                          <option value="off">無</option><option value="lite">Lite（基本）</option><option value="standard">標準（庫存 / 退款 / 交更）</option><option value="full">全功能（採購 / AI）</option>
                        </select>
                        {planPosRank(p) > 0 && <>
                          <input type="number" min="0" value={p.posMaxProducts ?? (planPosMax(p) || 0)}
                            onChange={e => patchPlan(key, { posMaxProducts: Math.max(0, Number(e.target.value) || 0) })}
                            style={{width:70,border:"1.5px solid var(--c-light)",borderRadius:8,padding:"4px 8px",fontSize:14,fontWeight:800,textAlign:"right",outline:"none",color:"#4A4A68"}} />
                          <span style={{fontSize:12,fontWeight:700,color:"#AFAFC4"}}>件（0 = 無上限）</span>
                        </>}
                      </span>
                    </div>
                    {Object.entries(p.features).filter(([k]) => k !== "pos").map(([k, v], i, arr) => (
                      <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:14,padding:"14px 0",borderBottom:i===arr.length-1?"none":"2px solid var(--c-light)"}}>
                        <span style={{color:"#6A6A85",fontWeight:700}}>{k==="onlineBooking"?"網上預約":k==="scheduling"?"進階排班管理":k==="whatsapp"?"WhatsApp 一鍵通知":k==="wallet"?"虛擬儲值錢包":k==="analytics"?"進階數據分析":k==="loyalty"?"會員忠誠積分":k==="stampCard"?"電子積分卡":k==="autoNotify"?"電郵通知（確認/提醒）":k==="payroll"?"智能薪資計算":k==="removeBranding"?"移除品牌（白標）":k==="brandedApp"?"專屬品牌 PWA App":k==="aiConcierge"?"WhatsApp AI 客服":k}</span>
                        <Toggle on={!!v} onChange={nv => patchPlan(key, { features: { ...p.features, [k]: nv } })} />
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          );
        })()}

        {/* ── PLATFORM SETTINGS TAB ─── */}
        {tab === "platform" && <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <h2 style={{fontWeight:900,fontSize:20,margin:0,color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>平台設定 ⚙️ <HelpTip content={{title:"平台設定 ⚙️",body:"超級管理員可以調整全站默認值（域名、品牌、預設套餐、付款設定）。改動影響所有店鋪。"}} /></h2>

          {/* Domain Settings */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Globe size={20} color="var(--c-primary)" strokeWidth={2.5} /> 網域設定</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>修改後所有商戶的預約連結會自動更新</p>
            <Input label="網站網域" value={state.platformConfig?.domain||"pettobook.com"} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, domain:e.target.value, bookingUrlPrefix:e.target.value+"/shop/"}}))} placeholder="例：pettobook.com 或 yourdomain.com" />
            <div style={{background:"var(--c-lighter)",borderRadius:16,padding:"12px 16px",marginTop:12,border:"2px solid var(--c-border)"}}>
              <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 4px",fontWeight:800}}>預約連結格式預覽</p>
              <p style={{fontSize:14,color:"var(--c-primary)",fontWeight:900,margin:0}}>{(state.platformConfig?.domain||"pettobook.com")+"/shop/{shop-slug}"}</p>
            </div>
            <div style={{background:"#FFFBEB",borderRadius:16,padding:"12px 16px",marginTop:10,border:"2px solid #FEF3C7",display:"flex",gap:10,alignItems:"flex-start"}}>
              <AlertTriangle size={18} color="#D97706" strokeWidth={2.5} style={{flexShrink:0,marginTop:2}} />
              <p style={{fontSize:12,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.5}}>修改網域後請確保 DNS 已指向正確的伺服器，並在 Vercel 專案設定中新增自訂網域。</p>
            </div>
          </Card>

          {/* Platform social / contact — surfaced as buttons on the public
              homepage (/directory). Each button only appears when its field
              is filled. Instagram/Facebook accept either a handle or full URL. */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📣 {T("平台社交媒體 / 聯絡","Platform social / contact")}</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700,lineHeight:1.5}}>{T("填寫後會喺主頁顯示對應按鈕；留空則唔顯示。","Filled fields show as buttons on the homepage; blank ones are hidden.")}</p>
            <Input label={T("Instagram（帳號或連結）","Instagram (handle or URL)")} value={state.platformConfig?.instagram || ""} onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), instagram: e.target.value } }))} placeholder="pettobook / https://instagram.com/pettobook" />
            <div style={{height:10}} />
            <Input label={T("Facebook（專頁或連結）","Facebook (page or URL)")} value={state.platformConfig?.facebook || ""} onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), facebook: e.target.value } }))} placeholder="pettobook / https://facebook.com/pettobook" />
            <div style={{height:10}} />
            <Input label={T("聯絡電郵","Contact email")} type="email" value={state.platformConfig?.email || ""} onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), email: e.target.value } }))} placeholder="cs@pettobook.com" />
          </Card>

          {/* Platform SEO — superadmin-customizable home-page title/description/
              keywords. Injected server-side by api/home-og.js (read via the
              public_platform_seo RPC). Leave blank to use the built-in defaults. */}
          {(() => {
            const seo = state.platformConfig?.seo || {};
            const setSeo = (k, v) => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), seo: { ...((s.platformConfig || {}).seo || {}), [k]: v } } }));
            return (
              <Card style={{padding:24}}>
                <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>🔍 {T("平台 SEO（搜尋引擎）","Platform SEO")}</h3>
                <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700,lineHeight:1.5}}>{T("自訂主頁喺 Google 嘅標題、描述同關鍵字。留空就用預設值。改完按下面「儲存所有設定」。","Customize the home page's Google title, description & keywords. Blank = built-in defaults. Click Save below after editing.")}</p>
                <Input label={T("標題 (Title)","Title")} value={seo.title || ""} onChange={e => setSeo("title", e.target.value)} placeholder="PettoBook · 香港寵物美容預約平台" />
                <div style={{height:10}} />
                <div>
                  <p style={{fontSize:13,fontWeight:700,color:"#4A4A68",margin:"0 0 6px"}}>{T("描述 (Description)","Description")}</p>
                  <textarea value={seo.description || ""} onChange={e => setSeo("description", e.target.value)} placeholder={T("一兩句描述平台，約 150 字內","One or two sentences, ~150 chars")} style={{width:"100%",minHeight:70,padding:"10px 12px",borderRadius:12,border:"2px solid var(--c-border)",fontSize:14,fontWeight:600,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box"}} />
                </div>
                <div style={{height:10}} />
                <Input label={T("關鍵字 (Keywords，逗號分隔)","Keywords (comma-separated)")} value={seo.keywords || ""} onChange={e => setSeo("keywords", e.target.value)} placeholder="寵物美容,寵物美容預約,狗狗美容,貓貓美容" />
                <div style={{height:10}} />
                <Input label={T("分享圖片網址 (OG image URL)","OG image URL")} value={seo.ogImage || ""} onChange={e => setSeo("ogImage", e.target.value)} placeholder="https://pettobook.com/hero-grooming.png" />
              </Card>
            );
          })()}

          {/* Maintenance mode — when ON, customers cannot book (public booking
              page + in-app booking are blocked with a notice). Owners keep full
              access so they can keep operating during an upgrade. */}
          {(() => {
            const on = state.platformConfig?.maintenanceMode === true;
            return (
              <Card style={{padding:24, border: on ? "2px solid #F59E0B" : undefined, background: on ? "#FFFBEB" : undefined}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>🛠️ {T("維護模式","Maintenance mode")}</h3>
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{T("升級期間開啟：客戶將無法預約（會見到維護提示）。店主仍可正常使用。","Turn on during upgrades: customers can't book (they see a notice). Shop owners keep full access.")}</p>
                  </div>
                  <button onClick={() => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), maintenanceMode: !on } }))} style={{flexShrink:0, width:64, height:34, borderRadius:99, border:"none", cursor:"pointer", background: on ? "#F59E0B" : "#D1D5DB", position:"relative", transition:"background .2s"}}>
                    <span style={{position:"absolute", top:3, left: on ? 33 : 3, width:28, height:28, borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}} />
                  </button>
                </div>
                {on && (() => {
                  const scope = state.platformConfig?.maintenanceScope === "site" ? "site" : "booking";
                  const setScope = (v) => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), maintenanceScope: v } }));
                  return (
                    <div style={{margin:"12px 0 0"}}>
                      <p style={{fontSize:12,color:"#8A8A9E",marginBottom:6,fontWeight:800}}>{T("維護範圍","Scope")}</p>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>setScope("booking")} style={{flex:1,padding:"10px",borderRadius:12,border:"2px solid "+(scope==="booking"?"#F59E0B":"var(--c-light)"),background:scope==="booking"?"#F59E0B":"#fff",color:scope==="booking"?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{T("只停預約","Booking only")}</button>
                        <button onClick={()=>setScope("site")} style={{flex:1,padding:"10px",borderRadius:12,border:"2px solid "+(scope==="site"?"#F59E0B":"var(--c-light)"),background:scope==="site"?"#F59E0B":"#fff",color:scope==="site"?"#fff":"#4A4A68",fontWeight:800,fontSize:13,cursor:"pointer"}}>{T("整個網站","Whole site")}</button>
                      </div>
                      <div style={{fontSize:13,fontWeight:900,color:"#B45309",margin:"10px 0 0",padding:"8px 12px",background:"#FEF3C7",borderRadius:10}}>● {scope === "site" ? T("整個網站維護中（店主同客戶；管理員除外）","Whole site under maintenance (shops & customers; admin exempt)") : T("維護模式進行中 — 客戶預約已暫停","Maintenance in progress — customer booking paused")}</div>
                    </div>
                  );
                })()}
                <div style={{height:12}} />
                <Input label={T("維護提示訊息（客戶會見到）","Maintenance message (shown to customers)")} value={state.platformConfig?.maintenanceMessage || ""} onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), maintenanceMessage: e.target.value } }))} placeholder={T("例：系統升級中，暫停預約，約 30 分鐘後回復。","e.g. Upgrading — booking paused, back in ~30 min.")} />
              </Card>
            );
          })()}

          {/* RFQ marketplace toggle — enable the 出價搵店 / 詢價 feature when
              it's ready. When off, all entry points (owner inbox button +
              public/customer buttons) are hidden. */}
          {(() => {
            const on = state.platformConfig?.rfqEnabled === true;
            return (
              <Card style={{padding:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📨 {T("詢價市集（RFQ）","Request-a-quote (RFQ)")}</h3>
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{T("開啟後，客戶可「出價搵店」，店主可收詢價報價。未準備好可關閉，所有入口會隱藏。","When on, customers can post requests and shops can quote. Keep off until ready — all entry points stay hidden.")}</p>
                  </div>
                  <button onClick={() => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), rfqEnabled: !on } }))} style={{flexShrink:0, width:64, height:34, borderRadius:99, border:"none", cursor:"pointer", background: on ? "var(--c-primary)" : "#D1D5DB", position:"relative", transition:"background .2s"}}>
                    <span style={{position:"absolute", top:3, left: on ? 33 : 3, width:28, height:28, borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}} />
                  </button>
                </div>
                {on && <div style={{fontSize:13,fontWeight:900,color:"#047857",margin:"12px 0 0",padding:"8px 12px",background:"#D1FAE5",borderRadius:10}}>● {T("詢價功能已開啟","RFQ is live")}</div>}
              </Card>
            );
          })()}

          {/* SMS toggle — gate the SMS notification channel platform-wide.
              When off, shops only see Email (no SMS options anywhere) and the
              auto-notify engine routes everything to email. Turn on once the
              Twilio/SMS provider is configured and tested. */}
          {(() => {
            const on = state.platformConfig?.smsEnabled === true;
            return (
              <Card style={{padding:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>📱 {T("開放 SMS 通知","SMS notifications")}</h3>
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{T("開啟後，店主先見到 SMS 通知選項。未準備好（未設定 Twilio）請關閉，所有 SMS 選項會喺店主後台隱藏，自動通訊只用 Email。","When on, shops can use SMS notifications. Keep off until the SMS provider is configured — all SMS options stay hidden and auto-messaging uses email only.")}</p>
                  </div>
                  <button onClick={() => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), smsEnabled: !on } }))} style={{flexShrink:0, width:64, height:34, borderRadius:99, border:"none", cursor:"pointer", background: on ? "var(--c-primary)" : "#D1D5DB", position:"relative", transition:"background .2s"}}>
                    <span style={{position:"absolute", top:3, left: on ? 33 : 3, width:28, height:28, borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}} />
                  </button>
                </div>
                {on
                  ? <div style={{fontSize:13,fontWeight:900,color:"#047857",margin:"12px 0 0",padding:"8px 12px",background:"#D1FAE5",borderRadius:10}}>● {T("SMS 已開放畀店主","SMS is available to shops")}</div>
                  : <div style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"12px 0 0",padding:"8px 12px",background:"#FEF3C7",borderRadius:10}}>○ {T("SMS 已隱藏（店主只見 Email）","SMS hidden — shops see email only")}</div>}
              </Card>
            );
          })()}

          {/* WhatsApp AI concierge kill switch (platform-wide) + monthly reply
              cap. When off, the AI settings + auto-reply are hidden for every
              shop (the edge function also refuses to run). Cap is per-tenant,
              per calendar month; enforced server-side against message_log.
              Enterprise-plan shops only (see PLANS.aiConcierge). */}
          {(() => {
            const on = state.platformConfig?.aiConciergeEnabled === true;
            const cap = Number(state.platformConfig?.aiConciergeMonthlyCap) || 1500;
            return (
              <Card style={{padding:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>🤖 {T("開放 WhatsApp AI 客服","WhatsApp AI concierge")}</h3>
                    <p style={{fontSize:13,color:"#8A8A9E",margin:0,fontWeight:700,lineHeight:1.5}}>{T("開啟後，旗艦版店主先見到 AI 自動回覆設定。未設定 Gemini／Meta webhook 前請關閉。","When on, Enterprise shops can enable AI auto-reply. Keep off until Gemini + the Meta webhook are configured.")}</p>
                  </div>
                  <button onClick={() => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), aiConciergeEnabled: !on } }))} style={{flexShrink:0, width:64, height:34, borderRadius:99, border:"none", cursor:"pointer", background: on ? "var(--c-primary)" : "#D1D5DB", position:"relative", transition:"background .2s"}}>
                    <span style={{position:"absolute", top:3, left: on ? 33 : 3, width:28, height:28, borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}} />
                  </button>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0 0"}}>
                  <span style={{fontSize:13,fontWeight:800,color:"#6A6A85"}}>{T("每店每月 AI 回覆上限","Monthly AI replies per shop")}</span>
                  <input type="number" min="0" step="50" value={cap}
                    onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), aiConciergeMonthlyCap: Math.max(0, Number(e.target.value) || 0) } }))}
                    style={{width:90,border:"1.5px solid var(--c-light)",borderRadius:8,padding:"6px 10px",fontSize:14,fontWeight:800,textAlign:"right",outline:"none",color:"#4A4A68"}} />
                  <span style={{fontSize:12,fontWeight:700,color:"#AFAFC4"}}>{T("則／月","/mo")}</span>
                </div>
                {on
                  ? <div style={{fontSize:13,fontWeight:900,color:"#047857",margin:"12px 0 0",padding:"8px 12px",background:"#D1FAE5",borderRadius:10}}>● {T("AI 客服已開放（旗艦版）","AI concierge live (Enterprise)")}</div>
                  : <div style={{fontSize:13,fontWeight:900,color:"#92400E",margin:"12px 0 0",padding:"8px 12px",background:"#FEF3C7",borderRadius:10}}>○ {T("AI 客服已關閉（全平台隱藏）","AI concierge off — hidden platform-wide")}</div>}

                {/* Operator setup: the three edge-function secrets + Meta webhook
                    wiring. Shown here so it lives next to the kill switch. */}
                <details style={{marginTop:14,background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:12}}>
                  <summary style={{cursor:"pointer",listStyle:"none",padding:"11px 14px",fontSize:13,fontWeight:900,color:"#5B21B6",display:"flex",alignItems:"center",gap:8}}>
                    <HelpCircle size={16} strokeWidth={2.5} /> {T("設定步驟：Gemini 金鑰、App Secret、Webhook","Setup: Gemini key, App Secret & webhook")}
                  </summary>
                  <div style={{padding:"0 14px 14px",fontSize:12.5,color:"#4C1D95",fontWeight:600,lineHeight:1.65}}>
                    <p style={{margin:"4px 0 8px",fontWeight:800}}>{T("喺 Supabase → Edge Functions → Secrets 加入呢三個：","In Supabase → Edge Functions → Secrets, add these three:")}</p>
                    <ol style={{margin:0,paddingLeft:18,display:"flex",flexDirection:"column",gap:8}}>
                      <li><code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4}}>GEMINI_API_KEY</code> — {T("去 ","from ")}<a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{color:"#7C3AED",fontWeight:800}}>aistudio.google.com/apikey</a>{T(" 撳「Create API key」。","→ Create API key.")}</li>
                      <li><code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4}}>WA_APP_SECRET</code> — {T("Meta 應用程式 → 設定 → 基本 → 「應用程式密鑰 / App Secret」，撳「顯示」複製。","Meta app → Settings → Basic → 「App Secret」 → Show → copy.")}</li>
                      <li><code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4}}>WA_VERIFY_TOKEN</code> — {T("你自己改一串隨機字（例如一段密碼），兩邊要一樣。","any random string you invent (like a password) — must match on both sides.")}</li>
                    </ol>
                    <p style={{margin:"10px 0 6px",fontWeight:800}}>{T("然後喺 Meta 應用程式 → WhatsApp → Configuration → Webhook：","Then in Meta app → WhatsApp → Configuration → Webhook:")}</p>
                    <ul style={{margin:0,paddingLeft:18,display:"flex",flexDirection:"column",gap:6}}>
                      <li>Callback URL: <code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4,wordBreak:"break-all"}}>https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/whatsapp-webhook</code></li>
                      <li>Verify token: {T("同上面 ","same ")}<code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4}}>WA_VERIFY_TOKEN</code>{T(" 一樣。","value as above.")}</li>
                      <li>{T("撳「Verify and save」，然後喺 ","Click 「Verify and save」, then ")}<strong>messages</strong>{T(" 欄撳「Subscribe」。","→ Subscribe to the 「messages」 field.")}</li>
                    </ul>
                    <p style={{margin:"12px 0 6px",fontWeight:800}}>{T("用 Twilio 嘅店鋪（免建 Meta App）：","For shops on Twilio (no Meta app):")}</p>
                    <ul style={{margin:0,paddingLeft:18,display:"flex",flexDirection:"column",gap:6}}>
                      <li>{T("加多一個 secret ","Add one more secret ")}<code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4}}>WA_TWILIO_HOOK_SECRET</code>{T("（你自己改一串隨機字）。","(any random string you invent).")}</li>
                      <li>{T("喺 Twilio → WhatsApp Sender → Configuration，將收訊 webhook 設為：","In Twilio → WhatsApp Sender → Configuration, set the incoming webhook to:")} <code style={{background:"#EDE9FE",padding:"1px 5px",borderRadius:4,wordBreak:"break-all"}}>https://oiiuarooqzbqtuqtshzx.supabase.co/functions/v1/whatsapp-webhook?key=&lt;WA_TWILIO_HOOK_SECRET&gt;</code>{T("（method: HTTP POST）。","(method: HTTP POST).")}</li>
                    </ul>
                  </div>
                </details>
              </Card>
            );
          })()}

          {/* Plan pricing — base currency + FX rates. The base currency
              is what the prices on the 方案 tab are stored in; the FX
              table is used to convert into each shop's currency when
              the plan card is shown. Updating a rate flips every
              shop's displayed price instantly. */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>💱 訂閱費匯率</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700,lineHeight:1.5}}>店家睇到嘅方案價會由「基礎貨幣」按下面匯率自動換算。冇填嘅貨幣 → 用基礎貨幣顯示。</p>
            <Sel
              label="計價基礎貨幣"
              value={state.platformConfig?.basePlanCurrency || "HKD"}
              onChange={e => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), basePlanCurrency: e.target.value } }))}
            >
              {SUPPORTED_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Sel>
            <p style={{fontSize:11,color:"#AFAFC4",margin:"12px 0 6px",fontWeight:800,letterSpacing:0.3,textTransform:"uppercase"}}>對換率（1 {state.platformConfig?.basePlanCurrency || "HKD"} =）</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:8}}>
              {SUPPORTED_CURRENCIES.filter(c => c.code !== (state.platformConfig?.basePlanCurrency || "HKD")).map(c => {
                const rates = state.platformConfig?.fxRates || {};
                const rate = rates[c.code] ?? "";
                return (
                  <div key={c.code} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",borderRadius:10,border:"1.5px solid var(--c-light)",background:"#fff"}}>
                    <span style={{fontSize:12,fontWeight:800,color:"#6A6A85",minWidth:60}}>{c.code}</span>
                    <input
                      type="number" step="0.001" min="0"
                      value={rate}
                      onChange={e => {
                        const next = e.target.value;
                        setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), fxRates: { ...(s.platformConfig?.fxRates || {}), [c.code]: next === "" ? "" : Number(next) } } }));
                      }}
                      placeholder="例：0.13"
                      style={{flex:1,minWidth:0,border:"none",fontSize:13,fontWeight:800,color:"#4A4A68",outline:"none",background:"transparent",textAlign:"right"}}
                    />
                  </div>
                );
              })}
            </div>
            <p style={{fontSize:11,color:"#AFAFC4",margin:"12px 0 0",fontWeight:700,lineHeight:1.6}}>例：基礎係 HKD，HKD → USD 用 0.128（即 HK$1 ≈ US$0.128），HKD → CNY 用 0.925。可參考 <a href="https://www.exchange-rates.org/" target="_blank" rel="noopener" style={{color:"var(--c-primary)",textDecoration:"underline"}}>exchange-rates.org</a> 或銀行牌價。</p>
          </Card>

          {/* Platform branding — controls the "Powered by ..." footer text
              shown across the customer-facing surfaces. Empty string hides
              the footer entirely (white-label mode). */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}>🏷️ 平台標識</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>客戶介面底部顯示的「由 ... 提供」字樣。留空 = 隱藏（白牌模式）</p>
            <Input
              label="標識文字"
              value={state.platformConfig?.poweredByText ?? "Powered by PettoBook"}
              onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, poweredByText: e.target.value}}))}
              placeholder="Powered by PettoBook"
            />
            <p style={{fontSize:11,color:"#AFAFC4",margin:"6px 0 0",fontWeight:700}}>儲存後客戶端會即時更新</p>
          </Card>

          {/* Stripe Settings */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><DollarSign size={20} color="#7C3AED" strokeWidth={2.5} /> Stripe 支付設定</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>連接 Stripe 後商戶可收取線上訂金和錢包充值</p>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:20,border:"2px solid "+(state.platformConfig?.stripe?.enabled?"#BBF7D0":"#FECACA"),background:state.platformConfig?.stripe?.enabled?"#F0FDF4":"#FEF2F2",marginBottom:16}}>
              <span style={{fontSize:14,fontWeight:800,color:state.platformConfig?.stripe?.enabled?"#166534":"#991B1B"}}>{state.platformConfig?.stripe?.enabled?"Stripe 已啟用 ✅":"Stripe 未啟用"}</span>
              <Toggle on={state.platformConfig?.stripe?.enabled||false} onChange={v => setState(s => ({...s, platformConfig:{...s.platformConfig, stripe:{...s.platformConfig?.stripe, enabled:v}}}))} />
            </div>
            {/* NO KEY FIELDS HERE — deliberately.
                platform_config is SELECT-able by every authenticated user
                (policy platcfg_select_all, qual `true`), so a secret pasted
                into this card was readable by every shop owner, every staff
                member and every customer with an account. The boxes invited
                exactly that, labelled `sk_live_***`, while being read by
                nothing: the keys that actually work are Supabase Edge
                Function secrets (STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET),
                where no client can reach them.
                The 測試模式 toggle went with them — also read by nothing, and
                worse than useless: it let an admin believe they were in test
                mode while live keys were charging real cards. Sandbox vs live
                is decided solely by which key is in STRIPE_SECRET_KEY. */}
            <p style={{fontSize:12,color:"#8A8A9E",margin:"0 0 12px",fontWeight:700,lineHeight:1.6}}>
              🔑 API 密鑰存放喺 Supabase Edge Function secrets（<code>STRIPE_SECRET_KEY</code>、<code>STRIPE_WEBHOOK_SECRET</code>），唔會經呢度儲存。
              用測試定正式，睇嗰條 key 係 <code>sk_test_</code> 定 <code>sk_live_</code>。
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <Sel label="貨幣" value={state.platformConfig?.stripe?.currency||"HKD"} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, stripe:{...s.platformConfig?.stripe, currency:e.target.value}}}))}>
                <option value="HKD">HKD 港幣</option><option value="USD">USD 美元</option><option value="TWD">TWD 新台幣</option><option value="CNY">CNY 人民幣</option>
              </Sel>
            </div>
          </Card>

          {/* SMS / WhatsApp Provider Settings */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><MessageCircle size={20} color="#22C55E" strokeWidth={2.5} /> 訊息服務設定</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>設定 Twilio 或其他 SMS/WhatsApp 服務商</p>
            {/* Platform WhatsApp option hidden — per-tenant tenant_messaging_secrets.whatsapp_* covers the BYO-WhatsApp Business API case for paying shops. */}
            <div style={{marginBottom:16}}>
              <div style={{padding:16,borderRadius:20,border:"2px solid "+(state.platformConfig?.smsProvider?.enabled?"#BFDBFE":"var(--c-light)"),background:state.platformConfig?.smsProvider?.enabled?"#EFF6FF":"#FFFDFD",textAlign:"center"}}>
                <p style={{fontSize:14,fontWeight:800,margin:"0 0 8px",color:"#4A4A68"}}>SMS 📱</p>
                <Toggle on={state.platformConfig?.smsProvider?.enabled||false} onChange={v => setState(s => ({...s, platformConfig:{...s.platformConfig, smsProvider:{...s.platformConfig?.smsProvider, enabled:v}}}))} />
                <p style={{fontSize:11,margin:"8px 0 0",color:"#8A8A9E",fontWeight:700}}>{state.platformConfig?.smsProvider?.provider||"twilio"}</p>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <Sel label="服務商" value={state.platformConfig?.smsProvider?.provider||"twilio"} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, smsProvider:{...s.platformConfig?.smsProvider, provider:e.target.value}}}))}>
                <option value="twilio">Twilio</option><option value="vonage">Vonage (Nexmo)</option><option value="messagebird">MessageBird</option>
              </Sel>
              <Input label="Account SID" value={state.platformConfig?.smsProvider?.accountSid||""} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, smsProvider:{...s.platformConfig?.smsProvider, accountSid:e.target.value}}}))} placeholder="ACxxxxxxxxxxxxxxx" />
              <Input label="Auth Token" value={state.platformConfig?.smsProvider?.authTokenHint||""} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, smsProvider:{...s.platformConfig?.smsProvider, authTokenHint:e.target.value}}}))} placeholder="***" type="password" />
              <Input label="發送號碼" value={state.platformConfig?.smsProvider?.fromNumber||""} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, smsProvider:{...s.platformConfig?.smsProvider, fromNumber:e.target.value}}}))} placeholder="+852XXXXXXXX" />
            </div>
            {/* Platform WhatsApp Deep Link option hidden — tenant-level only. */}
          </Card>

          {/* Auto-notification Engine Settings */}
          <Card style={{padding:24}}>
            <h3 style={{fontWeight:900,fontSize:18,margin:"0 0 4px",color:"#4A4A68",display:"flex",alignItems:"center",gap:8}}><Zap size={20} color="#F59E0B" strokeWidth={2.5} /> 自動通知引擎</h3>
            <p style={{fontSize:13,color:"#8A8A9E",margin:"0 0 16px",fontWeight:700}}>設定排程引擎的運行參數（需要 Supabase Edge Function + pg_cron）</p>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:20,border:"2px solid var(--c-border)",background:"var(--c-lighter)"}}>
                <span style={{fontSize:14,fontWeight:800,color:"#4A4A68"}}>引擎啟用</span>
                <Toggle on={state.platformConfig?.autoNotifEngine?.enabled!==false} onChange={v => setState(s => ({...s, platformConfig:{...s.platformConfig, autoNotifEngine:{...s.platformConfig?.autoNotifEngine, enabled:v}}}))} />
              </div>
              <Input label="排程 (cron 格式)" value={state.platformConfig?.autoNotifEngine?.cronSchedule||"0 9 * * *"} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, autoNotifEngine:{...s.platformConfig?.autoNotifEngine, cronSchedule:e.target.value}}}))} placeholder="0 9 * * *" />
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Input label="預約提醒 (小時前)" type="number" value={state.platformConfig?.autoNotifEngine?.reminderHoursBefore||24} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, autoNotifEngine:{...s.platformConfig?.autoNotifEngine, reminderHoursBefore:parseInt(e.target.value)||24}}}))} />
                <Input label="久未到店 (天)" type="number" value={state.platformConfig?.autoNotifEngine?.inactiveCustomerDays||60} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, autoNotifEngine:{...s.platformConfig?.autoNotifEngine, inactiveCustomerDays:parseInt(e.target.value)||60}}}))} />
              </div>
              <Input label="疫苗到期提醒 (天前)" type="number" value={state.platformConfig?.autoNotifEngine?.vaccineDaysBeforeExpiry||30} onChange={e => setState(s => ({...s, platformConfig:{...s.platformConfig, autoNotifEngine:{...s.platformConfig?.autoNotifEngine, vaccineDaysBeforeExpiry:parseInt(e.target.value)||30}}}))} />
            </div>
            <div style={{background:"#EFF6FF",borderRadius:16,padding:"14px 16px",marginTop:16,border:"2px solid #BFDBFE"}}>
              <p style={{fontSize:13,fontWeight:800,color:"#1E40AF",margin:"0 0 6px"}}>📋 自動通知如何發送？</p>
              <div style={{fontSize:12,color:"#3B82F6",fontWeight:700,lineHeight:1.8}}>
                <p style={{margin:"0 0 4px"}}>1. <strong>即時通知</strong>（預約確認、美容完成、取消）→ 操作完成後立即觸發</p>
                <p style={{margin:"0 0 4px"}}>2. <strong>排程通知</strong>（預約提醒、疫苗到期、生日、久未到店）→ 每天 cron 排程檢查</p>
                <p style={{margin:"0 0 4px"}}>3. 發送方式根據「自動通知」分頁中每個通知的設定（WhatsApp 或 SMS）</p>
                <p style={{margin:0}}>4. WhatsApp Deep Link 模式下會開啟 WhatsApp 應用；API 模式需要 Twilio 設定</p>
              </div>
            </div>
          </Card>

          <div style={{marginTop:24,marginBottom:8}}>
            <details style={{background:"#fff",border:"1px solid #E9E6F6",borderRadius:18,padding:"4px 16px 16px"}}>
              <summary style={{cursor:"pointer",fontWeight:900,fontSize:15,color:"#4C1D95",padding:"14px 0",listStyle:"none",display:"flex",alignItems:"center",gap:8}}>
                <BookOpen size={18} /> 使用說明中心（FAQ / 教學）
              </summary>
              <p style={{fontSize:12.5,color:"#8A8A9E",fontWeight:700,margin:"0 0 14px"}}>編輯 /help 頁面同店主後台顯示嘅教學內容。支援粗體、標題、清單、連結。</p>
              <HelpEditor
                content={state.platformConfig?.helpContent || DEFAULT_HELP_CONTENT}
                onChange={(c) => setState(s => ({ ...s, platformConfig: { ...(s.platformConfig || {}), helpContent: c } }))}
              />
            </details>
          </div>
          <Btn onClick={savePlatformConfig} style={{width:"100%",padding:"16px"}}><Save size={18} /> 儲存所有設定</Btn>
        </div>}
      </div>
    </div>
  );
};

// ── ROOT ───────────────────────────────────────────────────────────────────
import { useAppState } from "./lib/useAppState.js";
import BlogPage from "./blog/BlogPage.jsx";
import { HelpCenter, HelpEditor } from "./components/HelpCenter.jsx";
import { DEFAULT_HELP_CONTENT } from "./content/helpContent.js";
import FeaturesPage from "./components/FeaturesPage.jsx";
import PricingPage from "./components/PricingPage.jsx";
import CustomerShopChat from "./components/CustomerShopChat.jsx";
import PhotoLightbox from "./components/PhotoLightbox.jsx";
import CustomerShopStore from "./components/CustomerShopStore.jsx";
import PosApp, { PointsEarnSettings } from "./components/pos/PosApp.jsx";
import CustomerPurchases from "./components/pos/CustomerPurchases.jsx";
import AnnouncementEmailEditor, { effectiveAnnounceEmail } from "./components/AnnouncementEmailEditor.jsx";
import { signUp as authSignUp } from "./lib/auth.js";
import { purchaseTier as rcPurchaseTier, restore as rcRestore, playManageUrl as rcPlayManageUrl } from "./lib/revenuecat.js";
import { fetchStaffPay, upsertStaffPaySettings, upsertWageEntry, deleteWageEntry, recordPayout, voidPayout, ownerGrantVoucher, ownerBroadcastNotification, fetchShopNews, upsertShopNews, deleteShopNews, upsertPet, upsertAppointment, deleteAppointment, pushNotification, insertTenant, patchProfile, patchTenant, upsertService, upsertAddon, fetchMessagingSecrets, upsertMessagingSecrets, sendWhatsApp, fetchWaConversations, fetchWaMessages, setWaConversationMode, markWaConversationRead, insertWaStaffMessage, fetchAiConciergeUsage, listWaTemplates, fetchWaUnread, fetchPublicTenantBundle, fetchPublicBusySlots, fetchPublicAlbum, fetchPublicDirectory, fetchListingBySlug, submitShopClaim, fetchShopClaims, setShopClaimStatus, sendSalesMessage, pollSalesMessages, fetchSalesChats, fetchSalesMessages, replySalesChat, markSalesChatRead, setSalesChatStatus, submitContactMessage, requestDemo, ownerUpdateCustomer, requestCustomerClaim, fetchPendingClaims, approveCustomerClaim, rejectCustomerClaim, postGroomingRequest, fetchMyGroomingRequests, fetchQuotesForRequest, fetchShopInbox, submitQuote, acceptGroomingQuote, cancelGroomingRequest, createWalkinCustomer, createCustomerAccount, resetCustomerPassword, sendCustomerLogin, customerSyntheticEmail, createStaff, sendCustomerOtp, verifyCustomerOtp, linkPetsOnLogin, fetchActivityLogs, logActivity, upsertPlatformConfig, uploadTenantMedia, uploadGroomingMedia, calcTax, validatePromoCode, redeemPromoCode, fetchPromoCodes, upsertPromoCode, deletePromoCode, fetchServicePackages, upsertServicePackage, deleteServicePackage, fetchCustomerPackages, fetchTenantCustomerPackages, purchaseCustomerPackage, redeemCustomerPackage, restoreCustomerPackageVisit, adjustCustomerPackageVisit, extendCustomerPackage, refundCustomerPackage, recordPaymentAtomic, getGoogleAuthorizeUrl, disconnectGoogleCalendar, syncAppointmentToGoogle, requestPackagePurchase, sendSms, sendEmail, createStripeCheckout, createSubscriptionCheckout, createStripePortalSession, recordConsent, exportMyData, requestDataDeletion, requestAccountDeletion, upsertCustomerMembership, leaveCustomerMembership, setCustomerStamps, ensurePushSubscription, sendPush, fetchStaffDeductions, addStaffDeduction, deleteStaffDeduction, fetchShopFunnel, fetchShopFollowups, logShopFollowup, claimDocUrl, computeTieredCommission, settleBookingDeposit, refundCancellation, patchCustomerMembership, debitCustomerWallet, creditCustomerWallet, awardLoyaltyForApt, revokeLoyaltyForApt, setCustomerBlock, fetchSupportTickets, createSupportTicket, fetchTicketMessages, postTicketMessage, updateTicketStatus, askShopAssistant, registerDeviceToken, persistSiriConfig, announceEmail, setTenantPosEnabled } from "./lib/api.js";
import { signIn as authSignInDirect, sendPasswordReset, updatePassword, signInWithGoogle } from "./lib/auth.js";
import { supabase as supabaseDirect } from "./lib/supabaseClient.js";
import { getHolidaysForYear, getHolidayForDate, SUPPORTED_COUNTRIES, preloadHolidays, subscribeHolidaysReady } from "./lib/holidays.js";
import { seedTenantDefaults } from "./lib/seedDefaults.js";
import { SETTING_TEMPLATES, templateLabels, getTemplate as getSettingTemplate, previewTemplate as previewSettingTemplate, applyTemplate as applySettingTemplate, snapshotTemplateState, restoreTemplateSnapshot } from "./lib/settingTemplates.js";
import { DEFAULT_DISCLAIMER_SENIOR, DEFAULT_TERMS_BOOKING, DEFAULT_TERMS_CAT } from "./lib/defaultTemplates.js";
import { CURRENCIES, LANGUAGES, t as i18nT, getLocale as i18nGetLocale, setLocale as i18nSetLocale, listLocales as i18nListLocales, useLocale as i18nUseLocale, cycleLocale as i18nCycleLocale, localeShort as i18nLocaleShort } from "./lib/i18n.js";
import { BREEDS, searchBreeds, breedLabel, getBreedById, inferBreedId } from "./lib/breeds.js";
import { validatePhone, validateEmail, validateAmount, normalizePhoneDigits, normalizeNumeric, formatPhoneDisplay, floorCents, ceilCents, parseCopiedPhone } from "./lib/validation.js";
import ErrorBoundary from "./lib/ErrorBoundary.jsx";
import { computePay, DEFAULT_SETTINGS as PAY_ENGINE_DEFAULTS, payoutsOverlapping as payoutsOverlappingRows, payoutCovering as payoutCoveringRow } from "./lib/payroll.js";

export default function App() {
  // Root-level locale subscription. Without this, ONLY components that call
  // i18nUseLocale() themselves (OwnerApp, CustomerApp) re-render on a
  // language flip — the login screen, marketing home, booking sheets, and
  // any standalone modal sit on a stale render and the toggle "does
  // nothing." Subscribing here forces the entire tree to re-evaluate
  // i18nT() calls when the user switches language.
  i18nUseLocale();
  const { state, setState, profile, loading, error, syncStatus, signIn, signOut, reload, setRealtimeTenant } = useAppState();
  // Set once the forced first-login password change succeeds, so the modal
  // closes immediately instead of waiting on reload() to re-fetch the profile
  // (measured: still open after 30s even though both writes had landed).
  const [pwChangedId, setPwChangedId] = useState(null);
  // Expose to nested components via window for now (avoids prop-drilling
  // through 3 layers to the settings page). Read by OwnerApp's save bar.
  useEffect(() => { if (typeof window !== "undefined") window.__petosSyncStatus = syncStatus; }, [syncStatus]);
  // Surface auth/load errors back to the login screen so a hung post-login
  // reload or a missing-profile failure doesn't leave the user with an
  // unexplained empty form. The map keeps the customer-rejection text intact
  // and falls through to whatever message useAppState set.
  const loginErrMsg = error?.message === "CUSTOMER_LOGIN_REJECTED"
    ? "客戶請使用店家提供的專屬預約連結登入"
    : (error?.message || "");
  // Re-render once the lazy-loaded date-holidays library is ready, so calendars
  // pick up the holiday hints without needing user interaction.
  const [, setHolidaysVer] = useState(0);
  useEffect(() => {
    preloadHolidays();
    const off = subscribeHolidaysReady(() => setHolidaysVer(v => v + 1));
    return off;
  }, []);
  const [screen, setScreen] = useState(() => {
    if (typeof window === "undefined") return "login";
    let h = window.location.hash || "";
    const p = window.location.pathname || "/";
    // ── Clean-path support: /shop/{slug}[/{tab}] (also legacy /book/{slug}) ──
    // Vercel's catch-all rewrite serves index.html for any path, so we just
    // need to detect the path here and synthesize the hash route that the
    // rest of the app already understands. The optional second segment
    // becomes the tab name (gallery / info / policies / book).
    const pathMatch = p.match(/^\/(?:shop|book)\/([^/]+)(?:\/([^/]+))?\/?$/);
    if (pathMatch) {
      const slug = pathMatch[1];
      const sub = pathMatch[2]; // undefined | "book" | "gallery" | "info" | "policies"
      h = "#/shop/" + slug + (sub ? "/" + sub : "");
      // We don't replaceState here — the path itself stays clean ("/shop/petto"
      // or "/shop/petto/gallery"); the hash is purely an internal route key
      // for the existing screen-router and is invisible to bookmarks because
      // we never write it back to the URL bar.
    }
    // Public album: /album/{token} or #/album/{token}. Same trick — clean
    // URL stays visible to the customer; hash route key is internal.
    const albumPathMatch = p.match(/^\/album\/([^/?#]+)\/?$/);
    if (albumPathMatch) {
      h = "#/album/" + albumPathMatch[1];
    }
    // Public shop directory: /directory (also #/directory). Anon-accessible.
    if (/^\/directory\/?$/.test(p)) {
      h = "#/directory";
    }
    // ── Recover from malformed OAuth-appended hash ────────────────────────
    // When `redirectTo` had a hash like `#/shop/{slug}`, Supabase OAuth
    // appends `#access_token=...` producing `#/shop/{slug}#access_token=...`
    // which gets percent-encoded into one hash `#/shop/{slug}%23access_token=...`.
    // Detect this and extract the clean booking part so the slug matches.
    const malformedMatch = h.match(/^(#\/(?:shop|book)\/[^%/#]+)(?:%23|#)(access_token=|refresh_token=|error=)/);
    if (malformedMatch) {
      h = malformedMatch[1];
      window.location.hash = h;
    }
    // ── Restore booking destination after Google OAuth ────────────────────
    try {
      const saved = window.localStorage.getItem("petos:booking-redirect");
      const hasOAuthFragment = /access_token=|refresh_token=|error=/.test(h);
      if (saved && /^#\/(?:shop|book)\//.test(saved) && !hasOAuthFragment && !/^#\/(?:shop|book)\//.test(h)) {
        window.location.hash = saved;
        h = saved;
        window.localStorage.removeItem("petos:booking-redirect");
      }
    } catch { /* localStorage unavailable — skip */ }
    if (/^#\/(?:shop|book)\//.test(h)) return "public_booking";
    if (/^#\/album\//.test(h)) return "public_album";
    if (/^#\/listing\//.test(h)) return "directory_listing";
    if (/^#\/directory/.test(h)) return "directory_index";
    if (/^#\/reset/.test(h)) return "reset_password";
    if (/^#\/forgot/.test(h)) return "forgot_password";
    if (/^#\/privacy/.test(h)) return "privacy";
    if (/^#\/merchant/.test(h)) return "merchant";
    if (/^#\/terms/.test(h)) return "terms";
    return "login";
  });

  // Secondary restore — fallback for OAuth round-trips where the path was
  // lost. With clean-path /shop/{slug} URLs the path itself is preserved
  // through Supabase OAuth (only the hash fragment carries access_token),
  // so this restore is rarely needed. Skip entirely when the pathname
  // already encodes a /shop/ or /book/ route — otherwise the restore
  // injects a leftover hash and produces ugly URLs like
  // `/shop/petto/book#/shop/petto`.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let saved = null;
    try { saved = window.localStorage.getItem("petos:booking-redirect"); } catch { return; }
    if (!saved || !/^#\/(?:shop|book)\//.test(saved)) return;
    const currentPath = window.location.pathname || "/";
    const currentHash = window.location.hash || "";
    // If the path already routes to a shop, the restore is unnecessary AND
    // harmful (it stacks the hash on top of the path).
    if (/^\/(?:shop|book)\//.test(currentPath)) {
      try { window.localStorage.removeItem("petos:booking-redirect"); } catch {}
      return;
    }
    if (/access_token=|refresh_token=|error=/.test(currentHash)) return;
    if (!/^#\/(?:shop|book)\//.test(currentHash)) {
      window.location.hash = saved;
    }
    try { window.localStorage.removeItem("petos:booking-redirect"); } catch {}
  }, [profile]);

  // Supabase fires PASSWORD_RECOVERY when the user lands via the password-reset
  // email link. Switch to the reset screen so they can set a new password.
  useEffect(() => {
    const { data } = supabaseDirect.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setScreen("reset_password");
    });
    return () => data.subscription.unsubscribe();
  }, []);
  const [publicTenantId, setPublicTenantId] = useState(null);
  const [publicError, setPublicError] = useState(null);
  const [customerInitialTab, setCustomerInitialTab] = useState(null); // when redirected from a booking link
  // Set when a customer who came through the shop door chooses to carry on as
  // a customer, so the notice doesn't reappear for the rest of the session.
  const [shopDoorDismissed, setShopDoorDismissed] = useState(false);
  // For multi-shop customers: which membership/tenant slug is "active" right
  // now. Mirrored to window.__petosActiveCustomerSlug so dataLoader can read
  // it without prop drilling.
  // Seed from the last shop the customer used so returning multi-shop
  // customers land where they left off. A shop deep-link / OAuth return
  // still overrides this via setActiveCustomerSlug (runs after init).
  const [activeCustomerSlug, setActiveCustomerSlug] = useState(() => {
    try { return (typeof localStorage !== "undefined" && localStorage.getItem("petos:lastCustomerSlug")) || null; } catch { return null; }
  });
  useEffect(() => {
    if (typeof window !== "undefined") window.__petosActiveCustomerSlug = activeCustomerSlug || null;
    try { if (activeCustomerSlug) localStorage.setItem("petos:lastCustomerSlug", activeCustomerSlug); } catch { /* storage evicted — non-critical */ }
  }, [activeCustomerSlug]);

  // useAppState calls window.__petosRouteAfterAuth({ slug, tab }) after a
  // Google OAuth round-trip so we can land the customer on the right shop
  // + tab without prop drilling.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__petosRouteAfterAuth = ({ slug, tab } = {}) => {
      if (slug) setActiveCustomerSlug(slug);
      if (tab) setCustomerInitialTab(tab);
      // Reset to "login" so the role-based router takes over; for an
      // authenticated customer, App.jsx renders CustomerApp.
      setScreen("login");
    };
    return () => { try { delete window.__petosRouteAfterAuth; } catch {} };
  }, []);

  // Local React state for anonymous public-booking tenant — separate from the
  // main useAppState `state` to avoid the syncStateDiff wrapper from firing
  // unauthorized DB writes back to Supabase.
  const [publicTenant, setPublicTenant] = useState(null);
  const [publicStaffUsers, setPublicStaffUsers] = useState([]);
  // Non-member (external) listing currently being viewed at #/listing/{slug}.
  const [publicListing, setPublicListing] = useState(null);
  // AI blog reader route (/blog and /blog/{slug}) — public, anon-accessible.
  const [blogSlug, setBlogSlug] = useState(null);
  const [publicListingLoading, setPublicListingLoading] = useState(false);

  // ── Public shop tab (overview / gallery / info / policies / book) ──
  // Driven by URL pathname so each tab gets its own shareable link.
  const [shopHomeTab, setShopHomeTab] = useState("overview");

  // Simulate URL-based routing via hash for in-app screens, plus pathname
  // detection for the clean /shop/{slug}[/{tab}] marketing URLs.
  useEffect(() => {
    let cancelled = false;
    const checkHash = async () => {
      const hash = window.location.hash;
      const path = window.location.pathname || "/";
      if (/^#\/reset/.test(hash)) { setScreen("reset_password"); return; }
      if (/^#\/forgot/.test(hash)) { setScreen("forgot_password"); return; }
      if (/^#\/privacy/.test(hash)) { setScreen("privacy"); return; }
      if (/^#\/merchant/.test(hash)) { setScreen("merchant"); return; }
      if (/^#\/terms/.test(hash)) { setScreen("terms"); return; }
      if (/^(?:\/en)?\/help(?:\/[a-z0-9-]+)?\/?$/i.test(path) || /^#\/help/.test(hash)) { setScreen("help"); return; }
      // /features and its task pages (/features/daily, /features/price-list, …);
      // FeaturesPage reads the task from the path itself.
      if (/^(?:\/en)?\/features(?:\/[a-z-]+)?\/?$/.test(path) || /^#\/features/.test(hash)) { setScreen("features"); return; }
      if (/^(?:\/en)?\/pricing\/?$/.test(path) || /^#\/pricing/.test(hash)) { setScreen("pricing"); return; }
      // Public album: /album/{token} or #/album/{token}. Anon-readable
      // page; do NOT fall through to the public_* → login reset at the
      // bottom of this function (that was knocking the album back to
      // login on every effect re-run).
      const albumPathMatch2 = path.match(/^\/album\/([^/?#]+)\/?$/);
      const albumHashMatch  = hash.match(/^#\/album\/([^/?#]+)/);
      if (albumPathMatch2 || albumHashMatch) {
        setScreen("public_album");
        return;
      }
      // Non-member external listing: /listing/{slug} or #/listing/{slug}.
      // Anon-readable basic profile for a shop not yet on PetOS.
      const listingPathMatch = path.match(/^\/listing\/([^/?#]+)\/?$/);
      const listingHashMatch = hash.match(/^#\/listing\/([^/?#]+)/);
      const listingSlug = (listingPathMatch && listingPathMatch[1]) || (listingHashMatch && listingHashMatch[1]) || null;
      if (listingSlug) {
        setScreen("directory_listing");
        setPublicListingLoading(true);
        try {
          const row = await fetchListingBySlug(listingSlug);
          if (cancelled) return;
          setPublicListing(row);
        } catch (e) {
          if (cancelled) return;
          console.error("[PettoBook] listing fetch failed:", e);
          setPublicListing(null);
        }
        if (!cancelled) setPublicListingLoading(false);
        return;
      }
      // Public shop directory — same anon-accessible guard so a /directory
      // visit doesn't get bounced to login when state effects re-run.
      if (/^\/directory(?:\/[a-z-]+)?\/?$/.test(path) || /^#\/directory/.test(hash)) {
        setScreen("directory_index");
        return;
      }
      // AI blog — /blog (index) and /blog/{slug} (article). Anon-accessible, so
      // guard like the directory (don't bounce to login on effect re-runs).
      const blogPathMatch = path.match(/^\/blog\/([^/?#]+)\/?$/);
      const blogHashMatch = hash.match(/^#\/blog\/([^/?#]+)/);
      const blogPost = (blogPathMatch && blogPathMatch[1]) || (blogHashMatch && blogHashMatch[1]) || null;
      if (blogPost) { setBlogSlug(blogPost); setScreen("blog_post"); return; }
      if (/^\/blog\/?$/.test(path) || /^#\/blog\/?$/.test(hash)) { setScreen("blog_index"); return; }
      // Path-based detection — supports clean URLs like /shop/petto/gallery.
      // Also keep hash-based detection for backwards-compat with old links.
      const pathMatch = path.match(/^\/(?:shop|book)\/([^/]+)(?:\/([^/]+))?\/?$/);
      const hashMatch = hash.match(/^#\/(?:shop|book)\/([^/]+)(?:\/([^/]+))?$/);
      const m = pathMatch || hashMatch;
      if (m) {
        const slug = m[1];
        const sub = m[2]; // undefined | book | gallery | info | policies
        setPublicError(null);
        // First check in-memory state (logged-in path)
        let t = state.tenants.find(x => x.bookingSlug === slug);
        if (!t) {
          try {
            const bundle = await fetchPublicTenantBundle(slug);
            if (cancelled) return;
            if (bundle?.tenant) {
              t = bundle.tenant;
              setPublicTenant(t);
              setPublicStaffUsers(bundle.staffUsers || []);
            } else {
              setPublicError("找不到此美容院 (" + slug + ")");
            }
          } catch (e) {
            console.error("[PettoBook] public tenant fetch failed:", e);
            setPublicError(e?.message || "載入失敗");
            setScreen(s => /^public_/.test(s) ? "login" : s);
            return;
          }
        }
        if (t) {
          // Logged-in customer of THIS shop → kick them to their app's book tab
          // (skip the public homepage entirely). Clear the URL completely so
          // they don't get stuck with /shop/petto/book leaking the marketing
          // path into their dashboard view.
          if (profile?.role === "customer" && profile.tenantId === t.id && sub === "book") {
            try {
              window.history.replaceState({}, "", "/");
              window.localStorage.removeItem("petos:booking-redirect");
            } catch { /* ignore */ }
            window.location.hash = "";
            setCustomerInitialTab("book");
            setScreen("login");
            return;
          }
          setPublicTenantId(t.id);
          // /shop/{slug}  → PublicBooking (now carries the unified
          //   landing — auth + about + staff + policy + hours + info).
          // /shop/{slug}/book → also PublicBooking (legacy URL kept
          //   working so old WhatsApp links / printed QR codes don't
          //   404).
          // /shop/{slug}/gallery | info | policies → kept on the rich
          //   PublicShopHome for those who want the tabbed view.
          if (!sub || sub === "book") {
            // Clean up the URL bar: drop the legacy /book segment so
            // bookmarks + shared links use the canonical /shop/{slug}.
            if (sub === "book" && typeof window !== "undefined") {
              try { window.history.replaceState({}, "", "/shop/" + slug); } catch { /* ignore */ }
            }
            setScreen("public_booking");
          } else {
            setShopHomeTab(sub);
            setScreen("public_shop_home");
          }
          return;
        }
      }
      setScreen(s => /^public_/.test(s) ? "login" : s);
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    // Path-based navigation between shop-home tabs uses pushState; popstate
    // fires on back/forward AND we manually dispatch it after pushState to
    // keep the listener in sync.
    window.addEventListener("popstate", checkHash);
    return () => {
      cancelled = true;
      window.removeEventListener("hashchange", checkHash);
      window.removeEventListener("popstate", checkHash);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Navigate between shop-home tabs without a full reload. Uses pushState so
  // the back button works, then synthesizes a popstate so the checkHash
  // listener (which is the route source-of-truth) re-evaluates the screen.
  const navigateShopTab = useCallback((slug, sub) => {
    // Keep "overview" on its own /overview path so the 概覽 tab stays on the
    // info page instead of falling through to the booking link (/shop/{slug}).
    const targetPath = "/shop/" + slug + (sub ? "/" + sub : "");
    try {
      window.history.pushState({}, "", targetPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      window.location.assign(targetPath);
    }
  }, []);

  const live = profile && state.users[profile.id] ? state.users[profile.id] : profile;
  const activeTenant = live?.tenantId ? state.tenants.find(t => t.id === live.tenantId) : null;
  const customerTheme = useCustomerTheme();
  const themeName = (live?.role === "customer" && customerTheme) || activeTenant?.theme || "peach";
  const currentTheme = THEMES[themeName] || THEMES.peach;

  // Whole-site maintenance (scope === "site"): block everyone except the
  // platform admin. Auth screens stay reachable so an admin can log in to
  // switch it off; the page also offers an admin-login shortcut.
  {
    const mc = state.platformConfig;
    const isAdminAcct = !!(live && (live.role === "superadmin" || live.isSuper));
    const authScreens = ["login", "login_form", "register"];
    if (mc?.maintenanceMode === true && mc?.maintenanceScope === "site" && !isAdminAcct && !authScreens.includes(screen)) {
      return (
        <>
          <ToastContainer />
          <div style={{ minHeight:"100vh", background:"var(--c-light)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center" }}>
            <div style={{fontSize:56, marginBottom:16}}>🛠️</div>
            <h1 style={{fontSize:24, fontWeight:900, color:"#3A3A56", margin:"0 0 10px"}}>{T("系統升級維護中","Under maintenance")}</h1>
            <p style={{fontSize:14, fontWeight:700, color:"#6A6A85", margin:0, maxWidth:400, lineHeight:1.6}}>{mc?.maintenanceMessage || T("我哋正在進行系統升級，暫時無法使用，請稍後再試。不便之處，敬請原諒。","We're upgrading the system and it's temporarily unavailable. Please check back soon — sorry for the inconvenience.")}</p>
            <button onClick={() => setScreen("login_form")} style={{marginTop:26, background:"none", border:"none", color:"#AFAFC4", fontSize:12, fontWeight:700, cursor:"pointer", textDecoration:"underline"}}>{T("管理員登入","Admin login")}</button>
          </div>
        </>
      );
    }
  }

  if (screen === "blog_index") {
    return <BlogPage mode="index" appLocale={i18nGetLocale()} onHome={() => { window.location.href = "/"; }} onOpenPost={(s) => { window.location.href = "/blog/" + s; }} />;
  }
  if (screen === "blog_post") {
    return <BlogPage mode="post" slug={blogSlug} appLocale={i18nGetLocale()} onHome={() => { window.location.href = "/"; }} onOpenPost={(s) => { window.location.href = "/blog/" + s; }} />;
  }

  if (screen === "directory_index") {
    // Anonymous shop directory — no login required to browse.
    // Tapping a card routes to the existing /shop/{slug} booking page.
    return (
      <>
        <ToastContainer />
        <DirectoryIndex
          onPickShop={(slug) => {
            if (!slug) return;
            // Persist the "came from directory" intent across page
            // reloads so the back-to-directory chip survives a refresh.
            try { sessionStorage.setItem("petos:from-directory", "1"); } catch {}
            try { window.history.replaceState({ from: "directory" }, "", "/shop/" + slug + "/overview"); } catch {}
            window.location.href = "/shop/" + slug + "/overview";
          }}
          onPickListing={(slug) => {
            if (!slug) return;
            try { sessionStorage.setItem("petos:from-directory", "1"); } catch {}
            window.location.href = "/listing/" + slug;
          }}
          onShopLogin={() => {
            try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch {}
            window.location.hash = "";
            window.location.pathname = "/";
          }}
          onShopRegister={() => {
            try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch {}
            try { window.history.replaceState({}, "", "/"); } catch {}
            window.location.hash = "";
            setScreen("register");
          }}
          onPostRequest={() => {
            // Anon visitor: stash intent + send them to login. The
            // CustomerApp boot effect reads petos:pending-rfq=1 and
            // auto-opens the PostRequestSheet on first render.
            if (!profile?.id) {
              try { localStorage.setItem("petos:pending-rfq", "1"); } catch {}
              toast("請先登入或註冊以發出詢價", "info");
              window.location.hash = "";
              window.location.pathname = "/";
              return;
            }
            // Already logged in: route them to their customer app with
            // the auto-open flag set.
            try { localStorage.setItem("petos:pending-rfq", "1"); } catch {}
            window.location.hash = "";
            window.location.pathname = "/";
          }}
          social={state.platformConfig || {}}
          rfqEnabled={state.platformConfig?.rfqEnabled === true}
        />
      </>
    );
  }

  if (screen === "directory_listing") {
    // Read-only basic profile for a non-member (external) shop. Anon-readable.
    return (
      <>
        <ToastContainer />
        <NonMemberShop
          listing={publicListing}
          loading={publicListingLoading}
          onBack={() => { window.location.href = "/directory"; }}
        />
      </>
    );
  }

  if (screen === "public_album") {
    // Customer-facing before/after album, opened via the share link the
    // owner sends after grooming. Anon-readable via fetch_public_album
    // RPC keyed on the token in the URL. No auth required.
    //
    // Token lookup checks BOTH the clean path (/album/{token}) and the
    // hash route (#/album/{token}). The clean path is what the owner's
    // share button generates; the screen-router earlier in this file
    // already accepts either form. Previously this block only matched
    // the hash, so a customer opening the clean URL got "缺少分享連結
    // token" even though the route resolved correctly.
    const path = (typeof window !== "undefined" && window.location.pathname) || "";
    const h = (typeof window !== "undefined" && window.location.hash) || "";
    const pathMatch = path.match(/^\/album\/([^/?#]+)\/?$/);
    const hashMatch = h.match(/^#\/album\/([^/?#]+)/);
    const token = (pathMatch && pathMatch[1]) || (hashMatch && hashMatch[1]) || null;
    return (
      <>
        <ToastContainer />
        <PublicAlbumPage token={token} />
      </>
    );
  }

  if (screen === "public_shop_home") {
    const tenant = state.tenants.find(t => t.id === publicTenantId) || publicTenant;
    if (!tenant) {
      return (
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)"}}>
          <div style={{background:"rgba(255,255,255,0.9)",borderRadius:32,padding:"32px 48px",textAlign:"center",boxShadow:"0 16px 40px rgba(0,0,0,0.1)",maxWidth:380}}>
            {publicError ? <>
              <div style={{fontSize:36,marginBottom:8}}>😢</div>
              <p style={{fontWeight:800,color:"#4A4A68",fontSize:15,margin:"0 0 6px"}}>{publicError}</p>
              <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 12px"}}>請確認連結是否正確</p>
              <button onClick={() => { window.history.replaceState({}, "", "/"); location.reload(); }} style={{background:"var(--c-primary)",color:"#fff",border:"none",borderRadius:99,padding:"10px 20px",fontSize:13,fontWeight:800,cursor:"pointer"}}>返回首頁</button>
            </> : <>
              <PawPrint size={40} color="var(--c-primary)" strokeWidth={2.5} style={{animation:"popIn 0.6s ease infinite alternate"}} />
              <p style={{margin:"12px 0 0",fontWeight:800,color:"#4A4A68",fontSize:14}}>載入美容院資料…</p>
            </>}
          </div>
        </div>
      );
    }
    return (
      <>
        <ToastContainer />
        <UndoSnackbarContainer />
        <PublicShopHome
          tenant={tenant}
          staffUsers={publicStaffUsers}
          activeTab={shopHomeTab}
          onTab={(t) => navigateShopTab(tenant.bookingSlug, t)}
          onBook={() => navigateShopTab(tenant.bookingSlug, "book")}
        />
      </>
    );
  }

  if (screen === "public_booking") {
    // Prefer logged-in path (state.tenants), otherwise fall back to public-loaded tenant
    const tenant = state.tenants.find(t => t.id === publicTenantId) || publicTenant;
    if (!tenant) {
      return (
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(circle at 15% 50%, var(--c-light), var(--c-grad1) 50%, var(--c-grad2) 100%)"}}>
          <div style={{background:"rgba(255,255,255,0.9)",borderRadius:32,padding:"32px 48px",textAlign:"center",boxShadow:"0 16px 40px rgba(0,0,0,0.1)",maxWidth:380}}>
            {publicError ? <>
              <div style={{fontSize:36,marginBottom:8}}>😢</div>
              <p style={{fontWeight:800,color:"#4A4A68",fontSize:15,margin:"0 0 6px"}}>{publicError}</p>
              <p style={{fontSize:12,color:"#8A8A9E",fontWeight:700,margin:"0 0 12px"}}>請確認連結是否正確</p>
              <button onClick={() => { window.location.hash = ""; location.reload(); }} style={{background:"var(--c-primary)",color:"#fff",border:"none",borderRadius:99,padding:"10px 20px",fontSize:13,fontWeight:800,cursor:"pointer"}}>返回登入</button>
            </> : <>
              <PawPrint size={40} color="var(--c-primary)" strokeWidth={2.5} style={{animation:"popIn 0.6s ease infinite alternate"}} />
              <p style={{margin:"12px 0 0",fontWeight:800,color:"#4A4A68",fontSize:14}}>載入美容院資料…</p>
            </>}
          </div>
        </div>
      );
    }
    const tenantTheme = THEMES[tenant.theme] || THEMES.peach;
    // Merge public staff users into a derived state for PublicBooking, so anonymous users see the staff selector
    const pbState = {
      ...state,
      users: { ...state.users, ...Object.fromEntries((publicStaffUsers||[]).map(u => [u.id, u])) },
    };
    return (
      <div style={{ ...tenantTheme }}>
        <ToastContainer />
        <UndoSnackbarContainer />
        {profile && <PushPermissionBanner user={profile} />}
        <ErrorBoundary><PublicBooking tenant={tenant} state={pbState} setState={setState} profile={profile} onDone={(opts) => {
          // Clear marketing-path URL so role-based routing takes over.
          try { window.history.replaceState({}, "", "/"); } catch {}
          window.location.hash = "";
          if (opts?.gotoBook) setCustomerInitialTab("book");
          // Force the active-tenant slug so dataLoader picks the right shop
          // when reloading (otherwise it falls back to memberships[0] which
          // may not match this PublicBooking flow).
          if (tenant?.bookingSlug) setActiveCustomerSlug(tenant.bookingSlug);
          // Re-run loadStateForUser so the freshly-linked tenant gets its
          // services / addons / staff attached. Without this, CustomerApp
          // renders with state.tenants populated from the PublicBooking path
          // (which only has tenant metadata, no services) and the user has
          // to refresh manually to see service options.
          if (profile && reload) reload(profile);
          // setScreen("login") just kicks the App back into role-based routing;
          // since the user is authenticated now, App will render CustomerApp.
          setScreen("login");
        }} /></ErrorBoundary>
      </div>
    );
  }

  if (screen === "register") {
    return (
      <div style={{ ...currentTheme }}>
        <ToastContainer />
        <UndoSnackbarContainer />
        <ShopRegister
          onBack={() => setScreen("login")}
          onRegistered={() => { setScreen("login"); }}
          state={state} setState={setState}
        />
      </div>
    );
  }

  if (screen === "forgot_password") {
    return (
      <div style={{ ...currentTheme }}>
        <ToastContainer />
        <UndoSnackbarContainer />
        <ForgotPassword onBack={() => { window.location.hash = ""; setScreen("login"); }} />
      </div>
    );
  }

  if (screen === "reset_password") {
    return (
      <div style={{ ...currentTheme }}>
        <ToastContainer />
        <UndoSnackbarContainer />
        <ResetPassword onDone={() => setScreen("login")} />
      </div>
    );
  }

  if (screen === "privacy") {
    return <PrivacyPage onBack={() => { window.history.back(); }} />;
  }
  if (screen === "terms") {
    return <TermsPage onBack={() => { window.history.back(); }} />;
  }
  if (screen === "merchant") {
    return <MerchantPage onBack={() => { window.history.back(); }} />;
  }
  if (screen === "pricing") {
    return <PricingPage
      onSignup={() => {
        try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch { /* ignore */ }
        try { window.history.replaceState({}, "", "/"); } catch { /* ignore */ }
        window.location.hash = "";
        setScreen("register");
      }}
    />;
  }
  if (screen === "features") {
    return <FeaturesPage
      onSignup={() => {
        // Same entry as the landing page's shop sign-up button.
        try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch { /* ignore */ }
        try { window.history.replaceState({}, "", "/"); } catch { /* ignore */ }
        window.location.hash = "";
        setScreen("register");
      }}
    />;
  }
  if (screen === "help") {
    return <HelpCenter
      content={state.platformConfig?.helpContent || DEFAULT_HELP_CONTENT}
      lang={/^\/en\//.test(window.location.pathname) || !(i18nGetLocale?.() || "zh-HK").startsWith("zh") ? "en" : "zh"}
      onBack={() => {
        // Mirror the ForgotPassword pattern: clear the route and reset to
        // "login" so the App falls back into role-based routing (shows the
        // owner/customer app if logged in, else the login screen). We can't
        // rely on history.back() — the router's no-match fallback (line ~30004)
        // only resets public_* screens, so "help" would otherwise stick.
        try { window.history.replaceState({}, "", "/"); } catch { /* ignore */ }
        window.location.hash = "";
        setScreen("login");
      }}
    />;
  }

  return (
    <div style={{ ...currentTheme, minHeight:"100vh", display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@500;600;700;800;900&display=swap');
        * { box-sizing: border-box; font-family: 'Nunito', 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, sans-serif; }
        body { background: #FCFCFF; color: #4A4A68; margin: 0; -webkit-tap-highlight-color: transparent; }
        ::-webkit-scrollbar { display: none; }
        input, select, textarea { font-family: inherit; }
        @keyframes popIn { 0% { transform: scale(0.95); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .cute-card { animation: popIn 0.3s cubic-bezier(0.32,0.72,0,1); }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
      <ToastContainer />
        <UndoSnackbarContainer />
      {/* Loader visibility: show whenever
            (a) initial cold boot (loading=true, no profile yet) — pre-React
                #initial-loader hands off here seamlessly, OR
            (b) any in-flight post-auth bootstrap (loading=true, no usable
                state for the profile yet) — covers the "post-login landing
                flash" bug where the parent would render MarketingHome for a
                tick before onAuthChange's setLoading kicked in.
          We still allow the role view to render while loading=true if the
          user has cached state for their profile, so warm reopens keep the
          fast hydrate-then-refresh path. */}
      {(loading && !profile) || (loading && profile && !state.users[profile.id]) ? (
        // Matches the HTML #initial-loader exactly so the handoff from
        // pre-React loader to React loader is visually seamless (no "two
        // loaders" flash).
        <div style={{position:"fixed",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(circle at 15% 50%, #FFE9F2, #FFD8E8 50%, #FFC4DE 100%)"}}>
          <div style={{background:"rgba(255,255,255,0.9)",borderRadius:32,padding:"32px 48px",textAlign:"center",boxShadow:"0 16px 40px rgba(0,0,0,0.08)"}}>
            <img src="/icon-512.png" alt="PettoBook" width="64" height="64" style={{margin:"0 auto 12px",display:"block",borderRadius:18,animation:"pulse 1.2s ease-in-out infinite alternate"}} />
            <p style={{margin:0,fontWeight:800,color:"#4A4A68",fontSize:14}}>{zhEn("PettoBook 載入中…", "Loading PettoBook…")}</p>
            <button onClick={() => { try { localStorage.clear(); } catch {} location.reload(); }} style={{marginTop:14,background:"none",border:"none",color:"#8A8A9E",fontSize:12,cursor:"pointer",fontWeight:700,textDecoration:"underline"}}>{zhEn("太久了？點此重置並重新登入", "Taking too long? Tap to reset and log in again")}</button>
          </div>
        </div>
      ) : !profile || !live ? (
        // OAuth callback in flight — show a friendly "登入中" page instead
        // of the marketing landing. This catches the case where Supabase's
        // detectSessionInUrl hasn't finished parsing the access_token yet.
        /access_token=|refresh_token=|provider_token=/.test(typeof window !== "undefined" ? window.location.hash : "") ? (
          <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"radial-gradient(circle at 15% 50%, #FFE9F2, #FFD8E8 50%, #FFC4DE 100%)"}}>
            <div style={{background:"rgba(255,255,255,0.9)",borderRadius:32,padding:"32px 48px",textAlign:"center",boxShadow:"0 16px 40px rgba(0,0,0,0.08)"}}>
              <PawPrint size={36} color="#FF6B8B" strokeWidth={2.5} style={{margin:"0 auto 12px",display:"block",animation:"pulse 1.2s ease-in-out infinite alternate"}} />
              <p style={{margin:0,fontWeight:800,color:"#4A4A68",fontSize:14}}>正在完成登入…</p>
            </div>
          </div>
        ) : screen === "login_form" || (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) ? (
          // Native (iOS / Android) shells skip the marketing landing — the
          // app is downloaded by people who already know what PettoBook is.
          // They land on the login form directly.
          <Login
            onSignIn={(email, password) => signIn(email, password)}
            onRegister={() => setScreen("register")}
            onForgot={() => { window.location.hash = "#/forgot"; setScreen("forgot_password"); }}
            externalErr={loginErrMsg}
          />
        ) : (
          // Public home page = lightweight landing (no shop list → loads no shop
          // photos → no Google cost). The full listing lives at /directory.
          <>
            <ToastContainer />
            <DirectoryIndex
              landing
              onBrowse={() => { window.location.href = "/directory"; }}
              onShopLogin={() => { try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch {} setScreen("login_form"); }}
              onShopRegister={() => { try { localStorage.setItem("petos:auth-source", "shop-login:" + Date.now()); } catch {} setScreen("register"); }}
              social={state.platformConfig || {}}
              rfqEnabled={state.platformConfig?.rfqEnabled === true}
          rfqEnabled={state.platformConfig?.rfqEnabled === true}
            />
          </>
        )
      ) : (() => {
        // Resolve effective view:
        //   - Anyone with `role === "superadmin"` always sees SuperAdminApp.
        //   - Anyone with `is_super = true` AND a normal role (owner/staff/
        //     customer) can flip between their role view and SuperAdminApp
        //     via the localStorage flag `petos:view-as`. Default = role view.
        let viewAs = null;
        try { viewAs = window.localStorage.getItem("petos:view-as"); } catch { /* ignore */ }
        const showSuper = live.role === "superadmin" || (live.isSuper && viewAs === "super");
        if (showSuper) return <SuperAdminApp state={state} setState={setState} user={live} onLogout={signOut} />;

        // Shop-door guard: a CUSTOMER account signed in through 店主登入.
        // Only fires when that door was used in the last few minutes, so it
        // can never interrupt someone arriving via the customer door or a
        // booking link. Owners whose registration never finished are NOT
        // handled here — they already get ShopRegister with a 註冊未完成
        // banner further down, which is the better recovery.
        if (!shopDoorDismissed && live.role === "customer" && cameThroughShopDoor()) {
          return (
            <ShopDoorGuard
              user={live}
              kind="customer"
              onContinue={() => setShopDoorDismissed(true)}
              onRegisterShop={() => {
                // owner-register is the existing promotion path: on the next
                // boot useAppState flips this customer-default profile to
                // owner, which then renders ShopRegister. Reusing it means a
                // customer can open a shop on the account they already have,
                // instead of hitting "email already registered".
                try { localStorage.setItem("petos:auth-intent", "owner-register"); } catch { /* ignore */ }
                clearShopDoor();
                window.location.href = window.location.origin + "/";
              }}
              onLogout={signOut}
            />
          );
        }
        return null; // continue to role-based render below
      })() || (
        live.role === "customer"
          ? (
              <CustomerApp state={state} setState={setState} user={live} onLogout={signOut} initialTab={customerInitialTab} onInitialTabConsumed={() => setCustomerInitialTab(null)} onSwitchShop={(slug) => {
                setActiveCustomerSlug(slug);
                // Look up the tenant id for the new slug and rebind the
                // realtime channel so updates to the actively-viewed shop
                // (services, appointments, etc.) actually arrive without a
                // full page reload.
                try {
                  const nextTenant = (state.tenants || []).find(t => t.bookingSlug === slug || t.slug === slug);
                  if (nextTenant && setRealtimeTenant) setRealtimeTenant(nextTenant.id);
                } catch { /* ignore */ }
                reload && reload(profile);
              }} onLeaveShop={async (tenantId) => {
                try {
                  await leaveCustomerMembership({ tenantId });
                  toast("已離開店舖", "success");
                  // If we left the active shop, fall back to another membership
                  // (or clear so dataLoader renders the unaffiliated empty state).
                  if (live.tenantId === tenantId) {
                    const next = (state.memberships || []).find(m => m.tenantId !== tenantId);
                    const nextTenant = next && (state.tenants || []).find(t => t.id === next.tenantId);
                    if (nextTenant) {
                      setActiveCustomerSlug(nextTenant.bookingSlug);
                      try { if (setRealtimeTenant) setRealtimeTenant(nextTenant.id); } catch { /* ignore */ }
                    } else {
                      setActiveCustomerSlug(null);
                      try { localStorage.removeItem("petos:lastCustomerSlug"); } catch { /* ignore */ }
                    }
                  }
                  reload && reload(profile);
                } catch (e) {
                  toast("離開店舖失敗：" + (e?.message || e), "error");
                }
              }} />
            )
          : live.role === "owner" && !live.tenantId
            ? (
                // Stuck owner: signed up but tenant creation never completed
                // (typical when email-confirmation flow lost the pending
                // payload). Route them back to ShopRegister so they can
                // finish without manual SQL recovery.
                <div style={{ ...currentTheme }}>
                  <ToastContainer />
        <UndoSnackbarContainer />
                  <div style={{padding:"16px 20px 0",maxWidth:680,margin:"0 auto"}}>
                    <div style={{background:"#FEF3C7",border:"2px solid #FCD34D",borderRadius:16,padding:14,marginBottom:0,display:"flex",alignItems:"flex-start",gap:10}}>
                      <span style={{fontSize:18}}>⚠️</span>
                      <p style={{fontSize:13,color:"#92400E",margin:0,fontWeight:700,lineHeight:1.5}}>
                        {zhEn("您的店鋪註冊未完成。請填寫以下資料以繼續，系統會自動建立預設服務、SPA 同附加項目。", "Your shop sign-up isn't finished. Fill in the details below to continue — default services, SPA and add-ons are set up for you automatically.")}
                      </p>
                    </div>
                  </div>
                  <ShopRegister
                    onBack={signOut}
                    onRegistered={() => { try { window.location.reload(); } catch {} }}
                    state={state} setState={setState}
                  />
                </div>
              )
            : <StaffSeatGate user={live} onLogout={signOut}><OwnerApp state={state} setState={setState} user={live} onLogout={signOut} syncStatus={syncStatus} /></StaffSeatGate>
      )}
      {/* First-login password change — owner-provisioned accounts land
          here with must_change_password=true. The modal blocks all
          interaction until the user picks a new password, then patches
          the flag off. Renders above whichever app screen is mounted.
          「稍後再改」 only hides it until the next login (flag untouched). */}
      {live?.mustChangePassword && pwChangedId !== live.id && (
        <ForcePasswordChange profile={live} onLater={() => setPwChangedId(live.id)} onDone={() => {
          setPwChangedId(live.id);
          setState(s => s.users?.[live.id]
            ? { ...s, users: { ...s.users, [live.id]: { ...s.users[live.id], mustChangePassword: false } } }
            : s);
          reload && reload(live);
        }} />
      )}
    </div>
  );
}
